/* This file is generated by scripts/build-legacy.mjs. */
import React from "react";
import { createRoot } from "react-dom/client";
import { RetailQuoteMetric, RetailQuoteProductBadge, RetailQuoteSheet } from "../components/retail-quote-sheet";
import { invoiceDocumentTaxRows, invoiceTaxSummaryRows } from "../lib/invoice-tax-summary";
import { buildReportCsv, buildReportWorkbook, downloadReportBlob } from "../lib/report-workbook";
import { buildAccountingMonthlySummary } from "../lib/accounting-monthly-summary.mjs";
import { PlatformGate, getInitialRole, isModeLocked, resolveInitialMode, rpApi } from "../platform.jsx";
import { explainHttpFailure, explainNetworkFailure, isExpectedHttpFailure } from "../error-guidance.mjs";
import { supportTicketEntityScope } from "../support-ticket-scope.js";
import { findRetailPriceList, retailPriceListIdentity } from "../../functions/api/_lib/price-list-identity.mjs";
import "../app-shell.css";
import "../inventory-workflow.css";
import "../page-help.css";
import "../../legacy/app/styles.css";

/* ===== data.js ===== */
/* ============================================================
   RANCH POWER — Shared mock data (system of record-ish)
   Everything hangs on: SKU · Lot · Location · Owner · Status
   ============================================================ */
(function () {
  const FLAVORS = [
    { id: 'ginger-citrus',  name: 'Ginger Citrus',  abbr: 'GC', color: 'var(--f-ginger)',   hex: '#9c6b2f' },
    { id: 'key-lime',       name: 'Key Lime',       abbr: 'KL', color: 'var(--f-keylime)',  hex: '#7aa61f' },
    { id: 'blue-raspberry', name: 'Blue Raspberry', abbr: 'BR', color: 'var(--f-blueras)',  hex: '#3f6fc4' },
    { id: 'blood-orange',   name: 'Blood Orange',   abbr: 'BO', color: 'var(--f-bloodor)',  hex: '#c0492a' },
    { id: 'pink-lemonade',  name: 'Pink Lemonade',  abbr: 'PL', color: 'var(--f-pinklem)',  hex: '#cf5a78' },
    { id: 'peach-mango',    name: 'Peach Mango',    abbr: 'PM', color: 'var(--f-peachman)', hex: '#d98a4a' },
  ];
  const flavor = id => FLAVORS.find(f => f.id === id);

  const SELLABLE_IDENTIFIERS = {
    GC: { caseSku: 'RPEDGC12', caseUpc: '627146023641' },
    KL: { caseSku: 'RPEDKL12', caseUpc: '627146023689' },
    BR: { caseSku: 'RPEDBR12', caseUpc: '627146134170' },
    BO: { caseSku: 'RPEDBL12', caseUpc: '627146023665' },
    PL: { caseSku: 'RPEDPL12', caseUpc: '627146023672' },
    PM: { caseSku: 'RPEDPM12', caseUpc: '627146134132' }
  };

  // SKUs — each flavour as a 12-pack case + mixer packs. "case" = 12-pack unit.
  const SKUS = [
    ...FLAVORS.map((f, i) => ({
      id: 'RP-12-' + f.abbr, flavor: f.id, name: f.name + ' 355ml', pack: '12-Pack',
      price: 36.50, deposit: 1.20, code: SELLABLE_IDENTIFIERS[f.abbr].caseSku, upc: SELLABLE_IDENTIFIERS[f.abbr].caseUpc,
    })),
    { id: 'RP-MIX-GC', flavor: 'ginger-citrus',  name: 'Ginger Citrus Mixer', pack: 'Mixer 12', price: 33.5, deposit: 1.20, code: 'RPMXGC' },
    { id: 'RP-MIX-KL', flavor: 'key-lime',       name: 'Key Lime Mixer',      pack: 'Mixer 12', price: 33.5, deposit: 1.20, code: 'RPMXKL' },
    { id: 'RP-MIX-BR', flavor: 'blue-raspberry', name: 'Blue Raspberry Mixer',pack: 'Mixer 12', price: 33.5, deposit: 1.20, code: 'RPMXBR' },
    { id: 'RP-MIX-BO', flavor: 'blood-orange',   name: 'Blood Orange Mixer',  pack: 'Mixer 12', price: 33.5, deposit: 1.20, code: 'RPMXBO' },
    { id: 'RP-MIX-PL', flavor: 'pink-lemonade',  name: 'Pink Lemonade Mixer', pack: 'Mixer 12', price: 33.5, deposit: 1.20, code: 'RPMXPL' },
    { id: 'RP-MIX-PM', flavor: 'peach-mango',    name: 'Peach Mango Mixer',   pack: 'Mixer 12', price: 33.5, deposit: 1.20, code: 'RPMXPM' },
    { id: 'RP-MIX-TRI', flavor: 'mixed',         name: 'Variety Pack B | BR,GC,KL', pack: '12-Pack', price: 36.50, deposit: 1.20, code: 'RPEDMX12B', upc: '627146134200', lotCode: 'RP-VP-', visualLabel: 'MIXB', customerVisible: true, tri: ['blue-raspberry','ginger-citrus','key-lime'] },
    { id: 'RP-MIX-NEW3', flavor: 'mixed',        name: 'Variety Pack C | BO,PM,PL', pack: '12-Pack', price: 36.50, deposit: 1.20, code: 'RPEDMX12C', upc: '627146134231', lotCode: 'RP-VP-B-', visualLabel: 'MIXC', customerVisible: true, tri: ['peach-mango','blood-orange','pink-lemonade'] },
    { id: 'RP-MIX-ALL6', flavor: 'mixed',        name: 'Variety Pack A | All 6 Flavours', pack: '12-Pack', price: 36.50, deposit: 1.20, code: 'RPEDMX12A', upc: '627146134156', lotCode: 'RP-VP-C-', visualLabel: 'MIXA', customerVisible: true, tri: ['blue-raspberry','ginger-citrus','key-lime','peach-mango','blood-orange','pink-lemonade'] },
  ];
  const sku = id => SKUS.find(s => s.id === id);

  const TERRITORIES = ['Northern BC', 'Squamish', 'Kamloops', 'Kelowna'];
  const TERRITORY_CENTER = {
    'Northern BC': [54.50000000, -125.00000000],
    Squamish: [49.70000000, -123.16000000],
    Kamloops: [50.67000000, -120.33000000],
    Kelowna: [49.89000000, -119.49000000],
  };
  const TERRITORY_RECORDS = TERRITORIES.map((name, index) => ({
    centerLat: TERRITORY_CENTER[name]?.[0] || null,
    centerLng: TERRITORY_CENTER[name]?.[1] || null,
    id: name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
    locatorGroupToleranceMeters: 35,
    name,
    sortOrder: (index + 1) * 10,
    active: true
  }));

  const LOCATIONS = [
    { id: 'WH-NBC', name: 'Northern BC Warehouse', type: 'Main Warehouse', territory: 'Northern BC', cap: 800, used: 612, lat: 54.5, lng: -125.0 },
    { id: 'WH-SQ',  name: 'Squamish Warehouse',    type: 'Main Warehouse', territory: 'Squamish',    cap: 500, used: 310, lat: 49.70, lng: -123.16 },
    { id: 'WH-KAM', name: 'Kamloops Warehouse',    type: 'Main Warehouse', territory: 'Kamloops',    cap: 400, used: 228, lat: 50.67, lng: -120.33 },
    { id: 'WH-KEL', name: 'Kelowna Warehouse',     type: 'Main Warehouse', territory: 'Kelowna',     cap: 450, used: 301, lat: 49.89, lng: -119.49 },
    { id: 'MW-SQ',  name: 'Squamish Rep Storage',  type: 'Micro-Warehouse', territory: 'Squamish',   cap: 100, used: 67, rep: 'Dana Whitfield', valueCap: 3000, valueUsed: 2144 },
    { id: 'MW-KEL', name: 'Kelowna Rep Storage',   type: 'Micro-Warehouse', territory: 'Kelowna',    cap: 100, used: 41, rep: 'Marco Ferreira', valueCap: 3000, valueUsed: 1312 },
    { id: 'FBA',    name: 'Amazon FBA — YVR2',     type: 'FBA',            territory: 'FBA' },
    { id: 'DIST-1', name: 'Interior Distributors DC', type: 'Distributor', territory: 'Kamloops' },
  ];
  const loc = id => LOCATIONS.find(l => l.id === id);

  const CUSTOMERS = [
    { id: 'C-001', name: 'Superette Market', banner: 'Superette', type: 'Retailer', territory: 'Squamish', city: 'Squamish, BC', tier: 'Retailer Tier B', terms: 'Net 15', credit: 'Good', rep: 'Dana Whitfield', lastOrder: '2026-05-21', lastCases: 24, risk: 'High', daysSince: 12, status: 'Active', lat: 49.701, lng: -123.155, buyer: 'Jamie Lee', ytd: 18400, note: 'Prefers delivery Tue/Thu. Interested in Mango promo.' },
    { id: 'C-002', name: 'Mountain Gym', banner: 'Mountain Gym', type: 'Gym', territory: 'Squamish', city: 'Squamish, BC', tier: 'Retailer Tier B', terms: 'Net 15', credit: 'Good', rep: 'Dana Whitfield', lastOrder: '2026-05-30', lastCases: 12, risk: 'Low', daysSince: 4, status: 'Active', lat: 49.71, lng: -123.14, buyer: 'Priya N.', ytd: 9200 },
    { id: 'C-003', name: 'Creekside Cafe', banner: 'Creekside', type: 'Cafe', territory: 'Squamish', city: 'Squamish, BC', tier: 'Retailer Tier A', terms: 'Net 30', credit: 'New', rep: 'Dana Whitfield', lastOrder: null, lastCases: 0, risk: 'Prospect', daysSince: null, status: 'Prospect', lat: 49.69, lng: -123.17, buyer: 'Owner', ytd: 0 },
    { id: 'C-004', name: 'North Foods Co-op', banner: 'North Foods', type: 'Retailer', territory: 'Northern BC', city: 'Prince George, BC', tier: 'Retailer Tier A', terms: 'Net 30', credit: 'Good', rep: 'Sam Okoro', lastOrder: '2026-05-18', lastCases: 60, risk: 'Medium', daysSince: 16, status: 'Active', lat: 53.91, lng: -122.74, buyer: 'R. Donnelly', ytd: 52800 },
    { id: 'C-005', name: 'Okanagan Outfitters', banner: 'OK Outfitters', type: 'Retailer', territory: 'Kelowna', city: 'Kelowna, BC', tier: 'Retailer Tier B', terms: 'Net 15', credit: 'Watch', rep: 'Marco Ferreira', lastOrder: '2026-05-09', lastCases: 30, risk: 'High', daysSince: 25, status: 'Active', lat: 49.88, lng: -119.50, buyer: 'T. Vasquez', ytd: 21300, hold: true },
    { id: 'C-006', name: 'Loop Roasters', banner: 'Loop', type: 'Cafe', territory: 'Kelowna', city: 'Kelowna, BC', tier: 'Retailer Tier B', terms: 'Net 15', credit: 'Good', rep: 'Marco Ferreira', lastOrder: '2026-05-28', lastCases: 8, risk: 'Low', daysSince: 6, status: 'Active', lat: 49.89, lng: -119.48, buyer: 'K. Boyd', ytd: 6400 },
    { id: 'C-007', name: 'Highway 5 Pit Stop', banner: 'Hwy5', type: 'Convenience', territory: 'Kamloops', city: 'Kamloops, BC', tier: 'Retailer Tier B', terms: 'Net 15', credit: 'Good', rep: 'Sam Okoro', lastOrder: '2026-05-12', lastCases: 18, risk: 'High', daysSince: 22, status: 'Active', lat: 50.68, lng: -120.34, buyer: 'D. Singh', ytd: 14900 },
    { id: 'C-008', name: 'Interior Distributors', banner: 'Interior Dist.', type: 'Distributor', territory: 'Kamloops', city: 'Kamloops, BC', tier: 'Distributor', terms: 'Net 30', credit: 'Good', rep: 'Darren', lastOrder: '2026-05-25', lastCases: 300, risk: 'Medium', daysSince: 9, status: 'Active', lat: 50.66, lng: -120.31, buyer: 'Purchasing', ytd: 184000 },
  ];
  const cust = id => CUSTOMERS.find(c => c.id === id);

  // Order line helper
  const L = (skuId, qty) => ({ sku: skuId, qty });

  const ORDERS = [
    { id: 'SO-1042', cust: 'C-001', territory: 'Squamish', rep: 'Dana Whitfield', source: 'WH-SQ', delivery: 'Ranch Delivery', status: 'Allocated', date: '2026-06-10', created: '2026-06-04 09:22', reason: 'Normal reorder', lines: [L('RP-12-GC', 12), L('RP-12-PM', 8), L('RP-12-KL', 4)] },
    { id: 'SO-1041', cust: 'C-002', territory: 'Squamish', rep: 'Dana Whitfield', source: 'WH-SQ', delivery: 'Ranch Delivery', status: 'Picked', date: '2026-06-09', created: '2026-06-03 14:02', reason: 'Normal reorder', lines: [L('RP-12-GC', 6), L('RP-12-BR', 6)] },
    { id: 'SO-1040', cust: 'C-004', territory: 'Northern BC', rep: 'Sam Okoro', source: 'WH-NBC', delivery: 'Carrier / Freight', status: 'Submitted', date: '2026-06-12', created: '2026-06-04 08:10', reason: 'Distributor replenishment', lines: [L('RP-12-GC', 24), L('RP-12-KL', 18), L('RP-12-BR', 12), L('RP-MIX-TRI', 6)] },
    { id: 'SO-1039', cust: 'C-008', territory: 'FBA', rep: 'Darren', source: 'WH-KAM', delivery: 'Carrier / Freight', status: 'Inbound', date: '2026-06-15', created: '2026-06-02 11:30', reason: 'Amazon replenishment', fba: true, lines: [L('RP-12-GC', 100), L('RP-12-KL', 60)] },
    { id: 'SO-1047', cust: 'C-006', territory: 'Kelowna', rep: 'Marco Ferreira', source: 'WH-KEL', delivery: 'Ranch Delivery', status: 'Approved', date: '2026-06-11', created: '2026-06-04 10:41', reason: 'Promo', lines: [L('RP-12-PL', 8), L('RP-MIX-PM', 4)] },
    { id: 'SO-1046', cust: 'C-007', territory: 'Kamloops', rep: 'Sam Okoro', source: 'WH-KAM', delivery: 'Customer Pickup', status: 'Needs Approval', date: '2026-06-10', created: '2026-06-04 07:55', reason: 'Rep push', flag: 'Discount over threshold (14%)', lines: [L('RP-12-GC', 18), L('RP-12-BO', 6)] },
    { id: 'SO-1045', cust: 'C-005', territory: 'Kelowna', rep: 'Marco Ferreira', source: 'WH-KEL', delivery: 'Ranch Delivery', status: 'On Hold', date: '2026-06-13', created: '2026-06-03 16:20', reason: 'Normal reorder', flag: 'Customer credit hold', lines: [L('RP-12-PL', 12), L('RP-12-PM', 6)] },
    { id: 'SO-1038', cust: 'C-002', territory: 'Squamish', rep: 'Dana Whitfield', source: 'MW-SQ', delivery: 'Ranch Delivery', status: 'Delivered', date: '2026-06-01', created: '2026-05-30 13:10', reason: 'Normal reorder', lines: [L('RP-12-GC', 6), L('RP-12-KL', 4), L('RP-12-PM', 2)] },
  ];

  // Inventory balances (sellable + non-sellable)
  const INV = [
    { sku: 'RP-12-GC', loc: 'WH-SQ',  owner: 'Ranch Power', status: 'Available', lot: 'RP-GC-001', qty: 120, exp: '2027-04' },
    { sku: 'RP-12-GC', loc: 'WH-SQ',  owner: 'Ranch Power', status: 'Allocated', lot: 'RP-GC-001', qty: 32, exp: '2027-04' },
    { sku: 'RP-12-GC', loc: 'MW-SQ',  owner: 'Ranch Power', status: 'Fronted to Rep', lot: 'RP-GC-001', qty: 18, exp: '2027-04' },
    { sku: 'RP-12-GC', loc: 'FBA',    owner: 'Ranch Power', status: 'FBA Inbound', lot: 'RP-GC-001', qty: 100, exp: '2027-04' },
    { sku: 'RP-12-GC', loc: 'WH-KEL', owner: 'Ranch Power', status: 'Available', lot: 'RP-GC-001', qty: 86, exp: '2027-04' },
    { sku: 'RP-12-KL', loc: 'WH-KEL', owner: 'Ranch Power', status: 'Available', lot: 'RP-LIME-001', qty: 80, exp: '2027-05' },
    { sku: 'RP-12-KL', loc: 'WH-SQ',  owner: 'Ranch Power', status: 'Available', lot: 'RP-LIME-001', qty: 44, exp: '2027-05' },
    { sku: 'RP-12-KL', loc: 'MW-SQ',  owner: 'Dana Whitfield', status: 'Purchased by Rep', lot: 'RP-LIME-001', qty: 8, exp: '2027-05' },
    { sku: 'RP-12-BR', loc: 'WH-NBC', owner: 'Ranch Power', status: 'QA Hold', lot: 'RP-BR-001', qty: 240, exp: '2027-06' },
    { sku: 'RP-12-BR', loc: 'WH-SQ',  owner: 'Ranch Power', status: 'Available', lot: 'RP-BR-001', qty: 36, exp: '2027-06' },
    { sku: 'RP-12-BO', loc: 'WH-KAM', owner: 'Ranch Power', status: 'Available', lot: 'RP-2026-004', qty: 52, exp: '2027-06' },
    { sku: 'RP-12-PL', loc: 'WH-KEL', owner: 'Ranch Power', status: 'Available', lot: 'RP-2026-005', qty: 28, exp: '2027-07' },
    { sku: 'RP-12-PM', loc: 'WH-SQ',  owner: 'Ranch Power', status: 'Available', lot: 'RP-2026-006', qty: 64, exp: '2027-07' },
    { sku: 'RP-12-PM', loc: 'MW-SQ',  owner: 'Ranch Power', status: 'Fronted to Rep', lot: 'RP-2026-006', qty: 5, exp: '2027-07' },
    { sku: 'RP-12-PM', loc: 'WH-NBC', owner: 'Ranch Power', status: 'Available', lot: 'RP-2026-006', qty: 110, exp: '2027-07' },
    { sku: 'RP-12-GC', loc: 'WH-SQ',  owner: 'Ranch Power', status: 'Damaged', lot: 'RP-GC-001', qty: 2, exp: '2027-04' },
    { sku: 'RP-12-PM', loc: 'WH-NBC', owner: 'Ranch Power', status: 'Available', lot: 'RP-2026-006', qty: 90, exp: '2027-07' },
  ];

  // Rep micro-warehouse holdings (for mobile)
  const MY_INV = [
    { sku: 'RP-12-GC', available: 18, fronted: 18, purchased: 0, sample: 0, limit: 30 },
    { sku: 'RP-12-KL', available: 12, fronted: 4, purchased: 8, sample: 0, limit: 25 },
    { sku: 'RP-12-PM', available: 5, fronted: 5, purchased: 0, sample: 2, limit: 15 },
  ];

  const LEDGER = [
    { date: '2026-06-04 09:25', type: 'Allocation', sku: 'RP-12-GC', from: 'Squamish · Available', to: 'Squamish · Reserved', qty: -12, user: 'system', ref: 'SO-1042' },
    { date: '2026-06-04 08:40', type: 'Transfer', sku: 'RP-12-KL', from: 'Kelowna WH', to: 'Kamloops WH', qty: 60, user: 'S. Okoro', ref: 'TO-209' },
    { date: '2026-06-03 17:02', type: 'Damage', sku: 'RP-12-GC', from: 'Freight', to: 'Damaged', qty: -2, user: 'Warehouse', ref: 'DMG-88' },
    { date: '2026-06-03 11:15', type: 'Production', sku: 'RP-12-BR', from: 'Canning', to: 'QA Hold', qty: 240, user: 'system', ref: 'PRD-118' },
    { date: '2026-06-02 14:48', type: 'Receipt', sku: 'RP-12-PM', from: 'Northern BC inbound', to: 'Northern BC · Available', qty: 200, user: 'J. Park', ref: 'TO-201' },
    { date: '2026-06-02 09:30', type: 'FBA Ship', sku: 'RP-12-GC', from: 'Kamloops WH', to: 'Amazon FBA Inbound', qty: 100, user: 'Darren', ref: 'FBA-77' },
  ];

  const SHIPMENTS = [
    { id: 'SH-330', type: 'Transfer', status: 'In Transit', from: 'Northern BC WH', to: 'Squamish WH', carrier: 'Freight Partner A', pickup: '2026-06-10 14:00', eta: '2026-06-11 09:00', bol: 'BOL-7782', pro: 'PRO-992018', chep: 8, cases: 180 },
  ];

  // Dispatch jobs for the board
  const DISPATCH = [
    { id: 'SO-1041', label: 'Mountain Gym', territory: 'Squamish', cases: 12, window: '9:30 AM', lane: 'van1' },
    { id: 'SO-1042', label: 'Superette Market', territory: 'Squamish', cases: 24, window: '10:30 AM', lane: 'van1' },
    { id: 'TO-209',  label: 'N.BC → Squamish', territory: 'Transfer', cases: 180, window: 'Pickup 2 PM', lane: 'freightA' },
    { id: 'SO-1047', label: 'Loop Roasters', territory: 'Kelowna', cases: 12, window: '—', lane: 'unassigned' },
    { id: 'SO-1046', label: 'Hwy 5 Pit Stop', territory: 'Kamloops', cases: 24, window: '—', lane: 'unassigned' },
    { id: 'SO-1045', label: 'OK Outfitters', territory: 'Kelowna', cases: 18, window: '—', lane: 'unassigned' },
  ];
  const LANES = [
    { id: 'van1', name: 'Ranch Van 1', driver: 'L. Tran', kind: 'Ranch Delivery' },
    { id: 'van2', name: 'Ranch Van 2', driver: 'M. Ortiz', kind: 'Ranch Delivery' },
    { id: 'freightA', name: 'Freight Partner A', driver: 'Carrier', kind: 'Freight' },
  ];
  const DISPATCH_LOAD_CHECKS = [];
  const STOCK_REQUESTS = [
    {
      id: 'SR-DEMO-001', requestedBy: 'Dana Whitfield', requester: 'Dana Whitfield',
      source: 'WH-SQ', destination: 'MW-SQ', territory: 'Squamish',
      ownership: 'Fronted to Rep', status: 'Pending', notes: 'Weekend retailer push',
      created: '2026-06-05 09:30', updated: '2026-06-05 09:30',
      lines: [L('RP-12-GC', 12), L('RP-12-KL', 12), L('RP-12-PM', 6)]
    },
  ];
  const stockRequest = id => STOCK_REQUESTS.find(r => r.id === id);
  const FIELD_RECEIPTS = [
    {
      id: 'FR-DEMO-001', submittedBy: 'Dana Whitfield', submitter: 'Dana Whitfield',
      source: 'WH-SQ', sourceLocationId: 'WH-SQ', location: 'MW-SQ', territory: 'Squamish', status: 'Pending',
      receivedAt: '2026-06-07', notes: 'Picked up from Squamish transfer.',
      created: '2026-06-07 10:15', updated: '2026-06-07 10:15',
      sellableCases: 12, damagedCases: 1, totalCases: 13, photoCount: 1,
      lines: [{ sku: 'RP-12-GC', qty: 12, damagedQuantity: 1, lot: 'RP-GC-001', photos: [], damagePhotos: [] }]
    },
  ];
  const fieldReceipt = id => FIELD_RECEIPTS.find(r => r.id === id);

  // Trade spend / deductions
  const DEDUCTIONS = [
    { id: 'TS-501', cust: 'C-004', type: 'Slotting', amount: 2400, status: 'Approved', period: 'Q2 2026', skus: ['RP-12-GC','RP-12-KL'], gl: 'Trade — Slotting', owner: 'S. Manager' },
    { id: 'TS-502', cust: 'C-008', type: 'Chargeback', amount: 880, status: 'Disputed', period: 'May 2026', skus: ['RP-12-BR'], gl: 'Trade — Chargeback', owner: 'Accounting' },
    { id: 'TS-503', cust: 'C-005', type: 'Promo Allowance', amount: 640, status: 'Pending', period: 'Jun 2026', skus: ['RP-12-PL','RP-12-PM'], gl: 'Trade — Promo', owner: 'S. Manager' },
    { id: 'TS-504', cust: 'C-007', type: 'Chargeback', amount: 310, status: 'Pending', period: 'May 2026', skus: ['RP-12-GC'], gl: 'Trade — Chargeback', owner: 'Accounting' },
    { id: 'TS-505', cust: 'C-001', type: 'Credit Memo', amount: 192, status: 'Closed', period: 'May 2026', skus: ['RP-12-GC'], gl: 'Trade — Credit', owner: 'Accounting' },
  ];

  // Territory analytics
  const TERRITORY_STATS = [
    { territory: 'Squamish',    revenue: 14200, cases: 412, growth: 18, doors: 14, reorderRisk: 3, rep: 'Dana Whitfield' },
    { territory: 'Northern BC', revenue: 16800, cases: 530, growth: 9,  doors: 9,  reorderRisk: 2, rep: 'Sam Okoro' },
    { territory: 'Kamloops',    revenue: 7400,  cases: 226, growth: -4, doors: 11, reorderRisk: 4, rep: 'Sam Okoro' },
    { territory: 'Kelowna',     revenue: 9300,  cases: 281, growth: 22, doors: 12, reorderRisk: 3, rep: 'Marco Ferreira' },
  ];

  // 12-week sales trend (cases)
  const TREND = [210, 234, 228, 256, 268, 254, 290, 312, 305, 330, 356, 372];

  // Flavor mix
  const FLAVOR_MIX = [
    { flavor: 'ginger-citrus', share: 34 },
    { flavor: 'key-lime', share: 22 },
    { flavor: 'blue-raspberry', share: 16 },
    { flavor: 'peach-mango', share: 14 },
    { flavor: 'pink-lemonade', share: 8 },
    { flavor: 'blood-orange', share: 6 },
  ];

  // ---- Production ----
  const PROD_STAGES = ['Draft', 'Scheduled', 'Inputs Allocated', 'In Production', 'Produced', 'QA Hold', 'Released', 'Closed'];
  const PRODUCTION = [
    { id: 'PRD-118', flavor: 'blue-raspberry', partner: 'Sea-to-Sky Canning', run: '2026-06-11', status: 'QA Hold', outCans: 5760, outCases: 240, lot: 'RP-BR-001',
      inputs: [
        { name: 'Blue Raspberry Concentrate', lot: 'ING-014', req: '120 kg', avail: '150 kg', ok: true },
        { name: 'Caffeine / L-theanine premix', lot: 'ING-002', req: '8 kg', avail: '11 kg', ok: true },
        { name: '355ml Cans', lot: 'CAN-778', req: '5,760', avail: '12,000', ok: true },
        { name: 'Lids', lot: 'LID-812', req: '5,760', avail: '4,900', ok: false },
      ], actualOut: 5760, waste: 42, qa: 'Pending' },
    { id: 'PRD-119', flavor: 'ginger-citrus', partner: 'Sea-to-Sky Canning', run: '2026-06-20', status: 'Scheduled', outCans: 10000, outCases: 416, lot: 'RP-GC-001',
      inputs: [
        { name: 'Ginger Citrus Concentrate', lot: 'ING-008', req: '210 kg', avail: '240 kg', ok: true },
        { name: '355ml Cans', lot: 'CAN-778', req: '10,000', avail: '12,000', ok: true },
        { name: 'Lids', lot: 'LID-812', req: '10,000', avail: '4,900', ok: false },
      ], actualOut: null, waste: null, qa: 'Pending' },
    { id: 'PRD-117', flavor: 'key-lime', partner: 'Sea-to-Sky Canning', run: '2026-05-28', status: 'Released', outCans: 7200, outCases: 300, lot: 'RP-LIME-001',
      inputs: [
        { name: 'Key Lime Concentrate', lot: 'ING-011', req: '150 kg', avail: '160 kg', ok: true },
        { name: '355ml Cans', lot: 'CAN-776', req: '7,200', avail: '8,000', ok: true },
        { name: 'Lids', lot: 'LID-809', req: '7,200', avail: '9,000', ok: true },
      ], actualOut: 7188, waste: 12, qa: 'Passed' },
  ];

  // ---- Procurement ----
  const PURCHASE_ORDERS = [
    { id: 'PO-221', supplier: 'Pacific Can Co.', category: 'Cans', eta: '2026-06-15', status: 'Open', risk: 'Low', terms: 'Net 30',
      items: [{ name: '355ml Cans', ordered: 24000, recvd: 0, cost: 0.118, origin: 'Canada', tariff: '—' }] },
    { id: 'PO-222', supplier: 'Summit Lid Supply', category: 'Lids', eta: '2026-06-22', status: 'Delayed', risk: 'High', terms: 'Net 30',
      items: [{ name: '355ml Lids', ordered: 20000, recvd: 0, cost: 0.012, origin: 'USA', tariff: 'TBD' }] },
    { id: 'PO-223', supplier: 'Cascade Ingredients', category: 'Ingredients', eta: '2026-06-12', status: 'Shipped', risk: 'Low', terms: 'Net 15',
      items: [{ name: 'Peach Mango Concentrate', ordered: 300, recvd: 0, cost: 14.20, origin: 'Canada', tariff: '—' }] },
    { id: 'PO-225', supplier: 'Global Alu Trading', category: 'Cans', eta: '2026-07-02', status: 'Open', risk: 'Medium', terms: 'Net 45',
      items: [{ name: '355ml Cans', ordered: 30000, recvd: 0, cost: 0.101, origin: 'China', tariff: '7.5%' }] },
  ];
  const REORDER_ALERTS = [
    { label: '355ml Lids below production requirement', meta: '4,900 on hand · 10,000 needed for PRD-119', tone: 'danger' },
    { label: 'Peach Mango concentrate coverage: 12 days', meta: 'Reorder point reached', tone: 'warn' },
  ];
  const MATERIAL_ORIGINS = [
    { lot: 'CAN-778', supplier: 'Pacific Can Co.', material: '355ml Can', origin: 'Canada', po: 'PO-221', docs: 'Complete' },
    { lot: 'LID-812', supplier: 'Summit Lid Supply', material: '355ml Lid', origin: 'USA', po: 'PO-222', docs: 'Missing' },
    { lot: 'CAN-779', supplier: 'Global Alu Trading', material: '355ml Can', origin: 'China', po: 'PO-225', docs: 'Review' },
    { lot: 'CAN-776', supplier: 'Pacific Can Co.', material: '355ml Can', origin: 'Canada', po: 'PO-218', docs: 'Complete' },
  ];

  // ---- CHEP pallets ----
  const CHEP = [
    { loc: 'Northern BC WH', sys: 72, chep: 72, status: 'OK' },
    { loc: 'Squamish WH', sys: 30, chep: 28, status: 'Review' },
    { loc: 'Interior Distributors DC', sys: 50, chep: 50, status: 'OK' },
    { loc: 'In Transit', sys: 32, chep: 30, status: 'Review' },
  ];

  // ---- Amazon FBA ----
  const FBA_SUMMARY = { available: 820, inbound: 1200, reserved: 110, damaged: 12 };
  const FBA_SHIPMENTS = [
    { id: 'FBA-8812', dest: 'YVR2 · Delta', flavor: 'ginger-citrus', sent: 600, recvd: 600, status: 'Closed' },
    { id: 'FBA-8813', dest: 'YYZ4 · Toronto', flavor: 'peach-mango', sent: 600, recvd: 590, status: 'Disputed' },
    { id: 'FBA-8814', dest: 'TBD', flavor: 'key-lime', sent: 1200, recvd: 0, status: 'Pending' },
  ];

  const ROLES = [
    { id: 'rep', name: 'Sales Rep', who: 'Dana Whitfield', platform: 'Field', desc: 'Territory-limited' },
    { id: 'warehouse', name: 'Warehouse', who: 'J. Park', platform: 'Field', desc: 'Squamish WH' },
    { id: 'dispatch', name: 'Dispatcher', who: 'L. Tran', platform: 'Field', desc: 'Logistics' },
    { id: 'ops', name: 'Operations', who: 'A. Reyes', platform: 'Operations', desc: 'Full operational' },
    { id: 'leadership', name: 'Leadership', who: 'C. Hale', platform: 'Operations', desc: 'Full read + approvals' },
  ];
  const EVENTS = [];
  const EVENT_TEAM = [];
  const EVENT_INVENTORY_PLAN = [];
  const EVENT_INVENTORY_ALLOCATIONS = [];
  const EVENT_SALES_LINKS = [];
  const EVENT_CLOSEOUTS = [];
  const EVENT_INVENTORY_CLOSEOUT_RECEIPTS = [];
  const EVENT_INVENTORY_OPEN_CASE_RECEIPTS = [];
  const OPEN_CASE_BATCHES = [];
  const OPEN_CASE_MOVEMENTS = [];

  window.RP = {
    FINISHED_GOODS_CASE_PACK_SIZE: 12,
    FLAVORS, flavor, SKUS, sku, TERRITORIES, TERRITORY_RECORDS, LOCATIONS, loc, CUSTOMERS, cust,
    ORDERS, INV, MY_INV, LEDGER, SHIPMENTS, DISPATCH, LANES, DISPATCH_LOAD_CHECKS, STOCK_REQUESTS, FIELD_RECEIPTS, DEDUCTIONS,
    PORTAL_AVAILABILITY: [], PORTAL_FULFILLMENT: [],
    EVENTS, EVENT_TEAM, EVENT_INVENTORY_PLAN, EVENT_INVENTORY_ALLOCATIONS, EVENT_SALES_LINKS, EVENT_CLOSEOUTS, EVENT_INVENTORY_CLOSEOUT_RECEIPTS, EVENT_INVENTORY_OPEN_CASE_RECEIPTS, OPEN_CASE_BATCHES, OPEN_CASE_MOVEMENTS,
    TERRITORY_STATS, TREND, FLAVOR_MIX, ROLES,
    PROD_STAGES, PRODUCTION, PURCHASE_ORDERS, REORDER_ALERTS, MATERIAL_ORIGINS, CHEP, FBA_SUMMARY, FBA_SHIPMENTS,
    can: id => 'assets/cans/' + id + '.png',
    money: n => {
      const v = Number(n);
      return '$' + Math.round(Number.isFinite(v) ? v : 0).toLocaleString('en-CA');
    },
    money2: n => {
      const v = Number(n);
      return '$' + (Number.isFinite(v) ? v : 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    addCustomer: function (data) {
      const n = CUSTOMERS.length + 1;
      const id = data.id || ('C-' + String(n).padStart(3, '0'));
      const c = Object.assign({
        id, banner: data.name, tier: 'Default Retail', terms: 'Prepaid', credit: 'New',
        lastOrder: null, lastCases: 0, risk: 'Prospect', daysSince: null, status: 'Prospect',
        buyer: data.contact || '—', ytd: 0,
      }, data);
      if (!data.skipCreds) c.creds = window.RP.genCreds(c, true);
      CUSTOMERS.unshift(c);
      return c;
    },
    upsertCustomer: function (data) {
      const existing = data && data.id ? cust(data.id) : null;
      if (existing) return Object.assign(existing, data);
      CUSTOMERS.unshift(data);
      return data;
    },
    stockRequest,
    upsertStockRequest: function (data) {
      const existing = data && data.id ? stockRequest(data.id) : null;
      if (existing) return Object.assign(existing, data);
      STOCK_REQUESTS.unshift(data);
      return data;
    },
    fieldReceipt,
    upsertFieldReceipt: function (data) {
      const existing = data && data.id ? fieldReceipt(data.id) : null;
      if (existing) return Object.assign(existing, data);
      FIELD_RECEIPTS.unshift(data);
      return data;
    },
    // ---- Client portal credentials ----
    _slug: function (s) { return (s || 'client').toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 14) || 'client'; },
    _pw: function () {
      const L = 'ABCDEFGHJKLMNPQRSTUVWXYZ', D = '23456789';
      let p = '';
      for (let i = 0; i < 4; i++) p += L[Math.floor(Math.random() * L.length)];
      for (let i = 0; i < 3; i++) p += D[Math.floor(Math.random() * D.length)];
      return 'Ranch-' + p;
    },
    _token: function () { let t = ''; const h = 'abcdef0123456789'; for (let i = 0; i < 16; i++) t += h[Math.floor(Math.random() * 16)]; return t; },
    genCreds: function (c, fresh) {
      const token = window.RP._token();
      return {
        username: window.RP._slug(c.name) + '@portal',
        password: window.RP._pw(),
        status: fresh ? 'Invited' : 'Active',
        mustReset: !!fresh,
        link: 'order.ranchpower.com/go/' + token,
        token,
        lastSent: fresh ? 'Just now' : '—',
        lastLogin: fresh ? null : 'Yesterday 4:12 PM',
      };
    },
    resetPassword: function (custId) {
      const c = window.RP.cust(custId); if (!c) return null;
      c.creds = c.creds || window.RP.genCreds(c, true);
      c.creds.password = window.RP._pw();
      c.creds.mustReset = true;
      c.creds.status = c.creds.status === 'Disabled' ? 'Invited' : c.creds.status;
      c.creds.lastSent = 'Just now';
      return c.creds;
    },
    sendCredentials: function (custId) {
      const c = window.RP.cust(custId); if (!c) return null;
      c.creds = c.creds || window.RP.genCreds(c, true);
      c.creds.lastSent = 'Just now';
      return c.creds;
    },
  };

  // Backfill credentials onto existing active customers
  CUSTOMERS.forEach((c, i) => {
    if (c.status === 'Active') {
      c.creds = window.RP.genCreds(c, false);
      c.creds.lastLogin = ['12 min ago', '2 hours ago', 'Yesterday 9:40 AM', '3 days ago'][i % 4];
    } else if (c.status === 'Prospect') {
      c.creds = window.RP.genCreds(c, true);
    }
  });
})();


/* ===== data_ext.js ===== */
/* ============================================================
   RANCH POWER — Extended data: Production · Procurement ·
   Tariff · CHEP · FBA · Admin.  Extends window.RP.
   ============================================================ */
(function () {
  const PRODUCTION = [
    { id: 'PRD-118', flavor: 'ginger-citrus', sku: 'RP-12-GC', lot: 'RP-GC-001', partner: 'Sea-to-Sky Canning', runDate: '2026-06-20', status: 'Scheduled', cans: 10000, cases: 416,
      inputs: [
        { name: 'Caffeine + B-vitamin base', lot: 'ING-001', req: '120 kg', avail: '150 kg', ok: true },
        { name: 'Ginger Citrus flavour', lot: 'ING-002', req: '40 kg', avail: '45 kg', ok: true },
        { name: '355ml Cans', lot: 'CAN-778', req: '10,000', avail: '12,000', ok: true },
        { name: 'Lids', lot: 'LID-812', req: '10,000', avail: '9,500', ok: false },
      ], qa: 'Pending', output: null, waste: null },
    { id: 'PRD-117', flavor: 'key-lime', sku: 'RP-12-KL', lot: 'RP-LIME-001', partner: 'Sea-to-Sky Canning', runDate: '2026-06-08', status: 'In Production', cans: 8000, cases: 333,
      inputs: [
        { name: 'Caffeine + B-vitamin base', lot: 'ING-001', req: '96 kg', avail: '150 kg', ok: true },
        { name: 'Key Lime flavour', lot: 'ING-004', req: '32 kg', avail: '40 kg', ok: true },
        { name: '355ml Cans', lot: 'CAN-778', req: '8,000', avail: '12,000', ok: true },
        { name: 'Lids', lot: 'LID-812', req: '8,000', avail: '9,500', ok: true },
      ], qa: 'Pending', output: null, waste: null },
    { id: 'PRD-116', flavor: 'blue-raspberry', sku: 'RP-12-BR', lot: 'RP-BR-001', partner: 'Sea-to-Sky Canning', runDate: '2026-06-03', status: 'QA Hold', cans: 5760, cases: 240,
      inputs: [
        { name: 'Caffeine + B-vitamin base', lot: 'ING-001', req: '70 kg', avail: '150 kg', ok: true },
        { name: 'Blue Raspberry flavour', lot: 'ING-006', req: '24 kg', avail: '30 kg', ok: true },
        { name: '355ml Cans', lot: 'CAN-779', req: '5,760', avail: '9,000', ok: true },
        { name: 'Lids', lot: 'LID-812', req: '5,760', avail: '9,500', ok: true },
      ], qa: 'Awaiting micro results', output: 5760, waste: 140, coa: 'COA-116.pdf' },
    { id: 'PRD-115', flavor: 'peach-mango', sku: 'RP-12-PM', partner: 'Sea-to-Sky Canning', runDate: '2026-05-26', status: 'Released', cans: 7200, cases: 300,
      inputs: [
        { name: 'Caffeine + B-vitamin base', lot: 'ING-001', req: '86 kg', avail: '150 kg', ok: true },
        { name: 'Peach Mango flavour', lot: 'ING-005', req: '29 kg', avail: '34 kg', ok: true },
        { name: '355ml Cans', lot: 'CAN-778', req: '7,200', avail: '12,000', ok: true },
        { name: 'Lids', lot: 'LID-810', req: '7,200', avail: '8,000', ok: true },
      ], qa: 'Passed', output: 7180, waste: 220, coa: 'COA-115.pdf' },
  ];
  const PROD_STATUSES = ['Draft', 'Scheduled', 'Inputs Allocated', 'In Production', 'Produced', 'QA Hold', 'Released', 'Closed'];

  const PURCHASE_ORDERS = [
    { id: 'PO-221', supplier: 'PacRim Can Co.', category: 'Cans', eta: '2026-06-15', status: 'Open', risk: 'Low', terms: 'Net 30',
      items: [{ name: '355ml Cans', ordered: 120000, recvd: 0, unit: 0.085, origin: 'Canada', tariff: 'N/A' }] },
    { id: 'PO-222', supplier: 'NorthLid Industries', category: 'Lids', eta: '2026-06-22', status: 'Delayed', risk: 'High', terms: 'Net 30',
      items: [{ name: '355ml Lids', ordered: 20000, recvd: 0, unit: 0.012, origin: 'USA', tariff: 'TBD' }] },
    { id: 'PO-223', supplier: 'Cascade Ingredients', category: 'Ingredient', eta: '2026-06-12', status: 'Shipped', risk: 'Low', terms: 'Net 15',
      items: [{ name: 'Caffeine + B-vitamin base', ordered: 400, recvd: 0, unit: 14.2, origin: 'Canada', tariff: 'N/A' }] },
    { id: 'PO-225', supplier: 'Pacific Metals Ltd.', category: 'Cans', eta: '2026-07-02', status: 'Open', risk: 'Medium', terms: 'Net 30',
      items: [{ name: '355ml Cans', ordered: 80000, recvd: 0, unit: 0.079, origin: 'China', tariff: 'Review' }] },
  ];
  const REORDER_ALERTS = [
    { item: '355ml Lids', msg: 'Below production requirement', sev: 'high', cover: '4 days' },
    { item: 'Peach Mango flavour', msg: 'Ingredient coverage low', sev: 'warn', cover: '12 days' },
  ];

  const MATERIALS = [
    { lot: 'CAN-778', supplier: 'PacRim Can Co.', material: '355ml Can', origin: 'Canada', po: 'PO-221', docs: 'Complete', hs: '7612.90', duty: '0%' },
    { lot: 'LID-812', supplier: 'NorthLid Industries', material: '355ml Lid', origin: 'USA', po: 'PO-222', docs: 'Missing', hs: '8309.90', duty: 'TBD' },
    { lot: 'CAN-779', supplier: 'Pacific Metals Ltd.', material: '355ml Can', origin: 'China', po: 'PO-225', docs: 'Review', hs: '7612.90', duty: '12%' },
    { lot: 'ING-001', supplier: 'Cascade Ingredients', material: 'Vitamin base', origin: 'Canada', po: 'PO-223', docs: 'Complete', hs: '2106.90', duty: '0%' },
  ];

  const FBA_SUMMARY = { available: 820, inbound: 1200, reserved: 110, damaged: 12 };
  const FBA_SHIPMENTS = [
    { id: 'FBA-8812', dest: 'YVR2', flavor: 'ginger-citrus', sku: 'Ginger Citrus', sent: 600, recvd: 600, status: 'Closed' },
    { id: 'FBA-8813', dest: 'YYZ4', flavor: 'peach-mango', sku: 'Peach Mango', sent: 600, recvd: 590, status: 'Discrepancy' },
    { id: 'FBA-8814', dest: 'TBD', flavor: 'key-lime', sku: 'Key Lime', sent: 1200, recvd: 0, status: 'Planned' },
    { id: 'FBA-8811', dest: 'YVR2', flavor: 'blue-raspberry', sku: 'Blue Raspberry', sent: 400, recvd: 400, status: 'Closed' },
  ];

  const CHEP = {
    system: 184, chep: 180, variance: 4,
    locations: [
      { loc: 'Northern BC WH', system: 72, chep: 72, variance: 0 },
      { loc: 'Squamish WH', system: 30, chep: 28, variance: 2 },
      { loc: 'Interior Distributors DC', system: 50, chep: 50, variance: 0 },
      { loc: 'In Transit', system: 32, chep: 30, variance: 2 },
    ],
  };

  const USERS = [
    { name: 'Dana Whitfield', role: 'Sales Rep', area: 'Squamish', email: 'dana@ranchpower.com', status: 'Active', last: '2 min ago' },
    { name: 'Sam Okoro', role: 'Sales Rep', area: 'Northern BC · Kamloops', email: 'sam@ranchpower.com', status: 'Active', last: '1 hr ago' },
    { name: 'Marco Ferreira', role: 'Sales Rep', area: 'Kelowna', email: 'marco@ranchpower.com', status: 'Active', last: '20 min ago' },
    { name: 'J. Park', role: 'Warehouse', area: 'Squamish WH', email: 'jpark@ranchpower.com', status: 'Active', last: '5 min ago' },
    { name: 'L. Tran', role: 'Dispatcher', area: 'Squamish', email: 'ltran@ranchpower.com', status: 'Active', last: 'Just now' },
    { name: 'A. Reyes', role: 'Operations', area: 'All', email: 'areyes@ranchpower.com', status: 'Active', last: 'Just now' },
    { name: 'C. Hale', role: 'Leadership', area: 'All', email: 'chale@ranchpower.com', status: 'Active', last: '3 hr ago' },
    { name: 'Accounting', role: 'Accountant', area: 'All', email: 'finance@ranchpower.com', status: 'Active', last: '1 hr ago' },
  ];

  // Permission matrix: roles × capability
  const PERM_ROLES = ['Sales Rep', 'Warehouse', 'Dispatcher', 'Operations', 'Leadership', 'Accountant'];
  const PERM_CAPS = [
    { cap: 'Create orders', vals: [1, 0, 0, 1, 1, 0] },
    { cap: 'Approve discounts', vals: [0, 0, 0, 1, 1, 0] },
    { cap: 'Move inventory', vals: [0, 1, 1, 1, 0, 0] },
    { cap: 'Write-off damage', vals: [0, 0, 0, 1, 1, 0] },
    { cap: 'See margin / GTN', vals: [0, 0, 0, 1, 1, 1] },
    { cap: 'Manage trade spend', vals: [0, 0, 0, 1, 1, 1] },
    { cap: 'Run production', vals: [0, 0, 0, 1, 0, 0] },
    { cap: 'Manage users', vals: [0, 0, 0, 0, 1, 0] },
  ];

  const PRICE_LISTS = [
    { name: 'Distributor', skuPrice: 28.0, doors: 2, terms: 'Net 30' },
    { name: 'Retailer Tier A', skuPrice: 30.0, doors: 6, terms: 'Net 30' },
    { name: 'Retailer Tier B', skuPrice: 32.5, doors: 24, terms: 'Net 15' },
    { name: 'Default Retail', skuPrice: 36.5, doors: 14, terms: 'Prepaid' },
    { name: 'Slopeside Retail', skuPrice: 36.0, doors: 0, terms: 'Prepaid' },
  ];

  const APPROVAL_RULES = [
    { id: 'DISCOUNT_ABOVE_10', rule: 'Discount above 10%', route: 'Sales Manager', active: true },
    { id: 'CREDIT_HOLD_CUSTOMER', rule: 'Order to credit-hold customer', route: 'Operations', active: true },
    { id: 'DAMAGE_WRITE_OFF', rule: 'Damage write-off any qty', route: 'Operations', active: true },
    { id: 'REP_FRONTED_STOCK', rule: 'Rep fronted stock over $3,000', route: 'Operations', active: true },
    { id: 'TRADE_SPEND_DEDUCTION', rule: 'New trade-spend deduction', route: 'Sales Manager', active: true },
  ];
  const SHOPIFY_SYNC_RUNS = [];
  const SHOPIFY_INVENTORY_RECONCILIATIONS = [];

  Object.assign(window.RP, {
    PRODUCTION, PROD_STATUSES, PURCHASE_ORDERS, REORDER_ALERTS, MATERIALS,
    FBA_SUMMARY, FBA_SHIPMENTS, CHEP, USERS, PERM_ROLES, PERM_CAPS, PRICE_LISTS, APPROVAL_RULES, SHOPIFY_INVENTORY_RECONCILIATIONS, SHOPIFY_SYNC_RUNS,
  });
})();


/* ===== data_master.js ===== */
/* ============================================================
   RANCH POWER — Master data registry + create helpers.
   Suppliers/vendors live here; adders keep new records
   consistent and return the created object for confirmation.
   ============================================================ */
(function () {
  const SUPPLIERS = [
    { id: 'SUP-01', name: 'PacRim Can Co.', category: 'Cans', country: 'Canada', terms: 'Net 30', contact: 'orders@pacrimcan.ca', status: 'Active', openPOs: 1 },
    { id: 'SUP-02', name: 'NorthLid Industries', category: 'Lids', country: 'USA', terms: 'Net 30', contact: 'sales@northlid.com', status: 'Active', openPOs: 1 },
    { id: 'SUP-03', name: 'Cascade Ingredients', category: 'Ingredient', country: 'Canada', terms: 'Net 15', contact: 'hello@cascadeing.ca', status: 'Active', openPOs: 1 },
    { id: 'SUP-04', name: 'Pacific Metals Ltd.', category: 'Cans', country: 'China', terms: 'Net 30', contact: 'export@pacificmetals.cn', status: 'Watch', openPOs: 1 },
    { id: 'SUP-05', name: 'Sea-to-Sky Canning', category: 'Co-Packer', country: 'Canada', terms: 'Net 15', contact: 'runs@seatoskycanning.ca', status: 'Active', openPOs: 0 },
  ];
  const supplier = id => SUPPLIERS.find(s => s.id === id);

  const pad = (n, w) => String(n).padStart(w || 3, '0');

  Object.assign(window.RP, {
    SUPPLIERS, supplier,

    /* ---- Suppliers / vendors ---- */
    addSupplier: function (d) {
      const id = 'SUP-' + pad(SUPPLIERS.length + 1, 2);
      const s = Object.assign({ id, status: 'Active', openPOs: 0 }, d);
      SUPPLIERS.unshift(s);
      return s;
    },

    /* ---- Locations / warehouses ---- */
    addLocation: function (d) {
      const prefix = d.type === 'Micro-Warehouse' ? 'MW' : d.type === 'Distributor' ? 'DIST' : d.type === 'FBA' ? 'FBA' : 'WH';
      const id = d.id || (prefix + '-' + (d.code || pad(window.RP.LOCATIONS.length + 1, 2)));
      const l = Object.assign({ id, cap: 0, used: 0 }, d);
      window.RP.LOCATIONS.push(l);
      return l;
    },

    /* ---- Materials / ingredients (raw inputs w/ origin + tariff) ---- */
    addMaterial: function (d) {
      const m = Object.assign({ docs: 'Review', hs: '—', duty: 'Review', po: '—' }, d);
      window.RP.MATERIALS.unshift(m);
      return m;
    },

    /* ---- Products / SKUs ---- */
    addProduct: function (d) {
      const fl = window.RP.flavor(d.flavor);
      const abbr = fl ? fl.abbr : (d.code || 'XX');
      const id = d.id || ('RP-12-' + abbr);
      const visualLabel = d.visualLabel || (fl ? fl.abbr : String(abbr || id).replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() || 'SKU');
      const visualColor = d.visualColor || (fl && (fl.color || fl.hex)) || '';
      const s = Object.assign({ id, deposit: 1.20, code: 'RP12' + abbr, visualLabel, visualColor }, d);
      window.RP.SKUS.push(s);
      return s;
    },

    /* ---- Purchase orders ---- */
    addPurchaseOrder: function (d) {
      const num = 226 + window.RP.PURCHASE_ORDERS.filter(p => /^PO-2\d\d$/.test(p.id)).length;
      const id = 'PO-' + num;
      const po = Object.assign({ id, status: 'Open', risk: 'Low' }, d);
      window.RP.PURCHASE_ORDERS.unshift(po);
      const sup = SUPPLIERS.find(s => s.name === d.supplier);
      if (sup) sup.openPOs = (sup.openPOs || 0) + 1;
      return po;
    },

    /* ---- Production runs ---- */
    addProductionRun: function (d) {
      const num = 120 + window.RP.PRODUCTION.filter(p => /^PRD-1\d\d$/.test(p.id)).length;
      const id = 'PRD-' + num;
      const cases = Math.round((d.cans || 0) / window.RP.FINISHED_GOODS_CASE_PACK_SIZE);
      const run = Object.assign({
        id, status: 'Scheduled', qa: 'Pending', output: null, waste: null, cases,
        inputs: [
          { name: 'Caffeine + B-vitamin base', lot: 'ING-001', req: Math.round((d.cans || 0) * 0.012) + ' kg', avail: '150 kg', ok: true },
          { name: (window.RP.flavor(d.flavor) || {}).name + ' flavour', lot: 'ING-0' + (Math.floor(Math.random() * 9) + 1), req: Math.round((d.cans || 0) * 0.004) + ' kg', avail: '40 kg', ok: true },
          { name: '355ml Cans', lot: 'CAN-778', req: (d.cans || 0).toLocaleString(), avail: '12,000', ok: true },
          { name: 'Lids', lot: 'LID-812', req: (d.cans || 0).toLocaleString(), avail: '9,500', ok: (d.cans || 0) <= 9500 },
        ],
      }, d);
      window.RP.PRODUCTION.unshift(run);
      return run;
    },

    /* ---- Users ---- */
    addUser: function (d) {
      const u = Object.assign({ status: 'Invited', last: 'Invite sent' }, d);
      window.RP.USERS.unshift(u);
      return u;
    },

    /* ---- Price lists ---- */
    addPriceList: function (d) {
      const p = Object.assign({ doors: 0 }, d);
      window.RP.PRICE_LISTS.push(p);
      return p;
    },

    /* ---- Territories ---- */
    addTerritory: function (d) {
      const id = String(d.id || d.name || 'TERRITORY')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64) || 'TERRITORY';
      const territory = Object.assign({ active: true, id, sortOrder: (window.RP.TERRITORY_RECORDS || []).length * 10 + 10 }, d, { id });
      if (!Array.isArray(window.RP.TERRITORY_RECORDS)) window.RP.TERRITORY_RECORDS = [];
      const idx = window.RP.TERRITORY_RECORDS.findIndex(item => item.id === id);
      if (idx >= 0) window.RP.TERRITORY_RECORDS.splice(idx, 1, territory);
      else window.RP.TERRITORY_RECORDS.push(territory);
      if (!Array.isArray(window.RP.TERRITORIES)) window.RP.TERRITORIES = [];
      const nameIdx = window.RP.TERRITORIES.findIndex(name => String(name).toLowerCase() === String(territory.name).toLowerCase());
      if (territory.active === false) {
        if (nameIdx >= 0) window.RP.TERRITORIES.splice(nameIdx, 1);
      } else if (nameIdx >= 0) {
        window.RP.TERRITORIES.splice(nameIdx, 1, territory.name);
      } else {
        window.RP.TERRITORIES.push(territory.name);
      }
      return territory;
    },

    /* ---- Approval rules ---- */
    addApprovalRule: function (d) {
      const id = String(d.id || d.rule || 'APPROVAL_RULE')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64) || 'APPROVAL_RULE';
      const r = Object.assign({ active: true, id }, d);
      window.RP.APPROVAL_RULES.push(r);
      return r;
    },

    /* ---- Transfer orders (ledger movement) ---- */
    addTransfer: function (d) {
      const num = 210 + window.RP.LEDGER.filter(l => /^TO-2\d\d$/.test(l.ref || '')).length;
      const ref = d.ref || ('TO-' + num);
      let remaining = d.qty;
      const fromLocation = window.RP.loc(d.from) || {};
      const toLocation = window.RP.loc(d.to) || {};
      const sourceStatuses = fromLocation.type === 'Micro-Warehouse'
        ? new Set(['Available', 'Fronted to Rep', 'Purchased by Rep'])
        : new Set(['Available']);
      const destinationFor = row => {
        if (toLocation.type === 'Micro-Warehouse') {
          if (row.status === 'Purchased by Rep') return { owner: row.owner || 'Rep', status: 'Purchased by Rep' };
          return { owner: 'Ranch Power', status: 'Fronted to Rep' };
        }
        return { owner: 'Ranch Power', status: 'Available' };
      };
      window.RP.INV.filter(i => i.sku === d.sku && i.loc === d.from && sourceStatuses.has(i.status || 'Available') && i.qty > 0).forEach(i => {
        if (remaining <= 0) return;
        const moved = Math.min(i.qty, remaining);
        i.qty -= moved;
        remaining -= moved;
        const next = destinationFor(i);
        const dest = window.RP.INV.find(x => x.sku === d.sku && x.loc === d.to && x.status === next.status && x.owner === next.owner && x.lot === i.lot);
        if (dest) dest.qty += moved;
        else window.RP.INV.push({ sku: d.sku, loc: d.to, owner: next.owner, status: next.status, lot: i.lot || 'UNASSIGNED', qty: moved });
      });
      window.RP.INV = window.RP.INV.filter(i => i.qty > 0);
      window.RP.LEDGER.unshift({
        date: '2026-06-04 11:40', type: 'Transfer', sku: d.sku,
        from: (window.RP.loc(d.from) || {}).name || d.from,
        to: (window.RP.loc(d.to) || {}).name || d.to,
        qty: d.qty, user: 'A. Reyes', ref,
      });
      return ref;
    },
  });
})();


/* ===== data_analytics.js ===== */
/* ============================================================
   RANCH POWER — Analytics dataset (deep-dive)
   Synthetic but internally-consistent figures for the
   Analytics workspace. Extends window.RP.ANA.
   ============================================================ */
(function () {
  const UNASSIGNED_REP_LABEL = 'Unassigned rep';
  const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const HOURS = ['7a', '8a', '9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p', '6p'];

  // ---- Hour × Day heatmap (order COUNT placed). B2B rhythm: Tue–Thu mornings hottest.
  // base curve by hour (retail/cafe ordering): ramp to 10–11a, dip at noon, second peak 2–4p
  const hourCurve = [3, 8, 17, 26, 24, 12, 14, 22, 25, 19, 9, 4];
  const dowMult = { Mon: 0.85, Tue: 1.25, Wed: 1.30, Thu: 1.15, Fri: 0.95, Sat: 0.45, Sun: 0.20 };
  const heatmap = DOW.map(d => ({
    day: d,
    cells: hourCurve.map((v, i) => {
      const noise = ((i * 7 + d.charCodeAt(0)) % 5) - 2;
      return Math.max(0, Math.round(v * dowMult[d] + noise));
    }),
  }));

  // ---- Day of week rollup (revenue + cases + avg order value)
  const dow = DOW.map(d => {
    const cases = Math.round(420 * dowMult[d]);
    return { day: d, cases, revenue: cases * 32, orders: Math.round(cases / 9) };
  });

  // ---- 12-week trend (rev, cases, orders, AOV)
  const weeks = [];
  const baseCases = [210, 234, 228, 256, 268, 254, 290, 312, 305, 330, 356, 372];
  baseCases.forEach((cs, i) => {
    const orders = Math.round(cs / 8.5 + (i % 3));
    weeks.push({
      label: 'W' + (i + 1),
      week: 'Wk ' + (i + 1),
      cases: cs,
      revenue: cs * 32,
      orders,
      aov: Math.round((cs * 32) / orders),
      newDoors: [1, 0, 2, 1, 1, 0, 2, 3, 1, 2, 2, 1][i],
    });
  });

  // ---- By territory (extends TERRITORY_STATS with channel + momentum spark)
  const byTerritory = [
    { territory: 'Northern BC', revenue: 16800, cases: 530, growth: 9, doors: 9, reorderRisk: 2, rep: 'Sam Okoro', spark: [38, 41, 39, 44, 46, 48, 50, 53], aov: 612 },
    { territory: 'Squamish', revenue: 14200, cases: 412, growth: 18, doors: 14, reorderRisk: 3, rep: 'Dana Whitfield', spark: [28, 30, 31, 33, 36, 38, 40, 42], aov: 384 },
    { territory: 'Kelowna', revenue: 9300, cases: 281, growth: 22, doors: 12, reorderRisk: 3, rep: 'Marco Ferreira', spark: [16, 18, 19, 22, 24, 25, 27, 29], aov: 342 },
    { territory: 'Kamloops', revenue: 7400, cases: 226, growth: -4, doors: 11, reorderRisk: 4, rep: 'Sam Okoro', spark: [24, 23, 22, 22, 21, 20, 19, 18], aov: 298 },
  ];

  // ---- By sales rep (richer activity metrics)
  const byRep = [
    { rep: 'Sam Okoro', territory: 'Northern BC · Kamloops', revenue: 24200, cases: 756, orders: 89, growth: 6, doors: 20, visits: 142, winRate: 0.34, aov: 272, newDoors: 5, spark: [62, 64, 61, 66, 68, 70, 72, 75] },
    { rep: 'Dana Whitfield', territory: 'Squamish', revenue: 14200, cases: 412, orders: 58, growth: 18, doors: 14, visits: 118, winRate: 0.41, aov: 245, newDoors: 4, spark: [28, 30, 31, 33, 36, 38, 40, 42] },
    { rep: 'Marco Ferreira', territory: 'Kelowna', revenue: 9300, cases: 281, orders: 41, growth: 22, doors: 12, visits: 96, winRate: 0.38, aov: 227, newDoors: 3, spark: [16, 18, 19, 22, 24, 25, 27, 29] },
    { rep: 'Distributor / Unassigned', territory: 'All', revenue: 18400, cases: 575, orders: 12, growth: 11, doors: 2, visits: 0, winRate: 0.0, aov: 1533, newDoors: 0, spark: [48, 50, 49, 52, 54, 55, 57, 60] },
  ];

  // ---- By customer type (channel)
  const byType = [
    { type: 'Distributor', revenue: 18400, cases: 575, doors: 2, growth: 11, share: 31 },
    { type: 'Retailer', revenue: 16900, cases: 504, doors: 14, growth: 9, share: 28 },
    { type: 'Convenience', revenue: 9200, cases: 276, doors: 11, growth: 14, share: 15 },
    { type: 'Gym', revenue: 6400, cases: 192, doors: 8, growth: 26, share: 11 },
    { type: 'Cafe', revenue: 5300, cases: 162, doors: 9, growth: 7, share: 9 },
    { type: 'Grocery', revenue: 3600, cases: 110, doors: 5, growth: 19, share: 6 },
  ];

  // ---- By SKU / flavor velocity (units = cases, 4-wk)
  const bySku = [
    { flavor: 'ginger-citrus', cases: 612, revenue: 19584, growth: 12, velocity: 4.4, returns: 0.4 },
    { flavor: 'key-lime', cases: 396, revenue: 12672, growth: 8, velocity: 3.1, returns: 0.6 },
    { flavor: 'blue-raspberry', cases: 288, revenue: 9216, growth: 19, velocity: 2.6, returns: 0.5 },
    { flavor: 'peach-mango', cases: 252, revenue: 8064, growth: 24, velocity: 2.3, returns: 0.3 },
    { flavor: 'pink-lemonade', cases: 144, revenue: 4608, growth: -3, velocity: 1.4, returns: 0.9 },
    { flavor: 'blood-orange', cases: 108, revenue: 3456, growth: 5, velocity: 1.1, returns: 0.7 },
  ];

  const byTerritoryFlavor = byTerritory.map((territory, territoryIndex) => ({
    territory: territory.territory,
    flavors: bySku.slice(0, 4).map((sku, flavorIndex) => {
      const weight = Math.max(0.12, 0.38 - flavorIndex * 0.07 + territoryIndex * 0.015);
      return {
        flavor: sku.flavor,
        cases: Math.round(territory.cases * weight),
        revenue: Math.round(territory.revenue * weight)
      };
    }).sort((a, b) => b.cases - a.cases)
  }));

  // ---- Channel / order source mix
  const channelMix = [
    { channel: 'Rep-entered (mobile)', orders: 188, share: 54 },
    { channel: 'Client portal', orders: 92, share: 26 },
    { channel: 'Distributor EDI', orders: 38, share: 11 },
    { channel: 'Phone / email', orders: 31, share: 9 },
  ];

  // ---- Order-size distribution (cases per order)
  const orderSizes = [
    { band: '1–5', count: 64 }, { band: '6–11', count: 118 }, { band: '12–23', count: 92 },
    { band: '24–47', count: 48 }, { band: '48–99', count: 18 }, { band: '100+', count: 9 },
  ];

  // ---- Top KPIs for analytics header
  const summary = {
    revenue: 58200, revenueGrowth: 12,
    cases: 1788, casesGrowth: 9,
    orders: 349, ordersGrowth: 7,
    aov: 167, aovGrowth: 4,
    activeDoors: 46, newDoors: 12,
    repVisits: 356,
  };

  // ---- Best/worst windows (derived insights)
  const insights = [
    { kind: 'peak', icon: 'clock', text: 'Wednesday 9–11 AM is your hottest order window', detail: 'Staff the warehouse pick line early mid-week.' },
    { kind: 'risk', icon: 'alert', text: 'Kamloops down 4% — 4 doors at reorder risk', detail: 'Sam to prioritize Highway 5 & Interior this week.' },
    { kind: 'growth', icon: 'trend', text: 'Gym channel growing 26% — fastest segment', detail: 'Expand Mountain Gym-style accounts in Squamish.' },
    { kind: 'product', icon: 'package', text: 'Peach Mango velocity up 24% off a small base', detail: 'Consider a mixer-pack promo to accelerate.' },
  ];

  window.RP.ANA = {
    DOW, HOURS, heatmap, dow, weeks, byTerritory, byTerritoryFlavor, byRep, byType, bySku, channelMix, orderSizes, summary, insights,
  };

  const LIVE_CLOSED_ORDER_STATUSES = new Set(['Cancelled', 'Closed']);
  const ACTIVE_CUSTOMER_STATUSES = new Set(['Active', 'Approved']);

  const num = value => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  function parseOrderDate(value) {
    if (!value) return null;
    const parsed = new Date(String(value).replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function liveOrderDate(order) {
    return parseOrderDate(order.created || order.updated || order.date);
  }

  function liveCustomerDate(customer) {
    return parseOrderDate(customer.createdAt || customer.created || customer.updatedAt);
  }

  function liveOrderActive(order) {
    return !LIVE_CLOSED_ORDER_STATUSES.has(String(order && order.status || ''));
  }

  function liveLineUnits(line) {
    return Math.max(0, num(line && (line.qty ?? line.quantity)));
  }

  function liveSku(line) {
    const rawSku = line && (line.sku || line.inventorySku || line.inventorySkuId);
    const canonical = typeof rpCanonicalSkuId === 'function' ? rpCanonicalSkuId(rawSku) : rawSku;
    return RP.sku(canonical) || (typeof rpSkuItem === 'function' ? rpSkuItem(canonical) : null) || RP.sku(rawSku) || {};
  }

  function liveLineCaseQty(line) {
    const explicit = num(line && (line.caseQty ?? line.caseQuantity ?? line.case_quantity));
    if (explicit > 0) return explicit;
    const units = liveLineUnits(line);
    const sku = liveSku(line);
    const unit = String((line && line.unit) || sku.unit || '').toLowerCase();
    return unit === 'can' ? Math.round((units / 12) * 10000) / 10000 : units;
  }

  function liveLineRevenue(line) {
    const sku = liveSku(line);
    const units = liveLineUnits(line);
    const price = num(line && line.price) || num(sku.price);
    return units * price;
  }

  function filterValue(value) {
    return String(value || '').trim();
  }

  function isAllFilter(value) {
    const text = filterValue(value).toLowerCase();
    return !text || text === 'all';
  }

  function normalizeAnalyticsFilters(filters) {
    return {
      customerType: filterValue(filters && filters.customerType),
      flavor: filterValue(filters && filters.flavor),
      rep: filterValue(filters && filters.rep),
      territory: filterValue(filters && filters.territory)
    };
  }

  function liveLineFlavorIds(line) {
    const sku = liveSku(line);
    if (Array.isArray(sku.tri) && sku.tri.length) return sku.tri.filter(flavor => RP.flavor(flavor));
    const flavor = sku.flavor || sku.visualFlavor || '';
    if (RP.flavor(flavor)) return [flavor];
    const inferred = typeof rpSkuFlavorId === 'function' ? rpSkuFlavorId(sku.id || (line && (line.sku || line.inventorySku || line.inventorySkuId))) : '';
    return inferred && RP.flavor(inferred) ? [inferred] : ['unknown'];
  }

  function liveLineMatchesFilters(line, filters) {
    return isAllFilter(filters.flavor) || liveLineFlavorIds(line).includes(filters.flavor);
  }

  function liveLineFilterShare(line, filters) {
    if (isAllFilter(filters.flavor)) return 1;
    const flavors = liveLineFlavorIds(line);
    return flavors.includes(filters.flavor) ? 1 / Math.max(1, flavors.length) : 0;
  }

  function liveFilteredLines(order, filters) {
    const lines = Array.isArray(order && order.lines) ? order.lines : [];
    return lines.filter(line => liveLineMatchesFilters(line, filters));
  }

  function liveOrderMatchesFilters(order, customersById, filters) {
    const customer = liveCustomerForOrder(order, customersById);
    const territory = order.territory || customer.territory || 'Unassigned';
    const rep = order.rep || customer.rep || UNASSIGNED_REP_LABEL;
    const customerType = customer.type || 'Unclassified';
    if (!isAllFilter(filters.territory) && territory !== filters.territory) return false;
    if (!isAllFilter(filters.rep) && rep !== filters.rep) return false;
    if (!isAllFilter(filters.customerType) && customerType !== filters.customerType) return false;
    if (!isAllFilter(filters.flavor) && !liveFilteredLines(order, filters).length) return false;
    return true;
  }

  function liveOrderTotals(order, filters = {}) {
    const normalizedFilters = normalizeAnalyticsFilters(filters);
    const lines = liveFilteredLines(order, normalizedFilters);
    const cases = lines.reduce((sum, line) => sum + liveLineCaseQty(line) * liveLineFilterShare(line, normalizedFilters), 0);
    const lineRevenue = lines.reduce((sum, line) => {
      return sum + liveLineRevenue(line) * liveLineFilterShare(line, normalizedFilters);
    }, 0);
    const hasStoredSubtotal = order && Object.prototype.hasOwnProperty.call(order, 'subtotal');
    const storedRevenue = hasStoredSubtotal ? num(order.subtotal) : lineRevenue;
    return { cases, revenue: isAllFilter(normalizedFilters.flavor) && hasStoredSubtotal ? storedRevenue : lineRevenue };
  }

  function liveFlavorForSku(skuId) {
    const sku = liveSku({ sku: skuId });
    const flavor = sku.flavor || sku.visualFlavor || skuId || 'unknown';
    return RP.flavor(flavor) ? flavor : 'unknown';
  }

  function liveFlavorMeta(flavor) {
    return RP.flavor(flavor) || { id: flavor, name: flavor === 'unknown' ? 'Unmapped SKU' : flavor, color: 'var(--faint)' };
  }

  function liveRange(range) {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const start = new Date(now);
    if (range === '1d') {
      start.setHours(0, 0, 0, 0);
    } else if (range === '7d') {
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
    } else if (range === 'ytd') {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
    } else {
      start.setDate(start.getDate() - (range === '4w' ? 28 : 84));
      start.setHours(0, 0, 0, 0);
    }
    const previous = new Date(start);
    previous.setTime(start.getTime() - (now.getTime() - start.getTime()));
    return { now, previous, start };
  }

  function inRange(date, start, end) {
    return date && date >= start && date < end;
  }

  function percentDelta(current, previous) {
    if (!previous) return current ? null : 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  function growthSortValue(value) {
    return value == null ? Number.POSITIVE_INFINITY : value;
  }

  function growthPhrase(value) {
    if (value == null) return 'is new';
    return `is ${value >= 0 ? 'up' : 'down'} ${Math.abs(value)}%`;
  }

  function blankMetrics(extra) {
    return Object.assign({ cases: 0, orders: 0, revenue: 0 }, extra || {});
  }

  function addOrderMetric(target, order, filters) {
    const totals = liveOrderTotals(order, filters);
    if (!totals.cases && !totals.revenue) return;
    target.cases += totals.cases;
    target.revenue += totals.revenue;
    target.orders += 1;
  }

  function liveCustomerLookup() {
    const customers = typeof rpVisibleCustomers === 'function' ? rpVisibleCustomers(RP.CUSTOMERS) : (RP.CUSTOMERS || []);
    return new Map(customers.map(customer => [customer.id, customer]));
  }

  function liveCustomerForOrder(order, customersById) {
    return customersById.get(order && order.cust) || {};
  }

  function liveGroupOrders(orders, keyFn, filters) {
    const map = new Map();
    orders.forEach(order => {
      const key = keyFn(order);
      if (!key) return;
      if (!map.has(key)) map.set(key, blankMetrics());
      addOrderMetric(map.get(key), order, filters);
    });
    return map;
  }

  function liveMetric(map, key) {
    return map.get(key) || blankMetrics();
  }

  function startOfWeek(date) {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    value.setDate(value.getDate() - ((value.getDay() + 6) % 7));
    return value;
  }

  function addDays(date, days) {
    const value = new Date(date);
    value.setDate(value.getDate() + days);
    return value;
  }

  function addMonths(date, months) {
    const value = new Date(date);
    value.setMonth(value.getMonth() + months);
    return value;
  }

  function bucketLabel(date, mode) {
    return date.toLocaleDateString(undefined, mode === 'month' ? { month: 'short' } : { month: 'short', day: 'numeric' });
  }

  function hourLabel(hour) {
    if (hour === 0) return '12a';
    if (hour < 12) return `${hour}a`;
    if (hour === 12) return '12p';
    return `${hour - 12}p`;
  }

  function liveBucketedOrders(orders, window, count, filters, range) {
    const bucketMode = range === '1d' ? 'hour' : range === '7d' ? 'day' : range === 'ytd' ? 'month' : 'week';
    const buckets = [];
    if (bucketMode === 'hour') {
      Array.from({ length: 24 }, (_, hour) => {
        const start = new Date(window.start);
        start.setHours(hour, 0, 0, 0);
        const end = new Date(start);
        end.setHours(hour + 1, 0, 0, 0);
        buckets.push({ start, end, label: hourLabel(hour), week: `${hourLabel(hour)} - ${hourLabel((hour + 1) % 24)}` });
      });
    } else if (bucketMode === 'day') {
      Array.from({ length: count }, (_, index) => addDays(window.start, index)).forEach(start => {
        const end = addDays(start, 1);
        buckets.push({ start, end, label: bucketLabel(start, 'day'), week: bucketLabel(start, 'day') });
      });
    } else if (bucketMode === 'month') {
      const cursor = new Date(window.start);
      cursor.setDate(1);
      cursor.setHours(0, 0, 0, 0);
      while (cursor <= window.now && buckets.length < 12) {
        const start = new Date(cursor);
        const end = addMonths(start, 1);
        buckets.push({ start, end, label: bucketLabel(start, 'month'), week: bucketLabel(start, 'month') });
        cursor.setMonth(cursor.getMonth() + 1);
      }
    } else {
      const lastStart = startOfWeek(window.now);
      Array.from({ length: count }, (_, index) => addDays(lastStart, -7 * (count - 1 - index))).forEach(start => {
        const end = addDays(start, 7);
        buckets.push({ start, end, label: bucketLabel(start, 'week'), week: `${bucketLabel(start, 'week')} - ${bucketLabel(addDays(end, -1), 'week')}` });
      });
    }
    buckets.forEach(bucket => Object.assign(bucket, {
      cases: 0,
      newDoors: 0,
      orders: 0,
      revenue: 0
    }));
    orders.forEach(order => {
      const date = liveOrderDate(order);
      if (!date || date > window.now) return;
      const bucket = buckets.find(item => date >= item.start && date < item.end);
      if (!bucket) return;
      addOrderMetric(bucket, order, filters);
    });
    buckets.forEach(bucket => {
      bucket.revenue = Math.round(bucket.revenue);
      bucket.aov = bucket.orders ? Math.round(bucket.revenue / bucket.orders) : 0;
    });
    return buckets;
  }

  function liveSparkForOrders(orders, window, valueFn, filters) {
    const buckets = liveBucketedOrders(orders, window, 8, filters, '8w');
    return buckets.map(bucket => valueFn ? valueFn(bucket) : bucket.cases);
  }

  function liveHeatmap(orders) {
    const rows = DOW.map(day => ({ day, cells: HOURS.map(() => 0) }));
    orders.forEach(order => {
      const date = liveOrderDate(order);
      if (!date) return;
      const day = DOW[(date.getDay() + 6) % 7];
      const hour = date.getHours();
      const hourIndex = Math.min(HOURS.length - 1, Math.max(0, hour - 7));
      const row = rows.find(item => item.day === day);
      if (row) row.cells[hourIndex] += 1;
    });
    return rows;
  }

  function liveDow(orders, filters) {
    const map = new Map(DOW.map(day => [day, { day, cases: 0, orders: 0, revenue: 0 }]));
    orders.forEach(order => {
      const date = liveOrderDate(order);
      if (!date) return;
      const day = DOW[(date.getDay() + 6) % 7];
      addOrderMetric(map.get(day), order, filters);
    });
    return DOW.map(day => map.get(day));
  }

  function liveSkuGroups(orders, previousOrders, filters) {
    const current = new Map();
    const previous = new Map();
    const addLines = (map, order) => {
      (order.lines || []).forEach(line => {
        if (!liveLineMatchesFilters(line, filters)) return;
        const qty = liveLineCaseQty(line);
        if (!qty) return;
        const flavors = liveLineFlavorIds(line).filter(flavor => isAllFilter(filters.flavor) || flavor === filters.flavor);
        const share = flavors.length ? 1 / flavors.length : 1;
        flavors.forEach(flavor => {
          if (!map.has(flavor)) map.set(flavor, { cases: 0, revenue: 0, orders: new Set(), flavor });
          const row = map.get(flavor);
          row.cases += qty * share;
          row.revenue += liveLineRevenue(line) * share;
          row.orders.add(order.id);
        });
      });
    };
    orders.forEach(order => addLines(current, order));
    previousOrders.forEach(order => addLines(previous, order));

    const flavors = new Set([...current.keys(), ...previous.keys()]);
    if (!flavors.size) (RP.FLAVORS || []).slice(0, 4).forEach(flavor => flavors.add(flavor.id));

    return Array.from(flavors).map(flavor => {
      const row = current.get(flavor) || { cases: 0, revenue: 0, orders: new Set(), flavor };
      const prev = previous.get(flavor) || { cases: 0, revenue: 0 };
      const ordersCount = row.orders.size || 1;
      return {
        flavor,
        cases: Math.round(row.cases),
        revenue: Math.round(row.revenue),
        growth: percentDelta(row.cases, prev.cases),
        velocity: Math.round((row.cases / ordersCount) * 10) / 10,
        returns: 0
      };
    }).sort((a, b) => b.cases - a.cases);
  }

  function liveTerritoryFlavorGroups(orders, previousOrders, filters, customersById) {
    const current = new Map();
    const previous = new Map();
    const addLines = (map, order) => {
      const customer = liveCustomerForOrder(order, customersById);
      const territory = order.territory || customer.territory || 'Unassigned';
      if (!map.has(territory)) map.set(territory, new Map());
      const territoryMap = map.get(territory);
      (order.lines || []).forEach(line => {
        if (!liveLineMatchesFilters(line, filters)) return;
        const qty = liveLineCaseQty(line);
        if (!qty) return;
        const flavors = liveLineFlavorIds(line).filter(flavor => isAllFilter(filters.flavor) || flavor === filters.flavor);
        const share = flavors.length ? 1 / flavors.length : 1;
        flavors.forEach(flavor => {
          if (!territoryMap.has(flavor)) territoryMap.set(flavor, { cases: 0, revenue: 0, orders: new Set(), flavor });
          const row = territoryMap.get(flavor);
          row.cases += qty * share;
          row.revenue += liveLineRevenue(line) * share;
          row.orders.add(order.id);
        });
      });
    };
    orders.forEach(order => addLines(current, order));
    previousOrders.forEach(order => addLines(previous, order));
    const territories = new Set([...current.keys(), ...previous.keys()]);
    return Array.from(territories).map(territory => {
      const cur = current.get(territory) || new Map();
      const prev = previous.get(territory) || new Map();
      const flavors = new Set([...cur.keys(), ...prev.keys()]);
      return {
        territory,
        flavors: Array.from(flavors).map(flavor => {
          const row = cur.get(flavor) || { cases: 0, revenue: 0, orders: new Set(), flavor };
          const prior = prev.get(flavor) || { cases: 0, revenue: 0 };
          return {
            flavor,
            cases: Math.round(row.cases),
            revenue: Math.round(row.revenue),
            growth: percentDelta(row.cases, prior.cases),
            orders: row.orders.size
          };
        }).sort((a, b) => b.cases - a.cases)
      };
    }).sort((a, b) => {
      const totalA = (a.flavors || []).reduce((sum, flavor) => sum + flavor.revenue, 0);
      const totalB = (b.flavors || []).reduce((sum, flavor) => sum + flavor.revenue, 0);
      return totalB - totalA;
    });
  }

  function liveOrderSizeBands(orders, filters) {
    const bands = [
      { band: '1-5', min: 1, max: 5, count: 0 },
      { band: '6-11', min: 6, max: 11, count: 0 },
      { band: '12-23', min: 12, max: 23, count: 0 },
      { band: '24-47', min: 24, max: 47, count: 0 },
      { band: '48-99', min: 48, max: 99, count: 0 },
      { band: '100+', min: 100, max: Infinity, count: 0 },
    ];
    orders.forEach(order => {
      const cases = liveOrderTotals(order, filters).cases;
      const band = bands.find(item => cases >= item.min && cases <= item.max);
      if (band) band.count += 1;
    });
    return bands.map(({ band, count }) => ({ band, count }));
  }

  function liveChannelMix(orders, customersById) {
    const counts = new Map();
    orders.forEach(order => {
      const customer = liveCustomerForOrder(order, customersById);
      let channel = 'Phone / email';
      if (order.shopifyOrderId || order.shopifyDraftOrderId) channel = 'Shopify / client portal';
      else if (/distributor/i.test(customer.type || '')) channel = 'Distributor';
      else if (order.rep && order.rep !== UNASSIGNED_REP_LABEL) channel = 'Rep-entered';
      counts.set(channel, (counts.get(channel) || 0) + 1);
    });
    const total = orders.length || 1;
    const rows = Array.from(counts.entries()).map(([channel, count]) => ({
      channel,
      orders: count,
      share: Math.round((count / total) * 100)
    }));
    return rows.length ? rows.sort((a, b) => b.orders - a.orders) : [{ channel: 'No live orders', orders: 0, share: 0 }];
  }

  function liveInsights(input) {
    const bestDay = [...input.dow].sort((a, b) => b.cases - a.cases)[0];
    const activeTerritories = input.byTerritory.filter(row => row.orders || row.cases || row.revenue);
    const activeTypes = input.byType.filter(row => row.cases || row.revenue);
    const activeSkus = input.bySku.filter(row => row.cases || row.revenue);
    const weakTerritory = [...activeTerritories].sort((a, b) => (a.growth == null ? Number.POSITIVE_INFINITY : a.growth) - (b.growth == null ? Number.POSITIVE_INFINITY : b.growth))[0];
    const topType = [...activeTypes].sort((a, b) => growthSortValue(b.growth) - growthSortValue(a.growth))[0];
    const topSku = [...activeSkus].sort((a, b) => growthSortValue(b.growth) - growthSortValue(a.growth))[0];
    const skuMeta = topSku ? liveFlavorMeta(topSku.flavor) : null;
    return [
      bestDay && bestDay.orders ? { kind: 'peak', icon: 'clock', text: `${bestDay.day} is the busiest live order day`, detail: `${bestDay.orders} orders and ${Math.round(bestDay.cases)} cases in the selected range.` } : null,
      weakTerritory ? { kind: weakTerritory.growth < 0 ? 'risk' : 'peak', icon: weakTerritory.growth < 0 ? 'alert' : 'pin', text: `${weakTerritory.territory} ${growthPhrase(weakTerritory.growth)}`, detail: `${Math.round(weakTerritory.cases)} cases from ${weakTerritory.orders || 0} live orders.` } : null,
      topType ? { kind: 'growth', icon: 'trend', text: `${topType.type} is the fastest-growing customer type`, detail: `${topType.doors} active doors and ${Math.round(topType.cases)} cases.` } : null,
      topSku ? { kind: 'product', icon: 'package', text: `${skuMeta.name} is the strongest flavor trend`, detail: `${Math.round(topSku.cases)} cases and ${Math.round(topSku.velocity)} cases/order velocity.` } : null,
    ].filter(Boolean).slice(0, 4);
  }

  function liveCustomerMatchesFilters(customer, filters) {
    if (!isAllFilter(filters.territory) && (customer.territory || 'Unassigned') !== filters.territory) return false;
    if (!isAllFilter(filters.rep) && (customer.rep || UNASSIGNED_REP_LABEL) !== filters.rep) return false;
    if (!isAllFilter(filters.customerType) && (customer.type || 'Unclassified') !== filters.customerType) return false;
    return true;
  }

  function buildLiveAnalytics(range, filtersInput) {
    const filters = normalizeAnalyticsFilters(filtersInput || {});
    const windowRange = liveRange(range);
    const customersById = liveCustomerLookup();
    const ordersAll = (RP.ORDERS || []).filter(order => liveOrderActive(order) && liveOrderMatchesFilters(order, customersById, filters));
    const orders = ordersAll.filter(order => inRange(liveOrderDate(order), windowRange.start, windowRange.now));
    const previousOrders = ordersAll.filter(order => inRange(liveOrderDate(order), windowRange.previous, windowRange.start));
    const weeks = liveBucketedOrders(ordersAll, windowRange, range === '1d' ? 24 : range === '7d' ? 7 : range === '4w' ? 4 : 12, filters, range);
    const currentTotals = orders.reduce((sum, order) => {
      const totals = liveOrderTotals(order, filters);
      sum.cases += totals.cases;
      sum.revenue += totals.revenue;
      sum.orders += 1;
      return sum;
    }, blankMetrics());
    const previousTotals = previousOrders.reduce((sum, order) => {
      const totals = liveOrderTotals(order, filters);
      sum.cases += totals.cases;
      sum.revenue += totals.revenue;
      sum.orders += 1;
      return sum;
    }, blankMetrics());

    const territoryCurrent = liveGroupOrders(orders, order => order.territory || liveCustomerForOrder(order, customersById).territory || 'Unassigned', filters);
    const territoryPrevious = liveGroupOrders(previousOrders, order => order.territory || liveCustomerForOrder(order, customersById).territory || 'Unassigned', filters);
    const repCurrent = liveGroupOrders(orders, order => order.rep || liveCustomerForOrder(order, customersById).rep || UNASSIGNED_REP_LABEL, filters);
    const repPrevious = liveGroupOrders(previousOrders, order => order.rep || liveCustomerForOrder(order, customersById).rep || UNASSIGNED_REP_LABEL, filters);
    const typeCurrent = liveGroupOrders(orders, order => liveCustomerForOrder(order, customersById).type || 'Unclassified', filters);
    const typePrevious = liveGroupOrders(previousOrders, order => liveCustomerForOrder(order, customersById).type || 'Unclassified', filters);

    const visibleCustomers = typeof rpVisibleCustomers === 'function' ? rpVisibleCustomers(RP.CUSTOMERS) : (RP.CUSTOMERS || []);
    const activeCustomers = visibleCustomers.filter(customer => ACTIVE_CUSTOMER_STATUSES.has(String(customer.status || '')) && liveCustomerMatchesFilters(customer, filters));
    const visits = RP.VISITS || [];
    const showEmptyDimensions = isAllFilter(filters.flavor);
    const byTerritoryKeys = new Set([
      ...territoryCurrent.keys(),
      ...territoryPrevious.keys(),
      ...(showEmptyDimensions && isAllFilter(filters.territory) ? (RP.TERRITORIES || []) : []),
      ...activeCustomers.map(customer => customer.territory || 'Unassigned')
    ]);
    const byTerritory = Array.from(byTerritoryKeys).filter(Boolean).map(territory => {
      const metric = liveMetric(territoryCurrent, territory);
      const prev = liveMetric(territoryPrevious, territory);
      const territoryCustomers = activeCustomers.filter(customer => (customer.territory || 'Unassigned') === territory);
      const territoryOrders = orders.filter(order => (order.territory || liveCustomerForOrder(order, customersById).territory || 'Unassigned') === territory);
      const rep = territoryOrders.find(order => order.rep)?.rep || territoryCustomers.find(customer => customer.rep)?.rep || UNASSIGNED_REP_LABEL;
      return {
        territory,
        revenue: Math.round(metric.revenue),
        cases: Math.round(metric.cases),
        growth: percentDelta(metric.revenue, prev.revenue),
        doors: territoryCustomers.length,
        reorderRisk: territoryCustomers.filter(customer => customer.risk === 'High' || num(customer.daysSince) >= 14).length,
        rep,
        spark: liveSparkForOrders(territoryOrders, windowRange, null, filters),
        aov: metric.orders ? Math.round(metric.revenue / metric.orders) : 0,
        orders: metric.orders
      };
    }).sort((a, b) => b.revenue - a.revenue);

    const repKeys = new Set([...repCurrent.keys(), ...repPrevious.keys(), ...activeCustomers.map(customer => customer.rep).filter(Boolean)]);
    const byRep = Array.from(repKeys).map(rep => {
      const metric = liveMetric(repCurrent, rep);
      const prev = liveMetric(repPrevious, rep);
      const repCustomers = activeCustomers.filter(customer => customer.rep === rep);
      const repOrders = orders.filter(order => (order.rep || liveCustomerForOrder(order, customersById).rep || UNASSIGNED_REP_LABEL) === rep);
      const repVisits = visits.filter(visit => visit.who === rep).length;
      const newDoors = repCustomers.filter(customer => inRange(liveCustomerDate(customer), windowRange.start, windowRange.now)).length;
      return {
        rep,
        territory: repCustomers[0]?.territory || repOrders[0]?.territory || 'All',
        revenue: Math.round(metric.revenue),
        cases: Math.round(metric.cases),
        orders: metric.orders,
        growth: percentDelta(metric.revenue, prev.revenue),
        doors: repCustomers.length,
        visits: repVisits,
        winRate: repVisits ? metric.orders / repVisits : 0,
        aov: metric.orders ? Math.round(metric.revenue / metric.orders) : 0,
        newDoors,
        spark: liveSparkForOrders(repOrders, windowRange, null, filters)
      };
    }).sort((a, b) => b.revenue - a.revenue);

    const typeKeys = new Set([...typeCurrent.keys(), ...typePrevious.keys(), ...activeCustomers.map(customer => customer.type || 'Unclassified')]);
    const totalTypeRevenue = Array.from(typeCurrent.values()).reduce((sum, row) => sum + row.revenue, 0) || 1;
    const byType = Array.from(typeKeys).map(type => {
      const metric = liveMetric(typeCurrent, type);
      const prev = liveMetric(typePrevious, type);
      const doors = activeCustomers.filter(customer => (customer.type || 'Unclassified') === type).length;
      return {
        type,
        revenue: Math.round(metric.revenue),
        cases: Math.round(metric.cases),
        doors,
        growth: percentDelta(metric.revenue, prev.revenue),
        share: Math.round((metric.revenue / totalTypeRevenue) * 100)
      };
    }).sort((a, b) => b.revenue - a.revenue);

    const bySku = liveSkuGroups(orders, previousOrders, filters);
    const byTerritoryFlavor = liveTerritoryFlavorGroups(orders, previousOrders, filters, customersById);
    const dow = liveDow(orders, filters);
    const summary = {
      revenue: Math.round(currentTotals.revenue),
      revenueGrowth: percentDelta(currentTotals.revenue, previousTotals.revenue),
      cases: Math.round(currentTotals.cases),
      casesGrowth: percentDelta(currentTotals.cases, previousTotals.cases),
      orders: currentTotals.orders,
      ordersGrowth: percentDelta(currentTotals.orders, previousTotals.orders),
      aov: currentTotals.orders ? Math.round(currentTotals.revenue / currentTotals.orders) : 0,
      aovGrowth: percentDelta(currentTotals.orders ? currentTotals.revenue / currentTotals.orders : 0, previousTotals.orders ? previousTotals.revenue / previousTotals.orders : 0),
      activeDoors: activeCustomers.length,
      newDoors: activeCustomers.filter(customer => inRange(liveCustomerDate(customer), windowRange.start, windowRange.now)).length,
      repVisits: visits.length
    };
    const payload = {
      DOW,
      HOURS,
      byRep,
      bySku,
      byTerritory,
      byTerritoryFlavor,
      byType,
      channelMix: liveChannelMix(orders, customersById),
      dow,
      heatmap: liveHeatmap(orders),
      orderSizes: liveOrderSizeBands(orders, filters),
      summary,
      weeks
    };
    payload.insights = liveInsights(payload);
    if (!payload.insights.length) {
      payload.insights = [{ kind: 'peak', icon: 'analytics', text: 'No live sales activity in this range yet', detail: 'Create or import live orders to populate analytics.' }];
    }
    return payload;
  }

  window.RP.liveAnalytics = function (range, filters) {
    if (window.rpHasLiveAuth && window.rpHasLiveAuth()) {
      throw new Error('Live sales analytics must be loaded from /api/analytics/sales.');
    }
    return window.RP.ANA;
  };
})();

const RP_SALES_ANALYTICS_RANGES = new Set(['1d', '7d', '4w', '12w', 'mtd', 'ytd']);

function rpSalesAnalyticsText(value) {
  return value == null ? '' : String(value).trim();
}

function rpSalesAnalyticsQuery(range, filters) {
  const requestedRange = rpSalesAnalyticsText(range).toLowerCase();
  const source = filters && typeof filters === 'object' ? filters : {};
  return {
    range: RP_SALES_ANALYTICS_RANGES.has(requestedRange) ? requestedRange : '12w',
    filters: {
      customerType: rpSalesAnalyticsText(source.customerType),
      flavor: rpSalesAnalyticsText(source.flavor),
      rep: rpSalesAnalyticsText(source.rep),
      territory: rpSalesAnalyticsText(source.territory)
    }
  };
}

function rpNormalizeSalesAnalytics(payload) {
  if (!payload || payload.schemaVersion !== 1 || !payload.summary || !payload.operational) {
    throw new Error('Sales analytics returned an unsupported response.');
  }
  if (!payload.provenance || payload.provenance.source !== 'postgres' || payload.provenance.complete !== true || payload.provenance.rowLimit !== null) {
    throw new Error('Sales analytics could not confirm complete database totals.');
  }
  for (const key of ['weeks', 'dow', 'heatmap', 'byTerritory', 'byRep', 'byType', 'bySku', 'byProduct', 'byTerritoryFlavor', 'channelMix', 'orderSizes']) {
    if (!Array.isArray(payload[key])) throw new Error(`Sales analytics is missing ${key}.`);
  }
  return payload;
}

async function rpFetchSalesAnalytics(query, signal) {
  const params = new URLSearchParams();
  params.set('range', query.range);
  Object.entries(query.filters || {}).forEach(([key, value]) => {
    const clean = rpSalesAnalyticsText(value);
    if (clean && clean.toLowerCase() !== 'all') params.set(key, clean);
  });
  const payload = await rpApi(`/api/analytics/sales?${params.toString()}`, { signal });
  return rpNormalizeSalesAnalytics(payload);
}

function useRpSalesAnalytics(range = '12w', filters = {}) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const queryKey = JSON.stringify({
    ...rpSalesAnalyticsQuery(range, filters),
    actorId: rpSalesAnalyticsText(profile && profile.id),
    customerId: rpSalesAnalyticsText(profile && (profile.customer_id || profile.customerId)),
    live,
    role: rpSalesAnalyticsText(profile && profile.role),
    territoryScope: rpSalesAnalyticsText(profile && profile.territory)
  });
  const query = useMemo(() => JSON.parse(queryKey), [queryKey]);
  const requestRef = useRef(null);
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState(() => ({
    asOf: '', data: live ? null : window.RP.ANA, error: '', loaded: !live,
    loading: false, queryKey
  }));

  useEffect(() => () => {
    if (requestRef.current) requestRef.current.abort();
  }, []);

  useEffect(() => {
    if (!live) return undefined;
    const refresh = () => setRetryToken(value => value + 1);
    window.addEventListener('rp-data-refresh', refresh);
    return () => window.removeEventListener('rp-data-refresh', refresh);
  }, [live]);

  useEffect(() => {
    if (requestRef.current) requestRef.current.abort();
    if (!live) {
      setState({ asOf: '', data: window.RP.ANA, error: '', loaded: true, loading: false, queryKey });
      return undefined;
    }

    const controller = new AbortController();
    requestRef.current = controller;
    setState({ asOf: '', data: null, error: '', loaded: false, loading: true, queryKey });
    rpFetchSalesAnalytics(query, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        setState({ asOf: rpSalesAnalyticsText(data.asOf), data, error: '', loaded: true, loading: false, queryKey });
      })
      .catch(error => {
        if (controller.signal.aborted || (error && error.name === 'AbortError')) return;
        setState({
          asOf: '',
          data: null,
          error: error && error.message ? error.message : 'Could not load complete sales analytics.',
          loaded: false,
          loading: false,
          queryKey
        });
      });
    return () => controller.abort();
  }, [live, queryKey, retryToken]);

  const current = state.queryKey === queryKey;
  const currentState = current
    ? state
    : { ...state, asOf: '', data: null, error: '', loaded: false, loading: live };
  return {
    ...currentState,
    initialLoading: live && currentState.loading && !currentState.loaded,
    isLive: live,
    retry: () => setRetryToken(value => value + 1)
  };
}


/* ===== data_leads.js ===== */
/* ============================================================
   RANCH POWER — Leads / CRM data + coaching engine
   Lead → customer pipeline with a guided next-best-action
   playbook. Extends window.RP.LEADS_API.
   ============================================================ */
(function () {
  // Pipeline stages (ordered). 'won' converts to a customer; 'lost' is terminal.
  const STAGES = [
    { id: 'idea', label: 'Idea', short: 'Idea', color: 'var(--faint)', hint: 'A place worth pursuing. Research it, then make first contact.' },
    { id: 'contacted', label: 'Contacted', short: 'Contacted', color: 'var(--info)', hint: 'Intro made. Get a yes to a sample drop.' },
    { id: 'sampling', label: 'Sampling', short: 'Sampling', color: 'var(--f-blueras)', hint: 'Product is in their hands. Book the decision visit.' },
    { id: 'proposal', label: 'Proposal', short: 'Proposal', color: 'var(--amber)', hint: 'Pricing presented. Ask for the first order.' },
    { id: 'won', label: 'Won', short: 'Won', color: 'var(--ok)', hint: 'Converted to a customer. Set up their account.' },
    { id: 'lost', label: 'Lost', short: 'Lost', color: 'var(--danger)', hint: 'Not now. Log the reason and a date to revisit.' },
  ];
  const stage = id => STAGES.find(s => s.id === id);
  const ACTIVE_STAGES = ['idea', 'contacted', 'sampling', 'proposal'];

  const ACTIVITY_TYPES = [
    { id: 'call', label: 'Call', icon: 'phone' },
    { id: 'visit', label: 'Visit', icon: 'pin' },
    { id: 'sample', label: 'Sample drop', icon: 'package' },
    { id: 'email', label: 'Email', icon: 'mail' },
    { id: 'note', label: 'Note', icon: 'pen' },
  ];
  const actType = id => ACTIVITY_TYPES.find(a => a.id === id) || ACTIVITY_TYPES[4];

  // ---- Playbook: per-stage guidance the app coaches reps with ----
  const PLAYBOOK = {
    idea: {
      goal: 'Make a strong first contact within 3 days.',
      sla: 3,
      next: 'call',
      cta: 'Log first contact',
      tips: [
        'Lead with the local angle — BC-made, built for people who work hard.',
        'Name-drop a nearby account already carrying Ranch Power.',
        'Ask one question: “What energy drink moves best for you right now?”',
        'Goal of the call: earn a 5-minute sample drop, not a sale.',
      ],
    },
    contacted: {
      goal: 'Convert interest into a sample drop within 5 days.',
      sla: 5,
      next: 'sample',
      cta: 'Schedule sample drop',
      tips: [
        'Offer a free sample case — Ginger Citrus + one wildcard flavour.',
        'Bring the one-pager and price list for their tier.',
        'Confirm who actually signs off on new SKUs.',
        'Set the decision date before you leave.',
      ],
    },
    sampling: {
      goal: 'Book the decision visit within 7 days of the drop.',
      sla: 7,
      next: 'visit',
      cta: 'Book decision visit',
      tips: [
        'Follow up 48h after the drop — “How did the team like them?”',
        'Bring a shelf-ready display recommendation.',
        'Have the opening-order quantity ready (start 6–12 cases).',
        'Address price objections with case-margin math, not discounts.',
      ],
    },
    proposal: {
      goal: 'Close the opening order this week.',
      sla: 5,
      next: 'visit',
      cta: 'Convert to customer',
      tips: [
        'Recommend a starter assortment, not the full line.',
        'Lock delivery cadence (Tue/Thu) to build a habit.',
        'Offer the mixer pack as the “try the range” option.',
        'If they hesitate, propose a 30-day trial with a reorder check-in.',
      ],
    },
  };

  // ---- Demo leads ----
  const DEMO_TODAY = new Date('2026-06-04');
  const daysAgo = n => { const d = new Date(DEMO_TODAY); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const daysAhead = n => { const d = new Date(DEMO_TODAY); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

  let LEADS = [
    {
      id: 'L-1007', name: 'Sea to Sky Bottle Co.', type: 'Convenience', territory: 'Squamish', rep: 'Dana Whitfield',
      contact: 'Owner — Ray', phone: '(604) 555-0148', email: 'ray@seatoskybottle.ca', city: 'Squamish, BC',
      stage: 'idea', temp: 'Warm', source: 'Rep idea', potential: 8, created: daysAgo(1), lastTouch: null,
      nextAction: { type: 'call', due: daysAhead(2), note: 'Cold intro call' },
      activities: [{ date: daysAgo(1), type: 'note', note: 'Drove past — busy corner store near the climbing gym. Worth a shot.', by: 'Dana Whitfield' }],
      notes: 'High foot traffic from climbers and trades. Good fit for Grit. Driven. positioning.',
    },
    {
      id: 'L-1006', name: 'Garibaldi Provisions', type: 'Grocery', territory: 'Squamish', rep: 'Dana Whitfield',
      contact: 'Buyer — Mel', phone: '(604) 555-0172', email: 'mel@garibaldiprov.com', city: 'Squamish, BC',
      stage: 'contacted', temp: 'Hot', source: 'Referral', potential: 20, created: daysAgo(9), lastTouch: daysAgo(2),
      nextAction: { type: 'sample', due: daysAhead(1), note: 'Drop sample case — Ginger Citrus + Key Lime' },
      activities: [
        { date: daysAgo(2), type: 'call', note: 'Mel is keen — wants to taste before committing. Books new SKUs monthly.', by: 'Dana Whitfield' },
        { date: daysAgo(9), type: 'note', note: 'Referred by Mountain Gym. Independent grocery, strong local-brand shelf.', by: 'Dana Whitfield' },
      ],
      notes: 'Referral from an existing account — warm intro. Decision-maker identified.',
    },
    {
      id: 'L-1005', name: 'Basecamp Cafe', type: 'Cafe', territory: 'Squamish', rep: 'Dana Whitfield',
      contact: 'Manager — Jo', phone: '(604) 555-0190', email: 'jo@basecampcafe.ca', city: 'Squamish, BC',
      stage: 'sampling', temp: 'Hot', source: 'Walk-in', potential: 6, created: daysAgo(16), lastTouch: daysAgo(6),
      nextAction: { type: 'visit', due: daysAgo(1), note: 'Decision visit — overdue follow-up on samples' },
      activities: [
        { date: daysAgo(6), type: 'sample', note: 'Dropped 1 case mixed. Staff loved Peach Mango.', by: 'Dana Whitfield' },
        { date: daysAgo(11), type: 'visit', note: 'Walked in, met Jo. Open to a cooler-door trial.', by: 'Dana Whitfield' },
        { date: daysAgo(16), type: 'note', note: 'Spotted empty energy-drink facings — opportunity.', by: 'Dana Whitfield' },
      ],
      notes: 'Samples landed well. Follow-up is overdue — momentum at risk.',
    },
    {
      id: 'L-1004', name: 'Chief Climbing Gym', type: 'Gym', territory: 'Squamish', rep: 'Dana Whitfield',
      contact: 'GM — Priya', phone: '(604) 555-0111', email: 'priya@chiefclimbing.ca', city: 'Squamish, BC',
      stage: 'proposal', temp: 'Hot', source: 'Event', potential: 14, created: daysAgo(23), lastTouch: daysAgo(3),
      nextAction: { type: 'visit', due: daysAhead(1), note: 'Present opening order — 12-case starter' },
      activities: [
        { date: daysAgo(3), type: 'email', note: 'Sent Tier B price list + 12-case starter proposal.', by: 'Dana Whitfield' },
        { date: daysAgo(8), type: 'visit', note: 'Tasting with staff went great. Priya wants pricing.', by: 'Dana Whitfield' },
        { date: daysAgo(15), type: 'sample', note: 'Dropped 2 cases for the front desk fridge.', by: 'Dana Whitfield' },
        { date: daysAgo(23), type: 'note', note: 'Met Priya at the Squamish trades expo.', by: 'Dana Whitfield' },
      ],
      notes: 'Closest to converting. Pricing is the last hurdle — has budget approval.',
    },
    {
      id: 'L-1003', name: 'Tantalus Market', type: 'Grocery', territory: 'Squamish', rep: 'Dana Whitfield',
      contact: 'Owner — Sasha', phone: '(604) 555-0133', email: null, city: 'Squamish, BC',
      stage: 'contacted', temp: 'Cold', source: 'Rep idea', potential: 12, created: daysAgo(20), lastTouch: daysAgo(14),
      nextAction: { type: 'call', due: daysAgo(7), note: 'Re-engage — went quiet after first call' },
      activities: [
        { date: daysAgo(14), type: 'call', note: 'Brief call. Said “maybe later”. Price-sensitive.', by: 'Dana Whitfield' },
        { date: daysAgo(20), type: 'note', note: 'Independent grocer. Carries 3 competitor energy brands.', by: 'Dana Whitfield' },
      ],
      notes: 'Cooled off. Needs a fresh angle — maybe a promo allowance.',
    },
    {
      id: 'L-1002', name: 'Valley Fitness', type: 'Gym', territory: 'Kelowna', rep: 'Marco Ferreira',
      contact: 'Owner — Devon', phone: '(250) 555-0166', email: 'devon@valleyfit.ca', city: 'Kelowna, BC',
      stage: 'sampling', temp: 'Warm', source: 'Inbound', potential: 10, created: daysAgo(12), lastTouch: daysAgo(4),
      nextAction: { type: 'call', due: daysAhead(2), note: 'Check sample feedback' },
      activities: [
        { date: daysAgo(4), type: 'sample', note: 'Dropped mixed case. Devon traveling, will taste next week.', by: 'Marco Ferreira' },
        { date: daysAgo(12), type: 'email', note: 'Inbound form submission — interested after seeing IG.', by: 'Marco Ferreira' },
      ],
      notes: 'Inbound lead — already aware of the brand.',
    },
    {
      id: 'L-1001', name: 'Whistler Bunkhouse', type: 'Convenience', territory: 'Squamish', rep: 'Dana Whitfield',
      contact: 'Manager — Theo', phone: '(604) 555-0102', email: 'theo@bunkhouse.ca', city: 'Whistler, BC',
      stage: 'won', temp: 'Hot', source: 'Referral', potential: 18, created: daysAgo(34), lastTouch: daysAgo(5),
      convertedTo: null,
      nextAction: null,
      activities: [
        { date: daysAgo(5), type: 'note', note: 'Agreed to a 12-case opening order. Converting to customer.', by: 'Dana Whitfield' },
        { date: daysAgo(12), type: 'visit', note: 'Presented proposal. Theo said yes.', by: 'Dana Whitfield' },
      ],
      notes: 'Staff accommodation with a busy store — strong repeat potential.',
    },
    {
      id: 'L-1000', name: 'Squamish Gas & Go', type: 'Convenience', territory: 'Squamish', rep: 'Dana Whitfield',
      contact: 'Owner', phone: null, email: null, city: 'Squamish, BC',
      stage: 'lost', temp: 'Cold', source: 'Rep idea', potential: 10, created: daysAgo(40), lastTouch: daysAgo(18),
      lostReason: 'Exclusive supplier contract', revisit: daysAhead(90),
      nextAction: null,
      activities: [
        { date: daysAgo(18), type: 'call', note: 'Locked into an exclusive distributor deal until Q4.', by: 'Dana Whitfield' },
        { date: daysAgo(40), type: 'note', note: 'Big volume site on the highway.', by: 'Dana Whitfield' },
      ],
      notes: 'Revisit in Q4 when their contract lapses.',
    },
  ];

  // ---- helpers ----
  function leadTodayYmd() {
    if (typeof rpTodayYmd === 'function') return rpTodayYmd();
    return new Date().toISOString().slice(0, 10);
  }
  function leadDueIn(days) {
    if (typeof rpTodayYmd === 'function') return rpTodayYmd(null, days || 0);
    const d = new Date();
    d.setDate(d.getDate() + (days || 0));
    return d.toISOString().slice(0, 10);
  }
  function leadNow() {
    const local = typeof rpNowLocalInput === 'function' ? String(rpNowLocalInput()).replace(' ', 'T') : '';
    const parsed = local ? new Date(local) : new Date();
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }
  function dueDate(value) {
    const text = String(value || '').trim();
    if (!text) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return new Date(`${text}T23:59:59`);
    const normalized = text.replace(' ', 'T').slice(0, 16);
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  function formatDue(value) {
    const text = String(value || '').trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (!match) return text;
    const date = new Date(`${match[1]}-${match[2]}-${match[3]}T12:00:00`);
    const day = Number.isNaN(date.getTime()) ? `${match[1]}-${match[2]}-${match[3]}` : new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(date);
    if (!match[4]) return day;
    const rawHour = Number(match[4]);
    const displayHour = ((rawHour + 11) % 12) + 1;
    const suffix = rawHour >= 12 ? 'PM' : 'AM';
    return `${day} at ${displayHour}:${match[5]} ${suffix}`;
  }
  function daysSince(dateStr) {
    if (!dateStr) return null;
    return Math.round((leadNow() - new Date(dateStr)) / 86400000);
  }
  function isOverdue(lead) {
    if (!lead.nextAction || !lead.nextAction.due) return false;
    const due = dueDate(lead.nextAction.due);
    return !!due && due < leadNow();
  }
  // Lead score 0–100: stage progress + temperature + recency + momentum
  function score(lead) {
    const stageW = { idea: 10, contacted: 30, sampling: 55, proposal: 80, won: 100, lost: 0 }[lead.stage] || 0;
    const tempW = { Hot: 18, Warm: 9, Cold: 0 }[lead.temp] || 0;
    const ds = daysSince(lead.lastTouch);
    const recency = ds == null ? 4 : ds <= 3 ? 12 : ds <= 7 ? 7 : ds <= 14 ? 2 : -6;
    const overdue = isOverdue(lead) ? -12 : 0;
    return Math.max(0, Math.min(100, Math.round(stageW * 0.6 + tempW + recency + overdue + 10)));
  }
  function health(lead) {
    if (lead.stage === 'won') return { label: 'Won', tone: 'ok' };
    if (lead.stage === 'lost') return { label: 'Lost', tone: 'neutral' };
    if (isOverdue(lead)) return { label: 'Overdue', tone: 'danger' };
    const ds = daysSince(lead.lastTouch);
    if (ds != null && ds > 10) return { label: 'Going cold', tone: 'warn' };
    const s = score(lead);
    if (s >= 70) return { label: 'On track', tone: 'ok' };
    return { label: 'Active', tone: 'info' };
  }

  // ---- Next-best-action coaching engine ----
  function nextBestAction(lead) {
    if (lead.stage === 'won') return { urgent: false, title: 'Set up their account', detail: 'Convert this lead into a prospect and queue approval.', cta: 'Convert to customer', action: 'convert', tips: ['Confirm delivery cadence and opening order.', 'Set their price-list tier before approval.'] };
    if (lead.stage === 'lost') return { urgent: false, title: 'Revisit later', detail: lead.revisit ? `Set to revisit around ${lead.revisit}.` : 'Log a date to circle back.', cta: 'Reopen lead', action: 'reopen', tips: ['Re-approach with a fresh hook — a new flavour or promo.'] };
    const pb = PLAYBOOK[lead.stage];
    const ds = daysSince(lead.lastTouch);
    const overdue = isOverdue(lead);
    let title, detail;
    if (overdue) { title = 'Follow-up is overdue'; detail = `${lead.nextAction.note}. You're past the ${pb.sla}-day window — reach out today before this goes cold.`; }
    else if (ds != null && ds > pb.sla) { title = 'Time to follow up'; detail = `It's been ${ds} days since your last touch. ${pb.goal}`; }
    else { title = pb.goal; detail = lead.nextAction ? lead.nextAction.note : 'Keep the momentum going.'; }
    return { urgent: overdue, title, detail, cta: pb.cta, action: lead.stage === 'proposal' ? 'convert' : 'advance', channel: pb.next, tips: pb.tips };
  }

  // ---- mutations ----
  let seq = 1008;
  function addLead(data) {
    const id = 'L-' + (seq++);
    const lead = Object.assign({
      id, stage: 'idea', temp: 'Warm', source: 'Rep idea', potential: 10, rep: 'Dana Whitfield',
      created: leadTodayYmd(), lastTouch: null,
      nextAction: { type: 'call', due: leadDueIn(2), note: 'Make first contact' },
      activities: [], notes: '',
    }, data);
    if (data.notes) lead.activities.unshift({ date: leadTodayYmd(), type: 'note', note: data.notes, by: lead.rep });
    LEADS.unshift(lead);
    return lead;
  }
  function logActivity(leadId, act) {
    const lead = LEADS.find(l => l.id === leadId); if (!lead) return;
    lead.activities.unshift({ date: leadTodayYmd(), by: lead.rep, ...act });
    lead.lastTouch = leadTodayYmd();
    if (act.nextAction) lead.nextAction = act.nextAction;
    return lead;
  }
  function advanceStage(leadId, toStage, extra) {
    const lead = LEADS.find(l => l.id === leadId); if (!lead) return;
    lead.stage = toStage;
    Object.assign(lead, extra || {});
    return lead;
  }
  function convertLead(leadId, data) {
    const lead = LEADS.find(l => l.id === leadId); if (!lead) return null;
    const contacts = data && Array.isArray(data.contacts) ? data.contacts : [];
    const primary = (contacts.find(c => c && c.primary) || contacts[0] || {});
    const cust = RP.addCustomer({
      name: lead.name, type: lead.type, territory: lead.territory, city: lead.city,
      rep: lead.rep, contact: primary.name || lead.contact, buyer: primary.name || lead.contact, phone: primary.phone || lead.phone, email: primary.email || lead.email, contacts,
      status: 'Prospect', risk: 'Prospect', credit: 'New', tier: data && data.tier ? data.tier : lead.tier || 'Default Retail', terms: data && data.terms ? data.terms : 'Prepaid', skipCreds: true,
      defaultDeliveryMethod: data && data.defaultDeliveryMethod ? data.defaultDeliveryMethod : 'Ranch Delivery',
      onboardingQuestionnaire: { defaultDeliveryMethod: data && data.defaultDeliveryMethod ? data.defaultDeliveryMethod : 'Ranch Delivery' },
      lat: { Squamish: 49.70, Kelowna: 49.88, Kamloops: 50.67, 'Northern BC': 53.91 }[lead.territory] || 49.7,
      lng: { Squamish: -123.16, Kelowna: -119.49, Kamloops: -120.33, 'Northern BC': -122.74 }[lead.territory] || -123.1,
      note: 'Converted from lead ' + lead.id + '. ' + (lead.notes || ''),
    });
    if (RP.addTask) {
      cust.approvalTask = RP.addTask({
        title: 'Approve customer: ' + cust.name,
        owner: 'Sales Manager',
        assigner: lead.rep,
        priority: 'High',
        cat: 'Sales',
        due: leadDueIn(1),
        cust: cust.id,
        note: 'Converted from lead ' + lead.id + '. Approve before customer portal ordering is enabled.'
      });
    }
    lead.stage = 'won';
    lead.convertedTo = cust.id;
    return cust;
  }
  function upsertLead(lead) {
    if (!lead || !lead.id) return lead;
    const idx = LEADS.findIndex(l => l.id === lead.id);
    if (idx >= 0) LEADS[idx] = { ...LEADS[idx], ...lead };
    else LEADS.unshift(lead);
    return LEADS.find(l => l.id === lead.id);
  }
  function deleteLead(leadId) {
    const idx = LEADS.findIndex(l => l.id === leadId);
    if (idx < 0) return null;
    const [removed] = LEADS.splice(idx, 1);
    return removed;
  }

  window.RP.LEADS = LEADS;
  window.RP.LEADS_API = {
    STAGES, stage, ACTIVE_STAGES, ACTIVITY_TYPES, actType, PLAYBOOK,
    daysSince, isOverdue, formatDue, score, health, nextBestAction,
    addLead, logActivity, advanceStage, convertLead, upsertLead, deleteLead,
    forRep: name => LEADS.filter(l => l.rep === name),
  };
})();


/* ===== data_ops.js ===== */
/* ============================================================
   RANCH POWER — Ops add-ons: Tasks · Sales Goals · Expenses ·
   Public retail listing.  Extends window.RP. Runs after data.js.
   ============================================================ */
(function () {
  const RP = window.RP;

  /* ============================================================ TASKS
     Assign / track / organize work for the team. Hangs on:
     owner (assignee) · assigner · status · due · linked record. */
  const TASK_STATUSES = ['To Do', 'In Progress', 'Blocked', 'Done'];
  const TASK_PRIORITIES = ['Urgent', 'High', 'Medium', 'Low'];
  const TASK_CATEGORIES = ['Sales', 'Operations', 'Warehouse', 'Logistics', 'Finance', 'Admin', 'Support'];

  // People who can own / assign tasks (mirrors RP.USERS but trimmed to a name list)
  const TEAM = [
    { name: 'Dana Whitfield', role: 'Sales Rep', area: 'Squamish' },
    { name: 'Sam Okoro', role: 'Sales Rep', area: 'Northern BC · Kamloops' },
    { name: 'Marco Ferreira', role: 'Sales Rep', area: 'Kelowna' },
    { name: 'J. Park', role: 'Warehouse', area: 'Squamish WH' },
    { name: 'L. Tran', role: 'Dispatcher', area: 'Squamish' },
    { name: 'A. Reyes', role: 'Operations', area: 'All' },
    { name: 'C. Hale', role: 'Leadership', area: 'All' },
    { name: 'Accounting', role: 'Accountant', area: 'All' },
  ];

  const TASKS = [
    { id: 'TSK-218', title: 'Pitch Peach Mango end-cap to Superette', owner: 'Dana Whitfield', assigner: 'A. Reyes', status: 'In Progress', priority: 'High', cat: 'Sales', due: '2026-06-06', created: '2026-06-03', cust: 'C-001', note: 'Buyer keen on a Mango promo. Bring sell sheet + sample case.' },
    { id: 'TSK-217', title: 'Recover OK Outfitters — 25 days no order', owner: 'Marco Ferreira', assigner: 'A. Reyes', status: 'To Do', priority: 'Urgent', cat: 'Sales', due: '2026-06-05', created: '2026-06-02', cust: 'C-005', note: 'On credit watch. Confirm AR cleared before pitching reorder.' },
    { id: 'TSK-216', title: 'Write off 3 damaged Ginger Citrus cases', owner: 'J. Park', assigner: 'A. Reyes', status: 'Blocked', priority: 'High', cat: 'Warehouse', due: '2026-06-05', created: '2026-06-03', note: 'DMG-88 · awaiting manager approval to post the write-off.' },
    { id: 'TSK-215', title: 'Chase NorthLid on delayed PO-222 lids', owner: 'A. Reyes', assigner: 'C. Hale', status: 'In Progress', priority: 'Urgent', cat: 'Operations', due: '2026-06-05', created: '2026-06-02', note: 'Lids shortfall blocks PRD-118. Get a firm ship date.' },
    { id: 'TSK-214', title: 'Sequence Friday delivery route — Squamish', owner: 'L. Tran', assigner: 'A. Reyes', status: 'To Do', priority: 'Medium', cat: 'Logistics', due: '2026-06-06', created: '2026-06-04', note: '4 stops + 1 transfer pickup. Mind Superette Tue/Thu window.' },
    { id: 'TSK-213', title: 'Reconcile May trade-spend chargebacks', owner: 'Accounting', assigner: 'C. Hale', status: 'In Progress', priority: 'Medium', cat: 'Finance', due: '2026-06-09', created: '2026-06-01', note: 'TS-502 disputed. Match deductions to invoices before close.' },
    { id: 'TSK-212', title: 'Set June sales goals for all reps', owner: 'A. Reyes', assigner: 'C. Hale', status: 'Done', priority: 'High', cat: 'Admin', due: '2026-06-02', created: '2026-05-29', note: 'Loaded into the Sales Goals board.' },
    { id: 'TSK-211', title: 'Onboard Creekside Cafe — credit app', owner: 'Dana Whitfield', assigner: 'A. Reyes', status: 'To Do', priority: 'Medium', cat: 'Sales', due: '2026-06-10', created: '2026-06-04', cust: 'C-003', note: 'Prospect. Collect signed credit application + first PO.' },
    { id: 'TSK-210', title: 'Cycle-count Aisle A before month close', owner: 'J. Park', assigner: 'A. Reyes', status: 'To Do', priority: 'Low', cat: 'Warehouse', due: '2026-06-12', created: '2026-06-04', note: 'Two counts overdue. Scan against system on commit.' },
    { id: 'TSK-209', title: 'Submit Q2 mileage logs', owner: 'Sam Okoro', assigner: 'Accounting', status: 'In Progress', priority: 'Medium', cat: 'Finance', due: '2026-06-08', created: '2026-06-03', note: 'Northern BC + Kamloops loops. Attach receipts for fuel.' },
  ];
  const taskById = id => TASKS.find(t => t.id === id);

  /* ============================================================ SALES GOALS
     Management sets targets per rep per period; actuals tracked to date.
     Status derives from pace (actual vs target vs time elapsed). */
  // June 2026 — today is the 5th, ~17% through the month.
  const PERIOD_ELAPSED = { 'June 2026': 5 / 30, 'Q2 2026': (30 + 31 + 5) / 91 };

  const SALES_GOALS = [
    { id: 'GL-DW-JUN', rep: 'Dana Whitfield', territory: 'Squamish', period: 'June 2026', setBy: 'A. Reyes',
      revTarget: 16000, revActual: 4620, casesTarget: 480, casesActual: 138, doorsTarget: 3, doorsActual: 1,
      note: 'Push Mango promo across Tier B doors.' },
    { id: 'GL-SO-JUN', rep: 'Sam Okoro', territory: 'Northern BC · Kamloops', period: 'June 2026', setBy: 'A. Reyes',
      revTarget: 24000, revActual: 3120, casesTarget: 720, casesActual: 96, doorsTarget: 4, doorsActual: 0,
      note: 'Kamloops trending down 4% — protect base, win 2 convenience doors.' },
    { id: 'GL-MF-JUN', rep: 'Marco Ferreira', territory: 'Kelowna', period: 'June 2026', setBy: 'C. Hale',
      revTarget: 12000, revActual: 4880, casesTarget: 360, casesActual: 150, doorsTarget: 3, doorsActual: 2,
      note: 'Strongest growth territory (+22%). Stretch on new doors.' },
  ];
  const goalById = id => SALES_GOALS.find(g => g.id === id);

  // status from pace — compares progress to time elapsed in the period
  function goalStatus(actual, target, elapsed) {
    if (target <= 0) return 'On Track';
    const prog = actual / target;
    if (prog >= 1) return 'Hit';
    const expected = Math.max(0.0001, elapsed);
    const pace = prog / expected;        // 1 = exactly on pace
    if (pace >= 1.1) return 'Ahead';
    if (pace >= 0.9) return 'On Track';
    if (pace >= 0.65) return 'At Risk';
    return 'Behind';
  }
  // overall goal status = worst of its three metrics
  function goalOverall(g, elapsed) {
    const order = ['Behind', 'At Risk', 'On Track', 'Ahead', 'Hit'];
    const ss = [
      goalStatus(g.revActual, g.revTarget, elapsed),
      goalStatus(g.casesActual, g.casesTarget, elapsed),
      goalStatus(g.doorsActual, g.doorsTarget, elapsed),
    ];
    return ss.reduce((w, s) => (order.indexOf(s) < order.indexOf(w) ? s : w), 'Hit');
  }

  /* ============================================================ EXPENSES
     Field reps & staff submit expenses w/ receipts → Accounting reviews,
     sets tax code + GL, and approves / reimburses. */
  const EXP_STATUSES = ['Submitted', 'Approved', 'Reimbursed', 'Needs Info', 'Rejected'];
  const TAX_CODES = ['GST 5%', 'GST + PST 12%', 'PST 7%', 'HST 13%', 'Zero-rated', 'Exempt', 'Meals (50% GST)'];
  const EXP_GL = [
    '6010 · Travel — Mileage', '6020 · Travel — Meals & Ent.', '6030 · Travel — Lodging',
    '6040 · Vehicle & Fuel', '5400 · Samples (COGS)', '6100 · Office & Supplies',
    '6200 · Shipping & Postage', '6300 · Marketing & Promo', '6900 · Other Expense',
  ];
  // category → suggested GL + tax + icon (accountant confirms / overrides)
  const EXP_CATS = {
    'Mileage': { gl: '6010 · Travel — Mileage', tax: 'Zero-rated', icon: 'truck' },
    'Meals & Ent.': { gl: '6020 · Travel — Meals & Ent.', tax: 'Meals (50% GST)', icon: 'star' },
    'Lodging': { gl: '6030 · Travel — Lodging', tax: 'GST 5%', icon: 'home' },
    'Fuel': { gl: '6040 · Vehicle & Fuel', tax: 'GST + PST 12%', icon: 'truck' },
    'Samples': { gl: '5400 · Samples (COGS)', tax: 'GST 5%', icon: 'package' },
    'Office Supplies': { gl: '6100 · Office & Supplies', tax: 'GST + PST 12%', icon: 'doc' },
    'Shipping': { gl: '6200 · Shipping & Postage', tax: 'GST 5%', icon: 'truck' },
    'Marketing': { gl: '6300 · Marketing & Promo', tax: 'GST 5%', icon: 'trade' },
    'Other': { gl: '6900 · Other Expense', tax: 'GST 5%', icon: 'box' },
  };

  const EXPENSES = [
    { id: 'EXP-2048', who: 'Dana Whitfield', role: 'Sales Rep', date: '2026-06-04', cat: 'Mileage', merchant: '142 km @ $0.68', amount: 96.56, status: 'Submitted', cust: 'C-001', receipt: true, taxCode: null, gl: null, reimbursable: true, note: 'Squamish loop — Superette, Mountain Gym, Creekside.' },
    { id: 'EXP-2047', who: 'Dana Whitfield', role: 'Sales Rep', date: '2026-06-03', cat: 'Meals & Ent.', merchant: 'Howe Sound Brew Pub', amount: 86.40, status: 'Submitted', cust: 'C-002', receipt: true, taxCode: null, gl: null, reimbursable: true, note: 'Lunch w/ Mountain Gym buyer — promo planning.' },
    { id: 'EXP-2046', who: 'Sam Okoro', role: 'Sales Rep', date: '2026-06-03', cat: 'Fuel', merchant: 'Petro-Canada — PG', amount: 112.30, status: 'Approved', cust: null, receipt: true, taxCode: 'GST + PST 12%', gl: '6040 · Vehicle & Fuel', reimbursable: true, note: 'Northern BC territory drive.', by: 'Accounting' },
    { id: 'EXP-2045', who: 'Marco Ferreira', role: 'Sales Rep', date: '2026-06-02', cat: 'Samples', merchant: 'Internal stock — 2 cs', amount: 64.00, status: 'Submitted', cust: 'C-006', receipt: true, taxCode: null, gl: null, reimbursable: false, note: 'Sampling at Loop Roasters.' },
    { id: 'EXP-2044', who: 'Marco Ferreira', role: 'Sales Rep', date: '2026-06-01', cat: 'Lodging', merchant: 'Kelowna Inn', amount: 189.00, status: 'Needs Info', cust: null, receipt: false, taxCode: null, gl: null, reimbursable: true, note: 'Overnight before early Vernon visits.', flag: 'Receipt missing' },
    { id: 'EXP-2043', who: 'J. Park', role: 'Warehouse', date: '2026-06-02', cat: 'Office Supplies', merchant: 'Staples — Squamish', amount: 34.99, status: 'Approved', cust: null, receipt: true, taxCode: 'GST + PST 12%', gl: '6100 · Office & Supplies', reimbursable: true, note: 'Label printer ribbon for receiving.', by: 'Accounting' },
    { id: 'EXP-2042', who: 'Dana Whitfield', role: 'Sales Rep', date: '2026-05-30', cat: 'Samples', merchant: 'Internal stock — 1 cs', amount: 32.00, status: 'Reimbursed', cust: 'C-003', receipt: true, taxCode: 'GST 5%', gl: '5400 · Samples (COGS)', reimbursable: false, note: 'Creekside Cafe first sampling.', by: 'Accounting' },
    { id: 'EXP-2041', who: 'Sam Okoro', role: 'Sales Rep', date: '2026-05-29', cat: 'Meals & Ent.', merchant: 'The Noble Pig', amount: 54.10, status: 'Approved', cust: 'C-007', receipt: true, taxCode: 'Meals (50% GST)', gl: '6020 · Travel — Meals & Ent.', reimbursable: true, note: 'Hwy 5 Pit Stop buyer coffee.', by: 'Accounting' },
    { id: 'EXP-2040', who: 'L. Tran', role: 'Dispatcher', date: '2026-06-04', cat: 'Fuel', merchant: 'Chevron — Squamish', amount: 78.50, status: 'Submitted', cust: null, receipt: true, taxCode: null, gl: null, reimbursable: true, note: 'Ranch Van 1 top-up.' },
  ];
  const expById = id => EXPENSES.find(e => e.id === id);
  function expTax(e) {
    if (!e.taxCode) return 0;
    const m = { 'GST 5%': 0.05, 'GST + PST 12%': 0.12, 'PST 7%': 0.07, 'HST 13%': 0.13, 'Meals (50% GST)': 0.025 }[e.taxCode];
    if (!m) return 0;
    // amount is tax-inclusive; back out the embedded tax
    return e.amount - e.amount / (1 + m);
  }

  const VISITS = [
    { id: 'VST-1003', cust: 'C-001', type: 'Reorder check', date: '2026-06-04', when: 'Recently', note: 'Shelf running low on Ginger Citrus. Buyer keen on Mango promo.', who: 'Dana Whitfield', followUp: true, shelf: { 'RP-12-GC': 6, 'RP-12-KL': 4, 'RP-12-PM': 0 }, photos: { shelf: true, display: false } },
    { id: 'VST-1002', cust: 'C-001', type: 'Merchandising', date: '2026-05-15', when: '3 weeks ago', note: 'Built end-cap display. Took photos.', who: 'Dana Whitfield', followUp: false, shelf: {}, photos: { shelf: true, display: true } },
    { id: 'VST-1001', cust: 'C-001', type: 'New account pitch', date: '2026-04-24', when: '6 weeks ago', note: 'First meeting - sampled the line, positive.', who: 'Dana Whitfield', followUp: true, shelf: {}, photos: { shelf: false, display: false } },
  ];
  const visitById = id => VISITS.find(v => v.id === id);

  /* ============================================================ PUBLIC RETAIL
     Address + map pin per customer; "mark as public retailer" pushes the
     door to the public store-locator. Backfill onto existing customers. */
  const PUBLIC_DEFAULTS = {
    'C-001': { address: '38005 Cleveland Ave', postal: 'V8B 0A1', phone: '(604) 892-3310', public: true, publicName: 'Superette Market', hours: 'Mon–Sat 8–9 · Sun 9–7' },
    'C-002': { address: '1900 Garibaldi Way', postal: 'V8B 0A4', phone: '(604) 898-4420', public: true, publicName: 'Mountain Gym Shop', hours: 'Daily 6–10' },
    'C-004': { address: '1600 15th Ave', postal: 'V2L 3X3', phone: '(250) 564-7781', public: true, publicName: 'North Foods Co-op', hours: 'Mon–Sun 8–9' },
    'C-006': { address: '565 Bernard Ave', postal: 'V1Y 6N9', phone: '(250) 717-0042', public: true, publicName: 'Loop Roasters', hours: 'Daily 7–6' },
    'C-005': { address: '2339 Hwy 97 N', postal: 'V1X 4H9', phone: '(250) 765-1180', public: false, publicName: 'Okanagan Outfitters', hours: 'Mon–Sat 9–6' },
    'C-007': { address: '4150 Trans-Canada Hwy', postal: 'V2C 6V5', phone: '(250) 374-9902', public: true, publicName: 'Highway 5 Pit Stop', hours: '24 hours' },
    'C-003': { address: '1201 Commercial Way', postal: 'V8B 0M5', phone: '(604) 815-7745', public: false, publicName: 'Creekside Cafe', hours: 'Daily 7–4' },
    'C-008': { address: '750 Laval Cres', postal: 'V2C 5P5', phone: '(250) 372-1100', public: false, publicName: 'Interior Distributors', hours: 'By appointment' },
  };
  RP.CUSTOMERS.forEach(c => {
    const d = PUBLIC_DEFAULTS[c.id] || {};
    c.address = d.address || (c.city ? c.city.split(',')[0] : '');
    c.postal = d.postal || '';
    c.phone = c.phone || d.phone || '';
    c.public = d.public != null ? d.public : false;
    c.publicName = d.publicName || c.name;
    c.hours = d.hours || 'Mon–Fri 9–5';
  });

  /* ---- helpers / adders ---- */
  const pad = (n, w) => String(n).padStart(w || 3, '0');

  Object.assign(RP, {
    TASK_STATUSES, TASK_PRIORITIES, TASK_CATEGORIES, TEAM, TASKS, taskById,
    SALES_GOALS, goalById, goalStatus, goalOverall, PERIOD_ELAPSED,
    EXP_STATUSES, TAX_CODES, EXP_GL, EXP_CATS, EXPENSES, expById, expTax,
    VISITS, visitById,

    addTask: function (d) {
      const id = 'TSK-' + (219 + TASKS.filter(t => /^TSK-2\d\d$/.test(t.id)).length);
      const t = Object.assign({ id, status: 'To Do', created: '2026-06-05', assigner: 'A. Reyes' }, d);
      TASKS.unshift(t);
      return t;
    },
    upsertTask: function (d) {
      const t = taskById(d.id);
      if (t) return Object.assign(t, d);
      TASKS.unshift(d);
      return d;
    },
    setTaskStatus: function (id, status) { const t = taskById(id); if (t) t.status = status; return t; },

    addGoal: function (d) {
      const init = (d.rep || 'XX').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
      const id = 'GL-' + init + '-' + (d.period || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 5).toUpperCase();
      const g = Object.assign({ id, setBy: 'A. Reyes', revActual: 0, casesActual: 0, doorsActual: 0 }, d);
      const ex = SALES_GOALS.findIndex(x => x.rep === g.rep && x.period === g.period);
      if (ex >= 0) SALES_GOALS[ex] = Object.assign(SALES_GOALS[ex], g);
      else SALES_GOALS.unshift(g);
      return g;
    },
    upsertGoal: function (d) {
      const ex = SALES_GOALS.findIndex(x => x.id === d.id || (x.rep === d.rep && x.period === d.period));
      if (ex >= 0) return Object.assign(SALES_GOALS[ex], d);
      SALES_GOALS.unshift(d);
      return d;
    },

    addExpense: function (d) {
      const id = 'EXP-' + (2049 + EXPENSES.filter(e => /^EXP-2\d\d\d$/.test(e.id)).length - 9);
      const e = Object.assign({ id, status: 'Submitted', taxCode: null, gl: null, date: '2026-06-05' }, d);
      EXPENSES.unshift(e);
      return e;
    },
    upsertExpense: function (d) {
      const e = expById(d.id);
      if (e) return Object.assign(e, d);
      EXPENSES.unshift(d);
      return d;
    },
    reviewExpense: function (id, patch) { const e = expById(id); if (e) Object.assign(e, patch); return e; },
    addVisit: function (d) {
      const id = 'VST-' + (1004 + VISITS.filter(v => /^VST-\d+$/.test(v.id)).length - 3);
      const v = Object.assign({ id, date: '2026-06-05', when: 'Just now', followUp: false, shelf: {}, photos: {} }, d);
      VISITS.unshift(v);
      return v;
    },
    upsertVisit: function (d) {
      const v = visitById(d.id);
      if (v) return Object.assign(v, d);
      VISITS.unshift(d);
      return d;
    },
  });
})();


/* ===== ui.jsx ===== */
/* ============================================================
   RANCH POWER — Shared UI primitives  →  window
   ============================================================ */
const { useState, useEffect, useRef, useMemo } = React;

/* ---- Icons (lucide-style line set) ---- */
const ICON_PATHS = {
  home: 'M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5',
  orders: 'M9 3h6a1 1 0 0 1 1 1v1h2a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2V4a1 1 0 0 1 1-1ZM8 5v1h8V5M9 12h6M9 16h6',
  customers: 'M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM22 19v-1a4 4 0 0 0-3-3.87M16 4.13A4 4 0 0 1 16 11.6',
  inventory: 'M21 8 12 3 3 8m18 0-9 5-9-5m18 0v8l-9 5-9-5V8m9 5v8',
  dispatch: 'M3 6h11v9H3zM14 9h4l3 3v3h-7zM7.5 18.5A1.5 1.5 0 1 0 7.5 15.5a1.5 1.5 0 0 0 0 3ZM17.5 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  production: 'M3 21V8l5 3V8l5 3V5l5 3v13H3ZM7 21v-4M12 21v-4M17 21v-4',
  procurement: 'M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7ZM9 7V5a3 3 0 0 1 6 0v2',
  fba: 'M3 9l9-6 9 6v9l-9 6-9-6V9ZM3 9l9 6 9-6M12 15v6M7.5 6.2 16.5 12',
  trade: 'M12 2v20M17 5.5c0-1.7-2-3-5-3s-5 1.3-5 3 2 2.5 5 3 5 1.3 5 3-2 3-5 3-5-1.3-5-3',
  analytics: 'M3 3v18h18M7 15l3-4 3 3 5-7',
  admin: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 13a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 2.6 7a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H7a1.6 1.6 0 0 0 1-1.5V1a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V7a1.6 1.6 0 0 0 1.5 1H23a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM21 21l-4.3-4.3',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  chevR: 'M9 6l6 6-6 6', chevL: 'M15 6l-6 6 6 6', chevD: 'M6 9l6 6 6-6', chevU: 'M6 15l6-6 6 6',
  arrowR: 'M5 12h14M13 6l6 6-6 6', arrowL: 'M19 12H5M11 6l-6 6 6 6',
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  check: 'M20 6 9 17l-5-5', x: 'M18 6 6 18M6 6l12 12',
  trash: 'M3 6h18M8 6V4h8v2M6 6l1 15h10l1-15M10 10v7M14 10v7',
  filter: 'M22 3H2l8 9.5V19l4 2v-8.5L22 3Z',
  pin: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0ZM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  camera: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2v11ZM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  pen: 'M17 3a2.8 2.8 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z',
  package: 'M16.5 9.4 7.5 4.2M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16ZM3.3 7 12 12l8.7-5M12 22V12',
  scan: 'M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 8v8M11 8v8M16 8v8',
  more: 'M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  truck: 'M3 6h11v9H3zM14 9h4l3 3v3h-7zM7.5 18.5A1.5 1.5 0 1 0 7.5 15.5a1.5 1.5 0 0 0 0 3ZM17.5 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  trend: 'M23 6l-9.5 9.5-5-5L1 18M17 6h6v6',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2',
  phone: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7',
  card: 'M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7ZM2 10h20M6 15h4',
  clipboard: 'M9 5h6M9 3h6a2 2 0 0 1 2 2v1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1V5a2 2 0 0 1 2-2ZM8 6h8M8 11h8M8 15h6',
  tap: 'M5 9a5 5 0 0 1 0 6M9 6a9 9 0 0 1 0 12M13 9a5 5 0 0 1 0 6M17 6a9 9 0 0 1 0 12',
  link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  box: 'M3 9l9-5 9 5v6l-9 5-9-5V9ZM3 9l9 5 9-5',
  refresh: 'M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16',
  layers: 'M12 2 2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5',
  doc: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6M8 13h8M8 17h8',
  flag: 'M4 22V4s1-1 4-1 5 2 8 2 4-1 4-1v10s-1 1-4 1-5-2-8-2-4 1-4 1',
  star: 'M12 2l3 6.5 7 .9-5 4.8 1.3 7L12 18l-6.3 3.2L7 14.2l-5-4.8 7-.9L12 2Z',
  tasks: 'M8 6h13M8 12h13M8 18h13M3 5.5l1.2 1.2L6.6 4.3M3 11.5l1.2 1.2 2.4-2.4M3 17.5l1.2 1.2 2.4-2.4',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  receipt: 'M6 2h12v20l-2.5-1.6L13 22l-2.5-1.6L8 22l-2-1.6V2ZM9 7h6M9 11h6M9 15h4',
  wallet: 'M3 7a2 2 0 0 1 2-2h12v3M3 7v11a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H5a2 2 0 0 1-2-1ZM17 13h.01',
  lock: 'M5 10h14v11H5zM8 10V7a4 4 0 0 1 8 0v3',
};
function Icon({ name, size = 18, sw = 1.75, style, className }) {
  const d = ICON_PATHS[name] || ICON_PATHS.box;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
      style={style} className={className} aria-hidden="true">
      {d.split('M').filter(Boolean).map((seg, i) => <path key={i} d={'M' + seg} />)}
    </svg>
  );
}

/* ---- Ranch Power buffalo + wordmark (rendered as <img> + filter so they survive capture) ---- */
function Buffalo({ size = 28, white = true, style }) {
  return <img src="assets/brand/buffalo.svg" alt="Ranch Power" width={size} height={size * (227 / 341)} style={{ display: 'block', filter: white ? 'brightness(0) invert(1)' : 'none', ...style }} />;
}
function Wordmark({ height = 18, white = true, style }) {
  return <img src="assets/brand/wordmark.svg" alt="Ranch Power" height={height} style={{ display: 'block', width: 'auto', filter: white ? 'brightness(0) invert(1)' : 'none', ...style }} />;
}
function Logo({ white = true, accent = 'var(--amber)', compact = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
      <Buffalo size={32} white={white} />
      {!compact && (
        <div style={{ lineHeight: 1 }}>
          <Wordmark height={14} white={white} />
          <div style={{ fontFamily: 'var(--ff-display)', fontWeight: 600, fontSize: 9.5, letterSpacing: '0.22em', color: accent, marginTop: 5 }}>GRIT. DRIVEN.</div>
        </div>
      )}
    </div>
  );
}

async function rpUploadPrivateFile(kind, file, label) {
  if (!file) return null;
  const allowed = ['application/pdf', 'image/heic', 'image/heif', 'image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
  const allowedExt = ['heic', 'heif', 'jpeg', 'jpg', 'pdf', 'png', 'webp'];
  const ext = String(file.name || '').split('.').pop().toLowerCase();
  if (!allowed.includes(file.type) && !allowedExt.includes(ext)) throw new Error('Attach a PDF or image file.');
  if (file.size > 8 * 1024 * 1024) throw new Error('File must be 8 MB or smaller.');

  const form = new FormData();
  form.set('kind', kind);
  form.set('label', label || '');
  form.set('file', file);
  const response = await rpApi('/api/files/upload', {
    method: 'POST',
    body: form
  });
  return response.file || null;
}

const RP_PROTECTED_FETCH_TIMEOUT_MS = 45000;

async function rpCurrentAuthSession(forceRefresh) {
  if (typeof window !== 'undefined' && typeof window.RP_GET_AUTH_SESSION === 'function') {
    return window.RP_GET_AUTH_SESSION({ forceRefresh });
  }
  const auth = window.RP_AUTH || {};
  return auth.session || null;
}

async function rpFetchWithTimeout(path, options = {}, timeoutMs = RP_PROTECTED_FETCH_TIMEOUT_MS) {
  const existingSignal = options.signal;
  const controller = new AbortController();
  let timeoutId = null;
  let abortListener = null;

  if (existingSignal && existingSignal.aborted) {
    controller.abort(existingSignal.reason);
  } else if (existingSignal) {
    abortListener = () => controller.abort(existingSignal.reason);
    existingSignal.addEventListener('abort', abortListener, { once: true });
  }

  timeoutId = window.setTimeout(() => controller.abort(new Error('Network request timed out. Check your connection and try again.')), Math.max(1000, timeoutMs));

  try {
    return await fetch(path, { ...options, signal: controller.signal });
  } catch (err) {
    if (controller.signal.aborted && !(existingSignal && existingSignal.aborted)) {
      throw new Error('Network request timed out. Check your connection and try again.');
    }
    throw err;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
    if (existingSignal && abortListener) existingSignal.removeEventListener('abort', abortListener);
  }
}

async function rpFetchProtected(path, options = {}, forceRefresh = false) {
  const { timeoutMs = RP_PROTECTED_FETCH_TIMEOUT_MS, ...fetchOptions } = options;
  const headers = new Headers(options.headers || {});
  const session = await rpCurrentAuthSession(forceRefresh);
  const token = session && session.access_token;
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (!String(headers.get('Authorization') || '').trim()) {
    throw new Error('Your secure session ended. Sign in again to continue.');
  }
  if (!headers.has('X-Ranch-Power-Portal')) headers.set('X-Ranch-Power-Portal', typeof resolveInitialMode === 'function' ? resolveInitialMode() : 'operations');
  const res = await rpFetchWithTimeout(path, { ...fetchOptions, headers, credentials: 'same-origin' }, timeoutMs)
    .catch(error => { throw explainNetworkFailure(error, fetchOptions.method); });
  if (res.status === 401 && !forceRefresh) return rpFetchProtected(path, options, true);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let payload = {};
    try {
      payload = text ? JSON.parse(text) : {};
    } catch {
      payload = {};
    }
    const plainText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const message = explainHttpFailure({ ...payload, status: res.status, method: fetchOptions.method, error: payload.error || plainText });
    const entityScope = supportTicketEntityScope(path, fetchOptions.body);
    const ticket = {
      automatic: true,
      error: message,
      errorCode: payload.code || '',
      errorSummary: message,
      httpMethod: fetchOptions.method || 'GET',
      httpStatus: res.status,
      requestPath: path,
      ...entityScope,
      title: `Protected request failed: ${path}`,
      context: {
        code: payload.code || '',
        ...entityScope,
        method: fetchOptions.method || 'GET',
        path,
        status: res.status,
        response: plainText.slice(0, 500)
      },
      source: 'Automatic error report - protected request'
    };
    if (isExpectedHttpFailure(res.status)) {
      window.dispatchEvent(new CustomEvent('rp-toast', { detail: { msg: message, tone: 'warn' } }));
    } else {
      rpNotifyErrorWithTicket(ticket);
    }
    throw Object.assign(new Error(message), { status: res.status, code: payload.code || '' });
  }
  return res;
}

function rpErrorMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error.trim();
  if (error.message) return String(error.message).trim();
  try {
    return JSON.stringify(error).slice(0, 500);
  } catch {
    return String(error || '').trim();
  }
}

function rpErrorStack(error) {
  if (error && error.stack) return String(error.stack).trim();
  return '';
}

function rpErrorPage() {
  try {
    return `${window.location.hostname}${window.location.pathname}`;
  } catch {
    return '';
  }
}

function rpErrorSafeRequestPath(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  try {
    return new URL(text, 'https://ranchpower.invalid').pathname.slice(0, 500) || '/';
  } catch {
    return (text.split(/[?#]/, 1)[0] || '/').slice(0, 500);
  }
}

function rpErrorSafePage(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const safe = text.split(/[?#]/, 1)[0];
  try {
    if (/^https?:\/\//i.test(safe)) {
      const url = new URL(safe);
      return `${url.hostname}${url.pathname}`.slice(0, 500);
    }
  } catch {}
  return safe.slice(0, 500);
}

function rpRedactSupportText(value, maxLength = 900) {
  return String(value || '')
    .replace(/https?:\/\/[^\s<>"')\]]+/gi, raw => {
      try {
        const url = new URL(raw);
        return `${url.origin}${url.pathname}`;
      } catch {
        return raw.split(/[?#]/, 1)[0];
      }
    })
    .replace(/\b(authorization)\b(\s*[=:]\s*)(?:(?:bearer|basic)\s+)?[^\s,;&#]+/gi, '$1$2[redacted]')
    .replace(/\b(access_token|refresh_token|id_token|authorization|code|token|jwt|session|password|secret|api[_-]?key)\b\s*[=:]\s*[^\s,;&#]+/gi, '$1=[redacted]')
    .slice(0, maxLength);
}

function rpRedactErrorContextValue(key, value) {
  if (/path/i.test(key)) return rpErrorSafeRequestPath(value);
  if (/page|url|location/i.test(key)) return rpErrorSafePage(value);
  return rpRedactSupportText(value);
}

function rpErrorSource(source) {
  if (source) return source;
  let mode = 'operations';
  try {
    mode = typeof resolveInitialMode === 'function' ? resolveInitialMode() : 'operations';
  } catch {}
  const label = mode === 'field' ? 'Sales app' : mode === 'client' ? 'Customer portal' : 'Operations portal';
  return `Automatic error report - ${label}`;
}

function rpErrorProfileLine() {
  try {
    const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
    return [profile.display_name || profile.email || '', profile.role || ''].filter(Boolean).join(' - ');
  } catch {
    return '';
  }
}

function rpErrorContextLines(context) {
  if (!context) return [];
  if (Array.isArray(context)) return context.map(item => String(item || '').trim()).filter(Boolean);
  if (typeof context === 'string') return context.trim() ? [context.trim()] : [];
  if (typeof context === 'object') {
    return Object.entries(context)
      .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
      .map(([key, value]) => `- ${key}: ${rpRedactErrorContextValue(key, value)}`);
  }
  return [String(context)];
}

function rpBuildErrorTicketPayload(input = {}) {
  const message = rpErrorMessage(input.error || input.message) || 'Unknown application error.';
  const safeMessage = rpRedactSupportText(message, 900) || 'Unknown application error.';
  const page = rpErrorSafePage(input.page) || rpErrorPage();
  const source = rpErrorSource(input.source);
  const context = input.context && typeof input.context === 'object' && !Array.isArray(input.context) ? input.context : {};
  const httpStatus = Number(input.httpStatus || context.status);
  const automatic = input.automatic === true || /automatic error report/i.test(source);
  const expectedClientResponse = automatic && Number.isInteger(httpStatus) && httpStatus >= 400 && httpStatus < 500 && ![408, 429].includes(httpStatus);
  const requestPath = rpErrorSafeRequestPath(input.requestPath || context.path || '');
  const httpMethod = String(input.httpMethod || context.method || 'GET').trim().toUpperCase().slice(0, 20);
  const errorCode = String(input.errorCode || context.code || (input.error && input.error.code) || '').trim().slice(0, 120);
  const entityScope = supportTicketEntityScope(requestPath, {
    customerId: input.customerId || context.customerId,
    orderId: input.orderId || context.orderId
  });
  const title = rpRedactSupportText(input.title || `Application error: ${safeMessage}`, 110).replace(/\s+/g, ' ');
  const stack = rpRedactSupportText(rpErrorStack(input.error), 2500);
  const lines = [
    `Error: ${safeMessage}`,
    '',
    `Page: ${page || '-'}`,
    `Source: ${source}`,
    `Profile: ${rpErrorProfileLine() || '-'}`,
    `User agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : '-'}`,
    `Reported at: ${new Date().toISOString()}`,
    ...rpErrorContextLines(input.context),
    stack ? '' : '',
    stack ? 'Stack:' : '',
    stack ? stack.slice(0, 2500) : ''
  ].filter(line => line !== null && line !== undefined);
  return {
    automatic,
    ...entityScope,
    errorCode,
    errorSummary: safeMessage.slice(0, 500),
    httpMethod,
    httpStatus: Number.isInteger(httpStatus) ? httpStatus : null,
    message: lines.join('\n').trim().slice(0, 6000),
    page,
    priority: expectedClientResponse ? 'Medium' : automatic ? 'Urgent' : (input.priority || 'High'),
    requestPath,
    source,
    title,
    type: expectedClientResponse ? 'Portal help' : (input.type || 'Bug')
  };
}

async function rpSubmitErrorTicket(input = {}) {
  const payload = rpBuildErrorTicketPayload(input);
  if (payload.message.length < 8) payload.message = 'Unknown application error.';
  const response = await rpApi('/api/support/ticket', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  if (response && response.task && RP && typeof RP.upsertTask === 'function') {
    RP.upsertTask(response.task);
    window.dispatchEvent(new CustomEvent('rp-data-refresh'));
  }
  return response && (response.task || { id: response.id });
}

function rpNotifyErrorWithTicket(input = {}) {
  if (typeof window === 'undefined') return;
  const message = rpErrorMessage(input.error || input.message);
  if (!message) return;
  window.dispatchEvent(new CustomEvent('rp-toast', {
    detail: {
      msg: input.toastMessage || message,
      tone: 'danger',
      errorTicket: {
        ...input,
        error: input.error || message
      }
    }
  }));
}

function ErrorTicketButton({ compact, ...ticketInput }) {
  const { error } = ticketInput;
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const message = rpErrorMessage(error);
  if (!message) return null;
  const submit = async event => {
    event && event.stopPropagation && event.stopPropagation();
    if (busy || created) return;
    setBusy(true);
    setSubmitError('');
    try {
      const task = await rpSubmitErrorTicket(ticketInput);
      setCreated(task || { id: 'created' });
      toast(`Support ticket ${task && task.id ? task.id : 'created'}`, 'ok');
    } catch (err) {
      const next = rpErrorMessage(err) || 'Could not submit support ticket.';
      setSubmitError(next);
      toast(next, 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span style={{ display: 'inline-flex', flexDirection: compact ? 'row' : 'column', alignItems: compact ? 'center' : 'flex-start', gap: compact ? 8 : 6, marginTop: compact ? 0 : 10 }}>
      <button type="button" className="btn btn-sm" disabled={busy || !!created} onClick={submit} style={{ color: created ? 'var(--ok)' : 'var(--danger)' }}>
        <Icon name={created ? 'check' : 'mail'} size={14} />{created ? 'Ticket submitted' : busy ? 'Submitting...' : 'Submit ticket'}
      </button>
      {submitError && <span style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700 }}>{submitError}</span>}
    </span>
  );
}

function ErrorNotice({ children, error, title, context, source, page, style }) {
  const message = children || rpErrorMessage(error);
  if (!message) return null;
  return (
    <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, ...style }}>
      <div>{message}</div>
      <ErrorTicketButton error={error || message} title={title} context={context} source={source} page={page} />
    </div>
  );
}

async function rpOpenInvoicePdf(orderId) {
  const res = await rpFetchProtected(`/api/invoices/pdf?id=${encodeURIComponent(orderId)}`, { headers: { Accept: 'application/pdf' } });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function rpOpenProtectedFile(path) {
  const target = window.open('', '_blank');
  try {
    const res = await rpFetchProtected(path);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (target) target.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (target) target.close();
    throw err;
  }
}

function ProtectedImage({ src, alt = '', style = {}, placeholderIcon = 'camera' }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let nextUrl = '';
    setObjectUrl('');
    setFailed(false);
    if (!src) {
      setFailed(true);
      return () => {};
    }
    rpFetchProtected(src)
      .then(res => res.blob())
      .then(blob => {
        nextUrl = URL.createObjectURL(blob);
        if (active) setObjectUrl(nextUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (nextUrl) URL.revokeObjectURL(nextUrl);
    };
  }, [src]);
  if (objectUrl) return <img src={objectUrl} alt={alt} style={style} />;
  return (
    <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', color: failed ? 'var(--danger)' : 'var(--faint)' }}>
      <Icon name={failed ? 'alert' : placeholderIcon} size={22} />
    </div>
  );
}

function ProtectedImageLightbox({ photo, onClose, onDelete, deleting = false }) {
  useEffect(() => {
    if (!photo) return undefined;
    const onKey = event => {
      if (event.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photo, onClose]);

  if (!photo) return null;
  const label = photo.label || photo.name || 'Customer photo';
  const detail = [photo.locationName || photo.location || photo.category, photo.when].filter(Boolean).join(' - ');
  return (
    <div role="dialog" aria-modal="true" aria-label={label} onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'oklch(0.18 0.03 240 / 0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div onClick={event => event.stopPropagation()} style={{ width: 'min(980px, 100%)', maxHeight: 'calc(100vh - 36px)', display: 'flex', flexDirection: 'column', background: 'var(--surface)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
            {detail && <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</div>}
          </div>
          {onDelete && <button className="btn btn-sm" disabled={deleting} onClick={onDelete} style={{ color: 'var(--danger)', opacity: deleting ? 0.65 : 1 }}><Icon name="trash" size={14} />{deleting ? 'Removing...' : 'Remove'}</button>}
          <button className="btn btn-sm" onClick={onClose} aria-label="Close photo"><Icon name="x" size={15} /></button>
        </div>
        <div style={{ minHeight: 220, background: 'oklch(0.16 0.025 240)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <ProtectedImage src={photo.url} alt={label} style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 160px)', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }} />
        </div>
      </div>
    </div>
  );
}

function rpUploadLabel(file) {
  if (!file) return '';
  const kb = Math.max(1, Math.round(file.size / 1024));
  return `${file.name} · ${kb} KB`;
}

/* ---- Status pill ---- */
const STATUS_MAP = {
  'Available': ['ok'], 'Approved': ['ok'], 'Delivered': ['ok'], 'Paid': ['ok'], 'OK': ['ok'], 'Good': ['ok'], 'Closed': ['ok'], 'Active': ['ok'], 'Healthy': ['ok'],
  'Fulfilled': ['ok'],
  'Allocated': ['info'], 'Picked': ['info'], 'Submitted': ['info'], 'In Transit': ['info'], 'Inbound': ['info'], 'FBA Inbound': ['info'], 'Approved ': ['info'], 'Issued': ['info'], 'Sent': ['info'], 'Partial': ['info'], 'Requested': ['amber-status'], 'Invited': ['warn'], 'Disabled': ['neutral'],
  'Needs Approval': ['warn'], 'Needs Review': ['warn'], 'Missing': ['warn'], 'On Hold': ['warn'], 'QA Hold': ['warn'], 'Pending': ['warn'], 'Draft': ['warn'], 'Ready': ['warn'], 'Unpaid': ['warn'], 'Overdue': ['danger'], 'Delayed': ['warn'], 'Delivery pending': ['warn'], 'Watch': ['warn'], 'Disputed': ['warn'], 'Medium': ['warn'], 'Short': ['warn'],
  'Damaged': ['danger'], 'Lost': ['danger'], 'Cancelled': ['danger'], 'High': ['danger'], 'Hold': ['danger'],
  'Fronted to Rep': ['amber-status'], 'Purchased by Rep': ['info'], 'Prospect': ['neutral'], 'New': ['neutral'], 'Low': ['ok'], 'Not issued': ['neutral'], 'Not invoiced': ['neutral'],
  'To Do': ['neutral'], 'In Progress': ['info'], 'Blocked': ['danger'], 'Done': ['ok'],
  'Urgent': ['danger'], 'Reimbursed': ['ok'], 'Needs Info': ['warn'], 'Rejected': ['danger'],
  'On Track': ['ok'], 'At Risk': ['warn'], 'Behind': ['danger'], 'Hit': ['ok'], 'Ahead': ['info'], 'Public': ['ok'],
};
function statusColors(key) {
  const map = {
    ok: ['var(--ok)', 'var(--ok-bg)'], warn: ['var(--warn)', 'var(--warn-bg)'],
    danger: ['var(--danger)', 'var(--danger-bg)'], info: ['var(--info)', 'var(--info-bg)'],
    'amber-status': ['var(--amber-deep)', 'var(--amber-soft)'], neutral: ['var(--muted)', 'var(--surface-2)'],
  };
  const k = (STATUS_MAP[key] || ['neutral'])[0];
  return map[k] || map.neutral;
}
function Pill({ status, children, dot = true, style }) {
  const [fg, bg] = statusColors(status || children);
  return <span className="pill" style={{ color: fg, background: bg, ...style }}>{dot && <span className="dot" />}{children || status}</span>;
}

const RP_ORDER_TIMELINE_STAGES = ['Submitted', 'Approved', 'Allocated', 'Picked', 'Dispatched', 'Delivered', 'Invoiced'];
const RP_QUICK_SALE_CUSTOMER_ID = 'C-QUICK-CASH-SALES';
const RP_CUSTOMER_TYPES = ['Retailer', 'Consumer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor', 'Direct / Other'];
const RP_BUSINESS_CUSTOMER_TYPES = RP_CUSTOMER_TYPES.filter(type => !/consumer|direct/i.test(type));
const RP_LEAD_TYPES = ['Retailer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor', 'Other'];
function rpOrderFulfillmentStage(status, stages) {
  const value = String(status || '').trim();
  if (['Requested', 'Submitted', 'Needs Approval', 'On Hold'].includes(value)) return 'Submitted';
  if (value === 'Inbound') return 'Allocated';
  if (['Invoiced', 'Paid', 'Closed'].includes(value)) {
    return Array.isArray(stages) && stages.includes('Invoiced') ? 'Invoiced' : 'Delivered';
  }
  return value || 'Submitted';
}
const RP_SALES_EDITABLE_ORDER_STATUSES = new Set(['Draft', 'Requested', 'Submitted', 'Submitted / Requested', 'Needs Approval', 'On Hold', 'Approved', 'Allocated']);
function rpOrderInvoiceLocked(order) {
  if (!order || typeof order !== 'object') return false;
  const invoiceNumber = String(order.invoiceNumber || order.invoice_number || '').trim();
  const invoiceIssuedAt = String(order.invoiceIssuedAt || order.invoice_issued_at || '').trim();
  const invoiceStatus = String(order.invoiceStatus || order.invoice_status || '').trim().toLowerCase();
  return Boolean(invoiceNumber || invoiceIssuedAt || (invoiceStatus && !['not invoiced', 'draft'].includes(invoiceStatus)));
}
function rpOrderEditableBySales(orderOrStatus) {
  if (orderOrStatus && typeof orderOrStatus === 'object' && rpOrderInvoiceLocked(orderOrStatus)) return false;
  const status = String(orderOrStatus && typeof orderOrStatus === 'object' ? orderOrStatus.status : orderOrStatus || '').trim();
  return RP_SALES_EDITABLE_ORDER_STATUSES.has(status);
}
function rpCustomerAccountKind(customer) {
  if (!customer || typeof customer !== 'object') return 'business';
  const shopifyCompanyId = String(customer.shopifyCompanyId || customer.shopify_company_id || '').trim();
  const type = String(customer.type || customer.customerType || customer.customer_type || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const tier = String(customer.tier || '').trim().toLowerCase();
  if (shopifyCompanyId || type === 'shopify company') return 'business';
  if (type === 'shopify' || /\bconsumer\b|\bdirect\b/.test(`${type} ${tier}`)) return 'consumer';
  return 'business';
}
function rpIsConsumerCustomer(customer) {
  return rpCustomerAccountKind(customer) === 'consumer';
}
function rpIsQuickSaleCustomer(customerOrId) {
  const id = String(customerOrId && typeof customerOrId === 'object' ? customerOrId.id : customerOrId || '').trim();
  if (id === RP_QUICK_SALE_CUSTOMER_ID) return true;
  if (!customerOrId || typeof customerOrId !== 'object') return false;
  const label = String(customerOrId.name || customerOrId.displayName || customerOrId.display_name || customerOrId.legalName || customerOrId.legal_name || '').trim();
  return /^quick cash sales?$/i.test(label);
}
function rpCustomerIdentityValues(customer, key) {
  if (!customer || typeof customer !== 'object') return [];
  const rows = Array.isArray(customer[key]) ? customer[key] : [];
  return rows.map(row => String(row && (row.value || row.email || row.phone || row.number) || '').trim()).filter(Boolean);
}
function rpCustomerContactIdentityValues(customer) {
  if (!customer || typeof customer !== 'object') return [];
  const contacts = Array.isArray(customer.contacts) ? customer.contacts : [];
  return contacts.flatMap(contact => [
    contact && contact.email,
    contact && contact.phone,
    contact && contact.phoneNumber,
    contact && contact.phone_number
  ].map(value => String(value || '').trim()).filter(Boolean));
}
function rpCustomerHasContactIdentity(customer) {
  if (!customer || typeof customer !== 'object') return false;
  return Boolean(
    String(customer.email || customer.buyerEmail || customer.buyer_email || '').trim()
      || String(customer.phone || '').trim()
      || rpCustomerIdentityValues(customer, 'emailAddresses').length
      || rpCustomerIdentityValues(customer, 'email_addresses').length
      || rpCustomerIdentityValues(customer, 'phoneNumbers').length
      || rpCustomerIdentityValues(customer, 'phone_numbers').length
      || rpCustomerContactIdentityValues(customer).length
  );
}
function rpIsAnonymousShopifyCustomer(customer) {
  if (!customer || typeof customer !== 'object') return false;
  if (rpIsQuickSaleCustomer(customer)) return false;
  const id = String(customer.id || '').trim();
  const name = String(customer.name || customer.displayName || customer.display_name || customer.legalName || customer.legal_name || '').trim();
  const type = String(customer.type || customer.customerType || customer.customer_type || '').trim();
  const hasShopifyAccount = Boolean(String(customer.shopifyCustomerId || customer.shopify_customer_id || customer.shopifyCompanyId || customer.shopify_company_id || '').trim());
  const taggedHidden = Boolean(customer.onboardingQuestionnaire && customer.onboardingQuestionnaire.hiddenAnonymousShopifyPos);
  return !hasShopifyAccount
    && !rpCustomerHasContactIdentity(customer)
    && (/^SHOP-GUEST-/i.test(id) || /^shopify\s+#?\d+$/i.test(name) || taggedHidden)
    && (!type || /^shopify$/i.test(type));
}
function rpVisibleCustomers(customers = RP.CUSTOMERS) {
  return (customers || []).filter(customer => !rpIsQuickSaleCustomer(customer) && !rpIsAnonymousShopifyCustomer(customer));
}
function rpOrderIsQuickSale(order) {
  return Boolean(order && (order.quickSale || order.quick_sale || rpIsQuickSaleCustomer(order.cust || order.customerId || order.customer_id)));
}
function rpOrderShopifyReference(order) {
  if (!order) return '';
  const po = String(order.po || order.poNumber || order.po_number || '').trim();
  if (/^#?\d+$/.test(po)) return `Shopify ${po.startsWith('#') ? po : `#${po}`}`;
  const text = [order.reason, order.note].map(value => String(value || '')).filter(Boolean).join('\n');
  const match = text.match(/Imported from Shopify(?: POS)? order\s+(#[A-Za-z0-9-]+)/i);
  return match ? `Shopify ${match[1]}` : '';
}
function rpOrderCustomerDisplayName(order, customer) {
  const shopifyReference = rpOrderIsQuickSale(order) ? rpOrderShopifyReference(order) : '';
  if (shopifyReference) return shopifyReference;
  return String(customer && (customer.name || customer.displayName || customer.display_name || customer.legalName || customer.legal_name) || '').trim() || 'Customer';
}
function rpOrderReasonLabel(order) {
  const raw = String((order && (order.reason || order.note)) || '').trim();
  if (!raw || raw === 'Live order' || raw === 'Ops-entered') return '';
  const firstLine = raw.split(/\n+/).map(line => line.trim()).find(Boolean) || '';
  const cleaned = firstLine
    .replace(/\s*\[\d{4}-\d{2}-\d{2}T[\s\S]*$/, '')
    .replace(/\s*Payment recorded:[\s\S]*$/i, '')
    .trim();
  return cleaned || '';
}
function rpOrderReasonDisplay(order, fallback = '') {
  const label = rpOrderReasonLabel(order);
  if (label) return label;
  const raw = String((order && (order.reason || order.note)) || '').trim();
  if (!raw) return fallback;
  if (raw === 'Live order' || raw === 'Ops-entered') return raw;
  return /\[\d{4}-\d{2}-\d{2}T|Payment recorded:/i.test(raw) ? fallback : raw;
}
function rpOrderTimelineDate(value) {
  if (!value) return 'Pending';
  return rpFormatLocalDateTime(value, { empty: 'Pending' }) || String(value).slice(0, 16);
}
function rpOrderActor(value) {
  const text = String(value || '').trim();
  if (!text) return 'Ranch Power';
  if (!text.includes('@')) return text;
  return text.split('@')[0].split(/[._-]/).filter(Boolean).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ') || text;
}
function rpOrderEventTitle(event, order) {
  const action = String(event.action || '');
  const after = event.after || {};
  if (action === 'order.submit') return after.status === 'Requested' ? 'Order requested' : 'Order submitted';
  if (action === 'order.update') return 'Order edited';
  if (action === 'order.status.update') return `Moved to ${after.status || order.status || 'next status'}`;
  return 'Order activity';
}
function rpOrderEventDetail(event) {
  const action = String(event.action || '');
  const after = event.after || {};
  const before = event.before || {};
  if (action === 'order.submit') {
    const parts = [];
    if (after.mode === 'shopify-draft-order' || after.draftOrderId) parts.push('Shopify draft order created');
    if (after.total != null) parts.push(`Total ${RP.money(Number(after.total || 0))}`);
    return parts.join(' - ');
  }
  if (action === 'order.update') {
    const parts = [];
    if (after.lineCount != null) parts.push(`${after.lineCount} line${Number(after.lineCount) === 1 ? '' : 's'}`);
    if (after.deliveryDate && after.deliveryDate !== before.deliveryDate) parts.push(`Delivery ${after.deliveryDate}`);
    if (after.shopifyDraftSynced) parts.push('Shopify draft synced');
    if (after.total != null && after.total !== before.total) parts.push(`Total ${RP.money(Number(after.total || 0))}`);
    return parts.join(' - ') || 'Order details updated';
  }
  if (action === 'order.status.update') {
    const parts = [];
    if (before.status && after.status && before.status !== after.status) parts.push(`${before.status} to ${after.status}`);
    if (after.note) parts.push(after.note);
    return parts.join(' - ');
  }
  return '';
}
function rpOrderTimelineFromNotes(order) {
  const note = String(order.note || '');
  const matches = [];
  note.split(/\n+/).forEach(line => {
    const m = line.match(/^\[([^\]]+)\]\s+(.+?)\s+set status to\s+([^:]+):\s*(.*)$/i);
    if (!m) return;
    matches.push({
      title: `Moved to ${m[3].trim()}`,
      detail: m[4].trim(),
      actor: m[2].trim(),
      at: m[1].trim(),
      tone: m[3].trim() === 'Cancelled' ? 'danger' : 'ok'
    });
  });
  return matches;
}
function rpOrderTimeline(order, opts = {}) {
  if (!order) return [];
  const entries = (order.events || []).map(event => ({
    title: rpOrderEventTitle(event, order),
    detail: rpOrderEventDetail(event),
    actor: rpOrderActor(event.actorEmail || event.actorId),
    at: event.createdAt,
    status: event.after && event.after.status,
    tone: event.action === 'order.status.update' && event.after && event.after.status === 'Cancelled' ? 'danger' : (event.action === 'order.update' ? 'info' : 'ok')
  }));

  const noteEntries = rpOrderTimelineFromNotes(order);
  if (!entries.length && noteEntries.length) entries.push(...noteEntries);

  const status = String(order.status || 'Submitted').trim();
  if (!entries.length) {
    entries.push({
      title: status === 'Requested' ? 'Order requested' : 'Order submitted',
      detail: order.shopifyDraftOrderId ? 'Shopify draft order linked' : '',
      actor: order.rep || 'Ranch Power',
      at: order.created,
      tone: 'ok'
    });
  }

  if (status && !['Requested', 'Submitted'].includes(status) && !entries.some(entry => entry.status === status)) {
    entries.push({
      title: `Current status: ${status}`,
      detail: status === 'Cancelled' ? 'Order was cancelled before fulfillment completed' : '',
      actor: 'Ranch Power',
      at: order.updated || order.created,
      status,
      tone: status === 'Cancelled' ? 'danger' : status === 'On Hold' || status === 'Needs Approval' ? 'warn' : 'info'
    });
  }

  const sorted = entries
    .filter(Boolean)
    .map((entry, idx) => ({
      ...entry,
      key: `${entry.at || 'pending'}-${entry.title}-${idx}`,
      when: rpOrderTimelineDate(entry.at)
    }))
    .sort((a, b) => {
      const at = new Date(a.at || 0).getTime();
      const bt = new Date(b.at || 0).getTime();
      return (Number.isNaN(at) ? 0 : at) - (Number.isNaN(bt) ? 0 : bt);
    });
  return opts.newestFirst ? sorted.reverse() : sorted;
}

/* ---- Flavor chip / swatch ---- */
function FlavorChip({ flavorId, sku, size = 26, ring = true }) {
  const visual = rpSkuVisualMeta(sku || flavorId);
  if (!visual.label) return <span style={{ width: size, height: size, borderRadius: 6, background: 'var(--surface-2)', display: 'inline-block', flexShrink: 0 }} />;
  return (
    <span title={visual.name} style={{ position: 'relative', width: size, height: size, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: visual.color, color: visual.textColor, fontFamily: 'var(--ff-display)', fontWeight: 700, fontSize: size * 0.42, letterSpacing: '0.02em', boxShadow: ring ? 'inset 0 0 0 1px rgba(255,255,255,.25)' : 'none', flexShrink: 0 }}>{visual.label}</span>
  );
}
function CanThumb({ flavorId, sku, h = 54 }) {
  const visual = rpSkuVisualMeta(sku || flavorId);
  return <img src={RP.can(visual.flavorId || 'blue-raspberry')} alt="" style={{ height: h, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(20,40,60,.18))' }} />;
}

/* ---- KPI card ---- */
function KPICard({ label, value, unit, delta, icon, accent, spark, onClick, active }) {
  const up = delta != null && delta >= 0;
  return (
    <div className="card rp-kpi-card" onClick={onClick} style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10, cursor: onClick ? 'pointer' : 'default', position: 'relative', overflow: 'hidden', borderColor: active ? (accent || 'var(--amber)') : undefined, boxShadow: active ? '0 0 0 1px ' + (accent || 'var(--amber)') + ' inset' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span className="eyebrow nowrap rp-kpi-label">{label}</span>
        {icon && <span style={{ color: accent || 'var(--muted)' }}><Icon name={icon} size={17} /></span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
        <span className="disp nowrap rp-kpi-value" style={{ fontSize: 34, color: 'var(--ink)' }}>{value}</span>
        {unit && <span className="nowrap rp-kpi-unit" style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{unit}</span>}
      </div>
      {(delta != null || spark) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {delta != null && <span style={{ fontSize: 12, fontWeight: 700, color: up ? 'var(--ok)' : 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Icon name={up ? 'trend' : 'trend'} size={13} style={{ transform: up ? 'none' : 'scaleY(-1)' }} />{up ? '+' : ''}{delta}%</span>}
          {spark && <Sparkline data={spark} color={accent || 'var(--amber)'} w={70} h={22} />}
        </div>
      )}
    </div>
  );
}

/* ---- Sparkline + bar/line helpers ---- */
function Sparkline({ data, color = 'var(--amber)', w = 120, h = 32, fill = true }) {
  const max = Math.max(...data), min = Math.min(...data);
  const rng = max - min || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - ((v - min) / rng) * (h - 4) - 2]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ` L${w} ${h} L0 ${h} Z`;
  const gid = 'sg' + Math.round(w + h + data[0]);
  return (
    <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
      {fill && <><defs><linearGradient id={gid} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={color} stopOpacity="0.20" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs><path d={area} fill={`url(#${gid})`} /></>}
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.6" fill={color} />
    </svg>
  );
}

/* ---- Avatar ---- */
function Avatar({ name, size = 30, color = 'var(--navy)' }) {
  const init = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return <span style={{ width: size, height: size, borderRadius: '50%', background: color, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, flexShrink: 0, fontFamily: 'var(--ff-ui)' }}>{init}</span>;
}

/* ---- Auth/profile helpers ---- */
function rpAuthProfile() {
  return (window.RP_AUTH && window.RP_AUTH.profile) || (window.RP_ACCOUNT && window.RP_ACCOUNT.profile) || {};
}
function rpAuthDetails() {
  return (window.RP_AUTH && window.RP_AUTH.auth) || (window.RP_ACCOUNT && window.RP_ACCOUNT.auth) || {};
}
function rpIsDemoMode() {
  return !!(window.RP_AUTH && window.RP_AUTH.demo);
}
function rpHasLiveAuth() {
  return !!(window.RP_AUTH && !window.RP_AUTH.demo && rpAuthProfile());
}
function rpNormalizeRole(role) {
  const normalized = String(role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const aliases = {
    administrator: 'admin',
    admins: 'admin',
    superadmin: 'admin',
    super_admin: 'admin',
    system_admin: 'admin',
    operations: 'ops',
    operations_manager: 'ops',
    founder: 'leadership',
    owner: 'leadership',
    sales_rep: 'rep',
    sales: 'rep',
    dispatcher: 'dispatch',
    accounting: 'accountant',
    finance: 'accountant',
    client_account: 'customer',
    client: 'customer'
  };
  return aliases[normalized] || normalized;
}
function rpCanInviteUsers() {
  const profile = rpAuthProfile();
  return String(profile.email || '').trim().toLowerCase() === 'darren@ranchpower.com';
}
function rpProfileFeatureFlags(profile) {
  const p = profile || rpAuthProfile();
  const raw = p.featureFlags || p.feature_flags || {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, value === true || value === 'true' || value === 1 || value === '1']));
}
function rpProfileHasFeature(profile, key) {
  return rpProfileFeatureFlags(profile)[key] === true;
}
function rpCanDeleteDuplicateLead(lead, profile = rpAuthProfile()) {
  if (!lead) return false;
  if (!rpHasLiveAuth()) return true;
  const role = rpNormalizeRole(profile && profile.role);
  if (['admin', 'leadership', 'ops', 'sales_manager'].includes(role)) return true;
  if (!['rep', 'partner'].includes(role)) return false;
  const actorId = String(profile && profile.id || '').trim();
  // Only compare stable account IDs. `lead.rep` is a display label in the live
  // bootstrap payload and must never be interpreted as delete authority.
  const assignedRepId = String(lead.assignedRepId || lead.assigned_rep_id || lead.repId || '').trim();
  return Boolean(actorId && assignedRepId === actorId);
}
function rpRetailAccountDefaultPriceListName(profile) {
  const p = profile || rpAuthProfile();
  const requested = String(p.retailQuoteDefaultPriceListName || p.retail_quote_default_price_list_name || 'Default Retail').trim();
  const approved = ['default retail', 'slopeside retail'].includes(retailPriceListIdentity(rpPriceListByName(requested) || requested)) ? requested : 'Default Retail';
  return findRetailPriceList(RP.PRICE_LISTS || [], approved)?.name || approved;
}
function rpRetailAccountNewPriceListName(value, profile) {
  const requested = String(value || '').trim();
  return ['default retail', 'slopeside retail'].includes(retailPriceListIdentity(rpPriceListByName(requested) || requested))
    ? findRetailPriceList(RP.PRICE_LISTS || [], requested)?.name || requested
    : rpRetailAccountDefaultPriceListName(profile);
}
function rpCanSetCustomAccountPricing(profile) {
  const p = profile || rpAuthProfile();
  const role = rpNormalizeRole(p.role);
  return ['admin', 'leadership', 'ops', 'sales_manager'].includes(role)
    || p.retailQuoteCustomPricingEnabled === true
    || p.retail_quote_custom_pricing_enabled === true;
}
function rpCanManageCustomerAccountPricing(profile) {
  const p = profile || rpAuthProfile();
  return ['admin', 'leadership', 'ops', 'sales_manager'].includes(rpNormalizeRole(p.role));
}
function rpExistingCustomerPriceListOptionNames({ currentName = '', excludeConsumer = true } = {}) {
  const current = String(currentName || '').trim();
  if (!rpCanManageCustomerAccountPricing()) return [current || rpDefaultPriceListName()];
  return rpPriceListOptionNames({ currentName: current, excludeConsumer });
}
function rpAccountPriceListOptionNames({ currentName = '', excludeConsumer = true } = {}) {
  const defaultName = rpRetailAccountDefaultPriceListName();
  if (!rpCanSetCustomAccountPricing()) return [String(currentName || '').trim() || defaultName];
  const current = String(currentName || '').trim();
  const approvedNames = rpPriceListOptionNames({ excludeConsumer })
    .filter(name => ['default retail', 'slopeside retail'].includes(retailPriceListIdentity(rpPriceListByName(name) || name)));
  if (!approvedNames.some(name => retailPriceListIdentity(rpPriceListByName(name) || name) === 'default retail')) approvedNames.unshift('Default Retail');
  // Existing accounts may retain a historical tier while unrelated details
  // are edited. New forms start on an approved name, so old tiers never leak
  // into a new-account picker.
  if (current && !approvedNames.some(name => name.toLowerCase() === current.toLowerCase())) approvedNames.push(current);
  return approvedNames;
}
function rpDashboardWorkflowIntelligenceEnabled(role) {
  if (window.RP_AUTH && window.RP_AUTH.developerData) return true;
  return rpProfileHasFeature(rpAuthProfile(), 'dashboardWorkflowIntelligence');
}
function rpCustomerInProfileScope(profile, customer) {
  if (!profile || !customer) return false;
  if (profile.id && customer.repId && String(profile.id) === String(customer.repId)) return true;
  const profileName = String(profile.display_name || profile.name || rpNameFromEmail(profile.email)).trim().toLowerCase();
  const customerRep = String(customer.rep || '').trim().toLowerCase();
  if (profileName && customerRep && profileName === customerRep) return true;
  return rpAreaMatches(profile.territory, customer.territory);
}
function rpCanSendCustomerPortalAccess(customer) {
  const profile = rpAuthProfile();
  const role = rpNormalizeRole(profile.role);
  if (rpCanInviteUsers()) return true;
  if (['admin', 'leadership', 'ops', 'sales_manager'].includes(role)) return true;
  if (!customer || !['rep', 'partner'].includes(role)) return false;
  if (!rpCustomerInProfileScope(profile, customer)) return false;
  if (rpIsConsumerCustomer(customer)) return true;
  return rpProfileHasFeature(profile, 'sendRetailPortalInvites');
}
function rpCanApproveCustomers(profile, customer) {
  const p = profile || rpAuthProfile();
  const role = rpNormalizeRole(p.role);
  if (['admin', 'leadership', 'ops', 'sales_manager'].includes(role)) return true;
  if (!['rep', 'partner'].includes(role) || !rpProfileHasFeature(p, 'approveRetailCustomers')) return false;
  if (!customer) return true;
  return !rpIsConsumerCustomer(customer) && rpCustomerInProfileScope(p, customer);
}
function rpCanEditCustomerProfile(customer, profile) {
  const p = profile || rpAuthProfile();
  const role = rpNormalizeRole(p.role);
  if (['admin', 'leadership', 'ops', 'sales_manager'].includes(role)) return true;
  if (!customer || !['rep', 'partner'].includes(role)) return false;
  return rpCustomerInProfileScope(p, customer);
}
function rpCanIssueCustomerInvoices(customer, profile) {
  const p = profile || rpAuthProfile();
  const role = rpNormalizeRole(p.role);
  if (['admin', 'leadership', 'ops', 'sales_manager', 'accountant'].includes(role)) return true;
  return Boolean(customer && ['rep', 'partner'].includes(role) && rpCustomerInProfileScope(p, customer));
}
function rpCustomerOrderReady(customer) {
  return ['Active', 'Approved'].includes(String(customer && customer.status || '').trim());
}
function rpCustomerQuoteProspect(customer) {
  const questionnaire = customer && (customer.onboardingQuestionnaire || customer.onboarding_questionnaire);
  return String(customer && customer.status || '').trim() === 'Prospect'
    && questionnaire
    && typeof questionnaire === 'object'
    && questionnaire.quickQuoteProspect === true;
}
function rpCustomerPortalInviteReady(customer) {
  const submittedAt = String(customer && (customer.onboardingSubmittedAt || customer.onboarding_submitted_at) || '').trim();
  return rpCustomerOrderReady(customer) || (rpCustomerQuoteProspect(customer) && !submittedAt);
}
function rpDeveloperDataPreference() {
  if (typeof window.RP_DEVELOPER_DATA_ENABLED === 'function') return window.RP_DEVELOPER_DATA_ENABLED();
  try {
    return window.localStorage.getItem('rp:developer-data') === '1' || window.sessionStorage.getItem('rp:developer-data') === '1';
  } catch {
    return false;
  }
}
function rpSetDeveloperDataPreference(enabled) {
  if (typeof window.RP_SET_DEVELOPER_DATA_PREFERENCE === 'function') return window.RP_SET_DEVELOPER_DATA_PREFERENCE(enabled);
  try {
    if (enabled) window.localStorage.setItem('rp:developer-data', '1');
    else {
      window.localStorage.removeItem('rp:developer-data');
      window.sessionStorage.removeItem('rp:developer-data');
    }
    return true;
  } catch {
    return false;
  }
}
function rpCanUseDeveloperData() {
  if (typeof window.RP_CAN_USE_DEVELOPER_DATA === 'function') return window.RP_CAN_USE_DEVELOPER_DATA();
  const profile = (window.RP_AUTH && (window.RP_AUTH.liveProfile || window.RP_AUTH.profile)) || rpAuthProfile();
  const email = String(profile.email || '').trim().toLowerCase();
  const role = rpNormalizeRole(profile.role);
  return email === 'darren@ranchpower.com' || role === 'admin';
}
function rpSwitchDeveloperData(enabled) {
  rpSetDeveloperDataPreference(enabled);
  const url = new URL(window.location.href);
  ['devData', 'developerData', 'demoData'].forEach(key => url.searchParams.delete(key));
  if (enabled) url.searchParams.set('devData', '1');
  window.location.assign(url.toString());
}
function rpCustomerContacts(c) {
  return Array.isArray(c && c.contacts) ? c.contacts.filter(x => x && (x.name || x.email || x.phone)) : [];
}
function rpPrimaryCustomerPhone(c) {
  const phones = Array.isArray(c && c.phoneNumbers) ? c.phoneNumbers : [];
  const primary = phones.find(x => x && x.primary) || phones[0];
  return (c && c.phone) || (primary && primary.value) || '';
}
function rpSecondaryCustomerContact(c) {
  return rpCustomerContacts(c).find(x => !x.primary) || {};
}
function rpActiveApprovalRule(id) {
  return (RP.APPROVAL_RULES || []).find(rule => rule && rule.id === id && rule.active !== false) || null;
}
function rpDiscountPercent(value) {
  const match = String(value || '').match(/(\d+(?:\.\d+)?)\s*%/);
  if (match) {
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
}
function rpCustomerOnCreditHold(customer) {
  const values = [customer && customer.credit, customer && customer.status]
    .map(value => String(value || '').trim().toLowerCase())
    .filter(Boolean);
  return values.some(value => ['hold', 'on hold', 'credit hold', 'credit_hold', 'watch', 'suspended', 'blocked'].includes(value));
}
function rpOrderApprovalPreview(customer, discountLabel, baseStatus) {
  const reasons = [];
  let route = null;
  let status = baseStatus || 'Requested';
  const creditRule = rpActiveApprovalRule('CREDIT_HOLD_CUSTOMER');
  const discountRule = rpActiveApprovalRule('DISCOUNT_ABOVE_10');
  const discountPercent = rpDiscountPercent(discountLabel);

  if (creditRule && rpCustomerOnCreditHold(customer)) {
    status = 'On Hold';
    route = creditRule.route || 'Operations';
    reasons.push('Customer credit hold');
  }

  if (discountRule && discountPercent > 10) {
    if (!route) route = discountRule.route || 'Sales Manager';
    if (status !== 'On Hold') status = 'Needs Approval';
    reasons.push(`Discount over threshold (${discountPercent}%)`);
  }

  return {
    discountPercent,
    reason: reasons.join('; '),
    required: reasons.length > 0,
    route,
    status
  };
}
function rpNameFromEmail(email) {
  const local = String(email || '').split('@')[0] || '';
  return local.split(/[._-]+/).filter(Boolean).map(x => x.charAt(0).toUpperCase() + x.slice(1)).join(' ');
}
function rpDisplayName(fallback) {
  const p = rpAuthProfile();
  return p.display_name || rpNameFromEmail(p.email) || fallback || 'Ranch Power';
}
function rpRoleLabel(role) {
  const map = {
    admin: 'Administrator',
    leadership: 'Leadership',
    ops: 'Operations',
    sales_manager: 'Sales Manager',
    rep: 'Sales Rep',
    partner: 'Partner',
    warehouse: 'Warehouse',
    dispatch: 'Dispatcher',
    accountant: 'Accountant',
    marketing: 'Marketing',
    customer: 'Customer'
  };
  const key = rpNormalizeRole(role);
  return map[key] || (key ? key.split('_').map(x => x.charAt(0).toUpperCase() + x.slice(1)).join(' ') : 'User');
}
const RP_TASK_PLACEHOLDER_NAMES = new Set(['Dana Whitfield', 'Sam Okoro', 'Marco Ferreira', 'J. Park', 'L. Tran', 'A. Reyes', 'C. Hale', 'Accounting']);
function rpTaskOwnerLabel(owner) {
  const text = String(owner || '').trim();
  return rpHasLiveAuth() && RP_TASK_PLACEHOLDER_NAMES.has(text) ? 'Unassigned' : text;
}
const RP_GLOBAL_AREA_KEYS = new Set(['all', 'all / unassigned', 'all/unassigned', 'national', 'shared', 'unassigned']);
function rpAreaNames(value) {
  if (Array.isArray(value)) return value.flatMap(rpAreaNames);
  if (value && typeof value === 'object') {
    return rpAreaNames(value.territories || value.responsibilityAreas || value.responsibility_areas || value.territory || value.area);
  }
  return String(value || '')
    .split(/\s*(?:\||;|,|\u00b7|\u2022)\s*/)
    .map(item => item.trim())
    .filter(Boolean);
}
function rpProfileAreas(profile) {
  return rpAreaNames(profile || rpAuthProfile()).filter(area => !RP_GLOBAL_AREA_KEYS.has(rpTerritoryKey(area)));
}
function rpAreaLabel(value, fallback = 'Unassigned') {
  const areas = rpAreaNames(value).filter(Boolean);
  return areas.length ? areas.join(', ') : fallback;
}
function rpAreaMatches(areas, territory) {
  const target = rpTerritoryKey(territory);
  if (!target) return false;
  const list = rpAreaNames(areas);
  if (!list.length) return false;
  return list.some(area => {
    const key = rpTerritoryKey(area);
    return key === target || RP_GLOBAL_AREA_KEYS.has(key);
  });
}
const RP_DEFAULT_TIME_ZONE = 'America/Vancouver';
const RP_TERRITORY_TIME_ZONES = [
  { match: /dawson|fort st\.?\s*john|fort saint john|charlie lake|peace/i, zone: 'America/Dawson_Creek' },
  { match: /calgary|alberta|edmonton|yyc/i, zone: 'America/Edmonton' },
  { match: /kamloops|kelowna|squamish|whistler|vancouver|lower mainland|fraser|british columbia|\bbc\b|northern bc/i, zone: 'America/Vancouver' }
];
function rpValidTimeZone(value) {
  const zone = String(value || '').trim();
  if (!zone) return '';
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(new Date());
    return zone;
  } catch {
    return '';
  }
}
function rpTimeZoneForArea(value) {
  const text = rpAreaNames(value).join(' ') || String(value || '');
  if (!text) return '';
  const match = RP_TERRITORY_TIME_ZONES.find(row => row.match.test(text));
  return match ? match.zone : '';
}
function rpBusinessTimeZone(source) {
  const src = source && typeof source === 'object' && !Array.isArray(source) ? source : {};
  const explicit = rpValidTimeZone(src.timeZone || src.time_zone || src.timezone || (typeof window !== 'undefined' ? window.RP_TIME_ZONE : ''));
  if (explicit) return explicit;
  const profile = rpAuthProfile();
  const profileZone = rpValidTimeZone(profile.timeZone || profile.time_zone || profile.timezone);
  if (profileZone) return profileZone;
  return rpTimeZoneForArea(src.territory || src.area || src.city || src.location || profile.territory || profile.responsibilityAreas || profile.responsibility_areas) || RP_DEFAULT_TIME_ZONE;
}
function rpDatePartsInZone(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone || RP_DEFAULT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  return Object.fromEntries(formatter.formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type, part.value]));
}
function rpYmdFromParts(parts) {
  return `${parts.year}-${parts.month}-${parts.day}`;
}
function rpFormatUtcYmd(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}
function rpTodayYmd(source, offsetDays = 0) {
  const parts = rpDatePartsInZone(new Date(), rpBusinessTimeZone(source));
  const date = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + Number(offsetDays || 0)));
  return rpFormatUtcYmd(date);
}
function rpAddDaysYmd(value, days = 0, source) {
  const text = String(value || '').trim();
  let parts = null;
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) parts = { year: match[1], month: match[2], day: match[3] };
  if (!parts) {
    const parsed = rpParseDateTime(text) || new Date();
    parts = rpDatePartsInZone(parsed, rpBusinessTimeZone(source));
  }
  const date = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + Number(days || 0)));
  return rpFormatUtcYmd(date);
}
function rpNowLocalInput(source) {
  const parts = rpDatePartsInZone(new Date(), rpBusinessTimeZone(source));
  return `${rpYmdFromParts(parts)} ${parts.hour}:${parts.minute}`;
}
function rpParseDateTime(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function rpFormatLocalDateTime(value, options = {}) {
  const parsed = rpParseDateTime(value);
  if (!parsed) return options.empty ?? '';
  const dateOptions = {
    timeZone: rpBusinessTimeZone(options.source),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  };
  if (options.year) dateOptions.year = 'numeric';
  return new Intl.DateTimeFormat('en-CA', dateOptions).format(parsed);
}
function rpFormatLocalDate(value, options = {}) {
  const parsed = rpParseDateTime(value);
  if (!parsed) return options.empty ?? '';
  const dateOptions = {
    timeZone: rpBusinessTimeZone(options.source),
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  return new Intl.DateTimeFormat('en-CA', dateOptions).format(parsed);
}
function rpProfileDescriptor(profile) {
  const p = profile || rpAuthProfile();
  const areas = rpProfileAreas(p);
  if (areas.length) return areas.join(', ');
  if (p.territory) return rpAreaLabel(p.territory);
  if (p.customer_id) return 'Customer account';
  return 'Ranch Power';
}
function rpAccountStatus(profile, auth) {
  const p = profile || rpAuthProfile();
  const a = auth || rpAuthDetails();
  if (!rpHasLiveAuth()) return 'Demo profile';
  if (p.active === false) return 'Disabled';
  if (!p.profile_completed_at) return 'Setup needed';
  if (a.lastSignInAt) return 'Signed in';
  if (a.emailConfirmedAt) return 'Accepted';
  if (a.invitedAt) return 'Invited';
  return 'Active';
}
function rpFormatAccountTime(value) {
  const text = String(value || '').trim();
  if (!text) return 'Not recorded';
  return rpFormatLocalDateTime(text, { empty: text });
}
function rpCurrentPersona(role) {
  const fallback = (window.RP && RP.ROLES && RP.ROLES.find(r => r.id === role)) || {};
  if (!rpHasLiveAuth()) {
    return {
      name: fallback.who || 'Ranch Power',
      email: '',
      role: fallback.name || rpRoleLabel(role),
      desc: fallback.desc || rpProfileDescriptor({}),
      live: false
    };
  }
  const p = rpAuthProfile();
  return {
    name: rpDisplayName(),
    email: p.email || '',
    role: rpRoleLabel(p.role || role),
    desc: rpProfileDescriptor(p),
    live: true
  };
}
function useRpIdentity(role) {
  const [, setIdentityTick] = useState(0);
  useEffect(() => {
    const refresh = () => setIdentityTick(x => x + 1);
    window.addEventListener('rp-auth-profile-change', refresh);
    window.addEventListener('rp-data-refresh', refresh);
    return () => {
      window.removeEventListener('rp-auth-profile-change', refresh);
      window.removeEventListener('rp-data-refresh', refresh);
    };
  }, []);
  return rpCurrentPersona(role);
}
function useRpPagedRows(rows, pageSize = 75, resetKey = '') {
  const [page, setPage] = useState(1);
  const safeRows = Array.isArray(rows) ? rows : [];
  const size = Math.max(1, Number(pageSize) || 75);
  const total = safeRows.length;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const start = total ? (safePage - 1) * size : 0;
  const end = Math.min(total, start + size);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  return {
    end,
    page: safePage,
    pageSize: size,
    rows: safeRows.slice(start, end),
    setPage,
    start,
    total,
    totalPages
  };
}
const RP_SERVER_PAGED_FILTER_PARAMS = new Set([
  'area',
  'customerType',
  'delivery',
  'fulfillment',
  'invoice',
  'payMethod',
  'payment',
  'rep',
  'status',
  'terms',
  'territory'
]);
const RP_SERVER_PAGED_METADATA_CACHE = new Map();
function rpServerPagedText(value) {
  return value == null ? '' : String(value).trim();
}
function rpServerPagedOrder(item) {
  if (!item || typeof item !== 'object') return null;
  return item.order && typeof item.order === 'object' ? item.order : item;
}
function rpServerPagedMergeCustomerValue(existing, incoming) {
  if (incoming == null || (typeof incoming === 'string' && !incoming.trim())) return existing;
  if (Array.isArray(incoming)) return incoming.length ? incoming : existing;
  if (typeof incoming === 'object') {
    const entries = Object.entries(incoming);
    if (!entries.length) return existing;
    const base = existing && typeof existing === 'object' && !Array.isArray(existing) ? existing : {};
    return entries.reduce((merged, [key, value]) => {
      merged[key] = rpServerPagedMergeCustomerValue(base[key], value);
      return merged;
    }, { ...base });
  }
  return incoming;
}
function rpUpsertServerPagedCustomer(customer) {
  if (!customer || typeof customer !== 'object' || !rpServerPagedText(customer.id)) return null;
  const existing = typeof RP.cust === 'function' ? RP.cust(customer.id) : null;
  const merged = rpServerPagedMergeCustomerValue(existing || {}, customer);
  const persisted = { ...merged, id: customer.id, skipCreds: true, persisted: true };
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const role = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role) : String(profile.role || '').trim().toLowerCase();
  if (role === 'customer') delete persisted.note;
  const saved = typeof RP.upsertCustomer === 'function' ? RP.upsertCustomer(persisted) : persisted;
  if (role === 'customer' && saved) delete saved.note;
  return saved;
}
function rpCacheServerPagedItem(item) {
  if (!item || typeof item !== 'object') return item;
  const customer = rpUpsertServerPagedCustomer(item.customer);
  return customer ? { ...item, customer } : item;
}
function rpMergeServerPagedItems(current, incoming) {
  const merged = [];
  const indexes = new Map();
  [...(Array.isArray(current) ? current : []), ...(Array.isArray(incoming) ? incoming : [])].forEach((item, index) => {
    const order = rpServerPagedOrder(item);
    const key = rpServerPagedText(order && order.id) || `row-${index}`;
    if (indexes.has(key)) {
      merged[indexes.get(key)] = item;
      return;
    }
    indexes.set(key, merged.length);
    merged.push(item);
  });
  return merged;
}
function rpNormalizeServerPagedResponse(payload, requestedLimit) {
  if (!payload || payload.schemaVersion !== 1 || !Array.isArray(payload.items)) {
    throw new Error('Order history returned an unsupported response.');
  }
  if (payload.items.some(item => !item || typeof item.order !== 'object' || !rpServerPagedText(item.order.id))) {
    throw new Error('Order history returned an invalid row.');
  }
  const items = payload.items.map(rpCacheServerPagedItem);
  const rawPage = payload.page && typeof payload.page === 'object' ? payload.page : {};
  const rawTotal = payload.total && typeof payload.total === 'object' ? payload.total : {};
  const totalValue = rawTotal.value == null || rawTotal.value === '' ? NaN : Number(rawTotal.value);
  return {
    facets: payload.facets && typeof payload.facets === 'object' ? payload.facets : {},
    items,
    mode: rpServerPagedText(payload.mode),
    page: {
      hasMore: rawPage.hasMore === true,
      limit: Math.max(1, Number(rawPage.limit) || requestedLimit),
      nextCursor: rpServerPagedText(rawPage.nextCursor),
      returned: Math.max(0, Number(rawPage.returned) || items.length)
    },
    summary: payload.summary && typeof payload.summary === 'object' ? payload.summary : {},
    total: {
      asOf: rpServerPagedText(rawTotal.asOf),
      value: Number.isFinite(totalValue) && totalValue >= 0 ? totalValue : null
    }
  };
}
function rpServerActorScopeKey() {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : null;
  const areas = typeof rpProfileAreas === 'function'
    ? rpProfileAreas(profile).map(rpServerPagedText).filter(Boolean).sort()
    : [];
  return JSON.stringify([
    rpServerPagedText(profile && profile.id),
    rpServerPagedText(profile && profile.role),
    rpServerPagedText(profile && profile.territory),
    rpServerPagedText(profile && (profile.customer_id || profile.customerId)),
    areas
  ]);
}
function rpServerPagedMetadataKey(query) {
  return JSON.stringify([
    rpServerActorScopeKey(),
    rpServerPagedText(query && query.mode),
    rpServerPagedText(query && query.customerId)
  ]);
}
function rpServerPagedWithCachedMetadata(result, query) {
  const key = rpServerPagedMetadataKey(query);
  const counts = result && result.summary && result.summary.counts;
  if (counts && typeof counts === 'object' && Object.keys(counts).length) {
    RP_SERVER_PAGED_METADATA_CACHE.set(key, { facets: result.facets, summary: result.summary });
    return result;
  }
  const cached = RP_SERVER_PAGED_METADATA_CACHE.get(key);
  if (!cached) return result;
  return {
    ...result,
    facets: { ...(result.facets || {}), ...(cached.facets || {}) },
    // A prior money total can be stale after a payment. Keep cached facet and
    // tab metadata for navigation, but never substitute an old invoice KPI.
    summary: query.mode === 'invoices'
      ? { ...(cached.summary || {}), metrics: null }
      : (cached.summary || result.summary)
  };
}

const RP_EXACT_ORDER_REQUESTS = new Map();

function rpExactOrderText(value) {
  return value == null ? '' : String(value).trim();
}

function rpExactOrderDescriptor(options = {}) {
  const orderId = rpExactOrderText(options.orderId);
  const invoiceId = rpExactOrderText(options.invoiceId);
  if (orderId && invoiceId) return { error: 'Use either an order ID or an invoice ID, not both.', invoiceId, orderId };
  if (!orderId && !invoiceId) return { error: '', invoiceId: '', orderId: '' };
  if (invoiceId && /^DRAFT-/i.test(invoiceId)) {
    const draftOrderId = invoiceId.slice(6).trim();
    if (!draftOrderId) return { error: 'The draft invoice reference is invalid.', invoiceId, orderId: '' };
    return { error: '', invoiceId, lookupOrderId: draftOrderId, mode: 'invoices', orderId: '' };
  }
  return {
    error: '',
    invoiceId,
    lookupInvoiceId: invoiceId,
    lookupOrderId: orderId,
    mode: invoiceId ? 'invoices' : 'orders',
    orderId
  };
}

function rpExactOrderCacheVisible(order) {
  if (!(typeof rpHasLiveAuth === 'function' && rpHasLiveAuth())) return true;
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const role = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role) : String(profile.role || '').toLowerCase();
  if (['admin', 'leadership', 'ops', 'sales_manager', 'warehouse', 'dispatch', 'accountant', 'marketing'].includes(role)) return true;
  if (role === 'customer') {
    const customerId = rpExactOrderText(profile.customer_id || profile.customerId);
    return Boolean(customerId && rpExactOrderText(order && (order.cust || order.customerId || order.customer_id)) === customerId);
  }
  return ['rep', 'partner'].includes(role) && typeof rpOrderInProfileScope === 'function' && rpOrderInProfileScope(profile, order);
}

function rpExactOrderFromCache(options = {}) {
  const descriptor = rpExactOrderDescriptor(options);
  if (descriptor.error) return null;
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const role = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role) : String(profile.role || '').toLowerCase();
  const scopeKey = rpExactOrderScopeKey();
  const rows = (Array.isArray(RP.ORDERS) ? RP.ORDERS : []).filter(order => {
    if (!rpExactOrderCacheVisible(order)) return false;
    if (!live) return true;
    if (order.exactCacheScopeKey !== scopeKey) return false;
    if (order.exactDetailComplete !== true || (role !== 'customer' && order.eventsComplete !== true)) return false;
    const customerId = rpExactOrderText(order && (order.cust || order.customerId || order.customer_id));
    const customer = customerId && typeof RP.cust === 'function' ? RP.cust(customerId) : null;
    return !customerId || Boolean(customer && rpExactOrderText(customer.name || customer.legalName));
  });
  if (descriptor.orderId) return rows.find(order => rpExactOrderText(order && order.id) === descriptor.orderId) || null;
  if (!descriptor.invoiceId) return null;
  return rows.find(order => {
    const id = rpExactOrderText(order && order.id);
    return rpExactOrderText(order && order.invoiceNumber) === descriptor.invoiceId
      || `DRAFT-${id}` === descriptor.invoiceId
      || id === descriptor.invoiceId;
  }) || null;
}

function rpExactOrderScopeKey() {
  return rpServerActorScopeKey();
}

function rpExactOrderRequestKey(descriptor) {
  return JSON.stringify([
    rpExactOrderScopeKey(),
    descriptor.mode,
    descriptor.lookupOrderId || '',
    descriptor.lookupInvoiceId || ''
  ]);
}

function rpExactOrderMatches(order, descriptor) {
  if (!order || typeof order !== 'object') return false;
  const id = rpExactOrderText(order.id);
  if (descriptor.orderId) return id === descriptor.orderId;
  if (descriptor.lookupOrderId) return id === descriptor.lookupOrderId && `DRAFT-${id}` === descriptor.invoiceId;
  return rpExactOrderText(order.invoiceNumber) === descriptor.invoiceId;
}

async function rpFetchExactOrder(descriptor, signal) {
  const params = new URLSearchParams();
  params.set('mode', descriptor.mode);
  params.set('limit', '2');
  if (descriptor.lookupOrderId) params.set('orderId', descriptor.lookupOrderId);
  if (descriptor.lookupInvoiceId) params.set('invoiceId', descriptor.lookupInvoiceId);
  const payload = await rpApi(`/api/orders/list?${params.toString()}`, { signal });
  const result = rpNormalizeServerPagedResponse(payload, 2);
  const rows = result.items.map(rpServerPagedOrder).filter(Boolean);
  const matches = rows.filter(order => rpExactOrderMatches(order, descriptor));
  if (matches.length > 1) throw new Error('The exact order lookup returned more than one matching record.');
  if (!matches.length && rows.length) throw new Error('The exact order lookup returned an unexpected record.');
  return matches.length ? rpUpsertOrder({ ...matches[0], exactCacheScopeKey: rpExactOrderScopeKey() }) : null;
}

function rpExactOrderAbortError() {
  const error = new Error('The exact order request was cancelled.');
  error.name = 'AbortError';
  return error;
}

function rpExactOrderRequestEntry(descriptor) {
  const key = rpExactOrderRequestKey(descriptor);
  const current = RP_EXACT_ORDER_REQUESTS.get(key);
  if (current && !current.controller.signal.aborted) return current;

  const controller = new AbortController();
  const entry = { consumers: 0, controller, key, settled: false };
  entry.promise = rpFetchExactOrder(descriptor, controller.signal);
  RP_EXACT_ORDER_REQUESTS.set(key, entry);
  entry.promise.then(
    () => {
      entry.settled = true;
      if (RP_EXACT_ORDER_REQUESTS.get(key) === entry) RP_EXACT_ORDER_REQUESTS.delete(key);
    },
    () => {
      entry.settled = true;
      if (RP_EXACT_ORDER_REQUESTS.get(key) === entry) RP_EXACT_ORDER_REQUESTS.delete(key);
    }
  );
  return entry;
}

function rpJoinExactOrderRequest(descriptor, signal) {
  if (signal && signal.aborted) return Promise.reject(rpExactOrderAbortError());
  const entry = rpExactOrderRequestEntry(descriptor);
  entry.consumers += 1;
  return new Promise((resolve, reject) => {
    let finished = false;
    const release = () => {
      if (signal) signal.removeEventListener('abort', abort);
      entry.consumers = Math.max(0, entry.consumers - 1);
      if (!entry.consumers && !entry.settled) entry.controller.abort();
    };
    const abort = () => {
      if (finished) return;
      finished = true;
      release();
      reject(rpExactOrderAbortError());
    };
    if (signal) signal.addEventListener('abort', abort, { once: true });
    entry.promise.then(
      order => {
        if (finished) return;
        finished = true;
        release();
        resolve(order);
      },
      error => {
        if (finished) return;
        finished = true;
        release();
        reject(error);
      }
    );
  });
}

async function rpLoadExactOrder(options = {}) {
  const descriptor = rpExactOrderDescriptor(options);
  if (descriptor.error) throw new Error(descriptor.error);
  if (!descriptor.orderId && !descriptor.invoiceId) return null;
  const cached = rpExactOrderFromCache(descriptor);
  if (cached) return cached;
  if (!(typeof rpHasLiveAuth === 'function' && rpHasLiveAuth())) return null;
  return rpJoinExactOrderRequest(descriptor, options.signal);
}

function useRpExactOrder(options = {}) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const enabled = options.enabled !== false;
  const descriptor = rpExactOrderDescriptor(options);
  const queryKey = JSON.stringify([rpExactOrderScopeKey(), enabled, descriptor.orderId || '', descriptor.invoiceId || '', descriptor.error || '']);
  const localOrder = enabled ? rpExactOrderFromCache(descriptor) : null;
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState(() => ({
    error: descriptor.error || '',
    loaded: !enabled || !live || Boolean(localOrder) || Boolean(descriptor.error),
    loading: enabled && live && !localOrder && !descriptor.error && Boolean(descriptor.orderId || descriptor.invoiceId),
    order: localOrder,
    queryKey
  }));

  useEffect(() => {
    const cached = enabled ? rpExactOrderFromCache(descriptor) : null;
    if (!enabled || (!descriptor.orderId && !descriptor.invoiceId) || !live || descriptor.error || cached) {
      setState({
        error: descriptor.error || '',
        loaded: true,
        loading: false,
        order: cached,
        queryKey
      });
      return undefined;
    }

    const controller = new AbortController();
    setState({ error: '', loaded: false, loading: true, order: null, queryKey });
    rpLoadExactOrder({ ...descriptor, signal: controller.signal })
      .then(order => {
        if (!controller.signal.aborted) setState({ error: '', loaded: true, loading: false, order, queryKey });
      })
      .catch(error => {
        if (controller.signal.aborted || (error && error.name === 'AbortError')) return;
        setState({
          error: error && error.message ? error.message : 'The order could not be loaded.',
          loaded: false,
          loading: false,
          order: null,
          queryKey
        });
      });
    return () => controller.abort();
  }, [live, queryKey, retryToken]);

  useEffect(() => {
    if (!live || !enabled) return undefined;
    const refresh = () => setRetryToken(value => value + 1);
    window.addEventListener('rp-data-refresh', refresh);
    return () => window.removeEventListener('rp-data-refresh', refresh);
  }, [live, enabled]);

  const current = state.queryKey === queryKey ? state : {
    error: descriptor.error || '',
    loaded: !enabled || !live || Boolean(localOrder) || Boolean(descriptor.error),
    loading: enabled && live && !localOrder && !descriptor.error && Boolean(descriptor.orderId || descriptor.invoiceId),
    order: localOrder,
    queryKey
  };
  return {
    ...current,
    isLive: live,
    notFound: current.loaded && !current.loading && !current.error && !current.order,
    retry: () => setRetryToken(value => value + 1)
  };
}

async function rpFetchServerPagedRows(query, cursor, signal) {
  const params = new URLSearchParams();
  params.set('mode', query.mode);
  params.set('limit', String(query.limit));
  if (cursor) params.set('cursor', cursor);
  if (query.customerId) params.set('customerId', query.customerId);
  if (query.search) params.set('q', query.search);
  if (query.tab && query.tab !== 'all') params.set('tab', query.tab);
  Object.entries(query.filters || {}).forEach(([key, value]) => {
    const clean = rpServerPagedText(value);
    if (clean && RP_SERVER_PAGED_FILTER_PARAMS.has(key)) params.set(key, clean);
  });
  const payload = await rpApi(`/api/orders/list?${params.toString()}`, { signal });
  return rpServerPagedWithCachedMetadata(rpNormalizeServerPagedResponse(payload, query.limit), query);
}
function rpServerPagedAbort(error, controller) {
  return Boolean(controller && controller.signal && controller.signal.aborted) || Boolean(error && error.name === 'AbortError');
}
function useRpServerPagedRows(options = {}) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const actorScopeKey = rpServerActorScopeKey();
  const limit = Math.max(1, Math.min(100, Number(options.limit) || 75));
  const rawSearch = rpServerPagedText(options.search);
  const [debouncedSearch, setDebouncedSearch] = useState(rawSearch);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(rawSearch), 300);
    return () => window.clearTimeout(timer);
  }, [rawSearch]);
  const filterEntries = Object.entries(options.filters || {})
    .filter(([key, value]) => RP_SERVER_PAGED_FILTER_PARAMS.has(key) && rpServerPagedText(value))
    .sort(([left], [right]) => left.localeCompare(right));
  const queryKey = JSON.stringify({
    actorScopeKey,
    customerId: rpServerPagedText(options.customerId),
    filters: Object.fromEntries(filterEntries),
    limit,
    mode: options.mode === 'invoices' ? 'invoices' : 'orders',
    search: debouncedSearch,
    tab: rpServerPagedText(options.tab || 'all')
  });
  const query = useMemo(() => JSON.parse(queryKey), [queryKey]);
  const requestRef = useRef(null);
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState(() => ({
    error: '', facets: {}, items: [], loaded: false, loading: false, loadingMore: false,
    page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
    summary: {}, total: { asOf: '', value: null }
  }));

  useEffect(() => () => {
    if (requestRef.current) requestRef.current.abort();
  }, []);

  useEffect(() => {
    if (!live) return undefined;
    const refresh = () => setRetryToken(value => value + 1);
    window.addEventListener('rp-data-refresh', refresh);
    return () => window.removeEventListener('rp-data-refresh', refresh);
  }, [live]);

  useEffect(() => {
    if (requestRef.current) requestRef.current.abort();
    if (!live) {
      setState({
        error: '', facets: {}, items: [], loaded: false, loading: false, loadingMore: false,
        page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
        summary: {}, total: { asOf: '', value: null }
      });
      return undefined;
    }

    const controller = new AbortController();
    requestRef.current = controller;
    setState({
      error: '', facets: {}, items: [], loaded: false, loading: true, loadingMore: false,
      page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
      summary: {}, total: { asOf: '', value: null }
    });
    rpFetchServerPagedRows(query, '', controller.signal)
      .then(result => {
        if (controller.signal.aborted) return;
        setState({ ...result, error: '', loaded: true, loading: false, loadingMore: false, queryKey });
      })
      .catch(error => {
        if (rpServerPagedAbort(error, controller)) return;
        setState(prev => ({
          ...prev,
          error: error && error.message ? error.message : 'Could not load order history.',
          loaded: false,
          loading: false,
          loadingMore: false,
          queryKey
        }));
      });
    return () => controller.abort();
  }, [live, queryKey, retryToken]);

  const loadMore = async () => {
    if (!live || state.queryKey !== queryKey || state.loading || state.loadingMore || !state.page.hasMore || !state.page.nextCursor) return;
    if (requestRef.current) requestRef.current.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState(prev => ({ ...prev, error: '', loadingMore: true }));
    try {
      const result = await rpFetchServerPagedRows(query, state.page.nextCursor, controller.signal);
      if (controller.signal.aborted) return;
      setState(prev => ({
        ...prev,
        error: '',
        items: rpMergeServerPagedItems(prev.items, result.items),
        loaded: true,
        loading: false,
        loadingMore: false,
        mode: result.mode || prev.mode,
        page: result.page,
        total: result.total && result.total.value != null ? result.total : prev.total,
        queryKey
      }));
    } catch (error) {
      if (rpServerPagedAbort(error, controller)) return;
      setState(prev => ({
        ...prev,
        error: error && error.message ? error.message : 'Could not load more order history.',
        loadingMore: false
      }));
    }
  };

  const currentState = state.queryKey === queryKey ? state : {
    error: '', facets: {}, items: [], loaded: false, loading: live, loadingMore: false,
    page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
    summary: {}, total: { asOf: '', value: null }
  };
  return {
    ...currentState,
    initialLoading: live && currentState.loading && !currentState.loaded,
    isLive: live,
    loadMore,
    retry: () => setRetryToken(value => value + 1)
  };
}

function rpNormalizeDispatchDirectoryResponse(payload, requestedLimit) {
  if (!payload || payload.schemaVersion !== 1 || !Array.isArray(payload.items)) {
    throw new Error('Dispatch routes returned an unsupported response.');
  }
  const items = payload.items.map(item => {
    if (!item || typeof item !== 'object' || !rpServerPagedText(item.id) || !item.order || !rpServerPagedText(item.order.id)) {
      throw new Error('Dispatch routes returned an invalid stop.');
    }
    // Dispatch rows intentionally stay outside the global order cache. The
    // exact-order loader owns that cache and fetches the complete billing and
    // audit shape before a detail, edit, pick, or POD screen uses it.
    return item;
  });
  const rawPage = payload.page && typeof payload.page === 'object' ? payload.page : {};
  const rawTotal = payload.total && typeof payload.total === 'object' ? payload.total : {};
  const totalValue = rawTotal.value == null || rawTotal.value === '' ? NaN : Number(rawTotal.value);
  return {
    items,
    manifest: payload.manifest && typeof payload.manifest === 'object' ? payload.manifest : null,
    page: {
      hasMore: rawPage.hasMore === true,
      limit: Math.max(1, Number(rawPage.limit) || requestedLimit),
      nextCursor: rpServerPagedText(rawPage.nextCursor),
      returned: Math.max(0, Number(rawPage.returned) || items.length)
    },
    summary: payload.summary && typeof payload.summary === 'object' ? payload.summary : {},
    total: {
      asOf: rpServerPagedText(rawTotal.asOf || payload.snapshotAt),
      value: Number.isFinite(totalValue) && totalValue >= 0 ? totalValue : null
    }
  };
}

function rpDispatchJobFromItem(item) {
  if (!item || !item.order) return null;
  const order = item.order;
  const route = item.route || {};
  const stop = item.stop || {};
  const customer = item.customer || {};
  return {
    cases: Number(item.cases || 0),
    driver: route.driver || '',
    id: order.id || item.id,
    label: item.label || customer.name || order.cust || 'Customer',
    lane: route.laneId || 'unassigned',
    laneName: route.laneName || '',
    order,
    routeDate: route.date || null,
    routeId: route.id || null,
    routeLoadCheck: route.loadCheck || null,
    routeStatus: route.status || null,
    routeType: route.type || order.delivery || 'Ranch Delivery',
    status: order.status || item.status || 'Assigned',
    stopNotes: stop.notes || '',
    stopSequence: Number(stop.sequence || 0),
    stopStatus: stop.status || '',
    territory: order.territory || customer.territory || 'Unassigned',
    window: item.window || order.date || 'Unscheduled'
  };
}

async function rpFetchDispatchRows(query, cursor, signal) {
  const params = new URLSearchParams();
  params.set('limit', String(query.limit));
  params.set('scope', query.scope);
  if (query.routeDate) params.set('routeDate', query.routeDate);
  if (query.routeId) params.set('routeId', query.routeId);
  if (cursor) params.set('cursor', cursor);
  const payload = await rpApi(`/api/dispatch/list?${params.toString()}`, { signal });
  return rpNormalizeDispatchDirectoryResponse(payload, query.limit);
}

async function rpLoadDispatchManifest(routeId, options = {}) {
  const id = rpServerPagedText(routeId);
  if (!id) throw new Error('A route is required to load its manifest.');
  const params = new URLSearchParams({ routeId: id });
  const payload = await rpApi(`/api/dispatch/manifest?${params.toString()}`, { signal: options.signal });
  const raw = payload && payload.manifest;
  const route = raw && raw.route;
  if (!payload || payload.schemaVersion !== 1 || !raw || !route || rpServerPagedText(route.id) !== id || !Array.isArray(raw.stops)) {
    throw new Error('Route manifest returned an unsupported response.');
  }
  const number = value => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return {
    id,
    laneId: rpServerPagedText(route.laneId || route.lane_id),
    laneName: rpServerPagedText(route.laneName || route.lane_name) || 'Route Manifest',
    driver: rpServerPagedText(route.driver || route.driver_name),
    routeDate: rpServerPagedText(route.date || route.routeDate || route.route_date),
    routeType: rpServerPagedText(route.type || route.routeType || route.route_type) || 'Ranch Delivery',
    status: rpServerPagedText(route.status) || 'Draft',
    stopCount: Math.max(0, number(raw.stopCount ?? raw.stop_count)),
    cases: Math.max(0, number(raw.expectedCases ?? raw.expected_cases)),
    lines: Array.isArray(raw.lines) ? raw.lines : [],
    loadCheck: raw.loadCheck && typeof raw.loadCheck === 'object' ? raw.loadCheck : null,
    stops: raw.stops.map(stop => ({
      sequence: Math.max(0, number(stop.sequence ?? stop.stopSequence ?? stop.stop_sequence)),
      orderId: rpServerPagedText(stop.orderId || stop.order_id),
      customer: rpServerPagedText(stop.customer || stop.customerName || stop.customer_name) || 'Customer',
      territory: rpServerPagedText(stop.territory),
      cases: Math.max(0, number(stop.cases)),
      window: rpServerPagedText(stop.window || stop.requestedDeliveryDate || stop.requested_delivery_date),
      orderStatus: rpServerPagedText(stop.orderStatus || stop.order_status),
      stopStatus: rpServerPagedText(stop.stopStatus || stop.stop_status),
      notes: rpServerPagedText(stop.notes)
    })).filter(stop => stop.orderId)
  };
}

function useRpDispatchRows(options = {}) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const enabled = options.enabled !== false;
  const limit = Math.max(1, Math.min(100, Number(options.limit) || 50));
  const rawScope = rpServerPagedText(options.scope).toLowerCase();
  const scope = ['all', 'unassigned'].includes(rawScope) ? rawScope : 'assigned';
  const queryKey = JSON.stringify({
    limit,
    routeDate: rpServerPagedText(options.routeDate),
    routeId: rpServerPagedText(options.routeId),
    scope
  });
  const query = useMemo(() => JSON.parse(queryKey), [queryKey]);
  const requestRef = useRef(null);
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState(() => ({
    error: '', items: [], loaded: false, loading: false, loadingMore: false, manifest: null,
    page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
    summary: {}, total: { asOf: '', value: null }
  }));

  useEffect(() => () => {
    if (requestRef.current) requestRef.current.abort();
  }, []);

  useEffect(() => {
    if (!live || !enabled) return undefined;
    const refresh = () => setRetryToken(value => value + 1);
    window.addEventListener('rp-data-refresh', refresh);
    return () => window.removeEventListener('rp-data-refresh', refresh);
  }, [live, enabled]);

  useEffect(() => {
    if (requestRef.current) requestRef.current.abort();
    if (!live || !enabled) {
      setState({
        error: '', items: [], loaded: false, loading: false, loadingMore: false, manifest: null,
        page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
        summary: {}, total: { asOf: '', value: null }
      });
      return undefined;
    }
    const controller = new AbortController();
    requestRef.current = controller;
    setState({
      error: '', items: [], loaded: false, loading: true, loadingMore: false, manifest: null,
      page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
      summary: {}, total: { asOf: '', value: null }
    });
    rpFetchDispatchRows(query, '', controller.signal)
      .then(result => {
        if (!controller.signal.aborted) setState({ ...result, error: '', loaded: true, loading: false, loadingMore: false, queryKey });
      })
      .catch(error => {
        if (rpServerPagedAbort(error, controller)) return;
        setState(prev => ({
          ...prev,
          error: error && error.message ? error.message : 'Could not load dispatch routes.',
          loaded: false,
          loading: false,
          loadingMore: false,
          queryKey
        }));
      });
    return () => controller.abort();
  }, [live, enabled, queryKey, retryToken]);

  const loadMore = async () => {
    if (!live || !enabled || state.queryKey !== queryKey || state.loading || state.loadingMore || !state.page.hasMore || !state.page.nextCursor) return;
    if (requestRef.current) requestRef.current.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState(prev => ({ ...prev, error: '', loadingMore: true }));
    try {
      const result = await rpFetchDispatchRows(query, state.page.nextCursor, controller.signal);
      if (controller.signal.aborted) return;
      setState(prev => ({
        ...prev,
        error: '',
        items: rpMergeServerPagedItems(prev.items, result.items),
        loaded: true,
        loading: false,
        loadingMore: false,
        page: result.page,
        total: result.total && result.total.value != null ? result.total : prev.total,
        queryKey
      }));
    } catch (error) {
      if (rpServerPagedAbort(error, controller)) return;
      setState(prev => ({
        ...prev,
        error: error && error.message ? error.message : 'Could not load more dispatch stops.',
        loadingMore: false
      }));
    }
  };

  return {
    ...state,
    initialLoading: live && enabled && state.loading && !state.loaded,
    isLive: live,
    loadMore,
    retry: () => setRetryToken(value => value + 1)
  };
}
const RP_SERVER_CUSTOMER_FILTER_PARAMS = new Set(['focus', 'rep', 'risk', 'scope', 'segment', 'sort', 'status', 'territory', 'type']);
const RP_SERVER_CUSTOMER_METADATA_CACHE = new Map();
function rpNormalizeServerPagedCustomers(payload, requestedLimit, options = {}) {
  if (!payload || payload.schemaVersion !== 1 || !Array.isArray(payload.items)) {
    throw new Error('Customer directory returned an unsupported response.');
  }
  if (payload.items.some(item => !item || typeof item !== 'object' || !rpServerPagedText(item.id))) {
    throw new Error('Customer directory returned an invalid row.');
  }
  const items = options.persistItems === false
    ? payload.items.slice()
    : payload.items.map(rpUpsertServerPagedCustomer).filter(Boolean);
  const rawPage = payload.page && typeof payload.page === 'object' ? payload.page : {};
  const rawTotal = payload.total && typeof payload.total === 'object' ? payload.total : {};
  const totalValue = rawTotal.value == null || rawTotal.value === '' ? NaN : Number(rawTotal.value);
  return {
    facets: payload.facets && typeof payload.facets === 'object' ? payload.facets : {},
    items,
    page: {
      hasMore: rawPage.hasMore === true,
      limit: Math.max(1, Number(rawPage.limit) || requestedLimit),
      nextCursor: rpServerPagedText(rawPage.nextCursor),
      returned: Math.max(0, Number(rawPage.returned) || items.length)
    },
    summary: payload.summary && typeof payload.summary === 'object' ? payload.summary : {},
    total: {
      asOf: rpServerPagedText(rawTotal.asOf || payload.snapshotAt),
      value: Number.isFinite(totalValue) && totalValue >= 0 ? totalValue : null
    }
  };
}
function rpServerCustomerMetadataKey(query = {}) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : null;
  return JSON.stringify([
    rpServerPagedText(profile && profile.id),
    rpServerPagedText(profile && profile.role),
    rpServerPagedText(profile && profile.territory),
    rpServerPagedText(profile && (profile.customer_id || profile.customerId)),
    'customers',
    rpServerPagedText(query && query.filters && query.filters.scope)
  ]);
}
function rpServerCustomersWithCachedMetadata(result, query = {}) {
  const key = rpServerCustomerMetadataKey(query);
  const counts = result && result.summary && result.summary.counts;
  if (counts && typeof counts === 'object' && Object.keys(counts).length) {
    RP_SERVER_CUSTOMER_METADATA_CACHE.set(key, { facets: result.facets, summary: result.summary });
    return result;
  }
  const cached = RP_SERVER_CUSTOMER_METADATA_CACHE.get(key);
  if (!cached) return result;
  return {
    ...result,
    facets: { ...(result.facets || {}), ...(cached.facets || {}) },
    summary: cached.summary || result.summary
  };
}
async function rpFetchServerPagedCustomers(query, cursor, signal) {
  const params = new URLSearchParams();
  params.set('limit', String(query.limit));
  if (cursor) params.set('cursor', cursor);
  if (query.search) params.set('q', query.search);
  Object.entries(query.filters || {}).forEach(([key, value]) => {
    const clean = rpServerPagedText(value);
    if (clean && RP_SERVER_CUSTOMER_FILTER_PARAMS.has(key)) params.set(key, clean);
  });
  const payload = await rpApi(`/api/customers/list?${params.toString()}`, { signal });
  return rpServerCustomersWithCachedMetadata(rpNormalizeServerPagedCustomers(payload, query.limit, {
    persistItems: query.persistItems
  }), query);
}
function rpMergeServerPagedCustomers(current, incoming) {
  const merged = [];
  const indexes = new Map();
  [...(Array.isArray(current) ? current : []), ...(Array.isArray(incoming) ? incoming : [])].forEach((customer, index) => {
    const key = rpServerPagedText(customer && customer.id) || `customer-${index}`;
    if (indexes.has(key)) {
      merged[indexes.get(key)] = customer;
      return;
    }
    indexes.set(key, merged.length);
    merged.push(customer);
  });
  return merged;
}
function useRpServerPagedCustomers(options = {}) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const enabled = options.enabled !== false;
  const persistItems = options.persistItems !== false;
  const actorScopeKey = rpServerActorScopeKey();
  const limit = Math.max(1, Math.min(100, Number(options.limit) || 75));
  const rawSearch = rpServerPagedText(options.search);
  const [debouncedSearch, setDebouncedSearch] = useState(rawSearch);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(rawSearch), 300);
    return () => window.clearTimeout(timer);
  }, [rawSearch]);
  const filterEntries = Object.entries(options.filters || {})
    .filter(([key, value]) => RP_SERVER_CUSTOMER_FILTER_PARAMS.has(key) && rpServerPagedText(value))
    .sort(([left], [right]) => left.localeCompare(right));
  const queryKey = JSON.stringify({ actorScopeKey, enabled, filters: Object.fromEntries(filterEntries), limit, persistItems, search: debouncedSearch });
  const query = useMemo(() => JSON.parse(queryKey), [queryKey]);
  const requestRef = useRef(null);
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState(() => ({
    error: '', facets: {}, items: [], loaded: false, loading: live && enabled, loadingMore: false,
    page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
    summary: {}, total: { asOf: '', value: null }
  }));

  useEffect(() => () => {
    if (requestRef.current) requestRef.current.abort();
  }, []);
  useEffect(() => {
    if (!live || !enabled) return undefined;
    const refresh = () => setRetryToken(value => value + 1);
    window.addEventListener('rp-data-refresh', refresh);
    return () => window.removeEventListener('rp-data-refresh', refresh);
  }, [enabled, live]);
  useEffect(() => {
    if (requestRef.current) requestRef.current.abort();
    if (!live || !enabled) {
      setState({
        error: '', facets: {}, items: [], loaded: false, loading: false, loadingMore: false,
        page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
        summary: {}, total: { asOf: '', value: null }
      });
      return undefined;
    }
    const controller = new AbortController();
    requestRef.current = controller;
    setState({
      error: '', facets: {}, items: [], loaded: false, loading: true, loadingMore: false,
      page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
      summary: {}, total: { asOf: '', value: null }
    });
    rpFetchServerPagedCustomers(query, '', controller.signal)
      .then(result => {
        if (controller.signal.aborted) return;
        setState({ ...result, error: '', loaded: true, loading: false, loadingMore: false, queryKey });
      })
      .catch(error => {
        if (rpServerPagedAbort(error, controller)) return;
        setState(prev => ({
          ...prev,
          error: error && error.message ? error.message : 'Could not load customer directory.',
          loaded: false,
          loading: false,
          loadingMore: false,
          queryKey
        }));
      });
    return () => controller.abort();
  }, [enabled, live, queryKey, retryToken]);

  const loadMore = async () => {
    if (!live || !enabled || state.queryKey !== queryKey || state.loading || state.loadingMore || !state.page.hasMore || !state.page.nextCursor) return;
    if (requestRef.current) requestRef.current.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState(prev => ({ ...prev, error: '', loadingMore: true }));
    try {
      const result = await rpFetchServerPagedCustomers(query, state.page.nextCursor, controller.signal);
      if (controller.signal.aborted) return;
      setState(prev => ({
        ...prev,
        error: '',
        items: rpMergeServerPagedCustomers(prev.items, result.items),
        loaded: true,
        loading: false,
        loadingMore: false,
        page: result.page,
        queryKey,
        total: result.total && result.total.value != null ? result.total : prev.total
      }));
    } catch (error) {
      if (rpServerPagedAbort(error, controller)) return;
      setState(prev => ({
        ...prev,
        error: error && error.message ? error.message : 'Could not load more customers.',
        loadingMore: false
      }));
    }
  };
  const currentState = state.queryKey === queryKey ? state : {
    error: '', facets: {}, items: [], loaded: false, loading: live && enabled, loadingMore: false,
    page: { hasMore: false, limit, nextCursor: '', returned: 0 }, queryKey,
    summary: {}, total: { asOf: '', value: null }
  };
  return {
    ...currentState,
    initialLoading: live && enabled && currentState.loading && !currentState.loaded,
    isLive: live,
    loadMore,
    retry: () => setRetryToken(value => value + 1)
  };
}

const RP_EXACT_CUSTOMER_REQUESTS = new Map();
const RP_EXACT_CUSTOMER_CACHE = new Map();

function rpExactCustomerDescriptor(options = {}) {
  const customerId = rpServerPagedText(options.customerId);
  if (customerId.length > 120) return { customerId, error: 'The customer ID is invalid.' };
  return { customerId, error: '' };
}

function rpExactCustomerRequestKey(descriptor) {
  return JSON.stringify([rpServerActorScopeKey(), descriptor.customerId]);
}

function rpExactCustomerFromCache(descriptor) {
  if (!descriptor.customerId || descriptor.error) return null;
  if (!(typeof rpHasLiveAuth === 'function' && rpHasLiveAuth())) {
    return typeof RP.cust === 'function' ? RP.cust(descriptor.customerId) : null;
  }
  return RP_EXACT_CUSTOMER_CACHE.get(rpExactCustomerRequestKey(descriptor)) || null;
}

async function rpFetchExactCustomer(descriptor, signal) {
  const params = new URLSearchParams({ customerId: descriptor.customerId, limit: '2' });
  const payload = await rpApi(`/api/customers/list?${params.toString()}`, { signal });
  if (!payload || payload.lookup !== 'exact') throw new Error('Customer lookup returned an unsupported response.');
  const result = rpNormalizeServerPagedCustomers(payload, 2);
  const matches = result.items.filter(customer => rpServerPagedText(customer && customer.id) === descriptor.customerId);
  if (matches.length > 1) throw new Error('The exact customer lookup returned more than one matching record.');
  if (!matches.length && result.items.length) throw new Error('The exact customer lookup returned an unexpected record.');
  if (!matches.length) return null;
  const incoming = payload.items.find(customer => rpServerPagedText(customer && customer.id) === descriptor.customerId) || {};
  const customer = rpUpsertServerPagedCustomer(matches[0]);
  if (customer && Object.prototype.hasOwnProperty.call(incoming, 'note')) customer.note = incoming.note || '';
  RP_EXACT_CUSTOMER_CACHE.set(rpExactCustomerRequestKey(descriptor), customer);
  return customer;
}

function rpExactCustomerAbortError() {
  const error = new Error('The exact customer request was cancelled.');
  error.name = 'AbortError';
  return error;
}

function rpExactCustomerRequestEntry(descriptor) {
  const key = rpExactCustomerRequestKey(descriptor);
  const current = RP_EXACT_CUSTOMER_REQUESTS.get(key);
  if (current && !current.controller.signal.aborted) return current;
  const controller = new AbortController();
  const entry = { consumers: 0, controller, key, settled: false };
  entry.promise = rpFetchExactCustomer(descriptor, controller.signal);
  RP_EXACT_CUSTOMER_REQUESTS.set(key, entry);
  entry.promise.then(
    () => {
      entry.settled = true;
      if (RP_EXACT_CUSTOMER_REQUESTS.get(key) === entry) RP_EXACT_CUSTOMER_REQUESTS.delete(key);
    },
    () => {
      entry.settled = true;
      if (RP_EXACT_CUSTOMER_REQUESTS.get(key) === entry) RP_EXACT_CUSTOMER_REQUESTS.delete(key);
    }
  );
  return entry;
}

function rpJoinExactCustomerRequest(descriptor, signal) {
  if (signal && signal.aborted) return Promise.reject(rpExactCustomerAbortError());
  const entry = rpExactCustomerRequestEntry(descriptor);
  entry.consumers += 1;
  return new Promise((resolve, reject) => {
    let finished = false;
    const release = () => {
      if (signal) signal.removeEventListener('abort', abort);
      entry.consumers = Math.max(0, entry.consumers - 1);
      if (!entry.consumers && !entry.settled) entry.controller.abort();
    };
    const abort = () => {
      if (finished) return;
      finished = true;
      release();
      reject(rpExactCustomerAbortError());
    };
    if (signal) signal.addEventListener('abort', abort, { once: true });
    entry.promise.then(
      customer => {
        if (finished) return;
        finished = true;
        release();
        resolve(customer);
      },
      error => {
        if (finished) return;
        finished = true;
        release();
        reject(error);
      }
    );
  });
}

async function rpLoadExactCustomer(options = {}) {
  const descriptor = rpExactCustomerDescriptor(options);
  if (descriptor.error) throw new Error(descriptor.error);
  if (!descriptor.customerId) return null;
  const cached = rpExactCustomerFromCache(descriptor);
  if (cached) return cached;
  if (!(typeof rpHasLiveAuth === 'function' && rpHasLiveAuth())) return null;
  return rpJoinExactCustomerRequest(descriptor, options.signal);
}

function useRpExactCustomer(options = {}) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const enabled = options.enabled !== false;
  const descriptor = rpExactCustomerDescriptor(options);
  const requestKey = rpExactCustomerRequestKey(descriptor);
  const queryKey = JSON.stringify([requestKey, enabled, descriptor.error]);
  const localCustomer = enabled ? rpExactCustomerFromCache(descriptor) : null;
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState(() => ({
    customer: localCustomer,
    error: descriptor.error || '',
    loaded: !enabled || !live || Boolean(localCustomer) || Boolean(descriptor.error),
    loading: enabled && live && !localCustomer && !descriptor.error && Boolean(descriptor.customerId),
    queryKey
  }));

  useEffect(() => {
    if (!live || !enabled || !descriptor.customerId) return undefined;
    const refresh = () => {
      RP_EXACT_CUSTOMER_CACHE.delete(requestKey);
      setRetryToken(value => value + 1);
    };
    window.addEventListener('rp-data-refresh', refresh);
    return () => window.removeEventListener('rp-data-refresh', refresh);
  }, [live, queryKey]);

  useEffect(() => {
    const cached = enabled ? rpExactCustomerFromCache(descriptor) : null;
    if (!enabled || !descriptor.customerId || !live || descriptor.error || cached) {
      setState({ customer: cached, error: descriptor.error || '', loaded: true, loading: false, queryKey });
      return undefined;
    }
    const controller = new AbortController();
    setState({ customer: null, error: '', loaded: false, loading: true, queryKey });
    rpLoadExactCustomer({ customerId: descriptor.customerId, signal: controller.signal })
      .then(customer => {
        if (!controller.signal.aborted) setState({ customer, error: '', loaded: true, loading: false, queryKey });
      })
      .catch(error => {
        if (controller.signal.aborted || (error && error.name === 'AbortError')) return;
        setState({
          customer: null,
          error: error && error.message ? error.message : 'The customer could not be loaded.',
          loaded: false,
          loading: false,
          queryKey
        });
      });
    return () => controller.abort();
  }, [live, queryKey, retryToken]);

  const current = state.queryKey === queryKey ? state : {
    customer: localCustomer,
    error: descriptor.error || '',
    loaded: !enabled || !live || Boolean(localCustomer) || Boolean(descriptor.error),
    loading: enabled && live && !localCustomer && !descriptor.error && Boolean(descriptor.customerId),
    queryKey
  };
  return {
    ...current,
    isLive: live,
    notFound: current.loaded && !current.loading && !current.error && !current.customer,
    retry: () => {
      RP_EXACT_CUSTOMER_CACHE.delete(requestKey);
      setRetryToken(value => value + 1);
    }
  };
}

function rpServerFacetOptions(facets, key) {
  const source = facets && facets[key];
  const values = Array.isArray(source) ? source : (source && Array.isArray(source.values) ? source.values : null);
  if (values) {
    return Array.from(new Set(values.map(value => rpServerPagedText(value && typeof value === 'object' ? (value.value ?? value.label) : value)).filter(Boolean)));
  }
  if (source && typeof source === 'object') return Object.keys(source).filter(keyValue => keyValue !== 'values' && rpServerPagedText(keyValue));
  return [];
}
function rpServerSummaryValue(summary, key, fallback = null) {
  const containers = [summary && summary.counts, summary && summary.tabs, summary, summary && summary.metrics];
  for (const container of containers) {
    if (!container || typeof container !== 'object') continue;
    const matchingKey = Object.prototype.hasOwnProperty.call(container, key)
      ? key
      : Object.keys(container).find(candidate => candidate.toLowerCase() === String(key).toLowerCase());
    if (!matchingKey || container[matchingKey] == null) continue;
    const raw = container[matchingKey];
    const parsed = Number(raw && typeof raw === 'object' ? (raw.value ?? raw.count) : raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}
function RpServerLoadMore({ error, items, label = 'rows', loadingMore, onLoadMore, onRetry, page, total }) {
  const loaded = Array.isArray(items) ? items.length : 0;
  const totalValue = total && total.value != null && total.value !== '' && Number.isFinite(Number(total.value)) ? Number(total.value) : null;
  const hasMore = Boolean(page && page.hasMore);
  const hasCursor = Boolean(page && page.nextCursor);
  const countText = totalValue == null
    ? `Loaded ${loaded.toLocaleString()} ${label}`
    : !hasMore && loaded === totalValue
      ? `Showing all ${totalValue.toLocaleString()} ${label}`
      : `Loaded ${loaded.toLocaleString()} of ${totalValue.toLocaleString()} ${label}`;
  const incompleteWithoutCursor = (hasMore && !hasCursor) || (totalValue != null && loaded < totalValue && !hasMore);
  return (
    <div style={{ borderTop: '1px solid var(--line)', background: 'var(--surface-2)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>{countText}</div>
        {hasMore && hasCursor && <button type="button" className="btn btn-sm" disabled={loadingMore} onClick={onLoadMore} style={{ opacity: loadingMore ? 0.62 : 1 }}><Icon name={loadingMore ? 'refresh' : 'plus'} size={14} />{loadingMore ? 'Loading...' : `Load More ${label}`}</button>}
      </div>
      {incompleteWithoutCursor && <div style={{ marginTop: 9, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>More {label} exist, but the server did not provide a continuation cursor. Refresh or report this data-loading error.</div>}
      {error && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 9, color: 'var(--danger)', fontSize: 12.5, fontWeight: 650 }}><span>{error}</span><button type="button" className="btn btn-sm" onClick={onRetry}>Try Again</button></div>}
    </div>
  );
}
function RpPagination({ page, setPage, total, totalPages, start, end, label = 'rows' }) {
  if (!total || totalPages <= 1) return null;
  const previous = () => setPage(Math.max(1, page - 1));
  const next = () => setPage(Math.min(totalPages, page + 1));
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)', flexWrap: 'wrap' }}>
      <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>
        Showing <span className="mono" style={{ color: 'var(--ink)', fontWeight: 700 }}>{start + 1}-{end}</span> of <span className="mono" style={{ color: 'var(--ink)', fontWeight: 700 }}>{total}</span> {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={previous} style={{ opacity: page <= 1 ? 0.45 : 1 }}>
          <Icon name="chevL" size={14} />Previous
        </button>
        <span className="mono" style={{ color: 'var(--muted)', fontSize: 12 }}>{page} / {totalPages}</span>
        <button type="button" className="btn btn-sm" disabled={page >= totalPages} onClick={next} style={{ opacity: page >= totalPages ? 0.45 : 1 }}>
          Next<Icon name="chevR" size={14} />
        </button>
      </div>
    </div>
  );
}
function rpFieldPersona(role) {
  return rpCurrentPersona(role || 'rep');
}
function rpFieldUserName(role) {
  const p = rpFieldPersona(role);
  return p.name || 'Ranch Power';
}
function rpFieldFirstName(role) {
  return (rpFieldUserName(role).split(/\s+/).filter(Boolean)[0]) || 'there';
}
function rpFieldTerritory(role) {
  const areas = rpFieldTerritories(role);
  if (areas.length) return areas[0];
  return rpHasLiveAuth() ? 'Unassigned' : 'Squamish';
}
function rpFieldTerritories(role) {
  const profile = rpAuthProfile();
  const profileAreas = rpProfileAreas(profile);
  if (profileAreas.length) return profileAreas;
  const name = rpFieldUserName(role);
  const location = RP.LOCATIONS.find(l => l.rep === name && l.territory);
  if (location) return [location.territory];
  const customer = RP.CUSTOMERS.find(c => c.rep === name && c.territory);
  if (customer) return [customer.territory];
  const goal = RP.SALES_GOALS.find(g => g.rep === name && g.territory);
  if (goal) return [goal.territory];
  const desc = rpFieldPersona(role).desc;
  if (desc && !['Ranch Power', 'Customer account', 'Territory-limited', 'Full operational'].includes(desc)) return rpProfileAreas({ territory: desc });
  return rpHasLiveAuth() ? [] : ['Squamish'];
}
function rpFieldCoversTerritory(role, territory) {
  return rpAreaMatches(rpFieldTerritories(role), territory);
}
function rpFieldCustomers(role) {
  const customers = rpVisibleCustomers(RP.CUSTOMERS);
  if (rpHasLiveAuth()) {
    const profile = rpAuthProfile();
    const areas = rpProfileAreas(profile);
    const roleKey = rpNormalizeRole(profile.role || role);
    const globalAccess = ['admin', 'leadership', 'ops', 'sales_manager', 'accountant'].includes(roleKey) && !areas.length;
    if (globalAccess) return customers;
    return customers.filter(c => rpCustomerInProfileScope(profile, c));
  }
  const name = rpFieldUserName(role);
  return customers.filter(c => c.rep === name || rpFieldCoversTerritory(role, c.territory));
}
function rpOrderInProfileScope(profile, order) {
  if (!profile || !order) return false;
  const customer = RP.cust(order.cust || order.customerId || order.customer_id);
  if (customer && rpCustomerInProfileScope(profile, customer)) return true;

  const profileId = String(profile.id || '').trim();
  const orderRepId = String(order.repId || order.rep_id || '').trim();
  if (profileId && orderRepId && profileId === orderRepId) return true;

  const profileName = String(profile.display_name || profile.name || rpNameFromEmail(profile.email)).trim().toLowerCase();
  const orderRep = String(order.rep || '').trim().toLowerCase();
  if (profileName && orderRep && profileName === orderRep) return true;

  const areas = rpProfileAreas(profile);
  const territory = order.territory || (customer && customer.territory);
  return Boolean(areas.length && territory && rpAreaMatches(areas, territory));
}
function rpFieldOrders(role) {
  if (rpHasLiveAuth()) {
    const profile = rpAuthProfile();
    const areas = rpProfileAreas(profile);
    const roleKey = rpNormalizeRole(profile.role || role);
    const globalAccess = ['admin', 'leadership', 'ops', 'sales_manager', 'accountant'].includes(roleKey) && !areas.length;
    if (globalAccess) return RP.ORDERS;
    return RP.ORDERS.filter(o => rpOrderInProfileScope(profile, o));
  }
  const name = rpFieldUserName(role);
  return RP.ORDERS.filter(o => o.rep === name || rpFieldCoversTerritory(role, o.territory));
}
const RP_DUPLICATE_ACCOUNT_SUFFIXES = new Set(['a', 'an', 'and', 'bc', 'canada', 'co', 'company', 'corp', 'corporation', 'inc', 'incorporated', 'ltd', 'limited', 'the']);
function rpDuplicateTextKey(value, options = {}) {
  const words = String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const filtered = options.keepSuffixes ? words : words.filter(word => !RP_DUPLICATE_ACCOUNT_SUFFIXES.has(word));
  return filtered.join(' ');
}
function rpDuplicatePhoneKey(value) {
  const digits = String(value || '').replace(/\D+/g, '');
  return digits.length >= 7 ? digits.slice(-10) : '';
}
function rpDuplicateTokenScore(a, b) {
  const left = new Set(String(a || '').split(/\s+/).filter(Boolean));
  const right = new Set(String(b || '').split(/\s+/).filter(Boolean));
  if (!left.size || !right.size) return 0;
  let shared = 0;
  left.forEach(token => { if (right.has(token)) shared += 1; });
  return shared / Math.max(left.size, right.size);
}
function rpCustomerDuplicateCandidates(input, options = {}) {
  const draft = input || {};
  const excludeId = String(options.excludeId || draft.id || '').trim();
  const nameKey = rpDuplicateTextKey(draft.name || draft.displayName || draft.legalName);
  const addressKey = rpDuplicateTextKey(draft.address, { keepSuffixes: true });
  const cityKey = rpDuplicateTextKey(draft.city, { keepSuffixes: true });
  const territoryKey = rpDuplicateTextKey(draft.territory, { keepSuffixes: true });
  const emailKey = String(draft.email || draft.buyerEmail || '').trim().toLowerCase();
  const phoneKey = rpDuplicatePhoneKey(draft.phone);
  if (nameKey.length < 4 && addressKey.length < 6 && !emailKey && !phoneKey) return [];
  return rpVisibleCustomers(Array.isArray(RP.CUSTOMERS) ? RP.CUSTOMERS : [])
    .filter(customer => customer && String(customer.id || '') !== excludeId)
    .map(customer => {
      const customerNameKey = rpDuplicateTextKey(customer.name || customer.displayName || customer.legalName);
      const customerAddressKey = rpDuplicateTextKey(customer.address || customer.shippingAddress?.address || customer.shippingAddress?.address1, { keepSuffixes: true });
      const customerCityKey = rpDuplicateTextKey(customer.city, { keepSuffixes: true });
      const customerTerritoryKey = rpDuplicateTextKey(customer.territory, { keepSuffixes: true });
      const customerEmailKey = String(customer.email || customer.buyerEmail || '').trim().toLowerCase();
      const customerPhoneKey = rpDuplicatePhoneKey(customer.phone || customer.primaryPhone);
      const reasons = [];
      let score = 0;
      if (emailKey && customerEmailKey && emailKey === customerEmailKey) { score += 95; reasons.push('same email'); }
      if (phoneKey && customerPhoneKey && phoneKey === customerPhoneKey) { score += 90; reasons.push('same phone'); }
      if (addressKey && customerAddressKey && addressKey === customerAddressKey) { score += 85; reasons.push('same address'); }
      if (nameKey && customerNameKey) {
        if (nameKey === customerNameKey) { score += 75; reasons.push('same name'); }
        else {
          const tokenScore = rpDuplicateTokenScore(nameKey, customerNameKey);
          if (tokenScore >= 0.8 && Math.min(nameKey.length, customerNameKey.length) >= 6) { score += 48; reasons.push('similar name'); }
          else if ((nameKey.includes(customerNameKey) || customerNameKey.includes(nameKey)) && Math.min(nameKey.length, customerNameKey.length) >= 7) { score += 42; reasons.push('similar name'); }
        }
      }
      if (score > 0 && cityKey && customerCityKey && cityKey === customerCityKey) score += 8;
      if (score > 0 && territoryKey && customerTerritoryKey && territoryKey === customerTerritoryKey) score += 6;
      return { customer, reasons: Array.from(new Set(reasons)), score };
    })
    .filter(match => match.score >= 70)
    .sort((a, b) => b.score - a.score || String(a.customer.name || '').localeCompare(String(b.customer.name || '')))
    .slice(0, 4);
}
function CustomerDuplicateWarning({ candidates }) {
  const rows = Array.isArray(candidates) ? candidates : [];
  if (!rows.length) return null;
  return (
    <div style={{ border: '1px solid var(--amber)', background: 'var(--amber-soft)', color: 'var(--amber-deep)', borderRadius: 11, padding: '11px 12px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
        <Icon name="alert" size={17} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800 }}>Possible existing account</div>
          <div style={{ fontSize: 12, lineHeight: 1.45, marginTop: 3, color: 'var(--ink-soft)' }}>Review these before creating another account. Separate franchise or location accounts are still okay.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 9 }}>
            {rows.map(match => (
              <div key={match.customer.id} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 9, padding: '8px 9px', color: 'var(--ink)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 12.5 }}>{match.customer.name}</div>
                  <Pill status={match.customer.status || match.customer.risk || 'Active'} />
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 3, lineHeight: 1.35 }}>
                  {[match.customer.city, match.customer.type, match.reasons.join(', ')].filter(Boolean).join(' - ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
const RP_ORDER_HISTORY_EXCLUDED_STATUSES = new Set(['Cancelled', 'Canceled']);
function rpParseOrderHistoryDate(value) {
  const raw = String(value || '').trim();
  if (!raw || raw.toUpperCase() === 'ASAP') return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function rpOrderHistoryDate(order) {
  if (!order) return null;
  return rpParseOrderHistoryDate(order.created || order.createdAt || order.created_at || order.submittedAt || order.submitted_at || order.date || order.updated || order.updatedAt || order.updated_at);
}
function rpCustomerOrders(customerOrId, options = {}) {
  const id = String(typeof customerOrId === 'object' ? (customerOrId && customerOrId.id) : customerOrId || '').trim();
  if (!id) return [];
  const includeCancelled = options.includeCancelled === true;
  return (Array.isArray(RP.ORDERS) ? RP.ORDERS : [])
    .filter(order => {
      if (!order) return false;
      const customerId = String(order.cust || order.customerId || order.customer_id || '').trim();
      if (customerId !== id) return false;
      return includeCancelled || !RP_ORDER_HISTORY_EXCLUDED_STATUSES.has(String(order.status || ''));
    })
    .slice()
    .sort((a, b) => {
      const at = rpOrderHistoryDate(a);
      const bt = rpOrderHistoryDate(b);
      return (bt ? bt.getTime() : 0) - (at ? at.getTime() : 0) || String(b.id || '').localeCompare(String(a.id || ''));
    });
}
function rpCustomerHasExactOrderAggregates(customer) {
  return Boolean(
    customer
    && typeof rpHasLiveAuth === 'function'
    && rpHasLiveAuth()
    && Object.prototype.hasOwnProperty.call(customer, 'orderCount')
  );
}
function rpOptionalFiniteNumber(value) {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
function rpCustomerOrderSummary(customerOrId, options = {}) {
  const customer = typeof customerOrId === 'object' ? customerOrId : (RP.cust ? RP.cust(customerOrId) : null);
  const orders = Array.isArray(options.orders) ? options.orders.filter(Boolean) : rpCustomerOrders(customerOrId);
  const exact = rpCustomerHasExactOrderAggregates(customer);
  const aggregateLastOrderId = String(customer && customer.lastOrderId || '').trim();
  const aggregateLastOrderDate = rpParseOrderHistoryDate(customer && customer.lastOrder);
  const cachedAggregateOrder = aggregateLastOrderId ? orders.find(order => String(order && order.id || '') === aggregateLastOrderId) : null;
  const lastOrder = exact ? (cachedAggregateOrder || (!aggregateLastOrderDate ? orders[0] || null : null)) : (orders[0] || null);
  const orderDate = exact ? (aggregateLastOrderDate || rpOrderHistoryDate(lastOrder)) : rpOrderHistoryDate(lastOrder);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let daysSince = exact ? rpOptionalFiniteNumber(customer && customer.daysSince) : null;
  if (daysSince === null && orderDate) daysSince = Math.max(0, Math.floor((today.getTime() - new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate()).getTime()) / 86400000));
  if (daysSince === null && customer && customer.lastOrder) daysSince = rpOptionalFiniteNumber(customer.daysSince);
  const totals = lastOrder ? rpOrderTotals(lastOrder) : null;
  return {
    cadenceDays: exact ? rpOptionalFiniteNumber(customer && customer.cadenceDays) : null,
    complete: exact,
    daysSince,
    lastCases: exact ? rpFiniteNumber(customer && customer.lastCases) : (totals ? totals.cases : rpFiniteNumber(customer && customer.lastCases)),
    lastOrder,
    lastOrderId: exact ? aggregateLastOrderId : String(lastOrder && lastOrder.id || ''),
    lastOrderDate: orderDate ? rpAddDaysYmd(orderDate, 0, customer || lastOrder) : (lastOrder ? rpOrderDateLabel(lastOrder, '') : String(customer && customer.lastOrder || '')),
    orderCount: exact ? rpFiniteNumber(customer && customer.orderCount) : orders.length,
    typicalOrderCases: exact ? rpOptionalFiniteNumber(customer && customer.typicalOrderCases) : null,
    orders
  };
}
function rpCustomerOrderHistoryLabel(customerOrId) {
  const summary = rpCustomerOrderSummary(customerOrId);
  if (!summary.orderCount && !summary.lastOrder && !summary.lastOrderDate) return 'Never ordered';
  if (summary.daysSince === 0) return 'Last order today';
  if (summary.daysSince === 1) return 'Last order yesterday';
  if (Number.isFinite(summary.daysSince)) return `Last order ${summary.daysSince} days ago`;
  return `Last order ${summary.lastOrderDate}`;
}
function rpRestockToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}
function rpRestockDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  }
  const raw = String(value || '').trim();
  if (!raw || raw.toUpperCase() === 'ASAP') return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}
function rpRestockDaysBetween(left, right = rpRestockToday()) {
  const a = rpRestockDate(left);
  const b = rpRestockDate(right) || rpRestockToday();
  if (!a || !b) return null;
  return Math.floor((b.getTime() - a.getTime()) / 86400000);
}
function rpRestockAddDays(value, days) {
  const date = rpRestockDate(value) || rpRestockToday();
  const next = new Date(date);
  next.setDate(next.getDate() + Math.round(Number(days) || 0));
  return next;
}
function rpRestockMedian(values) {
  const nums = values.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}
function rpRestockClamp(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}
function rpRestockCustomerVisits(customerOrId) {
  const id = String(typeof customerOrId === 'object' ? (customerOrId && customerOrId.id) : customerOrId || '').trim();
  if (!id) return [];
  return (Array.isArray(RP.VISITS) ? RP.VISITS : [])
    .filter(visit => String(visit && (visit.cust || visit.customerId || visit.customer_id) || '').trim() === id)
    .slice()
    .sort((a, b) => {
      const bd = rpRestockDate(b.date || b.visitedAt || b.visited_at || b.createdAt || b.created_at);
      const ad = rpRestockDate(a.date || a.visitedAt || a.visited_at || a.createdAt || a.created_at);
      return (bd ? bd.getTime() : 0) - (ad ? ad.getTime() : 0);
    });
}
function rpRestockShelfSignal(visits) {
  const rows = Array.isArray(visits) ? visits : [];
  const visit = rows.find(row => {
    const shelf = row && (row.shelf || row.shelfCounts || row.shelf_counts);
    return shelf && typeof shelf === 'object' && Object.values(shelf).some(value => Number.isFinite(Number(value)));
  }) || null;
  const shelf = visit && (visit.shelf || visit.shelfCounts || visit.shelf_counts);
  if (!shelf || typeof shelf !== 'object') return { empty: 0, low: 0, total: null, skuCount: 0, visit: null, daysSince: null, lowSkus: [] };
  const entries = Object.entries(shelf)
    .map(([sku, value]) => ({ sku, count: Number(value) }))
    .filter(row => Number.isFinite(row.count));
  const lowSkus = entries.filter(row => row.count <= 2);
  return {
    empty: entries.filter(row => row.count <= 0).length,
    low: lowSkus.length,
    lowSkus: lowSkus.map(row => row.sku),
    total: entries.reduce((sum, row) => sum + row.count, 0),
    skuCount: entries.length,
    visit,
    daysSince: visit ? rpRestockDaysBetween(visit.date || visit.visitedAt || visit.visited_at || visit.createdAt || visit.created_at) : null
  };
}
function rpRestockOrderDates(orders) {
  return (orders || [])
    .map(order => ({ order, date: rpOrderSalesDate(order) || rpOrderHistoryDate(order) }))
    .filter(row => row.date)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}
function rpRestockCustomerEligible(customer) {
  if (!customer || !customer.id) return false;
  if (typeof rpOrderIsQuickSale === 'function' && rpOrderIsQuickSale({ cust: customer.id })) return false;
  const text = [customer.type, customer.customerType, customer.tier].map(value => String(value || '').toLowerCase()).join(' ');
  if (/\bconsumer\b|direct|distributor/.test(text)) return false;
  const status = String(customer.status || '').toLowerCase();
  return ['active', 'approved'].includes(status) || (typeof rpCustomerOrderReady === 'function' && rpCustomerOrderReady(customer));
}
function rpRestockConfidence(orderCount, shelf) {
  if (orderCount >= 3 && shelf && shelf.total != null) return 'High';
  if (orderCount >= 2 || (shelf && shelf.total != null)) return 'Medium';
  return 'Low';
}
function rpRestockCheckLabel(daysUntil, status) {
  if (status === 'High' || daysUntil <= 0) return 'Check today';
  if (daysUntil <= 2) return 'Check in 2 days';
  if (daysUntil <= 7) return 'Check this week';
  if (daysUntil <= 14) return 'Watch next week';
  return 'Healthy';
}
function rpCustomerRestockSignal(customerOrId, options = {}) {
  const customer = typeof customerOrId === 'object' ? customerOrId : (RP.cust ? RP.cust(customerOrId) : null);
  const today = rpRestockToday();
  const fallbackCadence = rpRestockClamp(options.fallbackCadenceDays || 21, 7, 60);
  if (!customer) {
    return { customer: null, eligible: false, status: 'Submitted', score: 0, label: 'No account', reasons: ['Customer not found'], confidence: 'Low' };
  }

  const orders = rpCustomerOrders(customer)
    .filter(order => !['Cancelled', 'Canceled', 'Closed'].includes(String(order.status || '')))
    .slice(0, 8);
  const exact = rpCustomerHasExactOrderAggregates(customer);
  const orderRows = rpRestockOrderDates(orders);
  const localLatest = orderRows[orderRows.length - 1] || null;
  const aggregateLastDate = exact ? rpRestockDate(customer.lastOrder) : null;
  const latestDate = aggregateLastDate || (localLatest && localLatest.date) || null;
  const aggregateLastOrderId = exact ? String(customer.lastOrderId || '').trim() : '';
  const latestOrder = exact
    ? (aggregateLastOrderId ? orders.find(order => String(order && order.id || '') === aggregateLastOrderId) || null : null)
    : (localLatest && localLatest.order) || null;
  const orderCount = exact ? Math.max(0, rpFiniteNumber(customer.orderCount)) : orders.length;
  const hasOrderHistory = orderCount > 0 || Boolean(latestDate);
  const intervals = [];
  for (let index = 1; index < orderRows.length; index += 1) {
    const gap = rpRestockDaysBetween(orderRows[index - 1].date, orderRows[index].date);
    if (gap != null && gap >= 3 && gap <= 120) intervals.push(gap);
  }
  const aggregateCadence = exact ? rpOptionalFiniteNumber(customer.cadenceDays) : null;
  const cadenceDays = rpRestockClamp((aggregateCadence && aggregateCadence > 0 ? aggregateCadence : null) || rpRestockMedian(intervals) || fallbackCadence, 7, 75);
  const recentOrders = orders.slice(0, Math.min(4, orders.length));
  const aggregateCases = exact ? rpOptionalFiniteNumber(customer.typicalOrderCases) : null;
  const avgCases = aggregateCases != null
    ? aggregateCases
    : recentOrders.length
      ? recentOrders.reduce((sum, order) => sum + Number((rpOrderTotals(order) || {}).cases || 0), 0) / recentOrders.length
      : 0;
  const velocity = cadenceDays ? avgCases / cadenceDays : 0;
  const aggregateDaysSince = exact ? rpOptionalFiniteNumber(customer.daysSince) : null;
  const daysSinceOrder = aggregateDaysSince != null ? aggregateDaysSince : (latestDate ? rpRestockDaysBetween(latestDate, today) : null);
  const nextOrderDate = latestDate ? rpRestockAddDays(latestDate, cadenceDays) : null;
  const proactiveDate = nextOrderDate ? rpRestockAddDays(nextOrderDate, -3) : today;
  const daysUntilCheck = hasOrderHistory && latestDate ? rpRestockDaysBetween(today, proactiveDate) : 0;
  const daysUntilExpectedOrder = nextOrderDate ? rpRestockDaysBetween(today, nextOrderDate) : null;
  const visits = rpRestockCustomerVisits(customer);
  const latestVisit = visits[0] || null;
  const daysSinceVisit = latestVisit ? rpRestockDaysBetween(latestVisit.date || latestVisit.visitedAt || latestVisit.visited_at || latestVisit.createdAt || latestVisit.created_at, today) : null;
  const shelf = rpRestockShelfSignal(visits);
  const shelfDays = shelf.total != null && velocity > 0 ? shelf.total / velocity : null;
  const orderReady = typeof rpCustomerOrderReady === 'function' ? rpCustomerOrderReady(customer) : true;
  const eligible = rpRestockCustomerEligible(customer);
  const reasons = [];
  let score = 0;

  if (!orderReady) {
    score += 8;
    reasons.push('Ordering not ready');
  }
  if (!hasOrderHistory) {
    score += orderReady ? 32 : 12;
    reasons.push(orderReady ? 'Ready for first order' : 'No order history');
  } else {
    if (daysUntilExpectedOrder != null && daysUntilExpectedOrder <= 0) {
      const overdue = Math.abs(daysUntilExpectedOrder);
      score += 34 + Math.min(24, overdue * 2);
      reasons.push(overdue ? `${overdue}d past usual reorder` : 'Usual reorder window is now');
    } else if (daysUntilCheck <= 0) {
      score += 24;
      reasons.push('Inside check-in window');
    } else if (daysUntilCheck <= 7) {
      score += 14;
      reasons.push(`${daysUntilCheck}d to check-in window`);
    }
    if (daysSinceOrder != null) reasons.push(`${daysSinceOrder}d since order`);
    if (avgCases > 0) reasons.push(`${Math.round(avgCases)} cs typical order`);
  }

  if (shelf.empty) {
    score += 42;
    reasons.unshift(`${shelf.empty} SKU out on shelf`);
  } else if (shelf.low) {
    score += 28;
    reasons.unshift(`${shelf.low} SKU low on shelf`);
  }
  if (shelfDays != null && shelfDays <= 7) {
    score += 20;
    reasons.push(`${Math.max(1, Math.round(shelfDays))}d shelf cover est.`);
  }
  if (latestVisit && latestVisit.followUp) {
    score += 16;
    reasons.push('Visit follow-up flagged');
  }
  if (!latestVisit && eligible) {
    score += 10;
    reasons.push('No shelf check logged');
  } else if (daysSinceVisit != null && daysSinceVisit >= Math.max(14, Math.round(cadenceDays * 0.75))) {
    score += 12;
    reasons.push(`${daysSinceVisit}d since shelf check`);
  }

  const cappedScore = Math.min(100, Math.round(score));
  const status = cappedScore >= 58 ? 'High' : cappedScore >= 28 ? 'Pending' : 'Submitted';
  const checkInDate = status === 'High' || daysUntilCheck <= 0 ? today : proactiveDate;
  const checkInYmd = rpAddDaysYmd(checkInDate, 0, customer);
  const label = rpRestockCheckLabel(daysUntilCheck, status);
  const cadenceLabel = hasOrderHistory ? `Usually every ${Math.round(cadenceDays)}d` : 'Learning first cadence';
  const confidence = rpRestockConfidence(orderCount, shelf);
  const uniqueReasons = Array.from(new Set(reasons.filter(Boolean))).slice(0, 5);

  return {
    avgCases: Math.round(avgCases),
    cadenceDays: Math.round(cadenceDays),
    cadenceLabel,
    checkInDate: checkInYmd,
    confidence,
    customer,
    dailyCases: Math.round(velocity * 10) / 10,
    daysSinceOrder,
    daysSinceVisit,
    daysUntilCheckIn: Number.isFinite(daysUntilCheck) ? Math.round(daysUntilCheck) : null,
    daysUntilExpectedOrder,
    eligible,
    label,
    lastOrder: latestOrder,
    lastOrderId: aggregateLastOrderId || String(latestOrder && latestOrder.id || ''),
    nextOrderDate: nextOrderDate ? rpAddDaysYmd(nextOrderDate, 0, customer) : null,
    reasons: uniqueReasons.length ? uniqueReasons : ['Healthy account'],
    score: cappedScore,
    shelf,
    shelfDays: shelfDays == null ? null : Math.round(shelfDays),
    status,
    summary: hasOrderHistory
      ? `${cadenceLabel}; last order ${daysSinceOrder == null ? 'recently' : `${daysSinceOrder}d ago`}.`
      : 'No reorder pattern yet; capture the first order and shelf count.',
    visit: latestVisit
  };
}
function rpRestockRecommendations(customers, options = {}) {
  const limit = Number.isFinite(Number(options.limit)) ? Number(options.limit) : 12;
  return (Array.isArray(customers) ? customers : (RP.CUSTOMERS || []))
    .map(customer => rpCustomerRestockSignal(customer, options))
    .filter(row => row && row.eligible !== false)
    .sort((a, b) => b.score - a.score || (a.daysUntilCheckIn ?? 999) - (b.daysUntilCheckIn ?? 999) || String(a.customer && a.customer.name || '').localeCompare(String(b.customer && b.customer.name || '')))
    .slice(0, limit);
}
function rpFieldTasks(role) {
  if (rpHasLiveAuth()) return RP.TASKS;
  const name = rpFieldUserName(role);
  return RP.TASKS.filter(t => t.owner === name);
}
function rpFieldStockRequests(role) {
  const list = RP.STOCK_REQUESTS || [];
  if (rpHasLiveAuth()) return list;
  const profile = rpAuthProfile();
  const id = profile.id || '';
  const name = rpFieldUserName(role);
  return list.filter(r =>
    !r.requestedBy
    || r.requestedBy === id
    || r.requestedBy === name
    || r.requester === name
    || rpFieldCoversTerritory(role, r.territory)
  );
}
function rpFieldReceipts(role) {
  const list = RP.FIELD_RECEIPTS || [];
  if (rpHasLiveAuth()) return list;
  const profile = rpAuthProfile();
  const id = profile.id || '';
  const name = rpFieldUserName(role);
  return list.filter(r =>
    !r.submittedBy
    || r.submittedBy === id
    || r.submittedBy === name
    || r.submitter === name
    || rpFieldCoversTerritory(role, r.territory)
  );
}
function rpFieldFirstCustomerId(role) {
  const customer = rpFieldCustomers(role)[0] || RP.CUSTOMERS[0];
  return customer ? customer.id : null;
}
function rpFieldDefaultLocation(role) {
  const profile = rpAuthProfile();
  const locationId = String(profile.defaultLocationId || profile.default_location_id || '').trim();
  if (!locationId) return null;
  return (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : []).find(l => l && l.active !== false && String(l.id || '') === locationId) || null;
}
function rpFieldMicroWarehouse(role) {
  const profile = rpAuthProfile();
  const profileId = String(profile.id || '').trim();
  const defaultLocationId = String(profile.defaultLocationId || profile.default_location_id || '').trim();
  const territories = rpFieldTerritories(role);
  const micros = (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : []).filter(l => l && l.type === 'Micro-Warehouse' && l.active !== false);
  const assignedMicros = profileId ? micros.filter(l => String(l.assignedUserId || '').trim() === profileId) : [];
  const territoryMicros = micros.filter(l => rpAreaMatches(territories, l.territory));
  const authorizedMicros = Array.from(new Map([...assignedMicros, ...territoryMicros].map(location => [location.id, location])).values());
  const configuredDefault = defaultLocationId ? authorizedMicros.find(location => String(location.id || '') === defaultLocationId) : null;
  if (configuredDefault) return configuredDefault;
  if (assignedMicros.length === 1) return assignedMicros[0];
  if (territoryMicros.length === 1) return territoryMicros[0];
  return null;
}
function rpFiniteNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}
const RP_DEFAULT_PRICE_LIST_NAME = 'Default Retail';
const RP_DEFAULT_CASE_PRICE = 36.50;
// MXA/MXB are historical identifiers and intentionally keep their original targets.
// New Mix B inventory must use an explicit NEW3 alias so old UI records are not remapped.
const RP_SKU_ALIAS_BY_KEY = {
  GC12: 'RP-12-GC', RPGC12: 'RP-12-GC', RP12GC: 'RP-12-GC', RPEDGC12: 'RP-12-GC', RPED12GC: 'RP-12-GC', RP12GINGERCITRUS: 'RP-12-GC', RPEDGC01: 'RP-CAN-GC',
  KL12: 'RP-12-KL', RPKL12: 'RP-12-KL', RP12KL: 'RP-12-KL', RPEDKL12: 'RP-12-KL', RPED12KL: 'RP-12-KL', RP12KEYLIME: 'RP-12-KL', RPEDKL01: 'RP-CAN-KL',
  BR12: 'RP-12-BR', BL12: 'RP-12-BR', RPBR12: 'RP-12-BR', RPBL12: 'RP-12-BR', RP12BR: 'RP-12-BR', RP12BL: 'RP-12-BR', RPEDBR12: 'RP-12-BR', RPED12BR: 'RP-12-BR', RPED12BL: 'RP-12-BR', RP12BLUERASPBERRY: 'RP-12-BR', RPEDBR01: 'RP-CAN-BR',
  BO12: 'RP-12-BO', RPBO12: 'RP-12-BO', RP12BO: 'RP-12-BO', RPEDBO12: 'RP-12-BO', RPED12BO: 'RP-12-BO', RP12BLOODORANGE: 'RP-12-BO', RPEDBL12: 'RP-12-BO', RPEDBL01: 'RP-CAN-BO',
  PL12: 'RP-12-PL', RPPL12: 'RP-12-PL', RP12PL: 'RP-12-PL', RPEDPL12: 'RP-12-PL', RPED12PL: 'RP-12-PL', RP12PINKLEMONADE: 'RP-12-PL', RPEDPL01: 'RP-CAN-PL',
  PM12: 'RP-12-PM', RPPM12: 'RP-12-PM', RP12PM: 'RP-12-PM', RPEDPM12: 'RP-12-PM', RPED12PM: 'RP-12-PM', RP12PEACHMANGO: 'RP-12-PM', RPEDPM01: 'RP-CAN-PM',
  MX12: 'RP-MIX-TRI', MIX12: 'RP-MIX-TRI', MXB: 'RP-MIX-TRI', MIXB: 'RP-MIX-TRI', RPMXB: 'RP-MIX-TRI', RPMXTRI: 'RP-MIX-TRI', RPMIXTRI: 'RP-MIX-TRI', RPMX: 'RP-MIX-TRI', RPEDMX12: 'RP-MIX-TRI', RPEDMX12B: 'RP-MIX-TRI', RPEDMXB12: 'RP-MIX-TRI', RPEDMIX12: 'RP-MIX-TRI', RPMIX12: 'RP-MIX-TRI',
  RPMXNEW3: 'RP-MIX-NEW3', RPMIXNEW3: 'RP-MIX-NEW3', RPEDMXNEW3: 'RP-MIX-NEW3', RPEDMX12C: 'RP-MIX-NEW3', MIXNEW3: 'RP-MIX-NEW3',
  MXA: 'RP-MIX-ALL6', MIXA: 'RP-MIX-ALL6', RPMXA: 'RP-MIX-ALL6', RPEDMX12A: 'RP-MIX-ALL6', RPEDMXA12: 'RP-MIX-ALL6', RPMIXALL6: 'RP-MIX-ALL6', RPMXALL6: 'RP-MIX-ALL6',
  GC: 'RP-CAN-GC', RPGC: 'RP-CAN-GC', RPEDGC: 'RP-CAN-GC', RPSINGLEGC: 'RP-CAN-GC', RPCANGC: 'RP-CAN-GC', RP1GC: 'RP-CAN-GC', RPGC1: 'RP-CAN-GC', RPEDGCSINGLE: 'RP-CAN-GC',
  KL: 'RP-CAN-KL', RPKL: 'RP-CAN-KL', RPEDKL: 'RP-CAN-KL', RPSINGLEKL: 'RP-CAN-KL', RPCANKL: 'RP-CAN-KL', RP1KL: 'RP-CAN-KL', RPKL1: 'RP-CAN-KL', RPEDKLSINGLE: 'RP-CAN-KL',
  BR: 'RP-CAN-BR', BL: 'RP-CAN-BR', RPBR: 'RP-CAN-BR', RPEDBR: 'RP-CAN-BR', RPBL: 'RP-CAN-BR', RPEDBL: 'RP-CAN-BR', RPSINGLEBR: 'RP-CAN-BR', RPSINGLEBL: 'RP-CAN-BR', RPCANBR: 'RP-CAN-BR', RPCANBL: 'RP-CAN-BR', RP1BR: 'RP-CAN-BR', RP1BL: 'RP-CAN-BR', RPBR1: 'RP-CAN-BR', RPBL1: 'RP-CAN-BR', RPEDBRSINGLE: 'RP-CAN-BR', RPEDBLSINGLE: 'RP-CAN-BR',
  BO: 'RP-CAN-BO', RPBO: 'RP-CAN-BO', RPEDBO: 'RP-CAN-BO', RPSINGLEBO: 'RP-CAN-BO', RPCANBO: 'RP-CAN-BO', RP1BO: 'RP-CAN-BO', RPBO1: 'RP-CAN-BO', RPEDBOSINGLE: 'RP-CAN-BO',
  PL: 'RP-CAN-PL', RPPL: 'RP-CAN-PL', RPEDPL: 'RP-CAN-PL', RPSINGLEPL: 'RP-CAN-PL', RPCANPL: 'RP-CAN-PL', RP1PL: 'RP-CAN-PL', RPPL1: 'RP-CAN-PL', RPEDPLSINGLE: 'RP-CAN-PL',
  PM: 'RP-CAN-PM', RPPM: 'RP-CAN-PM', RPEDPM: 'RP-CAN-PM', RPSINGLEPM: 'RP-CAN-PM', RPCANPM: 'RP-CAN-PM', RP1PM: 'RP-CAN-PM', RPPM1: 'RP-CAN-PM', RPEDPMSINGLE: 'RP-CAN-PM'
};
const RP_CASE_SKU_FLAVORS = {
  'RP-12-GC': 'ginger-citrus',
  'RP-12-KL': 'key-lime',
  'RP-12-BR': 'blue-raspberry',
  'RP-12-BO': 'blood-orange',
  'RP-12-PL': 'pink-lemonade',
  'RP-12-PM': 'peach-mango'
};
const RP_MIX_SKU_TRI = {
  'RP-MIX-TRI': ['blue-raspberry', 'ginger-citrus', 'key-lime'],
  'RP-MIX-NEW3': ['peach-mango', 'blood-orange', 'pink-lemonade'],
  'RP-MIX-ALL6': ['blue-raspberry', 'ginger-citrus', 'key-lime', 'peach-mango', 'blood-orange', 'pink-lemonade']
};
const RP_MIX_SKU_NAMES = {
  'RP-MIX-TRI': 'Variety Pack B | BR,GC,KL',
  'RP-MIX-NEW3': 'Variety Pack C | BO,PM,PL',
  'RP-MIX-ALL6': 'Variety Pack A | All 6 Flavours'
};
const RP_MIX_SKU_CODES = {
  'RP-MIX-TRI': 'RPEDMX12B',
  'RP-MIX-NEW3': 'RPEDMX12C',
  'RP-MIX-ALL6': 'RPEDMX12A'
};
const RP_MIX_SKU_UPCS = {
  'RP-MIX-TRI': '627146134200',
  'RP-MIX-NEW3': '627146134231',
  'RP-MIX-ALL6': '627146134156'
};
const RP_MIX_SKU_VISUAL_LABELS = {
  'RP-MIX-TRI': 'MIXB',
  'RP-MIX-NEW3': 'MIXC',
  'RP-MIX-ALL6': 'MIXA'
};
const RP_CASE_SKU_CODES = {
  'RP-12-GC': 'RPEDGC12', 'RP-12-KL': 'RPEDKL12', 'RP-12-BR': 'RPEDBR12',
  'RP-12-BO': 'RPEDBL12', 'RP-12-PL': 'RPEDPL12', 'RP-12-PM': 'RPEDPM12'
};
const RP_CASE_SKU_UPCS = {
  'RP-12-GC': '627146023641', 'RP-12-KL': '627146023689', 'RP-12-BR': '627146134170',
  'RP-12-BO': '627146023665', 'RP-12-PL': '627146023672', 'RP-12-PM': '627146134132'
};
const RP_CAN_SKU_FLAVORS = {
  'RP-CAN-GC': 'ginger-citrus',
  'RP-CAN-KL': 'key-lime',
  'RP-CAN-BR': 'blue-raspberry',
  'RP-CAN-BO': 'blood-orange',
  'RP-CAN-PL': 'pink-lemonade',
  'RP-CAN-PM': 'peach-mango'
};
const RP_CAN_SKU_CODES = {
  'RP-CAN-GC': 'RPEDGC01', 'RP-CAN-KL': 'RPEDKL01', 'RP-CAN-BR': 'RPEDBR01',
  'RP-CAN-BO': 'RPEDBL01', 'RP-CAN-PL': 'RPEDPL01', 'RP-CAN-PM': 'RPEDPM01'
};
const RP_CAN_SKU_UPCS = {
  'RP-CAN-GC': '627146023627', 'RP-CAN-KL': '627146023634', 'RP-CAN-BR': '627146023610',
  'RP-CAN-BO': '627146023603', 'RP-CAN-PL': '627146023658', 'RP-CAN-PM': '627146023696'
};
const RP_CAN_TO_CASE_SKU = {
  'RP-CAN-GC': 'RP-12-GC',
  'RP-CAN-KL': 'RP-12-KL',
  'RP-CAN-BR': 'RP-12-BR',
  'RP-CAN-BO': 'RP-12-BO',
  'RP-CAN-PL': 'RP-12-PL',
  'RP-CAN-PM': 'RP-12-PM'
};
const RP_SKU_LOT_CODE_DEFAULTS = {
  'RP-12-GC': 'RP-GC-',
  'RP-12-KL': 'RP-LIME-',
  'RP-12-BR': 'RP-BR-',
  'RP-12-BO': 'RP-BO-',
  'RP-12-PL': 'RP-PL-',
  'RP-12-PM': 'RP-PM-',
  'RP-CAN-GC': 'RP-GC-',
  'RP-CAN-KL': 'RP-LIME-',
  'RP-CAN-BR': 'RP-BR-',
  'RP-CAN-BO': 'RP-BO-',
  'RP-CAN-PL': 'RP-PL-',
  'RP-CAN-PM': 'RP-PM-',
  'RP-MIX-TRI': 'RP-VP-',
  'RP-MIX-NEW3': 'RP-VP-B-',
  'RP-MIX-ALL6': 'RP-VP-C-'
};
function rpSkuKey(value) {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}
function rpSkuFlavorCodeFromKey(key) {
  const value = String(key || '');
  if (/GINGER|CITRUS|\bGC\b/.test(value) || value.includes('GINGERCITRUS')) return 'GC';
  if (/KEYLIME|\bLIME\b|\bKL\b/.test(value) || value.includes('KEYLIME')) return 'KL';
  if (/BLUERASPBERRY|RASPBERRY|\bBR\b|\bBL\b/.test(value) || value.includes('BLUERASPBERRY')) return 'BR';
  if (/BLOODORANGE|\bBLOOD\b|\bBO\b/.test(value) || value.includes('BLOODORANGE')) return 'BO';
  if (/PINKLEMONADE|\bPINK\b|LEMONADE|\bPL\b/.test(value) || value.includes('PINKLEMONADE')) return 'PL';
  if (/PEACHMANGO|\bPEACH\b|\bMANGO\b|\bPM\b/.test(value) || value.includes('PEACHMANGO')) return 'PM';
  return '';
}
function rpCanonicalSkuId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const key = rpSkuKey(raw);
  const alias = RP_SKU_ALIAS_BY_KEY[key];
  if (alias) return alias;
  const direct = RP.sku(raw);
  if (direct) return direct.id;
  if (/ALL6|ALLSIX|VARIETY|MIXED12PACKC|MIXPACKC/.test(key)) return 'RP-MIX-ALL6';
  if (/NEW3|PMBOPL|PEACHMANGOBLOODORANGEPINKLEMONADE|PEACHMANGOPINKLEMONADEBLOODORANGE/.test(key)) return 'RP-MIX-NEW3';
  if (/MIXED|MIXER|TRAIL|BRGCKL|BLUERASPBERRYGINGERCITRUSKEYLIME/.test(key)) return 'RP-MIX-TRI';
  const flavorCode = rpSkuFlavorCodeFromKey(key);
  if (flavorCode) {
    const looksSingle = /SINGLE|1CAN|ONECAN|CAN|355ML|INDIVIDUAL/.test(key) && !/12PACK|PACK12|12CAN|CASE|MIXER|VARIETY/.test(key);
    return looksSingle ? `RP-CAN-${flavorCode}` : `RP-12-${flavorCode}`;
  }
  return raw;
}
function rpCanonicalInventorySkuId(value) {
  const canonicalId = rpCanonicalSkuId(value);
  return RP_CAN_TO_CASE_SKU[canonicalId] || canonicalId;
}
function rpSyntheticCaseSku(id) {
  const flavorId = RP_CASE_SKU_FLAVORS[id];
  const flavor = flavorId && RP.flavor(flavorId);
  return flavor ? { id, flavor: flavorId, name: `${flavor.name} 355mL`, pack: '12-Pack', price: rpDefaultCasePrice ? rpDefaultCasePrice() : RP_DEFAULT_CASE_PRICE, deposit: 1.20, code: RP_CASE_SKU_CODES[id] || rpSkuKey(id), upc: RP_CASE_SKU_UPCS[id] || '' } : null;
}
function rpSyntheticMixerSku(id) {
  const tri = RP_MIX_SKU_TRI[id];
  return tri ? { id, flavor: 'mixed', name: RP_MIX_SKU_NAMES[id] || 'Variety Pack', pack: '12-Pack', price: rpDefaultCasePrice ? rpDefaultCasePrice() : RP_DEFAULT_CASE_PRICE, deposit: 1.20, code: RP_MIX_SKU_CODES[id] || rpSkuKey(id), upc: RP_MIX_SKU_UPCS[id] || '', visualLabel: RP_MIX_SKU_VISUAL_LABELS[id] || 'MIX', customerVisible: true, active: true, tri } : null;
}
function rpSyntheticCanSku(id) {
  const flavorId = RP_CAN_SKU_FLAVORS[id];
  const flavor = flavorId && RP.flavor(flavorId);
  return flavor ? { id, flavor: flavorId, name: `${flavor.name} 355mL`, pack: 'Single Can', price: 0, deposit: 0.10, code: RP_CAN_SKU_CODES[id] || rpSkuKey(id), upc: RP_CAN_SKU_UPCS[id] || '' } : null;
}
function rpCleanLotCode(value) {
  const code = String(value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  if (!code) return '';
  return (code.endsWith('-') || code.endsWith('_')) ? code.slice(0, 80) : `${code}-`.slice(0, 80);
}
function rpSkuDefaultLotCode(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  const canonicalId = rpCanonicalInventorySkuId((item && item.id) || sku);
  if (RP_SKU_LOT_CODE_DEFAULTS[canonicalId]) return RP_SKU_LOT_CODE_DEFAULTS[canonicalId];
  if (/^RP-MIX-/i.test(canonicalId)) return 'RP-VP-';
  const flavorId = rpSkuFlavorId(item || canonicalId);
  const flavor = flavorId && RP.flavor(flavorId);
  const code = flavor && flavor.abbr ? flavor.abbr : String(canonicalId || '').replace(/^RP-12-/i, '').replace(/^RP-CAN-/i, '');
  if (!code || code === canonicalId) return 'RP-';
  return `RP-${String(code).toUpperCase()}-`;
}
function rpSkuLotCode(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  return rpCleanLotCode(item && item.lotCode) || rpSkuDefaultLotCode(item || sku);
}
function rpPriceLists() {
  return Array.isArray(RP.PRICE_LISTS) ? RP.PRICE_LISTS.filter(p => p && p.active !== false) : [];
}
function rpPriceListOptionNames(options) {
  const settings = options || {};
  if (settings.consumerOnly) return ['Consumer'];
  const liveNames = rpPriceLists()
    .map(priceList => String(priceList && priceList.name || '').trim())
    .filter(Boolean);
  const fallbackNames = ['Default Retail', 'Retailer Tier A', 'Retailer Tier B', 'Distributor'];
  const names = liveNames.length ? liveNames : fallbackNames;
  const currentName = String(settings.currentName || '').trim();
  if (currentName && !names.some(name => name.toLowerCase() === currentName.toLowerCase())) names.push(currentName);
  return names.filter((name, index) => {
    if (settings.excludeConsumer && retailPriceListIdentity(rpPriceListByName(name) || name) === 'consumer') return false;
    return names.findIndex(candidate => candidate.toLowerCase() === name.toLowerCase()) === index;
  });
}
function rpPriceListTerms(name, fallback = 'Prepaid') {
  const list = rpPriceListByName(name);
  const terms = String(list && list.terms || '').trim();
  return terms || fallback;
}
function rpAccountPaymentTermOptionNames(currentName = '') {
  const names = ['Prepaid', 'COD', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Distributor Terms'];
  rpPriceLists().forEach(priceList => {
    const terms = String(priceList && priceList.terms || '').trim();
    if (terms) names.push(terms);
  });
  const current = String(currentName || '').trim();
  if (current) names.push(current);
  return names.filter((name, index) => names.findIndex(candidate => candidate.toLowerCase() === name.toLowerCase()) === index);
}
function rpPriceListByName(name) {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return null;
  const lists = rpPriceLists();
  const exact = lists.find(p => String(p.name || '').trim().toLowerCase() === key);
  if (exact) return exact;
  const identity = value => String(value || '').trim().toLowerCase().replace(/\s*\|\s*\$?\d+(?:\.\d{1,2})?$/, '').trim();
  const byId = lists.find(p => p.id && String(p.id).toLowerCase() === key);
  if (byId) return byId;
  const matches = lists.filter(p => identity(p.name) === identity(key) || (p.aliases || []).some(alias => identity(alias) === identity(key)));
  return matches.length === 1 ? matches[0] : null;
}
function rpCustomerPricingLabel(customer) {
  const custom = rpCustomerCustomCasePrice(customer);
  const list = rpPriceListByName(customer && (customer.priceListId || customer.price_list_id || customer.tier)) || (!(customer && customer.tier) ? rpDefaultPriceList() : null);
  if (!custom && !list) return 'Price list needs updating';
  return `$${rpCustomerCasePrice(customer).toFixed(2)} per 12-pack case${custom ? ' · Custom price' : ''}`;
}
function rpPriceListOptionLabel(name) {
  const list = rpPriceListByName(name);
  return list ? `${list.name} — $${Number(list.skuPrice).toFixed(2)}/case` : name;
}
function rpDefaultPriceList() {
  return rpPriceListByName(RP_DEFAULT_PRICE_LIST_NAME) || rpPriceLists()[0] || null;
}
function rpDefaultPriceListName() {
  const list = rpDefaultPriceList();
  return list && list.name ? list.name : RP_DEFAULT_PRICE_LIST_NAME;
}
function rpCustomerPriceList(customerOrId) {
  const customer = typeof customerOrId === 'string' ? RP.cust(customerOrId) : customerOrId;
  return rpPriceListByName(customer && (customer.priceListId || customer.price_list_id || customer.tier)) || rpDefaultPriceList();
}
function rpCustomerPriceListName(customerOrId) {
  const list = rpCustomerPriceList(customerOrId);
  return list && list.name ? list.name : rpDefaultPriceListName();
}
function rpDefaultCasePrice() {
  const list = rpDefaultPriceList();
  const price = rpFiniteNumber(list && list.skuPrice, 0);
  return price > 0 ? price : RP_DEFAULT_CASE_PRICE;
}
function rpCustomerCustomCasePrice(customerOrId) {
  const customer = typeof customerOrId === 'string' ? RP.cust(customerOrId) : customerOrId;
  const price = rpFiniteNumber(customer && (customer.customCasePrice ?? customer.custom_case_price), 0);
  return price > 0 ? rpMoneyNumber(price) : 0;
}
function rpCustomerCasePrice(customerOrId) {
  const customPrice = rpCustomerCustomCasePrice(customerOrId);
  if (customPrice > 0) return customPrice;
  const list = rpCustomerPriceList(customerOrId);
  const price = rpFiniteNumber(list && list.skuPrice, 0);
  return price > 0 ? price : rpDefaultCasePrice();
}
function rpPortalSkuPrice(skuOrId) {
  const sku = typeof skuOrId === 'string' ? skuOrId : skuOrId && skuOrId.id;
  const canonicalId = rpCanonicalSkuId(sku);
  if (!canonicalId || !Array.isArray(RP.PORTAL_PRICE_MAP)) return 0;
  const row = RP.PORTAL_PRICE_MAP.find(item => rpCanonicalSkuId(item && item.sku) === canonicalId);
  const price = rpFiniteNumber(row && row.casePrice, 0);
  return price > 0 ? rpMoneyNumber(price) : 0;
}
function rpCustomerSkuPrice(customerOrId, skuOrId) {
  const portalPrice = rpPortalSkuPrice(skuOrId);
  if (portalPrice > 0) return portalPrice;
  const price = rpCustomerCasePrice(customerOrId);
  if (price > 0) return rpMoneyNumber(price);
  const sku = typeof skuOrId === 'string' ? rpSkuItem(skuOrId) : skuOrId;
  return rpMoneyNumber(sku && sku.price);
}
function rpSkuPrice(skuId, customerOrId = null) {
  if (customerOrId) return rpCustomerSkuPrice(customerOrId, skuId);
  const sku = rpSkuItem(skuId);
  return rpFiniteNumber(sku && sku.price, 0);
}
function rpSkuDisplayName(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  return String(item && item.name || sku || 'SKU').replace(/ 355m[lL]/, '');
}
function rpSkuCode(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  const canonicalId = rpCanonicalSkuId((item && item.id) || sku);
  return String(item && (item.code || item.sku) || RP_CASE_SKU_CODES[canonicalId] || RP_CAN_SKU_CODES[canonicalId] || RP_MIX_SKU_CODES[canonicalId] || canonicalId || '').trim();
}
function rpSkuUpc(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  const canonicalId = rpCanonicalSkuId((item && item.id) || sku);
  return String(item && item.upc || RP_CASE_SKU_UPCS[canonicalId] || RP_CAN_SKU_UPCS[canonicalId] || RP_MIX_SKU_UPCS[canonicalId] || '').trim();
}
function rpSkuExternalCode(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  const internalId = String(item && item.id || (typeof sku === 'string' ? sku : '') || '').trim();
  const canonicalId = rpCanonicalSkuId(internalId);
  const mappedCode = String(RP_CASE_SKU_CODES[canonicalId] || RP_CAN_SKU_CODES[canonicalId] || RP_MIX_SKU_CODES[canonicalId] || '').trim();
  const itemCode = String(item && (item.code || item.sku) || '').trim();
  const code = mappedCode || itemCode;
  return code && (!internalId || code.toUpperCase() !== internalId.toUpperCase()) ? code : '';
}
function rpSkuExternalUpc(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  const canonicalId = rpCanonicalSkuId((item && item.id) || sku);
  return String(RP_CASE_SKU_UPCS[canonicalId] || RP_CAN_SKU_UPCS[canonicalId] || RP_MIX_SKU_UPCS[canonicalId] || item && item.upc || '').trim();
}
function rpSkuExternalIdentity(sku) {
  const code = rpSkuExternalCode(sku);
  const upc = rpSkuExternalUpc(sku);
  return `${code || 'SKU unavailable'} - ${upc ? `UPC ${upc}` : 'UPC unavailable'}`;
}
function rpSkuItem(sku) {
  if (!sku) return null;
  if (typeof sku !== 'string') return sku;
  const canonicalId = rpCanonicalSkuId(sku);
  const item = RP.sku(canonicalId) || rpSyntheticCaseSku(canonicalId) || rpSyntheticMixerSku(canonicalId) || rpSyntheticCanSku(canonicalId);
  if (item && RP_MIX_SKU_TRI[canonicalId] && !Array.isArray(item.tri)) return { ...item, tri: RP_MIX_SKU_TRI[canonicalId] };
  if (item) return item;
  const flavor = RP.flavor(sku);
  return flavor ? { id: sku, flavor: sku, name: flavor.name } : null;
}
function rpSkuFlavorId(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  const flavor = item && (item.flavor || item.visualFlavor);
  if (RP.flavor(flavor)) return flavor;
  const canonicalId = rpCanonicalSkuId(item && item.id || sku);
  if (RP_CASE_SKU_FLAVORS[canonicalId]) return RP_CASE_SKU_FLAVORS[canonicalId];
  if (RP_CAN_SKU_FLAVORS[canonicalId]) return RP_CAN_SKU_FLAVORS[canonicalId];
  const flavorCode = rpSkuFlavorCodeFromKey(rpSkuKey([item && item.id, item && item.code, item && item.name, item && item.pack, sku].filter(Boolean).join(' ')));
  return flavorCode ? RP_CASE_SKU_FLAVORS[`RP-12-${flavorCode}`] || '' : '';
}
function rpSkuBadgeText(value, fallback = 'SKU') {
  const label = String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return (label || fallback).slice(0, 6);
}
function rpSkuVisualLabel(sku) {
  const item = rpSkuItem(sku);
  if (item && item.visualLabel) return rpSkuBadgeText(item.visualLabel, '');
  const flavor = item && RP.flavor(item.flavor);
  if (flavor && flavor.abbr) return rpSkuBadgeText(flavor.abbr);
  if (rpSkuIsMixer(item)) return 'MIX';
  const name = String(item && item.name || sku || '');
  const initials = rpSkuBadgeText(name.split(/[\s_-]+/).map(part => part.charAt(0)).join(''), '');
  if (initials.length >= 2) return initials.slice(0, 3);
  return rpSkuBadgeText((item && (item.code || item.id)) || sku || 'SKU').slice(0, 3);
}
function rpSkuVisualTextColor(color) {
  const hex = String(color || '').trim().replace('#', '');
  if (!/^[0-9A-F]{6}$/i.test(hex)) return '#fff';
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 0.72 ? 'var(--ink)' : '#fff';
}
function rpSkuVisualFlavor(sku) {
  const item = rpSkuItem(sku);
  const flavorId = rpSkuFlavorId(item || sku);
  if (flavorId) return flavorId;
  if (item && Array.isArray(item.tri)) {
    const first = item.tri.find(flavor => RP.flavor(flavor));
    if (first) return first;
  }
  return 'blue-raspberry';
}
function rpSkuVisualColor(sku) {
  const item = rpSkuItem(sku);
  const color = String(item && item.visualColor || '').trim();
  if (/^#(?:[0-9A-F]{3}|[0-9A-F]{6})$/i.test(color)) return color;
  const flavor = RP.flavor(rpSkuVisualFlavor(item || sku));
  if (flavor && flavor.color) return flavor.color;
  return rpSkuIsMixer(item) ? 'var(--navy)' : 'var(--amber)';
}
function rpSkuVisualMeta(sku) {
  const item = rpSkuItem(sku);
  const flavorId = rpSkuVisualFlavor(item || sku);
  const flavor = RP.flavor(flavorId);
  const color = rpSkuVisualColor(item || sku);
  return {
    label: rpSkuVisualLabel(item || sku),
    color,
    textColor: rpSkuVisualTextColor(color),
    name: item ? rpSkuDisplayName(item) : (flavor && flavor.name) || 'SKU',
    flavorId: flavor ? flavorId : 'blue-raspberry'
  };
}
function rpSkuIsMixer(sku) {
  const pack = String(sku && sku.pack || '').toLowerCase();
  const flavor = String(sku && sku.flavor || '').toLowerCase();
  return pack.includes('mix') || flavor === 'mixed';
}
function rpSkuLooksOperationalArtifact(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  const text = [item && item.id, item && item.code, item && item.sku, item && item.name, item && item.pack, item && item.unit, sku]
    .map(value => String(value || '').toLowerCase())
    .join(' ');
  return /\b(deposit|container|recycling|fee|shipping|delivery|discount|tip|gift\s*card)\b/.test(text);
}
function rpSkuIsOrderable(sku) {
  if (!sku || !sku.id || sku.active === false) return false;
  const price = rpFiniteNumber(sku.price, NaN);
  return Number.isFinite(price) && price > 0;
}
function rpSkuLooksCustomerSellable(sku) {
  if (!sku || !sku.id || sku.active === false) return false;
  if (rpSkuLooksOperationalArtifact(sku)) return false;
  if (String(sku.unit || '').toLowerCase() === 'can') return false;
  if (String(sku.pack || '').toLowerCase().includes('single')) return false;
  return /^RP-12-[A-Z0-9]+$/i.test(String(sku.id || ''))
    || /^RP-MIX-[A-Z0-9]+$/i.test(String(sku.id || ''))
    || /\b(12\s*[- ]?pack|mixer\s*12|case)\b/i.test(String(sku.pack || ''));
}
function rpSkuCustomerVisible(sku) {
  const item = rpSkuItem(sku);
  if (!item || item.active === false) return false;
  if (item.customerVisible != null) return item.customerVisible !== false;
  return rpSkuLooksCustomerSellable(item);
}
function rpCustomerCatalogSkus(options = {}) {
  return rpOrderableSkus(options).filter(rpSkuCustomerVisible);
}
function rpSkuIsManualOrderable(sku) {
  const item = rpSkuItem(sku);
  return rpSkuIsOrderable(item) && rpSkuLooksCustomerSellable(item);
}
function rpSkuSort(a, b) {
  const ap = rpSkuIsMixer(a) ? 2 : 1;
  const bp = rpSkuIsMixer(b) ? 2 : 1;
  if (ap !== bp) return ap - bp;
  return String(a.name || a.id).localeCompare(String(b.name || b.id));
}
function rpOrderableSkus(options = {}) {
  const includeIds = new Set((options.includeIds || []).filter(Boolean).map(rpCanonicalSkuId));
  return (Array.isArray(RP.SKUS) ? RP.SKUS : [])
    .filter(sku => rpSkuIsOrderable(sku) || includeIds.has(rpCanonicalSkuId(sku.id)))
    .slice()
    .sort(rpSkuSort);
}
function rpSkuIsInventoryProduct(sku) {
  const item = typeof sku === 'string' ? rpSkuItem(sku) : sku;
  if (!item || !item.id || item.active === false) return false;
  const canonicalId = rpCanonicalInventorySkuId(item.id);
  const pack = String(item.pack || '').toLowerCase();
  const unit = String(item.unit || '').toLowerCase();
  if (rpSkuLooksOperationalArtifact(item)) return false;
  if (unit === 'can' || pack.includes('single')) return false;
  return /^RP-12-[A-Z0-9]+$/i.test(canonicalId)
    || /^RP-MIX-[A-Z0-9]+$/i.test(canonicalId)
    || /\b(12\s*[- ]?pack|case)\b/i.test(String(item.pack || ''));
}
function rpInventorySkus(options = {}) {
  const includeIds = new Set((options.includeIds || []).filter(Boolean).map(rpCanonicalInventorySkuId));
  const byId = new Map();
  (Array.isArray(RP.SKUS) ? RP.SKUS : []).forEach(sku => {
    const canonicalId = rpCanonicalInventorySkuId(sku && sku.id);
    if (!canonicalId) return;
    const canonicalItem = rpSkuItem(canonicalId);
    const item = canonicalItem ? {
      ...sku,
      ...canonicalItem,
      price: rpFiniteNumber(canonicalItem.price, 0) > 0 ? canonicalItem.price : sku.price
    } : { ...sku, id: canonicalId };
    if (rpSkuIsInventoryProduct(item)) byId.set(canonicalId, { ...item, id: canonicalId });
  });
  includeIds.forEach(id => {
    if (byId.has(id)) return;
    const item = rpSkuItem(id);
    if (rpSkuIsInventoryProduct(item)) byId.set(id, item);
  });
  return Array.from(byId.values()).sort(rpSkuSort);
}
function rpManualOrderSkus(options = {}) {
  const seen = new Set();
  const rows = [];
  rpOrderableSkus(options).forEach(sku => {
    if (!rpSkuLooksCustomerSellable(sku)) return;
    const canonicalId = rpCanonicalSkuId(sku.id);
    if (!canonicalId || seen.has(canonicalId)) return;
    const canonicalItem = rpSkuItem(canonicalId);
    const row = canonicalItem && rpSkuLooksCustomerSellable(canonicalItem)
      ? { ...sku, ...canonicalItem, price: rpFiniteNumber(canonicalItem.price, 0) > 0 ? canonicalItem.price : sku.price }
      : { ...sku, id: canonicalId };
    rows.push(row);
    seen.add(canonicalId);
  });
  return rows.sort(rpSkuSort);
}
function rpCasePackSkus(options = {}) {
  return rpOrderableSkus(options).filter(sku => !rpSkuIsMixer(sku));
}
function rpMixerSkus(options = {}) {
  return rpOrderableSkus(options).filter(rpSkuIsMixer);
}
function rpInventoryAvailableCases(skuId, locId) {
  const canonicalId = rpCanonicalInventorySkuId(skuId);
  const rows = Array.isArray(RP.INV) ? RP.INV.filter(row => row && String(row.unit || 'case').toLowerCase() !== 'can' && rpCanonicalInventorySkuId(row.sku) === canonicalId && (!locId || row.loc === locId)) : [];
  if (!rows.length) return locId ? 0 : null;
  return rows.reduce((sum, row) => {
    const status = String(row.status || 'Available').toLowerCase();
    if (!['available', 'fronted to rep', 'purchased by rep'].includes(status)) return sum;
    return sum + Math.max(0, rpFiniteNumber(row.qty));
  }, 0);
}
const RP_GIVEAWAY_STOCK_STATUSES = new Set(['available', 'fronted to rep', 'purchased by rep', 'sample', 'sample / demo']);
function rpInventoryGiveawayCases(skuId, locId) {
  const canonicalId = rpCanonicalInventorySkuId(skuId);
  const rows = Array.isArray(RP.INV) ? RP.INV.filter(row => row && String(row.unit || 'case').toLowerCase() !== 'can' && rpCanonicalInventorySkuId(row.sku) === canonicalId && (!locId || row.loc === locId)) : [];
  if (!rows.length) return locId ? 0 : null;
  return rows.reduce((sum, row) => {
    const status = String(row.status || 'Available').toLowerCase();
    if (!RP_GIVEAWAY_STOCK_STATUSES.has(status)) return sum;
    return sum + Math.max(0, rpFiniteNumber(row.qty));
  }, 0);
}
function rpAvailabilityLabel(skuId, locId) {
  const qty = rpInventoryAvailableCases(skuId, locId);
  if (qty == null) return 'In stock';
  if (qty <= 0) return 'Out of stock';
  if (qty < 15) return 'Low stock';
  return 'In stock';
}
const RP_ORDER_SOURCE_TYPES = new Set(['Main Warehouse', 'Micro-Warehouse', 'Distributor', 'FBA']);
const RP_OPEN_ORDER_STOCK_STATUSES = new Set(['Requested', 'Submitted', 'Needs Approval', 'On Hold', 'Approved', 'Allocated', 'Picked', 'Dispatched']);
function rpTerritoryKey(value) {
  return String(value || '').trim().toLowerCase();
}
function rpDeliveryMethodIsFreight(value) {
  const text = rpTerritoryKey(value).replace(/[\s_-]+/g, ' ');
  return ['carrier', 'freight', 'carrier freight', 'carrier / freight', 'shipping', 'ship'].includes(text);
}
function rpLocationIsCentralFreightSource(location) {
  const type = String(location && (location.type || location.location_type) || '').trim();
  if (type !== 'Main Warehouse') return false;
  const text = [location && location.id, location && location.name, location && location.code, location && location.territory]
    .map(value => rpTerritoryKey(value))
    .join(' ');
  return text.includes('jori') || text.includes('calgary') || text.includes('yyc');
}
function rpLocationCanFulfillTerritory(location, territory, options = {}) {
  // Pickup happens at the selected warehouse, regardless of the customer's home territory.
  if (['customer pickup', 'customer pick up', 'pickup', 'pick up'].includes(rpTerritoryKey(options.deliveryMethod).replace(/[\s_-]+/g, ' '))) return true;
  if (rpLocationIsCentralFreightSource(location) && rpDeliveryMethodIsFreight(options.deliveryMethod)) return true;
  const locTerritory = rpTerritoryKey(location && location.territory);
  const orderTerritories = rpAreaNames(territory);
  if (!locTerritory || !orderTerritories.length) return true;
  if (['all', 'national', 'shared', 'unassigned'].includes(locTerritory)) return true;
  return orderTerritories.some(area => locTerritory === rpTerritoryKey(area));
}
function rpLocationAssignedToCustomerRep(location, customer) {
  if (!location || !customer) return false;
  const repId = String(customer.repId || '').trim();
  const assignedUserId = String(location.assignedUserId || '').trim();
  if (repId && assignedUserId && repId === assignedUserId) return true;
  const repName = String(customer.rep || '').trim().toLowerCase();
  const locationRep = String(location.rep || '').trim().toLowerCase();
  return Boolean(repName && locationRep && repName === locationRep);
}
function rpLocationCanFulfillCustomer(location, customerOrTerritory, options = {}) {
  if (customerOrTerritory && typeof customerOrTerritory === 'object') {
    return rpLocationAssignedToCustomerRep(location, customerOrTerritory)
      || rpLocationCanFulfillTerritory(location, customerOrTerritory.territory, options);
  }
  return rpLocationCanFulfillTerritory(location, customerOrTerritory, options);
}
function rpOrderSourceLocations() {
  return (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : [])
    .filter(location => location && location.id && location.active !== false && RP_ORDER_SOURCE_TYPES.has(location.type));
}
function rpOrderLineCommittedCaseEquivalent(line) {
  if (!line) return 0;
  const qty = rpFiniteNumber(line.qty ?? line.quantity);
  const explicitCaseQty = rpFiniteNumber(line.caseQty ?? line.caseQuantity ?? line.case_quantity, NaN);
  const sku = rpSkuItem(line.sku);
  const unit = String(line.unit || (sku && sku.unit) || '').toLowerCase();
  return Number.isFinite(explicitCaseQty) && explicitCaseQty > 0 ? explicitCaseQty : (unit === 'can' || unit === 'cans' ? qty / 12 : qty);
}
function rpCachedOrderCommittedCases(order, canonicalSkuId, locId, selectedLot = null) {
  if (!order || order.source !== locId || !RP_OPEN_ORDER_STOCK_STATUSES.has(order.status)) return 0;
  const normalizedLot = selectedLot == null ? null : String(selectedLot || '').trim().toUpperCase();
  return (order.lines || []).reduce((sum, line) => {
    const lineSkuId = line && rpCanonicalInventorySkuId(line.inventorySku || line.inventorySkuId || line.sku);
    if (lineSkuId !== canonicalSkuId) return sum;
    const lineLot = String(line && line.lot || '').trim().toUpperCase();
    if (normalizedLot && lineLot && lineLot !== normalizedLot) return sum;
    return sum + rpOrderLineCommittedCaseEquivalent(line);
  }, 0);
}
function rpOrderCommittedCases(skuId, locId, options = {}) {
  if (!locId) return 0;
  const canonicalId = rpCanonicalInventorySkuId(skuId);
  const excludeId = String(options.excludeOrderId || '');
  if (rpHasLiveAuth()) {
    const total = (Array.isArray(RP.ORDER_STOCK_COMMITMENTS) ? RP.ORDER_STOCK_COMMITMENTS : []).reduce((sum, row) => {
      if (!row) return sum;
      const sourceLocationId = String(row.sourceLocationId || row.source_location_id || '');
      const rowSkuId = rpCanonicalInventorySkuId(row.sku || row.sku_id);
      if (sourceLocationId !== locId || rowSkuId !== canonicalId) return sum;
      const quantity = Math.max(0, rpFiniteNumber(row.quantity));
      const unit = String(row.unit || 'case').toLowerCase();
      return sum + (unit === 'can' || unit === 'cans' ? quantity / 12 : quantity);
    }, 0);
    const excludedOrder = excludeId
      ? (Array.isArray(RP.ORDERS) ? RP.ORDERS : []).find(order => String(order && order.id || '') === excludeId)
      : null;
    const excluded = rpCachedOrderCommittedCases(excludedOrder, canonicalId, locId);
    return Math.max(0, total - excluded);
  }
  return (Array.isArray(RP.ORDERS) ? RP.ORDERS : []).reduce((sum, order) => {
    if (!order || order.id === excludeId || order.source !== locId || !RP_OPEN_ORDER_STOCK_STATUSES.has(order.status)) return sum;
    return sum + (order.lines || []).reduce((lineSum, line) => {
      const lineSkuId = line && rpCanonicalInventorySkuId(line.inventorySku || line.inventorySkuId || line.sku);
      if (lineSkuId !== canonicalId) return lineSum;
      return lineSum + rpOrderLineCommittedCaseEquivalent(line);
    }, 0);
  }, 0);
}
function rpOrderAvailableCases(skuId, locId, options = {}) {
  const onHand = rpInventoryAvailableCases(skuId, locId);
  if (onHand == null) return null;
  return Math.max(0, Math.floor(onHand - rpOrderCommittedCases(skuId, locId, options)));
}
function rpOrderAvailabilityLabel(skuId, locId, options = {}) {
  const qty = rpOrderAvailableCases(skuId, locId, options);
  if (qty == null) return 'In stock';
  if (qty <= 0) return 'Out of stock';
  if (qty < 15) return 'Low stock';
  return 'In stock';
}
function rpLocationSellableCases(locId, options = {}) {
  let known = false;
  const total = rpManualOrderSkus().reduce((sum, sku) => {
    const value = rpOrderAvailableCases(sku.id, locId, options);
    if (value == null) return sum;
    known = true;
    return sum + value;
  }, 0);
  return known ? Math.round(total) : null;
}
function rpDefaultOrderSourceForCustomer(customer, fallback = 'WH-SQ', options = {}) {
  const sources = rpOrderSourceLocations();
  const matching = sources.filter(location => rpLocationCanFulfillCustomer(location, customer, options));
  const assigned = sources.filter(location => rpLocationAssignedToCustomerRep(location, customer));
  const availableByLocation = new Map(sources.map(location => [location.id, rpLocationSellableCases(location.id)]));
  const sourceCases = location => rpFiniteNumber(availableByLocation.get(location && location.id));
  const hasStock = location => sourceCases(location) > 0;
  const byMostStock = (a, b) => sourceCases(b) - sourceCases(a) || (a.type === 'Main Warehouse' ? 0 : 1) - (b.type === 'Main Warehouse' ? 0 : 1) || String(a.name || a.id).localeCompare(String(b.name || b.id));
  const stockedAssigned = assigned.filter(hasStock).sort(byMostStock);
  const stockedMatching = matching.filter(hasStock).sort(byMostStock);
  const stockedSources = sources.filter(hasStock).sort(byMostStock);
  const fallbackLocation = sources.find(location => location.id === fallback);
  const preferred = stockedAssigned[0]
    || stockedMatching[0]
    || matching.find(location => location.type === 'Main Warehouse')
    || matching[0]
    || (fallbackLocation && hasStock(fallbackLocation) ? fallbackLocation : null)
    || stockedSources[0]
    || fallbackLocation
    || sources[0];
  return preferred ? preferred.id : fallback;
}
function rpMoneyNumber(value) {
  return Math.round(rpFiniteNumber(value) * 100) / 100;
}
function rpPlainObjectUi(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
const RP_DELIVERY_METHOD_OPTIONS = ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight'];
function rpNormalizeDeliveryMethod(value, fallback = 'Ranch Delivery') {
  const text = String(value || '').trim();
  if (!text) return fallback;
  const normalized = text.toLowerCase().replace(/[\s_-]+/g, ' ');
  if (['pickup', 'customer pickup', 'pick up', 'customer pick up'].includes(normalized)) return 'Customer Pickup';
  if (['carrier', 'freight', 'carrier freight', 'carrier / freight', 'shipping', 'ship'].includes(normalized)) return 'Carrier / Freight';
  if (['ranch delivery', 'delivery', 'local delivery'].includes(normalized)) return 'Ranch Delivery';
  return RP_DELIVERY_METHOD_OPTIONS.includes(text) ? text : fallback;
}
function rpCustomerDefaultDeliveryMethod(customer, fallback = 'Ranch Delivery') {
  const onboarding = rpPlainObjectUi(customer && (customer.onboardingQuestionnaire || customer.onboarding_questionnaire));
  return rpNormalizeDeliveryMethod(
    onboarding.defaultDeliveryMethod
    || onboarding.default_delivery_method
    || (customer && (customer.defaultDeliveryMethod || customer.default_delivery_method)),
    fallback
  );
}
function rpCustomerDeliveryNeedsAddress(method) {
  return rpNormalizeDeliveryMethod(method) !== 'Customer Pickup';
}
function rpAddressFromCustomerLocation(location, customer) {
  const loc = rpPlainObjectUi(location);
  const ship = rpPlainObjectUi(loc.shippingAddress || loc.shipping_address);
  const cust = rpPlainObjectUi(customer);
  const custShip = rpPlainObjectUi(cust.shippingAddress || cust.shipping_address);
  return String(
    loc.address
    || ship.address
    || ship.address1
    || cust.address
    || custShip.address
    || custShip.address1
    || ''
  ).trim();
}
function rpCustomerHasDeliveryAddress(customer, location) {
  return Boolean(rpAddressFromCustomerLocation(location, customer));
}
function rpCustomerDeliveryWarning(customer, method, location) {
  const normalized = rpNormalizeDeliveryMethod(method);
  if (!rpCustomerDeliveryNeedsAddress(normalized) || rpCustomerHasDeliveryAddress(customer, location)) return '';
  const name = String((customer && (customer.name || customer.banner)) || 'This customer').trim();
  return `${name} is set to ${normalized}, but needs a ship-to address before delivery orders can be submitted.`;
}
function rpCustomerWithDefaultDelivery(customer, method) {
  const normalized = rpNormalizeDeliveryMethod(method);
  const onboarding = rpPlainObjectUi(customer && (customer.onboardingQuestionnaire || customer.onboarding_questionnaire));
  return {
    ...(customer || {}),
    defaultDeliveryMethod: normalized,
    onboardingQuestionnaire: { ...onboarding, defaultDeliveryMethod: normalized }
  };
}
const RP_ACCOUNT_CREDIT_VOID_STATUSES = new Set(['void', 'voided', 'cancelled', 'canceled']);
const RP_ACCOUNT_CREDIT_CLOSED_STATUSES = new Set(['applied', 'closed', 'refunded', 'settled', 'paid', 'paid out']);
function rpCustomerCreditKind(row) {
  const normalized = String((row && (row.kind || row.type || row.direction || row.category)) || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return ['debt', 'debit', 'charge', 'negative_credit', 'balance_due', 'receivable'].includes(normalized) ? 'debt' : 'credit';
}
function rpCustomerCreditSettlements(row) {
  const source = Array.isArray(row && row.settlements) ? row.settlements : (Array.isArray(row && row.settlement_events) ? row.settlement_events : []);
  const settlements = source
    .filter(item => item && rpFiniteNumber(item.amount) > 0)
    .map(item => ({
      amount: rpMoneyNumber(item.amount),
      createdAt: String(item.createdAt || item.created_at || ''),
      createdBy: String(item.createdBy || item.created_by || ''),
      date: String(item.date || '').slice(0, 10),
      id: String(item.id || ''),
      method: String(item.method || 'cash'),
      note: String(item.note || ''),
      reason: String(item.reason || 'Account credit payout'),
      reference: String(item.reference || '')
    }));
  const legacyAmount = rpMoneyNumber((row && (row.settlementAmount || row.settlement_amount)) || 0);
  if (legacyAmount > 0 && settlements.length === 0) {
    settlements.push({
      amount: legacyAmount,
      createdAt: String((row && (row.settledAt || row.settled_at || row.updatedAt || row.updated_at)) || ''),
      createdBy: String((row && (row.settledBy || row.settled_by)) || ''),
      date: String((row && (row.settlementDate || row.settlement_date || row.date)) || '').slice(0, 10),
      id: String((row && (row.settlementId || row.settlement_id)) || ''),
      method: String((row && (row.settlementMethod || row.settlement_method)) || 'cash'),
      note: String((row && (row.settlementNote || row.settlement_note)) || ''),
      reason: String((row && (row.settlementReason || row.settlement_reason)) || 'Account credit payout'),
      reference: String((row && (row.settlementReference || row.settlement_reference)) || '')
    });
  }
  return settlements.sort((a, b) => String(b.date || b.createdAt || '').localeCompare(String(a.date || a.createdAt || '')));
}
function rpCustomerCreditSettledAmount(row) {
  return rpMoneyNumber(rpCustomerCreditSettlements(row).reduce((sum, settlement) => sum + rpFiniteNumber(settlement.amount), 0));
}
function rpCustomerCreditOpenAmount(row) {
  return rpMoneyNumber(Math.max(0, rpFiniteNumber(row && row.amount) - rpCustomerCreditSettledAmount(row)));
}
function rpCustomerCreditIsOpen(row) {
  const status = String((row && row.status) || '').trim().toLowerCase();
  if (RP_ACCOUNT_CREDIT_VOID_STATUSES.has(status)) return false;
  const openAmount = rpCustomerCreditOpenAmount(row);
  if (openAmount <= 0.005) return false;
  if (RP_ACCOUNT_CREDIT_CLOSED_STATUSES.has(status) && rpCustomerCreditSettledAmount(row) <= 0.005) return false;
  return true;
}
function rpCustomerAccountCredits(customer) {
  const overrides = rpPlainObjectUi(customer && customer.invoiceOverrides);
  const rows = Array.isArray(overrides.accountCredits) ? overrides.accountCredits : (Array.isArray(overrides.account_credits) ? overrides.account_credits : []);
  return rows
    .filter(row => row && rpFiniteNumber(row.amount) > 0)
    .map(row => {
      const credit = {
        amount: rpMoneyNumber(row.amount),
        createdAt: String(row.createdAt || row.created_at || ''),
        createdBy: String(row.createdBy || row.created_by || ''),
        date: String(row.date || '').slice(0, 10),
        id: String(row.id || ''),
        kind: rpCustomerCreditKind(row),
        note: String(row.note || ''),
        reason: String(row.reason || (rpCustomerCreditKind(row) === 'debt' ? 'Account debt' : 'Account credit')),
        reference: String(row.reference || ''),
        settlements: rpCustomerCreditSettlements(row),
        source: String(row.source || 'manual'),
        sourceInvoiceNumber: String(row.sourceInvoiceNumber || row.source_invoice_number || ''),
        sourceOrderId: String(row.sourceOrderId || row.source_order_id || ''),
        status: String(row.status || 'Open')
      };
      credit.settledAmount = rpCustomerCreditSettledAmount(credit);
      credit.openAmount = rpCustomerCreditOpenAmount(credit);
      return credit;
    })
    .filter(row => row.kind === 'credit')
    .sort((a, b) => String(b.date || b.createdAt || '').localeCompare(String(a.date || a.createdAt || '')));
}
function rpCustomerAccountCreditBalance(customer) {
  return rpMoneyNumber(rpCustomerAccountCredits(customer).reduce((sum, row) => {
    return rpCustomerCreditIsOpen(row) ? sum + rpFiniteNumber(row.openAmount) : sum;
  }, 0));
}
function rpCustomerAccountDebts(customer) {
  const overrides = rpPlainObjectUi(customer && customer.invoiceOverrides);
  const rows = Array.isArray(overrides.accountCredits) ? overrides.accountCredits : (Array.isArray(overrides.account_credits) ? overrides.account_credits : []);
  return rows
    .filter(row => row && rpFiniteNumber(row.amount) > 0)
    .map(row => {
      const debt = {
        amount: rpMoneyNumber(row.amount),
        createdAt: String(row.createdAt || row.created_at || ''),
        createdBy: String(row.createdBy || row.created_by || ''),
        date: String(row.date || '').slice(0, 10),
        id: String(row.id || ''),
        kind: rpCustomerCreditKind(row),
        note: String(row.note || ''),
        reason: String(row.reason || 'Account debt'),
        reference: String(row.reference || ''),
        settlements: rpCustomerCreditSettlements(row),
        source: String(row.source || 'manual'),
        sourceInvoiceNumber: String(row.sourceInvoiceNumber || row.source_invoice_number || ''),
        sourceOrderId: String(row.sourceOrderId || row.source_order_id || ''),
        status: String(row.status || 'Open')
      };
      debt.settledAmount = rpCustomerCreditSettledAmount(debt);
      debt.openAmount = rpCustomerCreditOpenAmount(debt);
      return debt;
    })
    .filter(row => row.kind === 'debt')
    .sort((a, b) => String(b.date || b.createdAt || '').localeCompare(String(a.date || a.createdAt || '')));
}
function rpCustomerAccountDebtBalance(customer) {
  return rpMoneyNumber(rpCustomerAccountDebts(customer).reduce((sum, row) => {
    return rpCustomerCreditIsOpen(row) ? sum + rpFiniteNumber(row.openAmount) : sum;
  }, 0));
}
function rpStripeVault(customer) {
  const overrides = rpPlainObjectUi(customer && customer.invoiceOverrides);
  const vault = rpPlainObjectUi(overrides.stripe || overrides.stripeVault || overrides.stripe_vault);
  const paymentMethods = Array.isArray(vault.paymentMethods) ? vault.paymentMethods : (Array.isArray(vault.payment_methods) ? vault.payment_methods : []);
  return {
    customerId: String(vault.customerId || vault.customer_id || vault.stripeCustomerId || vault.stripe_customer_id || ''),
    defaultPaymentMethodId: String(vault.defaultPaymentMethodId || vault.default_payment_method_id || vault.defaultPaymentMethod || vault.default || ''),
    paymentMethods
  };
}
function rpStripeDefaultCard(customer) {
  const vault = rpStripeVault(customer);
  const active = vault.paymentMethods.filter(row => row && String(row.provider || 'stripe') === 'stripe' && String(row.status || 'active').toLowerCase() === 'active');
  const wanted = vault.defaultPaymentMethodId;
  return active.find(row => String(row.id || '') === wanted) || active[0] || null;
}
function rpStripeCardLabel(card) {
  if (!card) return 'No saved card';
  const brand = String(card.brand || 'Card').trim();
  const last4 = String(card.last4 || card.last_4 || '').trim();
  const expMonth = Number(card.expMonth || card.exp_month || 0);
  const expYear = Number(card.expYear || card.exp_year || 0);
  const expiry = expMonth && expYear ? ` exp ${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}` : '';
  return `${brand}${last4 ? ` ending ${last4}` : ''}${expiry}`;
}
let RP_STRIPE_SDK_PROMISE = null;
function rpLoadStripeSdk() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (RP_STRIPE_SDK_PROMISE) return RP_STRIPE_SDK_PROMISE;
  RP_STRIPE_SDK_PROMISE = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
    const script = existing || document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => window.Stripe ? resolve(window.Stripe) : reject(new Error('Stripe.js did not load.'));
    script.onerror = () => {
      RP_STRIPE_SDK_PROMISE = null;
      reject(new Error('Could not load Stripe.js.'));
    };
    if (!existing) document.head.appendChild(script);
  });
  return RP_STRIPE_SDK_PROMISE;
}
function rpOwn(obj, key) {
  return obj && Object.prototype.hasOwnProperty.call(obj, key);
}
function rpAdjustmentType(adjustment) {
  return String((adjustment && (adjustment.type || adjustment.adjustmentType || adjustment.adjustment_type)) || '').toLowerCase();
}
function rpAdjustmentTotal(order, types) {
  const wanted = new Set((Array.isArray(types) ? types : [types]).map(type => String(type).toLowerCase()));
  return rpMoneyNumber((order && order.adjustments || []).reduce((sum, adjustment) => {
    return wanted.has(rpAdjustmentType(adjustment)) ? sum + rpFiniteNumber(adjustment.amount) : sum;
  }, 0));
}
function rpExemptionEnabled(values, code) {
  const source = values && typeof values === 'object' && !Array.isArray(values) ? values : {};
  const direct = source[code] ?? source[String(code).toUpperCase()] ?? source[String(code).toLowerCase()];
  return direct === true || String(direct || '').toLowerCase() === 'true';
}
function rpFeePolicyExempt(values) {
  return rpExemptionEnabled(values, 'CONTAINER_FEE') || rpExemptionEnabled(values, 'CONTAINER_DEPOSIT') || rpExemptionEnabled(values, 'RECYCLING_FEE');
}
function rpOverrideRate(values, code, fallback) {
  const source = values && typeof values === 'object' && !Array.isArray(values) ? values : {};
  const direct = source[code] ?? source[String(code).toUpperCase()] ?? source[String(code).toLowerCase()];
  if (direct == null || direct === '') return fallback;
  const parsed = Number(direct);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}
function rpTaxBaseForRule(rule, totals) {
  const tokens = String(rule && (rule.appliesTo || rule.applies_to) || 'goods').toLowerCase().split(/[,+\s]+/).filter(Boolean);
  const targets = new Set(tokens.length ? tokens : ['goods']);
  let base = 0;
  if (targets.has('goods') || targets.has('subtotal')) base += totals.sub;
  if (targets.has('fee') || targets.has('fees')) base += totals.fee;
  if (targets.has('deposit') || targets.has('deposits')) base += totals.dep;
  return rpMoneyNumber(base);
}
function rpCustomerPstNumber(customer) {
  const onboarding = customer && customer.onboardingQuestionnaire && typeof customer.onboardingQuestionnaire === 'object' ? customer.onboardingQuestionnaire : {};
  const business = onboarding.businessInformation && typeof onboarding.businessInformation === 'object' ? onboarding.businessInformation : {};
  const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
  return String(customer && (customer.pstNumber || customer.pstHstNumber || customer.pst_hst_number) || overrides.pstNumber || overrides.pst_number || business.pstHstNumber || business.pst_hst_number || '').trim();
}
function rpTaxExemptForRule(customer, exemptions, code) {
  if (String(code || '').toUpperCase() === 'PST' && rpCustomerPstNumber(customer)) return true;
  return rpExemptionEnabled(exemptions, code);
}
function rpEstimatedTax(order, totals, customer) {
  const region = String(order && order.taxRegion || customer && customer.taxRegion || 'BC').toUpperCase();
  const rules = (RP.TAX_RULES || []).filter(rule => rule && rule.active !== false && String(rule.region || 'BC').toUpperCase() === region);
  if (!rules.length) return 0;
  const exemptions = customer && customer.taxExemptions || {};
  const overrides = customer && customer.taxOverrides || {};
  return rpMoneyNumber(rules.reduce((sum, rule) => {
    const code = String(rule.code || '').toUpperCase();
    if (!code || rpTaxExemptForRule(customer, exemptions, code)) return sum;
    const base = rpTaxBaseForRule(rule, totals);
    const rate = rpOverrideRate(overrides, code, rpFiniteNumber(rule.rate));
    return sum + base * rate;
  }, 0));
}
function rpOrderTotals(order) {
  const o = order || {};
  const customer = o.customer || (o.cust ? RP.cust(o.cust) : null) || null;
  const feeExemptions = customer && customer.feeExemptions || {};
  const containerFeeExempt = rpFeePolicyExempt(feeExemptions);
  let sub = 0, dep = 0, fee = 0, cases = 0;
  (o.lines || []).forEach(line => {
    const sku = RP.sku(line.sku) || {};
    const qty = rpFiniteNumber(line.qty ?? line.quantity);
    const unit = String(line.unit || sku.unit || 'case').toLowerCase();
    const caseQty = unit === 'item' ? 0 : (rpFiniteNumber(line.caseQty ?? line.caseQuantity ?? line.case_quantity) || (unit === 'can' ? qty / 12 : qty));
    const price = rpOwn(line, 'price') ? rpFiniteNumber(line.price) : (customer ? rpCustomerSkuPrice(customer, sku) : rpFiniteNumber(sku.price));
    const deposit = rpOwn(line, 'deposit') ? rpFiniteNumber(line.deposit) : rpFiniteNumber(sku.deposit);
    const recyclingFee = rpOwn(line, 'recyclingFee') ? rpFiniteNumber(line.recyclingFee) : rpFiniteNumber(sku.recyclingFee);
    const containerFee = deposit + recyclingFee;
    sub += price * qty;
    if (!containerFeeExempt) fee += containerFee * qty;
    cases += caseQty;
  });

  sub = rpMoneyNumber(rpOwn(o, 'subtotal') ? o.subtotal : sub);
  dep = rpMoneyNumber(rpOwn(o, 'depositTotal') ? o.depositTotal : (rpAdjustmentTotal(o, 'deposit') || dep));
  fee = rpMoneyNumber(rpOwn(o, 'feeTotal') ? o.feeTotal : (rpAdjustmentTotal(o, 'fee') || fee));
  const creditCardFee = rpMoneyNumber(o.creditCardFeeTotal || 0);
  const containerFee = rpMoneyNumber(Math.max(0, dep + fee - creditCardFee));
  const tax = rpMoneyNumber(rpOwn(o, 'taxTotal') ? o.taxTotal : (rpAdjustmentTotal(o, 'tax') || rpEstimatedTax(o, { sub, dep, fee }, customer)));
  const total = rpMoneyNumber(rpOwn(o, 'total') ? o.total : sub + dep + fee + tax);
  return { sub, dep, fee, containerFee, creditCardFee, tax, total, cases };
}
const RP_SALES_METRIC_EXCLUDED_STATUSES = new Set(['Cancelled', 'Canceled', 'Closed']);
const RP_FIELD_OPEN_ORDER_STATUSES = new Set(['Requested', 'Submitted', 'Needs Approval', 'On Hold', 'Approved', 'Allocated', 'Picked', 'Dispatched', 'Inbound']);
function rpSalesMetricDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw || raw.toUpperCase() === 'ASAP') return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function rpOrderSalesDate(order) {
  return rpSalesMetricDate(order && (order.created || order.createdAt || order.created_at || order.submittedAt || order.submitted_at || order.updated || order.updatedAt || order.updated_at || order.date));
}
function rpSalesMetricWindow(range = 'mtd', now = new Date()) {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  if (range === 'today' || range === '1d') {
    start.setHours(0, 0, 0, 0);
  } else if (range === '7d') {
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 6);
  } else if (range === 'ytd') {
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
  } else {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  }
  return { start, end };
}
function rpOrderCountsAsSalesMetric(order) {
  return Boolean(order && !RP_SALES_METRIC_EXCLUDED_STATUSES.has(String(order.status || '').trim()));
}
function rpOrderIsOpenForField(order) {
  const status = String(order && order.status || '').trim();
  return RP_FIELD_OPEN_ORDER_STATUSES.has(status) || Boolean(status && !['Delivered', 'Invoiced', 'Paid', 'Closed', 'Cancelled', 'Canceled'].includes(status));
}
function rpSalesSummary(orders, options = {}) {
  const rows = Array.isArray(orders) ? orders : [];
  const range = options.range || 'mtd';
  const { start, end } = rpSalesMetricWindow(range, options.now || new Date());
  return rows.reduce((summary, order) => {
    if (!rpOrderCountsAsSalesMetric(order)) return summary;
    const date = rpOrderSalesDate(order);
    if (date && (date < start || date > end)) return summary;
    if (!date && options.requireDate !== false) return summary;
    const totals = rpOrderTotals(order);
    summary.cases += totals.cases;
    summary.revenue += totals.sub;
    summary.orders += 1;
    return summary;
  }, { cases: 0, revenue: 0, orders: 0 });
}
function rpFieldInventory(role, locationOverride) {
  if (!rpHasLiveAuth()) return (RP.MY_INV || []).map(item => ({ ...item, used: rpFiniteNumber(item.available) + rpFiniteNumber(item.sample) }));

  const micro = arguments.length > 1 ? locationOverride : rpFieldMicroWarehouse(role);
  if (!micro) return [];

  const seedBySku = new Map((RP.MY_INV || []).map(item => [item.sku, item]));
  const groups = new Map();

  (RP.INV || [])
    .filter(row => row && String(row.unit || 'case').toLowerCase() !== 'can' && row.loc === micro.id && rpFiniteNumber(row.qty) > 0)
    .forEach(row => {
      const current = groups.get(row.sku) || {
        sku: row.sku,
        available: 0,
        fronted: 0,
        purchased: 0,
        sample: 0,
        used: 0,
        limit: 0
      };
      const qty = rpFiniteNumber(row.qty);
      const status = String(row.status || 'Available').toLowerCase();
      current.used += qty;
      if (status === 'fronted to rep') {
        current.fronted += qty;
        current.available += qty;
      } else if (status === 'purchased by rep') {
        current.purchased += qty;
        current.available += qty;
      } else if (status === 'sample' || status === 'sample / demo') {
        current.sample += qty;
      } else if (status === 'available') {
        current.available += qty;
      }
      groups.set(row.sku, current);
    });

  const skuOrder = new Map((RP.SKUS || []).map((sku, index) => [sku.id, index]));
  return Array.from(groups.values())
    .map(item => {
      const seeded = seedBySku.get(item.sku) || {};
      const totalCases = item.available + item.sample;
      return {
        ...item,
        available: Math.round(item.available),
        fronted: Math.round(item.fronted),
        purchased: Math.round(item.purchased),
        sample: Math.round(item.sample),
        used: Math.round(item.used),
        limit: Math.max(rpFiniteNumber(seeded.limit, 0), Math.round(totalCases), 1)
      };
    })
    .sort((a, b) => (skuOrder.get(a.sku) ?? 9999) - (skuOrder.get(b.sku) ?? 9999) || a.sku.localeCompare(b.sku));
}
function rpFieldInventoryUsedCases(items) {
  return (items || []).reduce((sum, item) => sum + rpFiniteNumber(item.used, rpFiniteNumber(item.available) + rpFiniteNumber(item.sample)), 0);
}
function rpFieldInventoryFrontedValue(items) {
  return (items || []).reduce((sum, item) => sum + (rpSkuPrice(item.sku) * rpFiniteNumber(item.fronted)), 0);
}
function rpFieldInventoryValueCap(micro) {
  return Math.max(rpFiniteNumber(micro && micro.valueCap, 0), 3000);
}
async function rpRefreshLiveData(reason = 'manual') {
  if (!rpHasLiveAuth() || typeof window.RP_REFRESH_DATA !== 'function') return false;
  const payload = await window.RP_REFRESH_DATA({ reason });
  if (!payload || !payload.refreshEventPublished) window.dispatchEvent(new CustomEvent('rp-data-refresh', { detail: { status: 'ok', source: reason, at: new Date().toISOString() } }));
  return true;
}
function rpUpsertOrder(order) {
  if (!order || !order.id) return order;
  const idx = RP.ORDERS.findIndex(x => x.id === order.id);
  if (idx >= 0) RP.ORDERS.splice(idx, 1, order);
  else RP.ORDERS.unshift(order);
  return order;
}
function rpOrderFromApi(response, fallback) {
  const base = fallback || {};
  if (response && response.order) {
    return {
      ...base,
      ...response.order,
      exactCacheScopeKey: '',
      exactDetailComplete: response.order.exactDetailComplete === true,
      eventsComplete: response.order.eventsComplete === true,
      lines: response.order.lines || base.lines || [],
      external: response.order.external || base.external || response.draftOrder || response
    };
  }
  return {
    ...base,
    exactCacheScopeKey: '',
    exactDetailComplete: false,
    eventsComplete: false,
    id: (response && response.orderId) || base.id,
    external: (response && (response.draftOrder || response)) || base.external,
    status: response && response.mode === 'shopify-draft-order' ? 'Submitted' : base.status
  };
}
function rpOrderIsAsapDelivery(orderOrDate) {
  if (orderOrDate && typeof orderOrDate === 'object') {
    const date = String(orderOrDate.date || '').trim();
    if (date.toUpperCase() === 'ASAP') return true;
    const preference = String(orderOrDate.deliveryPreference || '').trim().toUpperCase();
    const notes = String(orderOrDate.note || orderOrDate.reason || '').toLowerCase();
    return preference === 'ASAP' || notes.includes('delivery requested: asap');
  }
  const date = String(orderOrDate || '').trim();
  return date.toUpperCase() === 'ASAP';
}
function rpOrderDateInputValue(orderOrDate, fallback = '') {
  const date = String(orderOrDate && typeof orderOrDate === 'object' ? orderOrDate.date || '' : orderOrDate || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : fallback;
}
function rpOrderDateLabel(orderOrDate, fallback = 'Unscheduled') {
  if (rpOrderIsAsapDelivery(orderOrDate)) return 'ASAP';
  if (orderOrDate && typeof orderOrDate === 'object') {
    const date = String(orderOrDate.date || '').trim();
    if (date) return date;
    return fallback;
  }
  const date = String(orderOrDate || '').trim();
  return date || fallback;
}

const RP_ORDER_CANCELLED_STATUSES = new Set(['Cancelled', 'Canceled', 'Void']);
const RP_ORDER_DELIVERED_STATUSES = new Set(['Delivered', 'Invoiced', 'Closed']);
const RP_ORDER_BILLING_ONLY_STATUSES = new Set(['Paid', 'Partial', 'Overdue', 'Sent', 'Issued', 'Ready']);

function rpPaymentMethodScanLabel(code) {
  const value = String(code || '').trim();
  if (!value) return 'Not set';
  if (typeof rpInvoicePaymentMethodLabel === 'function') return rpInvoicePaymentMethodLabel(value);
  const labels = {
    account_terms: 'Account terms',
    bank_transfer: 'Bank transfer',
    e_transfer: 'e-Transfer',
    cheque: 'Cheque',
    cash: 'Cash',
    credit_card: 'Credit card',
    shopify_checkout: 'Shopify checkout',
    shopify_pos: 'Shopify POS'
  };
  return labels[value] || value;
}

function rpOrderDeliveryScan(order) {
  const raw = String(order && order.status ? order.status : 'Submitted').trim() || 'Submitted';
  const directoryLabel = String(order && order.fulfillmentLabel || '').trim();
  const cancelled = RP_ORDER_CANCELLED_STATUSES.has(raw);
  const delivered = !cancelled && RP_ORDER_DELIVERED_STATUSES.has(raw);
  const billingOnly = RP_ORDER_BILLING_ONLY_STATUSES.has(raw);
  const stageSource = billingOnly ? 'Submitted' : raw;
  const stage = typeof rpOrderFulfillmentStage === 'function' ? rpOrderFulfillmentStage(stageSource, ['Submitted', 'Approved', 'Allocated', 'Picked', 'Dispatched', 'Delivered', 'Invoiced']) : stageSource;
  return {
    raw,
    stage,
    label: directoryLabel || (cancelled ? 'Cancelled' : (delivered ? 'Delivered' : (billingOnly ? 'Delivery pending' : stage))),
    delivered,
    cancelled,
    open: !cancelled && !delivered
  };
}

function rpOrderInvoiceScan(order) {
  const source = order || {};
  const externalShopifyPaid = Boolean(source.shopifyOrderId || source.shopify_order_id)
    && /^shopify_(?:pos|checkout)$/.test(String(source.invoicePaymentMethod || source.invoice_payment_method || ''))
    && ['Paid', 'Closed'].includes(String(source.financialStatus || source.financial_status || ''));
  const inv = typeof RP !== 'undefined' && typeof RP.invoice === 'function' ? RP.invoice(source.invoiceNumber || source.id) : null;
  const directoryInvoiceLabel = !inv ? String(source.invoiceLabel || '').trim() : '';
  const directoryPaymentStatus = !inv ? String(source.paymentStatus || '').trim() : '';
  const directoryInvoiceStatus = !inv ? String(source.invoiceDirectoryStatus || '').trim() : '';
  const hasDirectoryProjection = Boolean(directoryInvoiceLabel || directoryPaymentStatus || directoryInvoiceStatus || source.invoiceReady === true);
  const cancelled = RP_ORDER_CANCELLED_STATUSES.has(String(source.status || '').trim());
  const staleCancelledInvoice = cancelled && Boolean(source.invoiceNumber) && !inv && !hasDirectoryProjection;
  const hasInvoiceNumber = !staleCancelledInvoice && Boolean(source.invoiceNumber || (inv && inv.id && inv.status !== 'Draft') || (hasDirectoryProjection && !['', 'Not invoiced', 'Ready'].includes(directoryInvoiceLabel)));
  const rawStatus = inv && typeof RP.invoiceStatus === 'function'
    ? RP.invoiceStatus(inv)
    : (staleCancelledInvoice ? 'Not invoiced' : (directoryInvoiceStatus || source.invoiceStatus || source.financialStatus || (hasInvoiceNumber ? 'Issued' : 'Not invoiced')));
  const invoiceStatus = String(rawStatus || '').trim() || 'Not invoiced';
  const ready = !externalShopifyPaid && (inv ? invoiceStatus === 'Draft' : Boolean(source.invoiceReady === true || directoryInvoiceLabel === 'Ready' || directoryInvoiceStatus === 'Ready' || directoryInvoiceStatus === 'Draft'));
  const issued = Boolean(hasInvoiceNumber && invoiceStatus !== 'Draft');
  const totals = inv && typeof RP.invoiceTotals === 'function' ? RP.invoiceTotals(inv) : null;
  const paidAmount = totals ? Number(totals.paid || 0) : Number(source.paidAmount || 0);
  const balance = totals ? Number(totals.balance || 0) : Number(source.balanceDue || 0);
  const sourceInvoiceStatus = staleCancelledInvoice ? '' : String(source.invoiceStatus || '');
  const sourceFinancialStatus = staleCancelledInvoice ? '' : String(source.financialStatus || '');
  const settledStatus = ['Paid', 'Closed'].includes(invoiceStatus) || ['Paid', 'Closed'].includes(sourceInvoiceStatus) || ['Paid', 'Closed'].includes(sourceFinancialStatus);
  const partiallyPaid = invoiceStatus === 'Partial' || paidAmount > 0.005;
  let paymentStatus = externalShopifyPaid ? 'Paid' : directoryPaymentStatus || 'Not invoiced';
  if (!directoryPaymentStatus) {
    if (ready) paymentStatus = 'Not invoiced';
    else if (settledStatus || (issued && balance <= 0.005 && paidAmount > 0.005)) paymentStatus = 'Paid';
    else if (partiallyPaid) paymentStatus = 'Partial';
    else if (invoiceStatus === 'Overdue') paymentStatus = 'Overdue';
    else if (issued || ['Issued', 'Sent'].includes(invoiceStatus)) paymentStatus = 'Unpaid';
  }
  const invoiceLabel = externalShopifyPaid ? 'Paid' : directoryInvoiceLabel || (settledStatus ? invoiceStatus : (ready ? 'Ready' : (issued ? invoiceStatus : 'Not invoiced')));
  const paymentMethod = (inv && inv.paymentMethod) || source.invoicePaymentMethod || source.paymentMethod || '';
  return {
    inv,
    invoiceStatus,
    invoiceLabel,
    paymentStatus,
    paymentMethod,
    paymentMethodLabel: rpPaymentMethodScanLabel(paymentMethod),
    issued,
    ready,
    paid: paymentStatus === 'Paid',
    open: ['Unpaid', 'Overdue', 'Partial'].includes(paymentStatus),
    balance,
    paidAmount
  };
}

function rpOrderAreaLabel(order, customer) {
  const c = customer || {};
  return String(c.city || c.location || (order && order.city) || (order && order.territory) || '').trim();
}

function rpOrderIsBackorder(order) {
  if (!order) return false;
  if (order.backorder === true) return true;
  return /production\/backorder order|backorder order/i.test(String(order.note || order.reason || ''));
}

function rpOrderAttentionLabel(order, billing, delivery) {
  const bill = billing || rpOrderInvoiceScan(order);
  const ship = delivery || rpOrderDeliveryScan(order);
  if (ship.cancelled) return '';
  if (bill.paymentStatus === 'Paid' && !ship.delivered) return 'Paid, delivery pending';
  if (ship.delivered && ['Unpaid', 'Overdue', 'Partial'].includes(bill.paymentStatus)) return 'Delivered, payment open';
  if (ship.delivered && bill.invoiceLabel === 'Ready') return 'Ready to bill';
  if (!ship.delivered && bill.paymentStatus === 'Overdue') return 'Overdue invoice';
  return '';
}
async function rpRefreshAfterMutation(label) {
  try {
    return await rpRefreshLiveData('save');
  } catch (err) {
    const message = err && err.message ? err.message : 'Live data refresh failed.';
    toast(`${label || 'Change saved'}, but live refresh failed: ${message}`, 'warn');
    return false;
  }
}

/* ---- Segmented control ---- */
function Segmented({ options, value, onChange, size = 'md' }) {
  const controlRef = useRef(null);
  useEffect(() => {
    const control = controlRef.current;
    const selected = control && control.querySelector('[aria-pressed="true"]');
    if (!selected || control.scrollWidth <= control.clientWidth) return;
    const selectedLeft = selected.offsetLeft;
    const selectedRight = selectedLeft + selected.offsetWidth;
    const visibleLeft = control.scrollLeft;
    const visibleRight = visibleLeft + control.clientWidth;
    if (selectedLeft < visibleLeft) control.scrollTo({ left: selectedLeft - 3, behavior: 'smooth' });
    else if (selectedRight > visibleRight) control.scrollTo({ left: selectedRight - control.clientWidth + 3, behavior: 'smooth' });
  }, [value]);
  return (
    <div ref={controlRef} className="rp-segmented rp-scroll" role="group" style={{ display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: 3, gap: 2 }}>
      {options.map(o => {
        const v = typeof o === 'string' ? o : o.value, l = typeof o === 'string' ? o : o.label;
        const on = v === value;
        return <button type="button" aria-pressed={on} key={v} onClick={() => onChange(v)} style={{ border: 'none', borderRadius: 6, padding: size === 'sm' ? '5px 10px' : '7px 14px', fontSize: size === 'sm' ? 12 : 13, fontWeight: 600, background: on ? 'var(--surface)' : 'transparent', color: on ? 'var(--ink)' : 'var(--muted)', boxShadow: on ? 'var(--shadow-sm)' : 'none', transition: 'all .14s' }}>{l}</button>;
      })}
    </div>
  );
}

/* ---- Progress bar ---- */
function Bar({ value, max = 100, color = 'var(--amber)', h = 7, bg = 'var(--surface-2)' }) {
  return <div style={{ height: h, background: bg, borderRadius: 100, overflow: 'hidden', width: '100%' }}><div style={{ height: '100%', width: Math.min(100, (value / max) * 100) + '%', background: color, borderRadius: 100, transition: 'width .4s ease' }} /></div>;
}

Object.assign(window, { Icon, Buffalo, Wordmark, Logo, Pill, statusColors, ProtectedImage, ProtectedImageLightbox, rpOrderFulfillmentStage, rpOrderEditableBySales, rpIsConsumerCustomer, rpIsQuickSaleCustomer, rpIsAnonymousShopifyCustomer, rpVisibleCustomers, rpOrderIsQuickSale, rpOrderShopifyReference, rpOrderCustomerDisplayName, RP_QUICK_SALE_CUSTOMER_ID, RP_CUSTOMER_TYPES, RP_BUSINESS_CUSTOMER_TYPES, RP_LEAD_TYPES, RP_DELIVERY_METHOD_OPTIONS, rpNormalizeDeliveryMethod, rpCustomerDefaultDeliveryMethod, rpCustomerDeliveryNeedsAddress, rpAddressFromCustomerLocation, rpCustomerHasDeliveryAddress, rpCustomerDeliveryWarning, rpCustomerWithDefaultDelivery, rpOrderReasonLabel, rpOrderReasonDisplay, rpOrderTimeline, FlavorChip, CanThumb, KPICard, Sparkline, Avatar, rpAuthProfile, rpAuthDetails, rpIsDemoMode, rpHasLiveAuth, rpNormalizeRole, rpCanInviteUsers, rpProfileFeatureFlags, rpProfileHasFeature, rpDashboardWorkflowIntelligenceEnabled, rpCustomerInProfileScope, rpCanSendCustomerPortalAccess, rpCanApproveCustomers, rpCanEditCustomerProfile, rpCanIssueCustomerInvoices, rpCustomerOrderReady, rpCustomerDuplicateCandidates, CustomerDuplicateWarning, rpCustomerCreditSettlements, rpCustomerCreditSettledAmount, rpCustomerCreditOpenAmount, rpCustomerCreditIsOpen, rpCustomerAccountCredits, rpCustomerAccountCreditBalance, rpCustomerAccountDebts, rpCustomerAccountDebtBalance, rpStripeVault, rpStripeDefaultCard, rpStripeCardLabel, rpLoadStripeSdk, rpCustomerOrders, rpCustomerOrderSummary, rpCustomerOrderHistoryLabel, rpCanUseDeveloperData, rpDeveloperDataPreference, rpSetDeveloperDataPreference, rpSwitchDeveloperData, rpDisplayName, rpRoleLabel, rpTaskOwnerLabel, rpAreaNames, rpProfileAreas, rpAreaLabel, rpAreaMatches, rpBusinessTimeZone, rpTodayYmd, rpAddDaysYmd, rpNowLocalInput, rpFormatLocalDateTime, rpFormatLocalDate, rpProfileDescriptor, rpAccountStatus, rpFormatAccountTime, rpCurrentPersona, useRpIdentity, rpLoadExactOrder, useRpExactOrder, useRpServerPagedRows, useRpDispatchRows, rpLoadDispatchManifest, rpDispatchJobFromItem, useRpServerPagedCustomers, rpLoadExactCustomer, useRpExactCustomer, rpServerFacetOptions, rpServerSummaryValue, RpPagination, RpServerLoadMore, rpFieldPersona, rpFieldUserName, rpFieldFirstName, rpFieldTerritory, rpFieldTerritories, rpFieldCoversTerritory, rpFieldCustomers, rpOrderInProfileScope, rpFieldOrders, rpFieldTasks, rpFieldStockRequests, rpFieldReceipts, rpFieldFirstCustomerId, rpFieldDefaultLocation, rpFieldMicroWarehouse, rpFieldInventory, rpFieldInventoryUsedCases, rpFieldInventoryFrontedValue, rpFieldInventoryValueCap, rpPriceListByName, rpDefaultPriceList, rpDefaultPriceListName, rpDefaultCasePrice, rpCustomerPriceList, rpCustomerPriceListName, rpCustomerCasePrice, rpPortalSkuPrice, rpCustomerSkuPrice, rpSkuPrice, rpCanonicalSkuId, rpCanonicalInventorySkuId, rpSkuFlavorId, rpSkuDisplayName, rpSkuCode, rpSkuUpc, rpSkuExternalCode, rpSkuExternalUpc, rpSkuExternalIdentity, rpCleanLotCode, rpSkuDefaultLotCode, rpSkuVisualFlavor, rpSkuVisualLabel, rpSkuVisualColor, rpSkuVisualMeta, rpSkuIsMixer, rpSkuLooksOperationalArtifact, rpSkuIsOrderable, rpSkuIsInventoryProduct, rpSkuLooksCustomerSellable, rpCustomerCatalogSkus, rpSkuIsManualOrderable, rpManualOrderSkus, rpOrderableSkus, rpCasePackSkus, rpMixerSkus, rpInventoryAvailableCases, rpInventoryGiveawayCases, rpAvailabilityLabel, rpLocationCanFulfillTerritory, rpLocationCanFulfillCustomer, rpOrderSourceLocations, rpOrderCommittedCases, rpOrderAvailableCases, rpOrderAvailabilityLabel, rpLocationSellableCases, rpDefaultOrderSourceForCustomer, rpOrderTotals, rpOrderSalesDate, rpOrderCountsAsSalesMetric, rpSalesMetricWindow, rpSalesSummary, rpUploadPrivateFile, rpFetchProtected, rpOpenProtectedFile, rpSubmitErrorTicket, rpNotifyErrorWithTicket, ErrorTicketButton, ErrorNotice, rpRefreshLiveData, rpUpsertOrder, rpOrderFromApi, rpOrderIsAsapDelivery, rpOrderDateInputValue, rpOrderDateLabel, rpPaymentMethodScanLabel, rpOrderDeliveryScan, rpOrderInvoiceScan, rpOrderAreaLabel, rpRefreshAfterMutation, Segmented, Bar });
Object.assign(window, { rpCustomerQuoteProspect, rpCustomerPortalInviteReady, rpOpenInvoicePdf });
Object.assign(window, { rpCustomerRestockSignal, rpRestockRecommendations, rpOrderIsBackorder });
Object.assign(window, { useRpPagedRows });
Object.assign(window, { rpRetailAccountDefaultPriceListName, rpRetailAccountNewPriceListName, rpCanSetCustomAccountPricing, rpCanManageCustomerAccountPricing, rpExistingCustomerPriceListOptionNames, rpAccountPriceListOptionNames });
Object.assign(window, { rpOrderInvoiceLocked });
Object.assign(window, { rpPriceListTerms, rpAccountPaymentTermOptionNames, rpCustomerCustomCasePrice });
Object.assign(window, { rpCanDeleteDuplicateLead });


/* ===== printed_lot.jsx ===== */
// Printed labels are annotations on one existing case-lot identity. Never use
// a displayed label as a new ledger key or fold a loose-can SKU into a case SKU.
function rpPrintedLotSkuId(sku) {
  const item = typeof rpSkuItem === 'function' ? rpSkuItem(sku) : null;
  return String(item && item.id || sku && sku.id || sku || '').trim().toUpperCase();
}

function rpPrintedLotRows(sku) {
  const skuId = rpPrintedLotSkuId(sku);
  const rows = typeof RP !== 'undefined' && Array.isArray(RP.PRINTED_LOT_METADATA) ? RP.PRINTED_LOT_METADATA : [];
  return rows.filter(row => row && row.skuId === skuId && typeof row.lot === 'string' && typeof row.printedLot === 'string'
    && row.lot.trim().length <= 100 && row.lot.trim() && /^[A-Z0-9._:-]{1,80}$/.test(row.printedLot) && row.lot !== row.printedLot);
}

function rpPrintedLotMetadata(sku, lot, packagingRunId) {
  const canonical = String(lot || '');
  const matches = rpPrintedLotRows(sku).filter(row => row.lot === canonical && (!packagingRunId || row.packagingRunId === packagingRunId));
  return matches.length === 1 ? matches[0] : null;
}

function rpPrintedLotLabel(sku, lot, packagingRunId) {
  const original = String(lot || '');
  const row = rpPrintedLotMetadata(sku, original, packagingRunId);
  return row ? row.printedLot : original;
}

function rpResolvePrintedLotInput(sku, value) {
  const input = String(value || '').trim().toUpperCase();
  const matches = rpPrintedLotRows(sku).filter(row => row.printedLot === input || row.lot.trim().toUpperCase() === input);
  // An ambiguous annotation cannot silently select a stock identity. Returning
  // the original input lets exact-lot validation and the database guard reject it.
  return matches.length === 1 ? matches[0].lot : input;
}

function rpExistingPrintedLotMessage(sku, value) {
  const input = String(value || '').trim().toUpperCase();
  const row = rpPrintedLotRows(sku).find(item => item.printedLot === input || item.lot === input);
  return row ? `This is already recorded stock: ${rpPrintedLotLabel(sku, row.lot)}. Choose the existing lot for a movement; do not pack or receive it again.` : '';
}

// Include exhausted lots and signed runs: zero stock does not make a lot new.
function rpPackagingLotConflict(sku, value, historical) {
  const skuId = rpPrintedLotSkuId(sku);
  const lot = rpResolvePrintedLotInput(sku, value);
  const normalized = String(lot || '').trim().toUpperCase();
  if (!skuId || !normalized) return null;
  const matches = row => rpPrintedLotSkuId(row.outputSkuId || row.sku || row.skuId) === skuId && String(row.lot || '').trim().toUpperCase() === normalized;
  const runs = (RP.PACKAGING_RUNS || []).filter(matches);
  const active = runs.find(row => row.status !== 'Reversed');
  const recorded = runs.length || ['LOTS', 'INV', 'LEDGER', 'PRODUCTION'].some(key => (RP[key] || []).some(matches)) || rpExistingPrintedLotMessage(sku, value);
  if (historical ? !active : !recorded) return null;
  return { lot, run: active || runs[0] || null, message: active
    ? `This is already recorded stock: ${rpPrintedLotLabel(sku, lot)}. Packaging session ${active.id}: ${Number(active.cases || 0).toLocaleString()} cases, packed ${active.packedOn}, ${active.status}. This is the original packed total, not today's stock on hand. Do not record this same session or allocate its cans again. For genuinely additional packing, choose Record Another Packing Session in Production.`
    : `This product and lot already have inventory history: ${rpPrintedLotLabel(sku, lot)}. New packaging cannot add these cases again. Review the existing stock and its ledger. Historical Packaging is only for missing source-can history, not a quantity correction.` };
}


/* ===== help_inventory.js ===== */
/* Inventory help is descriptive only. It does not submit business actions.
   Labels and effects checked against inventory_workflow.jsx, desktop_supply.jsx,
   desktop_inventory.jsx, desktop_ops.jsx, desktop_admin.jsx, the field screens,
   production/inventory APIs, and docs/WADE_INVENTORY_WORKFLOW.md. */
const RP_INVENTORY_HELP_GUIDES = {
  production: {
    title: 'From produced cans to released cases',
    summary: 'Record what BevMax produced, account for the cans actually packed, and release the finished cases after quality checks. Packing creates cases at the production facility; a separate completed transfer puts them in a warehouse or rep’s storage.',
    before: [
      'Have the BevMax run report, invoice date, best-known production date, can counts by flavour, packing report, exact case labels, and quality evidence. Keep estimates labelled as estimates.',
      'Check existing can runs and packaging rows first. A can-run ID identifies loose-can production; a packaging-run ID identifies a case-packing event. They are different records.',
      'Use whole cans for a Can Run and whole sealed cases or flats for packaging. Each finished case contains 12 cans. A lot group does not replace the exact lot.',
      'Confirm you are in the live app with an authorized operations account. Demo results do not change live stock. Missing data or a loading error is not proof that a run never existed.'
    ],
    steps: [
      {
        title: 'Check whether the production is already recorded',
        body: 'Check 1. BevMax Can Runs and 2. Case Packaging from Cans. Use History on a packaging row to inspect earlier entries. Where an original total was corrected, the row notes that correction. Do not pack the difference again just to change an old entry. Production records describe packing, not today’s stock at each location; use Inventory for location counts. If cases are already included in inventory, do not record new packaging to explain them.',
        check: 'I checked existing records and know whether I am recording new work or investigating history.'
      },
      {
        title: 'Record the loose cans',
        body: 'Select Can Run. Enter BevMax Run ID, Can SKU, BevMax Invoice Date, Best-Known Production Date and Total Cans Produced. Set Date Confidence and Can Count Confidence to the evidence, add the report reference in Notes, then select Record Can Run. Repeat for each actual flavour/run. This records loose cans; it adds no finished cases.',
        check: 'The exact run, flavour, count, dates and confidence levels match the source document.'
      },
      {
        title: 'Check the unallocated cans',
        body: 'Remaining cans marked Needs count are calculated estimates and cannot supply packing yet. Management can choose Reconcile loose cans, enter the whole cans physically remaining for that exact batch, add the count evidence and reason, review the source, acknowledge the count and confirm. Enter zero only when verified; blank does not mean zero. The verified remainder then supports future packing and decreases as those cans are used. This keeps original production and historical allocations unchanged. A count above the production-supported remainder needs source-record review. If a connection fails, reopen the screen and Recover same count. Do not use original packed-case totals as a loose-can count.',
        check: 'Each flavour has enough verified remaining cans for the recipe; one flavour alone cannot supply a variety pack.'
      },
      {
        title: 'Record the actual packing and recipe',
        body: 'Select Pack Cases. Choose Finished Product, then enter Cases / Flats, Packed Date, Finished Goods Lot, Lot Group and a Packaging Note. Review the recipe and every Source Can Run(s). Single-flavour cases use 12 cans of that flavour; a three-flavour case uses four of each; a six-flavour case uses two of each. Choose mixed products by the displayed composition, because older A/B/C names may differ. This form suggests older available runs automatically. Adjust Cans from each source run to match the actual report, using zero for unused runs. Confirm every source before posting. Remaining loose cans from the same can run can be used in later sessions.',
        check: 'The output case count and every source-can allocation agree with the physical packing report.',
        link: { page: 'move-stock', label: 'Open Move Stock for exact source allocations' }
      },
      {
        title: 'Record a later packing session under the same lot',
        body: 'On the original packaging row select Pack More, or choose Record Another Packing Session when Pack Cases finds an existing lot. Enter only the additional cases, actual session date, exact source cans, reason and packing report reference. Confirm that these cases have never been recorded. The same finished lot is retained; each session gets its own history and QA release. A source can lot and a finished case lot are different records.',
        check: 'These are additional physically packed cases, not a replacement total or stock already counted.'
      },
      {
        title: 'Post the new cases to QA Hold',
        body: 'In Pack Cases, select Post to QA Hold only after reviewing the output and source cans. The action allocates those cans and creates the stated cases at the production facility in QA Hold. Confirm the packaging row says Posted at BevMax and check Inventory → Balances. Cases are not yet ready for an ordinary usable-stock transfer.',
        check: 'The new packaging row and QA Hold cases are present once, with the correct product and lot.',
        link: { page: 'inventory', label: 'Check case balances', detail: { type: 'inventory-view', view: 'balances' } }
      },
      {
        title: 'Verify the lot and release quality-cleared cases',
        body: 'In Inventory → Lot Master, use Edit on the exact product/lot and review Origin, Producer, Production date, Expiry date and Evidence / Notes. Correct either date and Save Lot. Set Date Control to Verified only with confirmed production and expiry evidence; do not invent dates to clear a warning. If only expiry is supported, use Confirm expiry only; this keeps production and joint Date Control unchanged. Then return to the exact QA Hold packaging row and select Release after quality clearance, or use Move Stock → Cases passed quality checks with its evidence and explicit confirmation. Confirm the row is Released and the cases are Available at the same facility. Changing Lot Master’s Lifecycle alone does not release the packaging inventory.',
        check: 'Quality clearance and date evidence are confirmed; the released quantity remains at the correct facility.',
        link: { page: 'inventory', label: 'Review Lot Master', detail: { type: 'inventory-view', view: 'lots' } }
      },
      {
        title: 'Record each real handoff',
        body: 'When the cases physically leave BevMax, use Move Stock → Stock changed hands. Record the actual destination, product, exact lot, cases, time, handler or carrier and CHEP use. The completed transfer immediately updates both locations. The receiver then acknowledges that same transfer. Repeat only for each further physical leg, such as warehouse to warehouse or warehouse to rep storage.',
        check: 'Each physical leg has one completed transfer and the receiver can identify its TO reference.',
        link: { page: 'move-stock', label: 'Continue the stock journey' }
      },
      {
        title: 'Close finished packaging administration',
        body: 'On a Released packaging row, select Close, read Close Completed Packaging and select Confirm Close Run when that run’s administration is complete. Closed records remain in history and their cases remain usable. Close does not move cases, consume them, release source cans or undo production.',
        check: 'I mean to finish the packaging record, not cancel its stock.'
      },
      {
        title: 'Correct a can-run fact with an audit trail',
        body: 'Use Edit on the can-run row. Review the existing values, change the incorrect invoice date, production details or count as supported, enter Correction Reason and select Review changes, then Save changes. Changing the run ID or Can SKU requires reconciling its linked records; the editor explains those dependencies. Counts cannot be reduced below cans already allocated. Historical held production totals are protected; use Reconcile loose cans to establish the physical remainder for future packing. If someone changed the record while you were editing, reload and compare their revision before trying again. History retains the previous and corrected facts.',
        check: 'The correction has evidence, keeps the original identity and does not contradict existing allocations.'
      },
      {
        title: 'Correct the original packed total, date or source cans',
        body: 'Use Edit on a packaging row to correct the original cases packed, packed date and notes. Review changes shows the effect before Save changes. The row and production total use the saved values; History retains the earlier values and reason. This edits packing paperwork without moving stock, consuming cans or changing orders. Use Edit on an Inventory balance for current physical stock. Edit source allocations opens the separate stock-changing correction review; it explains source shortages, historical holds or moved/reserved stock that prevent that correction.',
        check: 'The corrected session total and every source allocation match the evidence.'
      },
      {
        title: 'Choose the right packaging correction',
        body: 'Reverse cancels packaging; it is not the completion button. Read Reverse Packaging Run, enter Correction Reason, acknowledge the stock effect and use Reverse Signed Run only for a genuine cancellation. For new packaging, reversal removes the original cases and frees the original source cans only when the stock and history allow it. Correct Lot is for an eligible unmoved run whose recorded lot is wrong. Restore is for an eligible accidental reversal. Restore with Corrected Lot combines restoration with a supported lot correction while leaving the original run reversed. For these correction tools, enter the evidence and reason, use Check Stock and Sources, read every blocker and then use the specific Confirm button. A successful correction preserves the original history rather than deleting it.',
        check: 'The preview identifies the exact run, source cans, case quantity, lot and intended correction; no blocker remains.'
      },
      {
        title: 'Treat moved lots and historical stock separately',
        body: 'If the cases have moved, do not reverse later movements just to rename an old packaging lot. Give Operations the packaging reference, product, current and printed lot, quantities and evidence for a reviewed correction. A saved printed-lot label can identify the existing lot without rewriting its stock history; lists show the printed code, while historical references remain available in the record details. Historical Packaging is only for allocating source cans to cases already counted elsewhere: enter Prior Inventory / Case-Sheet Reference and Historical Evidence Note, then Record Without Inventory. It adds zero cases. Use it only after reconciling the original case record.',
        check: 'I know whether this is a label clarification, a supported stock correction or historical reconciliation; I am not creating the same cases twice.'
      }
    ],
    warnings: [
      'Do not use Reverse to mark a run complete. It can remove cases and free their source cans. Close is the administrative completion action.',
      'Do not use new packaging, Batch Receive, legacy sealed-flat entry or an opening balance to replace stock that is merely missing from a recent list.',
      'Restore and lot correction can be blocked by subsequent movements, reservations, replacement stock or reused cans. A blocker needs investigation; changing references or quantities to get around it loses the original history.',
      'A printed label is another way to identify the existing lot, not an additional lot or extra stock. The label must belong to the exact product and packaging run.',
      'After an uncertain correction response, preserve the pending request. Recover Correction or Recover Same Correction checks that original action; starting a new correction risks duplicating the intent.'
    ],
    faqs: [
      { question: 'What is the difference between produced, packed, released and closed?', answer: 'Produced records loose cans. Packed allocates those cans and creates QA Hold cases. Released makes the quality-cleared cases Available at the same location. Closed finishes the packaging record without moving or removing its cases.' },
      { question: 'Can I correct the BevMax invoice date?', answer: 'Yes. Open the can-run row’s Edit, correct BevMax Invoice Date, enter Correction Reason and Review changes, then Save changes. Review Best-Known Production Date separately: changing the invoice date does not change that field automatically. Use History to verify the before-and-after dates and actor. The original run ID and Can SKU remain fixed.' },
      { question: 'The automatic source runs are wrong. What should I do?', answer: 'Do not post that allocation. Move Stock → Cases were packed lets you select the exact source runs and cans used. Suggest oldest runs is only a starting point; confirm it against the actual packing report.' },
      { question: 'Does a saved can run prove the physical count is verified?', answer: 'No. Read Can Count Confidence. Estimated, BevMax reported and Verified count express different evidence. Keep the correct confidence and arrange the missing confirmation rather than treating a saved number as a verified count.' },
      { question: 'How do I undo an accidental packaging reversal?', answer: 'Open the reversed row’s Restore, provide reason and evidence, and Check Stock and Sources. The service must prove that the original case quantity can be restored using the original sources without duplicating stock. Confirm only an eligible preview. Both the reversal and restoration remain in Production History.' },
      { question: 'Can I correct a label after cases have moved?', answer: 'A stock-changing lot correction may be blocked. Preserve the existing movement history and request a reviewed printed-lot label mapping where appropriate. The current page displays saved labels and their history; it does not provide a general label-editing button.' }
    ],
    history: [
      { title: 'Production History', body: 'Use the page’s History for all production events or a row’s History for that run. Review action, time, actor, Before, After and Reason / evidence. Refresh reloads records; Load Older History retrieves earlier events. A restored run must show the original reversal and the compensating action.', link: { page: 'production', label: 'Open production history', detail: { type: 'production-history' } } },
      { title: 'Printed lot labels', body: 'The Printed lot labels section inside Production History shows the saved printed code, recorded lot, product, packaging run, actor, date, reason and evidence. This is label history, separate from movements of cases.' },
      { title: 'Case movement proof', body: 'Check Inventory → Ledger for the packaging, release, reversal or correction effect, and Balances for the resulting quantity and status. A Closed production row alone does not prove delivery to a warehouse.', link: { page: 'inventory', label: 'Open movement ledger', detail: { type: 'inventory-view', view: 'ledger' } } }
    ],
    related: ['move-stock', 'inventory', 'procurement', 'dispatch']
  },
  'move-stock': {
    title: 'Move Stock: the complete cans-to-rep journey',
    summary: 'Start with what actually happened, keep incomplete work safely, and confirm one stock event at a time. The usual journey is cans produced → cases packed → quality release → completed handoff → arrival acknowledged, repeated for each further handoff.',
    before: [
      'Know the product, exact lot or source can run, whole quantity, actual date, source and destination. Keep the report, label photo, bill of lading or handoff reference nearby.',
      'Check Production, Inventory → Ledger and existing Saved work before starting. One real event needs one posted record, even if several people helped.',
      'Choose actual custody locations. A carrier or driver can handle the stock without becoming its recorded warehouse or owner.',
      'Use Refresh stock data if the lists are stale. A “Stock lists may be incomplete” warning means an empty list cannot establish that there is no stock.'
    ],
    steps: [
      {
        title: 'Start at the stage that actually happened',
        body: 'Choose Cans were produced, Cases were packed, Cases passed quality checks, Stock changed hands, Stock arrived or Build mixed packs. You can start at any stage. If the can and packaging records already exist, go straight to the actual next event instead of repeating them.',
        check: 'This action describes one real event that has not already been recorded.'
      },
      {
        title: 'Save missing details without changing stock',
        body: 'Enter the facts you know. For “Is this event already recorded?”, leave “I’m not sure yet” if you cannot prove it is new. Add Report, label or handoff reference, notes, Who can confirm the missing detail? and What needs to happen next? Attach a label photo or paperwork if useful, wait for the upload, then Save for later. Uploading alone does not save the draft’s link. A named follow-up does not send a message. Saved for later is not a posted movement.',
        check: 'The draft records both the known facts and the next missing detail, and any uploaded evidence is linked by a saved draft.'
      },
      {
        title: 'Record cans produced',
        body: 'For Cans were produced, enter the exact BevMax run ID, flavour, whole cans, invoice date, best-known production date and both confidence choices. Record produced cans adds the can run only; no finished cases appear. If the run already exists and its facts are wrong, use Production → Edit instead of adding another run.',
        check: 'The can run is new and the date/count confidence matches the evidence.',
        link: { page: 'production', label: 'Review can runs' }
      },
      {
        title: 'Turn the actual source cans into cases',
        body: 'For Cases were packed, select the finished product and actual sealed cases, exact case lot, lot group and packed date. Review each recipe component. Suggest oldest runs can fill an initial allocation, but select the physical Source can run and Cans used for every flavour; use + Add a source run for this flavour when packing used more than one run. Confirm the source checkbox only when the report agrees. Create cases in QA Hold allocates those cans and creates the finished cases at the production facility.',
        check: 'All recipe totals and exact source allocations match the completed packing.'
      },
      {
        title: 'Release only after the quality review',
        body: 'For Cases passed quality checks, select the exact QA Hold packaging run. Verify product, printed lot, cases, production/expiry evidence and quality clearance. Add its evidence reference and confirm the quality checkbox. Release these cases changes their usable status at the same location; it does not ship them. For an older missing run, enter its exact PKG reference and use Find exact record.',
        check: 'The chosen run is explicitly cleared for release and its evidence is recorded.'
      },
      {
        title: 'Record the completed warehouse or rep handoff',
        body: 'For Stock changed hands, select Where did it leave from? and Who has custody now? Choose the movement method, actual handoff date/time and handler or carrier. Say whether CHEP pallets moved and enter the whole pallet count if they did. Add BOL or tracking references when available. Under What changed hands?, choose the exact product, lot and case count; use + Add product / lot for each additional product or lot. Record this completed transfer subtracts cases from the source and adds them to the destination immediately, with any declared CHEP movement. It is for a completed handoff, not a planned shipment.',
        check: 'Both endpoints, every exact lot, the cases, handler/carrier, handoff time and CHEP count match the physical movement.'
      },
      {
        title: 'Confirm arrival against the existing transfer',
        body: 'For Stock arrived, choose the receiving location and Completed inbound transfer. Read the original TO reference, source, product, exact lots and quantities against the stock received. Enter Actual received date and confirm the full-match checkbox only if everything matches. Acknowledge this exact transfer adds no stock and moves no CHEP again. If an older TO is missing from the list, enter its exact reference and Find exact record.',
        check: 'The full received quantity and exact lots match the same completed TO; no second stock receipt is needed.'
      },
      {
        title: 'Repeat for the next real leg into a rep’s hands',
        body: 'A later warehouse-to-warehouse or warehouse-to-rep handoff is another Stock changed hands action with its own actual endpoints. The destination rep’s Micro-Warehouse records their custody. Ranch Power-owned replenishment normally becomes Fronted to Rep, not a purchase by that rep. The receiver acknowledges the new TO. Each transfer changes where cases are held; its acknowledgement only confirms the existing record.',
        check: 'The final cases appear at the rep’s actual storage, with the correct ownership/status and acknowledgement.'
      },
      {
        title: 'Use Build mixed packs only for physically rebuilt cases',
        body: 'Select where the work happened, the displayed mixed-pack composition and the custody being opened. Cases of EACH flavour to open is a per-flavour quantity, not the total output. Choose one exact eligible source lot for each flavour and enter the actual repack date and a new mixed-pack label using the displayed prefix. Confirm the source cases were physically opened and the new mixed cases are complete. The action consumes those finished source cases and creates mixed cases at the same location; it is different from packing directly from loose production cans. Available and Fronted to Rep are separate holdings, and purchased/sample stock is excluded.',
        check: 'The complete physical recipe, exact source lots, custody and output label match the repack.'
      },
      {
        title: 'Review the stock effect, then explicitly confirm',
        body: 'Select Check next step →. Read Current record check, the quantity/location/status changes, evidence, blockers and related transfers or stock requests. Resolve every gap before posting. A related request may already account for this handoff; do not fulfill it and post a separate transfer for the same cases. Once Ready for your review appears, check the authorization statement and use the action-specific confirmation button. Completing this help checklist itself does not submit anything.',
        check: 'The reviewed effect is exactly the intended event, duplicate checks are resolved and I am authorized to confirm it.'
      },
      {
        title: 'Verify completion or recover the original request',
        body: 'Success shows Action confirmed, a Reference and the Workflow ID. Check the matching Production or Inventory record before moving on. Start the next step → opens another stage; it does not post it automatically. If a result is uncertain, reopen the original Saved work item, use Check saved status and Recover original request. Its locked details and reference are preserved. Do not start another workflow for the same event.',
        check: 'I have a confirmed result and matching stock record, or I have retained the original pending workflow for recovery.',
        link: { page: 'inventory', label: 'Verify the stock record', detail: { type: 'inventory-view', view: 'ledger' } }
      }
    ],
    warnings: [
      'Already recorded or uncertain history stays a draft for review. It is not permission to create replacement stock. Historical reconciliation belongs in the reviewed Production process.',
      'If product, lot, destination or received quantity does not match the TO, keep a draft with the discrepancy. There is no partial acknowledgement in this flow. If the full matching shipment arrived but some cases are damaged, acknowledge the full handoff and report the damage separately at the destination.',
      'Do not create a receipt, opening balance or second transfer merely because the desired lot is absent. Check release status, earlier movements, reservations, ownership and data loading first.',
      'Only the creator can edit or post their saved action. Team visibility lets colleagues review progress; it does not transfer ownership of the draft.',
      'Demo drafts stay in that browser and never change live inventory. In live mode, Save for later is needed before leaving; unsupported connectivity is not an offline posting queue.'
    ],
    faqs: [
      { question: 'What does Save for later do?', answer: 'It stores the draft, notes, linked evidence and follow-up in your signed-in account. It does not allocate cans, release cases, move stock or acknowledge delivery. Inventory changes only after the applicable review and confirmation.' },
      { question: 'How do I cancel or delete something I made while trying this out?', answer: 'Use Cancel or the Move Stock link to leave without saving your latest edits. Opening a stage does not save it; Save for later and Check next step do. An earlier saved version remains in Saved work. To remove it, choose Discard draft on its card or inside the draft and confirm the exact title and workflow reference. No inventory changes. Only the creator can discard an unposted draft; this cannot be undone. Completed or uncertain posting requests must retain their original records and cannot be discarded.' },
      { question: 'Why does the transfer already appear at the destination before acknowledgement?', answer: 'A completed transfer records the real handoff and updates both locations immediately. The receiver’s acknowledgement confirms that exact transfer, with no additional quantity or pallet movement.' },
      { question: 'What should I do after a timeout or lost posting response?', answer: 'Keep the original saved workflow. Open it from Saved work and use Check saved status or Recover original request. The service preserves the original request and can return its result. A fresh reference is not a safe retry.' },
      { question: 'What if someone edited the draft on another device?', answer: 'The app reports a revision conflict. Your entered copy remains visible. Compare what you entered with the saved work, then use Reload saved version if appropriate; this discards the unsaved copy. Review the current record before confirming again.' },
      { question: 'How do I find work that is not in the first list?', answer: 'Open Saved work, choose My work or Team visibility, and use Load older saved work when available. For an older packaging run or completed transfer, use the exact PKG/TO reference lookup in its action.' },
      { question: 'Does the printed code RP-PM-001 replace the old 001 stock?', answer: 'No. The printed code identifies the same existing product/lot. Older references remain in the record details and history. Select that listed stock. Do not create a second case lot under the printed code.' }
    ],
    history: [
      { title: 'Saved work and original results', body: 'Saved work separates unfinished drafts, original requests needing recovery and Completed work. Open the item to read its reference, evidence and result. Team visibility supports coordination, while only the creator can continue their action.', link: { page: 'move-stock', label: 'Open Move Stock' } },
      { title: 'Independent stock confirmation', body: 'Use Production → History for can/packaging changes. Use Inventory → Ledger and location History for case movements, then Field Receipts for arrival evidence. A saved workflow alone is not proof of a posted stock effect.', link: { page: 'inventory', label: 'Open movement ledger', detail: { type: 'inventory-view', view: 'ledger' } } },
      { title: 'Arrival evidence', body: 'Field Receipts records receipt evidence/status. Match its original transfer reference and full quantities; acknowledging an already posted TO has no second stock effect.', link: { page: 'inventory', label: 'Open Field Receipts', detail: { type: 'inventory-view', view: 'receipts' } } }
    ],
    related: ['production', 'inventory', 'dispatch', 'procurement']
  },
  inventory: {
    title: 'Read stock correctly and record each movement once',
    summary: 'Inventory answers where cases are, which product and lot they belong to, and what can happen to them next. Use the specific action for a transfer, sale, giveaway, damage report, repack or physical count so its reason and history remain clear.',
    before: [
      'Separate location from ownership and status. A rep’s Micro-Warehouse is custody. Fronted to Rep remains company-owned; Purchased by Rep is a different holding. QA Hold, Damaged and other restricted statuses are not ordinary usable stock.',
      'Match the exact SKU and lot. A printed-label note identifies existing recorded stock; a lot group or a similar-looking code does not identify an interchangeable batch.',
      'Read reservations as commitments, not completed deliveries. An on-hand total does not mean all of it can be promised or transferred.',
      'Count sealed cases separately from individual open cans. The Open Cans records are not the unallocated can-production runs on the Production page.'
    ],
    steps: [
      {
        title: 'Establish the current location and status',
        body: 'Open Balances and locate the actual warehouse, production facility, event or rep storage. Read the product, lot and status, then use that location’s History to inspect movements. If the balance seems wrong, trace the most recent real handoff before recording anything new. Creating a location does not give it stock.',
        check: 'I found the actual custody location and understand the stock’s ownership/status.'
      },
      {
        title: 'Review stock still needed for existing commitments',
        body: 'Balances shows Stock still needed when open orders and pending reservations exceed eligible recorded stock at their source, even when that product has no inventory row. Select Review shortages to expand the compact list, then Orders beside a product to see the associated orders and pending reservations. Open an order reference to view its full details; a pending order that has not yet been created locally is labelled separately. Use Next and Previous for more shortages. Read the product, location, unit, On hand, Committed and Still needed together. A required lot can be short even when another lot has stock. SKU, Location and search narrow this view; clear Owner, Status and Lot filters to compare the complete source pool. Orders and reservations remain unchanged. Count the physical stock and confirm its current source and exact lot before shipping; the recorded balance is not proof of a physical count.',
        check: 'I have identified the unmet commitments and the physical source that still needs confirming.'
      },
      {
        title: 'Check dates and printed labels in Lot Master',
        body: 'Lot Master is the identity and date directory, including historical and exhausted lots; a row does not mean stock is currently on hand. Use Balances for current cases and Open Cans for loose stock. Choose Edit dates on the exact product/lot. Product shows the exact stored SKU and whether it is cases or loose cans. Recorded lot is the protected reference used by movement history. Printed label shows only a saved mapping to the physical label; when no separate mapping exists, the page says so. Lot group is an organizational label and does not rename either reference. Admin, leadership, operations and warehouse users can correct case Production date or Expiry date, explain the correction in Evidence / Notes, then Save Lot. Date Control Verified requires both dates to be supported. Loose-can lots support Confirm expiry only here; other source details are shown for reference. Editing lifecycle does not replace Production’s Release action.',
        check: 'The physical label and recorded lot refer to the same product, with appropriate date evidence.'
      },
      {
        title: 'Confirm expiry when production is still uncertain',
        body: 'From the exact lot’s editor, choose Confirm expiry only. Enter the supported Expiry date, Correction or confirmation reason and Expiry evidence. Refresh and review expiry shows the current and proposed dates with your evidence. Check the explicit confirmation and choose Confirm expiry. This can confirm an unchanged expiry or correct it; it leaves production, joint Date Control, quantities and historical ledger dates unchanged. If the response is uncertain, use Recover same confirmation, including after closing or reloading. Its original request stays locked until resolved. A later edit can supersede an earlier confirmation; review the current lot before another change.',
        check: 'The exact product, lot, expiry and evidence agree; an expiry confirmation does not verify production.'
      },
      {
        title: 'Use the completed-transfer workflow for a real handoff',
        body: 'Select Move Stock for the guided Stock changed hands action, or Transfer for the direct form. Record actual endpoints, product, exact lot, whole cases, handoff method/time, handler or carrier, and CHEP use. Put different lots on separate lines. Company-owned usable cases can move to rep storage, between reps, or back to a warehouse when those are real physical handoffs. Fronted stock returned to a warehouse becomes Available there. Purchased stock, damaged stock and ownership corrections need the appropriate review rather than an ordinary replenishment transfer.',
        check: 'The transfer describes a completed physical movement and preserves the intended ownership.',
        link: { page: 'move-stock', label: 'Record a handoff in Move Stock' }
      },
      {
        title: 'Acknowledge an existing TO without adding it again',
        body: 'Use Batch Receive → Choose Recorded Inbound Transfer. Select Receiving Into and the Completed Inbound Transfer, compare every exact lot and case quantity, enter Received Date and use Acknowledge Exact Transfer. Move Stock → Stock arrived offers the same guided purpose and an older-reference lookup. The cases are already recorded by the completed TO. “No transfer was recorded - record this handoff now” is an exception for a proven unrecorded handoff; it creates the movement and must not duplicate an existing one.',
        check: 'The acknowledgement matches the full existing transfer; any missing or different stock is being investigated.'
      },
      {
        title: 'Distinguish requests from stock fulfillment',
        body: 'Under Requests, open the exact request and compare source, destination, ownership and items to the physical plan. Approve records approval but does not move stock. Fulfill Transfer does move the requested stock. Check for a handoff already posted under another reference before fulfilling. Do not use both request fulfillment and a standalone transfer for the same cases. A fulfilled request is not automatically a completed TO in the standard inbound-transfer list; investigate its own record rather than making a second receipt.',
        check: 'The request has not already been fulfilled through another stock movement.',
        link: { page: 'inventory', label: 'Open stock requests', detail: { type: 'inventory-view', view: 'requests' } }
      },
      {
        title: 'Record sales through Orders',
        body: 'Use the order and fulfillment workflow for customer supply, with the actual stock source and lot selection where required. Reservations/commitments hold stock before completion; the supported fulfillment action records the sale’s stock effect. Do not also enter a Transfer or Giveaway for the same customer cases. Cancelling an unfulfilled order is not a return of already delivered cases. Give Operations the original order, product/lot, actual returned quantity and evidence for a linked return or correction, including any separate account adjustment.',
        check: 'Customer cases are accounted for by their order, without a duplicate manual removal.',
        link: { page: 'orders', label: 'Open Orders' }
      },
      {
        title: 'Record actual giveaways, damage or loss',
        body: 'For a whole-case giveaway, select Giveaway, choose source, product/quantity, recipient and reason, complete the required Expected outcome or Notes, then Log Giveaway. Staff / Rep removes cases; it is not a transfer into rep storage. For damage, expiry, quality concern or missing cases, the authorized field screen More → Report Damage / Loss records exact location, product, lot, quantity, reason and photos before Submit Report. These reports change usable stock immediately according to the incident; later approval does not mean no stock effect occurred yet. If that screen is unavailable, keep the evidence and ask Operations to handle the supported report.',
        check: 'The action describes the actual disposition, and I have not already deducted the same cases elsewhere.'
      },
      {
        title: 'Rebuild mixed packs or handle event cans in their own flows',
        body: 'Build Mix Packs consumes exact eligible finished-case source lots and creates the selected complete mixed recipe at the same location. It does not create more cans. Use Move Stock’s guided Build mixed packs if you need to save incomplete details. For event allocations, case opening, samples, sales and closeout, use Events; do not also transfer or give away those same allocations. Leftover open cans do not become sealed cases by rounding their total.',
        check: 'Repacked cases and open/event cans each retain their source lot history.',
        link: { page: 'events', label: 'Open Events for event stock' }
      },
      {
        title: 'Use a real physical-count submission',
        body: 'Management can select Inventory → Reconcile Count, choose the location and exact existing product/lot, enter the physical cases and evidence, confirm stock movements are paused, and select Save Count. The recorded count, physical count and difference appear as you type. Saving refreshes the stock first; if it has changed, check the updated difference and save again. Missing details or blocked corrections show an explanation. View Count History opens the ledger. An uncertain response must use Recover Same Count. This adjusts stock on hand, not original packing totals or can allocations. In the authorized field layout, reps use More → Count My Storage and warehouse users use More → Cycle Count. Check the location at the top, enter each displayed product/lot’s Physical cases, including zero, and Submit Count. The count can post a variance. If the location is wrong, a needed lot is absent or the holdings are ambiguous, stop and give Operations the signed count sheet. Keep ownership and usable/damaged stock distinguishable.',
        check: 'The exact location, physical count, difference, custody and evidence agree; movements are paused until the count is confirmed.'
      },
      {
        title: 'Verify the result and retain its reference',
        body: 'Check Ledger for the action’s reference, product, lot, location and quantity effect, then reopen Balances. Use Field Receipts for acknowledgement evidence and Requests for fulfillment status. After an uncertain response, first look for that original reference. Use the original action’s recovery feature where provided; otherwise ask Operations to reconcile the evidence before resubmitting. A reverse-direction transfer is appropriate for a real return, not an unexplained attempt to erase an erroneous record.',
        check: 'The ledger and current holdings match the one intended event.'
      }
    ],
    warnings: [
      'Do not add an opening balance, receipt or new production to force a missing source lot into a transfer. Check history and data loading first.',
      'Arrival acknowledgement is exact, not partial. Missing cases or a different lot require investigation. If all cases physically arrived but some are damaged, acknowledge the matching handoff and report damage separately.',
      'A physical return does not itself settle a rep’s purchased-stock account or a customer credit. Keep stock and account corrections linked to their original records.',
      'Reconcile Count only supports unambiguous released Ranch Power stock. Placeholder lots, QA, damaged, purchased and mixed ownership need investigation; do not create a new receipt to force a correction.',
      'Recent lists can omit older records. Do not interpret absence from a recent view as proof that a movement was never posted.'
    ],
    faqs: [
      { question: 'Why do recorded lot 001, printed label and lot group differ?', answer: 'They serve different purposes. A recorded lot such as 001 remains the historical stock reference for that exact SKU. A saved printed label can identify those same cases, such as RP-BLO-001, without renaming their history. Lot group is editable organization only. View printed-label history opens the mapping’s evidence. For an eligible original packaging run, Review recorded-lot correction opens Correct Lot and its stock/source checks. An existing saved printed label cannot be replaced through Save Lot; a changed label needs its exact new code, supporting evidence and a reviewed label correction.' },
      { question: 'What is an OPEN lot?', answer: 'Some loose-can references are generated by repack corrections so recovered cans stay separate from sealed cases. When the exact Open Cans batch confirms the link, Lot Master shows its source lot and repack correction reference, plus the source cases’ saved printed label when available. View Open Cans shows the related loose stock. A matching-looking code alone does not prove that relationship, and it does not mean a new case production run happened.' },
      { question: 'What if someone else edits a lot while my editor is open?', answer: 'Save Lot checks the version you opened and rejects an older draft if the lot changed. Your unsaved fields remain visible and no newer dates are overwritten. Copy any details you need, choose Discard edits and refresh list, reopen the lot and review its current dates before re-entering your correction. An older cached lot with no edit version must refresh before editing.' },
      { question: 'Why are cases at a rep still company stock?', answer: 'Company-owned stock held at our regional rep warehouses is shown as Available. The warehouse identifies where it is held; Ranch Power remains the owner. Older movement history may use Fronted to Rep. Purchased by Rep remains a separate holding.' },
      { question: 'Can I approve a stock request now and fulfill it later?', answer: 'Yes. Approve changes the request status without moving cases. Fulfill Transfer has the stock effect, so check the actual handoff and possible duplicate movement at that point.' },
      { question: 'How do I correct an incorrect giveaway?', answer: 'Open its Ledger row and, when eligible, use Reverse Giveaway with a reason. This keeps the correction linked. Do not invent a receipt to replace the quantity.' },
      { question: 'Where do I see how a location reached its current balance?', answer: 'Use the location’s History and Inventory → Ledger. These views use the records currently loaded, so a loading warning or missing older row needs investigation before you conclude the history is complete. Incoming and outgoing totals describe recorded movements; they are not the current on-hand count or proof of another pending delivery. The location popup previews only eight balance rows; use Balances and its filters for the full loaded balance table.' },
      { question: 'Why cannot I build a mixed pack from all cases shown at a location?', answer: 'The recipe needs eligible exact source lots and one compatible custody holding. Available and Fronted to Rep are not combined in one build, purchased/sample stock is excluded, and released/date/quantity requirements still apply.' },
      { question: 'Is a damage or loss report just a request awaiting approval?', answer: 'No. Its supported incident action already moves cases out of usable stock or removes the missing quantity. Review the recorded result before any later disposition so the same loss is not posted twice.' }
    ],
    history: [
      { title: 'Ledger and location History', body: 'Ledger shows recorded stock effects. Open a row to inspect its reference and linked details; location History helps trace that location’s incoming/outgoing movements. Compare those records with Balances rather than relying only on a notification.', link: { page: 'inventory', label: 'Open movement ledger', detail: { type: 'inventory-view', view: 'ledger' } } },
      { title: 'Requests and Field Receipts', body: 'Requests records approval and fulfillment separately. Field Receipts holds receipt evidence/status. Review whether a receipt is an acknowledgement of an already completed transfer or an older approval-based receipt before taking any action.', link: { page: 'inventory', label: 'Open Field Receipts', detail: { type: 'inventory-view', view: 'receipts' } } },
      { title: 'Production and event provenance', body: 'Use Production → History for how cases were made or corrected, and the relevant event’s records for allocations, open cans and closeout. An event closeout correction should use its supported linked recovery/reversal process, not a second manual stock entry.', link: { page: 'production', label: 'Open production history', detail: { type: 'production-history' } } }
    ],
    related: ['move-stock', 'production', 'dispatch', 'orders', 'events']
  },
  dispatch: {
    title: 'Coordinate the route, then verify the actual handoff',
    summary: 'Dispatch organizes who carries orders and when. A route assignment or manifest is not proof that cases changed custody or reached the customer. Record completed inventory handoffs and delivery evidence in their own supported workflows.',
    before: [
      'Confirm the actual route date, dispatcher/carrier, customer stops, case totals and source stock. Do not rely on a decorative date heading as today’s route evidence.',
      'Separate an order delivery from a warehouse/rep stock transfer. They use different references and stock actions.',
      'Check CHEP custody and paperwork before declaring pallets on a completed handoff. Cases and pallets are different units.'
    ],
    steps: [
      {
        title: 'Choose the actual dispatcher lane and route',
        body: 'Open Board and review the correct driver/carrier and route date. If a lane is needed, Add Dispatcher lets you choose an existing dispatcher/driver or Custom carrier / driver, then enter the lane label, route type and date and Add Lane. Adding a lane does not create a user account or grant app access.',
        check: 'The lane identifies the real carrier/driver and intended route date.'
      },
      {
        title: 'Assign orders and review the saved route',
        body: 'Assign an order by moving its card to the intended lane, then verify its saved assignment. Optimize Routes can order eligible route stops, but it does not move cases or record delivery. Check the resulting route and any conflict/error. Transfer cards whose reference starts TO are different: moving those cards currently changes the local board display only, so do not use that drag as a durable transfer assignment or inventory action.',
        check: 'The order assignment was saved, and no planning action is being treated as a completed handoff.'
      },
      {
        title: 'Check the complete manifest before loading',
        body: 'Open Manifests, identify the correct lane/date and review its stops and cases. Copy loads the complete live manifest for that route when available. Compare the physical load with the actual orders, exact product/lot picking evidence and any required load check. A manifest is a plan: loading, arrival and delivery still need their own records.',
        check: 'The load and complete route manifest agree, and missing or extra cases have been resolved.'
      },
      {
        title: 'Record completed custody transfers and receipt',
        body: 'For stock physically handed from a facility or warehouse to another warehouse or rep storage, use Move Stock → Stock changed hands. Enter the actual custody destination, handler/carrier, time, exact lots/cases and CHEP count. The receiver uses Stock arrived to acknowledge that same TO. For a customer order delivery, use its authorized order/delivery workflow rather than an extra stock transfer or giveaway.',
        check: 'The actual handoff has the correct stock or order reference and the receiving evidence is linked.',
        link: { page: 'move-stock', label: 'Open the completed-handoff workflow' }
      },
      {
        title: 'Keep CHEP custody separate and evidenced',
        body: 'Open CHEP Pallets. Receive from Pool is for pallets actually issued from CHEP; Return to Pool is for an actual return to CHEP. Physical Count is an evidence-backed correction. Enter Custody Location, Occurred At, Pallet Count, Evidence Reference and Reason, plus direction for a count adjustment, then Record Movement. These actions affect pallets only. Pallets already included in a completed stock transfer must not be entered again as a pool issue or receiving movement.',
        check: 'The pallet action describes a real issue, return or evidenced count, with no duplicate transfer entry.'
      },
      {
        title: 'Close the operational loop',
        body: 'After the route, compare completed orders and TO references against the actual deliveries and receipts. Investigate mismatches in product, lot, quantity, destination or CHEP count before posting anything to balance them. A missing receipt acknowledgement does not mean the completed transfer failed to move stock.',
        check: 'Every completed handoff has a clear result, and unresolved discrepancies retain their original references.'
      }
    ],
    warnings: [
      'Dragging a TO transfer card is currently local board organization only. It does not save a durable transfer assignment or move inventory.',
      'Assigning or optimizing a route is not a delivery confirmation. Do not mark stock received because it appears on a manifest.',
      'Do not create a CHEP pool issue solely to clear an insufficient-pallet warning. Reconcile the actual opening custody and evidence first.',
      'A driver name identifies a handler, not necessarily the destination that holds or owns the cases.'
    ],
    faqs: [
      { question: 'Does Add Dispatcher invite someone to the app?', answer: 'No. It creates a route lane using an existing profile or a custom driver/carrier name. Account access and invitations are separate administration.' },
      { question: 'When do pallet balances change?', answer: 'A completed stock transfer with CHEP use changes the declared pallet custody along with the handoff. Separate pool issues, pool returns and supported count adjustments also change pallets. Acknowledging the same TO does not move those pallets again.' },
      { question: 'Where should I record warehouse-to-rep stock?', answer: 'Use Move Stock → Stock changed hands with the warehouse as source and the rep’s actual Micro-Warehouse as destination. A dispatch lane or driver is not a substitute for those custody locations.' },
      { question: 'Does Copy prove that the route was delivered?', answer: 'No. It copies the route manifest. Check the relevant order’s delivery record and the stock transfer/receipt records for actual completion.' }
    ],
    history: [
      { title: 'Manifests', body: 'Manifests provides the route’s current stops and case totals. Use the exact route/date and complete live manifest; this is planning evidence, not an immutable record of delivery.', link: { page: 'dispatch', label: 'Open Dispatch & Logistics' } },
      { title: 'CHEP Movement Journal', body: 'The CHEP Pallets tab includes CHEP Movement Journal with occurred date, reference, movement, route/location, pallet quantity and reason. Compare it with CHEP Pallets by Custody Location.' },
      { title: 'Completed stock and delivery records', body: 'Use Inventory → Ledger and Field Receipts for custody transfers and acknowledgements, and the original Orders records for customer deliveries.', link: { page: 'inventory', label: 'Open movement ledger', detail: { type: 'inventory-view', view: 'ledger' } } }
    ],
    related: ['move-stock', 'inventory', 'orders']
  },
  procurement: {
    title: 'Receive production materials without creating finished stock',
    summary: 'Procurement tracks supplier purchase orders, documents and material receipts. Receiving empty cans or other materials is separate from recording BevMax’s filled-can production and packing finished cases.',
    before: [
      'Have the supplier PO, invoice, packing slip, actual receipt count, damage count, material identity/lot and receiving date/evidence.',
      'Open the existing PO and check Received before recording a delivery. Enter only this receipt’s quantities, not the cumulative total already received.',
      'Keep raw-material units, filled production cans and finished cases distinct. A supplier receipt does not make finished cases available for transfer.'
    ],
    steps: [
      {
        title: 'Find or create the correct purchase order',
        body: 'Open Purchase Orders and match the supplier, material, quantity and PO reference. Use Purchase Order only when the purchase is not already represented. Reorder can prefill a new order, so check for an existing open order first. Review the supplier under Suppliers rather than making a duplicate supplier for the same business.',
        check: 'This purchase is represented by one correct PO with the intended supplier and materials.'
      },
      {
        title: 'Keep supplier and origin evidence with the PO',
        body: 'Open the PO detail and use Supplier Invoice, COO / Origin Docs and Tariff Notes to Upload, Replace or View the relevant documents. Keep the actual material lot and supplier evidence aligned with the PO. The Aluminum Origin & Tariff view supports review of recorded materials; it does not establish missing origin facts for you.',
        check: 'The supporting documents and material identity belong to this PO.'
      },
      {
        title: 'Record the actual delivery quantities',
        body: 'On the PO, select Receive or Receive Remaining. Review each Received now and Damaged field against the physical count and packing slip. Received now initially proposes the remaining ordered amount; change it if this delivery is partial or different. The received and damaged quantities are recorded separately, so check both carefully rather than assuming one is automatically subtracted from the other. Confirm the displayed variance acknowledgement when applicable, then Confirm Receipt.',
        check: 'Every entered quantity is for this delivery, with damage and any variance checked against the paperwork.'
      },
      {
        title: 'Verify the received totals before any further receipt',
        body: 'Reopen the PO and verify each line’s Received total and its Partial or Received state against previous receipts plus this delivery. Review the recorded material quantities in Aluminum Origin & Tariff. If the response was lost or showed an error, do not blindly submit the same delivery again: check what was saved and have Operations reconcile any partial result using the PO and packing slip.',
        check: 'The PO and material totals include this delivery once, and any uncertain result is reconciled.'
      },
      {
        title: 'Continue through Production when cans are actually produced',
        body: 'Approve, Mark Shipped and Delay describe the PO’s progress; Close finishes an eligible PO’s administration. None substitutes for recording filled cans or case packing. Once BevMax has actually produced cans, use Production → Can Run, then the can-to-case and quality-release workflow. Use the actual reports and recipe allocations rather than converting the PO’s ordered amount directly into finished cases.',
        check: 'Finished-can and finished-case entries are based on production evidence, separate from the material receipt.',
        link: { page: 'production', label: 'Open the production workflow' }
      }
    ],
    warnings: [
      'Receive Remaining defaults to the outstanding ordered quantity. It is not evidence that all of it arrived; replace it with the actual delivery count.',
      'The receipt flow does not expose the durable frozen-request recovery used by Move Stock. After an uncertain result, reconcile the PO and material records before resubmitting.',
      'Do not use procurement receipt, PO approval or Mark Shipped as a finished-stock entry. They do not create packed cases or release quality holds.',
      'Origin and tariff fields must follow supplier evidence. This guide does not determine tariff treatment or replace a required specialist review.'
    ],
    faqs: [
      { question: 'Does receiving empty cans increase finished can production?', answer: 'No. It records a material receipt. BevMax’s actual filled-can output must be recorded separately as a Can Run, and finished cases require their own packaging action.' },
      { question: 'Should I enter the total received so far?', answer: 'No. Received now is this receipt’s additional quantity. First check the PO’s existing Received total, then enter the actual new delivery only.' },
      { question: 'What if Confirm Receipt timed out?', answer: 'Reopen the original PO and compare its received totals and material records with the delivery evidence. The operation may have saved some or all of its work. Ask Operations to reconcile an unclear result before entering another receipt.' },
      { question: 'Where is the full material receipt history?', answer: 'This page exposes PO details/documents and current material quantities, but not a dedicated complete raw-material movement-history viewer. Keep the PO and receipt evidence, and ask Operations for the underlying receipt records when current totals cannot explain a discrepancy.' }
    ],
    history: [
      { title: 'Purchase order details and documents', body: 'Open the PO row to review its current ordered/received quantities, status and uploaded evidence. These current totals are not a complete timeline of every receipt.', link: { page: 'procurement', label: 'Open Procurement' } },
      { title: 'Material totals', body: 'Aluminum Origin & Tariff shows recorded materials and quantities for comparison. If a receipt is uncertain, reconcile these with the PO and packing slip before another submission.' },
      { title: 'Separate production history', body: 'Filled-can and packaging activity is recorded under Production → History, not by the PO’s receipt or closed status.', link: { page: 'production', label: 'Open production history', detail: { type: 'production-history' } } }
    ],
    related: ['production', 'inventory', 'move-stock']
  },
  fba: {
    title: 'FBA: know which records are actually confirmed',
    summary: 'The current FBA page contains planning/display controls that are not a complete live shipment or claims workflow. Use verified shipment evidence and the recorded inventory journey before treating cases as shipped, received or reimbursed.',
    before: [
      'Have the verified Amazon shipment reference, destination, products, exact lots/cases, carrier evidence and actual receipt/discrepancy records.',
      'Check the original stock location and any completed handoff before adding another movement.',
      'Do not treat example shipment identifiers, displayed claims or a notification from an unsupported button as live Amazon evidence.'
    ],
    steps: [
      {
        title: 'Verify the external shipment independently',
        body: 'Confirm the shipment and receiving evidence in the authorized Amazon/carrier records, then compare it with the physical goods and your existing inventory references. The current FBA display cannot establish that Amazon accepted or received a shipment.',
        check: 'The shipment, destination, case quantities and physical status are supported by current external evidence.'
      },
      {
        title: 'Understand what Create Shipment currently does',
        body: 'Create Shipment adds a shipment to this page’s in-memory display. It does not currently provide a durable live shipment submission, create an Amazon shipment or post a finished-stock movement. Do not use it as completion evidence or rely on it surviving a reload. Keep the actual shipment reference with the supported inventory record.',
        check: 'I have not mistaken a local shipment display for a saved live shipment or inventory transfer.'
      },
      {
        title: 'Record actual stock custody through the supported workflow',
        body: 'Have Operations confirm the correct existing source/destination location and ownership treatment, then record the real completed custody handoff through Move Stock when supported. Use exact products, lots, case counts, time and carrier evidence. Do not create another movement if the shipment already has a completed transfer. A custody location or receiving scan alone does not establish a change of ownership.',
        check: 'Any recorded inventory movement matches a real handoff and appears only once.',
        link: { page: 'move-stock', label: 'Open the supported stock workflow' }
      },
      {
        title: 'Resolve receiving discrepancies and claims from evidence',
        body: 'Compare actual received stock with the original shipment and completed transfer. Keep shortage/damage evidence and have Operations reconcile the original records. Track, File Reimbursement and File Case currently show notifications rather than completing the claimed external action. They are not proof of tracking, a filed claim, reimbursement or an inventory adjustment.',
        check: 'Any discrepancy has its original references and evidence, and no unsupported claim action is being treated as completed.'
      }
    ],
    warnings: [
      'This page is not currently an end-to-end live FBA shipment/claims integration. Create Shipment is local display state; Track and claim buttons are notifications only.',
      'Displayed reimbursement examples are not a live accounting or claims ledger. Verify amounts and status in the authorized source records.',
      'Do not invent a Batch Receive, giveaway or write-off to make an FBA discrepancy disappear. Preserve the original shipment and stock references for review.',
      'Do not duplicate a completed transfer just because its external receiving or reimbursement process remains open.'
    ],
    faqs: [
      { question: 'Does Create Shipment save and submit a live FBA shipment?', answer: 'No. In the current implementation it changes the page’s browser-memory list only. It does not post inventory or submit a shipment to Amazon.' },
      { question: 'Has File Reimbursement or File Case filed a claim?', answer: 'No. These controls currently show a notification. Complete the claim in the authorized external process and retain its actual reference.' },
      { question: 'Does Track open confirmed live tracking?', answer: 'The current button only shows a notification. Use the verified carrier tracking reference in the source shipment paperwork.' },
      { question: 'Where should I verify the stock movement?', answer: 'Use Inventory → Ledger and the exact recorded transfer, then compare physical receiving evidence. The FBA display alone is not proof that inventory moved or was received.' }
    ],
    history: [
      { title: 'Inventory records remain the stock evidence', body: 'Use Inventory → Ledger and the exact transfer/receipt references for recorded custody changes. The FBA page’s local list and example claims are not a durable movement history.', link: { page: 'inventory', label: 'Open movement ledger', detail: { type: 'inventory-view', view: 'ledger' } } },
      { title: 'External shipment and claim records', body: 'Keep the actual Amazon shipment, carrier and claim references from their authorized systems with your Operations evidence. A local notification is not an external audit record.' }
    ],
    related: ['inventory', 'move-stock', 'dispatch']
  }
};


/* ===== help_operations.js ===== */
/* Page help for the Operations Hub. Keep action names aligned with the source
   views; this catalog describes existing controls and does not grant access. */
const RP_OPERATIONS_HELP_GUIDES = {
  accounting: {
    title: 'Prepare accounting reports',
    summary: 'Review income, expenses, balances and inventory with the source evidence needed to reconcile Ranch Power and Shopify records.',
    before: [
      'Use an Accountant, Operations, Leadership or Administrator account. The reports available elsewhere in the hub can differ by role.',
      'Know the period you need and have the invoices, receipts, Shopify records and bank evidence needed to resolve differences.'
    ],
    steps: [
      { title: 'Choose the period', body: 'Set Start date and End date. The default is the last complete calendar month. As-of date follows End date. Read the selected report description and date basis: some reports show period activity, some show balances, and loose cans show the current position.', check: 'The dates and report description match the question you are answering.' },
      { title: 'Inspect a report', body: 'Choose a section and Report, then Load rows. Use Next page when offered for Ranch Power records. Shopify previews show the first source page; they are not the full extract. View receipt opens an expense attachment using your signed-in access.', check: 'Review source coverage and warnings before interpreting a blank result as no activity.' },
      { title: 'Download the complete evidence', body: 'Leave Include Shopify source sales, refunds, payments, payouts and fees in the workbook checked for a combined export. Choose Export month-end Excel, or Export selected CSV for one report. Wait for the download confirmation; Cancel export stops an active export.', check: 'Keep the downloaded data together with its Source coverage and export details. The export must finish successfully.' },
      { title: 'Reconcile related records once', body: 'Match exact order, invoice, transaction and payout references. A Shopify sale may also have a Ranch Power invoice. Sales, payments, refunds and payouts describe different parts of the same activity; do not add all sheets together as income. Keep source currencies and cases, cans and merchandise quantities separate.', link: { page: 'invoicing', label: 'Review invoices' } }
    ],
    warnings: [
      'Missing Shopify history or payout access blocks a combined export. A Ranch Power only export is clearly marked and excludes Shopify; it is not a complete combined file.',
      'Missing POS location names or event links require review. The system does not invent event attribution or convert currencies.',
      'Finished inventory is a quantity report without an approved costing basis. Exporting does not move stock or close an accounting period.'
    ],
    faqs: [
      { question: 'Why did the export stop without a file?', answer: 'The export checks every requested page. Changed source records, missing access, failed requests or too many rows stop the download rather than create a partial file. Follow the error, retry a changed source, or narrow the period.' },
      { question: 'Why is this different from Expenses or the dashboard?', answer: 'Those screens are operational views. The expense review inbox contains up to 250 recent records. Accounting reads the complete requested period and states each report’s date basis.' },
      { question: 'Does an empty fees or payouts preview mean zero fees?', answer: 'Only an available source with the requested pages verified supports a complete result. Unavailable or incomplete coverage means the data has not been established.' }
    ],
    history: [
      { title: 'Keep the export evidence', body: 'The workbook records source, period, cutoff, row counts and warnings. Shopify is read live with a cutoff, not from an immutable historical archive.' },
      { title: 'Trace a source record', body: 'Use Invoices for payment history, the Inventory movement ledger report for stock movements, and Shopify & event reconciliation for recorded source links.', link: { page: 'accounting', label: 'Open Accounting' } }
    ],
    related: ['invoicing', 'expenses', 'reports', 'events', 'inventory']
  },

  dashboard: {
    title: 'Use the Command Center',
    summary: 'Start the day by finding the work that needs attention, then open the underlying record to take action.',
    before: ['Check that you are signed in to the correct account. Your role controls the pages and records you can open.', 'Read any loading, unavailable or limited-queue notice before relying on totals.'],
    steps: [
      { title: 'Start with Priority Triage', body: 'Open a Priority Triage item to reach the relevant workspace or record. Review the actual order, task or stock request before completing it.', check: 'The opened record is the one described by the alert.' },
      { title: 'Review operational exceptions', body: 'Use Order Aging, Finance Risk, Open Exceptions and Pending Approvals to find delayed or blocked work. Follow Orders or A/R to the full working lists.', link: { page: 'orders', label: 'Open orders' } },
      { title: 'Plan follow-up', body: 'Retail Restock Radar suggests customer follow-up from recorded history. Open the customer to review recent orders before contacting them. Use Tasks to assign the next step and owner.', link: { page: 'tasks', label: 'Assign follow-up work' } },
      { title: 'Check the result in its workspace', body: 'After saving a change, return to the source order, invoice or task and confirm its status. Use Reports & Exports when you need a complete extract rather than a dashboard summary.', check: 'The underlying record shows the intended result; clearing a notification alone does not complete the work.' }
    ],
    warnings: ['A large operational queue notice means some workspace lists use a limited recent cache. Complete order and invoice history remains in the paged lists.', 'Unavailable sales or coverage figures are not zero. A coaching card marked Preview is a planning aid.'],
    faqs: [
      { question: 'Why are a figure or chart missing?', answer: 'Complete source data may still be loading or a service may have failed. Read the notice and use Try Again or Retry where offered. Do not substitute a partial list total.' },
      { question: 'Does Mark all read finish those tasks?', answer: 'No. It clears notification read status. Complete or resolve the actual task or business record in its workspace.' },
      { question: 'Why can another team member see more pages?', answer: 'Menu access and record scope follow the signed-in role and assignments. Ask an administrator to review your access if it does not match your work.' }
    ],
    history: [{ title: 'Follow the source record', body: 'The Command Center is a summary, not a company-wide action ledger. Open an order’s Activity Log, an invoice’s Payments section, or the appropriate workspace history.' }],
    related: ['tasks', 'orders', 'invoicing', 'customers', 'reports']
  },

  tasks: {
    title: 'Assign work and report a problem',
    summary: 'Give each task an owner, a clear outcome and a due date. Support tickets can include screenshots and related records.',
    before: ['Have the account or order reference if the task concerns a specific record.', 'For a support ticket, record what happened, what you expected and which page you were using.'],
    steps: [
      { title: 'Find existing work first', body: 'Use the task tabs, search and filters. Switch between Board and List, or open the support view. Open a card or row to read its details before creating a duplicate.' },
      { title: 'Assign a task', body: 'Choose Assign Task. Enter the title, assignee, priority, category, due date and useful notes. Check the due date rather than accepting it without review, then choose Assign Task.', check: 'The saved task appears with the intended owner and due date.' },
      { title: 'Submit a support ticket with evidence', body: 'Choose Support Ticket. Select the type and priority, link an Account and Order / invoice when relevant, and fill Details and Page / context. Use Screenshots or files for up to six images or PDFs, 8 MB each. Wait for uploads, then Submit Ticket.', check: 'The confirmation shows the created ticket and its attachments.' },
      { title: 'Keep progress current', body: 'Open the task to change Assignee or Move to status. Use Block or Unblock when work cannot proceed, Mark Done when it is finished, or Reopen if more work is needed. Approval, removal and account-setup tasks have their own actions; read those before completing them.', check: 'Reopen the task and verify the saved status, not only its position on the board.' }
    ],
    warnings: ['Task reassignment and status controls save changes; dragging between board columns also changes status.', 'Send Invite and Send Intake Link send real account messages when available. A support attachment upload does not submit the ticket until Submit Ticket succeeds.'],
    faqs: [
      { question: 'Why can’t I complete an approval task?', answer: 'Some tasks require a permitted reviewer or completed customer intake. Follow the task’s explanation and use Open Customer or Open Account to resolve the prerequisite.' },
      { question: 'Why can’t I add my screenshot?', answer: 'Use a supported image or PDF within the six-file and 8 MB per-file limits. Wait for each upload to finish and read any upload error before submitting.' },
      { question: 'How do I find an older linked order?', answer: 'Use the Order / invoice search in the ticket form and Load More Orders when offered. The visible options are a page of accessible records.' }
    ],
    history: [
      { title: 'Review the task record', body: 'Open the task to see its ID, current status, Assigned by, Created date, notes and linked account. Support tickets also retain submitted details and attachments.' },
      { title: 'Investigate an earlier change', body: 'This dialog does not show a full change timeline. Give Operations the task ID when an administrator needs to investigate recorded audit events.', link: { page: 'admin', label: 'Open Admin' } }
    ],
    related: ['dashboard', 'customers', 'orders', 'contacts']
  },

  contacts: {
    title: 'Handle website inquiries',
    summary: 'The Website Inbox keeps the customer’s message, a shared owner and the team’s response history together.',
    before: ['Sign in to a live team account; the shared inbox is not populated in demo mode.', 'Read existing notes and ownership so two people do not handle the same inquiry independently.'],
    steps: [
      { title: 'Find the inquiry', body: 'Use Open inquiries or Open & unassigned, then Search inquiries, Show and Owner to narrow the list. Open the message. Previous and Next move through matching pages.' },
      { title: 'Take ownership', body: 'Choose Assign to me or select Assigned to. Set Status to reflect the next step: New, In progress or Waiting.', check: 'Wait for Inquiry updated and confirm the selected owner.' },
      { title: 'Reply and record the outcome', body: 'Reply by email opens your email app. Send the reply there, then return to enter a Team note and choose Add note. Record the handoff or next step clearly.', check: 'The note appears in Activity. Adding a note does not send a reply.' },
      { title: 'Resolve or classify the inquiry', body: 'Choose Mark resolved when handled, or Mark as spam for an unwanted inquiry. Reopen inquiry and Restore to inbox let you bring it back when needed.', check: 'The status changes and the inquiry appears under the matching filter.' }
    ],
    warnings: ['If another person changed the inquiry, use Reload latest details before making another change. An uncertain save may already have completed; review it first.', 'Team notes are internal. Reply by email uses your email app and is not automatically a logged in-app send.'],
    faqs: [
      { question: 'Where did the inquiry go?', answer: 'Resolved and spam inquiries leave the open view. Change Show or use the Resolved or Spam totals, and clear the Owner and search filters.' },
      { question: 'Why is editing temporarily blocked?', answer: 'A conflicting or unconfirmed update requires Reload latest details. Your note is retained so you can compare before submitting it again.' },
      { question: 'Is the inbox current?', answer: 'It refreshes periodically and when you return to the window. Use Refresh if needed. A load error explicitly warns that an existing list may be out of date.' }
    ],
    history: [{ title: 'Read Activity', body: 'Open the inquiry and scroll to Activity for recorded notes and changes with actor and time. The last-updated time is shown below it.' }],
    related: ['tasks', 'customers', 'email-log']
  },

  'email-log': {
    title: 'Check email delivery',
    summary: 'See recorded invoice, receipt, quote and other email sends, then investigate recipients that need attention.',
    before: ['Have the recipient address, subject, invoice number or record reference.', 'Operations dashboard access is required for the full log; the server also limits which records your account can read.'],
    steps: [
      { title: 'Search the send', body: 'Enter an email, subject, invoice or record number in Search email history and choose Search. Select Delivery status, starting with Needs attention when reviewing problems.' },
      { title: 'Read Delivery details', body: 'Open Delivery details on the relevant message. Compare the recipients, timestamps, reason and individual recorded events. Provider accepted is different from Delivered.', check: 'You have identified the affected address and the actual delivery outcome.' },
      { title: 'Correct the source before resending', body: 'For an address problem, review the customer’s invoice address and extra invoice contacts. Return to the original invoice, receipt or quote to send through its normal confirmation flow.', link: { page: 'customers', label: 'Review customer contacts' } },
      { title: 'Check the follow-up result', body: 'Use Refresh and Previous or Next to inspect the later send. On orders or invoices, expand Email needs attention to review the same record’s delivery evidence.', check: 'A later confirmed delivery to the affected address resolves the problem; provider acceptance alone does not prove delivery.' }
    ],
    warnings: ['Delivery unknown can mean an earlier send needs recovery. Recover the original send or ask Operations to reconcile it before starting another.', 'An older message may have only a saved summary. Missing delivery, open or click tracking is not proof that the recipient did not receive or read it.'],
    faqs: [
      { question: 'Why is an email still flagged after I resent it?', answer: 'The unresolved recipient needs a later confirmed delivery. An accepted resend, or a delivery to a different address, must not hide the earlier problem.' },
      { question: 'Why is there no email in this view?', answer: 'Clear search and status filters and check the record reference. Only recorded, accessible sends are shown; this is not a copy of every email sent outside the application.' },
      { question: 'Can I resend from this log?', answer: 'The log explains delivery. Open the original business record and use its sending controls after confirming recipients and the prior result.' }
    ],
    history: [{ title: 'Read the event sequence', body: 'Delivery details shows saved provider events, times, affected recipients and reasons. Earlier sends without full history are labelled as such.' }],
    related: ['invoicing', 'orders', 'customers', 'marketing', 'pipeline']
  },

  orders: {
    title: 'Create and manage orders',
    summary: 'Keep the order, fulfillment, invoice and payment states clear. Each reflects a different part of the sale.',
    before: ['Confirm the approved customer, products, case quantities, delivery method and requested date.', 'For a Shopify order, establish whether inventory has already been handled before resolving its inventory queue.'],
    steps: [
      { title: 'Find the order or create it once', body: 'Search and filter Orders before choosing New Order. Complete the customer, items and fulfillment details, review the displayed total and charges, then Create Order.', check: 'The saved order has its own reference and the intended items, quantities and delivery details.' },
      { title: 'Read the order detail', body: 'Open the row and review its items, price breakdown, Customer, Fulfillment and Invoice sections. Payment and delivery are separate: paid stock may still need delivery, and delivered stock may still be unpaid.' },
      { title: 'Take the available next action', body: 'Use the action offered for the current status only after the real step has happened. Edit is available only while the order is editable. For a required invoice, choose Issue Invoice and review the payment method, required PO and email options before confirming.', check: 'The status or invoice reference updates on the same order.', link: { page: 'invoicing', label: 'Open Invoicing' } },
      { title: 'Resolve Shopify stock deliberately', body: 'For an order in the Shopify inventory queue, inspect the source, exact lots and any event link before Resolve Inventory. Batch Resolve is for deliberately selected orders. Review each selection before confirming; Ignore is not a stock movement.', check: 'The reconciliation result matches what happened physically, without deducting the same sale twice.' }
    ],
    warnings: ['An issued invoice locks order items and pricing. Follow the invoice correction or reconciliation process before editing; do not create a second sale to work around the lock.', 'Recover Invoice Request and Retry Original Request refer to an unfinished original action. Resolve that request before attempting a new one.', 'Removing, reversing delivery or resolving Shopify inventory can affect other records. Read the explanation and verify the original evidence first.'],
    faqs: [
      { question: 'Why can’t I create or edit this order?', answer: 'The customer may need approval, an address or stock choice may be missing, or an invoice/status may lock the order. Follow the form’s stated blocker rather than changing unrelated values.' },
      { question: 'Why is an older order missing?', answer: 'Use the full Orders search, clear saved filters and load more matching records. Other workspaces may show a limited recent queue.' },
      { question: 'Does Paid mean Delivered?', answer: 'No. Use the separate payment, invoice and fulfillment fields. The paid-not-delivered and delivered-unpaid views help find those differences.' }
    ],
    history: [{ title: 'Read Activity Log', body: 'Open the order and review Activity Log. Use its invoice link for payment history and its email status for delivery evidence.' }],
    related: ['customers', 'invoicing', 'dispatch', 'events', 'email-log']
  },

  invoicing: {
    title: 'Issue invoices and record payments',
    summary: 'Review the charge breakdown, issue the document, confirm recipients and keep payment evidence against the original invoice.',
    before: ['Confirm the correct order, customer, billing address and any required PO number.', 'Have the actual payment or refund evidence before recording money received or returned.'],
    steps: [
      { title: 'Find the invoice or ready order', body: 'Use search and the All, Unpaid, Overdue, Paid or Ready views. Ready to Bill opens orders awaiting an invoice. Open a row to review the source order and full document.' },
      { title: 'Issue or recover the invoice', body: 'Use Issue or Issue Invoice when ready, and review payment method, terms and send options. If Recover Invoice Request appears, use that original request instead of creating another.', check: 'The invoice has a saved number and issue details. A Ready row is not an issued invoice.' },
      { title: 'Check the PDF and recipients', body: 'Use PDF to review the document. Email opens a recipient confirmation with To and CC, including configured invoice-copy contacts. Check the list and choose the displayed Send Invoice or Send Receipt action.', check: 'Inspect the email delivery status after sending; accepted is not the same as delivered.', link: { page: 'email-log', label: 'Check email delivery' } },
      { title: 'Record the actual payment or refund', body: 'Use Record Payment for a received payment, with amount, method and reference. Charge Card charges the saved card. Enter Card Manually lets you enter a card from a signed authorization form in secure Stripe fields, review the balance and 2.4% fee, and charge the invoice without replacing the saved card. Confirm the signed authorization before charging. If a payment is pending, use Check Payment Status to finish the same payment. Use Refund only for the appropriate recorded return, checking its amount and method.', check: 'Review Payments and the remaining Balance. A partial payment should leave the correct remainder.' }
    ],
    warnings: ['Charge Card can collect real money. Recording an offline payment does not charge a card.', 'A refund record or reimbursed status should be matched to the actual payment evidence. Do not assume a status change proves bank settlement.', 'Email, payment and edit controls may be unavailable while an original invoice request is being recovered or when your account lacks permission.'],
    faqs: [
      { question: 'How do I include another invoice recipient?', answer: 'Edit the customer’s contacts and enable Receives invoice and receipt copies for each valid address. Review To and CC in the email confirmation before sending.' },
      { question: 'Why is the payment amount blocked?', answer: 'It must be positive and cannot exceed the current invoice balance. Recheck existing payments before entering another payment.' },
      { question: 'Which view should I use for a past period?', answer: 'Use Accounting → Invoices & balances for the dated extract and evidence flags. Invoicing is primarily the current working view.' }
    ],
    history: [
      { title: 'Review Payments', body: 'The invoice detail shows recorded payments. Open its linked Order for Activity Log and expand email status for delivery history.' },
      { title: 'Keep period evidence', body: 'Use Export A/R for a working report or Accounting for dated reconciliation and source coverage.', link: { page: 'accounting', label: 'Open Accounting' } }
    ],
    related: ['orders', 'customers', 'email-log', 'accounting']
  },

  customers: {
    title: 'Maintain customer accounts',
    summary: 'Keep the account, contacts, billing preferences and locations correct before ordering or sending documents.',
    before: ['Search for the business and location before adding a customer, including existing distributor-supplied locations.', 'Have the correct buyer, billing recipients, delivery address, account terms and assigned team member.'],
    steps: [
      { title: 'Find or add the account', body: 'Use Customers search and filters, loading more results if needed. Open an existing account or choose Add Customer. Check possible duplicate accounts before creating another.' },
      { title: 'Update contacts and invoice copies', body: 'Choose Edit Account. In Customer Contacts, keep the Primary Buyer correct and add the other contacts. Enable Receives invoice and receipt copies for each person who should receive them, with a valid email, then Save Changes.', check: 'The Contacts section shows Invoice + receipt copies for the intended people.' },
      { title: 'Complete onboarding and approval', body: 'Review Retail Onboarding and the account’s status. Record Paper Form or Update Onboarding captures supplied details. If New Order shows Approval Needed, complete the stated intake and approval steps with a permitted reviewer.', check: 'New Order becomes available only when this account is eligible to order.' },
      { title: 'Use the account’s linked evidence', body: 'Review Order History, locations, account balance and available card details. Shopify Sync offers Preview Push before Push to Shopify; inspect its warnings and intended changes before a permitted push.', check: 'Reopen the account to confirm saved details. An account record and a Shopify link are separate things.' }
    ],
    warnings: ['A contact’s invoice-copy flag adds document recipients; it does not grant portal access or resend earlier documents.', 'Credit, Debt and Payout controls affect the recorded account balance. Use actual references and avoid duplicating an invoice payment or refund.', 'Remove or Request Removal follows its own safeguards. Preserve a real account’s order and financial history.'],
    faqs: [
      { question: 'Why can’t this customer order?', answer: 'The account may need onboarding or approval, be archived, or be a directory-only distributor-supplied location. Read the account explanation and ask the responsible reviewer to resolve it.' },
      { question: 'Why did only the buyer get the invoice?', answer: 'Check the saved billing address, the additional contacts’ invoice-copy flags and their email validity. Then inspect the actual To/CC list and Email Delivery for that send.' },
      { question: 'Why doesn’t Order History show everything yet?', answer: 'Live history loads in pages. Use the load-more control and resolve any loading error before treating the visible rows as complete.' }
    ],
    history: [{ title: 'Use the linked histories', body: 'Customer detail contains Order History and recorded account balance entries. Open an order or invoice for its activity, payments and email evidence. There is no single customer change timeline on this screen.' }],
    related: ['orders', 'invoicing', 'pipeline', 'email-log', 'tasks']
  },

  pipeline: {
    title: 'Work a lead through the pipeline',
    summary: 'Keep contact details, the next follow-up and recorded activity together as a lead moves toward an approved customer account.',
    before: ['Check for an existing lead or customer before adding another.', 'Have a useful next action and current contact details; estimated potential is a planning estimate.'],
    steps: [
      { title: 'Choose the next lead', body: 'Review Needs Attention or Overdue Follow-ups and open the lead. Read its stage, Contact Details, notes and Activity before taking the recommended next move.' },
      { title: 'Add or correct the details', body: 'Use Add Lead for a new opportunity. Enter the business, territory and required potential with contact information. On an existing lead, Edit in Contact Details opens Save Lead.', check: 'The lead shows the correct business and contact details after saving.' },
      { title: 'Record the real follow-up', body: 'Use the lead’s available activity controls and next-step action. Email opens a composer; review its recipient and content before sending. Review email shows the recorded send and follow-up evidence.', check: 'Activity reflects what happened and the next action is appropriate.' },
      { title: 'Link the right account or preserve a lost lead', body: 'When converting, choose Link existing customer or Create new prospect after checking duplicate candidates. A prospect may still need intake and approval. For a real lead no longer interested, use Mark as Lost - keep history.', check: 'The lead is linked to the intended customer or retained with the correct lost status.' }
    ],
    warnings: ['Delete duplicate / test lead is for genuinely unused duplicate or test records. Customer, activity and email history can block deletion.', 'Manual email suppression blocks an exact address across pipeline and marketing sends. Enter a factual reason and use it only when that block is intended.'],
    faqs: [
      { question: 'Does converting a lead approve the customer?', answer: 'No. Linking or creating a prospect establishes the account relationship. Complete the customer’s required onboarding and approval before ordering.' },
      { question: 'Why are email metrics blank?', answer: 'Provider-backed metrics require live access and tracking. Read their coverage or error message; preview mode does not simulate delivery results.' },
      { question: 'Where should I record an unsuccessful conversation?', answer: 'Use the lead’s activity controls and notes so the next person can see what happened. Mark as Lost preserves that history when the opportunity is finished.' }
    ],
    history: [{ title: 'Read Activity and Review email', body: 'The lead panel lists recorded activities and dates. Review email and the Email Delivery workspace provide the saved provider evidence for sends.', link: { page: 'email-log', label: 'Open Email Delivery' } }],
    related: ['customers', 'tasks', 'marketing', 'email-log']
  },

  marketing: {
    title: 'Manage restock subscribers',
    summary: 'Review restock requests, select an intended audience and confirm an email campaign before it is sent.',
    before: ['Confirm the product is ready for the message you plan to send.', 'Your account must have campaign-management permission. Viewing subscribers does not always include permission to change them or send.'],
    steps: [
      { title: 'Review the requests', body: 'Use the status tabs, product filter and search in Restock Subscribers. Refresh reloads records. Export CSV exports the currently visible matching list.' },
      { title: 'Select the exact recipients', body: 'Tick the intended subscriber rows and choose Email Selected. The search box narrows the visible list; do not assume search text alone defines an unselected Email Blast audience. Explicit selections make the intended set clear.', check: 'The selected count and confirmation’s unique-recipient count match the intended audience.' },
      { title: 'Review and send the message', body: 'Check Subject, Message, Button URL and Button Label. Decide whether to Mark sent subscribers as notified. Read the audience, tick the send confirmation and choose Send Blast only when ready.', check: 'Review the sent-versus-recipient result, including any failures.' },
      { title: 'Check the outcome', body: 'Review Recent Email Blasts and use Email Delivery for delivery problems. Notified and Mark Notified update request status; they are not a substitute for an actual send.', link: { page: 'email-log', label: 'Review email delivery' } }
    ],
    warnings: ['Send Blast sends real email. Closed and unsubscribed rows are excluded from selectable recipients.', 'Close and Suppress change subscriber handling. Confirm the correct address before suppressing it.', 'This screen loads a bounded subscriber list; it is not an unlimited audience archive.'],
    faqs: [
      { question: 'Why is Send Blast disabled?', answer: 'Check management permission, an active recipient, the confirmation checkbox and the required subject/message length. The dialog explains its audience.' },
      { question: 'Why are the selected and recipient counts different?', answer: 'One address may have several product requests. The confirmation counts unique active email recipients, excluding closed or unsubscribed entries.' },
      { question: 'Does Notified mean Delivered?', answer: 'No. It is a subscriber workflow status. Use the email’s delivery evidence to establish what happened after sending.' }
    ],
    history: [{ title: 'Review Recent Email Blasts', body: 'This section shows the subject, sender, time, sent count and recorded blast status. Individual delivery evidence is in Email Delivery.' }],
    related: ['email-log', 'pipeline', 'contacts']
  },

  events: {
    title: 'Plan an event and reconcile its closeout',
    summary: 'Keep the event plan, actual case custody, opened cans and linked POS sales distinct so the closeout can be checked.',
    before: ['Know the dates, venue, responsible team, source location and exact stock lots.', 'Before closing out, collect the physical counts and relevant sales evidence. A plan or a Shopify location label is not proof of a completed stock movement or sale.'],
    steps: [
      { title: 'Create or find the event', body: 'Use Calendar, Board or List with the search and filters. Choose New Event or Edit, check start/end dates, status, venue, customer and default source, then Save Event.', check: 'Event Detail shows the intended dates and status.' },
      { title: 'Record the plan and actual custody separately', body: 'In the editor, Add Team and Add Stock build the plan. Once an event is eligible, Add Cases records exact-lot cases at the event. Open Cases handles cases actually opened for loose cans. These are separate from simply saving an Inventory Plan.', check: 'Inventory shows the expected Cases At Event and Open Loose counts.', link: { page: 'inventory', label: 'Review stock and lots' } },
      { title: 'Review linked sales', body: 'In POS And Closeout, check Linked orders and can counts. Review Shopify inventory reconciliation in Orders when source or event attribution needs attention. Singles, cases and merchandise must retain their actual units.', link: { page: 'orders', label: 'Review Shopify orders' } },
      { title: 'Reconcile the count', body: 'Choose Close Out. Review Case Stock and Loose Cans, enter verified counts and dispositions, then Reconcile Closeout. If an exact request is pending, use its Recover action before starting another.', check: 'Count status shows the reconciled result and Immutable closeout history contains the receipt.' }
    ],
    warnings: ['Add Cases changes recorded stock custody; a planned quantity does not. Do not use both workflows to record the same physical movement twice.', 'A reconciled closeout cannot simply be overwritten. Permitted users can Reverse Closeout with a reason; this preserves the original receipt and records a reversal.', 'Linked POS counts cover recorded links. Use Accounting source coverage and reconciliation reports to investigate missing Shopify activity.'],
    faqs: [
      { question: 'Why is Add Cases disabled?', answer: 'Case inventory can only be added to an eligible active booked event. Review the event’s current status and the explanation next to Inventory.' },
      { question: 'Why must I recover an exact request?', answer: 'A stock or closeout action may already have saved before its response was lost. Recovery checks the original saved request and receipt, preventing a second movement.' },
      { question: 'Does closing an event prove all Shopify sales are included?', answer: 'No. Check recorded event links and the complete Shopify extracts. An event count, financial sale and bank payout are separate evidence.' }
    ],
    history: [{ title: 'Review closeout receipts', body: 'POS And Closeout shows recent Immutable closeout history, including reversals. Use the receipt reference when asking Operations to investigate older or unconfirmed actions.' }],
    related: ['inventory', 'move-stock', 'orders', 'accounting', 'tasks']
  },

  goals: {
    title: 'Set and review sales goals',
    summary: 'Review the selected period’s revenue, case and new-door targets, then use recorded sales to discuss progress.',
    before: ['Confirm the person and reporting period. A monthly target and a quarterly target answer different questions.', 'Goal editing requires a permitted management role. Team members can review the targets available to them.'],
    steps: [
      { title: 'Choose the period', body: 'Select the period at the top. Read each person’s scope as well as the team totals. An empty period may simply have no saved goals.' },
      { title: 'Set a goal when permitted', body: 'Choose Set Goal, select Team Member and Period, and enter Revenue Target. Add Cases Target, New Doors Target and a Coaching note as needed, then Set Goal.', check: 'A card appears for the intended team member and period with the saved target values.' },
      { title: 'Review and update the target', body: 'Use Edit on the relevant card and Update Goal. An existing goal’s period is locked in that editor, so check the correct period before making the change.', check: 'Reopen the card’s editor to confirm the saved targets and coaching note.' },
      { title: 'Discuss the evidence behind progress', body: 'Compare the displayed actuals with targets and the Attainment Leaderboard. Use Territory or Analytics for supporting sales detail. Treat the pace marker as a planning indicator and verify source dates before using it to make a performance decision.', link: { page: 'analytics', label: 'Review sales analytics' } }
    ],
    warnings: ['Goal totals are management targets and displayed progress, not a complete accounting statement.', 'A team member’s territory or company scope must be correct before interpreting comparisons.'],
    faqs: [
      { question: 'Why don’t I see Set Goal or Edit?', answer: 'Those actions are reserved for permitted management accounts. Ask the person responsible for goals to make the change.' },
      { question: 'Why is a team member missing from the picker?', answer: 'The picker uses active staff profiles in eligible roles. An administrator may need to correct the person’s profile or assignment.' },
      { question: 'Can I move a saved goal into another period?', answer: 'The edit form locks its period. Select the intended period and review whether a separate goal is needed rather than changing the meaning of an existing target.' }
    ],
    history: [{ title: 'Review saved values', body: 'The goal card, coaching note and editor show the current target. This page does not show a complete target-change timeline; keep the period and team-member reference when asking management to investigate a change.' }],
    related: ['analytics', 'pipeline', 'tasks', 'reports']
  },

  trade: {
    title: 'Record and review trade deductions',
    summary: 'Track slotting, chargebacks, promotional allowances and credit memos with the customer and related orders.',
    before: ['Have the customer, supporting deduction document, amount and business period.', 'Check whether the same amount already appears as an invoice adjustment, credit or expense.'],
    steps: [
      { title: 'Review existing deductions', body: 'Look in Deductions & Fees and open the relevant row. Check its ID, customer, type, period, amount, coding and current status before adding another.' },
      { title: 'Record a new deduction', body: 'If permitted, choose Record Deduction. Select the eligible Customer, Type and available Period, enter a positive Amount and choose Record.', check: 'The saved deduction appears with its own ID and Pending status.' },
      { title: 'Check the order matches', body: 'Open the deduction and inspect Match to Orders / Invoices. Select the actual supporting records; review any initially selected match rather than assuming it is correct. Load more orders if needed.' },
      { title: 'Save the review outcome', body: 'Use Approve or Dispute as appropriate. A disputed record offers Withdraw Dispute or Resolve. Read the current status before acting.', check: 'Reopen the deduction and confirm the saved status and matched references.' }
    ],
    warnings: ['The available Period choices are limited. If the correct period is missing, ask Operations to resolve it rather than assigning a false period.', 'The gross-to-net display combines its displayed sales basis with recorded deductions. Use Accounting for period reconciliation before treating it as a complete financial total.', 'A deduction match or approval is not proof that money moved or that an invoice balance was automatically settled.'],
    faqs: [
      { question: 'Why are no customers available?', answer: 'The form requires an eligible customer account. Resolve its account status in Customers rather than choosing an unrelated account.' },
      { question: 'Why is Gross Sales blank?', answer: 'Complete live sales may be unavailable. Read the status notice and retry. A missing total is not zero sales.' },
      { question: 'Why can’t I change a closed match?', answer: 'Closed deductions are presented as settled and their matching controls are locked. Ask the responsible reviewer to inspect the record and supporting evidence.' }
    ],
    history: [{ title: 'Keep the deduction reference', body: 'The detail shows current status, owner, coding and matched orders. Accounting → Trade spend & deductions provides recorded period evidence; this page is not a full change-history ledger.', link: { page: 'accounting', label: 'Open Accounting' } }],
    related: ['customers', 'orders', 'invoicing', 'expenses', 'accounting']
  },

  expenses: {
    title: 'Review and code expenses',
    summary: 'Check the receipt and recorded amount before approving an expense, requesting information or recording reimbursement.',
    before: ['Expense review requires Administrator, Leadership, Operations, Sales Manager or Accountant permission.', 'Have the source receipt and any reimbursement evidence. This page is a recent review inbox, not the complete expense archive.'],
    steps: [
      { title: 'Find the expense', body: 'Use To Review, Approved, Reimbursed or All, plus search and filters. Open the expense row and confirm who submitted it, the merchant, date, category and amount.' },
      { title: 'Inspect the receipt and coding', body: 'Choose View to open the protected receipt when available. Review Tax Code, GL Account and the reimbursement setting against the supplied evidence.', check: 'The source receipt supports the expense details. A calculated tax preview is not a substitute for that document.' },
      { title: 'Save the review decision', body: 'For Submitted or Needs Info records, choose Approve & Code, Request Info or Reject. Request Info records that more information is needed; coordinate the specific missing detail with the submitter.', check: 'The saved status and coding appear on the same expense.' },
      { title: 'Record reimbursement after checking payment', body: 'For an approved expense, use Mark Reimbursed only when the reimbursement has actually been handled. For the full period, use Open Accounting for complete period records or Export Expenses.', check: 'The record appears in Reimbursed and can be matched to payment evidence.', link: { page: 'accounting', label: 'Open complete period records' } }
    ],
    warnings: ['The inbox contains up to 250 recently loaded expenses. Its counts and totals cover those records only.', 'Mark Reimbursed records status; it does not send a bank payment.', 'Older records may not have an uploaded receipt. Do not interpret an absent attachment as proof of the tax treatment.'],
    faqs: [
      { question: 'Why is an older expense missing?', answer: 'Clear filters first. For a complete date range, use Accounting → Expense line items or the expense report rather than the recent review inbox.' },
      { question: 'Why won’t View open the receipt?', answer: 'The older record may have no file, access may be unavailable, or the protected request may have failed. Read the error and obtain the source receipt if it is missing.' },
      { question: 'Does Request Info automatically explain what is missing?', answer: 'The current review control changes the status. It has no message field for a detailed request, so tell the submitter which evidence or correction is needed.' }
    ],
    history: [{ title: 'Trace the expense and receipt', body: 'Keep the expense ID and its receipt reference. The current review status is shown here; use Accounting’s complete period extract for the saved coding and evidence fields.' }],
    related: ['accounting', 'reports', 'tasks', 'trade']
  },

  reports: {
    title: 'Build a complete report export',
    summary: 'Choose an individual report or workbook, set its filters, inspect a preview and download all matching accessible records.',
    before: ['Know the question, date range and fields required. The catalog is limited to reports your account may access.', 'For combined Ranch Power and Shopify accounting evidence, use the Accounting workspace.'],
    steps: [
      { title: 'Choose the report or workbook', body: 'Select a workbook card or use Report or workbook. For a workbook, Configure workbook sheet chooses which sheet’s filters and preview you are editing.' },
      { title: 'Set the filters and columns', body: 'Use the available date presets and report filters. Date presets apply to compatible workbook sheets; other filters apply to the sheet being configured. Select fields under Choose fields, or use Select All and Defaults.' },
      { title: 'Run a fresh preview', body: 'Choose Run Preview. After changing filters or columns, choose Update Preview before relying on the count. The preview shows the first rows with the exact matching total.', check: 'The report, filters, count and displayed Snapshot match your intent.' },
      { title: 'Export and retain the details', body: 'Choose Export Excel, Export CSV or Export Complete Workbook. A workbook includes all safe columns declared for each sheet; its column picker controls the active preview and optional CSV. Wait for the completed download message.', check: 'The download completed without a partial-file or row-limit error. Keep its export context with the data.' }
    ],
    warnings: ['A preview is not the full export. Export controls retrieve all matching rows up to the stated allowance.', 'If the row allowance is exceeded or completeness checks fail, no partial file is downloaded. Narrow the period or filters and retry.', 'Refresh Catalog reloads definitions; it does not grant extra access.'],
    faqs: [
      { question: 'Why did my workbook include columns I unticked?', answer: 'Complete workbooks include every safe declared column for each sheet. The picker customizes the active preview and its CSV; individual-report exports use their selected columns.' },
      { question: 'Why did the count change?', answer: 'Filters or source records may have changed. Update Preview and read the snapshot and final export result instead of relying on an older preview.' },
      { question: 'Where are complete Shopify sales, fees and payouts?', answer: 'Use Accounting and leave its Shopify inclusion option selected. Read Source coverage in the combined workbook.', }
    ],
    history: [{ title: 'Keep export context', body: 'The preview shows its Snapshot; downloaded workbooks include their report and filter context. There is no saved-export library on this page, so retain the file you downloaded.' }],
    related: ['accounting', 'analytics', 'orders', 'invoicing', 'expenses']
  },

  analytics: {
    title: 'Explore sales performance',
    summary: 'Compare recorded sales by time, territory, rep, product and customer type using a consistent period and filter set.',
    before: ['Choose whether the question is about Dollars Sold or Cases Sold.', 'Check that complete sales analytics have loaded. These sales views are different from cash receipts, payouts and accounting reconciliation.'],
    steps: [
      { title: 'Set the measure and period', body: 'Choose Dollars Sold or Cases Sold, then 1D, 7D, 4W, 12W or YTD. Keep the same period and measure when comparing results.' },
      { title: 'Apply the relevant scope', body: 'Filter Territory, Rep, Customer Type and Flavor. Use Clear filters to return to the wider view.', check: 'The selected scope matches the people, products and accounts you intend to compare.' },
      { title: 'Read the appropriate tab', body: 'Use Overview for the summary, Timing for day/hour patterns, Territory for location comparisons, Sales Reps for people, Products for products, and Customer Type for account groups.' },
      { title: 'Follow up with source evidence', body: 'Use Reports & Exports for underlying report data and configure its filters explicitly. Use Accounting when reconciling invoice income, Shopify activity or cash settlements.', check: 'You can explain the result’s period, filters and source before sharing it.', link: { page: 'reports', label: 'Open Reports & Exports' } }
    ],
    warnings: ['A dash, loading notice or unavailable message is not zero activity. Use Try Again when the complete source cannot load.', 'Sales figures are not proof that invoices were paid or that a Shopify payout reached the bank.'],
    faqs: [
      { question: 'Why are the charts not shown?', answer: 'The page waits for complete analytics. Read the source-status message and retry rather than treating a limited operational cache as the full sales result.' },
      { question: 'Why does another report show a different number?', answer: 'Compare dates, filters, units and date basis. Orders, issued invoices, payments and payouts describe different events and can fall in different periods.' },
      { question: 'Does opening Reports carry every analytics filter?', answer: 'Review and set the report’s own filters. The Reports & Exports shortcut opens a reporting workbook; do not assume your entire chart configuration became its export scope.' }
    ],
    history: [{ title: 'Trace the period', body: 'Analytics is a read-only view, not an action log. Keep the selected period and filters with any shared result, and use Reports or Accounting for the underlying evidence.' }],
    related: ['reports', 'accounting', 'goals', 'orders', 'customers']
  },

  admin: {
    title: 'Manage access and configuration',
    summary: 'Use the appropriate Admin section to maintain team access, locations and business settings, then verify the saved change.',
    before: ['Admin is available to Operations, Leadership and Administrator roles, with additional restrictions on individual actions.', 'Know the exact person or record to change. Permission to view Admin does not automatically permit sending account setup links.'],
    steps: [
      { title: 'Choose the correct section', body: 'Use Users, Audit Log, Locations, Territories, Products, Price Lists, Approval Rules, Public Retailers, Settings, Integrations or Permissions. Open the existing record before choosing an Add action.' },
      { title: 'Review a user’s actual access', body: 'In Users, open the person’s live details. Check email, role, status, territory and linked account. Save only the intended changes. Review the recorded authentication/setup status separately from the business role.', check: 'Reload or reopen the user and confirm the saved role and assignments.' },
      { title: 'Send account access only when ready', body: 'Invite User opens the setup controls. Check the address and role before Send Setup Link or any password action. Live setup-link permission is restricted to the designated administrator; follow the explanation shown if the action is unavailable.', check: 'Use the returned result and the person’s setup status. Opening an invite form does not send access.' },
      { title: 'Retire an unused location safely', body: 'In Locations, choose Remove safely. Review its references and blockers, and choose a replacement where required. Follow the preview and confirmation rather than deleting history or inventing a physical transfer.', check: 'Confirm the result and review Location removal history. Unresolved stock or references may prevent removal.' },
      { title: 'Verify the recorded change', body: 'Open Audit Log, select the appropriate category and Refresh. Open an event to inspect actor, record reference, Before and After. Use Integrations for the settings or status actually shown; a configuration entry alone does not prove complete imported data.', link: { page: 'email-log', label: 'Check recorded email delivery' } }
    ],
    warnings: ['Role changes, setup links, prices and approval settings affect other people’s workflows. Check the exact record and intended result before confirming.', 'The Audit Log view is a recent bounded list, not an unlimited searchable archive.', 'Location retirement is a configuration/history action. Use the inventory workflow for stock that actually moves.'],
    faqs: [
      { question: 'Why can I see Users but not invite someone?', answer: 'Live invites have an extra administrator restriction. The page identifies when your account may view records but cannot send setup links.' },
      { question: 'Why is safe removal blocked?', answer: 'The preview found stock, active work or references that cannot safely be reassigned as requested. Resolve the listed cause and review a new preview.' },
      { question: 'Why is an expected audit event not visible?', answer: 'Choose All or the relevant category and Refresh. The view loads recent recorded events only; provide the record ID and time to Operations for a deeper investigation.' }
    ],
    history: [
      { title: 'Audit Log', body: 'Open a recorded event to see its actor, time, entity and saved Before/After values. Copy Event copies that displayed evidence.' },
      { title: 'Location removal history', body: 'The Locations section includes the removal history so retained references can be explained after a location is retired.' }
    ],
    related: ['account', 'tasks', 'email-log', 'inventory', 'customers']
  },

  account: {
    title: 'Manage your account',
    summary: 'Check your identity and access, maintain the contact details shown on retailer quotes and manage your sign-in.',
    before: ['Open My Account from your name in the operations menu.', 'Confirm the displayed email is your own account before changing a password or requesting a reset link.'],
    steps: [
      { title: 'Check your profile', body: 'Review the email, role and territory shown in your profile. These explain much of the access you have. Ask an administrator to correct role or assignment problems; the display-name field does not change your permissions.' },
      { title: 'Update your quote contact details', body: 'In Public Quote Profile, enter Display name, Phone shown to retailers and Local location or address. Leave an optional contact detail blank to omit it, then Save Profile.', check: 'Wait for the saved confirmation and verify the fields remain correct.' },
      { title: 'Set or reset your password', body: 'In Sign-in & Security, enter matching New password and Confirm password values, following the displayed minimum, then Set Password. Email Reset Link sends a reset message to the account email.', check: 'The page confirms Password updated or Reset email sent. Use the reset message to complete that separate flow.' },
      { title: 'Sign out when finished', body: 'Choose Sign out on a shared device. Sign back in with your own account when returning.', check: 'Your signed-in workspace is no longer available without signing in again.' }
    ],
    warnings: ['Public Quote Profile details are shown on retailer sell sheets and direct quote emails. Enter information intended for customers.', 'Demo profile and sign-in controls do not update a live account.'],
    faqs: [
      { question: 'Can I change my role here?', answer: 'No. My Account shows your access information. Ask an administrator to review your role and territory in Admin.' },
      { question: 'Why won’t my password save?', answer: 'Check that both values match and meet the displayed requirements. Read any returned error; do not assume it saved without the confirmation.' },
      { question: 'Why is a quote missing my phone or location?', answer: 'Review Public Quote Profile and Save Profile. Blank optional fields are intentionally omitted from quote contact details.' }
    ],
    history: [{ title: 'Confirm the current settings', body: 'This page shows your current profile and result messages, not a full security timeline. An administrator can investigate recorded user/security events when needed.', link: { page: 'admin', label: 'Open Admin' } }],
    related: ['admin', 'tasks']
  }
};


/* ===== help_field.js ===== */
/* Field/Sales help is static guidance. Related IDs open guides, not workspaces
   or business actions. The help UI must filter every guide through ACCESS. */
const RP_FIELD_HELP_GUIDES = {
  'field-home': {
    title: 'Find your next piece of work',
    summary: 'Your home page brings together work assigned to your account. Open a record before deciding what needs to happen next.',
    before: ['Check that you are signed in as yourself and that the location or territory shown is yours.'],
    steps: [
      { title: 'Start with the current queue', body: 'Sales uses Home or Overview, warehouse uses Tasks and Pick, and drivers use Route. Open the relevant item to see its current status.' },
      { title: 'Use the page for the job', body: 'Use the bottom tabs, More, or the desktop Sales menu to open the tools available to your role. Help explains the current page without submitting anything.' },
      { title: 'Check the saved result', body: 'After an action, wait for its confirmation and retain the reference. If the result is unclear, check the existing record before starting again.' }
    ],
    warnings: ['A notification or summary count is a starting point. Read the underlying record before changing stock, sending an email or marking work complete.'],
    faqs: [{ question: 'Why is a tool or record missing?', answer: 'Your role, assigned area and storage locations control what you can use. Ask the team to check your assignment; do not switch to another person\'s account.' }],
    history: [{ title: 'Find prior work', body: 'Open Orders, Stock Requests, My Tasks or My Expenses as appropriate. Use the available search and older-record controls rather than relying only on the home-page summary.' }],
    related: ['field-orders', 'field-inventory', 'field-picking', 'field-delivery', 'field-tasks', 'field-support']
  },
  'field-orders': {
    title: 'Track orders and invoices',
    summary: 'Use Orders to follow the customer order, fulfillment and invoice. These are separate statuses.',
    before: ['Have the order number or customer name. Confirm that you are opening the correct order, especially after a retry.'],
    steps: [
      { title: 'Find the order', body: 'Open Orders, search or filter the list, and open the order. Use the older-record control when needed; a recent queue is not the complete history.' },
      { title: 'Read the status and next action', body: 'Check fulfillment, invoice and payment details separately. An issued or emailed invoice does not mean stock was delivered or payment was received.' },
      { title: 'Handle the specific issue', body: 'Use the available order or invoice controls for the intended action. Check the recipient and saved invoice details before any email, and inspect Email delivery when an email needs attention.', check: 'Reopen the same order and confirm the intended change is saved.' }
    ],
    warnings: ['An issued invoice can lock items and pricing. Ask for the correct invoice correction process instead of creating a duplicate order.', 'Sending or resending an invoice contacts the customer. Opening its delivery history does not.'],
    faqs: [{ question: 'Does Sent mean the customer received the email?', answer: 'It means the send was accepted. Delivery, bounce and other provider events appear separately. An open event is useful evidence, but missing open tracking does not prove the email was unread.' }],
    history: [{ title: 'Order and invoice evidence', body: 'Open the order for its timeline and linked invoice. The customer\'s Orders tab also shows order history. Email delivery shows the recorded send and recipient events for that document.' }],
    related: ['field-order-entry', 'field-exceptions', 'field-customers', 'field-support']
  },
  'field-order-entry': {
    title: 'Create or edit an order',
    summary: 'Work through Customer & Source, Add Products, Discounts & Notes, then Review.',
    before: ['Confirm the customer, actual fulfillment source, delivery method and requested date. Have any required PO number ready.'],
    steps: [
      { title: 'Choose the customer and source', body: 'In Customer & Source, use the existing account and the location that will actually supply the stock. Check delivery details and any account approval messages.' },
      { title: 'Enter the products and terms', body: 'Add the correct products and quantities. Review discounts, notes and any approval requirements. For event singles, use the event-linked flow and its available open cans.' },
      { title: 'Review before submitting', body: 'Review products, container charges, taxes, payment charges if applicable, delivery and the Customer Invoice choices. Submit Order creates the order; Save Changes edits an eligible order. Record Sale and Submit Advance Order apply only to their displayed modes.', check: 'Use View Order on the result and keep its reference.' }
    ],
    warnings: ['Invoice choices on the final review can issue or email a real invoice. Read them before submitting.', 'Do not select another source or inflate its stock to bypass a shortage. Resolve the actual stock or choose an offered advance-order workflow.'],
    faqs: [{ question: 'What if the save times out?', answer: 'Check Orders for the result and use any offered original-request recovery. Do not repeatedly create a new order for the same customer handoff.' }],
    history: [{ title: 'Review the saved order', body: 'Orders and the customer\'s Orders tab hold the saved record. Reordering starts another order; it is not a correction to the original.' }],
    related: ['field-orders', 'field-customers', 'field-inventory', 'field-quotes']
  },
  'field-exceptions': {
    title: 'Resolve an order exception',
    summary: 'Exceptions gathers orders needing attention, such as approvals, holds, backorders and unpaid or overdue work.',
    before: ['Identify whether the problem is with the order, inventory, delivery or invoice.'],
    steps: [
      { title: 'Filter the queue', body: 'Select the relevant exception and search for the customer or order. Load more results if needed.' },
      { title: 'Open the original record', body: 'Read the reason and next action, then open the order. Confirm current information before contacting the customer or requesting another team\'s help.' },
      { title: 'Resolve and recheck', body: 'Use the authorized action on the underlying record. Return to the queue and check whether the exception is resolved.' }
    ],
    warnings: ['An exception is not permission to override approval, payment or inventory controls.'],
    faqs: [{ question: 'Why is an order still listed?', answer: 'Several issues can apply to one order. Resolving a delivery problem does not automatically clear an unpaid invoice or another hold.' }],
    history: [{ title: 'Keep the original reference', body: 'Use the order timeline and invoice details for the evidence. Include the order number when escalating through Support.' }],
    related: ['field-orders', 'field-tasks', 'field-support']
  },
  'field-customers': {
    title: 'Find and maintain a customer',
    summary: 'Keep one accurate customer account with the right contacts, delivery details and supply arrangement.',
    before: ['Search the existing directory before adding a customer. A location supplied by a distributor may already be listed.'],
    steps: [
      { title: 'Open the correct account', body: 'Use Customers and its business, consumer and follow-up filters. Check the name, address and contact before ordering, logging a visit or editing.' },
      { title: 'Add or update the facts', body: 'In the add or edit form, confirm contact details, location, supply channel and delivery information. Set Receives invoice and receipt copies only for additional contacts who should receive those copies.' },
      { title: 'Follow the approval state', body: 'Save the account and follow any approval requirement before ordering. A prospect or a distributor-supplied directory entry is not automatically ready for a direct order.', check: 'Reopen the saved customer and verify the changed details.' }
    ],
    warnings: ['Creating a customer does not grant portal access or place an order by itself. Read any separate invitation or approval action before using it.', 'Invoice and receipt copy preferences do not replace the primary invoice recipient. Check that recipient on the invoice too.'],
    faqs: [{ question: 'Can I edit every customer?', answer: 'Editing depends on your account scope and permissions. If the required control is unavailable, send the correct customer reference and requested change to the team.' }],
    history: [{ title: 'Customer activity', body: 'The customer page includes orders and recorded activity. Use its Orders tab and older-record controls for the complete available order history.' }],
    related: ['field-orders', 'field-pipeline', 'field-visits', 'field-quotes']
  },
  'field-pipeline': {
    title: 'Keep a lead moving',
    summary: 'Pipeline tracks prospects, contacts, stages and the next follow-up before they become customer accounts.',
    before: ['Check for an existing lead or customer and confirm the buyer\'s contact details.'],
    steps: [
      { title: 'Find or add the lead', body: 'Open Pipeline and the matching lead. Use Add Lead only if the record does not already exist.' },
      { title: 'Record a real interaction', body: 'Use Log touch for the contact method, outcome and next follow-up. Review whether Save & Advance is selected: it also changes the lead stage.' },
      { title: 'Convert when appropriate', body: 'In Convert to Customer, choose Link Customer for an existing account or Create Prospect for a new one. Review the details before confirming.' }
    ],
    warnings: ['Use Mark as lost for a real lead that is no longer interested, so the history remains. Permanent deletion is for eligible duplicates or test leads.', 'A saved touch is not an email send. Use an explicit email action only when you intend to contact the lead.'],
    faqs: [{ question: 'Does conversion approve the customer for orders?', answer: 'No. Creating a prospect and account approval are separate steps. Open the customer and follow its current approval state.' }],
    history: [{ title: 'Lead activity', body: 'Open the lead to review its recorded touches and follow-up information. Keep that history when linking it to the customer.' }],
    related: ['field-customers', 'field-quotes', 'field-visits', 'field-tasks']
  },
  'field-visits': {
    title: 'Log a store visit',
    summary: 'Record what happened at the store so the next visit or follow-up starts with useful facts.',
    before: ['Open the correct customer and have the visit date, observations and any permitted photos ready.'],
    steps: [
      { title: 'Confirm the store', body: 'Open the store\'s visit form. Check the customer name before entering shelf observations or notes.' },
      { title: 'Record the outcome', body: 'Fill the available visit fields, useful notes and follow-up details. Attach relevant evidence where offered.' },
      { title: 'Save the visit', body: 'Select Save Visit and wait for confirmation. Create an order separately if the customer wants stock.' }
    ],
    warnings: ['A visit or shelf observation does not change warehouse inventory or submit a customer order.'],
    faqs: [{ question: 'Where can I find an earlier visit?', answer: 'Use Visit Log in the desktop Sales menu, or reopen the customer\'s recorded activity. Include the customer and date if you need help locating it.' }],
    history: [{ title: 'Visit evidence', body: 'Saved visits retain the customer, date and recorded notes. Check that record before submitting the same visit again.' }],
    related: ['field-customers', 'field-orders', 'field-tasks']
  },
  'field-inventory': {
    title: 'Understand My Inventory',
    summary: 'My Inventory shows stock in your associated storage, including the separate fronted, purchased and sample quantities.',
    before: ['Check the selected Inventory location if you have more than one. Count physical cases separately from open loose cans.'],
    steps: [
      { title: 'Check the location and stock type', body: 'Read each product\'s fronted, purchased and sample labels. Fronted stock remains company-owned; purchased stock and samples have different custody and use rules.' },
      { title: 'Choose the matching action', body: 'Request Stock asks for more stock. Received acknowledges a completed transfer. Giveaway records stock handed out free. Build Mix Packs records an eligible physical repack.' },
      { title: 'Check what was already recorded', body: 'Open Stock Requests using its history control and read the status before arranging another handoff. If quantities disagree, gather the lot, location and movement references for the team.' }
    ],
    warnings: ['Do not add another receipt because stock is missing from the screen. First check the location and whether the original movement was recorded.', 'A corrected printed lot label refers to the same existing stock; the original reference is retained in its history. It is not a second lot to receive.'],
    faqs: [{ question: 'Why can I not select a warehouse?', answer: 'Personal inventory is restricted to associated storage. Ask the team to correct your location assignment rather than selecting an unrelated company warehouse.' }],
    history: [{ title: 'Request and movement references', body: 'Stock Request History shows your requests. Keep transfer and receipt references for Operations to match against the location\'s inventory history; My Inventory is a balance view.' }],
    related: ['field-requests', 'field-receiving', 'field-repack', 'field-giveaways', 'field-count', 'field-returns']
  },
  'field-requests': {
    title: 'Request stock and track fulfillment',
    summary: 'A stock request asks Operations to review and fulfill a need. Submitting it does not move cases.',
    before: ['Check your existing Stock Requests and current storage balance. Confirm the source, destination and intended ownership.'],
    steps: [
      { title: 'Enter the request', body: 'Open Request Stock. Confirm the destination in the heading, choose Source and Ownership Type, then enter whole cases by product and useful Notes.' },
      { title: 'Submit once', body: 'Select Submit Request and retain the SR reference. The request starts Pending; wait for review and fulfillment rather than recording the same stock through another flow.' },
      { title: 'Follow the saved request', body: 'Use View Requests or Stock Requests, open the request and read its status, items, source, destination and ownership.', check: 'Match the physical handoff with the team\'s fulfillment record.' }
    ],
    warnings: ['Fronted to Rep, Purchased by Rep and Sample / Demo are different arrangements. Do not choose one just to bypass a limit.', 'An SR reference is not a completed TO transfer reference. A fulfilled request already posts its stock movement; do not create another transfer or receipt for those same cases.'],
    faqs: [{ question: 'How do I acknowledge a fulfilled SR in Received?', answer: 'The Received screen expects a completed transfer reference, not an SR. Ask Operations to reconcile the existing fulfillment if you need receiving evidence; do not manufacture a second movement.' }],
    history: [{ title: 'Stock Requests', body: 'Open the saved SR for its progress, items, ownership, updated date and notes. New Request creates a separate request.' }],
    related: ['field-inventory', 'field-receiving', 'field-support']
  },
  'field-receiving': {
    title: 'Confirm cases received into your storage',
    summary: 'Received confirms an existing completed transfer. The transfer already moved the stock; this acknowledgement adds no stock.',
    before: ['Have the completed TO reference, actual destination, product and lot counts, and handoff evidence. Confirm all transferred cases physically arrived.'],
    steps: [
      { title: 'Find the original transfer', body: 'From My Inventory choose Received. Enter Completed Transfer Reference and check Received from, Receiving into and Received date. A listed transfer can fill its source and lines.' },
      { title: 'Match every product and lot', body: 'Compare the actual cases with the full signed transfer. Keep separate lots on separate lines; Add another lot of this SKU is available when needed. Add notes and receiving photos where useful.' },
      { title: 'Acknowledge the same handoff', body: 'Select Acknowledge Transfer only when all SKU, lot and case quantities match. Retain the receipt and linked TO reference.', check: 'The result says Transfer Acknowledged and explains that inventory was already posted.' }
    ],
    warnings: ['If the reference is missing, quantities are short or a lot differs, stop and ask Operations to reconcile the transfer. Do not make the numbers match by inventing a receipt.', 'The form may show damaged-case inputs, but this acknowledgement rejects a damage split. If all cases arrived, acknowledge the complete quantities, then use Damage / Loss with photos for the damaged cases at the destination.'],
    faqs: [{ question: 'What if I only have an SR number?', answer: 'That is a stock request, not a completed TO transfer. Check with Operations before proceeding; the fulfillment may already have moved these cases.' }],
    history: [{ title: 'Keep both references', body: 'The acknowledgement stays linked to the completed transfer. Operations can review the receipt and movement evidence using those references.' }],
    related: ['field-inventory', 'field-requests', 'field-damage', 'field-returns']
  },
  'field-warehouse-receiving': {
    title: 'Receive stock at a warehouse',
    summary: 'First decide whether the custody handoff already has a completed transfer. The two receiving modes have different effects.',
    before: ['Have the actual source and destination, signed transfer if one exists, exact lots, case counts and transport evidence.'],
    steps: [
      { title: 'Prefer the existing transfer', body: 'In Receive, select the destination and its Completed Inbound Transfer. Compare the signed SKU and lot lines to the actual arrival.' },
      { title: 'Acknowledge a recorded handoff', body: 'If every line matches, enter the received date and notes, then choose Acknowledge Cases. This confirms the transfer without adding inventory again.' },
      { title: 'Record a missing handoff only once', body: 'Use the explicit unplanned mode only after confirming no transfer or stock-request fulfillment already recorded this movement. Supply the source, exact lot, cases, actual handoff time, transport details and CHEP use before Record Case Handoff.' }
    ],
    warnings: ['The unplanned mode creates a completed source-to-destination transfer and changes both balances. It is not an opening balance or a way around missing source stock.', 'Shortages or wrong lots need reconciliation. Record destination damage separately with evidence after the full physical arrival has been confirmed.'],
    faqs: [{ question: 'Why is an expected transfer missing?', answer: 'Check the destination and whether someone already acknowledged it. Ask the team for the original reference before switching to unplanned mode.' }],
    history: [{ title: 'Receiving evidence', body: 'Keep the TO and acknowledgement references. Operations can trace them through the destination\'s history and Field Receipts.' }],
    related: ['field-transfer', 'field-damage', 'field-count', 'field-support']
  },
  'field-transfer': {
    title: 'Record a completed warehouse transfer',
    summary: 'Transfer Stock records a physical custody handoff and immediately moves cases between the selected locations.',
    before: ['Confirm the handoff happened and was not already recorded by a transfer, receipt or fulfilled stock request. Have exact source lots and CHEP counts.'],
    steps: [
      { title: 'Check both locations', body: 'Choose From and To. They must differ. Check the names carefully; the form can start with default locations.' },
      { title: 'Enter actual source cases', body: 'Choose quantities from the displayed product and lot holdings. Record CHEP pallets moved and Notes. Set zero pallets only if no CHEP pallets were used.' },
      { title: 'Record the completed movement', body: 'Choose Record Completed Transfer and keep its TO reference. Give that reference to the receiver for acknowledgement.', check: 'Confirm Transfer Recorded and check the resulting source and destination balances.' }
    ],
    warnings: ['This mobile form records an internal transfer at the current time. Ask Operations to record movements needing a historical time or different transport documentation.', 'Do not use a generic warehouse transfer to change rep ownership, settle purchased stock or duplicate an SR fulfillment.'],
    faqs: [{ question: 'Can I record a planned shipment?', answer: 'This button records a completed handoff. Coordinate a future movement first and post only the physical event that actually occurred.' }],
    history: [{ title: 'Transfer reference', body: 'The saved TO links the movement evidence and its later acknowledgement. Provide that reference when asking Operations to inspect location history.' }],
    related: ['field-warehouse-receiving', 'field-returns', 'field-count', 'field-support']
  },
  'field-count': {
    title: 'Count the cases physically on hand',
    summary: 'A cycle count records the actual case count by product and lot. Submission can adjust stock; it is not just a checklist.',
    before: ['Confirm the location in the heading. Pause or coordinate movements while counting so the count represents one point in time.'],
    steps: [
      { title: 'Count each listed lot', body: 'Physically count the cases for every product and exact lot. Keep separate lots separate. System quantities stay hidden during entry.' },
      { title: 'Enter every result', body: 'Fill Physical cases for each line. Enter zero only when you checked that none remain; a blank line is still uncounted.' },
      { title: 'Review and submit', body: 'Recount any uncertainty before Submit Count. Read Count Submitted and retain the CC reference.', check: 'The result states whether variances were posted to the ledger.' }
    ],
    warnings: ['Do not use a count to hide an unrecorded transfer, a known sale or a receipt already in progress. Reconcile those records first.', 'Unexpected stock or mixed ownership that the form cannot distinguish needs review. Do not assign an invented lot or combine fronted, purchased and sample stock to force submission.'],
    faqs: [{ question: 'Can I count loose cans here?', answer: 'This page counts cases. Event loose cans have their own records; ask the team to reconcile them through the event/open-can workflow.' }],
    history: [{ title: 'Count evidence', body: 'Keep the CC reference, location and count date. Operations can inspect the count and any resulting ledger variance.' }],
    related: ['field-inventory', 'field-damage', 'field-returns', 'field-support']
  },
  'field-giveaways': {
    title: 'Log cases given away',
    summary: 'Log Giveaway records a real free handout and removes cases from the selected source stock.',
    before: ['Know the source location, products, case quantities, recipient and business reason.'],
    steps: [
      { title: 'Choose the source and items', body: 'From My Inventory choose Giveaway. Check Source Location and enter the products and whole-case quantities actually handed out.' },
      { title: 'Explain the handout', body: 'Choose Recipient Type and the customer, prospect or recipient name. Fill Reason and any requested Expected Outcome, Follow-up Date and Notes.' },
      { title: 'Post the giveaway', body: 'Select Log Giveaway and wait for Giveaway Logged. Keep the saved reference.', check: 'Check the selected location\'s remaining stock.' }
    ],
    warnings: ['A giveaway is not a transfer to another rep, a return, a paid sale or a damage report.', 'Do not record the same free stock again as an order or inventory adjustment.'],
    faqs: [{ question: 'What about samples handed out as individual cans?', answer: 'This form records whole cases. Use the event/open-can process for loose cans and ask the team if the actual activity is not represented.' }],
    history: [{ title: 'Giveaway record', body: 'The success reference identifies the ledger event and its recipient/reason evidence. Keep it with any follow-up task.' }],
    related: ['field-inventory', 'field-events', 'field-damage', 'field-visits']
  },
  'field-damage': {
    title: 'Report damaged or missing cases',
    summary: 'Damage / Loss moves affected cases out of sellable stock and records the incident evidence.',
    before: ['Identify the correct location, exact on-hand lot, affected case count and incident. Have at least one photo ready.'],
    steps: [
      { title: 'Identify the affected stock', body: 'Choose Incident Type, Location, Product and Lot. Enter only the cases affected by this incident.' },
      { title: 'Attach the evidence', body: 'Select the Reason Code, add clear Notes, and attach a damage photo. Describe missing stock honestly rather than implying it was physically damaged.' },
      { title: 'Submit and review', body: 'Choose Submit Report. Read the resulting status and any approval message and keep the DMG reference.', check: 'The reported cases should no longer be counted as sellable stock.' }
    ],
    warnings: ['Do not report the same loss twice through both a damage report and a cycle-count reduction.', 'A damaged arrival does not justify adding extra stock. Reconcile the transfer first and report damage against its destination lot.'],
    faqs: [{ question: 'What if the lot is missing from the choices?', answer: 'Stop and provide the location, physical label and original movement reference to the team. Do not select a different lot just to submit.' }],
    history: [{ title: 'Incident evidence', body: 'The saved report reference ties the inventory change to the reason and photos. Operations can inspect its status and any required review.' }],
    related: ['field-receiving', 'field-warehouse-receiving', 'field-count', 'field-support']
  },
  'field-returns': {
    title: 'Return stock or hand it to another rep',
    summary: 'A return or rep-to-rep handoff needs the correct custody record. Changing who physically holds stock is different from changing who owns it.',
    before: ['List the exact products, lots, cases, current holder, receiving location and whether the stock is fronted, purchased or samples.'],
    steps: [
      { title: 'Check the existing record', body: 'Find the original transfer, request fulfillment or purchase evidence. Confirm whether the return or new handoff has already been recorded.' },
      { title: 'Coordinate the correct movement', body: 'Give Operations the source, recipient, lot counts, condition and ownership. The Sales screens do not offer a general return or peer-transfer approval button.' },
      { title: 'Match the confirmation to the physical event', body: 'Retain the movement reference and check the source and recipient balances. Use Received only when the team provides an eligible completed TO transfer and all its lines actually match.' }
    ],
    warnings: ['Do not use Giveaway, Damage / Loss or Cycle Count to make a return or rep-to-rep handoff disappear from one balance.', 'Returning rep-purchased stock does not itself prove a refund or ownership settlement. Ask the team to handle those records explicitly.'],
    faqs: [{ question: 'Can I create a receipt at the receiving rep instead?', answer: 'No. A receipt acknowledges its original completed transfer. Creating another movement can duplicate stock or change custody incorrectly.' }],
    history: [{ title: 'Keep the chain of references', body: 'Keep the original movement or purchase reference and the return/new handoff reference together, including the names of both people and the date.' }],
    related: ['field-inventory', 'field-requests', 'field-receiving', 'field-transfer', 'field-support']
  },
  'field-repack': {
    title: 'Build mix packs from existing cases',
    summary: 'Build Mix Packs consumes eligible source cases and records the mixed cases physically made at the same location.',
    before: ['Use stock in your associated storage. Know the exact source lots, recipe, date and the new physical mixed-pack label.'],
    steps: [
      { title: 'Choose the location and mix', body: 'Open Build Mix Packs from My Inventory or Sales Overview where offered. Select the correct storage, target mix and eligible custody.' },
      { title: 'Match the recipe to physical stock', body: 'Choose each required source lot and the number of complete builds. Read the displayed source consumption and mixed-case output before saving.' },
      { title: 'Record the finished work', body: 'Use the new target lot from the physical label and record only completed repacking. Keep the result reference and check the source and mixed-case balances.' }
    ],
    warnings: ['Purchased and sample stock are excluded from this workflow. Source lots must be released, unexpired and eligible at this location.', 'Do not repack digitally just to make a different product available for an order. The cases must actually have been rebuilt.'],
    faqs: [{ question: 'Why are source lots unavailable?', answer: 'Check the location, custody, recipe, quality release and expiry. Ask the team to resolve missing evidence rather than substituting an unrelated lot.' }],
    history: [{ title: 'Repack evidence', body: 'The saved repack reference links source-case consumption to mixed-case output. Operations can trace it in inventory history.' }],
    related: ['field-inventory', 'field-count', 'field-events', 'field-support']
  },
  'field-events': {
    title: 'Prepare and track event stock',
    summary: 'An event keeps its allocated cases, open loose cans and singles sales together.',
    before: ['Find the existing event before creating another. Confirm its dates, location, status and the people handling stock.'],
    steps: [
      { title: 'Open or create the event', body: 'Use Events and open the correct record. Use New Event only when needed, enter its details and choose Create.' },
      { title: 'Allocate stock before using it', body: 'Use Add Cases to select the actual source and exact lots. This records event stock; do not also record another transfer for the same allocation.' },
      { title: 'Record the actual event activity', body: 'Use Open Cases when cases are physically opened, then Sell Singles for the event-linked sale. Review Remaining Cases and Open Loose Cans as work proceeds.' }
    ],
    warnings: ['A closed or otherwise locked event cannot receive more cases. Do not create a second event to bypass its state.', 'Unsold cases, leftover loose cans and losses need reconciliation. Ask the team to complete closeout; do not treat a balance disappearing from a filter as a return.'],
    faqs: [{ question: 'What if an allocation times out?', answer: 'Use Recover Exact Allocation when offered. It checks the saved original request instead of creating another stock allocation.' }],
    history: [{ title: 'Event record', body: 'Open the event for its status, closeout information, case balances and open-can batches. Keep allocation and opening references with the event.' }],
    related: ['field-event-opening', 'field-order-entry', 'field-giveaways', 'field-returns']
  },
  'field-event-opening': {
    title: 'Open event cases for singles',
    summary: 'Open Cases converts allocated event cases into tracked loose cans. It does not create additional stock.',
    before: ['Add the actual cases to the event first. Confirm the source lot, number of cases physically opened and any damaged cans.'],
    steps: [
      { title: 'Choose the event stock', body: 'From the event choose Open Cases. Confirm Event Stock Source and Rep Holding Open Cans, then select eligible case quantities.' },
      { title: 'Check the can totals', body: 'Enter any damaged cans and review the saleable-can total. Damaged cans cannot exceed the cans opened. Mixer packs are not openable in this workflow.' },
      { title: 'Confirm the opening', body: 'Submit only after the cases have physically been opened. Return to the event and check Open Loose Cans before Sell Singles.' }
    ],
    warnings: ['Do not record the same opening as a giveaway or another case adjustment.', 'If an original request is pending, use Recover Exact Request and preserve its details instead of making a new opening.'],
    faqs: [{ question: 'How are leftovers handled?', answer: 'Keep them with their event and open-can batch. Ask the team to reconcile or close out the actual remainder rather than counting loose cans as sealed cases.' }],
    history: [{ title: 'Open-can evidence', body: 'The event shows its open loose cans and batches. Keep the saved opening reference for the source-lot and can reconciliation.' }],
    related: ['field-events', 'field-order-entry', 'field-damage']
  },
  'field-picking': {
    title: 'Pick an assigned warehouse order',
    summary: 'Pick records preparation of an existing order. It does not mean the customer has received it.',
    before: ['Confirm the correct order, customer, actual warehouse and required products. Use the real pick documentation for location and lot checks.'],
    steps: [
      { title: 'Open the pick', body: 'Open Pick and select the order. Check each required product and case quantity against the actual stock.' },
      { title: 'Record what you physically picked', body: 'Enter the picked quantities and CHEP pallets out. Report damaged stock through Damage / Loss if needed.' },
      { title: 'Complete only the actual pick', body: 'Choose Complete Pick when all required products are prepared. Resolve shortages with the team before completing.', check: 'Confirm the saved order status before releasing the prepared load.' }
    ],
    warnings: ['The screen\'s aisle and lot hints are not proof of an exact physical allocation. Verify the actual warehouse instructions and labels.', 'Do not increase a picked count just to enable Complete Pick.'],
    faqs: [{ question: 'Is a picked order delivered?', answer: 'No. Load checking, dispatch and proof of delivery are separate work.' }],
    history: [{ title: 'Order progress', body: 'Keep the order number and check its saved progress. Delivery evidence belongs to the later delivery record.' }],
    related: ['field-warehouse-receiving', 'field-damage', 'field-delivery', 'field-support']
  },
  'field-delivery': {
    title: 'Load and deliver an assigned route',
    summary: 'Use the assigned route, verify the actual load, then record the receiving confirmation for each delivered order.',
    before: ['Confirm your route and stop list. Have the actual load and receiving person available for the appropriate step.'],
    steps: [
      { title: 'Check the route and load', body: 'Open Route or Route Map. In Vehicle Load Check, compare the loaded cases with the fetched route manifest, enter the physical counts and notes, then Submit.' },
      { title: 'Open the actual delivery stop', body: 'Open the ready stop and verify its customer, address, products and order reference before handing over stock.' },
      { title: 'Record receipt by the customer', body: 'Enter Receiver Name and obtain the displayed receiving confirmation. Add the delivery photo where offered, then choose Mark Delivered only after the handoff is complete.', check: 'Wait for Delivered and confirm the order number on the result.' }
    ],
    warnings: ['Do not mark a failed, partial or refused delivery complete just to clear the route. Contact the team with the order and actual outcome.', 'A map position or load check alone is not proof that the customer received an order.'],
    faqs: [{ question: 'What if the manifest will not load?', answer: 'Use Retry manifest and resolve the error before submitting a load check. Do not substitute counts from another route.' }],
    history: [{ title: 'Delivery evidence', body: 'Keep the route and order references. The order\'s saved progress and proof-of-delivery evidence document the actual handoff.' }],
    related: ['field-damage', 'field-returns', 'field-tasks', 'field-support']
  },
  'field-tasks': {
    title: 'Work through assigned tasks',
    summary: 'My Tasks shows assigned work and its current status. Open the task to understand the requested outcome.',
    before: ['Read the task, linked customer or record, due date and any instructions.'],
    steps: [
      { title: 'Find the task', body: 'Use My Tasks and its filters, then open the task. Check its current status before starting.' },
      { title: 'Keep the status truthful', body: 'Use the available status controls as work progresses. Mark Done only when the requested outcome is complete; Reopen Task returns completed work to In Progress.' },
      { title: 'Handle special actions deliberately', body: 'A portal invitation task may show Send Setup Link. That sends an actual email; check the intended account before using it.' }
    ],
    warnings: ['Marking a general task Done does not itself receive stock, pay an invoice or fix the linked business record. Perform and verify that work separately.'],
    faqs: [{ question: 'What if someone changed the task while I was working?', answer: 'Refresh and read its latest state before trying again. Do not overwrite a newer update without understanding it.' }],
    history: [{ title: 'Completed work', body: 'Use the Done filter and open the original task. Include the task ID in any follow-up or support request.' }],
    related: ['field-orders', 'field-support', 'field-notifications']
  },
  'field-expenses': {
    title: 'Submit and track an expense',
    summary: 'Submit the actual cost with evidence, then follow accounting review in My Expenses.',
    before: ['Have the receipt, merchant, date, tax-inclusive total and who paid the expense.'],
    steps: [
      { title: 'Enter the expense', body: 'Choose Category and enter Amount (tax incl.), Merchant and Date. Link the relevant account when useful and explain the business purpose in Note.' },
      { title: 'Attach the receipt and payer', body: 'Use Snap / attach receipt. Leave Reimburse to me on only for money you paid personally; off means paid on the company card.' },
      { title: 'Submit and follow review', body: 'Choose Submit and keep the expense reference. My Expenses shows Submitted, Needs Info, Approved and Reimbursed states as the work progresses.' }
    ],
    warnings: ['Approved does not mean already reimbursed. Read the actual status.', 'If an unconfirmed submission is recovered on screen, submit that original request to verify its result rather than creating another expense.'],
    faqs: [{ question: 'Who sets the tax treatment?', answer: 'Accounting reviews the submitted cost and sets the tax code. Provide the complete receipt and actual tax-inclusive amount.' }],
    history: [{ title: 'My Expenses', body: 'Open My Expenses to find the saved expense, receipt and current review status. Use its reference when providing missing information.' }],
    related: ['field-tasks', 'field-support']
  },
  'field-quotes': {
    title: 'Prepare a retail quote or sell sheet',
    summary: 'Quick Retail Quote prepares customer-facing products, pricing and a sell sheet. Sending contacts the selected recipients.',
    before: ['Confirm the retailer, recipients and contact permissions, selected products, price basis and any delivery details.'],
    steps: [
      { title: 'Prepare the quote', body: 'Choose the existing customer when a match is offered. Check products, pricing, delivery options, subject and message, and review the customer preview.' },
      { title: 'Review charges and approvals', body: 'Read the product, container, tax and payment-charge breakdown where shown. If the draft needs price approval, use Request approval and Check status; approval applies to that exact draft.' },
      { title: 'Choose the intended output', body: 'Open Sell Sheet prepares the document. Send Test to Me sends a real private test. Send to contacts sends the customer quote. From Saved Quotes, Edit & Resend creates a new revision.' }
    ],
    warnings: ['Do not treat Send Test to Me as a no-send preview: it sends to your Ranch Power inbox.', 'A quote is not an order, payment or inventory reservation. Send New Revision sends another customer communication.'],
    faqs: [{ question: 'What if the original send is uncertain?', answer: 'Open Saved Quotes and its Email delivery evidence. Use the saved quote or offered recovery instead of repeatedly sending a new revision.' }],
    history: [{ title: 'Saved Quotes', body: 'Saved Quotes retains issued versions and PDFs. Use View PDF and Email delivery for the selected quote; editing a saved quote creates a new revision rather than rewriting its history.' }],
    related: ['field-customers', 'field-order-entry', 'field-pipeline', 'field-support']
  },
  'field-goals': {
    title: 'Read your sales goals',
    summary: 'My Goals shows targets set by management and the recorded progress against them.',
    before: ['Check the goal period and measure before comparing it with your current activity.'],
    steps: [
      { title: 'Read the target', body: 'Open My Goals and check the target, period, progress and status.' },
      { title: 'Plan the next action', body: 'Use the relevant customer, lead or task record to plan work that contributes to the goal.' },
      { title: 'Check a discrepancy at its source', body: 'If progress looks wrong, identify the specific order or activity and ask the team to review it.' }
    ],
    warnings: ['Do not create duplicate orders or activity records to increase a goal total.'],
    faqs: [{ question: 'Can I change the target here?', answer: 'These targets are set by management. Discuss a correction or revised target with the person responsible for the goal.' }],
    history: [{ title: 'Supporting activity', body: 'Use the original order, customer or lead records to explain progress rather than relying only on the summary number.' }],
    related: ['field-orders', 'field-pipeline', 'field-tasks']
  },
  'field-support': {
    title: 'Ask for help or report a problem',
    summary: 'Support / Feedback creates a tracked ticket with the details needed to investigate.',
    before: ['Have the page, record reference, what you expected, what happened and any useful screenshots or documents.'],
    steps: [
      { title: 'Describe the problem', body: 'Choose the support type and appropriate priority. Add a clear title and message, including the exact steps and whether a save or send may already have succeeded.' },
      { title: 'Attach relevant evidence', body: 'Link the customer or order when applicable. Attach up to six images or PDFs using the form; wait for each upload to finish before submitting.' },
      { title: 'Submit once and retain the ticket', body: 'Submit the completed form and keep the ticket reference. Use it when providing further information.' }
    ],
    warnings: ['Do not include passwords, payment-card details or sign-in codes in a ticket or screenshot.', 'Opening Help or Support does not submit a ticket. Uploading a file stores evidence; the form\'s submit action creates the ticket.'],
    faqs: [{ question: 'What if an attachment fails?', answer: 'The form keeps successful attachments. Retry the failed file and verify the list before submitting.' }],
    history: [{ title: 'Ticket reference', body: 'The confirmation identifies the support task. Include that reference in follow-up so the investigation stays together.' }],
    related: ['field-tasks', 'field-account', 'field-home']
  },
  'field-account': {
    title: 'Manage your account and preferences',
    summary: 'My Account shows your signed-in identity, profile and available account settings.',
    before: ['Check the signed-in name and role. Use your own account for work so the audit trail identifies the correct person.'],
    steps: [
      { title: 'Check your profile', body: 'Open My Account or Settings. Review the identity and assignment shown, and use the available profile fields for changes you are permitted to make.' },
      { title: 'Save preferences deliberately', body: 'Use the settings provided for notifications and account details. Save the relevant section and wait for its confirmation.' },
      { title: 'Handle access problems separately', body: 'Use the password controls for your own account. Ask the team to correct missing role, territory or warehouse assignments.' }
    ],
    warnings: ['Do not share passwords or verification codes. Profile editing does not grant additional inventory or customer permissions.'],
    faqs: [{ question: 'Can I use desktop Sales on a phone?', answer: 'Where Desktop Workspace is offered, it changes the layout. It does not change your permissions or which records belong to your account.' }],
    history: [{ title: 'Account changes', body: 'Verify saved profile information by reopening the page. Use Support for an unexpected account or assignment issue.' }],
    related: ['field-notifications', 'field-support', 'field-home']
  },
  'field-notifications': {
    title: 'Read notifications and open the work',
    summary: 'Notifications point to tasks, orders and other records that may need attention.',
    before: ['Check the notification date and open its linked record before acting.'],
    steps: [
      { title: 'Read the message', body: 'Review the type, reference and timing so you can distinguish a new issue from an older update.' },
      { title: 'Open the relevant record', body: 'Use the notification\'s available destination. The record\'s current status is the authority for the next action.' },
      { title: 'Manage unread status', body: 'Use the displayed read controls when appropriate. This changes notification state, not the business record.' }
    ],
    warnings: ['Marking a notification read does not approve a request, complete a task or confirm a delivery.'],
    faqs: [{ question: 'Why is a linked item unavailable?', answer: 'It may no longer be in your scope or may need to be reloaded. Keep the reference and ask the team to check it rather than recreating the record.' }],
    history: [{ title: 'Underlying evidence', body: 'Use the linked task, order, stock request or expense for its actual history. Notifications are prompts to review that work.' }],
    related: ['field-tasks', 'field-account', 'field-support']
  },
  'field-documents': {
    title: 'Find field documents',
    summary: 'Documents groups the resources available in your field workspace.',
    before: ['Know the document or shipment you need, and check that the displayed resource applies to your work.'],
    steps: [
      { title: 'Find the resource', body: 'Open Documents & SOPs or BOLs & Documents from More where offered and review the available groups.' },
      { title: 'Check the reference', body: 'Confirm the title, version or shipment reference before relying on the document.' },
      { title: 'Ask for missing evidence', body: 'If the document cannot be opened or the exact reference is absent, ask the team for the current document.' }
    ],
    warnings: ['A listed document or preview is not confirmation that stock was transferred or delivered.'],
    faqs: [{ question: 'Can I use a similar shipment\'s document?', answer: 'Use the paperwork for the actual movement. A similar product or destination does not make another shipment\'s evidence interchangeable.' }],
    history: [{ title: 'Keep movement references', body: 'Match paperwork to the original order, transfer or delivery reference when requesting historical evidence.' }],
    related: ['field-warehouse-receiving', 'field-delivery', 'field-support']
  }
};

// Explicit mapping covers the mobile registry and the Sales-only root screens.
// Unknown screen IDs should fall back to field-home in the help integration.
const RP_FIELD_HELP_ROUTES = {
  'sales-overview': 'field-home',
  'rep-home': 'field-home',
  'more': 'field-home',
  'rep-orders': 'field-orders',
  'rep-order-detail': 'field-orders',
  'create-order': 'field-order-entry',
  'order-exceptions': 'field-exceptions',
  'rep-customers': 'field-customers',
  'rep-customer': 'field-customers',
  'add-customer': 'field-customers',
  'edit-customer': 'field-customers',
  'rep-pipeline': 'field-pipeline',
  'add-lead': 'field-pipeline',
  'lead-detail': 'field-pipeline',
  'edit-lead-contact': 'field-pipeline',
  'log-touch': 'field-pipeline',
  'convert-lead': 'field-pipeline',
  'visit-log': 'field-visits',
  'sales-visits': 'field-visits',
  'rep-inventory': 'field-inventory',
  'request-stock': 'field-requests',
  'stock-requests': 'field-requests',
  'stock-request-detail': 'field-requests',
  'field-receipt': 'field-receiving',
  'giveaway-log': 'field-giveaways',
  'damage-report': 'field-damage',
  'transfer-stock': 'field-transfer',
  'cycle-count': 'field-count',
  'rep-events': 'field-events',
  'event-detail': 'field-events',
  'event-new': 'field-events',
  'event-add-cases': 'field-events',
  'event-open-cases': 'field-event-opening',
  'wh-home': 'field-picking',
  'wh-pick': 'field-picking',
  'pick-order': 'field-picking',
  'wh-receive': 'field-warehouse-receiving',
  'dp-home': 'field-delivery',
  'dp-map': 'field-delivery',
  'load-check': 'field-delivery',
  'delivery-pod': 'field-delivery',
  'my-tasks': 'field-tasks',
  'task-detail': 'field-tasks',
  'submit-expense': 'field-expenses',
  'my-expenses': 'field-expenses',
  'my-goals': 'field-goals',
  'quick-retail-quote': 'field-quotes',
  'retail-quotes': 'field-quotes',
  'retail-quote-edit': 'field-quotes',
  'support-ticket': 'field-support',
  'settings': 'field-account',
  'notifications': 'field-notifications',
  'documents': 'field-documents'
};

// These guide permissions follow offered field workflows, not merely whether a
// component is registered. Related guides must pass the same role check.
const RP_FIELD_HELP_ACCESS = {
  'field-home': ['rep', 'warehouse', 'dispatch'],
  'field-orders': ['rep'],
  'field-order-entry': ['rep'],
  'field-exceptions': ['rep'],
  'field-customers': ['rep'],
  'field-pipeline': ['rep'],
  'field-visits': ['rep'],
  'field-inventory': ['rep'],
  'field-requests': ['rep'],
  'field-receiving': ['rep'],
  'field-warehouse-receiving': ['warehouse'],
  'field-transfer': ['warehouse'],
  'field-count': ['rep', 'warehouse'],
  'field-giveaways': ['rep'],
  'field-damage': ['rep', 'warehouse', 'dispatch'],
  'field-returns': ['rep', 'warehouse', 'dispatch'],
  'field-repack': ['rep'],
  'field-events': ['rep'],
  'field-event-opening': ['rep'],
  'field-picking': ['warehouse'],
  'field-delivery': ['dispatch'],
  'field-tasks': ['rep', 'warehouse', 'dispatch'],
  'field-expenses': ['rep', 'warehouse', 'dispatch'],
  'field-quotes': ['rep'],
  'field-goals': ['rep'],
  'field-support': ['rep', 'warehouse', 'dispatch'],
  'field-account': ['rep', 'warehouse', 'dispatch'],
  'field-notifications': ['rep', 'warehouse', 'dispatch'],
  'field-documents': ['warehouse', 'dispatch']
};


/* ===== page_help.jsx ===== */
/* Contextual guidance is local, read-only content. Opening help must never
   remount a working form, post a workflow, or grant access to another area. */
const RP_PAGE_HELP_GUIDES = { ...RP_INVENTORY_HELP_GUIDES, ...RP_OPERATIONS_HELP_GUIDES, ...RP_FIELD_HELP_GUIDES };

function rpHelpPage(page, detail, portal) {
  if (portal === 'field') return RP_FIELD_HELP_ROUTES[page] || 'field-home';
  return ({ order: 'orders', invoice: 'invoicing', customer: 'customers', event: 'events' })[detail && detail.type] || page;
}

function rpHelpAllowedPages(role, portal) {
  if (portal === 'field') {
    // These authorized field accounts use the rep workflows in getInitialRole.
    const workflowRole = ['ops', 'sales_manager', 'partner'].includes(role) ? 'rep' : role;
    return Object.keys(RP_FIELD_HELP_GUIDES).filter(id =>
      ['admin', 'leadership'].includes(role) || (RP_FIELD_HELP_ACCESS[id] || []).includes(workflowRole));
  }
  return Object.keys({ ...RP_INVENTORY_HELP_GUIDES, ...RP_OPERATIONS_HELP_GUIDES }).filter(id => deskCanUseNav(id, role));
}

function rpHelpGuideText(guide) {
  return [guide.title, guide.summary,
    'BEFORE YOU START', ...(guide.before || []),
    'STEPS', ...(guide.steps || []).flatMap((step, i) => [`${i + 1}. ${step.title}`, step.body, step.check ? `Check: ${step.check}` : '']),
    'CHECKS AND CAUTIONS', ...(guide.warnings || []),
    'QUESTIONS', ...(guide.faqs || []).flatMap(row => [row.question, row.answer]),
    'HISTORY AND PROOF', ...(guide.history || []).flatMap(row => [row.title, row.body])
  ].filter(Boolean).join('\n\n');
}

function rpHelpSearch(ids, query) {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return ids.map(id => ({ id, guide: RP_PAGE_HELP_GUIDES[id] })).filter(({ guide }) => {
    const text = rpHelpGuideText(guide).toLowerCase();
    return words.every(word => text.includes(word));
  }).map(({ id, guide }) => ({ id, title: guide.title,
    match: (guide.faqs || []).find(row => words.some(word => `${row.question} ${row.answer}`.toLowerCase().includes(word)))?.question || guide.summary
  }));
}

function PageHelp({ page, detail, role, portal = 'operations', go }) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const activeRole = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role || role) : String(profile.role || role || '').toLowerCase();
  const allowed = rpHelpAllowedPages(activeRole, portal);
  const contextPage = rpHelpPage(page, detail, portal);
  const currentPage = allowed.includes(contextPage) ? contextPage : allowed[0];
  const [open, setOpen] = useState(false);
  const [fromForm, setFromForm] = useState(false);
  const guide = RP_PAGE_HELP_GUIDES[currentPage];
  useEffect(() => {
    const openHelp = () => { setFromForm(true); setOpen(true); };
    window.RP_OPEN_PAGE_HELP = openHelp;
    return () => { if (window.RP_OPEN_PAGE_HELP === openHelp) delete window.RP_OPEN_PAGE_HELP; };
  }, [currentPage, activeRole]);
  if (!guide) return null;
  return <>
    <aside className="rp-page-help" aria-label="Page help" data-help-page={currentPage}>
      <span><strong>Need a hand?</strong> Find steps, answers and history.</span>
      <button type="button" className="btn btn-sm" aria-haspopup="dialog" onClick={() => { setFromForm(false); setOpen(true); }}><Icon name="doc" size={15} />Help &amp; tour</button>
    </aside>
    {open && <PageHelpDialog key={`${portal}:${activeRole}:${currentPage}`} currentPage={currentPage} allowed={allowed} portal={portal} go={go} fromForm={fromForm}
      onClose={() => setOpen(false)} />}
  </>;
}

function PageHelpDialog({ currentPage, allowed, portal, go, fromForm, onClose }) {
  const [selected, setSelected] = useState(currentPage);
  const [query, setQuery] = useState('');
  const [tour, setTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [question, setQuestion] = useState('');
  const [support, setSupport] = useState(false);
  const [supportState, setSupportState] = useState({ busy: false, dirty: false });
  const dialog = useRef(null), content = useRef(null), search = useRef(null), stepHeading = useRef(null);
  const guide = RP_PAGE_HELP_GUIDES[selected];
  const steps = guide.steps || [];
  const step = steps[stepIndex];
  const results = rpHelpSearch(allowed, query);
  useEffect(() => {
    const previous = document.activeElement;
    const element = dialog.current;
    element.showModal();
    search.current?.focus();
    return () => { element.close(); if (previous && previous.isConnected) previous.focus(); };
  }, []);
  useEffect(() => { if (content.current) content.current.scrollTop = 0; if (tour) stepHeading.current?.focus(); }, [selected, tour, stepIndex]);
  function choose(id, match = '') {
    if (!allowed.includes(id)) return;
    setSelected(id); setQuery(''); setTour(false); setStepIndex(0); setQuestion(match);
  }
  function openPage(link) {
    if (fromForm || portal !== 'operations' || !go || !allowed.includes(link.page)) return;
    if (currentPage !== 'move-stock' && !window.confirm(`Open ${RP_PAGE_HELP_GUIDES[link.page]?.title || link.page}? Save or finish any changes on the current page first. Choose Cancel to return to your work.`)) return;
    onClose();
    // Use the shell's navigation, including the existing unsaved-work guard.
    go(link.page, link.detail || null);
  }
  function leaveSupport(next) {
    if (support && (supportState.busy || (supportState.dirty && !window.confirm('Discard your unsent support question?')))) return;
    next();
  }
  const linkButton = link => link && !fromForm && portal === 'operations' && go && allowed.includes(link.page)
    ? <button type="button" className="btn btn-sm" onClick={() => openPage(link)}>{link.label || `Open ${RP_PAGE_HELP_GUIDES[link.page]?.title || link.page}`}<Icon name="chevR" size={14} /></button> : null;
  function download() {
    const text = `${rpHelpGuideText(guide)}\n\nGuide downloaded ${new Date().toLocaleDateString()}. Reopen in-app help for the current guide. Reading a guide does not complete or post a task.\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = `Ranch-Power-${selected}-guide.txt`; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return <dialog ref={dialog} className="rp-help-dialog" aria-labelledby="rp-help-title"
    onCancel={event => { event.preventDefault(); event.stopPropagation(); leaveSupport(onClose); }}
    onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); leaveSupport(onClose); } }}>
    <div className="rp-help-header">
      <div><span className="eyebrow">Ranch Power help</span><h2 id="rp-help-title">{support ? `Ask about ${guide.title}` : guide.title}</h2></div>
      <button type="button" className="btn btn-sm" disabled={support && supportState.busy} onClick={() => leaveSupport(onClose)} aria-label="Close help">Close<Icon name="x" size={16} /></button>
    </div>
    {!support && !tour && <div className="rp-help-tools">
      <label>Search guides and questions<input ref={search} className="field" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Try: wrong lot, missing stock, invoice…" /></label>
      <label>Browse a page<select className="field" value={selected} onChange={event => choose(event.target.value)}>{allowed.map(id => <option key={id} value={id}>{RP_PAGE_HELP_GUIDES[id].title}</option>)}</select></label>
    </div>}
    <div className="rp-help-body rp-scroll" ref={content}>
      {support ? <><button type="button" className="btn btn-sm" disabled={supportState.busy} onClick={() => leaveSupport(() => setSupport(false))}>Back to guide</button><p className="rp-help-readonly">Include the record reference and what you tried. Attach screenshots if they help. The question is sent only when you submit the form.</p><SupportTicketForm source={`Page help: ${guide.title}`} compact onStateChange={setSupportState} onCancel={() => leaveSupport(() => setSupport(false))} /></> : query.trim() ? <section aria-label="Help search results"><p role="status">{results.length ? `${results.length} matching guide${results.length === 1 ? '' : 's'}` : 'No matching guide. Try a shorter phrase, browse a page, or send a question below.'}</p><div className="rp-help-results">{results.map(result => <button type="button" key={result.id} onClick={() => choose(result.id, result.match)}><strong>{result.title}</strong><span>{result.match}</span><span className="rp-help-link">Read guide →</span></button>)}</div></section> : <>
        {!tour && <p className="rp-help-summary">{guide.summary}</p>}
        <p className="rp-help-readonly">Your work stays open underneath. This guide explains the steps; it does not save, approve or move anything.</p>
        {fromForm && <p className="rp-help-readonly">You can read any guide here. Finish or cancel your open form before navigating to another page.</p>}
        <div className="rp-help-mode"><button type="button" className={`btn btn-sm${!tour ? ' btn-primary' : ''}`} aria-pressed={!tour} onClick={() => setTour(false)}>Full guide</button><button type="button" className={`btn btn-sm${tour ? ' btn-primary' : ''}`} aria-pressed={tour} onClick={() => { setStepIndex(0); setTour(true); }}>Walk me through it</button><button type="button" className="btn btn-sm" onClick={download}>Download guide</button></div>
        {tour && step ? <section className="rp-help-tour" aria-label="Step-by-step walkthrough">
          <label className="rp-help-step-picker">Jump to the part you need<select className="field" value={stepIndex} onChange={event => setStepIndex(Number(event.target.value))}>{steps.map((row, index) => <option key={index} value={index}>{index + 1}. {row.title}</option>)}</select></label>
          <p className="eyebrow" role="status">Step {stepIndex + 1} of {steps.length}</p>
          <progress value={stepIndex + 1} max={steps.length} aria-label="Guide progress" />
          <h3 ref={stepHeading} tabIndex="-1">{step.title}</h3><p>{step.body}</p>
          {step.check && <p className="rp-help-check"><strong>Check your work: </strong>{step.check}</p>}
          {linkButton(step.link)}
          <div className="rp-help-tour-controls"><button type="button" className="btn" disabled={stepIndex === 0} onClick={() => setStepIndex(index => index - 1)}>Previous</button>{stepIndex < steps.length - 1 ? <button type="button" className="btn btn-primary" onClick={() => setStepIndex(index => index + 1)}>Next step</button> : <button type="button" className="btn btn-primary" onClick={() => setTour(false)}>Finish walkthrough</button>}</div>
          <p className="rp-help-readonly">Use the page controls to perform the real task. Reaching the last guide step is not a confirmation that the task was recorded.</p>
        </section> : <>
          <section><h3>Before you start</h3><ul>{(guide.before || []).map(text => <li key={text}>{text}</li>)}</ul></section>
          <section><h3>How to do it</h3><ol className="rp-help-steps">{steps.map((row, index) => <li key={index}><h4>{row.title}</h4><p>{row.body}</p>{row.check && <p className="rp-help-check"><strong>Check your work: </strong>{row.check}</p>}{linkButton(row.link)}</li>)}</ol></section>
        </>}
        {(guide.warnings || []).length > 0 && <section className="rp-help-cautions"><h3>Checks and cautions</h3><ul>{guide.warnings.map(text => <li key={text}>{text}</li>)}</ul></section>}
        <section><h3>Questions and troubleshooting</h3>{(guide.faqs || []).map(row => <details className="rp-help-question" key={`${selected}:${row.question}`} open={question === row.question}><summary>{row.question}</summary><p>{row.answer}</p></details>)}</section>
        <section><h3>History and proof</h3><p className="rp-help-readonly">Use the record reference to connect each action to its result. An empty or filtered list is not proof that an event never happened.</p>{(guide.history || []).map((row, index) => <article className="rp-help-history" key={index}><h4>{row.title}</h4><p>{row.body}</p>{linkButton(row.link)}</article>)}</section>
        {(guide.related || []).some(id => allowed.includes(id)) && <section><h3>Related guides</h3><div className="rp-help-mode">{guide.related.filter(id => allowed.includes(id)).map(id => <button type="button" className="btn btn-sm" key={id} onClick={() => choose(id)}>{RP_PAGE_HELP_GUIDES[id].title}</button>)}</div></section>}
      </>}
    </div>
    {!support && <footer className="rp-help-footer"><div><strong>Still stuck or found a missing answer?</strong><span>Send the page, record reference, what you expected and a screenshot. Don’t repeat a stock action while its result is uncertain.</span></div><button type="button" className="btn btn-sm" onClick={() => { setSupport(true); if (content.current) content.current.scrollTop = 0; }}>Ask through Support</button></footer>}
  </dialog>;
}


/* ===== pricing_breakdown.jsx ===== */
// Presentation only. Keep rpOrderTotals and the invoice service authoritative.
function rpPricingBreakdownRows(order, suppliedTotals) {
  const o = order || {}, t = suppliedTotals || rpOrderTotals(o), rows = [{ label: 'Product subtotal', amount: t.sub }];
  const money = value => Math.round((Number(value) || 0) * 100) / 100;
  const adjustments = Array.isArray(o.adjustments) ? o.adjustments : [];
  const add = (label, amount) => { if (money(amount)) rows.push({ label, amount: money(amount) }); };
  if (t.discount) add('Discount', -t.discount);
  for (const type of ['deposit', 'fee']) {
    const total = money(type === 'deposit' ? t.dep : t.fee);
    const grouped = new Map();
    adjustments.filter(row => rpAdjustmentType(row) === type).forEach(row => {
      const code = String(row.code || '').toUpperCase();
      const label = code === 'CREDIT_CARD_PROCESSING_FEE' ? 'Credit card processing (included)' : String(row.label || (type === 'deposit' ? 'Container deposit' : 'Other fees'));
      grouped.set(label, money((grouped.get(label) || 0) + Number(row.amount || 0)));
    });
    const detailed = money([...grouped.values()].reduce((sum, amount) => sum + amount, 0));
    if (detailed > total + 0.005) { add(type === 'deposit' ? 'Deposits' : 'Fees (recorded total)', total); continue; }
    grouped.forEach((amount, label) => add(label, amount));
    let remainder = money(total - detailed);
    if (type === 'fee' && !adjustments.some(row => String(row.code || '').toUpperCase() === 'CREDIT_CARD_PROCESSING_FEE') && t.creditCardFee > 0) {
      const card = Math.min(remainder, money(t.creditCardFee)); add('Credit card processing (included)', card); remainder = money(remainder - card);
    }
    const hasDetailedFees = adjustments.some(row => rpAdjustmentType(row) === type);
    add(type === 'deposit' ? 'Container deposits' : hasDetailedFees ? 'Other fees' : 'Container fee', remainder);
  }
  let taxes = adjustments;
  if (!rpOwn(o, 'taxTotal') && !adjustments.some(row => rpAdjustmentType(row) === 'tax')) {
    const customer = o.customer || (o.cust && RP.cust(o.cust)) || {};
    const region = String(o.taxRegion || customer.taxRegion || 'BC').toUpperCase();
    taxes = (RP.TAX_RULES || []).filter(rule => rule.active !== false && String(rule.region || 'BC').toUpperCase() === region).filter(rule => !rpTaxExemptForRule(customer, customer.taxExemptions || {}, String(rule.code || '').toUpperCase())).map(rule => {
      const code = String(rule.code || '').toUpperCase();
      const rate = rpOverrideRate(customer.taxOverrides || {}, code, rpFiniteNumber(rule.rate));
      return { type: 'tax', code, label: rule.name || rule.label || code, rate, amount: money(rpTaxBaseForRule(rule, t) * rate) };
    });
  }
  const taxRows = invoiceTaxSummaryRows(taxes, t.tax);
  taxRows.forEach(row => add(row.label, row.amount));
  if (!t.tax) rows.push({ label: 'Tax', amount: 0 });
  const remainder = money(t.total - rows.reduce((sum, row) => sum + Number(row.amount || 0), 0));
  add('Other recorded adjustments', remainder);
  return rows;
}
function PricingBreakdown({ order, totals, estimated = false, showCardOption = true, paymentMethod }) {
  const t = totals || rpOrderTotals(order), rows = rpPricingBreakdownRows(order, t);
  const cardOption = showCardOption && !t.creditCardFee && t.total > 0 && !['Paid', 'Cancelled', 'Canceled', 'Closed'].includes(String(order && (order.invoiceStatus || order.financialStatus || order.status) || ''));
  const cardFee = Math.round(Number(t.total) * 0.024 * 100) / 100;
  return <section aria-label={estimated ? 'Estimated price breakdown' : 'Price breakdown'} style={{ minWidth: 0, width: '100%', marginTop: 12, fontSize: 13, lineHeight: 1.5 }}>
    {rows.map((row, index) => <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '3px 0', color: 'var(--muted)' }}><span>{row.label}</span><span className="mono" style={{ whiteSpace: 'nowrap' }}>{RP.money2(row.amount)}</span></div>)}
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 8, fontWeight: 800 }}><span>{estimated ? 'Estimated total' : 'Total'}</span><span className="mono">{RP.money2(t.total)}</span></div>
    {estimated && <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 0 0' }}>Based on this account’s current prices, fee exemptions and tax rules. The saved order confirms the final amounts.</p>}
    {cardOption && <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 0 0' }}><strong>{paymentMethod === 'credit_card' ? 'Selected credit card payment' : 'Credit card option'}:</strong> {RP.money2(cardFee)} estimated processing fee (2.4%) if the full total is paid by card; {RP.money2(Number(t.total) + cardFee)} including that fee. This conditional fee is not included above. The charge screen confirms the fee on the amount paid by card.</p>}
  </section>;
}
Object.assign(window, { PricingBreakdown, rpPricingBreakdownRows });


/* ===== invoice_issue_request.js ===== */
/* Durable invoice-issue identity. Request bodies stay in memory, never in storage. */
function createInvoiceIssueRequestStore(options = {}) {
  const prefix = 'rp:invoice-issue:v1:';
  const requestIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const hashPattern = /^[0-9a-f]{64}$/;
  const phases = new Set(['pending', 'issued_pending', 'settled', 'cancelled']);
  const emailStatuses = new Set(['unknown', 'not_requested', 'pending', 'sending', 'sent', 'ambiguous', 'reconcile_required']);
  const originalBodies = new Map();
  const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
  const clean = value => String(value == null ? '' : value).trim();
  const cryptoApi = () => options.crypto || globalThis.crypto;
  const locksApi = () => own(options, 'locks') ? options.locks : globalThis.navigator && globalThis.navigator.locks;

  function failure(code, message, requestId) {
    const error = new Error(message);
    error.code = code;
    if (requestId) error.requestId = requestId;
    error.recoveryRequired = true;
    return error;
  }

  function storage() {
    try {
      const value = own(options, 'storage') ? options.storage : globalThis.localStorage;
      if (!value || typeof value.getItem !== 'function' || typeof value.setItem !== 'function') throw new Error('unavailable');
      return value;
    } catch {
      throw failure('INVOICE_ISSUE_STORAGE_UNAVAILABLE', 'Invoice retry protection could not access browser storage. No new invoice request was sent. Enable site storage and try again.');
    }
  }

  async function digest(value) {
    const crypto = cryptoApi();
    if (!crypto || !crypto.subtle || typeof crypto.subtle.digest !== 'function') {
      throw failure('INVOICE_ISSUE_HASH_UNAVAILABLE', 'This browser cannot safely identify an invoice request. Use a current secure browser before issuing.');
    }
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async function scopeDetails(scope) {
    const profileId = clean(scope && scope.profileId).toLowerCase();
    const orderId = clean(scope && scope.orderId);
    if (!profileId || !orderId) throw failure('INVOICE_ISSUE_SCOPE_REQUIRED', 'A signed-in account and saved order are required before issuing an invoice.');
    const scopeHash = await digest(JSON.stringify(['invoice.issue.v1', profileId, orderId]));
    return { orderId, scopeHash, key: prefix + scopeHash };
  }

  function normalizeIntent(input) {
    const intent = {};
    const supported = new Set(['paymentMethod', 'invoiceNote', 'emailTo', 'sendEmail']);
    for (const key of Object.keys(input || {})) {
      if (!supported.has(key)) throw failure('INVOICE_ISSUE_UNSUPPORTED_INTENT', 'This invoice request includes unsupported fields. Refresh before issuing.');
    }
    if (own(input, 'paymentMethod')) intent.paymentMethod = clean(input.paymentMethod);
    if (own(input, 'invoiceNote')) intent.invoiceNote = clean(input.invoiceNote).replace(/\r\n?/g, '\n').slice(0, 600).trim();
    if (own(input, 'emailTo')) intent.emailTo = clean(input.emailTo).toLowerCase();
    if (own(input, 'sendEmail')) intent.sendEmail = input.sendEmail === true;
    return intent;
  }

  function read(details) {
    let raw;
    try { raw = storage().getItem(details.key); } catch (error) {
      if (error.code) throw error;
      throw failure('INVOICE_ISSUE_STORAGE_UNAVAILABLE', 'Invoice retry history could not be read. Do not start another invoice request until site storage is available.');
    }
    if (raw == null) return null;
    let row;
    try { row = JSON.parse(raw); } catch { /* A corrupt marker is not an absent request. */ }
    if (!row || row.v !== 1 || row.scopeHash !== details.scopeHash
      || !requestIdPattern.test(row.requestId || '')
      || !(row.intentHash === null || hashPattern.test(row.intentHash || ''))
      || !phases.has(row.phase) || !emailStatuses.has(row.emailStatus)
      || Object.keys(row).some(key => !['v', 'scopeHash', 'requestId', 'intentHash', 'phase', 'emailStatus'].includes(key))) {
      throw failure('INVOICE_ISSUE_STORAGE_INVALID', 'Saved invoice retry information is invalid. Reconcile this order before starting another invoice request.');
    }
    return row;
  }

  function publicRecord(row) {
    return row ? { requestId: row.requestId, phase: row.phase, pending: !['settled', 'cancelled'].includes(row.phase), issued: ['issued_pending', 'settled'].includes(row.phase), emailStatus: row.emailStatus } : null;
  }

  function write(details, row) {
    const serialized = JSON.stringify(row);
    try {
      const target = storage();
      target.setItem(details.key, serialized);
      if (target.getItem(details.key) !== serialized) throw new Error('write was not retained');
    } catch {
      throw failure('INVOICE_ISSUE_STORAGE_UNAVAILABLE', 'Invoice retry information could not be saved. Do not submit a new invoice request; recover the original request when storage is available.', row.requestId);
    }
    if (typeof options.onChange === 'function') options.onChange();
    else if (typeof window !== 'undefined') window.dispatchEvent(new Event('rp:invoice-issue-request'));
    return publicRecord(row);
  }

  async function exclusive(details, work) {
    const locks = locksApi();
    if (!locks || typeof locks.request !== 'function') {
      throw failure('INVOICE_ISSUE_LOCK_UNAVAILABLE', 'This browser cannot safely coordinate invoice retries across tabs. Use a current browser with site storage enabled; do not start a second request.');
    }
    return locks.request(details.key, { mode: 'exclusive' }, work);
  }

  function recoveryBody(details, row) {
    return { clientRequestId: row.requestId, orderId: details.orderId, recoverOnly: true };
  }

  async function prepare(scope, input, { replaceSettled = false } = {}) {
    const details = await scopeDetails(scope);
    const intent = normalizeIntent(input);
    const intentHash = await digest(JSON.stringify(intent));
    return exclusive(details, () => {
      const current = read(details);
      if (current && current.phase !== 'cancelled') {
        if (current.phase === 'settled') {
          if (current.intentHash === intentHash) return recoveryBody(details, current);
          if (!replaceSettled) throw failure('INVOICE_ISSUE_ALREADY_RESOLVED', 'The original invoice request is complete. Use Update Invoice explicitly before submitting changed invoice details.', current.requestId);
        } else if (current.intentHash !== intentHash) {
          throw failure('INVOICE_ISSUE_PENDING_CHANGED', 'An earlier invoice request is unresolved. Changes here cannot replace it. Retry the original request or reconcile it before making changes.', current.requestId);
        } else {
          const original = originalBodies.get(details.key);
          if (current.phase === 'pending' && original && original.clientRequestId === current.requestId) return { ...original };
          return recoveryBody(details, current);
        }
      }
      const crypto = cryptoApi();
      if (!crypto || typeof crypto.randomUUID !== 'function') throw failure('INVOICE_ISSUE_UUID_UNAVAILABLE', 'This browser cannot create a safe invoice request identity.');
      const requestId = crypto.randomUUID();
      if (!requestIdPattern.test(requestId)) throw failure('INVOICE_ISSUE_UUID_UNAVAILABLE', 'This browser returned an invalid invoice request identity.');
      write(details, { v: 1, scopeHash: details.scopeHash, requestId, intentHash, phase: 'pending', emailStatus: 'unknown' });
      const body = { orderId: details.orderId, ...intent, clientRequestId: requestId };
      originalBodies.set(details.key, body);
      return { ...body };
    });
  }

  async function recover(scope, { allowOriginal = false } = {}) {
    const details = await scopeDetails(scope);
    const current = read(details);
    if (!current) throw failure('INVOICE_ISSUE_REQUEST_MISSING', 'No saved invoice request was found for this account and order. Refresh the order before issuing.');
    const original = originalBodies.get(details.key);
    if (allowOriginal && current.phase === 'pending' && original && original.clientRequestId === current.requestId) return { ...original };
    return recoveryBody(details, current);
  }

  async function recordResponse(scope, requestId, response) {
    const details = await scopeDetails(scope);
    return exclusive(details, () => {
      const current = read(details);
      if (!current || current.requestId !== requestId || clean(response && response.requestId) !== requestId) {
        throw failure('INVOICE_ISSUE_RESPONSE_MISMATCH', 'The invoice response did not match its saved request. Keep the original request and reconcile the order.', current && current.requestId || requestId);
      }
      if (current.phase === 'settled') return publicRecord(current);
      if (current.phase === 'cancelled') {
        if (response.issued === true) throw failure('INVOICE_ISSUE_RESPONSE_MISMATCH', 'This response conflicts with a confirmed cancelled request. Reconcile the order before continuing.', requestId);
        return publicRecord(current);
      }
      const cancelled = response.cancelled === true && response.issued === false
        && response.definitivelyNotCommitted === true && response.recoveryRequired === false
        && response.orderId === details.orderId;
      if (cancelled && current.phase === 'issued_pending') throw failure('INVOICE_ISSUE_RESPONSE_MISMATCH', 'The cancellation response conflicts with a saved invoice. Recover the original request before continuing.', requestId);
      const email = response.email || {};
      const status = emailStatuses.has(email.durableStatus) ? email.durableStatus : 'unknown';
      const terminal = response.issued === true && response.recoveryRequired !== true && email.pending !== true
        && (status === 'not_requested' || (status === 'sent' && email.sent === true));
      const next = {
        ...current,
        phase: cancelled ? 'cancelled' : terminal ? 'settled' : response.issued === true || current.phase === 'issued_pending' ? 'issued_pending' : 'pending',
        emailStatus: cancelled ? 'not_requested' : status
      };
      const saved = write(details, next);
      if (next.phase !== 'pending') originalBodies.delete(details.key);
      return saved;
    });
  }

  async function adoptRecovery(scope, payload) {
    if (!payload || payload.code !== 'INVOICE_ISSUE_RECOVERY_REQUIRED' || !requestIdPattern.test(payload.existingRequestId || '')) return null;
    const details = await scopeDetails(scope);
    return exclusive(details, () => {
      const current = read(details);
      if (current && current.requestId === payload.existingRequestId) return publicRecord(current);
      originalBodies.delete(details.key);
      return write(details, { v: 1, scopeHash: details.scopeHash, requestId: payload.existingRequestId, intentHash: null, phase: 'pending', emailStatus: 'unknown' });
    });
  }

  async function execute(scope, input, transport, { retry = false, replaceSettled = false, cancelIfUncommitted = false } = {}) {
    const body = cancelIfUncommitted
      ? { ...await recover(scope), cancelIfUncommitted: true }
      : retry ? await recover(scope, { allowOriginal: true }) : await prepare(scope, input, { replaceSettled });
    let response;
    try {
      response = await transport(body);
    } catch (error) {
      await adoptRecovery(scope, error && error.payload);
      throw error;
    }
    try {
      const record = await recordResponse(scope, body.clientRequestId, response);
      return { response, record };
    } catch (error) {
      if (['INVOICE_ISSUE_STORAGE_UNAVAILABLE', 'INVOICE_ISSUE_LOCK_UNAVAILABLE'].includes(error.code)
        && response && response.issued === true && response.requestId === body.clientRequestId) {
        return {
          response: { ...response, recoveryRequired: true, warnings: [...(Array.isArray(response.warnings) ? response.warnings : []), 'Invoice saved, but browser retry information could not be finalized. Recover the original request before issuing again.'] },
          record: { requestId: body.clientRequestId, phase: 'issued_pending', pending: true, issued: true, emailStatus: 'unknown' }
        };
      }
      throw error;
    }
  }

  return Object.freeze({
    prepare, recover, recordResponse, adoptRecovery, execute,
    peek: async scope => publicRecord(read(await scopeDetails(scope)))
  });
}

const rpInvoiceIssueRequest = createInvoiceIssueRequestStore();

function rpInvoiceIssueWarnings(response) {
  const warnings = Array.isArray(response && response.warnings) ? response.warnings.filter(value => typeof value === 'string' && value.trim()) : [];
  if (response && response.email && response.email.warning) warnings.push(String(response.email.warning));
  if (response && (response.recoveryRequired || response.email && response.email.pending) && !warnings.length) {
    warnings.push('Invoice saved; the original email request still needs recovery. Retry the original request below; do not send a new email.');
  }
  return [...new Set(warnings)];
}

function useRpInvoiceIssueRequest(orderId) {
  const profileId = String(rpAuthProfile().id || '');
  const enabled = Boolean(orderId && profileId && !rpIsDemoMode());
  const scope = { profileId, orderId: String(orderId || '') };
  const scopeToken = JSON.stringify(scope);
  const currentScope = useRef(scopeToken);
  currentScope.current = scopeToken;
  const [state, setState] = useState({ token: scopeToken, loading: enabled, record: null, error: '' });
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const refresh = async () => {
    if (!enabled) return null;
    try {
      const record = await rpInvoiceIssueRequest.peek(scope);
      if (currentScope.current === scopeToken) setState({ token: scopeToken, loading: false, record, error: '' });
      return record;
    } catch (error) {
      if (currentScope.current === scopeToken) setState({ token: scopeToken, loading: false, record: null, error: error.message });
      throw error;
    }
  };

  useEffect(() => {
    if (!enabled) { setState({ token: scopeToken, loading: false, record: null, error: '' }); return; }
    let active = true;
    const update = () => { if (active) refresh().catch(() => {}); };
    update();
    window.addEventListener('storage', update);
    window.addEventListener('rp:invoice-issue-request', update);
    return () => { active = false; window.removeEventListener('storage', update); window.removeEventListener('rp:invoice-issue-request', update); };
  }, [scopeToken, enabled]);

  const run = async (intent, { retry = false, replaceSettled = false, cancelIfUncommitted = false, orderId: targetOrderId = scope.orderId } = {}) => {
    if (inFlight.current) throw new Error('This invoice request is already running. Wait for its result.');
    const requestScope = { profileId, orderId: targetOrderId };
    if (!profileId || !targetOrderId) throw new Error('A signed-in account and saved order are required.');
    inFlight.current = true;
    setBusy(true);
    try {
      const result = await rpInvoiceIssueRequest.execute(requestScope, intent, body => {
        if (String(rpAuthProfile().id || '') !== profileId) throw new Error('The signed-in account changed. Return to the original account to recover this invoice request.');
        return rpApi('/api/invoices/issue', { method: 'POST', body: JSON.stringify(body) });
      }, { retry, replaceSettled, cancelIfUncommitted });
      if (currentScope.current === JSON.stringify(requestScope)) setState({ token: scopeToken, loading: false, record: result.record, error: '' });
      if (result.response.issued !== true && result.record.phase !== 'cancelled') throw new Error('The original invoice request is not yet confirmed. Retry that request; do not create a replacement.');
      return { ...result.response, cancelled: result.record.phase === 'cancelled', recoveryRequired: result.record.pending || result.response.recoveryRequired === true };
    } finally {
      inFlight.current = false;
      setBusy(false);
      refresh().catch(() => {});
    }
  };

  const readyState = state.token === scopeToken ? state : { loading: enabled, record: null, error: '' };
  return {
    ...readyState,
    busy,
    pending: Boolean(readyState.record && readyState.record.pending),
    settled: Boolean(readyState.record && readyState.record.phase === 'settled'),
    cancelled: Boolean(readyState.record && readyState.record.phase === 'cancelled'),
    run,
    retry: options => run(null, { ...options, retry: true }),
    cancelUnstarted: options => run(null, { ...options, cancelIfUncommitted: true }),
    refresh
  };
}

if (typeof window !== 'undefined') Object.assign(window, { rpInvoiceIssueRequest, rpInvoiceIssueWarnings, useRpInvoiceIssueRequest });

export { createInvoiceIssueRequestStore, rpInvoiceIssueWarnings };


/* ===== lead_email.jsx ===== */
/* ============================================================
   RANCH POWER — Shared lead email composer + engagement history
   Used by Operations and the responsive Sales web experience.
   ============================================================ */

const LEAD_EMAIL_CONTACT_BASES = [
  { id: 'express_consent', label: 'Opted into ongoing Ranch Power emails', hint: 'Use only when the person explicitly agreed, consented, opted in, or gave permission to receive ongoing Ranch Power commercial or marketing email.' },
  { id: 'retailer_staff_direction', label: 'Retailer staff asked me to email this person', hint: 'One introduction only: identify the staff member/role, their direction or provision of an email/contact path, and the buyer/order role. Wait for a human reply before another send.' },
  { id: 'provided_directly', label: 'They gave me their email directly', hint: 'The contact personally provided this address for a relevant business follow-up within the last 180 days.' },
  { id: 'inbound_request', label: 'Requested this email or information', hint: 'Use when the person asked Ranch Power to send this specific follow-up, pricing, samples, or information within the last 180 days.' },
  { id: 'existing_business_relationship', label: 'Existing customer or business relationship', hint: 'Record the qualifying invoice, transaction, or active contract date (maximum 730 days).' },
  { id: 'event_followup', label: 'Requested follow-up at an event', hint: 'They requested follow-up at a tasting, show, or other event within the last 180 days.' },
];

const LEAD_EMAIL_SITUATIONS = [
  { id: 'referred_contact', label: 'Retailer-directed introduction' },
  { id: 'after_call', label: 'After a call or conversation' },
  { id: 'after_voicemail', label: 'After leaving a voicemail' },
  { id: 'sample_offer', label: 'Offer a sample or tasting' },
  { id: 'sample_delivered', label: 'Ask for sample feedback' },
  { id: 'pricing_requested', label: 'Send requested pricing' },
  { id: 'meeting_recap', label: 'Recap a meeting or visit' },
  { id: 'objection_followup', label: 'Follow up on a concern' },
  { id: 'no_response', label: 'No response yet' },
  { id: 'reach_out_later', label: 'They asked me to reconnect later' },
  { id: 'reactivation', label: 'Re-engage a quiet lead' },
  { id: 'first_order', label: 'Suggest a first order' },
  { id: 'close_loop', label: 'Respectfully close the loop' },
];

const LEAD_EMAIL_VALUE_ANGLES = [
  { id: 'distributor_launch', label: 'Distributor launch overview' },
  { id: 'better_alternative', label: 'A better everyday energy-drink alternative' },
  { id: 'balanced_formula', label: 'A balanced, functional formula' },
  { id: 'no_added_sugar', label: 'No added sugar positioning' },
  { id: 'functional_ingredients', label: 'Electrolytes, Lion’s Mane, L-Theanine, B6 and B12' },
  { id: 'natural_flavour', label: 'Natural flavour, without a sugary drink' },
  { id: 'pricing_only', label: 'Keep it to pricing' },
];

const LEAD_EMAIL_OFFERS = [
  { id: 'none', label: 'No offer — keep the ask simple' },
  { id: 'sample_drop', label: 'Sample drop' },
  { id: 'tasting', label: 'Staff tasting' },
  { id: 'price_list', label: 'Price list' },
  { id: 'wholesale_sheet', label: 'Wholesale sheet' },
  { id: 'starter_order', label: 'Starter-order recommendation' },
  { id: 'trial_30_day', label: '30-day trial' },
  { id: 'display_recommendation', label: 'Display recommendation' },
  { id: 'local_reference', label: 'Relevant local retailer reference' },
];

const LEAD_EMAIL_CTAS = [
  { id: 'distributor_ordering', label: 'Order through distributor' },
  { id: 'send_samples', label: 'Offer two sample-drop times' },
  { id: 'share_pricing', label: 'Offer or confirm pricing details' },
  { id: 'pricing_fit', label: 'Check whether pricing works' },
  { id: 'book_tasting', label: 'Book a short tasting' },
  { id: 'quick_call', label: 'Suggest a brief call' },
  { id: 'feedback', label: 'Ask one feedback question' },
  { id: 'right_person', label: 'Confirm the right person' },
  { id: 'close_loop', label: 'Close the loop respectfully' },
];

const LEAD_EMAIL_TONES = [
  { id: 'warm', label: 'Warm and personal' },
  { id: 'concise', label: 'Extra concise' },
  { id: 'consultative', label: 'Helpful and consultative' },
];

const LEAD_EMAIL_CONTEXT_PRESETS = [
  {
    id: 'connection',
    contextKey: 'priorInteraction',
    label: 'How did you connect?',
    hint: 'Choose the real interaction that led to this email.',
    allowCustom: true,
    options: [
      { id: 'none', label: 'No interaction detail', value: '' },
      { id: 'store_visit', label: 'Store visit', value: 'Thanks for taking a few minutes to speak with me during my store visit.', situations: ['after_call', 'sample_offer', 'pricing_requested', 'meeting_recap', 'objection_followup', 'first_order'] },
      { id: 'conversation', label: 'Conversation', value: 'Thanks for taking the time to speak with me.', situations: ['after_call', 'pricing_requested', 'meeting_recap', 'objection_followup', 'first_order'] },
      { id: 'phone_call', label: 'Phone call', value: 'Thanks for taking the time to speak with me by phone.', situations: ['after_call', 'pricing_requested', 'meeting_recap', 'objection_followup', 'first_order'] },
      { id: 'email_exchange', label: 'Email or messages', value: 'I wanted to follow up on our recent messages.', situations: ['after_call', 'pricing_requested', 'meeting_recap', 'objection_followup', 'first_order'] },
      { id: 'voicemail', label: 'Voicemail', value: 'I left a brief voicemail and wanted to make this easy to find.', situations: ['after_voicemail'] },
      { id: 'samples', label: 'Samples dropped off', value: 'I wanted to check in now that your team has had some time with the samples.', situations: ['sample_delivered'] },
      { id: 'event', label: 'Tasting or event', value: 'It was great meeting you at the recent tasting or event.', situations: ['after_call', 'sample_offer', 'meeting_recap', 'pricing_requested'] },
      { id: 'staff_introduction', label: 'Retailer staff introduction', value: 'Your team directed me to you as the person who handles new products or ordering.', situations: ['referred_contact'] },
      { id: 'earlier_email', label: 'Earlier email, no reply', value: 'I wanted to add one useful detail to my earlier note.', situations: ['no_response', 'close_loop'] },
      { id: 'requested_information', label: 'They requested information', value: 'You asked me to send over some information.', situations: ['after_call', 'pricing_requested', 'meeting_recap'] },
      { id: 'reconnect_now', label: 'Asked to reconnect now', value: 'You asked me to reconnect around now.', situations: ['reach_out_later'] },
      { id: 'earlier_conversation', label: 'Earlier conversation', value: 'It has been a little while since we spoke, and I wanted to reconnect.', situations: ['reactivation'] },
      { id: 'no_direct_conversation', label: 'No direct conversation', value: 'I wanted to introduce Ranch Power and see whether it may be relevant for your store.', situations: ['referred_contact', 'sample_offer'] },
    ],
  },
  {
    id: 'retailer_signal',
    contextKey: 'retailerPriority',
    label: 'Strongest retailer signal',
    hint: 'Choose only what the retailer actually signalled.',
    allowCustom: true,
    options: [
      { id: 'none', label: 'No clear signal', value: '' },
      { id: 'interested_next_step', label: 'Interested and asked for next steps', value: 'You were interested in Ranch Power and asked about next steps.' },
      { id: 'requested_pricing', label: 'Requested pricing', value: 'Clear wholesale pricing and a practical shelf price are the most useful details.', situations: ['pricing_requested'] },
      { id: 'open_to_information', label: 'Open to more information', value: 'You were open to receiving more information about Ranch Power.' },
      { id: 'wants_feedback', label: 'Wants team or sample feedback', value: 'You wanted your team to try Ranch Power before deciding.', situations: ['sample_offer', 'sample_delivered'] },
      { id: 'decision_maker', label: 'Needs another decision-maker', value: 'Another decision-maker needs to review the next step.' },
      { id: 'margin_shelf_price', label: 'Margin and shelf price', value: 'Retailer margin and a practical shelf price are the main considerations.' },
      { id: 'easy_first_order', label: 'Easy first order', value: 'A simple, low-risk first order is the priority.' },
      { id: 'shelf_fit', label: 'Shelf or cooler fit', value: 'Shelf or cooler fit is the main consideration.' },
      { id: 'local_product', label: 'BC-owned company', value: 'Ranch Power is a BC-owned company.' },
      { id: 'multiple_locations', label: 'Multiple locations', value: 'There may be an opportunity across more than one location.', situations: ['referred_contact', 'after_call', 'sample_offer', 'sample_delivered', 'meeting_recap', 'first_order'] },
    ],
  },
  {
    id: 'agreement',
    contextKey: 'promisedNextStep',
    label: 'What was agreed?',
    hint: 'Choose what they asked for or what you agreed to do.',
    allowCustom: true,
    options: [
      { id: 'none', label: 'Nothing specific', value: '' },
      { id: 'send_pricing', label: 'Send pricing and MSRP', value: 'As promised, I am sending the wholesale cost and suggested retail pricing.', situations: ['pricing_requested', 'after_call', 'meeting_recap'] },
      { id: 'starter_mix', label: 'Suggest a starter mix', value: 'As promised, I am following up with a simple starter-mix recommendation.', situations: ['first_order', 'after_call', 'meeting_recap'] },
      { id: 'sample_drop', label: 'Arrange a sample drop', value: 'I am following up to arrange a sample drop.', situations: ['sample_offer', 'after_call', 'meeting_recap'] },
      { id: 'tasting', label: 'Arrange a tasting', value: 'I am following up to arrange a short tasting.', situations: ['sample_offer', 'after_call', 'meeting_recap'] },
      { id: 'wholesale_information', label: 'Send wholesale information', value: 'As promised, I am sending the wholesale information.', situations: ['after_call', 'meeting_recap', 'pricing_requested'] },
      { id: 'sample_feedback', label: 'Ask for sample feedback', value: 'I am checking back as promised after your team tried the samples.', situations: ['sample_delivered'] },
      { id: 'reconnect', label: 'Reconnect now', value: 'I am reconnecting at the time we discussed.', situations: ['reach_out_later', 'reactivation'] },
    ],
  },
  {
    id: 'concern',
    contextKey: 'objection',
    label: 'Main concern',
    hint: 'Choose one concern only when the retailer raised it.',
    allowCustom: true,
    options: [
      { id: 'none', label: 'No reason given', value: '' },
      { id: 'margin', label: 'Margin or price', value: 'The main concern was retailer margin or price.' },
      { id: 'shelf_space', label: 'Shelf space', value: 'The main concern was limited shelf or cooler space.' },
      { id: 'timing', label: 'Timing', value: 'The main concern was timing.' },
      { id: 'approval', label: 'Needs approval', value: 'The next step needs approval from another decision-maker.' },
      { id: 'staff_feedback', label: 'Needs staff feedback', value: 'Your team wanted to provide feedback before deciding.' },
      { id: 'existing_product', label: 'Already carries another product', value: 'You already carry another product in this category.' },
      { id: 'uncertain_demand', label: 'Uncertain customer demand', value: 'You were unsure about customer demand.' },
    ],
  },
  {
    id: 'real_detail',
    contextKey: 'personalNote',
    label: 'One real detail',
    hint: 'Use one genuine detail, or leave it out.',
    allowCustom: true,
    options: [
      { id: 'none', label: 'No extra detail', value: '' },
      { id: 'other_locations', label: 'They mentioned other locations', value: 'You mentioned that Ranch Power may also be relevant for your other locations.', situations: ['referred_contact', 'after_call', 'sample_offer', 'sample_delivered', 'meeting_recap', 'first_order'] },
      { id: 'sample_favourite', label: 'Their team had a favourite', value: 'Your team had a clear favourite among the samples.', situations: ['sample_delivered', 'meeting_recap'] },
    ],
  },
];

const LEAD_EMAIL_CONTEXT_PRESET_DEFAULTS = {
  referred_contact: { connection: 'staff_introduction', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  after_call: { connection: 'conversation', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  after_voicemail: { connection: 'voicemail', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  sample_offer: { connection: 'no_direct_conversation', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  sample_delivered: { connection: 'samples', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  pricing_requested: { connection: 'requested_information', retailer_signal: 'requested_pricing', agreement: 'none', concern: 'none', real_detail: 'none' },
  meeting_recap: { connection: 'conversation', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  objection_followup: { connection: 'conversation', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  no_response: { connection: 'earlier_email', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  reach_out_later: { connection: 'reconnect_now', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  reactivation: { connection: 'earlier_conversation', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  first_order: { connection: 'none', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
  close_loop: { connection: 'earlier_email', retailer_signal: 'none', agreement: 'none', concern: 'none', real_detail: 'none' },
};

const LEAD_EMAIL_REPLY_OUTCOMES = [
  { id: 'positive', label: 'Positive / interested' },
  { id: 'referral', label: 'Referred me to someone else' },
  { id: 'not_now', label: 'Not now / follow up later' },
  { id: 'out_of_office', label: 'Out of office' },
  { id: 'neutral', label: 'Neutral reply' },
  { id: 'negative', label: 'Not interested' },
  { id: 'replied', label: 'Other reply' },
  { id: 'unsubscribe', label: 'Asked not to be emailed' },
];

const LEAD_EMAIL_SUBJECT_LIMIT = 200;
const LEAD_EMAIL_BODY_LIMIT = 20000;
const LEAD_EMAIL_MSRP_PRESETS = [4.49, 5, 5.25];

const LEAD_EMAIL_STAGE_DEFAULTS = {
  idea: { situation: 'referred_contact', valueAngle: 'better_alternative', offer: 'sample_drop', cta: 'send_samples' },
  contacted: { situation: 'sample_offer', valueAngle: 'better_alternative', offer: 'sample_drop', cta: 'send_samples' },
  sampling: { situation: 'sample_delivered', valueAngle: 'balanced_formula', offer: 'tasting', cta: 'feedback' },
  proposal: { situation: 'first_order', valueAngle: 'better_alternative', offer: 'starter_order', cta: 'quick_call' },
  won: { situation: 'meeting_recap', valueAngle: 'better_alternative', offer: 'none', cta: 'quick_call' },
  lost: { situation: 'reactivation', valueAngle: 'better_alternative', offer: 'none', cta: 'close_loop' },
};

const LEAD_EMAIL_SITUATION_DEFAULTS = {
  after_call: { offer: 'sample_drop', cta: 'quick_call' },
  after_voicemail: { offer: 'sample_drop', cta: 'quick_call' },
  close_loop: { offer: 'sample_drop', cta: 'close_loop' },
  first_order: { offer: 'starter_order', cta: 'feedback' },
  meeting_recap: { offer: 'sample_drop', cta: 'feedback' },
  no_response: { offer: 'wholesale_sheet', cta: 'send_samples' },
  objection_followup: { offer: 'sample_drop', cta: 'feedback' },
  pricing_requested: { offer: 'none', cta: 'pricing_fit' },
  reach_out_later: { offer: 'sample_drop', cta: 'quick_call' },
  reactivation: { offer: 'sample_drop', cta: 'send_samples' },
  referred_contact: { offer: 'sample_drop', cta: 'right_person' },
  sample_delivered: { offer: 'sample_drop', cta: 'feedback' },
  sample_offer: { offer: 'sample_drop', cta: 'send_samples' },
};

function leadEmailClean(value) {
  return String(value == null ? '' : value).trim();
}

function leadEmailDistributorNameError(value) {
  const name = leadEmailClean(value);
  if (name.length < 2) return 'Enter the distributor name.';
  if (name.length > 50) return 'Distributor name must be 50 characters or fewer.';
  return /^[\p{L}\p{N}][\p{L}\p{N} &'’().,/+-]*$/u.test(name)
    ? ''
    : 'Distributor name contains unsupported characters.';
}

function leadEmailContactBasisDetailText(value) {
  return String(value == null ? '' : value)
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, 600)
    .trim();
}

function leadEmailAddressValid(value) {
  return /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(leadEmailClean(value));
}

function leadEmailBasisDate(value) {
  const clean = leadEmailClean(value);
  if (!clean) return '';
  const parsed = new Date(clean);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

function leadEmailLocalDateYmd(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function leadEmailBasisDateValid(value, basis) {
  const clean = leadEmailClean(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) return false;
  const parsed = new Date(`${clean}T00:00:00Z`);
  const structurallyValid = !Number.isNaN(parsed.getTime())
    && parsed.toISOString().slice(0, 10) === clean
    && clean <= leadEmailLocalDateYmd();
  if (!structurallyValid) return false;
  const maxAgeDays = {
    provided_directly: 180,
    retailer_staff_direction: 180,
    inbound_request: 180,
    existing_business_relationship: 730,
    event_followup: 180,
  }[leadEmailClean(basis)];
  if (!maxAgeDays) return leadEmailClean(basis) === 'express_consent';
  const expires = new Date(parsed);
  expires.setUTCDate(expires.getUTCDate() + maxAgeDays);
  return expires.toISOString().slice(0, 10) >= leadEmailLocalDateYmd();
}

function leadEmailContactNameParts(value) {
  const clean = leadEmailClean(value).replace(/\s+/g, ' ');
  if (!clean) return { firstName: '', lastName: '' };
  const parts = clean.split(' ');
  return { firstName: parts.shift() || '', lastName: parts.join(' ') };
}

function leadEmailLegacyContactParts(value) {
  const clean = leadEmailClean(value);
  if (!clean) return { firstName: '', lastName: '', title: '' };
  const rolePattern = /^(owner|buyer|manager|general manager|gm|purchasing|purchaser|store manager|category manager|director|founder|operator|staff)$/i;
  const pieces = clean.split(/\s+(?:—|–|-|\|)\s+/).map(leadEmailClean).filter(Boolean);
  if (pieces.length >= 2 && rolePattern.test(pieces[0])) {
    return { ...leadEmailContactNameParts(pieces.slice(1).join(' ')), title: pieces[0] };
  }
  if (pieces.length >= 2 && rolePattern.test(pieces[pieces.length - 1])) {
    return { ...leadEmailContactNameParts(pieces.slice(0, -1).join(' ')), title: pieces[pieces.length - 1] };
  }
  if (rolePattern.test(clean)) return { firstName: '', lastName: '', title: clean };
  return { ...leadEmailContactNameParts(clean), title: '' };
}

function leadEmailLooksLikeBusinessName(value, leadName) {
  const clean = leadEmailClean(value).replace(/[^\p{L}\p{N}]+/gu, ' ').trim().toLowerCase();
  const business = leadEmailClean(leadName).replace(/[^\p{L}\p{N}]+/gu, ' ').trim().toLowerCase();
  if (!clean) return false;
  if (business && clean === business) return true;
  const compact = clean.replace(/\s/g, '');
  const digits = (compact.match(/\d/g) || []).length;
  if (compact.length >= 5 && digits / compact.length >= 0.45) return true;
  return /\b(?:inc|incorporated|llc|ltd|limited|corp|corporation|company|co|dairy|market|foods|grocery|cafe|store|gym|fitness|provisions|bottle|restaurant|hotel|lodge|pharmacy)\b/i.test(clean);
}

function leadEmailContactBasisDetailError(basis, value) {
  const normalizedBasis = leadEmailClean(basis);
  if (!normalizedBasis) return 'Choose why Ranch Power may email this person.';
  const detail = leadEmailContactBasisDetailText(value);
  const words = detail.match(/[A-Za-z][A-Za-z'’-]*/g) || [];
  if (detail.length < 10 || words.length < 2) {
    return 'Add a short factual note with at least two words.';
  }
  if (normalizedBasis === 'express_consent') {
    const consentAction = /\b(?:agreed|agreement|consent(?:ed)?|gave\s+(?:us\s+)?permission|granted\s+(?:us\s+)?permission|opted[ -]?in|subscribed)\b/i;
    const consentNegation = /\b(?:no|not|never|without|did(?:\s+not|n't)|has(?:\s+not|n't)|had(?:\s+not|n't)|refused|declined)\b[^.!?\n]{0,50}\b(?:agree(?:d|ment)?|consent(?:ed)?|permission|opt(?:ed)?[ -]?in|subscrib(?:e|ed))\b/i;
    const emailScope = /^(?=[\s\S]*\b(?:ranch\s+power|commercial|marketing)\b)(?=[\s\S]*\b(?:e-?mails?|messages?|communications?)\b)/i;
    if (!consentNegation.test(detail) && consentAction.test(detail) && emailScope.test(detail)) return '';
    const soundsLikeRequestedFollowup = /\b(?:asked|requested|pricing|quote|samples?|information|follow[ -]?up)\b/i.test(detail)
      && !/\b(?:ongoing|future|commercial|marketing|promotions?|newsletter|updates?)\b/i.test(detail);
    return soundsLikeRequestedFollowup
      ? 'This sounds like a requested follow-up, not an opt-in to ongoing emails. Choose “Requested this email or information” instead.'
      : 'For ongoing-email consent, note that the person explicitly agreed, consented, opted in, or gave permission to receive Ranch Power commercial or marketing email.';
  }
  if (normalizedBasis === 'retailer_staff_direction') {
    const staffIdentity = /\b(?:front[- ]desk|front[- ]counter|counter|receiving|receiver|store\s+manager|general\s+manager|manager|owner|clerk|cashier|store\s+associate|team\s+member|staff\s+member|store\s+employee)\b/i;
    const namedStaffSource = /\b[A-Z][\p{L}'’.-]{1,39}\s+(?:at|from)\s+(?:the\s+)?(?:store|retailer|location|front[- ]desk|front[- ]counter|counter|receiving)\b/u;
    const directionAction = /\b(?:directed|told|asked|instructed|said|provided|gave|shared|sent|referred|pointed)\b/i;
    const emailContactChannel = /\b(?:e-?mail(?:\s+address)?|address|contact(?:ed|ing)?|reach(?:ed|ing)?\s+out|write|message)\b/i;
    const buyerOrderRelevance = /\b(?:buyer|buying|purchas\w*|procurement|order(?:ing|s)?|new\s+products?|new\s+vendors?|new\s+accounts?|category\s+manager|handles?\s+(?:the\s+)?(?:buying|purchasing|orders?|new\s+products?|new\s+vendors?))\b/i;
    return (staffIdentity.test(detail) || namedStaffSource.test(detail))
      && directionAction.test(detail)
      && emailContactChannel.test(detail)
      && buyerOrderRelevance.test(detail)
      ? ''
      : 'Identify the retailer staff member or role, how they directed or provided the email contact, and why this person handles buying or orders.';
  }
  const patterns = {
    provided_directly: /\b(?:provided|gave|shared|email|address|follow[ -]?up|conversation|visit|call)\b/i,
    inbound_request: /\b(?:asked|requested|inquiry|contacted|pricing|samples?|information)\b/i,
    existing_business_relationship: /\b(?:invoice|order|purchase|transaction|customer|account|contract)\b/i,
    event_followup: /\b(?:event|tasting|show|expo|market|conference|festival)\b/i,
  };
  if (patterns[normalizedBasis] && patterns[normalizedBasis].test(detail)) return '';
  if (normalizedBasis === 'inbound_request') {
    return 'Describe what the person asked Ranch Power to email, such as pricing, samples, or information.';
  }
  if (normalizedBasis === 'existing_business_relationship') {
    return 'Record the qualifying invoice, order, transaction, customer account, or active contract.';
  }
  if (normalizedBasis === 'event_followup') {
    return 'Name the tasting, show, or event where the person requested follow-up.';
  }
  return 'Describe when the person provided their email and what follow-up it was for.';
}

function leadEmailContactBasisDetailValid(basis, value) {
  return !leadEmailContactBasisDetailError(basis, value);
}

function leadEmailNormalizeContact(contact, index) {
  const row = contact || {};
  const parsed = leadEmailContactNameParts(row.name || row.fullName || row.full_name);
  return {
    id: leadEmailClean(row.id || row.contactId || row.contact_id),
    firstName: leadEmailClean(row.firstName || row.first_name || parsed.firstName),
    lastName: leadEmailClean(row.lastName || row.last_name || parsed.lastName),
    title: leadEmailClean(row.title || row.role || row.jobTitle || row.job_title),
    email: leadEmailClean(row.email || row.value).toLowerCase(),
    phone: leadEmailClean(row.phone || row.phoneNumber || row.phone_number),
    primary: Boolean(row.primary || row.isPrimary || row.is_primary),
    contactBasis: leadEmailClean(row.contactBasis || row.contact_basis),
    contactBasisDetail: leadEmailClean(row.contactBasisDetail || row.contact_basis_detail),
    basisObtainedAt: leadEmailBasisDate(row.basisObtainedAt || row.basis_obtained_at),
  };
}

function leadEmailContactsForLead(lead) {
  const rows = Array.isArray(lead && lead.contacts)
    ? lead.contacts.filter(row => row && row.active !== false && (row.email || row.name || row.firstName || row.first_name))
    : [];
  if (rows.length) {
    const orderedRows = [...rows].sort((left, right) => Number(Boolean(right.primary || right.isPrimary || right.is_primary)) - Number(Boolean(left.primary || left.isPrimary || left.is_primary)));
    return orderedRows.map(leadEmailNormalizeContact).map(row => leadEmailLooksLikeBusinessName([row.firstName, row.lastName].filter(Boolean).join(' '), lead && lead.name)
      ? { ...row, firstName: '', lastName: '' }
      : row);
  }
  const legacy = leadEmailLegacyContactParts(lead && lead.contact);
  const safeLegacy = leadEmailLooksLikeBusinessName([legacy.firstName, legacy.lastName].filter(Boolean).join(' '), lead && lead.name)
    ? { ...legacy, firstName: '', lastName: '' }
    : legacy;
  return [{
    id: '',
    firstName: safeLegacy.firstName,
    lastName: safeLegacy.lastName,
    title: safeLegacy.title,
    email: leadEmailClean(lead && lead.email).toLowerCase(),
    phone: leadEmailClean(lead && lead.phone),
    primary: true,
    contactBasis: leadEmailClean(lead && (lead.contactBasis || lead.contact_basis)),
    contactBasisDetail: leadEmailClean(lead && (lead.contactBasisDetail || lead.contact_basis_detail)),
    basisObtainedAt: leadEmailBasisDate(lead && (lead.basisObtainedAt || lead.basis_obtained_at)),
  }];
}

function leadEmailContextPresetGroups(value, fallback = LEAD_EMAIL_CONTEXT_PRESETS) {
  if (!Array.isArray(value) || !value.length) return fallback;
  const groups = value.map(group => {
    const contextKey = leadEmailClean(group && group.contextKey);
    const options = (Array.isArray(group && group.options) ? group.options : []).map(option => ({
      id: leadEmailClean(option && option.id),
      label: leadEmailClean(option && (option.label || option.name)),
      hint: leadEmailClean(option && (option.description || option.hint)),
      value: leadEmailClean(option && option.value),
      situations: Array.isArray(option && option.situations)
        ? option.situations.map(leadEmailClean).filter(Boolean)
        : [],
    })).filter(option => option.id && option.label);
    return {
      id: leadEmailClean(group && group.id),
      contextKey,
      label: leadEmailClean(group && group.label),
      hint: leadEmailClean(group && (group.description || group.hint)),
      allowCustom: group && group.allowCustom !== false,
      options,
    };
  }).filter(group => group.id && group.contextKey && group.label && group.options.length);
  return groups.length ? groups : fallback;
}

function leadEmailContextPresetDefaults(value, fallback = LEAD_EMAIL_CONTEXT_PRESET_DEFAULTS) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fallback;
  const rows = {};
  Object.entries(value).forEach(([situation, selections]) => {
    if (!selections || typeof selections !== 'object' || Array.isArray(selections)) return;
    rows[leadEmailClean(situation)] = Object.fromEntries(
      Object.entries(selections).map(([groupId, optionId]) => [leadEmailClean(groupId), leadEmailClean(optionId)])
    );
  });
  return Object.keys(rows).length ? rows : fallback;
}

function leadEmailContextPresetState(
  situation,
  groups = LEAD_EMAIL_CONTEXT_PRESETS,
  defaults = LEAD_EMAIL_CONTEXT_PRESET_DEFAULTS
) {
  const normalizedSituation = leadEmailClean(situation);
  const requested = defaults[normalizedSituation] || defaults.referred_contact || {};
  const context = {
    whyNow: '',
    retailerPriority: '',
    priorInteraction: '',
    objection: '',
    promisedNextStep: '',
    personalNote: '',
  };
  const presetIds = {};
  const customValues = {};
  const touched = {};
  groups.forEach(group => {
    const visible = group.options.filter(option => leadEmailContextOptionVisible(option, normalizedSituation));
    const selected = visible.find(option => option.id === requested[group.id])
      || visible.find(option => option.id === 'none')
      || visible[0];
    presetIds[group.id] = selected ? selected.id : '';
    context[group.contextKey] = selected ? selected.value : '';
    customValues[group.id] = '';
  });
  return { context, presetIds, customValues, touched };
}

function leadEmailContextOptionVisible(option, situation) {
  return !Array.isArray(option && option.situations)
    || !option.situations.length
    || option.situations.includes(leadEmailClean(situation));
}

function leadEmailReconcileContextPresetState(situation, groups, defaults, currentState) {
  const fallback = leadEmailContextPresetState(situation, groups, defaults);
  const current = currentState && typeof currentState === 'object' ? currentState : {};
  const currentContext = current.context && typeof current.context === 'object' ? current.context : {};
  const currentPresetIds = current.presetIds && typeof current.presetIds === 'object' ? current.presetIds : {};
  const currentCustomValues = current.customValues && typeof current.customValues === 'object' ? current.customValues : {};
  const currentTouched = current.touched && typeof current.touched === 'object' ? current.touched : {};
  const context = { ...fallback.context };
  const presetIds = {};
  const customValues = {};
  const touched = {};
  groups.forEach(group => {
    const currentId = leadEmailClean(currentPresetIds[group.id]);
    const visible = group.options.filter(option => leadEmailContextOptionVisible(option, situation));
    const currentOption = visible.find(option => option.id === currentId);
    const fallbackOption = visible.find(option => option.id === fallback.presetIds[group.id])
      || visible.find(option => option.id === 'none')
      || visible[0];
    const hasSavedCustom = Object.prototype.hasOwnProperty.call(currentCustomValues, group.id);
    const savedCustom = hasSavedCustom
      ? String(currentCustomValues[group.id] == null ? '' : currentCustomValues[group.id])
      : currentId === 'custom'
        ? String(currentContext[group.contextKey] == null ? '' : currentContext[group.contextKey])
        : '';
    const keepCustom = currentId === 'custom' && group.allowCustom !== false;
    const keepTouchedOption = Boolean(currentTouched[group.id] && currentOption);
    const selected = keepTouchedOption ? currentOption : fallbackOption;

    customValues[group.id] = savedCustom;
    if (keepCustom) {
      presetIds[group.id] = 'custom';
      context[group.contextKey] = savedCustom;
      touched[group.id] = true;
    } else {
      presetIds[group.id] = selected ? selected.id : '';
      context[group.contextKey] = selected ? selected.value : '';
      if (keepTouchedOption) touched[group.id] = true;
    }
  });

  return { context, presetIds, customValues, touched };
}

function leadEmailSelectContextPreset(currentState, group, situation, optionId) {
  const current = currentState && typeof currentState === 'object' ? currentState : {};
  const context = current.context && typeof current.context === 'object' ? current.context : {};
  const presetIds = current.presetIds && typeof current.presetIds === 'object' ? current.presetIds : {};
  const customValues = current.customValues && typeof current.customValues === 'object' ? current.customValues : {};
  const touched = current.touched && typeof current.touched === 'object' ? current.touched : {};
  const requestedId = leadEmailClean(optionId);
  const selected = group.options.find(option =>
    option.id === requestedId && leadEmailContextOptionVisible(option, situation)
  );
  const useCustom = requestedId === 'custom' && group.allowCustom !== false;
  if (!useCustom && !selected) return currentState;

  const hasSavedCustom = Object.prototype.hasOwnProperty.call(customValues, group.id);
  const savedCustom = hasSavedCustom
    ? String(customValues[group.id] == null ? '' : customValues[group.id])
    : presetIds[group.id] === 'custom'
      ? String(context[group.contextKey] == null ? '' : context[group.contextKey])
      : '';

  return {
    context: {
      ...context,
      [group.contextKey]: useCustom ? savedCustom : selected.value,
    },
    presetIds: { ...presetIds, [group.id]: useCustom ? 'custom' : selected.id },
    customValues: { ...customValues, [group.id]: savedCustom },
    touched: { ...touched, [group.id]: true },
  };
}

function leadEmailSetCustomContextValue(currentState, group, value) {
  const text = String(value == null ? '' : value);
  return {
    ...currentState,
    context: { ...currentState.context, [group.contextKey]: text },
    customValues: { ...currentState.customValues, [group.id]: text },
    touched: { ...currentState.touched, [group.id]: true },
  };
}

function leadEmailInitialContext(lead, situation) {
  return leadEmailContextPresetState(situation);
}

function leadEmailCcAddresses(value) {
  return Array.from(new Set(String(value || '').split(/[,;\n]+/).map(item => item.trim().toLowerCase()).filter(Boolean)));
}

function leadEmailClientRequestId(leadId) {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  return `lead-email-${leadEmailClean(leadId) || 'unknown'}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function leadEmailResponseDraft(response) {
  const source = response && response.draft ? response.draft : response || {};
  const subjectOptions = Array.from(new Set([
    ...(Array.isArray(source.subjectOptions) ? source.subjectOptions : []),
    ...(Array.isArray(response && response.subjectOptions) ? response.subjectOptions : []),
  ].map(leadEmailClean).filter(Boolean))).slice(0, 2);
  return {
    subject: leadEmailClean(source.subject),
    subjectOptions,
    bodyText: leadEmailClean(source.bodyText || source.body || source.text),
    generation: leadEmailGeneration(source.generation),
    templateKey: leadEmailClean(source.templateKey),
    templateVersion: leadEmailClean(source.templateVersion),
    suggestedFollowUpAt: leadEmailClean(source.suggestedFollowUpAt || source.suggestedFollowUp),
    qualityChecks: Array.isArray(source.qualityChecks) ? source.qualityChecks : [],
    pricing: source.pricing && typeof source.pricing === 'object' ? source.pricing : null,
    replyTo: leadEmailClean(source.replyTo || response && response.replyTo),
    compliance: response && response.compliance || source.compliance || {},
  };
}

function leadEmailHistoryRows(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response && response.messages)) return response.messages;
  if (Array.isArray(response && response.history)) return response.history;
  if (Array.isArray(response && response.items)) return response.items;
  return [];
}

function leadEmailEventsOfType(message, type, primaryOnly = false) {
  const events = Array.isArray(message && message.events) ? message.events : [];
  const primaryEmail = leadEmailClean(message && (message.toEmail || message.to_email || message.to)).toLowerCase();
  const hasCc = Array.isArray(message && message.cc) && message.cc.some(Boolean);
  return events.filter(event => {
    const eventType = leadEmailClean(event && (event.type || event.eventType || event.event_type)).toLowerCase();
    const typeMatches = eventType === type
      || eventType === `email.${type}`
      || type === 'replied' && eventType === 'email.reply_recorded';
    if (!typeMatches) return false;
    if (!primaryOnly) return true;
    const affectedEmail = leadEmailClean(event && (event.affectedEmail || event.affected_email)).toLowerCase();
    return affectedEmail ? affectedEmail === primaryEmail : !hasCc;
  });
}

function leadEmailEventOfType(message, type, primaryOnly = false) {
  return leadEmailEventsOfType(message, type, primaryOnly)[0] || null;
}

function leadEmailMessageFacts(message) {
  const deliveryStatus = leadEmailClean(message && (message.deliveryStatus || message.delivery_status || message.status)).toLowerCase();
  const hasCc = Array.isArray(message && message.cc) && message.cc.some(Boolean);
  const openedEvents = leadEmailEventsOfType(message, 'opened', true);
  const clickedEvents = leadEmailEventsOfType(message, 'clicked', true);
  const openedEvent = openedEvents[0] || null;
  const clickedEvent = clickedEvents[0] || null;
  const deliveredEvent = leadEmailEventOfType(message, 'delivered', true);
  const bouncedEvent = leadEmailEventOfType(message, 'bounced', true);
  const complainedEvent = leadEmailEventOfType(message, 'complained', true);
  const repliedEvent = leadEmailEventOfType(message, 'replied', true);
  return {
    sentAt: message && (message.sentAt || message.sent_at),
    deliveredAt: message && (message.deliveredAt || message.delivered_at) || deliveredEvent && (deliveredEvent.occurredAt || deliveredEvent.createdAt),
    openedAt: openedEvent && (openedEvent.occurredAt || openedEvent.createdAt)
      || (!hasCc && message && (message.openedAt || message.opened_at || message.firstOpenedAt || message.first_opened_at)),
    clickedAt: clickedEvent && (clickedEvent.occurredAt || clickedEvent.createdAt)
      || (!hasCc && message && (message.clickedAt || message.clicked_at || message.firstClickedAt || message.first_clicked_at)),
    bouncedAt: message && (message.bouncedAt || message.bounced_at) || bouncedEvent && (bouncedEvent.occurredAt || bouncedEvent.createdAt),
    complainedAt: message && (message.complainedAt || message.complained_at) || complainedEvent && (complainedEvent.occurredAt || complainedEvent.createdAt),
    repliedAt: message && (message.repliedAt || message.replied_at) || repliedEvent && (repliedEvent.occurredAt || repliedEvent.createdAt),
    openCount: openedEvents.length || (!hasCc ? Number(message && (message.openCount || message.open_count) || 0) : 0),
    clickCount: clickedEvents.length || (!hasCc ? Number(message && (message.clickCount || message.click_count) || 0) : 0),
    deliveryStatus,
  };
}

function leadEmailFormatDate(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return leadEmailClean(value);
  return new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    year: parsed.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function leadEmailOptionLabel(options, id) {
  const item = options.find(option => option.id === id);
  return item ? item.label : leadEmailClean(id).replace(/_/g, ' ');
}

function leadEmailOptionRows(value, fallback) {
  if (!Array.isArray(value) || !value.length) return fallback;
  const rows = value.map(option => ({
    id: leadEmailClean(option && option.id),
    label: leadEmailClean(option && (option.label || option.name)),
    hint: leadEmailClean(option && (option.description || option.hint)),
  })).filter(option => option.id && option.label);
  return rows.length ? rows : fallback;
}

function leadEmailWritingOptions(value) {
  const source = value && typeof value === 'object' ? value : {};
  const rawProviders = Array.isArray(source.providers)
    ? source.providers
    : Array.isArray(source.providerChoices)
      ? source.providerChoices
      : Array.isArray(source.choices)
        ? source.choices
        : [];
  const providers = leadEmailOptionRows(rawProviders, [])
    .filter(option => ['template', 'groq', 'cloudflare'].includes(option.id));
  const requestedDefault = leadEmailClean(source.defaultProvider || source.default || source.provider).toLowerCase();
  const defaultProvider = providers.some(option => option.id === requestedDefault)
    ? requestedDefault
    : providers[0] && providers[0].id || '';
  return { defaultProvider, providers };
}

function leadEmailGeneration(value) {
  const source = value && typeof value === 'object' ? value : {};
  const modeValue = leadEmailClean(source.mode).toLowerCase();
  const mode = modeValue === 'assisted' || modeValue === 'template_fallback'
    ? modeValue
    : 'template';
  const providerValue = leadEmailClean(source.provider).toLowerCase();
  const provider = ['template', 'groq', 'cloudflare'].includes(providerValue)
    ? providerValue
    : 'template';
  return {
    mode,
    provider,
    model: leadEmailClean(source.model),
    notice: leadEmailClean(source.notice),
  };
}

function leadEmailGenerationLabel(generation, writingOptions) {
  if (!generation || generation.mode === 'template') return 'Template draft';
  const provider = writingOptions.providers.find(option => option.id === generation.provider);
  const label = provider && provider.label || 'Natural writing';
  return generation.mode === 'template_fallback'
    ? `${label} was unavailable. Template draft used.`
    : `${label} assisted draft`;
}

function leadEmailCheckBlocked(check) {
  if (!check || typeof check === 'string') return false;
  const status = leadEmailClean(check.status).toLowerCase();
  return status === 'blocked' || status === 'error' || check.passed === false && status !== 'warning';
}

function leadEmailPriceModelRows(value) {
  return (Array.isArray(value) ? value : []).map(row => ({
    name: leadEmailClean(row && row.name),
    caseCost: Number(row && (row.caseCost != null ? row.caseCost : row.skuPrice)),
    terms: leadEmailClean(row && row.terms),
  })).filter(row => row.name && Number.isFinite(row.caseCost) && row.caseCost > 0);
}

function leadEmailInitialPriceModels() {
  return [{ name: 'Default Retail', caseCost: 36.5, terms: 'Prepaid' }];
}

function leadEmailMoney(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : '—';
}

function leadEmailQuotePreview(priceModels, priceModelName, msrpValue) {
  const model = priceModels.find(row => row.name === priceModelName);
  const msrp = Number(msrpValue);
  if (!model || !Number.isFinite(msrp) || msrp <= 0) return null;
  const exactCostPerCan = model.caseCost / 12;
  const costPerCan = Math.round(exactCostPerCan * 100) / 100;
  const grossProfitPerCan = Math.round((msrp - exactCostPerCan) * 100) / 100;
  const grossMarginPercent = Math.round((((msrp - exactCostPerCan) / msrp) * 100) * 10) / 10;
  return { ...model, costPerCan, grossMarginPercent, grossProfitPerCan, msrp };
}

function LeadEmailField({ label, hint, children, required }) {
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <span className="field-label" style={{ marginBottom: 5 }}>{label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}</span>
      {children}
      {hint && <span style={{ display: 'block', color: 'var(--faint)', fontSize: 11.25, lineHeight: 1.4, marginTop: 5 }}>{hint}</span>}
    </label>
  );
}

function LeadEmailContextPresetField({ group, situation, presetId, value, onPreset, onCustom }) {
  const options = group.options.filter(option => leadEmailContextOptionVisible(option, situation) || option.id === presetId);
  const selected = group.options.find(option => option.id === presetId);
  const fieldSuffix = String(group.id || 'context').replace(/[^a-z0-9_-]+/gi, '-').toLowerCase();
  const selectId = `lead-email-context-${fieldSuffix}`;
  const customId = `${selectId}-custom`;
  const hintId = `${selectId}-hint`;
  const hint = presetId === 'custom'
    ? 'Custom text is used only for this draft and can be edited before sending.'
    : selected && (selected.hint || selected.description) || group.hint;
  return (
    <div style={{ display: 'block', minWidth: 0 }}>
      <label className="field-label" htmlFor={selectId} style={{ display: 'block', marginBottom: 5 }}>{group.label}</label>
      <select
        className="field"
        data-lead-email-context-preset={group.id}
        id={selectId}
        value={presetId}
        onChange={event => onPreset(group, event.target.value)}
        aria-describedby={hint ? hintId : undefined}
      >
        {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
        {group.allowCustom && <option value="custom">Custom...</option>}
      </select>
      {presetId === 'custom' && (
        <div style={{ marginTop: 7 }}>
          <label className="field-label" htmlFor={customId} style={{ display: 'block', marginBottom: 5 }}>Custom detail</label>
          <textarea
            aria-describedby={hint ? hintId : undefined}
            className="field"
            data-lead-email-context-custom={group.id}
            id={customId}
            maxLength={500}
            rows="2"
            value={value}
            onChange={event => onCustom(group, event.target.value)}
            placeholder="Add one short, factual detail"
          />
        </div>
      )}
      {hint && <span id={hintId} style={{ display: 'block', color: 'var(--faint)', fontSize: 11.25, lineHeight: 1.4, marginTop: 5 }}>{hint}</span>}
    </div>
  );
}

function LeadEmailHistory({ lead, rows, loading, error, onReload, onMarkReplied, marking, live = true, webhookConfigured = false, engagementTrackingConfigured = false }) {
  const [replyFor, setReplyFor] = useState('');
  const [replyOutcome, setReplyOutcome] = useState('positive');
  const [replyNote, setReplyNote] = useState('');
  const [replyAffectedEmail, setReplyAffectedEmail] = useState('');

  const openReplyForm = id => {
    setReplyFor(current => current === id ? '' : id);
    setReplyOutcome('positive');
    setReplyNote('');
    setReplyAffectedEmail('');
  };

  const submitReply = async (id, message) => {
    const recipients = Array.from(new Set([
      leadEmailClean(message && (message.toEmail || message.to_email || message.to || message.recipientEmail || message.recipient_email)).toLowerCase(),
      ...(Array.isArray(message && message.cc) ? message.cc : []),
    ].map(leadEmailClean).filter(Boolean)));
    if (recipients.length > 1 && !replyAffectedEmail) return;
    const saved = await onMarkReplied(id, replyOutcome, replyNote, replyAffectedEmail);
    if (saved) {
      setReplyFor('');
      setReplyOutcome('positive');
      setReplyNote('');
      setReplyAffectedEmail('');
    }
  };

  if (loading) return <div role="status" className="rp-lead-email-empty"><Icon name="refresh" size={20} /><div>Loading message history…</div></div>;
  if (error) {
    return (
      <div className="rp-lead-email-empty" role="alert">
        <div style={{ color: 'var(--danger)', fontWeight: 800 }}>History is unavailable</div>
        <div>{error}</div>
        <button className="btn btn-sm" onClick={onReload}>Try again</button>
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="rp-lead-email-empty">
        <Icon name="mail" size={22} />
        <div style={{ color: 'var(--ink)', fontWeight: 800 }}>{live ? 'No tracked emails yet' : 'Live email history is not available in preview mode'}</div>
        <div>{live ? 'Messages sent from this tool will appear here. Analytics appear only from recorded provider events.' : 'Sign in to see provider-backed delivery and engagement events.'}</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ color: 'var(--faint)', fontSize: 11.5, lineHeight: 1.45 }}>
        {webhookConfigured && engagementTrackingConfigured
          ? 'Primary-recipient open and click estimates can be caused by privacy tools or automated activity. Treat replies and pipeline movement as stronger intent.'
          : 'Primary-recipient open/click estimates are unavailable until Resend engagement tracking and its signed webhook are configured. Replies remain the strongest intent signal.'}
      </div>
      {rows.map((message, index) => {
        const facts = leadEmailMessageFacts(message);
        const id = leadEmailClean(message && (message.messageId || message.message_id || message.id || message.providerMessageId || message.provider_message_id));
        const subject = leadEmailClean(message && message.subject) || '(No subject)';
        const recipient = leadEmailClean(message && (message.toEmail || message.to_email || message.to || message.recipientEmail || message.recipient_email));
        const recipients = Array.from(new Set([
          recipient.toLowerCase(),
          ...(Array.isArray(message && message.cc) ? message.cc : []),
        ].map(leadEmailClean).filter(Boolean)));
        const attributedEvents = (Array.isArray(message && message.events) ? message.events : []).flatMap((event, eventIndex) => {
          const type = leadEmailClean(event && (event.type || event.eventType || event.event_type)).toLowerCase();
          const affectedEmail = leadEmailClean(event && (event.affectedEmail || event.affected_email)).toLowerCase();
          if (!affectedEmail || affectedEmail === recipient.toLowerCase()) return [];
          const outcome = leadEmailClean(event && event.outcome);
          const labels = {
            'email.reply_recorded': `Reply · ${affectedEmail}${outcome ? ` · ${outcome.replace(/_/g, ' ')}` : ''}`,
            'email.bounced': `Bounce · ${affectedEmail}`,
            'email.complained': `Complaint · ${affectedEmail}`,
            'email.suppressed': `Suppressed · ${affectedEmail}`,
            'email.unsubscribed': `Unsubscribed · ${affectedEmail}`,
          };
          return labels[type] ? [{
            key: leadEmailClean(event && event.id) || `${type}:${affectedEmail}:${eventIndex}`,
            label: labels[type],
            negative: type !== 'email.reply_recorded' || ['negative', 'unsubscribe'].includes(outcome),
          }] : [];
        });
        const status = facts.complainedAt
          ? { label: 'Complaint', fg: 'var(--danger)', bg: 'var(--danger-bg)' }
          : facts.bouncedAt || facts.deliveryStatus === 'bounced'
            ? { label: 'Bounced', fg: 'var(--danger)', bg: 'var(--danger-bg)' }
            : facts.repliedAt
              ? { label: 'Replied', fg: 'var(--ok)', bg: 'var(--ok-bg)' }
              : facts.deliveredAt || facts.deliveryStatus === 'delivered'
                ? { label: 'Delivered', fg: 'var(--ok)', bg: 'var(--ok-bg)' }
                : { label: facts.deliveryStatus === 'failed' ? 'Failed' : facts.deliveryStatus === 'pending' ? 'Pending' : 'Sent', fg: facts.deliveryStatus === 'failed' ? 'var(--danger)' : 'var(--info)', bg: facts.deliveryStatus === 'failed' ? 'var(--danger-bg)' : 'var(--info-bg)' };
        return (
          <div key={id || index} data-lead-email-message={id || index} className="card" style={{ padding: 13, boxShadow: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'var(--ink)', fontSize: 13.5, fontWeight: 800, overflowWrap: 'anywhere' }}>{subject}</div>
                <div style={{ color: 'var(--faint)', fontSize: 11.5, marginTop: 3, overflowWrap: 'anywhere' }}>{recipient || lead && lead.email || 'Recipient not returned'}{facts.sentAt ? ` · ${leadEmailFormatDate(facts.sentAt)}` : ''}</div>
              </div>
              <span className="pill" style={{ background: status.bg, color: status.fg }}>{status.label}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 11 }}>
              {facts.deliveredAt && <span className="pill" title={leadEmailFormatDate(facts.deliveredAt)} style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}><Icon name="check" size={11} />Delivered</span>}
              {(facts.openedAt || facts.openCount > 0) && <span className="pill" title={leadEmailFormatDate(facts.openedAt)} style={{ background: 'var(--info-bg)', color: 'var(--info)' }}><Icon name="tap" size={11} />Primary recipient open estimate{facts.openCount > 1 ? ` · ${facts.openCount}` : ''}</span>}
              {(facts.clickedAt || facts.clickCount > 0) && <span className="pill" title={leadEmailFormatDate(facts.clickedAt)} style={{ background: 'var(--info-bg)', color: 'var(--info)' }}><Icon name="link" size={11} />Primary recipient click estimate{facts.clickCount > 1 ? ` · ${facts.clickCount}` : ''}</span>}
              {facts.repliedAt && <span className="pill" title={leadEmailFormatDate(facts.repliedAt)} style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}><Icon name="mail" size={11} />Reply recorded</span>}
              {attributedEvents.map(event => <span key={event.key} className="pill" style={{ background: event.negative ? 'var(--danger-bg)' : 'var(--ok-bg)', color: event.negative ? 'var(--danger)' : 'var(--ok)' }}><Icon name={event.negative ? 'alert' : 'mail'} size={11} />{event.label}</span>)}
            </div>
            {facts.sentAt && id && !['failed', 'pending'].includes(facts.deliveryStatus) && (
              <div style={{ marginTop: 10 }}>
                {replyFor === id ? (
                  <div style={{ display: 'grid', gap: 8, padding: 10, borderRadius: 10, background: 'var(--surface-2)' }}>
                    <LeadEmailField label="Reply outcome">
                      <select className="field" value={replyOutcome} onChange={event => {
                        setReplyOutcome(event.target.value);
                        setReplyAffectedEmail('');
                      }}>
                        {LEAD_EMAIL_REPLY_OUTCOMES.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                      </select>
                    </LeadEmailField>
                    {recipients.length > 1 && (
                      <LeadEmailField label={replyOutcome === 'unsubscribe' ? 'Who asked to unsubscribe?' : 'Who replied?'} required hint={replyOutcome === 'unsubscribe' ? 'Only the selected address will be added to the commercial-email suppression list.' : 'Attribute this outcome to the correct To or CC recipient.'}>
                        <select className="field" value={replyAffectedEmail} onChange={event => setReplyAffectedEmail(event.target.value)}>
                          <option value="">Choose the recipient address…</option>
                          {recipients.map(address => <option key={address} value={address}>{address}</option>)}
                        </select>
                      </LeadEmailField>
                    )}
                    <LeadEmailField label="Reply note" hint={replyOutcome === 'unsubscribe' ? 'This outcome suppresses future outreach to this address.' : 'Optional. Record what they said or the next step.'}>
                      <textarea className="field" rows="2" maxLength="1000" value={replyNote} onChange={event => setReplyNote(event.target.value)} placeholder="Optional context for the next rep…" />
                    </LeadEmailField>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 7 }}>
                      <button className="btn btn-sm" disabled={marking === id} onClick={() => setReplyFor('')}>Cancel</button>
                      <button className={`btn btn-sm ${replyOutcome === 'unsubscribe' ? '' : 'btn-primary'}`} disabled={marking === id || recipients.length > 1 && !replyAffectedEmail} onClick={() => submitReply(id, message)}>
                        <Icon name="check" size={13} />{marking === id ? 'Saving…' : replyOutcome === 'unsubscribe' ? 'Record & suppress' : 'Record reply'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm" disabled={Boolean(marking)} onClick={() => openReplyForm(id)}>
                      <Icon name="check" size={13} />Mark reply received
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LeadEmailPulse({ lead, onOpen, actionLabel = 'Build email' }) {
  const [state, setState] = useState({ loading: true, error: '', rows: [], summary: null, webhookConfigured: false, engagementTrackingConfigured: false });
  const live = typeof leadLiveMode !== 'function' || leadLiveMode();
  useEffect(() => {
    let cancelled = false;
    if (!live || !lead || !lead.id) {
      setState({ loading: false, error: '', rows: [], summary: null, webhookConfigured: false, engagementTrackingConfigured: false });
      return () => { cancelled = true; };
    }
    setState({ loading: true, error: '', rows: [], summary: null, webhookConfigured: false, engagementTrackingConfigured: false });
    rpApi(`/api/leads/email/history?leadId=${encodeURIComponent(lead.id)}`)
      .then(response => {
        if (!cancelled) setState({
          loading: false,
          error: '',
          rows: leadEmailHistoryRows(response),
          summary: response && response.summary || null,
          webhookConfigured: Boolean(response && response.webhookConfigured),
          engagementTrackingConfigured: Boolean(response && response.engagementTrackingConfigured),
        });
      })
      .catch(err => {
        if (!cancelled) setState({ loading: false, error: err && err.message ? err.message : 'Could not load email insights.', rows: [], summary: null, webhookConfigured: false, engagementTrackingConfigured: false });
      });
    return () => { cancelled = true; };
  }, [lead && lead.id, live]);

  const rows = state.rows;
  const facts = rows.map(leadEmailMessageFacts);
  const summary = state.summary || {};
  const latestSentIndex = facts
    .map((item, index) => ({ index, sent: item.sentAt ? Date.parse(item.sentAt) : 0 }))
    .filter(item => Number.isFinite(item.sent) && item.sent > 0)
    .sort((left, right) => right.sent - left.sent)[0]?.index;
  const latestMessage = latestSentIndex == null ? null : rows[latestSentIndex];
  const latestFacts = latestSentIndex == null ? null : facts[latestSentIndex];
  const latestFollowUp = latestMessage && (latestMessage.suggestedFollowUpAt || latestMessage.suggested_follow_up_at);
  const fallbackOverdue = Boolean(
    latestFacts
    && !latestFacts.repliedAt
    && !latestFacts.bouncedAt
    && !latestFacts.complainedAt
    && latestFollowUp
    && Number.isFinite(Date.parse(latestFollowUp))
    && Date.parse(latestFollowUp) < Date.now()
  ) ? 1 : 0;
  const overdue = Number.isFinite(Number(summary.overdueFollowUps)) ? Number(summary.overdueFollowUps) : fallbackOverdue;
  const delivered = Number.isFinite(Number(summary.delivered)) ? Number(summary.delivered) : facts.filter(item => item.deliveredAt || item.deliveryStatus === 'delivered').length;
  const openSignals = facts.reduce((total, item) => total + Math.max(item.openCount, item.openedAt ? 1 : 0), 0);
  const clickSignals = facts.reduce((total, item) => total + Math.max(item.clickCount, item.clickedAt ? 1 : 0), 0);
  const bounces = Number.isFinite(Number(summary.bounced)) ? Number(summary.bounced) : facts.filter(item => item.bouncedAt || item.deliveryStatus === 'bounced').length;
  const metrics = [
    ['Sent', Number.isFinite(Number(summary.sent)) ? Number(summary.sent) : facts.filter(item => item.sentAt).length],
    ['Delivered', state.webhookConfigured || delivered > 0 ? delivered : '—'],
    ['Primary recipient open estimates', state.webhookConfigured && state.engagementTrackingConfigured || openSignals > 0 ? openSignals : '—'],
    ['Primary recipient click estimates', state.webhookConfigured && state.engagementTrackingConfigured || clickSignals > 0 ? clickSignals : '—'],
    ['Replies', Number.isFinite(Number(summary.replies)) ? Number(summary.replies) : facts.filter(item => item.repliedAt).length],
    ['Bounces', state.webhookConfigured || bounces > 0 ? bounces : '—'],
  ];
  return (
    <div className="card" data-lead-email-pulse={lead && lead.id} style={{ padding: 15, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 11 }}>
        <div>
          <div className="eyebrow">Email engagement</div>
          <div style={{ color: 'var(--faint)', fontSize: 10.75, marginTop: 3 }}>Provider events for this lead only</div>
        </div>
        <button className="btn btn-sm btn-primary" onClick={onOpen}><Icon name="mail" size={13} />{actionLabel}</button>
      </div>
      {state.loading ? (
        <div role="status" style={{ color: 'var(--faint)', fontSize: 12 }}>Loading tracked email activity…</div>
      ) : state.error ? (
        <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.75, fontWeight: 650 }}>{state.error}</div>
      ) : !live ? (
        <div style={{ color: 'var(--info)', fontSize: 11.75, fontWeight: 650 }}>Live sends and engagement signals appear here after sign-in.</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 7 }}>
            {metrics.map(([label, value]) => (
              <div key={label} style={{ padding: '8px 7px', borderRadius: 9, background: 'var(--surface-2)', textAlign: 'center' }}>
                <div className="disp" style={{ color: label === 'Bounces' && Number(value) > 0 ? 'var(--danger)' : 'var(--ink)', fontSize: 17 }}>{value}</div>
                <div style={{ color: 'var(--faint)', fontSize: 9.5, marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
          {overdue > 0 && <div style={{ marginTop: 9, padding: '8px 10px', borderRadius: 9, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 11.5, fontWeight: 750 }}><Icon name="alert" size={12} style={{ marginRight: 6, verticalAlign: -2 }} />{overdue} suggested follow-up{overdue === 1 ? ' is' : 's are'} overdue</div>}
          <div style={{ color: 'var(--faint)', fontSize: 10.5, lineHeight: 1.4, marginTop: 8 }}>
            {state.webhookConfigured && state.engagementTrackingConfigured
              ? 'Primary-recipient open and click estimates are directional and may include privacy or automated activity.'
              : 'Primary-recipient open/click estimates are unavailable until Resend engagement tracking and its signed webhook are configured.'}
          </div>
        </>
      )}
    </div>
  );
}

function LeadEmailComposer({ lead, onClose, onSent, allowSend = false }) {
  const leadContactsKey = JSON.stringify(Array.isArray(lead && lead.contacts) ? lead.contacts : []);
  const [contacts, setContacts] = useState(() => leadEmailContactsForLead(lead));
  const initialPriceModels = leadEmailInitialPriceModels();
  const initialDefaults = LEAD_EMAIL_STAGE_DEFAULTS[lead && lead.stage] || LEAD_EMAIL_STAGE_DEFAULTS.idea;
  const initialContextState = leadEmailInitialContext(lead, initialDefaults.situation);
  const [contactIndex, setContactIndex] = useState(0);
  const [contact, setContact] = useState(() => leadEmailContactsForLead(lead)[0]);
  const [situation, setSituation] = useState(initialDefaults.situation);
  const [valueAngle, setValueAngle] = useState(initialDefaults.valueAngle);
  const [offer, setOffer] = useState(initialDefaults.offer);
  const [cta, setCta] = useState(initialDefaults.cta);
  const [usesDistributor, setUsesDistributor] = useState(false);
  const [distributorName, setDistributorName] = useState('');
  const [tone, setTone] = useState('warm');
  const [priceModels, setPriceModels] = useState(initialPriceModels);
  const [priceModelName, setPriceModelName] = useState(() => {
    const defaultModel = initialPriceModels.find(row => row.name === 'Default Retail')
      || initialPriceModels[0];
    return defaultModel ? defaultModel.name : '';
  });
  const [msrp, setMsrp] = useState('4.49');
  const [msrpPresets, setMsrpPresets] = useState(LEAD_EMAIL_MSRP_PRESETS);
  const [operationalDetailOptions, setOperationalDetailOptions] = useState([]);
  const [operationalDetailIds, setOperationalDetailIds] = useState([]);
  const [writingOptions, setWritingOptions] = useState({ defaultProvider: '', providers: [] });
  const [writingProvider, setWritingProvider] = useState('');
  const [containerFeePolicy, setContainerFeePolicy] = useState({ containerFeePerCase: null, containerFeeNote: 'Applicable container fees and taxes are extra.' });
  const [optionsError, setOptionsError] = useState('');
  const [contextPresetGroups, setContextPresetGroups] = useState(LEAD_EMAIL_CONTEXT_PRESETS);
  const [contextPresetDefaults, setContextPresetDefaults] = useState(LEAD_EMAIL_CONTEXT_PRESET_DEFAULTS);
  const [contextPresetState, setContextPresetState] = useState(initialContextState);
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [ccText, setCcText] = useState('');
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);
  const [tab, setTab] = useState('build');
  const [busy, setBusy] = useState('');
  const [contactSaving, setContactSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyCapabilities, setHistoryCapabilities] = useState({ webhookConfigured: false, engagementTrackingConfigured: false });
  const [historyReload, setHistoryReload] = useState(0);
  const [marking, setMarking] = useState('');
  const [optionSets, setOptionSets] = useState({
    contactBases: LEAD_EMAIL_CONTACT_BASES,
    situations: LEAD_EMAIL_SITUATIONS,
    valueAngles: LEAD_EMAIL_VALUE_ANGLES,
    offers: LEAD_EMAIL_OFFERS,
    ctas: LEAD_EMAIL_CTAS,
    tones: LEAD_EMAIL_TONES,
  });
  const requestIdRef = useRef(leadEmailClientRequestId(lead && lead.id));
  const sendAttemptedRef = useRef(false);
  const situationRef = useRef(initialDefaults.situation);
  const draftInputRevisionRef = useRef(0);
  const live = typeof leadLiveMode !== 'function' || leadLiveMode();
  situationRef.current = situation;
  const contextPresetIds = contextPresetState.presetIds;
  const context = contextPresetState.context;
  const email = leadEmailClean(contact && contact.email).toLowerCase();
  const invalidEmail = Boolean(email) && !leadEmailAddressValid(email);
  const cc = leadEmailCcAddresses(ccText);
  const invalidCc = cc.filter(address => !leadEmailAddressValid(address));
  const ccIncludesRecipient = Boolean(email && cc.includes(email));
  const tooManyCc = cc.length > 10;
  const pricingQuotePreview = leadEmailQuotePreview(priceModels, priceModelName, msrp);
  const pricingInvalid = situation === 'pricing_requested' && (!priceModelName || !pricingQuotePreview || Number(msrp) < 1 || Number(msrp) > 25);
  const distributorNameError = usesDistributor ? leadEmailDistributorNameError(distributorName) : '';
  const suspiciousContactName = leadEmailLooksLikeBusinessName([contact.firstName, contact.lastName].filter(Boolean).join(' '), lead && lead.name);
  const storedContactLookedLikeBusiness = Boolean(
    leadEmailLooksLikeBusinessName(lead && lead.contact, lead && lead.name)
    || (Array.isArray(lead && lead.contacts) && lead.contacts.some(row => leadEmailLooksLikeBusinessName(row && (row.name || row.fullName || row.full_name), lead && lead.name)))
  );
  const selectedBasis = optionSets.contactBases.find(option => option.id === contact.contactBasis);
  const contactBasisDetailRequired = Boolean(contact.contactBasis);
  const contactBasisDetailError = contact.contactBasis
    ? leadEmailContactBasisDetailError(contact.contactBasis, contact.contactBasisDetail)
    : '';
  const missingContactBasisDetail = Boolean(contactBasisDetailError);
  const suggestedContactBasis = contact.contactBasis === 'express_consent'
    && contactBasisDetailError.startsWith('This sounds like a requested follow-up')
    ? 'inbound_request'
    : '';
  const missingBasisObtainedAt = !leadEmailBasisDateValid(contact.basisObtainedAt, contact.contactBasis);
  const contactBasisConfirmationText = contact.contactBasis === 'retailer_staff_direction'
    ? 'I confirm the note identifies the retailer staff member or role, their direction or provision of this buyer/order email contact, and that this basis allows one introduction only. It is not a statutory referral and cannot support an external CC.'
    : contact.contactBasis === 'express_consent'
      ? 'I confirm the recipient explicitly agreed, consented, opted in, or gave permission to receive ongoing Ranch Power commercial or marketing email; a pricing request alone is not express consent.'
      : 'I confirm this contact’s documented basis is accurate and the address was not scraped or guessed.';

  useEffect(() => {
    setContacts(leadEmailContactsForLead(lead));
  }, [lead && lead.id, leadContactsKey]);

  useEffect(() => {
    let cancelled = false;
    if (!live) return undefined;
    setOptionsError('');
    rpApi(`/api/leads/email/options?leadId=${encodeURIComponent(lead.id)}`).then(response => {
      if (cancelled) return;
      const pricingOptions = response && response.pricing || {};
      const nextModels = leadEmailPriceModelRows(pricingOptions.priceModels);
      if (nextModels.length) {
        setPriceModels(nextModels);
        const policyDefault = leadEmailClean(pricingOptions.defaultPriceModelName).toLowerCase();
        setPriceModelName((nextModels.find(row => row.name.toLowerCase() === policyDefault)
          || nextModels.find(row => row.name === 'Default Retail')
          || nextModels[0]).name);
      }
      if (Array.isArray(pricingOptions.msrpPresets) && pricingOptions.msrpPresets.length) {
        setMsrpPresets(pricingOptions.msrpPresets.map(Number).filter(value => Number.isFinite(value) && value > 0));
      }
      setOperationalDetailOptions(leadEmailOptionRows(pricingOptions.operationalDetails, []));
      setContainerFeePolicy(pricingOptions.containerFeePolicy || { containerFeePerCase: null, containerFeeNote: 'Applicable container fees and taxes are extra.' });
      const nextWritingOptions = leadEmailWritingOptions(response && response.writing);
      setWritingOptions(nextWritingOptions);
      setWritingProvider(current => nextWritingOptions.providers.some(option => option.id === current)
        ? current
        : nextWritingOptions.defaultProvider);
      const nextContextPresetGroups = leadEmailContextPresetGroups(response && response.contextPresets, LEAD_EMAIL_CONTEXT_PRESETS);
      const nextContextPresetDefaults = leadEmailContextPresetDefaults(response && response.contextPresetDefaults, LEAD_EMAIL_CONTEXT_PRESET_DEFAULTS);
      draftInputRevisionRef.current += 1;
      setDraft(null);
      setSubject('');
      setBodyText('');
      setNotice('');
      setContextPresetGroups(nextContextPresetGroups);
      setContextPresetDefaults(nextContextPresetDefaults);
      setContextPresetState(current => leadEmailReconcileContextPresetState(
        situationRef.current,
        nextContextPresetGroups,
        nextContextPresetDefaults,
        current
      ));
      setOptionSets(current => ({
        contactBases: leadEmailOptionRows(response && response.contactBases, current.contactBases),
        situations: leadEmailOptionRows(response && response.situations, current.situations),
        valueAngles: leadEmailOptionRows(response && response.valueAngles, current.valueAngles),
        offers: leadEmailOptionRows(response && response.offers, current.offers),
        ctas: leadEmailOptionRows(response && response.ctas, current.ctas),
        tones: leadEmailOptionRows(response && response.tones, current.tones),
      }));
    }).catch(err => {
      if (!cancelled) setOptionsError(err && err.message ? err.message : 'Could not load current retailer pricing.');
    });
    return () => {
      cancelled = true;
    };
  }, [lead && lead.id, live]);

  const invalidateDraft = () => {
    draftInputRevisionRef.current += 1;
    setDraft(null);
    setSubject('');
    setBodyText('');
    setNotice('');
  };
  const setContactField = (key, value) => {
    setContact(current => ({ ...current, [key]: value }));
    setPermissionConfirmed(false);
    setError('');
    invalidateDraft();
  };
  const setContextField = (group, value) => {
    setContextPresetState(current => leadEmailSetCustomContextValue(current, group, value));
    invalidateDraft();
  };
  const setContextPresetChoice = (group, optionId) => {
    setContextPresetState(current => leadEmailSelectContextPreset(current, group, situation, optionId));
    invalidateDraft();
  };
  const setBuilderChoice = (setter, value) => {
    setter(value);
    invalidateDraft();
  };
  const selectSituation = value => {
    situationRef.current = value;
    setSituation(value);
    const defaults = LEAD_EMAIL_SITUATION_DEFAULTS[value] || { offer: 'sample_drop', cta: 'send_samples' };
    setOffer(defaults.offer);
    setCta(usesDistributor ? 'distributor_ordering' : defaults.cta);
    setValueAngle(current => usesDistributor
      ? 'distributor_launch'
      : value === 'pricing_requested'
        ? 'pricing_only'
        : current === 'pricing_only' || current === 'distributor_launch'
          ? 'better_alternative'
          : current);
    const nextContextState = leadEmailContextPresetState(value, contextPresetGroups, contextPresetDefaults);
    setContextPresetState(nextContextState);
    invalidateDraft();
  };
  const setDistributorMode = enabled => {
    setUsesDistributor(enabled);
    if (enabled) {
      setValueAngle('distributor_launch');
      setCta('distributor_ordering');
    } else {
      const defaults = LEAD_EMAIL_SITUATION_DEFAULTS[situation] || { cta: 'send_samples' };
      setValueAngle(situation === 'pricing_requested' ? 'pricing_only' : 'better_alternative');
      setCta(defaults.cta);
    }
    invalidateDraft();
  };
  const updateDistributorName = value => {
    setDistributorName(value);
    invalidateDraft();
  };
  const setPricingChoice = (setter, value) => {
    setter(value);
    invalidateDraft();
  };
  const toggleOperationalDetail = id => {
    setOperationalDetailIds(current => current.includes(id)
      ? current.filter(value => value !== id)
      : [...current, id]);
    invalidateDraft();
  };
  const setReviewedSendField = (setter, value) => {
    draftInputRevisionRef.current += 1;
    if (sendAttemptedRef.current) {
      requestIdRef.current = leadEmailClientRequestId(lead.id);
      sendAttemptedRef.current = false;
    }
    setter(value);
  };
  const selectContact = value => {
    draftInputRevisionRef.current += 1;
    if (value === 'new') {
      setContactIndex('new');
      setContact({ id: '', firstName: '', lastName: '', title: '', email: '', phone: '', primary: false, contactBasis: '', contactBasisDetail: '', basisObtainedAt: '' });
    } else {
      const index = Number(value);
      setContactIndex(index);
      setContact({ ...contacts[index] });
    }
    setDraft(null);
    setSubject('');
    setBodyText('');
    setPermissionConfirmed(false);
  };

  const apiInput = () => ({
    leadId: lead.id,
    contact: {
      ...(leadEmailClean(contact.id) ? { id: leadEmailClean(contact.id) } : {}),
      firstName: leadEmailClean(contact.firstName),
      lastName: leadEmailClean(contact.lastName),
      title: leadEmailClean(contact.title),
      email,
      phone: leadEmailClean(contact.phone),
      primary: Boolean(contact.primary),
      contactBasis: leadEmailClean(contact.contactBasis),
      ...(leadEmailContactBasisDetailText(contact.contactBasisDetail) ? { contactBasisDetail: leadEmailContactBasisDetailText(contact.contactBasisDetail) } : {}),
      basisObtainedAt: leadEmailClean(contact.basisObtainedAt),
    },
    situation,
    valueAngle,
    offer,
    ...(situation === 'pricing_requested' ? {
      pricing: {
        priceModelName,
        msrp: Number(msrp),
      },
      operationalDetailIds,
    } : {}),
    context: Object.fromEntries(Object.entries(context).map(([key, value]) => [key, leadEmailClean(value)])),
    cta,
    distributor: {
      enabled: usesDistributor,
      ...(usesDistributor ? { name: leadEmailClean(distributorName) } : {}),
    },
    tone,
    ...(writingProvider ? { writingProvider } : {}),
  });

  const loadHistory = async () => {
    if (!lead || !lead.id) return;
    if (!live) {
      setHistory([]);
      setHistoryCapabilities({ webhookConfigured: false, engagementTrackingConfigured: false });
      setHistoryLoading(false);
      setHistoryError('');
      return;
    }
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await rpApi(`/api/leads/email/history?leadId=${encodeURIComponent(lead.id)}`);
      setHistory(leadEmailHistoryRows(response));
      setHistoryCapabilities({
        webhookConfigured: Boolean(response && response.webhookConfigured),
        engagementTrackingConfigured: Boolean(response && response.engagementTrackingConfigured),
      });
    } catch (err) {
      setHistory([]);
      setHistoryCapabilities({ webhookConfigured: false, engagementTrackingConfigured: false });
      setHistoryError(err && err.message ? err.message : 'Could not load tracked messages.');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [lead && lead.id, historyReload]);

  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape' && !busy && !contactSaving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, contactSaving, onClose]);

  const saveContactDetails = async () => {
    setError('');
    setNotice('');
    if (!live) {
      setError('Sign in to save structured contact details.');
      return;
    }
    if (!email || invalidEmail || suspiciousContactName || !contact.contactBasis || missingContactBasisDetail || missingBasisObtainedAt || !permissionConfirmed) {
      setError(!email
        ? 'Add the retailer contact’s email address.'
        : invalidEmail
          ? 'Enter a valid retailer email address.'
          : suspiciousContactName
            ? 'Use a person’s name or clear the name fields for a neutral greeting.'
            : !contact.contactBasis
              ? 'Choose why Ranch Power may email this person.'
            : missingContactBasisDetail
              ? contactBasisDetailError
              : missingBasisObtainedAt
                ? 'Record the date when Ranch Power received this address or contact basis.'
                : 'Confirm that the documented contact basis is accurate.');
      return;
    }
    setContactSaving(true);
    try {
      const response = await rpApi('/api/leads/contacts/upsert', {
        method: 'POST',
        body: JSON.stringify({
          leadId: lead.id,
          contact: {
            ...(leadEmailClean(contact.id) ? { id: leadEmailClean(contact.id) } : {}),
            firstName: leadEmailClean(contact.firstName),
            lastName: leadEmailClean(contact.lastName),
            title: leadEmailClean(contact.title),
            email,
            phone: leadEmailClean(contact.phone),
            contactBasis: leadEmailClean(contact.contactBasis),
            ...(leadEmailContactBasisDetailText(contact.contactBasisDetail) ? { contactBasisDetail: leadEmailContactBasisDetailText(contact.contactBasisDetail) } : {}),
            basisObtainedAt: leadEmailClean(contact.basisObtainedAt),
            primary: Boolean(contact.primary),
            active: true,
          },
          permissionConfirmed: true,
        }),
      });
      invalidateDraft();
      if (response && response.contact) {
        const savedContact = leadEmailNormalizeContact(response.contact, 0);
        const returnedContacts = Array.isArray(response.contacts) && response.contacts.length
          ? leadEmailContactsForLead({ ...lead, contacts: response.contacts })
          : [
              ...contacts.filter(row => row.id !== savedContact.id && row.email !== savedContact.email),
              savedContact,
            ];
        const savedIndex = Math.max(0, returnedContacts.findIndex(row =>
          (savedContact.id && row.id === savedContact.id) || row.email === savedContact.email
        ));
        setContacts(returnedContacts);
        setContactIndex(savedIndex);
        setContact(returnedContacts[savedIndex] || savedContact);
      }
      setNotice('Structured contact details saved.');
      if (typeof onSent === 'function') onSent(response);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save contact details.');
    } finally {
      setContactSaving(false);
    }
  };

  const preview = async () => {
    setError('');
    setNotice('');
    if (!live) {
      setError('Sign in to build a server-validated retailer email.');
      return;
    }
    if (!email || invalidEmail || !contact.contactBasis || missingContactBasisDetail || missingBasisObtainedAt || suspiciousContactName || pricingInvalid || distributorNameError) {
      setError(!email
        ? 'Add the retailer contact’s email address.'
        : invalidEmail
          ? 'Enter a valid retailer email address.'
          : suspiciousContactName
            ? 'That looks like a business name, not a person. Add the contact’s real name or clear it to use “Hi there.”'
            : !contact.contactBasis
              ? 'Choose why Ranch Power may email this person.'
            : missingContactBasisDetail
              ? contactBasisDetailError
            : missingBasisObtainedAt
              ? 'Record when Ranch Power received this address or contact basis.'
            : pricingInvalid
              ? 'Choose an active price model and an MSRP between $1.00 and $25.00.'
            : distributorNameError
              ? distributorNameError
            : 'Review the contact details.');
      return;
    }
    const previewRevision = draftInputRevisionRef.current;
    const previewInput = apiInput();
    setBusy('preview');
    try {
      const response = await rpApi('/api/leads/email/preview', {
        method: 'POST',
        body: JSON.stringify(previewInput),
      });
      if (draftInputRevisionRef.current !== previewRevision) {
        setNotice('Email inputs changed while the draft was building. Build again to use the latest selections.');
        return;
      }
      const nextDraft = leadEmailResponseDraft(response);
      if (!nextDraft.subject || !nextDraft.bodyText) throw new Error('The email builder returned an incomplete draft.');
      const returnedOptions = response && response.options || {};
      const returnedPricing = returnedOptions.pricing || {};
      const returnedWriting = leadEmailWritingOptions(returnedOptions.writing);
      const returnedContextPresetGroups = leadEmailContextPresetGroups(returnedOptions.contextPresets, contextPresetGroups);
      const returnedContextPresetDefaults = leadEmailContextPresetDefaults(returnedOptions.contextPresetDefaults, contextPresetDefaults);
      setOptionSets(current => ({
        contactBases: leadEmailOptionRows(returnedOptions.contactBases, current.contactBases),
        situations: leadEmailOptionRows(returnedOptions.situations, current.situations),
        valueAngles: leadEmailOptionRows(returnedOptions.valueAngles, current.valueAngles),
        offers: leadEmailOptionRows(returnedOptions.offers, current.offers),
        ctas: leadEmailOptionRows(returnedOptions.ctas, current.ctas),
        tones: leadEmailOptionRows(returnedOptions.tones, current.tones),
      }));
      const returnedPriceModels = leadEmailPriceModelRows(returnedPricing.priceModels);
      if (returnedPriceModels.length) setPriceModels(returnedPriceModels);
      if (Array.isArray(returnedPricing.msrpPresets) && returnedPricing.msrpPresets.length) {
        setMsrpPresets(returnedPricing.msrpPresets.map(Number).filter(value => Number.isFinite(value) && value > 0));
      }
      setOperationalDetailOptions(leadEmailOptionRows(returnedPricing.operationalDetails, operationalDetailOptions));
      if (returnedPricing.containerFeePolicy) setContainerFeePolicy(returnedPricing.containerFeePolicy);
      setWritingOptions(returnedWriting);
      setWritingProvider(current => returnedWriting.providers.some(option => option.id === current)
        ? current
        : returnedWriting.defaultProvider);
      setContextPresetGroups(returnedContextPresetGroups);
      setContextPresetDefaults(returnedContextPresetDefaults);
      setContextPresetState(current => leadEmailReconcileContextPresetState(
        situationRef.current,
        returnedContextPresetGroups,
        returnedContextPresetDefaults,
        current
      ));
      if (response && response.contact) {
        const normalizedContact = leadEmailNormalizeContact(response.contact, 0);
        setContact(current => ({
          ...current,
          ...Object.fromEntries(Object.entries(normalizedContact).filter(([, value]) => leadEmailClean(value))),
        }));
      }
      setDraft(nextDraft);
      setSubject(nextDraft.subject);
      setBodyText(nextDraft.bodyText);
      requestIdRef.current = leadEmailClientRequestId(lead.id);
      sendAttemptedRef.current = false;
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not build this email.');
    } finally {
      setBusy('');
    }
  };

  const send = async () => {
    setError('');
    setNotice('');
    if (!draft || !leadEmailClean(subject) || !leadEmailClean(bodyText)) {
      setError('Build and review the email before sending.');
      return;
    }
    if (subject.length > LEAD_EMAIL_SUBJECT_LIMIT || bodyText.length > LEAD_EMAIL_BODY_LIMIT) {
      setError(subject.length > LEAD_EMAIL_SUBJECT_LIMIT
        ? `Shorten the subject to ${LEAD_EMAIL_SUBJECT_LIMIT} characters or fewer.`
        : `Shorten the message to ${LEAD_EMAIL_BODY_LIMIT.toLocaleString()} characters or fewer.`);
      return;
    }
    if (!email || invalidEmail || invalidCc.length || ccIncludesRecipient || tooManyCc) {
      setError(invalidCc.length
        ? `Fix the invalid CC address${invalidCc.length > 1 ? 'es' : ''}: ${invalidCc.join(', ')}`
        : ccIncludesRecipient
          ? 'Remove the primary recipient from CC.'
          : tooManyCc
            ? 'Use no more than 10 CC recipients.'
            : 'Enter a valid retailer email address.');
      return;
    }
    if (!contact.contactBasis || missingContactBasisDetail || missingBasisObtainedAt || !permissionConfirmed) {
      setError(missingContactBasisDetail ? contactBasisDetailError : 'Confirm the contact basis before sending.');
      return;
    }
    setBusy('send');
    sendAttemptedRef.current = true;
    try {
      const response = await rpApi('/api/leads/email/send', {
        method: 'POST',
        headers: { 'Idempotency-Key': requestIdRef.current },
        body: JSON.stringify({
          ...apiInput(),
          clientRequestId: requestIdRef.current,
          final: {
            subject: subject,
            bodyText: bodyText,
          },
          subject: subject,
          bodyText: bodyText,
          cc,
          pricingSnapshot: draft.pricing || null,
          permissionConfirmed: true,
          saveContact: true,
          templateKey: draft.templateKey || undefined,
          templateVersion: draft.templateVersion || undefined,
        }),
      });
      const sentMessage = response && (response.message || response.email || response);
      const sentTo = leadEmailClean(sentMessage && (sentMessage.toEmail || sentMessage.to || sentMessage.recipientEmail)) || email;
      setNotice(`Email sent to ${sentTo}.`);
      setTab('history');
      setHistoryReload(value => value + 1);
      requestIdRef.current = leadEmailClientRequestId(lead.id);
      sendAttemptedRef.current = false;
      if (typeof onSent === 'function') onSent(response);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not send this email.');
    } finally {
      setBusy('');
    }
  };

  const markReplied = async (messageId, outcome = 'replied', note = '', affectedEmail = '') => {
    if (!messageId || marking) return false;
    setMarking(messageId);
    setHistoryError('');
    try {
      await rpApi('/api/leads/email/mark-replied', {
        method: 'POST',
        body: JSON.stringify({
          leadId: lead.id,
          messageId,
          outcome,
          note: leadEmailClean(note),
          ...(leadEmailClean(affectedEmail) ? { affectedEmail: leadEmailClean(affectedEmail).toLowerCase() } : {}),
        }),
      });
      setHistoryReload(value => value + 1);
      if (typeof onSent === 'function') onSent();
      return true;
    } catch (err) {
      setHistoryError(err && err.message ? err.message : 'Could not record the reply.');
      return false;
    } finally {
      setMarking('');
    }
  };

  const contactLabel = row => {
    const name = [row.firstName, row.lastName].filter(Boolean).join(' ');
    return [name || 'Unnamed contact', row.title, row.email].filter(Boolean).join(' · ');
  };
  const qualityChecks = draft && draft.qualityChecks || [];
  const draftContentEdited = Boolean(draft && (
    leadEmailClean(subject) !== leadEmailClean(draft.subject)
    || leadEmailClean(bodyText) !== leadEmailClean(draft.bodyText)
  ));
  const blockedChecks = qualityChecks.filter(leadEmailCheckBlocked);
  const complianceIndicatesBlocked = Boolean(draft && draft.compliance && (
    draft.compliance.passed === false
    || draft.compliance.allowed === false
    || draft.compliance.canSend === false
    || draft.compliance.blocked === true
  ));
  const blockedQualityCheck = !draftContentEdited && blockedChecks.length > 0;
  const complianceBlocked = !draftContentEdited && complianceIndicatesBlocked;
  const contactCanSave = Boolean(live && email && !invalidEmail && !suspiciousContactName && contact.contactBasis && !missingContactBasisDetail && !missingBasisObtainedAt && permissionConfirmed && !contactSaving);
  const canSend = Boolean(allowSend && draft && leadEmailClean(subject) && leadEmailClean(bodyText) && email && !invalidEmail && !invalidCc.length && !ccIncludesRecipient && !tooManyCc && !suspiciousContactName && contact.contactBasis && !missingContactBasisDetail && !missingBasisObtainedAt && !blockedQualityCheck && !complianceBlocked && permissionConfirmed && !busy);

  return (
    <div className="rp-lead-email-backdrop" onMouseDown={() => !busy && onClose()} role="presentation">
      <section className="rp-lead-email-modal" onMouseDown={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Build email for ${lead && lead.name}`}>
        <header className="rp-lead-email-header">
          <div style={{ minWidth: 0 }}>
            <div className="eyebrow" style={{ color: 'oklch(0.82 0.04 70)' }}>Retailer outreach</div>
            <h2 className="disp" style={{ color: '#fff', fontSize: 23, margin: '5px 0 0', textTransform: 'uppercase' }}>Email {lead && lead.name}</h2>
            <div style={{ color: 'oklch(0.82 0.02 235)', fontSize: 12.5, marginTop: 4 }}>{allowSend ? 'Context-built, team-edited, and tracked in the pipeline.' : 'Review contact evidence and history, or experiment with a local draft preview.'}</div>
          </div>
          <button className="rp-lead-email-close" disabled={Boolean(busy)} onClick={onClose} aria-label="Close email builder"><Icon name="x" size={18} /></button>
        </header>

        <div className="rp-lead-email-tabs" role="tablist" aria-label="Email builder sections">
          <button role="tab" aria-selected={tab === 'build'} className={tab === 'build' ? 'active' : ''} onClick={() => setTab('build')}><Icon name="pen" size={14} />{allowSend ? 'Build & send' : 'Local preview'}</button>
          <button role="tab" aria-selected={tab === 'history'} className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}><Icon name="trend" size={14} />History{history.length ? ` (${history.length})` : ''}</button>
        </div>

        {notice && <div className="rp-lead-email-notice" role="status"><Icon name="check" size={15} />{notice}</div>}
        {!live && <div className="rp-lead-email-notice" role="status" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}><Icon name="alert" size={15} />Preview mode does not generate, send, or simulate email analytics.</div>}
        {!allowSend && <div className="rp-lead-email-notice" role="status" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}><Icon name="user" size={15} />This role can review contact evidence and tracked outcomes, but it cannot send retailer email.</div>}
        {error && <div className="rp-lead-email-error" role="alert"><Icon name="alert" size={15} />{error}</div>}

        {tab === 'history' ? (
          <div className="rp-lead-email-body">
            <LeadEmailHistory lead={lead} rows={history} loading={historyLoading} error={historyError} onReload={() => setHistoryReload(value => value + 1)} onMarkReplied={markReplied} marking={marking} live={live} {...historyCapabilities} />
          </div>
        ) : (
          <div className="rp-lead-email-body">
            <div className="rp-lead-email-grid">
              <div style={{ minWidth: 0 }}>
                <section className="rp-lead-email-section">
                  <div className="rp-lead-email-section-title"><span>1</span><div><strong>Who are you emailing?</strong><small>Use a person’s name, not the retailer’s legal or business name.</small></div></div>
                  {contacts.length > 1 && (
                    <LeadEmailField label="Saved contact">
                      <select className="field" value={contactIndex} onChange={event => selectContact(event.target.value)}>
                        {contacts.map((row, index) => <option key={row.id || index} value={index}>{contactLabel(row)}</option>)}
                        <option value="new">+ Add another contact</option>
                      </select>
                    </LeadEmailField>
                  )}
                  {contacts.length === 1 && contactIndex !== 'new' && (
                    <button className="btn btn-sm" type="button" onClick={() => selectContact('new')} style={{ marginBottom: 11 }}><Icon name="plus" size={13} />Use another contact</button>
                  )}
                  <div className="rp-lead-email-two">
                    <LeadEmailField label="First name" hint={!leadEmailClean(contact.firstName) ? 'No first name means the draft will use a neutral “Hi there” greeting.' : ''}>
                      <input className="field" autoComplete="given-name" value={contact.firstName} onChange={event => setContactField('firstName', event.target.value)} placeholder="e.g. Morgan" style={{ borderColor: suspiciousContactName ? 'var(--danger)' : undefined }} />
                    </LeadEmailField>
                    <LeadEmailField label="Last name">
                      <input className="field" autoComplete="family-name" value={contact.lastName} onChange={event => setContactField('lastName', event.target.value)} placeholder="Optional" />
                    </LeadEmailField>
                  </div>
                  {storedContactLookedLikeBusiness && !leadEmailClean(contact.firstName) && !leadEmailClean(contact.lastName) && (
                    <div style={{ padding: '9px 10px', borderRadius: 9, background: 'var(--amber-soft)', color: 'var(--amber-deep)', fontSize: 11.5, fontWeight: 700, lineHeight: 1.4 }}>
                      The saved contact looked like a business name, so it was not used as a greeting. Add the person’s real name or leave both fields blank for “Hi there.”
                    </div>
                  )}
                  <div className="rp-lead-email-two">
                    <LeadEmailField label="Role">
                      <input className="field" value={contact.title} onChange={event => setContactField('title', event.target.value)} placeholder="Buyer, manager, owner…" />
                    </LeadEmailField>
                    <LeadEmailField label="Email" required>
                      <input className="field" type="email" autoComplete="email" value={contact.email} onChange={event => setContactField('email', event.target.value)} placeholder="buyer@retailer.ca" style={{ borderColor: invalidEmail ? 'var(--danger)' : undefined }} />
                    </LeadEmailField>
                  </div>
                  <div className="rp-lead-email-two">
                    <LeadEmailField label="Phone">
                      <input className="field" type="tel" autoComplete="tel" value={contact.phone || ''} onChange={event => setContactField('phone', event.target.value)} placeholder="Optional" />
                    </LeadEmailField>
                    <label className="rp-lead-email-confirm" style={{ alignSelf: 'end', minHeight: 42, alignItems: 'center' }}>
                      <input type="checkbox" checked={Boolean(contact.primary)} onChange={event => setContactField('primary', event.target.checked)} />
                      <span>Use as primary lead contact</span>
                    </label>
                  </div>
                  <LeadEmailField label="Why may we email this person?" required hint={selectedBasis && selectedBasis.hint}>
                    <select className="field" value={contact.contactBasis} onChange={event => setContactField('contactBasis', event.target.value)}>
                      <option value="">Choose the contact basis…</option>
                      {optionSets.contactBases.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                  </LeadEmailField>
                  {suspiciousContactName && <div style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700, marginTop: -5, marginBottom: 10 }}>This looks like the retailer’s name. Enter a person’s name, or clear both name fields to use “Hi there.”</div>}
                  {contact.contactBasis && (
                    <>
                      <LeadEmailField label="Contact-basis note" required={contactBasisDetailRequired} hint={contact.contactBasis === 'retailer_staff_direction' ? 'This permits one introduction only. Do not schedule an email follow-up until a human reply is recorded or a stronger basis is saved.' : contact.contactBasis === 'express_consent' ? 'Use this only for an opt-in to ongoing Ranch Power commercial or marketing email—not one requested follow-up.' : contact.contactBasis === 'inbound_request' ? 'Describe the specific email, pricing, samples, or information the person requested.' : 'A short factual note keeps the outreach auditable.'}>
                        <input
                          aria-describedby={missingContactBasisDetail && leadEmailClean(contact.contactBasisDetail) ? 'lead-email-contact-basis-error' : undefined}
                          aria-invalid={missingContactBasisDetail}
                          className="field"
                          maxLength={600}
                          value={contact.contactBasisDetail}
                          onChange={event => setContactField('contactBasisDetail', event.target.value)}
                          placeholder={contact.contactBasis === 'retailer_staff_direction' ? 'e.g. Alex at the front desk provided Morgan’s email because Morgan handles new orders' : contact.contactBasis === 'express_consent' ? 'e.g. Morgan agreed by phone to receive ongoing Ranch Power commercial email' : contact.contactBasis === 'inbound_request' ? 'e.g. Taylor asked me during today’s visit to email the requested pricing' : 'e.g. Email provided during Tuesday’s store visit'}
                          style={{ borderColor: missingContactBasisDetail ? 'var(--danger)' : undefined }}
                        />
                      </LeadEmailField>
                      {missingContactBasisDetail && leadEmailClean(contact.contactBasisDetail) && (
                        <div id="lead-email-contact-basis-error" role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700, lineHeight: 1.45, marginTop: -5, marginBottom: 10 }}>
                          <div>{contactBasisDetailError}</div>
                          {suggestedContactBasis === 'inbound_request' && (
                            <button className="btn btn-sm" type="button" onClick={() => setContactField('contactBasis', 'inbound_request')} style={{ marginTop: 8 }}>
                              Use “Requested this email or information”
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {contact.contactBasis && (
                    <LeadEmailField label="Date received" required hint="Record when the address or documented basis was originally provided—not today’s verification date.">
                      <input className="field" type="date" max={leadEmailLocalDateYmd()} value={leadEmailClean(contact.basisObtainedAt)} onChange={event => setContactField('basisObtainedAt', event.target.value)} style={{ borderColor: missingBasisObtainedAt ? 'var(--danger)' : undefined }} />
                    </LeadEmailField>
                  )}
                  {contact.contactBasis && missingBasisObtainedAt && contact.basisObtainedAt && <div style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700, marginTop: -5, marginBottom: 10 }}>This contact basis is invalid or outside its approved age window. Record fresh evidence before sending.</div>}
                  <label className="rp-lead-email-confirm">
                    <input type="checkbox" checked={permissionConfirmed} onChange={event => { setPermissionConfirmed(event.target.checked); setError(''); }} />
                    <span>{contactBasisConfirmationText}</span>
                  </label>
                  <button className="btn" type="button" disabled={!contactCanSave} onClick={saveContactDetails} style={{ width: '100%', opacity: contactCanSave ? 1 : 0.6 }}>
                    <Icon name="check" size={14} />{contactSaving ? 'Saving contact…' : 'Save contact details'}
                  </button>
                </section>

                <section className="rp-lead-email-section">
                  <div className="rp-lead-email-section-title"><span>2</span><div><strong>Why are you emailing now?</strong><small>Start with the reason for this email, then adjust only the facts that make this retailer different.</small></div></div>
                  <LeadEmailField label="Reason for this email">
                    <select className="field" value={situation} onChange={event => selectSituation(event.target.value)}>
                      {optionSets.situations.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                    </select>
                  </LeadEmailField>
                  <div data-lead-email-distributor-tools="true" style={{ border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 11, padding: 12, marginBottom: 12 }}>
                    <LeadEmailField label="Working with a distributor?" required hint="Choose Yes when distributor availability should lead the subject and message.">
                      <select
                        aria-label="Working with a distributor?"
                        className="field"
                        value={usesDistributor ? 'yes' : 'no'}
                        onChange={event => setDistributorMode(event.target.value === 'yes')}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </LeadEmailField>
                    {usesDistributor && (
                      <>
                        <LeadEmailField label="Distributor name" required hint="The exact name becomes the lead subject and the main story of the email.">
                          <input
                            aria-invalid={Boolean(distributorNameError)}
                            className="field"
                            data-lead-email-distributor-name="true"
                            maxLength={50}
                            onChange={event => updateDistributorName(event.target.value)}
                            placeholder="e.g. Slopeside Supply"
                            style={{ borderColor: distributorNameError ? 'var(--danger)' : undefined }}
                            value={distributorName}
                          />
                        </LeadEmailField>
                        {distributorNameError && distributorName && <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700 }}>{distributorNameError}</div>}
                      </>
                    )}
                  </div>
                  {situation === 'pricing_requested' && (
                    <div data-lead-email-pricing-tools="true" style={{ border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 11, padding: 12, marginBottom: 12 }}>
                      <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 3 }}>Requested retailer pricing</div>
                      <div style={{ color: 'var(--muted)', fontSize: 11.25, lineHeight: 1.4, marginBottom: 11 }}>The case cost and fee policy come from the same active records used by invoicing. Only pricing approved for your account or this converted customer is shown.</div>
                      {optionsError && <div style={{ color: 'var(--danger)', fontSize: 11.25, fontWeight: 700, marginBottom: 9 }}>{optionsError}</div>}
                      <LeadEmailField label="Price model" required hint="A manager must approve any other wholesale price before it can be emailed.">
                        <select className="field" value={priceModelName} onChange={event => setPricingChoice(setPriceModelName, event.target.value)}>
                          {!priceModels.length && <option value="">Current price models unavailable</option>}
                          {priceModels.map(model => <option key={model.name} value={model.name}>{model.name} — {leadEmailMoney(model.caseCost)} / case{model.terms ? ` · ${model.terms}` : ''}</option>)}
                        </select>
                      </LeadEmailField>
                      <div style={{ marginTop: 11 }}>
                        <div className="field-label">Suggested MSRP</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 8 }}>
                          {msrpPresets.map(value => {
                            const formatted = Number(value).toFixed(2);
                            return <button key={formatted} type="button" className={`btn btn-sm${Number(msrp) === Number(value) ? ' btn-primary' : ''}`} onClick={() => setPricingChoice(setMsrp, formatted)}>{leadEmailMoney(value)}</button>;
                          })}
                        </div>
                        <input className="field" data-lead-email-custom-msrp="true" type="number" min="1" max="25" step="0.01" inputMode="decimal" value={msrp} onChange={event => setPricingChoice(setMsrp, event.target.value)} aria-label="Custom MSRP" />
                      </div>
                      {pricingQuotePreview && (
                        <div data-lead-email-margin-preview="true" style={{ marginTop: 11, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 9, padding: 10, fontSize: 11.5, lineHeight: 1.5 }}>
                          <div><strong>Merchandise:</strong> {leadEmailMoney(pricingQuotePreview.caseCost)} / 12-pack · {leadEmailMoney(pricingQuotePreview.costPerCan)} / can</div>
                          <div><strong>At {leadEmailMoney(pricingQuotePreview.msrp)} MSRP:</strong> {leadEmailMoney(pricingQuotePreview.grossProfitPerCan)} gross profit / can · {pricingQuotePreview.grossMarginPercent.toFixed(1)}% gross margin</div>
                          <div style={{ color: 'var(--muted)', marginTop: 3 }}>{containerFeePolicy && containerFeePolicy.containerFeeNote || 'Applicable container fees and taxes are extra.'} Margin uses merchandise cost before the retailer's other costs.</div>
                        </div>
                      )}
                      {operationalDetailOptions.length > 0 && (
                        <div style={{ marginTop: 11 }}>
                          <div className="field-label">Useful details to mention</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                            {operationalDetailOptions.map(option => (
                              <label key={option.id} className="rp-lead-email-confirm" style={{ margin: 0, padding: '7px 9px', flex: '1 1 180px' }}>
                                <input type="checkbox" checked={operationalDetailIds.includes(option.id)} onChange={() => toggleOperationalDetail(option.id)} />
                                <span><strong style={{ display: 'block', color: 'var(--ink)' }}>{option.label}</strong>{option.hint}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="rp-lead-email-two">
                    {usesDistributor ? (
                      <LeadEmailField label="Email focus" hint="The distributor leads the subject and opening; the approved Ranch Power overview follows.">
                        <div className="field" data-lead-email-distributor-focus="true">Distributor launch overview</div>
                      </LeadEmailField>
                    ) : (
                      <LeadEmailField label={situation === 'pricing_requested' ? 'Product recap' : 'Most relevant Ranch Power angle'}>
                        <select className="field" value={valueAngle} onChange={event => setBuilderChoice(setValueAngle, event.target.value)}>
                          {optionSets.valueAngles
                            .filter(option => situation === 'pricing_requested' || option.id !== 'pricing_only')
                            .filter(option => option.id !== 'distributor_launch')
                            .map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                        </select>
                      </LeadEmailField>
                    )}
                    <LeadEmailField label="Useful offer">
                      <select className="field" value={offer} onChange={event => setBuilderChoice(setOffer, event.target.value)}>
                        {optionSets.offers.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                      </select>
                    </LeadEmailField>
                  </div>
                  <div className="rp-lead-email-two">
                    {usesDistributor ? (
                      <LeadEmailField label="One easy next step" hint="The draft asks whether local distributor ordering makes Ranch Power worth a look.">
                        <div className="field">Order through distributor</div>
                      </LeadEmailField>
                    ) : (
                      <LeadEmailField label="One easy next step">
                        <select className="field" value={cta} onChange={event => setBuilderChoice(setCta, event.target.value)}>
                          {optionSets.ctas.filter(option => option.id !== 'distributor_ordering').map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                        </select>
                      </LeadEmailField>
                    )}
                    <LeadEmailField label="Tone">
                      <select className="field" value={tone} onChange={event => setBuilderChoice(setTone, event.target.value)}>
                        {optionSets.tones.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                      </select>
                    </LeadEmailField>
                  </div>
                  {writingOptions.providers.length > 0 && (
                    <LeadEmailField label="Writing style" hint="This changes the generated wording only. After building, edit the subject and message freely.">
                      <select className="field" value={writingProvider || writingOptions.defaultProvider} onChange={event => setBuilderChoice(setWritingProvider, event.target.value)}>
                        {writingOptions.providers.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                      </select>
                    </LeadEmailField>
                  )}
                  <div>
                    <div className="field-label" style={{ marginBottom: 3 }}>Sales context</div>
                    <div style={{ color: 'var(--faint)', fontSize: 11.25, lineHeight: 1.4, marginBottom: 9 }}>
                      Useful defaults are already selected. Change only what makes this retailer different.
                    </div>
                    <div className="rp-lead-email-two">
                      {contextPresetGroups
                        .filter(group => group.contextKey !== 'objection' || situation === 'objection_followup' || situation === 'no_response' || situation === 'reactivation')
                        .map(group => (
                          <LeadEmailContextPresetField
                            group={group}
                            key={group.id}
                            onCustom={setContextField}
                            onPreset={setContextPresetChoice}
                            presetId={contextPresetIds[group.id] || ''}
                            situation={situation}
                            value={context[group.contextKey] || ''}
                          />
                        ))}
                    </div>
                  </div>
                  <button className="btn btn-primary btn-lg" disabled={busy === 'preview' || !live} onClick={preview} style={{ width: '100%', marginTop: 2, opacity: live ? 1 : 0.6 }}>
                    <Icon name="star" size={16} />{busy === 'preview' ? 'Building draft…' : !live ? 'Sign in to build draft' : draft ? 'Rebuild from context' : 'Build email draft'}
                  </button>
                </section>
              </div>

              <div style={{ minWidth: 0 }}>
                <section className="rp-lead-email-section rp-lead-email-preview">
                  <div className="rp-lead-email-section-title"><span>3</span><div><strong>{allowSend ? 'Make it yours' : 'Local draft preview'}</strong><small>{allowSend ? 'Edit the subject and message freely. Your reviewed copy is sent as written.' : 'Experiment here if useful. These subject and body edits are not saved.'}</small></div></div>
                  {!draft ? (
                    <div className="rp-lead-email-empty" style={{ minHeight: 220 }}>
                      <Icon name="doc" size={24} />
                      <div style={{ color: 'var(--ink)', fontWeight: 800 }}>Your draft will appear here</div>
                      <div>Answer the useful context questions, then build a concise starting point.</div>
                    </div>
                  ) : (
                    <>
                      <div
                        role="status"
                        style={{
                          alignItems: 'flex-start',
                          background: draft.generation.mode === 'template_fallback' ? 'var(--warn-bg)' : 'var(--info-bg)',
                          border: '1px solid var(--line)',
                          borderRadius: 9,
                          color: draft.generation.mode === 'template_fallback' ? 'var(--amber-deep)' : 'var(--info)',
                          display: 'flex',
                          gap: 8,
                          marginBottom: 12,
                          padding: '9px 10px',
                        }}
                      >
                        <Icon name={draft.generation.mode === 'template_fallback' ? 'alert' : 'star'} size={14} style={{ flex: '0 0 auto', marginTop: 1 }} />
                        <div style={{ minWidth: 0 }}>
                          <strong style={{ display: 'block', fontSize: 11.5 }}>{leadEmailGenerationLabel(draft.generation, writingOptions)}</strong>
                          {draft.generation.notice && <span style={{ display: 'block', fontSize: 10.75, lineHeight: 1.4, marginTop: 2 }}>{draft.generation.notice}</span>}
                        </div>
                      </div>
                      {draft.subjectOptions.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div className="field-label">Subject suggestions</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                            {draft.subjectOptions.map(option => <button key={option} type="button" className="btn btn-sm" onClick={() => setReviewedSendField(setSubject, option)} style={{ height: 'auto', whiteSpace: 'normal', textAlign: 'left' }}>{option}</button>)}
                          </div>
                        </div>
                      )}
                      <LeadEmailField label="Subject" required>
                        <input className="field" maxLength={LEAD_EMAIL_SUBJECT_LIMIT} value={subject} onChange={event => setReviewedSendField(setSubject, event.target.value)} />
                      </LeadEmailField>
                      <LeadEmailField label="Message" required hint={`${bodyText.length.toLocaleString()} / ${LEAD_EMAIL_BODY_LIMIT.toLocaleString()} characters · Plain-text style keeps retailer outreach personal.`}>
                        <textarea className="field rp-lead-email-message" rows="14" maxLength={LEAD_EMAIL_BODY_LIMIT} value={bodyText} onChange={event => setReviewedSendField(setBodyText, event.target.value)} />
                      </LeadEmailField>
                      {draftContentEdited && (
                        <div style={{ alignItems: 'flex-start', background: 'color-mix(in srgb, var(--ok) 9%, white)', border: '1px solid var(--line)', borderRadius: 9, color: 'var(--ok)', display: 'flex', fontSize: 11.25, fontWeight: 700, gap: 7, lineHeight: 1.45, marginBottom: 11, padding: '8px 9px' }}>
                          <Icon name="check" size={13} style={{ flex: '0 0 auto', marginTop: 1 }} />
                          <span>Your edits are the final copy. Approved pricing and payment facts are checked again before sending; surrounding prose is not otherwise rescanned.</span>
                        </div>
                      )}
                      {!draftContentEdited && qualityChecks.length > 0 && (
                        <div className="rp-lead-email-checks">
                          {qualityChecks.map((check, index) => {
                            const text = typeof check === 'string' ? check : leadEmailClean(check && (check.detail || check.label || check.message || check.name));
                            const status = typeof check === 'string' ? 'pass' : leadEmailClean(check && check.status).toLowerCase();
                            const passed = typeof check === 'string' || status === 'pass' || (!status && check && check.passed !== false);
                            const blocked = status === 'blocked';
                            return text ? <span key={`${text}-${index}`} style={{ color: passed ? 'var(--ok)' : blocked ? 'var(--danger)' : 'var(--amber-deep)' }}><Icon name={passed ? 'check' : 'alert'} size={12} />{text}</span> : null;
                          })}
                        </div>
                      )}
                      {draft.suggestedFollowUpAt && <div style={{ color: 'var(--muted)', fontSize: 11.75, lineHeight: 1.45, marginTop: 10 }}><strong>Suggested follow-up:</strong> {leadEmailFormatDate(draft.suggestedFollowUpAt)}</div>}
                      <div style={{ height: 1, background: 'var(--line)', margin: '16px 0' }} />
                      <LeadEmailField label="CC" hint="Optional. Separate addresses with commas. Every external CC must be a saved contact with its own documented stronger basis and date received; retailer staff direction is never enough for CC.">
                        <input className="field" type="text" value={ccText} onChange={event => {
                          setReviewedSendField(setCcText, event.target.value);
                          setPermissionConfirmed(false);
                        }} placeholder="owner@retailer.ca, partner@retailer.ca" style={{ borderColor: invalidCc.length ? 'var(--danger)' : undefined }} />
                      </LeadEmailField>
                      {invalidCc.length > 0 && <div style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700, marginTop: -5, marginBottom: 10 }}>Invalid: {invalidCc.join(', ')}</div>}
                      {ccIncludesRecipient && <div style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700, marginTop: -5, marginBottom: 10 }}>The primary recipient is already in To. Remove it from CC.</div>}
                      {tooManyCc && <div style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700, marginTop: -5, marginBottom: 10 }}>Use no more than 10 CC recipients.</div>}
                      {cc.length > 0 && (
                        <label className="rp-lead-email-confirm" style={{ marginTop: 0, marginBottom: 11 }}>
                          <input type="checkbox" checked={permissionConfirmed} onChange={event => setPermissionConfirmed(event.target.checked)} />
                          <span>{contactBasisConfirmationText} Every external CC has its own saved stronger basis and date received; none were scraped or guessed.</span>
                        </label>
                      )}
                      {(blockedQualityCheck || complianceBlocked) && <div style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 750 }}>{draftContentEdited ? 'A contact or sending-policy check must be resolved before sending.' : 'Resolve the blocked draft check and rebuild before sending.'}</div>}
                      <div className="rp-lead-email-reply-note">
                        <Icon name="mail" size={15} />
                        <div><strong>Replies go to the signed-in sender.</strong><span>{draft.replyTo ? ` Reply-To: ${draft.replyTo}.` : ' The retailer can reply normally and continue the conversation in the sender’s Ranch Power inbox.'}</span></div>
                      </div>
                      <div style={{ color: 'var(--faint)', fontSize: 11.25, lineHeight: 1.45 }}>Ranch Power’s required sender identification and unsubscribe footer is added by the server at send time and is not removed when this message is edited.</div>
                      {allowSend ? (
                        <>
                          {!permissionConfirmed && <div style={{ color: 'var(--amber-deep)', fontSize: 11.5, fontWeight: 750 }}>Confirm the documented contact basis in step 1 before sending.</div>}
                          <button className="btn btn-primary btn-lg" disabled={!canSend} onClick={send} style={{ width: '100%', opacity: canSend ? 1 : 0.6 }}>
                            <Icon name="mail" size={16} />{busy === 'send' ? 'Sending…' : `Send to ${leadEmailClean(contact.firstName) || email}`}
                          </button>
                        </>
                      ) : (
                        <div className="rp-lead-email-confirm" style={{ background: 'var(--surface-2)' }}>
                          <Icon name="user" size={16} style={{ flex: '0 0 auto' }} />
                          <span><strong style={{ display: 'block', color: 'var(--ink)' }}>This role cannot send retailer email.</strong>The local preview is not saved or used as another person’s signature.</span>
                        </div>
                      )}
                    </>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

Object.assign(window, {
  LeadEmailComposer,
  LeadEmailPulse,
  LEAD_EMAIL_CONTACT_BASES,
  LEAD_EMAIL_CONTEXT_PRESET_DEFAULTS,
  LEAD_EMAIL_CONTEXT_PRESETS,
  LEAD_EMAIL_SITUATIONS,
  LEAD_EMAIL_VALUE_ANGLES,
  LEAD_EMAIL_OFFERS,
  LEAD_EMAIL_CTAS,
  LEAD_EMAIL_TONES,
  leadEmailContactsForLead,
});


/* ===== invoice_data.jsx ===== */
/* ============================================================
   RANCH POWER - Invoice adapter for live order-backed billing
   ============================================================ */
(function () {
  const RP = window.RP;
  const GST = 0.05;
  const PST = 0.07;
  const READY_TO_INVOICE_STATUSES = new Set(['Delivered', 'Invoiced', 'Paid', 'Closed']);
  const FINAL_STATUSES = new Set(['Paid', 'Closed']);
  const TERM_DAYS = { 'Prepaid': 0, 'COD': 0, 'Due on receipt': 0, 'Net 15': 15, 'Net 30': 30, 'Net 45': 45 };
  const PAY_METHODS = [
    { id: 'account_terms', label: 'Account terms', sub: 'Pay by invoice terms', icon: 'clock' },
    { id: 'bank_transfer', label: 'Bank transfer', sub: 'EFT / wire transfer', icon: 'mail' },
    { id: 'e_transfer', label: 'e-Transfer', sub: 'Interac e-Transfer', icon: 'mail' },
    { id: 'cheque', label: 'Cheque', sub: 'Reference the invoice number', icon: 'doc' },
    { id: 'credit_card', label: 'Credit card', sub: 'Authorized Stripe saved card', icon: 'card' },
    { id: 'shopify_pos', label: 'Shopify POS', sub: 'Paid at point of sale', icon: 'tap', shopify: true },
    { id: 'shopify_checkout', label: 'Shopify checkout', sub: 'Paid through Shopify', icon: 'card', shopify: true },
    { id: 'account_credit', label: 'Account credit', sub: 'Hold credit on customer account', icon: 'wallet' },
    { id: 'card', label: 'Credit card', sub: 'Authorized Stripe saved card', icon: 'card' },
    { id: 'etransfer', label: 'e-Transfer', sub: 'Interac e-Transfer', icon: 'mail' },
    { id: 'cash', label: 'Cash', sub: 'Collected on delivery', icon: 'wallet' }
  ];
  const CARD_CAPTURE = [
    { id: 'link', label: 'Payment link', sub: 'Customer pays through Shopify', icon: 'link' },
    { id: 'reader', label: 'Card reader', sub: 'Shopify POS reader', icon: 'card' },
    { id: 'tap', label: 'Tap to Pay', sub: 'Shopify POS mobile capture', icon: 'tap', mobileOnly: true },
    { id: 'manual', label: 'Manual entry', sub: 'Shopify virtual terminal', icon: 'pen' }
  ];
  const today = () => typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10);
  const ymd = value => {
    if (!value) return '';
    if (typeof rpAddDaysYmd === 'function') return rpAddDaysYmd(value, 0);
    const d = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    return d.toISOString().slice(0, 10);
  };
  const addDays = (iso, days) => {
    if (typeof rpAddDaysYmd === 'function') return rpAddDaysYmd(iso || today(), days);
    const base = iso ? new Date(iso + 'T00:00:00') : new Date();
    base.setDate(base.getDate() + Number(days || 0));
    return base.toISOString().slice(0, 10);
  };
  const daysBetween = (a, b) => Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000);
  const n = value => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const money = value => Math.round(n(value) * 100) / 100;
  const orderForInvoice = inv => inv && (inv.sourceOrder || RP.ORDERS.find(o => o.id === inv.order));
  const draftInvoiceId = order => `DRAFT-${order.id}`;
  const invoiceNumberForOrder = order => order.invoiceNumber || draftInvoiceId(order);
  const paymentMethodLabel = code => (PAY_METHODS.find(method => method.id === code) || {}).label || code || 'Account terms';
  const orderInvoiceRawStatus = order => String(order?.invoiceStatus || order?.financialStatus || order?.status || '').trim();
  const orderIsSettledWithoutInvoice = order => !order?.invoiceNumber && FINAL_STATUSES.has(orderInvoiceRawStatus(order));
  const orderNeedsInvoiceRow = order => {
    if (!order || !order.id || order.status === 'Cancelled') return false;
    if (order.invoiceNumber) return true;
    if (orderIsSettledWithoutInvoice(order)) return false;
    return READY_TO_INVOICE_STATUSES.has(order.status);
  };
  const customerInvoiceEmail = (order, customer) => {
    const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
    return String(order?.invoiceEmailTo || overrides.invoiceEmail || customer?.email || customer?.buyerEmail || '').trim();
  };
  const customerInvoiceNote = (order, customer) => {
    const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
    return String(order?.invoiceNote || overrides.invoiceNote || overrides.invoice_note || '').trim();
  };
  const customerTerms = customer => String(customer?.terms || 'Prepaid').trim() || 'Prepaid';
  const termsDueDate = (customer, issued) => {
    const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
    const overrideDays = Number(overrides.dueDays);
    if (Number.isFinite(overrideDays) && overrideDays >= 0) return addDays(issued, Math.round(overrideDays));
    const terms = customerTerms(customer);
    const match = terms.match(/net\s*(\d+)/i);
    return addDays(issued, match ? Number(match[1]) : (TERM_DAYS[terms] ?? 0));
  };

  const DEMO_INVOICES = [
    {
      id: 'INV-2026-0142', order: 'SO-1038', cust: 'C-002', terms: 'Net 15', issued: '2026-06-01', due: '2026-06-16',
      status: 'Sent', taxPst: true, discount: 0, po: '', lines: [
        { sku: 'RP-12-GC', qty: 6, price: 32, deposit: 1.2 },
        { sku: 'RP-12-KL', qty: 4, price: 32, deposit: 1.2 },
        { sku: 'RP-12-PM', qty: 2, price: 32, deposit: 1.2 }
      ],
      payments: [{ id: 'PMT-3301', method: 'card', amount: 444.48, date: '2026-06-01', by: 'Dana Whitfield', shopify: { id: 'shpfy_7c41a9d2e8b35f016a4c', order: 'SP-40912', brand: 'Visa', last4: '4242', capture: 'tap', location: 'Ranch Power POS' } }]
    },
    {
      id: 'INV-2026-0143', order: 'SO-1042', cust: 'C-001', terms: 'Net 15', issued: '2026-06-04', due: '2026-06-19',
      status: 'Sent', taxPst: true, discount: 0, po: 'PO-5582', lines: [
        { sku: 'RP-12-GC', qty: 12, price: 32, deposit: 1.2 },
        { sku: 'RP-12-PM', qty: 8, price: 32, deposit: 1.2 },
        { sku: 'RP-12-KL', qty: 4, price: 32, deposit: 1.2 }
      ],
      payments: []
    },
    {
      id: 'INV-2026-0138', order: 'SO-1040', cust: 'C-004', terms: 'Net 30', issued: '2026-05-10', due: '2026-06-09',
      status: 'Sent', taxPst: false, discount: 120, po: 'NF-22014', lines: [
        { sku: 'RP-12-GC', qty: 24, price: 32, deposit: 1.2 },
        { sku: 'RP-12-KL', qty: 18, price: 32, deposit: 1.2 },
        { sku: 'RP-12-BR', qty: 12, price: 32, deposit: 1.2 },
        { sku: 'RP-MIX-TRI', qty: 6, price: 30, deposit: 1.2 }
      ],
      payments: [{ id: 'PMT-3288', method: 'e_transfer', amount: 900, date: '2026-05-20', by: 'A. Reyes', ref: 'INTERAC-88213' }]
    },
    {
      id: 'INV-2026-0135', order: 'SO-1045', cust: 'C-005', terms: 'Net 15', issued: '2026-05-12', due: '2026-05-27',
      status: 'Sent', taxPst: true, discount: 0, po: '', lines: [
        { sku: 'RP-12-PL', qty: 12, price: 32, deposit: 1.2 },
        { sku: 'RP-12-PM', qty: 6, price: 32, deposit: 1.2 }
      ],
      payments: []
    },
    {
      id: 'INV-2026-0144', order: 'SO-1041', cust: 'C-002', terms: 'Net 15', issued: '2026-06-05', due: '2026-06-20',
      status: 'Draft', taxPst: true, discount: 0, po: '', lines: [
        { sku: 'RP-12-GC', qty: 6, price: 32, deposit: 1.2 },
        { sku: 'RP-12-BR', qty: 6, price: 32, deposit: 1.2 }
      ],
      payments: []
    }
  ];

  function invoiceLinesFromOrder(order) {
    return (order.lines || []).map(line => {
      const skuId = line.skuId || line.sku;
      const sku = rpSkuItem(skuId) || {};
      return {
        sku: skuId,
        skuId,
        skuCode: String(line.skuCode || '').trim() || rpSkuExternalCode(sku),
        upc: String(line.upc || '').trim() || rpSkuExternalUpc(sku),
        qty: n(line.qty),
        price: n(line.price != null ? line.price : sku.price),
        deposit: n(line.deposit != null ? line.deposit : sku.deposit),
        recyclingFee: n(line.recyclingFee != null ? line.recyclingFee : sku.recyclingFee)
      };
    });
  }

  function paymentIdForLabel(label, fallback) {
    const normalized = String(label || '').trim().toLowerCase();
    const match = PAY_METHODS.find(method => method.id === normalized || String(method.label || '').toLowerCase() === normalized);
    return (match && match.id) || fallback || 'bank_transfer';
  }

  function noteField(line, label) {
    const match = line.match(new RegExp(`${label}:\\s*([^;\\n]+)`, 'i'));
    return match ? match[1].trim() : '';
  }

  function noteAmount(line) {
    return money(noteField(line, 'Amount').replace(/[^0-9.-]/g, ''));
  }

  function invoicePaymentsFromOrder(order, total) {
    const rawStatus = orderInvoiceRawStatus(order);
    const notes = String(order.note || order.reason || '');
    const rows = notes.split(/\n+/).map(row => row.trim()).filter(Boolean);
    const payments = [];
    rows.forEach((line, index) => {
      if (/Payment recorded:/i.test(line)) {
        const amount = noteAmount(line);
        payments.push({
          id: `PMT-${order.id}-${index}`,
          method: paymentIdForLabel(noteField(line, 'Payment recorded'), order.invoicePaymentMethod || 'bank_transfer'),
          amount: amount || money(total || order.total),
          date: noteField(line, 'Date') || ymd(order.updated || order.invoiceIssuedAt || today()),
          by: noteField(line, 'By'),
          ref: noteField(line, 'Reference'),
          kind: 'payment'
        });
      } else if (/Refund recorded:/i.test(line)) {
        const amount = noteAmount(line);
        payments.push({
          id: `REF-${order.id}-${index}`,
          method: paymentIdForLabel(noteField(line, 'Refund recorded'), 'cash'),
          amount: -Math.abs(amount),
          date: noteField(line, 'Date') || ymd(order.updated || today()),
          by: noteField(line, 'By'),
          ref: noteField(line, 'Reference'),
          reason: noteField(line, 'Reason'),
          note: noteField(line, 'Note'),
          kind: 'refund'
        });
      }
    });

    const hasPayment = payments.some(payment => n(payment.amount) > 0);
    if (!hasPayment && FINAL_STATUSES.has(rawStatus)) {
      const refundTotal = payments.filter(payment => n(payment.amount) < 0).reduce((sum, payment) => sum + Math.abs(n(payment.amount)), 0);
      payments.unshift({
        id: `PMT-${order.id}`,
        method: order.invoicePaymentMethod || 'bank_transfer',
        amount: money((total || order.total || 0) + refundTotal),
        date: ymd(order.updated || order.invoiceIssuedAt || today()),
        by: '',
        ref: '',
        kind: 'payment'
      });
    }

    return payments;
  }

  function invoiceFromOrder(order, customerOverride) {
    const customer = customerOverride || RP.cust(order.cust) || {};
    const issued = ymd(order.invoiceIssuedAt || order.created || today());
    const due = order.invoiceDueDate || termsDueDate(customer, issued);
    const total = n(order.total) || (typeof rpOrderTotals === 'function' ? n(rpOrderTotals(order).total) : 0);
    const payments = invoicePaymentsFromOrder(order, total);
    return {
      id: invoiceNumberForOrder(order),
      order: order.id,
      cust: order.cust,
      terms: customerTerms(customer),
      issued,
      due,
      status: order.invoiceNumber ? (order.invoiceStatus || 'Issued') : (orderIsSettledWithoutInvoice(order) ? orderInvoiceRawStatus(order) : 'Draft'),
      taxPst: true,
      discount: 0,
      po: order.po || '',
      lines: invoiceLinesFromOrder(order),
      adjustments: order.adjustments || [],
      payments,
      sourceOrder: order,
      invoiceUrl: order.invoiceUrl || '',
      emailTo: customerInvoiceEmail(order, customer),
      note: customerInvoiceNote(order, customer),
      paymentMethod: order.invoicePaymentMethod || (customer.invoiceOverrides && customer.invoiceOverrides.defaultPaymentMethod) || 'account_terms',
      invoiceLastSentAt: order.invoiceLastSentAt || '',
      invoiceFirstOpenedAt: order.invoiceFirstOpenedAt || '',
      invoiceLastOpenedAt: order.invoiceLastOpenedAt || '',
      invoiceOpenCount: Number(order.invoiceOpenCount || 0)
    };
  }

  function liveInvoiceRows() {
    return (RP.ORDERS || [])
      .filter(orderNeedsInvoiceRow)
      .map(invoiceFromOrder)
      .sort((a, b) => String(b.issued || '').localeCompare(String(a.issued || '')) || String(b.order).localeCompare(String(a.order)));
  }

  function invoiceRows() {
    return typeof rpHasLiveAuth === 'function' && rpHasLiveAuth() ? liveInvoiceRows() : DEMO_INVOICES;
  }

  function invoice(id) {
    const key = String(id || '');
    return invoiceRows().find(inv => inv.id === key || inv.order === key || (draftInvoiceId({ id: inv.order }) === key)) || null;
  }

  function invoiceForOrder(orderId) {
    return invoiceRows().find(inv => inv.order === orderId) || null;
  }

  function invoicesForCust(customerId) {
    return invoiceRows().filter(inv => inv.cust === customerId);
  }

  function totals(inv) {
    const order = orderForInvoice(inv);
    const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
    if (live && order && typeof rpOrderTotals === 'function') {
      const t = rpOrderTotals(order);
      const paid = (inv.payments || []).reduce((sum, pay) => sum + n(pay.amount), 0);
      return {
        sub: money(t.sub),
        dep: money(t.containerFee || t.dep),
        fee: money(t.fee),
        containerFee: money(t.containerFee || t.dep),
        creditCardFee: money(t.creditCardFee || order.creditCardFeeTotal),
        discount: 0,
        gst: money(t.tax),
        pst: 0,
        tax: money(t.tax),
        total: money(t.total),
        paid: money(paid),
        balance: money(Math.max(0, t.total - paid)),
        cases: n(t.cases)
      };
    }

    let sub = 0, dep = 0, cases = 0;
    (inv.lines || []).forEach(line => {
      sub += n(line.price) * n(line.qty);
      dep += n(line.deposit) * n(line.qty);
      cases += n(line.qty);
    });
    const discount = n(inv.discount);
    const taxable = Math.max(0, sub - discount);
    const gst = taxable * GST;
    const pst = inv.taxPst ? taxable * PST : 0;
    const total = taxable + dep + gst + pst;
    const paid = (inv.payments || []).reduce((sum, pay) => sum + n(pay.amount), 0);
    return { sub: money(sub), dep: money(dep), fee: money(dep), containerFee: money(dep), creditCardFee: 0, discount: money(discount), gst: money(gst), pst: money(pst), tax: money(gst + pst), total: money(total), paid: money(paid), balance: money(Math.max(0, total - paid)), cases };
  }

  function invoiceStatus(inv) {
    if (!inv) return 'Draft';
    const order = orderForInvoice(inv);
    const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
    if (live && order) {
      const raw = orderInvoiceRawStatus(order) || 'Issued';
      if (!order.invoiceNumber && FINAL_STATUSES.has(raw)) return 'Paid';
      if (!order.invoiceNumber) return 'Draft';
      const t = totals(inv);
      if (t.balance <= 0.005 && t.paid > 0) return 'Paid';
      if (raw === 'Partial' || t.paid > 0) return t.balance > 0.005 ? 'Partial' : 'Paid';
      if (FINAL_STATUSES.has(raw)) return 'Paid';
      if (inv.due && daysBetween(inv.due, today()) > 0) return 'Overdue';
      return order.invoiceLastSentAt ? 'Sent' : 'Issued';
    }
    if (inv.status === 'Draft' || inv.status === 'Void') return inv.status;
    const t = totals(inv);
    if (t.balance <= 0.005) return 'Paid';
    if (t.paid > 0) return inv.due && daysBetween(inv.due, today()) > 0 ? 'Overdue' : 'Partial';
    return inv.due && daysBetween(inv.due, today()) > 0 ? 'Overdue' : 'Sent';
  }

  function generateInvoice(orderId, opts = {}) {
    if (typeof rpHasLiveAuth === 'function' && rpHasLiveAuth()) return invoiceForOrder(orderId);
    const existing = invoiceForOrder(orderId);
    if (existing) return existing;
    const order = RP.ORDERS.find(row => row.id === orderId);
    if (!order) return null;
    const customer = RP.cust(order.cust) || {};
    const issued = today();
    const inv = {
      id: `INV-${new Date().getFullYear()}-${String(1400 + DEMO_INVOICES.length).padStart(4, '0')}`,
      order: order.id,
      cust: order.cust,
      terms: opts.terms || customerTerms(customer),
      issued,
      due: termsDueDate(customer, issued),
      status: opts.send ? 'Sent' : 'Draft',
      taxPst: opts.taxPst !== false,
      discount: 0,
      po: order.po || '',
      lines: invoiceLinesFromOrder(order),
      payments: []
    };
    DEMO_INVOICES.unshift(inv);
    return inv;
  }

  let paymentSeq = 4000;
  function addPayment(invId, payment) {
    const inv = invoice(invId);
    if (!inv || orderForInvoice(inv)) return null;
    const pay = { id: `PMT-${paymentSeq++}`, date: today(), ...payment };
    inv.payments = inv.payments || [];
    inv.payments.push(pay);
    if (inv.status === 'Draft') inv.status = 'Sent';
    return pay;
  }

  function sendInvoice(invId) {
    const inv = invoice(invId);
    if (inv && !orderForInvoice(inv) && inv.status === 'Draft') inv.status = 'Sent';
    return inv;
  }

  function metrics() {
    let ar = 0, overdue = 0, collectedMtd = 0, paidCount = 0, payDays = [];
    const currentMonth = today().slice(0, 7);
    invoiceRows().forEach(inv => {
      const t = totals(inv);
      const st = invoiceStatus(inv);
      if (!['Paid', 'Draft', 'Void'].includes(st)) ar += t.balance;
      if (st === 'Overdue') overdue += t.balance;
      if (st === 'Paid') {
        paidCount += 1;
        if (inv.issued) payDays.push(7);
      }
      const payments = inv.payments || [];
      collectedMtd += payments.reduce((sum, payment) => (
        String(payment.date || '').slice(0, 7) === currentMonth ? sum + n(payment.amount) : sum
      ), 0);
      if (!payments.length && st === 'Paid' && String(inv.issued || '').slice(0, 7) === currentMonth) {
        collectedMtd += t.paid > 0 ? t.paid : t.total;
      }
    });
    return {
      ar: money(ar),
      overdue: money(overdue),
      collectedMtd: money(collectedMtd),
      avgDays: payDays.length ? Math.round(payDays.reduce((sum, value) => sum + value, 0) / payDays.length) : 0,
      paidCount
    };
  }

  async function rpEmailInvoiceOrder(orderOrInvoice, recipients) {
    const inv = orderOrInvoice && orderOrInvoice.order ? orderOrInvoice : invoice(orderOrInvoice && orderOrInvoice.id);
    const order = orderForInvoice(inv) || orderOrInvoice;
    if (!order || !order.id) throw new Error('Order was not found for this invoice.');
    const options = typeof recipients === 'string' ? { to: [recipients] } : (recipients || {});
    const to = Array.isArray(options.to) ? options.to : [options.to || inv.emailTo || order.invoiceEmailTo || ''];
    const cc = Array.isArray(options.cc) ? options.cc : [];
    const response = await rpApi('/api/invoices/email', {
      method: 'POST',
      body: JSON.stringify({ orderId: order.id, to, cc })
    });
    order.invoiceLastSentAt = response.invoice && response.invoice.lastSentAt ? response.invoice.lastSentAt : new Date().toISOString();
    if (to[0]) order.invoiceEmailTo = to[0];
    const label = invoiceStatus(inv) === 'Paid' ? 'Receipt' : 'Invoice';
    await rpRefreshAfterMutation(`${label} ${order.invoiceNumber || order.id} emailed`);
    return response;
  }

  Object.defineProperty(RP, 'INVOICES', {
    configurable: true,
    get: invoiceRows
  });

  Object.assign(RP, {
    GST_RATE: GST,
    PST_RATE: PST,
    PAY_METHODS,
    CARD_CAPTURE,
    TERM_DAYS,
    invoice,
    invoicesForCust,
    invoiceForOrder,
    invoiceTotals: totals,
    invoiceStatus,
    generateInvoice,
    addPayment,
    sendInvoice,
    invoiceMetrics: metrics,
    payMethod: id => PAY_METHODS.find(method => method.id === id),
    invDaysOverdue: inv => inv && inv.due ? Math.max(0, daysBetween(inv.due, today())) : 0
  });
  Object.assign(window, { rpInvoiceOrder: orderForInvoice, rpInvoiceFromOrder: invoiceFromOrder, rpInvoicePaymentMethodLabel: paymentMethodLabel, rpEmailInvoiceOrder });
})();


/* ===== email_log.jsx ===== */
const RP_EMAIL_STATUS_LABELS = { sent: 'Sent · awaiting delivery', accepted: 'Accepted by provider', delivered: 'Delivered', opened: 'Opened', clicked: 'Clicked', bounced: 'Bounced', failed: 'Failed', complained: 'Marked as spam', suppressed: 'Suppressed', delayed: 'Delivery delayed', unknown: 'Delivery unknown', pending: 'Waiting to send', replied: 'Replied' };
function rpEmailLogTime(value) { if (!value) return '—'; const date = new Date(value); return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(); }
function rpEmailUnresolvedDeliveries(rows) {
  return rows.filter(row => {
    const addresses = row.affected_recipients?.length ? row.affected_recipients : row.recipients || [];
    return row.needsAttention && (!addresses.length || !addresses.every(address => rows.some(next =>
      new Date(next.created_at) > new Date(row.created_at)
      && (next.history || []).some(event => ['email.delivered','email.opened','email.clicked'].includes(event.event_type) && event.recipients?.length === 1 && event.recipients[0] === address)
      && !(next.history || []).some(event => ['email.bounced','email.failed','email.complained','email.suppressed'].includes(event.event_type) && (event.recipients || []).includes(address)))));
  });
}
function EmailDeliveryRow({ row, onOpenRecord }) {
  const [expanded, setExpanded] = useState(false);
  const originType = row.order_id ? 'order' : row.entity_type === 'retail_quote' ? 'quote' : '';
  const originId = row.order_id || row.entity_id;
  return <div style={{ border: '1px solid var(--line)', borderLeft: row.needsAttention ? '4px solid var(--danger)' : undefined, borderRadius: 9, padding: 12, background: 'var(--surface)', marginBottom: 10, minWidth: 0, overflowWrap: 'anywhere' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}><b style={{ color: row.needsAttention ? 'var(--danger)' : 'var(--ink)' }}>{RP_EMAIL_STATUS_LABELS[row.delivery_status] || row.delivery_status}</b><span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{rpEmailLogTime(row.created_at)}</span></div>
    <div style={{ fontWeight: 650, marginTop: 6, overflowWrap: 'anywhere' }}>{row.subject || row.email_kind || 'Email'}</div>
    <div style={{ fontSize: 12.5, marginTop: 4, overflowWrap: 'anywhere' }}>{(row.recipients || []).join(', ') || 'No recipient saved'}</div>
    {row.needsAttention && <div role="status" style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8, lineHeight: 1.5 }}><b>{(row.affected_recipients || []).length ? `Check ${(row.affected_recipients || []).join(', ')}. ` : 'Review this email. '}</b>{row.reason || 'Check the recipient address and delivery details before sending again.'}{row.delivery_status === 'unknown' && ' Recover the original send or ask Operations to reconcile it before starting another.'}</div>}
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 9, flexWrap: 'wrap', fontSize: 12 }}>
      {originId && (onOpenRecord && originType ? <button className="btn btn-sm" onClick={() => onOpenRecord({ entityType: originType, entityId: originId })}>{row.invoice_number || originId}</button> : <span>{row.invoice_number || originId}</span>)}
      <button className="btn btn-sm btn-ghost" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>{expanded ? 'Hide details' : 'Delivery details'}</button>
    </div>
    {expanded && <div style={{ marginTop: 9, paddingTop: 9, borderTop: '1px solid var(--line)', fontSize: 12, lineHeight: 1.6 }}>
      <div>Provider accepted: {rpEmailLogTime(row.sent_at)} · Delivered: {rpEmailLogTime(row.delivered_at)}</div><div>Opened: {rpEmailLogTime(row.opened_at)} · Clicked: {rpEmailLogTime(row.clicked_at)}</div>
      {row.provider_message_id && <div style={{ color: 'var(--muted)', overflowWrap: 'anywhere' }}>Provider reference: {row.provider_message_id}</div>}
      {(row.history || []).map((event, index) => <div key={index} style={{ marginTop: 7 }}><b>{RP_EMAIL_STATUS_LABELS[String(event.event_type).replace('email.', '').replace('delivery_delayed', 'delayed')] || event.event_type}</b> · {rpEmailLogTime(event.occurred_at)}<div>{(event.recipients || []).join(', ')}{event.reason ? ` — ${event.reason}` : ''}</div></div>)}
      {!row.history?.length && <div style={{ color: 'var(--muted)', marginTop: 7 }}>Earlier sends may have only the saved summary; a complete event history was not recorded.</div>}
    </div>}
  </div>;
}
function EmailDeliveryLog({ entityType = '', entityId = '', compact = false, initialStatus = '', onOpenRecord }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let live = true;
    setBusy(true); setError(''); setData(null);
    const params = new URLSearchParams({ page: String(page), status, search: query });
    if (entityType && entityId) { params.set('entityType', entityType); params.set('entityId', entityId); }
    rpApi(`/api/email-delivery?${params}`).then(result => { if (live) setData(result); }).catch(err => { if (live) setError(err.message || 'Email history could not be loaded.'); }).finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [entityType, entityId, status, query, page, version]);
  return <div style={{ padding: compact ? 0 : 24, maxWidth: 1100, margin: '0 auto' }}>
    {!compact && <><h2 style={{ margin: '0 0 8px' }}>Email delivery</h2><p style={{ color: 'var(--muted)', fontSize: 13 }}>Follow invoices, receipts, quotes, and other email. Review delivery problems and confirm addresses before sending again.</p></>}
    <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 14 }}><select className="field" aria-label="Delivery status" value={status} onChange={e => { setStatus(e.target.value); setPage(0); }} style={{ width: 'auto' }}><option value="">All statuses</option><option value="attention">Needs attention</option>{['sent','delivered','opened','clicked','bounced','failed','delayed','unknown'].map(value => <option key={value} value={value}>{RP_EMAIL_STATUS_LABELS[value]}</option>)}</select>
      {!compact && <form onSubmit={e => { e.preventDefault(); setQuery(search); setPage(0); }} style={{ display: 'flex', gap: 6, flex: '1 1 280px', minWidth: 0 }}><input className="field" aria-label="Search email history" value={search} onChange={e => setSearch(e.target.value)} placeholder="Email, subject, invoice or record number" style={{ minWidth: 0, flex: 1 }} /><button className="btn" type="submit">Search</button></form>}
      <button className="btn" disabled={busy} onClick={() => setVersion(value => value + 1)}>Refresh</button></div>
    {busy && <p role="status">Loading email history…</p>}{error && <p role="alert" style={{ color: 'var(--danger)' }}>{error}</p>}
    {data && <><div style={{ color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.5, marginBottom: 12 }}>{data.note}{!data.tracking?.delivery && <div>Delivery tracking has not been confirmed in setup; a sent email may have no delivery update.</div>}{!data.tracking?.engagement && <div>Open and click tracking has not been confirmed in setup.</div>}</div>
      {!data.rows?.length && <p>No recorded email matches this view.</p>}{(data.rows || []).map(row => <EmailDeliveryRow key={row.id} row={row} onOpenRecord={onOpenRecord} />)}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><button className="btn btn-sm" disabled={!page || busy} onClick={() => setPage(value => value - 1)}>Previous</button><span style={{ fontSize: 12 }}>Page {page + 1}</span><button className="btn btn-sm" disabled={!data.hasMore || busy} onClick={() => setPage(value => value + 1)}>Next</button></div></>}
  </div>;
}
function EmailDeliveryStatus({ entityType = 'order', entityId, compact = true }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : null;
  const enabled = !!entityId && !!profile && profile.role !== 'customer' && (typeof rpIsDemoMode !== 'function' || !rpIsDemoMode());
  useEffect(() => { let live = true; setData(null); setError(''); if (!enabled) return () => { live = false; };
    rpApi(`/api/email-delivery?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`).then(result => { if (live) setData(result); }).catch(() => { if (live) setError('Email status unavailable'); });
    return () => { live = false; };
  }, [enabled, entityType, entityId, version]);
  if (!enabled) return null;
  const rows = data?.rows || [];
  // A later confirmed delivery to the same address can resolve an older issue.
  // An accepted resend or a delivery to a different recipient cannot hide it.
  const attentionCount = data?.attentionCount === undefined ? rpEmailUnresolvedDeliveries(rows).length : Number(data.attentionCount || 0);
  const label = error || (attentionCount ? `Email needs attention (${attentionCount})` : rows.length ? (RP_EMAIL_STATUS_LABELS[rows[0].delivery_status] || 'Email history') : data ? 'No email recorded' : 'Checking email…');
  return <div className="rp-email-status" style={{ margin: compact ? '8px 0' : '16px 0' }}><style>{'@media print { .rp-email-status { display: none !important; } }'}</style><button className="btn btn-sm" onClick={() => { setOpen(!open); if (!open) setVersion(value => value + 1); }} style={{ color: attentionCount || error ? 'var(--danger)' : 'var(--muted)', whiteSpace: 'normal', textAlign: 'left' }}><Icon name="mail" size={14} />{label}</button>{open && <div style={{ padding: 12, marginTop: 8, border: '1px solid var(--line)', borderRadius: 10 }}><EmailDeliveryLog entityType={entityType} entityId={entityId} initialStatus={attentionCount ? 'attention' : ''} compact /></div>}</div>;
}


/* ===== invoice_shared.jsx ===== */
/* ============================================================
   RANCH POWER - Shared invoice UI
   ============================================================ */

const RP_CREDIT_CARD_TERMS_NOTE = 'A 2.4% credit card processing fee applies to credit card payments.';

function rpInvoiceMissingRequiredPo(order, customer) {
  return Boolean(customer && customer.requiresPo === true && !String(order && (order.po || order.po_number) || '').trim());
}

function InvoicePoRequirement({ order, customer, disabled = false, onSaved }) {
  const savedPo = String(order && (order.po || order.po_number) || '').trim();
  const [draft, setDraft] = useState(savedPo);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const issued = Boolean(order.invoiceNumber || order.invoice_number || order.invoiceIssuedAt || order.invoice_issued_at);
  useEffect(() => { setDraft(savedPo); setError(''); }, [order.id, savedPo]);
  if (!(customer && customer.requiresPo) && !savedPo) return null;
  const save = async () => {
    if (busy || disabled || issued || !draft.trim()) return;
    setBusy(true); setError('');
    try {
      const result = rpIsDemoMode()
        ? { order: { id: order.id, po: draft.trim() } }
        : await rpApi('/api/orders/po', { method: 'POST', body: JSON.stringify({ orderId: order.id, po: draft.trim(), expectedPo: savedPo }) });
      if (!result.order || result.order.id !== order.id || !String(result.order.po || '').trim()) throw new Error('The saved PO could not be confirmed. Refresh the order.');
      const updated = rpUpsertOrder({ ...order, po: result.order.po });
      Object.assign(order, updated);
      setDraft(result.order.po);
      onSaved && onSaved(updated);
    } catch (failure) { setError(failure && failure.message || 'Could not save the PO number.'); }
    finally { setBusy(false); }
  };
  return <div data-invoice-po="true" style={{ border: '1px solid var(--line)', borderRadius: 9, padding: 12, marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 12.5, fontWeight: 750 }}>PO number{customer && customer.requiresPo ? ' (required for invoice)' : ''}
      <input className="field" aria-label="Invoice PO number" maxLength={120} disabled={disabled || busy || issued} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Customer purchase order number" style={{ marginTop: 6 }} />
    </label>
    {!issued && <button type="button" className="btn btn-sm" disabled={disabled || busy || !draft.trim() || draft.trim() === savedPo} onClick={save} style={{ marginTop: 8 }}>{busy ? 'Saving PO...' : 'Save PO'}</button>}
    {rpInvoiceMissingRequiredPo(order, customer) && !issued && <div role="status" style={{ color: 'var(--amber-deep)', fontSize: 12, marginTop: 8 }}>Requires PO. Save a PO number before issuing this invoice.</div>}
    {rpInvoiceMissingRequiredPo(order, customer) && issued && <div role="status" style={{ color: 'var(--muted)', fontSize: 12, marginTop: 8 }}>This invoice was already issued without a PO. Operations must reconcile the existing invoice before reissuing it.</div>}
    {error && <div role="alert" style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>{error}</div>}
  </div>;
}

function payMeta(method) {
  const m = RP.payMethod(method);
  return m || { id: method, label: rpInvoicePaymentMethodLabel(method), icon: 'wallet' };
}

function captureLabel(id) {
  const c = (RP.CARD_CAPTURE || []).find(row => row.id === id);
  return c ? c.label : id;
}

function invoiceCustomerLines(customer) {
  const address = [customer.address, customer.city, customer.postal].filter(Boolean).join(', ');
  return [
    customer.name || customer.displayName || customer.legalName,
    customer.buyer,
    address || customer.city
  ].filter(Boolean);
}

function invoiceCustomerPstNumber(customer) {
  const onboarding = customer && customer.onboardingQuestionnaire && typeof customer.onboardingQuestionnaire === 'object' ? customer.onboardingQuestionnaire : {};
  const business = onboarding.businessInformation && typeof onboarding.businessInformation === 'object' ? onboarding.businessInformation : {};
  const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
  return String(customer && (customer.pstNumber || customer.pstHstNumber || customer.pst_hst_number) || overrides.pstNumber || overrides.pst_number || business.pstHstNumber || business.pst_hst_number || '').trim();
}

function invoicePstExemptionNumber(customer, adjustments) {
  const pstNumber = invoiceCustomerPstNumber(customer);
  if (!pstNumber) return '';
  const pstCharged = (adjustments || []).some(row => String(row.code || '').toUpperCase() === 'PST' && Number(row.amount || 0) > 0);
  return pstCharged ? '' : pstNumber;
}

function invoiceIssuedLabel(inv) {
  return inv.issued || (inv.sourceOrder && String(inv.sourceOrder.invoiceIssuedAt || '').slice(0, 10)) || '';
}

function invoiceLineLabel(line) {
  const sku = rpSkuItem(line.skuId || line.sku);
  const label = String(line.label || '').trim();
  if (label) {
    const labelSku = rpSkuItem(label);
    if (!labelSku || (sku && labelSku.id !== sku.id)) return label;
  }
  return rpSkuDisplayName(sku || 'Unknown product');
}

function invoiceLineExternalIdentity(line) {
  const internalId = String(line.skuId || line.sku || '').trim();
  const sku = rpSkuItem(internalId);
  const suppliedCode = String(line.skuCode || '').trim();
  const suppliedUpc = String(line.upc || '').trim();
  const code = rpSkuExternalCode(sku || '')
    || (suppliedCode && suppliedCode.toUpperCase() !== internalId.toUpperCase() ? suppliedCode : '');
  const upc = rpSkuExternalUpc(sku || '') || (/^[0-9]{8,14}$/.test(suppliedUpc) ? suppliedUpc : '');
  return `${code || 'SKU unavailable'} - ${upc ? `UPC ${upc}` : 'UPC unavailable'}`;
}

const RP_INVOICE_COMPANY = {
  name: 'Ranch Power Group Inc.',
  address: ['#2700 - 685 Centre St S', 'Calgary, AB, T2G 1S5, Canada'],
  phone: '+1 (587) 887-1164',
  email: 'sales@ranchpower.com',
  remittanceEmail: 'ar@ranchpower.com',
  gst: 'GST # 76296 9160 RT0001',
  pst: 'BC PST # PST-1510-7736'
};

function invoiceAdjustmentCode(row) {
  return String((row && (row.code || row.type || row.adjustmentType || row.adjustment_type)) || '').trim();
}

function invoiceAdjustmentAmount(row) {
  const value = Number(row && row.amount);
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
}

function invoiceAdjustmentQuantity(row) {
  const value = Number(row && row.quantity);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

function invoiceAdjustmentRate(row, amount, quantity) {
  const value = Number(row && row.rate);
  if (Number.isFinite(value) && value > 0) return value;
  return quantity > 0 ? amount / quantity : amount;
}

function invoiceIsContainerAdjustment(row) {
  const code = invoiceAdjustmentCode(row).toUpperCase();
  if (['CONTAINER_FEE', 'CONTAINER_DEPOSIT', 'RECYCLING_FEE'].includes(code)) return true;
  const label = String((row && row.label) || '').toLowerCase();
  return label.includes('container') || label.includes('recycling');
}

function invoiceIsTaxAdjustment(row) {
  const type = String((row && (row.type || row.adjustmentType || row.adjustment_type)) || '').toLowerCase();
  const code = invoiceAdjustmentCode(row).toUpperCase();
  return type === 'tax' || ['GST', 'PST', 'HST', 'QST', 'RST', 'TAX'].includes(code) || code.endsWith('_TAX');
}

function invoicePercentLabel(rate) {
  const value = Number(rate);
  if (!Number.isFinite(value) || value <= 0) return '-';
  const percent = value <= 1 ? value * 100 : value;
  const rounded = Math.round(percent * 1000) / 1000;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(3).replace(/\.?0+$/, '')}%`;
}

function invoiceAdjustmentDisplayRows(adjustments) {
  const rows = [];
  const grouped = new Map();
  (adjustments || []).forEach((adjustment, index) => {
    const amount = invoiceAdjustmentAmount(adjustment);
    if (!amount) return;
    const quantity = invoiceAdjustmentQuantity(adjustment);
    const rate = invoiceAdjustmentRate(adjustment, amount, quantity);
    const code = invoiceAdjustmentCode(adjustment) || 'ADJUSTMENT';
    const label = adjustment.label || code || 'Adjustment';

    if (invoiceIsContainerAdjustment(adjustment)) {
      const unit = Math.round(rate * 100) / 100;
      const key = `${code}:${label}:${unit}`;
      let row = grouped.get(key);
      if (!row) {
        row = {
          id: adjustment.id || key,
          amount: 0,
          code,
          label,
          quantity: 0,
          quantityLabel: '0',
          unitLabel: RP.money2(unit)
        };
        grouped.set(key, row);
        rows.push(row);
      }
      row.amount = Math.round((row.amount + amount) * 100) / 100;
      row.quantity += quantity;
      row.quantityLabel = Number.isInteger(row.quantity) ? String(row.quantity) : row.quantity.toFixed(2).replace(/\.?0+$/, '');
      return;
    }

    rows.push({
      id: adjustment.id || `${code}-${index}`,
      amount,
      code,
      label,
      quantityLabel: invoiceIsTaxAdjustment(adjustment) ? '-' : (Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(2).replace(/\.?0+$/, '')),
      unitLabel: invoiceIsTaxAdjustment(adjustment) ? invoicePercentLabel(rate) : RP.money2(rate || amount)
    });
  });
  return rows;
}

function InvoiceDocument({ inv, paper = true }) {
  const c = RP.cust(inv.cust) || {};
  const t = RP.invoiceTotals(inv);
  const st = RP.invoiceStatus(inv);
  const over = st === 'Overdue';
  const paid = st === 'Paid';
  const documentTitle = paid ? 'Receipt' : 'Invoice';
  const invoiceNo = st === 'Draft' ? 'Ready to issue' : inv.id;
  const method = inv.paymentMethod || (inv.sourceOrder && inv.sourceOrder.invoicePaymentMethod) || 'account_terms';
  const adjustments = invoiceAdjustmentDisplayRows(inv.adjustments || []);
  const taxRows = invoiceDocumentTaxRows(inv, t, { gst: RP.GST_RATE || 0.05, pst: RP.PST_RATE || 0.07 });
  const pricingOrder = inv.sourceOrder || { adjustments: [...(inv.adjustments || []), ...(!(inv.adjustments || []).length ? taxRows.map(row => ({ ...row, type: 'tax', rate: row.ratePercent == null ? undefined : row.ratePercent / 100 })) : [])], taxTotal: t.tax };
  const pricingTotals = inv.sourceOrder ? { ...rpOrderTotals(inv.sourceOrder), total: t.total } : { ...t, dep: 0, fee: Number(t.containerFee || t.dep || 0) + Number(t.creditCardFee || 0) };
  const pricingDetailRows = rpPricingBreakdownRows(pricingOrder, pricingTotals);
  const pstExemptionNumber = invoicePstExemptionNumber(c, adjustments);
  const invoiceNote = String(inv.note || '').trim();
  return (
    <div className="rp-invoice-doc" style={{ background: paper ? 'var(--surface)' : 'transparent', borderRadius: paper ? 'var(--radius)' : 0, border: paper ? '1px solid var(--line)' : 'none', overflow: 'hidden', boxShadow: paper ? 'var(--shadow-sm)' : 'none' }}>
      <div style={{ background: 'var(--navy)', color: '#fff', padding: '22px 26px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
          <Buffalo size={40} />
          <div style={{ lineHeight: 1 }}>
            <Wordmark height={15} />
            <div style={{ fontFamily: 'var(--ff-display)', fontWeight: 600, fontSize: 9.5, letterSpacing: '0.22em', color: 'var(--amber)', marginTop: 6 }}>GRIT. DRIVEN.</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="disp" style={{ fontSize: 26, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>{documentTitle}</div>
          <div className="mono" style={{ fontSize: 13, marginTop: 7, opacity: 0.85 }}>{invoiceNo}</div>
        </div>
      </div>

      <div style={{ padding: '22px 26px' }}>
        {typeof EmailDeliveryStatus === 'function' && <EmailDeliveryStatus entityType="order" entityId={inv.orderId || inv.sourceOrder?.id || inv.order} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 20, marginBottom: 22 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Billed To</div>
            {invoiceCustomerLines(c).map((line, index) => (
              <div key={index} style={{ fontWeight: index === 0 ? 750 : 500, fontSize: index === 0 ? 15 : 13, color: index === 0 ? 'var(--ink)' : 'var(--muted)', marginTop: index ? 3 : 0, lineHeight: 1.45 }}>{line}</div>
            ))}
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>From</div>
            <div style={{ fontWeight: 750, fontSize: 15 }}>{RP_INVOICE_COMPANY.name}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3, lineHeight: 1.5 }}>
              {RP_INVOICE_COMPANY.address[0]}<br />
              {RP_INVOICE_COMPANY.address[1]}<br />
              {RP_INVOICE_COMPANY.phone}<br />
              {RP_INVOICE_COMPANY.email}
            </div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 8, lineHeight: 1.45 }}>
              {RP_INVOICE_COMPANY.gst}<br />
              {RP_INVOICE_COMPANY.pst}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <DocMeta k="Status" v={<Pill status={st}>{st === 'Draft' ? 'Ready' : st}</Pill>} />
            <DocMeta k="Issued" v={<span className="mono">{invoiceIssuedLabel(inv) || 'Pending'}</span>} />
            <DocMeta k="Due" v={<span className="mono" style={{ color: over ? 'var(--danger)' : undefined, fontWeight: over ? 700 : undefined }}>{st === 'Draft' ? 'After issue' : (inv.due || 'Upon receipt')}</span>} />
            <DocMeta k="Terms" v={inv.terms || c.terms || 'Account terms'} />
            <DocMeta k="Payment" v={rpInvoicePaymentMethodLabel(method)} />
            {pstExemptionNumber && <DocMeta k="PST exempt #" v={<span className="mono">{pstExemptionNumber}</span>} />}
            {!paid && <DocMeta k="E-transfer" v={<span className="mono">{RP_INVOICE_COMPANY.remittanceEmail}</span>} />}
            <DocMeta k="Order" v={<span className="mono">{inv.order}</span>} />
            {inv.po && <DocMeta k="PO #" v={<span className="mono">{inv.po}</span>} />}
          </div>
        </div>

        <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Unit / Rate</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {(inv.lines || []).map((line, index) => {
                const sku = rpSkuItem(line.skuId || line.sku) || {};
                const amount = Number(line.price || 0) * Number(line.qty || 0);
                return (
                  <tr key={`${line.sku}-${index}`}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FlavorChip sku={sku || line.sku} size={24} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{invoiceLineLabel(line).replace(' 355ml', '')}</div>
                          <div className="mono" style={{ fontSize: 11, color: 'var(--faint)' }}>{invoiceLineExternalIdentity(line)}{sku.pack ? ` - ${sku.pack}` : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="num">{line.qty} cs</td>
                    <td className="num">{RP.money2(line.price)}</td>
                    <td className="num" style={{ fontWeight: 650 }}>{RP.money2(amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <div style={{ width: 'min(340px, 100%)', display: 'flex', flexDirection: 'column', gap: 7, fontSize: 13.5 }}>
            {pricingDetailRows.map((row, index) => <DocLine key={index} k={row.label} v={RP.money2(row.amount)} muted={index > 0} />)}
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 9, marginTop: 3 }}>
              <DocLine k={<b style={{ fontSize: 14 }}>{documentTitle} Total</b>} v={<b className="disp" style={{ fontSize: 20 }}>{RP.money2(t.total)}</b>} />
            </div>
            {t.paid > 0 && <DocLine k="Paid to date" v={'-' + RP.money2(t.paid)} muted />}
            {st === 'Draft' ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-2)', color: 'var(--muted)', borderRadius: 8, padding: '9px 12px', marginTop: 4, fontWeight: 700 }}><span>Ready to issue</span><span className="disp" style={{ fontSize: 17 }}>{RP.money2(t.total)}</span></div>
            ) : t.balance > 0.005 ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', background: over ? 'var(--danger-bg)' : 'var(--amber-soft)', color: over ? 'var(--danger)' : 'var(--amber-deep)', borderRadius: 8, padding: '9px 12px', marginTop: 4 }}><span style={{ fontWeight: 750 }}>Balance Due</span><span className="disp" style={{ fontSize: 19 }}>{RP.money2(t.balance)}</span></div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--ok-bg)', color: 'var(--ok)', borderRadius: 8, padding: '9px 12px', marginTop: 4, fontWeight: 750 }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Icon name="check" size={16} />Paid in Full</span><span className="disp" style={{ fontSize: 17 }}>{RP.money2(t.total)}</span></div>
            )}
          </div>
        </div>

        {invoiceNote && (
          <div style={{ marginTop: 16, background: 'var(--amber-soft)', color: 'var(--amber-deep)', border: '1px solid var(--amber)', borderRadius: 8, padding: '11px 13px', fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
            <b>Note:</b> {invoiceNote}
          </div>
        )}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--line-2)', fontSize: 11.5, color: 'var(--faint)', lineHeight: 1.5 }}>
          {paid
            ? `Payment received. Thank you for your business. ${RP_CREDIT_CARD_TERMS_NOTE} Container deposits are refundable under the BC return-to-retail program. ${RP_INVOICE_COMPANY.gst}; ${RP_INVOICE_COMPANY.pst}.`
            : `Remit by the due date to keep terms current. ${RP_CREDIT_CARD_TERMS_NOTE} E-transfer payments can be sent to ${RP_INVOICE_COMPANY.remittanceEmail}. Credit-card payments are processed by Ranch Power through its secure Stripe saved-card flow. Container deposits are refundable under the BC return-to-retail program. ${RP_INVOICE_COMPANY.gst}; ${RP_INVOICE_COMPANY.pst}.`}
        </div>
      </div>
    </div>
  );
}

function DocMeta({ k, v }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5 }}><span style={{ color: 'var(--muted)' }}>{k}</span><span style={{ fontWeight: 650, textAlign: 'right' }}>{v}</span></div>;
}

function DocLine({ k, v, muted, hint }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: muted ? 'var(--muted)' : 'var(--ink)' }}><span>{k}{hint && <span style={{ fontSize: 11, color: 'var(--faint)', marginLeft: 5 }}>{hint}</span>}</span><span className="mono" style={{ fontWeight: 650, textAlign: 'right' }}>{v}</span></div>;
}

function PaymentHistory({ inv, empty }) {
  const pays = inv.payments || [];
  if (!pays.length) return empty || <div style={{ fontSize: 13, color: 'var(--faint)', padding: '14px 0' }}>Payments appear here as soon as they are recorded.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {pays.map(payment => {
        const m = payMeta(payment.method);
        const isRefund = payment.kind === 'refund' || Number(payment.amount || 0) < 0;
        const amount = Math.abs(Number(payment.amount || 0));
        return (
          <div key={payment.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', background: isRefund ? 'var(--danger-bg)' : 'var(--surface-2)', borderRadius: 10, border: '1px solid var(--line)' }}>
            <span style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--surface)', border: '1px solid var(--line)', color: isRefund ? 'var(--danger)' : 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={isRefund ? 'arrowL' : m.icon} size={17} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 650 }}>{isRefund ? 'Refund' : m.label}{isRefund ? ` - ${m.label}` : payment.shopify ? ' - ' + captureLabel(payment.shopify.capture) : ''}</span>
                {payment.shopify && <span className="mono" style={{ fontSize: 11, color: 'var(--ok)', fontWeight: 650 }}>{payment.shopify.brand} **{payment.shopify.last4}</span>}
              </div>
              {payment.reason && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{payment.reason}</div>}
              <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>{payment.date}{payment.by ? ' - ' + payment.by : ''}{payment.ref ? ' - ' + payment.ref : ''}</div>
            </div>
            <span className="mono" style={{ fontWeight: 750, fontSize: 14, color: isRefund ? 'var(--danger)' : 'var(--ink)' }}>{isRefund ? '-' : ''}{RP.money2(amount)}</span>
          </div>
        );
      })}
    </div>
  );
}

function StripeCardSetupModal({ customer, onClose, onSaved, consentText }) {
  const mountRef = useRef(null);
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const paymentElementRef = useRef(null);
  const setupIntentRef = useRef('');
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const live = rpHasLiveAuth();
  const consentPolicyVersion = '2026-07-22';
  const defaultConsent = `The customer authorizes Ranch Power to save this card securely with Stripe and charge it off-session only for approved Ranch Power invoices. Charges may occur when an invoice is issued and will equal the outstanding invoice balance plus any disclosed card-processing fee. The customer may revoke authorization before a charge by contacting Ranch Power.`;

  useEffect(() => {
    let cancelled = false;
    async function prepare() {
      if (!live) {
        setError('Saved cards are only available in live mode.');
        setBusy(false);
        return;
      }
      try {
        setBusy(true);
        setError('');
        const setup = await rpApi('/api/payments/setup-intent', {
          method: 'POST',
          body: JSON.stringify({
            customerId: customer && customer.id
          })
        });
        if (cancelled) return;
        setupIntentRef.current = setup.setupIntentId || '';
        const Stripe = await rpLoadStripeSdk();
        if (cancelled) return;
        const stripe = Stripe(setup.publishableKey);
        const elements = stripe.elements({ clientSecret: setup.clientSecret });
        const paymentElement = elements.create('payment', { layout: 'tabs' });
        stripeRef.current = stripe;
        elementsRef.current = elements;
        paymentElementRef.current = paymentElement;
        if (mountRef.current) paymentElement.mount(mountRef.current);
      } catch (err) {
        if (!cancelled) setError(err && err.message ? err.message : 'Could not prepare Stripe card setup.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    prepare();
    return () => {
      cancelled = true;
      try { paymentElementRef.current && paymentElementRef.current.destroy(); } catch (err) { }
    };
  }, [customer && customer.id]);

  const save = async () => {
    if (busy || saving || !stripeRef.current || !elementsRef.current) return;
    if (!consentConfirmed) {
      setError('Confirm the customer authorization before saving this card.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      });
      if (result.error) throw new Error(result.error.message || 'Stripe could not save the card.');
      const setupIntentId = result.setupIntent && result.setupIntent.id || setupIntentRef.current;
      const response = await rpApi('/api/payments/confirm-setup', {
        method: 'POST',
        body: JSON.stringify({
          consentConfirmed: true,
          consentPolicyVersion,
          consentText: defaultConsent,
          customerId: customer && customer.id,
          makeDefault: true,
          setupIntentId
        })
      });
      if (response.customer) {
        customer.invoiceOverrides = response.customer.invoiceOverrides || customer.invoiceOverrides || {};
        customer.acceptedPaymentMethods = response.customer.acceptedPaymentMethods || customer.acceptedPaymentMethods || {};
      }
      toast('Card saved securely with Stripe', 'ok');
      onSaved && onSaved(response);
      onClose && onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not save card.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Save Card" sub={customer && customer.name ? customer.name : 'Stripe vault'} onClose={onClose} width={560}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || saving || !live || !consentConfirmed} onClick={save} style={{ opacity: busy || saving || !live || !consentConfirmed ? 0.6 : 1 }}><Icon name="card" size={15} />{saving ? 'Saving...' : 'Save Card'}</button></>}>
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 13px', color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.45, marginBottom: 14 }}>
        Card details are entered in Stripe's secure element. Ranch Power stores only the Stripe vault reference, card brand, expiry, and last four digits. Saving a new card replaces the active card used for future invoice charges.
      </div>
      <div style={{ background: 'var(--amber-soft)', border: '1px solid var(--amber)', borderRadius: 10, padding: '10px 12px', color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 700, lineHeight: 1.45, marginBottom: 14 }}>{RP_CREDIT_CARD_TERMS_NOTE}</div>
      <div ref={mountRef} style={{ minHeight: 118, border: '1px solid var(--line)', borderRadius: 11, padding: 14, background: '#fff', marginBottom: 12 }} />
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, border: '1px solid var(--line)', borderRadius: 10, padding: '11px 12px', marginBottom: 12, background: 'var(--surface-2)', cursor: 'pointer' }}>
        <input type="checkbox" checked={consentConfirmed} onChange={event => { setConsentConfirmed(event.target.checked); setError(''); }} style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
        <span style={{ color: 'var(--ink)', fontSize: 12.5, lineHeight: 1.48 }}><strong>I confirm the customer/cardholder gave this authorization:</strong> {defaultConsent}</span>
      </label>
      {busy && <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>Preparing secure card entry...</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

Object.assign(window, { InvoiceDocument, DocMeta, DocLine, PaymentHistory, StripeCardSetupModal, payMeta, captureLabel, invoiceAdjustmentDisplayRows, invoiceDocumentTaxRows, RP_INVOICE_COMPANY });


/* ===== charts.jsx ===== */
/* ============================================================
   RANCH POWER — Chart toolkit (SVG, brand-styled)
   LineArea · GroupedBars · HeatGrid · RankBars · Donut · ColBars
   ============================================================ */

/* ---- Axis-light line + area chart, multi-series ---- */
function LineArea({ series, labels, h = 200, money, fmt }) {
  // series: [{ name, color, data:[] }]
  const ref = useRef(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    const on = () => { if (ref.current) setW(ref.current.clientWidth); };
    on(); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on);
  }, []);
  const padL = 44, padB = 24, padT = 10, padR = 8;
  const allVals = series.flatMap(s => s.data);
  const max = Math.max(...allVals) * 1.12 || 1;
  const min = 0;
  const iw = Math.max(60, w - padL - padR), ih = h - padB - padT;
  const x = i => labels.length <= 1 ? padL + iw / 2 : padL + (i / Math.max(1, labels.length - 1)) * iw;
  const y = v => padT + ih - ((v - min) / (max - min)) * ih;
  const fmtV = v => fmt ? fmt(v) : money ? RP.money(v) : v.toLocaleString();
  const ticks = 4;
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', overflow: 'visible' }}>
        {Array.from({ length: ticks + 1 }).map((_, t) => {
          const val = min + (max - min) * (t / ticks);
          const yy = y(val);
          return (
            <g key={t}>
              <line x1={padL} x2={w - padR} y1={yy} y2={yy} stroke="var(--line-2)" strokeWidth="1" />
              <text x={padL - 8} y={yy + 3.5} textAnchor="end" fontSize="10" fill="var(--faint)" fontFamily="var(--ff-mono)">{fmtV(Math.round(val)).replace(/,000$/, 'k').replace('$', '$')}</text>
            </g>
          );
        })}
        {series.map((s, si) => {
          const gid = 'la' + si + Math.round(s.data[0] || 0);
          const line = s.data.map((v, i) => (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1)).join(' ');
          const area = line + ` L${x(s.data.length - 1)} ${padT + ih} L${padL} ${padT + ih} Z`;
          return (
            <g key={si}>
              {si === 0 && <><defs><linearGradient id={gid} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={s.color} stopOpacity="0.18" /><stop offset="1" stopColor={s.color} stopOpacity="0" /></linearGradient></defs><path d={area} fill={`url(#${gid})`} /></>}
              <path d={line} fill="none" stroke={s.color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              {s.data.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="2.6" fill="var(--surface)" stroke={s.color} strokeWidth="1.8" />)}
            </g>
          );
        })}
        {labels.map((l, i) => ((labels.length <= 12 || i % 2 === 0) &&
          <text key={i} x={x(i)} y={h - 6} textAnchor="middle" fontSize="10" fill="var(--muted)" fontFamily="var(--ff-mono)">{l}</text>
        ))}
      </svg>
    </div>
  );
}

/* ---- Grouped/single column bars with x labels ---- */
function ColBars({ data, color = 'var(--amber)', h = 190, money, highlightLast, valueKey = 'value', labelKey = 'label' }) {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  useEffect(() => { const on = () => { if (ref.current) setW(ref.current.clientWidth); }; on(); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
  const padL = 40, padB = 26, padT = 16;
  const vals = data.map(d => d[valueKey]);
  const max = Math.max(...vals) * 1.14 || 1;
  const iw = Math.max(60, w - padL - 6), ih = h - padB - padT;
  const bw = iw / data.length;
  const ticks = 3;
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', overflow: 'visible' }}>
        {Array.from({ length: ticks + 1 }).map((_, t) => {
          const val = max * (t / ticks), yy = padT + ih - (val / max) * ih;
          return <g key={t}><line x1={padL} x2={w - 6} y1={yy} y2={yy} stroke="var(--line-2)" strokeWidth="1" /><text x={padL - 7} y={yy + 3.5} textAnchor="end" fontSize="9.5" fill="var(--faint)" fontFamily="var(--ff-mono)">{money ? '$' + Math.round(val / 1000) + 'k' : Math.round(val)}</text></g>;
        })}
        {data.map((d, i) => {
          const bh = (d[valueKey] / max) * ih;
          const bx = padL + i * bw + bw * 0.18, bwid = bw * 0.64;
          const last = highlightLast && i === data.length - 1;
          return (
            <g key={i}>
              <rect x={bx} y={padT + ih - bh} width={bwid} height={bh} rx="3" fill={last ? 'var(--amber)' : color} opacity={highlightLast ? (last ? 1 : 0.32 + (i / data.length) * 0.5) : 1} />
              <text x={bx + bwid / 2} y={h - 8} textAnchor="middle" fontSize="9.5" fill="var(--muted)" fontFamily="var(--ff-mono)">{d[labelKey]}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ---- Hour × Day heat grid ---- */
function HeatGrid({ rows, cols, data, color = 'var(--amber)' }) {
  // data: [{ day, cells:[] }] aligned to cols
  const max = Math.max(...data.flatMap(r => r.cells)) || 1;
  const [hover, setHover] = useState(null);
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 520 }}>
        <div style={{ display: 'grid', gridTemplateColumns: `46px repeat(${cols.length}, 1fr)`, gap: 4 }}>
          <div />
          {cols.map(c => <div key={c} style={{ textAlign: 'center', fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--ff-mono)', paddingBottom: 4 }}>{c}</div>)}
          {data.map(row => (
            <React.Fragment key={row.day}>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--ink-soft)' }}>{row.day}</div>
              {row.cells.map((v, i) => {
                const t = v / max;
                const on = hover && hover[0] === row.day && hover[1] === i;
                return (
                  <div key={i} onMouseEnter={() => setHover([row.day, i, v])} onMouseLeave={() => setHover(null)}
                    title={`${row.day} ${cols[i]} · ${v} orders`}
                    style={{ aspectRatio: '1.4', borderRadius: 5, background: t === 0 ? 'var(--surface-2)' : `color-mix(in oklab, ${color} ${15 + t * 85}%, var(--surface))`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: t > 0.55 ? '#fff' : 'var(--muted)', cursor: 'default', outline: on ? '2px solid var(--navy)' : 'none', transition: 'outline .1s' }}>
                    {t > 0.4 ? v : ''}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 10.5, color: 'var(--faint)' }}>Fewer</span>
          {[0.15, 0.4, 0.65, 0.9].map(t => <span key={t} style={{ width: 16, height: 12, borderRadius: 3, background: `color-mix(in oklab, ${color} ${15 + t * 85}%, var(--surface))` }} />)}
          <span style={{ fontSize: 10.5, color: 'var(--faint)' }}>More orders</span>
        </div>
      </div>
    </div>
  );
}

/* ---- Ranked horizontal bars ---- */
function RankBars({ items, money, color = 'var(--navy)', showGrowth }) {
  // items: [{ label, value, growth, sub, swatch }]
  const max = Math.max(...items.map(i => i.value)) || 1;
  const growthText = value => value == null ? 'New' : `${value >= 0 ? '+' : ''}${value}%`;
  const growthColor = value => value == null ? 'var(--info)' : value >= 0 ? 'var(--ok)' : 'var(--danger)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      {items.map((it, i) => (
        <div key={i}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5, gap: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {it.swatch && <span style={{ width: 10, height: 10, borderRadius: 3, background: it.swatch, flexShrink: 0 }} />}
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
              {it.sub && <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>{it.sub}</span>}
            </span>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexShrink: 0 }}>
              <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{money ? RP.money(it.value) : it.value.toLocaleString()}</span>
              {showGrowth && <span style={{ fontSize: 11.5, fontWeight: 700, color: growthColor(it.growth), width: 42, textAlign: 'right' }}>{growthText(it.growth)}</span>}
            </span>
          </div>
          <div style={{ height: 8, background: 'var(--surface-2)', borderRadius: 100, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: (it.value / max * 100) + '%', background: it.swatch || (showGrowth && it.growth < 0 ? 'var(--danger)' : color), borderRadius: 100, transition: 'width .5s ease' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---- Donut ---- */
function Donut({ items, size = 160, thickness = 26, centerLabel, centerValue }) {
  // items: [{ label, value, color }]
  const total = items.reduce((s, i) => s + i.value, 0) || 1;
  const r = (size - thickness) / 2, cx = size / 2, cy = size / 2, C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={thickness} />
        {items.map((it, i) => {
          const frac = it.value / total, dash = frac * C;
          const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={it.color} strokeWidth={thickness} strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc} transform={`rotate(-90 ${cx} ${cy})`} />;
          acc += dash; return el;
        })}
        {centerValue != null && <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="700" fontFamily="var(--ff-display)" fill="var(--ink)">{centerValue}</text>}
        {centerLabel && <text x={cx} y={cy + 15} textAnchor="middle" fontSize="10" fill="var(--muted)" fontFamily="var(--ff-display)" letterSpacing="1.4" >{centerLabel.toUpperCase()}</text>}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 140 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: it.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: 'var(--ink-soft)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
            <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{Math.round(it.value / total * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

Object.assign(window, { LineArea, ColBars, HeatGrid, RankBars, Donut });


/* ===== tweaks-panel.jsx ===== */
// @ds-adherence-ignore -- omelette starter scaffold (raw elements/hex/px by design)

/* BEGIN USAGE */
// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
// Exports (to window): useTweaks, TweaksPanel, TweakSection, TweakRow, TweakSlider,
//   TweakToggle, TweakRadio, TweakSelect, TweakText, TweakNumber, TweakColor, TweakButton.
//
// Owns the host protocol (listens for __activate_edit_mode / __deactivate_edit_mode,
// posts __edit_mode_available / __edit_mode_set_keys / __edit_mode_dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "palette": ["#D97757", "#29261b", "#f6f4ef"],
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        options={['#D97757', '#2A6FDB', '#1F8A5B', '#7A5AE0']}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakColor  label="Palette" value={t.palette}
//                        options={[['#D97757', '#29261b', '#f6f4ef'],
//                                  ['#475569', '#0f172a', '#f1f5f9']]}
//                        onChange={(v) => setTweak('palette', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// TweakRadio is the segmented control for 2–3 short options (auto-falls-back to
// TweakSelect past ~16/~10 chars per label); reach for TweakSelect directly when
// options are many or long. For color tweaks always curate 3-4 options rather than
// a free picker; an option can also be a whole 2–5 color palette (the stored value
// is the array). The Tweak* controls are a floor, not a ceiling — build custom
// controls inside the panel if a tweak calls for UI they don't cover.
/* END USAGE */
// ─────────────────────────────────────────────────────────────────────────────

const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    transform:scale(var(--dc-inv-zoom,1));transform-origin:bottom right;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;box-sizing:border-box;width:100%;min-width:0;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;min-height:22px;
    border-radius:6px;cursor:default;padding:4px 6px;line-height:1.2;
    overflow-wrap:anywhere}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;box-sizing:border-box;min-width:0;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}

  .twk-chips{display:flex;gap:6px}
  .twk-chip{position:relative;appearance:none;flex:1;min-width:0;height:46px;
    padding:0;border:0;border-radius:6px;overflow:hidden;cursor:default;
    box-shadow:0 0 0 .5px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.06);
    transition:transform .12s cubic-bezier(.3,.7,.4,1),box-shadow .12s}
  .twk-chip:hover{transform:translateY(-1px);
    box-shadow:0 0 0 .5px rgba(0,0,0,.18),0 4px 10px rgba(0,0,0,.12)}
  .twk-chip[data-on="1"]{box-shadow:0 0 0 1.5px rgba(0,0,0,.85),
    0 2px 6px rgba(0,0,0,.15)}
  .twk-chip>span{position:absolute;top:0;bottom:0;right:0;width:34%;
    display:flex;flex-direction:column;box-shadow:-1px 0 0 rgba(0,0,0,.1)}
  .twk-chip>span>i{flex:1;box-shadow:0 -1px 0 rgba(0,0,0,.1)}
  .twk-chip>span>i:first-child{box-shadow:none}
  .twk-chip svg{position:absolute;top:6px;left:6px;width:13px;height:13px;
    filter:drop-shadow(0 1px 1px rgba(0,0,0,.3))}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (__edit_mode_set_keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  // Accepts either setTweak('key', value) or setTweak({ key: value, ... }) so a
  // useState-style call doesn't write a "[object Object]" key into the persisted
  // JSON block.
  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues((prev) => ({ ...prev, ...edits }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits }, '*');
    // Same-window signal so in-page listeners (deck-stage rail thumbnails)
    // can react — the parent message only reaches the host, not peers.
    window.dispatchEvent(new CustomEvent('tweakchange', { detail: edits }));
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts __edit_mode_dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes __deactivate_edit_mode back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);
      else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel" data-omelette-chrome=""
           style={{ right: offsetRef.current.x, bottom: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">
          {children}
        </div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  // Segments wrap mid-word once per-segment width runs out. The track is
  // ~248px (280 panel − 28 body pad − 4 seg pad), each button loses 12px
  // to its own padding, and 11.5px system-ui averages ~6.3px/char — so 2
  // options fit ~16 chars each, 3 fit ~10. Past that (or >3 options), fall
  // back to a dropdown rather than wrap.
  const labelLen = (o) => String(typeof o === 'object' ? o.label : o).length;
  const maxLen = options.reduce((m, o) => Math.max(m, labelLen(o)), 0);
  const fitsAsSegments = maxLen <= ({ 2: 16, 3: 10 }[options.length] ?? 0);
  if (!fitsAsSegments) {
    // <select> emits strings — map back to the original option value so the
    // fallback stays type-preserving (numbers, booleans) like the segment path.
    const resolve = (s) => {
      const m = options.find((o) => String(typeof o === 'object' ? o.value : o) === s);
      return m === undefined ? s : typeof m === 'object' ? m.value : m;
    };
    return <TweakSelect label={label} value={value} options={options}
                        onChange={(s) => onChange(resolve(s))} />;
  }
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

// Relative-luminance contrast pick — checkmarks drawn over a swatch need to
// read on both #111 and #fafafa without per-option configuration. Hex input
// only (#rgb / #rrggbb); named or rgb()/hsl() colors fall through to "light".
function __twkIsLight(hex) {
  const h = String(hex).replace('#', '');
  const x = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.padEnd(6, '0');
  const n = parseInt(x.slice(0, 6), 16);
  if (Number.isNaN(n)) return true;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return r * 299 + g * 587 + b * 114 > 148000;
}

const __TwkCheck = ({ light }) => (
  <svg viewBox="0 0 14 14" aria-hidden="true">
    <path d="M3 7.2 5.8 10 11 4.2" fill="none" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round"
          stroke={light ? 'rgba(0,0,0,.78)' : '#fff'} />
  </svg>
);

// TweakColor — curated color/palette picker. Each option is either a single
// hex string or an array of 1-5 hex strings; the card adapts — a lone color
// renders solid, a palette renders colors[0] as the hero (left ~2/3) with the
// rest stacked in a sharp column on the right. onChange emits the
// option in the shape it was passed (string stays string, array stays array).
// Without options it falls back to the native color input for back-compat.
function TweakColor({ label, value, options, onChange }) {
  if (!options || !options.length) {
    return (
      <div className="twk-row twk-row-h">
        <div className="twk-lbl"><span>{label}</span></div>
        <input type="color" className="twk-swatch" value={value}
               onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  // Native <input type=color> emits lowercase hex per the HTML spec, so
  // compare case-insensitively. String() guards JSON.stringify(undefined),
  // which returns the primitive undefined (no .toLowerCase).
  const key = (o) => String(JSON.stringify(o)).toLowerCase();
  const cur = key(value);
  return (
    <TweakRow label={label}>
      <div className="twk-chips" role="radiogroup">
        {options.map((o, i) => {
          const colors = Array.isArray(o) ? o : [o];
          const [hero, ...rest] = colors;
          const sup = rest.slice(0, 4);
          const on = key(o) === cur;
          return (
            <button key={i} type="button" className="twk-chip" role="radio"
                    aria-checked={on} data-on={on ? '1' : '0'}
                    aria-label={colors.join(', ')} title={colors.join(' · ')}
                    style={{ background: hero }}
                    onClick={() => onChange(o)}>
              {sup.length > 0 && (
                <span>
                  {sup.map((c, j) => <i key={j} style={{ background: c }} />)}
                </span>
              )}
              {on && <__TwkCheck light={__twkIsLight(hero)} />}
            </button>
          );
        })}
      </div>
    </TweakRow>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});


/* ===== desktop_shell.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop Operations Console : shell + dashboard
   ============================================================ */

const DESK_NAV = [
  { id: 'accounting', label: 'Accounting', icon: 'receipt' },
  { id: 'dashboard', label: 'Command Center', icon: 'home' },
  { id: 'tasks', label: 'Tasks', icon: 'tasks' },
  { id: 'contacts', label: 'Website Inbox', icon: 'mail' },
  { id: 'email-log', label: 'Email Delivery', icon: 'mail' },
  { id: 'orders', label: 'Orders', icon: 'orders' },
  { id: 'invoicing', label: 'Invoicing', icon: 'receipt' },
  { id: 'customers', label: 'Customers', icon: 'customers' },
  { id: 'pipeline', label: 'Pipeline', icon: 'trend' },
  { id: 'marketing', label: 'Marketing', icon: 'mail' },
  { id: 'events', label: 'Events', icon: 'calendar' },
  { id: 'goals', label: 'Sales Goals', icon: 'target' },
  { id: 'move-stock', label: 'Move Stock', icon: 'layers' },
  { id: 'inventory', label: 'Inventory', icon: 'inventory' },
  { id: 'dispatch', label: 'Dispatch & Logistics', icon: 'dispatch' },
  { id: 'production', label: 'Production', icon: 'production' },
  { id: 'procurement', label: 'Procurement', icon: 'procurement' },
  { id: 'fba', label: 'FBA', icon: 'fba' },
  { id: 'trade', label: 'Trade Spend', icon: 'trade' },
  { id: 'expenses', label: 'Expenses', icon: 'receipt' },
  { id: 'reports', label: 'Reports', icon: 'download' },
  { id: 'analytics', label: 'Analytics', icon: 'analytics' },
  { id: 'admin', label: 'Admin', icon: 'admin' },
];

const DESK_NAV_ACCESS = {
  accounting: ['ops', 'accountant'],
  dashboard: ['ops', 'sales_manager', 'warehouse', 'dispatch', 'accountant', 'marketing', 'rep', 'partner'],
  'email-log': ['ops', 'sales_manager', 'warehouse', 'dispatch', 'accountant', 'marketing'],
  tasks: ['ops', 'sales_manager', 'warehouse', 'dispatch', 'accountant', 'marketing', 'rep', 'partner'],
  orders: ['ops', 'sales_manager', 'warehouse', 'dispatch', 'accountant', 'rep', 'partner'],
  invoicing: ['ops', 'sales_manager', 'accountant', 'rep', 'partner'],
  customers: ['ops', 'sales_manager', 'marketing', 'rep', 'partner'],
  pipeline: ['ops', 'sales_manager', 'marketing', 'rep', 'partner'],
  marketing: ['ops', 'sales_manager', 'marketing'],
  events: ['ops', 'sales_manager', 'warehouse', 'dispatch', 'marketing', 'rep', 'partner'],
  goals: ['sales_manager', 'rep', 'partner'],
  'move-stock': ['ops', 'warehouse'],
  inventory: ['ops', 'warehouse', 'dispatch'],
  dispatch: ['ops', 'warehouse', 'dispatch'],
  production: ['ops', 'warehouse'],
  procurement: ['ops', 'warehouse', 'accountant'],
  fba: ['ops', 'warehouse', 'accountant'],
  trade: ['ops', 'sales_manager', 'accountant', 'marketing'],
  expenses: ['ops', 'sales_manager', 'accountant', 'rep', 'partner'],
  reports: ['ops', 'sales_manager', 'warehouse', 'dispatch', 'accountant', 'marketing'],
  analytics: ['ops', 'sales_manager', 'accountant', 'marketing'],
  admin: ['ops']
};

function deskNormalizeRole(role) {
  const value = String(role || '').trim();
  if (typeof rpNormalizeRole === 'function') return rpNormalizeRole(value);
  return value.toLowerCase().replace(/[\s-]+/g, '_');
}

function deskActiveRole(fallbackRole) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  return deskNormalizeRole(profile.role || fallbackRole || 'ops');
}

function deskCanUseNav(navId, role) {
  if (navId === 'account') return true;
  if (navId === 'contacts') return deskCanUseNav('dashboard', role);
  const normalized = deskNormalizeRole(role);
  if (normalized === 'admin' || normalized === 'leadership') return true;
  return (DESK_NAV_ACCESS[navId] || []).includes(normalized);
}

function deskVisibleNav(role) {
  return DESK_NAV.filter(item => deskCanUseNav(item.id, role));
}

const DESK_NAV_GROUPS = [
  { id: 'daily', label: 'Daily work', ids: ['accounting', 'dashboard', 'tasks', 'events'] },
  { id: 'sales', label: 'Sales & accounts', ids: ['customers', 'pipeline', 'orders', 'invoicing'] },
  { id: 'inventory', label: 'Inventory & production', ids: ['move-stock', 'inventory', 'production', 'procurement', 'dispatch', 'fba'] },
  { id: 'communications', label: 'Communications', ids: ['contacts', 'email-log', 'marketing'] },
  { id: 'planning', label: 'Planning & reporting', ids: ['goals', 'trade', 'expenses', 'reports', 'analytics'] },
  { id: 'administration', label: 'Administration', ids: ['admin'] },
];

function deskVisibleNavGroups(role) {
  const items = new Map(deskVisibleNav(role).map(item => [item.id, item]));
  return DESK_NAV_GROUPS.map(group => ({ ...group, items: group.ids.map(id => items.get(id)).filter(Boolean) })).filter(group => group.items.length);
}

function deskNavGroupId(navId) {
  return (DESK_NAV_GROUPS.find(group => group.ids.includes(navId)) || {}).id || '';
}

function deskNavGroupPreferenceKey(role, profileId) {
  return `rp-operations-menu:v1:${encodeURIComponent(profileId || 'demo')}:${encodeURIComponent(deskNormalizeRole(role))}`;
}

function deskReadNavGroupPreferences(storageKey, activeNav) {
  const expanded = Object.fromEntries(DESK_NAV_GROUPS.map(group => [group.id, group.id === 'daily']));
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
      for (const group of DESK_NAV_GROUPS) if (typeof saved[group.id] === 'boolean') expanded[group.id] = saved[group.id];
    }
  } catch (_) {}
  const activeGroup = deskNavGroupId(activeNav);
  if (activeGroup) expanded[activeGroup] = true;
  return expanded;
}

function deskSaveNavGroupPreferences(storageKey, expanded) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(DESK_NAV_GROUPS.map(group => [group.id, expanded[group.id] === true]))));
  } catch (_) {}
}

function deskInventoryWorkflowRoute() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('workspace') !== 'move-stock') return null;
  const id = params.get('workflow') || '';
  return { nav: 'move-stock', detail: /^WF-[A-Z0-9_-]{8,100}$/i.test(id) ? { type: 'inventory-workflow', id } : null };
}

function deskDefaultWorkspace(role) {
  return deskNormalizeRole(role) === 'accountant' ? 'accounting' : 'dashboard';
}

const DESK_DETAIL_WORKSPACES = {order:'orders',invoice:'invoicing',customer:'customers',event:'events',task:'tasks',warehouse:'inventory','stock-request':'inventory','field-receipt':'inventory','open-cans':'inventory','inventory-view':'inventory','inventory-workflow':'move-stock','production-history':'production','production-run':'production','admin-tab':'admin',report:'reports',bundle:'reports'};
function deskRouteFromLocation(role) {
  const params=new URLSearchParams(window.location.search);
  const requested=params.get('workspace');
  const nav=(DESK_NAV.some(item=>item.id===requested)||requested==='account')&&deskCanUseNav(requested,role)?requested:deskDefaultWorkspace(role);
  if(nav==='move-stock') return deskInventoryWorkflowRoute() || {nav,detail:null};
  const type=params.get('detail'),id=params.get('record'),view=params.get('view');
  if(!DESK_DETAIL_WORKSPACES[type] || !deskCanUseNav(DESK_DETAIL_WORKSPACES[type],role))return {nav,detail:null};
  if(!id&&!['inventory-view','admin-tab','open-cans','production-history'].includes(type))return {nav,detail:null};
  const detail={type};
  if(id && id.length<=200)detail.id=id;
  if(view && /^[a-z-]{1,50}$/.test(view))detail.view=view;
  const back=params.get('backToOrder');if(type==='invoice'&&back&&back.length<=200)detail.backToOrder=back;
  return {nav,detail};
}
function deskInitialWorkspace(role) {return deskRouteFromLocation(role).nav;}
function deskRouteUrl(nav,detail) {
  const url=new URL(window.location.href);
  ['workspace','workflow','detail','record','view','backToOrder'].forEach(key=>url.searchParams.delete(key));
  url.searchParams.set('workspace',nav);
  if(nav==='move-stock') {if(/^WF-[A-Z0-9_-]{8,100}$/i.test(detail?.id||''))url.searchParams.set('workflow',detail.id);}
  else if(detail&&DESK_DETAIL_WORKSPACES[detail.type]) {
    url.searchParams.set('detail',detail.type);
    if(detail.id)url.searchParams.set('record',detail.id);
    if(detail.view)url.searchParams.set('view',detail.view);
    if(detail.backToOrder)url.searchParams.set('backToOrder',detail.backToOrder);
  }
  return url;
}
function deskWriteRoute(nav,detail,mode='push') {
  const url=deskRouteUrl(nav,detail);
  const previous=window.history.state||{};
  const index=Number.isInteger(previous.ranchPowerOpsIndex)?previous.ranchPowerOpsIndex:0;
  const push=mode==='push'&&url.toString()!==window.location.href;
  window.history[push?'pushState':'replaceState']({...previous,ranchPowerOpsIndex:index+(push?1:0)},'',url.toString());
}
// Workflow autosaves update the current history entry, never add navigation.
function deskWriteInventoryWorkflowRoute(nav,detail) {deskWriteRoute(nav,detail,'replace');}
function DeskAccessDenied({ go, nav, role }) {
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <PageHead eyebrow="Limited Access" title="This section is not available"
        sub="Your current role does not include this workspace area." />
      <div className="card" style={{ maxWidth: 620, padding: '22px 24px' }}>
        <LiveDataGuardNotice title="Access is restricted for this role.">
          {typeof rpRoleLabel === 'function' ? rpRoleLabel(role) : role} accounts only see the tools that match their responsibilities.
        </LiveDataGuardNotice>
        <button className="btn btn-primary" onClick={() => go('dashboard')}><Icon name="home" size={15} />Back to Command Center</button>
      </div>
    </div>
  );
}

function DeskBreadcrumbs({nav,detail,role,go}) {
  const home=deskDefaultWorkspace(role),label=id=>id==='account'?'My Account':DESK_NAV.find(item=>item.id===id)?.label||id;
  const crumbs=[{nav:home,label:label(home)}];
  if(nav!==home)crumbs.push({nav,label:label(nav)});
  if(detail)crumbs.push({label:detail.view?({pricing:'Price Lists','open-cans':'Open Cans',lots:'Lot Master'}[detail.view]||detail.view.replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase())):detail.id||detail.type.replace(/-/g,' ')});
  return <nav aria-label="Breadcrumb" className="rp-breadcrumbs"><ol>{crumbs.map((crumb,index)=><li key={index}>{index>0&&<span aria-hidden="true">/</span>}{index===crumbs.length-1?<span aria-current="page" title={crumb.label}>{crumb.label}</span>:<a href={deskRouteUrl(crumb.nav,null).toString()} onClick={event=>{if(event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;event.preventDefault();go(crumb.nav);}}>{crumb.label}</a>}</li>)}</ol></nav>;
}

function DeskSidebarNavigation({ nav, role, mini, go, salesAnalytics, dispatchDirectory, navigationVersion = 0 }) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : null;
  const storageKey = deskNavGroupPreferenceKey(role, profile && profile.id);
  const scope = `${storageKey}:${nav}:${navigationVersion}`;
  const [preferences, setPreferences] = useState(() => ({ scope, expanded: deskReadNavGroupPreferences(storageKey, nav) }));
  // Derive the new account/navigation state immediately; a pending effect must
  // never expose another account's choices or leave the active group hidden.
  const expanded = preferences.scope === scope ? preferences.expanded : deskReadNavGroupPreferences(storageKey, nav);
  useEffect(() => { setPreferences(current => current.scope === scope ? current : { scope, expanded: deskReadNavGroupPreferences(storageKey, nav) }); }, [scope, storageKey, nav]);
  const toggle = id => {
    const next = { ...expanded, [id]: !expanded[id] };
    setPreferences({ scope, expanded: next });
    deskSaveNavGroupPreferences(storageKey, next);
  };
  return <nav className="rp-scroll" aria-label="Operations sections" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: mini ? '10px 10px 12px' : '4px 12px 12px', display: 'flex', flexDirection: 'column', gap: mini ? 9 : 5 }}>
    {deskVisibleNavGroups(role).map((group, index) => <div key={group.id} data-desk-nav-group={group.id} style={{ flexShrink: 0, borderTop: index ? '1px solid oklch(1 0 0 / 0.1)' : undefined, paddingTop: index ? 7 : 0 }}>
      {!mini && <button type="button" id={`desk-nav-heading-${group.id}`} aria-expanded={expanded[group.id] === true} aria-controls={`desk-nav-items-${group.id}`} onClick={() => toggle(group.id)} style={{ border: 'none', background: 'transparent', color: 'oklch(0.83 0.015 235)', width: '100%', minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '7px 8px', textAlign: 'left', fontSize: 11, fontWeight: 650, letterSpacing: '.025em' }}>
        <span>{group.label}</span><span aria-hidden="true" style={{ display: 'flex', transform: expanded[group.id] ? 'rotate(90deg)' : undefined }}><Icon name="chevR" size={13} /></span>
      </button>}
      <div id={`desk-nav-items-${group.id}`} role="group" aria-label={mini ? group.label : undefined} aria-labelledby={mini ? undefined : `desk-nav-heading-${group.id}`} hidden={!mini && !expanded[group.id]}>
        {group.items.map(item => {
          const on = nav === item.id;
          const badge = deskNavBadge(item.id, salesAnalytics, dispatchDirectory);
          return <button type="button" key={item.id} data-desk-nav-item={item.id} onClick={() => go(item.id)} aria-label={item.label} aria-current={on ? 'page' : undefined} title={mini ? `${item.label} · ${group.label}` : undefined} style={{ display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'flex-start', gap: 11, padding: mini ? '11px 0' : '9px 12px', borderRadius: 8, border: 'none', background: on ? 'oklch(1 0 0 / 0.12)' : 'transparent', color: on ? '#fff' : 'oklch(0.85 0.015 235)', fontSize: 13.5, fontWeight: on ? 600 : 500, textAlign: 'left', width: '100%', position: 'relative', transition: 'background .12s' }}
            onMouseEnter={e => { if (!on) e.currentTarget.style.background = 'oklch(1 0 0 / 0.12)'; }}
            onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
            {on && !mini && <span style={{ position: 'absolute', left: -12, top: 6, bottom: 6, width: 3, background: 'var(--amber)', borderRadius: 3 }} />}
            <span style={{ position: 'relative', display: 'flex' }}><Icon name={item.icon} size={18} />
              {badge > 0 && mini && <span style={{ position: 'absolute', top: -5, right: -6, background: 'var(--amber)', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 100, minWidth: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{badge}</span>}
            </span>
            {!mini && <span style={{ flex: 1 }}>{item.label}</span>}
            {badge > 0 && !mini && <span style={{ background: 'var(--amber)', color: '#fff', fontSize: 10.5, fontWeight: 700, borderRadius: 100, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{badge}</span>}
          </button>;
        })}
      </div>
    </div>)}
  </nav>;
}

/* ---- Desktop notifications (ops console) ---- */
const DESK_NOTES = [
  { i: 'alert', tone: 'danger', t: '3 damaged-case reports awaiting write-off', m: 'DMG-88 · DMG-86 · DMG-84', when: '12m ago', unread: true, nav: 'inventory' },
  { i: 'flag', tone: 'warn', t: 'SO-1046 needs manager approval', m: 'Highway 5 Pit Stop · discount over threshold', when: '40m ago', unread: true, nav: 'orders', detail: { type: 'order', id: 'SO-1046' } },
  { i: 'procurement', tone: 'warn', t: 'PO-222 lids delayed — blocks PRD-118', m: 'Summit Lid Supply · High risk', when: '1h ago', unread: true, nav: 'procurement', demoOnly: true },
  { i: 'fba', tone: 'info', t: 'FBA receiving discrepancy on FBA-8813', m: 'YYZ4 · −10 cases vs shipped', when: '2h ago', nav: 'fba' },
  { i: 'truck', tone: 'warn', t: 'CHEP pallet variance on SH-330', m: '8 shipped · 7 received', when: '3h ago', nav: 'dispatch' },
  { i: 'check', tone: 'ok', t: 'SO-1038 delivered & signed', m: 'Mountain Gym · 12 cases', when: 'Yesterday', nav: 'orders', detail: { type: 'order', id: 'SO-1038' } },
];

const LIVE_DEMO_ONLY_MODULES = {
  fba: {
    label: 'FBA',
    icon: 'fba',
    scope: ['Amazon shipments', 'FBA receiving', 'Discrepancy cases', 'Marketplace inventory']
  }
};

function deskRows(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === 'object') return Object.values(value).filter(Boolean);
  return [];
}

function rpIsLiveDemoOnlyModule(navId) {
  return rpHasLiveAuth() && !!LIVE_DEMO_ONLY_MODULES[navId];
}

const DESK_CLOSED_TASK_STATUSES = new Set(['Done', 'Closed', 'Cancelled']);
const DESK_ACTIONABLE_ORDER_STATUSES = new Set(['Requested', 'Submitted', 'Needs Approval', 'On Hold']);
const DESK_CLOSED_DISPATCH_STATUSES = new Set(['Delivered', 'Complete', 'Cancelled', 'Closed']);
const DESK_OPEN_EXPENSE_STATUSES = new Set(['Submitted', 'Needs Info']);
const DESK_STOCK_APPROVAL_STATUSES = new Set(['Requested', 'Pending']);
const DESK_FIELD_RECEIPT_APPROVAL_STATUSES = new Set(['Pending']);
const DESK_CUSTOMER_APPROVAL_STATUSES = new Set(['Prospect', 'Pending', 'Submitted', 'Needs Approval', 'Review']);

function deskOpenTasks() {
  return deskRows(RP.TASKS).filter(task => !DESK_CLOSED_TASK_STATUSES.has(String(task.status || '')));
}

function deskActionableOrders() {
  return deskRows(RP.ORDERS).filter(order => DESK_ACTIONABLE_ORDER_STATUSES.has(String(order.status || '')));
}

function deskApprovalOrders() {
  return deskRows(RP.ORDERS).filter(order => ['Needs Approval', 'On Hold'].includes(String(order.status || '')));
}

function deskInventoryLocationName(id) {
  const loc = RP.loc && RP.loc(id);
  return (loc && loc.name) || id || 'Unassigned';
}

function deskTotalLineCases(lines) {
  return (Array.isArray(lines) ? lines : []).reduce((sum, line) => sum + Number(line.qty || line.quantity || 0) + Number(line.damagedQuantity || 0), 0);
}

function deskCustomerNeedsApproval(customer) {
  if (!customer || rpCustomerOrderReady(customer)) return false;
  const status = String(customer.status || 'Prospect').trim() || 'Prospect';
  if (['Draft', 'Inactive', 'Disabled', 'Archived', 'Deleted'].includes(status)) return false;
  return DESK_CUSTOMER_APPROVAL_STATUSES.has(status) || status.toLowerCase().includes('approval');
}

function deskIsApprovalTask(task) {
  if (!task || DESK_CLOSED_TASK_STATUSES.has(String(task.status || ''))) return false;
  const id = String(task.id || '');
  const title = String(task.title || '');
  const category = String(task.cat || task.category || '');
  return /^RMV-/i.test(id)
    || /^Review .* request:/i.test(title)
    || /^Approve customer:/i.test(title)
    || (category === 'Admin' && /\b(approve|approval|review|remove|delete|deletion)\b/i.test(title));
}

function deskApprovalItems() {
  const taskItems = deskOpenTasks()
    .filter(deskIsApprovalTask)
    .map(task => ({
      customerId: task.cust || '',
      detail: { type: 'task', id: task.id },
      icon: /^RMV-/i.test(String(task.id || '')) ? 'alert' : 'tasks',
      id: task.id,
      kind: 'task',
      meta: [rpTaskOwnerLabel(task.owner), task.due ? `Due ${String(task.due).slice(5)}` : ''].filter(Boolean).join(' - '),
      nav: 'tasks',
      status: task.status || 'To Do',
      title: task.title || task.id
    }));
  const orderItems = deskApprovalOrders().map(order => ({
    detail: { type: 'order', id: order.id },
    icon: 'orders',
    id: order.id,
    kind: 'order',
    meta: `${RP.cust(order.cust).name} - ${order.flag || order.reason || 'Review required'}`,
    nav: 'orders',
    status: order.status,
    title: order.id
  }));
  const taskCustomerIds = new Set(taskItems.map(item => item.customerId).filter(Boolean));
  const visibleCustomers = typeof rpVisibleCustomers === 'function' ? rpVisibleCustomers(RP.CUSTOMERS) : (RP.CUSTOMERS || []);
  const customerItems = deskRows(visibleCustomers)
    .filter(customer => deskCustomerNeedsApproval(customer) && !taskCustomerIds.has(customer.id))
    .map(customer => ({
      customerId: customer.id,
      detail: { type: 'customer', id: customer.id },
      icon: 'customers',
      id: customer.id,
      kind: 'customer',
      meta: [customer.type || 'Account', customer.territory, customer.rep].filter(Boolean).join(' - '),
      nav: 'customers',
      status: customer.status || 'Prospect',
      title: customer.name || customer.id
    }));
  const stockRequestItems = deskRows(RP.STOCK_REQUESTS)
    .filter(request => DESK_STOCK_APPROVAL_STATUSES.has(String(request.status || '')))
    .map(request => ({
      detail: { type: 'stock-request', id: request.id },
      icon: 'layers',
      id: request.id,
      kind: 'stock-request',
      meta: [
        request.requester || request.requestedBy || 'Field rep',
        `${request.totalCases || deskTotalLineCases(request.lines)} cs`,
        `${deskInventoryLocationName(request.source)} -> ${deskInventoryLocationName(request.destination)}`
      ].filter(Boolean).join(' - '),
      nav: 'inventory',
      status: request.status || 'Pending',
      title: `Stock request ${request.id}`
    }));
  const fieldReceiptItems = deskRows(RP.FIELD_RECEIPTS)
    .filter(receipt => DESK_FIELD_RECEIPT_APPROVAL_STATUSES.has(String(receipt.status || '')))
    .map(receipt => ({
      detail: { type: 'field-receipt', id: receipt.id },
      icon: 'download',
      id: receipt.id,
      kind: 'field-receipt',
      meta: [
        receipt.submitter || receipt.submittedBy || 'Field rep',
        `${receipt.totalCases || deskTotalLineCases(receipt.lines)} cs`,
        `into ${deskInventoryLocationName(receipt.location)}`
      ].filter(Boolean).join(' - '),
      nav: 'inventory',
      status: receipt.status || 'Pending',
      title: `Inventory receipt ${receipt.id}`
    }));
  return [...taskItems, ...orderItems, ...customerItems, ...stockRequestItems, ...fieldReceiptItems];
}

function deskOpenDispatchJobs() {
  return deskRows(RP.DISPATCH).filter(job => !DESK_CLOSED_DISPATCH_STATUSES.has(String(job.status || '')));
}

function deskOpenExpenses() {
  return deskRows(RP.EXPENSES).filter(expense => DESK_OPEN_EXPENSE_STATUSES.has(String(expense.status || '')));
}

function deskOpenEventActions() {
  const actionable = new Set(['needs_staffing', 'inventory_planning', 'packed', 'in_progress', 'pending_reconciliation']);
  return deskRows(RP.EVENTS).filter(event => actionable.has(String(event.status || '')));
}

function deskNavBadge(navId, salesAnalytics, dispatchDirectory) {
  if (rpIsLiveDemoOnlyModule(navId)) return 0;
  if (navId === 'tasks') {
    return deskOpenTasks().length;
  }
  if (navId === 'orders') {
    if (rpHasLiveAuth()) {
      if (!salesAnalytics || !salesAnalytics.loaded || !salesAnalytics.data) return 0;
      return Math.max(0, Number(salesAnalytics.data.operational && salesAnalytics.data.operational.approvalOrders) || 0);
    }
    return deskActionableOrders().length;
  }
  if (navId === 'invoicing') {
    if (rpHasLiveAuth()) {
      if (!salesAnalytics || !salesAnalytics.loaded || !salesAnalytics.data) return 0;
      const operational = salesAnalytics.data.operational || {};
      return Math.max(0, Number(operational.invoiceDraft) || 0) + Math.max(0, Number(operational.invoiceOverdue) || 0);
    }
    return typeof RP.invoiceMetrics === 'function' ? RP.INVOICES.filter(inv => RP.invoiceStatus(inv) === 'Draft' || RP.invoiceStatus(inv) === 'Overdue').length : 0;
  }
  if (navId === 'dispatch') {
    if (rpHasLiveAuth()) {
      if (!dispatchDirectory || !dispatchDirectory.loaded) return 0;
      const exact = dispatchDirectory.summary && dispatchDirectory.summary.open;
      return exact == null ? dispatchDirectory.items.filter(item => item && item.status !== 'Delivered').length : Math.max(0, Number(exact) || 0);
    }
    return deskOpenDispatchJobs().length;
  }
  if (navId === 'expenses') {
    return deskOpenExpenses().length;
  }
  if (navId === 'events') {
    return deskOpenEventActions().length;
  }
  return 0;
}

function deskDemoNotifications() {
  return DESK_NOTES
    .filter(x => !rpIsLiveDemoOnlyModule(x.nav) && !(rpHasLiveAuth() && x.demoOnly))
    .map((x, index) => ({ id: x.id || `demo-${index}`, ...x }));
}

function deskLiveNotifications(salesAnalytics) {
  const dashboard = dashboardMetrics(salesAnalytics);
  const approvals = deskApprovalItems();
  const exceptions = dashboardExceptions(dashboard, approvals).map((item, index) => ({
    id: `exception-${item.nav || 'general'}-${item.label}-${index}`,
    i: item.icon,
    tone: item.tone,
    t: item.label,
    m: item.meta || '',
    when: 'Live now',
    unread: true,
    nav: item.nav,
    detail: item.detail || null
  }));
  const tasks = deskOpenTasks().filter(task => !deskIsApprovalTask(task)).slice(0, 3).map(task => ({
    id: `task-${task.id}`,
    i: 'tasks',
    tone: task.status === 'Blocked' || task.priority === 'Urgent' ? 'danger' : 'warn',
    t: task.title || task.id,
    m: [rpTaskOwnerLabel(task.owner), task.due ? `Due ${String(task.due).slice(5)}` : ''].filter(Boolean).join(' - '),
    when: task.status || 'Open task',
    unread: true,
    nav: 'tasks',
    detail: { type: 'task', id: task.id }
  }));
  const expenses = deskOpenExpenses().slice(0, 3).map(expense => ({
    id: `expense-${expense.id}`,
    i: 'receipt',
    tone: 'warn',
    t: `${expense.id || 'Expense'} awaiting review`,
    m: [expense.who, expense.merchant].filter(Boolean).join(' - '),
    when: expense.date || 'Submitted',
    unread: true,
    nav: 'expenses'
  }));
  return [...exceptions, ...tasks, ...expenses].filter(item => item && !rpIsLiveDemoOnlyModule(item.nav)).slice(0, 8);
}

function deskNotificationItems(salesAnalytics) {
  return rpHasLiveAuth() ? deskLiveNotifications(salesAnalytics) : deskDemoNotifications();
}

function LiveDataGuardNotice({ title, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, background: 'var(--warn-bg)', border: '1px solid oklch(0.74 0.13 70 / 0.35)', color: 'var(--amber-deep)', borderRadius: 10, padding: '12px 14px', marginBottom: 18, fontSize: 13, lineHeight: 1.45 }}>
      <Icon name="alert" size={17} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ fontWeight: 800, color: 'var(--ink)', marginBottom: 2 }}>{title}</div>
        <div>{children}</div>
      </div>
    </div>
  );
}

function SalesAnalyticsStatus({ state, label = 'Sales totals' }) {
  if (!state || !state.isLive) return null;
  if (state.initialLoading || (state.loading && !state.loaded)) {
    return (
      <div data-sales-analytics-status="loading" style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 18, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 9, background: 'var(--surface-2)', color: 'var(--muted)', fontSize: 12.5 }}>
        <Icon name="refresh" size={15} /><span>Loading complete {label.toLowerCase()} from the database...</span>
      </div>
    );
  }
  if (state.error) {
    return (
      <LiveDataGuardNotice title={`Complete ${label.toLowerCase()} are unavailable.`}>
        <span>{state.error} No bounded-cache totals are being shown in their place. </span>
        <button type="button" className="btn btn-sm" onClick={state.retry} style={{ marginLeft: 8 }}>Try Again</button>
      </LiveDataGuardNotice>
    );
  }
  return null;
}

function DemoOnlyModuleView({ nav }) {
  const spec = LIVE_DEMO_ONLY_MODULES[nav.id] || { label: nav.label, icon: nav.icon, scope: ['Planned live wiring'] };
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <PageHead eyebrow="Phase 2 / Demo-only" title={spec.label}
        sub="This workspace is protected in live mode so placeholder records are not mistaken for production data." />
      <div style={{ maxWidth: 760 }}>
        <LiveDataGuardNotice title="Live writes are not enabled for this module yet.">
          The wireframe version is still available in developer/demo data mode, but production users cannot create or update these records until the Supabase tables and protected APIs are added.
        </LiveDataGuardNotice>
        <div className="card" style={{ padding: '22px 24px' }}>
          <SectionTitle>Planned Live Scope</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
            {spec.scope.map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface-2)', border: '1px solid var(--line-2)', borderRadius: 9, padding: '10px 11px', fontSize: 13, fontWeight: 600 }}>
                <Icon name={spec.icon} size={15} style={{ color: 'var(--muted)', flexShrink: 0 }} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotifBell({ go, salesAnalytics }) {
  const [open, setOpen] = useState(false);
  const sourceItems = deskNotificationItems(salesAnalytics);
  const sourceKey = sourceItems.map(item => item.id).join('|');
  const [readIds, setReadIds] = useState(() => new Set());
  useEffect(() => {
    setReadIds(prev => {
      const valid = new Set(sourceItems.map(item => item.id));
      const next = new Set([...prev].filter(id => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sourceKey]);
  const items = sourceItems.map(item => ({ ...item, unread: !readIds.has(item.id) }));
  const unread = items.filter(i => i.unread).length;
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  const markAll = () => setReadIds(prev => new Set([...prev, ...items.map(item => item.id)]));
  const openItem = (a, i) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next.add(a.id);
      return next;
    });
    setOpen(false);
    if (a.nav) go(a.nav, a.detail || null);
  };
  return (
    <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      <button title="Notifications" onClick={() => setOpen(o => !o)} style={{ border: '1px solid ' + (open ? 'var(--amber)' : 'var(--line)'), background: open ? 'var(--amber-soft)' : 'var(--surface)', color: open ? 'var(--amber-deep)' : 'var(--ink-soft)', width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <Icon name="bell" size={17} />
        {unread > 0 && <span style={{ position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 100, background: 'var(--amber)', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unread}</span>}
      </button>
      {open && (
        <div className="rp-scroll" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 60, width: 380, maxWidth: 'calc(100vw - 32px)', maxHeight: 460, overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: 'var(--shadow-lg)', animation: 'rp-pop .16s ease both' }}>
          <div style={{ position: 'sticky', top: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: '1px solid var(--line)', background: 'var(--surface)' }}>
            <span className="disp" style={{ fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notifications</span>
            {unread > 0 && <button onClick={markAll} style={{ border: 'none', background: 'none', color: 'var(--amber-deep)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Mark all read</button>}
          </div>
          <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.map((a, i) => {
              const [fg, bg] = statusColors(a.tone === 'danger' ? 'Damaged' : a.tone === 'warn' ? 'Pending' : a.tone === 'ok' ? 'Approved' : 'Submitted');
              return (
                <button key={a.id || i} onClick={() => openItem(a, i)} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left', border: 'none', background: a.unread ? 'var(--surface-2)' : 'transparent', borderRadius: 9, padding: '11px 11px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = a.unread ? 'var(--surface-2)' : 'transparent'}>
                  <span style={{ width: 32, height: 32, borderRadius: 9, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={a.i} size={16} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3 }}>{a.t}</span>
                      {a.unread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0, marginTop: 4 }} />}
                    </div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 3 }}>{a.m}</div>
                    <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 3 }}>{a.when}</div>
                  </div>
                </button>
              );
            })}
            {!items.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: '24px 18px', textAlign: 'center' }}>No live notifications right now.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function useIsNarrow(bp) {
  const [n, setN] = useState(typeof window !== 'undefined' && window.innerWidth < (bp || 900));
  useEffect(() => {
    const on = () => setN(window.innerWidth < (bp || 900));
    window.addEventListener('resize', on); on();
    return () => window.removeEventListener('resize', on);
  }, [bp]);
  return n;
}

class DesktopModuleBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Desktop module failed', { module: this.props.label, error, info });
  }
  render() {
    if (!this.state.error) return this.props.children;
    const message = this.state.error && this.state.error.message ? this.state.error.message : 'The workspace module could not be opened.';
    return (
      <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
        <PageHead eyebrow="Module error" title={`${this.props.label || 'Workspace'} unavailable`}
          sub="This section hit a rendering problem, but the rest of Operations is still available." />
        <div className="card" style={{ maxWidth: 700, padding: '20px 22px' }}>
          <LiveDataGuardNotice title="This page could not be opened cleanly.">
            This section had a problem loading. Try again. If it keeps happening, send a support ticket.
          </LiveDataGuardNotice>
          <details style={{ marginBottom: 12 }}><summary>Details for support</summary><code>{message}</code></details>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => this.setState({ error: null })}>Try again</button>
            <ErrorTicketButton
              error={this.state.error}
              title={`${this.props.label || 'Workspace'} module failed`}
              context={{ module: this.props.label || 'Workspace' }}
              source="Automatic error report - module crash"
              compact
            />
          </div>
        </div>
      </div>
    );
  }
}

function DeskShell({ role }) {
  const [nav, setNav] = useState(() => deskInitialWorkspace(deskActiveRole(role)));
  const [detail, setDetail] = useState(() => deskRouteFromLocation(deskActiveRole(role)).detail); // {type, id}
  const isNarrow = useIsNarrow(900);
  const [collapsed, setCollapsed] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const menuButtonRef = React.useRef(null);
  const closeMenuRef = React.useRef(null);
  const closeDrawer = () => {
    menuButtonRef.current?.focus();
    setDrawer(false);
  };
  useEffect(() => { if (isNarrow && drawer) closeMenuRef.current?.focus(); }, [isNarrow, drawer]);
  const [navigationVersion, setNavigationVersion] = useState(0);
  const authRole = deskActiveRole(role);
  const reportsEnabled = deskCanUseNav('reports', authRole);
  const salesAnalytics = useRpSalesAnalytics('mtd');
  const canReadDispatch = ['admin', 'leadership', 'ops', 'sales_manager', 'warehouse', 'dispatch'].includes(authRole);
  const dispatchDirectory = useRpDispatchRows({ enabled: canReadDispatch, limit: 100, scope: 'all' });
  const unassignedDispatchDirectory = useRpDispatchRows({ enabled: canReadDispatch, limit: 25, scope: 'unassigned' });
  const go = (n, d = null) => {
    if (nav === 'move-stock' && n !== 'move-stock' && typeof window.RP_INVENTORY_WORKFLOW_CAN_LEAVE === 'function' && !window.RP_INVENTORY_WORKFLOW_CAN_LEAVE()) return;
    setNav(deskCanUseNav(n, authRole) ? n : deskDefaultWorkspace(authRole));
    setDetail(deskCanUseNav(n, authRole) && d ? { ...d } : null);
    deskWriteRoute(deskCanUseNav(n, authRole) ? n : deskDefaultWorkspace(authRole), deskCanUseNav(n, authRole) ? d : null);
    historyIndex.current=window.history.state.ranchPowerOpsIndex;
    setNavigationVersion(value => value + 1);
    if (isNarrow && drawer) closeDrawer();
    else setDrawer(false);
  };
  const historyIndex=React.useRef(window.history.state?.ranchPowerOpsIndex||0);
  const restoringHistory=React.useRef(false);
  useEffect(()=>{deskWriteRoute(nav,detail,'replace');historyIndex.current=window.history.state.ranchPowerOpsIndex;},[]);
  useEffect(()=>{
    const restore=()=>{
      if(restoringHistory.current){restoringHistory.current=false;return;}
      const target=deskRouteFromLocation(authRole),nextIndex=window.history.state?.ranchPowerOpsIndex;
      if(nav==='move-stock'&&typeof window.RP_INVENTORY_WORKFLOW_CAN_LEAVE==='function'&&!window.RP_INVENTORY_WORKFLOW_CAN_LEAVE()) {
        if(Number.isInteger(nextIndex)&&nextIndex!==historyIndex.current){restoringHistory.current=true;window.history.go(historyIndex.current-nextIndex);}
        else deskWriteRoute(nav,detail,'replace');
        return;
      }
      historyIndex.current=Number.isInteger(nextIndex)?nextIndex:0;
      setNav(target.nav);setDetail(target.detail);setDrawer(false);setNavigationVersion(value=>value+1);
    };
    window.addEventListener('popstate',restore);return ()=>window.removeEventListener('popstate',restore);
  },[nav,detail,authRole]);
  const mini = collapsed && !isNarrow;
  useEffect(() => {
    if (!deskCanUseNav(nav, authRole)) {
      setDetail(null);
      setNav(deskDefaultWorkspace(authRole));
      deskWriteRoute(deskDefaultWorkspace(authRole),null,'replace');
    }
  }, [authRole, nav]);
  useEffect(() => {
    const openAccount = () => go('account');
    window.RP_OPEN_ACCOUNT = openAccount;
    return () => { if (window.RP_OPEN_ACCOUNT === openAccount) window.RP_OPEN_ACCOUNT = null; };
  }, [authRole]);

  const leadership = authRole === 'leadership' || authRole === 'admin';
  let body;
  if (!deskCanUseNav(nav, authRole)) body = <DeskAccessDenied go={go} nav={DESK_NAV.find(n => n.id === nav)} role={authRole} />;
  else if (detail && detail.type === 'order') body = <OrderDetail id={detail.id} back={() => go(nav)} go={go} />;
  else if (detail && detail.type === 'invoice') body = <InvoiceDetail id={detail.id} back={detail.backToOrder ? () => go('orders', { type: 'order', id: detail.backToOrder }) : () => go(nav)} backLabel={detail.backToOrder ? 'Back to Order' : 'Back to Invoicing'} go={go} />;
  else if (detail && detail.type === 'customer') body = <CustomerDetailDesk id={detail.id} back={() => go(nav)} go={go} />;
  else if (detail && detail.type === 'event') body = <EventsView go={go} detail={detail} />;
  else if (nav === 'dashboard') body = <Dashboard go={go} role={authRole} leadership={leadership} reportsEnabled={reportsEnabled} salesAnalytics={salesAnalytics} unassignedDispatchDirectory={unassignedDispatchDirectory} />;
  else if (nav === 'accounting') body = <AccountingView go={go} />;
  else if (nav === 'tasks') body = <TasksView go={go} role={authRole} detail={detail} />;
  else if (nav === 'contacts') body = <ContactInboxView />;
  else if (nav === 'email-log') body = <EmailDeliveryLog />;
  else if (nav === 'orders') body = <OrdersView go={go} reportsEnabled={reportsEnabled} />;
  else if (nav === 'invoicing') body = <InvoicingView go={go} reportsEnabled={reportsEnabled} />;
  else if (nav === 'customers') body = <CustomersView go={go} />;
  else if (nav === 'pipeline') body = <PipelineView go={go} />;
  else if (nav === 'marketing') body = <MarketingView go={go} />;
  else if (nav === 'events') body = <EventsView go={go} detail={detail} />;
  else if (nav === 'goals') body = <SalesGoalsView go={go} role={authRole} leadership={leadership} />;
  else if (nav === 'move-stock') body = <InventoryWorkflowView go={go} detail={detail} />;
  else if (nav === 'inventory') body = <InventoryView go={go} detail={detail} />;
  else if (nav === 'dispatch') body = <DispatchView go={go} directory={dispatchDirectory} />;
  else if (nav === 'analytics') body = <AnalyticsView go={go} leadership={leadership} />;
  else if (nav === 'trade') body = <TradeView go={go} leadership={leadership} />;
  else if (nav === 'expenses') body = <ExpensesView go={go} reportsEnabled={reportsEnabled} />;
  else if (nav === 'reports') body = <ReportsView go={go} detail={detail} />;
  else if (nav === 'account') body = <AccountView role={authRole} />;
  else if (nav === 'production') body = rpIsLiveDemoOnlyModule(nav) ? <DemoOnlyModuleView nav={DESK_NAV.find(n => n.id === nav)} /> : <ProductionView go={go} detail={detail} />;
  else if (nav === 'procurement') body = rpIsLiveDemoOnlyModule(nav) ? <DemoOnlyModuleView nav={DESK_NAV.find(n => n.id === nav)} /> : <ProcurementView go={go} />;
  else if (nav === 'fba') body = rpIsLiveDemoOnlyModule(nav) ? <DemoOnlyModuleView nav={DESK_NAV.find(n => n.id === nav)} /> : <FBAView go={go} />;
  else if (nav === 'admin') body = <AdminView go={go} detail={detail} />;
  else body = <StubView nav={DESK_NAV.find(n => n.id === nav)} />;

  const who = useRpIdentity(authRole);
  const orderCache = window.RP_BOOTSTRAP && window.RP_BOOTSTRAP.orderCache;
  const orderCacheLimited = Boolean(orderCache && orderCache.hasMore && orderCache.operationalComplete === false);
  const historyNativeNav = ['accounting', 'orders', 'invoicing', 'customers', 'reports'].includes(nav);
  const sidebar = (
    <aside style={{ width: mini ? 70 : 244, flexShrink: 0, background: 'var(--navy)', color: 'oklch(0.9 0.01 235)', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--navy-900)', transition: 'width .2s ease', height: '100%' }}>
      <div style={{ padding: mini ? '20px 0 18px' : '20px 14px 18px', borderBottom: '1px solid oklch(1 0 0 / 0.08)', display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'space-between', gap: 8 }}>
        {mini ? <Buffalo size={28} /> : <Logo color="oklch(0.97 0.01 80)" />}
        {!isNarrow && !mini && <button onClick={() => setCollapsed(true)} title="Collapse" style={{ border: 'none', background: 'oklch(1 0 0 / 0.08)', color: 'oklch(0.85 0.02 235)', width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="chevL" size={16} /></button>}
        {isNarrow && <button ref={closeMenuRef} aria-label="Close menu" onClick={closeDrawer} style={{ border: 'none', background: 'oklch(1 0 0 / 0.08)', color: '#fff', width: 30, height: 30, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="x" size={17} /></button>}
      </div>
      {!mini && <div style={{ padding: '8px 0 6px', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'oklch(0.72 0.02 235)', fontFamily: 'var(--ff-display)', fontWeight: 600, paddingLeft: 20, marginTop: 8 }}>Operations Hub</div>}
      <DeskSidebarNavigation nav={nav} role={authRole} mini={mini} go={go} salesAnalytics={salesAnalytics} dispatchDirectory={dispatchDirectory} navigationVersion={navigationVersion} />
      {mini ? (
        <div style={{ padding: '12px 0', borderTop: '1px solid oklch(1 0 0 / 0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <Avatar name={who.name || 'Ops'} color="var(--amber)" size={32} />
          <button onClick={() => setCollapsed(false)} title="Expand" style={{ border: 'none', background: 'oklch(1 0 0 / 0.08)', color: 'oklch(0.85 0.02 235)', width: 30, height: 30, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="chevR" size={16} /></button>
        </div>
      ) : (
        <button onClick={() => go('account')} style={{ padding: 12, border: 'none', borderTop: '1px solid oklch(1 0 0 / 0.08)', background: nav === 'account' ? 'oklch(1 0 0 / 0.08)' : 'transparent', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%', color: 'inherit' }}>
          <Avatar name={who.name || 'Ops'} color="var(--amber)" />
          <div style={{ lineHeight: 1.25, flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{who.name}</div>
            <div style={{ fontSize: 11, color: 'oklch(0.72 0.02 235)' }}>{who.role}</div>
          </div>
          <Icon name="chevU" size={15} style={{ color: 'oklch(0.7 0.02 235)' }} />
        </button>
      )}
    </aside>
  );

  const curNav = nav === 'account' ? { label: 'My Account', icon: 'user' } : DESK_NAV.find(n => n.id === nav);
  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--paper)', position: 'relative', overflow: 'hidden' }}>
      {/* Desktop / rail sidebar (static) */}
      {!isNarrow && sidebar}

      {/* Mobile off-canvas drawer */}
      {isNarrow && (
        <>
          <div onClick={closeDrawer} aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'oklch(0.24 0.035 235 / 0.5)', backdropFilter: 'blur(2px)', zIndex: 40, opacity: drawer ? 1 : 0, pointerEvents: drawer ? 'auto' : 'none', transition: 'opacity .2s' }} />
          <div id="operations-menu-drawer" inert={drawer ? undefined : ''} aria-hidden={!drawer} onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); closeDrawer(); } }} style={{ position: 'absolute', top: 0, bottom: 0, left: 0, zIndex: 41, transform: drawer ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .24s ease', boxShadow: drawer ? 'var(--shadow-lg)' : 'none' }}>{sidebar}</div>
        </>
      )}

      {/* Main column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Top toolbar — toggle lives here on every size */}
        <div style={{ flexShrink: 0, height: 52, borderBottom: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px' }}>
          <button ref={menuButtonRef} onClick={() => isNarrow ? setDrawer(true) : setCollapsed(c => !c)} title="Menu" aria-label="Menu" aria-expanded={isNarrow ? drawer : !collapsed} aria-controls={isNarrow ? 'operations-menu-drawer' : undefined} style={{ border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={isNarrow ? 'more' : (mini ? 'chevR' : 'chevL')} size={18} sw={2} style={isNarrow ? { transform: 'rotate(90deg)' } : undefined} />
          </button>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            <Icon name={curNav?.icon || 'home'} size={17} style={{ color: 'var(--amber-deep)', flexShrink: 0 }} />
            <DeskBreadcrumbs nav={nav} detail={detail} role={authRole} go={go} />
          </div>
          <NotifBell go={go} salesAnalytics={salesAnalytics} />
        </div>

        {orderCacheLimited && !historyNativeNav && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 16px', borderBottom: '1px solid oklch(0.72 0.12 70 / 0.35)', background: 'oklch(0.95 0.06 80)', color: 'oklch(0.38 0.08 55)', fontSize: 12.5 }}>
            <span><strong>Large operational queue:</strong> this workspace cache contains the newest {Number(orderCache.returned || 0).toLocaleString()} open orders. Complete order and invoice history remains available in the paged lists.</span>
            <button type="button" className="btn btn-sm" onClick={() => go('orders')} style={{ flexShrink: 0 }}>Open complete orders</button>
          </div>
        )}

        {/* Body */}
        <main className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
          <PageHelp page={nav} detail={detail} role={authRole} go={go} />
          <DesktopModuleBoundary key={`${nav}:${detail ? detail.type + ':' + detail.id : 'list'}`} label={(curNav && curNav.label) || 'Workspace'}>
            {body}
          </DesktopModuleBoundary>
        </main>
      </div>
    </div>
  );
}

/* ---- Page header used across desktop views ---- */
function PageHead({ eyebrow, title, sub, actions }) {
  return (
    <div className="rp-page-head" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
      <div className="rp-page-head-copy">
        {eyebrow && <div className="eyebrow" style={{ marginBottom: 6 }}>{eyebrow}</div>}
        <h1 className="disp" style={{ margin: 0, fontSize: 32, color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>{title}</h1>
        {sub && <div style={{ color: 'var(--muted)', fontSize: 13.5, marginTop: 5 }}>{sub}</div>}
      </div>
      {actions && <div className="rp-page-head-actions rp-scroll" role="toolbar" aria-label={`${title} actions`} style={{ display: 'flex', gap: 9 }}>{actions}</div>}
    </div>
  );
}

function SectionTitle({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
      <h3 className="disp" style={{ margin: 0, fontSize: 16, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{children}</h3>
      {right}
    </div>
  );
}

function AccountView({ role }) {
  const who = useRpIdentity(role);
  const live = rpHasLiveAuth();
  const authProfile = rpAuthProfile();
  const authDetails = rpAuthDetails();
  const [displayName, setDisplayName] = useState(who.name || '');
  const [quotePhone, setQuotePhone] = useState(authProfile.quote_phone || '');
  const [quoteLocation, setQuoteLocation] = useState(authProfile.quote_location || '');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  useEffect(() => {
    setDisplayName(who.name || '');
  }, [who.name]);
  useEffect(() => {
    setQuotePhone(authProfile.quote_phone || '');
    setQuoteLocation(authProfile.quote_location || '');
  }, [authProfile.quote_phone, authProfile.quote_location]);
  const profileRows = [
    ['Email', who.email || authProfile.email || 'demo@ranchpower.com'],
    ['Role', who.role],
    ['Territory', authProfile.territory || 'All assigned access'],
    ['Quote phone', authProfile.quote_phone || 'Not shown'],
    ['Quote location', authProfile.quote_location || authProfile.territory || 'Not shown'],
    ['Customer context', authProfile.customer_id || 'None'],
    ['Account status', rpAccountStatus(authProfile, authDetails)],
    ['Last sign-in', live ? rpFormatAccountTime(authDetails.lastSignInAt) : 'Demo session'],
    ['Invite accepted', live ? rpFormatAccountTime(authDetails.emailConfirmedAt) : 'Demo session'],
  ];
  const saveProfile = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.updateProfile || profileBusy) return;
    setProfileBusy(true);
    setProfileMessage('');
    try {
      await window.RP_ACCOUNT.updateProfile({
        display_name: displayName,
        quote_phone: quotePhone,
        quote_location: quoteLocation,
        setup_complete: true
      });
      setProfileMessage('Profile saved.');
      toast('Profile saved', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not save profile.';
      setProfileMessage(msg);
      toast(msg, 'danger');
    } finally {
      setProfileBusy(false);
    }
  };
  const savePassword = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.updatePassword || passwordBusy) return;
    setPasswordMessage('');
    if (newPassword !== confirmPassword) {
      setPasswordMessage('Passwords do not match.');
      return;
    }
    setPasswordBusy(true);
    try {
      await window.RP_ACCOUNT.updatePassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('Password updated.');
      toast('Password updated', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not update password.';
      setPasswordMessage(msg);
      toast(msg, 'danger');
    } finally {
      setPasswordBusy(false);
    }
  };
  const sendReset = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.sendPasswordResetEmail || passwordBusy) return;
    setPasswordBusy(true);
    setPasswordMessage('');
    try {
      await window.RP_ACCOUNT.sendPasswordResetEmail(who.email || authProfile.email);
      setPasswordMessage('Reset email sent.');
      toast('Password reset email sent', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not send reset email.';
      setPasswordMessage(msg);
      toast(msg, 'danger');
    } finally {
      setPasswordBusy(false);
    }
  };
  const signOut = () => {
    if (window.RP_SIGN_OUT) window.RP_SIGN_OUT();
    else toast('Signed out (demo)', 'info');
  };

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <PageHead eyebrow="Secure Profile" title="My Account" sub="Manage your Ranch Power identity and sign-in settings." />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 0.9fr) minmax(320px, 1.1fr)', gap: 18, alignItems: 'start' }}>
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <Avatar name={who.name} size={54} color="var(--navy)" />
            <div style={{ minWidth: 0 }}>
              <div className="disp" style={{ fontSize: 23, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{who.name}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{who.role} - {who.desc}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, fontSize: 13.5 }}>
            {profileRows.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 14 }}>
                <span style={{ color: 'var(--muted)' }}>{k}</span>
                <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
              </div>
            ))}
          </div>
          <button className="btn" style={{ width: '100%', marginTop: 18, color: 'var(--danger)' }} onClick={signOut}><Icon name="arrowL" size={15} />Sign out</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>Public Quote Profile</SectionTitle>
            {live ? (
              <>
                <label className="field-label">Display name</label>
                <input className="field" value={displayName} onChange={e => setDisplayName(e.target.value)} autoComplete="name" />
                <label className="field-label" style={{ marginTop: 10 }}>Phone shown to retailers</label>
                <input className="field" value={quotePhone} onChange={e => setQuotePhone(e.target.value)} autoComplete="tel" inputMode="tel" maxLength={40} placeholder="e.g. 778-868-1514" />
                <label className="field-label" style={{ marginTop: 10 }}>Local location or address</label>
                <input className="field" value={quoteLocation} onChange={e => setQuoteLocation(e.target.value)} autoComplete="street-address" maxLength={160} placeholder="e.g. Squamish, BC" />
                <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45, marginTop: 8 }}>These details appear on your retailer sell sheets and direct quote emails. Leave either blank to omit it.</div>
                <button className="btn btn-primary" disabled={profileBusy || !displayName.trim()} onClick={saveProfile} style={{ width: '100%', marginTop: 10 }}><Icon name="check" size={15} />Save Profile</button>
                {profileMessage && <div style={{ fontSize: 11.5, color: profileMessage.includes('saved') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{profileMessage}</div>}
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>Demo profile controls are disabled. Live users can save their display name here.</div>
            )}
          </div>

          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>Sign-in & Security</SectionTitle>
            {live ? (
              <>
                <label className="field-label">New password</label>
                <input className="field" type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 12 characters" />
                <label className="field-label" style={{ marginTop: 10 }}>Confirm password</label>
                <input className="field" type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                <button className="btn btn-primary" disabled={passwordBusy || !newPassword || !confirmPassword} onClick={savePassword} style={{ width: '100%', marginTop: 10 }}><Icon name="check" size={15} />Set Password</button>
                <button className="btn" disabled={passwordBusy} onClick={sendReset} style={{ width: '100%', marginTop: 8 }}><Icon name="mail" size={15} />Email Reset Link</button>
                {passwordMessage && <div style={{ fontSize: 11.5, color: passwordMessage.includes('updated') || passwordMessage.includes('sent') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{passwordMessage}</div>}
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>Demo sign-in uses local placeholder controls only.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   COMMAND CENTER
   ============================================================ */
const DASHBOARD_CLOSED_ORDER_STATUSES = new Set(['Delivered', 'Invoiced', 'Cancelled', 'Closed']);
const DASHBOARD_ACTIVE_ORDER_STATUSES = new Set(['Requested', 'Submitted', 'Needs Approval', 'On Hold', 'Approved', 'Allocated', 'Picked', 'Dispatched', 'Inbound']);
const DASHBOARD_PENDING_STOCK_REQUEST_STATUSES = new Set(['Requested', 'Pending', 'Approved']);

function dashboardDate(value) {
  if (!value) return null;
  const parsed = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function dashboardStartOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function dashboardStartOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function dashboardAddMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function dashboardDateLabel(date) {
  return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function dashboardOrderIsActive(order) {
  const status = String(order.status || '');
  return DASHBOARD_ACTIVE_ORDER_STATUSES.has(status) || !DASHBOARD_CLOSED_ORDER_STATUSES.has(status);
}

function dashboardOrderTotals(order) {
  const lines = Array.isArray(order.lines) ? order.lines : [];
  let cases = 0;
  let productRevenue = 0;
  lines.forEach(line => {
    const qty = Number(line.qty ?? line.quantity ?? 0);
    if (!Number.isFinite(qty) || qty <= 0) return;
    const sku = typeof rpSkuItem === 'function' ? rpSkuItem(line.sku) : RP.sku(line.sku);
    const unit = String((line && line.unit) || (sku && sku.unit) || '').toLowerCase();
    const explicitCases = Number(line.caseQty ?? line.caseQuantity ?? line.case_quantity);
    const caseQty = Number.isFinite(explicitCases) && explicitCases > 0 ? explicitCases : (unit === 'can' ? qty / 12 : qty);
    cases += caseQty;
    const price = Number(line.price ?? (sku && sku.price) ?? 0);
    if (Number.isFinite(price) && price > 0) productRevenue += price * qty;
  });

  const storedRevenue = Number(order.subtotal || 0);
  const storedTotal = Number(order.total || 0);
  const revenue = Number.isFinite(storedRevenue) && storedRevenue > 0 ? storedRevenue : productRevenue;
  return {
    cases,
    revenue,
    total: Number.isFinite(storedTotal) && storedTotal > 0 ? storedTotal : revenue
  };
}

function dashboardOrdersInRange(orders, start, end) {
  return orders.filter(order => {
    if (String(order.status || '') === 'Cancelled') return false;
    const date = dashboardDate(order.created || order.updated || order.date);
    return date && date >= start && date < end;
  });
}

function dashboardPercentDelta(current, previous) {
  if (!previous) return current ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function dashboardTrendBuckets(orders, now) {
  const start = dashboardStartOfDay(now);
  start.setDate(start.getDate() - 77);
  const buckets = Array.from({ length: 12 }, () => ({ cases: 0, revenue: 0 }));

  orders.forEach(order => {
    if (String(order.status || '') === 'Cancelled') return;
    const date = dashboardDate(order.created || order.updated || order.date);
    if (!date || date < start || date > now) return;
    const index = Math.min(11, Math.max(0, Math.floor((date - start) / (7 * 24 * 60 * 60 * 1000))));
    const totals = dashboardOrderTotals(order);
    buckets[index].cases += totals.cases;
    buckets[index].revenue += totals.revenue;
  });

  return {
    cases: buckets.map(bucket => bucket.cases),
    revenue: buckets.map(bucket => Math.round(bucket.revenue))
  };
}

function dashboardFlavorMix(orders) {
  const casesByFlavor = new Map();
  let totalCases = 0;

  orders.forEach(order => {
    if (String(order.status || '') === 'Cancelled') return;
    (order.lines || []).forEach(line => {
      const qty = Number(line.qty ?? line.quantity ?? 0);
      const explicitCases = Number(line.caseQty ?? line.caseQuantity ?? line.case_quantity);
      const sku = typeof rpSkuItem === 'function' ? rpSkuItem(line.sku) : RP.sku(line.sku);
      const unit = String((line && line.unit) || (sku && sku.unit) || '').toLowerCase();
      const caseQty = Number.isFinite(explicitCases) && explicitCases > 0 ? explicitCases : (unit === 'can' ? qty / 12 : qty);
      const flavors = Array.isArray(sku && sku.tri) && sku.tri.length
        ? sku.tri.filter(flavor => RP.flavor(flavor))
        : [typeof rpSkuFlavorId === 'function' ? rpSkuFlavorId(sku || line.sku) : (sku && sku.flavor)].filter(flavor => RP.flavor(flavor));
      if (!flavors.length || !Number.isFinite(caseQty) || caseQty <= 0) return;
      const share = caseQty / flavors.length;
      flavors.forEach(flavor => casesByFlavor.set(flavor, (casesByFlavor.get(flavor) || 0) + share));
      totalCases += caseQty;
    });
  });

  if (!totalCases) return (RP.FLAVOR_MIX || []).slice(0, 4);

  return Array.from(casesByFlavor.entries())
    .map(([flavor, cases]) => ({ flavor, share: Math.round((cases / totalCases) * 100), cases }))
    .sort((a, b) => b.cases - a.cases)
    .slice(0, 4);
}

function dashboardAnalyticsFlavorMix(data) {
  const rows = data && Array.isArray(data.bySku) ? data.bySku : [];
  const totalCases = rows.reduce((sum, row) => sum + Math.max(0, Number(row.cases) || 0), 0);
  if (!totalCases) return [];
  return rows.map(row => ({
    cases: Math.max(0, Number(row.cases) || 0),
    flavor: row.flavor || row.sku,
    share: Math.round((Math.max(0, Number(row.cases) || 0) / totalCases) * 100)
  })).filter(row => row.flavor && RP.flavor(row.flavor)).sort((a, b) => b.cases - a.cases).slice(0, 4);
}

function dashboardExactNumber(value) {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function dashboardFiniteNumber(value) {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function dashboardAnalyticsData(state) {
  return state && state.loaded && state.data ? state.data : null;
}

function dashboardAnalyticsTrend(data) {
  const weeks = data && Array.isArray(data.weeks) ? data.weeks : [];
  return {
    cases: weeks.map(bucket => Math.max(0, Number(bucket.cases) || 0)),
    revenue: weeks.map(bucket => Math.max(0, Math.round(Number(bucket.revenue) || 0)))
  };
}

function dashboardAnalyticsOrderAging(operational, openOrders) {
  const source = operational && operational.orderAging;
  if (!source || typeof source !== 'object') return [];
  const localById = new Map((openOrders || []).map(order => [String(order.id || ''), order]));
  return [
    { id: 'approval', label: 'Needs approval' },
    { id: 'allocation', label: 'Ready to allocate' },
    { id: 'warehouse', label: 'Warehouse/dispatch' }
  ].map(bucket => {
    const exact = source[bucket.id] || {};
    const oldestOrderId = String(exact.oldestOrderId || '');
    return {
      ...bucket,
      cases: Math.max(0, Number(exact.cases) || 0),
      count: Math.max(0, Number(exact.count) || 0),
      oldest: localById.get(oldestOrderId) || null,
      oldestAge: Math.max(0, Number(exact.oldestAgeDays) || 0),
      oldestOrderId
    };
  });
}

function dashboardStockoutRisks() {
  const availableBySku = new Map();
  (RP.INV || []).forEach(row => {
    if (String(row.unit || 'case').toLowerCase() === 'can') return;
    const status = String(row.status || 'Available').toLowerCase();
    if (!['available', 'fronted to rep', 'purchased by rep'].includes(status)) return;
    const qty = Number(row.qty || 0);
    if (!Number.isFinite(qty) || qty <= 0) return;
    const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(row.sku) : row.sku;
    availableBySku.set(skuId, (availableBySku.get(skuId) || 0) + qty);
  });

  const skus = typeof rpManualOrderSkus === 'function' ? rpManualOrderSkus() : (RP.SKUS || []);
  return skus
    .map(sku => ({ sku, qty: availableBySku.get(typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(sku.id) : sku.id) || 0 }))
    .filter(row => row.qty > 0 && row.qty <= 24)
    .sort((a, b) => a.qty - b.qty);
}

function dashboardDaysBetween(start, end) {
  const a = dashboardDate(start);
  const b = dashboardDate(end);
  if (!a || !b) return 0;
  return Math.max(0, Math.round((dashboardStartOfDay(b) - dashboardStartOfDay(a)) / 86400000));
}

function dashboardOrderAgeDays(order, now = new Date()) {
  return dashboardDaysBetween(order.created || order.createdAt || order.created_at || order.updated || order.date, now);
}

function dashboardOrderAging(openOrders, now = new Date()) {
  const buckets = [
    { id: 'approval', label: 'Needs approval', statuses: new Set(['Requested', 'Submitted', 'Needs Approval', 'On Hold']) },
    { id: 'allocation', label: 'Ready to allocate', statuses: new Set(['Approved']) },
    { id: 'warehouse', label: 'Warehouse/dispatch', statuses: new Set(['Allocated', 'Picked', 'Dispatched', 'Inbound']) }
  ].map(bucket => ({ ...bucket, count: 0, oldest: null, oldestAge: 0, cases: 0 }));

  openOrders.forEach(order => {
    const status = String(order.status || '');
    const bucket = buckets.find(item => item.statuses.has(status)) || buckets[0];
    const age = dashboardOrderAgeDays(order, now);
    const totals = dashboardOrderTotals(order);
    bucket.count += 1;
    bucket.cases += totals.cases;
    if (!bucket.oldest || age > bucket.oldestAge) {
      bucket.oldest = order;
      bucket.oldestAge = age;
    }
  });

  return buckets;
}

function dashboardInventoryCoverRows(now = new Date(), operationalData = null, trendData = null) {
  const availableBySku = new Map();
  (RP.INV || []).forEach(row => {
    if (String(row.unit || 'case').toLowerCase() === 'can') return;
    const status = String(row.status || 'Available').toLowerCase();
    if (!['available', 'fronted to rep', 'purchased by rep'].includes(status)) return;
    const qty = Number(row.qty || 0);
    if (!Number.isFinite(qty) || qty <= 0) return;
    const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(row.sku) : row.sku;
    availableBySku.set(skuId, (availableBySku.get(skuId) || 0) + qty);
  });

  const liveAnalytics = rpHasLiveAuth();
  const committedBySku = new Map();
  const velocityBySku = new Map();
  if (liveAnalytics) {
    const commitments = Array.isArray(RP.ORDER_STOCK_COMMITMENTS)
      ? RP.ORDER_STOCK_COMMITMENTS
      : null;
    (commitments || []).forEach(row => {
      const rawSku = row.sku;
      const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(rawSku) : rawSku;
      const quantity = Math.max(0, Number(row.quantity) || 0);
      const caseQuantity = String(row.unit || 'case').toLowerCase() === 'can' ? quantity / 12 : quantity;
      if (skuId) committedBySku.set(skuId, (committedBySku.get(skuId) || 0) + caseQuantity);
    });
    (trendData && Array.isArray(trendData.byProduct) ? trendData.byProduct : []).forEach(row => {
      const rawSku = row.sku || row.flavor;
      const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(rawSku) : rawSku;
      if (skuId) velocityBySku.set(skuId, Math.max(0, Number(row.cases) || 0));
    });
  } else {
    (RP.ORDERS || []).filter(dashboardOrderIsActive).forEach(order => {
      (order.lines || []).forEach(line => {
        const qty = Number(line.qty ?? line.quantity ?? 0);
        if (!Number.isFinite(qty) || qty <= 0) return;
        const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(line.inventorySku || line.inventorySkuId || line.sku) : (line.inventorySku || line.inventorySkuId || line.sku);
        const explicitCases = Number(line.caseQty ?? line.caseQuantity ?? line.case_quantity);
        const sku = typeof rpSkuItem === 'function' ? rpSkuItem(line.sku) : RP.sku(line.sku);
        const unit = String(line.unit || (sku && sku.unit) || '').toLowerCase();
        const caseQty = Number.isFinite(explicitCases) && explicitCases > 0 ? explicitCases : (unit === 'can' ? qty / 12 : qty);
        committedBySku.set(skuId, (committedBySku.get(skuId) || 0) + caseQty);
      });
    });
    const velocityStart = new Date(now);
    velocityStart.setDate(velocityStart.getDate() - 56);
    dashboardOrdersInRange(RP.ORDERS || [], velocityStart, now).forEach(order => {
      (order.lines || []).forEach(line => {
        const qty = Number(line.qty ?? line.quantity ?? 0);
        if (!Number.isFinite(qty) || qty <= 0) return;
        const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(line.inventorySku || line.inventorySkuId || line.sku) : (line.inventorySku || line.inventorySkuId || line.sku);
        const explicitCases = Number(line.caseQty ?? line.caseQuantity ?? line.case_quantity);
        const sku = typeof rpSkuItem === 'function' ? rpSkuItem(line.sku) : RP.sku(line.sku);
        const unit = String(line.unit || (sku && sku.unit) || '').toLowerCase();
        const caseQty = Number.isFinite(explicitCases) && explicitCases > 0 ? explicitCases : (unit === 'can' ? qty / 12 : qty);
        velocityBySku.set(skuId, (velocityBySku.get(skuId) || 0) + caseQty);
      });
    });
  }

  const skus = typeof rpManualOrderSkus === 'function' ? rpManualOrderSkus() : (RP.SKUS || []);
  return skus
    .filter(sku => sku && sku.id && sku.active !== false)
    .map(sku => {
      const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(sku.id) : sku.id;
      const available = availableBySku.get(skuId) || 0;
      const commitmentsComplete = !liveAnalytics || Array.isArray(RP.ORDER_STOCK_COMMITMENTS);
      const committed = commitmentsComplete ? (committedBySku.get(skuId) || 0) : null;
      const net = committed == null ? null : Math.max(0, available - committed);
      const velocityDays = liveAnalytics ? 84 : 56;
      const salesCases = velocityBySku.get(skuId) || 0;
      const velocity = salesCases / velocityDays;
      const salesComplete = !liveAnalytics || Boolean(trendData && Array.isArray(trendData.byProduct));
      const days = net == null || !salesComplete ? null : (velocity > 0 ? Math.floor(net / velocity) : (net > 0 ? 999 : 0));
      return { available, committed, days, net, salesCases, salesComplete, sku, velocity };
    })
    .filter(row => row.available > 0 || Number(row.committed || 0) > 0 || row.velocity > 0)
    .sort((a, b) => liveAnalytics ? (a.days == null) - (b.days == null) || (a.days || 0) - (b.days || 0) || b.salesCases - a.salesCases : a.days - b.days || a.net - b.net)
    .slice(0, 5);
}

function dashboardFinanceRisk(salesAnalytics) {
  const visibleCustomers = typeof rpVisibleCustomers === 'function' ? rpVisibleCustomers(RP.CUSTOMERS) : (RP.CUSTOMERS || []);
  const creditHolds = visibleCustomers.filter(customer => typeof rpCustomerOnCreditHold === 'function'
    ? rpCustomerOnCreditHold(customer)
    : ['Hold', 'Watch', 'Credit Hold'].includes(String(customer.credit || customer.status || '')));
  if (rpHasLiveAuth()) {
    const data = dashboardAnalyticsData(salesAnalytics);
    const operational = data && data.operational ? data.operational : null;
    return {
      arBalance: operational ? dashboardExactNumber(operational.arBalance) : null,
      creditHolds,
      openCount: operational ? dashboardExactNumber(operational.invoiceUnpaid) : null,
      overdueBalance: null,
      overdueCount: operational ? dashboardExactNumber(operational.invoiceOverdue) : null
    };
  }
  const invoices = typeof RP.INVOICES !== 'undefined' ? (RP.INVOICES || []) : [];
  const rows = typeof RP.invoiceStatus === 'function' && typeof RP.invoiceTotals === 'function'
    ? invoices.map(inv => ({ inv, status: RP.invoiceStatus(inv), totals: RP.invoiceTotals(inv) }))
    : [];
  const open = rows.filter(row => !['Paid', 'Draft', 'Void'].includes(row.status));
  const overdue = rows.filter(row => row.status === 'Overdue');
  const ar = typeof RP.invoiceMetrics === 'function'
    ? RP.invoiceMetrics()
    : {
      ar: open.reduce((sum, row) => sum + Number(row.totals.balance || 0), 0),
      overdue: overdue.reduce((sum, row) => sum + Number(row.totals.balance || 0), 0)
    };
  return {
    arBalance: Number(ar.ar || 0),
    creditHolds,
    openCount: open.length,
    overdueBalance: Number(ar.overdue || 0),
    overdueCount: overdue.length
  };
}

function dashboardMetrics(salesAnalytics, trendAnalytics) {
  const now = new Date();
  const live = rpHasLiveAuth();
  const salesData = live ? dashboardAnalyticsData(salesAnalytics) : null;
  const trendData = live ? dashboardAnalyticsData(trendAnalytics) : null;
  const operational = salesData && salesData.operational ? salesData.operational : null;
  const monthStart = dashboardStartOfMonth(now);
  const nextMonth = dashboardAddMonths(monthStart, 1);
  const previousMonth = dashboardAddMonths(monthStart, -1);
  const orders = Array.isArray(RP.ORDERS) ? RP.ORDERS : [];
  const currentOrders = live ? [] : dashboardOrdersInRange(orders, monthStart, nextMonth);
  const previousOrders = live ? [] : dashboardOrdersInRange(orders, previousMonth, monthStart);
  const localCurrentTotals = currentOrders.reduce((sum, order) => {
    const totals = dashboardOrderTotals(order);
    sum.cases += totals.cases;
    sum.revenue += totals.revenue;
    return sum;
  }, { cases: 0, revenue: 0 });
  const localPreviousTotals = previousOrders.reduce((sum, order) => {
    const totals = dashboardOrderTotals(order);
    sum.cases += totals.cases;
    sum.revenue += totals.revenue;
    return sum;
  }, { cases: 0, revenue: 0 });
  const today = dashboardStartOfDay(now);
  const trend = live ? dashboardAnalyticsTrend(trendData) : dashboardTrendBuckets(orders, now);
  const openOrders = orders.filter(dashboardOrderIsActive);
  const overdueOrders = openOrders.filter(order => {
    const due = dashboardDate(order.date);
    return due && dashboardStartOfDay(due) < today;
  });
  const stockoutRisks = dashboardStockoutRisks();
  const pendingStockRequests = (RP.STOCK_REQUESTS || []).filter(request => DASHBOARD_PENDING_STOCK_REQUEST_STATUSES.has(String(request.status || '')));
  const inventoryCover = dashboardInventoryCoverRows(now, salesData, trendData);
  const financeRisk = dashboardFinanceRisk(salesAnalytics);
  const orderAging = live ? dashboardAnalyticsOrderAging(operational, openOrders) : dashboardOrderAging(openOrders, now);
  const summary = salesData && salesData.summary ? salesData.summary : null;

  return {
    approvalOrderCount: live ? (operational ? dashboardExactNumber(operational.approvalOrders) : null) : deskApprovalOrders().length,
    casesDelta: live ? (summary ? dashboardFiniteNumber(summary.casesGrowth) : null) : dashboardPercentDelta(localCurrentTotals.cases, localPreviousTotals.cases),
    casesMtd: live ? (summary ? dashboardExactNumber(summary.cases) : null) : localCurrentTotals.cases,
    flavorMix: live ? dashboardAnalyticsFlavorMix(salesData) : dashboardFlavorMix(currentOrders.length ? currentOrders : orders),
    financeRisk,
    inventoryCover,
    openOrderCount: live ? (operational ? dashboardExactNumber(operational.openOrders) : null) : openOrders.length,
    openOrders,
    orderAging,
    overdueOrderCount: live ? (operational ? dashboardExactNumber(operational.overdueOrders) : null) : overdueOrders.length,
    overdueOrders,
    pendingStockRequests,
    revenueDelta: live ? (summary ? dashboardFiniteNumber(summary.revenueGrowth) : null) : dashboardPercentDelta(localCurrentTotals.revenue, localPreviousTotals.revenue),
    revenueMtd: live ? (summary ? dashboardExactNumber(summary.revenue) : null) : localCurrentTotals.revenue,
    stockoutRisks,
    todayLabel: dashboardDateLabel(now),
    trendCases: trend.cases,
    trendRevenue: trend.revenue
  };
}

function dashboardApprovalDisplay(metrics, approvals) {
  const items = Array.isArray(approvals) ? approvals : [];
  const orderItems = items.filter(item => item.kind === 'order');
  const otherItems = items.filter(item => item.kind !== 'order');
  return {
    live: rpHasLiveAuth(),
    orderCount: metrics && metrics.approvalOrderCount != null ? Number(metrics.approvalOrderCount) : null,
    orderItems,
    otherItems
  };
}

function dashboardExceptions(metrics, approvals) {
  const exceptions = [];
  const approvalDisplay = dashboardApprovalDisplay(metrics, approvals);

  if (metrics.stockoutRisks.length) {
    const first = metrics.stockoutRisks[0];
    exceptions.push({
      icon: 'alert',
      tone: 'danger',
      label: `${metrics.stockoutRisks.length} SKU${metrics.stockoutRisks.length === 1 ? '' : 's'} at low stock`,
      meta: `${first.sku.name || first.sku.id} - ${first.qty} cases available`,
      nav: 'inventory'
    });
  }

  if (metrics.overdueOrderCount != null && metrics.overdueOrderCount > 0) {
    const localIds = metrics.overdueOrders.slice(0, 3).map(order => order.id).filter(Boolean);
    exceptions.push({
      icon: 'clock',
      tone: 'warn',
      label: `${metrics.overdueOrderCount} order${metrics.overdueOrderCount === 1 ? '' : 's'} past requested delivery date`,
      meta: localIds.length ? localIds.join(' - ') : 'Open the complete order list for details',
      nav: 'orders'
    });
  }

  if (!approvalDisplay.live && approvals.length) {
    exceptions.push({
      detail: approvals.length === 1 ? approvals[0].detail : null,
      icon: 'tasks',
      tone: 'warn',
      label: `${approvals.length} approval item${approvals.length === 1 ? '' : 's'} waiting for review`,
      meta: approvals.slice(0, 3).map(item => item.id).join(' - '),
      nav: approvals.length === 1 ? approvals[0].nav : 'dashboard'
    });
  }
  if (approvalDisplay.live && approvalDisplay.orderCount != null && approvalDisplay.orderCount > 0) {
    const first = approvalDisplay.orderItems[0];
    exceptions.push({
      detail: approvalDisplay.orderCount === 1 && first ? first.detail : null,
      icon: 'orders',
      tone: 'warn',
      label: `${approvalDisplay.orderCount} order approval${approvalDisplay.orderCount === 1 ? '' : 's'} waiting for review`,
      meta: approvalDisplay.orderItems.length ? approvalDisplay.orderItems.slice(0, 3).map(item => item.id).join(' - ') : 'Open the complete order approval queue',
      nav: 'orders'
    });
  }
  if (approvalDisplay.live && approvalDisplay.otherItems.length) {
    exceptions.push({
      detail: approvalDisplay.otherItems.length === 1 ? approvalDisplay.otherItems[0].detail : null,
      icon: 'tasks',
      tone: 'warn',
      label: `${approvalDisplay.otherItems.length} loaded non-order approval item${approvalDisplay.otherItems.length === 1 ? '' : 's'}`,
      meta: `Working-set preview - ${approvalDisplay.otherItems.slice(0, 3).map(item => item.id).join(' - ')}`,
      nav: approvalDisplay.otherItems.length === 1 ? approvalDisplay.otherItems[0].nav : 'dashboard'
    });
  }

  const approvalStockRequestIds = new Set(approvals.filter(item => item.kind === 'stock-request').map(item => item.id));
  const stockRequestsWaitingFulfillment = metrics.pendingStockRequests.filter(request => !approvalStockRequestIds.has(request.id));
  if (stockRequestsWaitingFulfillment.length) {
    exceptions.push({
      icon: 'inventory',
      tone: 'info',
      label: `${stockRequestsWaitingFulfillment.length} field stock request${stockRequestsWaitingFulfillment.length === 1 ? '' : 's'} waiting fulfillment`,
      meta: stockRequestsWaitingFulfillment.slice(0, 3).map(request => request.id).join(' - '),
      nav: 'inventory'
    });
  }

  return exceptions.filter(e => !rpIsLiveDemoOnlyModule(e.nav));
}

function dashboardTriageSeverityMeta(severity) {
  if (severity === 'Critical') return { rank: 0, fg: 'var(--danger)', bg: 'var(--danger-bg)', status: 'Damaged' };
  if (severity === 'High') return { rank: 1, fg: 'var(--amber-deep)', bg: 'var(--warn-bg)', status: 'Pending' };
  if (severity === 'Medium') return { rank: 2, fg: 'var(--info)', bg: 'var(--info-bg)', status: 'Submitted' };
  return { rank: 3, fg: 'var(--muted)', bg: 'var(--surface-2)', status: 'Submitted' };
}

function dashboardTriageItems(metrics, approvals, unassignedDispatchDirectory) {
  const rows = [];
  const approvalDisplay = dashboardApprovalDisplay(metrics, approvals);
  const add = item => {
    if (!item || rpIsLiveDemoOnlyModule(item.nav)) return;
    const meta = dashboardTriageSeverityMeta(item.severity);
    rows.push({ ...item, rank: meta.rank });
  };

  if (metrics.stockoutRisks.length) {
    const first = metrics.stockoutRisks[0];
    add({
      action: 'Review stock',
      icon: 'alert',
      id: 'stockout-risk',
      impact: `${first.qty} cs available`,
      meta: `${rpSkuDisplayName(first.sku)} is the tightest SKU`,
      nav: 'inventory',
      owner: 'Warehouse',
      severity: first.qty <= 6 ? 'Critical' : 'High',
      title: `${metrics.stockoutRisks.length} low-stock SKU${metrics.stockoutRisks.length === 1 ? '' : 's'}`
    });
  }

  if (metrics.overdueOrderCount != null && metrics.overdueOrderCount > 0) {
    const localIds = metrics.overdueOrders.slice(0, 3).map(order => order.id).filter(Boolean);
    add({
      action: 'Open orders',
      icon: 'clock',
      id: 'overdue-orders',
      impact: localIds.length ? localIds.join(' - ') : 'Complete order history',
      meta: 'Past requested delivery date',
      nav: 'orders',
      owner: 'Operations',
      severity: 'High',
      title: `${metrics.overdueOrderCount} overdue order${metrics.overdueOrderCount === 1 ? '' : 's'}`
    });
  }

  if (!approvalDisplay.live && approvals.length) {
    const first = approvals[0];
    add({
      action: approvals.length === 1 ? 'Review item' : 'Review queue',
      detail: approvals.length === 1 ? first.detail : null,
      icon: 'tasks',
      id: 'approval-queue',
      impact: approvals.slice(0, 3).map(item => item.id).join(' - '),
      meta: first.meta || 'Approval required',
      nav: approvals.length === 1 ? first.nav : 'dashboard',
      owner: 'Approver',
      severity: 'High',
      title: `${approvals.length} approval item${approvals.length === 1 ? '' : 's'}`
    });
  }
  if (approvalDisplay.live && approvalDisplay.orderCount != null && approvalDisplay.orderCount > 0) {
    const first = approvalDisplay.orderItems[0];
    add({
      action: approvalDisplay.orderCount === 1 && first ? 'Review order' : 'Open approval queue',
      detail: approvalDisplay.orderCount === 1 && first ? first.detail : null,
      icon: 'orders',
      id: 'order-approval-queue',
      impact: approvalDisplay.orderItems.length ? approvalDisplay.orderItems.slice(0, 3).map(item => item.id).join(' - ') : 'Complete database queue',
      meta: 'Exact database count',
      nav: 'orders',
      owner: 'Approver',
      severity: 'High',
      title: `${approvalDisplay.orderCount} order approval${approvalDisplay.orderCount === 1 ? '' : 's'}`
    });
  }
  if (approvalDisplay.live && approvalDisplay.otherItems.length) {
    const first = approvalDisplay.otherItems[0];
    add({
      action: approvalDisplay.otherItems.length === 1 ? 'Review loaded item' : 'Review loaded items',
      detail: approvalDisplay.otherItems.length === 1 ? first.detail : null,
      icon: 'tasks',
      id: 'loaded-non-order-approval-queue',
      impact: approvalDisplay.otherItems.slice(0, 3).map(item => item.id).join(' - '),
      meta: 'Working-set preview; not a complete count',
      nav: approvalDisplay.otherItems.length === 1 ? first.nav : 'dashboard',
      owner: 'Approver',
      severity: 'High',
      title: `${approvalDisplay.otherItems.length} loaded non-order approval item${approvalDisplay.otherItems.length === 1 ? '' : 's'}`
    });
  }

  const approvalStockRequestIds = new Set(approvals.filter(item => item.kind === 'stock-request').map(item => item.id));
  const stockRequestsWaitingFulfillment = (metrics.pendingStockRequests || []).filter(request => !approvalStockRequestIds.has(request.id));
  if (stockRequestsWaitingFulfillment.length) {
    add({
      action: 'Fulfill request',
      icon: 'inventory',
      id: 'stock-request-fulfillment',
      impact: stockRequestsWaitingFulfillment.slice(0, 3).map(request => request.id).join(' - '),
      meta: 'Approved or pending field stock movement',
      nav: 'inventory',
      owner: 'Warehouse',
      severity: 'Medium',
      title: `${stockRequestsWaitingFulfillment.length} stock request${stockRequestsWaitingFulfillment.length === 1 ? '' : 's'} waiting`
    });
  }

  const expenses = deskOpenExpenses();
  if (expenses.length) {
    const total = expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    add({
      action: 'Review expenses',
      icon: 'receipt',
      id: 'expense-review',
      impact: RP.money(Math.round(total)),
      meta: expenses.slice(0, 3).map(expense => expense.id || expense.who).join(' - '),
      nav: 'expenses',
      owner: 'Finance',
      severity: expenses.some(expense => expense.flag || String(expense.status || '') === 'Needs Info') ? 'High' : 'Medium',
      title: `${expenses.length} expense${expenses.length === 1 ? '' : 's'} open`
    });
  }

  const liveUnassigned = rpHasLiveAuth() && unassignedDispatchDirectory;
  const unassignedJobs = liveUnassigned
    ? (unassignedDispatchDirectory.items || []).map(rpDispatchJobFromItem).filter(Boolean)
    : deskOpenDispatchJobs().filter(job => !job.lane || job.lane === 'unassigned' || !job.driver);
  const unassignedCount = liveUnassigned && unassignedDispatchDirectory.loaded && unassignedDispatchDirectory.summary && unassignedDispatchDirectory.summary.total != null
    ? Number(unassignedDispatchDirectory.summary.total || 0)
    : unassignedJobs.length;
  const unassignedCases = liveUnassigned && unassignedDispatchDirectory.loaded && unassignedDispatchDirectory.summary && unassignedDispatchDirectory.summary.cases != null
    ? Number(unassignedDispatchDirectory.summary.cases || 0)
    : unassignedJobs.reduce((sum, job) => sum + Number(job.cases || 0), 0);
  if (unassignedCount) {
    add({
      action: 'Assign route',
      icon: 'truck',
      id: 'dispatch-unassigned',
      impact: `${unassignedCases} cs`,
      meta: unassignedJobs.slice(0, 3).map(job => job.id).join(' - '),
      nav: 'dispatch',
      owner: 'Dispatch',
      severity: 'Medium',
      title: `${unassignedCount} dispatch job${unassignedCount === 1 ? '' : 's'} unassigned`
    });
  }

  const failedSyncs = (RP.SHOPIFY_SYNC_RUNS || [])
    .filter(run => run && (run.error || ['failed', 'error'].includes(String(run.status || '').toLowerCase())))
    .slice(0, 3);
  if (failedSyncs.length) {
    add({
      action: 'Open integrations',
      icon: 'alert',
      id: 'shopify-sync-failures',
      impact: failedSyncs.map(run => run.topic || run.id).join(' - '),
      meta: failedSyncs[0].error || 'Shopify sync needs review',
      nav: 'admin',
      owner: 'Admin',
      severity: 'High',
      title: `${failedSyncs.length} Shopify sync issue${failedSyncs.length === 1 ? '' : 's'}`
    });
  }

  deskOpenTasks()
    .filter(task => !deskIsApprovalTask(task))
    .slice()
    .sort((a, b) => {
      const priority = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
      return (priority[a.priority] ?? 4) - (priority[b.priority] ?? 4) || String(a.due || '9999').localeCompare(String(b.due || '9999'));
    })
    .slice(0, 2)
    .forEach(task => add({
      action: 'Open task',
      detail: { type: 'task', id: task.id },
      icon: 'tasks',
      id: `task-${task.id}`,
      impact: task.due ? `Due ${String(task.due).slice(5)}` : task.status || 'Open',
      meta: task.note || task.cat || '',
      nav: 'tasks',
      owner: rpTaskOwnerLabel(task.owner),
      severity: task.priority === 'Urgent' || task.status === 'Blocked' ? 'High' : 'Medium',
      title: task.title || task.id
    }));

  return rows.sort((a, b) => a.rank - b.rank || String(a.title).localeCompare(String(b.title))).slice(0, 7);
}

function dashboardCoverLabel(days) {
  if (!Number.isFinite(days)) return 'No demand signal';
  if (days >= 999) return 'No 8-wk demand';
  return `${days}d cover`;
}

function dashboardCoverStatus(row) {
  if (!row || row.net <= 0 || row.days <= 7) return 'High';
  if (row.days <= 21) return 'Pending';
  return 'Submitted';
}

function dashboardAgeStatus(age) {
  if (age >= 7) return 'High';
  if (age >= 3) return 'Pending';
  return 'Submitted';
}

const DASHBOARD_MANAGER_SALES_ROLES = new Set(['admin', 'leadership', 'ops', 'sales_manager', 'marketing']);
const DASHBOARD_MANAGER_REP_ROLES = new Set(['rep', 'partner']);
const DASHBOARD_REORDER_WATCH_DAYS = 21;

function dashboardRestockCustomerInResponsibilityScope(customer, profileAreas) {
  const areaKeys = new Set((typeof rpAreaNames === 'function' ? rpAreaNames(profileAreas) : [])
    .map(area => String(area || '').trim().toLowerCase())
    .filter(Boolean));
  if (!areaKeys.size) return true;
  const territoryKey = String(customer && customer.territory || '').trim().toLowerCase();
  return Boolean(territoryKey && areaKeys.has(territoryKey));
}

function dashboardRestockRowsForProfile(customers, profile = (typeof rpAuthProfile === 'function' ? rpAuthProfile() : {})) {
  const role = deskNormalizeRole(profile && profile.role);
  if (!DASHBOARD_MANAGER_SALES_ROLES.has(role)) return [];
  const areas = typeof rpProfileAreas === 'function' ? rpProfileAreas(profile) : [];
  const seen = new Set();
  return (Array.isArray(customers) ? customers : []).filter(customer => {
    const id = String(customer && customer.id || '').trim();
    if (!id || seen.has(id) || !dashboardRestockCustomerInResponsibilityScope(customer, areas)) return false;
    const restock = customer && customer.restock;
    if (!restock || restock.eligible === false || restock.checkInDue === false) return false;
    seen.add(id);
    return true;
  }).map(customer => ({ ...customer.restock, customer }));
}

function dashboardRestockCountLabel(state, shown) {
  const rawExact = state && state.total && state.total.value;
  const exact = rawExact == null || rawExact === '' ? NaN : Number(rawExact);
  if (Number.isSafeInteger(exact) && exact >= 0) return `${exact.toLocaleString()} due`;
  const count = Math.max(0, Number(shown) || 0);
  return `${count.toLocaleString()}${state && state.page && state.page.hasMore ? '+' : ''} due`;
}

function dashboardSalesMonthName(index) {
  return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][index] || 'June';
}

function dashboardSalesDefaultPeriod(now = new Date()) {
  return `${dashboardSalesMonthName(now.getMonth())} ${now.getFullYear()}`;
}

function dashboardSalesElapsed(period, now = new Date()) {
  if (RP.PERIOD_ELAPSED && Number.isFinite(Number(RP.PERIOD_ELAPSED[period]))) return Number(RP.PERIOD_ELAPSED[period]);
  const monthly = String(period || '').match(/^[A-Za-z]+\s+\d{4}$/);
  if (!monthly) return 0.5;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return Math.min(1, Math.max(0.01, now.getDate() / daysInMonth));
}

function dashboardManagerSalesEnabled(role, leadership) {
  const normalized = deskNormalizeRole(role || (typeof rpAuthProfile === 'function' ? rpAuthProfile().role : ''));
  if (!DASHBOARD_MANAGER_SALES_ROLES.has(normalized) && !leadership) return false;
  return typeof rpDashboardWorkflowIntelligenceEnabled === 'function' && rpDashboardWorkflowIntelligenceEnabled(normalized);
}

function dashboardRepKey(value) {
  return String(value || '').trim().toLowerCase();
}

function dashboardOpenTask(task) {
  return !['Complete', 'Completed', 'Done', 'Resolved', 'Cancelled', 'Canceled'].includes(String(task.status || ''));
}

function dashboardGoalStatus(goal, elapsed) {
  if (!goal) return 'No Goal';
  if (typeof RP.goalOverall === 'function') return RP.goalOverall(goal, elapsed);
  const revPct = Number(goal.revTarget || 0) > 0 ? Number(goal.revActual || 0) / Number(goal.revTarget || 0) : 0;
  if (revPct + 0.1 < elapsed) return 'Behind';
  if (revPct < elapsed) return 'At Risk';
  return 'On Track';
}

function dashboardGoalPercent(goal, field) {
  if (!goal) return 0;
  const target = Number(goal[`${field}Target`] || 0);
  const actual = Number(goal[`${field}Actual`] || 0);
  return target > 0 ? Math.round((actual / target) * 100) : 0;
}

function dashboardCustomerReorderDays(customer) {
  const summary = typeof rpCustomerOrderSummary === 'function' ? rpCustomerOrderSummary(customer) : {};
  const days = Number(summary.daysSince ?? customer.daysSince);
  if (Number.isFinite(days)) return days;
  return summary.lastOrder || summary.lastOrderDate || customer.lastOrder ? 0 : null;
}

function dashboardSalesManagerData(now = new Date(), salesData = null) {
  const live = rpHasLiveAuth();
  const period = dashboardSalesDefaultPeriod(now);
  const elapsed = dashboardSalesElapsed(period, now);
  const customers = typeof rpVisibleCustomers === 'function' ? rpVisibleCustomers(RP.CUSTOMERS) : (Array.isArray(RP.CUSTOMERS) ? RP.CUSTOMERS : []);
  const orders = Array.isArray(RP.ORDERS) ? RP.ORDERS : [];
  const goals = (RP.SALES_GOALS || []).filter(goal => goal && goal.active !== false);
  const visits = Array.isArray(RP.VISITS) ? RP.VISITS : [];
  const tasks = Array.isArray(RP.TASKS) ? RP.TASKS : [];
  const customersById = new Map(customers.map(customer => [customer.id, customer]));
  const reps = new Map();
  const territories = new Map();

  const addRep = (name, territory, repId = '') => {
    const rep = String(name || '').trim();
    if (!rep || rep === 'Unassigned rep') return null;
    const key = dashboardRepKey(repId || rep);
    if (!key) return null;
    if (!reps.has(key)) {
      reps.set(key, {
        activeCustomers: 0,
        attentionOrders: [],
        attentionOrdersComplete: !live,
        cases: 0,
        creditHolds: 0,
        firstOrderOpportunities: 0,
        goal: null,
        goalStatus: 'No Goal',
        goalRevenuePct: 0,
        goalCasesPct: 0,
        name: rep,
        openTasks: 0,
        overdueTasks: 0,
        restockNow: 0,
        restockSignals: [],
        restockSoon: 0,
        revenue: 0,
        staleCustomers: [],
        territory: territory || 'Unassigned',
        visits30: 0
      });
    } else if (territory && reps.get(key).territory === 'Unassigned') {
      reps.get(key).territory = territory;
    }
    return reps.get(key);
  };

  (RP.USER_PROFILES || []).forEach(user => {
    const userRole = deskNormalizeRole(user.role);
    if (!DASHBOARD_MANAGER_REP_ROLES.has(userRole)) return;
    addRep(user.name || user.display_name || user.email, user.territory || (Array.isArray(user.territories) ? user.territories.join(', ') : ''), user.id);
  });
  customers.forEach(customer => addRep(customer.rep, customer.territory, customer.repId));
  goals.forEach(goal => addRep(goal.rep, goal.territory, goal.repId));

  customers.forEach(customer => {
    const row = addRep(customer.rep, customer.territory, customer.repId);
    const territory = String(customer.territory || 'Unassigned').trim() || 'Unassigned';
    if (!territories.has(territory)) territories.set(territory, { activeCustomers: 0, creditHolds: 0, dormantCustomers: 0, name: territory, openOrders: live ? null : 0, restockDue: 0, revenue: 0 });
    const territoryRow = territories.get(territory);
    const active = ['Active', 'Approved'].includes(String(customer.status || ''));
    const creditHold = typeof rpCustomerOnCreditHold === 'function'
      ? rpCustomerOnCreditHold(customer)
      : ['Hold', 'Watch', 'Credit Hold'].includes(String(customer.credit || customer.status || ''));
    const days = dashboardCustomerReorderDays(customer);
    const restock = typeof rpCustomerRestockSignal === 'function' ? rpCustomerRestockSignal(customer) : null;
    const orderReady = typeof rpCustomerOrderReady === 'function' ? rpCustomerOrderReady(customer) : active;
    if (active) territoryRow.activeCustomers += 1;
    if (creditHold) territoryRow.creditHolds += 1;
    if (active && days != null && days >= DASHBOARD_REORDER_WATCH_DAYS) territoryRow.dormantCustomers += 1;
    if (restock && restock.eligible !== false && (restock.status === 'High' || restock.daysUntilCheckIn <= 7)) territoryRow.restockDue += 1;
    if (!row) return;
    if (active) row.activeCustomers += 1;
    if (creditHold) row.creditHolds += 1;
    if (orderReady && days == null) row.firstOrderOpportunities += 1;
    if (orderReady && days != null && days >= DASHBOARD_REORDER_WATCH_DAYS) row.staleCustomers.push(customer);
    if (restock && restock.eligible !== false && (restock.status === 'High' || restock.daysUntilCheckIn <= 7)) {
      row.restockSignals.push(restock);
      if (restock.status === 'High') row.restockNow += 1;
      else row.restockSoon += 1;
    }
  });

  (live ? [] : orders).forEach(order => {
    if (String(order.status || '') === 'Cancelled') return;
    const customer = customersById.get(order.cust) || {};
    const rep = order.rep || customer.rep;
    const row = addRep(rep, order.territory || customer.territory, order.repId || customer.repId);
    const territory = String(order.territory || customer.territory || 'Unassigned').trim() || 'Unassigned';
    if (!territories.has(territory)) territories.set(territory, { activeCustomers: 0, creditHolds: 0, dormantCustomers: 0, name: territory, openOrders: 0, restockDue: 0, revenue: 0 });
    const totals = live ? { cases: 0, revenue: 0 } : dashboardOrderTotals(order);
    if (!live) territories.get(territory).revenue += totals.revenue;
    if (dashboardOrderIsActive(order)) territories.get(territory).openOrders += 1;
    if (!row) return;
    if (!live) {
      row.revenue += totals.revenue;
      row.cases += totals.cases;
    }
    const due = dashboardDate(order.date || order.requestedDeliveryDate);
    const overdue = due && dashboardStartOfDay(due) < dashboardStartOfDay(now);
    if (dashboardOrderIsActive(order) && (overdue || ['Requested', 'Submitted', 'Needs Approval', 'On Hold'].includes(String(order.status || '')))) {
      row.attentionOrders.push(order);
    }
  });

  if (live && salesData) {
    (Array.isArray(salesData.byRep) ? salesData.byRep : []).forEach(metric => {
      const row = addRep(metric.rep, metric.territory, metric.repId);
      if (!row) return;
      row.revenue = Math.max(0, Number(metric.revenue) || 0);
      row.cases = Math.max(0, Number(metric.cases) || 0);
      if (Number.isFinite(Number(metric.doors))) row.activeCustomers = Math.max(0, Number(metric.doors));
    });
    (Array.isArray(salesData.byTerritory) ? salesData.byTerritory : []).forEach(metric => {
      const territory = String(metric.territory || 'Unassigned').trim() || 'Unassigned';
      if (!territories.has(territory)) territories.set(territory, { activeCustomers: 0, creditHolds: 0, dormantCustomers: 0, name: territory, openOrders: null, restockDue: 0, revenue: 0 });
      const row = territories.get(territory);
      row.revenue = Math.max(0, Number(metric.revenue) || 0);
      if (Number.isFinite(Number(metric.doors))) row.activeCustomers = Math.max(0, Number(metric.doors));
    });
  }

  goals.filter(goal => goal.period === period).forEach(goal => {
    const row = addRep(goal.rep, goal.territory, goal.repId);
    if (!row) return;
    row.goal = goal;
    row.goalStatus = dashboardGoalStatus(goal, elapsed);
    row.goalRevenuePct = dashboardGoalPercent(goal, 'rev');
    row.goalCasesPct = dashboardGoalPercent(goal, 'cases');
  });

  visits.forEach(visit => {
    const visitDate = dashboardDate(visit.date || visit.created || visit.createdAt);
    if (!visitDate || dashboardDaysBetween(visitDate, now) > 30) return;
    const row = addRep(visit.who, '', '');
    if (row) row.visits30 += 1;
  });

  tasks.filter(dashboardOpenTask).forEach(task => {
    const row = addRep(task.owner, '', '');
    if (!row) return;
    row.openTasks += 1;
    const due = dashboardDate(task.due);
    if (due && dashboardStartOfDay(due) < dashboardStartOfDay(now)) row.overdueTasks += 1;
  });

  const coaching = Array.from(reps.values()).map(row => {
    let focus = 'Maintain cadence';
    let nav = 'analytics';
    let status = 'Submitted';
    let rank = 4;
    if (row.goalStatus === 'Behind' || row.goalStatus === 'At Risk') {
      focus = 'Goal recovery';
      nav = 'goals';
      status = row.goalStatus === 'Behind' ? 'High' : 'Pending';
      rank = row.goalStatus === 'Behind' ? 0 : 1;
    } else if (row.restockSignals.length) {
      focus = 'Restock check-ins';
      nav = 'customers';
      status = row.restockNow ? 'High' : 'Pending';
      rank = row.restockNow ? 1 : 2;
    } else if (row.staleCustomers.length || row.firstOrderOpportunities) {
      focus = row.staleCustomers.length ? 'Reorder outreach' : 'First-order push';
      nav = 'customers';
      status = row.staleCustomers.length >= 3 ? 'High' : 'Pending';
      rank = 2;
    } else if (row.attentionOrdersComplete && row.attentionOrders.length) {
      focus = 'Order cleanup';
      nav = 'orders';
      status = 'Pending';
      rank = 3;
    } else if (row.visits30 === 0 && row.activeCustomers > 0) {
      focus = 'Visit cadence';
      nav = 'analytics';
      status = 'Pending';
      rank = 3;
    }
    const signal = (row.goalStatus === 'Behind' ? 8 : row.goalStatus === 'At Risk' ? 5 : 0)
      + row.restockNow * 4
      + row.restockSoon * 2
      + row.staleCustomers.length * 2
      + (row.attentionOrdersComplete ? row.attentionOrders.length : 0)
      + row.overdueTasks;
    return { ...row, focus, nav, rank, signal, status };
  }).filter(row => row.activeCustomers || row.goal || row.revenue || row.openTasks || row.attentionOrders.length)
    .sort((a, b) => a.rank - b.rank || b.signal - a.signal || b.revenue - a.revenue || a.name.localeCompare(b.name))
    .slice(0, 5);

  const territoryHealth = Array.from(territories.values())
    .sort((a, b) => (b.restockDue + b.dormantCustomers + Number(b.openOrders || 0) + b.creditHolds) - (a.restockDue + a.dormantCustomers + Number(a.openOrders || 0) + a.creditHolds) || b.revenue - a.revenue)
    .slice(0, 5);

  const restockRadar = !live && typeof rpRestockRecommendations === 'function'
    ? rpRestockRecommendations(customers, { limit: 6 }).filter(row => row.status !== 'Submitted' || row.daysUntilCheckIn <= 7)
    : [];

  return { coaching, period, restockRadar, territoryHealth };
}

function Dashboard({ go, role, leadership, reportsEnabled, salesAnalytics, unassignedDispatchDirectory }) {
  const [trendMode, setTrendMode] = useState('cases');
  const trendAnalytics = useRpSalesAnalytics('12w');
  const dashboard = dashboardMetrics(salesAnalytics, trendAnalytics);
  const byLoc = RP.LOCATIONS.filter(l => l.type === 'Main Warehouse').map(l => ({
    loc: l, cases: RP.INV.filter(i => String(i.unit || 'case').toLowerCase() !== 'can' && i.loc === l.id && (!i.status || i.status === 'Available')).reduce((s, i) => s + Number(i.qty || 0), 0),
  }));
  const approvals = deskApprovalItems();
  const exceptions = dashboardExceptions(dashboard, approvals);
  const triageItems = dashboardTriageItems(dashboard, approvals, unassignedDispatchDirectory);
  const orderAgingRows = dashboard.orderAging.filter(bucket => bucket.count);
  const financeRisk = dashboard.financeRisk || {};
  const financeOverdue = dashboardExactNumber(financeRisk.overdueBalance);
  const financeAr = dashboardExactNumber(financeRisk.arBalance);
  const financeHoldCount = (financeRisk.creditHolds || []).length;
  const managerSalesVisible = dashboardManagerSalesEnabled(role, leadership);
  const restockRadarVisible = DASHBOARD_MANAGER_SALES_ROLES.has(deskNormalizeRole(role)) || leadership;
  const serverRestockRadar = useRpServerPagedCustomers({
    enabled: restockRadarVisible,
    limit: 6,
    persistItems: false,
    filters: { focus: 'due', scope: 'responsibility', segment: 'business', sort: 'restock' }
  });
  const managerSalesReady = !salesAnalytics.isLive || salesAnalytics.loaded;
  const managerSales = (managerSalesVisible || restockRadarVisible) ? dashboardSalesManagerData(new Date(), dashboardAnalyticsData(salesAnalytics)) : { coaching: [], period: '', restockRadar: [], territoryHealth: [] };
  const restockRadar = serverRestockRadar.isLive
    ? (serverRestockRadar.loaded && !serverRestockRadar.error
      ? dashboardRestockRowsForProfile(serverRestockRadar.items, rpAuthProfile())
      : [])
    : managerSales.restockRadar || [];
  const restockRadarCount = serverRestockRadar.isLive
    ? (serverRestockRadar.error
      ? 'Unavailable'
      : serverRestockRadar.loaded
        ? dashboardRestockCountLabel(serverRestockRadar, restockRadar.length)
        : 'Loading...')
    : `${restockRadar.length} due`;

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <PageHead eyebrow={dashboard.todayLabel} title="Command Center"
        sub="Inventory movement is the system of record. Here's where Ranch Power stands today."
        actions={<>
          {reportsEnabled && <button className="btn btn-sm" onClick={() => go('reports', { type: 'bundle', id: 'full-operations' })}><Icon name="download" size={15} />Reports & Exports</button>}
          <button className="btn btn-sm btn-primary" onClick={() => go('orders')}><Icon name="plus" size={15} />New Order</button>
        </>} />

      <SalesAnalyticsStatus state={salesAnalytics} label="MTD and operational sales totals" />
      <SalesAnalyticsStatus state={trendAnalytics} label="12-week sales trends" />

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        <KPICard label="Revenue · MTD" value={dashboard.revenueMtd == null ? '—' : RP.money(Math.round(dashboard.revenueMtd))} delta={dashboard.revenueDelta} icon="trade" accent="var(--ok)" spark={dashboard.trendRevenue.length ? dashboard.trendRevenue : null} />
        <KPICard label="Cases Sold · MTD" value={dashboard.casesMtd == null ? '—' : dashboard.casesMtd.toLocaleString()} delta={dashboard.casesDelta} icon="package" accent="var(--amber)" spark={dashboard.trendCases.length ? dashboard.trendCases : null} />
        <KPICard label="Open Orders" value={dashboard.openOrderCount == null ? '—' : dashboard.openOrderCount.toLocaleString()} icon="orders" accent="var(--info)" onClick={() => go('orders')} />
        <KPICard label="Stockout Risks" value={dashboard.stockoutRisks.length.toLocaleString()} icon="alert" accent="var(--danger)" onClick={() => go('inventory')} />
      </div>

      <div className="card" style={{ padding: '18px 20px', marginBottom: 18 }}>
        <SectionTitle right={<span className="pill" style={{ color: triageItems.length ? 'var(--amber-deep)' : 'var(--ok)', background: triageItems.length ? 'var(--amber-soft)' : 'var(--ok-bg)' }}>{triageItems.length} active</span>}>Priority Triage</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 }}>
          {triageItems.map(item => {
            const meta = dashboardTriageSeverityMeta(item.severity);
            return (
              <button key={item.id} onClick={() => go(item.nav, item.detail || null)} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 13px', display: 'flex', gap: 11, alignItems: 'flex-start', minHeight: 104, cursor: 'pointer', boxShadow: 'var(--shadow-sm)' }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, color: meta.fg, background: meta.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={item.icon} size={17} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                    <span className="pill" style={{ background: meta.bg, color: meta.fg, fontSize: 10.5 }}>{item.severity}</span>
                    <span style={{ color: 'var(--faint)', fontSize: 11.5, fontWeight: 700 }}>{item.owner}</span>
                  </span>
                  <span style={{ display: 'block', color: 'var(--ink)', fontSize: 13.5, fontWeight: 750, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                  <span style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginTop: 4, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.meta || item.impact}</span>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 9 }}>
                    <span className="mono" style={{ color: 'var(--ink-soft)', fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.impact}</span>
                    <span style={{ color: 'var(--amber-deep)', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap' }}>{item.action}</span>
                  </span>
                </span>
              </button>
            );
          })}
          {!triageItems.length && <div style={{ gridColumn: '1 / -1', color: 'var(--faint)', fontSize: 13, padding: 20, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10 }}>No priority triage items right now.</div>}
        </div>
      </div>

      {restockRadarVisible && (
        <div className="card" data-restock-radar-source={serverRestockRadar.isLive ? 'live-scoped-directory' : 'demo-local'} style={{ padding: '18px 20px', marginBottom: 18 }}>
          <SectionTitle right={<span className="pill" style={{ color: restockRadar.some(row => row.status === 'High') ? 'var(--danger)' : 'var(--amber-deep)', background: restockRadar.some(row => row.status === 'High') ? 'var(--danger-bg)' : 'var(--amber-soft)' }}>{restockRadarCount}</span>}>Retail Restock Radar</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
            {serverRestockRadar.isLive && serverRestockRadar.initialLoading && <div data-restock-radar-loading="true" role="status" style={{ gridColumn: '1 / -1', color: 'var(--muted)', fontSize: 13, padding: 18, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10 }}>Loading restock priorities for your responsibility areas...</div>}
            {serverRestockRadar.isLive && serverRestockRadar.error && <div data-restock-radar-error="true" role="alert" style={{ gridColumn: '1 / -1', color: 'var(--danger)', background: 'var(--danger-bg)', fontSize: 13, padding: 18, textAlign: 'center', border: '1px solid var(--danger)', borderRadius: 10 }}><div>Restock Radar is unavailable right now. No unscoped customer warnings are being substituted.</div><button type="button" className="btn btn-sm" onClick={serverRestockRadar.retry} style={{ marginTop: 10 }}>Retry</button></div>}
            {restockRadar.map(row => {
              const [fg, bg] = statusColors(row.status);
              const customer = row.customer || {};
              return (
                <button key={customer.id || customer.name} onClick={() => go('customers', { type: 'customer', id: customer.id })} style={{ textAlign: 'left', border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 9, cursor: 'pointer', boxShadow: 'var(--shadow-sm)' }}>
                  <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', color: 'var(--ink)', fontSize: 13.5, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.name}</span>
                      <span style={{ display: 'block', color: 'var(--faint)', fontSize: 11.5, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[customer.territory, customer.rep].filter(Boolean).join(' - ')}</span>
                    </span>
                    <span className="pill" style={{ color: fg, background: bg, flexShrink: 0 }}>{row.label}</span>
                  </span>
                  <span style={{ color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.35 }}>{row.reasons.slice(0, 2).join(' - ')}</span>
                  <span style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 7, paddingTop: 7, borderTop: '1px solid var(--line-2)' }}>
                    <span className="mono" style={{ color: 'var(--ink)', fontSize: 11.5, fontWeight: 800 }}>{row.checkInDate ? row.checkInDate.slice(5) : 'Today'}<span style={{ display: 'block', color: 'var(--faint)', fontFamily: 'var(--ff-sans)', fontWeight: 600, marginTop: 2 }}>check by</span></span>
                    <span className="mono" style={{ color: 'var(--ink)', fontSize: 11.5, fontWeight: 800 }}>{row.cadenceDays || '--'}d<span style={{ display: 'block', color: 'var(--faint)', fontFamily: 'var(--ff-sans)', fontWeight: 600, marginTop: 2 }}>cadence</span></span>
                    <span className="mono" style={{ color: 'var(--ink)', fontSize: 11.5, fontWeight: 800 }}>{row.confidence || 'Low'}<span style={{ display: 'block', color: 'var(--faint)', fontFamily: 'var(--ff-sans)', fontWeight: 600, marginTop: 2 }}>confidence</span></span>
                  </span>
                </button>
              );
            })}
            {!restockRadar.length && (!serverRestockRadar.isLive || serverRestockRadar.loaded) && !serverRestockRadar.initialLoading && !serverRestockRadar.error && <div data-restock-radar-empty="true" style={{ gridColumn: '1 / -1', color: 'var(--faint)', fontSize: 13, padding: 18, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10 }}>No retail restock check-ins are due in your responsibility areas.</div>}
          </div>
        </div>
      )}

      {managerSalesVisible && managerSalesReady && (
        <div className="card" style={{ padding: '18px 20px', marginBottom: 18 }}>
          <SectionTitle right={<span className="pill" style={{ color: 'var(--info)', background: 'var(--info-bg)' }}>Preview</span>}>Sales Coaching</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(280px, 0.7fr)', gap: 18 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 9 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>Coaching priorities</span>
                <button className="btn btn-ghost btn-sm" onClick={() => go('goals')}>Goals<Icon name="chevR" size={14} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {managerSales.coaching.map(row => {
                  const [fg, bg] = statusColors(row.status);
                  return (
                    <button key={row.name} onClick={() => go(row.nav)} style={{ width: '100%', textAlign: 'left', border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '11px 12px', display: 'grid', gridTemplateColumns: 'minmax(130px, 0.9fr) minmax(180px, 1.1fr) auto', gap: 12, alignItems: 'center', cursor: 'pointer' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <Avatar name={row.name} size={30} color="var(--navy)" />
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 750, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
                          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.territory}</span>
                        </span>
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                          <span className="pill" style={{ color: fg, background: bg, fontSize: 10.5 }}>{row.focus}</span>
                          {row.goal && <span className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{row.goalRevenuePct}% rev</span>}
                        </span>
                        <span style={{ display: 'block', color: 'var(--muted)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.restockSignals.length} restock due - {row.attentionOrdersComplete ? `${row.attentionOrders.length} order attention` : 'order attention: open complete queue'} - {row.visits30} visits/30d
                        </span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        <Pill status={row.status}>{row.goalStatus !== 'No Goal' ? row.goalStatus : `${row.openTasks} tasks`}</Pill>
                        <Icon name="chevR" size={15} style={{ color: 'var(--faint)' }} />
                      </span>
                    </button>
                  );
                })}
                {!managerSales.coaching.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 18, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10 }}>No coaching priority signals from current sales data.</div>}
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 9 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>Territory health</span>
                <button className="btn btn-ghost btn-sm" onClick={() => go('analytics')}>Analytics<Icon name="chevR" size={14} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {managerSales.territoryHealth.map(row => {
                  const risk = row.creditHolds || row.restockDue >= 3 || row.dormantCustomers >= 3 || (row.openOrders != null && row.openOrders >= 5);
                  return (
                    <button key={row.name} onClick={() => go('analytics')} style={{ width: '100%', textAlign: 'left', border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
                      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', marginBottom: 6 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 750, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
                        <span className="mono" style={{ fontSize: 12, color: risk ? 'var(--amber-deep)' : 'var(--ok)', fontWeight: 750 }}>{RP.money(Math.round(row.revenue || 0))}</span>
                      </span>
                      <span style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 7 }}>
                        <span className="mono" style={{ fontSize: 11.5, color: 'var(--muted)' }}>{row.activeCustomers} doors</span>
                        <span className="mono" style={{ fontSize: 11.5, color: row.restockDue ? 'var(--amber-deep)' : 'var(--muted)' }}>{row.restockDue || 0} check-ins</span>
                        <span className="mono" style={{ fontSize: 11.5, color: row.openOrders ? 'var(--ink)' : 'var(--muted)' }}>{row.openOrders == null ? 'Queue: open list' : `${row.openOrders} in queue`}</span>
                      </span>
                    </button>
                  );
                })}
                {!managerSales.territoryHealth.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 18, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10 }}>No territory health rows yet.</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 18, marginBottom: 18 }}>
        <div className="card" style={{ padding: '18px 20px' }}>
          <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => go('orders')}>Orders<Icon name="chevR" size={14} /></button>}>Order Aging</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orderAgingRows.map(bucket => {
              const status = dashboardAgeStatus(bucket.oldestAge);
              return (
                <div key={bucket.id} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 10, borderBottom: '1px solid var(--line-2)' }}>
                  <span style={{ width: 32, height: 32, borderRadius: 9, color: statusColors(status)[0], background: statusColors(status)[1], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={bucket.id === 'warehouse' ? 'truck' : 'clock'} size={16} /></span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bucket.label}</span>
                    <span className="mono" style={{ display: 'block', fontSize: 11.5, color: 'var(--faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bucket.count} order{bucket.count === 1 ? '' : 's'} - {bucket.cases.toLocaleString()} cs</span>
                  </span>
                  <Pill status={status} style={{ flexShrink: 0 }}>{bucket.oldestAge ? `${bucket.oldestAge}d oldest` : 'Current'}</Pill>
                </div>
              );
            })}
            {!orderAgingRows.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 14, textAlign: 'center' }}>{salesAnalytics.isLive && !salesAnalytics.loaded ? 'Complete order aging is not available yet.' : 'No open orders in aging buckets.'}</div>}
          </div>
        </div>

        <div className="card" style={{ padding: '18px 20px' }}>
          <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => go('inventory')}>Inventory<Icon name="chevR" size={14} /></button>}>Inventory Cover</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {dashboard.inventoryCover.slice(0, 4).map(row => {
              const hasExactCover = row.committed != null && row.net != null && row.days != null && row.salesComplete;
              const status = hasExactCover ? dashboardCoverStatus(row) : 'Submitted';
              const daysValue = hasExactCover ? (row.days >= 999 ? 56 : Math.max(0, row.days)) : 0;
              return (
                <div key={row.sku.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', marginBottom: 5 }}>
                    <span style={{ minWidth: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.sku.name || row.sku.id}</span>
                    <span className="mono" style={{ color: statusColors(status)[0], fontSize: 12, fontWeight: 750, whiteSpace: 'nowrap' }}>{hasExactCover ? dashboardCoverLabel(row.days) : row.salesComplete ? `${Math.round(row.salesCases || 0).toLocaleString()} cs / 12 wk` : 'Sales velocity unavailable'}</span>
                  </div>
                  {hasExactCover && <Bar value={daysValue} max={56} color={status === 'High' ? 'var(--danger)' : status === 'Pending' ? 'var(--warn)' : 'var(--ok)'} />}
                  <div className="mono" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 5, color: 'var(--faint)', fontSize: 11.5 }}>
                    <span>{hasExactCover ? `${row.net.toLocaleString()} net cs` : `${row.available.toLocaleString()} on hand`}</span>
                    <span>{hasExactCover ? `${row.available.toLocaleString()} on hand - ${row.committed.toLocaleString()} committed` : row.salesComplete ? 'Complete database sales history' : 'Waiting for complete sales history'}</span>
                  </div>
                </div>
              );
            })}
            {!dashboard.inventoryCover.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 14, textAlign: 'center' }}>No inventory cover risks from current data.</div>}
          </div>
        </div>

        <div className="card" style={{ padding: '18px 20px' }}>
          <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => go('invoicing')}>A/R<Icon name="chevR" size={14} /></button>}>Finance Risk</SectionTitle>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingBottom: 10, borderBottom: '1px solid var(--line-2)' }}>
              <span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', fontWeight: 700 }}>Outstanding A/R</span>
                <span className="mono" style={{ display: 'block', marginTop: 2, color: 'var(--faint)', fontSize: 11.5 }}>{financeRisk.openCount == null ? 'Loading complete invoice count' : `${financeRisk.openCount} open invoice${Number(financeRisk.openCount) === 1 ? '' : 's'}`}</span>
              </span>
              <span className="disp" style={{ color: financeAr ? 'var(--ink)' : 'var(--ok)', fontSize: 24 }}>{financeAr == null ? '—' : RP.money(Math.round(financeAr))}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingBottom: 10, borderBottom: '1px solid var(--line-2)' }}>
              <span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', fontWeight: 700 }}>{salesAnalytics.isLive ? 'Overdue Invoices' : 'Overdue Balance'}</span>
                <span className="mono" style={{ display: 'block', marginTop: 2, color: 'var(--faint)', fontSize: 11.5 }}>{financeRisk.overdueCount == null ? 'Loading complete invoice count' : `${financeRisk.overdueCount} overdue invoice${Number(financeRisk.overdueCount) === 1 ? '' : 's'}`}</span>
              </span>
              <Pill status={financeRisk.overdueCount ? 'High' : 'Submitted'}>{salesAnalytics.isLive ? (financeRisk.overdueCount == null ? '—' : financeRisk.overdueCount) : RP.money(Math.round(financeOverdue || 0))}</Pill>
            </div>
            <button onClick={() => go('customers')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', border: 0, background: 'transparent', padding: 0, textAlign: 'left', cursor: 'pointer' }}>
              <span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', fontWeight: 700 }}>Credit Holds</span>
                <span className="mono" style={{ display: 'block', marginTop: 2, color: 'var(--faint)', fontSize: 11.5 }}>{financeHoldCount ? (financeRisk.creditHolds || []).slice(0, 2).map(customer => customer.name || customer.id).join(' - ') : 'No customer holds'}</span>
              </span>
              <Pill status={financeHoldCount ? 'Pending' : 'Submitted'}>{financeHoldCount}</Pill>
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 18, marginBottom: 18 }}>
        {/* Sales trend */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <SectionTitle right={<Segmented size="sm" options={[{ value: 'cases', label: 'Cases' }, { value: 'rev', label: 'Revenue' }]} value={trendMode} onChange={setTrendMode} />}>Sales Trend · 12 wk</SectionTitle>
          {trendAnalytics.isLive && !trendAnalytics.loaded
            ? <div style={{ height: 180, display: 'grid', placeItems: 'center', color: 'var(--faint)', fontSize: 13 }}>Complete trend totals are not available yet.</div>
            : <TrendChart data={trendMode === 'rev' ? dashboard.trendRevenue : dashboard.trendCases} mode={trendMode} />}
          <div style={{ display: 'flex', gap: 16, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line-2)', flexWrap: 'wrap', rowGap: 8 }}>
            {dashboard.flavorMix.map(m => (
              <div key={m.flavor} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: RP.flavor(m.flavor).color }} />
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{RP.flavor(m.flavor).name}</span>
                <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{m.share}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Exceptions */}
        <div className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
          <SectionTitle right={<span className="pill" style={{ color: 'var(--danger)', background: 'var(--danger-bg)' }}>{exceptions.length} open</span>}>Open Exceptions</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            {exceptions.map((e, i) => {
              const [fg, bg] = statusColors(e.tone === 'danger' ? 'Damaged' : e.tone === 'warn' ? 'Pending' : 'Submitted');
              return (
                <div key={i} onClick={() => go(e.nav, e.detail || null)} style={{ display: 'flex', gap: 11, padding: '11px 8px', borderRadius: 8, cursor: 'pointer', alignItems: 'center' }}
                  onMouseEnter={ev => ev.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, background: bg, color: fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={e.icon} size={16} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>{e.label}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>{e.meta}</div>
                  </div>
                  <Icon name="chevR" size={15} style={{ color: 'var(--faint)', flexShrink: 0 }} />
                </div>
              );
            })}
            {exceptions.length === 0 && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 20, textAlign: 'center' }}>No live exceptions right now.</div>}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {/* Inventory by location */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => go('inventory')}>View all<Icon name="chevR" size={14} /></button>}>Inventory by Location</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {byLoc.map(b => (
              <div key={b.loc.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{b.loc.name}</span>
                  <span className="mono tnum" style={{ fontSize: 12.5, color: 'var(--muted)' }}><b style={{ color: 'var(--ink)' }}>{b.cases}</b> / {b.loc.cap || 'n/a'} cs</span>
                </div>
                <Bar value={b.cases} max={b.loc.cap || Math.max(b.cases, 1)} color={b.loc.cap && b.cases / b.loc.cap > 0.85 ? 'var(--warn)' : 'var(--navy)'} />
              </div>
            ))}
          </div>
        </div>

        {/* Pending approvals */}
        <div className="card" style={{ padding: '18px 20px' }}>
          <SectionTitle right={<span className="pill" style={{ color: 'var(--amber-deep)', background: 'var(--amber-soft)' }}>{approvals.length} pending</span>}>Pending Approvals</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {approvals.map(item => (
              <div key={`${item.kind}-${item.id}`} onClick={() => go(item.nav, item.detail)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 9, cursor: 'pointer', background: 'var(--surface-2)' }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: ['task', 'stock-request', 'field-receipt'].includes(item.kind) ? 'var(--amber-deep)' : 'var(--info)', background: ['task', 'stock-request', 'field-receipt'].includes(item.kind) ? 'var(--amber-soft)' : 'var(--info-bg)', flexShrink: 0 }}><Icon name={item.icon} size={16} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="mono nowrap" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{item.title}</span>
                    <Pill status={item.status} />
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.meta}</div>
                </div>
                {leadership && <button className="btn btn-sm btn-primary" onClick={e => { e.stopPropagation(); go(item.nav, item.detail); }}>Review</button>}
              </div>
            ))}
            {approvals.length === 0 && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 20, textAlign: 'center' }}>Nothing waiting on you.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Trend chart (bars + line) */
function TrendChart({ data, mode = 'cases' }) {
  const w = 560, h = 150, pad = 6;
  const safeData = data && data.length ? data : Array.from({ length: 12 }, () => 0);
  const max = Math.max(1, ...safeData) * 1.1;
  const bw = (w - pad * 2) / safeData.length;
  const peak = Math.max(0, ...safeData);
  const fmt = v => mode === 'rev' ? RP.money(v) : v.toLocaleString() + ' cs';
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 150, display: 'block' }}>
        {[0.25, 0.5, 0.75].map(g => <line key={g} x1={pad} x2={w - pad} y1={h * g} y2={h * g} stroke="var(--line-2)" strokeWidth="1" />)}
        {safeData.map((v, i) => {
          const bh = (v / max) * (h - 16);
          const x = pad + i * bw + bw * 0.22, bwid = bw * 0.56;
          const last = i === safeData.length - 1;
          return <rect key={i} x={x} y={h - bh} width={bwid} height={bh} rx="2.5" fill={last ? 'var(--amber)' : 'var(--navy)'} opacity={last ? 1 : 0.16 + (i / safeData.length) * 0.5}><title>{`Wk ${i + 1}: ${fmt(v)}`}</title></rect>;
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--faint)' }}>
        <span className="mono">12 wks ago</span>
        <span className="mono">Peak {fmt(peak)}</span>
        <span className="mono">This wk</span>
      </div>
    </div>
  );
}

Object.assign(window, { DeskShell, PageHead, SectionTitle, TrendChart });


/* ===== desktop_actions.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Toast bus, Modal, action modals
   ============================================================ */

/* ---- Global toast bus (works across babel files) ---- */
function toast(msg, tone) { window.dispatchEvent(new CustomEvent('rp-toast', { detail: { msg, tone: tone || 'ok' } })); }

function ToastHost() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let n = 0;
    const on = e => {
      const id = ++n;
      setItems(it => [...it, { id, ...e.detail }]);
      setTimeout(() => setItems(it => it.filter(x => x.id !== id)), e.detail && e.detail.errorTicket ? 15000 : 3200);
    };
    window.addEventListener('rp-toast', on);
    return () => window.removeEventListener('rp-toast', on);
  }, []);
  return (
    <div style={{ position: 'fixed', bottom: 22, right: 22, zIndex: 9000, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
      {items.map(t => {
        const [fg, bg] = statusColors(t.tone === 'danger' ? 'Damaged' : t.tone === 'warn' ? 'Pending' : t.tone === 'info' ? 'Submitted' : 'Approved');
        return (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--navy)', color: '#fff', padding: '13px 16px', borderRadius: 11, boxShadow: 'var(--shadow-lg)', minWidth: 260, maxWidth: 420, animation: 'rp-pop .22s ease both' }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={t.tone === 'danger' ? 'alert' : t.tone === 'info' ? 'refresh' : 'check'} size={15} /></span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 500, lineHeight: 1.35 }}>
              <span>{t.msg}</span>
              {t.errorTicket && <span style={{ display: 'block', marginTop: 8 }}><ErrorTicketButton {...t.errorTicket} compact /></span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---- Modal shell ---- */
function Modal({ title, sub, onClose, children, footer, width = 540 }) {
  const isCompact = useIsNarrow(640);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    if (!isCompact) return undefined;
    const setViewportUnit = () => {
      const height = window.visualViewport && window.visualViewport.height ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty('--rp-modal-vh', `${height * 0.01}px`);
    };
    setViewportUnit();
    window.addEventListener('resize', setViewportUnit);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setViewportUnit);
      window.visualViewport.addEventListener('scroll', setViewportUnit);
    }
    return () => {
      window.removeEventListener('resize', setViewportUnit);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setViewportUnit);
        window.visualViewport.removeEventListener('scroll', setViewportUnit);
      }
    };
  }, [isCompact]);
  const overlayPadding = isCompact ? '8px 8px calc(16px + env(safe-area-inset-bottom, 0px))' : 24;
  const maxHeight = isCompact ? 'calc((var(--rp-modal-vh, 1vh) * 100) - 32px - env(safe-area-inset-bottom, 0px))' : '88vh';
  const footerPadding = isCompact ? '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))' : '16px 24px';
  return (
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 8000, background: 'oklch(0.24 0.035 235 / 0.5)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: overlayPadding }}>
      <div onMouseDown={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: isCompact ? 10 : 16, boxShadow: 'var(--shadow-lg)', width, maxWidth: '100%', maxHeight, display: 'flex', flexDirection: 'column', animation: 'rp-pop .2s ease both', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: isCompact ? '15px 16px 12px' : '20px 24px 16px', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="disp" style={{ margin: 0, fontSize: isCompact ? 19 : 22, textTransform: 'uppercase', letterSpacing: '0.01em', lineHeight: 1.12 }}>{title}</h2>
            {sub && <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 5 }}>{sub}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {typeof window.RP_OPEN_PAGE_HELP === 'function' && <button type="button" className="btn btn-sm" onClick={() => window.RP_OPEN_PAGE_HELP()}>Help</button>}
            <button type="button" aria-label="Close dialog" onClick={onClose} className="btn btn-ghost" style={{ padding: 7, marginRight: -7 }}><Icon name="x" size={18} /></button>
          </div>
        </div>
        <div className="rp-scroll" style={{ padding: isCompact ? '16px' : '20px 24px', overflowY: 'auto', minHeight: 0 }}>{children}</div>
        {footer && <div className="rp-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: footerPadding, borderTop: '1px solid var(--line)', background: 'var(--surface-2)', flexShrink: 0 }}>{footer}</div>}
      </div>
    </div>
  );
}

function MField({ label, required, children, half }) {
  return <div style={{ marginBottom: 16, flex: half ? 1 : 'unset' }}><label className="field-label">{label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}</label>{children}</div>;
}

function rpBlankCustomerContact(primary) {
  return { name: '', role: primary ? 'Buyer' : '', email: '', phone: '', primary: !!primary, receivesInvoices: false };
}
function rpContactDraftFrom(input, fallback, primary) {
  const value = input || {};
  return {
    email: String(value.email || fallback?.email || '').trim().toLowerCase(),
    name: String(value.name || value.contact || value.displayName || fallback?.name || '').trim(),
    phone: String(value.phone || value.phoneNumber || fallback?.phone || '').trim(),
    primary: Boolean(value.primary || primary),
    receivesInvoices: value.receivesInvoices === true || value.receives_invoices === true,
    role: String(value.role || value.title || value.label || fallback?.role || (primary ? 'Buyer' : '')).trim(),
    shopifyContactId: value.shopifyContactId || value.shopify_contact_id || fallback?.shopifyContactId || undefined,
    shopifyCustomerId: value.shopifyCustomerId || value.shopify_customer_id || fallback?.shopifyCustomerId || undefined,
    source: value.source || fallback?.source || undefined
  };
}
function rpCleanCustomerContactsDrafts(contacts) {
  const rows = (Array.isArray(contacts) ? contacts : [])
    .map((contact, index) => {
      const draft = rpContactDraftFrom(contact, {}, index === 0 && contact && contact.primary);
      const row = {
        email: draft.email,
        name: draft.name,
        phone: draft.phone,
        primary: draft.primary,
        receivesInvoices: draft.receivesInvoices,
        role: draft.role || (draft.primary ? 'Buyer' : undefined)
      };
      if (draft.shopifyContactId) row.shopifyContactId = draft.shopifyContactId;
      if (draft.shopifyCustomerId) row.shopifyCustomerId = draft.shopifyCustomerId;
      if (draft.source) row.source = draft.source;
      return row;
    })
    .filter(contact => contact.name || contact.email || contact.phone);
  if (!rows.length) return [];
  const primaryIndex = Math.max(0, rows.findIndex(contact => contact.primary));
  return rows.map((contact, index) => ({ ...contact, primary: index === primaryIndex }));
}
function rpContactEmailInvalid(contact) {
  const email = String(contact && contact.email || '').trim();
  return (contact?.receivesInvoices === true && !email) || (!!email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email));
}
function rpCustomerContactsHaveInvalidEmail(contacts) {
  return (contacts || []).some(rpContactEmailInvalid);
}
function rpIsDirectConsumerType(type) {
  return /consumer|direct/i.test(String(type || '').trim());
}
function rpCustomerTypeOptions() {
  return window.RP_CUSTOMER_TYPES || ['Retailer', 'Consumer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor', 'Direct / Other'];
}
function rpBusinessCustomerTypeOptions() {
  return window.RP_BUSINESS_CUSTOMER_TYPES || ['Retailer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor'];
}
function rpLeadTypeOptions() {
  return window.RP_LEAD_TYPES || ['Retailer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor', 'Other'];
}
function rpPublicLocatorEligibleType(type) {
  return !rpIsDirectConsumerType(type) && String(type || '').trim().toLowerCase() !== 'distributor';
}
function rpPrimaryCustomerContactDraft(contacts) {
  const rows = Array.isArray(contacts) ? contacts : [];
  return rows.find(contact => contact && contact.primary) || rows[0] || {};
}
function rpCustomerLocations(c) {
  const rows = Array.isArray(c && c.locations) ? c.locations : [];
  if (rows.length) return rows.filter(loc => loc && loc.active !== false);
  if (!c) return [];
  return [{
    id: `${c.id || 'customer'}-MAIN`,
    name: c.name || c.banner || 'Location',
    type: 'Ship-to',
    address: c.address || '',
    city: c.city || '',
    postal: c.postal || '',
    phone: c.phone || '',
    lat: c.lat ?? null,
    lng: c.lng ?? null,
    shippingAddress: c.shippingAddress || {},
    billingAddress: c.billingAddress || {},
    coordinateVerification: c.coordinateVerification || null,
    contacts: c.contacts || [],
    active: true
  }];
}
function rpPrimaryCustomerLocation(c) {
  const rows = rpCustomerLocations(c);
  return rows.find(location => location.id === `${c.id}-MAIN`)
    || rows.find(location => String(location.type || '').toLowerCase() !== 'billing')
    || rows[0] || {};
}
function rpBlankCustomerLocation() {
  return { name: '', type: 'Ship-to', address: '', city: '', postal: '', phone: '', lat: '', lng: '', contactName: '', contactEmail: '', contactPhone: '' };
}
function rpCoordinateDraft(value) {
  if (value == null || String(value).trim() === '') return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return String(Math.round(parsed * 100000000) / 100000000);
}
function rpCoordinateInput(value, fallback) {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.round(parsed * 100000000) / 100000000 : NaN;
}
function rpLatitudeInvalid(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  const parsed = Number(raw);
  return !Number.isFinite(parsed) || parsed < -90 || parsed > 90;
}
function rpLongitudeInvalid(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  const parsed = Number(raw);
  return !Number.isFinite(parsed) || parsed < -180 || parsed > 180;
}
function rpCoordinatePairIncomplete(lat, lng) {
  return Boolean(String(lat ?? '').trim()) !== Boolean(String(lng ?? '').trim());
}
function rpLocationDraftFrom(input) {
  const value = input || {};
  const contacts = Array.isArray(value.contacts) ? value.contacts : [];
  const primary = contacts.find(contact => contact && contact.primary) || contacts[0] || {};
  return {
    ...value,
    id: value.id || '',
    name: String(value.name || value.label || '').trim(),
    type: String(value.type || value.locationType || 'Ship-to').trim() || 'Ship-to',
    address: String(value.address ?? value.shippingAddress?.address ?? value.shippingAddress?.address1 ?? '').trim(),
    city: String(value.city ?? value.shippingAddress?.city ?? '').trim(),
    postal: String(value.postal ?? value.shippingAddress?.postal ?? '').trim(),
    address2: String(value.address2 ?? value.shippingAddress?.address2 ?? '').trim(),
    province: String(value.province || value.shippingAddress?.province || 'BC').trim(),
    country: String(value.country || value.shippingAddress?.country || 'Canada').trim(),
    coordinateVerification: value.coordinateVerification ?? value.shippingAddress?.coordinateVerification ?? null,
    phone: String(value.phone ?? value.shippingAddress?.phone ?? '').trim(),
    lat: String(value.lat ?? '').trim(),
    lng: String(value.lng ?? '').trim(),
    contactName: String(value.contactName ?? primary.name ?? '').trim(),
    contactEmail: String(value.contactEmail ?? primary.email ?? '').trim().toLowerCase(),
    contactPhone: String(value.contactPhone ?? primary.phone ?? '').trim()
  };
}
function rpCleanCustomerLocationDrafts(locations) {
  return (Array.isArray(locations) ? locations : [])
    .map(location => {
      const draft = rpLocationDraftFrom(location);
      const previousContacts = Array.isArray(draft.contacts) ? draft.contacts : [];
      const primaryIndex = Math.max(0, previousContacts.findIndex(contact => contact.primary));
      const primary = { ...previousContacts[primaryIndex], name: draft.contactName, email: draft.contactEmail, phone: draft.contactPhone, primary: true, role: previousContacts[primaryIndex]?.role || 'Receiving' };
      const contacts = previousContacts.filter((_, index) => index !== primaryIndex);
      if (primary.name || primary.email || primary.phone) contacts.splice(primaryIndex, 0, primary);
      const previousPrimary = previousContacts[primaryIndex] || {};
      const removedEmails = new Set(previousPrimary.email && previousPrimary.email !== draft.contactEmail ? [previousPrimary.email.toLowerCase()] : []);
      const removedPhones = new Set([
        previousPrimary.phone && previousPrimary.phone !== draft.contactPhone ? previousPrimary.phone : '',
        draft.shippingAddress?.phone && draft.shippingAddress.phone !== draft.phone ? draft.shippingAddress.phone : ''
      ].filter(Boolean));
      const row = {
        ...draft,
        name: draft.name,
        type: draft.type,
        address: draft.address,
        city: draft.city,
        postal: draft.postal,
        phone: draft.phone,
        lat: rpCoordinateInput(draft.lat, null),
        lng: rpCoordinateInput(draft.lng, null),
        shippingAddress: { ...draft.shippingAddress, address1: draft.address, address: draft.address, address2: draft.address2, city: draft.city, postal: draft.postal, province: draft.province, country: draft.country, phone: draft.phone, coordinateVerification: draft.coordinateVerification },
        emailAddresses: (draft.emailAddresses || []).filter(entry => !removedEmails.has(String(typeof entry === 'string' ? entry : entry.value || entry.email || '').toLowerCase())),
        phoneNumbers: (draft.phoneNumbers || []).filter(entry => !removedPhones.has(typeof entry === 'string' ? entry : entry.value || entry.phone)),
        contacts
      };
      if (draft.id) row.id = draft.id;
      return row;
    })
    .filter(location => location.name || location.address || location.city || location.phone || (location.contacts || []).length);
}
function rpCustomerLocationsHaveInvalidCoordinates(locations) {
  return (Array.isArray(locations) ? locations : []).some(location => {
    const draft = rpLocationDraftFrom(location);
    return rpCoordinatePairIncomplete(draft.lat, draft.lng) || rpLatitudeInvalid(draft.lat) || rpLongitudeInvalid(draft.lng);
  });
}
function rpFormatCoordinate(value) {
  if (value == null || String(value).trim() === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(Math.round(parsed * 100000000) / 100000000) : '';
}
function rpCoordinateAddressKey(address = {}) {
  const clean = value => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const country = clean(address.country);
  return [clean(address.address || address.address1), clean(address.city), clean(address.province) || 'bc', clean(address.postal || address.zip).replace(/\s/g, ''), !country || country === 'ca' ? 'canada' : country].join('|');
}
function CoordinatePicker({ label = 'Map Pin', lat, lng, onChange, city, address, postal = '', province = '', country = '', verification, error }) {
  const [manual, setManual] = useState(false);
  const addressLabel = [address, city, province, postal].filter(Boolean).join(', ');
  const addressKey = rpCoordinateAddressKey({ address, city, province, postal, country });
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const requestVersion = useRef(0);
  useEffect(() => {
    requestVersion.current += 1;
    setResults([]);
    setBusy(false);
    setMessage('');
    return () => { requestVersion.current += 1; };
  }, [addressKey]);
  const parsedLat = Number(lat);
  const parsedLng = Number(lng);
  const hasPin = String(lat ?? '').trim() !== '' && String(lng ?? '').trim() !== '' && !rpLatitudeInvalid(lat) && !rpLongitudeInvalid(lng);
  const hasAddress = Boolean(String(address || '').trim() && String(city || '').trim());
  const verified = hasPin && verification?.addressKey === addressKey && Number(verification.lat) === parsedLat && Number(verification.lng) === parsedLng;
  const setPair = (nextLat, nextLng, proof = null) => onChange(rpFormatCoordinate(nextLat), rpFormatCoordinate(nextLng), proof);
  const changeManualPin = (nextLat, nextLng) => {
    requestVersion.current += 1;
    setBusy(false);
    setResults([]);
    setMessage('');
    onChange(nextLat, nextLng, null);
  };
  const confirmPin = (nextLat, nextLng, source, matchedLabel) => {
    if (!hasAddress) { setMessage('Enter the street address and city before confirming its pin.'); return; }
    requestVersion.current += 1;
    setBusy(false);
    setPair(nextLat, nextLng, { source, addressKey, lat: Number(rpFormatCoordinate(nextLat)), lng: Number(rpFormatCoordinate(nextLng)), label: matchedLabel || addressLabel, verifiedAt: new Date().toISOString() });
    setResults([]);
    setMessage('Pin confirmed for this address.');
  };
  const useSearch = async () => {
    if (!hasAddress) { setMessage('Enter the business street address and city first.'); return; }
    const version = ++requestVersion.current;
    setBusy(true);
    setResults([]);
    setMessage('');
    try {
      const response = await rpApi('/api/customers/geocode', { method: 'POST', body: JSON.stringify({ query: addressLabel }) });
      if (version !== requestVersion.current) return;
      setResults(response.candidates || []);
      setMessage(response.message || 'Choose the matching address.');
    } catch (err) {
      if (version === requestVersion.current) setMessage(err.message || 'Address search failed. Try again or enter a verified pin manually.');
    } finally {
      if (version === requestVersion.current) setBusy(false);
    }
  };
  const useBrowserLocation = () => {
    if (!navigator.geolocation) { toast('Browser location is not available.', 'warn'); return; }
    const version = ++requestVersion.current;
    setBusy(true);
    setMessage('Reading this device location. Use it only while at the business.');
    setResults([]);
    navigator.geolocation.getCurrentPosition(
      pos => {
        if (version !== requestVersion.current) return;
        setPair(pos.coords.latitude, pos.coords.longitude);
        setManual(true);
        setBusy(false);
        setMessage(`Device location is accurate to about ${Math.round(pos.coords.accuracy)} m. Check it in Maps, then confirm only if you are at this business.`);
      },
      () => { if (version === requestVersion.current) { setBusy(false); setMessage('Could not read this device location. Search the address or enter a verified pin.'); } },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };
  const mapsQuery = hasPin ? `${parsedLat},${parsedLng}` : addressLabel;
  return (
    <div style={{ marginBottom: 16 }}>
      <label className="field-label">{label}</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, marginBottom: 8 }}>
        <div style={{ alignSelf: 'center', color: 'var(--muted)', fontSize: 12 }}>{addressLabel || 'Enter the business address above to find its pin.'}</div>
        <button type="button" className="btn" disabled={busy || !hasAddress} onClick={useSearch}><Icon name="search" size={14} />{busy ? 'Searching...' : 'Find address'}</button>
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>Street-address search covers BC. For other provinces or countries, look up the business in Maps and enter its pin manually.</div>
      {message && <div role="status" style={{ fontSize: 12, marginTop: 8 }}>{message}</div>}
      {results.length > 0 && <div style={{ display: 'grid', gap: 7, marginTop: 8 }}>{results.map(result => <button type="button" className="btn" key={result.id} disabled={!hasAddress} onClick={() => confirmPin(result.lat, result.lng, result.source, result.label)} style={{ justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'normal' }}><Icon name="pin" size={14} /><span>{result.label}<span style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>Use this address · {result.score}% address match</span></span></button>)}</div>}
      <div className="mono" style={{ marginTop: 10, color: error ? 'var(--danger)' : 'var(--muted)', fontSize: 12 }}>{hasPin ? `${rpFormatCoordinate(parsedLat)}, ${rpFormatCoordinate(parsedLng)} · ${verified ? 'Confirmed' : 'Needs confirmation'}` : 'No pin set'}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {mapsQuery && <a className="btn btn-sm" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`} target="_blank" rel="noopener noreferrer"><Icon name="pin" size={13} />Check in Maps</a>}
        <button type="button" className="btn btn-sm" disabled={busy} onClick={useBrowserLocation}><Icon name="target" size={13} />Use device location</button>
        <button type="button" className="btn btn-sm" onClick={() => setManual(v => !v)}><Icon name="pen" size={13} />Manual</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { requestVersion.current += 1; setBusy(false); setResults([]); setMessage(''); onChange('', '', null); }}><Icon name="x" size={13} />Clear</button>
      </div>
      {manual && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 10, marginTop: 10 }}>
          <input className="field mono" type="number" step="0.00000001" aria-label="Latitude" placeholder="Latitude" value={lat ?? ''} onChange={e => changeManualPin(e.target.value, lng ?? '')} style={{ borderColor: (rpCoordinatePairIncomplete(lat, lng) || rpLatitudeInvalid(lat)) ? 'var(--danger)' : undefined }} />
          <input className="field mono" type="number" step="0.00000001" aria-label="Longitude" placeholder="Longitude" value={lng ?? ''} onChange={e => changeManualPin(lat ?? '', e.target.value)} style={{ borderColor: (rpCoordinatePairIncomplete(lat, lng) || rpLongitudeInvalid(lng)) ? 'var(--danger)' : undefined }} />
        </div>
      )}
      {hasPin && !verified && <button type="button" className="btn btn-sm" disabled={!hasAddress} onClick={() => confirmPin(parsedLat, parsedLng, 'manual', addressLabel)} style={{ marginTop: 10 }}>Confirm this pin for this address</button>}
      <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 8 }}>Address data: <a href="https://digital.gov.bc.ca/bcgov-common-components/bc-address-geocoder/" target="_blank" rel="noopener noreferrer">Province of British Columbia</a></div>
    </div>
  );
}
function CustomerLocationsEditor({ locations, onChange, territory, account, primaryContact }) {
  const rows = Array.isArray(locations) ? locations : [];
  const setLocation = (index, key, value) => onChange(rows.map((location, i) => i === index ? { ...location, [key]: value, ...(['address', 'city', 'postal', 'province', 'country'].includes(key) ? { lat: '', lng: '', coordinateVerification: null, shippingAddress: { ...location.shippingAddress, coordinateVerification: null } } : {}) } : location));
  const removeLocation = index => onChange(rows.filter((_, i) => i !== index));
  const addLocation = () => onChange([...rows, rpBlankCustomerLocation()]);
  const buyerDraft = primaryContact || {};
  const copyBuyerToLocation = index => {
    onChange(rows.map((location, i) => i === index ? {
      ...location,
      contactEmail: String(buyerDraft.email || '').trim().toLowerCase(),
      contactName: String(buyerDraft.name || '').trim(),
      contactPhone: String(buyerDraft.phone || '').trim(),
      phone: String(location.phone || buyerDraft.phone || '').trim()
    } : location));
  };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div>
          <div className="eyebrow">Additional locations</div>
          <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 3 }}>The primary location is edited above. Add only separate branches or receiving addresses here.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={addLocation}><Icon name="plus" size={15} />Location</button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div style={{ border: '1px dashed var(--line)', borderRadius: 10, padding: 13, color: 'var(--muted)', fontSize: 12.5 }}>No saved locations yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((location, index) => {
            const draft = rpLocationDraftFrom(location);
            const badEmail = draft.contactEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.contactEmail);
            const incompleteCoords = rpCoordinatePairIncomplete(draft.lat, draft.lng);
            const badLat = incompleteCoords || rpLatitudeInvalid(draft.lat);
            const badLng = incompleteCoords || rpLongitudeInvalid(draft.lng);
            return (
              <div key={index} style={{ border: '1px solid var(--line)', borderRadius: 11, background: 'var(--surface-2)', padding: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 9, background: '#fff', color: 'var(--muted)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="pin" size={15} /></span>
                    <div><div style={{ fontWeight: 800, fontSize: 13 }}>Location {index + 1}</div><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Storefront, warehouse, receiving bay, or branch</div></div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-sm" title="Use primary buyer as this location contact" onClick={() => copyBuyerToLocation(index)}><Icon name="user" size={13} />Same as Buyer</button>
                    <button type="button" className="btn btn-ghost" onClick={() => removeLocation(index)} aria-label="Remove location"><Icon name="x" size={15} /></button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(130px,.5fr)', gap: 12 }}>
                  <MField label="Location Name"><input className="field" placeholder="e.g. North Vancouver Store" value={draft.name} onChange={e => setLocation(index, 'name', e.target.value)} /></MField>
                  <MField label="Type"><select className="field" value={draft.type} onChange={e => setLocation(index, 'type', e.target.value)}>{['Ship-to', 'Storefront', 'Warehouse', 'Billing', 'Pickup'].map(type => <option key={type}>{type}</option>)}</select></MField>
                </div>
                <MField label="Street Address"><input className="field" placeholder="Street address" value={draft.address} onChange={e => setLocation(index, 'address', e.target.value)} /></MField>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(100px,.35fr) minmax(120px,.45fr)', gap: 12 }}>
                  <MField label="City"><input className="field" placeholder="City" value={draft.city} onChange={e => setLocation(index, 'city', e.target.value)} /></MField>
                  <MField label="Postal"><input className="field" placeholder="V0N..." value={draft.postal} onChange={e => setLocation(index, 'postal', e.target.value)} /></MField>
                  <MField label="Phone"><input className="field" type="tel" placeholder="Receiving phone" value={draft.phone} onChange={e => setLocation(index, 'phone', e.target.value)} /></MField>
                </div>
                <CoordinatePicker label="Location pin" lat={draft.lat} lng={draft.lng} verification={draft.coordinateVerification} postal={draft.postal} province={draft.province} country={draft.country} territory={territory} city={draft.city} address={draft.address} error={badLat || badLng} onChange={(nextLat, nextLng, verification) => onChange(rows.map((locationRow, i) => i === index ? { ...locationRow, lat: nextLat, lng: nextLng, coordinateVerification: verification, shippingAddress: { ...locationRow.shippingAddress, coordinateVerification: verification } } : locationRow))} />
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(120px,.55fr)', gap: 12 }}>
                  <MField label="Location Contact"><input className="field" placeholder="Optional" value={draft.contactName} onChange={e => setLocation(index, 'contactName', e.target.value)} /></MField>
                  <MField label="Contact Email"><input className="field" type="email" placeholder="receiver@example.com" value={draft.contactEmail} onChange={e => setLocation(index, 'contactEmail', e.target.value)} style={{ borderColor: badEmail ? 'var(--danger)' : undefined }} /></MField>
                  <MField label="Contact Phone"><input className="field" type="tel" placeholder="Optional" value={draft.contactPhone} onChange={e => setLocation(index, 'contactPhone', e.target.value)} /></MField>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
function CustomerContactsEditor({ contacts, onChange, tried }) {
  const rows = contacts && contacts.length ? contacts : [rpBlankCustomerContact(true)];
  const primary = rows.find(contact => contact && contact.primary) || rows[0] || {};
  const setContact = (index, key, value) => {
    onChange(rows.map((contact, i) => {
      if (key === 'primary') return { ...contact, primary: i === index };
      return i === index ? { ...contact, [key]: value } : contact;
    }));
  };
  const removeContact = index => {
    const next = rows.filter((_, i) => i !== index);
    if (!next.length) onChange([rpBlankCustomerContact(true)]);
    else if (!next.some(contact => contact.primary)) onChange(next.map((contact, i) => ({ ...contact, primary: i === 0 })));
    else onChange(next);
  };
  const addContact = () => onChange([...rows, rpBlankCustomerContact(false)]);
  const copyPrimaryToContact = index => {
    onChange(rows.map((contact, i) => i === index ? {
      ...contact,
      email: primary.email || '',
      name: primary.name || '',
      phone: primary.phone || ''
    } : contact));
  };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div>
          <div className="eyebrow">Customer Contacts</div>
          <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 3 }}>Primary contact is used for buyer details and portal sign-in.</div>
        </div>
        <button type="button" className="btn" onClick={addContact}><Icon name="plus" size={15} />Contact</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((contact, index) => {
          const badEmail = tried && rpContactEmailInvalid(contact);
          return (
            <div key={index} style={{ border: '1px solid var(--line)', borderRadius: 11, background: contact.primary ? 'var(--surface)' : 'var(--surface-2)', padding: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 9, background: contact.primary ? 'var(--navy)' : '#fff', color: contact.primary ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--line)', flexShrink: 0 }}><Icon name="user" size={15} /></span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--ink)' }}>{contact.primary ? 'Primary Buyer' : `Contact ${index + 1}`}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{contact.primary ? 'Buyer, orders, and account setup' : 'Billing, owner, receiver, or backup contact'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {!contact.primary && <button type="button" className="btn btn-sm" onClick={() => copyPrimaryToContact(index)}><Icon name="user" size={13} />Same as Buyer</button>}
                  {!contact.primary && <button type="button" className="btn btn-ghost" onClick={() => setContact(index, 'primary', true)}><Icon name="check" size={14} />Primary</button>}
                  {rows.length > 1 && <button type="button" className="btn btn-ghost" onClick={() => removeContact(index)} aria-label="Remove contact"><Icon name="x" size={15} /></button>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(130px,.72fr)', gap: 12 }}>
                <MField label="Name"><input className="field" placeholder="Contact name" value={contact.name || ''} onChange={e => setContact(index, 'name', e.target.value)} /></MField>
                <MField label="Role"><input className="field" placeholder={contact.primary ? 'Buyer' : 'Billing, Owner...'} value={contact.role || ''} onChange={e => setContact(index, 'role', e.target.value)} /></MField>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
                <MField label={contact.primary ? 'Buyer Email' : 'Email'}>
                  <input className="field" type="email" placeholder="name@example.com" value={contact.email || ''} onChange={e => setContact(index, 'email', e.target.value)} style={{ borderColor: badEmail ? 'var(--danger)' : undefined }} />
                  {badEmail && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>Enter a valid email</div>}
                </MField>
                <MField label={contact.primary ? 'Primary Phone' : 'Phone'}><input className="field" type="tel" placeholder="(604) 555-0100" value={contact.phone || ''} onChange={e => setContact(index, 'phone', e.target.value)} /></MField>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}><input type="checkbox" checked={contact.receivesInvoices === true} onChange={e => setContact(index, 'receivesInvoices', e.target.checked)} />Receives invoice and receipt copies</label>
              <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 5 }}>Copies are sent in addition to the primary invoice address or billing override.</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function rpAssignableCustomerReps() {
  const profiles = Array.isArray(RP.USER_PROFILES) ? RP.USER_PROFILES : [];
  const roles = new Set(['admin', 'leadership', 'ops', 'sales_manager', 'rep', 'partner', 'marketing', 'dispatch', 'warehouse', 'accountant']);
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const currentId = String(profile.id || '').trim();
  const live = profiles
    .filter(p => p && p.active !== false && roles.has(rpNormalizeRole(p.role)))
    .map(p => ({
      id: p.id,
      name: p.name || p.display_name || p.email || p.id,
      role: p.role || '',
      territory: p.territory || ''
    }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (live.length) {
    const ordered = [];
    const add = rep => {
      if (!rep || !rep.id || ordered.some(item => item.id === rep.id)) return;
      ordered.push(rep);
    };
    if (currentId) add(live.find(rep => String(rep.id || '') === currentId));
    live.forEach(add);
    return ordered;
  }
  return [
    { id: 'demo:Darren', name: 'Darren', role: 'leadership', territory: 'Squamish', demo: true },
    { id: 'demo:Dana Whitfield', name: 'Dana Whitfield', role: 'rep', territory: 'Squamish', demo: true },
    { id: 'demo:Sam Okoro', name: 'Sam Okoro', role: 'rep', territory: 'Northern BC', demo: true },
    { id: 'demo:Marco Ferreira', name: 'Marco Ferreira', role: 'rep', territory: 'Kelowna', demo: true }
  ];
}

const RP_FEE_POLICY_CODES = [
  { code: 'CONTAINER_FEE', label: 'Container Fee' }
];

function rpPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function rpTaxPolicyFromCustomer(c) {
  return {
    feeExemptions: { ...rpPlainObject(c && c.feeExemptions) },
    taxExemptions: { ...rpPlainObject(c && c.taxExemptions) },
    taxOverrides: { ...rpPlainObject(c && c.taxOverrides) },
    taxRegion: String(c && c.taxRegion || 'BC').trim().toUpperCase() || 'BC'
  };
}
function rpCleanPolicyFlags(value) {
  return Object.entries(rpPlainObject(value)).reduce((acc, [code, on]) => {
    if (on === true || String(on || '').toLowerCase() === 'true') acc[String(code).toUpperCase()] = true;
    return acc;
  }, {});
}
function rpCleanFeePolicyFlags(value) {
  const flags = rpCleanPolicyFlags(value);
  if (flags.RECYCLING_FEE || flags.CONTAINER_DEPOSIT) flags.CONTAINER_FEE = true;
  delete flags.RECYCLING_FEE;
  delete flags.CONTAINER_DEPOSIT;
  return flags;
}
function rpCleanTaxOverrides(value) {
  return Object.entries(rpPlainObject(value)).reduce((acc, [code, rate]) => {
    const parsed = Number(rate);
    if (Number.isFinite(parsed) && parsed >= 0) acc[String(code).toUpperCase()] = Math.round(parsed * 100000) / 100000;
    return acc;
  }, {});
}
function rpCleanTaxPolicy(policy) {
  return {
    feeExemptions: rpCleanFeePolicyFlags(policy && policy.feeExemptions),
    taxExemptions: rpCleanPolicyFlags(policy && policy.taxExemptions),
    taxOverrides: rpCleanTaxOverrides(policy && policy.taxOverrides),
    taxRegion: String(policy && policy.taxRegion || 'BC').trim().toUpperCase() || 'BC'
  };
}
function rpTaxRegionOptions() {
  const regions = new Set((RP.TAX_RULES || []).map(rule => String(rule && rule.region || '').toUpperCase()).filter(Boolean));
  regions.add('BC');
  return Array.from(regions).sort();
}
function rpTaxRulesForRegion(region) {
  const wanted = String(region || 'BC').toUpperCase();
  return (RP.TAX_RULES || [])
    .filter(rule => rule && rule.active !== false && String(rule.region || 'BC').toUpperCase() === wanted)
    .sort((a, b) => (Number(a.sortOrder || a.sort_order || 0) - Number(b.sortOrder || b.sort_order || 0)) || String(a.code || '').localeCompare(String(b.code || '')));
}
function rpTaxOverridePercentValue(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return String(Math.round(parsed * 100000) / 1000).replace(/\.?0+$/, '');
}
function rpCustomerTaxPolicySummary(c) {
  const policy = rpCleanTaxPolicy(rpTaxPolicyFromCustomer(c));
  const rules = rpTaxRulesForRegion(policy.taxRegion);
  const charged = rules
    .map(rule => String(rule.code || '').toUpperCase())
    .filter(code => code && !rpExemptionEnabled(policy.taxExemptions, code));
  const notCharged = rules
    .map(rule => String(rule.code || '').toUpperCase())
    .filter(code => code && rpExemptionEnabled(policy.taxExemptions, code));
  const fees = Object.keys(rpCleanFeePolicyFlags(policy.feeExemptions));
  const overrides = Object.keys(rpCleanTaxOverrides(policy.taxOverrides));
  const parts = [`${policy.taxRegion} tax`];
  if (charged.length) parts.push(`${charged.join('/')} charged`);
  if (notCharged.length) parts.push(`${notCharged.join('/')} not charged`);
  if (fees.length) parts.push('container fee not charged');
  if (overrides.length) parts.push(`${overrides.length} override`);
  return parts.join(' - ');
}

const RP_PAYMENT_METHODS = [
  { code: 'account_terms', label: 'Account terms' },
  { code: 'bank_transfer', label: 'Bank transfer' },
  { code: 'e_transfer', label: 'e-Transfer' },
  { code: 'cheque', label: 'Cheque' },
  { code: 'cash', label: 'Cash' },
  { code: 'credit_card', label: 'Credit card' }
];

function rpPaymentPolicyFromCustomer(c) {
  const accepted = rpPlainObject(c && c.acceptedPaymentMethods);
  return {
    acceptedPaymentMethods: {
      account_terms: accepted.account_terms !== false,
      bank_transfer: accepted.bank_transfer !== false,
      e_transfer: accepted.e_transfer !== false,
      cheque: true,
      cash: accepted.cash !== false,
      credit_card: accepted.credit_card === true
    },
    invoiceOverrides: { ...rpPlainObject(c && c.invoiceOverrides) }
  };
}
function rpAccountCreditsFromOverrides(overrides) {
  const source = rpPlainObject(overrides);
  const rows = Array.isArray(source.accountCredits) ? source.accountCredits : (Array.isArray(source.account_credits) ? source.account_credits : []);
  return rows.filter(row => row && Number(row.amount) > 0);
}
function rpCleanPaymentPolicy(policy) {
  const accepted = rpPlainObject(policy && policy.acceptedPaymentMethods);
  const methods = {
    account_terms: accepted.account_terms !== false,
    bank_transfer: accepted.bank_transfer !== false,
    e_transfer: accepted.e_transfer !== false,
    cheque: true,
    cash: accepted.cash !== false,
    credit_card: accepted.credit_card === true
  };
  const overrides = rpPlainObject(policy && policy.invoiceOverrides);
  const cleanOverrides = {};
  const invoiceEmail = String(overrides.invoiceEmail || '').trim().toLowerCase();
  const defaultPaymentMethod = String(overrides.defaultPaymentMethod || '').trim();
  const dueDays = overrides.dueDays == null || String(overrides.dueDays).trim() === '' ? NaN : Number(overrides.dueDays);
  const invoiceNote = String(overrides.invoiceNote || '').trim();
  const pstNumber = String(overrides.pstNumber || overrides.pst_number || '').trim();
  if (invoiceEmail) cleanOverrides.invoiceEmail = invoiceEmail;
  if (defaultPaymentMethod && methods[defaultPaymentMethod]) cleanOverrides.defaultPaymentMethod = defaultPaymentMethod;
  if (pstNumber) cleanOverrides.pstNumber = pstNumber.slice(0, 80);
  if (Number.isFinite(dueDays) && dueDays >= 0) cleanOverrides.dueDays = Math.round(dueDays);
  if (invoiceNote) cleanOverrides.invoiceNote = invoiceNote.slice(0, 500);
  const accountCredits = rpAccountCreditsFromOverrides(overrides);
  if (accountCredits.length) cleanOverrides.accountCredits = accountCredits;
  const stripe = rpPlainObject(overrides.stripe || overrides.stripeVault || overrides.stripe_vault);
  if (Object.keys(stripe).length) cleanOverrides.stripe = stripe;
  const cardOnFile = rpPlainObject(overrides.cardOnFile || overrides.card_on_file || overrides.savedCard || overrides.saved_card);
  if (Object.keys(cardOnFile).length) cleanOverrides.cardOnFile = cardOnFile;
  if (cleanOverrides.defaultPaymentMethod === 'credit_card' && !methods.credit_card) delete cleanOverrides.defaultPaymentMethod;
  return { acceptedPaymentMethods: methods, invoiceOverrides: cleanOverrides };
}
function rpCustomerPaymentPolicySummary(c) {
  const policy = rpCleanPaymentPolicy(rpPaymentPolicyFromCustomer(c));
  const accepted = RP_PAYMENT_METHODS
    .filter(method => policy.acceptedPaymentMethods[method.code])
    .map(method => method.label);
  const defaultMethod = policy.invoiceOverrides.defaultPaymentMethod
    ? (RP_PAYMENT_METHODS.find(method => method.code === policy.invoiceOverrides.defaultPaymentMethod) || {}).label
    : '';
  return [accepted.length ? accepted.join(', ') : 'No payment methods enabled', defaultMethod ? `default ${defaultMethod}` : 'default account terms'].join(' - ');
}
function CustomerPaymentPolicyEditor({ policy, onChange, disabled = false }) {
  const value = rpCleanPaymentPolicy(policy);
  const setPolicy = patch => onChange({ ...policy, ...patch });
  const setMethod = (code, checked) => setPolicy({ acceptedPaymentMethods: { ...value.acceptedPaymentMethods, [code]: checked } });
  const setOverride = (key, raw) => setPolicy({ invoiceOverrides: { ...rpPlainObject(policy && policy.invoiceOverrides), [key]: raw } });
  const allowedDefaultOptions = RP_PAYMENT_METHODS.filter(method => value.acceptedPaymentMethods[method.code]);
  return (
    <fieldset disabled={disabled} style={{ border: '1px solid var(--line)', borderRadius: 11, background: 'var(--surface-2)', padding: 14, margin: '0 0 16px', minWidth: 0, opacity: disabled ? 0.65 : 1 }}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>Invoice & Payment Policy</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(145px,1fr))', gap: 9, marginBottom: 14 }}>
        {RP_PAYMENT_METHODS.map(method => (
          <label key={method.code} style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#fff', border: '1px solid var(--line)', borderRadius: 9, padding: '10px 11px', fontSize: 13, fontWeight: 700 }}>
            <input type="checkbox" checked={!!value.acceptedPaymentMethods[method.code]} onChange={e => setMethod(method.code, e.target.checked)} />
            {method.label}
          </label>
        ))}
      </div>
      <details><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Optional invoice overrides</summary>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(160px,.55fr)', gap: 12 }}>
        <MField label="Invoice Email Override"><input className="field" type="email" placeholder="billing@example.com" value={value.invoiceOverrides.invoiceEmail || ''} onChange={e => setOverride('invoiceEmail', e.target.value)} /></MField>
        <MField label="Default Payment"><select className="field" value={value.invoiceOverrides.defaultPaymentMethod || ''} onChange={e => setOverride('defaultPaymentMethod', e.target.value)}>
          <option value="">Account default</option>
          {allowedDefaultOptions.map(method => <option key={method.code} value={method.code}>{method.label}</option>)}
        </select></MField>
      </div>
      <MField label="PST Exemption #"><input className="field" placeholder="PST number for exempt invoices" value={value.invoiceOverrides.pstNumber || ''} onChange={e => setOverride('pstNumber', e.target.value)} /></MField>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 12 }}>
        <MField label="Due Days Override"><input className="field" type="number" min="0" step="1" placeholder="Use payment terms" value={value.invoiceOverrides.dueDays ?? ''} onChange={e => setOverride('dueDays', e.target.value)} /></MField>
      </div>
      <div className="muted" style={{ fontSize: 12, margin: '-4px 0 12px' }}>Credit-card payments use Ranch Power's standard 2.4% processing fee.</div>
      <MField label="Invoice Note"><textarea className="field" rows={2} placeholder="Optional invoice instructions" value={value.invoiceOverrides.invoiceNote || ''} onChange={e => setOverride('invoiceNote', e.target.value)} style={{ resize: 'vertical' }} /></MField>
      </details>
    </fieldset>
  );
}
function CustomerTaxPolicyEditor({ policy, onChange, disabled = false }) {
  const value = rpCleanTaxPolicy(policy);
  const rules = rpTaxRulesForRegion(value.taxRegion);
  const setPolicy = patch => onChange({ ...policy, ...patch });
  const setFlag = (group, code, checked) => setPolicy({ [group]: { ...rpPlainObject(policy && policy[group]), [code]: checked } });
  const setFeeFlag = (code, checked) => {
    const next = { ...rpPlainObject(policy && policy.feeExemptions), [code]: checked };
    delete next.RECYCLING_FEE;
    delete next.CONTAINER_DEPOSIT;
    setPolicy({ feeExemptions: next });
  };
  const setOverride = (code, raw) => {
    const next = { ...rpPlainObject(policy && policy.taxOverrides) };
    if (raw === '') delete next[code];
    else {
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed >= 0) next[code] = parsed / 100;
    }
    setPolicy({ taxOverrides: next });
  };
  return (
    <fieldset disabled={disabled} style={{ border: '1px solid var(--line)', borderRadius: 11, background: 'var(--surface-2)', padding: 14, margin: '0 0 16px', minWidth: 0, opacity: disabled ? 0.65 : 1 }}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>Tax & Fee Policy</div>
      <MField label="Tax Region">
        <select className="field" value={value.taxRegion} onChange={e => setPolicy({ taxRegion: e.target.value })}>
          {rpTaxRegionOptions().map(region => <option key={region} value={region}>{region}</option>)}
        </select>
      </MField>
      <div style={{ display: 'grid', gap: 9, marginBottom: 12 }}>
        {rules.map(rule => {
          const code = String(rule.code || '').toUpperCase();
          return (
            <div key={rule.id || code} style={{ display: 'grid', gridTemplateColumns: 'minmax(110px,1fr) 150px 112px', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid var(--line)', borderRadius: 9, padding: '10px 11px' }}>
              <div><div style={{ fontWeight: 800, fontSize: 13 }}>{rule.label || code}</div><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{rpTaxOverridePercentValue(rule.rate)}%</div></div>
              <select className="field" value={rpExemptionEnabled(value.taxExemptions, code) ? 'not_charged' : 'charged'} onChange={e => setFlag('taxExemptions', code, e.target.value === 'not_charged')}>
                <option value="charged">Charge</option>
                <option value="not_charged">Do not charge</option>
              </select>
              <input className="field" type="number" min="0" step="0.001" placeholder="Override %" value={rpTaxOverridePercentValue(rpPlainObject(policy && policy.taxOverrides)[code])} onChange={e => setOverride(code, e.target.value)} />
            </div>
          );
        })}
        {!rules.length && <div style={{ color: 'var(--faint)', fontSize: 12.5, padding: '8px 0' }}>No active tax rules for this region.</div>}
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {RP_FEE_POLICY_CODES.map(item => (
          <div key={item.code} style={{ display: 'grid', gridTemplateColumns: 'minmax(130px,1fr) 210px', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid var(--line)', borderRadius: 9, padding: '10px 11px' }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>{item.label}</div>
            <select className="field" value={rpExemptionEnabled(value.feeExemptions, item.code) ? 'not_charged' : 'charged'} onChange={e => setFeeFlag(item.code, e.target.value === 'not_charged')}>
              <option value="charged">Charge</option>
              <option value="not_charged">Do not charge</option>
            </select>
          </div>
        ))}
      </div>
    </fieldset>
  );
}

/* ---- Add Customer (desktop) ---- */
function AddCustomerModal({ onClose, onAdded }) {
  const canManageAccountPricing = rpCanManageCustomerAccountPricing();
  const canManageInvoicePolicy = ['admin', 'leadership', 'ops', 'sales_manager'].includes(rpNormalizeRole(rpAuthProfile()?.role));
  const [f, setF] = useState(() => {
    const reps = rpAssignableCustomerReps();
    const firstRep = reps.find(r => r.id) || reps[0] || { id: '' };
    const tier = rpRetailAccountDefaultPriceListName();
    return { name: '', type: 'Retailer', supplyChannel: 'direct', distributorName: '', requiresPo: false, coordinateVerification: null, territory: 'Squamish', city: '', address: '', address2: '', province: 'BC', country: 'Canada', postal: '', lat: '', lng: '', tier, terms: rpPriceListTerms(tier, 'Prepaid'), customCasePrice: '', rep: firstRep.id, defaultDeliveryMethod: 'Ranch Delivery', note: '', pub: false };
  });
  const [contacts, setContacts] = useState([rpBlankCustomerContact(true)]);
  const [locations, setLocations] = useState([]);
  const [taxPolicy, setTaxPolicy] = useState(() => rpTaxPolicyFromCustomer({}));
  const [paymentPolicy, setPaymentPolicy] = useState(() => rpPaymentPolicyFromCustomer({}));
  const set = (k, v) => setF(s => ({ ...s, [k]: v, ...(['address', 'city', 'postal', 'province', 'country'].includes(k) ? { lat: '', lng: '', coordinateVerification: null } : {}) }));
  const [tried, setTried] = useState(false);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const consumerAccount = rpIsDirectConsumerType(f.type);
  const directoryOnly = f.supplyChannel === 'distributor';
  const publicListingEligible = rpPublicLocatorEligibleType(f.type);
  const defaultDeliveryMethod = typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(f.defaultDeliveryMethod) : f.defaultDeliveryMethod;
  const deliveryNeedsAddress = typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(defaultDeliveryMethod) : defaultDeliveryMethod !== 'Customer Pickup';
  const primaryDraft = rpPrimaryCustomerContactDraft(contacts);
  const primaryEmail = String(primaryDraft.email || '').trim().toLowerCase();
  const primaryPhone = String(primaryDraft.phone || '').trim();
  const primaryEmailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(primaryEmail);
  const customCasePriceText = String(f.customCasePrice || '').trim();
  const customCasePriceValue = customCasePriceText ? Number(customCasePriceText) : null;
  const customCasePriceInvalid = customCasePriceValue !== null && (!Number.isFinite(customCasePriceValue) || customCasePriceValue < 0.01 || customCasePriceValue > 99999999.99);
  const selectedMasterPrice = rpPriceListByName(f.tier);
  const duplicateCandidates = rpCustomerDuplicateCandidates({
    address: f.address,
    city: f.city || f.territory + ', BC',
    email: primaryEmail,
    name: f.name,
    phone: primaryPhone,
    postal: f.postal,
    territory: f.territory,
    type: f.type
  });
  const save = async () => {
    setTried(true);
    setError('');
    if (!f.name.trim() || busy) return;
    if (directoryOnly && (!f.address.trim() || !f.city.trim())) {
      setError('Enter the business street address and city. Contact details are optional.');
      return;
    }
    if (rpCustomerContactsHaveInvalidEmail(contacts)) {
      setError('Enter valid email addresses for every contact or leave them blank.');
      return;
    }
    if (consumerAccount && (!primaryEmailValid || !primaryPhone)) {
      setError('Consumer accounts require a valid email and phone number.');
      return;
    }
    if (!directoryOnly && customCasePriceInvalid) {
      setError('Enter a custom 12-pack price greater than zero, or leave it blank to use the master price list.');
      return;
    }
    if (rpCoordinatePairIncomplete(f.lat, f.lng) || rpLatitudeInvalid(f.lat) || rpLongitudeInvalid(f.lng) || rpCustomerLocationsHaveInvalidCoordinates(locations)) {
      setError('Enter latitude and longitude as a complete valid pair, or leave both blank.');
      return;
    }
    setBusy(true);
    const repOptions = rpAssignableCustomerReps();
    const selectedRep = repOptions.find(rep => rep.id === f.rep) || repOptions[0] || { id: '', name: 'Unassigned rep' };
    const assignedRepId = selectedRep.demo ? '' : selectedRep.id;
    const latValue = rpCoordinateInput(f.lat, null);
    const lngValue = rpCoordinateInput(f.lng, null);
    const cleanedContacts = rpCleanCustomerContactsDrafts(contacts);
    const cleanedTaxPolicy = rpCleanTaxPolicy(taxPolicy);
    const cleanedPaymentPolicy = rpCleanPaymentPolicy(paymentPolicy);
    const primaryContact = cleanedContacts.find(contact => contact.primary) || cleanedContacts[0] || {};
    const accountLocation = { name: f.name.trim(), type: directoryOnly ? 'Storefront' : defaultDeliveryMethod === 'Customer Pickup' ? 'Pickup' : 'Ship-to', address: f.address.trim(), address2: f.address2, province: f.province, country: f.country, city: f.city.trim(), postal: f.postal.trim(), phone: primaryContact.phone || '', lat: latValue, lng: lngValue, coordinateVerification: f.coordinateVerification, shippingAddress: { coordinateVerification: f.coordinateVerification }, contacts: cleanedContacts };
    const cleanedLocations = rpCleanCustomerLocationDrafts(locations);
    const locationRows = [accountLocation, ...cleanedLocations];
    const status = consumerAccount ? 'Approved' : 'Prospect';
    const local = { ...f, customCasePrice: customCasePriceValue, name: f.name.trim(), city: f.city || f.territory + ', BC', buyer: primaryContact.name || '—', email: primaryContact.email || '', phone: primaryContact.phone || '', contacts: cleanedContacts, status: 'Prospect', risk: 'Prospect', lat: latValue, lng: lngValue, address: f.address.trim() || (f.city || f.territory), public: publicListingEligible && f.pub, publicName: f.name.trim(), hours: 'Mon–Fri 9–5', postal: '', note: f.note.trim() || 'Added from Operations console — pending credit approval.' };
    local.defaultDeliveryMethod = defaultDeliveryMethod;
    local.onboardingQuestionnaire = { defaultDeliveryMethod };
    local.postal = f.postal.trim();
    local.city = f.city.trim();
    local.address = f.address.trim();
    local.locations = locationRows;
    local.public = publicListingEligible && f.pub && (!directoryOnly || !!f.coordinateVerification);
    if (directoryOnly) local.note = f.note.trim();
    Object.assign(local, cleanedTaxPolicy);
    Object.assign(local, cleanedPaymentPolicy);
    local.skipCreds = true;
    local.rep = selectedRep.name || 'Unassigned rep';
    local.repId = assignedRepId || '';
    try {
      let serverCustomer = null;
      let createWarnings = [];
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/create', {
          method: 'POST',
          body: JSON.stringify({
            address: f.address.trim(),
            address2: f.address2,
            province: f.province,
            country: f.country,
            supplyChannel: f.supplyChannel,
            distributorName: f.distributorName.trim(),
            ...(canManageInvoicePolicy ? { requiresPo: f.requiresPo } : {}),
            coordinateVerification: f.coordinateVerification,
            ...(!directoryOnly ? { acceptedPaymentMethods: cleanedPaymentPolicy.acceptedPaymentMethods } : {}),
            assignedRepId,
            buyerEmail: primaryContact.email || '',
            buyerName: primaryContact.name || '',
            city: f.city.trim(),
            contacts: cleanedContacts,
            ...(!directoryOnly ? { customCasePrice: customCasePriceValue } : {}),
            customerType: f.type,
            defaultDeliveryMethod,
            displayName: f.name.trim(),
            internalNote: f.note.trim(),
            lat: latValue,
            locations: locationRows,
            lng: lngValue,
            phone: primaryContact.phone || '',
            postal: f.postal.trim(),
            publicLocator: publicListingEligible && f.pub && (!directoryOnly || !!f.coordinateVerification),
            status,
            ...(!directoryOnly ? { invoiceOverrides: cleanedPaymentPolicy.invoiceOverrides, ...cleanedTaxPolicy, terms: consumerAccount ? 'Prepaid' : f.terms, tier: consumerAccount ? 'Consumer' : f.tier } : {}),
            territory: f.territory
          })
        });
        serverCustomer = response.customer;
        createWarnings = [
          response.locationSaveError ? 'location save needs review' : '',
          response.internalNoteWarning ? 'internal note was not saved' : '',
          response.pstNumberTaskError ? 'PST follow-up task was not queued' : ''
        ].filter(Boolean);
      }
      const c = serverCustomer
        ? RP.upsertCustomer({ ...local, ...serverCustomer, skipCreds: true, persisted: true })
        : RP.addCustomer(local);
      if (serverCustomer) await rpRefreshAfterMutation(`${c.name} added`);
      toast(createWarnings.length ? `${c.name} added, but ${createWarnings.join(', ')}.` : (consumerAccount ? `${c.name} added and ready for orders` : `${c.name} added as a prospect`), createWarnings.length ? 'warn' : undefined);
      onAdded && onAdded(c);
      setCreated(c);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create customer.');
    } finally {
      setBusy(false);
    }
  };
  const copy = (text, label) => { try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (e) { } toast((label || 'Copied') + ' to clipboard', 'info'); };

  if (created) {
    const cr = created.creds;
    return (
      <Modal title="Customer Added" sub={`${created.name} · ${created.id}`} onClose={onClose} width={500}
        footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--ok-bg)', color: 'var(--ok)', padding: '12px 15px', borderRadius: 10, marginBottom: 18, fontSize: 13.5, fontWeight: 600 }}>
          <Icon name={cr ? 'mail' : 'check'} size={18} />{directoryOnly ? (created.public ? 'Business saved and published on Find Us.' : 'Business saved. Confirm its location pin to publish it on Find Us.') : cr ? 'Portal credentials emailed to the buyer.' : 'Customer record created as a prospect. Approve before ordering or portal access.'}
        </div>
        {cr && <>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Generated Login</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label className="field-label">Username</label>
              <div style={{ display: 'flex', gap: 8 }}><input className="field mono" readOnly value={cr.username} style={{ flex: 1 }} /><button className="btn" onClick={() => copy(cr.username, 'Username')}><Icon name="doc" size={15} /></button></div>
            </div>
            <div>
              <label className="field-label">Temporary password</label>
              <div style={{ display: 'flex', gap: 8 }}><input className="field mono" readOnly value={cr.password} style={{ flex: 1 }} /><button className="btn" onClick={() => copy(cr.password, 'Password')}><Icon name="doc" size={15} /></button></div>
            </div>
            <div>
              <label className="field-label">Quick-access link</label>
              <div style={{ display: 'flex', gap: 8 }}><input className="field mono" readOnly value={cr.link} style={{ flex: 1, fontSize: 12 }} /><button className="btn btn-navy" onClick={() => copy('https://' + cr.link, 'Link')}><Icon name="doc" size={15} />Copy</button></div>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 12, lineHeight: 1.45 }}>The buyer must set a new password at first sign-in. You can resend or reset anytime from the customer's profile.</div>
        </>}
      </Modal>
    );
  }

  return (
    <Modal title="Add Customer" sub="Create a live customer or prospect record" onClose={onClose} width={700}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Adding...' : 'Add Customer'}</button></>}>
      <MField label="Supplied by"><select className="field" aria-label="Supplied by" value={f.supplyChannel} onChange={e => setF(s => ({ ...s, supplyChannel: e.target.value, pub: e.target.value === 'distributor' ? true : s.pub, type: e.target.value === 'distributor' && !rpPublicLocatorEligibleType(s.type) ? 'Retailer' : s.type }))}><option value="direct">Ranch Power directly</option><option value="distributor">A distributor</option></select></MField>
      {directoryOnly && <><MField label="Distributor name (optional)"><input className="field" value={f.distributorName} onChange={e => set('distributorName', e.target.value)} /></MField><InfoNote icon="pin">Add the business name and street location. Contact details are optional. Confirm its pin to publish it on Find Us.</InfoNote></>}
      <MField label={consumerAccount ? 'Customer Name' : 'Business Name'} required>
        <input className="field" placeholder={consumerAccount ? 'e.g. Alex Morgan' : 'e.g. Cliffside Market'} value={f.name} onChange={e => set('name', e.target.value)} style={{ borderColor: tried && !f.name.trim() ? 'var(--danger)' : undefined }} />
        {tried && !f.name.trim() && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>{consumerAccount ? 'Customer name is required' : 'Business name is required'}</div>}
      </MField>
      <CustomerDuplicateWarning candidates={duplicateCandidates} />
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Customer Type" half><select className="field" value={f.type} onChange={e => setF(s => ({ ...s, type: e.target.value, pub: rpPublicLocatorEligibleType(e.target.value) ? s.pub : false, tier: rpIsDirectConsumerType(e.target.value) ? 'Consumer' : (s.tier === 'Consumer' ? rpRetailAccountDefaultPriceListName() : s.tier), terms: rpIsDirectConsumerType(e.target.value) ? 'Prepaid' : s.terms }))}>{(directoryOnly ? rpCustomerTypeOptions().filter(rpPublicLocatorEligibleType) : rpCustomerTypeOptions()).map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Territory" half><select className="field" value={f.territory} onChange={e => set('territory', e.target.value)}>{RP.TERRITORIES.map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      {!directoryOnly && <MField label="Default Delivery">
        <select className="field" value={defaultDeliveryMethod} onChange={e => set('defaultDeliveryMethod', e.target.value)}>
          {(window.RP_DELIVERY_METHOD_OPTIONS || ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight']).map(option => <option key={option} value={option}>{option}</option>)}
        </select>
        {deliveryNeedsAddress && !f.address.trim() && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35, marginTop: 8 }}>Delivery orders need a ship-to address. Choose Customer Pickup if this account should be allowed to order without one.</div>}
      </MField>}
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="City" half><input className="field" placeholder="City, BC" value={f.city} onChange={e => set('city', e.target.value)} /></MField>
        <MField label="Postal" half><input className="field" placeholder="V8B 0A1" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} /></MField>
      </div>
      <details open={consumerAccount || undefined} style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Buyer &amp; account contacts{consumerAccount ? '' : ' (optional)'}</summary><CustomerContactsEditor contacts={contacts} onChange={setContacts} tried={tried} /></details>
      {consumerAccount && tried && (!primaryEmailValid || !primaryPhone) && <div style={{ marginTop: -8, marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Consumer accounts require a valid primary email and phone number.</div>}
      <MField label={deliveryNeedsAddress ? (consumerAccount ? 'Delivery Address' : 'Street Address') : `${consumerAccount ? 'Delivery Address' : 'Street Address'} (optional)`}><input className="field" placeholder={consumerAccount ? 'Optional delivery address' : 'e.g. 38005 Cleveland Ave'} value={f.address} onChange={e => set('address', e.target.value)} /></MField>
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Unit, province &amp; country</summary><MField label="Unit / address line 2"><input className="field" value={f.address2} onChange={e => set('address2', e.target.value)} /></MField><div style={{ display: 'flex', gap: 14 }}><MField label="Province / state" half><input className="field" value={f.province} onChange={e => set('province', e.target.value)} /></MField><MField label="Country" half><input className="field" value={f.country} onChange={e => set('country', e.target.value)} /></MField></div></details>
      <MField label="Internal note"><textarea className="field" rows={3} placeholder="Delivery instructions, meeting place, account reminders" value={f.note} onChange={e => set('note', e.target.value)} style={{ resize: 'vertical' }} /></MField>
      <CoordinatePicker label="Primary location pin" lat={f.lat} lng={f.lng} verification={f.coordinateVerification} postal={f.postal} province={f.province} country={f.country} territory={f.territory} city={f.city} address={f.address} error={rpCoordinatePairIncomplete(f.lat, f.lng) || rpLatitudeInvalid(f.lat) || rpLongitudeInvalid(f.lng)} onChange={(nextLat, nextLng, verification) => setF(s => ({ ...s, lat: nextLat, lng: nextLng, coordinateVerification: verification, pub: s.supplyChannel === 'distributor' && verification ? true : s.pub }))} />
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Additional locations ({locations.length})</summary>
      <CustomerLocationsEditor
        locations={locations}
        onChange={setLocations}
        territory={f.territory}
        account={{ address: f.address, city: f.city || f.territory + ', BC', postal: f.postal, lat: f.lat, lng: f.lng, name: f.name, phone: primaryDraft.phone || primaryPhone }}
        primaryContact={primaryDraft}
      /></details>
      {!directoryOnly && <>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Master Price List" half><select className="field" aria-label="Master Price List" value={f.tier} disabled={consumerAccount || (!canManageAccountPricing && !rpCanSetCustomAccountPricing())} onChange={e => { const tier = e.target.value; setF(s => ({ ...s, tier, terms: rpPriceListTerms(tier, s.terms) })); }}>{(consumerAccount ? ['Consumer'] : canManageAccountPricing ? rpExistingCustomerPriceListOptionNames({ currentName: f.tier, excludeConsumer: true }) : rpAccountPriceListOptionNames({ currentName: f.tier, excludeConsumer: true })).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}</select></MField>
        <MField label="Assigned Rep" half><select className="field" value={f.rep} onChange={e => set('rep', e.target.value)}>{rpAssignableCustomerReps().map(rep => <option key={rep.id || 'unassigned'} value={rep.id}>{rep.name}{rep.territory ? ` - ${rep.territory}` : ''}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Custom 12-Pack Price" half>
          <input className="field" aria-label="Custom 12-Pack Price" aria-invalid={tried && customCasePriceInvalid} type="number" min="0.01" max="99999999.99" step="0.01" inputMode="decimal" placeholder={selectedMasterPrice && selectedMasterPrice.skuPrice ? RP.money2(selectedMasterPrice.skuPrice) : 'Use master price'} value={f.customCasePrice} disabled={!canManageAccountPricing} onChange={e => set('customCasePrice', e.target.value)} style={{ borderColor: tried && customCasePriceInvalid ? 'var(--danger)' : undefined }} />
          <div style={{ fontSize: 11.5, color: customCasePriceText ? 'var(--amber-deep)' : 'var(--muted)', fontWeight: customCasePriceText ? 700 : 500, marginTop: 5 }}>{customCasePriceText ? 'Customer-specific price overrides the selected master list.' : 'Optional. Leave blank to use the selected master price.'}</div>
        </MField>
        <MField label="Payment Terms" half><select className="field" aria-label="Payment Terms" value={f.terms} disabled={consumerAccount} onChange={e => set('terms', e.target.value)}>{rpAccountPaymentTermOptionNames(f.terms).map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      {consumerAccount && <InfoNote icon="customers">Consumer accounts are prepaid, require only name, email, and phone, and are ready for orders immediately.</InfoNote>}
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Tax &amp; fee settings</summary><CustomerTaxPolicyEditor policy={taxPolicy} onChange={setTaxPolicy} /></details>
      <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, fontWeight: 700 }}><input type="checkbox" checked={f.requiresPo} disabled={!canManageInvoicePolicy} onChange={e => set('requiresPo', e.target.checked)} />Requires PO on invoices</label>
      <CustomerPaymentPolicyEditor policy={paymentPolicy} onChange={setPaymentPolicy} />
      </>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', background: f.pub ? 'var(--amber-soft)' : 'var(--surface-2)', borderRadius: 11, border: '1px solid ' + (f.pub ? 'var(--amber)' : 'var(--line)') }}>
        <div><div style={{ fontSize: 13.5, fontWeight: 700, color: f.pub ? 'var(--amber-deep)' : 'var(--ink)' }}>Mark as public retailer</div><div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{publicListingEligible ? (directoryOnly && !f.coordinateVerification ? 'Saved privately until you confirm the location pin.' : 'Show this location on ranchpower.com/find') : 'Only business locations can be listed publicly'}</div></div>
        <button type="button" aria-label="Publish on Find Us" aria-pressed={f.pub} disabled={!publicListingEligible} onClick={() => set('pub', !f.pub)} style={{ width: 46, height: 27, borderRadius: 100, border: 'none', background: f.pub ? 'var(--amber)' : 'var(--line)', position: 'relative', flexShrink: 0, opacity: publicListingEligible ? 1 : 0.55 }}><span style={{ position: 'absolute', top: 3, left: f.pub ? 22 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} /></button>
      </div>
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

/* ---- Edit Customer (desktop) ---- */
function rpRoleAwareCustomerUpdatePayload({ before, next, profile, customer }) {
  const sourceBefore = before && typeof before === 'object' ? before : {};
  const sourceNext = next && typeof next === 'object' ? next : {};
  const role = rpNormalizeRole(profile && profile.role);
  const management = ['admin', 'leadership', 'ops', 'sales_manager'].includes(role);
  const field = ['rep', 'partner'].includes(role);
  const allowed = management
    ? new Set(Object.keys(sourceNext))
    : field ? new Set([
         'id', 'address', 'buyerEmail', 'buyerName', 'city', 'contacts',
         'defaultDeliveryMethod', 'displayName', 'emailAddresses', 'internalNote',
         'invoiceOverrides', 'lat', 'legalName', 'lng', 'locations', 'notes',
         'phone', 'phoneNumbers', 'postal', 'publicLocator', 'taxExemptions',
         'supplyChannel', 'distributorName', 'coordinateVerification', 'address2', 'province', 'country'
       ]) : new Set(['id']);
  if (field && rpCanManageCustomerAccountPricing(profile)) {
    allowed.add('tier');
    allowed.add('customCasePrice');
  }
  if (field && rpCanApproveCustomers(profile, customer)) {
    allowed.add('status');
    allowed.add('creditStatus');
  }
  const payload = { id: sourceNext.id };
  Object.keys(sourceNext).forEach(key => {
    if (key === 'id' || !allowed.has(key)) return;
    if (JSON.stringify(sourceNext[key]) !== JSON.stringify(sourceBefore[key])) payload[key] = sourceNext[key];
  });
  return payload;
}

function EditCustomerModal({ customer, onClose, onSaved }) {
  const c = customer || {};
  const [primaryLocation, setPrimaryLocation] = useState(() => rpLocationDraftFrom(rpPrimaryCustomerLocation(c)));
  const editorProfile = rpAuthProfile();
  const editorRole = rpNormalizeRole(editorProfile && editorProfile.role);
  const fieldEditor = ['rep', 'partner'].includes(editorRole);
  const managementApprovalOverride = ['admin', 'leadership', 'ops', 'sales_manager'].includes(editorRole);
  const canManageAccountPricing = rpCanManageCustomerAccountPricing(editorProfile);
  const canApproveAccount = rpCanApproveCustomers(editorProfile, c);
  const initialRep = (() => {
    const reps = rpAssignableCustomerReps();
    return c.repId || (reps.find(rep => rep.name === c.rep) || {}).id || '';
  })();
  const [f, setF] = useState({
    address: primaryLocation.address,
    address2: primaryLocation.address2,
    province: primaryLocation.province,
    country: primaryLocation.country,
    city: primaryLocation.city,
    lat: rpCoordinateDraft(primaryLocation.lat),
    lng: rpCoordinateDraft(primaryLocation.lng),
    coordinateVerification: primaryLocation.coordinateVerification,
    supplyChannel: c.supplyChannel || 'direct',
    distributorName: c.distributorName || '',
    requiresPo: !!c.requiresPo,
    name: c.name || '',
    note: c.note || '',
    postal: primaryLocation.postal,
    pub: !!c.public,
    rep: initialRep,
    status: c.status || 'Prospect',
    terms: c.terms || 'Prepaid',
    territory: c.territory || 'Squamish',
    tier: c.tier || 'Default Retail',
    customCasePrice: c.customCasePrice == null ? '' : String(c.customCasePrice),
    defaultDeliveryMethod: typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(c) : 'Ranch Delivery',
    type: c.type || 'Retailer'
  });
  const [contacts, setContacts] = useState(() => {
    const fallback = { email: c.email || '', name: c.buyer === 'Buyer' ? '' : (c.buyer || ''), phone: rpPrimaryCustomerPhone(c), role: 'Buyer' };
    const existing = rpCustomerContacts(c);
    const drafts = existing.length
      ? existing.map((contact, index) => rpContactDraftFrom(contact, index === 0 ? fallback : {}, Boolean(contact.primary || index === 0)))
      : [rpContactDraftFrom({}, fallback, true)];
    if (drafts.length && !drafts.some(contact => contact.primary)) drafts[0].primary = true;
    return drafts.length ? drafts : [rpBlankCustomerContact(true)];
  });
  const [locations, setLocations] = useState(() => {
    const rows = rpCustomerLocations(c);
    return rows.filter(location => location.id !== primaryLocation.id).map(rpLocationDraftFrom);
  });
  const [taxPolicy, setTaxPolicy] = useState(() => rpTaxPolicyFromCustomer(c));
  const [paymentPolicy, setPaymentPolicy] = useState(() => rpPaymentPolicyFromCustomer(c));
  const set = (k, v) => setF(s => ({ ...s, [k]: v, ...(['address', 'city', 'postal', 'province', 'country'].includes(k) ? { lat: '', lng: '', coordinateVerification: null } : {}) }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const consumerAccount = rpIsDirectConsumerType(f.type);
  const directoryOnly = f.supplyChannel === 'distributor';
  const publicListingEligible = rpPublicLocatorEligibleType(f.type);
  const defaultDeliveryMethod = typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(f.defaultDeliveryMethod) : f.defaultDeliveryMethod;
  const deliveryNeedsAddress = typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(defaultDeliveryMethod) : defaultDeliveryMethod !== 'Customer Pickup';
  const intakePending = Boolean(rpCustomerQuoteProspect(c) && !c.onboardingSubmittedAt);
  const intakeBlocksApproval = Boolean(intakePending && !managementApprovalOverride);
  const archivedCustomer = Boolean(c.archivedAt || c.archived_at || String(c.status || '').trim().toLowerCase() === 'inactive');
  const customCasePriceText = String(f.customCasePrice || '').trim();
  const customCasePriceValue = customCasePriceText ? Number(customCasePriceText) : null;
  const customCasePriceInvalid = customCasePriceValue !== null && (!Number.isFinite(customCasePriceValue) || customCasePriceValue < 0.01 || customCasePriceValue > 99999999.99);
  const selectedMasterPrice = rpPriceListByName(f.tier);
  const save = async () => {
    setTried(true);
    setError('');
    if (!f.name.trim() || busy) return;
    if (directoryOnly && (!f.address.trim() || !f.city.trim())) {
      setError('Enter the business street address and city. Contact details are optional.');
      return;
    }
    if (archivedCustomer) {
      setError('This archived account is unavailable for edits or approval. Restore its saved quote first, or use the active customer account.');
      return;
    }
    if (intakeBlocksApproval && ['Active', 'Approved'].includes(f.status)) {
      setError('Send the retailer intake link and wait for submission before activating this quote-created account.');
      return;
    }
    if (rpCustomerContactsHaveInvalidEmail(contacts) || rpContactEmailInvalid({ email: primaryLocation.contactEmail }) || locations.some(location => rpContactEmailInvalid({ email: location.contactEmail }))) {
      setError('Enter valid email addresses for every contact or leave them blank.');
      return;
    }
    if (!directoryOnly && customCasePriceInvalid) {
      setError('Enter a custom 12-pack price greater than zero, or leave it blank to use the master price list.');
      return;
    }
    if (rpCoordinatePairIncomplete(f.lat, f.lng) || rpLatitudeInvalid(f.lat) || rpLongitudeInvalid(f.lng) || rpCustomerLocationsHaveInvalidCoordinates(locations)) {
      setError('Enter latitude and longitude as a complete valid pair, or leave both blank.');
      return;
    }
    setBusy(true);
    const latValue = rpCoordinateInput(f.lat, c.lat || null);
    const lngValue = rpCoordinateInput(f.lng, c.lng || null);
    const cleanedContacts = rpCleanCustomerContactsDrafts(contacts);
    const cleanedTaxPolicy = rpCleanTaxPolicy(taxPolicy);
    const cleanedPaymentPolicy = rpCleanPaymentPolicy(paymentPolicy);
    const primaryContact = cleanedContacts.find(contact => contact.primary) || cleanedContacts[0] || {};
    const accountLocation = { ...primaryLocation, id: primaryLocation.id || `${c.id}-MAIN`, name: primaryLocation.name === c.name ? f.name.trim() : primaryLocation.name || f.name.trim(), address: f.address.trim(), address2: f.address2, province: f.province, country: f.country, city: f.city.trim(), postal: f.postal.trim(), lat: latValue, lng: lngValue, coordinateVerification: f.coordinateVerification, shippingAddress: { ...primaryLocation.shippingAddress, coordinateVerification: f.coordinateVerification } };
    const cleanedLocations = rpCleanCustomerLocationDrafts(locations);
    const locationRows = rpCleanCustomerLocationDrafts([accountLocation, ...locations]);
    const repOptions = rpAssignableCustomerReps();
    const selectedRep = repOptions.find(rep => rep.id === f.rep) || repOptions[0] || { id: '', name: 'Unassigned rep' };
    const assignedRepId = selectedRep.demo ? '' : selectedRep.id;
    const fullPayload = {
      address: f.address.trim(),
      address2: f.address2,
      province: f.province,
      country: f.country,
      supplyChannel: f.supplyChannel,
      distributorName: f.distributorName.trim(),
      requiresPo: f.requiresPo,
      coordinateVerification: f.coordinateVerification,
      acceptedPaymentMethods: cleanedPaymentPolicy.acceptedPaymentMethods,
      assignedRepId,
      buyerEmail: primaryContact.email || '',
      buyerName: primaryContact.name || '',
      city: f.city.trim(),
      contacts: cleanedContacts,
      customCasePrice: customCasePriceValue,
      customerType: f.type,
      defaultDeliveryMethod,
      displayName: f.name.trim(),
      id: c.id,
      internalNote: f.note.trim(),
      lat: latValue,
      locations: locationRows,
      lng: lngValue,
      phone: primaryContact.phone || '',
      postal: f.postal.trim(),
      publicLocator: publicListingEligible && f.pub && (!directoryOnly || !!f.coordinateVerification),
      status: f.status,
      invoiceOverrides: cleanedPaymentPolicy.invoiceOverrides,
      ...cleanedTaxPolicy,
      terms: f.terms,
      territory: f.territory,
      tier: f.tier
    };
    const existingContacts = rpCustomerContacts(c);
    const existingContactDrafts = existingContacts.length
      ? existingContacts.map((contact, index) => rpContactDraftFrom(contact, {}, Boolean(contact.primary || index === 0)))
      : [rpContactDraftFrom({}, { email: c.email || '', name: c.buyer === 'Buyer' ? '' : (c.buyer || ''), phone: rpPrimaryCustomerPhone(c), role: 'Buyer' }, true)];
    const existingCleanedContacts = rpCleanCustomerContactsDrafts(existingContactDrafts);
    const existingPrimaryContact = existingCleanedContacts.find(contact => contact.primary) || existingCleanedContacts[0] || {};
    const beforeDefaultDeliveryMethod = typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(c) : 'Ranch Delivery';
    const beforeLat = c.lat == null || c.lat === '' ? null : Number(c.lat);
    const beforeLng = c.lng == null || c.lng === '' ? null : Number(c.lng);
    const existingLocationRows = rpCleanCustomerLocationDrafts(rpCustomerLocations(c).map(rpLocationDraftFrom));
    const beforeAccountLocation = {
      id: `${c.id}-MAIN`,
      name: String(c.name || '').trim(),
      type: beforeDefaultDeliveryMethod === 'Customer Pickup' ? 'Pickup' : 'Ship-to',
      address: String(c.address || '').trim(),
      city: String(c.city || c.territory || '').trim(),
      postal: String(c.postal || '').trim(),
      phone: existingPrimaryContact.phone || '',
      lat: beforeLat,
      lng: beforeLng,
      contacts: existingCleanedContacts
    };
    const beforePaymentPolicy = rpCleanPaymentPolicy(rpPaymentPolicyFromCustomer(c));
    const beforePayload = {
      address2: c.address2 || '',
      province: c.province || 'BC',
      country: c.country || 'Canada',
      supplyChannel: c.supplyChannel || 'direct',
      distributorName: c.distributorName || '',
      requiresPo: !!c.requiresPo,
      coordinateVerification: c.coordinateVerification || null,
      address: String(c.address || '').trim(),
      acceptedPaymentMethods: beforePaymentPolicy.acceptedPaymentMethods,
      assignedRepId: initialRep,
      buyerEmail: String(c.email || '').trim().toLowerCase(),
      buyerName: c.buyer === 'Buyer' ? '' : String(c.buyer || '').trim(),
      city: String(c.city || c.territory || '').trim(),
      contacts: existingCleanedContacts,
      customCasePrice: c.customCasePrice == null ? null : Number(c.customCasePrice),
      customerType: c.type || 'Retailer',
      defaultDeliveryMethod: beforeDefaultDeliveryMethod,
      displayName: String(c.name || '').trim(),
      id: c.id,
      internalNote: String(c.note || '').trim(),
      invoiceOverrides: beforePaymentPolicy.invoiceOverrides,
      lat: beforeLat,
      locations: existingLocationRows.length ? existingLocationRows : [beforeAccountLocation],
      lng: beforeLng,
      phone: rpPrimaryCustomerPhone(c),
      postal: String(c.postal || '').trim(),
      publicLocator: !!c.public,
      status: c.status || 'Prospect',
      ...rpCleanTaxPolicy(rpTaxPolicyFromCustomer(c)),
      terms: c.terms || 'Prepaid',
      territory: c.territory || 'Squamish',
      tier: c.tier || 'Default Retail'
    };
    if (directoryOnly) ['acceptedPaymentMethods', 'customCasePrice', 'invoiceOverrides', 'taxExemptions', 'taxOverrides', 'taxRegion', 'feeExemptions', 'feeOverrides', 'terms', 'tier', 'requiresPo'].forEach(key => { fullPayload[key] = beforePayload[key]; });
    const payload = rpRoleAwareCustomerUpdatePayload({ before: beforePayload, next: fullPayload, profile: editorProfile, customer: c });
    try {
      let serverCustomer = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/update', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        serverCustomer = response.customer;
      }
      const updated = serverCustomer
        ? RP.upsertCustomer({ ...c, ...serverCustomer, skipCreds: true, persisted: true })
        : RP.upsertCustomer({ ...c, ...cleanedTaxPolicy, ...cleanedPaymentPolicy, supplyChannel: fullPayload.supplyChannel, distributorName: fullPayload.distributorName, requiresPo: fullPayload.requiresPo, coordinateVerification: fullPayload.coordinateVerification, name: fullPayload.displayName, banner: fullPayload.displayName, type: fullPayload.customerType, territory: fullPayload.territory, city: fullPayload.city, postal: fullPayload.postal, tier: fullPayload.tier, customCasePrice: fullPayload.customCasePrice, terms: fullPayload.terms, status: fullPayload.status, risk: fullPayload.status === 'Prospect' ? 'Prospect' : 'Low', buyer: fullPayload.buyerName || 'Buyer', email: fullPayload.buyerEmail, phone: fullPayload.phone, contacts: cleanedContacts, locations: locationRows, lat: latValue, lng: lngValue, address: fullPayload.address, defaultDeliveryMethod, onboardingQuestionnaire: { ...(c.onboardingQuestionnaire || {}), defaultDeliveryMethod }, note: fullPayload.internalNote, public: fullPayload.publicLocator, rep: selectedRep.name || 'Unassigned rep', repId: assignedRepId || '' });
      if (serverCustomer) await rpRefreshAfterMutation(`${updated.name} updated`);
      toast(`${updated.name} updated`, 'ok');
      onSaved && onSaved(updated);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update customer.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit Customer" sub={`${c.id || ''} account details`} onClose={onClose} width={700}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || archivedCustomer} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : archivedCustomer ? 'Archived Account' : 'Save Changes'}</button></>}>
      {archivedCustomer && <div role="alert" style={{ marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}>This account is archived and unavailable for edits or approval. Restore its saved quote first, or use the active customer account.</div>}
      <MField label="Supplied by"><select className="field" aria-label="Supplied by" value={f.supplyChannel} onChange={e => setF(s => ({ ...s, supplyChannel: e.target.value, pub: e.target.value === 'distributor' ? true : s.pub, type: e.target.value === 'distributor' && !rpPublicLocatorEligibleType(s.type) ? 'Retailer' : s.type }))}><option value="direct">Ranch Power directly</option><option value="distributor">A distributor</option></select></MField>
      {directoryOnly && <><MField label="Distributor name (optional)"><input className="field" value={f.distributorName} onChange={e => set('distributorName', e.target.value)} /></MField><InfoNote icon="pin">Add the business name and street location. Contact details are optional. Confirm its pin to publish it on Find Us.</InfoNote></>}
      <MField label="Business Name" required>
        <input className="field" value={f.name} onChange={e => set('name', e.target.value)} style={{ borderColor: tried && !f.name.trim() ? 'var(--danger)' : undefined }} />
        {tried && !f.name.trim() && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>Business name is required</div>}
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Customer Type" half><select className="field" value={f.type} disabled={fieldEditor} onChange={e => setF(s => { const nextType = e.target.value; const nextConsumer = rpIsDirectConsumerType(nextType); const nextTier = nextConsumer ? 'Consumer' : (s.tier === 'Consumer' ? rpRetailAccountDefaultPriceListName() : s.tier); return { ...s, type: nextType, pub: rpPublicLocatorEligibleType(nextType) ? s.pub : false, tier: nextTier, terms: nextConsumer ? 'Prepaid' : (s.tier === 'Consumer' ? rpPriceListTerms(nextTier, s.terms) : s.terms) }; })}>{(directoryOnly ? rpCustomerTypeOptions().filter(rpPublicLocatorEligibleType) : rpCustomerTypeOptions().concat('Shopify')).map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Account Status" half><select className="field" value={f.status} disabled={archivedCustomer || (fieldEditor && !canApproveAccount)} onChange={e => set('status', e.target.value)}>{(fieldEditor ? Array.from(new Set([f.status, 'Active', 'Approved'])) : ['Prospect', 'Active', 'Approved', 'Draft', 'Inactive']).map(t => <option key={t} disabled={intakeBlocksApproval && ['Active', 'Approved'].includes(t)}>{t}</option>)}</select></MField>
      </div>
      {!directoryOnly && !archivedCustomer && intakeBlocksApproval && <div style={{ marginTop: -4, marginBottom: 14, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35 }}>This quote-created account stays a Prospect until the retailer submits intake. Use Send Intake Link on the customer page.</div>}
      {!directoryOnly && !archivedCustomer && intakePending && managementApprovalOverride && <div style={{ marginTop: -4, marginBottom: 14, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35 }}>Retailer intake is still incomplete. Authorized managers can approve this account now; follow up with the retailer to finish onboarding.</div>}
      {!directoryOnly && <MField label="Default Delivery">
        <select className="field" value={defaultDeliveryMethod} onChange={e => set('defaultDeliveryMethod', e.target.value)}>
          {(window.RP_DELIVERY_METHOD_OPTIONS || ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight']).map(option => <option key={option} value={option}>{option}</option>)}
        </select>
        {deliveryNeedsAddress && !f.address.trim() && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35, marginTop: 8 }}>Delivery orders need a ship-to address. Choose Customer Pickup if this account should be allowed to order without one.</div>}
      </MField>}
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Territory" half><select className="field" value={f.territory} disabled={fieldEditor} onChange={e => set('territory', e.target.value)}>{RP.TERRITORIES.map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="City" half><input className="field" value={f.city} onChange={e => set('city', e.target.value)} /></MField>
      </div>
      <MField label="Postal"><input className="field" placeholder="V8B 0A1" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} /></MField>
      <MField label={deliveryNeedsAddress ? 'Street Address' : 'Street Address (optional)'}><input className="field" value={f.address} onChange={e => set('address', e.target.value)} /></MField>
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Unit, province &amp; country</summary><MField label="Unit / address line 2"><input className="field" value={f.address2} onChange={e => set('address2', e.target.value)} /></MField><div style={{ display: 'flex', gap: 14 }}><MField label="Province / state" half><input className="field" value={f.province} onChange={e => set('province', e.target.value)} /></MField><MField label="Country" half><input className="field" value={f.country} onChange={e => set('country', e.target.value)} /></MField></div></details>
      <MField label="Internal note"><textarea className="field" rows={3} value={f.note} onChange={e => set('note', e.target.value)} placeholder="Delivery instructions, meeting place, account reminders" style={{ resize: 'vertical' }} /></MField>
      <CoordinatePicker label="Primary location pin" lat={f.lat} lng={f.lng} verification={f.coordinateVerification} postal={f.postal} province={f.province} country={f.country} territory={f.territory} city={f.city} address={f.address} error={rpCoordinatePairIncomplete(f.lat, f.lng) || rpLatitudeInvalid(f.lat) || rpLongitudeInvalid(f.lng)} onChange={(nextLat, nextLng, verification) => setF(s => ({ ...s, lat: nextLat, lng: nextLng, coordinateVerification: verification, pub: s.supplyChannel === 'distributor' && verification ? true : s.pub }))} />
      <details open={consumerAccount || undefined} style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Buyer &amp; account contacts{consumerAccount ? '' : ' (optional)'}</summary><CustomerContactsEditor contacts={contacts} onChange={setContacts} tried={tried} /></details>
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Primary location receiving contact (optional)</summary>
        <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 12 }}>Use this only when deliveries have a different contact from the account buyer.</div>
        <MField label="Receiving contact"><input className="field" value={primaryLocation.contactName} onChange={e => setPrimaryLocation(row => ({ ...row, contactName: e.target.value }))} /></MField>
        <MField label="Receiving email"><input className="field" type="email" value={primaryLocation.contactEmail} onChange={e => setPrimaryLocation(row => ({ ...row, contactEmail: e.target.value }))} /></MField>
        <MField label="Receiving phone"><input className="field" type="tel" value={primaryLocation.contactPhone || primaryLocation.phone} onChange={e => setPrimaryLocation(row => ({ ...row, contactPhone: e.target.value, phone: row.phone === row.contactPhone || !row.contactPhone ? e.target.value : row.phone }))} /></MField>
      </details>
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Additional locations ({locations.length})</summary>
      <CustomerLocationsEditor
        locations={locations}
        onChange={setLocations}
        territory={f.territory}
        account={{ address: f.address, city: f.city || f.territory, postal: f.postal, lat: f.lat, lng: f.lng, name: f.name, phone: rpPrimaryCustomerContactDraft(contacts).phone || rpPrimaryCustomerPhone(c) }}
        primaryContact={rpPrimaryCustomerContactDraft(contacts)}
      /></details>
      {!directoryOnly && <>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Master Price List" half><select className="field" aria-label="Master Price List" value={f.tier} disabled={consumerAccount || !canManageAccountPricing} onChange={e => { const tier = e.target.value; setF(s => ({ ...s, tier, terms: rpPriceListTerms(tier, s.terms) })); }}>{(consumerAccount ? ['Consumer'] : rpExistingCustomerPriceListOptionNames({ currentName: f.tier, excludeConsumer: true })).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}</select></MField>
        <MField label="Assigned Rep" half><select className="field" value={f.rep} disabled={fieldEditor} onChange={e => set('rep', e.target.value)}>{rpAssignableCustomerReps().map(rep => <option key={rep.id || 'house'} value={rep.id}>{rep.name}{rep.territory ? ` - ${rep.territory}` : ''}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Custom 12-Pack Price" half>
          <input className="field" aria-label="Custom 12-Pack Price" aria-invalid={tried && customCasePriceInvalid} type="number" min="0.01" max="99999999.99" step="0.01" inputMode="decimal" placeholder={selectedMasterPrice && selectedMasterPrice.skuPrice ? RP.money2(selectedMasterPrice.skuPrice) : 'Use master price'} value={f.customCasePrice} disabled={!canManageAccountPricing} onChange={e => set('customCasePrice', e.target.value)} style={{ borderColor: tried && customCasePriceInvalid ? 'var(--danger)' : undefined }} />
          <div style={{ fontSize: 11.5, color: customCasePriceText ? 'var(--amber-deep)' : 'var(--muted)', fontWeight: customCasePriceText ? 700 : 500, marginTop: 5 }}>{customCasePriceText ? 'Customer-specific price overrides the selected master list.' : 'Optional. Clear this field to return to the selected master price.'}</div>
        </MField>
        <MField label="Payment Terms" half><select className="field" aria-label="Payment Terms" value={f.terms} disabled={consumerAccount || !canManageAccountPricing} onChange={e => set('terms', e.target.value)}>{rpAccountPaymentTermOptionNames(f.terms).map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <details style={{ marginBottom: 16 }}><summary style={{ cursor: 'pointer', fontWeight: 700, marginBottom: 12 }}>Tax &amp; fee settings</summary><CustomerTaxPolicyEditor policy={taxPolicy} onChange={setTaxPolicy} disabled={fieldEditor} /></details>
      <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, fontWeight: 700 }}><input type="checkbox" checked={f.requiresPo} disabled={!managementApprovalOverride} onChange={e => set('requiresPo', e.target.checked)} />Requires PO on invoices</label>
      <CustomerPaymentPolicyEditor policy={paymentPolicy} onChange={setPaymentPolicy} disabled={fieldEditor} />
      </>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', background: f.pub ? 'var(--amber-soft)' : 'var(--surface-2)', borderRadius: 11, border: '1px solid ' + (f.pub ? 'var(--amber)' : 'var(--line)') }}>
        <div><div style={{ fontSize: 13.5, fontWeight: 700, color: f.pub ? 'var(--amber-deep)' : 'var(--ink)' }}>Public retailer listing</div><div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{publicListingEligible ? (directoryOnly && !f.coordinateVerification ? 'Saved privately until you confirm the location pin.' : 'Show this location on ranchpower.com/find') : 'Only business locations can be listed publicly'}</div></div>
        <button type="button" aria-label="Publish on Find Us" aria-pressed={f.pub} disabled={!publicListingEligible} onClick={() => set('pub', !f.pub)} style={{ width: 46, height: 27, borderRadius: 100, border: 'none', background: f.pub ? 'var(--amber)' : 'var(--line)', position: 'relative', flexShrink: 0, opacity: publicListingEligible ? 1 : 0.55 }}><span style={{ position: 'absolute', top: 3, left: f.pub ? 22 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} /></button>
      </div>
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

/* ---- Record Deduction (desktop) ---- */
function RecordDeductionModal({ onClose, onAdded }) {
  const [f, setF] = useState({ cust: 'C-004', type: 'Slotting', amount: '', period: 'Q2 2026', gl: 'Trade — Slotting' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const types = { 'Slotting': 'Trade — Slotting', 'Chargeback': 'Trade — Chargeback', 'Promo Allowance': 'Trade — Promo', 'Credit Memo': 'Trade — Credit' };
  const save = () => {
    setTried(true);
    const amt = parseFloat(f.amount);
    if (!amt || amt <= 0) return;
    if (!Array.isArray(RP.DEDUCTIONS)) RP.DEDUCTIONS = [];
    const id = 'TS-' + (505 + RP.DEDUCTIONS.length);
    RP.DEDUCTIONS.unshift({ id, cust: f.cust, type: f.type, amount: amt, status: 'Pending', period: f.period, skus: [], gl: types[f.type], owner: 'S. Manager' });
    toast(`Deduction ${id} recorded · ${RP.money(amt)}`, 'info');
    onAdded && onAdded();
    onClose();
  };
  return (
    <Modal title="Record Deduction" sub="Trade spend posts against gross-to-net" onClose={onClose} width={500}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Record</button></>}>
      <MField label="Customer" required><select className="field" value={f.cust} onChange={e => set('cust', e.target.value)}>{rpVisibleCustomers(RP.CUSTOMERS).filter(rpCustomerOrderReady).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Type" half><select className="field" value={f.type} onChange={e => set('type', e.target.value)}>{Object.keys(types).map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Period" half><select className="field" value={f.period} onChange={e => set('period', e.target.value)}>{['Q2 2026', 'Jun 2026', 'May 2026', 'Q3 2026'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <MField label="Amount" required>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
          <input className="field" style={{ paddingLeft: 26, borderColor: tried && !(parseFloat(f.amount) > 0) ? 'var(--danger)' : undefined }} placeholder="0.00" type="number" value={f.amount} onChange={e => set('amount', e.target.value)} />
        </div>
        {tried && !(parseFloat(f.amount) > 0) && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>Enter an amount</div>}
      </MField>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5 }}><Icon name="trade" size={15} />Posts to <b style={{ marginLeft: 2 }}>{types[f.type]}</b> · routes to Sales Manager for approval.</div>
    </Modal>
  );
}

/* ---- New Order (desktop quick builder) ---- */
function LegacyNewOrderModal({ onClose, presetCust }) {
  const [custId, setCustId] = useState(presetCust || '');
  const [qty, setQty] = useState({});
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitOrderId] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `SO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 20).toUpperCase()}`
    : `SO-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`);
  const skus = rpManualOrderSkus();
  const lines = Object.entries(qty).filter(([k, v]) => v > 0);
  const cases = lines.reduce((s, [k, v]) => s + v, 0);
  const c = custId ? RP.cust(custId) : null;
  const customerOrderReady = c ? rpCustomerOrderReady(c) : false;
  const deliveryMethod = c && typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(c) : 'Ranch Delivery';
  const total = rpOrderTotals({ cust: custId, lines: lines.map(([sku, qty]) => ({ sku, qty })) }).total;
  const save = async () => {
    setTried(true);
    setError('');
    if (!custId || cases === 0 || busy) return;
    if (!customerOrderReady) {
      setError('Customer must be approved before orders can be created.');
      return;
    }
    setBusy(true);
    let id = submitOrderId;
    let external = null;
    let response = null;
    let status = 'Submitted';
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        response = await rpApi('/api/orders/submit', {
          method: 'POST',
          body: JSON.stringify({
            id: submitOrderId,
            customerId: custId,
            deliveryMethod,
            lineItems: lines.map(([sku, q]) => {
              const item = rpSkuItem(sku);
              return {
                sku,
                title: item ? item.name : sku,
                quantity: q,
                originalUnitPrice: rpCustomerSkuPrice(c, item).toFixed(2),
                deposit: item && item.deposit || 0
              };
            }),
            note: 'Ops quick order',
            sourceLocationId: 'WH-SQ'
          })
        });
        id = response.orderId || id;
        external = response.draftOrder || response;
        status = (response.order && response.order.status) || (response.mode === 'shopify-draft-order' ? 'Submitted' : 'Requested');
      }
      const fallbackOrder = { id, cust: custId, territory: c.territory, rep: c.rep, source: 'WH-SQ', delivery: deliveryMethod, status, date: '2026-06-14', created: typeof rpNowLocalInput === 'function' ? rpNowLocalInput(c) : new Date().toISOString().slice(0, 16).replace('T', ' '), reason: 'Ops-entered', external, lines: lines.map(([sku, q]) => ({ sku, qty: q })) };
      const savedOrder = rpUpsertOrder(rpOrderFromApi(response, fallbackOrder));
      await rpRefreshAfterMutation(`Order ${savedOrder.id} created`);
      id = savedOrder.id;
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create order.');
      setBusy(false);
      return;
    }
    toast(`Order ${id} created for ${c.name} · ${cases} cases`);
    setBusy(false);
    onClose();
  };
  return (
    <Modal title="New Order" sub="Quick order builder" onClose={onClose} width={580}
      footer={<><div style={{ flex: 1, alignSelf: 'center' }}>{cases > 0 && <span style={{ fontSize: 13, color: 'var(--muted)' }}><b className="disp" style={{ fontSize: 18, color: 'var(--ink)' }}>{RP.money(total)}</b> · {cases} cs</span>}</div><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: custId && cases && !busy ? 1 : 0.5 }}><Icon name="check" size={15} />{busy ? 'Creating...' : 'Create Order'}</button></>}>
      <MField label="Customer" required>
        <select className="field" value={custId} onChange={e => setCustId(e.target.value)} style={{ borderColor: tried && !custId ? 'var(--danger)' : undefined }}>
          <option value="">Select a customer…</option>
          {rpVisibleCustomers(RP.CUSTOMERS).filter(rpCustomerOrderReady).map(c => <option key={c.id} value={c.id}>{c.name} · {c.territory}</option>)}
        </select>
      </MField>
      {c && rpCustomerOnCreditHold(c) && rpActiveApprovalRule('CREDIT_HOLD_CUSTOMER') && <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}><Icon name="alert" size={15} />On credit hold — order will route for override.</div>}
      {c && !customerOrderReady && <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}><Icon name="flag" size={15} />Approve this customer before creating orders or portal access.</div>}
      <label className="field-label">Products</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {skus.map(s => {
              const q = qty[s.id] || 0;
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 11px', border: '1px solid ' + (q ? 'var(--amber)' : 'var(--line)'), borderRadius: 9 }}>
              <FlavorChip sku={s} size={26} />
              <div style={{ flex: 1 }}><div style={{ fontWeight: 500, fontSize: 13.5 }}>{rpSkuDisplayName(s)}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{s.pack} - {RP.money2(c ? rpCustomerSkuPrice(c, s) : rpDefaultCasePrice())}/case</div></div>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                <button className="btn btn-ghost" style={{ padding: '6px 9px', borderRadius: 0 }} onClick={() => setQty(p => ({ ...p, [s.id]: Math.max(0, q - 1) }))}><Icon name="minus" size={14} /></button>
                <span className="mono tnum" style={{ width: 32, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>{q}</span>
                <button className="btn btn-ghost" style={{ padding: '6px 9px', borderRadius: 0, color: 'var(--amber-deep)' }} onClick={() => setQty(p => ({ ...p, [s.id]: q + 1 }))}><Icon name="plus" size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
      {error && <div style={{ marginTop: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function defaultDeliveryDate() {
  if (typeof rpTodayYmd === 'function') return rpTodayYmd(null, 7);
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
}
function qtyFromOrder(order) {
  return (order && order.lines ? order.lines : []).reduce((acc, line) => {
    const item = rpSkuItem(line.sku);
    if (item && rpSkuIsManualOrderable(item)) acc[item.id] = Number(line.qty || 0);
    return acc;
  }, {});
}
function rpOrderCleanLot(value) {
  return String(value || '').trim().replace(/\s+/g, '').toUpperCase();
}
function rpOrderLineLotMap(order) {
  return (order && order.lines ? order.lines : []).reduce((acc, line) => {
    const item = rpSkuItem(line.sku);
    const lot = rpOrderCleanLot(line.lot);
    if (item && lot) acc[item.id] = lot;
    return acc;
  }, {});
}
function rpOrderLotRowsForSku(skuId, locId) {
  const canonicalId = rpCanonicalInventorySkuId(skuId);
  const rows = Array.isArray(RP.INV) ? RP.INV.filter(row => {
    if (!row || String(row.unit || 'case').toLowerCase() === 'can') return false;
    if (rpCanonicalInventorySkuId(row.sku) !== canonicalId) return false;
    if (locId && row.loc !== locId) return false;
    return Boolean(rpOrderCleanLot(row.lot));
  }) : [];
  const byLot = new Map();
  rows.forEach(row => {
    const lot = rpOrderCleanLot(row.lot);
    const status = String(row.status || 'Available').toLowerCase();
    const qty = ['available', 'fronted to rep', 'purchased by rep'].includes(status) ? Math.max(0, rpFiniteNumber(row.qty)) : 0;
    const current = byLot.get(lot) || { lot, qty: 0 };
    current.qty += qty;
    byLot.set(lot, current);
  });
  return Array.from(byLot.values())
    .filter(row => row.qty > 0)
    .sort((a, b) => a.lot.localeCompare(b.lot));
}
function rpOrderLotOptionsForSku(skuId, locId) {
  const scoped = rpOrderLotRowsForSku(skuId, locId);
  return scoped.length ? scoped : rpOrderLotRowsForSku(skuId, '');
}
function rpOrderCommittedCasesByLot(skuId, locId, lot, options = {}) {
  if (!locId) return 0;
  const canonicalId = rpCanonicalInventorySkuId(skuId);
  const selectedLot = rpOrderCleanLot(lot);
  const excludeId = String(options.excludeOrderId || '');
  if (rpHasLiveAuth()) {
    const total = (Array.isArray(RP.ORDER_STOCK_COMMITMENTS) ? RP.ORDER_STOCK_COMMITMENTS : []).reduce((sum, row) => {
      if (!row) return sum;
      const sourceLocationId = String(row.sourceLocationId || row.source_location_id || '');
      const rowSkuId = rpCanonicalInventorySkuId(row.sku || row.sku_id);
      if (sourceLocationId !== locId || rowSkuId !== canonicalId) return sum;
      const rowLot = rpOrderCleanLot(row.lot);
      if (selectedLot && rowLot && rowLot !== selectedLot) return sum;
      const quantity = Math.max(0, rpFiniteNumber(row.quantity));
      const unit = String(row.unit || 'case').toLowerCase();
      return sum + (unit === 'can' || unit === 'cans' ? quantity / 12 : quantity);
    }, 0);
    const excludedOrder = excludeId
      ? (Array.isArray(RP.ORDERS) ? RP.ORDERS : []).find(order => String(order && order.id || '') === excludeId)
      : null;
    const excluded = rpCachedOrderCommittedCases(excludedOrder, canonicalId, locId, selectedLot);
    return Math.max(0, total - excluded);
  }
  return (Array.isArray(RP.ORDERS) ? RP.ORDERS : []).reduce((sum, order) => {
    if (!order || order.id === excludeId || order.source !== locId || !RP_OPEN_ORDER_STOCK_STATUSES.has(order.status)) return sum;
    return sum + (order.lines || []).reduce((lineSum, line) => {
      const lineSkuId = line && rpCanonicalInventorySkuId(line.inventorySku || line.inventorySkuId || line.sku);
      if (lineSkuId !== canonicalId) return lineSum;
      const lineLot = rpOrderCleanLot(line.lot);
      if (selectedLot && lineLot && lineLot !== selectedLot) return lineSum;
      return lineSum + rpOrderLineCommittedCaseEquivalent(line);
    }, 0);
  }, 0);
}
function rpOrderAvailableCasesByLot(skuId, locId, lot, options = {}) {
  const selectedLot = rpOrderCleanLot(lot);
  if (!selectedLot) return rpOrderAvailableCases(skuId, locId, options);
  const canonicalId = rpCanonicalInventorySkuId(skuId);
  const rows = Array.isArray(RP.INV) ? RP.INV.filter(row => row && String(row.unit || 'case').toLowerCase() !== 'can' && rpCanonicalInventorySkuId(row.sku) === canonicalId && row.loc === locId && rpOrderCleanLot(row.lot) === selectedLot) : [];
  const onHand = rows.reduce((sum, row) => {
    const status = String(row.status || 'Available').toLowerCase();
    if (!['available', 'fronted to rep', 'purchased by rep'].includes(status)) return sum;
    return sum + Math.max(0, rpFiniteNumber(row.qty));
  }, 0);
  return Math.max(0, Math.floor(onHand - rpOrderCommittedCasesByLot(skuId, locId, selectedLot, options)));
}
function rpOrderLotStemForSku(skuId, locId) {
  if (typeof rpSkuLotCode === 'function') {
    const configured = rpSkuLotCode(skuId);
    if (configured) return configured;
  }
  const option = rpOrderLotOptionsForSku(skuId, locId)[0];
  const match = option && option.lot.match(/^(.*?)(\d+)$/);
  if (match) return match[1];
  const canonicalId = rpCanonicalInventorySkuId(skuId);
  if (/^RP-MIX-/i.test(canonicalId)) return 'RP-VP-';
  const code = canonicalId.replace(/^RP-12-/i, '').replace(/^RP-CAN-/i, '');
  if (!code || code === canonicalId) return 'RP-';
  return `RP-${code === 'KL' ? 'LIME' : code}-`;
}
function rpOrderLotFromSuffix(skuId, suffix, locId) {
  const match = String(suffix || '').trim().match(/(\d+)$/);
  if (!match) return '';
  return `${rpOrderLotStemForSku(skuId, locId)}${match[1].padStart(3, '0')}`;
}

function rpOrderBackorderMode(order) {
  const text = String((order && (order.note || order.reason || order.notes)) || '').toLowerCase();
  return text.includes('production/backorder') || text.includes('backorder order');
}

function NewOrderModal({ onClose, presetCust, editOrder, onSaved }) {
  const isEdit = Boolean(editOrder && editOrder.id);
  const invoiceLocked = Boolean(isEdit && typeof rpOrderInvoiceLocked === 'function' && rpOrderInvoiceLocked(editOrder));
  const editable = !isEdit || (typeof rpOrderEditableBySales === 'function'
    ? rpOrderEditableBySales(editOrder)
    : !invoiceLocked && ['Submitted', 'Approved', 'Allocated', 'Needs Approval', 'On Hold', 'Requested'].includes(editOrder.status));
  const [quickSaleMode, setQuickSaleMode] = useState(false);
  const quickSale = !isEdit && quickSaleMode;
  const initialOrderCustomer = isEdit ? RP.cust(editOrder.cust) : (presetCust ? RP.cust(presetCust) : null);
  const initialDeliveryMethod = isEdit
    ? (editOrder.delivery || 'Ranch Delivery')
    : (typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(initialOrderCustomer) : 'Ranch Delivery');
  const [custId, setCustId] = useState(isEdit ? editOrder.cust : (presetCust || ''));
  const [customerLocationId, setCustomerLocationId] = useState(isEdit ? (editOrder.customerLocationId || '') : '');
  const [qty, setQty] = useState(() => isEdit ? qtyFromOrder(editOrder) : {});
  const [lots, setLots] = useState(() => isEdit ? rpOrderLineLotMap(editOrder) : {});
  const [lotSuffix, setLotSuffix] = useState('');
  const [backorderMode, setBackorderMode] = useState(() => isEdit ? rpOrderBackorderMode(editOrder) : false);
  const [source, setSource] = useState(isEdit ? (editOrder.source || 'WH-SQ') : rpDefaultOrderSourceForCustomer(presetCust ? RP.cust(presetCust) : null, 'WH-SQ', { deliveryMethod: initialDeliveryMethod }));
  const [method, setMethod] = useState(initialDeliveryMethod);
  const [methodTouched, setMethodTouched] = useState(Boolean(isEdit));
  const [deliveryTiming, setDeliveryTiming] = useState(isEdit && rpOrderIsAsapDelivery(editOrder) ? 'asap' : 'date');
  const [date, setDate] = useState(isEdit ? rpOrderDateInputValue(editOrder, defaultDeliveryDate()) : defaultDeliveryDate());
  const [quickSaleLabel, setQuickSaleLabel] = useState('');
  const [quickSalePaymentMethod, setQuickSalePaymentMethod] = useState('cash');
  const [quickSalePaymentReference, setQuickSalePaymentReference] = useState('');
  const [po, setPo] = useState(isEdit ? (editOrder.po || '') : '');
  const [note, setNote] = useState(isEdit ? (editOrder.note || (editOrder.reason === 'Live order' || editOrder.reason === 'Ops-entered' ? '' : editOrder.reason || '')) : 'Ops quick order');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitOrderId] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `SO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 20).toUpperCase()}`
    : `SO-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`);
  const [updateRequestId] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `ORDER-UPDATE-${window.crypto.randomUUID()}`
    : `ORDER-UPDATE-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
  const [expectedUpdatedAt] = useState(() => {
    if (!isEdit) return '';
    const raw = String(editOrder.updated || editOrder.updatedAt || editOrder.updated_at || '').trim();
    const timestamp = Date.parse(raw);
    return Number.isFinite(timestamp) && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/.test(raw)
      ? raw
      : '';
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const liveCustomerDirectory = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const customerPage = useRpServerPagedCustomers({
    limit: 50,
    search: customerSearch,
    filters: { focus: 'active', sort: 'name' }
  });
  const editSkuIds = isEdit ? (editOrder.lines || []).map(line => line.sku) : [];
  const skus = rpManualOrderSkus({ includeIds: editSkuIds });
  const locationOptions = rpOrderSourceLocations();
  const selectedCustomer = custId ? RP.cust(custId) : null;
  const customerDirectoryRows = liveCustomerDirectory ? customerPage.items : rpVisibleCustomers(RP.CUSTOMERS);
  const customerOptions = Array.from(new Map(
    [selectedCustomer, ...customerDirectoryRows]
      .filter(Boolean)
      .map(customer => [customer.id, customer])
  ).values()).filter(customer => rpCustomerOrderReady(customer) || customer.id === custId);
  const lines = Object.entries(qty).filter(([k, v]) => v > 0);
  const cases = lines.reduce((s, [k, v]) => s + v, 0);
  const sourceLocation = RP.loc(source) || { id: source, name: source || 'No source selected' };
  const quickSaleCustomer = { id: RP_QUICK_SALE_CUSTOMER_ID, name: 'Quick cash sale', type: 'Consumer', tier: 'Consumer', territory: sourceLocation.territory || rpFieldTerritory(), taxRegion: 'BC' };
  const c = quickSale ? quickSaleCustomer : custId ? RP.cust(custId) : null;
  const customerOrderReady = quickSale || (c ? rpCustomerOrderReady(c) : false);
  const total = rpOrderTotals({ customer: quickSale ? quickSaleCustomer : undefined, cust: quickSale ? '' : custId, lines: lines.map(([sku, qty]) => ({ sku, qty })) }).total;
  const customerLocations = quickSale ? [] : rpCustomerLocations(c);
  const selectedCustomerLocationId = customerLocationId || (customerLocations[0] || {}).id || '';
  const selectedCustomerLocation = customerLocations.find(location => location.id === selectedCustomerLocationId) || customerLocations[0] || null;
  const selectedCustomerAddress = String((selectedCustomerLocation && (selectedCustomerLocation.address || selectedCustomerLocation.address1)) || (c && c.address) || '').trim();
  const selectedLotForSku = skuId => rpOrderCleanLot(lots[rpCanonicalSkuId(skuId)] || lots[skuId]);
  const setSkuLot = (skuId, lot) => setLots(current => ({ ...current, [rpCanonicalSkuId(skuId)]: rpOrderCleanLot(lot) }));
  const applyLotSuffix = () => {
    if (!lines.length || !String(lotSuffix || '').trim()) return;
    setLots(current => {
      const next = { ...current };
      lines.forEach(([sku]) => {
        const lot = rpOrderLotFromSuffix(sku, lotSuffix, source);
        if (lot) next[rpCanonicalSkuId(sku)] = lot;
      });
      return next;
    });
  };
  const deliveryNeedsCustomerAddress = Boolean(!quickSale && c && (typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(method) : method !== 'Customer Pickup'));
  const customerAddressIssueText = deliveryNeedsCustomerAddress && !selectedCustomerAddress
    ? (typeof rpCustomerDeliveryWarning === 'function' ? rpCustomerDeliveryWarning(c, method, selectedCustomerLocation) : `${c.name} needs a business or ship-to address before ${method} orders can be submitted. Open the customer and add the address first.`)
    : '';
  const sourceFulfillmentOptions = { deliveryMethod: method };
  const sourceTerritoryBlocked = Boolean(!quickSale && !backorderMode && c && !rpLocationCanFulfillCustomer(sourceLocation, c, sourceFulfillmentOptions));
  const sourceOptions = locationOptions.slice().sort((a, b) => {
    const am = quickSale || backorderMode || (c && rpLocationCanFulfillCustomer(a, c, sourceFulfillmentOptions)) ? 0 : 1;
    const bm = quickSale || backorderMode || (c && rpLocationCanFulfillCustomer(b, c, sourceFulfillmentOptions)) ? 0 : 1;
    const as = rpLocationSellableCases(a.id) > 0 ? 0 : 1;
    const bs = rpLocationSellableCases(b.id) > 0 ? 0 : 1;
    const at = a.type === 'Main Warehouse' ? 0 : 1;
    const bt = b.type === 'Main Warehouse' ? 0 : 1;
    return am - bm || as - bs || at - bt || String(a.name || a.id).localeCompare(String(b.name || b.id));
  });
  const stockIssues = lines.map(([sku, q]) => {
    const lot = selectedLotForSku(sku);
    const available = rpOrderAvailableCasesByLot(sku, source, lot, { excludeOrderId: isEdit ? editOrder.id : null });
    return available != null && q > available ? { sku, lot, qty: q, available } : null;
  }).filter(Boolean);
  const stockIssueText = sourceTerritoryBlocked
    ? `${sourceLocation.name || source} is not assigned to ${c.territory}. Choose a source in this customer's territory.`
    : stockIssues.length
      ? `${rpSkuDisplayName(rpSkuItem(stockIssues[0].sku) || stockIssues[0].sku)}${stockIssues[0].lot ? ` lot ${stockIssues[0].lot}` : ''} has only ${stockIssues[0].available} cases available from ${sourceLocation.name || source}.`
      : '';
  const sourceTerritoryWarningText = Boolean(!quickSale && backorderMode && c && !rpLocationCanFulfillCustomer(sourceLocation, c, sourceFulfillmentOptions))
    ? `${sourceLocation.name || source} is outside ${c.territory}; backorder mode will allow this source.`
    : '';
  const blockingStockIssueText = backorderMode ? '' : stockIssueText;
  const requestedDelivery = deliveryTiming === 'asap' ? 'ASAP' : date;
  const setSkuQty = (skuId, value) => {
    const parsed = Number(value);
    const next = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
    setQty(current => ({ ...current, [skuId]: next }));
  };
  useEffect(() => {
    if (quickSale) {
      setCustomerLocationId('');
      return;
    }
    const locations = rpCustomerLocations(custId ? RP.cust(custId) : null);
    if (!locations.some(location => location.id === customerLocationId)) {
      setCustomerLocationId((locations[0] || {}).id || '');
    }
    const customer = custId ? RP.cust(custId) : null;
    const currentSource = RP.loc(source);
    const currentSourceCases = currentSource ? rpLocationSellableCases(currentSource.id) : 0;
    if (!isEdit && !backorderMode && customer && currentSource && (!rpLocationCanFulfillCustomer(currentSource, customer, { deliveryMethod: method }) || currentSourceCases <= 0)) {
      setSource(rpDefaultOrderSourceForCustomer(customer, source, { deliveryMethod: method }));
    }
  }, [custId, quickSale, backorderMode, method]);
  useEffect(() => {
    if (quickSale || isEdit || methodTouched || !c) return;
    const next = typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(c) : 'Ranch Delivery';
    if (next && next !== method) setMethod(next);
  }, [custId, quickSale, methodTouched]);
  const save = async () => {
    setTried(true);
    setError('');
    const quickSaleDate = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: sourceLocation.territory || rpFieldTerritory() }) : defaultDeliveryDate();
    const finalRequestedDelivery = quickSale ? quickSaleDate : requestedDelivery;
    if ((!quickSale && !custId) || cases === 0 || !finalRequestedDelivery || busy || !editable) return;
    if (isEdit && !expectedUpdatedAt) {
      setError('This order is missing its server revision. Refresh the order before saving changes.');
      return;
    }
    if (!customerOrderReady) {
      setError(`Customer must be approved before orders can be ${isEdit ? 'updated' : 'created'}.`);
      return;
    }
    if (blockingStockIssueText) {
      setError(stockIssueText);
      return;
    }
    if (customerAddressIssueText) {
      setError(customerAddressIssueText);
      return;
    }
    setBusy(true);
    let id = isEdit ? editOrder.id : submitOrderId;
    let external = null;
    let response = null;
    let status = isEdit ? editOrder.status : 'Submitted';
    let shopifyDraftWarning = '';
    const orderLines = lines.map(([sku, q]) => ({ sku, qty: q, lot: selectedLotForSku(sku) || '' }));
    const lineItems = lines.map(([sku, q]) => {
      const item = rpSkuItem(sku);
      return {
        lot: selectedLotForSku(sku) || undefined,
        sku,
        title: item ? item.name : sku,
        quantity: q,
        originalUnitPrice: rpCustomerSkuPrice(c, item).toFixed(2),
        deposit: item && item.deposit || 0
      };
    });
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        response = await rpApi(isEdit ? '/api/orders/update' : '/api/orders/submit', {
          method: 'POST',
          body: JSON.stringify({
             id: isEdit ? editOrder.id : submitOrderId,
             expectedUpdatedAt: isEdit ? expectedUpdatedAt : undefined,
             requestId: isEdit ? updateRequestId : undefined,
            quickSale,
            orderType: quickSale ? 'quick_sale' : undefined,
            customerId: quickSale ? undefined : custId,
            customerLocationId: quickSale ? undefined : selectedCustomerLocationId || undefined,
            deliveryDate: finalRequestedDelivery,
            deliveryMethod: quickSale ? 'Customer Pickup' : method,
            eventName: quickSaleLabel.trim() || undefined,
            lineItems,
            note: quickSale ? (note.trim() === 'Ops quick order' ? null : note.trim() || null) : note.trim() || null,
            allowBackorder: !quickSale && backorderMode,
            paymentMethod: quickSale ? quickSalePaymentMethod : undefined,
            paymentReference: quickSale ? quickSalePaymentReference.trim() || undefined : undefined,
            po: quickSale ? '' : po,
            sourceLocationId: source
          })
         });
        if (isEdit && response && response.code === 'shopify_mutation_in_progress') {
          setError(response.error || 'This exact update is still being confirmed. Wait a moment, then retry without changing the order.');
          setBusy(false);
          return;
        }
        id = response.orderId || id;
        external = response.draftOrder || response;
        status = isEdit
          ? ((response.order && response.order.status) || editOrder.status || status)
          : ((response.order && response.order.status) || (response.mode === 'shopify-draft-order' ? 'Submitted' : 'Requested'));
        shopifyDraftWarning = response.shopifyDraftWarning || '';
      }
      const fallbackNote = quickSale ? (quickSaleLabel.trim() || 'Quick cash sale') : [backorderMode ? 'Production/backorder order' : '', note.trim()].filter(Boolean).join(' - ');
      const fallbackOrder = { ...(editOrder || {}), id, cust: quickSale ? RP_QUICK_SALE_CUSTOMER_ID : custId, customerLocationId: quickSale ? null : selectedCustomerLocationId || null, quickSale, territory: quickSale ? (sourceLocation.territory || rpFieldTerritory()) : c.territory, rep: quickSale ? rpFieldUserName() : c.rep, source, delivery: quickSale ? 'Customer Pickup' : method, status, date: finalRequestedDelivery, deliveryPreference: quickSale ? 'Scheduled' : deliveryTiming === 'asap' ? 'ASAP' : 'Scheduled', created: editOrder ? editOrder.created : (typeof rpNowLocalInput === 'function' ? rpNowLocalInput(c) : new Date().toISOString().slice(0, 16).replace('T', ' ')), updated: new Date().toISOString(), reason: fallbackNote || (isEdit ? editOrder.reason : 'Ops-entered'), po: quickSale ? '' : po, note: fallbackNote, external, financialStatus: quickSale ? 'Paid' : editOrder && editOrder.financialStatus, invoicePaymentMethod: quickSale ? quickSalePaymentMethod : editOrder && editOrder.invoicePaymentMethod, invoiceStatus: quickSale ? 'Paid' : editOrder && editOrder.invoiceStatus, lines: orderLines };
      const savedOrder = rpUpsertOrder(rpOrderFromApi(response, fallbackOrder));
      await rpRefreshAfterMutation(shopifyDraftWarning ? `Order ${savedOrder.id} updated - Shopify draft sync warning` : `Order ${savedOrder.id} ${isEdit ? 'updated' : 'created'}`);
      const syncedOrder = RP.ORDERS.find(x => x.id === savedOrder.id) || savedOrder;
      id = syncedOrder.id;
      if (onSaved) onSaved(syncedOrder);
    } catch (err) {
      setError(err && err.message ? err.message : `Could not ${isEdit ? 'update' : 'create'} order.`);
      setBusy(false);
      return;
    }
    toast(shopifyDraftWarning || `Order ${id} ${isEdit ? 'updated' : 'created'} for ${c.name} - ${cases} cases`, shopifyDraftWarning ? 'warn' : undefined);
    setBusy(false);
    onClose();
  };
  return (
    <Modal title={isEdit ? `Edit ${editOrder.id}` : 'New Order'} sub={isEdit ? 'Update order details and Shopify draft when linked' : 'Quick order builder'} onClose={onClose} width={640}
      footer={<><div style={{ flex: 1, alignSelf: 'center' }}>{cases > 0 && <span style={{ fontSize: 13, color: 'var(--muted)' }}><b className="disp" style={{ fontSize: 18, color: 'var(--ink)' }}>{RP.money(total)}</b> - {cases} cs{backorderMode && <span style={{ color: 'var(--amber-deep)', fontWeight: 700 }}> - backorder</span>}</span>}</div><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !editable || !customerOrderReady || !!blockingStockIssueText || !!customerAddressIssueText} onClick={save} style={{ opacity: (quickSale || custId) && cases && requestedDelivery && editable && customerOrderReady && !busy && !blockingStockIssueText && !customerAddressIssueText ? 1 : 0.5 }}><Icon name="check" size={15} />{busy ? 'Saving...' : quickSale ? 'Record Quick Sale' : isEdit ? 'Save Changes' : 'Create Order'}</button></>}>
      {!editable && <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}><Icon name="alert" size={15} />{invoiceLocked ? `Invoice ${editOrder.invoiceNumber || editOrder.invoice_number || 'history'} locks this order's items and pricing. Void or reconcile the invoice before editing.` : `This order is ${String(editOrder.status || 'locked').toLowerCase()} and can no longer be edited.`}</div>}
      {!isEdit && <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button type="button" onClick={() => setQuickSaleMode(false)} style={{ flex: 1, border: '1px solid ' + (!quickSale ? 'var(--amber)' : 'var(--line)'), background: !quickSale ? 'var(--amber-soft)' : 'var(--surface)', color: !quickSale ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 800 }}>Customer order</button>
        <button type="button" onClick={() => setQuickSaleMode(true)} style={{ flex: 1, border: '1px solid ' + (quickSale ? 'var(--amber)' : 'var(--line)'), background: quickSale ? 'var(--amber-soft)' : 'var(--surface)', color: quickSale ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 800 }}>Quick cash sale</button>
      </div>}
      {!quickSale && <MField label="Customer" required>
        {liveCustomerDirectory && <div style={{ position: 'relative', marginBottom: 8 }}>
          <Icon name="search" size={15} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--faint)' }} />
          <input data-order-customer-server-search="desktop" className="field" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Search the complete customer directory..." style={{ paddingLeft: 34 }} />
        </div>}
        <select className="field" value={custId} disabled={liveCustomerDirectory && customerPage.initialLoading && !selectedCustomer} onChange={e => setCustId(e.target.value)} style={{ borderColor: tried && !custId ? 'var(--danger)' : undefined }}>
          <option value="">{liveCustomerDirectory && customerPage.initialLoading ? 'Loading customers...' : 'Select a customer...'}</option>
          {customerOptions.map(c => <option key={c.id} value={c.id}>{c.name} - {c.territory}</option>)}
        </select>
        {liveCustomerDirectory && customerPage.initialLoading && <div data-order-customer-loading="desktop" style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 7 }}>Searching the complete scoped customer directory...</div>}
        {liveCustomerDirectory && !customerPage.initialLoading && !customerPage.loaded && customerPage.error && <div data-order-customer-error="desktop" style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 700, marginTop: 7 }}>{customerPage.error} <button type="button" className="btn btn-sm" onClick={customerPage.retry} style={{ marginLeft: 6 }}>Try Again</button></div>}
        {liveCustomerDirectory && customerPage.loaded && customerOptions.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 7 }}>No order-ready customers match this search.</div>}
        {liveCustomerDirectory && customerPage.loaded && <div data-order-customer-pagination="desktop" style={{ marginTop: 8, border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}><RpServerLoadMore {...customerPage} label="customers" onLoadMore={customerPage.loadMore} onRetry={customerPage.loadMore} /></div>}
      </MField>}
      {quickSale && <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 160px', gap: 14 }}>
        <MField label="Sale Label">
          <input className="field" value={quickSaleLabel} onChange={e => setQuickSaleLabel(e.target.value)} placeholder="Event name or short note" />
        </MField>
        <MField label="Payment">
          <select className="field" value={quickSalePaymentMethod} onChange={e => setQuickSalePaymentMethod(e.target.value)}>
            <option value="cash">Cash</option>
            <option value="e_transfer">e-Transfer</option>
            <option value="bank_transfer">Bank transfer</option>
            <option value="cheque">Cheque</option>
          </select>
        </MField>
      </div>}
      {quickSale && quickSalePaymentMethod !== 'cash' && <MField label="Payment Reference">
        <input className="field" value={quickSalePaymentReference} onChange={e => setQuickSalePaymentReference(e.target.value)} placeholder="Optional reference, authorization, or memo" />
      </MField>}
      {!quickSale && c && !customerOrderReady && <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}><Icon name="flag" size={15} />Approve this customer before creating or updating orders.</div>}
      {!quickSale && c && customerLocations.length > 0 && <MField label="Ship-to Location" required>
        <select className="field" value={selectedCustomerLocationId} onChange={e => setCustomerLocationId(e.target.value)}>
          {customerLocations.map(location => <option key={location.id} value={location.id}>{location.name || 'Location'}{location.city ? ` - ${location.city}` : ''}</option>)}
        </select>
      </MField>}
      {!quickSale && c && customerAddressIssueText && <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}><Icon name="alert" size={15} />{customerAddressIssueText}</div>}
      {!quickSale && c && !customerAddressIssueText && deliveryNeedsCustomerAddress && selectedCustomerAddress && <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: -8, marginBottom: 14 }}>Ship-to: {selectedCustomerAddress}</div>}
      {!quickSale && c && rpCustomerOnCreditHold(c) && rpActiveApprovalRule('CREDIT_HOLD_CUSTOMER') && <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}><Icon name="alert" size={15} />On credit hold - order will route for override.</div>}
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Source" half={!quickSale}>
          <select className="field" value={source} onChange={e => setSource(e.target.value)}>
            {sourceOptions.map(loc => <option key={loc.id} value={loc.id}>{loc.name}{loc.territory ? ` - ${loc.territory}` : ''}</option>)}
          </select>
        </MField>
        {!quickSale && <MField label="Delivery Method" half>
          <select className="field" value={method} onChange={e => { setMethod(e.target.value); setMethodTouched(true); }}>
            {['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight'].map(x => <option key={x}>{x}</option>)}
          </select>
        </MField>}
      </div>
      {!quickSale && <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, border: '1px solid ' + (backorderMode ? 'var(--amber)' : 'var(--line)'), background: backorderMode ? 'var(--amber-soft)' : 'var(--surface)', borderRadius: 10, padding: '11px 12px', marginBottom: 14 }}>
        <input type="checkbox" checked={backorderMode} onChange={e => setBackorderMode(e.target.checked)} style={{ marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, color: backorderMode ? 'var(--amber-deep)' : 'var(--ink)', fontSize: 13.5 }}>Production / backorder order</div>
          <div style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.35, marginTop: 2 }}>Allow planned production quantities above current stock. Inventory will still be required before delivery is posted.</div>
        </div>
      </div>}
      {!quickSale && <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Requested Delivery" required half>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: deliveryTiming === 'date' ? 8 : 0 }}>
            {[{ value: 'asap', label: 'ASAP' }, { value: 'date', label: 'Pick date' }].map(option => (
              <button key={option.value} type="button" onClick={() => setDeliveryTiming(option.value)} style={{ border: '1px solid ' + (deliveryTiming === option.value ? 'var(--amber)' : 'var(--line)'), background: deliveryTiming === option.value ? 'var(--amber-soft)' : 'var(--surface)', color: deliveryTiming === option.value ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 8, padding: '9px 10px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.2 }}>
                {option.label}
              </button>
            ))}
          </div>
          {deliveryTiming === 'date' && <input className="field" type="date" value={date || ''} onChange={e => setDate(e.target.value)} style={{ borderColor: tried && !date ? 'var(--danger)' : undefined }} />}
          {deliveryTiming === 'asap' && <div style={{ color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.35, marginTop: 7 }}>Timing confirmed by ops or the assigned rep.</div>}
        </MField>
        <MField label={c && c.requiresPo ? 'PO # (required before invoicing)' : 'PO #'} half>
          <input className="field" value={po} onChange={e => setPo(e.target.value)} placeholder={c && c.requiresPo ? 'Purchase order number' : 'Optional'} />
        </MField>
      </div>}
      <label className="field-label">Products</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end', marginBottom: 8 }}>
        <div>
          <label className="field-label" style={{ marginBottom: 5 }}>Lot suffix</label>
          <input className="field mono" value={lotSuffix} onChange={e => setLotSuffix(e.target.value)} placeholder="002" />
        </div>
        <button className="btn" type="button" disabled={!lines.length} onClick={applyLotSuffix} style={{ height: 40, opacity: lines.length ? 1 : 0.5 }}>Apply to selected</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {skus.map(s => {
          const q = qty[s.id] || 0;
          const selectedLot = selectedLotForSku(s.id);
          const lotOptions = rpOrderLotOptionsForSku(s.id, source);
          const lotDatalistId = `lot-options-${String(s.id).replace(/[^a-z0-9]+/gi, '-')}`;
          const available = rpOrderAvailableCasesByLot(s.id, source, selectedLot, { excludeOrderId: isEdit ? editOrder.id : null });
          const empty = available != null && available <= 0;
          const low = available != null && available > 0 && available < 15;
          const plusBlocked = !backorderMode && available != null && q >= available;
          return (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '9px 11px', border: '1px solid ' + (q ? 'var(--amber)' : 'var(--line)'), borderRadius: 9 }}>
              <FlavorChip sku={s} size={26} />
              <div style={{ flex: '1 1 210px', minWidth: 0 }}><div style={{ fontWeight: 500, fontSize: 13.5 }}>{rpSkuDisplayName(s)}</div><div style={{ fontSize: 11.5, color: empty || low ? 'var(--danger)' : 'var(--faint)', fontWeight: empty || low ? 700 : 400 }}>{s.pack} - {available == null ? 'Availability pending' : `${available} available`} - {RP.money2(c ? rpCustomerSkuPrice(c, s) : rpDefaultCasePrice())}/case</div></div>
              {q > 0 && <div style={{ width: 160 }}>
                <input className="field mono" list={lotDatalistId} value={selectedLot} onChange={e => setSkuLot(s.id, e.target.value)} placeholder={rpOrderLotFromSuffix(s.id, '001', source) || 'Lot pending'} style={{ height: 35, fontSize: 12, padding: '7px 9px' }} />
                <datalist id={lotDatalistId}>
                  {lotOptions.map(option => <option key={option.lot} value={option.lot}>{`${option.lot} - ${option.qty} cs`}</option>)}
                </datalist>
              </div>}
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                <button className="btn btn-ghost" style={{ padding: '6px 9px', borderRadius: 0 }} onClick={() => setQty(p => ({ ...p, [s.id]: Math.max(0, q - 1) }))}><Icon name="minus" size={14} /></button>
                <input className="mono tnum" type="number" min="0" step="1" value={q || ''} onChange={e => setSkuQty(s.id, e.target.value)} placeholder="0" style={{ width: 54, border: 0, borderLeft: '1px solid var(--line)', borderRight: '1px solid var(--line)', background: 'var(--surface)', textAlign: 'center', fontWeight: 700, fontSize: 14, padding: '7px 4px', outline: 'none' }} />
                <button className="btn btn-ghost" disabled={plusBlocked} style={{ padding: '6px 9px', borderRadius: 0, color: 'var(--amber-deep)', opacity: plusBlocked ? 0.45 : 1 }} onClick={() => setQty(p => ({ ...p, [s.id]: backorderMode || available == null ? q + 1 : Math.min(available, q + 1) }))}><Icon name="plus" size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
      {cases > 0 && <PricingBreakdown order={{ customer: quickSale ? quickSaleCustomer : undefined, cust: quickSale ? '' : custId, lines: lines.map(([sku, qty]) => ({ sku, qty })) }} estimated showCardOption={!quickSale} />}
      {tried && cases === 0 && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 8 }}>Add at least one case.</div>}
      {sourceTerritoryWarningText && <div style={{ marginTop: 10, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{sourceTerritoryWarningText}</div>}
      {stockIssueText && <div style={{ marginTop: 10, background: backorderMode ? 'var(--warn-bg)' : 'var(--danger-bg)', color: backorderMode ? 'var(--amber-deep)' : 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{backorderMode ? `Backorder enabled: ${stockIssueText}` : stockIssueText}</div>}
      <MField label="Notes">
        <textarea className="field" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Optional order notes" style={{ resize: 'vertical', marginTop: 12 }} />
      </MField>
      {error && <div style={{ marginTop: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

/* ---- Customer Approval (ops view on customer detail) ---- */
function CustomerApprovalCard({ c, force, openEdit }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const ready = rpCustomerOrderReady(c);
  const quoteProspect = typeof rpCustomerQuoteProspect === 'function' && rpCustomerQuoteProspect(c);
  const intakePending = Boolean(quoteProspect && !c.onboardingSubmittedAt);
  const approvalRole = rpNormalizeRole(rpAuthProfile() && rpAuthProfile().role);
  const managementApprovalOverride = ['admin', 'leadership', 'ops', 'sales_manager'].includes(approvalRole);
  const intakeBlocksApproval = Boolean(intakePending && !managementApprovalOverride);
  const archivedCustomer = Boolean(c.archivedAt || c.archived_at || String(c.status || '').trim().toLowerCase() === 'inactive');
  const canApprove = rpCanApproveCustomers(null, c);
  const approve = async () => {
    if (busy || ready) return;
    if (archivedCustomer) { toast('This archived account is unavailable for approval. Restore its saved quote first, or use the active customer account.', 'warn'); return; }
    if (!canApprove) { toast('Your profile is not enabled to approve this customer.', 'warn'); return; }
    if (intakeBlocksApproval) { toast('Send the intake link below and approve after the retailer submits the form.', 'warn'); return; }
    setBusy(true);
    setError('');
    setWarning('');
    try {
      const response = await rpApi('/api/customers/update', {
        method: 'POST',
        body: JSON.stringify({
          creditStatus: c.credit === 'Watch' || c.credit === 'Hold' ? c.credit : 'Good',
          id: c.id,
          status: 'Approved'
        })
      });
      const customer = RP.upsertCustomer({ ...c, ...response.customer, status: 'Approved', risk: 'Low', credit: response.customer && response.customer.credit ? response.customer.credit : 'Good', approvalTaskCloseoutWarning: response.approvalTaskCloseoutWarning || '', closedApprovalTaskIds: Array.isArray(response.closedApprovalTaskIds) ? response.closedApprovalTaskIds : [], skipCreds: true, persisted: true });
      await rpRefreshAfterMutation(`${customer.name} approved`);
      const partialWarning = response.approvalTaskCloseoutWarning || response.portalInviteTaskError || '';
      if (partialWarning) setWarning(partialWarning);
      toast(response.portalInviteTaskId ? `${customer.name} approved - portal invite task queued` : `${customer.name} approved for ordering`, partialWarning ? 'warn' : 'ok');
      force && force();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not approve customer.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={<Pill status={archivedCustomer ? 'Inactive' : ready ? 'Active' : 'Pending'}>{archivedCustomer ? 'Archived' : ready ? 'Order-ready' : 'Needs approval'}</Pill>}>Customer Approval</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
        <Row k="Current status" v={<Pill status={c.status} />} />
        <Row k="Credit" v={<Pill status={c.credit || 'New'}>{c.credit || 'New'}</Pill>} />
        <Row k="Terms" v={c.terms || 'Prepaid'} />
      </div>
      {!ready && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-sm" disabled={archivedCustomer} style={{ flex: 1, opacity: archivedCustomer ? 0.55 : 1 }} onClick={archivedCustomer ? undefined : openEdit}><Icon name="pen" size={14} />Review Details</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !canApprove || intakeBlocksApproval || archivedCustomer} style={{ flex: 1, opacity: busy || !canApprove || intakeBlocksApproval || archivedCustomer ? 0.55 : 1 }} onClick={approve}><Icon name="check" size={14} />{busy ? 'Approving...' : archivedCustomer ? 'Archived' : intakeBlocksApproval ? 'Waiting for Intake' : 'Approve'}</button>
        </div>
      )}
      {ready && <div style={{ fontSize: 11.5, color: 'var(--ok)', marginTop: 10, fontWeight: 700 }}>This customer can be used for portal access and customer ordering.</div>}
      {archivedCustomer && <div role="alert" style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 10, fontWeight: 700 }}>This archived account is unavailable for edits or approval. Restore its saved quote first, or use the active customer account.</div>}
      {!archivedCustomer && intakeBlocksApproval && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 10, fontWeight: 700 }}>Send the secure intake link below, or record the paper form. Approval unlocks after the retailer submits it.</div>}
      {!archivedCustomer && intakePending && managementApprovalOverride && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 10, fontWeight: 700 }}>Retailer intake is incomplete. You can approve now and follow up to finish onboarding.</div>}
      {!ready && !archivedCustomer && !canApprove && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 10, fontWeight: 700 }}>Approval requires Operations, Sales Manager, Leadership, or Admin.</div>}
      {warning && <div role="alert" style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 10, fontWeight: 700 }}>{warning}</div>}
      {error && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 10, fontWeight: 700 }}>{error}</div>}
    </div>
  );
}

/* ---- Portal Access & Credentials (ops view on customer detail) ---- */
function PortalAccessCard({ c, force }) {
  const [busy, setBusy] = useState(false);
  const copy = (text, label) => { try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (e) { } toast((label || 'Copied') + ' to clipboard', 'info'); };
  const demo = window.RP_AUTH && window.RP_AUTH.demo;
  const [sentAt, setSentAt] = useState('');
  const portalReady = rpCustomerOrderReady(c);
  const quoteProspect = typeof rpCustomerQuoteProspect === 'function' && rpCustomerQuoteProspect(c);
  const portalInviteReady = typeof rpCustomerPortalInviteReady === 'function' ? rpCustomerPortalInviteReady(c) : portalReady;
  const intakeOnly = Boolean(!portalReady && portalInviteReady && quoteProspect);
  const canSendPortalAccess = typeof rpCanSendCustomerPortalAccess === 'function' ? rpCanSendCustomerPortalAccess(c) : rpCanInviteUsers();
  const buyerEmail = (c.email || '').trim().toLowerCase();
  const invite = async () => {
    if (busy) return;
    if (!canSendPortalAccess) { toast('You can only send portal access for customers assigned to you or in your area.', 'warn'); return; }
    if (!buyerEmail) { toast('Add a buyer email before sending an invite.', 'danger'); return; }
    if (!portalInviteReady) { toast('Approve this customer before sending normal portal access.', 'warn'); return; }
    setBusy(true);
    try {
      await rpApi('/api/users/invite', {
        method: 'POST',
        body: JSON.stringify({
          customerId: c.id,
          email: buyerEmail,
          name: c.buyer && c.buyer !== 'Buyer' ? c.buyer : c.name,
          role: 'customer',
          ...(intakeOnly ? { purpose: 'retailer_intake' } : {})
        })
      });
      setSentAt('Just now');
      toast(`${intakeOnly ? 'Intake link' : 'Invite'} sent to ${buyerEmail}`, 'ok');
      force && force();
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not send buyer invite.', 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (!demo) {
    return (
      <div className="card" style={{ padding: '18px 20px' }}>
        <SectionTitle right={<Pill status={portalReady ? 'Active' : 'Pending'}>{portalReady ? 'Ready' : intakeOnly ? 'Intake access' : 'Not ready'}</Pill>}>Portal Access</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
          <Row k="Buyer email" v={buyerEmail ? <span className="mono" style={{ fontSize: 12.5 }}>{buyerEmail}</span> : 'Missing'} />
          <Row k="Customer status" v={<Pill status={c.status} />} />
          <Row k="Order portal" v={<span className="mono" style={{ fontSize: 12 }}>order.ranchpower.com</span>} />
          <Row k="Portal sender" v={canSendPortalAccess ? 'Allowed' : 'Not allowed'} />
          {intakeOnly && <Row k="Access" v="Intake only - ordering locked" />}
          <Row k="Setup step" v={c.onboardingSubmittedAt ? 'Questionnaire complete' : 'Questionnaire required'} />
          {sentAt && <Row k="Last invite" v={sentAt} />}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => copy('https://order.ranchpower.com', 'Order portal')}><Icon name="doc" size={14} />Copy Portal</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !buyerEmail || !portalInviteReady || !canSendPortalAccess} style={{ flex: 1, opacity: busy || !buyerEmail || !portalInviteReady || !canSendPortalAccess ? 0.55 : 1 }} onClick={invite}><Icon name="mail" size={14} />{busy ? 'Sending...' : intakeOnly ? 'Send Intake Link' : 'Send Invite'}</button>
        </div>
        {!buyerEmail && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 10, fontWeight: 700 }}>Add the buyer email in Edit Customer before inviting.</div>}
        {buyerEmail && intakeOnly && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 10, fontWeight: 700 }}>The retailer can sign in to complete intake. Ordering and the full portal stay locked until approval.</div>}
        {buyerEmail && !portalInviteReady && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 10, fontWeight: 700 }}>{c.onboardingSubmittedAt ? 'Retailer intake is complete. Approve the customer to unlock portal access.' : 'Customer must be Active or Approved before normal portal access is sent.'}</div>}
        {buyerEmail && portalReady && !canSendPortalAccess && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 10, fontWeight: 700 }}>Portal access is limited to customer owners, covered territories, and management.</div>}
      </div>
    );
  }

  if (c.status === 'Prospect' && !c.creds) c.creds = RP.genCreds(c, true);
  const creds = c.creds;
  const reset = () => { RP.resetPassword(c.id); toast(`New password generated & emailed to ${c.name}`); force && force(); };
  const resend = () => { RP.sendCredentials(c.id); toast(`Credentials re-sent to ${c.name}`, 'info'); force && force(); };

  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={creds ? <Pill status={creds.status === 'Active' ? 'Active' : creds.status === 'Disabled' ? 'Disabled' : 'Invited'}>{creds.status}</Pill> : null}>Portal Access</SectionTitle>
      {!creds ? (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>No portal login yet.<button className="btn btn-sm btn-primary" style={{ marginTop: 10, width: '100%' }} onClick={() => { c.creds = RP.genCreds(c, true); toast('Portal access created & emailed'); force && force(); }}><Icon name="plus" size={14} />Create Login</button></div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
            <Row k="Username" v={<span className="mono" style={{ fontSize: 12.5 }}>{creds.username}</span>} />
            <Row k="Last login" v={creds.lastLogin || 'Never'} />
            <Row k="Last sent" v={creds.lastSent} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '9px 11px', background: 'var(--surface-2)', borderRadius: 8 }}>
            <Icon name="layers" size={14} style={{ color: 'var(--muted)', flexShrink: 0 }} />
            <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-soft)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{creds.link}</span>
            <button className="btn btn-ghost btn-sm" style={{ padding: '4px 7px' }} onClick={() => copy('https://' + creds.link, 'Quick-access link')}><Icon name="doc" size={14} /></button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-sm" style={{ flex: 1 }} onClick={resend}><Icon name="mail" size={14} />Resend</button>
            <button className="btn btn-sm btn-primary" style={{ flex: 1 }} onClick={reset}><Icon name="refresh" size={14} />Reset PW</button>
          </div>
        </>
      )}
    </div>
  );
}

function removeEntityFromRpData(entityType, entityId, response) {
  const type = String(entityType || '').toLowerCase().replace(/[\s-]+/g, '_');
  const id = String(entityId || '');
  if (!id || !window.RP) return;
  const record = response && response.record && typeof response.record === 'object' ? response.record : null;

  const removeFrom = (key, predicate) => {
    const rows = window.RP[key];
    if (!Array.isArray(rows)) return;
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (predicate(rows[i])) rows.splice(i, 1);
    }
  };

  if (type === 'invoice') {
    const recordOrderId = String(record && record.id || '').trim();
    const draftOrderId = id.replace(/^DRAFT-/i, '');
    (window.RP.ORDERS || []).forEach(order => {
      const matches = String(order && order.invoiceNumber || '') === id
        || (draftOrderId && String(order && order.id || '') === draftOrderId)
        || (recordOrderId && String(order && order.id || '') === recordOrderId);
      if (!matches) return;
      const priorCardFee = Number(order.creditCardFeeTotal || 0);
      order.creditCardFeeTotal = 0;
      order.financialStatus = 'Not invoiced';
      order.invoiceDueDate = null;
      order.invoiceEmailTo = null;
      order.invoiceFirstOpenedAt = null;
      order.invoiceIssuedAt = null;
      order.invoiceLastOpenedAt = null;
      order.invoiceLastSentAt = null;
      order.invoiceNote = '';
      order.invoiceNumber = null;
      order.invoiceOpenCount = 0;
      order.invoicePaymentMethod = null;
      order.invoiceStatus = 'Not invoiced';
      order.invoiceUrl = null;
      order.shopifyDraftOrderId = null;
      if (record) {
        if (record.status) order.status = record.status;
        if (record.total != null) order.total = Number(record.total || 0);
        if (record.fee_total != null || record.feeTotal != null) order.feeTotal = Number(record.fee_total ?? record.feeTotal ?? 0);
        if (record.notes != null) {
          order.reason = record.notes || 'Live order';
          order.note = record.notes || '';
        }
      } else {
        if (priorCardFee && Number.isFinite(Number(order.total))) order.total = Math.max(0, Number(order.total || 0) - priorCardFee);
        if (priorCardFee && Number.isFinite(Number(order.feeTotal))) order.feeTotal = Math.max(0, Number(order.feeTotal || 0) - priorCardFee);
      }
    });
    return;
  }

  if (type === 'order') {
    removeFrom('ORDERS', row => String(row && row.id) === id);
    removeFrom('DISPATCH', row => String(row && (row.id || row.orderId)) === id);
    removeFrom('DISPATCH_MANIFESTS', row => String(row && row.orderId) === id);
    return;
  }

  if (type === 'customer' || type === 'account') {
    const deletedOrderIds = new Set((window.RP.ORDERS || [])
      .filter(row => String(row && (row.cust || row.customerId)) === id)
      .map(row => String(row.id || ''))
      .filter(Boolean));
    removeFrom('CUSTOMERS', row => String(row && row.id) === id);
    removeFrom('ORDERS', row => String(row && (row.cust || row.customerId)) === id);
    removeFrom('VISITS', row => String(row && row.cust) === id);
    removeFrom('DISPATCH', row => deletedOrderIds.has(String(row && (row.id || row.orderId))));
    removeFrom('DISPATCH_MANIFESTS', row => deletedOrderIds.has(String(row && row.orderId)));
  }
}

function RemovalRequestModal({ entityId, entityLabel, entityType, onClose, onRequested, requestedAction = 'remove' }) {
  const [reason, setReason] = useState('');
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const normalizedEntityType = String(entityType || '').toLowerCase().replace(/[\s-]+/g, '_');
  const direct = typeof rpNormalizeRole === 'function' && rpNormalizeRole(rpAuthProfile().role) === 'admin';
  const isOrder = normalizedEntityType === 'order';
  const permanentEligible = direct && ['account', 'customer'].includes(normalizedEntityType);
  const hardDelete = permanentEligible && permanentDelete;
  const finalRequestedAction = hardDelete ? 'permanently_delete' : requestedAction;
  const valid = hardDelete ? reason.trim().length >= 6 : (direct || reason.trim().length >= 6);
  const submit = async () => {
    setError('');
    if (!valid || busy) {
      if (!valid) setError(hardDelete ? 'Add a short reason before permanently deleting this record.' : 'Add a short reason so leadership can review the request.');
      return;
    }
    setBusy(true);
    try {
      let task = null;
      let requestId = `RMV-${Date.now().toString().slice(-7)}`;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi(direct ? '/api/removal/direct' : '/api/removal/request', {
          method: 'POST',
          body: JSON.stringify({
            entityId,
            entityLabel,
            entityType,
            reason: reason.trim(),
            requestedAction: finalRequestedAction
          })
        });
        if (direct) {
          const refreshed = await rpRefreshAfterMutation(`${entityLabel || entityId} removed`);
          if (!refreshed && (response.hardDeleted || response.hidden || normalizedEntityType === 'invoice')) removeEntityFromRpData(entityType, entityId, response);
          toast(response.hardDeleted ? `${entityLabel || entityId} permanently deleted` : `${entityLabel || entityId} removed`, 'warn');
          onRequested && onRequested(response);
          onClose();
          return;
        }
        requestId = response.requestId || requestId;
        task = response.task || null;
        if (task && RP.upsertTask) RP.upsertTask(task);
        await rpRefreshAfterMutation(`Removal request ${requestId} submitted`);
      } else if (direct) {
        const hidden = !hardDelete && ['account', 'customer'].includes(normalizedEntityType);
        if (hardDelete || hidden || normalizedEntityType === 'invoice') removeEntityFromRpData(entityType, entityId, { direct: true, entity: { id: entityId, type: entityType }, hidden });
        toast(hardDelete ? `${entityLabel || entityId} permanently deleted` : `${entityLabel || entityId} removed`, 'warn');
        onRequested && onRequested({ direct: true, hardDeleted: hardDelete, hidden, entity: { id: entityId, type: entityType } });
        onClose();
        return;
      } else if (RP.upsertTask) {
        task = RP.upsertTask({
          id: requestId,
          title: `Review ${finalRequestedAction} request: ${entityLabel || entityId}`,
          owner: 'Leadership / Operations',
          assigner: typeof rpDisplayName === 'function' ? rpDisplayName() : 'Ranch Power',
          status: 'To Do',
          priority: 'High',
          cat: 'Admin',
          due: typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10),
          note: `Entity: ${entityType} ${entityId}\nReason: ${reason.trim()}`
        });
      }
      if (direct && hardDelete) removeEntityFromRpData(entityType, entityId);
      toast(`Removal request ${requestId} submitted`, 'warn');
      onRequested && onRequested(task || { id: requestId });
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not submit removal request.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={direct ? 'Remove Record' : 'Request Removal'} sub={`${entityType}: ${entityLabel || entityId}`} onClose={onClose} width={520}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid} onClick={submit} style={{ opacity: busy || !valid ? 0.55 : 1, background: 'var(--danger)', borderColor: 'var(--danger)' }}><Icon name="alert" size={15} />{busy ? (direct ? (hardDelete ? 'Deleting...' : isOrder ? 'Archiving...' : 'Removing...') : 'Submitting...') : direct ? (hardDelete ? 'Delete Permanently' : isOrder ? 'Archive Order' : 'Remove Now') : 'Submit Request'}</button></>}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: direct ? 'var(--danger-bg)' : 'var(--warn-bg)', color: direct ? 'var(--danger)' : 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, lineHeight: 1.45, marginBottom: 14 }}>
        <Icon name="alert" size={16} style={{ flexShrink: 0, marginTop: 1 }} />{direct ? (hardDelete ? 'Permanent delete is only available when no orders, saved quotes, quote access, payments, or other protected history exist. This cannot be undone.' : isOrder ? 'Order removal is an audited cancellation. Permanent deletion is unavailable so invoice, inventory, and order history stay intact.' : 'Remove Now marks the account Inactive and hides it from active customer lists while preserving its history.') : 'High-risk records are reviewed before removal. A task will be created for leadership and operations.'}
      </div>
      {permanentEligible && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, border: '1px solid var(--line)', borderRadius: 9, padding: '10px 12px', marginBottom: 14, cursor: busy ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={permanentDelete} disabled={busy} onChange={e => setPermanentDelete(e.target.checked)} style={{ marginTop: 2 }} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontWeight: 800, color: 'var(--danger)', fontSize: 13 }}>Permanently delete this {normalizedEntityType === 'order' ? 'order' : 'customer'}</span>
            <span style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.35 }}>Use this only for dependency-free test records or accounts entered in error. Accounts with saved quotes or business history must be hidden with Remove Now. Shopify-synced records can return on the next Shopify import.</span>
          </span>
        </label>
      )}
      <MField label="Reason" required={hardDelete || !direct}>
        <textarea className="field" rows="4" value={reason} onChange={e => setReason(e.target.value)} placeholder="Duplicate, entered in error, inactive account, cleanup request..." style={{ resize: 'none' }} />
      </MField>
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

Object.assign(window, { toast, ToastHost, Modal, MField, AddCustomerModal, EditCustomerModal, rpRoleAwareCustomerUpdatePayload, RecordDeductionModal, NewOrderModal, CustomerApprovalCard, PortalAccessCard, RemovalRequestModal });


/* ===== desktop_marketing.jsx ===== */
/* ============================================================
   RANCH POWER - Desktop : Marketing
   Restock subscribers, subscription cleanup, and email blasts.
   ============================================================ */

const MARKETING_STATUS_LABELS = {
  open: 'Open',
  notified: 'Notified',
  closed: 'Closed',
  unsubscribed: 'Unsubscribed'
};

const MARKETING_STATUS_TONES = {
  open: 'Submitted',
  notified: 'Approved',
  closed: 'Pending',
  unsubscribed: 'Damaged'
};

function marketingClean(value) {
  return String(value || '').trim();
}

function marketingDate(value) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
  } catch {
    return String(value).slice(0, 16);
  }
}

function marketingStatusPill(status) {
  const key = marketingClean(status || 'open').toLowerCase();
  return <Pill status={MARKETING_STATUS_TONES[key] || 'Submitted'}>{MARKETING_STATUS_LABELS[key] || status || 'Open'}</Pill>;
}

function marketingBlastStatusPill(status) {
  const key = marketingClean(status || 'draft').toLowerCase();
  const labels = { draft: 'Draft', failed: 'Failed', partial: 'Partial', sent: 'Sent' };
  const tones = { draft: 'Submitted', failed: 'Damaged', partial: 'Pending', sent: 'Approved' };
  return <Pill status={tones[key] || 'Submitted'}>{labels[key] || status || 'Draft'}</Pill>;
}

function marketingCsv(rows) {
  const headers = ['email', 'product', 'variant', 'handle', 'status', 'created_at', 'notified_at', 'notes', 'page'];
  const escape = value => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;
  return [
    headers.join(','),
    ...rows.map(row => headers.map(key => escape(row[key] || row[key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())])).join(','))
  ].join('\n');
}

function marketingDownloadCsv(rows) {
  const blob = new Blob([marketingCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ranch-power-restock-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function marketingProductLabel(row) {
  return [row.product, row.variant].map(marketingClean).filter(Boolean).join(' / ') || row.handle || 'Ranch Power';
}

function marketingEmailDomain(email) {
  return String(email || '').split('@')[1] || '';
}

function MarketingBlastModal({ audience, canManage, onClose, onSent, rows }) {
  const [subject, setSubject] = useState('Ranch Power restock update');
  const [message, setMessage] = useState('Good news from Ranch Power. The packs you asked about are being restocked, and we wanted to give you the first update.');
  const [ctaUrl, setCtaUrl] = useState('https://www.ranchpower.com/shop/');
  const [ctaLabel, setCtaLabel] = useState('Shop Ranch Power');
  const [markNotified, setMarkNotified] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const activeRows = rows.filter(row => !['closed', 'unsubscribed'].includes(String(row.status || '').toLowerCase()));
  const recipientCount = new Set(activeRows.map(row => String(row.email || '').toLowerCase()).filter(Boolean)).size;
  const disabled = !canManage || busy || !confirmed || recipientCount < 1 || subject.trim().length < 6 || message.trim().length < 20;

  const send = async () => {
    if (disabled) return;
    setBusy(true);
    setError('');
    try {
      const payload = await rpApi('/api/marketing/email-blasts/send', {
        method: 'POST',
        timeoutMs: 120000,
        body: JSON.stringify({
          audience: audience.type,
          confirm: true,
          ctaLabel,
          ctaUrl,
          handle: audience.handle || '',
          ids: audience.ids || [],
          markNotified,
          message,
          subject
        })
      });
      toast(`Email blast sent to ${payload.sent || 0} of ${payload.recipients || recipientCount} subscribers.`, payload.failed ? 'warn' : 'ok');
      onSent && onSent(payload);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not send email blast.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Send Email Blast" sub={`${recipientCount} unique recipient${recipientCount === 1 ? '' : 's'} in this audience`} onClose={onClose} width={720}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={disabled} onClick={send} style={{ opacity: disabled ? 0.6 : 1 }}><Icon name="mail" size={15} />{busy ? 'Sending...' : 'Send Blast'}</button></>}>
      {!canManage && <LiveDataGuardNotice title="Read-only access">Your role can view marketing subscribers, but cannot send campaigns.</LiveDataGuardNotice>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <MField label="Subject" required>
          <input className="field" value={subject} onChange={e => setSubject(e.target.value)} maxLength={180} />
        </MField>
        <MField label="Button URL">
          <input className="field" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="https://www.ranchpower.com/shop/" />
        </MField>
      </div>
      <MField label="Button Label">
        <input className="field" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} maxLength={60} />
      </MField>
      <MField label="Message" required>
        <textarea className="field" value={message} onChange={e => setMessage(e.target.value)} rows={8} style={{ resize: 'vertical', lineHeight: 1.45 }} />
      </MField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 2 }}>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 13px', fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={markNotified} onChange={e => setMarkNotified(e.target.checked)} style={{ marginTop: 2 }} />
          <span>Mark sent subscribers as notified</span>
        </label>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: confirmed ? 'var(--amber-soft)' : 'var(--surface-2)', border: '1px solid ' + (confirmed ? 'var(--amber)' : 'var(--line)'), borderRadius: 10, padding: '12px 13px', fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
          <span>I confirm this should be sent to the selected subscribers.</span>
        </label>
      </div>
      {error && <div style={{ marginTop: 14, color: 'var(--danger)', background: 'var(--danger-bg)', borderRadius: 9, padding: '10px 12px', fontSize: 13, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function MarketingView() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [blasts, setBlasts] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('open');
  const [handle, setHandle] = useState('All');
  const [selected, setSelected] = useState(() => new Set());
  const [blastOpen, setBlastOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await rpApi('/api/marketing/restock-requests?limit=2000');
      setRows(payload.requests || []);
      setBlasts(payload.blasts || []);
      setCanManage(payload.canManage !== false);
      setSelected(new Set());
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not load marketing subscribers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handles = useMemo(() => {
    const values = rows.map(row => row.handle).filter(Boolean);
    return ['All', ...Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const visible = rows.filter(row => {
    const key = String(row.status || 'open').toLowerCase();
    if (status !== 'all' && key !== status) return false;
    if (handle !== 'All' && row.handle !== handle) return false;
    const hay = [row.email, row.product, row.variant, row.handle, row.notes, row.source].join(' ').toLowerCase();
    return !search.trim() || hay.includes(search.trim().toLowerCase());
  });

  const selectedRows = visible.filter(row => selected.has(row.id));
  const blastRows = selectedRows.length ? selectedRows : visible.filter(row => ['open', 'notified'].includes(String(row.status || '').toLowerCase()));
  const openCount = rows.filter(row => row.status === 'open').length;
  const notifiedCount = rows.filter(row => row.status === 'notified').length;
  const closedCount = rows.filter(row => row.status === 'closed').length;
  const unsubscribedCount = rows.filter(row => row.status === 'unsubscribed').length;
  const uniqueEmails = new Set(rows.map(row => String(row.email || '').toLowerCase()).filter(Boolean)).size;
  const domains = Array.from(new Set(rows.map(row => marketingEmailDomain(row.email)).filter(Boolean))).length;

  const audience = selectedRows.length
    ? { type: 'selected', ids: selectedRows.map(row => row.id), handle: '' }
    : { type: status === 'notified' ? 'notified' : status === 'all' ? 'all_active' : 'open', ids: [], handle: handle === 'All' ? '' : handle };

  const toggleSelected = row => {
    if (['closed', 'unsubscribed'].includes(String(row.status || '').toLowerCase())) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const selectable = visible.filter(row => !['closed', 'unsubscribed'].includes(String(row.status || '').toLowerCase()));
    const allOn = selectable.length > 0 && selectable.every(row => selected.has(row.id));
    setSelected(prev => {
      const next = new Set(prev);
      selectable.forEach(row => allOn ? next.delete(row.id) : next.add(row.id));
      return next;
    });
  };

  const updateRows = async (ids, nextStatus) => {
    if (!canManage || !ids.length) return;
    setBusy(`${nextStatus}:${ids.join(',')}`);
    try {
      const payload = await rpApi('/api/marketing/restock-requests', {
        method: 'POST',
        body: JSON.stringify({ ids, status: nextStatus })
      });
      const updated = new Map((payload.requests || []).map(row => [row.id, row]));
      setRows(current => current.map(row => updated.get(row.id) || row));
      setSelected(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      toast(`${ids.length} subscriber${ids.length === 1 ? '' : 's'} updated.`, 'ok');
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not update subscriber.', 'danger');
    } finally {
      setBusy('');
    }
  };

  const clearFilters = () => {
    setStatus('all');
    setHandle('All');
    setSearch('');
  };

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <PageHead eyebrow="Audience & Retention" title="Marketing"
        sub="Manage restock subscribers from the public shop and send controlled subscriber updates."
        actions={<><button className="btn btn-sm" onClick={load} disabled={loading}><Icon name="refresh" size={14} />Refresh</button><button className="btn btn-sm" onClick={() => marketingDownloadCsv(visible)} disabled={!visible.length}><Icon name="download" size={14} />Export CSV</button><button className="btn btn-sm btn-primary" disabled={!canManage || !blastRows.length} onClick={() => setBlastOpen(true)} style={{ opacity: canManage && blastRows.length ? 1 : 0.6 }}><Icon name="mail" size={14} />Email Blast</button></>} />

      {error && <LiveDataGuardNotice title="Marketing data unavailable">{error}</LiveDataGuardNotice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))', gap: 14, marginBottom: 18 }}>
        <KPICard label="Open Requests" value={openCount} icon="mail" accent="var(--amber)" active={status === 'open'} onClick={() => setStatus('open')} />
        <KPICard label="Notified" value={notifiedCount} icon="check" accent="var(--ok)" active={status === 'notified'} onClick={() => setStatus('notified')} />
        <KPICard label="Unique Emails" value={uniqueEmails} unit={`${domains} domains`} icon="customers" accent="var(--info)" active={status === 'all'} onClick={() => setStatus('all')} />
        <KPICard label="Suppressed" value={closedCount + unsubscribedCount} unit={`${unsubscribedCount} unsubscribed`} icon="alert" accent="var(--danger)" active={status === 'unsubscribed'} onClick={() => setStatus('unsubscribed')} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          <SectionTitle>Restock Subscribers</SectionTitle>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['all', 'open', 'notified', 'closed', 'unsubscribed'].map(item => (
              <button key={item} className="btn btn-sm" onClick={() => setStatus(item)}
                style={{ background: status === item ? 'var(--navy)' : undefined, color: status === item ? '#fff' : undefined, borderColor: status === item ? 'var(--navy)' : undefined }}>
                {item === 'all' ? 'All' : MARKETING_STATUS_LABELS[item]}
              </button>
            ))}
          </div>
        </div>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)', display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 220px auto', gap: 10, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--faint)' }} />
            <input className="field" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search email, product, handle, or note..." style={{ paddingLeft: 36 }} />
          </div>
          <select className="field" value={handle} onChange={e => setHandle(e.target.value)}>
            {handles.map(item => <option key={item} value={item}>{item === 'All' ? 'All products' : item}</option>)}
          </select>
          <button className="btn btn-sm" onClick={clearFilters}><Icon name="x" size={13} />Clear</button>
        </div>
        <div className="rp-scroll" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ width: 42, padding: '10px 12px', textAlign: 'left' }}><input type="checkbox" onChange={toggleAllVisible} checked={visible.length > 0 && visible.filter(row => !['closed', 'unsubscribed'].includes(String(row.status || '').toLowerCase())).every(row => selected.has(row.id))} /></th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Subscriber</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Product</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Requested</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(row => {
                const disabledRow = ['closed', 'unsubscribed'].includes(String(row.status || '').toLowerCase());
                return (
                  <tr key={row.id} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={{ padding: '12px' }}><input type="checkbox" checked={selected.has(row.id)} disabled={disabledRow} onChange={() => toggleSelected(row)} /></td>
                    <td style={{ padding: '12px' }}>
                      <div className="mono" style={{ fontSize: 12.5, color: 'var(--ink)', fontWeight: 700 }}>{row.email}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 3 }}>{row.source || 'public_shop'}</div>
                    </td>
                    <td style={{ padding: '12px', maxWidth: 330 }}>
                      <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{marketingProductLabel(row)}</div>
                      <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.handle || '-'}</div>
                    </td>
                    <td style={{ padding: '12px' }}>{marketingStatusPill(row.status)}</td>
                    <td style={{ padding: '12px', color: 'var(--muted)', fontSize: 12.5 }}>{marketingDate(row.createdAt)}</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-sm" disabled={!canManage || busy || row.status === 'notified'} onClick={() => updateRows([row.id], 'notified')}><Icon name="check" size={13} />Notified</button>
                        <button className="btn btn-sm" disabled={!canManage || busy || row.status === 'closed'} onClick={() => updateRows([row.id], 'closed')}>Close</button>
                        <button className="btn btn-sm" disabled={!canManage || busy || row.status === 'unsubscribed'} onClick={() => updateRows([row.id], 'unsubscribed')} style={{ color: 'var(--danger)' }}>Suppress</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!visible.length && (
                <tr><td colSpan="6" style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--faint)' }}>{loading ? 'Loading subscribers...' : 'No subscribers match these filters.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {selectedRows.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--amber-soft)', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--amber-deep)' }}>{selectedRows.length} selected</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-sm" disabled={!canManage || busy} onClick={() => updateRows(selectedRows.map(row => row.id), 'notified')}><Icon name="check" size={13} />Mark Notified</button>
              <button className="btn btn-sm" disabled={!canManage || busy} onClick={() => updateRows(selectedRows.map(row => row.id), 'closed')}>Close</button>
              <button className="btn btn-sm btn-primary" disabled={!canManage || busy} onClick={() => setBlastOpen(true)}><Icon name="mail" size={13} />Email Selected</button>
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '16px 18px' }}>
        <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={load}><Icon name="refresh" size={13} />Refresh</button>}>Recent Email Blasts</SectionTitle>
        <div style={{ display: 'grid', gap: 8 }}>
          {blasts.map(blast => (
            <div key={blast.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 14, alignItems: 'center', border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 10, padding: '11px 13px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{blast.subject}</div>
                <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 3 }}>{blast.createdByEmail || 'System'} - {marketingDate(blast.sentAt || blast.createdAt)}</div>
              </div>
              <div className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{blast.sentCount || 0}/{blast.recipientCount || 0} sent</div>
              {marketingBlastStatusPill(blast.status)}
            </div>
          ))}
          {!blasts.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: '12px 4px' }}>No email blasts have been logged yet.</div>}
        </div>
      </div>

      {blastOpen && <MarketingBlastModal audience={audience} canManage={canManage} rows={blastRows} onClose={() => setBlastOpen(false)} onSent={load} />}
    </div>
  );
}

Object.assign(window, { MarketingView });


/* ===== desktop_create.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : master-data create modals.
   Every core record type an admin/manager needs to input:
   locations, suppliers, materials, products, POs, runs,
   users, price lists, approval rules, transfers.
   ============================================================ */

/* Small confirmation banner used inside modals after save */
function OkBanner({ children }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--ok-bg)', color: 'var(--ok)', padding: '12px 15px', borderRadius: 10, marginBottom: 18, fontSize: 13.5, fontWeight: 600 }}><Icon name="check" size={18} />{children}</div>;
}
function InfoNote({ icon, children }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5 }}><Icon name={icon || 'info'} size={15} style={{ flexShrink: 0 }} />{children}</div>;
}
function reqStyle(bad) { return bad ? { borderColor: 'var(--danger)' } : undefined; }
function ReqMsg({ show, children }) { return show ? <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>{children}</div> : null; }
function preciseCoordinate(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100000000) / 100000000 : parsed;
}
function territoryOptionsWith(current, extras) {
  const values = [];
  const add = value => {
    const name = String(value || '').trim();
    if (!name || values.some(item => item.toLowerCase() === name.toLowerCase())) return;
    values.push(name);
  };
  (Array.isArray(RP.TERRITORIES) ? RP.TERRITORIES : []).forEach(add);
  (Array.isArray(extras) ? extras : []).forEach(add);
  add(current);
  return values;
}

/* ============================================================ LOCATION */
function AddLocationModal({ location, onClose, onAdded, presetType }) {
  return <LocationMasterModal location={location} onClose={onClose} onAdded={onAdded} presetType={presetType} />;
}

function locationAssignableUsers() {
  if (rpHasLiveAuth()) {
    return (Array.isArray(RP.USER_PROFILES) ? RP.USER_PROFILES : [])
      .filter(user => user && user.active !== false && rpNormalizeRole(user.role) !== 'customer')
      .map(user => ({
        id: user.id,
        name: user.name || user.display_name || user.email || user.id,
        role: rpRoleLabel(user.role),
        territory: user.territory || ''
      }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }
  return ['Dana Whitfield', 'Sam Okoro', 'Marco Ferreira'].map(name => ({ id: name, name, role: 'Demo rep', territory: '' }));
}

function locationUserIdForName(name) {
  const target = String(name || '').trim().toLowerCase();
  if (!target) return '';
  const user = locationAssignableUsers().find(item => String(item.name || '').trim().toLowerCase() === target);
  return user ? user.id : '';
}

function LocationMasterModal({ location, onClose, onAdded, presetType }) {
  const editing = !!location;
  const initialTerritory = location ? location.territory || '' : (territoryOptionsWith('', [])[0] || 'Squamish');
  const [f, setF] = useState({
    code: location ? location.id || '' : '',
    name: location ? location.name || '' : '',
    type: location ? location.type || 'Main Warehouse' : presetType || 'Main Warehouse',
    territory: initialTerritory || 'Squamish',
    cap: location && location.cap != null ? String(location.cap) : '',
    lat: location && location.lat ? String(location.lat) : '',
    lng: location && location.lng ? String(location.lng) : '',
    rep: location ? location.rep || '' : '',
    assignedUserId: location ? location.assignedUserId || '' : '',
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const isMicro = f.type === 'Micro-Warehouse';
  const isDist = f.type === 'Distributor';
  const prefix = f.type === 'Micro-Warehouse' ? 'MW' : f.type === 'Distributor' ? 'DIST' : f.type === 'FBA' ? 'FBA' : f.type === 'Production Facility' ? 'FAC' : f.type === '3PL Hub' || f.type === '3PL Warehouse' ? '3PL' : 'WH';
  const assignableUsers = locationAssignableUsers();
  const assignedUserIdValue = rpHasLiveAuth() ? (f.assignedUserId || locationUserIdForName(f.rep)) : f.rep;
  const cleanId = value => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  const shortFromName = () => f.name.split(/\s+/).filter(Boolean).map(part => part[0]).join('').slice(0, 6).toUpperCase();
  const codeValue = cleanId(f.code || (f.name ? `${prefix}-${shortFromName()}` : ''));
  const duplicate = !editing && codeValue && RP.LOCATIONS.some(l => l.id === codeValue);
  const territoryCoords = { Alberta: [51.29, -114.01], 'Lower Mainland': [49.08, -123.06], Squamish: [49.70, -123.16], Kelowna: [49.88, -119.49], Kamloops: [50.67, -120.33], 'Northern BC': [53.91, -122.74], FBA: [49.19, -123.18] };
  const territoryOptions = territoryOptionsWith(f.territory, ['Alberta', 'Lower Mainland', 'FBA']);
  const coords = territoryCoords[f.territory] || [49.70, -123.16];
  const latValue = f.lat === '' ? coords[0] : preciseCoordinate(f.lat);
  const lngValue = f.lng === '' ? coords[1] : preciseCoordinate(f.lng);
  const capValue = isDist ? null : (parseInt(f.cap, 10) || (isMicro ? 100 : 400));

  const save = async () => {
    setTried(true);
    setErr('');
    if (!f.name.trim() || !codeValue || duplicate || !Number.isFinite(latValue) || !Number.isFinite(lngValue) || latValue < -90 || latValue > 90 || lngValue < -180 || lngValue > 180) return;
    setBusy(true);

    try {
      let l;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/locations/upsert', {
          method: 'POST',
          body: JSON.stringify({
            assignedUserId: isMicro ? assignedUserIdValue : '',
            capacityCases: capValue,
            id: codeValue,
            lat: latValue,
            lng: lngValue,
            name: f.name.trim(),
            territory: f.territory,
            type: f.type,
          }),
        });
        l = response.location;
        const refreshed = await rpRefreshAfterMutation(response.created ? 'Location added' : 'Location saved');
        if (!refreshed && l) {
          const idx = RP.LOCATIONS.findIndex(x => x.id === l.id);
          if (idx >= 0) RP.LOCATIONS.splice(idx, 1, l);
          else RP.LOCATIONS.push(l);
        }
      } else if (editing) {
        l = { ...location, id: codeValue, name: f.name.trim(), type: f.type, territory: f.territory, cap: capValue, lat: latValue, lng: lngValue, rep: f.rep || location.rep };
        const idx = RP.LOCATIONS.findIndex(x => x.id === location.id);
        if (idx >= 0) RP.LOCATIONS.splice(idx, 1, l);
      } else {
        l = RP.addLocation({
          id: codeValue,
          name: f.name.trim(),
          type: f.type,
          territory: f.territory,
          lat: latValue,
          lng: lngValue,
          code: codeValue.replace(`${prefix}-`, ''),
          cap: capValue,
          used: 0,
          rep: isMicro ? f.rep || undefined : undefined,
          valueCap: isMicro ? 3000 : undefined,
          valueUsed: isMicro ? 0 : undefined,
        });
      }

      toast(`${l.name} ${editing ? 'saved' : 'added'} - ${l.id}`);
      onAdded && onAdded(l);
      onClose();
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not save location.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={editing ? 'Edit Location' : 'Add Location'} sub="Production, 3PL, warehouse, and rep custody locations" onClose={onClose} width={560}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : editing ? 'Save Location' : 'Add Location'}</button></>}>
      <MField label="Location Name" required>
        <input className="field" placeholder="e.g. Vernon Cross-Dock" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !f.name.trim())} />
        <ReqMsg show={tried && !f.name.trim()}>Location name is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Type" half><select className="field" value={f.type} onChange={e => set('type', e.target.value)}>{['Production Facility', '3PL Hub', '3PL Warehouse', 'Main Warehouse', 'Micro-Warehouse', 'Distributor', 'FBA'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Territory" half><select className="field" value={f.territory} onChange={e => set('territory', e.target.value)}>{territoryOptions.map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        {!isDist && <MField label={isMicro ? 'Case Cap' : 'Capacity (cases)'} half><input className="field" type="number" placeholder={isMicro ? '100' : '400'} value={f.cap} onChange={e => set('cap', e.target.value)} /></MField>}
        <MField label="Location ID" half>
          <input className="field mono" placeholder={`${prefix}-CODE`} value={f.code} disabled={editing} onChange={e => set('code', cleanId(e.target.value))} style={reqStyle(tried && (!codeValue || duplicate))} />
          <ReqMsg show={tried && !codeValue}>Location ID is required</ReqMsg>
          <ReqMsg show={tried && duplicate}>That location ID already exists. Use Edit on the location row.</ReqMsg>
        </MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Latitude" half><input className="field mono" type="number" step="0.00000001" placeholder={String(coords[0])} value={f.lat} onChange={e => set('lat', e.target.value)} style={reqStyle(tried && (!Number.isFinite(latValue) || latValue < -90 || latValue > 90))} /></MField>
        <MField label="Longitude" half><input className="field mono" type="number" step="0.00000001" placeholder={String(coords[1])} value={f.lng} onChange={e => set('lng', e.target.value)} style={reqStyle(tried && (!Number.isFinite(lngValue) || lngValue < -180 || lngValue > 180))} /></MField>
      </div>
      {isMicro && <MField label="Assigned Rep">
        <select className="field" value={assignedUserIdValue} onChange={e => {
          const user = assignableUsers.find(item => item.id === e.target.value);
          setF(s => ({ ...s, assignedUserId: rpHasLiveAuth() ? e.target.value : '', rep: user ? user.name : e.target.value }));
        }}>
          <option value="">{rpHasLiveAuth() ? 'Unassigned' : 'Select a rep...'}</option>
          {assignableUsers.map(user => <option key={user.id} value={user.id}>{user.name}{user.territory ? ` - ${user.territory}` : ''}{user.role ? ` (${user.role})` : ''}</option>)}
        </select>
        {rpHasLiveAuth() && !assignableUsers.length && <ReqMsg show>Invite or activate staff profiles before assigning a micro-warehouse.</ReqMsg>}
      </MField>}
      <InfoNote icon="pin">{isMicro ? 'Rep storage is value-capped at $3,000 and tracked against fronted stock.' : isDist ? 'Distributor DCs hold consigned inventory you still own until sell-through.' : f.type === 'Production Facility' ? 'Production releases finished goods into this facility before any outbound transfer.' : 'New custody locations start empty - move stock in with an exact-lot transfer.'}</InfoNote>
      {err && <ReqMsg show>{err}</ReqMsg>}
    </Modal>
  );
}

function LegacyAddLocationModal({ onClose, onAdded, presetType }) {
  const [f, setF] = useState({ name: '', type: presetType || 'Main Warehouse', territory: territoryOptionsWith('', [])[0] || 'Squamish', cap: '', rep: '', code: '' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const isMicro = f.type === 'Micro-Warehouse';
  const isDist = f.type === 'Distributor';
  const save = () => {
    setTried(true);
    if (!f.name.trim()) return;
    const coords = { Squamish: [49.70, -123.16], Kelowna: [49.88, -119.49], Kamloops: [50.67, -120.33], 'Northern BC': [53.91, -122.74] }[f.territory] || [49.7, -123.1];
    const data = { name: f.name.trim(), type: f.type, territory: f.territory, lat: coords[0], lng: coords[1], code: f.code.trim() || undefined };
    if (!isDist && !isMicro) { data.cap = parseInt(f.cap, 10) || 400; data.used = 0; }
    if (isMicro) { data.cap = parseInt(f.cap, 10) || 100; data.used = 0; data.rep = f.rep || '—'; data.valueCap = 3000; data.valueUsed = 0; }
    const l = RP.addLocation(data);
    toast(`${l.name} added to the network · ${l.id}`);
    onAdded && onAdded(l);
    onClose();
  };
  return (
    <Modal title="Add Location" sub="Warehouses, rep micro-warehouses & distributor DCs" onClose={onClose} width={540}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Add Location</button></>}>
      <MField label="Location Name" required>
        <input className="field" placeholder="e.g. Vernon Cross-Dock" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !f.name.trim())} />
        <ReqMsg show={tried && !f.name.trim()}>Location name is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Type" half><select className="field" value={f.type} onChange={e => set('type', e.target.value)}>{['Main Warehouse', 'Micro-Warehouse', 'Distributor', 'FBA'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Territory" half><select className="field" value={f.territory} onChange={e => set('territory', e.target.value)}>{territoryOptionsWith(f.territory, ['FBA']).map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        {!isDist && <MField label={isMicro ? 'Case Cap' : 'Capacity (cases)'} half><input className="field" type="number" placeholder={isMicro ? '100' : '400'} value={f.cap} onChange={e => set('cap', e.target.value)} /></MField>}
        <MField label="Short Code" half><input className="field mono" placeholder="auto" value={f.code} onChange={e => set('code', e.target.value.toUpperCase())} /></MField>
      </div>
      {isMicro && <MField label="Assigned Rep"><select className="field" value={f.rep} onChange={e => set('rep', e.target.value)}><option value="">Select a rep...</option>{locationAssignableUsers().map(user => <option key={user.id} value={user.name}>{user.name}</option>)}</select></MField>}
      <InfoNote icon="pin">{isMicro ? 'Rep storage is value-capped at $3,000 and tracked against fronted stock.' : isDist ? 'Distributor DCs hold consigned inventory you still own until sell-through.' : 'New warehouses start empty — move stock in via a transfer order.'}</InfoNote>
    </Modal>
  );
}

/* ============================================================ SUPPLIER */
function DemoAddSupplierModal({ onClose, onAdded }) {
  const [f, setF] = useState({ name: '', category: 'Cans', country: 'Canada', terms: 'Net 30', contact: '' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const save = () => {
    setTried(true);
    if (!f.name.trim()) return;
    const s = RP.addSupplier({ name: f.name.trim(), category: f.category, country: f.country, terms: f.terms, contact: f.contact.trim() || '—' });
    toast(`${s.name} added to suppliers · ${s.id}`);
    onAdded && onAdded(s);
    onClose();
  };
  return (
    <Modal title="Add Supplier" sub="Vendors & co-packers for materials and runs" onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Add Supplier</button></>}>
      <MField label="Supplier Name" required>
        <input className="field" placeholder="e.g. Okanagan Glass & Can" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !f.name.trim())} />
        <ReqMsg show={tried && !f.name.trim()}>Supplier name is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Supplies" half><select className="field" value={f.category} onChange={e => set('category', e.target.value)}>{['Cans', 'Lids', 'Ingredient', 'Co-Packer', 'Packaging', 'Freight', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Country of Origin" half><select className="field" value={f.country} onChange={e => set('country', e.target.value)}>{['Canada', 'USA', 'China', 'Mexico', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Payment Terms" half><select className="field" value={f.terms} onChange={e => set('terms', e.target.value)}>{['Net 15', 'Net 30', 'Net 45', 'Prepaid'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Contact Email" half><input className="field" placeholder="orders@vendor.com" value={f.contact} onChange={e => set('contact', e.target.value)} /></MField>
      </div>
      {f.country !== 'Canada' && <InfoNote icon="alert">Non-Canada origin — materials from this supplier carry tariff exposure flagged on finished-goods lots.</InfoNote>}
    </Modal>
  );
}

/* ============================================================ MATERIAL / INGREDIENT */
function DemoAddMaterialModal({ onClose, onAdded }) {
  const [f, setF] = useState({ material: '', kind: 'Ingredient', supplier: RP.SUPPLIERS[0].name, origin: 'Canada', hs: '', duty: '0%' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const prefix = { Ingredient: 'ING', Can: 'CAN', Lid: 'LID', Packaging: 'PKG' }[f.kind] || 'MAT';
  const save = () => {
    setTried(true);
    if (!f.material.trim()) return;
    const lot = prefix + '-' + String(Math.floor(Math.random() * 900) + 100);
    const m = RP.addMaterial({ lot, material: f.material.trim(), supplier: f.supplier, origin: f.origin, hs: f.hs.trim() || '—', duty: f.duty });
    toast(`${m.material} added · lot ${m.lot}`);
    onAdded && onAdded(m);
    onClose();
  };
  return (
    <Modal title="Add Material" sub="Ingredients & packaging inputs with origin + tariff" onClose={onClose} width={540}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Add Material</button></>}>
      <MField label="Material Name" required>
        <input className="field" placeholder="e.g. Blood Orange flavour" value={f.material} onChange={e => set('material', e.target.value)} style={reqStyle(tried && !f.material.trim())} />
        <ReqMsg show={tried && !f.material.trim()}>Material name is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Type" half><select className="field" value={f.kind} onChange={e => set('kind', e.target.value)}>{['Ingredient', 'Can', 'Lid', 'Packaging'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Supplier" half><select className="field" value={f.supplier} onChange={e => set('supplier', e.target.value)}>{RP.SUPPLIERS.map(s => <option key={s.id}>{s.name}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Origin" half><select className="field" value={f.origin} onChange={e => set('origin', e.target.value)}>{['Canada', 'USA', 'China', 'Mexico', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="HS Code" half><input className="field mono" placeholder="e.g. 2106.90" value={f.hs} onChange={e => set('hs', e.target.value)} /></MField>
      </div>
      <MField label="Duty Rate"><select className="field" value={f.duty} onChange={e => set('duty', e.target.value)}>{['0%', 'N/A', '7.5%', '12%', 'Review', 'TBD'].map(t => <option key={t}>{t}</option>)}</select></MField>
    </Modal>
  );
}

/* ============================================================ PRODUCT / SKU */
function AddProductModal({ onClose, onAdded, product }) {
  return <ProductSkuModal onClose={onClose} onAdded={onAdded} product={product} />;
}

const SKU_VISUAL_SWATCHES = ['#B97A2E', '#2C4456', '#3F6FC4', '#5B8A3C', '#C0492A', '#7AA61F', '#CF5A78', '#D98A4A'];

function ProductSkuModal({ onClose, onAdded, product }) {
  const editing = !!product;
  const productFlavor = product && product.flavor ? product.flavor : '';
  const existingContainerFee = product ? Number(product.deposit || 0) + Number(product.recyclingFee || 0) : 1.20;
  const [f, setF] = useState({
    customerVisible: product ? rpSkuCustomerVisible(product) : true,
    code: product ? product.code || product.sku || '' : '',
    deposit: String(existingContainerFee || 0),
    flavor: productFlavor,
    id: '',
    lotCode: product ? product.lotCode || '' : '',
    name: product ? product.name || '' : '',
    pack: product ? product.pack || '12-Pack' : '12-Pack',
    price: product ? String(product.price || '') : '36.50',
    upc: product ? product.upc || '' : '',
    visualColor: product ? product.visualColor || '' : '',
    visualLabel: product ? product.visualLabel || '' : '',
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const cleanSku = value => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  const cleanLotCodeInput = value => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 80);
  const cleanVisualLabel = value => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  const cleanVisualColor = value => {
    const raw = String(value || '').trim().toUpperCase().replace(/[^#0-9A-F]/g, '');
    if (!raw) return '';
    const body = raw.replace(/#/g, '');
    return ('#' + body).slice(0, 7);
  };
  const flavorObj = RP.flavor(f.flavor);
  const customAbbr = cleanSku(f.name || (product && (product.code || product.id)) || 'SKU').slice(0, 10) || 'SKU';
  const fl = flavorObj || { abbr: customAbbr, name: f.name.trim() || (product && product.name) || 'Custom SKU' };
  const autoId = (f.pack === '12-Pack' ? 'RP-12-' : 'RP-MIX-') + fl.abbr;
  const skuId = editing ? product.id : cleanSku(f.id || autoId);
  const externalSku = cleanSku(f.code || (!editing ? skuId : product && (product.code || product.sku)));
  const upc = String(f.upc || '').replace(/\D/g, '').slice(0, 12);
  const displayName = f.name.trim() || (flavorObj ? fl.name + (f.pack === '12-Pack' ? ' 355ml' : f.pack === 'Mixer 12' ? ' Mixer' : '') : fl.name);
  const duplicate = !editing && RP.SKUS.some(s => s.id === skuId);
  const duplicateExternalSku = RP.SKUS.some(s => s.id !== skuId && cleanSku(s.code || s.sku) === externalSku);
  const upcInvalid = !!upc && !/^\d{12}$/.test(upc);
  const visualLabel = cleanVisualLabel(f.visualLabel) || rpSkuVisualLabel({ id: skuId, flavor: f.flavor || null, name: displayName, pack: f.pack });
  const visualColor = cleanVisualColor(f.visualColor);
  const visualColorInvalid = !!f.visualColor && !/^#(?:[0-9A-F]{3}|[0-9A-F]{6})$/i.test(visualColor);
  const priceValue = parseFloat(f.price);
  const priceInvalid = !Number.isFinite(priceValue) || priceValue < 0 || (f.customerVisible && priceValue <= 0);
  const previewBaseSku = { ...(product || {}), id: skuId, code: externalSku, upc, flavor: f.flavor || null, name: displayName, pack: f.pack, visualColor: visualColor || '', visualLabel };
  const lotCode = rpCleanLotCode(f.lotCode) || rpSkuDefaultLotCode(previewBaseSku);
  const previewSku = { ...previewBaseSku, lotCode };

  const save = async () => {
    setTried(true);
    setErr('');
    const price = priceValue;
    const deposit = parseFloat(f.deposit || '0');
    const recyclingFee = 0;
    if (!skuId || !externalSku || !displayName || priceInvalid || !Number.isFinite(deposit) || deposit < 0 || visualColorInvalid || upcInvalid || duplicate || duplicateExternalSku) return;
    setBusy(true);

    try {
      let p;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/skus/upsert', {
          method: 'POST',
          body: JSON.stringify({
            deposit,
            flavor: f.flavor || null,
            id: skuId,
            name: displayName,
            pack: f.pack,
            price,
            recyclingFee,
            sku: externalSku,
            upc: upc || null,
            lotCode,
            customerVisible: !!f.customerVisible,
            visualColor: visualColor || null,
            visualLabel: visualLabel || null,
          }),
        });
        p = response.sku;
        const refreshed = await rpRefreshAfterMutation(response.created ? 'Product added' : 'Product saved');
        if (!refreshed && p) {
          const idx = RP.SKUS.findIndex(s => s.id === p.id);
          if (idx >= 0) RP.SKUS.splice(idx, 1, p);
          else RP.SKUS.push(p);
        }
      } else if (editing) {
        p = { ...product, code: externalSku, upc, customerVisible: !!f.customerVisible, flavor: f.flavor || null, lotCode, name: displayName, pack: f.pack, price, deposit, recyclingFee, visualColor: visualColor || '', visualLabel };
        const idx = RP.SKUS.findIndex(s => s.id === product.id);
        if (idx >= 0) RP.SKUS.splice(idx, 1, p);
      } else {
        p = RP.addProduct({ id: skuId, code: externalSku, upc, customerVisible: !!f.customerVisible, flavor: f.flavor || null, lotCode, name: displayName, pack: f.pack, price, deposit, recyclingFee, visualColor: visualColor || '', visualLabel });
      }

      toast(`${displayName} ${editing ? 'saved' : 'added'} - ${externalSku}`);
      onAdded && onAdded(p);
      onClose();
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not save product.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? 'Edit Product' : 'Add Product'} sub="A SKU is the unit inventory and orders hang on" onClose={onClose} width={540}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : editing ? 'Save Product' : 'Add Product'}</button></>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 18, padding: '12px 14px', background: 'var(--surface-2)', borderRadius: 11, border: '1px solid var(--line)' }}>
        <FlavorChip sku={previewSku} size={38} />
        <div><div style={{ fontWeight: 700, fontSize: 14.5 }}>{displayName}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{externalSku ? `SKU ${externalSku}` : 'Product SKU required'}{upc ? ` · UPC ${upc}` : ''}</div></div>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Visual Preset" half><select className="field" value={f.flavor || ''} onChange={e => set('flavor', e.target.value)}>
          <option value="">Custom / mixer</option>
          {!flavorObj && f.flavor && <option value={f.flavor}>{f.flavor}</option>}
          {RP.FLAVORS.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select></MField>
        <MField label="Pack" half><select className="field" value={f.pack} onChange={e => set('pack', e.target.value)}>{['12-Pack', 'Mixer 12'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Badge" half><input className="field mono" placeholder={visualLabel} value={f.visualLabel} maxLength={6} onChange={e => set('visualLabel', cleanVisualLabel(e.target.value))} /></MField>
        <MField label="Badge Color" half>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            {SKU_VISUAL_SWATCHES.map(color => (
              <button key={color} type="button" title={color} aria-label={color} onClick={() => set('visualColor', color)} style={{ width: 28, height: 28, borderRadius: 7, border: visualColor === color ? '2px solid var(--ink)' : '1px solid var(--line)', background: color, cursor: 'pointer' }} />
            ))}
            <input className="field mono" placeholder="#B97A2E" value={f.visualColor} onChange={e => set('visualColor', cleanVisualColor(e.target.value))} style={{ width: 104, minWidth: 104, flex: '0 0 104px', height: 34, padding: '7px 9px', ...(tried && visualColorInvalid ? { borderColor: 'var(--danger)' } : {}) }} />
          </div>
          <ReqMsg show={tried && visualColorInvalid}>Use a hex color like #B97A2E</ReqMsg>
        </MField>
      </div>
      {!editing && (
        <MField label="Internal Product ID" required>
          <input className="field mono" placeholder={autoId} value={f.id} onChange={e => set('id', cleanSku(e.target.value))} style={reqStyle(tried && (!skuId || duplicate))} />
          <ReqMsg show={tried && !skuId}>Enter an internal product ID.</ReqMsg>
          <ReqMsg show={tried && duplicate}>That internal product ID already exists. Use Edit on the product row.</ReqMsg>
        </MField>
      )}
      {editing && <MField label="Internal Product ID"><input className="field mono" value={skuId} disabled /></MField>}
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Product SKU" half required>
          <input className="field mono" value={f.code} placeholder={skuId} onChange={e => set('code', cleanSku(e.target.value))} style={reqStyle(tried && (!externalSku || duplicateExternalSku))} />
          <ReqMsg show={tried && !externalSku}>Enter the customer-facing product SKU.</ReqMsg>
          <ReqMsg show={tried && duplicateExternalSku}>That product SKU is already assigned.</ReqMsg>
        </MField>
        <MField label="UPC" half>
          <input className="field mono" inputMode="numeric" maxLength={12} value={f.upc} placeholder="12 digits" onChange={e => set('upc', e.target.value.replace(/\D/g, '').slice(0, 12))} style={reqStyle(tried && upcInvalid)} />
          <ReqMsg show={tried && upcInvalid}>UPC must contain exactly 12 digits.</ReqMsg>
        </MField>
      </div>
      <MField label="Lot Code">
        <input className="field mono" placeholder={lotCode || 'RP-'} value={f.lotCode} onChange={e => set('lotCode', cleanLotCodeInput(e.target.value))} onBlur={() => set('lotCode', rpCleanLotCode(f.lotCode) || '')} />
      </MField>
      <MField label="Display Name" required><input className="field" placeholder={fl.name} value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !displayName)} /></MField>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 10, background: f.customerVisible ? 'var(--ok-bg)' : 'var(--surface-2)', color: f.customerVisible ? 'var(--ok)' : 'var(--muted)', fontWeight: 800, fontSize: 13, marginBottom: 14 }}>
        <input type="checkbox" checked={!!f.customerVisible} onChange={e => set('customerVisible', e.target.checked)} />
        Show on customer order page
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
        <MField label="Catalog Case Price" required>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
            <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && priceInvalid))} type="number" step="0.01" value={f.price} onChange={e => set('price', e.target.value)} />
          </div>
          <ReqMsg show={tried && priceInvalid}>{f.customerVisible ? 'Visible customer products need a price greater than zero.' : 'Price cannot be negative.'}</ReqMsg>
        </MField>
        <MField label="Container Fee">
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
            <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && !(parseFloat(f.deposit || '0') >= 0)))} type="number" step="0.01" value={f.deposit} onChange={e => set('deposit', e.target.value)} />
          </div>
          <ReqMsg show={tried && !(parseFloat(f.deposit || '0') >= 0)}>Container fee cannot be negative</ReqMsg>
        </MField>
      </div>
      {err && <ReqMsg show>{err}</ReqMsg>}
    </Modal>
  );
}

function LegacyAddProductModal({ onClose, onAdded }) {
  const [f, setF] = useState({ flavor: 'blood-orange', name: '', pack: '12-Pack', price: '32.00' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const fl = RP.flavor(f.flavor);
  const save = () => {
    setTried(true);
    const price = parseFloat(f.price);
    if (!price || price <= 0) return;
    const name = (f.name.trim() || fl.name) + (f.pack === '12-Pack' ? ' 355ml' : '');
    const id = (f.pack === '12-Pack' ? 'RP-12-' : 'RP-MIX-') + fl.abbr;
    const p = RP.addProduct({ id, flavor: f.flavor, name, pack: f.pack, price });
    toast(`${name} added to catalog · ${p.id}`);
    onAdded && onAdded(p);
    onClose();
  };
  return (
    <Modal title="Add Product" sub="A SKU is the unit inventory & orders hang on" onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Add Product</button></>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 18, padding: '12px 14px', background: 'var(--surface-2)', borderRadius: 11, border: '1px solid var(--line)' }}>
        <FlavorChip flavorId={f.flavor} size={38} />
        <div><div style={{ fontWeight: 700, fontSize: 14.5 }}>{f.name.trim() || fl.name}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{(f.pack === '12-Pack' ? 'RP-12-' : 'RP-MIX-') + fl.abbr}</div></div>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Flavour" half><select className="field" value={f.flavor} onChange={e => set('flavor', e.target.value)}>{RP.FLAVORS.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</select></MField>
        <MField label="Pack" half><select className="field" value={f.pack} onChange={e => set('pack', e.target.value)}>{['12-Pack', 'Mixer 12'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <MField label="Display Name"><input className="field" placeholder={fl.name} value={f.name} onChange={e => set('name', e.target.value)} /></MField>
        <MField label="Catalog Case Price" required>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
          <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && !(parseFloat(f.price) > 0)))} type="number" value={f.price} onChange={e => set('price', e.target.value)} />
        </div>
        <ReqMsg show={tried && !(parseFloat(f.price) > 0)}>Enter a price</ReqMsg>
      </MField>
    </Modal>
  );
}

/* ============================================================ PURCHASE ORDER */
function DemoNewPurchaseOrderModal({ onClose, onAdded, presetItem }) {
  const p0 = presetItem || {};
  const sup0 = RP.SUPPLIERS.find(s => s.name === p0.supplier) || RP.SUPPLIERS[0];
  const [f, setF] = useState({ supplier: sup0.name, category: p0.category || 'Cans', item: p0.name || '', ordered: '', unit: '', origin: sup0.country || 'Canada', eta: '2026-06-30', terms: sup0.terms || 'Net 30' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const qty = parseInt(f.ordered, 10), unit = parseFloat(f.unit);
  const value = (qty > 0 && unit > 0) ? qty * unit : 0;
  const valid = f.item.trim() && qty > 0 && unit > 0;
  const save = () => {
    setTried(true);
    if (!valid) return;
    const po = RP.addPurchaseOrder({
      supplier: f.supplier, category: f.category, eta: f.eta, terms: f.terms,
      risk: f.origin === 'China' ? 'Medium' : 'Low',
      items: [{ name: f.item.trim(), ordered: qty, recvd: 0, unit, origin: f.origin, tariff: f.origin === 'Canada' ? 'N/A' : 'Review' }],
    });
    toast(`${po.id} created · ${RP.money(value)} to ${po.supplier}`);
    onAdded && onAdded(po);
    onClose();
  };
  const supObj = RP.SUPPLIERS.find(s => s.name === f.supplier);
  return (
    <Modal title="New Purchase Order" sub="Order materials from a supplier" onClose={onClose} width={580}
      footer={<><div style={{ flex: 1, alignSelf: 'center' }}>{value > 0 && <span style={{ fontSize: 13, color: 'var(--muted)' }}><b className="disp" style={{ fontSize: 18, color: 'var(--ink)' }}>{RP.money(value)}</b> order value</span>}</div><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} style={{ opacity: valid ? 1 : 0.5 }}><Icon name="check" size={15} />Create PO</button></>}>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Supplier" half><select className="field" value={f.supplier} onChange={e => { const s = RP.SUPPLIERS.find(x => x.name === e.target.value); set('supplier', e.target.value); if (s) setF(st => ({ ...st, supplier: e.target.value, terms: s.terms, origin: s.country, category: s.category === 'Co-Packer' ? st.category : s.category })); }}>{RP.SUPPLIERS.map(s => <option key={s.id}>{s.name}</option>)}</select></MField>
        <MField label="Category" half><select className="field" value={f.category} onChange={e => set('category', e.target.value)}>{['Cans', 'Lids', 'Ingredient', 'Packaging', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <MField label="Item" required>
        <input className="field" placeholder="e.g. 355ml Cans" value={f.item} onChange={e => set('item', e.target.value)} style={reqStyle(tried && !f.item.trim())} />
        <ReqMsg show={tried && !f.item.trim()}>Item description is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Quantity" half>
          <input className="field" type="number" placeholder="e.g. 24000" value={f.ordered} onChange={e => set('ordered', e.target.value)} style={reqStyle(tried && !(qty > 0))} />
          <ReqMsg show={tried && !(qty > 0)}>Enter a quantity</ReqMsg>
        </MField>
        <MField label="Unit Cost" half>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
            <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && !(unit > 0)))} type="number" step="0.001" placeholder="0.000" value={f.unit} onChange={e => set('unit', e.target.value)} />
          </div>
          <ReqMsg show={tried && !(unit > 0)}>Enter unit cost</ReqMsg>
        </MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Origin" half><select className="field" value={f.origin} onChange={e => set('origin', e.target.value)}>{['Canada', 'USA', 'China', 'Mexico', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Expected" half><input className="field" type="date" value={f.eta} onChange={e => set('eta', e.target.value)} /></MField>
      </div>
      {supObj && f.origin !== 'Canada' && <InfoNote icon="alert">{f.origin} origin — duty/tariff will be flagged on received lots for landed-cost tracking.</InfoNote>}
    </Modal>
  );
}

/* ============================================================ PRODUCTION RUN */
function ScheduleProductionModal({ onClose, onAdded }) {
  const partnerOptions = ['BevMax'];
  const authRole = typeof rpNormalizeRole === 'function' ? rpNormalizeRole((rpAuthProfile() || {}).role) : '';
  const canHistoricalProduction = ['admin', 'leadership', 'ops'].includes(authRole);
  const facilityDateParts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Edmonton', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const facilityDateByType = Object.fromEntries(facilityDateParts.map(part => [part.type, part.value]));
  const facilityToday = `${facilityDateByType.year}-${facilityDateByType.month}-${facilityDateByType.day}`;
  const outputLocations = (RP.LOCATIONS || []).filter(l => /production facility/i.test(l.type || '') && /bevmax/i.test(`${l.id || ''} ${l.name || ''} ${l.organization || ''} ${l.facilityCode || ''}`));
  const fallbackOutputLocation = outputLocations.find(l => /bevmax/i.test(`${l.id || ''} ${l.name || ''} ${l.organization || ''}`)) || outputLocations[0] || { id: '' };
  const productionSkus = rpCasePackSkus();
  const defaultProductionSku = productionSkus.find(s => s.flavor === 'ginger-citrus') || productionSkus[0] || { id: '' };
  const [f, setF] = useState({ sku: defaultProductionSku.id, lot: '', lotGroup: '', partner: partnerOptions[0], outputLocationId: fallbackOutputLocation.id, runDate: facilityToday, cases: '', finishedGoodsOnly: false, materialTrackingNote: '' });
  const [runRef] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `PRD-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`
    : `PRD-${Date.now().toString(36).toUpperCase()}`);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const sku = RP.sku(f.sku);
  const productionFlavor = String((sku && (sku.flavor || sku.id)) || f.sku || 'production-run').toLowerCase().replace(/[^a-z0-9_-]/g, '');
  const fl = RP.flavor(sku && sku.flavor) || { name: rpSkuDisplayName(sku || f.sku || 'Production run'), abbr: String(f.sku || 'SKU').slice(0, 8) };
  const cases = Number(f.cases);
  const cans = Number.isInteger(cases) && cases > 0 ? cases * RP.FINISHED_GOODS_CASE_PACK_SIZE : 0;
  const finishedGoodsOnly = canHistoricalProduction && f.finishedGoodsOnly;
  const lot = String(f.lot || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  const valid = !!f.sku && !!lot && !!f.lotGroup.trim() && Number.isInteger(cases) && cases > 0 && !!f.outputLocationId && !!f.runDate
    && finishedGoodsOnly && !!f.materialTrackingNote.trim() && f.runDate <= facilityToday;
  const save = async () => {
    setTried(true);
    setErr('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      let run;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/production/schedule', {
          method: 'POST',
          body: JSON.stringify({
            cans,
            cases,
            finishedGoodsOnly,
            flavor: productionFlavor,
            lot,
            lotGroup: f.lotGroup.trim() || null,
            materialTrackingNote: finishedGoodsOnly ? f.materialTrackingNote.trim() : undefined,
            outputLocationId: f.outputLocationId,
            partner: f.partner,
            runId: runRef,
            runDate: f.runDate,
            skuId: f.sku
          })
        });
        run = response.run;
        const refreshed = await rpRefreshAfterMutation('Production run scheduled');
        if (!refreshed && run) RP.PRODUCTION.unshift(run);
      } else {
        run = RP.addProductionRun({ flavor: productionFlavor, lot, lotGroup: f.lotGroup.trim(), sku: f.sku || 'RP-12-' + fl.abbr, partner: f.partner, runDate: f.runDate, cans });
        toast(`${run.id} scheduled - ${cases} cases of ${fl.name}`);
      }
      onAdded && onAdded(run);
      onClose();
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not schedule production.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Record BevMax Production Run" sub="Capture already-produced sealed finished-goods flats" onClose={onClose} width={540}
      footer={<><div style={{ flex: 1, alignSelf: 'center' }}>{cases > 0 && <span style={{ fontSize: 13, color: 'var(--muted)' }}><b className="disp" style={{ fontSize: 18, color: 'var(--ink)' }}>{cases.toLocaleString()}</b> cases planned</span>}</div><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name="check" size={15} />{busy ? 'Scheduling...' : 'Schedule Run'}</button></>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 18, padding: '12px 14px', background: 'var(--surface-2)', borderRadius: 11, border: '1px solid var(--line)' }}>
        <FlavorChip sku={sku} size={38} />
        <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 14.5 }}>{fl.name}</div><div style={{ fontSize: 12, color: 'var(--muted)' }}>{f.partner}</div></div>
      </div>
      <MField label="Finished Goods Lot">
        <input className="field mono" placeholder="RP-GC-002" value={f.lot} onChange={e => set('lot', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} style={reqStyle(tried && !lot)} />
        <ReqMsg show={tried && !lot}>Enter a finished goods lot</ReqMsg>
      </MField>
      <MField label="Lot Group" required><input className="field" placeholder="Lot 2" value={f.lotGroup} onChange={e => set('lotGroup', e.target.value)} style={reqStyle(tried && !f.lotGroup.trim())} /><ReqMsg show={tried && !f.lotGroup.trim()}>Enter the production lot group</ReqMsg></MField>
      {canHistoricalProduction ? <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 12px', marginBottom: 12, border: '1px solid var(--line)', borderRadius: 10, background: finishedGoodsOnly ? 'var(--warn-bg)' : 'var(--surface-2)', cursor: 'pointer' }}>
        <input type="checkbox" checked={f.finishedGoodsOnly} onChange={e => set('finishedGoodsOnly', e.target.checked)} style={{ marginTop: 2 }} />
        <span><b style={{ display: 'block', fontSize: 13 }}>Historical finished-goods-only record</b><span style={{ display: 'block', marginTop: 2, fontSize: 11.5, color: 'var(--muted)' }}>Use only when this lot is already produced and BevMax evidence exists. No raw-material consumption is inferred.</span></span>
      </label> : <InfoNote icon="alert">Only admin, leadership, or operations can record already-produced lots. Exact-input scheduling is paused until the BevMax BOM is verified.</InfoNote>}
      {finishedGoodsOnly && <MField label="Material Tracking Exception" required><textarea className="field" rows="2" value={f.materialTrackingNote} onChange={e => set('materialTrackingNote', e.target.value)} placeholder="Example: Lot 2 was completed before raw-material lots were loaded into Ops; BevMax production records are the source evidence." style={reqStyle(tried && !f.materialTrackingNote.trim())} /><ReqMsg show={tried && !f.materialTrackingNote.trim()}>Explain why material consumption is out of scope for this historical run.</ReqMsg></MField>}
      {tried && canHistoricalProduction && !finishedGoodsOnly && <ReqMsg show>Select the explicit historical mode. Exact-input scheduling is unavailable until the BevMax BOM is verified.</ReqMsg>}
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Product" half><select className="field" value={f.sku} onChange={e => set('sku', e.target.value)}>{productionSkus.map(s => <option key={s.id} value={s.id}>{rpSkuDisplayName(s)}</option>)}</select></MField>
        <MField label="Co-Packer" half><select className="field" value={f.partner} onChange={e => set('partner', e.target.value)}>{partnerOptions.map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Run Date" half><input className="field" type="date" max={finishedGoodsOnly ? facilityToday : undefined} value={f.runDate} onChange={e => set('runDate', e.target.value)} /></MField>
        <MField label="Finished Cases / Flats" half>
          <input className="field" type="number" min="1" step="1" placeholder="e.g. 800" value={f.cases} onChange={e => set('cases', e.target.value)} style={reqStyle(tried && !(Number.isInteger(cases) && cases > 0))} />
          <ReqMsg show={tried && !(Number.isInteger(cases) && cases > 0)}>Enter a whole case/flat count</ReqMsg>
          {cases > 0 && <span style={{ fontSize: 11, color: 'var(--muted)' }}>{cans.toLocaleString()} cans in {cases.toLocaleString()} sealed flats</span>}
        </MField>
      </div>
      <MField label="QA Hold Location">
        <select className="field" value={f.outputLocationId} onChange={e => set('outputLocationId', e.target.value)}>
          {!outputLocations.length && <option value="">No active BevMax Production Facility</option>}
          {outputLocations.map(l => <option key={l.id} value={l.id}>{l.name || l.id}</option>)}
        </select>
      </MField>
      <InfoNote icon="production">{finishedGoodsOnly ? 'Material consumption is explicitly excluded; the exception note is signed with this immutable run. Verified lot dates and a COA or QA report are still required before release.' : 'Exact-input scheduling is paused until a BevMax-approved, versioned BOM replaces the demo material assumptions.'}</InfoNote>
      {err && <ReqMsg show>{err}</ReqMsg>}
    </Modal>
  );
}

/* ============================================================ INVITE USER */
function InviteUserModal({ onClose, onAdded }) {
  const [f, setF] = useState({ name: '', email: '', role: 'Sales Rep', area: '', defaultLocationId: '', setupMode: 'magic_link' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tempResult, setTempResult] = useState(null);
  const live = rpHasLiveAuth();
  const canInviteUsers = !live || rpCanInviteUsers();
  const inviteRoles = RP.PERM_ROLES.concat(['Customer']);
  const isCustomer = f.role === 'Customer';
  const roleKey = rpNormalizeRole(f.role);
  const isFieldInventoryRole = ['rep', 'partner'].includes(roleKey);
  const issuingPassword = f.setupMode === 'temporary_password';
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email);
  const valid = f.name.trim() && emailOk && (!isCustomer || f.area);
  const roleCol = RP.PERM_ROLES.indexOf(f.role);
  const caps = roleCol >= 0 ? RP.PERM_CAPS.filter(c => c.vals[roleCol]) : [];
  const territoryOptions = (Array.isArray(RP.TERRITORIES) ? RP.TERRITORIES : []).filter(Boolean);
  const defaultLocationOptions = (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : [])
    .filter(location => location && location.id && location.active !== false)
    .filter(location => {
      if (isCustomer) return false;
      if (isFieldInventoryRole) return location.type === 'Micro-Warehouse';
      return /warehouse|distributor|fba/i.test(`${location.type || ''} ${location.name || ''}`);
    })
    .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
  const save = async () => {
    setTried(true);
    if (issuingPassword && tempResult) return;
    setError('');
    setTempResult(null);
    if (!valid || busy) return;
    if (!canInviteUsers) {
      const msg = 'Only darren@ranchpower.com can send invites.';
      setError(msg);
      toast(msg, 'warn');
      return;
    }
    setBusy(true);
    const selectedLocation = defaultLocationOptions.find(location => location.id === f.defaultLocationId);
    const u = RP.addUser({ name: f.name.trim(), email: f.email.trim(), role: f.role, area: f.area.trim() || 'All', defaultLocationId: selectedLocation ? selectedLocation.id : '' });
    try {
      let response = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        response = await rpApi('/api/users/invite', {
          method: 'POST',
          body: JSON.stringify({
            name: f.name.trim(),
            email: f.email.trim(),
            role: f.role,
            setupMode: f.setupMode,
            temporaryPassword: issuingPassword,
            territory: isCustomer ? '' : (f.area.trim() || ''),
            customerId: isCustomer ? f.area : '',
            defaultLocationId: !isCustomer && selectedLocation ? selectedLocation.id : ''
          })
        });
      }
      if (response && response.defaultLocationId) u.defaultLocationId = response.defaultLocationId;
      if (issuingPassword && response && response.temporaryPassword) {
        setTempResult(response);
        toast(`Temporary password issued to ${u.email}`, 'ok');
        onAdded && onAdded(u);
        return;
      }
      if (response) await rpRefreshAfterMutation(`Setup link sent to ${u.email}`);
      toast(`Setup link sent to ${u.email}`);
      onAdded && onAdded(u);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not send invite.');
      RP.USERS = RP.USERS.filter(x => x.email !== u.email);
      onAdded && onAdded(null);
    } finally {
      setBusy(false);
    }
  };
  const copyTemp = () => {
    if (!tempResult || !tempResult.temporaryPassword) return;
    try { navigator.clipboard && navigator.clipboard.writeText(tempResult.temporaryPassword); } catch (e) { }
    toast('Temporary password copied', 'info');
  };
  return (
    <Modal title="Invite User" sub="Role-based access — permissions follow the role" onClose={onClose} width={560}
      footer={<><button className="btn" onClick={onClose}>{tempResult ? 'Done' : 'Cancel'}</button><button className="btn btn-primary" disabled={busy || !canInviteUsers || !!tempResult} style={{ opacity: busy || !canInviteUsers || tempResult ? 0.65 : 1 }} onClick={save}><Icon name="mail" size={15} />{tempResult ? 'Password Issued' : busy ? 'Sending...' : issuingPassword ? 'Issue Password' : 'Send Setup Link'}</button></>}>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Full Name" half>
          <input className="field" placeholder="e.g. Riley Chen" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !f.name.trim())} />
          <ReqMsg show={tried && !f.name.trim()}>Name is required</ReqMsg>
        </MField>
        <MField label="Work Email" half>
          <input className="field" placeholder="name@ranchpower.com" value={f.email} onChange={e => set('email', e.target.value)} style={reqStyle(tried && !emailOk)} />
          <ReqMsg show={tried && !emailOk}>Enter a valid email</ReqMsg>
        </MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Role" half><select className="field" value={f.role} onChange={e => set('role', e.target.value)}>{inviteRoles.map(r => <option key={r}>{r}</option>)}</select></MField>
        <MField label={isCustomer ? 'Customer Account' : 'Territory / Area'} half>
          {isCustomer ? (
            <>
              <select className="field" value={f.area} onChange={e => set('area', e.target.value)} style={reqStyle(tried && !f.area)}>
                <option value="">Select account...</option>
                {RP.CUSTOMERS.filter(rpCustomerOrderReady).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <ReqMsg show={tried && !f.area}>Customer account is required</ReqMsg>
            </>
          ) : (
            <select className="field" value={f.area} onChange={e => set('area', e.target.value)}>
              <option value="">All / unassigned</option>
              {territoryOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          )}
        </MField>
      </div>
      {!isCustomer && <MField label={isFieldInventoryRole ? 'Assigned Micro-Warehouse' : 'Default Warehouse'}>
        <select className="field" value={f.defaultLocationId} onChange={e => set('defaultLocationId', e.target.value)}>
          <option value="">No default warehouse</option>
          {defaultLocationOptions.map(location => <option key={location.id} value={location.id}>{location.name || location.id}{location.territory ? ` - ${location.territory}` : ''}{location.type ? ` (${location.type})` : ''}</option>)}
        </select>
        {isFieldInventoryRole && !defaultLocationOptions.length && <ReqMsg show>Add or activate a micro-warehouse before assigning sales-rep inventory.</ReqMsg>}
      </MField>}
      <div style={{ marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Setup Method</div>
        <Segmented options={[{ value: 'magic_link', label: 'Setup Link' }, { value: 'temporary_password', label: 'Temp Password' }]} value={f.setupMode} onChange={value => set('setupMode', value)} />
        <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>
          {issuingPassword ? 'Creates or updates the login password and asks the user to choose their own password after sign-in.' : 'Emails a secure link for the user to finish account setup.'}
        </div>
      </div>
      {!canInviteUsers && <div style={{ marginTop: 2, marginBottom: 14, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Invites are restricted to darren@ranchpower.com in live mode.</div>}
      {error && <div style={{ marginTop: 2, marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      {tempResult && (
        <div style={{ marginTop: 2, marginBottom: 14, background: 'var(--ok-bg)', color: 'var(--ok)', borderRadius: 9, padding: '12px 13px', fontSize: 12.5, fontWeight: 700 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span>Temporary password</span>
            <button className="btn btn-sm" onClick={copyTemp}><Icon name="doc" size={14} />Copy</button>
          </div>
          <div className="mono" style={{ marginTop: 8, fontSize: 15, color: 'var(--ink)' }}>{tempResult.temporaryPassword}</div>
          <div style={{ marginTop: 8, color: 'var(--muted)', fontWeight: 600 }}>{tempResult.temporaryPasswordEmail && tempResult.temporaryPasswordEmail.sent ? `Email sent to ${tempResult.email}.` : 'Email was not sent; copy the password and send it manually.'}</div>
        </div>
      )}
      <div style={{ marginTop: 4 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Grants · {f.role}</div>
        {roleKey === 'accountant' && <InfoNote icon="reports">Opens Accounting with invoice, expense, inventory and Shopify reports, plus Excel and CSV exports. Inventory reporting is read-only.</InfoNote>}
        {isCustomer ? <InfoNote icon="customers">Customer accounts are linked to one customer record and open on order.ranchpower.com.</InfoNote> : caps.length === 0 ? <div style={{ fontSize: 13, color: 'var(--faint)' }}>Base read-only access.</div> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {caps.map(c => <span key={c.cap} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 500, color: 'var(--ink-soft)', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 100, padding: '6px 12px' }}><Icon name="check" size={13} style={{ color: 'var(--ok)' }} />{c.cap}</span>)}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ============================================================ PRICE LIST */
function AddPriceListModal({ onClose, onAdded, priceList }) {
  return <PriceListModal onClose={onClose} onAdded={onAdded} priceList={priceList} />;
}

function PriceListModal({ onClose, onAdded, priceList }) {
  const editing = !!priceList;
  const [f, setF] = useState({
    doors: priceList ? String(priceList.doors || 0) : '0',
    name: priceList ? priceList.name || '' : '',
    skuPrice: priceList ? String(priceList.skuPrice || '') : '',
    terms: priceList ? priceList.terms || 'Net 30' : 'Net 30',
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const price = parseFloat(f.skuPrice);
  const doors = parseInt(f.doors, 10) || 0;
  const name = f.name.trim();
  const duplicate = name && RP.PRICE_LISTS.some(p => p.name !== priceList?.name && p.name.toLowerCase() === name.toLowerCase());

  const save = async () => {
    setTried(true);
    setErr('');
    if (!name || !(price > 0) || duplicate) return;
    setBusy(true);

    try {
      let p;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/pricing/upsert', {
          method: 'POST',
          body: JSON.stringify({ id: priceList?.id, expectedUpdatedAt: priceList?.updatedAt, doors, name, skuPrice: price, terms: f.terms }),
        });
        p = response.priceList;
        const refreshed = await rpRefreshAfterMutation(response.created ? 'Price list added' : 'Price list saved');
        if (!refreshed && p) {
          const idx = RP.PRICE_LISTS.findIndex(x => p.id ? x.id === p.id : x.name === p.name);
          if (idx >= 0) RP.PRICE_LISTS.splice(idx, 1, p);
          else RP.PRICE_LISTS.push(p);
        }
      } else if (editing) {
        p = { ...priceList, aliases: [...(priceList.aliases || []), priceList.name, name], doors, name, skuPrice: price, terms: f.terms };
        const idx = RP.PRICE_LISTS.findIndex(x => x.name === priceList.name);
        if (idx >= 0) RP.PRICE_LISTS.splice(idx, 1, p);
      } else {
        p = RP.addPriceList({ doors, name, skuPrice: price, terms: f.terms });
      }

      toast(`Price list "${p.name}" ${editing ? 'saved' : 'created'}`);
      onAdded && onAdded(p);
      onClose();
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not save price list.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? 'Edit Price List' : 'Add Price List'} sub="A tier customers can be assigned to" onClose={onClose} width={500}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : editing ? 'Save Price List' : 'Add Price List'}</button></>}>
      <MField label="Tier Name" required>
        <input className="field" placeholder="Retailer Tier C" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && (!name || duplicate))} />
        <ReqMsg show={tried && !name}>Name is required</ReqMsg>
        <ReqMsg show={tried && duplicate}>That price list already exists. Use Edit on the row.</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="12-Pack Price" half>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
            <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && !(price > 0)))} type="number" step="0.01" placeholder="0.00" value={f.skuPrice} onChange={e => set('skuPrice', e.target.value)} />
          </div>
          <ReqMsg show={tried && !(price > 0)}>Enter a positive price</ReqMsg>
        </MField>
        <MField label="Assigned Doors" half><input className="field" type="number" min="0" value={f.doors} onChange={e => set('doors', e.target.value)} /></MField>
      </div>
      <MField label="Default Terms"><select className="field" value={f.terms} onChange={e => set('terms', e.target.value)}>{['Prepaid', 'Net 7', 'Net 15', 'Net 30', 'Distributor Terms'].map(t => <option key={t}>{t}</option>)}</select></MField>
      {err && <ReqMsg show>{err}</ReqMsg>}
    </Modal>
  );
}

function LegacyAddPriceListModal({ onClose, onAdded }) {
  const [f, setF] = useState({ name: '', skuPrice: '', terms: 'Net 30' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const price = parseFloat(f.skuPrice);
  const valid = f.name.trim() && price > 0;
  const save = () => {
    setTried(true);
    if (!valid) return;
    const p = RP.addPriceList({ name: f.name.trim(), skuPrice: price, terms: f.terms });
    toast(`Price list "${p.name}" created`);
    onAdded && onAdded(p);
    onClose();
  };
  return (
    <Modal title="Add Price List" sub="A tier customers can be assigned to" onClose={onClose} width={480}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Create List</button></>}>
      <MField label="List Name" required>
        <input className="field" placeholder="e.g. National Chain" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !f.name.trim())} />
        <ReqMsg show={tried && !f.name.trim()}>List name is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="12-Pack Price" half>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
            <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && !(price > 0)))} type="number" step="0.01" placeholder="0.00" value={f.skuPrice} onChange={e => set('skuPrice', e.target.value)} />
          </div>
          <ReqMsg show={tried && !(price > 0)}>Enter a price</ReqMsg>
        </MField>
        <MField label="Default Terms" half><select className="field" value={f.terms} onChange={e => set('terms', e.target.value)}>{['Prepaid', 'Net 15', 'Net 30', 'Net 45'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
    </Modal>
  );
}

/* ============================================================ TERRITORY */
function territoryIdFromName(value) {
  return String(value || 'TERRITORY')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64) || 'TERRITORY';
}

function syncTerritoryArrays(territory) {
  if (!territory || !territory.name) return territory;
  if (!Array.isArray(RP.TERRITORY_RECORDS)) RP.TERRITORY_RECORDS = [];
  if (!Array.isArray(RP.TERRITORIES)) RP.TERRITORIES = [];

  const record = {
    active: territory.active !== false,
    centerLat: territory.centerLat == null ? null : Number(territory.centerLat),
    centerLng: territory.centerLng == null ? null : Number(territory.centerLng),
    id: territory.id || territoryIdFromName(territory.name),
    locatorGroupToleranceMeters: Number(territory.locatorGroupToleranceMeters || 35),
    name: territory.name,
    sortOrder: Number(territory.sortOrder || 0)
  };
  const idx = RP.TERRITORY_RECORDS.findIndex(item => (item.id || territoryIdFromName(item.name)) === record.id);
  if (idx >= 0) RP.TERRITORY_RECORDS.splice(idx, 1, record);
  else RP.TERRITORY_RECORDS.push(record);

  const names = RP.TERRITORY_RECORDS
    .filter(item => item && item.active !== false && item.name)
    .sort((a, b) => (Number(a.sortOrder || 0) - Number(b.sortOrder || 0)) || String(a.name || '').localeCompare(String(b.name || '')))
    .map(item => item.name);
  RP.TERRITORIES.splice(0, RP.TERRITORIES.length, ...names);
  return record;
}

function AddTerritoryModal({ onClose, onAdded, territory }) {
  const editing = !!territory;
  const [f, setF] = useState({
    active: territory ? territory.active !== false : true,
    centerLat: territory && territory.centerLat != null ? String(territory.centerLat) : '',
    centerLng: territory && territory.centerLng != null ? String(territory.centerLng) : '',
    id: territory ? territory.id || territoryIdFromName(territory.name) : '',
    locatorGroupToleranceMeters: territory ? String(territory.locatorGroupToleranceMeters || 35) : '35',
    name: territory ? territory.name || '' : '',
    sortOrder: territory ? String(territory.sortOrder || 0) : String((Array.isArray(RP.TERRITORY_RECORDS) ? RP.TERRITORY_RECORDS : []).reduce((max, item) => Math.max(max, Number(item.sortOrder || 0)), 0) + 10)
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const name = f.name.trim();
  const id = territoryIdFromName(f.id || name);
  const sortOrder = f.sortOrder === '' ? 0 : Number(f.sortOrder);
  const centerLatText = String(f.centerLat || '').trim();
  const centerLngText = String(f.centerLng || '').trim();
  const centerLat = centerLatText ? Number(centerLatText) : null;
  const centerLng = centerLngText ? Number(centerLngText) : null;
  const centerPairIncomplete = Boolean(centerLatText) !== Boolean(centerLngText);
  const centerLatInvalid = Boolean(centerLatText) && (!Number.isFinite(centerLat) || centerLat < -90 || centerLat > 90);
  const centerLngInvalid = Boolean(centerLngText) && (!Number.isFinite(centerLng) || centerLng < -180 || centerLng > 180);
  const locatorGroupToleranceMeters = f.locatorGroupToleranceMeters === '' ? 35 : Number(f.locatorGroupToleranceMeters);
  const toleranceInvalid = !Number.isFinite(locatorGroupToleranceMeters) || locatorGroupToleranceMeters < 5 || locatorGroupToleranceMeters > 5000;
  const records = Array.isArray(RP.TERRITORY_RECORDS) ? RP.TERRITORY_RECORDS : [];
  const duplicateId = !editing && records.some(item => (item.id || territoryIdFromName(item.name)) === id);
  const duplicateName = records.some(item => {
    const itemId = item.id || territoryIdFromName(item.name);
    return itemId !== id && String(item.name || '').trim().toLowerCase() === name.toLowerCase();
  });

  const save = async () => {
    setTried(true);
    setErr('');
    if (!name || duplicateId || duplicateName || !Number.isFinite(sortOrder) || centerPairIncomplete || centerLatInvalid || centerLngInvalid || toleranceInvalid || busy) return;
    setBusy(true);
    try {
      let saved;
      let renamedCount = 0;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/territories/upsert', {
          method: 'POST',
          body: JSON.stringify({ active: f.active, centerLat, centerLng, id, locatorGroupToleranceMeters, name, previousName: territory && territory.name ? territory.name : '', sortOrder })
        });
        saved = response.territory;
        renamedCount = Object.values((response && response.renamedReferences) || {}).reduce((sum, value) => sum + Number(value || 0), 0);
        const refreshed = await rpRefreshAfterMutation(response.created ? 'Territory added' : 'Territory saved');
        if (!refreshed && saved) syncTerritoryArrays(saved);
      } else if (typeof RP.addTerritory === 'function') {
        saved = RP.addTerritory({ active: f.active, centerLat, centerLng, id, locatorGroupToleranceMeters, name, sortOrder });
      } else {
        saved = syncTerritoryArrays({ active: f.active, centerLat, centerLng, id, locatorGroupToleranceMeters, name, sortOrder });
      }
      toast(`Territory "${saved.name}" ${editing ? 'saved' : 'created'}${renamedCount ? ` - ${renamedCount} records updated` : ''}`);
      onAdded && onAdded(saved);
      onClose();
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not save territory.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? 'Edit Territory' : 'Add Territory'} sub="Controls assignment options across staff, customers, locations and field workflows" onClose={onClose} width={520}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : editing ? 'Save Territory' : 'Add Territory'}</button></>}>
      <MField label="Territory Name" required>
        <input className="field" placeholder="e.g. Vancouver Island" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && (!name || duplicateName))} />
        <ReqMsg show={tried && !name}>Territory name is required</ReqMsg>
        <ReqMsg show={tried && duplicateName}>That territory name already exists.</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Territory ID" half>
          <input className="field mono" disabled={editing} placeholder="AUTO" value={f.id} onChange={e => set('id', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} style={reqStyle(tried && duplicateId)} />
          <ReqMsg show={tried && duplicateId}>That territory ID already exists.</ReqMsg>
        </MField>
        <MField label="Sort Order" half>
          <input className="field" type="number" min="0" step="1" placeholder="0" value={f.sortOrder} onChange={e => set('sortOrder', e.target.value)} style={reqStyle(tried && !Number.isFinite(sortOrder))} />
          <ReqMsg show={tried && !Number.isFinite(sortOrder)}>Enter a whole number</ReqMsg>
        </MField>
      </div>
      <MField label="Status">
        <select className="field" value={f.active ? 'active' : 'inactive'} onChange={e => set('active', e.target.value === 'active')}>
          <option value="active">Active - available in assignment dropdowns</option>
          <option value="inactive">Inactive - hidden from new assignments</option>
        </select>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Map Center Lat" half>
          <input className="field mono" type="number" min="-90" max="90" step="0.00000001" placeholder="49.70000000" value={f.centerLat} onChange={e => set('centerLat', e.target.value)} style={reqStyle(tried && (centerPairIncomplete || centerLatInvalid))} />
          <ReqMsg show={tried && centerPairIncomplete}>Enter latitude and longitude together.</ReqMsg>
          <ReqMsg show={tried && centerLatInvalid}>Latitude must be between -90 and 90.</ReqMsg>
        </MField>
        <MField label="Map Center Lng" half>
          <input className="field mono" type="number" min="-180" max="180" step="0.00000001" placeholder="-123.16000000" value={f.centerLng} onChange={e => set('centerLng', e.target.value)} style={reqStyle(tried && (centerPairIncomplete || centerLngInvalid))} />
          <ReqMsg show={tried && centerLngInvalid}>Longitude must be between -180 and 180.</ReqMsg>
        </MField>
      </div>
      <MField label="Locator Grouping Tolerance">
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <input className="field" type="number" min="5" max="5000" step="1" value={f.locatorGroupToleranceMeters} onChange={e => set('locatorGroupToleranceMeters', e.target.value)} style={Object.assign({ flex: 1 }, reqStyle(tried && toleranceInvalid))} />
          <span className="mono" style={{ color: 'var(--muted)', fontWeight: 700 }}>meters</span>
        </div>
        <ReqMsg show={tried && toleranceInvalid}>Use 5 to 5000 meters.</ReqMsg>
      </MField>
      <InfoNote icon="pin">Renaming a territory updates existing staff, customers, locations, orders, leads, stock requests and goals. Inactivating a territory removes it from new assignment choices.</InfoNote>
      {err && <ReqMsg show>{err}</ReqMsg>}
    </Modal>
  );
}

/* ============================================================ APPROVAL RULE */
function approvalRuleId(rule) {
  return String(rule || 'APPROVAL_RULE')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64) || 'APPROVAL_RULE';
}

function AddApprovalRuleModal({ onClose, onAdded, approvalRule }) {
  const editing = !!approvalRule;
  const [f, setF] = useState({
    active: approvalRule ? approvalRule.active !== false : true,
    id: approvalRule ? approvalRule.id || approvalRuleId(approvalRule.rule) : '',
    rule: approvalRule ? approvalRule.rule || '' : '',
    route: approvalRule ? approvalRule.route || 'Operations' : 'Operations'
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const rule = f.rule.trim();
  const id = f.id || approvalRuleId(rule);
  const duplicate = !editing && rule && RP.APPROVAL_RULES.some(r => String(r.rule || '').toLowerCase() === rule.toLowerCase());
  const save = async () => {
    setTried(true);
    setErr('');
    if (!rule || duplicate || busy) return;
    setBusy(true);
    try {
      let r;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/approval-rules/upsert', {
          method: 'POST',
          body: JSON.stringify({ active: f.active, id, rule, route: f.route })
        });
        r = response.approvalRule;
        const refreshed = await rpRefreshAfterMutation(response.created ? 'Approval rule added' : 'Approval rule saved');
        if (!refreshed && r) {
          const idx = RP.APPROVAL_RULES.findIndex(x => (x.id || approvalRuleId(x.rule)) === r.id);
          if (idx >= 0) RP.APPROVAL_RULES.splice(idx, 1, r);
          else RP.APPROVAL_RULES.push(r);
        }
      } else if (editing) {
        r = { ...approvalRule, active: f.active, id, rule, route: f.route };
        const idx = RP.APPROVAL_RULES.findIndex(x => x === approvalRule || (x.id && x.id === approvalRule.id) || x.rule === approvalRule.rule);
        if (idx >= 0) RP.APPROVAL_RULES.splice(idx, 1, r);
      } else {
        r = RP.addApprovalRule({ active: f.active, id, rule, route: f.route });
      }
      toast(`Approval rule ${editing ? 'saved' : 'added'} - routes to ${r.route}`);
      onAdded && onAdded(r);
      onClose();
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not save approval rule.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={editing ? 'Edit Approval Rule' : 'Add Approval Rule'} sub="Route exceptions to the right approver" onClose={onClose} width={520}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : editing ? 'Save Rule' : 'Add Rule'}</button></>}>
      <MField label="When (condition)" required>
        <input className="field" placeholder="e.g. Order value above $10,000" value={f.rule} onChange={e => set('rule', e.target.value)} style={reqStyle(tried && (!rule || duplicate))} />
        <ReqMsg show={tried && !rule}>Describe the condition</ReqMsg>
        <ReqMsg show={tried && duplicate}>That rule already exists. Use Edit on the row.</ReqMsg>
      </MField>
      <MField label="Routes To"><select className="field" value={f.route} onChange={e => set('route', e.target.value)}>{['Sales Manager', 'Operations', 'Leadership', 'Finance'].map(t => <option key={t}>{t}</option>)}</select></MField>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
        <input type="checkbox" checked={f.active} onChange={e => set('active', e.target.checked)} />
        Active
      </label>
      <InfoNote icon="flag">Matching events pause and wait for sign-off from <b style={{ marginLeft: 2 }}>{f.route}</b> before proceeding.</InfoNote>
      {err && <ReqMsg show>{err}</ReqMsg>}
    </Modal>
  );
}

function LegacyAddApprovalRuleModal({ onClose, onAdded }) {
  const [f, setF] = useState({ rule: '', route: 'Operations' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const save = () => {
    setTried(true);
    if (!f.rule.trim()) return;
    const r = RP.addApprovalRule({ rule: f.rule.trim(), route: f.route });
    toast(`Approval rule added · routes to ${r.route}`);
    onAdded && onAdded(r);
    onClose();
  };
  return (
    <Modal title="Add Approval Rule" sub="Route exceptions to the right approver" onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Add Rule</button></>}>
      <MField label="When (condition)" required>
        <input className="field" placeholder="e.g. Order value above $10,000" value={f.rule} onChange={e => set('rule', e.target.value)} style={reqStyle(tried && !f.rule.trim())} />
        <ReqMsg show={tried && !f.rule.trim()}>Describe the condition</ReqMsg>
      </MField>
      <MField label="Routes To"><select className="field" value={f.route} onChange={e => set('route', e.target.value)}>{['Sales Manager', 'Operations', 'Leadership', 'Finance'].map(t => <option key={t}>{t}</option>)}</select></MField>
      <InfoNote icon="flag">Matching events pause and wait for sign-off from <b style={{ marginLeft: 2 }}>{f.route}</b> before proceeding.</InfoNote>
    </Modal>
  );
}

/* ============================================================ TRANSFER ORDER */
function transferLotOptions(skuId, locationId) {
  const canonical = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(skuId) : skuId;
  const location = RP.loc(locationId);
  const statuses = new Set(location && location.type === 'Micro-Warehouse'
    ? ['Available', 'Fronted to Rep', 'Purchased by Rep']
    : ['Available']);
  const lots = new Map();
  (RP.INV || [])
    .filter(row => row && row.loc === locationId && statuses.has(row.status || 'Available'))
    .filter(row => (typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(row.sku) : row.sku) === canonical)
    .forEach(row => {
      const lot = String(row.lot || 'UNASSIGNED');
      const current = lots.get(lot) || { lot, quantity: 0, expiresAt: row.lotExpiresAt || row.exp || '' };
      current.quantity += Number(row.qty || 0);
      current.expiresAt = current.expiresAt || row.lotExpiresAt || row.exp || '';
      lots.set(lot, current);
    });
  return Array.from(lots.values())
    .filter(row => row.quantity > 0)
    .sort((a, b) => (a.expiresAt || '9999-12-31').localeCompare(b.expiresAt || '9999-12-31') || a.lot.localeCompare(b.lot));
}

function transferClientRef() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return `TO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
  return `TO-${Date.now().toString(36).toUpperCase()}`;
}

function transferLocalDateTime() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function TransferOrderModal({ onClose, onAdded, presetFrom, presetTo }) {
  const movableTypes = new Set(['Main Warehouse', 'Micro-Warehouse', 'Distributor', 'Production Facility', '3PL Hub', '3PL Warehouse', 'FBA']);
  const movable = RP.LOCATIONS.filter(l => l.active !== false && movableTypes.has(l.type));
  const transferSkus = rpInventorySkus();
  const defaultSku = transferSkus[0] ? transferSkus[0].id : '';
  const newLine = sku => ({ id: Date.now() + Math.random(), lot: '', qty: '', sku: sku || defaultSku });
  const requestedFrom = presetFrom && movable.some(location => location.id === presetFrom) ? presetFrom : '';
  const requestedTo = presetTo && movable.some(location => location.id === presetTo) ? presetTo : '';
  const defaultFrom = requestedFrom || (movable.find(location => location.id === 'WH-NBC' && location.id !== requestedTo) || movable.find(location => location.id !== requestedTo) || movable[0] || {}).id || '';
  const defaultTo = (requestedTo && requestedTo !== defaultFrom ? requestedTo : '') || (movable.find(location => location.id === 'WH-SQ' && location.id !== defaultFrom) || movable.find(location => location.id !== defaultFrom) || movable[0] || {}).id || '';
  const [f, setF] = useState({ bol: '', carrier: '', chepPalletCount: '', chepUsage: '', from: defaultFrom, handledBy: '', method: 'internal_transfer', notes: '', occurredAt: transferLocalDateTime(), ref: transferClientRef(), to: defaultTo, trackingNumber: '' });
  const [lines, setLines] = useState(() => [newLine(defaultSku)]);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const normalizedLines = lines.map(line => ({ ...line, lot: String(line.lot || '').trim(), quantity: parseInt(line.qty, 10) || 0 }));
  const requestedByHolding = normalizedLines.reduce((map, line) => {
    if (!line.sku) return map;
    const key = `${line.sku}|${line.lot || '*'}`;
    map[key] = (map[key] || 0) + line.quantity;
    return map;
  }, {});
  const sameLoc = f.from === f.to;
  const holdingAvailability = (skuId, lot) => {
    const options = transferLotOptions(skuId, f.from);
    return lot ? Number((options.find(option => option.lot === lot) || {}).quantity || 0) : options.reduce((sum, option) => sum + option.quantity, 0);
  };
  const overdraws = Object.entries(requestedByHolding)
    .filter(([, requested]) => requested > 0)
    .filter(([key, requested]) => {
      const [skuId, lot] = key.split('|');
      return requested > holdingAvailability(skuId, lot === '*' ? '' : lot);
    });
  const missingLotLine = normalizedLines.find(line => !line.lot);
  const pickupNeedsHandler = ['direct_pickup', 'rep_pickup'].includes(f.method) && !f.handledBy.trim();
  const freightNeedsCarrier = ['carrier_freight', 'courier'].includes(f.method) && !f.carrier.trim();
  const chepPalletCount = Number(f.chepPalletCount);
  const chepMissing = !['used', 'not_used'].includes(f.chepUsage);
  const chepCountInvalid = f.chepUsage === 'used' && (!Number.isInteger(chepPalletCount) || chepPalletCount <= 0);
  const invalidLine = normalizedLines.find(line => !line.sku || !(line.quantity > 0));
  const totalCases = normalizedLines.reduce((sum, line) => sum + line.quantity, 0);
  const valid = normalizedLines.length > 0 && totalCases > 0 && !invalidLine && !missingLotLine && !pickupNeedsHandler && !freightNeedsCarrier && !chepMissing && !chepCountInvalid && !!f.occurredAt && !sameLoc && !overdraws.length;
  const setLine = (lineId, key, value) => setLines(list => list.map(line => line.id === lineId ? { ...line, [key]: value, ...(key === 'sku' ? { lot: '' } : {}) } : line));
  const addLine = () => {
    const used = new Set(lines.map(line => line.sku));
    const nextSku = (transferSkus.find(sku => !used.has(sku.id)) || transferSkus[0] || {}).id || '';
    setLines(list => list.length >= 25 ? list : [...list, newLine(nextSku)]);
  };
  const removeLine = lineId => setLines(list => list.length > 1 ? list.filter(line => line.id !== lineId) : list);
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      let serverRef = '';
      let refreshed = false;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/inventory/transfer', {
          method: 'POST',
          body: JSON.stringify({
            bol: f.bol.trim() || undefined,
            carrier: f.carrier.trim() || undefined,
            chepPalletCount: f.chepUsage === 'used' ? chepPalletCount : 0,
            chepUsage: f.chepUsage,
            fromLocationId: f.from,
            handledBy: f.handledBy.trim() || undefined,
            lines: normalizedLines.map(line => ({ lot: line.lot || undefined, quantity: line.quantity, skuId: line.sku })),
            movementMethod: f.method,
            notes: f.notes.trim() || undefined,
            occurredAt: new Date(f.occurredAt).toISOString(),
            ref: f.ref,
            trackingNumber: f.trackingNumber.trim() || undefined,
            toLocationId: f.to
          })
        });
        serverRef = response.ref || '';
        refreshed = await rpRefreshAfterMutation(`Transfer ${serverRef || 'saved'}`);
      }
      let ref = serverRef;
      if (!refreshed) {
        normalizedLines.forEach(line => {
          const lineRef = RP.addTransfer({ sku: line.sku, lot: line.lot || undefined, from: f.from, to: f.to, qty: line.quantity, ref: ref || undefined });
          if (!ref) ref = lineRef;
        });
      }
      const skuLabel = normalizedLines.length === 1 ? rpSkuDisplayName(normalizedLines[0].sku) : `${normalizedLines.length} products`;
      toast(`Completed transfer ${ref} recorded - ${totalCases} cs ${skuLabel}`);
      onAdded && onAdded(ref);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not record transfer.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Record Completed Transfer" sub="Record the actual custody handoff; destination inventory is credited immediately" onClose={onClose} width={820}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name="layers" size={15} />{busy ? 'Recording...' : `Record Transfer - ${totalCases} cs`}</button></>}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end' }}>
        <MField label="From" half><select className="field" value={f.from} onChange={e => { set('from', e.target.value); setLines(list => list.map(line => ({ ...line, lot: '' }))); }} style={reqStyle(tried && sameLoc)}>{movable.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></MField>
        <div style={{ paddingBottom: 24, color: 'var(--faint)' }}><Icon name="arrowR" size={18} /></div>
        <MField label="To" half><select className="field" value={f.to} onChange={e => set('to', e.target.value)} style={reqStyle(tried && sameLoc)}>{movable.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></MField>
      </div>
      <ReqMsg show={tried && sameLoc}>Source and destination must differ</ReqMsg>
      <div className="card" style={{ padding: 12, marginTop: 12, background: 'var(--surface-2)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(150px,1fr))', gap: 10 }}>
          <MField label="Movement"><select className="field" value={f.method} onChange={e => set('method', e.target.value)}><option value="internal_transfer">Internal Transfer</option><option value="carrier_freight">Carrier / Freight</option><option value="courier">Courier</option><option value="direct_pickup">Direct Pickup</option><option value="rep_pickup">Rep Pickup</option></select></MField>
          <MField label="Handled By"><input className="field" value={f.handledBy} onChange={e => set('handledBy', e.target.value)} placeholder="Driver / rep" style={reqStyle(tried && pickupNeedsHandler)} /></MField>
          <MField label="Carrier"><input className="field" value={f.carrier} onChange={e => set('carrier', e.target.value)} placeholder="Required for freight" style={reqStyle(tried && freightNeedsCarrier)} /></MField>
          <MField label="Transfer Ref"><input className="field mono" value={f.ref} onChange={e => set('ref', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} /></MField>
          <MField label="Handoff Time"><input className="field" type="datetime-local" value={f.occurredAt} onChange={e => set('occurredAt', e.target.value)} style={reqStyle(tried && !f.occurredAt)} /></MField>
          <MField label="BOL"><input className="field mono" value={f.bol} onChange={e => set('bol', e.target.value)} placeholder="Optional" /></MField>
          <MField label="Tracking / PRO"><input className="field mono" value={f.trackingNumber} onChange={e => set('trackingNumber', e.target.value)} placeholder="Optional" /></MField>
          <MField label="CHEP Pallets"><select className="field" value={f.chepUsage} onChange={e => set('chepUsage', e.target.value)} style={reqStyle(tried && chepMissing)}><option value="">Select Yes / No</option><option value="used">Yes - CHEP used</option><option value="not_used">No CHEP pallets</option></select></MField>
          <MField label="CHEP Pallet Count"><input className="field mono" type="number" min="1" step="1" disabled={f.chepUsage !== 'used'} value={f.chepPalletCount} onChange={e => set('chepPalletCount', e.target.value)} placeholder={f.chepUsage === 'used' ? 'Required' : '0'} style={reqStyle(tried && chepCountInvalid)} /></MField>
          <div className="rp-transfer-notes" style={{ gridColumn: 'span 3' }}><MField label="Notes"><input className="field" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Route, pickup context, evidence reference..." /></MField></div>
        </div>
        <ReqMsg show={tried && pickupNeedsHandler}>Direct and rep pickups require the person who handled the stock.</ReqMsg>
        <ReqMsg show={tried && freightNeedsCarrier}>Freight and courier movements require the carrier's name. BOL and tracking references remain optional evidence.</ReqMsg>
        <ReqMsg show={tried && chepMissing}>Confirm explicitly whether this load used CHEP pallets.</ReqMsg>
        <ReqMsg show={tried && chepCountInvalid}>Enter the positive whole number of CHEP pallets that moved with the load.</ReqMsg>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '14px 0 8px' }}>
        <div className="eyebrow">Items</div>
        <button className="btn btn-sm" onClick={addLine} disabled={busy || lines.length >= 25}><Icon name="plus" size={14} />Add Product</button>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {normalizedLines.map((line, index) => {
          const lotOptions = transferLotOptions(line.sku, f.from);
          const available = holdingAvailability(line.sku, line.lot);
          const requested = requestedByHolding[`${line.sku}|${line.lot || '*'}`] || 0;
          const overdraw = line.sku && requested > available;
          const lotRequired = !line.lot;
          return (
            <div key={line.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(175px,.75fr) 105px 105px 42px', gap: 8, alignItems: 'start' }}>
              <select className="field" value={line.sku} onChange={e => setLine(line.id, 'sku', e.target.value)} style={reqStyle(tried && !line.sku)}>
                {transferSkus.map(s => <option key={s.id} value={s.id}>{rpSkuDisplayName(s)}</option>)}
              </select>
              <div><select className="field mono" value={line.lot} onChange={e => setLine(line.id, 'lot', e.target.value)} style={reqStyle(tried && lotRequired)}><option value="">Select exact lot</option>{lotOptions.map(option => <option key={option.lot} value={option.lot}>{rpPrintedLotLabel(line.sku, option.lot)} - {option.quantity} cs{option.expiresAt ? ` - exp ${option.expiresAt}` : ''}</option>)}</select><ReqMsg show={tried && lotRequired}>Required</ReqMsg></div>
              <div>
                <input className="field" type="number" min="0" placeholder="Cases" value={line.qty} onChange={e => setLine(line.id, 'qty', e.target.value)} style={reqStyle(tried && (!(line.quantity > 0) || overdraw))} />
                <ReqMsg show={tried && !(line.quantity > 0)}>Required</ReqMsg>
              </div>
              <div style={{ minHeight: 52, border: '1px solid var(--line)', borderRadius: 8, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '6px 9px', background: 'var(--surface-2)' }}>
                <span className="mono" style={{ color: overdraw ? 'var(--danger)' : 'var(--ink)', fontWeight: 800, fontSize: 13 }}>{available} cs</span>
                <span style={{ color: 'var(--faint)', fontSize: 10.5 }}>available</span>
              </div>
              <button className="btn btn-sm" disabled={busy || lines.length === 1} onClick={() => removeLine(line.id)} aria-label={`Remove line ${index + 1}`} style={{ height: 52, padding: 0 }}><Icon name="x" size={15} /></button>
            </div>
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, minHeight: 18 }}>
          <ReqMsg show={tried && !!invalidLine}>Every line needs a product and case count</ReqMsg>
          <ReqMsg show={tried && !!missingLotLine}>Select the exact lot for every product.</ReqMsg>
          <ReqMsg show={!!overdraws.length}>{overdraws.map(([key, requested]) => { const [skuId, lot] = key.split('|'); return `${rpSkuDisplayName(skuId)}${lot !== '*' ? ` ${lot}` : ''}: ${requested}/${holdingAvailability(skuId, lot === '*' ? '' : lot)} cs`; }).join(' / ')}</ReqMsg>
        </div>
      </div>
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

/* ============================================================ LIVE PROCUREMENT OVERRIDES */
function AddSupplierModal({ onClose, onAdded }) {
  const [f, setF] = useState({ name: '', category: 'Cans', country: 'Canada', terms: 'Net 30', contact: '' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const valid = f.name.trim().length > 1;
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      let supplier;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/procurement/supplier', {
          method: 'POST',
          body: JSON.stringify({
            category: f.category,
            contact: f.contact.trim(),
            country: f.country,
            name: f.name.trim(),
            terms: f.terms
          })
        });
        supplier = response.supplier;
        const refreshed = await rpRefreshAfterMutation(`${supplier.name} added`);
        if (!refreshed && supplier) RP.SUPPLIERS.unshift(supplier);
      } else {
        supplier = RP.addSupplier({ name: f.name.trim(), category: f.category, country: f.country, terms: f.terms, contact: f.contact.trim() || '-' });
      }
      toast(`${supplier.name} added to suppliers - ${supplier.id}`);
      onAdded && onAdded(supplier);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save supplier.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Add Supplier" sub="Vendors and co-packers for materials and runs" onClose={onClose} width={520}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name={busy ? 'refresh' : 'check'} size={15} />{busy ? 'Saving...' : 'Add Supplier'}</button></>}>
      <MField label="Supplier Name" required>
        <input className="field" placeholder="e.g. Okanagan Glass & Can" value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !valid)} />
        <ReqMsg show={tried && !valid}>Supplier name is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Supplies" half><select className="field" value={f.category} onChange={e => set('category', e.target.value)}>{['Cans', 'Lids', 'Ingredient', 'Co-Packer', 'Packaging', 'Freight', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Country of Origin" half><select className="field" value={f.country} onChange={e => set('country', e.target.value)}>{['Canada', 'USA', 'China', 'Mexico', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Payment Terms" half><select className="field" value={f.terms} onChange={e => set('terms', e.target.value)}>{['Net 15', 'Net 30', 'Net 45', 'Prepaid'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Contact Email" half><input className="field" placeholder="orders@vendor.com" value={f.contact} onChange={e => set('contact', e.target.value)} /></MField>
      </div>
      {f.country !== 'Canada' && <InfoNote icon="alert">Non-Canada origin materials from this supplier carry tariff exposure on finished-goods lots.</InfoNote>}
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function AddMaterialModal({ onClose, onAdded }) {
  const suppliers = Array.isArray(RP.SUPPLIERS) ? RP.SUPPLIERS : [];
  const firstSupplier = suppliers[0] || null;
  const [f, setF] = useState({ material: '', kind: 'Ingredient', supplier: firstSupplier ? firstSupplier.name : '', origin: firstSupplier ? firstSupplier.country : 'Canada', hs: '', duty: '0%' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const valid = f.material.trim().length > 1;
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      let material;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/procurement/material', {
          method: 'POST',
          body: JSON.stringify({
            duty: f.duty,
            hsCode: f.hs.trim(),
            kind: f.kind,
            material: f.material.trim(),
            origin: f.origin,
            supplierName: f.supplier
          })
        });
        material = response.material;
        const refreshed = await rpRefreshAfterMutation(`${material.material} added`);
        if (!refreshed && material) RP.MATERIALS.unshift(material);
      } else {
        const prefix = { Ingredient: 'ING', Can: 'CAN', Lid: 'LID', Packaging: 'PKG' }[f.kind] || 'MAT';
        const lot = prefix + '-' + String(Math.floor(Math.random() * 900) + 100);
        material = RP.addMaterial({ lot, material: f.material.trim(), supplier: f.supplier || 'Unassigned supplier', origin: f.origin, hs: f.hs.trim() || '-', duty: f.duty });
      }
      toast(`${material.material} added - lot ${material.lot}`);
      onAdded && onAdded(material);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save material.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Add Material" sub="Ingredients and packaging inputs with origin and tariff" onClose={onClose} width={540}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name={busy ? 'refresh' : 'check'} size={15} />{busy ? 'Saving...' : 'Add Material'}</button></>}>
      <MField label="Material Name" required>
        <input className="field" placeholder="e.g. Blood Orange flavour" value={f.material} onChange={e => set('material', e.target.value)} style={reqStyle(tried && !valid)} />
        <ReqMsg show={tried && !valid}>Material name is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Type" half><select className="field" value={f.kind} onChange={e => set('kind', e.target.value)}>{['Ingredient', 'Can', 'Lid', 'Packaging'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Supplier" half><select className="field" value={f.supplier} onChange={e => set('supplier', e.target.value)}><option value="">Unassigned supplier</option>{suppliers.map(s => <option key={s.id}>{s.name}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Origin" half><select className="field" value={f.origin} onChange={e => set('origin', e.target.value)}>{['Canada', 'USA', 'China', 'Mexico', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="HS Code" half><input className="field mono" placeholder="e.g. 2106.90" value={f.hs} onChange={e => set('hs', e.target.value)} /></MField>
      </div>
      <MField label="Duty Rate"><select className="field" value={f.duty} onChange={e => set('duty', e.target.value)}>{['0%', 'N/A', '7.5%', '12%', 'Review', 'TBD'].map(t => <option key={t}>{t}</option>)}</select></MField>
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function NewPurchaseOrderModal({ onClose, onAdded, presetItem }) {
  const suppliers = Array.isArray(RP.SUPPLIERS) ? RP.SUPPLIERS : [];
  const p0 = presetItem || {};
  const sup0 = suppliers.find(s => s.name === p0.supplier) || suppliers[0] || null;
  const [f, setF] = useState({ supplier: sup0 ? sup0.name : '', category: p0.category || 'Cans', item: p0.name || '', ordered: '', unit: '', origin: sup0 ? sup0.country : 'Canada', eta: '2026-06-30', terms: sup0 ? sup0.terms : 'Net 30' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const qty = parseInt(f.ordered, 10);
  const unit = parseFloat(f.unit);
  const value = (qty > 0 && unit > 0) ? qty * unit : 0;
  const valid = Boolean(f.supplier && f.item.trim() && qty > 0 && unit > 0);
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      let po;
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/procurement/upsert-po', {
          method: 'POST',
          body: JSON.stringify({
            category: f.category,
            eta: f.eta,
            items: [{ name: f.item.trim(), ordered: qty, unitCost: unit, origin: f.origin, tariff: f.origin === 'Canada' ? 'N/A' : 'Review' }],
            risk: f.origin === 'China' ? 'Medium' : 'Low',
            supplierName: f.supplier,
            terms: f.terms
          })
        });
        po = response.purchaseOrder;
        const refreshed = await rpRefreshAfterMutation(`${po.id} created`);
        if (!refreshed && po) RP.PURCHASE_ORDERS.unshift(po);
      } else {
        po = RP.addPurchaseOrder({
          supplier: f.supplier, category: f.category, eta: f.eta, terms: f.terms,
          risk: f.origin === 'China' ? 'Medium' : 'Low',
          items: [{ name: f.item.trim(), ordered: qty, recvd: 0, unit, origin: f.origin, tariff: f.origin === 'Canada' ? 'N/A' : 'Review' }],
        });
      }
      toast(`${po.id} created - ${RP.money(value)} to ${po.supplier}`);
      onAdded && onAdded(po);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create purchase order.');
    } finally {
      setBusy(false);
    }
  };
  const supObj = suppliers.find(s => s.name === f.supplier);
  return (
    <Modal title="New Purchase Order" sub="Order materials from a supplier" onClose={onClose} width={580}
      footer={<><div style={{ flex: 1, alignSelf: 'center' }}>{value > 0 && <span style={{ fontSize: 13, color: 'var(--muted)' }}><b className="disp" style={{ fontSize: 18, color: 'var(--ink)' }}>{RP.money(value)}</b> order value</span>}</div><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name={busy ? 'refresh' : 'check'} size={15} />{busy ? 'Creating...' : 'Create PO'}</button></>}>
      {!suppliers.length && <InfoNote icon="alert">Add a supplier before creating a live purchase order.</InfoNote>}
      <div style={{ display: 'flex', gap: 14, marginTop: suppliers.length ? 0 : 14 }}>
        <MField label="Supplier" half><select className="field" value={f.supplier} onChange={e => { const s = suppliers.find(x => x.name === e.target.value); set('supplier', e.target.value); if (s) setF(st => ({ ...st, supplier: e.target.value, terms: s.terms, origin: s.country, category: s.category === 'Co-Packer' ? st.category : s.category })); }}>{suppliers.map(s => <option key={s.id}>{s.name}</option>)}</select></MField>
        <MField label="Category" half><select className="field" value={f.category} onChange={e => set('category', e.target.value)}>{['Cans', 'Lids', 'Ingredient', 'Packaging', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <MField label="Item" required>
        <input className="field" placeholder="e.g. 355ml Cans" value={f.item} onChange={e => set('item', e.target.value)} style={reqStyle(tried && !f.item.trim())} />
        <ReqMsg show={tried && !f.item.trim()}>Item description is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Quantity" half>
          <input className="field" type="number" placeholder="e.g. 24000" value={f.ordered} onChange={e => set('ordered', e.target.value)} style={reqStyle(tried && !(qty > 0))} />
          <ReqMsg show={tried && !(qty > 0)}>Enter a quantity</ReqMsg>
        </MField>
        <MField label="Unit Cost" half>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
            <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && !(unit > 0)))} type="number" step="0.001" placeholder="0.000" value={f.unit} onChange={e => set('unit', e.target.value)} />
          </div>
          <ReqMsg show={tried && !(unit > 0)}>Enter unit cost</ReqMsg>
        </MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Origin" half><select className="field" value={f.origin} onChange={e => set('origin', e.target.value)}>{['Canada', 'USA', 'China', 'Mexico', 'Other'].map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Expected" half><input className="field" type="date" value={f.eta} onChange={e => set('eta', e.target.value)} /></MField>
      </div>
      {supObj && f.origin !== 'Canada' && <InfoNote icon="alert">{f.origin} origin - duty/tariff will be flagged on received lots for landed-cost tracking.</InfoNote>}
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

Object.assign(window, {
  AddLocationModal, AddSupplierModal, AddMaterialModal, AddProductModal,
  NewPurchaseOrderModal, ScheduleProductionModal, InviteUserModal,
  AddPriceListModal, AddTerritoryModal, AddApprovalRuleModal, TransferOrderModal,
});


/* ===== desktop_views.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Orders, Customers, Inventory
   ============================================================ */

function orderTotals(o) {
  return rpOrderTotals(o);
}

const ORDER_STAGES = ['Submitted', 'Approved', 'Allocated', 'Picked', 'Dispatched', 'Delivered', 'Invoiced'];

/* ---- Distinct values helper for filter options ---- */
function distinct(rows, fn) {
  const seen = [];
  rows.forEach(r => { const v = fn(r); if (v != null && v !== '' && !seen.includes(v)) seen.push(v); });
  return seen;
}

/* ---- Filter bar — real working dropdown filters ---- */
function FilterBar({ defs, value = {}, onChange, search, onSearch, searchPlaceholder }) {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (open == null) return;
    const close = () => setOpen(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  const activeCount = Object.values(value).filter(v => v && v !== 'All').length;
  return (
    <div className="rp-filter-bar" style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
      <div className="rp-filter-search" style={{ position: 'relative', flex: 1, minWidth: 240 }}>
        <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--faint)' }} />
        <input className="field" style={{ paddingLeft: 36 }} placeholder={searchPlaceholder} value={search} onChange={e => onSearch(e.target.value)} />
      </div>
      {(defs || []).map((d, i) => {
        const cur = value[d.label];
        const on = cur && cur !== 'All';
        return (
          <div key={i} style={{ position: 'relative' }}>
            <button className="btn btn-sm" onClick={e => { e.stopPropagation(); setOpen(o => o === d.label ? null : d.label); }}
              style={{ color: on ? 'var(--amber-deep)' : 'var(--ink-soft)', borderColor: on ? 'var(--amber)' : undefined, background: on ? 'var(--amber-soft)' : undefined, fontWeight: on ? 700 : undefined }}>
              <Icon name="filter" size={14} />{on ? cur : d.label}<Icon name="chevD" size={13} style={{ color: 'var(--faint)' }} />
            </button>
            {open === d.label && (
              <div onClick={e => e.stopPropagation()} className="rp-scroll" style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: 6, minWidth: 180, maxHeight: 300, overflowY: 'auto', animation: 'rp-pop .14s ease both' }}>
                {['All', ...d.options].map(opt => {
                  const sel = (cur || 'All') === opt;
                  return (
                    <button key={opt} onClick={() => { onChange(d.label, opt); setOpen(null); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', textAlign: 'left', border: 'none', background: sel ? 'var(--surface-2)' : 'transparent', color: opt === 'All' ? 'var(--muted)' : 'var(--ink)', padding: '8px 10px', borderRadius: 7, fontSize: 13, fontWeight: sel ? 600 : 500, cursor: 'pointer' }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = 'var(--surface-2)'; }}
                      onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt}</span>
                      {sel && opt !== 'All' && <Icon name="check" size={14} style={{ color: 'var(--amber-deep)', flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {activeCount > 0 && (
        <button className="btn btn-ghost btn-sm" onClick={() => onChange('__clear__')} style={{ color: 'var(--muted)' }}><Icon name="x" size={13} />Clear{activeCount > 1 ? ` (${activeCount})` : ''}</button>
      )}
    </div>
  );
}

function rpCleanFilterState(filters) {
  return Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => value && value !== 'All')
  );
}

function rpSavedListState(key, defaults = {}) {
  if (typeof window === 'undefined') return { ...defaults, filters: { ...(defaults.filters || {}) } };
  window.RP_DESK_LIST_STATE = window.RP_DESK_LIST_STATE || {};
  const saved = window.RP_DESK_LIST_STATE[key] || {};
  return {
    ...defaults,
    ...saved,
    filters: rpCleanFilterState({ ...(defaults.filters || {}), ...(saved.filters || {}) })
  };
}

function rpSaveListState(key, patch) {
  if (typeof window === 'undefined' || !key) return;
  window.RP_DESK_LIST_STATE = window.RP_DESK_LIST_STATE || {};
  const current = window.RP_DESK_LIST_STATE[key] || {};
  window.RP_DESK_LIST_STATE[key] = {
    ...current,
    ...patch,
    filters: patch && patch.filters ? rpCleanFilterState(patch.filters) : rpCleanFilterState(current.filters)
  };
}

/* ---- Hook: filter state + matcher for list views ---- */
function useFilters(initialFilters = {}, onCommit) {
  const [filters, setFilters] = useState(() => rpCleanFilterState(initialFilters));
  const onChange = (label, val) => {
    const next = label === '__clear__'
      ? {}
      : (val === 'All'
        ? (() => { const n = { ...filters }; delete n[label]; return n; })()
        : { ...filters, [label]: val });
    const cleaned = rpCleanFilterState(next);
    setFilters(cleaned);
    if (onCommit) onCommit(cleaned);
  };
  return [filters, onChange];
}

/* ============================================================ ORDERS */
function rpOrderDeskListRow(order, customerOverride) {
  const customer = customerOverride || RP.cust(order.cust) || {};
  const customerDisplayName = typeof rpOrderCustomerDisplayName === 'function' ? rpOrderCustomerDisplayName(order, customer) : (customer.name || 'Customer');
  const delivery = rpOrderDeliveryScan(order);
  const billing = rpOrderInvoiceScan(order);
  const area = rpOrderAreaLabel(order, customer);
  const attention = rpOrderAttentionLabel(order, billing, delivery);
  const inventoryStatus = order.shopifyInventoryStatus || (order.shopifyInventoryReconciliation && order.shopifyInventoryReconciliation.status) || '';
  const inventoryOpen = ['Pending', 'Needs Review'].includes(inventoryStatus);
  return { order, customer, customerDisplayName, delivery, billing, area, attention, inventoryStatus, inventoryOpen, totals: orderTotals(order) };
}
function rpOrderDeskServerFilters(filters) {
  return {
    area: filters.Area,
    fulfillment: filters.Fulfillment,
    invoice: filters.Invoice,
    payMethod: filters['Pay Method'],
    payment: filters.Payment,
    rep: filters.Rep,
    territory: filters.Territory
  };
}
function rpOrderDeskFacet(facets, key) {
  const values = typeof rpServerFacetOptions === 'function' ? rpServerFacetOptions(facets, key) : [];
  return values;
}
function OrdersView({ go, reportsEnabled }) {
  const savedState = rpSavedListState('orders', { search: '', tab: 'all', filters: {} });
  const [search, setSearchValue] = useState(savedState.search || '');
  const [tab, setTabValue] = useState(savedState.tab || 'all');
  const [modal, setModal] = useState(false);
  const [batchResolveOpen, setBatchResolveOpen] = useState(false);
  const [batchSelectMode, setBatchSelectMode] = useState(false);
  const [batchSelected, setBatchSelected] = useState({});
  const [filters, onFilter] = useFilters(savedState.filters, next => rpSaveListState('orders', { filters: next }));
  const setSearch = value => {
    setSearchValue(value);
    rpSaveListState('orders', { search: value });
  };
  const setTab = value => {
    setTabValue(value);
    rpSaveListState('orders', { tab: value });
  };
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const serverFilters = rpOrderDeskServerFilters(filters);
  const serverPage = useRpServerPagedRows({ mode: 'orders', limit: 75, search, tab, filters: serverFilters });
  const orderRows = live
    ? serverPage.items.map(item => rpOrderDeskListRow(item.order, item.customer))
    : RP.ORDERS.map(order => rpOrderDeskListRow(order));
  const localCounts = {
    all: orderRows.length,
    open: orderRows.filter(row => row.delivery.open).length,
    shopifyPos: orderRows.filter(row => /^SO-SHOP-/.test(String(row.order.id || '')) && row.order.delivery === 'Shopify POS').length,
    shopifyQueue: orderRows.filter(row => row.inventoryOpen).length,
    paidPending: orderRows.filter(row => row.billing.paymentStatus === 'Paid' && !row.delivery.delivered && !row.delivery.cancelled).length,
    deliveredUnpaid: orderRows.filter(row => row.delivery.delivered && ['Unpaid', 'Overdue', 'Partial'].includes(row.billing.paymentStatus)).length,
    readyBill: orderRows.filter(row => row.delivery.delivered && row.billing.invoiceLabel === 'Ready').length,
    approval: orderRows.filter(row => row.order.status === 'Needs Approval' || row.order.status === 'On Hold').length,
  };
  const totalRepresentsAll = tab === 'all' && !String(search || '').trim() && !Object.values(serverFilters).some(value => String(value || '').trim());
  const counts = live ? Object.fromEntries(Object.keys(localCounts).map(key => [key, rpServerSummaryValue(serverPage.summary, key, key === 'all' && totalRepresentsAll && serverPage.total.value != null ? serverPage.total.value : null)])) : localCounts;
  const orderDefs = [
    { label: 'Fulfillment', options: live ? rpOrderDeskFacet(serverPage.facets, 'fulfillment') : distinct(orderRows, row => row.delivery.label) },
    { label: 'Invoice', options: live ? rpOrderDeskFacet(serverPage.facets, 'invoice') : distinct(orderRows, row => row.billing.invoiceLabel) },
    { label: 'Payment', options: live ? rpOrderDeskFacet(serverPage.facets, 'payment') : distinct(orderRows, row => row.billing.paymentStatus) },
    { label: 'Territory', options: live ? rpOrderDeskFacet(serverPage.facets, 'territory') : distinct(orderRows, row => row.order.territory) },
    { label: 'Area', options: live ? rpOrderDeskFacet(serverPage.facets, 'area') : distinct(orderRows, row => row.area) },
    { label: 'Rep', options: live ? rpOrderDeskFacet(serverPage.facets, 'rep') : distinct(orderRows, row => row.order.rep) },
    { label: 'Pay Method', options: live ? rpOrderDeskFacet(serverPage.facets, 'payMethod') : distinct(orderRows, row => row.billing.paymentMethodLabel) },
  ];
  let rows = orderRows;
  if (!live && tab === 'open') rows = rows.filter(row => row.delivery.open);
  if (!live && tab === 'shopifyPos') rows = rows.filter(row => /^SO-SHOP-/.test(String(row.order.id || '')) && row.order.delivery === 'Shopify POS');
  if (!live && tab === 'shopifyQueue') rows = rows.filter(row => row.inventoryOpen);
  if (!live && tab === 'paidPending') rows = rows.filter(row => row.billing.paymentStatus === 'Paid' && !row.delivery.delivered && !row.delivery.cancelled);
  if (!live && tab === 'deliveredUnpaid') rows = rows.filter(row => row.delivery.delivered && ['Unpaid', 'Overdue', 'Partial'].includes(row.billing.paymentStatus));
  if (!live && tab === 'readyBill') rows = rows.filter(row => row.delivery.delivered && row.billing.invoiceLabel === 'Ready');
  if (!live && tab === 'approval') rows = rows.filter(row => row.order.status === 'Needs Approval' || row.order.status === 'On Hold');
  if (!live && filters.Fulfillment) rows = rows.filter(row => row.delivery.label === filters.Fulfillment);
  if (!live && filters.Invoice) rows = rows.filter(row => row.billing.invoiceLabel === filters.Invoice);
  if (!live && filters.Payment) rows = rows.filter(row => row.billing.paymentStatus === filters.Payment);
  if (!live && filters.Territory) rows = rows.filter(row => row.order.territory === filters.Territory);
  if (!live && filters.Area) rows = rows.filter(row => row.area === filters.Area);
  if (!live && filters.Rep) rows = rows.filter(row => row.order.rep === filters.Rep);
  if (!live && filters['Pay Method']) rows = rows.filter(row => row.billing.paymentMethodLabel === filters['Pay Method']);
  if (!live && search) {
    const q = search.toLowerCase();
    rows = rows.filter(row => [
      row.order.id,
      row.order.invoiceNumber,
      row.customerDisplayName,
      row.customer.buyer,
      row.customer.email,
      row.customer.city,
      row.order.territory,
      row.order.rep,
      row.billing.invoiceLabel,
      row.billing.paymentStatus,
      row.billing.paymentMethodLabel,
      row.inventoryStatus
    ].filter(Boolean).join(' ').toLowerCase().includes(q));
  }
  const pagination = useRpPagedRows(rows, 75, `${tab}|${search}|${JSON.stringify(filters)}`);
  const visibleRows = live ? rows : pagination.rows;
  const batchResolveRows = tab === 'shopifyQueue' ? rows.filter(row => row.inventoryOpen) : [];
  const batchPageRows = tab === 'shopifyQueue' ? visibleRows.filter(row => row.inventoryOpen) : [];
  const batchSelectedRows = batchResolveRows.filter(row => batchSelected[row.order.id]);
  const batchVisibleIds = batchPageRows.map(row => row.order.id);
  const batchVisibleSelected = batchVisibleIds.length > 0 && batchVisibleIds.every(id => batchSelected[id]);
  useEffect(() => {
    if (tab !== 'shopifyQueue') {
      setBatchSelectMode(false);
      setBatchSelected({});
    }
  }, [tab]);
  const toggleBatchSelection = (orderId, checked) => setBatchSelected(prev => ({ ...prev, [orderId]: checked }));
  const selectVisibleBatchRows = checked => {
    setBatchSelected(prev => {
      const next = { ...prev };
      batchVisibleIds.forEach(id => {
        if (checked) next[id] = true;
        else delete next[id];
      });
      return next;
    });
  };
  const openBatchModal = () => {
    if (!batchSelectedRows.length) {
      toast('Select at least one Shopify order first', 'warn');
      return;
    }
    setBatchResolveOpen(true);
  };

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {modal && <NewOrderModal onClose={() => setModal(false)} />}
      {batchResolveOpen && <ShopifyInventoryBatchResolveModal rows={batchSelectedRows} onClose={() => setBatchResolveOpen(false)} onResolved={async () => {
        setBatchSelected({});
        setBatchSelectMode(false);
        await rpRefreshAfterMutation('Shopify inventory batch resolved');
      }} />}
      <PageHead eyebrow="Sales & Transfer Orders" title="Orders"
        actions={<>{reportsEnabled && <button className="btn btn-sm" onClick={() => go('reports', { type: 'report', id: 'orders', filters: { ...(search ? { search } : {}), ...(filters.Territory ? { territory: filters.Territory } : {}), ...(filters.Fulfillment ? { deliveryMethod: filters.Fulfillment } : {}) } })}><Icon name="download" size={15} />Export</button>}<button className="btn btn-sm btn-primary" onClick={() => setModal(true)}><Icon name="plus" size={15} />New Order</button></>} />
      {tab === 'shopifyQueue' && batchResolveRows.length > 0 && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '-10px 0 14px', padding: '10px 12px', flexWrap: 'wrap' }}>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>
            {batchSelectMode ? <><strong style={{ color: 'var(--ink)' }}>{batchSelectedRows.length}</strong> selected from {batchResolveRows.length} {live ? 'loaded' : 'filtered'} queue orders</> : `${batchResolveRows.length} ${live ? 'loaded' : 'filtered'} queue orders`}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {batchSelectMode && <button className="btn btn-sm" onClick={() => selectVisibleBatchRows(!batchVisibleSelected)}>{batchVisibleSelected ? 'Clear Page' : 'Select Page'}</button>}
            {batchSelectMode && <button className="btn btn-sm btn-primary" disabled={!batchSelectedRows.length} onClick={openBatchModal}><Icon name="check" size={15} />Batch Resolve</button>}
            <button className="btn btn-sm" onClick={() => {
              setBatchSelectMode(value => !value);
              if (batchSelectMode) setBatchSelected({});
            }}>{batchSelectMode ? 'Cancel Selection' : 'Select Orders'}</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['all', 'All Orders'], ['shopifyPos', 'Shopify POS'], ['shopifyQueue', 'POS Stock Review'], ['open', 'Open Delivery'], ['paidPending', 'Paid, Not Delivered'], ['deliveredUnpaid', 'Delivered, Unpaid'], ['readyBill', 'Ready to Bill'], ['approval', 'Needs Approval']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ border: '1px solid ' + (tab === k ? 'var(--navy)' : 'var(--line)'), background: tab === k ? 'var(--navy)' : 'var(--surface)', color: tab === k ? '#fff' : 'var(--ink-soft)', borderRadius: 8, padding: '7px 13px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
            {l}{(counts[k] != null || k !== 'shopifyPos') && <span style={{ fontSize: 11, opacity: 0.7 }}>{counts[k] == null ? '…' : counts[k]}</span>}
          </button>
        ))}
      </div>
      <FilterBar defs={orderDefs} value={filters} onChange={onFilter} search={search} onSearch={setSearch} searchPlaceholder="Search order # or customer…" />
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="rp-table">
          <thead><tr>{batchSelectMode && tab === 'shopifyQueue' && <th style={{ width: 38 }}><input type="checkbox" checked={batchVisibleSelected} onChange={e => selectVisibleBatchRows(e.target.checked)} /></th>}<th>Order</th><th>Customer</th><th>Territory</th><th>Fulfillment</th><th>Invoice / Payment</th><th>Items</th><th className="num">Total</th><th>Delivery</th><th></th></tr></thead>
          <tbody>
            {visibleRows.map(({ order: o, customer: c, customerDisplayName, delivery, billing, attention, inventoryStatus, totals: t, area }) => {
              return (
                <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => { if (live) rpUpsertOrder(o); go('orders', { type: 'order', id: o.id }); }}>
                  {batchSelectMode && tab === 'shopifyQueue' && <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={!!batchSelected[o.id]} onChange={e => toggleBatchSelection(o.id, e.target.checked)} /></td>}
                  <td><span className="mono" style={{ fontWeight: 600, color: 'var(--ink)' }}>{o.id}</span>{o.flag && <Icon name="flag" size={13} style={{ color: 'var(--danger)', marginLeft: 6, verticalAlign: 'middle' }} />}{/^SO-SHOP-/.test(String(o.id || '')) && <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--muted)' }}>{o.delivery === 'Shopify POS' ? 'Shopify POS receipt' : 'Shopify sale'}</div>}{inventoryStatus && inventoryStatus !== 'Resolved' && <div style={{ marginTop: 4 }}><Pill status={inventoryStatus}>{inventoryStatus}</Pill></div>}</td>
                  <td><div style={{ fontWeight: 500 }}>{customerDisplayName}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{[c.type, area].filter(Boolean).join(' - ')}</div></td>
                  <td style={{ color: 'var(--muted)' }}>{o.territory}</td>
                  <td><Pill status={delivery.label}>{delivery.label}</Pill></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                      <Pill status={billing.invoiceLabel}>{billing.invoiceLabel}</Pill>
                      <Pill status={billing.paymentStatus}>{billing.paymentStatus}</Pill>
                    </div>
                    {attention && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', fontWeight: 700, marginTop: 4 }}>{attention}</div>}
                  </td>
                  <td><div style={{ display: 'flex', gap: 3 }}>{o.lines.slice(0, 4).map((l, i) => <FlavorChip key={i} sku={RP.sku(l.sku) || l.sku} size={20} />)}{o.lines.length > 4 && <span style={{ fontSize: 11, color: 'var(--faint)', alignSelf: 'center', marginLeft: 2 }}>+{o.lines.length - 4}</span>}</div></td>
                  <td className="num" style={{ fontWeight: 600, color: 'var(--ink)' }}>{RP.money(t.total)}</td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{rpOrderDateLabel(o)}</td>
                  <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
                </tr>
              );
            })}
            {live && serverPage.initialLoading && <tr><td colSpan={batchSelectMode && tab === 'shopifyQueue' ? 10 : 9} style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}><Icon name="refresh" size={18} /><div style={{ marginTop: 7, fontWeight: 700, color: 'var(--ink)' }}>Loading orders...</div></td></tr>}
            {live && !serverPage.initialLoading && !serverPage.loaded && serverPage.error && <tr><td colSpan={batchSelectMode && tab === 'shopifyQueue' ? 10 : 9} style={{ textAlign: 'center', color: 'var(--danger)', padding: 30 }}><div style={{ fontWeight: 700 }}>{serverPage.error}</div><button type="button" className="btn btn-sm" onClick={serverPage.retry} style={{ marginTop: 10 }}>Try Again</button></td></tr>}
            {(!live || serverPage.loaded) && rows.length === 0 && <tr><td colSpan={batchSelectMode && tab === 'shopifyQueue' ? 10 : 9} style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No orders match these filters.</td></tr>}
          </tbody>
        </table>
        {live
          ? serverPage.loaded && <RpServerLoadMore {...serverPage} label="orders" onLoadMore={serverPage.loadMore} onRetry={serverPage.loadMore} />
          : <RpPagination {...pagination} label="orders" />}
      </div>
    </div>
  );
}

const ORDER_INVOICE_PAYMENT_METHODS = [
  { code: 'account_terms', label: 'Account terms' },
  { code: 'bank_transfer', label: 'Bank transfer' },
  { code: 'e_transfer', label: 'e-Transfer' },
  { code: 'cheque', label: 'Cheque' },
  { code: 'cash', label: 'Cash' },
  { code: 'credit_card', label: 'Credit card' }
];

function orderInvoicePolicy(customer) {
  if (typeof rpPaymentPolicyFromCustomer === 'function') return rpPaymentPolicyFromCustomer(customer);
  return {
    acceptedPaymentMethods: { account_terms: true, bank_transfer: true, e_transfer: true, cheque: true, cash: true, credit_card: false },
    invoiceOverrides: {}
  };
}

function orderInvoicePaymentMethodLabel(code) {
  return (ORDER_INVOICE_PAYMENT_METHODS.find(method => method.code === code) || {}).label || code || 'Not set';
}

function orderInvoiceDefaultPaymentMethod(policy) {
  const allowed = ORDER_INVOICE_PAYMENT_METHODS.filter(method => policy && policy.acceptedPaymentMethods && policy.acceptedPaymentMethods[method.code]);
  const preferred = policy && policy.invoiceOverrides && (policy.invoiceOverrides.defaultPaymentMethod || policy.invoiceOverrides.default_payment_method);
  return allowed.some(method => method.code === preferred) ? preferred : ((allowed[0] || {}).code || 'account_terms');
}

function orderCustomerCardOnFile(customer) {
  const stripeCard = typeof rpStripeDefaultCard === 'function' ? rpStripeDefaultCard(customer) : null;
  if (stripeCard) {
    return {
      ready: true,
      id: String(stripeCard.id || ''),
      brand: String(stripeCard.brand || 'Card').trim(),
      last4: String(stripeCard.last4 || stripeCard.last_4 || '').trim(),
      label: typeof rpStripeCardLabel === 'function' ? rpStripeCardLabel(stripeCard) : ''
    };
  }
  const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
  const card = overrides.cardOnFile || overrides.card_on_file || overrides.savedCard || overrides.saved_card || {};
  const ready = card && typeof card === 'object' && ['ready', 'active', 'verified'].includes(String(card.status || '').toLowerCase());
  const token = card && typeof card === 'object' && (card.token || card.vaultToken || card.vault_token || card.customerPaymentMethodId || card.customer_payment_method_id);
  return {
    ready: Boolean(ready && token),
    id: card && typeof card === 'object' ? String(token || '').trim() : '',
    brand: card && typeof card === 'object' ? String(card.brand || card.network || '').trim() : '',
    last4: card && typeof card === 'object' ? String(card.last4 || card.last_4 || '').trim() : '',
    label: ''
  };
}

const ACCOUNT_CREDIT_PAYOUT_METHODS = [
  { code: 'cash', label: 'Cash' },
  { code: 'e_transfer', label: 'e-Transfer' },
  { code: 'bank_transfer', label: 'Bank transfer' },
  { code: 'cheque', label: 'Cheque' },
  { code: 'credit_card', label: 'Credit card' },
  { code: 'other', label: 'Other' }
];

function accountCreditPayoutMethodLabel(code) {
  if (code === 'invoice') return 'Invoice';
  return (ACCOUNT_CREDIT_PAYOUT_METHODS.find(method => method.code === code) || {}).label || code || 'Not recorded';
}

function orderInvoiceEmail(order, customer, policy) {
  return String((order && order.invoiceEmailTo) || (policy && policy.invoiceOverrides && policy.invoiceOverrides.invoiceEmail) || (customer && customer.email) || '').trim();
}

function orderInvoiceNote(order, customer, policy) {
  const overrides = policy && policy.invoiceOverrides ? policy.invoiceOverrides : (customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {});
  return String((order && order.invoiceNote) || overrides.invoiceNote || overrides.invoice_note || '').trim();
}

function orderInvoiceReceiptTime(value) {
  return typeof rpFormatLocalDateTime === 'function' ? rpFormatLocalDateTime(value, { empty: '' }) : (value ? String(value).slice(0, 16).replace('T', ' ') : '');
}

function InvoiceIssueModal({ order, customer = {}, onClose, onIssued }) {
  const policy = orderInvoicePolicy(customer);
  const allowed = ORDER_INVOICE_PAYMENT_METHODS.filter(method => policy.acceptedPaymentMethods[method.code]);
  const defaultMethod = orderInvoiceDefaultPaymentMethod(policy);
  const [paymentMethod, setPaymentMethod] = useState(defaultMethod);
  const [emailTo, setEmailTo] = useState(() => orderInvoiceEmail(order, customer, policy));
  const [invoiceNote, setInvoiceNote] = useState(() => orderInvoiceNote(order, customer, policy));
  const [sendEmail, setSendEmail] = useState(() => Boolean(orderInvoiceEmail(order, customer, policy)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [issueNotice, setIssueNotice] = useState('');
  const invoiceIssue = useRpInvoiceIssueRequest(order.id);
  const customerInternalNote = String(customer && customer.note || '').trim();
  const [, refreshPo] = useState(0);
  const missingRequiredPo = rpInvoiceMissingRequiredPo(order, customer);

  const issue = async (retryOriginal = false, cancelUnstarted = false) => {
    if (busy || invoiceIssue.busy || invoiceIssue.loading) return;
    if (!retryOriginal && missingRequiredPo) { setError('Save the required PO number before issuing the invoice.'); return; }
    setBusy(true);
    setError('');
    setIssueNotice('');
    try {
      const response = cancelUnstarted
        ? await invoiceIssue.cancelUnstarted()
        : retryOriginal ? await invoiceIssue.retry()
        : await invoiceIssue.run({
          paymentMethod,
          invoiceNote: invoiceNote.trim(),
          emailTo: sendEmail ? emailTo.trim() : '',
          sendEmail
          }, { replaceSettled: Boolean(order.invoiceNumber || order.invoice_number) });
      if (response.cancelled === true && response.definitivelyNotCommitted === true) {
        const message = 'The unstarted request is cancelled. No invoice was issued by that request. You can correct the details and choose Issue Invoice explicitly.';
        setIssueNotice(message);
        toast(message, 'ok');
        return;
      }
      const saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
      Object.assign(order, saved);
      const warnings = rpInvoiceIssueWarnings(response);
      if (cancelUnstarted) warnings.push('The invoice was already saved and could not be cancelled. Its original request was recovered.');
      try {
        await rpRefreshAfterMutation(`Invoice ${response.invoice && response.invoice.invoiceNumber || order.id} issued`);
      } catch {
        warnings.push('Invoice saved, but the dashboard refresh failed. Reopen the order to refresh its confirmed details.');
      }
      const refreshed = RP.ORDERS.find(x => x.id === order.id) || saved;
      Object.assign(order, refreshed);
      const successMessage = `Invoice ${order.invoiceNumber || order.id} ${retryOriginal ? 'recovered' : 'issued'}`;
      const message = [successMessage, ...warnings].join(' ');
      setIssueNotice(message);
      toast(message, warnings.length ? 'warn' : 'ok');
      onIssued && onIssued(refreshed);
      if (!response.recoveryRequired) onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not issue invoice.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={invoiceIssue.pending ? 'Recover Invoice Request' : order.invoiceNumber ? 'Update Invoice' : 'Issue Invoice'} sub={`${order.id} - ${customer.name || customer.legalName || 'Customer'}`} onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>{invoiceIssue.pending ? 'Close' : 'Cancel'}</button>{invoiceIssue.pending && <button className="btn" disabled={busy || invoiceIssue.busy || invoiceIssue.loading || !!invoiceIssue.error} onClick={() => issue(true, true)}>Cancel unstarted request</button>}<button className="btn btn-primary" disabled={busy || invoiceIssue.busy || invoiceIssue.loading || !!invoiceIssue.error || (!invoiceIssue.pending && (!paymentMethod || missingRequiredPo))} onClick={() => issue(invoiceIssue.pending)}><Icon name="receipt" size={15} />{busy ? 'Checking...' : invoiceIssue.pending ? 'Retry Original Request' : order.invoiceNumber ? 'Update Invoice' : 'Issue Invoice'}</button></>}>
      <InvoicePoRequirement order={order} customer={customer} disabled={busy || invoiceIssue.pending} onSaved={() => { refreshPo(value => value + 1); setError(''); }} />
      {invoiceIssue.pending && <div role="status" style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, lineHeight: 1.45 }}>An earlier invoice request still needs recovery. Retry its original details; changing these fields cannot replace it. Closing this window keeps the request available for recovery. Cancel unstarted request only unlocks changes if the server proves it cannot issue; it cannot cancel an invoice already saved.</div>}
      {invoiceIssue.error && <div role="alert" style={{ color: 'var(--danger)', marginBottom: 12 }}>{invoiceIssue.error}</div>}
      {customerInternalNote && (
        <div style={{ background: 'var(--amber-soft)', border: '1px solid var(--amber)', borderRadius: 10, padding: '11px 13px', marginBottom: 12 }}>
          <div className="eyebrow" style={{ color: 'var(--amber-deep)', marginBottom: 5 }}>Internal Note</div>
          <div style={{ color: 'var(--ink-soft)', fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{customerInternalNote}</div>
        </div>
      )}
      <MField label="Payment method">
        <select className="field" disabled={busy || invoiceIssue.pending} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
          {allowed.map(method => <option key={method.code} value={method.code}>{method.label}</option>)}
        </select>
      </MField>
      <MField label="Customer invoice note">
        <textarea className="field" rows={2} maxLength={600} disabled={busy || invoiceIssue.pending} value={invoiceNote} onChange={e => setInvoiceNote(e.target.value)} placeholder="Meet at receiving, delivery instructions, thank you..." style={{ resize: 'vertical' }} />
      </MField>
      <MField label="Email invoice to">
        <input className="field" type="email" disabled={busy || invoiceIssue.pending} value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="billing@example.com" />
      </MField>
      <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
        <input type="checkbox" disabled={busy || invoiceIssue.pending} checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
        Email invoice to customer
      </label>
      {paymentMethod === 'credit_card' && (
        <div style={{ background: 'var(--amber-soft)', color: 'var(--amber-deep)', border: '1px solid var(--amber)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>
          Credit-card processing fees will be added. Save an authorized Stripe card for this customer before issuing, then use Charge Invoice.
        </div>
      )}
      {issueNotice && <div role="status" style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 10 }}>{issueNotice}</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function ChargeInvoiceModal({ order, customer, onClose, onCharged }) {
  const policy = orderInvoicePolicy(customer);
  const [setupOpen, setSetupOpen] = useState(false);
  const [, bumpCard] = useState(0);
  const card = orderCustomerCardOnFile(customer);
  const emailDefault = orderInvoiceEmail(order, customer, policy);
  const [emailTo, setEmailTo] = useState(emailDefault);
  const [sendReceipt, setSendReceipt] = useState(() => Boolean(emailDefault));
  const [paymentNote, setPaymentNote] = useState('Stripe saved-card invoice charge.');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const live = !(window.RP_AUTH && window.RP_AUTH.demo);
  const cardLabel = card.ready
    ? (card.label || `${card.brand || 'Card'}${card.last4 ? ` ending ${card.last4}` : ''}`)
    : 'No vaulted card connected';

  const syncIssuedResponse = async (response, message) => {
    const saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
    Object.assign(order, saved);
    await rpRefreshAfterMutation(message);
    const refreshed = RP.ORDERS.find(x => x.id === order.id) || saved;
    Object.assign(order, refreshed);
    onCharged && onCharged(refreshed);
    return { refreshed };
  };

  const chargeSavedCard = async () => {
    if (busy || !card.ready) return;
    setBusy(true);
    setNotice('');
    setError('');
    try {
      const response = await rpApi('/api/invoices/stripe-charge', {
        method: 'POST',
        body: JSON.stringify({
          orderId: order.id,
          paymentMethodId: card.id,
          emailTo: sendReceipt ? emailTo.trim() : '',
          sendReceipt,
          paymentNote: paymentNote.trim()
        })
      });
      if (response.processing) {
        const message = response.message || 'Stripe is still processing this payment. No receipt has been issued yet.';
        setNotice(message);
        toast('Card payment is still processing', 'info');
        return;
      }
      const result = await syncIssuedResponse(response, `Card charged for ${response.invoice && response.invoice.invoiceNumber || order.id}`);
      const refreshed = result.refreshed;
      const amount = RP.money2(response.payment && response.payment.amount || refreshed.total || 0);
      const receiptEmailFailed = sendReceipt && !(response.email && response.email.sent);
      const warning = response.email && response.email.warning
        ? response.email.warning
        : 'Use Email on the invoice to send the receipt.';
      toast(receiptEmailFailed ? `Card charged for ${amount}, but the receipt email was not sent: ${warning}` : `Card charged for ${amount}. ${sendReceipt ? 'Receipt emailed.' : 'Receipt ready.'}`, receiptEmailFailed ? 'warn' : 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not charge saved card.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Modal title="Charge Invoice" sub={`${order.id} - ${customer.name}`} onClose={onClose} width={620}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn" disabled={busy || !live} onClick={() => setSetupOpen(true)} style={{ opacity: busy || !live ? 0.6 : 1 }}><Icon name="card" size={15} />{card.ready ? 'Replace Card' : 'Add Saved Card'}</button><button className="btn btn-primary" disabled={busy || !live || !card.ready} onClick={chargeSavedCard} style={{ opacity: busy || !live || !card.ready ? 0.6 : 1 }}><Icon name="check" size={15} />{busy ? 'Charging...' : 'Charge Saved Card'}</button></>}>
      <div style={{ background: card.ready ? 'var(--ok-bg)' : 'var(--amber-soft)', color: card.ready ? 'var(--ok)' : 'var(--amber-deep)', border: `1px solid ${card.ready ? 'oklch(0.6 0.11 150 / 0.3)' : 'var(--amber)'}`, borderRadius: 9, padding: '11px 12px', fontSize: 12.5, fontWeight: 750, marginBottom: 12 }}>
        {card.ready ? `${cardLabel} is ready for direct Stripe charging.` : 'Add an authorized saved Stripe card before charging. Do not enter or store raw card numbers in Ranch Power.'}
      </div>
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <Row k="Payment" v="Credit card" />
        <Row k="Card status" v={cardLabel} />
        <Row k="Direct charge" v={card.ready ? 'Stripe saved card' : 'Add card first'} />
        <Row k="Receipt" v={sendReceipt ? (emailTo || 'Email required') : 'Not emailed'} />
      </div>
      <MField label="Receipt email">
        <input className="field" type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="billing@example.com" />
      </MField>
      <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
        <input type="checkbox" checked={sendReceipt} onChange={e => setSendReceipt(e.target.checked)} />
        Email receipt to customer
      </label>
      <MField label="Internal note">
        <textarea className="field" rows={2} value={paymentNote} onChange={e => setPaymentNote(e.target.value)} style={{ resize: 'vertical' }} />
      </MField>
      <div style={{ color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.45, marginBottom: 12 }}>Charge Saved Card runs the payment through Stripe and immediately records a paid Ranch Power receipt.</div>
      {notice && <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: error ? 8 : 0 }}>{notice}</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
    {setupOpen && <StripeCardSetupModal customer={customer} onClose={() => setSetupOpen(false)} onSaved={() => bumpCard(x => x + 1)} />}
    </>
  );
}

function OrderInvoiceCard({ order, customer, onIssue, onCharge, onOpenInvoice, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const invoiceIssue = useRpInvoiceIssueRequest(order.id);
  const linkedInvoice = typeof RP.invoice === 'function' ? RP.invoice(order.invoiceNumber || order.id) : null;
  const cancelled = typeof rpOrderDeliveryScan === 'function' ? rpOrderDeliveryScan(order).cancelled : ['Cancelled', 'Canceled', 'Void'].includes(String(order.status || ''));
  const staleCancelledInvoice = cancelled && Boolean(order.invoiceNumber) && !linkedInvoice;
  const linkedInvoiceIssued = linkedInvoice && linkedInvoice.status !== 'Draft';
  const issued = !staleCancelledInvoice && Boolean(order.invoiceNumber || linkedInvoiceIssued);
  const invoiceId = issued ? (order.invoiceNumber || (linkedInvoiceIssued ? linkedInvoice.id : '')) : '';
  const invoiceStatus = issued ? (order.invoiceStatus || linkedInvoice?.status || (order.financialStatus === 'Paid' ? 'Paid' : 'Issued')) : 'Pending';
  const invoicePaymentMethod = order.invoicePaymentMethod || linkedInvoice?.paymentMethod;
  const invoiceDueDate = order.invoiceDueDate || linkedInvoice?.due;
  const policy = orderInvoicePolicy(customer);
  const invoiceSettled = !staleCancelledInvoice && (['Paid', 'Closed'].includes(String(order.invoiceStatus || '')) || ['Paid', 'Closed'].includes(String(order.financialStatus || '')));
  const importedShopifyPaid = /^SO-SHOP-/.test(String(order.id || '')) && Boolean(order.shopifyOrderId) && !order.invoiceNumber
    && /^shopify_(?:pos|checkout)$/.test(String(order.invoicePaymentMethod || '')) && invoiceSettled;
  const cardInvoiceActive = issued && invoicePaymentMethod === 'credit_card' && !invoiceSettled;
  const showChargeInvoice = !cancelled && !invoiceSettled && typeof onCharge === 'function' && cardInvoiceActive;
  const emailTo = linkedInvoice?.emailTo || orderInvoiceEmail(order, customer, policy);
  const live = !(window.RP_AUTH && window.RP_AUTH.demo);
  const canOpenInvoice = issued && typeof onOpenInvoice === 'function';
  const canIssueInvoice = live && !cancelled;
  if (importedShopifyPaid) return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={<Pill status="Paid">Paid</Pill>}>{invoicePaymentMethod === 'shopify_pos' ? 'Shopify POS sale' : 'Shopify sale'}</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
        <Row k="Payment" v={invoicePaymentMethod === 'shopify_pos' ? 'Collected in Shopify POS' : 'Collected in Shopify'} />
        <Row k="Shopify order" v={<span className="mono">{String(order.shopifyOrderId).split('/').pop()}</span>} />
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 12 }}>This sale was paid at the source. Stock review is separate from payment. Do not issue another invoice for this imported receipt.</div>
    </div>
  );
  const invoiceNumber = issued ? (
    canOpenInvoice ? (
      <button type="button" className="mono" onClick={onOpenInvoice} aria-label={`Open invoice ${invoiceId}`} title="Open invoice detail" style={{ border: 'none', background: 'transparent', padding: 0, margin: 0, color: 'var(--amber-deep)', fontWeight: 750, cursor: 'pointer', textAlign: 'right' }}>
        {invoiceId}
      </button>
    ) : <span className="mono">{invoiceId}</span>
  ) : 'Not issued';
  const resend = async () => {
    if (busy || !issued || invoiceIssue.pending) return;
    setBusy(true);
    try {
      const response = await rpApi('/api/invoices/email', {
        method: 'POST',
        body: JSON.stringify({ orderId: order.id, emailTo })
      });
      if (response.invoice) {
        order.invoiceLastSentAt = response.invoice.lastSentAt || order.invoiceLastSentAt;
        order.invoiceEmailTo = response.invoice.emailTo || emailTo;
      }
      toast(`${order.invoiceStatus === 'Paid' || order.financialStatus === 'Paid' ? 'Receipt' : 'Invoice'} ${order.invoiceNumber} emailed`, 'ok');
      onRefresh && onRefresh();
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not email invoice.', 'danger');
    } finally {
      setBusy(false);
    }
  };
  const openPdf = async () => {
    try {
      await rpOpenInvoicePdf(order.id);
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not open invoice PDF.', 'danger');
    }
  };
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={<Pill status={invoiceStatus}>{issued ? invoiceStatus : (cancelled ? 'Not invoiced' : 'Ready')}</Pill>}>Invoice</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13, marginBottom: 12 }}>
        <Row k="Number" v={invoiceNumber} />
        <Row k="Payment" v={issued ? orderInvoicePaymentMethodLabel(invoicePaymentMethod) : (typeof rpCustomerPaymentPolicySummary === 'function' ? rpCustomerPaymentPolicySummary(customer) : 'Account terms')} />
        {invoiceDueDate && <Row k="Due" v={<span className="mono">{invoiceDueDate}</span>} />}
        {order.creditCardFeeTotal > 0 && <Row k="Card fee" v={RP.money2(order.creditCardFeeTotal)} muted />}
        {issued && emailTo && <Row k="Email" v={<span className="mono" style={{ fontSize: 11.5 }}>{emailTo}</span>} />}
        {issued && order.invoiceLastSentAt && <Row k="Last sent" v={<span className="mono" style={{ fontSize: 11.5 }}>{orderInvoiceReceiptTime(order.invoiceLastSentAt)}</span>} />}
        {issued && order.invoiceFirstOpenedAt && <Row k="First opened" v={<span className="mono" style={{ fontSize: 11.5 }}>{orderInvoiceReceiptTime(order.invoiceFirstOpenedAt)}</span>} />}
        {issued && order.invoiceLastOpenedAt && <Row k="Last opened" v={<span className="mono" style={{ fontSize: 11.5 }}>{orderInvoiceReceiptTime(order.invoiceLastOpenedAt)}</span>} />}
        {issued && Number(order.invoiceOpenCount || 0) > 0 && <Row k="Open events" v={<span className="mono">{Number(order.invoiceOpenCount || 0)}</span>} />}
      </div>
      {invoiceIssue.pending && <div data-invoice-issue-recovery="desktop" role="status" style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.45, marginBottom: 12 }}>The original invoice request still needs recovery. Use Recover Invoice Request to check its saved result and original email; do not create a replacement or send another email.</div>}
      {invoiceIssue.error && <div role="alert" style={{ color: 'var(--danger)', fontSize: 12.5, marginBottom: 12 }}>{invoiceIssue.error}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {showChargeInvoice && <button className="btn btn-sm btn-primary" disabled={!canIssueInvoice} onClick={onCharge} style={{ opacity: canIssueInvoice ? 1 : 0.6 }}><Icon name="wallet" size={14} />Charge Invoice</button>}
        <button className="btn btn-sm btn-primary" disabled={(!canIssueInvoice && !invoiceIssue.pending) || invoiceIssue.loading || !!invoiceIssue.error} onClick={onIssue}><Icon name="receipt" size={14} />{invoiceIssue.pending ? 'Recover Invoice Request' : issued ? 'Update Invoice' : 'Issue Invoice'}</button>
        {canOpenInvoice && <button className="btn btn-sm" onClick={onOpenInvoice}><Icon name="arrowR" size={14} />Open Invoice</button>}
        {issued && <button className="btn btn-sm" onClick={openPdf}><Icon name="doc" size={14} />PDF</button>}
        {issued && <button className="btn btn-sm" disabled={busy || !emailTo || invoiceIssue.pending || invoiceIssue.loading || !!invoiceIssue.error} onClick={resend}><Icon name="mail" size={14} />{busy ? 'Sending...' : 'Email'}</button>}
      </div>
      {!live && <div style={{ color: 'var(--faint)', fontSize: 11.5, lineHeight: 1.35, marginTop: 10 }}>Invoices are issued only from live data.</div>}
    </div>
  );
}

/* ============================================================ ORDER DETAIL */
function OrderDetail({ id, back, go }) {
  const exactOrder = useRpExactOrder({ orderId: id });
  const backButton = <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />Back to Orders</button>;
  if (exactOrder.loading) return (
    <div className="rp-fade" data-exact-order-loading="true" style={{ padding: '26px 30px 40px' }}>
      {backButton}
      <div className="card" style={{ padding: 24, color: 'var(--muted)' }}><Icon name="refresh" size={18} /><div style={{ color: 'var(--ink)', fontWeight: 700, marginTop: 8 }}>Loading order {id}...</div></div>
    </div>
  );
  if (exactOrder.error) return (
    <div className="rp-fade" data-exact-order-error="true" style={{ padding: '26px 30px 40px' }}>
      {backButton}
      <div className="card" role="alert" style={{ padding: 24, color: 'var(--danger)' }}><SectionTitle>Order Could Not Load</SectionTitle><div style={{ fontSize: 14 }}>{exactOrder.error}</div><button type="button" className="btn btn-sm" onClick={exactOrder.retry} style={{ marginTop: 12 }}>Try Again</button></div>
    </div>
  );
  if (!exactOrder.order) {
    return (
      <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
        {backButton}
        <div className="card" style={{ padding: 24 }}>
          <SectionTitle>Order Not Found</SectionTitle>
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>This order does not exist or is outside your access scope.</div>
        </div>
      </div>
    );
  }
  return <OrderDetailFound key={exactOrder.order.id} id={id} back={back} go={go} order={exactOrder.order} />;
}

function OrderWarehouseModal({ order, onClose, onSaved }) {
  const [source, setSource] = useState(order.source || '');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const pending = useRef(null), saving = useRef(false);
  const locations = rpOrderSourceLocations();
  async function save() {
    if (saving.current) return;
    if (!source || source === order.source) { setError('Choose a different fulfillment warehouse.'); return; }
    saving.current = true; setBusy(true); setError('');
    try {
      if (!rpHasLiveAuth()) throw new Error('Sign in to change the fulfillment warehouse.');
      if (!pending.current) pending.current = { id: order.id, sourceLocationId: source, expectedUpdatedAt: order.updated || order.updatedAt || order.updated_at, requestId: `OW-${crypto.randomUUID()}` };
      const result = await rpApi('/api/orders/source', { method: 'POST', body: JSON.stringify(pending.current) });
      if (!result.accepted) throw new Error(result.error || 'Warehouse change was not confirmed.');
      Object.assign(order, { source: result.order.source_location_id, updated: result.order.updated_at, updatedAt: result.order.updated_at });
      onSaved(); onClose();
      await rpRefreshAfterMutation('Fulfillment warehouse updated');
    } catch (err) {
      if ([400, 403, 404, 409].includes(err.status)) pending.current = null;
      setError(err.message || 'Save was not confirmed. Retry the same change.');
    } finally { saving.current = false; setBusy(false); }
  }
  return <Modal title="Change fulfillment warehouse" sub={order.id} width={520} onClose={busy ? () => {} : onClose}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save warehouse'}</button></>}>
    {error && <div role="alert" style={{ color: 'var(--danger)', marginBottom: 14 }}>{error}</div>}
    <MField label="Fulfillment warehouse" required><select className="input" value={source} disabled={busy || Boolean(pending.current)} onChange={e => setSource(e.target.value)}>
      <option value="">Choose warehouse</option>{locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
    </select></MField>
    <p className="muted" style={{ fontSize: 13 }}>Updates the stock commitment. Invoice and payment stay unchanged.</p>
  </Modal>;
}

function OrderFulfillmentLotsModal({ order, onClose, onSaved }) {
  const [lots, setLots] = useState(() => Object.fromEntries(order.lines.map(l => [l.id, l.lot || ''])));
  const [note, setNote] = useState(''), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const pending = useRef(null), saving = useRef(false);
  async function save() {
    if (saving.current) return;
    if (!note.trim() || order.lines.some(l => !lots[l.id])) { setError('Choose a lot for each product and give a reason.'); return; }
    saving.current = true; setBusy(true); setError('');
    try {
      if (!rpHasLiveAuth()) throw new Error('Sign in to change fulfillment lots.');
      if (!pending.current) pending.current = { id: order.id, requestId: `OL-${crypto.randomUUID()}`, expectedUpdatedAt: order.updated || order.updatedAt || order.updated_at,
        note: note.trim(), lines: order.lines.map(l => ({ lineId: l.id, oldLot: l.lot || null, lot: lots[l.id] })) };
      const result = await rpApi('/api/orders/fulfillment-lots', { method: 'POST', body: JSON.stringify(pending.current) });
      if (!result.accepted) throw new Error(result.error || 'Lot changes were not confirmed.');
      order.lines.forEach(l => { const saved = result.lines.find(row => row.id === l.id); if (saved) l.lot = saved.lot; });
      order.updated = order.updatedAt = result.order.updated_at;
      onSaved(); onClose(); await rpRefreshAfterMutation('Fulfillment lots updated');
    } catch (err) {
      if ([400, 403, 404, 409].includes(err.status)) pending.current = null;
      setError(err.message || 'Save was not confirmed. Retry the same change.');
    } finally { saving.current = false; setBusy(false); }
  }
  return <Modal title="Edit fulfillment lots" sub={`${order.id} · ${RP.loc(order.source)?.name || order.source}`} width={640} onClose={busy ? () => {} : onClose}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save lots'}</button></>}>
    {error && <div role="alert" style={{ color: 'var(--danger)', marginBottom: 14 }}>{error}</div>}
    {order.lines.map(line => {
      const sku = line.inventorySku || line.sku;
      const options = rpOrderLotRowsForSku(sku, order.source);
      const currentMissing = line.lot && !options.some(row => row.lot === line.lot);
      return <MField key={line.id} label={`${rpSkuItem(line.sku)?.name || line.sku} · ${line.qty} ${line.unit === 'can' ? 'cans' : 'cases'}`} required>
        <select className="input" aria-label={`Fulfillment lot for ${line.sku}`} value={lots[line.id]} disabled={busy || Boolean(pending.current)} onChange={e => setLots(v => ({...v, [line.id]: e.target.value}))}>
          <option value="">Choose lot</option>
          {currentMissing && <option value={line.lot}>{rpPrintedLotLabel(sku,line.lot)} · current assignment</option>}
          {options.map(row => <option key={row.lot} value={row.lot}>{rpPrintedLotLabel(sku,row.lot)} · {row.qty} on hand</option>)}
        </select>
      </MField>;
    })}
    <MField label="Reason" required><input className="input" value={note} disabled={busy || Boolean(pending.current)} onChange={e => setNote(e.target.value)} /></MField>
    <p className="muted" style={{ fontSize: 13 }}>Stock is checked when saving. Quantities, prices and invoice stay unchanged.</p>
  </Modal>;
}

function OrderDetailFound({ id, back, go, order }) {
  const o = order;
  const c = RP.cust(o.cust) || {}, t = orderTotals(o);
  const orderCustomerName = typeof rpOrderCustomerDisplayName === 'function' ? rpOrderCustomerDisplayName(o, c) : (c.name || 'Customer');
  const timeline = rpOrderTimeline(o, { newestFirst: true });
  const reasonLabel = typeof rpOrderReasonDisplay === 'function' ? rpOrderReasonDisplay(o, 'Live order') : '';
  if (reasonLabel && o.reason !== reasonLabel) o.reason = reasonLabel;
  const [status, setStatus] = useState(o.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [warehouseOpen, setWarehouseOpen] = useState(false);
  const [fulfillmentLotsOpen, setFulfillmentLotsOpen] = useState(false);
  const [removalOpen, setRemovalOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [, force] = useState(0);
  const shopifyInventoryReconciliation = o.shopifyInventoryReconciliation || null;
  const shopifyInventoryOpen = shopifyInventoryReconciliation && ['Pending', 'Needs Review'].includes(String(shopifyInventoryReconciliation.status || ''));
  const deliveryScan = rpOrderDeliveryScan({ ...o, status });
  const billingScan = rpOrderInvoiceScan({ ...o, status });
  const attention = rpOrderAttentionLabel({ ...o, status }, billingScan, deliveryScan);
  const effectiveStatus = deliveryScan.label;
  const timelineStatus = billingScan.issued && deliveryScan.delivered ? 'Invoiced' : status;
  const normalizedStatus = typeof rpOrderFulfillmentStage === 'function' ? rpOrderFulfillmentStage(timelineStatus, ORDER_STAGES) : (timelineStatus === 'Needs Approval' || timelineStatus === 'On Hold' || timelineStatus === 'Requested' ? 'Submitted' : timelineStatus === 'Inbound' ? 'Allocated' : timelineStatus);
  const rawIdx = ORDER_STAGES.indexOf(normalizedStatus);
  const curIdx = rawIdx < 0 ? 0 : rawIdx;
  const nextStage = rawIdx >= 0 && rawIdx < ORDER_STAGES.length - 1 ? ORDER_STAGES[rawIdx + 1] : null;
  const needsApproval = ['Needs Approval', 'On Hold', 'Requested', 'Submitted'].includes(status);
  const orderInvoiceLocked = typeof rpOrderInvoiceLocked === 'function' && rpOrderInvoiceLocked({ ...o, status });
  const canEditOrder = typeof rpOrderEditableBySales === 'function'
    ? rpOrderEditableBySales({ ...o, status })
    : !orderInvoiceLocked && ['Submitted', 'Approved', 'Allocated', 'Needs Approval', 'On Hold', 'Requested'].includes(status);
  const canRemoveOrder = !['cancelled', 'canceled'].includes(String(status || '').trim().toLowerCase());
  const currentRole = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(rpAuthProfile().role) : '';
  const directRemoval = currentRole === 'admin';
  const canChangeWarehouse = ['admin', 'leadership', 'ops', 'sales_manager'].includes(currentRole);
  const canReopenForCorrection = !orderInvoiceLocked && ['Picked', 'Dispatched'].includes(status) && ['admin', 'leadership', 'ops', 'sales_manager'].includes(currentRole);
  const actionStatus = billingScan.issued && deliveryScan.delivered ? null : (needsApproval ? 'Approved' : nextStage);
  const canUndoDelivery = deliveryScan.delivered;
  const openAssociatedInvoice = () => {
    if (!go) return;
    const inv = typeof RP.invoice === 'function' ? RP.invoice(o.invoiceNumber || o.id) : null;
    const invoiceId = (inv && inv.id) || o.invoiceNumber;
    if (!invoiceId) {
      toast('Issue the invoice first, then it can be opened from this order.', 'warn');
      return;
    }
    go('invoicing', { type: 'invoice', id: invoiceId, backToOrder: o.id });
  };
  const persistStatus = async (nextStatus, note = '') => {
    if (!nextStatus || busy) return;
    const previousStatus = status;
    const previousOrderStatus = o.status;
    setBusy(true);
    setError('');
    try {
      if (!rpIsDemoMode()) {
        const response = await rpApi('/api/orders/status', {
          method: 'POST',
          body: JSON.stringify({ id: o.id, status: nextStatus, note })
        });
        const savedOrder = rpUpsertOrder(rpOrderFromApi(response, { ...o, status: nextStatus }));
        Object.assign(o, savedOrder);
        setStatus(savedOrder.status || nextStatus);
        await rpRefreshAfterMutation(`Order ${o.id} updated`);
        const refreshed = RP.ORDERS.find(x => x.id === id);
        if (refreshed) {
          Object.assign(o, refreshed);
          setStatus(refreshed.status);
        }
      } else {
        o.status = nextStatus;
        setStatus(nextStatus);
      }
      toast(`${o.id} moved to ${nextStatus}`, 'info');
    } catch (err) {
      const message = err && err.message ? err.message : 'Order status could not be updated.';
      o.status = previousOrderStatus;
      setStatus(previousStatus);
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  const undoDelivery = () => {
    if (busy) return;
    const ok = window.confirm(`Move ${o.id} back to Dispatched and restore its fulfillment inventory?`);
    if (!ok) return;
    persistStatus('Dispatched', 'Delivery marked in error; reopened to dispatched.');
  };
  const reopenForCorrection = () => {
    if (busy) return;
    const ok = window.confirm(`Move ${o.id} back to Allocated so its items can be corrected? No physical inventory will be posted or reversed.`);
    if (!ok) return;
    persistStatus('Allocated', 'Reopened by operations to correct order items before delivery.');
  };
  const actionLabel = needsApproval ? 'Approve' : actionStatus ? `Mark ${actionStatus}` : '';
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {editOpen && canEditOrder && <NewOrderModal editOrder={o} onClose={() => setEditOpen(false)} onSaved={saved => { Object.assign(o, saved); setStatus(saved.status || o.status); }} />}
      {fulfillmentLotsOpen && <OrderFulfillmentLotsModal order={o} onClose={() => setFulfillmentLotsOpen(false)} onSaved={() => { setError(''); force(x => x + 1); }} />}
      {warehouseOpen && <OrderWarehouseModal order={o} onClose={() => setWarehouseOpen(false)} onSaved={() => force(x => x + 1)} />}
      {removalOpen && <RemovalRequestModal entityType="order" entityId={o.id} entityLabel={`${o.id} - ${orderCustomerName}`} onClose={() => setRemovalOpen(false)} onRequested={() => back && back()} />}
      {invoiceOpen && <InvoiceIssueModal order={o} customer={c} onClose={() => setInvoiceOpen(false)} onIssued={saved => { Object.assign(o, saved); setStatus(saved.status || o.status); force(x => x + 1); }} />}
      {chargeOpen && <ChargeInvoiceModal order={o} customer={c} onClose={() => setChargeOpen(false)} onCharged={saved => { Object.assign(o, saved); setStatus(saved.status || o.status); force(x => x + 1); }} />}
      <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />Back to Orders</button>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className="disp" style={{ margin: 0, fontSize: 32, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{o.id}</h1>
            <Pill status={effectiveStatus}>{effectiveStatus}</Pill>
            <Pill status={billingScan.invoiceLabel}>{billingScan.invoiceLabel}</Pill>
            <Pill status={billingScan.paymentStatus}>{billingScan.paymentStatus}</Pill>
            {rpOrderIsBackorder(o) && <Pill status="On Hold">Advance order</Pill>}
          </div>
          {attention && <div style={{ color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 750, marginTop: 4 }}>{attention}</div>}
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 4 }}>{orderCustomerName} · {c.tier} · {o.reason}</div>
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canEditOrder && <button className="btn btn-sm" onClick={() => setEditOpen(true)}><Icon name="pen" size={15} />Edit</button>}
          {canReopenForCorrection && <button className="btn btn-sm" disabled={busy} onClick={reopenForCorrection} style={{ color: 'var(--amber-deep)' }}><Icon name="refresh" size={15} />Reopen to Edit</button>}
          <button className="btn btn-sm" onClick={() => toast(`Duplicated ${o.id} as a draft`, 'info')}><Icon name="orders" size={15} />Duplicate</button>
          {canUndoDelivery && <button className="btn btn-sm" disabled={busy} onClick={undoDelivery} style={{ color: 'var(--amber-deep)' }}><Icon name="refresh" size={15} />Undo Delivered</button>}
          {canRemoveOrder && <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setRemovalOpen(true)}><Icon name="x" size={15} />{directRemoval ? 'Remove' : 'Request Removal'}</button>}
          {actionStatus && <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => persistStatus(actionStatus)}><Icon name="check" size={15} />{busy ? 'Saving...' : actionLabel}</button>}
        </div>
      </div>

      {o.flag && <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--warn-bg)', border: '1px solid oklch(0.7 0.13 75 / 0.35)', color: 'var(--amber-deep)', padding: '11px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13.5, fontWeight: 500 }}><Icon name="alert" size={17} />{o.flag} — requires sales manager approval.</div>}

      {rpOrderIsBackorder(o) && <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '11px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13.5, fontWeight: 600 }}><Icon name="inventory" size={17} />Advance order. Its quantities are committed against incoming stock; physical inventory will post only when the order is fulfilled.</div>}

      {orderInvoiceLocked && <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--ink-soft)', padding: '11px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13.5, fontWeight: 650 }}><Icon name="lock" size={17} />Invoice {o.invoiceNumber || o.invoice_number || 'history'} locks this order's items and pricing. Void or reconcile the invoice before editing the order.</div>}

      {error && <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13.5, fontWeight: 700 }}><Icon name="alert" size={17} />{error}</div>}

      {shopifyInventoryOpen && <ShopifyInventoryReconciliationCard order={o} onResolved={async () => {
        await rpRefreshAfterMutation(`Shopify inventory reconciled for ${o.id}`);
        const refreshed = RP.ORDERS.find(x => x.id === id);
        if (refreshed) {
          Object.assign(o, refreshed);
          setStatus(refreshed.status || status);
          force(x => x + 1);
        }
      }} />}

      {/* Status stepper */}
      {typeof EmailDeliveryStatus === 'function' && <EmailDeliveryStatus entityType="order" entityId={o.id} />}
      <div className="card rp-stepper" style={{ padding: '20px 24px', marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 13, left: 14, right: 14, height: 2, background: 'var(--line)' }} />
          <div style={{ position: 'absolute', top: 13, left: 14, height: 2, background: 'var(--ok)', width: `calc(${(curIdx / (ORDER_STAGES.length - 1)) * 100}% - 28px)`, transition: 'width .4s' }} />
          {ORDER_STAGES.map((s, i) => {
            const done = i <= curIdx;
            return (
              <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, position: 'relative', zIndex: 1, flex: 1 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: done ? 'var(--ok)' : 'var(--surface)', border: '2px solid ' + (done ? 'var(--ok)' : 'var(--line)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{done ? <Icon name="check" size={15} /> : <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--faint)' }} />}</div>
                <span style={{ fontSize: 11.5, fontWeight: i === curIdx ? 700 : 500, color: i === curIdx ? 'var(--ink)' : 'var(--muted)' }}>{s}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 18 }}>
        {/* Items */}
        <div>
          <div className="card" style={{ overflow: 'hidden', marginBottom: 18 }}>
            <table className="rp-table">
              <thead><tr><th>SKU</th><th>Lot</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Container Fee</th><th className="num">Line Total</th></tr></thead>
              <tbody>
                {o.lines.map((l, i) => {
                  const s = RP.sku(l.sku), inventorySku = l.inventorySku || l.inventorySkuId || l.sku, inv = RP.INV.find(x => x.sku === inventorySku && (!o.source || x.loc === o.source)) || RP.INV.find(x => x.sku === inventorySku);
                  const price = l.price != null ? Number(l.price) : rpSkuPrice(l.sku);
                  const deposit = l.deposit != null ? Number(l.deposit) : (s && s.deposit || 0);
                  const recyclingFee = l.recyclingFee != null ? Number(l.recyclingFee) : (s && s.recyclingFee || 0);
                  const containerFee = deposit + recyclingFee;
                  const unit = String(l.unit || (s && s.unit) || 'case').toLowerCase();
                  const caseQty = unit === 'item' ? 0 : Number(l.caseQty || l.caseQuantity || (unit === 'can' ? Number(l.qty || 0) / 12 : l.qty) || 0);
                  const qtyLabel = unit === 'item' ? `${l.qty} item${Number(l.qty) === 1 ? '' : 's'}` : unit === 'can' ? `${l.qty} can${Number(l.qty) === 1 ? '' : 's'}` : `${l.qty} cs`;
                  return (
                    <tr key={i}>
                      <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FlavorChip sku={s || l.sku} size={26} /><div><div style={{ fontWeight: 500 }}>{rpSkuDisplayName(rpSkuItem(s || l.sku) || 'Unknown product')}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{rpSkuExternalIdentity(rpSkuItem(s || l.sku) || '')}</div></div></div></td>
                      <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{unit === 'item' ? '—' : l.lot || (inv ? inv.lot : 'Lot pending')}</td>
                      <td className="num"><div>{qtyLabel}</div>{unit === 'can' && <div style={{ fontSize: 11, color: 'var(--faint)' }}>{caseQty.toFixed(4)} cs</div>}</td>
                      <td className="num">{RP.money2(price)}</td>
                      <td className="num" style={{ color: 'var(--muted)' }}>{RP.money2(containerFee * l.qty)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{RP.money2(price * l.qty)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
              <div style={{ width: 240, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13.5 }}>
                <PricingBreakdown order={o} totals={t} />
              </div>
            </div>
          </div>
          {/* Activity */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle>Activity Log</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {timeline.map((a, i, arr) => (
                <div key={i} style={{ display: 'flex', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: i === 0 ? 'var(--amber)' : a.tone === 'danger' ? 'var(--danger)' : a.tone === 'ok' ? 'var(--ok)' : 'var(--line)', marginTop: 5 }} />
                    {i < arr.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--line-2)' }} />}
                  </div>
                  <div style={{ paddingBottom: 16 }}>
                    <div style={{ fontSize: 13, color: 'var(--ink)' }}>{a.title}</div>
                    {a.detail && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{a.detail}</div>}
                    <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>{a.when} - {a.actor}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Side */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle>Customer</SectionTitle>
            <div onClick={() => !rpOrderIsQuickSale(o) && go('customers', { type: 'customer', id: c.id })} style={{ cursor: rpOrderIsQuickSale(o) ? 'default' : 'pointer' }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{orderCustomerName}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', margintop: 2 }}>{c.city}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 14, fontSize: 13 }}>
              <Row k="Terms" v={c.terms} /><Row k="Credit" v={<Pill status={c.credit} />} /><Row k="Assigned rep" v={c.rep} /><Row k="Price list" v={c.tier} /><Row k="12-pack price" v={`${RP.money2(rpCustomerCasePrice(c))}${rpCustomerCustomCasePrice(c) > 0 ? ' · Custom' : ' · Master'}`} />
            </div>
          </div>
          <OrderInvoiceCard order={o} customer={c} onIssue={() => setInvoiceOpen(true)} onCharge={() => setChargeOpen(true)} onOpenInvoice={openAssociatedInvoice} onRefresh={() => force(x => x + 1)} />
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle>Fulfillment</SectionTitle>
            {canChangeWarehouse && <button className="btn btn-sm" style={{ marginBottom: 12 }} onClick={() => setWarehouseOpen(true)}><Icon name="pen" size={15} />Change warehouse</button>}
            {canChangeWarehouse && <button className="btn btn-sm" style={{ marginBottom: 12, marginLeft: 8 }} onClick={() => setFulfillmentLotsOpen(true)}><Icon name="pen" size={15} />Edit fulfillment lots</button>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
              <Row k="Source" v={(o.source && RP.loc(o.source)?.name) || (o.shopifyOrderId ? 'Shopify sale — no warehouse assigned' : 'Not assigned')} /><Row k="Method" v={o.delivery} /><Row k="Requested" v={<span className="mono">{rpOrderDateLabel(o)}</span>} /><Row k="Cases" v={t.cases + ' cs'} />{!o.shopifyOrderId && <Row k="CHEP pallets" v="1" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SHOPIFY_RECON_MICRO_STATUSES = ['Available', 'Fronted to Rep', 'Purchased by Rep'];
const SHOPIFY_RECON_CASE_TO_CAN_SKU = {
  'RP-12-BO': 'RP-CAN-BO',
  'RP-12-BR': 'RP-CAN-BR',
  'RP-12-GC': 'RP-CAN-GC',
  'RP-12-KL': 'RP-CAN-KL',
  'RP-12-PL': 'RP-CAN-PL',
  'RP-12-PM': 'RP-CAN-PM'
};
const SHOPIFY_RECON_CAN_TO_CASE_SKU = Object.entries(SHOPIFY_RECON_CASE_TO_CAN_SKU).reduce((acc, [caseSku, canSku]) => {
  acc[canSku] = caseSku;
  return acc;
}, {});

function ShopifyInventoryBatchResolveModal({ rows, onClose, onResolved }) {
  const candidates = (rows || []).filter(row => row && row.inventoryOpen && row.order);
  const orders = candidates.map(row => row.order);
  const firstOrder = orders[0] || {};
  const locations = (RP.LOCATIONS || []).filter(location => location && location.id && location.type !== 'FBA');
  const today = new Date().toISOString().slice(0, 10);
  const orderDates = orders.map(order => String(order.created || order.createdAt || today).slice(0, 10)).filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date));
  const initialEventDate = orderDates.length && orderDates.every(date => date === orderDates[0]) ? orderDates[0] : today;
  const firstReconciliation = firstOrder.shopifyInventoryReconciliation || {};
  const initialSourceLocationId = firstReconciliation.sourceLocationId || firstOrder.source || (locations[0] && locations[0].id) || '';
  const initialSourceLocation = locations.find(location => location.id === initialSourceLocationId) || {};
  const initialEventId = firstReconciliation.eventId || (firstReconciliation.resolution && firstReconciliation.resolution.eventId) || '';
  const initialEventLabel = (firstReconciliation.resolution && firstReconciliation.resolution.eventLabel) || `Shopify POS ${initialEventDate}`;
  const eventOptions = (RP.EVENTS || [])
    .filter(event => {
      if (!event || !event.id) return false;
      const status = String(event.status || '').toLowerCase();
      return event.id === initialEventId || !['cancelled', 'lost', 'settled'].includes(status);
    })
    .sort(typeof eventSort === 'function' ? eventSort : (a, b) => String(a.eventDate || '').localeCompare(String(b.eventDate || '')));
  const repRole = role => String(role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const repRoles = new Set(['admin', 'leadership', 'ops', 'sales_manager', 'rep', 'partner', 'marketing', 'dispatch', 'warehouse']);
  const repOptions = (RP.USER_PROFILES || [])
    .filter(profile => profile && profile.active !== false && repRoles.has(repRole(profile.role)))
    .map(profile => ({
      id: profile.id,
      label: profile.name || profile.displayName || profile.email || profile.id,
      territory: profile.territory || ''
    }))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  const [sourceLocationId, setSourceLocationId] = useState(initialSourceLocationId);
  const [assignedRepId, setAssignedRepId] = useState(firstReconciliation.assignedRepId || (firstReconciliation.resolution && firstReconciliation.resolution.assignedRepId) || firstOrder.repId || initialSourceLocation.assignedUserId || '');
  const [eventId, setEventId] = useState(initialEventId);
  const [eventDate, setEventDate] = useState(initialEventDate);
  const [eventLabel, setEventLabel] = useState(initialEventLabel);
  const [lotDefaults, setLotDefaults] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const selectedRows = candidates;
  const selectedOrders = orders;
  const selectedCount = selectedOrders.length;
  const effectiveEventLabel = String(eventLabel || '').trim() || `Shopify POS ${eventDate}`;
  const sourceLocation = (RP.LOCATIONS || []).find(location => location.id === sourceLocationId) || {};
  const allowedStatuses = sourceLocation.type === 'Micro-Warehouse' ? SHOPIFY_RECON_MICRO_STATUSES : ['Available'];
  const unitForLine = line => String(line.unit || '').toLowerCase().includes('can') ? 'can' : 'case';
  const rawInventorySkuId = line => String(line.inventorySku || line.inventorySkuId || line.sku || '').trim();
  const caseSkuForLine = line => {
    const raw = rawInventorySkuId(line).toUpperCase();
    if (raw.startsWith('RP-CAN-')) return SHOPIFY_RECON_CAN_TO_CASE_SKU[raw] || '';
    return raw;
  };
  const canSkuForLine = line => {
    const soldSku = String(line.sku || '').trim().toUpperCase();
    if (soldSku.startsWith('RP-CAN-')) return soldSku;
    return SHOPIFY_RECON_CASE_TO_CAN_SKU[caseSkuForLine(line)] || '';
  };
  const inventorySkuId = line => unitForLine(line) === 'can' ? caseSkuForLine(line) : rawInventorySkuId(line);
  const caseQuantity = line => Number(line.caseQty || line.caseQuantity || (unitForLine(line) === 'can' ? Number(line.qty || 0) / 12 : line.qty) || 0);
  const batchMatchesSelectedEvent = batch => {
    if (eventId) return batch.eventId === eventId || (!batch.eventId && batch.eventDate === eventDate && batch.eventLabel === effectiveEventLabel);
    return !batch.eventId && batch.eventDate === eventDate && batch.eventLabel === effectiveEventLabel;
  };
  const batchMatchesSelectedRep = batch => eventId
    ? !batch.assignedRepId || String(batch.assignedRepId) === String(assignedRepId || '')
    : String(batch.assignedRepId || '') === String(assignedRepId || '');
  const movementCanDelta = movement => {
    const qty = Number(movement.quantity || 0);
    const type = String(movement.type || movement.movementType || '').toLowerCase();
    const unit = String(movement.unit || '').toLowerCase();
    if (type === 'open_case') return unit === 'case' ? qty * 12 : qty;
    if (type === 'can_sale') return -qty;
    if (type === 'count_adjustment') return qty;
    return 0;
  };
  const batchRemainingCans = batch => Math.max(0, Math.round((RP.OPEN_CASE_MOVEMENTS || [])
    .filter(movement => movement.batchId === batch.id)
    .reduce((sum, movement) => sum + movementCanDelta(movement), 0) * 100) / 100);
  const lineGroups = selectedRows.reduce((groups, row) => {
    (row.order.lines || []).forEach(line => {
      const skuId = inventorySkuId(line);
      if (!skuId) return;
      const key = skuId.toUpperCase();
      const existing = groups.get(key) || {
        canSkuId: canSkuForLine(line),
        caseQuantity: 0,
        inventorySkuId: skuId,
        lines: 0,
        orderCount: 0,
        orders: new Set(),
        soldQuantity: 0,
        title: rpSkuDisplayName(rpSkuItem(line.sku) || 'Unknown product'),
        unit: unitForLine(line),
        visualSku: line.sku
      };
      existing.caseQuantity += caseQuantity(line);
      existing.lines += 1;
      existing.orders.add(row.order.id);
      existing.orderCount = existing.orders.size;
      existing.soldQuantity += Number(line.qty || 0);
      if (existing.unit !== 'can' && unitForLine(line) === 'can') existing.unit = 'can';
      if (!existing.canSkuId) existing.canSkuId = canSkuForLine(line);
      groups.set(key, existing);
    });
    return groups;
  }, new Map());
  const defaultRows = Array.from(lineGroups.values()).map(group => ({
    ...group,
    orderIds: Array.from(group.orders)
  })).sort((a, b) => String(a.inventorySkuId || '').localeCompare(String(b.inventorySkuId || '')));
  const lotOptions = group => {
    const caseOptions = (RP.INV || [])
      .filter(row => row && row.sku === group.inventorySkuId && row.loc === sourceLocationId && Number(row.qty || 0) > 0)
      .filter(row => String(row.unit || 'case').toLowerCase() !== 'can')
      .filter(row => allowedStatuses.includes(row.status || 'Available'))
      .map(row => ({ ...row, unit: 'case' }));
    if (group.unit !== 'can') return caseOptions.sort((a, b) => String(a.lot || '').localeCompare(String(b.lot || '')));

    const openBatchOptions = (RP.OPEN_CASE_BATCHES || [])
      .filter(batch =>
        batch.status === 'Active'
        && batch.sourceLocationId === sourceLocationId
        && batchMatchesSelectedRep(batch)
        && batchMatchesSelectedEvent(batch)
        && batch.caseSkuId === group.inventorySkuId
        && batch.canSkuId === group.canSkuId
      )
      .map(batch => ({
        lot: batch.lot,
        owner: 'Open Event Stock',
        qty: batchRemainingCans(batch),
        status: 'Open Event Stock',
        unit: 'can'
      }))
      .filter(option => Number(option.qty || 0) > 0);
    const byLot = new Map();
    [...caseOptions, ...openBatchOptions].forEach(option => {
      const key = option.lot || 'UNASSIGNED';
      const current = byLot.get(key);
      if (!current || String(option.unit || 'case') === 'case') byLot.set(key, option);
    });
    return Array.from(byLot.values()).sort((a, b) => String(a.lot || '').localeCompare(String(b.lot || '')));
  };
  const selectedLot = group => {
    const options = lotOptions(group);
    return lotDefaults[group.inventorySkuId] || (options.length === 1 ? options[0].lot : '');
  };
  const selectedLotForLine = line => {
    const skuId = inventorySkuId(line).toUpperCase();
    const group = defaultRows.find(item => String(item.inventorySkuId || '').toUpperCase() === skuId);
    return group ? selectedLot(group) : '';
  };
  const setGroupLot = (group, lot) => setLotDefaults(prev => ({ ...prev, [group.inventorySkuId]: lot }));
  const changeSourceLocation = value => {
    setSourceLocationId(value);
    setLotDefaults({});
    const nextLocation = locations.find(location => location.id === value) || {};
    if (!assignedRepId && nextLocation.assignedUserId) setAssignedRepId(nextLocation.assignedUserId);
  };
  const changeEventDate = value => {
    const nextDate = value || today;
    setEventDate(nextDate);
    setEventLabel(prev => prev === `Shopify POS ${eventDate}` ? `Shopify POS ${nextDate}` : prev);
  };
  const changeEvent = value => {
    setEventId(value);
    setLotDefaults({});
    const event = eventOptions.find(item => item.id === value);
    if (!event) return;
    const selectedDate = typeof eventDateValue === 'function' ? eventDateValue(event) : String(event.eventDate || '').slice(0, 10);
    if (selectedDate) setEventDate(selectedDate);
    if (event.name) setEventLabel(event.name);
    if (event.defaultSourceLocationId && event.defaultSourceLocationId !== sourceLocationId) changeSourceLocation(event.defaultSourceLocationId);
  };
  const submit = async () => {
    if (busy) return;
    setError('');
    setResult(null);
    if (!selectedCount) {
      setError('Select at least one order.');
      return;
    }
    if (!sourceLocationId) {
      setError('Select a source location.');
      return;
    }
    const missingLineIds = selectedOrders.flatMap(order => (order.lines || []).filter(line => !line.id));
    if (missingLineIds.length) {
      setError('Refresh the dashboard before batch resolving; imported line IDs are missing.');
      return;
    }
    const missingLots = defaultRows.filter(group => !selectedLot(group));
    if (missingLots.length) {
      setError(`Select a lot for ${rpSkuExternalIdentity(rpSkuItem(missingLots[0].inventorySkuId) || missingLots[0].inventorySkuId)}.`);
      return;
    }

    setBusy(true);
    try {
      const results = [];
      let resolvedCount = 0;
      for (const order of selectedOrders) {
        setResult({ failedCount: results.filter(item => !item.ok).length, inProgress: order.id, resolvedCount, results, total: selectedCount });
        try {
          await rpApi('/api/shopify/reconciliation', {
            method: 'POST',
            body: JSON.stringify({
              action: 'resolve',
              assignedRepId,
              eventId: eventId || undefined,
              eventDate,
              eventLabel: effectiveEventLabel,
              lines: (order.lines || []).map(line => ({
                caseQuantity: caseQuantity(line),
                inventorySkuId: inventorySkuId(line),
                lineId: line.id,
                lot: selectedLotForLine(line)
              })),
              notes: note,
              orderId: order.id,
              sourceLocationId
            })
          });
          resolvedCount += 1;
          results.push({ ok: true, orderId: order.id });
        } catch (err) {
          results.push({ ok: false, orderId: order.id, error: err && err.message ? err.message : `Could not reconcile ${order.id}.` });
        }
      }
      const summary = { failedCount: results.filter(item => !item.ok).length, resolvedCount, results, total: selectedCount };
      setResult(summary);
      if (summary.failedCount) {
        const firstFailure = results.find(item => !item.ok);
        const message = firstFailure && firstFailure.error ? firstFailure.error : `${summary.failedCount} order(s) could not be reconciled.`;
        setError(message);
        toast(`Resolved ${resolvedCount}, ${summary.failedCount} failed`, 'warn');
        if (resolvedCount) await rpRefreshAfterMutation('Shopify inventory batch partially resolved');
        return;
      }
      toast(`Resolved ${resolvedCount || selectedCount} Shopify inventory orders`, 'ok');
      if (onResolved) await onResolved();
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Shopify inventory batch could not be reconciled.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Batch Resolve Shopify" sub={`${selectedCount} selected queue order${selectedCount === 1 ? '' : 's'}`} onClose={onClose} width={1080}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !selectedCount} onClick={submit}><Icon name="check" size={16} />{busy ? 'Resolving...' : `Resolve ${selectedCount} Orders`}</button></>}>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      {result && result.inProgress && (
        <div style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>
          Resolving {result.inProgress} ({(result.resolvedCount || 0) + (result.failedCount || 0) + 1} of {result.total || selectedCount})
        </div>
      )}
      {result && result.failedCount > 0 && (
        <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>
          {(result.results || []).filter(item => !item.ok).slice(0, 4).map(item => <div key={item.orderId}>{item.orderId}: {item.error}</div>)}
        </div>
      )}
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 12px', color: 'var(--muted)', fontSize: 13, marginBottom: 14 }}>
        Batch will process the selected orders one at a time using the normal Shopify inventory resolver.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(210px, 1fr) minmax(220px, 1fr) minmax(150px, 0.6fr) minmax(220px, 1fr)', gap: 12, marginBottom: 14 }}>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Source location
          <select className="field" value={sourceLocationId} onChange={e => changeSourceLocation(e.target.value)}>
            {locations.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Sales rep
          <select className="field" value={assignedRepId} onChange={e => setAssignedRepId(e.target.value)}>
            <option value="">Unassigned</option>
            {repOptions.map(rep => <option key={rep.id} value={rep.id}>{rep.label}{rep.territory ? ` - ${rep.territory}` : ''}</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Event
          <select className="field" value={eventId} onChange={e => changeEvent(e.target.value)}>
            <option value="">No event / POS pool</option>
            {eventOptions.map(event => {
              const labelDate = typeof eventDateValue === 'function' ? eventDateValue(event) : String(event.eventDate || '').slice(0, 10);
              return <option key={event.id} value={event.id}>{event.name || event.id}{labelDate ? ` - ${labelDate}` : ''}</option>;
            })}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Event date
          <input className="field" type="date" value={eventDate} onChange={e => changeEventDate(e.target.value)} />
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Event label
          <input className="field" value={eventLabel} onChange={e => setEventLabel(e.target.value)} placeholder={`Shopify POS ${eventDate}`} />
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700, gridColumn: '1 / -1' }}>Note
          <input className="field" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional audit note" />
        </label>
      </div>
      <SectionTitle>Inventory Lots</SectionTitle>
      <div style={{ overflowX: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
        <table className="rp-table">
          <thead><tr><th>Item</th><th>Inventory SKU</th><th className="num">Orders</th><th className="num">Impact</th><th>Lot</th></tr></thead>
          <tbody>
            {defaultRows.map(group => {
              const options = lotOptions(group);
              const lot = selectedLot(group);
              return (
                <tr key={group.inventorySkuId}>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={RP.sku(group.visualSku) || group.visualSku} size={24} /><div><div style={{ fontWeight: 700 }}>{group.title}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{group.unit === 'can' ? `${group.soldQuantity} cans sold` : `${group.caseQuantity.toFixed(4)} cases sold`}</div></div></div></td>
                  <td className="mono" style={{ fontSize: 12 }}>{rpSkuExternalIdentity(rpSkuItem(group.inventorySkuId) || group.inventorySkuId)}</td>
                  <td className="num">{group.orderCount}</td>
                  <td className="num">{group.unit === 'can' ? `${group.soldQuantity} can` : `${group.caseQuantity.toFixed(4)} cs`}</td>
                  <td>
                    <select className="field" value={lot} onChange={e => setGroupLot(group, e.target.value)} style={{ minWidth: 230 }}>
                      <option value="">Select lot</option>
                      {options.map(option => {
                        const optionUnit = String(option.unit || 'case').toLowerCase() === 'can' ? 'can' : 'cs';
                        const optionQty = optionUnit === 'can' ? Number(option.qty || 0).toFixed(0) : Number(option.qty || 0).toFixed(2);
                        return <option key={`${group.inventorySkuId}-${option.lot}-${option.status}-${option.owner}-${optionUnit}`} value={option.lot}>{optionUnit === 'can' ? option.lot : rpPrintedLotLabel(group.inventorySkuId, option.lot)} - {optionQty} {optionUnit} - {option.status}</option>;
                      })}
                    </select>
                  </td>
                </tr>
              );
            })}
            {!defaultRows.length && <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--faint)', padding: 18 }}>Select orders to choose inventory lots.</td></tr>}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function ShopifyInventoryReconciliationCard({ order, onResolved }) {
  const reconciliation = order.shopifyInventoryReconciliation || {};
  const locations = (RP.LOCATIONS || []).filter(location => location && location.id && location.type !== 'FBA');
  const initialSourceLocationId = reconciliation.sourceLocationId || order.source || (locations[0] && locations[0].id) || '';
  const initialSourceLocation = locations.find(location => location.id === initialSourceLocationId) || {};
  const today = new Date().toISOString().slice(0, 10);
  const orderCreatedDate = String(order.created || order.createdAt || order.date || today).slice(0, 10);
  const initialEventDate = /^\d{4}-\d{2}-\d{2}$/.test(reconciliation.resolution && reconciliation.resolution.eventDate || '')
    ? reconciliation.resolution.eventDate
    : (/^\d{4}-\d{2}-\d{2}$/.test(orderCreatedDate) ? orderCreatedDate : today);
  const initialEventLabel = (reconciliation.resolution && reconciliation.resolution.eventLabel) || `Shopify POS ${initialEventDate}`;
  const initialEventId = reconciliation.eventId || (reconciliation.resolution && reconciliation.resolution.eventId) || '';
  const eventOptions = (RP.EVENTS || [])
    .filter(event => {
      if (!event || !event.id) return false;
      const status = String(event.status || '').toLowerCase();
      return event.id === initialEventId || !['cancelled', 'lost', 'settled'].includes(status);
    })
    .sort(typeof eventSort === 'function' ? eventSort : (a, b) => String(a.eventDate || '').localeCompare(String(b.eventDate || '')));
  const repRole = role => String(role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const repRoles = new Set(['admin', 'leadership', 'ops', 'sales_manager', 'rep', 'partner', 'marketing', 'dispatch', 'warehouse']);
  const repOptions = (RP.USER_PROFILES || [])
    .filter(profile => profile && profile.active !== false && repRoles.has(repRole(profile.role)))
    .map(profile => ({
      id: profile.id,
      label: profile.name || profile.displayName || profile.email || profile.id,
      role: profile.role || '',
      territory: profile.territory || ''
    }))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  const [sourceLocationId, setSourceLocationId] = useState(initialSourceLocationId);
  const [assignedRepId, setAssignedRepId] = useState(reconciliation.assignedRepId || (reconciliation.resolution && reconciliation.resolution.assignedRepId) || order.repId || initialSourceLocation.assignedUserId || '');
  const [eventDate, setEventDate] = useState(initialEventDate);
  const [eventLabel, setEventLabel] = useState(initialEventLabel);
  const [eventId, setEventId] = useState(initialEventId);
  const [lots, setLots] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const effectiveEventLabel = String(eventLabel || '').trim() || `Shopify POS ${eventDate}`;
  const sourceLocation = (RP.LOCATIONS || []).find(location => location.id === sourceLocationId) || {};
  const allowedStatuses = sourceLocation.type === 'Micro-Warehouse' ? SHOPIFY_RECON_MICRO_STATUSES : ['Available'];
  const lineKey = (line, index) => line.id || `${line.sku}-${index}`;
  const unitForLine = line => String(line.unit || '').toLowerCase().includes('can') ? 'can' : 'case';
  const rawInventorySkuId = line => String(line.inventorySku || line.inventorySkuId || line.sku || '').trim();
  const caseSkuForLine = line => {
    const raw = rawInventorySkuId(line).toUpperCase();
    if (raw.startsWith('RP-CAN-')) return SHOPIFY_RECON_CAN_TO_CASE_SKU[raw] || '';
    return raw;
  };
  const canSkuForLine = line => {
    const soldSku = String(line.sku || '').trim().toUpperCase();
    if (soldSku.startsWith('RP-CAN-')) return soldSku;
    return SHOPIFY_RECON_CASE_TO_CAN_SKU[caseSkuForLine(line)] || '';
  };
  const batchMatchesSelectedEvent = batch => {
    if (eventId) return batch.eventId === eventId || (!batch.eventId && batch.eventDate === eventDate && batch.eventLabel === effectiveEventLabel);
    return !batch.eventId && batch.eventDate === eventDate && batch.eventLabel === effectiveEventLabel;
  };
  const batchMatchesSelectedRep = batch => eventId
    ? !batch.assignedRepId || String(batch.assignedRepId) === String(assignedRepId || '')
    : String(batch.assignedRepId || '') === String(assignedRepId || '');
  const inventorySkuId = line => unitForLine(line) === 'can' ? caseSkuForLine(line) : rawInventorySkuId(line);
  const caseQuantity = line => Number(line.caseQty || line.caseQuantity || (unitForLine(line) === 'can' ? Number(line.qty || 0) / 12 : line.qty) || 0);
  const lotOptions = line => {
    const caseOptions = (RP.INV || [])
      .filter(row => row && row.sku === inventorySkuId(line) && row.loc === sourceLocationId && Number(row.qty || 0) > 0)
      .filter(row => String(row.unit || 'case').toLowerCase() !== 'can')
      .filter(row => allowedStatuses.includes(row.status || 'Available'))
      .map(row => ({ ...row, unit: 'case' }));
    if (unitForLine(line) !== 'can') return caseOptions.sort((a, b) => String(a.lot || '').localeCompare(String(b.lot || '')));

    const caseSku = caseSkuForLine(line);
    const canSku = canSkuForLine(line);
    const openBatchOptions = (RP.OPEN_CASE_BATCHES || [])
      .filter(batch =>
        batch.status === 'Active'
        && batch.sourceLocationId === sourceLocationId
        && batchMatchesSelectedRep(batch)
        && batchMatchesSelectedEvent(batch)
        && batch.caseSkuId === caseSku
        && batch.canSkuId === canSku
      )
      .map(batch => ({
        lot: batch.lot,
        owner: 'Open Event Stock',
        qty: batchRemainingCans(batch),
        status: 'Open Event Stock',
        unit: 'can'
      }))
      .filter(option => Number(option.qty || 0) > 0);
    const byLot = new Map();
    [...caseOptions, ...openBatchOptions].forEach(option => {
      const key = option.lot || 'UNASSIGNED';
      const current = byLot.get(key);
      if (!current || String(option.unit || 'case') === 'case') byLot.set(key, option);
    });
    return Array.from(byLot.values()).sort((a, b) => String(a.lot || '').localeCompare(String(b.lot || '')));
  };
  const selectedLot = (line, index) => {
    const key = lineKey(line, index);
    const options = lotOptions(line);
    return lots[key] || line.lot || (options.length === 1 ? options[0].lot : '');
  };
  const setLineLot = (line, index, lot) => setLots(prev => ({ ...prev, [lineKey(line, index)]: lot }));
  const changeSourceLocation = value => {
    setSourceLocationId(value);
    setLots({});
    const nextLocation = locations.find(location => location.id === value) || {};
    if (!assignedRepId && nextLocation.assignedUserId) setAssignedRepId(nextLocation.assignedUserId);
  };
  const changeEventDate = value => {
    const nextDate = value || today;
    setEventDate(nextDate);
    setEventLabel(prev => prev === `Shopify POS ${eventDate}` ? `Shopify POS ${nextDate}` : prev);
  };
  const changeEvent = value => {
    setEventId(value);
    setLots({});
    const selected = eventOptions.find(event => event.id === value);
    if (!selected) return;
    const selectedDate = typeof eventDateValue === 'function' ? eventDateValue(selected) : String(selected.eventDate || '').slice(0, 10);
    if (selectedDate) setEventDate(selectedDate);
    if (selected.name) setEventLabel(selected.name);
    if (selected.defaultSourceLocationId && selected.defaultSourceLocationId !== sourceLocationId) {
      changeSourceLocation(selected.defaultSourceLocationId);
    }
  };
  const movementCanDelta = movement => {
    const qty = Number(movement.quantity || 0);
    const type = String(movement.type || movement.movementType || '').toLowerCase();
    const unit = String(movement.unit || '').toLowerCase();
    if (type === 'open_case') return unit === 'case' ? qty * 12 : qty;
    if (type === 'can_sale') return -qty;
    if (type === 'count_adjustment') return qty;
    return 0;
  };
  const batchRemainingCans = batch => Math.max(0, Math.round((RP.OPEN_CASE_MOVEMENTS || [])
    .filter(movement => movement.batchId === batch.id)
    .reduce((sum, movement) => sum + movementCanDelta(movement), 0) * 100) / 100);
  const openBatchForLine = (line, index) => {
    const canSku = canSkuForLine(line);
    const caseSku = caseSkuForLine(line);
    const lot = selectedLot(line, index);
    if (!canSku || !caseSku || !lot) return null;
    return (RP.OPEN_CASE_BATCHES || []).find(batch =>
      batch.status === 'Active'
      && batch.sourceLocationId === sourceLocationId
      && batchMatchesSelectedRep(batch)
      && batchMatchesSelectedEvent(batch)
      && batch.caseSkuId === caseSku
      && batch.canSkuId === canSku
      && batch.lot === lot
    ) || null;
  };
  const canLinePlan = (line, index) => {
    if (unitForLine(line) !== 'can') return null;
    const soldCans = Number(line.qty || 0);
    const batch = openBatchForLine(line, index);
    const remainingBefore = batch ? batchRemainingCans(batch) : 0;
    const shortage = Math.max(0, soldCans - remainingBefore);
    const openedCases = shortage > 0 ? Math.ceil(shortage / 12) : 0;
    return {
      openedCases,
      remainingAfter: Math.max(0, remainingBefore + openedCases * 12 - soldCans),
      remainingBefore,
      soldCans
    };
  };
  const unresolvedCanLines = (order.lines || []).filter((line, index) => {
    return unitForLine(line) === 'can' && !selectedLot(line, index);
  });

  const submit = async (action = 'resolve') => {
    if (busy) return;
    if (!sourceLocationId) {
      setError('Select a source location.');
      return;
    }
    if (action === 'resolve') {
      const missingIds = (order.lines || []).filter(line => !line.id);
      if (missingIds.length) {
        setError('Refresh the dashboard before resolving; imported line IDs are missing.');
        return;
      }
      if (unresolvedCanLines.length) {
        setError('Select a lot for each single-can line before resolving.');
        return;
      }
    }

    setBusy(true);
    setError('');
    try {
      await rpApi('/api/shopify/reconciliation', {
        method: 'POST',
        body: JSON.stringify({
          action,
          assignedRepId,
          eventId: eventId || undefined,
          eventDate,
          eventLabel: effectiveEventLabel,
          orderId: order.id,
          sourceLocationId,
          notes: note,
          lines: action === 'resolve'
            ? (order.lines || []).map((line, index) => ({
                caseQuantity: caseQuantity(line),
                inventorySkuId: inventorySkuId(line),
                lineId: line.id,
                lot: selectedLot(line, index)
              }))
            : []
        })
      });
      toast(action === 'ignore' ? 'Shopify inventory queue item ignored' : 'Shopify inventory reconciled', action === 'ignore' ? 'info' : 'ok');
      if (onResolved) await onResolved();
    } catch (err) {
      const message = err && err.message ? err.message : 'Shopify inventory could not be reconciled.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  const ignore = () => {
    if (!window.confirm(`Ignore inventory reconciliation for ${order.id}?`)) return;
    submit('ignore');
  };

  return (
    <div className="card" style={{ padding: '18px 20px', marginBottom: 18, borderColor: 'oklch(0.7 0.13 75 / 0.35)' }}>
      <SectionTitle right={<Pill status={reconciliation.status || 'Pending'}>{reconciliation.status || 'Pending'}</Pill>}>Shopify Inventory Queue</SectionTitle>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(210px, 1fr) minmax(220px, 1fr) minmax(150px, 0.6fr) minmax(220px, 1fr)', gap: 12, marginBottom: 14 }}>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Source location
          <select className="field" value={sourceLocationId} onChange={e => changeSourceLocation(e.target.value)}>
            {locations.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Sales rep
          <select className="field" value={assignedRepId} onChange={e => setAssignedRepId(e.target.value)}>
            <option value="">Unassigned</option>
            {repOptions.map(rep => <option key={rep.id} value={rep.id}>{rep.label}{rep.territory ? ` - ${rep.territory}` : ''}</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Event
          <select className="field" value={eventId} onChange={e => changeEvent(e.target.value)}>
            <option value="">No event / POS pool</option>
            {eventOptions.map(event => {
              const labelDate = typeof eventDateValue === 'function' ? eventDateValue(event) : String(event.eventDate || '').slice(0, 10);
              return <option key={event.id} value={event.id}>{event.name || event.id}{labelDate ? ` - ${labelDate}` : ''}</option>;
            })}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Event date
          <input className="field" type="date" value={eventDate} onChange={e => changeEventDate(e.target.value)} />
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>Event label
          <input className="field" value={eventLabel} onChange={e => setEventLabel(e.target.value)} placeholder={`Shopify POS ${eventDate}`} />
        </label>
        <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--muted)', fontWeight: 700, gridColumn: '1 / -1' }}>Note
          <input className="field" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional audit note" />
        </label>
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
        <table className="rp-table">
          <thead><tr><th>Sold Item</th><th>Inventory SKU</th><th className="num">Sold Qty</th><th className="num">Inventory Impact</th><th>Lot</th></tr></thead>
          <tbody>
            {(order.lines || []).map((line, index) => {
              const options = lotOptions(line);
              const unit = unitForLine(line);
              const selected = selectedLot(line, index);
              const plan = canLinePlan(line, index);
              return (
                <tr key={lineKey(line, index)}>
                  <td><div style={{ fontWeight: 600 }}>{rpSkuDisplayName(rpSkuItem(line.sku) || 'Unknown product')}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{rpSkuExternalIdentity(rpSkuItem(line.sku) || '')}</div></td>
                  <td className="mono" style={{ fontSize: 12 }}>{rpSkuExternalIdentity(rpSkuItem(inventorySkuId(line)) || inventorySkuId(line))}</td>
                  <td className="num">{line.qty} {unit === 'can' ? 'can' : 'cs'}</td>
                  <td className="num">
                    {unit === 'can' && plan
                      ? <div>
                          <div>{plan.openedCases ? `open ${plan.openedCases} cs` : 'use open cans'}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{plan.remainingBefore} now {'->'} {plan.remainingAfter} left</div>
                        </div>
                      : `${caseQuantity(line).toFixed(4)} cs`}
                  </td>
                  <td>
                    <select className="field" value={selected} onChange={e => setLineLot(line, index, e.target.value)} style={{ minWidth: 190 }}>
                      <option value="">Select lot</option>
                      {options.map(option => {
                        const optionUnit = String(option.unit || 'case').toLowerCase() === 'can' ? 'can' : 'cs';
                        const optionQty = optionUnit === 'can' ? Number(option.qty || 0).toFixed(0) : Number(option.qty || 0).toFixed(2);
                        return <option key={`${option.lot}-${option.status}-${option.owner}-${optionUnit}`} value={option.lot}>{optionUnit === 'can' ? option.lot : rpPrintedLotLabel(inventorySkuId(line), option.lot)} - {optionQty} {optionUnit} - {option.status}</option>;
                      })}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
        <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Single cans open full cases into event stock, then sell loose cans from that batch.</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-sm" disabled={busy} onClick={ignore}><Icon name="x" size={14} />Ignore</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !sourceLocationId} onClick={() => submit('resolve')}><Icon name="check" size={14} />{busy ? 'Saving...' : 'Resolve Inventory'}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, muted }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: muted ? 'var(--muted)' : 'inherit' }}><span style={{ color: 'var(--muted)' }}>{k}</span><span style={{ fontWeight: 500, color: 'var(--ink)', textAlign: 'right' }}>{v}</span></div>;
}

/* ============================================================ CUSTOMERS */
function CustomersView({ go }) {
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [, force] = useState(0);
  const [filters, onFilter] = useFilters();
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const serverFilters = {
    type: filters.Type,
    territory: filters.Territory,
    status: filters.Status,
    risk: filters['Reorder Risk']
  };
  const serverPage = useRpServerPagedCustomers({ limit: 75, search, filters: serverFilters });
  const customers = live ? serverPage.items : rpVisibleCustomers(RP.CUSTOMERS);
  const custDefs = [
    { label: 'Type', options: live ? rpServerFacetOptions(serverPage.facets, 'type') : distinct(customers, c => c.type) },
    { label: 'Territory', options: live ? rpServerFacetOptions(serverPage.facets, 'territory') : distinct(customers, c => c.territory) },
    { label: 'Status', options: live ? rpServerFacetOptions(serverPage.facets, 'status') : distinct(customers, c => c.status) },
    { label: 'Reorder Risk', options: live ? rpServerFacetOptions(serverPage.facets, 'risk') : distinct(customers, c => c.risk) },
  ];
  let rows = customers;
  if (!live && filters.Type) rows = rows.filter(c => c.type === filters.Type);
  if (!live && filters.Territory) rows = rows.filter(c => c.territory === filters.Territory);
  if (!live && filters.Status) rows = rows.filter(c => c.status === filters.Status);
  if (!live && filters['Reorder Risk']) rows = rows.filter(c => c.risk === filters['Reorder Risk']);
  if (!live && search) rows = rows.filter(c => (c.name + c.city + c.type).toLowerCase().includes(search.toLowerCase()));
  const openCustomer = customer => {
    if (live && RP.upsertCustomer) RP.upsertCustomer({ ...customer, skipCreds: true, persisted: true });
    go('customers', { type: 'customer', id: customer.id });
  };
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {modal === 'add' && <AddCustomerModal onClose={() => setModal(null)} onAdded={() => force(x => x + 1)} />}
      {modal === 'order' && <NewOrderModal onClose={() => setModal(null)} />}
      <PageHead eyebrow="Accounts & Doors" title="Customers" actions={<button className="btn btn-sm btn-primary" onClick={() => setModal('add')}><Icon name="plus" size={15} />Add Customer</button>} />
      <FilterBar defs={custDefs} value={filters} onChange={onFilter} search={search} onSearch={setSearch} searchPlaceholder="Search customers…" />
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="rp-table">
          <thead><tr><th>Account</th><th>Type</th><th>Territory</th><th>Rep</th><th>Last Order</th><th>Reorder Risk</th><th className="num">YTD</th><th></th></tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openCustomer(c)}>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 11 }}><Avatar name={c.name} size={30} color={c.status === 'Prospect' ? 'var(--faint)' : 'var(--navy)'} /><div><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{c.city}</div></div></div></td>
                <td style={{ color: 'var(--muted)' }}>{c.type}</td>
                <td style={{ color: 'var(--muted)' }}>{c.territory}</td>
                <td style={{ color: 'var(--muted)' }}>{c.rep}</td>
                <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{c.lastOrder || '—'}{c.hold && <Icon name="alert" size={13} style={{ color: 'var(--danger)', marginLeft: 6, verticalAlign: 'middle' }} />}</td>
                <td><Pill status={c.risk} /></td>
                <td className="num" style={{ fontWeight: 600 }}>{RP.money(c.ytd)}</td>
                <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
              </tr>
            ))}
            {live && serverPage.initialLoading && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}><Icon name="refresh" size={18} /><div style={{ marginTop: 7, fontWeight: 700, color: 'var(--ink)' }}>Loading customers...</div></td></tr>}
            {live && !serverPage.initialLoading && !serverPage.loaded && serverPage.error && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--danger)', padding: 30 }}><div style={{ fontWeight: 700 }}>{serverPage.error}</div><button type="button" className="btn btn-sm" onClick={serverPage.retry} style={{ marginTop: 10 }}>Try Again</button></td></tr>}
            {(!live || serverPage.loaded) && rows.length === 0 && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No customers match these filters.</td></tr>}
          </tbody>
        </table>
        {live && serverPage.loaded && <RpServerLoadMore {...serverPage} label="customers" onLoadMore={serverPage.loadMore} onRetry={serverPage.loadMore} />}
      </div>
    </div>
  );
}

function CustomerShopifySyncCard({ c, force }) {
  const [busy, setBusy] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const live = !(window.RP_AUTH && window.RP_AUTH.demo);
  const profile = rpAuthProfile();
  const canPush = ['admin', 'leadership', 'ops'].includes(rpNormalizeRole(profile.role));

  const requestPreview = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/shopify/push-customer', {
        method: 'POST',
        body: JSON.stringify({ customerId: c.id, dryRun: true })
      });
      setPreview(response.preview);
      toast(response.message || 'Shopify customer preview ready', response.preview && response.preview.warnings && response.preview.warnings.length ? 'warn' : 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not preview Shopify customer push.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const pushCustomer = async () => {
    setPushing(true);
    setError('');
    try {
      const response = await rpApi('/api/shopify/push-customer', {
        method: 'POST',
        body: JSON.stringify({ customerId: c.id, dryRun: false, includeContacts: true })
      });
      if (response.customer) {
        c.shopifyCompanyId = response.customer.shopifyCompanyId || c.shopifyCompanyId;
        c.shopifyCustomerId = response.customer.shopifyCustomerId || c.shopifyCustomerId;
      }
      await rpRefreshAfterMutation('Shopify customer pushed');
      force && force();
      setPreview(response.preview || preview);
      toast(response.message || 'Customer pushed to Shopify', response.warnings && response.warnings.length ? 'warn' : 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not push customer to Shopify.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setPushing(false);
    }
  };

  const p = preview || null;
  const mode = p ? p.mode : (c.shopifyCompanyId ? 'update' : 'create');
  const warningCount = p && p.warnings ? p.warnings.length : 0;
  const linked = !!(c.shopifyCompanyId || (p && p.customer && p.customer.shopifyCompanyId));

  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={<Pill status={linked ? 'Active' : (warningCount ? 'Pending' : 'Invited')}>{linked ? 'Linked' : (mode === 'create' ? 'Ready' : 'Review')}</Pill>}>Shopify Sync</SectionTitle>
      <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.42, marginBottom: 12 }}>B2B company, buyer, location, and contact mirror.</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button className="btn btn-sm" disabled={!live || busy || pushing} onClick={requestPreview} style={{ opacity: !live || busy || pushing ? 0.65 : 1 }}><Icon name={busy ? 'refresh' : 'search'} size={14} />{busy ? 'Previewing...' : 'Preview Push'}</button>
        <button className="btn btn-sm btn-primary" disabled={!live || pushing || busy || !canPush || (p && p.blocked)} onClick={pushCustomer} style={{ opacity: !live || pushing || busy || !canPush || (p && p.blocked) ? 0.65 : 1 }}><Icon name={pushing ? 'refresh' : 'box'} size={14} />{pushing ? 'Pushing...' : 'Push to Shopify'}</button>
      </div>
      {!live && <div style={{ fontSize: 12.5, color: 'var(--faint)', marginBottom: 8 }}>Shopify writes are disabled while using developer data.</div>}
      {!canPush && live && <div style={{ fontSize: 12.5, color: 'var(--faint)', marginBottom: 8 }}>Only ops, leadership, and admin users can push to Shopify.</div>}
      <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
        <Row k="Company ID" v={c.shopifyCompanyId ? <span className="mono" style={{ fontSize: 12 }}>{c.shopifyCompanyId}</span> : 'Not linked'} />
        <Row k="Customer ID" v={c.shopifyCustomerId ? <span className="mono" style={{ fontSize: 12 }}>{c.shopifyCustomerId}</span> : 'Not linked'} />
        {p && <Row k="Mode" v={p.mode === 'create' ? 'Create Shopify company' : 'Update Shopify company'} />}
        {p && <Row k="Contacts" v={`${p.customer.contactCount || 0} local / ${p.extraContacts.length || 0} queued`} />}
      </div>
      {p && (
        <div style={{ marginTop: 12, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 9, padding: '10px 12px', display: 'grid', gap: 7 }}>
          <div className="eyebrow">Preview payload</div>
          <Row k="Company" v={p.input && p.input.company ? p.input.company.name : c.name} />
          <Row k="External ID" v={<span className="mono" style={{ fontSize: 12 }}>{p.input && p.input.company ? p.input.company.externalId : ''}</span>} />
          <Row k="Primary email" v={p.customer.primaryEmail ? <span className="mono" style={{ fontSize: 12 }}>{p.customer.primaryEmail}</span> : 'Missing'} />
          <Row k="Location" v={p.input && p.input.companyLocation ? p.input.companyLocation.name : 'No address'} />
        </div>
      )}
      {p && p.errors && p.errors.length > 0 && (
        <div style={{ marginTop: 10, display: 'grid', gap: 7 }}>
          {p.errors.map((item, index) => <div key={index} style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700 }}>{item}</div>)}
        </div>
      )}
      {p && p.warnings && p.warnings.length > 0 && (
        <div style={{ marginTop: 10, display: 'grid', gap: 7 }}>
          {p.warnings.slice(0, 3).map((item, index) => <div key={index} style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700 }}>{item}</div>)}
        </div>
      )}
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </div>
  );
}

function rpCustomerPhotoItems(customerId) {
  return (RP.VISITS || [])
    .filter(visit => visit && visit.cust === customerId && visit.photos)
    .flatMap(visit => Object.entries(visit.photos || {})
      .filter(([, value]) => value && typeof value === 'object' && value.url && value.attached !== false)
      .map(([key, value]) => ({
        id: `${visit.id}-${key}`,
        category: value.category || visit.type || 'Photo',
        customerId,
        label: value.label || value.name || key.replace(/_/g, ' '),
        locationId: value.locationId || '',
        locationName: value.locationName || '',
        path: value.path || '',
        photoKey: key,
        url: value.url,
        visitId: visit.id,
        when: visit.when || visit.date || 'Recently',
        who: visit.who || 'Ranch Power'
      })));
}

function CustomerPhotosCard({ c, locations, force }) {
  const fileInput = useRef(null);
  const [category, setCategory] = useState('Storefront');
  const [locationId, setLocationId] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [error, setError] = useState('');
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const photos = rpCustomerPhotoItems(c.id);
  const live = !(window.RP_AUTH && window.RP_AUTH.demo);
  const upload = async file => {
    if (!file || busy) return;
    if (!live) { toast('Photo uploads require live data.', 'warn'); return; }
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.set('customerId', c.id);
      form.set('customerLocationId', locationId || '');
      form.set('category', category);
      form.set('label', label.trim());
      form.set('file', file);
      const response = await rpApi('/api/customers/photo', { method: 'POST', body: form });
      if (response.visit && RP.upsertVisit) RP.upsertVisit(response.visit);
      await rpRefreshAfterMutation(`${c.name} photo uploaded`);
      setLabel('');
      toast('Customer photo uploaded', 'ok');
      force && force();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not upload customer photo.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };
  const removePhoto = async photo => {
    if (!photo || deletingId) return;
    if (!live) { toast('Photo removal requires live data.', 'warn'); return; }
    if (!window.confirm(`Remove ${photo.label || 'this photo'}?`)) return;
    setDeletingId(photo.id);
    setError('');
    try {
      const response = await rpApi('/api/customers/photo', {
        method: 'DELETE',
        body: JSON.stringify({ customerId: c.id, visitId: photo.visitId, photoKey: photo.photoKey })
      });
      if (response.visit && RP.upsertVisit) RP.upsertVisit(response.visit);
      await rpRefreshAfterMutation(`${c.name} photo removed`);
      setLightboxPhoto(null);
      toast(response.storageWarning ? 'Photo removed; storage cleanup needs review.' : 'Customer photo removed', response.storageWarning ? 'warn' : 'ok');
      force && force();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not remove customer photo.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setDeletingId('');
    }
  };
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={<Pill status={photos.length ? 'Active' : 'Pending'}>{photos.length} photo{photos.length === 1 ? '' : 's'}</Pill>}>Photos</SectionTitle>
      <input ref={fileInput} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => upload(e.target.files && e.target.files[0])} />
      <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
        <select className="field" value={locationId} onChange={e => setLocationId(e.target.value)}>
          <option value="">Account / general</option>
          {locations.map(location => <option key={location.id} value={location.id}>{location.name || 'Location'}{location.city ? ` - ${location.city}` : ''}</option>)}
        </select>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,.55fr) minmax(0,1fr)', gap: 8 }}>
          <select className="field" value={category} onChange={e => setCategory(e.target.value)}>
            {['Storefront', 'Cooler', 'Shelf', 'Display', 'Back stock', 'Receiving', 'Account'].map(item => <option key={item}>{item}</option>)}
          </select>
          <input className="field" value={label} onChange={e => setLabel(e.target.value)} placeholder="Optional caption" />
        </div>
        <button className="btn btn-primary" disabled={busy || !live} onClick={() => fileInput.current && fileInput.current.click()} style={{ opacity: busy || !live ? 0.6 : 1 }}><Icon name="camera" size={15} />{busy ? 'Uploading...' : 'Upload Photo'}</button>
      </div>
      {!live && <div style={{ color: 'var(--faint)', fontSize: 11.5, marginBottom: 10 }}>Uploads are disabled while using developer data.</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700, marginBottom: 10 }}>{error}</div>}
      {photos.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          {photos.slice(0, 8).map(photo => (
            <button key={photo.id} onClick={() => setLightboxPhoto(photo)} style={{ textAlign: 'left', border: '1px solid var(--line)', borderRadius: 9, overflow: 'hidden', background: 'var(--surface)', padding: 0, boxShadow: 'var(--shadow-sm)', cursor: 'zoom-in' }}>
              <ProtectedImage src={photo.url} alt={photo.label} style={{ width: '100%', height: 104, objectFit: 'cover', display: 'block' }} />
              <div style={{ padding: '8px 10px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.2 }}>{photo.label}</div>
                <div style={{ fontSize: 11.2, color: 'var(--muted)', marginTop: 3 }}>{photo.locationName || photo.category}</div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div style={{ border: '1px dashed var(--line)', borderRadius: 9, padding: 13, color: 'var(--muted)', fontSize: 12.5, textAlign: 'center' }}>No customer photos uploaded.</div>
      )}
      <ProtectedImageLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} onDelete={live && lightboxPhoto ? () => removePhoto(lightboxPhoto) : null} deleting={Boolean(lightboxPhoto && deletingId === lightboxPhoto.id)} />
    </div>
  );
}

const CUSTOMER_ONBOARDING_APPLICATION_TYPES = ['New Store', 'Ownership Change'];
const CUSTOMER_ONBOARDING_BUSINESS_TYPES = ['Convenience Store', 'Restaurant/Bar', 'Liquor Store', 'Hotel', 'Retail Store', 'Superette', 'Drug Store', 'Seasonal', 'Hardware Store', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Other'];
const CUSTOMER_ONBOARDING_EQUIPMENT = ['Need New Cooler Equipment', 'Existing Equipment'];
const CUSTOMER_ONBOARDING_PAYMENT_METHODS = ['COD', 'e-Transfer', 'Pre-Authorized Debit', 'Pre-Authorized Credit'];

function customerOnboardingInitialForm(c) {
  const existing = c && c.onboardingQuestionnaire && typeof c.onboardingQuestionnaire === 'object' ? c.onboardingQuestionnaire : {};
  const info = existing.businessInformation || {};
  const contacts = existing.businessContacts || {};
  return {
    legalBusinessName: info.legalBusinessName || c.name || '',
    storeNameDba: info.storeNameDba || c.banner || c.name || '',
    physicalDeliveryAddress: info.physicalDeliveryAddress || [c.address, c.city, c.postal].filter(Boolean).join(', '),
    mailingAddress: info.mailingAddress || '',
    storePhoneNumber: info.storePhoneNumber || c.phone || '',
    storeManager: info.storeManager || '',
    pstHstNumber: info.pstHstNumber || '',
    ownerName: contacts.ownerName || c.buyer || '',
    ownerPhone: contacts.ownerPhone || c.phone || '',
    ownerEmail: contacts.ownerEmail || c.email || '',
    accountsPayableName: contacts.accountsPayableName || '',
    accountsPayablePhone: contacts.accountsPayablePhone || '',
    accountsPayableEmail: contacts.accountsPayableEmail || '',
    applicationType: existing.applicationType || '',
    businessTypes: Array.isArray(existing.businessTypes) ? existing.businessTypes : [],
    otherBusinessType: existing.otherBusinessType || '',
    equipment: existing.equipment && Array.isArray(existing.equipment.selections) ? existing.equipment.selections : [],
    existingEquipmentType: existing.equipment && existing.equipment.existingEquipmentType ? existing.equipment.existingEquipmentType : '',
    paymentMethods: Array.isArray(existing.paymentMethods) ? existing.paymentMethods : []
  };
}

function customerOnboardingToggle(form, key, value) {
  const current = Array.isArray(form[key]) ? form[key] : [];
  return { ...form, [key]: current.includes(value) ? current.filter(item => item !== value) : current.concat(value) };
}

function CustomerOnboardingChoice({ checked, label, name, onChange, type = 'checkbox' }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-soft)', fontWeight: 700 }}>
      <input type={type} name={name} checked={checked} onChange={onChange} style={{ width: 15, height: 15, accentColor: 'var(--amber)', flexShrink: 0 }} />
      <span>{label}</span>
    </label>
  );
}

function CustomerOnboardingEntryModal({ c, onClose, onSaved }) {
  const [form, setForm] = useState(() => customerOnboardingInitialForm(c));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tried, setTried] = useState(false);
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const toggle = (key, value) => setForm(prev => customerOnboardingToggle(prev, key, value));
  const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
  const checkGrid = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px 12px', marginTop: 8 };
  const section = { border: '1px solid var(--line)', borderRadius: 10, padding: 14, background: 'var(--surface-2)', marginBottom: 14 };

  const save = async () => {
    if (busy) return;
    setTried(true);
    if (!Array.isArray(form.paymentMethods) || form.paymentMethods.length === 0) {
      setError('Select at least one payment method.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      let response;
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        response = { customer: { onboardingQuestionnaire: { ...form, businessInformation: {}, businessContacts: {} }, onboardingSubmittedAt: new Date().toISOString() } };
      } else {
        response = await rpApi('/api/account/onboarding', {
          method: 'POST',
          body: JSON.stringify({ customerId: c.id, questionnaire: form })
        });
      }
      const patch = {
        onboardingQuestionnaire: response.customer && response.customer.onboardingQuestionnaire ? response.customer.onboardingQuestionnaire : form,
        onboardingSubmittedAt: response.customer && response.customer.onboardingSubmittedAt ? response.customer.onboardingSubmittedAt : new Date().toISOString()
      };
      Object.assign(c, patch);
      if (RP.upsertCustomer) RP.upsertCustomer({ ...c, ...patch });
      await rpRefreshAfterMutation('Customer onboarding recorded');
      toast('Customer onboarding recorded', 'ok');
      onSaved && onSaved({ ...c, ...patch });
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save onboarding.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record Onboarding" sub={`${c.name} - transcribe paper form or update saved answers`} onClose={onClose} width={780}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} style={{ opacity: busy ? 0.65 : 1 }} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Save Onboarding'}</button></>}>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 800, marginBottom: 14 }}>{error}</div>}
      <div style={section}>
        <SectionTitle>Business Information</SectionTitle>
        <MField label="Legal business name" required><input className="field" value={form.legalBusinessName} onChange={e => set('legalBusinessName', e.target.value)} /></MField>
        <MField label="Store name D.B.A."><input className="field" value={form.storeNameDba} onChange={e => set('storeNameDba', e.target.value)} /></MField>
        <MField label="Physical delivery address" required><input className="field" value={form.physicalDeliveryAddress} onChange={e => set('physicalDeliveryAddress', e.target.value)} /></MField>
        <MField label="Mailing address"><input className="field" value={form.mailingAddress} onChange={e => set('mailingAddress', e.target.value)} /></MField>
        <div style={grid}>
          <MField label="Store phone number" required><input className="field" value={form.storePhoneNumber} onChange={e => set('storePhoneNumber', e.target.value)} /></MField>
          <MField label="Store manager"><input className="field" value={form.storeManager} onChange={e => set('storeManager', e.target.value)} /></MField>
        </div>
        <MField label="PST/HST # if applicable"><input className="field" value={form.pstHstNumber} onChange={e => set('pstHstNumber', e.target.value)} /></MField>
      </div>
      <div style={section}>
        <SectionTitle>Business Contacts</SectionTitle>
        <MField label="Name of owner(s)" required><input className="field" value={form.ownerName} onChange={e => set('ownerName', e.target.value)} /></MField>
        <div style={grid}>
          <MField label="Owner phone"><input className="field" value={form.ownerPhone} onChange={e => set('ownerPhone', e.target.value)} /></MField>
          <MField label="Owner email" required><input className="field" type="email" value={form.ownerEmail} onChange={e => set('ownerEmail', e.target.value)} /></MField>
        </div>
        <MField label="Accounts payable name"><input className="field" value={form.accountsPayableName} onChange={e => set('accountsPayableName', e.target.value)} /></MField>
        <div style={grid}>
          <MField label="Accounts payable phone"><input className="field" value={form.accountsPayablePhone} onChange={e => set('accountsPayablePhone', e.target.value)} /></MField>
          <MField label="Accounts payable email"><input className="field" type="email" value={form.accountsPayableEmail} onChange={e => set('accountsPayableEmail', e.target.value)} /></MField>
        </div>
      </div>
      <div style={section}>
        <SectionTitle>Application</SectionTitle>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {CUSTOMER_ONBOARDING_APPLICATION_TYPES.map(value => <CustomerOnboardingChoice key={value} type="radio" name="customer-onboarding-application" label={value} checked={form.applicationType === value} onChange={() => set('applicationType', value)} />)}
        </div>
      </div>
      <div style={section}>
        <SectionTitle>Type of Business</SectionTitle>
        <div style={checkGrid}>
          {CUSTOMER_ONBOARDING_BUSINESS_TYPES.map(value => <CustomerOnboardingChoice key={value} label={value} checked={form.businessTypes.includes(value)} onChange={() => toggle('businessTypes', value)} />)}
        </div>
        {form.businessTypes.includes('Other') && <MField label="Other business type"><input className="field" value={form.otherBusinessType} onChange={e => set('otherBusinessType', e.target.value)} /></MField>}
      </div>
      <div style={section}>
        <SectionTitle>Equipment</SectionTitle>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {CUSTOMER_ONBOARDING_EQUIPMENT.map(value => <CustomerOnboardingChoice key={value} label={value} checked={form.equipment.includes(value)} onChange={() => toggle('equipment', value)} />)}
        </div>
        {form.equipment.includes('Existing Equipment') && <MField label="Existing equipment type"><input className="field" value={form.existingEquipmentType} onChange={e => set('existingEquipmentType', e.target.value)} /></MField>}
      </div>
      <div style={{ ...section, borderColor: tried && form.paymentMethods.length === 0 ? 'var(--danger)' : 'var(--line)' }}>
        <SectionTitle>Payment Method *</SectionTitle>
        <div role="group" aria-invalid={tried && form.paymentMethods.length === 0 ? 'true' : 'false'} aria-describedby="customer-onboarding-payment-error" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {CUSTOMER_ONBOARDING_PAYMENT_METHODS.map(value => <CustomerOnboardingChoice key={value} label={value} checked={form.paymentMethods.includes(value)} onChange={() => toggle('paymentMethods', value)} />)}
        </div>
        {tried && form.paymentMethods.length === 0 && <div id="customer-onboarding-payment-error" role="alert" style={{ marginTop: 10, color: 'var(--danger)', fontSize: 12.5, fontWeight: 800 }}>Select at least one payment method.</div>}
        <div style={{ marginTop: 10, color: 'var(--muted)', fontSize: 12, lineHeight: 1.45 }}>{RP_CREDIT_CARD_TERMS_NOTE}</div>
      </div>
    </Modal>
  );
}

function CustomerOnboardingCard({ c, force }) {
  const [entryOpen, setEntryOpen] = useState(false);
  const q = c && c.onboardingQuestionnaire && typeof c.onboardingQuestionnaire === 'object' ? c.onboardingQuestionnaire : {};
  const info = q.businessInformation || {};
  const contacts = q.businessContacts || {};
  const submitted = Boolean(c && c.onboardingSubmittedAt);
  const businessTypes = Array.isArray(q.businessTypes) ? q.businessTypes.map(type => type === 'Other' && q.otherBusinessType ? `Other: ${q.otherBusinessType}` : type).join(', ') : '';
  const paymentMethods = Array.isArray(q.paymentMethods) ? q.paymentMethods.join(', ') : '';
  const equipment = q.equipment && Array.isArray(q.equipment.selections) ? q.equipment.selections.join(', ') : '';
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      {entryOpen && <CustomerOnboardingEntryModal c={c} onClose={() => setEntryOpen(false)} onSaved={() => force && force()} />}
      <SectionTitle right={<Pill status={submitted ? 'Active' : 'Pending'}>{submitted ? 'Complete' : 'Required'}</Pill>}>Retail Onboarding</SectionTitle>
      {!submitted ? (
        <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>The customer will complete the required B2B questionnaire the next time they use their order.ranchpower.com setup link.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
          <Row k="Submitted" v={rpFormatAccountTime(c.onboardingSubmittedAt)} />
          <Row k="Legal name" v={info.legalBusinessName || c.name} />
          <Row k="Store D.B.A." v={info.storeNameDba || 'Not provided'} />
          <Row k="Delivery" v={info.physicalDeliveryAddress || 'Not provided'} />
          <Row k="Mailing" v={info.mailingAddress || 'Same / not provided'} />
          <Row k="Store phone" v={info.storePhoneNumber || 'Not provided'} />
          <Row k="Store manager" v={info.storeManager || 'Not provided'} />
          <Row k="PST/HST" v={info.pstHstNumber || 'Not provided'} />
          <Row k="Owner" v={contacts.ownerName || 'Not provided'} />
          <Row k="Owner email" v={contacts.ownerEmail ? <span className="mono" style={{ fontSize: 12.5 }}>{contacts.ownerEmail}</span> : 'Not provided'} />
          <Row k="A/P contact" v={contacts.accountsPayableName || 'Not provided'} />
          <Row k="A/P email" v={contacts.accountsPayableEmail ? <span className="mono" style={{ fontSize: 12.5 }}>{contacts.accountsPayableEmail}</span> : 'Not provided'} />
          <Row k="Application" v={q.applicationType || 'Not provided'} />
          <Row k="Business type" v={businessTypes || 'Not provided'} />
          <Row k="Equipment" v={equipment || 'Not selected'} />
          <Row k="Payment method" v={paymentMethods || 'Not selected'} />
        </div>
      )}
      <button className="btn btn-sm" style={{ width: '100%', marginTop: 12 }} onClick={() => setEntryOpen(true)}><Icon name="doc" size={14} />{submitted ? 'Update Onboarding' : 'Record Paper Form'}</button>
    </div>
  );
}

function canRecordCustomerAccountCredit() {
  const role = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(rpAuthProfile().role) : '';
  return ['admin', 'leadership', 'ops', 'sales_manager', 'accountant'].includes(role);
}

function CustomerAccountCreditModal({ c, onClose, onAdded }) {
  const [amount, setAmount] = useState('');
  const [creditDate, setCreditDate] = useState(() => typeof rpTodayYmd === 'function' ? rpTodayYmd(c) : new Date().toISOString().slice(0, 10));
  const [creditReason, setCreditReason] = useState('Account credit');
  const [creditReference, setCreditReference] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (busy || !(Number(amount) > 0)) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/customers/credit', {
        method: 'POST',
        body: JSON.stringify({
          amount,
          creditDate,
          creditNote: creditNote.trim(),
          creditReason: creditReason.trim(),
          creditReference: creditReference.trim(),
          customerId: c.id
        })
      });
      c.invoiceOverrides = { ...(c.invoiceOverrides || {}), accountCredits: response.accountCredits || [] };
      await rpRefreshAfterMutation(`Account credit recorded for ${c.name}`);
      toast(`Account credit ${RP.money2(Number(amount || 0))} recorded`, 'ok');
      onAdded && onAdded(response);
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not record account credit.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add Account Credit" sub={c.name} onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !(Number(amount) > 0)} onClick={save}><Icon name="check" size={15} />{busy ? 'Recording...' : 'Add Credit'}</button></>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.2fr)', gap: 12 }}>
        <MField label="Credit amount">
          <input className="field" type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
        </MField>
        <MField label="Credit date">
          <input className="field" type="date" value={creditDate} onChange={e => setCreditDate(e.target.value)} />
        </MField>
      </div>
      <MField label="Reason">
        <select className="field" value={creditReason} onChange={e => setCreditReason(e.target.value)}>
          <option>Account credit</option>
          <option>PST exemption credit</option>
          <option>Overpayment credit</option>
          <option>Returned product credit</option>
          <option>Goodwill credit</option>
          <option>Pricing correction credit</option>
        </select>
      </MField>
      <MField label="Reference">
        <input className="field" value={creditReference} onChange={e => setCreditReference(e.target.value)} placeholder="Invoice, refund, or approval reference" />
      </MField>
      <MField label="Internal note">
        <textarea className="field" rows={2} value={creditNote} onChange={e => setCreditNote(e.target.value)} placeholder="Optional explanation for accounting" style={{ resize: 'vertical' }} />
      </MField>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function CustomerAccountDebtModal({ c, onClose, onAdded }) {
  const [amount, setAmount] = useState('');
  const [debtDate, setDebtDate] = useState(() => typeof rpTodayYmd === 'function' ? rpTodayYmd(c) : new Date().toISOString().slice(0, 10));
  const [debtReason, setDebtReason] = useState('Short payment');
  const [debtReference, setDebtReference] = useState('');
  const [debtNote, setDebtNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (busy || !(Number(amount) > 0)) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/customers/debt', {
        method: 'POST',
        body: JSON.stringify({
          amount,
          customerId: c.id,
          debtDate,
          debtNote: debtNote.trim(),
          debtReason: debtReason.trim(),
          debtReference: debtReference.trim()
        })
      });
      c.invoiceOverrides = { ...(c.invoiceOverrides || {}), accountCredits: response.accountCredits || [] };
      await rpRefreshAfterMutation(`Account debt recorded for ${c.name}`);
      toast(`Account debt ${RP.money2(Number(amount || 0))} recorded`, 'ok');
      onAdded && onAdded(response);
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not record account debt.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record Account Debt" sub={c.name} onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !(Number(amount) > 0)} onClick={save}><Icon name="check" size={15} />{busy ? 'Recording...' : 'Record Debt'}</button></>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.2fr)', gap: 12 }}>
        <MField label="Debt amount">
          <input className="field" type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
        </MField>
        <MField label="Debt date">
          <input className="field" type="date" value={debtDate} onChange={e => setDebtDate(e.target.value)} />
        </MField>
      </div>
      <MField label="Reason">
        <select className="field" value={debtReason} onChange={e => setDebtReason(e.target.value)}>
          <option>Short payment</option>
          <option>Prior invoice balance</option>
          <option>Pricing correction</option>
          <option>Delivery charge correction</option>
          <option>Account correction</option>
          <option>Other</option>
        </select>
      </MField>
      <MField label="Reference">
        <input className="field" value={debtReference} onChange={e => setDebtReference(e.target.value)} placeholder="Invoice, order, EMT, or approval reference" />
      </MField>
      <MField label="Internal note">
        <textarea className="field" rows={2} value={debtNote} onChange={e => setDebtNote(e.target.value)} placeholder="Optional explanation for accounting" style={{ resize: 'vertical' }} />
      </MField>
      <div style={{ background: 'var(--amber-soft)', color: 'var(--amber-deep)', border: '1px solid var(--amber)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, marginBottom: 12 }}>
        Open account debt is added as a previous account balance on the next issued invoice.
      </div>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function CustomerAccountCreditSettleModal({ c, credit, onClose, onSettled }) {
  const openAmount = typeof rpCustomerCreditOpenAmount === 'function' ? rpCustomerCreditOpenAmount(credit) : Math.max(0, Number((credit && credit.amount) || 0));
  const [settlementAmount, setSettlementAmount] = useState(() => openAmount > 0 ? openAmount.toFixed(2) : '');
  const [settlementDate, setSettlementDate] = useState(() => typeof rpTodayYmd === 'function' ? rpTodayYmd(c) : new Date().toISOString().slice(0, 10));
  const [settlementMethod, setSettlementMethod] = useState('cash');
  const [settlementReason, setSettlementReason] = useState('Customer cash refund');
  const [settlementReference, setSettlementReference] = useState('');
  const [settlementNote, setSettlementNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const amountNumber = Number(settlementAmount || 0);
  const invalidAmount = !(amountNumber > 0) || amountNumber - openAmount > 0.005;

  const save = async () => {
    if (busy || invalidAmount) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/customers/credit-settle', {
        method: 'POST',
        body: JSON.stringify({
          creditId: credit.id,
          customerId: c.id,
          settlementAmount,
          settlementDate,
          settlementMethod,
          settlementNote: settlementNote.trim(),
          settlementReason: settlementReason.trim(),
          settlementReference: settlementReference.trim()
        })
      });
      c.invoiceOverrides = { ...(c.invoiceOverrides || {}), accountCredits: response.accountCredits || [] };
      await rpRefreshAfterMutation(`Account credit payout recorded for ${c.name}`);
      toast(`Credit payout ${RP.money2(Number(settlementAmount || 0))} recorded`, 'ok');
      onSettled && onSettled(response);
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not record account credit payout.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record Credit Payout" sub={`${c.name} - ${credit.reason || 'Account credit'}`} onClose={onClose} width={560}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || invalidAmount} onClick={save}><Icon name="check" size={15} />{busy ? 'Recording...' : 'Record Payout'}</button></>}>
      <div style={{ border: '1px solid var(--line)', borderRadius: 9, padding: '10px 12px', background: 'var(--surface-2)', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
          <div style={{ fontWeight: 800 }}>{credit.reason || 'Account credit'}</div>
          <div className="disp" style={{ fontSize: 18 }}>{RP.money2(openAmount)}</div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 5, lineHeight: 1.35 }}>This keeps the original credit on the account and records money going back out.</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.2fr)', gap: 12 }}>
        <MField label="Payout amount">
          <input className="field" type="number" min="0" max={openAmount || undefined} step="0.01" value={settlementAmount} onChange={e => setSettlementAmount(e.target.value)} placeholder="0.00" />
        </MField>
        <MField label="Payout method">
          <select className="field" value={settlementMethod} onChange={e => setSettlementMethod(e.target.value)}>
            {ACCOUNT_CREDIT_PAYOUT_METHODS.map(method => <option key={method.code} value={method.code}>{method.label}</option>)}
          </select>
        </MField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
        <MField label="Payout date">
          <input className="field" type="date" value={settlementDate} onChange={e => setSettlementDate(e.target.value)} />
        </MField>
        <MField label="Reference">
          <input className="field" value={settlementReference} onChange={e => setSettlementReference(e.target.value)} placeholder="Cash receipt, cheque #, EFT ref" />
        </MField>
      </div>
      <MField label="Reason">
        <select className="field" value={settlementReason} onChange={e => setSettlementReason(e.target.value)}>
          <option>Customer cash refund</option>
          <option>PST exemption payout</option>
          <option>Overpayment payout</option>
          <option>Cheque refund</option>
          <option>e-Transfer refund</option>
          <option>Account correction</option>
          <option>Other</option>
        </select>
      </MField>
      <MField label="Internal note">
        <textarea className="field" rows={2} value={settlementNote} onChange={e => setSettlementNote(e.target.value)} placeholder="Optional explanation for accounting" style={{ resize: 'vertical' }} />
      </MField>
      {invalidAmount && settlementAmount && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, marginTop: -4 }}>Payout cannot exceed the open credit balance of {RP.money2(openAmount)}.</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function CustomerAccountCreditsCard({ c, credits, debts = [], balance, debtBalance = 0, onAdd, onAddDebt, onSettle, canAdd, canSettle }) {
  const isOpenCredit = credit => typeof rpCustomerCreditIsOpen === 'function'
    ? rpCustomerCreditIsOpen(credit)
    : !['applied', 'closed', 'void', 'voided', 'cancelled', 'canceled', 'refunded', 'settled'].includes(String(credit.status || '').toLowerCase());
  const openCredits = credits.filter(isOpenCredit);
  const settledCredits = credits.filter(credit => !isOpenCredit(credit));
  const openDebts = debts.filter(isOpenCredit);
  const appliedDebts = debts.filter(debt => !isOpenCredit(debt));
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>{balance > 0.005 && <Pill status="Active">Credit {RP.money2(balance)}</Pill>}{debtBalance > 0.005 && <Pill status="Overdue">Debt {RP.money2(debtBalance)}</Pill>}{canAdd && <button className="btn btn-ghost btn-sm" onClick={onAdd}><Icon name="plus" size={14} />Credit</button>}{canAdd && <button className="btn btn-ghost btn-sm" onClick={onAddDebt}><Icon name="plus" size={14} />Debt</button>}</div>}>Account Balance</SectionTitle>
      {openDebts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 13 }}>
          {openDebts.map((debt, index) => (
            <div key={debt.id || index} style={{ border: '1px solid var(--amber)', borderRadius: 9, padding: '10px 12px', background: 'var(--amber-soft)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                <div>
                  <div className="disp" style={{ fontSize: 18, color: 'var(--amber-deep)' }}>{RP.money2(debt.openAmount || debt.amount)}</div>
                  {debt.settledAmount > 0.005 && <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>Original {RP.money2(debt.amount)}</div>}
                </div>
                <Pill status={debt.status || 'Open'}>{debt.status || 'Open'}</Pill>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 5 }}>{debt.reason || 'Account debt'}</div>
              <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4, lineHeight: 1.35 }}>
                {[debt.date || 'No date', debt.sourceInvoiceNumber || debt.sourceOrderId || debt.reference, 'applies to next invoice'].filter(Boolean).join(' - ')}
              </div>
              {debt.note && <div style={{ color: 'var(--ink-soft)', fontSize: 12.5, marginTop: 6, lineHeight: 1.35 }}>{debt.note}</div>}
            </div>
          ))}
        </div>
      )}
      {openCredits.length === 0 ? (
        openDebts.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>No open credits or debts on this account.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {openCredits.map((credit, index) => (
            <div key={credit.id || index} style={{ border: '1px solid var(--line)', borderRadius: 9, padding: '10px 12px', background: 'var(--surface-2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                <div>
                  <div className="disp" style={{ fontSize: 18 }}>{RP.money2(credit.openAmount || credit.amount)}</div>
                  {credit.settledAmount > 0.005 && <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>Original {RP.money2(credit.amount)}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Pill status={credit.status || 'Open'}>{credit.status || 'Open'}</Pill>
                  {canSettle && <button className="btn btn-sm" onClick={() => onSettle && onSettle(credit)}><Icon name="wallet" size={14} />Payout</button>}
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 5 }}>{credit.reason || 'Account credit'}</div>
              <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4, lineHeight: 1.35 }}>
                {[credit.date || 'No date', credit.sourceInvoiceNumber || credit.sourceOrderId || credit.reference].filter(Boolean).join(' - ')}
              </div>
              {credit.note && <div style={{ color: 'var(--ink-soft)', fontSize: 12.5, marginTop: 6, lineHeight: 1.35 }}>{credit.note}</div>}
            </div>
          ))}
        </div>
      )}
      {appliedDebts.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 13, paddingTop: 12 }}>
          <div style={{ color: 'var(--muted)', fontFamily: 'var(--ff-display)', fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>Applied Debts</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {appliedDebts.slice(0, 5).map((debt, index) => {
              const latest = (debt.settlements || [])[0] || {};
              return (
                <div key={debt.id || index} style={{ border: '1px solid var(--line)', borderRadius: 9, padding: '9px 11px', background: 'var(--surface)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <div style={{ fontWeight: 800 }}>{RP.money2(debt.amount)}</div>
                    <Pill status={debt.status || 'Applied'}>{debt.status || 'Applied'}</Pill>
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 5, lineHeight: 1.35 }}>
                    {latest.reason || debt.reason || 'Applied to invoice'}
                  </div>
                  {latest.reference && <div className="mono" style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 4 }}>{latest.reference}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {settledCredits.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 13, paddingTop: 12 }}>
          <div style={{ color: 'var(--muted)', fontFamily: 'var(--ff-display)', fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>Settled Credits</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {settledCredits.slice(0, 5).map((credit, index) => {
              const latest = (credit.settlements || [])[0] || {};
              return (
                <div key={credit.id || index} style={{ border: '1px solid var(--line)', borderRadius: 9, padding: '9px 11px', background: 'var(--surface)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <div style={{ fontWeight: 800 }}>{RP.money2(credit.amount)}</div>
                    <Pill status={credit.status || 'Closed'}>{credit.status || 'Closed'}</Pill>
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 5, lineHeight: 1.35 }}>
                    {latest.amount ? `Paid out ${RP.money2(latest.amount)} by ${accountCreditPayoutMethodLabel(latest.method)}${latest.date ? ` on ${latest.date}` : ''}` : (credit.reason || 'Account credit closed')}
                  </div>
                  {latest.reference && <div className="mono" style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 4 }}>{latest.reference}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerPaymentVaultCard({ c, onAdd }) {
  const card = typeof rpStripeDefaultCard === 'function' ? rpStripeDefaultCard(c) : null;
  const cardLabel = typeof rpStripeCardLabel === 'function' ? rpStripeCardLabel(card) : (card ? `${card.brand || 'Card'} ending ${card.last4 || ''}` : 'No saved card');
  const consentedAt = card && (card.consentedAt || card.consented_at || card.updatedAt || card.updated_at);
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={onAdd}><Icon name="card" size={14} />{card ? 'Replace' : 'Add'}</button>}>Card Vault</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
        <Row k="Status" v={<Pill status={card ? 'Paid' : 'Pending'}>{card ? 'Saved' : 'No card'}</Pill>} />
        <Row k="Card" v={cardLabel} />
        {consentedAt && <Row k="Authorized" v={<span className="mono" style={{ fontSize: 11.5 }}>{orderInvoiceReceiptTime(consentedAt)}</span>} />}
        <Row k="Processor" v="Stripe" />
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 12.2, lineHeight: 1.4, marginTop: 12 }}>Ranch Power stores only the Stripe vault reference and card summary, never the full card number.</div>
    </div>
  );
}

function CustomerDetailDesk({ id, back, go }) {
  const exactCustomer = useRpExactCustomer({ customerId: id });
  const backButton = <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />Back to Customers</button>;
  if (exactCustomer.loading) return (
    <div className="rp-fade" data-exact-customer-loading="true" style={{ padding: '26px 30px 40px' }}>
      {backButton}
      <div className="card" style={{ padding: 24, color: 'var(--muted)' }}><Icon name="refresh" size={18} /><div style={{ color: 'var(--ink)', fontWeight: 700, marginTop: 8 }}>Loading customer {id}...</div></div>
    </div>
  );
  if (exactCustomer.error) return (
    <div className="rp-fade" data-exact-customer-error="true" style={{ padding: '26px 30px 40px' }}>
      {backButton}
      <div className="card" role="alert" style={{ padding: 24, color: 'var(--danger)' }}><SectionTitle>Customer Could Not Load</SectionTitle><div style={{ fontSize: 14 }}>{exactCustomer.error}</div><button type="button" className="btn btn-sm" onClick={exactCustomer.retry} style={{ marginTop: 12 }}>Try Again</button></div>
    </div>
  );
  if (!exactCustomer.customer) {
    return (
      <div className="rp-fade" data-exact-customer-missing="true" style={{ padding: '26px 30px 40px' }}>
        {backButton}
        <div className="card" style={{ padding: 24 }}>
          <SectionTitle>Customer Not Found</SectionTitle>
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>This customer does not exist or is outside your access scope.</div>
        </div>
      </div>
    );
  }
  return <CustomerDetailDeskFound key={exactCustomer.customer.id} id={id} back={back} go={go} customer={exactCustomer.customer} />;
}

function CustomerDetailDeskFound({ id, back, go, customer }) {
  const c = customer;
  const directoryOnly = c.supplyChannel === 'distributor';
  const [modal, setModal] = useState(null);
  const [settleCredit, setSettleCredit] = useState(null);
  const [, force] = useState(0);
  const liveOrderHistory = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const orderHistoryPage = useRpServerPagedRows({ mode: 'orders', limit: 50, customerId: id });
  const orders = liveOrderHistory
    ? orderHistoryPage.items.map(item => item.order).filter(order => order && order.cust === id)
    : RP.ORDERS.filter(o => o.cust === id);
  const contacts = rpCustomerContacts(c);
  const locations = typeof rpCustomerLocations === 'function' ? rpCustomerLocations(c) : (Array.isArray(c.locations) ? c.locations : []);
  const primaryPhone = rpPrimaryCustomerPhone(c);
  const orderReady = rpCustomerOrderReady(c);
  const directRemoval = typeof rpNormalizeRole === 'function' && rpNormalizeRole(rpAuthProfile().role) === 'admin';
  const accountCredits = typeof rpCustomerAccountCredits === 'function' ? rpCustomerAccountCredits(c) : [];
  const accountCreditBalance = typeof rpCustomerAccountCreditBalance === 'function' ? rpCustomerAccountCreditBalance(c) : 0;
  const accountDebts = typeof rpCustomerAccountDebts === 'function' ? rpCustomerAccountDebts(c) : [];
  const accountDebtBalance = typeof rpCustomerAccountDebtBalance === 'function' ? rpCustomerAccountDebtBalance(c) : 0;
  const restockSignal = typeof rpCustomerRestockSignal === 'function' ? rpCustomerRestockSignal(c) : null;
  const restockStatus = restockSignal ? restockSignal.status : 'Submitted';
  const [restockFg, restockBg] = statusColors(restockStatus);
  const canAddAccountCredit = canRecordCustomerAccountCredit();
  const accountBalanceLabel = accountDebtBalance > 0.005 ? `Owes ${RP.money2(accountDebtBalance)}` : RP.money2(accountCreditBalance);
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {modal === 'order' && <NewOrderModal presetCust={id} onClose={() => setModal(null)} />}
      {modal === 'edit' && <EditCustomerModal customer={c} onClose={() => setModal(null)} onSaved={() => force(x => x + 1)} />}
      {modal === 'credit' && <CustomerAccountCreditModal c={c} onClose={() => setModal(null)} onAdded={() => force(x => x + 1)} />}
      {modal === 'debt' && <CustomerAccountDebtModal c={c} onClose={() => setModal(null)} onAdded={() => force(x => x + 1)} />}
      {modal === 'stripe-card' && <StripeCardSetupModal customer={c} onClose={() => setModal(null)} onSaved={() => force(x => x + 1)} />}
      {settleCredit && <CustomerAccountCreditSettleModal c={c} credit={settleCredit} onClose={() => setSettleCredit(null)} onSettled={() => force(x => x + 1)} />}
      {modal === 'remove' && <RemovalRequestModal entityType="customer" entityId={c.id} entityLabel={c.name} onClose={() => setModal(null)} onRequested={() => back && back()} />}
      <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />Back to Customers</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22 }}>
        <Avatar name={c.name} size={54} color={c.status === 'Prospect' ? 'var(--faint)' : 'var(--navy)'} />
        <div style={{ flex: 1 }}>
          <h1 className="disp" style={{ margin: 0, fontSize: 30, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{c.name}</h1>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center' }}><Pill status={directoryOnly ? 'Active' : c.status}>{directoryOnly ? 'Distributor-supplied' : c.status}</Pill> · {c.type} · {c.city}</div>
        </div>
        <button className="btn" onClick={() => setModal('edit')}><Icon name="pen" size={16} />Edit Account</button>
        <button className="btn" style={{ color: 'var(--danger)' }} onClick={() => setModal('remove')}><Icon name="x" size={16} />{directRemoval ? 'Remove' : 'Request Removal'}</button>
        {!directoryOnly && <button className="btn btn-primary" disabled={!orderReady} title={orderReady ? 'Create an order' : 'Approve this customer before ordering'} onClick={() => orderReady && setModal('order')} style={{ opacity: orderReady ? 1 : 0.55 }}><Icon name={orderReady ? 'plus' : 'flag'} size={16} />{orderReady ? 'New Order' : 'Approval Needed'}</button>}
      </div>
      {directoryOnly && <div data-distributor-directory="true" className="card" style={{ padding: '18px 20px', marginBottom: 18 }}>
        <SectionTitle>Distributor-supplied</SectionTitle>
        <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{c.distributorName ? `Supplied by ${c.distributorName}. ` : ''}Saved in the customer directory for distributor coverage and Find Us.</div>
        <div style={{ marginTop: 9, fontWeight: 700, color: c.public ? 'var(--ok)' : 'var(--muted)' }}>{c.public ? 'Listed on Find Us' : c.coordinateVerification || locations.some(location => location.coordinateVerification) ? 'Location confirmed; listing is private' : 'Confirm the store location to publish on Find Us'}</div>
        <div style={{ marginTop: 7, fontSize: 12, color: 'var(--muted)' }}>Use Edit Account to update this record or change its supplier to Ranch Power directly.</div>
      </div>}
      {!directoryOnly && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 14, marginBottom: 18 }}>
        <KPICard label="YTD Revenue" value={RP.money(c.ytd)} accent="var(--ok)" />
        <KPICard label="Last Order" value={c.lastCases || 0} unit="cases" accent="var(--amber)" />
        <KPICard label="Check-In" value={restockSignal ? restockSignal.label : c.risk} accent={restockStatus === 'High' ? 'var(--danger)' : restockStatus === 'Pending' ? 'var(--amber)' : 'var(--ok)'} />
        <KPICard label="Credit" value={c.credit} accent="var(--info)" />
        <KPICard label="Account Balance" value={accountBalanceLabel} icon="wallet" accent={accountDebtBalance > 0.005 ? 'var(--amber)' : 'var(--ok)'} />
      </div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 18 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}><SectionTitle>Order History</SectionTitle></div>
          <table className="rp-table">
            <thead><tr><th>Order</th><th>Status</th><th className="num">Cases</th><th className="num">Total</th><th>Date</th></tr></thead>
            <tbody>{orders.map(o => { const t = orderTotals(o); return <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => { if (liveOrderHistory) rpUpsertOrder(o); go('orders', { type: 'order', id: o.id }); }}><td className="mono" style={{ fontWeight: 600 }}>{o.id}</td><td><Pill status={o.status} /></td><td className="num">{t.cases}</td><td className="num" style={{ fontWeight: 600 }}>{RP.money(t.total)}</td><td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{rpOrderDateLabel(o)}</td></tr>; })}
              {liveOrderHistory && orderHistoryPage.initialLoading && <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--muted)', padding: 26 }}><Icon name="refresh" size={17} /><div style={{ marginTop: 6, fontWeight: 700, color: 'var(--ink)' }}>Loading complete order history...</div></td></tr>}
              {liveOrderHistory && !orderHistoryPage.initialLoading && !orderHistoryPage.loaded && orderHistoryPage.error && <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--danger)', padding: 26 }}><div style={{ fontWeight: 700 }}>{orderHistoryPage.error}</div><button type="button" className="btn btn-sm" onClick={orderHistoryPage.retry} style={{ marginTop: 9 }}>Try Again</button></td></tr>}
              {(!liveOrderHistory || orderHistoryPage.loaded) && orders.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>{directoryOnly ? 'This location is supplied through a distributor.' : 'No orders yet — this is a prospect.'}</td></tr>}</tbody>
          </table>
          {liveOrderHistory && orderHistoryPage.loaded && <RpServerLoadMore {...orderHistoryPage} label="orders" onLoadMore={orderHistoryPage.loadMore} onRetry={orderHistoryPage.loadMore} />}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {!directoryOnly && restockSignal && (
            <div className="card" style={{ padding: '18px 20px' }}>
              <SectionTitle right={<span className="pill" style={{ color: restockFg, background: restockBg }}>{restockSignal.confidence} confidence</span>}>Restock Intelligence</SectionTitle>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
                {restockSignal.reasons.slice(0, 4).map(reason => <span key={reason} className="pill" style={{ color: 'var(--muted)', background: 'var(--surface-2)' }}>{reason}</span>)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
                <Row k="Recommended check-in" v={<span style={{ color: restockFg, fontWeight: 800 }}>{[restockSignal.label, restockSignal.checkInDate].filter(Boolean).join(' - ')}</span>} />
                <Row k="Learned cadence" v={restockSignal.cadenceLabel || 'Learning'} />
                <Row k="Next expected order" v={restockSignal.nextOrderDate || 'Needs order history'} />
                <Row k="Shelf signal" v={restockSignal.shelf && restockSignal.shelf.total != null ? (restockSignal.shelf.empty ? `${restockSignal.shelf.empty} SKU out` : restockSignal.shelf.low ? `${restockSignal.shelf.low} SKU low` : `${restockSignal.shelf.total} counted`) : 'No shelf count yet'} />
                <Row k="Typical order" v={restockSignal.avgCases ? `${restockSignal.avgCases} cases` : 'Learning'} />
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 12.2, lineHeight: 1.4, marginTop: 12 }}>{restockSignal.summary}</div>
            </div>
          )}
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => setModal('edit')}><Icon name="pen" size={14} />Edit</button>}>Account</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
              <Row k="Buyer" v={c.buyer} /><Row k="Buyer email" v={c.email ? <span className="mono" style={{ fontSize: 12.5 }}>{c.email}</span> : directoryOnly ? 'Not provided' : 'Missing'} /><Row k="Phone" v={primaryPhone ? <span className="mono" style={{ fontSize: 12.5 }}>{primaryPhone}</span> : directoryOnly ? 'Not provided' : 'Missing'} />
              {!directoryOnly && <><Row k="Terms" v={c.terms} /><Row k="Payment policy" v={typeof rpCustomerPaymentPolicySummary === 'function' ? rpCustomerPaymentPolicySummary(c) : 'Account terms'} /><Row k="Price list" v={c.tier} /><Row k="12-pack price" v={`${RP.money2(rpCustomerCasePrice(c))}${rpCustomerCustomCasePrice(c) > 0 ? ' · Custom' : ' · Master'}`} /><Row k="Tax policy" v={typeof rpCustomerTaxPolicySummary === 'function' ? rpCustomerTaxPolicySummary(c) : (c.taxRegion || 'BC')} /></>}
              <Row k="Assigned rep" v={c.rep} /><Row k="Territory" v={c.territory} />
            </div>
          </div>
          {!directoryOnly && <CustomerPaymentVaultCard c={c} onAdd={() => setModal('stripe-card')} />}
          {!directoryOnly && <CustomerAccountCreditsCard c={c} credits={accountCredits} debts={accountDebts} balance={accountCreditBalance} debtBalance={accountDebtBalance} canAdd={canAddAccountCredit} canSettle={canAddAccountCredit} onAdd={() => setModal('credit')} onAddDebt={() => setModal('debt')} onSettle={setSettleCredit} />}
          {contacts.length > 1 && (
            <div className="card" style={{ padding: '18px 20px' }}>
              <SectionTitle>Contacts</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {contacts.map((contact, index) => (
                  <div key={index} style={{ border: '1px solid var(--line)', borderRadius: 9, padding: '10px 12px', background: contact.primary ? 'var(--surface-2)' : 'var(--surface)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{contact.name || contact.email || contact.phone}</div>
                      {contact.primary && <Pill status="Active">Primary</Pill>}
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>{contact.role || 'Contact'}</div>
                    {contact.receivesInvoices === true && <div style={{ color: 'var(--ok)', fontSize: 11.5, marginTop: 4 }}>Invoice + receipt copies</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 8, color: 'var(--ink-soft)', fontSize: 12.5 }}>
                      {contact.email && <span className="mono">{contact.email}</span>}
                      {contact.phone && <span className="mono">{contact.phone}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {locations.length > 0 && (
            <div className="card" style={{ padding: '18px 20px' }}>
              <SectionTitle right={<Pill status={locations.length > 1 ? 'Submitted' : 'Active'}>{locations.length} total</Pill>}>Locations</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {locations.map((location, index) => (
                  <div key={location.id || index} style={{ border: '1px solid var(--line)', borderRadius: 9, padding: '10px 12px', background: 'var(--surface)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{location.name || 'Location'}</div>
                      {location.active === false && <Pill status="Disabled">Inactive</Pill>}
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>{location.type || 'Ship-to'}</div>
                    <div style={{ color: 'var(--ink-soft)', fontSize: 12.5, marginTop: 8, lineHeight: 1.35 }}>{[location.address, location.city, location.postal].filter(Boolean).join(', ') || 'Address missing'}</div>
                    {location.phone && <div className="mono" style={{ color: 'var(--ink-soft)', fontSize: 12.5, marginTop: 4 }}>{location.phone}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <CustomerPhotosCard c={c} locations={locations} force={() => force(x => x + 1)} />
          {!directoryOnly && <CustomerOnboardingCard c={c} force={() => force(x => x + 1)} />}
          <LocationPublicCard c={c} force={() => force(x => x + 1)} />
          {!directoryOnly && <CustomerShopifySyncCard c={c} force={() => force(x => x + 1)} />}
          {!directoryOnly && <CustomerApprovalCard c={c} force={() => force(x => x + 1)} openEdit={() => setModal('edit')} />}
          {!directoryOnly && <PortalAccessCard c={c} force={() => force(x => x + 1)} />}
          {c.note && <div className="card" style={{ padding: '18px 20px' }}><SectionTitle>Notes</SectionTitle><div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{c.note}</div></div>}
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { OrdersView, OrderDetail, CustomersView, CustomerDetailDesk, orderTotals, ORDER_STAGES, Row, FilterBar, distinct, useFilters });


/* ===== desktop_events.jsx ===== */
/* ============================================================
   RANCH POWER - Desktop : Events management
   ============================================================ */

const EVENT_STATUS_FLOW = [
  'lead',
  'evaluating',
  'quoted',
  'booked',
  'needs_staffing',
  'staffed',
  'inventory_planning',
  'packed',
  'in_progress',
  'pending_reconciliation',
  'reconciled',
  'settled',
  'cancelled',
  'lost'
];

const EVENT_STATUS_LABELS = {
  lead: 'Lead',
  evaluating: 'Evaluating',
  quoted: 'Quoted',
  booked: 'Booked',
  needs_staffing: 'Needs Staffing',
  staffed: 'Staffed',
  inventory_planning: 'Inventory Planning',
  packed: 'Packed',
  in_progress: 'In Progress',
  pending_reconciliation: 'Pending Reconciliation',
  reconciled: 'Reconciled',
  settled: 'Settled',
  cancelled: 'Cancelled',
  lost: 'Lost'
};

const EVENT_TYPE_OPTIONS = ['Festival', 'Sampling', 'Market', 'Retail Activation', 'Sports', 'Private Event', 'Trade Show', 'Community', 'Other'];
const EVENT_TEAM_ROLES = ['Lead', 'Sales', 'Sampling', 'Setup', 'POS', 'Driver', 'Warehouse', 'Support'];
const EVENT_PURPOSE_OPTIONS = ['sell_cases', 'sell_singles', 'samples', 'giveaways', 'display', 'backup', 'staff', 'other'];
const EVENT_INVENTORY_ALLOCATION_STATUSES = new Set(['booked', 'needs_staffing', 'staffed', 'inventory_planning', 'packed', 'in_progress']);
const EVENT_UNIT_OPTIONS = ['case', 'can', 'item'];
const EVENT_CLOSEOUT_LABELS = { not_started: 'Not Started', draft: 'Draft', submitted: 'Submitted', reconciled: 'Reconciled' };
const EVENT_ALLOCATED_STATUS = 'Event Allocated';
const EVENT_OPEN_CASE_STATUS = 'Open Event Stock';
const EVENT_CASE_PACK_SIZE = 12;
const EVENT_SINGLE_CASE_TO_CAN_SKU = {
  'RP-12-BO': 'RP-CAN-BO',
  'RP-12-BR': 'RP-CAN-BR',
  'RP-12-GC': 'RP-CAN-GC',
  'RP-12-KL': 'RP-CAN-KL',
  'RP-12-PL': 'RP-CAN-PL',
  'RP-12-PM': 'RP-CAN-PM'
};
const EVENT_SOURCE_SELLABLE_STATUSES = ['Available', 'Fronted to Rep', 'Purchased by Rep'];

function eventRows() {
  return Array.isArray(RP.EVENTS) ? RP.EVENTS : [];
}

function eventStatusLabel(status) {
  const key = String(status || 'lead').trim().toLowerCase();
  return EVENT_STATUS_LABELS[key] || key.split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function eventCanAllocateInventory(event) {
  return EVENT_INVENTORY_ALLOCATION_STATUSES.has(String(event && event.status || '').trim().toLowerCase());
}

function eventPurposeLabel(value) {
  return String(value || 'other').split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function eventDateValue(event) {
  return String(event && (event.eventDate || event.startAt || event.createdAt) || '').slice(0, 10);
}

function eventEndDateValue(event) {
  const start = eventDateValue(event);
  const end = String(event && (event.endDate || event.endAt) || '').slice(0, 10);
  return end && (!start || end >= start) ? end : start;
}

function eventDateDisplay(event) {
  const start = eventDateValue(event);
  if (!start) return 'Unscheduled';
  const end = eventEndDateValue(event);
  return end && end !== start ? `${start} to ${end}` : start;
}

function eventTimeDisplay(event) {
  const start = String(event && event.startAt || '');
  const end = String(event && event.endAt || '');
  const time = value => value && value.includes('T') ? value.slice(11, 16) : '';
  return [time(start), time(end)].filter(Boolean).join(' - ');
}

function eventStatusTone(status) {
  const value = String(status || '').toLowerCase();
  if (['reconciled', 'settled', 'staffed'].includes(value)) return 'Approved';
  if (['booked', 'packed', 'in_progress'].includes(value)) return 'In Progress';
  if (['pending_reconciliation', 'needs_staffing', 'inventory_planning', 'quoted', 'evaluating'].includes(value)) return 'Pending';
  if (['cancelled', 'lost'].includes(value)) return 'Cancelled';
  return 'Prospect';
}

function eventTeamRows(eventId) {
  return (RP.EVENT_TEAM || []).filter(row => row && row.eventId === eventId);
}

function eventPlanRows(eventId) {
  return (RP.EVENT_INVENTORY_PLAN || []).filter(row => row && row.eventId === eventId);
}

function eventAllocationRows(eventId) {
  return (RP.EVENT_INVENTORY_ALLOCATIONS || []).filter(row => row && row.eventId === eventId);
}

function eventSalesRows(eventId) {
  return (RP.EVENT_SALES_LINKS || []).filter(row => row && row.eventId === eventId);
}

function eventCloseoutRow(eventId) {
  return (RP.EVENT_CLOSEOUTS || []).find(row => row && row.eventId === eventId) || null;
}

function eventCloseoutReceiptRows(eventId) {
  return (RP.EVENT_INVENTORY_CLOSEOUT_RECEIPTS || [])
    .filter(row => row && row.eventId === eventId)
    .sort((left, right) => String(right.createdAt || '').localeCompare(String(left.createdAt || '')));
}

function eventActiveCloseoutReceipt(eventId) {
  const receipts = eventCloseoutReceiptRows(eventId);
  const reversed = new Set(receipts.filter(row => row.action === 'reversal').map(row => row.reversesReceiptId).filter(Boolean));
  return receipts.find(row => row.action === 'closeout' && !reversed.has(row.id)) || null;
}

function eventCanReverseCloseout() {
  const role = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(rpAuthProfile().role) : String(rpAuthProfile().role || '').toLowerCase();
  return ['admin', 'leadership', 'ops'].includes(role);
}

function eventCurrentActorId() {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : (window.RP_AUTH && window.RP_AUTH.profile) || {};
  return String(profile && profile.id || '').trim();
}

function eventActorStorageKey(kind, subject) {
  const actorId = eventCurrentActorId();
  return `rp:event-action:v1:${encodeURIComponent(actorId || 'missing-actor')}:${kind}:${encodeURIComponent(String(subject || 'unknown'))}`;
}

function eventCloseoutRequestStorageKey(event) {
  const latestReversal = eventCloseoutReceiptRows(event && event.id)
    .find(row => row.action === 'reversal');
  return eventActorStorageKey('closeout', `${event && event.id || 'unknown'}:${latestReversal && latestReversal.id || 'initial'}`);
}

function eventReversalRequestStorageKey(receiptId) {
  return eventActorStorageKey('closeout-reversal', receiptId);
}

function eventOpenCaseRequestStorageKey(eventId) {
  return eventActorStorageKey('open-cases', eventId);
}

function eventAllocationRequestStorageKey(eventId) {
  return eventActorStorageKey('allocate', eventId);
}

function eventLoadPendingActionRequest(storageKey) {
  try {
    const parsed = JSON.parse(window.localStorage && window.localStorage.getItem(storageKey) || 'null');
    const actorId = eventCurrentActorId();
    return parsed && parsed.version === 1 && actorId && parsed.actorId === actorId
      && parsed.clientRequestId && parsed.payload && typeof parsed.payload === 'object'
      ? parsed
      : null;
  } catch { return null; }
}

function eventPreparePendingActionRequest(storageKey, payload) {
  const actorId = eventCurrentActorId();
  if (!actorId) throw new Error('A signed-in actor is required before an Event inventory request can be saved safely.');
  let rawEvidence;
  try {
    if (!window.localStorage) throw new Error('Local recovery storage is unavailable.');
    rawEvidence = window.localStorage.getItem(storageKey);
  } catch (error) {
    throw new Error(error && error.message ? error.message : 'Local recovery storage is unavailable.');
  }
  const pending = eventLoadPendingActionRequest(storageKey);
  if (rawEvidence !== null && !pending) {
    throw new Error('Unrecognized Event recovery evidence is already saved for this actor and action. It was preserved without being overwritten; ask Operations to inspect it.');
  }
  if (pending && JSON.stringify(pending.payload) === JSON.stringify(payload)) return pending.clientRequestId;
  if (pending) {
    throw new Error('An unresolved Event request is already saved for this action. Recover that exact request before changing or replacing its payload.');
  }
  const clientRequestId = eventAllocationClientRequestId();
  const envelope = { actorId, clientRequestId, createdAt: new Date().toISOString(), payload, version: 1 };
  try {
    if (!window.localStorage) throw new Error('Local recovery storage is unavailable.');
    window.localStorage.setItem(storageKey, JSON.stringify(envelope));
    const verified = eventLoadPendingActionRequest(storageKey);
    if (!verified || verified.clientRequestId !== clientRequestId) throw new Error('The Event recovery request could not be verified after saving.');
  } catch (error) {
    throw new Error(error && error.message ? error.message : 'The Event recovery request could not be saved safely.');
  }
  return clientRequestId;
}

function eventLoadPendingOpenCaseRequest(storageKey) {
  return eventLoadPendingActionRequest(storageKey);
}

function eventPreparePendingOpenCaseRequest(storageKey, payload) {
  return eventPreparePendingActionRequest(storageKey, payload);
}

function eventClearPendingActionRequest(storageKey, expectedClientRequestId) {
  try {
    const pending = eventLoadPendingActionRequest(storageKey);
    if (!pending || pending.clientRequestId !== expectedClientRequestId) {
      throw new Error('The saved Event recovery envelope no longer matches the verified receipt.');
    }
    if (!window.localStorage) throw new Error('Local recovery storage is unavailable.');
    window.localStorage.removeItem(storageKey);
    if (window.localStorage.getItem(storageKey) !== null) throw new Error('The verified Event recovery envelope could not be cleared.');
  } catch (error) {
    throw new Error(error && error.message ? error.message : 'The verified Event recovery envelope could not be cleared.');
  }
}

function eventReceiptMatchesPending(receipt, pending, eventId) {
  if (!receipt || !pending) return false;
  const clientRequestId = receipt.clientRequestId || receipt.client_request_id || '';
  const receiptEventId = receipt.eventId || receipt.event_id || '';
  const actorId = receipt.actorId || receipt.actor_id || '';
  return clientRequestId === pending.clientRequestId
    && receiptEventId === eventId
    && actorId === eventCurrentActorId();
}

function eventUiCloseoutReceipt(receipt) {
  if (!receipt || !receipt.id) return null;
  return {
    id: receipt.id,
    action: receipt.action,
    clientRequestId: receipt.clientRequestId || receipt.client_request_id || '',
    eventId: receipt.eventId || receipt.event_id || '',
    actorId: receipt.actorId || receipt.actor_id || null,
    reversesReceiptId: receipt.reversesReceiptId || receipt.reverses_receipt_id || null,
    notes: receipt.notes || '',
    summary: receipt.summary && typeof receipt.summary === 'object' ? receipt.summary : {},
    ledgerIds: Array.isArray(receipt.ledgerIds || receipt.ledger_ids) ? (receipt.ledgerIds || receipt.ledger_ids) : [],
    allocationIds: Array.isArray(receipt.allocationIds || receipt.allocation_ids) ? (receipt.allocationIds || receipt.allocation_ids) : [],
    batchIds: Array.isArray(receipt.batchIds || receipt.batch_ids) ? (receipt.batchIds || receipt.batch_ids) : [],
    movementIds: Array.isArray(receipt.movementIds || receipt.movement_ids) ? (receipt.movementIds || receipt.movement_ids) : [],
    closeoutId: receipt.closeoutId || receipt.closeout_id || null,
    auditLogId: receipt.auditLogId || receipt.audit_log_id || null,
    createdAt: receipt.createdAt || receipt.created_at || new Date().toISOString()
  };
}

function eventUpsertCloseoutReceipt(receipt) {
  const normalized = eventUiCloseoutReceipt(receipt);
  if (!normalized) return null;
  RP.EVENT_INVENTORY_CLOSEOUT_RECEIPTS = [
    normalized,
    ...(RP.EVENT_INVENTORY_CLOSEOUT_RECEIPTS || []).filter(row => row && row.id !== normalized.id)
  ];
  return normalized;
}

function eventBatchMatchesEvent(batch, event) {
  if (!batch || !event) return false;
  if (batch.eventId) return batch.eventId === event.id;
  return batch.eventDate === eventDateValue(event) && batch.eventLabel === event.name;
}

function eventOpenCaseBatches(eventId) {
  const event = (RP.EVENTS || []).find(row => row && row.id === eventId);
  return (RP.OPEN_CASE_BATCHES || []).filter(row => eventBatchMatchesEvent(row, event));
}

function eventMovementCanDelta(movement) {
  const qty = Number(movement && movement.quantity || 0);
  const type = String(movement && (movement.type || movement.movementType) || '').toLowerCase();
  const unit = String(movement && movement.unit || '').toLowerCase();
  if (type === 'open_case') return unit === 'case' ? qty * 12 : qty;
  if (type === 'can_sale') return -qty;
  if (type === 'count_adjustment') return qty;
  return 0;
}

function eventOpenCanSummary(eventId) {
  const batches = eventOpenCaseBatches(eventId);
  return batches.reduce((summary, batch) => {
    const movements = (RP.OPEN_CASE_MOVEMENTS || []).filter(movement => movement.batchId === batch.id);
    const openedCases = movements.filter(movement => movement.type === 'open_case').reduce((sum, movement) => sum + Number(movement.quantity || 0), 0);
    const soldCans = movements.filter(movement => movement.type === 'can_sale').reduce((sum, movement) => sum + Number(movement.quantity || 0), 0);
    const remainingCans = Math.max(0, Math.round(movements.reduce((sum, movement) => sum + eventMovementCanDelta(movement), 0) * 100) / 100);
    summary.openedCases += openedCases;
    summary.soldCans += soldCans;
    summary.remainingCans += remainingCans;
    summary.batchCount += 1;
    return summary;
  }, { batchCount: 0, openedCases: 0, remainingCans: 0, soldCans: 0 });
}

function eventOpenBatchRemaining(batch) {
  const movements = (RP.OPEN_CASE_MOVEMENTS || []).filter(movement => movement.batchId === batch.id);
  return Math.max(0, Math.round(movements.reduce((sum, movement) => sum + eventMovementCanDelta(movement), 0) * 100) / 100);
}

function eventOwnerLabel(event) {
  return `Event Stock - ${String(event && event.id || '').trim()}`.slice(0, 180);
}

function eventOpenCanOwnerLabel(event) {
  return `Open Event Stock - ${String(event && event.id || 'Event').trim()}`.slice(0, 180);
}

function eventCaseCanSkuId(caseSkuId) {
  return EVENT_SINGLE_CASE_TO_CAN_SKU[String(caseSkuId || '').trim().toUpperCase()] || '';
}

function eventInventoryCaseSkuId(skuId) {
  return typeof rpCanonicalInventorySkuId === 'function'
    ? rpCanonicalInventorySkuId(skuId)
    : String(skuId || '').trim().toUpperCase();
}

function eventInventoryCaseName(skuId) {
  return rpSkuDisplayName(rpSkuItem(skuId) || skuId).replace(' 355ml', '');
}

function eventSourceCaseHoldings(locationId) {
  if (!locationId) return [];
  const location = RP.loc && RP.loc(locationId);
  const statuses = location && location.type === 'Micro-Warehouse' ? EVENT_SOURCE_SELLABLE_STATUSES : ['Available'];
  const grouped = new Map();
  (RP.INV || [])
    .filter(row => row && row.loc === locationId && String(row.unit || 'case').toLowerCase() !== 'can')
    .filter(row => statuses.includes(String(row.status || 'Available')) && Number(row.qty || 0) > 0)
    .forEach(row => {
      const skuId = eventInventoryCaseSkuId(row.sku);
      const sku = rpSkuItem(skuId) || rpSkuItem(row.sku) || row.sku;
      if (!skuId || !rpSkuIsInventoryProduct(sku)) return;
      const lot = row.lot || 'UNASSIGNED';
      const key = [skuId, lot].join('|');
      const current = grouped.get(key) || {
        exp: row.exp || row.lotExpiresAt || '',
        lot,
        qty: 0,
        skuId
      };
      current.qty += Number(row.qty || 0);
      current.exp = current.exp || row.exp || row.lotExpiresAt || '';
      grouped.set(key, current);
    });
  return Array.from(grouped.values())
    .filter(row => row.qty > 0)
    .sort((a, b) => eventInventoryCaseName(a.skuId).localeCompare(eventInventoryCaseName(b.skuId)) || String(a.lot).localeCompare(String(b.lot)));
}

function eventAllocatedCaseHoldings(event, locationId = '') {
  if (!event) return [];
  const owner = eventOwnerLabel(event);
  const grouped = new Map();
  (RP.INV || [])
    .filter(row => row && String(row.unit || 'case').toLowerCase() !== 'can')
    .filter(row => String(row.status || '') === EVENT_ALLOCATED_STATUS && String(row.owner || '') === owner)
    .filter(row => !locationId || row.loc === locationId)
    .filter(row => Number(row.qty || 0) > 0)
    .forEach(row => {
      const skuId = eventInventoryCaseSkuId(row.sku);
      const lot = row.lot || 'UNASSIGNED';
      const key = [skuId, row.loc || '', lot].join('|');
      const current = grouped.get(key) || {
        exp: row.exp || row.lotExpiresAt || '',
        lot,
        purpose: 'sell_cases',
        quantity: 0,
        skuId,
        sourceLocationId: row.loc || ''
      };
      current.quantity += Number(row.qty || 0);
      current.exp = current.exp || row.exp || row.lotExpiresAt || '';
      grouped.set(key, current);
    });
  return Array.from(grouped.values())
    .filter(row => row.quantity > 0)
    .sort((a, b) => eventInventoryCaseName(a.skuId).localeCompare(eventInventoryCaseName(b.skuId)) || String(a.lot).localeCompare(String(b.lot)));
}

function eventCloseoutCaseGroups(eventOrId) {
  const event = typeof eventOrId === 'string' ? eventRows().find(row => row.id === eventOrId) : eventOrId;
  const eventId = event && event.id || eventOrId;
  const liveRows = event ? eventAllocatedCaseHoldings(event) : [];
  if (liveRows.length) {
    return liveRows.map(row => ({
      damagedCases: '',
      giveawayCases: '',
      lot: row.lot || 'UNASSIGNED',
      missingCases: '',
      purpose: row.purpose || 'sell_cases',
      quantity: row.quantity,
      returnedCases: '',
      sampleCases: '',
      skuId: row.skuId,
      soldCases: '',
      sourceLocationId: row.sourceLocationId || ''
    }));
  }
  const map = new Map();
  eventAllocationRows(eventId)
    .filter(row => String(row.unit || 'case').toLowerCase() === 'case')
    .filter(row => !['Cancelled', 'Reconciled'].includes(String(row.status || '')))
    .forEach(row => {
      const key = [row.skuId, row.sourceLocationId || '', row.lot || 'UNASSIGNED'].join('|');
      const current = map.get(key) || {
        damagedCases: '',
        giveawayCases: '',
        lot: row.lot || 'UNASSIGNED',
        missingCases: '',
        purpose: row.purpose || 'sell_cases',
        quantity: 0,
        returnedCases: '',
        sampleCases: '',
        skuId: row.skuId,
        soldCases: '',
        sourceLocationId: row.sourceLocationId || ''
      };
      current.quantity += Number(row.quantity || 0);
      map.set(key, current);
    });
  return Array.from(map.values()).sort((a, b) => rpSkuDisplayName(rpSkuItem(a.skuId) || a.skuId).localeCompare(rpSkuDisplayName(rpSkuItem(b.skuId) || b.skuId)));
}

function eventLocationName(locationId) {
  const loc = RP.loc && RP.loc(locationId);
  return (loc && (loc.name || loc.id)) || locationId || 'Unassigned';
}

function eventCustomerName(customerId) {
  const customer = RP.cust && RP.cust(customerId);
  return (customer && customer.name) || customerId || '';
}

function eventUserOptions() {
  if (Array.isArray(RP.USER_PROFILES) && RP.USER_PROFILES.length) {
    return RP.USER_PROFILES
      .filter(user => user && user.id && user.active !== false && rpNormalizeRole(user.role) !== 'customer')
      .map(user => ({
        id: user.id,
        label: user.name || user.displayName || user.email || user.id,
        role: rpRoleLabel(user.role),
        territory: user.territory || ''
      }))
      .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  }
  return ['Darren Sutherland', 'Wade Sutherland', 'Lydia Sutherland', 'Dana Whitfield'].map(name => ({ id: name, label: name, role: 'Team', territory: '' }));
}

function eventSkuOptions() {
  const skus = typeof rpInventorySkus === 'function' ? rpInventorySkus() : (RP.SKUS || []);
  return skus
    .filter(sku => sku && sku.id && (!rpSkuLooksOperationalArtifact || !rpSkuLooksOperationalArtifact(sku)))
    .sort((a, b) => rpSkuDisplayName(a).localeCompare(rpSkuDisplayName(b)));
}

function eventLocationOptions() {
  const allowed = new Set(['Main Warehouse', 'Micro-Warehouse', 'Distributor']);
  return (RP.LOCATIONS || [])
    .filter(location => location && location.id && location.active !== false && allowed.has(location.type))
    .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
}

function eventCustomerOptions() {
  return (RP.CUSTOMERS || [])
    .filter(customer => customer && customer.id && customer.status !== 'Deleted')
    .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
}

function eventTerritoryOptions(current) {
  const values = [];
  const add = value => {
    const text = String(value || '').trim();
    if (text && !values.some(item => item.toLowerCase() === text.toLowerCase())) values.push(text);
  };
  (RP.TERRITORIES || []).forEach(add);
  (RP.LOCATIONS || []).forEach(location => add(location.territory));
  add(current);
  return values.length ? values : ['Unassigned'];
}

function eventSort(a, b) {
  return String(eventDateValue(a) || '9999-99-99').localeCompare(String(eventDateValue(b) || '9999-99-99')) || String(a.name || '').localeCompare(String(b.name || ''));
}

function eventCurrentMonthStart(dateValue) {
  const base = dateValue ? new Date(dateValue) : new Date();
  return new Date(base.getFullYear(), base.getMonth(), 1);
}

function eventYmd(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function eventDateFromYmd(value) {
  const text = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [year, month, day] = text.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function eventDateRange(event, maxDays = 120) {
  const startValue = eventDateValue(event);
  if (!startValue) return [];
  const endValue = eventEndDateValue(event) || startValue;
  const start = eventDateFromYmd(startValue);
  const end = eventDateFromYmd(endValue);
  if (!start || !end) return [startValue];
  const days = [];
  const cursor = new Date(start);
  while (cursor <= end && days.length < maxDays) {
    days.push(eventYmd(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days.length ? days : [startValue];
}

function eventOverlapsMonth(event, month) {
  const start = eventDateFromYmd(eventDateValue(event));
  const end = eventDateFromYmd(eventEndDateValue(event));
  if (!start || !end) return false;
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  return start <= monthEnd && end >= monthStart;
}

function eventAddMonths(date, delta) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function eventMonthLabel(date) {
  return date.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
}

function EventsView({ go, detail }) {
  const [mode, setMode] = useState('calendar');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(detail && detail.type === 'event' ? detail.id : '');
  const [month, setMonth] = useState(() => eventCurrentMonthStart());
  const [editing, setEditing] = useState(false);
  const [, force] = useState(0);
  const isCompact = useIsNarrow(980);
  const isPhone = useIsNarrow(620);
  const [filters, onFilter] = useFilters();
  const rows = eventRows().slice().sort(eventSort);
  const selected = rows.find(event => event.id === selectedId) || rows[0] || null;
  const nowYmd = rpTodayYmd ? rpTodayYmd() : new Date().toISOString().slice(0, 10);
  const openStatuses = new Set(['lead', 'evaluating', 'quoted', 'booked', 'needs_staffing', 'staffed', 'inventory_planning', 'packed', 'in_progress', 'pending_reconciliation']);
  const upcomingRows = rows.filter(event => eventEndDateValue(event) >= nowYmd && openStatuses.has(String(event.status || 'lead')));
  const activeRows = rows.filter(event => ['booked', 'needs_staffing', 'staffed', 'inventory_planning', 'packed', 'in_progress'].includes(String(event.status || '')));
  const reconcileRows = rows.filter(event => String(event.status || '') === 'pending_reconciliation' || String(event.closeoutStatus || '') === 'submitted');
  const openCanCount = rows.reduce((sum, event) => sum + eventOpenCanSummary(event.id).remainingCans, 0);
  const defs = [
    { label: 'Status', options: distinct(rows, event => eventStatusLabel(event.status)) },
    { label: 'Territory', options: distinct(rows, event => event.territory) },
    { label: 'Type', options: distinct(rows, event => event.eventType) }
  ];
  const filteredRows = rows.filter(event => {
    if (filters.Status && eventStatusLabel(event.status) !== filters.Status) return false;
    if (filters.Territory && event.territory !== filters.Territory) return false;
    if (filters.Type && event.eventType !== filters.Type) return false;
    const term = String(search || '').trim().toLowerCase();
    if (!term) return true;
    return [
      event.name,
      event.venueName,
      event.territory,
      event.eventType,
      eventCustomerName(event.customerId),
      eventLocationName(event.defaultSourceLocationId),
      event.notes
    ].join(' ').toLowerCase().includes(term);
  });

  useEffect(() => {
    if (detail && detail.type === 'event') setSelectedId(detail.id);
  }, [detail && detail.type, detail && detail.id]);

  return (
    <div className="rp-fade" style={{ padding: isPhone ? '16px 12px 30px' : isCompact ? '20px 18px 34px' : '26px 30px 40px' }}>
      {editing && <EventEditorModal event={editing === true ? null : editing} onClose={() => setEditing(false)} onSaved={eventId => { setEditing(false); if (eventId) setSelectedId(eventId); force(x => x + 1); }} />}
      <PageHead eyebrow="Field Marketing" title="Events"
        sub="Plan staff, stock, POS sales, samples, returns, and closeout counts from one place."
        actions={<button className="btn btn-sm btn-primary" onClick={() => setEditing(true)}><Icon name="plus" size={15} />New Event</button>} />

      <div className="rp-events-kpis" style={{ display: 'grid', gridTemplateColumns: isPhone ? 'repeat(2, minmax(0,1fr))' : isCompact ? 'repeat(3, minmax(0,1fr))' : 'repeat(5, minmax(0,1fr))', gap: isPhone ? 8 : 14, marginBottom: 18 }}>
        <KPICard label="Upcoming" value={upcomingRows.length} unit="events" icon="calendar" accent="var(--info)" />
        <KPICard label="Active Prep" value={activeRows.length} unit="events" icon="flag" accent="var(--amber)" />
        <KPICard label="Closeouts" value={reconcileRows.length} unit="open" icon="check" accent="var(--warn)" />
        <KPICard label="Open Cans" value={openCanCount.toLocaleString()} unit="cans" icon="package" accent="var(--ok)" onClick={() => go && go('inventory', { type: 'open-cans' })} />
        <KPICard label="Linked POS" value={(RP.EVENT_SALES_LINKS || []).length} unit="orders" icon="link" accent="var(--info)" />
      </div>

      <div style={{ display: 'flex', alignItems: isPhone ? 'stretch' : 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <Segmented options={[{ value: 'calendar', label: 'Calendar' }, { value: 'board', label: 'Board' }, { value: 'list', label: 'List' }]} value={mode} onChange={setMode} />
        {mode === 'calendar' && <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isPhone ? '100%' : 'auto', justifyContent: isPhone ? 'space-between' : 'flex-start' }}>
          <button className="btn btn-sm" onClick={() => setMonth(eventAddMonths(month, -1))}><Icon name="chevL" size={14} /></button>
          <div className="disp" style={{ fontSize: 16, minWidth: 150, textAlign: 'center', color: 'var(--ink)' }}>{eventMonthLabel(month)}</div>
          <button className="btn btn-sm" onClick={() => setMonth(eventAddMonths(month, 1))}><Icon name="chevR" size={14} /></button>
        </div>}
      </div>

      <FilterBar defs={defs} value={filters} onChange={onFilter} search={search} onSearch={setSearch} searchPlaceholder="Search events, venues, customers, locations..." />

      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1fr) minmax(320px, 0.44fr)', gap: isPhone ? 12 : 18, alignItems: 'start' }}>
        {mode === 'calendar'
          ? <EventsCalendar month={month} events={filteredRows} selectedId={selected && selected.id} onSelect={setSelectedId} compact={isCompact} />
          : mode === 'board'
            ? <EventsBoard events={filteredRows} selectedId={selected && selected.id} onSelect={setSelectedId} compact={isCompact} />
            : <EventsTable events={filteredRows} selectedId={selected && selected.id} onSelect={setSelectedId} compact={isCompact} />}
        <EventDetailPanel event={selected} onEdit={setEditing} go={go} />
      </div>
    </div>
  );
}

function EventsCalendar({ month, events, selectedId, onSelect, compact }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
  const eventsByDate = events.reduce((map, event) => {
    eventDateRange(event).forEach(key => {
      map[key] = map[key] || [];
      map[key].push(event);
    });
    return map;
  }, {});
  if (compact) {
    const monthRows = events
      .filter(event => eventOverlapsMonth(event, month))
      .sort(eventSort);
    const grouped = monthRows.reduce((map, event) => {
      eventDateRange(event).forEach(key => {
        if (key.slice(0, 7) !== eventYmd(month).slice(0, 7)) return;
        map[key] = map[key] || [];
        map[key].push(event);
      });
      return map;
    }, {});
    return (
      <div className="card" style={{ padding: 12, display: 'grid', gap: 12 }}>
        {Object.entries(grouped).map(([date, rows]) => (
          <div key={date} style={{ display: 'grid', gap: 8 }}>
            <div className="eyebrow" style={{ color: 'var(--ink-soft)' }}>{date}</div>
            {rows.map(event => <EventMiniCard key={`${date}-${event.id}`} event={event} selected={event.id === selectedId} onSelect={onSelect} compact />)}
          </div>
        ))}
        {!monthRows.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 18, textAlign: 'center' }}>No events in this month.</div>}
      </div>
    );
  }
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="eyebrow" style={{ padding: '10px 12px' }}>{day}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map(day => {
          const key = eventYmd(day);
          const dayRows = (eventsByDate[key] || []).sort(eventSort);
          const muted = day.getMonth() !== month.getMonth();
          return (
            <div key={key} style={{ minHeight: 118, borderRight: '1px solid var(--line)', borderBottom: '1px solid var(--line)', padding: 9, background: muted ? 'var(--surface-2)' : 'var(--surface)' }}>
              <div className="mono" style={{ color: muted ? 'var(--faint)' : 'var(--muted)', fontSize: 11.5, marginBottom: 7 }}>{day.getDate()}</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {dayRows.slice(0, 3).map(event => (
                  <button key={event.id} onClick={() => onSelect(event.id)}
                    style={{ textAlign: 'left', border: '1px solid ' + (selectedId === event.id ? 'var(--amber)' : 'var(--line)'), borderRadius: 7, background: selectedId === event.id ? 'var(--amber-soft)' : 'var(--surface)', padding: '6px 7px', cursor: 'pointer', boxShadow: selectedId === event.id ? '0 0 0 1px var(--amber) inset' : 'none' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.territory || eventStatusLabel(event.status)}</div>
                  </button>
                ))}
                {dayRows.length > 3 && <div style={{ fontSize: 11.5, color: 'var(--faint)', paddingLeft: 2 }}>+{dayRows.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventsBoard({ events, selectedId, onSelect, compact }) {
  const columns = [
    { id: 'lead', label: 'Lead', statuses: ['lead', 'evaluating', 'quoted'] },
    { id: 'booked', label: 'Booked', statuses: ['booked', 'needs_staffing', 'staffed'] },
    { id: 'prep', label: 'Inventory Prep', statuses: ['inventory_planning', 'packed'] },
    { id: 'live', label: 'Live', statuses: ['in_progress'] },
    { id: 'closeout', label: 'Closeout', statuses: ['pending_reconciliation', 'reconciled', 'settled'] }
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(5, minmax(180px, 1fr))', gap: 12, overflowX: compact ? 'visible' : 'auto' }}>
      {columns.map(column => {
        const rows = events.filter(event => column.statuses.includes(String(event.status || 'lead')));
        return (
          <div key={column.id} className="card" style={{ padding: 12, minHeight: compact ? 'auto' : 360 }}>
            <SectionTitle right={<span className="mono" style={{ color: 'var(--faint)', fontSize: 12 }}>{rows.length}</span>}>{column.label}</SectionTitle>
            <div style={{ display: 'grid', gap: 9 }}>
              {rows.map(event => <EventMiniCard key={event.id} event={event} selected={event.id === selectedId} onSelect={onSelect} compact={compact} />)}
              {!rows.length && <div style={{ color: 'var(--faint)', fontSize: 12.5, padding: '12px 4px' }}>Nothing here.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventMiniCard({ event, selected, onSelect, compact }) {
  const cans = eventOpenCanSummary(event.id);
  return (
    <button onClick={() => onSelect(event.id)} style={{ width: '100%', textAlign: 'left', border: '1px solid ' + (selected ? 'var(--amber)' : 'var(--line)'), borderRadius: 8, background: selected ? 'var(--amber-soft)' : 'var(--surface)', padding: 11, cursor: 'pointer', boxShadow: selected ? '0 0 0 1px var(--amber) inset' : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: compact ? 'flex-start' : 'flex-start', flexWrap: compact ? 'wrap' : 'nowrap' }}>
        <div style={{ fontWeight: 900, color: 'var(--ink)', lineHeight: 1.25, minWidth: 0 }}>{event.name}</div>
        <Pill status={eventStatusTone(event.status)}>{eventStatusLabel(event.status)}</Pill>
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 7 }}>{eventDateDisplay(event)}{eventTimeDisplay(event) ? ` - ${eventTimeDisplay(event)}` : ''}</div>
      <div style={{ color: 'var(--faint)', fontSize: 11.5, marginTop: 5 }}>{[event.venueName, event.territory].filter(Boolean).join(' - ') || 'No venue'}</div>
      {cans.batchCount > 0 && <div className="mono" style={{ color: 'var(--ok)', fontSize: 11.5, marginTop: 8 }}>{cans.remainingCans} open cans left</div>}
    </button>
  );
}

function EventsTable({ events, selectedId, onSelect, compact }) {
  if (compact) {
    return (
      <div className="card" style={{ padding: 12, display: 'grid', gap: 9 }}>
        {events.map(event => {
          const team = eventTeamRows(event.id);
          const planCases = eventPlanRows(event.id).filter(row => row.unit === 'case').reduce((sum, row) => sum + Number(row.quantity || 0), 0);
          return (
            <button key={event.id} onClick={() => onSelect(event.id)}
              style={{ border: '1px solid ' + (selectedId === event.id ? 'var(--amber)' : 'var(--line)'), background: selectedId === event.id ? 'var(--amber-soft)' : 'var(--surface)', borderRadius: 8, padding: 12, textAlign: 'left', boxShadow: selectedId === event.id ? '0 0 0 1px var(--amber) inset' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: 'var(--ink)', lineHeight: 1.2 }}>{event.name}</div>
                  <div style={{ color: 'var(--faint)', fontSize: 12, marginTop: 3 }}>{[event.venueName, eventCustomerName(event.customerId)].filter(Boolean).join(' - ')}</div>
                </div>
                <Pill status={eventStatusTone(event.status)}>{eventStatusLabel(event.status)}</Pill>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 10, fontSize: 12.5 }}>
                <Row k="Dates" v={`${eventDateDisplay(event)}${eventTimeDisplay(event) ? ` ${eventTimeDisplay(event)}` : ''}`} />
                <Row k="Source" v={eventLocationName(event.defaultSourceLocationId)} />
                <Row k="Team" v={team.length || '-'} />
                <Row k="Planned" v={`${planCases.toLocaleString()} cs`} />
              </div>
            </button>
          );
        })}
        {!events.length && <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 22 }}>No events match these filters.</div>}
      </div>
    );
  }
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <table className="rp-table">
        <thead><tr><th>Event</th><th>Dates</th><th>Status</th><th>Location</th><th>Team</th><th className="num">Planned</th><th className="num">POS Links</th></tr></thead>
        <tbody>
          {events.map(event => {
            const team = eventTeamRows(event.id);
            const planCases = eventPlanRows(event.id).filter(row => row.unit === 'case').reduce((sum, row) => sum + Number(row.quantity || 0), 0);
            return (
              <tr key={event.id} onClick={() => onSelect(event.id)} style={{ cursor: 'pointer', background: selectedId === event.id ? 'var(--amber-soft)' : undefined }}>
                <td><div style={{ fontWeight: 800 }}>{event.name}</div><div style={{ color: 'var(--faint)', fontSize: 11.5 }}>{[event.venueName, eventCustomerName(event.customerId)].filter(Boolean).join(' - ')}</div></td>
                <td className="mono" style={{ color: 'var(--muted)', fontSize: 12 }}>{eventDateDisplay(event)}{eventTimeDisplay(event) ? <div>{eventTimeDisplay(event)}</div> : null}</td>
                <td><Pill status={eventStatusTone(event.status)}>{eventStatusLabel(event.status)}</Pill></td>
                <td style={{ color: 'var(--muted)' }}>{eventLocationName(event.defaultSourceLocationId)}</td>
                <td style={{ color: 'var(--muted)' }}>{team.length || '-'}</td>
                <td className="num">{planCases.toLocaleString()} cs</td>
                <td className="num">{eventSalesRows(event.id).length}</td>
              </tr>
            );
          })}
          {!events.length && <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No events match these filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function EventDetailPanel({ event, onEdit, go }) {
  const [inventoryModal, setInventoryModal] = useState(null);
  const [closeoutOpen, setCloseoutOpen] = useState(false);
  const [reversalReceipt, setReversalReceipt] = useState(null);
  const isCompact = useIsNarrow(680);
  if (!event) {
    return (
      <div className="card" style={{ padding: 22 }}>
        <SectionTitle>Event Detail</SectionTitle>
        <div style={{ color: 'var(--faint)', fontSize: 13 }}>Create or select an event.</div>
      </div>
    );
  }
  const team = eventTeamRows(event.id);
  const plan = eventPlanRows(event.id);
  const allocations = eventAllocationRows(event.id);
  const sales = eventSalesRows(event.id);
  const closeout = eventCloseoutRow(event.id);
  const closeoutReceipts = eventCloseoutReceiptRows(event.id);
  const activeCloseoutReceipt = eventActiveCloseoutReceipt(event.id);
  const checkedOutCaseRows = eventAllocatedCaseHoldings(event);
  const openCanRows = eventOpenCaseBatches(event.id).filter(batch => batch.status !== 'Closed');
  const cans = eventOpenCanSummary(event.id);
  const canAllocateInventory = eventCanAllocateInventory(event);
  const closeoutDone = event.closeoutStatus === 'reconciled' || (closeout && closeout.status === 'reconciled');
  const closeoutReady = !closeoutDone;
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div className="card" style={{ padding: isCompact ? '15px 14px' : '18px 20px' }}>
        <SectionTitle right={<button className="btn btn-sm" onClick={() => onEdit(event)}><Icon name="pen" size={14} />Edit</button>}>Event Detail</SectionTitle>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: isCompact ? 'wrap' : 'nowrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--ink)', lineHeight: 1.15 }}>{event.name}</div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>{[event.venueName, event.territory].filter(Boolean).join(' - ')}</div>
          </div>
          <Pill status={eventStatusTone(event.status)}>{eventStatusLabel(event.status)}</Pill>
        </div>
        <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
          <Row k="Dates" v={`${eventDateDisplay(event)}${eventTimeDisplay(event) ? ` - ${eventTimeDisplay(event)}` : ''}`} />
          <Row k="Type" v={event.eventType || 'Event'} />
          <Row k="Customer" v={eventCustomerName(event.customerId) || 'None'} />
          <Row k="Source" v={eventLocationName(event.defaultSourceLocationId)} />
          <Row k="Attendance" v={event.expectedAttendance ? Number(event.expectedAttendance).toLocaleString() : '-'} />
          <Row k="Closeout" v={EVENT_CLOSEOUT_LABELS[event.closeoutStatus] || event.closeoutStatus || 'Not Started'} />
        </div>
        {event.notes && <div style={{ borderTop: '1px solid var(--line)', marginTop: 13, paddingTop: 12, color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.45 }}>{event.notes}</div>}
      </div>

      <div className="card" style={{ padding: isCompact ? '15px 14px' : '18px 20px' }}>
        <SectionTitle>Team</SectionTitle>
        <div style={{ display: 'grid', gap: 8 }}>
          {team.map(member => (
            <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Avatar name={member.name || member.userId} size={26} color="var(--navy)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{member.name || member.userId}</div>
                <div style={{ color: 'var(--faint)', fontSize: 11.5 }}>{member.role || 'Team'}</div>
              </div>
            </div>
          ))}
          {!team.length && <div style={{ color: 'var(--faint)', fontSize: 12.5 }}>No team assigned.</div>}
        </div>
      </div>

      <div className="card" style={{ padding: isCompact ? '15px 14px' : '18px 20px' }}>
        <SectionTitle right={<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-sm" disabled={!canAllocateInventory} title={canAllocateInventory ? 'Add exact-lot case inventory' : 'Inventory can only be added to an active booked event'} onClick={() => setInventoryModal('add-cases')}><Icon name="plus" size={14} />Add Cases</button>
          <button className="btn btn-sm" onClick={() => setInventoryModal('open-cases')}><Icon name="package" size={14} />Open Cases</button>
        </div>}>Inventory</SectionTitle>
        {!canAllocateInventory && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '9px 11px', fontSize: 12, fontWeight: 700, marginBottom: 10 }}>Case inventory cannot be added while this event is {eventStatusLabel(event.status)}.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'repeat(3, 1fr)', gap: 9, marginBottom: 12 }}>
          <EventMetric label="Planned" value={plan.reduce((sum, row) => sum + Number(row.quantity || 0), 0)} unit="units" />
          <EventMetric label="Cases At Event" value={checkedOutCaseRows.reduce((sum, row) => sum + Number(row.quantity || 0), 0)} unit="cases" />
          <EventMetric label="Open Loose" value={cans.remainingCans} unit="cans" />
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {checkedOutCaseRows.length > 0 && <div className="eyebrow">Checked-In Cases</div>}
          {checkedOutCaseRows.slice(0, 5).map(row => (
            <div key={`${row.skuId}-${row.sourceLocationId}-${row.lot}`} style={{ display: 'grid', gridTemplateColumns: isCompact ? 'minmax(0,1fr) auto' : 'minmax(0,1fr) 84px 110px', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <FlavorChip sku={rpSkuItem(row.skuId) || row.skuId} size={22} />
                <span style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{eventInventoryCaseName(row.skuId)}</span>
              </div>
              <span className="mono" style={{ color: 'var(--muted)' }}>{Number(row.quantity || 0).toLocaleString()} cs</span>
              {!isCompact && <span className="mono" style={{ color: 'var(--faint)' }}>{row.lot}</span>}
              {isCompact && <span className="mono" style={{ color: 'var(--faint)', gridColumn: '1 / -1' }}>{eventLocationName(row.sourceLocationId)} - {row.lot}</span>}
            </div>
          ))}
          {openCanRows.length > 0 && <div className="eyebrow" style={{ marginTop: checkedOutCaseRows.length ? 4 : 0 }}>Open Loose Cans</div>}
          {openCanRows.slice(0, 5).map(batch => (
            <div key={batch.id} style={{ display: 'grid', gridTemplateColumns: isCompact ? 'minmax(0,1fr) auto' : 'minmax(0,1fr) 84px 110px', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <FlavorChip sku={rpSkuItem(batch.canSkuId) || batch.canSkuId} size={22} />
                <span style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rpSkuDisplayName(rpSkuItem(batch.canSkuId) || batch.canSkuId)}</span>
              </div>
              <span className="mono" style={{ color: 'var(--muted)' }}>{eventOpenBatchRemaining(batch).toLocaleString()} can</span>
              {!isCompact && <span className="mono" style={{ color: 'var(--faint)' }}>{batch.lot}</span>}
              {isCompact && <span className="mono" style={{ color: 'var(--faint)', gridColumn: '1 / -1' }}>{eventLocationName(batch.sourceLocationId)} - {batch.lot}</span>}
            </div>
          ))}
          {!checkedOutCaseRows.length && !openCanRows.length && <div style={{ color: 'var(--faint)', fontSize: 12.5 }}>No cases checked in yet.</div>}
        </div>
      </div>

      <div className="card" style={{ padding: isCompact ? '15px 14px' : '18px 20px' }}>
        <SectionTitle right={<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {eventCanReverseCloseout() && activeCloseoutReceipt && <button className="btn btn-sm" onClick={() => setReversalReceipt(activeCloseoutReceipt)} style={{ color: 'var(--danger)' }}><Icon name="refresh" size={14} />Reverse Closeout</button>}
          <button className="btn btn-sm" disabled={closeoutDone || !closeoutReady} onClick={() => setCloseoutOpen(true)}><Icon name="check" size={14} />Close Out</button>
        </div>}>POS And Closeout</SectionTitle>
        <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
          <Row k="Linked orders" v={sales.length} />
          <Row k="Cases opened" v={`${cans.openedCases.toLocaleString()} cs`} />
          <Row k="Cans sold" v={`${cans.soldCans.toLocaleString()} can`} />
          <Row k="Remaining cans" v={`${cans.remainingCans.toLocaleString()} can`} />
          <Row k="Count status" v={(closeout && (EVENT_CLOSEOUT_LABELS[closeout.status] || closeout.status)) || 'Not started'} />
        </div>
        {closeoutReceipts.length > 0 && <div style={{ borderTop: '1px solid var(--line)', marginTop: 12, paddingTop: 10, display: 'grid', gap: 7 }}>
          <div className="eyebrow">Immutable closeout history</div>
          {closeoutReceipts.slice(0, 4).map(receipt => <div key={receipt.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5 }}>
            <span style={{ fontWeight: 800, color: receipt.action === 'reversal' ? 'var(--danger)' : 'var(--muted)' }}>{receipt.action === 'reversal' ? 'Reversal' : 'Closeout'} · {String(receipt.id).slice(0, 8)}</span>
            <span className="mono" style={{ color: 'var(--faint)' }}>{receipt.createdAt ? String(receipt.createdAt).replace('T', ' ').slice(0, 16) : ''}</span>
          </div>)}
        </div>}
      </div>
      {inventoryModal === 'add-cases' && canAllocateInventory && <EventAddCasesModal event={event} onClose={() => setInventoryModal(null)} />}
      {inventoryModal === 'open-cases' && <EventOpenCasesModal event={event} onClose={() => setInventoryModal(null)} />}
      {closeoutOpen && <EventCloseoutModal event={event} onClose={() => setCloseoutOpen(false)} />}
      {reversalReceipt && <EventCloseoutReversalModal event={event} receipt={reversalReceipt} onClose={() => setReversalReceipt(null)} />}
    </div>
  );
}

function eventHoldingKey(row) {
  return [row && row.skuId || '', row && row.lot || 'UNASSIGNED'].join('|');
}

function eventAllocationClientRequestId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return `evt-${window.crypto.randomUUID()}`;
  return `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function eventDefaultSourceLocationId(event) {
  const checkedOut = eventAllocatedCaseHoldings(event);
  return event && event.defaultSourceLocationId || (checkedOut[0] || {}).sourceLocationId || (eventLocationOptions()[0] || {}).id || '';
}

function eventDefaultRepId(event) {
  const team = eventTeamRows(event && event.id);
  const lead = team.find(row => ['lead', 'pos', 'sales'].includes(String(row.role || '').toLowerCase())) || team[0];
  return lead && lead.userId || '';
}

function EventAddCasesModal({ event, onClose }) {
  const isCompact = useIsNarrow(760);
  const requestStorageKey = eventAllocationRequestStorageKey(event.id);
  const [pendingRequest, setPendingRequest] = useState(() => eventLoadPendingActionRequest(requestStorageKey));
  const sourceLocations = eventLocationOptions();
  const [sourceLocationId, setSourceLocationId] = useState(() => pendingRequest && pendingRequest.payload.sourceLocationId || eventDefaultSourceLocationId(event));
  const [lines, setLines] = useState(() => pendingRequest && Array.isArray(pendingRequest.payload.lines) && pendingRequest.payload.lines.length
    ? pendingRequest.payload.lines.map((line, index) => ({ key: `recovery-${index}`, skuLot: `${line.skuId}|${line.lot}`, quantity: String(line.quantity), purpose: line.purpose || 'other' }))
    : [{ key: `line-${Date.now()}`, skuLot: '', quantity: '1', purpose: 'sell_singles' }]);
  const [notes, setNotes] = useState(() => pendingRequest && pendingRequest.payload.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const availableRows = eventSourceCaseHoldings(sourceLocationId);
  const firstOptionKey = availableRows[0] ? eventHoldingKey(availableRows[0]) : '';
  useEffect(() => {
    if (pendingRequest) return;
    setLines(prev => {
      if (!availableRows.length) return prev.map(row => ({ ...row, skuLot: '' }));
      return prev.map(row => availableRows.some(option => eventHoldingKey(option) === row.skuLot) ? row : { ...row, skuLot: firstOptionKey });
    });
  }, [sourceLocationId, firstOptionKey]);
  const selectedOption = skuLot => availableRows.find(option => eventHoldingKey(option) === skuLot);
  const addLine = () => setLines(prev => [...prev, { key: `line-${Date.now()}-${prev.length}`, skuLot: firstOptionKey, quantity: '1', purpose: 'sell_singles' }]);
  const removeLine = index => setLines(prev => prev.length <= 1 ? prev : prev.filter((_, rowIndex) => rowIndex !== index));
  const updateLine = (index, key, value) => setLines(prev => prev.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  const payloadLines = lines
    .map(row => ({ row, option: selectedOption(row.skuLot), quantity: Number(row.quantity || 0) }))
    .filter(item => item.option && item.quantity > 0)
    .map(item => ({
      lot: item.option.lot,
      purpose: item.row.purpose,
      quantity: item.quantity,
      skuId: item.option.skuId,
      unit: 'case'
    }));
  const valid = sourceLocationId && payloadLines.length > 0;
  const submit = async () => {
    if (busy || !valid || pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        toast('Event case check-in posts to the live ledger only.', 'info');
        onClose();
        return;
      }
      const payload = { eventId: event.id, sourceLocationId, lines: payloadLines, notes };
      const clientRequestId = eventPreparePendingActionRequest(requestStorageKey, payload);
      const preparedRequest = eventLoadPendingActionRequest(requestStorageKey);
      setPendingRequest(preparedRequest);
      const response = await rpApi('/api/events/allocate', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId, ...payload })
      });
      if (!eventReceiptMatchesPending(response && response.receipt, preparedRequest, event.id)) {
        throw new Error('The allocation may have committed, but its actor-bound receipt could not be verified. Recover this exact saved request.');
      }
      await rpRefreshAfterMutation('Event cases added');
      eventClearPendingActionRequest(requestStorageKey, clientRequestId);
      setPendingRequest(null);
      toast(`Cases added to event stock - receipt ${response.receipt.id}`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not add cases to the event.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  const recoverPending = async () => {
    if (busy || !pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/events/allocate', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId: pendingRequest.clientRequestId, ...pendingRequest.payload })
      });
      if (!eventReceiptMatchesPending(response && response.receipt, pendingRequest, event.id)) {
        throw new Error('The immutable allocation receipt could not be recovered. Keep this exact saved request and ask Operations to inspect it.');
      }
      await rpRefreshAfterMutation('Event allocation receipt recovered');
      eventClearPendingActionRequest(requestStorageKey, pendingRequest.clientRequestId);
      setPendingRequest(null);
      toast(`Allocation receipt recovered - ${response.receipt.id}`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not recover the pending allocation.';
      setError(message);
      toast(message, 'warn');
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Add Cases" sub={event.name} onClose={onClose} width={980}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid || Boolean(pendingRequest)} onClick={submit} style={{ opacity: valid && !busy && !pendingRequest ? 1 : 0.55 }}><Icon name="check" size={15} />{busy ? 'Adding...' : 'Add Cases'}</button></>}>
      {pendingRequest && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}><div>An actor-bound allocation request is saved on this device. Its payload cannot be replaced until the exact immutable receipt is recovered.</div><button className="btn btn-sm" disabled={busy} onClick={recoverPending} style={{ marginTop: 8 }}>Recover Exact Allocation</button></div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 12 }}>
        <MField label="Source location" required><select className="field" value={sourceLocationId} onChange={e => setSourceLocationId(e.target.value)}>{sourceLocations.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}</select></MField>
        <MField label="Note"><input className="field" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional check-in note" /></MField>
      </div>
      <SectionTitle right={<button className="btn btn-sm" disabled={!availableRows.length} onClick={addLine}><Icon name="plus" size={14} />Add Line</button>}>Case Stock</SectionTitle>
      {!availableRows.length && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>No available case stock found at this source location.</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {lines.map((row, index) => {
          const option = selectedOption(row.skuLot);
          return (
            <div key={row.key} style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0,1fr) 110px 150px 42px', gap: 8, border: isCompact ? '1px solid var(--line)' : 'none', borderRadius: isCompact ? 8 : 0, padding: isCompact ? 10 : 0 }}>
              <select className="field" value={row.skuLot} onChange={e => updateLine(index, 'skuLot', e.target.value)} disabled={!availableRows.length}>
                {availableRows.map(optionRow => (
                  <option key={eventHoldingKey(optionRow)} value={eventHoldingKey(optionRow)}>
                    {eventInventoryCaseName(optionRow.skuId)} - {optionRow.lot} - {Number(optionRow.qty || 0).toLocaleString()} cs available
                  </option>
                ))}
              </select>
              <input className="field" type="number" min="1" max={option ? Math.floor(Number(option.qty || 0)) : undefined} step="1" value={row.quantity} onChange={e => updateLine(index, 'quantity', e.target.value)} />
              <select className="field" value={row.purpose} onChange={e => updateLine(index, 'purpose', e.target.value)}>{EVENT_PURPOSE_OPTIONS.map(purpose => <option key={purpose} value={purpose}>{eventPurposeLabel(purpose)}</option>)}</select>
              <button className="btn btn-sm" onClick={() => removeLine(index)} disabled={lines.length <= 1}><Icon name="x" size={14} />{isCompact ? 'Remove' : ''}</button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function EventOpenCasesModal({ event, onClose }) {
  const isCompact = useIsNarrow(760);
  const requestStorageKey = eventOpenCaseRequestStorageKey(event.id);
  const [pendingRequest, setPendingRequest] = useState(() => eventLoadPendingOpenCaseRequest(requestStorageKey));
  const sourceOptions = Array.from(new Map([
    ...eventAllocatedCaseHoldings(event).map(row => [row.sourceLocationId, { id: row.sourceLocationId, name: eventLocationName(row.sourceLocationId) }]),
    event.defaultSourceLocationId ? [event.defaultSourceLocationId, { id: event.defaultSourceLocationId, name: eventLocationName(event.defaultSourceLocationId) }] : null
  ].filter(Boolean)).values());
  const [sourceLocationId, setSourceLocationId] = useState(() => pendingRequest && pendingRequest.payload.sourceLocationId || (sourceOptions[0] || {}).id || eventDefaultSourceLocationId(event));
  const [assignedRepId, setAssignedRepId] = useState(() => pendingRequest && pendingRequest.payload.assignedRepId || eventDefaultRepId(event));
  const [lines, setLines] = useState(() => pendingRequest && Array.isArray(pendingRequest.payload.lines) && pendingRequest.payload.lines.length
    ? pendingRequest.payload.lines.map((line, index) => ({ key: `recovery-${index}`, skuLot: `${line.caseSkuId}|${line.lot}`, quantity: String(line.quantity), damagedCans: line.damagedCans ? String(line.damagedCans) : '' }))
    : [{ key: `line-${Date.now()}`, skuLot: '', quantity: '1', damagedCans: '' }]);
  const [notes, setNotes] = useState(() => pendingRequest && pendingRequest.payload.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const teamOptions = eventTeamRows(event.id).filter(row => row.userId);
  const allocatedRows = eventAllocatedCaseHoldings(event, sourceLocationId);
  const availableRows = allocatedRows.filter(row => eventCaseCanSkuId(row.skuId));
  const unsupportedRows = allocatedRows.filter(row => !eventCaseCanSkuId(row.skuId));
  const firstOptionKey = availableRows[0] ? eventHoldingKey(availableRows[0]) : '';
  useEffect(() => {
    setLines(prev => {
      if (!availableRows.length) return prev.map(row => ({ ...row, skuLot: '' }));
      return prev.map(row => availableRows.some(option => eventHoldingKey(option) === row.skuLot) ? row : { ...row, skuLot: firstOptionKey });
    });
  }, [sourceLocationId, firstOptionKey]);
  const selectedOption = skuLot => availableRows.find(option => eventHoldingKey(option) === skuLot);
  const addLine = () => setLines(prev => [...prev, { key: `line-${Date.now()}-${prev.length}`, skuLot: firstOptionKey, quantity: '1', damagedCans: '' }]);
  const removeLine = index => setLines(prev => prev.length <= 1 ? prev : prev.filter((_, rowIndex) => rowIndex !== index));
  const updateLine = (index, key, value) => setLines(prev => prev.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  const payloadLines = lines
    .map(row => ({ row, option: selectedOption(row.skuLot), quantity: Number(row.quantity || 0), damagedCans: Number(row.damagedCans || 0) }))
    .filter(item => item.option && item.quantity > 0)
    .map(item => ({
      caseSkuId: item.option.skuId,
      damagedCans: item.damagedCans,
      lot: item.option.lot,
      quantity: item.quantity
    }));
  const invalidDamage = payloadLines.some(line => line.damagedCans < 0 || line.damagedCans > line.quantity * EVENT_CASE_PACK_SIZE);
  const valid = sourceLocationId && payloadLines.length > 0 && !invalidDamage;
  const submit = async () => {
    if (busy || !valid) return;
    setBusy(true);
    setError('');
    try {
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        toast('Opening event cases posts to the live ledger only.', 'info');
        onClose();
        return;
      }
      const payload = {
        assignedRepId: assignedRepId || null,
        eventId: event.id,
        lines: payloadLines,
        notes,
        sourceLocationId
      };
      const clientRequestId = eventPreparePendingOpenCaseRequest(requestStorageKey, payload);
      const preparedRequest = eventLoadPendingOpenCaseRequest(requestStorageKey);
      setPendingRequest(preparedRequest);
      const response = await rpApi('/api/events/open-cases', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId, ...payload })
      });
      const receipt = response && response.receipt;
      if (!eventReceiptMatchesPending(receipt, preparedRequest, event.id)) {
        throw new Error('The open-case write may have committed, but its immutable receipt could not be verified. Retry without changing the form to recover it safely.');
      }
      await rpRefreshAfterMutation('Event cases opened');
      eventClearPendingActionRequest(requestStorageKey, clientRequestId);
      setPendingRequest(null);
      toast(`Cases opened into loose cans - receipt ${receipt.id}`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not open event cases.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  const recoverPending = async () => {
    if (busy || !pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/events/open-cases', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId: pendingRequest.clientRequestId, ...pendingRequest.payload })
      });
      const receipt = response && response.receipt;
      if (!eventReceiptMatchesPending(receipt, pendingRequest, event.id)) {
        throw new Error('The immutable open-case receipt could not be recovered. Keep this request and ask Operations to inspect it.');
      }
      await rpRefreshAfterMutation('Event open-case receipt recovered');
      eventClearPendingActionRequest(requestStorageKey, pendingRequest.clientRequestId);
      setPendingRequest(null);
      toast(`Open-case receipt recovered - ${receipt.id}`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not recover the pending open-case request.';
      setError(message);
      toast(message, 'warn');
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Open Cases" sub={event.name} onClose={onClose} width={980}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid || Boolean(pendingRequest)} onClick={submit} style={{ opacity: valid && !busy && !pendingRequest ? 1 : 0.55 }}><Icon name="check" size={15} />{busy ? 'Opening...' : 'Open Cases'}</button></>}>
      {pendingRequest && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}><div>An actor-bound open-case request is saved on this device. Its payload cannot be replaced until the exact immutable receipt is recovered.</div><button className="btn btn-sm" disabled={busy} onClick={recoverPending} style={{ marginTop: 8 }}>Recover Exact Request</button></div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      {invalidDamage && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>Damaged cans cannot exceed the number of cans opened.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 12 }}>
        <MField label="Event stock source" required><select className="field" value={sourceLocationId} onChange={e => setSourceLocationId(e.target.value)}>{sourceOptions.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}</select></MField>
        <MField label="Event rep"><select className="field" value={assignedRepId} onChange={e => setAssignedRepId(e.target.value)}><option value="">Unassigned</option>{teamOptions.map(member => <option key={member.userId} value={member.userId}>{member.name || member.userId} - {member.role || 'Team'}</option>)}</select></MField>
        <MField label="Note"><input className="field" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional opening note" /></MField>
      </div>
      <SectionTitle right={<button className="btn btn-sm" disabled={!availableRows.length} onClick={addLine}><Icon name="plus" size={14} />Add Line</button>}>Cases To Open</SectionTitle>
      {!allocatedRows.length && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>No checked-in case stock found for this event source. Add cases first.</div>}
      {allocatedRows.length > 0 && !availableRows.length && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>No single-flavour 12-pack cases are available to open from this source.</div>}
      {unsupportedRows.length > 0 && <div style={{ color: 'var(--faint)', fontSize: 12.5, marginBottom: 12 }}>Mixer packs remain case stock in this workflow.</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {lines.map((row, index) => {
          const option = selectedOption(row.skuLot);
          const openedCans = Number(row.quantity || 0) * EVENT_CASE_PACK_SIZE;
          const saleableCans = Math.max(0, openedCans - Number(row.damagedCans || 0));
          return (
            <div key={row.key} style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0,1fr) 92px 118px 116px 42px', gap: 8, border: isCompact ? '1px solid var(--line)' : 'none', borderRadius: isCompact ? 8 : 0, padding: isCompact ? 10 : 0, alignItems: 'center' }}>
              <select className="field" value={row.skuLot} onChange={e => updateLine(index, 'skuLot', e.target.value)} disabled={!availableRows.length}>
                {availableRows.map(optionRow => (
                  <option key={eventHoldingKey(optionRow)} value={eventHoldingKey(optionRow)}>
                    {eventInventoryCaseName(optionRow.skuId)} - {optionRow.lot} - {Number(optionRow.quantity || 0).toLocaleString()} cs at event
                  </option>
                ))}
              </select>
              <input className="field" type="number" min="1" max={option ? Math.floor(Number(option.quantity || 0)) : undefined} step="1" value={row.quantity} onChange={e => updateLine(index, 'quantity', e.target.value)} />
              <input className="field" type="number" min="0" max={openedCans || undefined} step="1" value={row.damagedCans} onChange={e => updateLine(index, 'damagedCans', e.target.value)} placeholder="Damaged" />
              <div className="mono" style={{ color: 'var(--muted)', fontSize: 12 }}>{saleableCans.toLocaleString()} saleable can</div>
              <button className="btn btn-sm" onClick={() => removeLine(index)} disabled={lines.length <= 1}><Icon name="x" size={14} />{isCompact ? 'Remove' : ''}</button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function EventCloseoutModal({ event, onClose }) {
  const isCompact = useIsNarrow(760);
  const requestStorageKey = eventCloseoutRequestStorageKey(event);
  const [pendingRequest, setPendingRequest] = useState(() => eventLoadPendingActionRequest(requestStorageKey));
  const [caseRows, setCaseRows] = useState(() => eventCloseoutCaseGroups(event));
  const [canRows, setCanRows] = useState(() => eventOpenCaseBatches(event.id)
    .filter(batch => batch.status !== 'Closed')
    .map(batch => ({
      batchId: batch.id,
      canSkuId: batch.canSkuId,
      caseSkuId: batch.caseSkuId,
      close: true,
      countedCans: String(eventOpenBatchRemaining(batch)),
      expectedCans: eventOpenBatchRemaining(batch),
      lot: batch.lot,
      sourceLocationId: batch.sourceLocationId
    })));
  const [notes, setNotes] = useState(() => pendingRequest && pendingRequest.payload.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const updateCase = (index, key, value) => setCaseRows(prev => prev.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  const updateCan = (index, key, value) => setCanRows(prev => prev.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  const totalCaseOutcome = row => ['returnedCases', 'soldCases', 'sampleCases', 'giveawayCases', 'damagedCases', 'missingCases'].reduce((sum, key) => sum + Number(row[key] || 0), 0);
  const hasPayload = caseRows.every(row => totalCaseOutcome(row) === Number(row.quantity || 0));
  const submit = async () => {
    if (busy || !hasPayload || pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        eventId: event.id,
        notes,
        lines: caseRows
          .filter(row => totalCaseOutcome(row) > 0)
          .map(row => ({
            damagedCases: Number(row.damagedCases || 0),
            giveawayCases: Number(row.giveawayCases || 0),
            lot: row.lot,
            missingCases: Number(row.missingCases || 0),
            returnedCases: Number(row.returnedCases || 0),
            sampleCases: Number(row.sampleCases || 0),
            skuId: row.skuId,
            soldCases: Number(row.soldCases || 0),
            sourceLocationId: row.sourceLocationId
          })),
        canCounts: canRows.map(row => ({
          batchId: row.batchId,
          close: row.close,
          countedCans: Number(row.countedCans || 0)
        }))
      };
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        RP.EVENT_CLOSEOUTS = [{ id: `EC-DEMO-${Date.now()}`, eventId: event.id, status: 'reconciled', countedAt: new Date().toISOString(), summary: payload, notes }, ...(RP.EVENT_CLOSEOUTS || []).filter(row => row.eventId !== event.id)];
        RP.EVENTS = (RP.EVENTS || []).map(row => row.id === event.id ? { ...row, closeoutStatus: 'reconciled', status: 'reconciled' } : row);
        toast('Event closeout marked reconciled', 'ok');
      } else {
        const clientRequestId = eventPreparePendingActionRequest(requestStorageKey, payload);
        const preparedRequest = eventLoadPendingActionRequest(requestStorageKey);
        setPendingRequest(preparedRequest);
        const response = await rpApi('/api/events/closeout', { method: 'POST', body: JSON.stringify({ clientRequestId, ...payload }) });
        if (!eventReceiptMatchesPending(response && response.receipt, preparedRequest, event.id)) {
          throw new Error('The closeout may have committed, but its actor-bound receipt could not be verified. Recover this exact saved request.');
        }
        const receipt = eventUpsertCloseoutReceipt(response && response.receipt);
        if (!receipt || receipt.action !== 'closeout') throw new Error('Closeout committed without a verifiable receipt response. Retry with the same request ID.');
        await rpRefreshAfterMutation('Event closeout reconciled');
        eventClearPendingActionRequest(requestStorageKey, clientRequestId);
        setPendingRequest(null);
        toast(`Event closeout reconciled · receipt ${String(receipt.id).slice(0, 8)}`, 'ok');
      }
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not close out event inventory.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  const recoverPending = async () => {
    if (busy || !pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/events/closeout', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId: pendingRequest.clientRequestId, ...pendingRequest.payload })
      });
      if (!eventReceiptMatchesPending(response && response.receipt, pendingRequest, event.id)) {
        throw new Error('The immutable closeout receipt could not be recovered. Keep this exact saved request and ask Operations to inspect it.');
      }
      const receipt = eventUpsertCloseoutReceipt(response.receipt);
      if (!receipt || receipt.action !== 'closeout') throw new Error('The recovered receipt is not an Event closeout receipt.');
      await rpRefreshAfterMutation('Event closeout receipt recovered');
      eventClearPendingActionRequest(requestStorageKey, pendingRequest.clientRequestId);
      setPendingRequest(null);
      toast(`Event closeout recovered - receipt ${String(receipt.id).slice(0, 8)}`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not recover the pending Event closeout.';
      setError(message);
      toast(message, 'warn');
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Event Closeout" sub={event.name} onClose={onClose} width={1120}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !hasPayload || Boolean(pendingRequest)} onClick={submit} style={{ opacity: hasPayload && !busy && !pendingRequest ? 1 : 0.55 }}><Icon name="check" size={15} />{busy ? 'Reconciling...' : 'Reconcile Closeout'}</button></>}>
      {pendingRequest && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}><div>An actor-bound closeout request is saved on this device. Its counts and dispositions cannot be replaced until the exact immutable receipt is recovered.</div><button className="btn btn-sm" disabled={busy} onClick={recoverPending} style={{ marginTop: 8 }}>Recover Exact Closeout</button></div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      <SectionTitle>Case Stock</SectionTitle>
      {isCompact ? (
        <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
          {caseRows.map((row, index) => (
            <div key={`${row.skuId}-${row.sourceLocationId}-${row.lot}`} className="card" style={{ padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}><FlavorChip sku={rpSkuItem(row.skuId) || row.skuId} size={24} /><span><span style={{ display: 'block', fontWeight: 800 }}>{rpSkuDisplayName(rpSkuItem(row.skuId) || 'Unknown product')}</span><span className="mono" style={{ display: 'block', color: 'var(--faint)', fontSize: 10.5, marginTop: 2 }}>{rpSkuExternalIdentity(rpSkuItem(row.skuId) || '')}</span></span></span>
                <span className="mono" style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{Number(row.quantity || 0).toLocaleString()} cs</span>
              </div>
              <div style={{ color: 'var(--faint)', fontSize: 12, marginBottom: 10 }}>{eventLocationName(row.sourceLocationId)} - {row.lot}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
                {[
                  ['returnedCases', 'Return'],
                  ['soldCases', 'Cash Sold'],
                  ['sampleCases', 'Samples'],
                  ['giveawayCases', 'Giveaways'],
                  ['damagedCases', 'Damaged'],
                  ['missingCases', 'Missing']
                ].map(([key, label]) => (
                  <MField key={key} label={label}><input className="field" type="number" min="0" step="1" value={row[key]} onChange={e => updateCase(index, key, e.target.value)} /></MField>
                ))}
              </div>
            </div>
          ))}
          {!caseRows.length && <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 18 }}>No checked-out case stock.</div>}
        </div>
      ) : (
        <div className="table-wrap" style={{ marginBottom: 16, overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Source</th>
                <th>Lot</th>
                <th className="num">Checked Out</th>
                <th className="num">Return</th>
                <th className="num">Cash Sold</th>
                <th className="num">Samples</th>
                <th className="num">Giveaways</th>
                <th className="num">Damaged</th>
                <th className="num">Missing</th>
              </tr>
            </thead>
            <tbody>
              {caseRows.map((row, index) => (
                <tr key={`${row.skuId}-${row.sourceLocationId}-${row.lot}`}>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><FlavorChip sku={rpSkuItem(row.skuId) || row.skuId} size={24} /><span><span style={{ display: 'block', fontWeight: 700 }}>{rpSkuDisplayName(rpSkuItem(row.skuId) || 'Unknown product')}</span><span className="mono" style={{ display: 'block', color: 'var(--faint)', fontSize: 10.5, marginTop: 2 }}>{rpSkuExternalIdentity(rpSkuItem(row.skuId) || '')}</span></span></span></td>
                  <td>{eventLocationName(row.sourceLocationId)}</td>
                  <td className="mono">{row.lot}</td>
                  <td className="num mono">{Number(row.quantity || 0).toLocaleString()} cs</td>
                  {['returnedCases', 'soldCases', 'sampleCases', 'giveawayCases', 'damagedCases', 'missingCases'].map(key => (
                    <td className="num" key={key}><input className="field" type="number" min="0" step="1" value={row[key]} onChange={e => updateCase(index, key, e.target.value)} style={{ width: 86, textAlign: 'right' }} /></td>
                  ))}
                </tr>
              ))}
              {!caseRows.length && <tr><td colSpan="10" style={{ textAlign: 'center', color: 'var(--faint)', padding: 20 }}>No checked-out case stock.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <SectionTitle>Loose Cans</SectionTitle>
      {isCompact ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {canRows.map((row, index) => (
            <div key={row.batchId} className="card" style={{ padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}><FlavorChip sku={rpSkuItem(row.canSkuId) || row.canSkuId} size={24} /><span><span style={{ display: 'block', fontWeight: 800 }}>{rpSkuDisplayName(rpSkuItem(row.canSkuId) || 'Unknown product')}</span><span className="mono" style={{ display: 'block', color: 'var(--faint)', fontSize: 10.5, marginTop: 2 }}>{rpSkuExternalIdentity(rpSkuItem(row.canSkuId) || '')}</span></span></span>
                <span className="mono" style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{Number(row.expectedCans || 0).toLocaleString()} can</span>
              </div>
              <div style={{ color: 'var(--faint)', fontSize: 12, marginBottom: 10 }}>{eventLocationName(row.sourceLocationId)} - {row.lot}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, alignItems: 'end' }}>
                <MField label="Counted"><input className="field" type="number" min="0" step="1" value={row.countedCans} onChange={e => updateCan(index, 'countedCans', e.target.value)} /></MField>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 800, color: 'var(--muted)', paddingBottom: 16 }}><input type="checkbox" checked={row.close} onChange={e => updateCan(index, 'close', e.target.checked)} />Close</label>
              </div>
            </div>
          ))}
          {!canRows.length && <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 18 }}>No active loose-can batches.</div>}
        </div>
      ) : (
        <div className="table-wrap" style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>Batch</th><th>Source</th><th>Lot</th><th className="num">Expected</th><th className="num">Counted</th><th>Close</th></tr></thead>
            <tbody>
              {canRows.map((row, index) => (
                <tr key={row.batchId}>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><FlavorChip sku={rpSkuItem(row.canSkuId) || row.canSkuId} size={24} /><span><span style={{ display: 'block', fontWeight: 700 }}>{rpSkuDisplayName(rpSkuItem(row.canSkuId) || 'Unknown product')}</span><span className="mono" style={{ display: 'block', color: 'var(--faint)', fontSize: 10.5, marginTop: 2 }}>{rpSkuExternalIdentity(rpSkuItem(row.canSkuId) || '')}</span></span></span></td>
                  <td>{eventLocationName(row.sourceLocationId)}</td>
                  <td className="mono">{row.lot}</td>
                  <td className="num mono">{Number(row.expectedCans || 0).toLocaleString()} can</td>
                  <td className="num"><input className="field" type="number" min="0" step="1" value={row.countedCans} onChange={e => updateCan(index, 'countedCans', e.target.value)} style={{ width: 100, textAlign: 'right' }} /></td>
                  <td><label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 800, color: 'var(--muted)' }}><input type="checkbox" checked={row.close} onChange={e => updateCan(index, 'close', e.target.checked)} />Close</label></td>
                </tr>
              ))}
              {!canRows.length && <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--faint)', padding: 20 }}>No active loose-can batches.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <MField label="Closeout notes"><textarea className="field" rows="3" value={notes} onChange={e => setNotes(e.target.value)} /></MField>
    </Modal>
  );
}

function EventCloseoutReversalModal({ event, receipt, onClose }) {
  const storageKey = eventReversalRequestStorageKey(receipt.id);
  const [pendingRequest, setPendingRequest] = useState(() => eventLoadPendingActionRequest(storageKey));
  const [reason, setReason] = useState(() => pendingRequest && pendingRequest.payload.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const valid = reason.trim().length >= 3;
  const submit = async () => {
    if (busy || !valid || pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const payload = { closeoutReceiptId: receipt.id, notes: reason.trim() };
      const clientRequestId = eventPreparePendingActionRequest(storageKey, payload);
      const preparedRequest = eventLoadPendingActionRequest(storageKey);
      setPendingRequest(preparedRequest);
      const response = await rpApi('/api/events/reverse-closeout', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId, ...payload })
      });
      if (!eventReceiptMatchesPending(response && response.receipt, preparedRequest, event.id)) {
        throw new Error('The reversal may have committed, but its actor-bound receipt could not be verified. Recover this exact saved request.');
      }
      const reversal = eventUpsertCloseoutReceipt(response && response.receipt);
      if (!reversal || reversal.action !== 'reversal' || reversal.reversesReceiptId !== receipt.id) {
        throw new Error('Reversal committed without a verifiable receipt response. Retry with the same request ID.');
      }
      await rpRefreshAfterMutation('Event closeout reversal recorded');
      eventClearPendingActionRequest(storageKey, clientRequestId);
      setPendingRequest(null);
      toast(`Closeout reversed · receipt ${String(reversal.id).slice(0, 8)}`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not reverse this event closeout.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  const recoverPending = async () => {
    if (busy || !pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/events/reverse-closeout', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId: pendingRequest.clientRequestId, ...pendingRequest.payload })
      });
      if (!eventReceiptMatchesPending(response && response.receipt, pendingRequest, event.id)) {
        throw new Error('The immutable reversal receipt could not be recovered. Keep this exact request and ask Operations to inspect it.');
      }
      const reversal = eventUpsertCloseoutReceipt(response && response.receipt);
      if (!reversal || reversal.action !== 'reversal' || reversal.reversesReceiptId !== receipt.id) {
        throw new Error('The immutable reversal receipt could not be recovered. Keep this exact request and ask Operations to inspect it.');
      }
      await rpRefreshAfterMutation('Event closeout reversal receipt recovered');
      eventClearPendingActionRequest(storageKey, pendingRequest.clientRequestId);
      setPendingRequest(null);
      toast(`Closeout reversal recovered · receipt ${String(reversal.id).slice(0, 8)}`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not recover the pending closeout reversal.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Reverse Event Closeout" sub={event.name} onClose={onClose} width={620}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid || Boolean(pendingRequest)} onClick={submit} style={{ opacity: busy || !valid || pendingRequest ? 0.55 : 1, background: 'var(--danger)', borderColor: 'var(--danger)' }}><Icon name="refresh" size={15} />{busy ? 'Reversing...' : 'Reverse Closeout'}</button></>}>
      <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '11px 13px', fontSize: 12.5, lineHeight: 1.45, marginBottom: 14 }}>
        This appends an exact inverse of receipt <span className="mono">{receipt.id}</span>. The original closeout, inventory ledger, and audit evidence remain immutable.
      </div>
      {pendingRequest && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}><div>An actor-bound reversal request and reason are saved on this device. They cannot be replaced until the exact immutable receipt is recovered.</div><button className="btn btn-sm" disabled={busy} onClick={recoverPending} style={{ marginTop: 8 }}>Recover Exact Reversal</button></div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8, marginBottom: 14 }}>
        <EventMetric label="Ledger Rows" value={(receipt.ledgerIds || []).length} unit="rows" />
        <EventMetric label="Open-Case Movements" value={(receipt.movementIds || []).length} unit="rows" />
      </div>
      <MField label="Reason for reversal" required><textarea className="field" rows="4" value={reason} onChange={e => setReason(e.target.value)} placeholder="Describe the verified count, custody, or closeout error being corrected." style={{ resize: 'vertical' }} /></MField>
      <div style={{ color: 'var(--faint)', fontSize: 11.5, lineHeight: 1.4, marginTop: 8 }}>If the network response is interrupted, the exact request ID, receipt link, and reason stay saved for recovery after reopening this screen.</div>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}

function EventMetric({ label, value, unit }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '10px 11px', background: 'var(--surface-2)' }}>
      <div className="eyebrow" style={{ marginBottom: 5 }}>{label}</div>
      <div className="mono" style={{ fontSize: 15, fontWeight: 900, color: 'var(--ink)' }}>{Number(value || 0).toLocaleString()} <span style={{ color: 'var(--faint)', fontSize: 11 }}>{unit}</span></div>
    </div>
  );
}

function eventDraftFrom(event) {
  const date = eventDateValue(event) || (rpTodayYmd ? rpTodayYmd() : new Date().toISOString().slice(0, 10));
  const endDate = eventEndDateValue(event) || date;
  const time = value => value && value.includes('T') ? value.slice(11, 16) : '';
  return {
    id: event && event.id || '',
    name: event && event.name || '',
    venueName: event && event.venueName || '',
    customerId: event && event.customerId || '',
    territory: event && event.territory || (eventTerritoryOptions('')[0] || ''),
    eventType: event && event.eventType || EVENT_TYPE_OPTIONS[0],
    status: event && event.status || 'lead',
    eventDate: date,
    endDate,
    startTime: time(event && event.startAt),
    endTime: time(event && event.endAt),
    expectedAttendance: event && event.expectedAttendance != null ? String(event.expectedAttendance) : '',
    defaultSourceLocationId: event && event.defaultSourceLocationId || (eventLocationOptions()[0] || {}).id || '',
    shopifyLocationId: event && event.shopifyLocationId || '',
    notes: event && event.notes || '',
    closeoutStatus: event && event.closeoutStatus || 'not_started',
    team: event ? eventTeamRows(event.id).map(row => ({ rowId: row.id, userId: row.userId || '', role: row.role || 'Sales', notes: row.notes || '' })) : [],
    inventoryPlan: event ? eventPlanRows(event.id).map(row => ({ rowId: row.id, skuId: row.skuId || '', quantity: String(row.quantity || ''), unit: row.unit || 'case', purpose: row.purpose || 'sell_singles', notes: row.notes || '' })) : []
  };
}

function EventEditorModal({ event, onClose, onSaved }) {
  const [f, setF] = useState(() => eventDraftFrom(event));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tried, setTried] = useState(false);
  const isCompact = useIsNarrow(760);
  const isPhone = useIsNarrow(560);
  const locations = eventLocationOptions();
  const customers = eventCustomerOptions();
  const users = eventUserOptions();
  const skus = eventSkuOptions();
  const set = (key, value) => setF(prev => ({ ...prev, [key]: value }));
  const updateTeam = (index, key, value) => setF(prev => ({ ...prev, team: prev.team.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row) }));
  const updatePlan = (index, key, value) => setF(prev => ({ ...prev, inventoryPlan: prev.inventoryPlan.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row) }));
  const setStartDate = value => setF(prev => ({ ...prev, eventDate: value, endDate: !prev.endDate || prev.endDate < value ? value : prev.endDate }));
  const setEndDate = value => setF(prev => ({ ...prev, endDate: value }));
  const addTeam = () => setF(prev => ({ ...prev, team: [...prev.team, { userId: (users[0] || {}).id || '', role: 'Sales', notes: '' }] }));
  const removeTeam = index => setF(prev => ({ ...prev, team: prev.team.filter((_, rowIndex) => rowIndex !== index) }));
  const addPlan = () => setF(prev => ({ ...prev, inventoryPlan: [...prev.inventoryPlan, { skuId: (skus[0] || {}).id || '', quantity: '1', unit: 'case', purpose: 'sell_singles', notes: '' }] }));
  const removePlan = index => setF(prev => ({ ...prev, inventoryPlan: prev.inventoryPlan.filter((_, rowIndex) => rowIndex !== index) }));
  const startAt = f.eventDate && f.startTime ? `${f.eventDate}T${f.startTime}:00` : null;
  const endAt = f.endDate && f.endTime ? `${f.endDate}T${f.endTime}:00` : null;
  const datesValid = String(f.eventDate || '').trim() && String(f.endDate || '').trim() && f.endDate >= f.eventDate;
  const valid = String(f.name || '').trim() && datesValid;
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) {
      if (!datesValid) setError('End date must be on or after the start date.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        id: f.id || undefined,
        name: f.name,
        venueName: f.venueName,
        customerId: f.customerId || null,
        territory: f.territory,
        eventType: f.eventType,
        status: f.status,
        eventDate: f.eventDate,
        endDate: f.endDate || f.eventDate,
        startAt,
        endAt,
        expectedAttendance: f.expectedAttendance ? Number(f.expectedAttendance) : null,
        defaultSourceLocationId: f.defaultSourceLocationId || null,
        shopifyLocationId: f.shopifyLocationId || null,
        notes: f.notes,
        closeoutStatus: f.closeoutStatus,
        team: f.team.filter(row => row.userId).map(row => ({ userId: row.userId, role: row.role, notes: row.notes })),
        inventoryPlan: f.inventoryPlan
          .filter(row => row.skuId && Number(row.quantity || 0) > 0)
          .map(row => ({ skuId: row.skuId, quantity: Number(row.quantity || 0), unit: row.unit, purpose: row.purpose, notes: row.notes }))
      };
      let eventId = payload.id;
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        eventId = eventId || `EV-DEMO-${Date.now()}`;
        const next = { ...payload, id: eventId };
        RP.EVENTS = [next, ...(RP.EVENTS || []).filter(row => row.id !== eventId)];
        RP.EVENT_TEAM = [
          ...(RP.EVENT_TEAM || []).filter(row => row.eventId !== eventId),
          ...payload.team.map((row, index) => ({ ...row, id: `${eventId}-team-${index}`, eventId, name: (users.find(user => user.id === row.userId) || {}).label || row.userId }))
        ];
        RP.EVENT_INVENTORY_PLAN = [
          ...(RP.EVENT_INVENTORY_PLAN || []).filter(row => row.eventId !== eventId),
          ...payload.inventoryPlan.map((row, index) => ({ ...row, id: `${eventId}-plan-${index}`, eventId }))
        ];
      } else {
        const response = await rpApi('/api/events/upsert', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        eventId = response.event && response.event.id || eventId;
        await rpRefreshAfterMutation('Event saved');
      }
      toast(event ? 'Event updated' : 'Event created', 'ok');
      onSaved && onSaved(eventId);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save event.');
      toast(err && err.message ? err.message : 'Could not save event.', 'warn');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={event ? 'Edit Event' : 'New Event'} sub="Calendar, staffing, event stock, POS linkage, and closeout context" onClose={onClose} width={980}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.55 }}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Save Event'}</button></>}>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1.2fr 0.8fr', gap: 12 }}>
        <MField label="Event name" required><input className="field" value={f.name} onChange={e => set('name', e.target.value)} style={tried && !String(f.name || '').trim() ? { borderColor: 'var(--danger)' } : undefined} /></MField>
        <MField label="Status"><select className="field" value={f.status} onChange={e => set('status', e.target.value)}>{EVENT_STATUS_FLOW.map(status => <option key={status} value={status}>{eventStatusLabel(status)}</option>)}</select></MField>
        <MField label="Venue"><input className="field" value={f.venueName} onChange={e => set('venueName', e.target.value)} /></MField>
        <MField label="Type"><select className="field" value={f.eventType} onChange={e => set('eventType', e.target.value)}>{EVENT_TYPE_OPTIONS.map(type => <option key={type}>{type}</option>)}</select></MField>
        <MField label="Customer"><select className="field" value={f.customerId} onChange={e => set('customerId', e.target.value)}><option value="">No customer</option>{customers.map(customer => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select></MField>
        <MField label="Territory"><select className="field" value={f.territory} onChange={e => set('territory', e.target.value)}>{eventTerritoryOptions(f.territory).map(territory => <option key={territory}>{territory}</option>)}</select></MField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : isCompact ? 'repeat(2, minmax(0,1fr))' : 'repeat(5, minmax(0,1fr))', gap: 12 }}>
        <MField label="Start date" required><input className="field" type="date" value={f.eventDate} onChange={e => setStartDate(e.target.value)} style={tried && !f.eventDate ? { borderColor: 'var(--danger)' } : undefined} /></MField>
        <MField label="End date" required><input className="field" type="date" value={f.endDate} min={f.eventDate || undefined} onChange={e => setEndDate(e.target.value)} style={tried && (!f.endDate || f.endDate < f.eventDate) ? { borderColor: 'var(--danger)' } : undefined} /></MField>
        <MField label="Start time"><input className="field" type="time" value={f.startTime} onChange={e => set('startTime', e.target.value)} /></MField>
        <MField label="End time"><input className="field" type="time" value={f.endTime} onChange={e => set('endTime', e.target.value)} /></MField>
        <MField label="Attendance"><input className="field" type="number" min="0" value={f.expectedAttendance} onChange={e => set('expectedAttendance', e.target.value)} /></MField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 12 }}>
        <MField label="Default source"><select className="field" value={f.defaultSourceLocationId} onChange={e => set('defaultSourceLocationId', e.target.value)}><option value="">No default</option>{locations.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}</select></MField>
        <MField label="Shopify location id"><input className="field" value={f.shopifyLocationId} onChange={e => set('shopifyLocationId', e.target.value)} placeholder="Optional" /></MField>
      </div>

      <SectionTitle right={<button className="btn btn-sm" onClick={addTeam}><Icon name="plus" size={14} />Add Team</button>}>Team</SectionTitle>
      <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
        {f.team.map((row, index) => (
          <div key={row.rowId || index} style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0,1fr) 150px minmax(0,1fr) 42px', gap: 8, border: isCompact ? '1px solid var(--line)' : 'none', borderRadius: isCompact ? 8 : 0, padding: isCompact ? 10 : 0 }}>
            <select className="field" value={row.userId} onChange={e => updateTeam(index, 'userId', e.target.value)}>{users.map(user => <option key={user.id} value={user.id}>{user.label}{user.territory ? ` - ${user.territory}` : ''}</option>)}</select>
            <select className="field" value={row.role} onChange={e => updateTeam(index, 'role', e.target.value)}>{EVENT_TEAM_ROLES.map(role => <option key={role}>{role}</option>)}</select>
            <input className="field" value={row.notes} onChange={e => updateTeam(index, 'notes', e.target.value)} placeholder="Notes" />
            <button className="btn btn-sm" onClick={() => removeTeam(index)} style={isCompact ? { width: '100%' } : undefined}><Icon name="x" size={14} />{isCompact ? 'Remove' : ''}</button>
          </div>
        ))}
        {!f.team.length && <div style={{ color: 'var(--faint)', fontSize: 12.5 }}>No team rows yet.</div>}
      </div>

      <SectionTitle right={<button className="btn btn-sm" onClick={addPlan}><Icon name="plus" size={14} />Add Stock</button>}>Inventory Plan</SectionTitle>
      <div style={{ display: 'grid', gap: 8 }}>
        {f.inventoryPlan.map((row, index) => (
          <div key={row.rowId || index} style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0,1fr) 190px 150px minmax(0,1fr) 42px', gap: 8, border: isCompact ? '1px solid var(--line)' : 'none', borderRadius: isCompact ? 8 : 0, padding: isCompact ? 10 : 0 }}>
            <select className="field" value={row.skuId} onChange={e => updatePlan(index, 'skuId', e.target.value)}>{skus.map(sku => <option key={sku.id} value={sku.id}>{rpSkuDisplayName(sku)} - {rpSkuExternalIdentity(sku)}</option>)}</select>
            <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr 1fr' : '90px 100px', gap: 8 }}>
              <input className="field" type="number" min="0" value={row.quantity} onChange={e => updatePlan(index, 'quantity', e.target.value)} />
              <select className="field" value={row.unit} onChange={e => updatePlan(index, 'unit', e.target.value)}>{EVENT_UNIT_OPTIONS.map(unit => <option key={unit}>{unit}</option>)}</select>
            </div>
            <select className="field" value={row.purpose} onChange={e => updatePlan(index, 'purpose', e.target.value)}>{EVENT_PURPOSE_OPTIONS.map(purpose => <option key={purpose} value={purpose}>{eventPurposeLabel(purpose)}</option>)}</select>
            <input className="field" value={row.notes} onChange={e => updatePlan(index, 'notes', e.target.value)} placeholder="Notes" />
            <button className="btn btn-sm" onClick={() => removePlan(index)} style={isCompact ? { width: '100%' } : undefined}><Icon name="x" size={14} />{isCompact ? 'Remove' : ''}</button>
          </div>
        ))}
        {!f.inventoryPlan.length && <div style={{ color: 'var(--faint)', fontSize: 12.5 }}>No inventory rows yet.</div>}
      </div>
      <MField label="Notes"><textarea className="field" rows="3" value={f.notes} onChange={e => set('notes', e.target.value)} /></MField>
    </Modal>
  );
}


/* ===== desktop_invoicing.jsx ===== */
/* ============================================================
   RANCH POWER - Desktop invoicing / A/R
   ============================================================ */

function invoiceOrder(inv) {
  return typeof rpInvoiceOrder === 'function' ? rpInvoiceOrder(inv) : RP.ORDERS.find(order => order.id === inv.order);
}

function invoiceCustomer(inv) {
  return RP.cust(inv.cust) || {};
}

function invoiceSearchText(inv) {
  const c = invoiceCustomer(inv);
  const order = invoiceOrder(inv) || {};
  const delivery = rpOrderDeliveryScan(order);
  return [
    inv.id,
    inv.order,
    c.name,
    c.buyer,
    c.email,
    c.city,
    c.type,
    c.tier,
    order.territory,
    order.rep,
    delivery.label,
    inv.emailTo,
    inv.paymentMethod,
    rpPaymentMethodScanLabel(inv.paymentMethod)
  ].filter(Boolean).join(' ').toLowerCase();
}

function invoiceStatusLabel(status) {
  return status === 'Draft' ? 'Ready' : status;
}

function invoiceListRow(inv, customerOverride) {
  const order = invoiceOrder(inv) || {};
  const customer = customerOverride || invoiceCustomer(inv);
  const delivery = rpOrderDeliveryScan(order);
  return {
    inv,
    order,
    customer,
    delivery,
    st: RP.invoiceStatus(inv),
    t: RP.invoiceTotals(inv),
    territory: order.territory || customer.territory || '',
    area: rpOrderAreaLabel(order, customer),
    rep: order.rep || customer.rep || '',
    customerType: customer.tier || customer.type || ''
  };
}

function invoiceReceiptTime(value) {
  return typeof rpFormatLocalDateTime === 'function' ? rpFormatLocalDateTime(value, { empty: '' }) : (value ? String(value).slice(0, 16).replace('T', ' ') : '');
}

function invoiceEmailValues(value) {
  return String(value || '')
    .split(/[,;\n]+/)
    .map(email => email.trim().toLowerCase())
    .filter(Boolean);
}

function invoiceEmailIsValid(value) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || '').trim()) && String(value || '').trim().length <= 254;
}

function EmailInvoiceModal({ inv, order, emailTo, onClose, onDone }) {
  const documentLabel = RP.invoiceStatus(inv) === 'Paid' ? 'Receipt' : 'Invoice';
  const c = invoiceCustomer(inv);
  const [toValue, setToValue] = useState(emailTo || '');
  const [ccValue, setCcValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const rawTo = invoiceEmailValues(toValue);
  const savedCopies = (c.contacts || []).filter(contact => contact.receivesInvoices === true && contact.email).map(contact => String(contact.email).trim().toLowerCase());
  const rawCc = [...invoiceEmailValues(ccValue), ...savedCopies];
  const invalid = [...rawTo, ...rawCc].filter(email => !invoiceEmailIsValid(email));
  const to = [...new Set(rawTo)];
  const toSet = new Set(to);
  const cc = [...new Set(rawCc)].filter(email => !toSet.has(email));
  const recipientCount = to.length + cc.length;
  const tooMany = recipientCount > 20;
  const valid = to.length > 0 && invalid.length === 0 && !tooMany;

  const send = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      await rpEmailInvoiceOrder(inv, { to, cc });
      toast(`${documentLabel} ${order.invoiceNumber} emailed to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`, 'ok');
      onDone && onDone();
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : `Could not email ${documentLabel.toLowerCase()}.`;
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Email ${documentLabel}`} sub={`${order.invoiceNumber} - ${c.name || inv.cust || 'Customer'}`} onClose={busy ? () => {} : onClose} width={560}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid} onClick={send} style={{ opacity: busy || !valid ? 0.55 : 1 }}><Icon name="mail" size={15} />{busy ? 'Sending...' : `Send ${documentLabel}`}</button></>}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--amber-soft)', color: 'var(--amber-deep)', border: '1px solid var(--amber)', borderRadius: 10, padding: '12px 13px', marginBottom: 16, fontSize: 12.5, lineHeight: 1.45 }}>
        <Icon name="mail" size={17} style={{ flexShrink: 0, marginTop: 1 }} />
        <div><b>This will send an email immediately.</b><div style={{ marginTop: 3 }}>Review every recipient before confirming. Each person receives the same {documentLabel.toLowerCase()} email, PDF attachment, and payment instructions.</div></div>
      </div>
      <MField label="To" required>
        <textarea className="field" rows={2} value={toValue} onChange={event => setToValue(event.target.value)} placeholder="payables@example.com, owner@example.com" style={{ resize: 'vertical', borderColor: tried && (!to.length || invalid.some(email => rawTo.includes(email))) ? 'var(--danger)' : undefined }} />
        <div style={{ color: 'var(--faint)', fontSize: 11.5, marginTop: 5 }}>Separate multiple addresses with commas, semicolons, or new lines.</div>
      </MField>
      {savedCopies.length > 0 && <div style={{ fontSize: 12.5, marginBottom: 14 }}><b>Saved invoice copies:</b> {savedCopies.join(', ')}<div style={{ color: 'var(--muted)', marginTop: 4 }}>These contacts receive every invoice and receipt. Change this in Customer Contacts.</div></div>}
      <MField label="Additional CC (optional)">
        <textarea className="field" rows={2} value={ccValue} onChange={event => setCcValue(event.target.value)} placeholder="buyer@example.com, manager@example.com" style={{ resize: 'vertical', borderColor: tried && invalid.some(email => rawCc.includes(email)) ? 'var(--danger)' : undefined }} />
        <div style={{ color: 'var(--faint)', fontSize: 11.5, marginTop: 5 }}>CC recipients are visible to everyone on the email.</div>
      </MField>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5 }}>
        <span style={{ color: 'var(--muted)' }}>Recipients for this send</span>
        <b>{recipientCount} total{cc.length ? ` - ${cc.length} CC` : ''}</b>
      </div>
      <div style={{ color: 'var(--faint)', fontSize: 11.5, lineHeight: 1.45, marginTop: 9 }}>Saved invoice copies are included automatically. Other addresses entered here apply to this send.</div>
      {tried && !to.length && <div style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 700, marginTop: 10 }}>Add at least one To recipient.</div>}
      {tried && invalid.length > 0 && <div style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 700, marginTop: 10 }}>Correct the invalid email address{invalid.length === 1 ? '' : 'es'} before sending.</div>}
      {tried && tooMany && <div style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 700, marginTop: 10 }}>Invoices can be sent to at most 20 recipients at once.</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}

function EmailInvoiceButton({ inv, onDone, size = 'sm', disabled: recoveryBlocked = false }) {
  const [open, setOpen] = useState(false);
  const order = invoiceOrder(inv);
  const emailTo = inv.emailTo || (order && order.invoiceEmailTo) || '';
  const showConfirmation = event => {
    event && event.stopPropagation();
    if (!order || !order.invoiceNumber || recoveryBlocked) return;
    setOpen(true);
  };
  const disabled = !order || !order.invoiceNumber || recoveryBlocked;
  return <>{open && <EmailInvoiceModal inv={inv} order={order} emailTo={emailTo} onClose={() => setOpen(false)} onDone={onDone} />}<button className={`btn btn-${size}`} disabled={disabled} onClick={showConfirmation} style={{ opacity: disabled ? 0.62 : 1 }}><Icon name="mail" size={14} />Email</button></>;
}

function OpenInvoicePdfButton({ inv, size = 'sm' }) {
  const order = invoiceOrder(inv);
  const open = async event => {
    event && event.stopPropagation();
    if (!order || !order.invoiceNumber) return;
    try {
      await rpOpenInvoicePdf(order.id);
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not open invoice PDF.', 'danger');
    }
  };
  return <button className={`btn btn-${size}`} disabled={!order || !order.invoiceNumber} onClick={open} style={{ opacity: order && order.invoiceNumber ? 1 : 0.62 }}><Icon name="doc" size={14} />PDF</button>;
}

function RecordInvoicePaymentModal({ inv, order, onClose, onPaid }) {
  const c = invoiceCustomer(inv);
  const t = RP.invoiceTotals(inv);
  const methodIds = new Set(['bank_transfer', 'e_transfer', 'cheque', 'cash']);
  const methods = (RP.PAY_METHODS || [])
    .filter(method => methodIds.has(method.id))
    .filter((method, index, rows) => rows.findIndex(row => row.id === method.id) === index);
  const preferred = methods.some(method => method.id === inv.paymentMethod) ? inv.paymentMethod : 'bank_transfer';
  const paymentBalance = t.balance > 0.005 ? t.balance : t.total;
  const [paymentMethod, setPaymentMethod] = useState(preferred);
  const [paymentAmount, setPaymentAmount] = useState(() => Number(paymentBalance || 0).toFixed(2));
  const [paymentDate, setPaymentDate] = useState(() => typeof rpTodayYmd === 'function' ? rpTodayYmd(c) : new Date().toISOString().slice(0, 10));
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [emailTo, setEmailTo] = useState(inv.emailTo || order.invoiceEmailTo || '');
  const [sendEmail, setSendEmail] = useState(() => Boolean(inv.emailTo || order.invoiceEmailTo));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const paymentRequestRef = useRef('');
  const paymentAmountValue = Number(String(paymentAmount || '').replace(/[$,\s]/g, ''));
  const paymentAmountExceedsBalance = Number.isFinite(paymentAmountValue) && paymentAmountValue > paymentBalance + 0.005;
  const paymentAmountValid = paymentAmountValue > 0 && !paymentAmountExceedsBalance;
  const nextBalance = Number.isFinite(paymentAmountValue) ? Math.max(0, paymentBalance - paymentAmountValue) : paymentBalance;
  const paidInFull = nextBalance <= 0.005;

  const record = async () => {
    if (busy || !order) return;
    if (!paymentAmountValid) {
      setError(paymentAmountExceedsBalance ? `Payment amount cannot exceed the open balance of ${RP.money2(paymentBalance)}.` : 'Enter a payment amount greater than zero.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const clientRequestId = paymentRequestRef.current || crypto.randomUUID();
      paymentRequestRef.current = clientRequestId;
      const response = await rpApi('/api/invoices/payment', {
        method: 'POST',
        body: JSON.stringify({
          clientRequestId,
          orderId: order.id,
          paymentMethod,
          paymentAmount: paymentAmount.trim(),
          paymentDate,
          paymentReference: paymentReference.trim(),
          paymentNote: paymentNote.trim(),
          emailTo: sendEmail ? emailTo.trim() : '',
          sendEmail
        })
      });
      const saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
      Object.assign(order, saved);
      await rpRefreshAfterMutation(`${paidInFull ? 'Receipt' : 'Payment'} ${order.invoiceNumber || inv.id} recorded`);
      const refreshed = RP.ORDERS.find(x => x.id === order.id) || saved;
      Object.assign(order, refreshed);
      paymentRequestRef.current = '';
      const receiptEmailFailed = sendEmail && !(response.email && response.email.sent);
      const successMessage = `${paidInFull ? 'Receipt' : 'Payment'} ${refreshed.invoiceNumber || inv.id} recorded`;
      const emailWarning = response.email && response.email.warning
        ? response.email.warning
        : 'The payment was recorded, but the customer email was not sent. Use Email on the invoice to try again.';
      toast(receiptEmailFailed ? `${successMessage}. ${emailWarning}` : successMessage, receiptEmailFailed ? 'warn' : 'ok');
      onPaid && onPaid(refreshed);
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not record payment.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record Invoice Payment" sub={`${inv.id} - ${c.name}`} onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !paymentMethod || !paymentAmountValid} onClick={record}><Icon name="check" size={15} />{busy ? 'Recording...' : paymentMethod === 'cheque' ? 'Mark Paid by Cheque' : 'Record Payment'}</button></>}>
      <MField label="Payment amount">
        <input className="field" type="number" min="0.01" max={Number(paymentBalance || 0).toFixed(2)} step="0.01" inputMode="decimal" aria-invalid={paymentAmountExceedsBalance ? 'true' : 'false'} value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder={Number(paymentBalance || 0).toFixed(2)} style={{ borderColor: paymentAmountExceedsBalance ? 'var(--danger)' : undefined }} />
        {paymentAmountExceedsBalance && <div role="alert" style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 700, marginTop: 5 }}>Payment amount cannot exceed the open balance of {RP.money2(paymentBalance)}.</div>}
      </MField>
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 700 }}>Balance after payment</span>
        <span className="disp" style={{ fontSize: 20 }}>{RP.money2(nextBalance)}</span>
      </div>
      <MField label="Payment method">
        <select className="field" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
          {methods.map(method => <option key={method.id} value={method.id}>{method.label}</option>)}
        </select>
      </MField>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)', gap: 12 }}>
        <MField label="Payment date">
          <input className="field" type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
        </MField>
        <MField label={paymentMethod === 'cheque' ? 'Cheque number' : 'Reference'}>
          <input className="field" value={paymentReference} onChange={e => setPaymentReference(e.target.value)} placeholder={paymentMethod === 'cheque' ? 'Cheque #' : 'Interac ref, cash drop'} />
        </MField>
      </div>
      <MField label="Payment note">
        <textarea className="field" rows={2} value={paymentNote} onChange={e => setPaymentNote(e.target.value)} placeholder="Optional internal note" style={{ resize: 'vertical' }} />
      </MField>
      <MField label={`Email ${paidInFull ? 'receipt' : 'invoice'} to`}>
        <input className="field" type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="billing@example.com" />
      </MField>
      <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
        <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
        Email {paidInFull ? 'receipt' : 'invoice'} to customer
      </label>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function invoicePaymentMethodCode(inv, order) {
  return String(inv?.paymentMethod || order?.invoicePaymentMethod || '').trim().toLowerCase();
}

function invoiceIsCreditCard(inv, order) {
  return ['credit_card', 'card'].includes(invoicePaymentMethodCode(inv, order));
}

function invoiceSavedStripeCard(customer) {
  const card = typeof rpStripeDefaultCard === 'function' ? rpStripeDefaultCard(customer) : null;
  if (!card) return null;
  return {
    id: String(card.id || '').trim(),
    label: typeof rpStripeCardLabel === 'function' ? rpStripeCardLabel(card) : '',
    brand: String(card.brand || 'Card').trim(),
    last4: String(card.last4 || card.last_4 || '').trim()
  };
}

function InvoiceChargeCardModal({ inv, order, onClose, onCharged }) {
  const c = invoiceCustomer(inv);
  const t = RP.invoiceTotals(inv);
  const [setupOpen, setSetupOpen] = useState(false);
  const [, bumpCard] = useState(0);
  const savedCard = invoiceSavedStripeCard(c);
  const cardLabel = savedCard
    ? (savedCard.label || `${savedCard.brand}${savedCard.last4 ? ` ending ${savedCard.last4}` : ''}`)
    : 'No saved card connected';
  const emailDefault = inv.emailTo || order.invoiceEmailTo || c.email || c.buyerEmail || '';
  const [emailTo, setEmailTo] = useState(emailDefault);
  const [sendReceipt, setSendReceipt] = useState(() => Boolean(emailDefault));
  const [paymentNote, setPaymentNote] = useState('Credit-card payment recorded from invoicing.');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const live = !(window.RP_AUTH && window.RP_AUTH.demo);

  const syncPaymentResponse = async (response, message) => {
    const saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
    Object.assign(order, saved);
    await rpRefreshAfterMutation(message);
    const refreshed = RP.ORDERS.find(x => x.id === order.id) || saved;
    Object.assign(order, refreshed);
    onCharged && onCharged(refreshed);
    return refreshed;
  };

  const chargeSavedCard = async () => {
    if (busy || !savedCard) return;
    setBusy(true);
    setNotice('');
    setError('');
    try {
      const response = await rpApi('/api/invoices/stripe-charge', {
        method: 'POST',
        body: JSON.stringify({
          orderId: order.id,
          paymentMethodId: savedCard.id,
          emailTo: sendReceipt ? emailTo.trim() : '',
          sendReceipt,
          paymentNote: paymentNote.trim()
        })
      });
      if (response.processing) {
        const message = response.message || 'Stripe is still processing this payment. No receipt has been issued yet.';
        setNotice(message);
        toast('Card payment is still processing', 'info');
        return;
      }
      const refreshed = await syncPaymentResponse(response, `Card charged for ${response.invoice && response.invoice.invoiceNumber || inv.id}`);
      const amount = RP.money2(response.payment && response.payment.amount || refreshed.total || t.balance || 0);
      const receiptEmailFailed = sendReceipt && !(response.email && response.email.sent);
      const warning = response.email && response.email.warning
        ? response.email.warning
        : 'Use Email on the invoice to send the receipt.';
      toast(receiptEmailFailed ? `Card charged for ${amount}, but the receipt email was not sent: ${warning}` : `Card charged for ${amount}. ${sendReceipt ? 'Receipt emailed.' : 'Receipt ready.'}`, receiptEmailFailed ? 'warn' : 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not charge saved card.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal title="Charge Card" sub={`${inv.id} - ${c.name}`} onClose={onClose} width={620}
        footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn" disabled={busy || !live} onClick={() => setSetupOpen(true)} style={{ opacity: busy || !live ? 0.6 : 1 }}><Icon name="card" size={15} />{savedCard ? 'Replace Card' : 'Add Saved Card'}</button><button className="btn btn-primary" disabled={busy || !live || !savedCard} onClick={chargeSavedCard} style={{ opacity: busy || !live || !savedCard ? 0.6 : 1 }}><Icon name="check" size={15} />{busy ? 'Charging...' : 'Charge Saved Card'}</button></>}>
        <div style={{ background: savedCard ? 'var(--ok-bg)' : 'var(--amber-soft)', color: savedCard ? 'var(--ok)' : 'var(--amber-deep)', border: `1px solid ${savedCard ? 'oklch(0.6 0.11 150 / 0.3)' : 'var(--amber)'}`, borderRadius: 9, padding: '11px 12px', fontSize: 12.5, fontWeight: 750, marginBottom: 12 }}>
          {savedCard ? `${cardLabel} is ready for direct Stripe charging.` : 'Add a saved Stripe card to charge this invoice directly. Do not enter or store raw card numbers in Ranch Power.'}
        </div>
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          <Row k="Invoice balance" v={<span className="mono">{RP.money2(t.balance > 0.005 ? t.balance : t.total)}</span>} />
          <Row k="Payment" v="Credit card" />
          <Row k="Card status" v={cardLabel} />
          <Row k="Receipt" v={sendReceipt ? (emailTo || 'Email required') : 'Not emailed'} />
        </div>
        <MField label="Receipt email">
          <input className="field" type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="billing@example.com" />
        </MField>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
          <input type="checkbox" checked={sendReceipt} onChange={e => setSendReceipt(e.target.checked)} />
          Email receipt to customer
        </label>
        <MField label="Internal note">
          <textarea className="field" rows={2} value={paymentNote} onChange={e => setPaymentNote(e.target.value)} style={{ resize: 'vertical' }} />
        </MField>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.45, marginBottom: 12 }}>Charge Saved Card runs the payment through Stripe, applies the required 2.4% credit-card fee, and records the invoice as paid.</div>
        {notice && <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: error ? 8 : 0 }}>{notice}</div>}
        {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      </Modal>
      {setupOpen && <StripeCardSetupModal customer={c} onClose={() => setSetupOpen(false)} onSaved={() => bumpCard(value => value + 1)} />}
    </>
  );
}

function InvoiceManualCardModal({ inv, order, onClose, onCharged }) {
  const c = invoiceCustomer(inv);
  const mountRef = useRef(null);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const submittedRef = useRef(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [emailTo, setEmailTo] = useState(inv.emailTo || order.invoiceEmailTo || c.email || c.buyerEmail || '');
  const [sendReceipt, setSendReceipt] = useState(() => Boolean(inv.emailTo || order.invoiceEmailTo || c.email || c.buyerEmail));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const live = rpHasLiveAuth();
  const recovery = submitted || !!quote?.pending;
  const emailValid = !sendReceipt || invoiceEmailIsValid(emailTo.trim());
  const canCharge = live && !loading && !busy && quote && (recovery || (complete && authorized && cardholderName.trim() && emailValid));

  useEffect(() => {
    let cancelled = false;
    let card;
    async function prepare() {
      try {
        if (!live) throw new Error('Manual card payments are only available in live mode.');
        const response = await rpApi(`/api/invoices/manual-card?orderId=${encodeURIComponent(order.id)}`);
        if (cancelled) return;
        setQuote(response);
        if (response.pending) {
          setNotice('A card payment is already in progress for this invoice. Check its status to finish recording the existing payment.');
          return;
        }
        const Stripe = await rpLoadStripeSdk();
        if (cancelled) return;
        const stripe = Stripe(response.publishableKey);
        card = stripe.elements().create('card', { hidePostalCode: false });
        stripeRef.current = stripe;
        cardRef.current = card;
        card.on('change', event => {
          if (cancelled) return;
          setComplete(event.complete);
          setError(event.error?.message || '');
        });
        card.mount(mountRef.current);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not prepare secure card entry.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    prepare();
    return () => { cancelled = true; if (card) card.destroy(); };
  }, [order.id]);

  const charge = async () => {
    if (!canCharge) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      let body = submittedRef.current;
      if (!body) {
        if (quote.pending) {
          body = { orderId: order.id };
        } else {
          const result = await stripeRef.current.createPaymentMethod({
            type: 'card', card: cardRef.current,
            billing_details: { name: cardholderName.trim() }
          });
          if (result.error) throw new Error(result.error.message || 'Check the card details and try again.');
          body = {
            orderId: order.id,
            manualEntry: true,
            paymentMethodId: result.paymentMethod.id,
            expectedAmount: quote.amount,
            authorizationConfirmed: true,
            authorizationPolicyVersion: quote.authorizationPolicyVersion,
            emailTo: sendReceipt ? emailTo.trim() : '',
            sendReceipt
          };
        }
        submittedRef.current = body;
        setSubmitted(true);
        if (cardRef.current) cardRef.current.update({ disabled: true });
      }
      const response = await rpApi('/api/invoices/stripe-charge', { method: 'POST', body: JSON.stringify(recovery ? { ...body, resumeOnly: true } : body) });
      if (response.processing) {
        setNotice(response.message || 'Stripe is still processing this payment. Check Payment Status to reconcile the same charge.');
        return;
      }
      const saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
      Object.assign(order, saved);
      await rpRefreshAfterMutation(`Card payment recorded for ${inv.id}`).catch(() => {});
      const emailFailed = body.sendReceipt !== false && !(response.email?.sent || response.email?.replayed || response.email?.skipped);
      toast(emailFailed ? `Card charged. ${response.email?.warning || 'Use Email on the invoice to send the receipt.'}` : `Card charged for ${RP.money2(response.payment?.amount || quote.amount)}. Receipt ready.`, emailFailed ? 'warn' : 'ok');
      onCharged && onCharged(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not confirm this payment. Check Payment Status before taking another payment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Enter Card Manually" sub={`${inv.id} - ${c.name}`} onClose={busy ? () => {} : onClose} width={620}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!canCharge} onClick={charge}><Icon name="card" size={15} />{busy ? 'Processing...' : recovery ? 'Check Payment Status' : `Charge ${quote ? RP.money2(quote.amount) : 'Card'}`}</button></>}>
      <div style={{ color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.5, marginBottom: 14 }}>Enter the card from the customer's signed authorization form. Stripe handles the card details securely. This payment does not replace the customer's saved card.</div>
      {quote && <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        {!quote.pending && <><Row k="Outstanding balance before card fee" v={RP.money2(quote.balance)} /><Row k="Credit-card fee (2.4%)" v={RP.money2(quote.fee)} /></>}
        <Row k={quote.pending ? 'Payment in progress' : 'Total to charge'} v={<strong>{RP.money2(quote.amount)}</strong>} />
      </div>}
      {!quote?.pending && <>
        <MField label="Cardholder name" required><input className="field" aria-label="Cardholder name" autoComplete="cc-name" value={cardholderName} disabled={busy || recovery} onChange={event => setCardholderName(event.target.value)} /></MField>
        <MField label="Card details"><div ref={mountRef} style={{ minHeight: 60, border: '1px solid var(--line)', borderRadius: 10, padding: 16, background: '#fff' }} /></MField>
        <div style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.45, marginBottom: 14 }}>Enter the card number, expiry, security code and billing postal code in the secure Stripe fields above.</div>
        <div style={{ background: 'var(--amber-soft)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, marginBottom: 12 }}>{RP_CREDIT_CARD_TERMS_NOTE}</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
          <input type="checkbox" checked={authorized} disabled={busy || recovery} onChange={event => setAuthorized(event.target.checked)} style={{ marginTop: 3 }} />
          I confirm we have the cardholder's signed authorization to charge this invoice, including the disclosed 2.4% card fee.
        </label>
        <MField label="Receipt email"><input className="field" aria-label="Receipt email" type="email" value={emailTo} disabled={busy || recovery} onChange={event => setEmailTo(event.target.value)} placeholder="billing@example.com" /></MField>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, marginBottom: 12 }}><input type="checkbox" checked={sendReceipt} disabled={busy || recovery} onChange={event => setSendReceipt(event.target.checked)} />Email receipt to customer</label>
        {!emailValid && <div role="alert" style={{ color: 'var(--danger)', marginBottom: 12 }}>Enter a valid receipt email or turn off Email receipt.</div>}
      </>}
      {loading && <div role="status">Preparing secure card entry...</div>}
      {notice && <div role="status" style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, marginBottom: 12 }}>{notice}</div>}
      {error && <div role="alert" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5 }}>{error}</div>}
    </Modal>
  );
}

function RecordInvoiceRefundModal({ inv, order, onClose, onRefunded }) {
  const c = invoiceCustomer(inv);
  const t = RP.invoiceTotals(inv);
  const methodIds = new Set(['bank_transfer', 'e_transfer', 'cheque', 'cash', 'credit_card', 'account_credit']);
  const methods = (RP.PAY_METHODS || [])
    .filter(method => methodIds.has(method.id))
    .filter((method, index, rows) => rows.findIndex(row => row.id === method.id) === index);
  const overpaid = Math.max(0, Number(t.paid || 0) - Number(t.total || 0));
  const [refundMethod, setRefundMethod] = useState('cash');
  const [refundAmount, setRefundAmount] = useState(() => overpaid > 0.005 ? overpaid.toFixed(2) : '');
  const [refundDate, setRefundDate] = useState(() => typeof rpTodayYmd === 'function' ? rpTodayYmd(c) : new Date().toISOString().slice(0, 10));
  const [refundReason, setRefundReason] = useState(overpaid > 0.005 ? 'Overpayment / tax correction' : 'Customer refund');
  const [refundReference, setRefundReference] = useState('');
  const [refundNote, setRefundNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const refundRequestRef = useRef('');

  const record = async () => {
    if (busy || !order) return;
    setBusy(true);
    setError('');
    try {
      const clientRequestId = refundRequestRef.current || crypto.randomUUID();
      refundRequestRef.current = clientRequestId;
      const response = await rpApi('/api/invoices/refund', {
        method: 'POST',
        body: JSON.stringify({
          clientRequestId,
          orderId: order.id,
          amount: refundAmount,
          refundMethod,
          refundDate,
          refundReason: refundReason.trim(),
          refundReference: refundReference.trim(),
          refundNote: refundNote.trim()
        })
      });
      const saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
      Object.assign(order, saved);
      await rpRefreshAfterMutation(`Refund recorded for ${order.invoiceNumber || inv.id}`);
      const refreshed = RP.ORDERS.find(x => x.id === order.id) || saved;
      Object.assign(order, refreshed);
      refundRequestRef.current = '';
      toast(refundMethod === 'account_credit'
        ? `Account credit ${RP.money2(Number(refundAmount || 0))} recorded`
        : `Refund ${RP.money2(Number(refundAmount || 0))} recorded`, 'ok');
      onRefunded && onRefunded(refreshed);
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not record refund.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record Refund" sub={`${inv.id} - ${c.name}`} onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !(Number(refundAmount) > 0) || !refundMethod} onClick={record}><Icon name="check" size={15} />{busy ? 'Recording...' : 'Record Refund'}</button></>}>
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 700 }}>{overpaid > 0.005 ? 'Detected overpayment' : 'Invoice total'}</span>
        <span className="disp" style={{ fontSize: 22 }}>{RP.money2(overpaid > 0.005 ? overpaid : t.total)}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)', gap: 12 }}>
        <MField label="Refund amount">
          <input className="field" type="number" step="0.01" min="0" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} placeholder="0.00" />
        </MField>
        <MField label="Refund method">
          <select className="field" value={refundMethod} onChange={e => setRefundMethod(e.target.value)}>
            {methods.map(method => <option key={method.id} value={method.id}>{method.label}</option>)}
          </select>
        </MField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)', gap: 12 }}>
        <MField label="Refund date">
          <input className="field" type="date" value={refundDate} onChange={e => setRefundDate(e.target.value)} />
        </MField>
        <MField label="Reference">
          <input className="field" value={refundReference} onChange={e => setRefundReference(e.target.value)} placeholder="Cash, EFT ref, cheque #" />
        </MField>
      </div>
      <MField label="Reason">
        <select className="field" value={refundReason} onChange={e => setRefundReason(e.target.value)}>
          <option>Overpayment / tax correction</option>
          <option>PST exemption refund</option>
          <option>GST correction refund</option>
          <option>Returned product</option>
          <option>Goodwill credit</option>
          <option>Customer refund</option>
        </select>
      </MField>
      <MField label="Refund note">
        <textarea className="field" rows={2} value={refundNote} onChange={e => setRefundNote(e.target.value)} placeholder="Optional internal note" style={{ resize: 'vertical' }} />
      </MField>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function InvoiceEditModal({ inv, order, onClose, onUpdated }) {
  const methods = (RP.PAY_METHODS || [])
    .filter(method => ['account_terms', 'bank_transfer', 'e_transfer', 'cheque', 'cash', 'credit_card'].includes(method.id))
    .filter((method, index, rows) => rows.findIndex(row => row.id === method.id) === index);
  const hasTaxCode = code => (inv.adjustments || []).some(row => String(row.code || '').toUpperCase() === code && Number(row.amount || 0) > 0);
  const [paymentMethod, setPaymentMethod] = useState(methods.some(method => method.id === inv.paymentMethod) ? inv.paymentMethod : 'account_terms');
  const [emailTo, setEmailTo] = useState(inv.emailTo || order.invoiceEmailTo || '');
  const [invoiceNote, setInvoiceNote] = useState(inv.note || order.invoiceNote || '');
  const [removePst, setRemovePst] = useState(false);
  const [removeGst, setRemoveGst] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pstPresent = hasTaxCode('PST');
  const gstPresent = hasTaxCode('GST');

  const save = async () => {
    if (busy || !order) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/invoices/update', {
        method: 'POST',
        body: JSON.stringify({
          orderId: order.id,
          paymentMethod,
          emailTo: emailTo.trim(),
          invoiceNote: invoiceNote.trim(),
          exemptPst: removePst,
          exemptGst: removeGst
        })
      });
      const saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
      Object.assign(order, saved);
      await rpRefreshAfterMutation(`Invoice ${order.invoiceNumber || inv.id} updated`);
      const refreshed = RP.ORDERS.find(x => x.id === order.id) || saved;
      Object.assign(order, refreshed);
      toast(`Invoice ${refreshed.invoiceNumber || inv.id} updated`, 'ok');
      onUpdated && onUpdated(refreshed);
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not update invoice.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit Invoice" sub={`${inv.id} - ${invoiceCustomer(inv).name}`} onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !paymentMethod} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Save Invoice'}</button></>}>
      <MField label="Payment method">
        <select className="field" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
          {methods.map(method => <option key={method.id} value={method.id}>{method.label}</option>)}
        </select>
      </MField>
      <MField label="Invoice email">
        <input className="field" type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="billing@example.com" />
      </MField>
      <MField label="Customer invoice note">
        <textarea className="field" rows={3} maxLength={600} value={invoiceNote} onChange={e => setInvoiceNote(e.target.value)} placeholder="Meet at receiving, delivery instructions, thank you..." style={{ resize: 'vertical' }} />
      </MField>
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, opacity: pstPresent ? 1 : 0.55, marginBottom: 10 }}>
          <input type="checkbox" checked={removePst} disabled={!pstPresent} onChange={e => setRemovePst(e.target.checked)} />
          Remove PST from this invoice
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, opacity: gstPresent ? 1 : 0.55 }}>
          <input type="checkbox" checked={removeGst} disabled={!gstPresent} onChange={e => setRemoveGst(e.target.checked)} />
          Remove GST from this invoice
        </label>
      </div>
      {paymentMethod === 'credit_card' && <div style={{ background: 'var(--amber-soft)', color: 'var(--amber-deep)', border: '1px solid var(--amber)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>Credit-card processing fees will be recalculated. An active authorized Stripe card must be saved for this customer.</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function invoiceDeskServerFilters(filters) {
  return {
    area: filters.Area,
    customerType: filters['Customer Type'],
    delivery: filters.Delivery,
    payMethod: filters.Payment,
    rep: filters.Rep,
    status: filters.Status,
    terms: filters.Terms,
    territory: filters.Territory
  };
}
function invoiceDeskMetric(summary, keys, fallback = null) {
  const containers = [summary && summary.metrics, summary && summary.totals, summary];
  for (const key of keys) {
    for (const container of containers) {
      if (!container || container[key] == null) continue;
      const raw = container[key];
      const value = Number(raw && typeof raw === 'object' ? (raw.value ?? raw.amount) : raw);
      if (Number.isFinite(value)) return value;
    }
  }
  return fallback;
}
function invoiceDeskFacet(facets, key) {
  const values = typeof rpServerFacetOptions === 'function' ? rpServerFacetOptions(facets, key) : [];
  return values;
}
function invoiceDeskServerRow(item) {
  const order = item && item.order;
  if (!order) return null;
  const inv = typeof rpInvoiceFromOrder === 'function' ? rpInvoiceFromOrder(order, item.customer) : null;
  return inv ? invoiceListRow(inv, item.customer) : null;
}
function InvoicingView({ go, reportsEnabled }) {
  const savedState = rpSavedListState('invoicing', { search: '', tab: 'all', filters: {} });
  const [search, setSearchValue] = useState(savedState.search || '');
  const [tab, setTabValue] = useState(savedState.tab || 'all');
  const [filters, onFilter] = useFilters(savedState.filters, next => rpSaveListState('invoicing', { filters: next }));
  const [issueTarget, setIssueTarget] = useState(null);
  const [, force] = useState(0);
  const setSearch = value => {
    setSearchValue(value);
    rpSaveListState('invoicing', { search: value });
  };
  const setTab = value => {
    setTabValue(value);
    rpSaveListState('invoicing', { tab: value });
  };
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const serverFilters = invoiceDeskServerFilters(filters);
  const serverPage = useRpServerPagedRows({ mode: 'invoices', limit: 75, search, tab, filters: serverFilters });
  const withStatus = live ? serverPage.items.map(invoiceDeskServerRow).filter(Boolean) : RP.INVOICES.map(invoiceListRow);
  const localCounts = {
    all: withStatus.length,
    unpaid: withStatus.filter(row => ['Issued', 'Sent', 'Partial', 'Overdue'].includes(row.st)).length,
    overdue: withStatus.filter(row => row.st === 'Overdue').length,
    paid: withStatus.filter(row => row.st === 'Paid').length,
    draft: withStatus.filter(row => row.st === 'Draft').length,
    paidPending: withStatus.filter(row => row.st === 'Paid' && row.delivery.open).length,
    deliveredUnpaid: withStatus.filter(row => row.delivery.delivered && ['Issued', 'Sent', 'Partial', 'Overdue'].includes(row.st)).length
  };
  const totalRepresentsAll = tab === 'all' && !String(search || '').trim() && !Object.values(serverFilters).some(value => String(value || '').trim());
  const counts = live ? Object.fromEntries(Object.keys(localCounts).map(key => [key, rpServerSummaryValue(serverPage.summary, key, key === 'all' && totalRepresentsAll && serverPage.total.value != null ? serverPage.total.value : null)])) : localCounts;
  const fallbackMetrics = live ? { ar: null, overdue: null, collectedMtd: null } : RP.invoiceMetrics();
  const m = live ? {
    ar: invoiceDeskMetric(serverPage.summary, ['ar', 'outstandingAr', 'outstanding'], fallbackMetrics.ar),
    overdue: invoiceDeskMetric(serverPage.summary, ['overdueAmount', 'overdueBalance'], fallbackMetrics.overdue),
    collectedMtd: invoiceDeskMetric(serverPage.summary, ['collectedMtd', 'collectedMonth'], fallbackMetrics.collectedMtd)
  } : fallbackMetrics;
  let rows = withStatus;
  if (!live && tab === 'unpaid') rows = rows.filter(row => ['Issued', 'Sent', 'Partial', 'Overdue'].includes(row.st));
  if (!live && tab === 'overdue') rows = rows.filter(row => row.st === 'Overdue');
  if (!live && tab === 'paid') rows = rows.filter(row => row.st === 'Paid');
  if (!live && tab === 'draft') rows = rows.filter(row => row.st === 'Draft');
  if (!live && tab === 'paidPending') rows = rows.filter(row => row.st === 'Paid' && row.delivery.open);
  if (!live && tab === 'deliveredUnpaid') rows = rows.filter(row => row.delivery.delivered && ['Issued', 'Sent', 'Partial', 'Overdue'].includes(row.st));
  const defs = [
    { label: 'Status', options: live ? invoiceDeskFacet(serverPage.facets, 'status') : distinct(withStatus, row => row.st).map(invoiceStatusLabel) },
    { label: 'Territory', options: live ? invoiceDeskFacet(serverPage.facets, 'territory') : distinct(withStatus, row => row.territory) },
    { label: 'Area', options: live ? invoiceDeskFacet(serverPage.facets, 'area') : distinct(withStatus, row => row.area) },
    { label: 'Delivery', options: live ? invoiceDeskFacet(serverPage.facets, 'delivery') : distinct(withStatus, row => row.delivery.label) },
    { label: 'Rep', options: live ? invoiceDeskFacet(serverPage.facets, 'rep') : distinct(withStatus, row => row.rep) },
    { label: 'Customer Type', options: live ? invoiceDeskFacet(serverPage.facets, 'customerType') : distinct(withStatus, row => row.customerType) },
    { label: 'Terms', options: live ? invoiceDeskFacet(serverPage.facets, 'terms') : distinct(RP.INVOICES, inv => inv.terms) },
    { label: 'Payment', options: live ? invoiceDeskFacet(serverPage.facets, 'payMethod') : distinct(RP.INVOICES, inv => rpInvoicePaymentMethodLabel(inv.paymentMethod)) }
  ];
  if (!live && filters.Status) rows = rows.filter(row => invoiceStatusLabel(row.st) === filters.Status);
  if (!live && filters.Territory) rows = rows.filter(row => row.territory === filters.Territory);
  if (!live && filters.Area) rows = rows.filter(row => row.area === filters.Area);
  if (!live && filters.Delivery) rows = rows.filter(row => row.delivery.label === filters.Delivery);
  if (!live && filters.Rep) rows = rows.filter(row => row.rep === filters.Rep);
  if (!live && filters['Customer Type']) rows = rows.filter(row => row.customerType === filters['Customer Type']);
  if (!live && filters.Terms) rows = rows.filter(row => row.inv.terms === filters.Terms);
  if (!live && filters.Payment) rows = rows.filter(row => rpInvoicePaymentMethodLabel(row.inv.paymentMethod) === filters.Payment);
  if (!live && search) rows = rows.filter(row => invoiceSearchText(row.inv).includes(search.toLowerCase()));
  const pagination = useRpPagedRows(rows, 75, `${tab}|${search}|${JSON.stringify(filters)}`);
  const visibleRows = live ? rows : pagination.rows;

  const refresh = () => force(value => value + 1);
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {issueTarget && <InvoiceIssueModal order={issueTarget.order} customer={issueTarget.customer} onClose={() => setIssueTarget(null)} onIssued={refresh} />}
      <PageHead eyebrow="Accounts Receivable" title="Invoicing"
        sub="Issue invoices, send branded PDFs, and keep customer payment history in one place."
        actions={<>
          {reportsEnabled && <button className="btn btn-sm" onClick={() => go('reports', { type: 'report', id: 'invoices_ar', filters: { ...(search ? { search } : {}), ...(filters.Status ? { status: filters.Status } : {}), ...(filters.Territory ? { territory: filters.Territory } : {}) } })}><Icon name="download" size={15} />Export A/R</button>}
          <button className="btn btn-sm btn-primary" onClick={() => setTab('draft')}><Icon name="receipt" size={15} />Ready to Bill</button>
        </>} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 22 }}>
        <KPICard label="Outstanding A/R" value={m.ar == null ? '—' : RP.money(m.ar)} icon="receipt" accent="var(--amber)" onClick={() => setTab('unpaid')} active={tab === 'unpaid'} />
        <KPICard label="Overdue" value={m.overdue == null ? '—' : RP.money(m.overdue)} icon="alert" accent="var(--danger)" onClick={() => setTab('overdue')} active={tab === 'overdue'} />
        <KPICard label="Collected MTD" value={m.collectedMtd == null ? '—' : RP.money(m.collectedMtd)} icon="card" accent="var(--ok)" onClick={() => setTab('paid')} active={tab === 'paid'} />
        <KPICard label="Ready to Bill" value={counts.draft == null ? '—' : counts.draft} icon="clock" accent="var(--navy)" onClick={() => setTab('draft')} active={tab === 'draft'} />
      </div>
      {live && <p style={{ margin: '-11px 0 18px', color: 'var(--muted)', fontSize: 12 }}>
        CAD native invoices only. A/R uses issued invoice balances after recorded payments; Collected MTD uses dated invoice payments net of refunds. Shopify-linked sales and legacy paid statuses without dated payment records are excluded.
      </p>}

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['all', 'All'], ['unpaid', 'Unpaid'], ['overdue', 'Overdue'], ['paid', 'Paid'], ['paidPending', 'Paid, Not Delivered'], ['deliveredUnpaid', 'Delivered, Unpaid'], ['draft', 'Ready']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ border: '1px solid ' + (tab === key ? 'var(--navy)' : 'var(--line)'), background: tab === key ? 'var(--navy)' : 'var(--surface)', color: tab === key ? '#fff' : 'var(--ink-soft)', borderRadius: 8, padding: '7px 13px', fontSize: 13, fontWeight: 650, display: 'flex', alignItems: 'center', gap: 7 }}>
            {label}<span style={{ fontSize: 11, opacity: 0.7 }}>{counts[key] == null ? '…' : counts[key]}</span>
          </button>
        ))}
      </div>
      <FilterBar defs={defs} value={filters} onChange={onFilter} search={search} onSearch={setSearch} searchPlaceholder="Search invoice #, customer, order or payment..." />

      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="rp-table">
          <thead><tr><th>Invoice</th><th>Customer</th><th>Territory</th><th>Order</th><th>Delivery</th><th>Issued</th><th>Due</th><th>Status</th><th className="num">Total</th><th className="num">Balance</th><th></th></tr></thead>
          <tbody>
            {visibleRows.map(({ inv, st, t, customer: c, order, territory, area, rep, customerType, delivery }) => {
              const over = st === 'Overdue';
              const issued = st !== 'Draft';
              return (
                <tr key={`${inv.id}-${inv.order}`} style={{ cursor: 'pointer' }} onClick={() => { if (live && order) rpUpsertOrder(order); go('invoicing', { type: 'invoice', id: inv.id }); }}>
                  <td><span className="mono" style={{ fontWeight: 650, color: 'var(--ink)' }}>{issued ? inv.id : 'Ready'}</span></td>
                  <td><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{[customerType, area, rep].filter(Boolean).join(' - ') || (inv.terms || c.terms)}</div></td>
                  <td style={{ color: 'var(--muted)' }}>{territory || '-'}</td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{inv.order}</td>
                  <td><Pill status={delivery.label}>{delivery.label}</Pill></td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{issued ? inv.issued : 'Pending'}</td>
                  <td className="mono" style={{ fontSize: 12, color: over ? 'var(--danger)' : 'var(--muted)', fontWeight: over ? 700 : 400 }}>{issued ? inv.due : 'After issue'}{over && <span style={{ display: 'block', fontSize: 10 }}>{RP.invDaysOverdue(inv)}d late</span>}</td>
                  <td><Pill status={st}>{invoiceStatusLabel(st)}</Pill></td>
                  <td className="num" style={{ fontWeight: 650 }}>{RP.money2(t.total)}</td>
                  <td className="num" style={{ fontWeight: 750, color: t.balance > 0.005 ? (over ? 'var(--danger)' : 'var(--ink)') : 'var(--ok)' }}>{st === 'Draft' ? '-' : t.balance > 0.005 ? RP.money2(t.balance) : 'Paid'}</td>
                  <td>
                    {st === 'Draft'
                      ? <button className="btn btn-sm btn-primary" disabled={!live || !(order && order.id)} onClick={event => { event.stopPropagation(); if (order && order.id) { rpUpsertOrder(order); setIssueTarget({ order, customer: c }); } }} style={{ opacity: live && order && order.id ? 1 : 0.6 }}><Icon name="receipt" size={14} />Issue</button>
                      : <Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} />}
                  </td>
                </tr>
              );
            })}
            {live && serverPage.initialLoading && <tr><td colSpan="11" style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}><Icon name="refresh" size={18} /><div style={{ marginTop: 7, fontWeight: 700, color: 'var(--ink)' }}>Loading invoices...</div></td></tr>}
            {live && !serverPage.initialLoading && !serverPage.loaded && serverPage.error && <tr><td colSpan="11" style={{ textAlign: 'center', color: 'var(--danger)', padding: 30 }}><div style={{ fontWeight: 700 }}>{serverPage.error}</div><button type="button" className="btn btn-sm" onClick={serverPage.retry} style={{ marginTop: 10 }}>Try Again</button></td></tr>}
            {(!live || serverPage.loaded) && rows.length === 0 && <tr><td colSpan="11" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No invoices match these filters.</td></tr>}
          </tbody>
        </table>
        {live
          ? serverPage.loaded && <RpServerLoadMore {...serverPage} label="invoices" onLoadMore={serverPage.loadMore} onRetry={serverPage.loadMore} />
          : <RpPagination {...pagination} label="invoices" />}
      </div>
    </div>
  );
}

function InvoiceDetail({ id, back, backLabel = 'Back to Invoicing', go }) {
  const exactOrder = useRpExactOrder({ invoiceId: id });
  const [issueOpen, setIssueOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [manualCardOpen, setManualCardOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [, force] = useState(0);
  const inv = RP.invoice(id);
  const invoiceIssue = useRpInvoiceIssueRequest(exactOrder.order?.id || inv?.order);
  const backButton = <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />{backLabel}</button>;
  if (exactOrder.loading) {
    return (
      <div className="rp-fade" data-exact-invoice-loading="true" style={{ padding: '26px 30px 40px' }}>
        {backButton}
        <div className="card" style={{ padding: 24, color: 'var(--muted)' }}><Icon name="refresh" size={18} /><div style={{ color: 'var(--ink)', fontWeight: 700, marginTop: 8 }}>Loading invoice {id}...</div></div>
      </div>
    );
  }
  if (exactOrder.error) {
    return (
      <div className="rp-fade" data-exact-invoice-error="true" style={{ padding: '26px 30px 40px' }}>
        {backButton}
        <div className="card" role="alert" style={{ padding: 24, color: 'var(--danger)' }}><SectionTitle>Invoice Could Not Load</SectionTitle><div style={{ fontSize: 14 }}>{exactOrder.error}</div><button type="button" className="btn btn-sm" onClick={exactOrder.retry} style={{ marginTop: 12 }}>Try Again</button></div>
      </div>
    );
  }
  if (!inv) {
    return (
      <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
        {backButton}
        <div className="card" style={{ padding: 24 }}>Invoice not found.</div>
      </div>
    );
  }
  const order = invoiceOrder(inv);
  const c = invoiceCustomer(inv);
  const t = RP.invoiceTotals(inv);
  const st = RP.invoiceStatus(inv);
  const customerInternalNote = String(c && c.note || '').trim();
  const live = !(window.RP_AUTH && window.RP_AUTH.demo);
  const directRemoval = typeof rpNormalizeRole === 'function' && rpNormalizeRole(rpAuthProfile().role) === 'admin';
  const canMarkPaid = live && order && st !== 'Draft' && t.balance > 0.005;
  const canChargeCard = canMarkPaid && invoiceIsCreditCard(inv, order);
  const canEnterCard = canMarkPaid && !invoiceIssue.pending && !invoiceIssue.loading && !invoiceIssue.error
    && [order.status, order.invoiceStatus, order.financialStatus].every(value => !['paid', 'closed', 'canceled', 'cancelled', 'void', 'archived', 'refunded', 'settled'].includes(String(value || '').trim().toLowerCase()));
  const canRecordRefund = live && order && st !== 'Draft' && (Number(t.paid || 0) > 0.005 || st === 'Paid');
  const savedInvoiceCard = invoiceSavedStripeCard(c);
  const refresh = () => force(value => value + 1);
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {issueOpen && order && <InvoiceIssueModal order={order} customer={c} onClose={() => setIssueOpen(false)} onIssued={refresh} />}
      {paymentOpen && order && <RecordInvoicePaymentModal inv={inv} order={order} onClose={() => setPaymentOpen(false)} onPaid={refresh} />}
      {chargeOpen && order && <InvoiceChargeCardModal inv={inv} order={order} onClose={() => setChargeOpen(false)} onCharged={refresh} />}
      {manualCardOpen && order && <InvoiceManualCardModal inv={inv} order={order} onClose={() => setManualCardOpen(false)} onCharged={refresh} />}
      {refundOpen && order && <RecordInvoiceRefundModal inv={inv} order={order} onClose={() => setRefundOpen(false)} onRefunded={refresh} />}
      {editOpen && order && <InvoiceEditModal inv={inv} order={order} onClose={() => setEditOpen(false)} onUpdated={refresh} />}
      {removeOpen && <RemovalRequestModal entityType="invoice" entityId={inv.id} entityLabel={`${inv.id} - ${c.name}`} onClose={() => setRemoveOpen(false)} onRequested={() => back && back()} />}
      {backButton}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className="disp" style={{ margin: 0, fontSize: 32, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{st === 'Draft' ? inv.order : inv.id}</h1>
            <Pill status={st}>{invoiceStatusLabel(st)}</Pill>
          </div>
          <button onClick={() => go('orders', { type: 'order', id: inv.order })} style={{ color: 'var(--muted)', fontSize: 14, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}>{c.name} - from <span className="mono">{inv.order}</span><Icon name="arrowR" size={13} /></button>
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
          {(st === 'Draft' || invoiceIssue.pending) && <button className="btn btn-sm btn-primary" disabled={!live || !order || invoiceIssue.loading || !!invoiceIssue.error} onClick={() => setIssueOpen(true)}><Icon name="receipt" size={15} />{invoiceIssue.pending ? 'Recover Invoice Request' : 'Issue Invoice'}</button>}
          {st !== 'Draft' && <button className="btn btn-sm" disabled={!live || !order || invoiceIssue.pending} onClick={() => setEditOpen(true)}><Icon name="pen" size={15} />Edit Invoice</button>}
          {canChargeCard && <button className="btn btn-sm btn-primary" onClick={() => setChargeOpen(true)}><Icon name="card" size={15} />Charge Card</button>}
          {canEnterCard && <button className="btn btn-sm" onClick={() => setManualCardOpen(true)}><Icon name="card" size={15} />Enter Card Manually</button>}
          {canMarkPaid && <button className="btn btn-sm btn-primary" onClick={() => setPaymentOpen(true)}><Icon name="check" size={15} />Record Payment</button>}
          {canRecordRefund && <button className="btn btn-sm" onClick={() => setRefundOpen(true)}><Icon name="arrowL" size={15} />Refund</button>}
          {st !== 'Draft' && <OpenInvoicePdfButton inv={inv} />}
          {st !== 'Draft' && <EmailInvoiceButton inv={inv} onDone={refresh} disabled={invoiceIssue.pending || invoiceIssue.loading || !!invoiceIssue.error} />}
          {st !== 'Draft' && <button className="btn btn-sm" onClick={() => setRemoveOpen(true)} style={{ color: 'var(--danger)' }}><Icon name="x" size={15} />{directRemoval ? 'Remove Invoice' : 'Request Removal'}</button>}
        </div>
      </div>

      {invoiceIssue.pending && <div data-invoice-issue-recovery="invoice" role="status" style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.45, marginBottom: 16 }}>The original invoice request still needs recovery. Recover Invoice Request checks the saved result and original email without creating another invoice or intentionally resending it.</div>}
      {invoiceIssue.error && <div role="alert" style={{ color: 'var(--danger)', marginBottom: 16 }}>{invoiceIssue.error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(280px, 0.8fr)', gap: 18, alignItems: 'start' }}>
        <InvoiceDocument inv={inv} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 68 }}>
          <div className="card" style={{ padding: 20 }}>
            {st === 'Draft' ? (
              <>
                <div className="eyebrow">Ready to Bill</div>
                <div className="disp" style={{ fontSize: 34, marginTop: 6 }}>{RP.money2(t.total)}</div>
                <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>{inv.order} can be issued from live order data.</div>
                <button className="btn btn-lg btn-primary" disabled={!live || !order || invoiceIssue.loading || !!invoiceIssue.error} onClick={() => setIssueOpen(true)} style={{ width: '100%', marginTop: 16 }}><Icon name="receipt" size={17} />{invoiceIssue.pending ? 'Recover Invoice Request' : 'Issue Invoice'}</button>
              </>
            ) : t.balance > 0.005 ? (
              <>
                <div className="eyebrow">{st === 'Overdue' ? 'Overdue Balance' : 'Balance Due'}</div>
                <div className="disp" style={{ fontSize: 36, marginTop: 6, color: st === 'Overdue' ? 'var(--danger)' : 'var(--ink)' }}>{RP.money2(t.balance)}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>Due {inv.due} - {inv.terms}</div>
                <div style={{ marginTop: 16, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>Payment method is {rpInvoicePaymentMethodLabel(inv.paymentMethod)}.</div>
                {canChargeCard && (
                  <>
                    <div style={{ marginTop: 10, background: savedInvoiceCard ? 'var(--ok-bg)' : 'var(--amber-soft)', border: `1px solid ${savedInvoiceCard ? 'oklch(0.6 0.11 150 / 0.3)' : 'var(--amber)'}`, borderRadius: 10, padding: '11px 13px', fontSize: 12.5, color: savedInvoiceCard ? 'var(--ok)' : 'var(--amber-deep)', lineHeight: 1.45, fontWeight: 700 }}>
                      {savedInvoiceCard ? `Saved card: ${savedInvoiceCard.label || savedInvoiceCard.brand}` : 'No saved Stripe card connected yet.'}
                    </div>
                    <button className="btn btn-lg btn-primary" onClick={() => setChargeOpen(true)} style={{ width: '100%', marginTop: 10, justifyContent: 'center' }}><Icon name="card" size={17} />Charge Card</button>
                  </>
                )}
                {canMarkPaid && <button className="btn btn-lg btn-primary" onClick={() => setPaymentOpen(true)} style={{ width: '100%', marginTop: 10, justifyContent: 'center' }}><Icon name="check" size={17} />Record Payment</button>}
                {canEnterCard && <button className="btn btn-lg" onClick={() => setManualCardOpen(true)} style={{ width: '100%', marginTop: 10, justifyContent: 'center' }}><Icon name="card" size={17} />Enter Card Manually</button>}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, color: 'var(--ok)' }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--ok-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={24} /></div>
                  <div><div className="disp" style={{ fontSize: 22 }}>Paid</div><div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No open balance.</div></div>
                </div>
              </>
            )}
          </div>

          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>Invoice Controls</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5 }}>
              <Row k="Email" v={inv.emailTo ? <span className="mono" style={{ fontSize: 11.5 }}>{inv.emailTo}</span> : 'Missing'} />
              <Row k="Payment" v={rpInvoicePaymentMethodLabel(inv.paymentMethod)} />
              {order && order.creditCardFeeTotal > 0 && <Row k="Card fee" v={RP.money2(order.creditCardFeeTotal)} />}
              {order && order.invoiceLastSentAt && <Row k="Last sent" v={<span className="mono" style={{ fontSize: 11.5 }}>{invoiceReceiptTime(order.invoiceLastSentAt)}</span>} />}
              {order && order.invoiceFirstOpenedAt && <Row k="First opened" v={<span className="mono" style={{ fontSize: 11.5 }}>{invoiceReceiptTime(order.invoiceFirstOpenedAt)}</span>} />}
              {order && order.invoiceLastOpenedAt && <Row k="Last opened" v={<span className="mono" style={{ fontSize: 11.5 }}>{invoiceReceiptTime(order.invoiceLastOpenedAt)}</span>} />}
              {order && Number(order.invoiceOpenCount || 0) > 0 && <Row k="Open events" v={<span className="mono">{Number(order.invoiceOpenCount || 0)}</span>} />}
            </div>
            {customerInternalNote && (
              <div style={{ marginTop: 14, background: 'var(--amber-soft)', border: '1px solid var(--amber)', borderRadius: 10, padding: '11px 13px' }}>
                <div className="eyebrow" style={{ color: 'var(--amber-deep)', marginBottom: 5 }}>Internal Note</div>
                <div style={{ color: 'var(--ink-soft)', fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{customerInternalNote}</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {st !== 'Draft' && <OpenInvoicePdfButton inv={inv} />}
              {st !== 'Draft' && <button className="btn btn-sm" disabled={!live || !order} onClick={() => setEditOpen(true)} style={{ opacity: live && order ? 1 : 0.6 }}><Icon name="pen" size={14} />Edit</button>}
              {st !== 'Draft' && <EmailInvoiceButton inv={inv} onDone={refresh} disabled={invoiceIssue.pending || invoiceIssue.loading || !!invoiceIssue.error} />}
              {canChargeCard && <button className="btn btn-sm btn-primary" onClick={() => setChargeOpen(true)}><Icon name="card" size={14} />Charge Card</button>}
              {canMarkPaid && <button className="btn btn-sm btn-primary" onClick={() => setPaymentOpen(true)}><Icon name="check" size={14} />Record Payment</button>}
              {canRecordRefund && <button className="btn btn-sm" onClick={() => setRefundOpen(true)}><Icon name="arrowL" size={14} />Refund</button>}
              {order && <button className="btn btn-sm" onClick={() => go('orders', { type: 'order', id: order.id })}><Icon name="orders" size={14} />Order</button>}
              {st !== 'Draft' && <button className="btn btn-sm" onClick={() => setRemoveOpen(true)} style={{ color: 'var(--danger)' }}><Icon name="x" size={14} />{directRemoval ? 'Remove' : 'Request'}</button>}
            </div>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>Payments</SectionTitle>
            <PaymentHistory inv={inv} />
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { InvoicingView, InvoiceDetail, EmailInvoiceButton, OpenInvoicePdfButton, RecordInvoicePaymentModal, InvoiceChargeCardModal, InvoiceManualCardModal, RecordInvoiceRefundModal });


/* ===== desktop_inventory.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Inventory overview + ledger + stubs
   ============================================================ */

function InventoryView({ go, detail }) {
  const initialView = detail && detail.type === 'inventory-view' && ['balances', 'lots', 'ledger', 'requests', 'receipts', 'open-cans'].includes(detail.view) ? detail.view : detail && detail.type === 'stock-request' ? 'requests' : detail && detail.type === 'field-receipt' ? 'receipts' : detail && detail.type === 'open-cans' ? 'open-cans' : 'balances';
  const [view, setLocalView] = useState(initialView);
  const setView = value => {setLocalView(value);if(go)go('inventory',{type:'inventory-view',view:value});}; // balances | lots | ledger | requests | receipts | open-cans
  const [search, setSearch] = useState('');
  const [newGiveaway, setNewGiveaway] = useState(false);
  const [newReceive, setNewReceive] = useState(false);
  const [newRepack, setNewRepack] = useState(false);
  const [newTransfer, setNewTransfer] = useState(false);
  const [lotEditor, setLotEditor] = useState(null);
  const [cycleCount, setCycleCount] = useState(null);
  const [warehouseId, setWarehouseId] = useState(detail && detail.type === 'warehouse' ? detail.id : null);
  const [requestId, setRequestId] = useState(detail && detail.type === 'stock-request' ? detail.id : null);
  const [receiptId, setReceiptId] = useState(detail && detail.type === 'field-receipt' ? detail.id : null);
  const [, forceInv] = useState(0);
  const [balanceMode, setBalanceMode] = useState('products');
  const [group, setGroup] = useState('location'); // location | sku | status
  const [filters, onFilter] = useFilters();

  useEffect(() => {
    if (!detail) {setLocalView('balances');setWarehouseId(null);setRequestId(null);setReceiptId(null);return;}
    if (detail.type === 'inventory-view' && ['balances', 'lots', 'ledger', 'requests', 'receipts', 'open-cans'].includes(detail.view)) {
      setLocalView(detail.view);
      setSearch('');
      onFilter('__clear__');
      setWarehouseId(null); setRequestId(null); setReceiptId(null);
    }
    if (detail.type === 'stock-request') {
      setLocalView('requests');
      setRequestId(detail.id);
    }
    if (detail.type === 'field-receipt') {
      setLocalView('receipts');
      setReceiptId(detail.id);
    }
    if (detail.type === 'open-cans') {
      setLocalView('open-cans');
    }
    if (detail.type === 'warehouse') {
      setLocalView('balances');
      setGroup('location');
      setWarehouseId(detail.id);
    }
  }, [detail]);

  // Sellable rollup
  const caseInventoryRows = (RP.INV || []).filter(row => String(row.unit || 'case').toLowerCase() !== 'can');
  const sellableStatuses = ['Available'];
  const nonSellable = caseInventoryRows.filter(i => !sellableStatuses.includes(inventoryBalanceStatus(i))).reduce((s, i) => s + i.qty, 0);
  const availabilityPools = inventoryCommitmentPools({ includeUncommitted: true }).filter(row => row.unit === 'case');
  const commitmentsReady = Array.isArray(RP.ORDER_STOCK_COMMITMENTS);
  const committed = availabilityPools.reduce((sum, row) => sum + row.committed, 0);
  const available = availabilityPools.reduce((sum, row) => sum + Math.max(0, row.onHand - row.committed), 0);
  const stockRequests = RP.STOCK_REQUESTS || [];
  const openStockRequests = stockRequests.filter(r => ['Pending', 'Approved'].includes(r.status));
  const fieldReceipts = RP.FIELD_RECEIPTS || [];
  const pendingFieldReceipts = fieldReceipts.filter(r => String(r.status || '') === 'Pending');
  const openCanRows = inventoryOpenCaseRows().filter(row => row.status === 'Active');
  const openCanCount = openCanRows.reduce((sum, row) => sum + row.remainingCans, 0);
  const canBuildMixPacks = inventoryRepackOperationsRoleAllowed();
  const canReconcileCounts = ['admin', 'leadership', 'ops', 'sales_manager'].includes(rpAuthProfile().role);
  const commitmentPools = inventoryCommitmentPools();

  const invDefs = [
    { label: 'SKU', options: distinct([...caseInventoryRows, ...commitmentPools], i => inventorySkuExternalLabel(i.sku)) },
    { label: 'Location', options: inventoryLocationOptions().map(location => location.name || location.id) },
    { label: 'Owner', options: distinct(caseInventoryRows, i => i.owner) },
    { label: 'Status', options: distinct(caseInventoryRows, i => inventoryBalanceStatus(i)) },
    { label: 'Lot', options: distinct(caseInventoryRows, i => rpPrintedLotLabel(i.sku, i.lot)) },
  ];
  const ledgerDefs = inventoryLedgerFilterDefs(RP.LEDGER || []);
  const openCanDefs = [
    { label: 'SKU', options: distinct(openCanRows, row => inventorySkuExternalLabel(row.canSkuId)) },
    { label: 'Location', options: distinct(openCanRows, row => row.locationName) },
    { label: 'Rep', options: distinct(openCanRows, row => row.repName) },
    { label: 'Event', options: distinct(openCanRows, row => row.eventName) },
    { label: 'Lot', options: distinct(openCanRows, row => row.lot) },
  ];
  const lotRows = Array.isArray(RP.LOTS) ? RP.LOTS : [];
  const lotDefs = [
    { label: 'SKU', options: distinct(lotRows, row => inventorySkuExternalLabel(row.sku)) },
    { label: 'Lot Group', options: distinct(lotRows, row => row.lotGroup) },
    { label: 'Lifecycle', options: distinct(lotRows, row => row.lifecycleStatus) },
    { label: 'Date Status', options: distinct(lotRows, row => row.dateStatus) },
  ];

  let rows = caseInventoryRows;
  if (filters.SKU) rows = rows.filter(i => inventorySkuExternalLabel(i.sku) === filters.SKU);
  if (filters.Location) rows = rows.filter(i => inventoryLocationName(i.loc) === filters.Location);
  if (filters.Owner) rows = rows.filter(i => i.owner === filters.Owner);
  if (filters.Status) rows = rows.filter(i => inventoryBalanceStatus(i) === filters.Status);
  if (filters.Lot) rows = rows.filter(i => rpPrintedLotLabel(i.sku, i.lot) === filters.Lot);
  if (search) rows = rows.filter(i => [inventorySkuExternalLabel(i.sku), inventoryLocationName(i.loc), inventoryBalanceStatus(i), rpPrintedLotLabel(i.sku, i.lot), i.owner].join(' ').toLowerCase().includes(search.toLowerCase()));
  const ledgerRows = inventoryFilteredLedgerRows(RP.LEDGER || [], filters, search);
  const filteredOpenCanRows = inventoryFilteredOpenCaseRows(openCanRows, filters, search);
  const filteredLotRows = lotRows
    .filter(row => !filters.SKU || inventorySkuExternalLabel(row.sku) === filters.SKU)
    .filter(row => !filters['Lot Group'] || row.lotGroup === filters['Lot Group'])
    .filter(row => !filters.Lifecycle || row.lifecycleStatus === filters.Lifecycle)
    .filter(row => !filters['Date Status'] || row.dateStatus === filters['Date Status'])
    .filter(row => !search || [inventorySkuExternalLabel(row.sku), rpPrintedLotLabel(row.sku, row.lot), row.lotGroup, row.producer, row.lifecycleStatus, row.dateStatus, inventoryLocationName(row.originLocationId)].join(' ').toLowerCase().includes(search.toLowerCase()));
  const balanceRows = group === 'location' ? inventoryRowsWithEmptyLocations(rows, filters, search) : rows;
  const countOpenCaseBatch = async row => {
    const raw = window.prompt(`Count remaining cans for ${inventorySkuName(row.canSkuId)} - ${row.eventName || row.eventLabel}`, String(row.remainingCans));
    if (raw == null) return;
    const countedCans = Number(raw);
    if (!Number.isInteger(countedCans) || countedCans < 0) {
      toast('Enter a whole number of cans.', 'warn');
      return;
    }
    const notes = window.prompt('Count note', '') || '';
    try {
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        toast('Open-can count saved in live mode only', 'info');
        return;
      }
      const response = await rpApi('/api/inventory/open-case-count', {
        method: 'POST',
        body: JSON.stringify({
          batchId: row.id,
          close: countedCans === 0,
          countedCans,
          notes
        })
      });
      await rpRefreshAfterMutation('Open-can count saved');
      toast(`Open cans counted - variance ${response.delta || 0}`, 'ok');
      forceInv(x => x + 1);
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not save open-can count.', 'warn');
    }
  };

  return (
    <div className="rp-fade rp-inventory-page" style={{ padding: '26px 30px 40px' }}>
      {newReceive && <ReceiveInventoryModal presetLocationId={newReceive.locationId} onClose={() => setNewReceive(false)} onAdded={() => { setView('ledger'); forceInv(x => x + 1); }} />}
      {newRepack && <FinishedGoodsRepackModal onClose={() => setNewRepack(false)} onAdded={() => { setView('ledger'); forceInv(x => x + 1); }} />}
      {newTransfer && <TransferOrderModal presetFrom={newTransfer.from} presetTo={newTransfer.to} onClose={() => setNewTransfer(false)} onAdded={() => { setView('ledger'); forceInv(x => x + 1); }} />}
      {newGiveaway && <GiveawayModal onClose={() => setNewGiveaway(false)} onAdded={() => { setView('ledger'); forceInv(x => x + 1); }} />}
      {lotEditor && <InventoryLotModal lot={lotEditor.id ? lotEditor : null} go={go} onClose={() => setLotEditor(null)} onSaved={() => { setLotEditor(null); setView('lots'); forceInv(x => x + 1); }} />}
      {cycleCount && <InventoryCycleCountModal presetLocationId={cycleCount.locationId} presetSkuId={cycleCount.sku} presetLot={cycleCount.lot} onClose={() => setCycleCount(null)} onHistory={ref => { setCycleCount(null); setView('ledger'); onFilter('__clear__'); setSearch(ref || 'Cycle Count'); forceInv(x => x + 1); }} />}
      {requestId && <StockRequestModal id={requestId} onClose={() => setRequestId(null)} onChange={() => forceInv(x => x + 1)} />}
      {receiptId && <FieldReceiptModal id={receiptId} onClose={() => setReceiptId(null)} onChange={() => forceInv(x => x + 1)} />}
      {warehouseId && <WarehouseDetailModal
        id={warehouseId}
        go={go}
        onChange={() => forceInv(x => x + 1)}
        onClose={() => setWarehouseId(null)}
        onReceive={id => { setWarehouseId(null); setNewReceive({ locationId: id }); }}
        onTransferIn={id => { setWarehouseId(null); setNewTransfer({ to: id }); }}
        onTransferOut={id => { setWarehouseId(null); setNewTransfer({ from: id }); }}
        onCycleCount={canReconcileCounts ? id => { setWarehouseId(null); setCycleCount({ locationId: id }); } : null}
      />}
      <PageHead eyebrow="System of Record" title="Inventory"
        sub="Stock by location, lot and status."
        actions={<>{deskCanUseNav('move-stock', deskActiveRole()) && <button className="btn btn-sm btn-primary" onClick={() => go('move-stock')}><Icon name="layers" size={15} />Move Stock</button>}{view === 'lots' && <button className="btn btn-sm" onClick={() => setLotEditor({})}><Icon name="plus" size={15} />Lot Master</button>}{canReconcileCounts && <button className="btn btn-sm" onClick={() => setCycleCount({})}><Icon name="refresh" size={15} />Reconcile Count</button>}<button className="btn btn-sm" onClick={() => setNewGiveaway(true)}><Icon name="package" size={15} />Giveaway</button>{canBuildMixPacks && <button className="btn btn-sm" onClick={() => setNewRepack(true)}><Icon name="layers" size={15} />Build Mix Packs</button>}<button className="btn btn-sm" onClick={() => setNewReceive({})}><Icon name="plus" size={15} />Batch Receive</button><button className="btn btn-sm" onClick={() => setNewTransfer({})}><Icon name="layers" size={15} />Transfer</button></>} />

      <div className="rp-inventory-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 14, marginBottom: 20 }}>
        <KPICard label="Available" value={commitmentsReady ? available.toLocaleString() : "—"} unit="cases" icon="check" accent="var(--ok)" />
        <KPICard label="Committed" value={commitmentsReady ? committed.toLocaleString() : "—"} unit="cases" icon="orders" accent="var(--info)" />
        <KPICard label="Non-Sellable" value={nonSellable} unit="cases" icon="alert" accent="var(--warn)" />
        <KPICard label="Stock Requests" value={openStockRequests.length} unit="open" icon="layers" accent="var(--amber)" onClick={() => setView('requests')} active={view === 'requests'} />
        <KPICard label="Field Receipts" value={pendingFieldReceipts.length} unit="pending" icon="download" accent="var(--ok)" onClick={() => setView('receipts')} active={view === 'receipts'} />
        <KPICard label="Open Cans" value={openCanCount.toLocaleString()} unit="cans" icon="package" accent="var(--info)" onClick={() => setView('open-cans')} active={view === 'open-cans'} />
      </div>

      <div className="rp-inventory-view-controls" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <Segmented options={[{ value: 'balances', label: 'Balances' }, { value: 'lots', label: 'Lot Master' }, { value: 'open-cans', label: 'Open Cans' }, { value: 'ledger', label: 'Ledger' }, { value: 'requests', label: 'Requests' }, { value: 'receipts', label: 'Field Receipts' }]} value={view} onChange={setView} />
        {view === 'balances' && <><Segmented size="sm" options={[{value:'products',label:'Order availability'},{value:'lots',label:'Lot balances'}]} value={balanceMode} onChange={value => {setBalanceMode(value);setGroup('location');onFilter('__clear__');}} /><Segmented size="sm" options={[{ value: 'location', label: 'By Location' }, { value: 'sku', label: 'By SKU' }, ...(balanceMode === 'lots' ? [{ value: 'status', label: 'By Status' }] : [])]} value={group} onChange={setGroup} /></>}
      </div>

      <FilterBar
        defs={view === 'ledger' ? ledgerDefs : view === 'lots' ? lotDefs : view === 'open-cans' ? openCanDefs : view === 'balances' && balanceMode === 'products' ? invDefs.filter(def => ['SKU','Location'].includes(def.label)) : invDefs}
        value={filters}
        onChange={onFilter}
        search={search}
        onSearch={setSearch}
        searchPlaceholder={view === 'ledger' ? 'Search ledger event, SKU, ref, note, user...' : view === 'lots' ? 'Search SKU, lot, group, producer, origin...' : view === 'open-cans' ? 'Search event, SKU, lot, location, rep...' : 'Search SKU, lot, location, owner...'}
      />

      {view === 'balances' && <InventoryStockShortfalls pools={commitmentPools} go={go} filters={filters} search={search} onClearFilters={() => onFilter('__clear__')} />}

      {view === 'balances'
        ? (balanceMode === 'products' ? <InventoryAvailabilityTable pools={availabilityPools} group={group} filters={filters} search={search} go={go} onOpenLocation={setWarehouseId} onEdit={canReconcileCounts ? row => setCycleCount({locationId:row.loc,sku:row.sku,lot:row.lot}) : null} /> : balanceRows.length ? <BalancesTable rows={balanceRows} group={group} onOpenLocation={setWarehouseId} onEdit={canReconcileCounts ? row => setCycleCount({locationId:row.loc,sku:row.sku,lot:row.lot}) : null} /> : <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--faint)', fontSize: 13.5 }}>No inventory matches these filters.</div>)
        : view === 'lots'
          ? <InventoryLotsTable rows={filteredLotRows} onSelect={setLotEditor} />
          : view === 'open-cans'
          ? <OpenCaseBatchesTable rows={filteredOpenCanRows} onCount={countOpenCaseBatch} />
          : view === 'requests'
            ? <StockRequestsTable requests={stockRequests} onSelect={setRequestId} />
            : view === 'receipts'
              ? <FieldReceiptsTable receipts={fieldReceipts} onSelect={setReceiptId} />
              : <LedgerTable rows={ledgerRows} go={go} onChange={() => forceInv(x => x + 1)} />}
    </div>
  );
}

// Physical count correction: one existing exact lot, with a durable retry receipt.
function inventoryCycleCountSnapshot(locationId, skuId, lot) {
  const location = inventoryLocationOptions().find(row => row.id === locationId);
  if (!location) throw new Error('Choose an active inventory location.');
  if (!/^[A-Z0-9._:-]{1,100}$/.test(lot) || ['UNASSIGNED', 'CYCLE_COUNT', 'UNKNOWN', 'TBD', 'PENDING', 'N/A', 'NA'].includes(lot)) throw new Error('An existing exact lot is required; placeholder lots need Operations review.');
  const sku = (RP.SKUS || []).find(row => row.id === skuId && row.active !== false);
  if (!sku || String(sku.unit || 'case').toLowerCase() !== 'case') throw new Error('Choose an active case product.');
  const masters = (RP.LOTS || []).filter(row => row.sku === skuId && row.lot === lot);
  if (masters.length !== 1 || masters[0].lifecycleStatus !== 'Released') throw new Error('The exact lot must have one Released lot master. Resolve QA or lot identity first.');
  const rows = (RP.INV || []).filter(row => row.loc === locationId && rpCanonicalInventorySkuId(row.sku) === skuId && row.lot === lot && String(row.unit || 'case').toLowerCase() === 'case');
  if (!rows.length) throw new Error('This lot has no recorded balance at this location. Review its source and handoff first.');
  const expectedStatus = location.type === 'Micro-Warehouse' ? 'Fronted to Rep' : 'Available';
  if (rows.some(row => !Number.isSafeInteger(Number(row.qty)) || Number(row.qty) < 0 || (Number(row.qty) !== 0 && (row.owner !== 'Ranch Power' || row.status !== expectedStatus)))) throw new Error('This lot has mixed, unverified or restricted custody. Purchased, sample, QA, damaged and allocated stock require Operations review.');
  // A zero holding still needs explicit custody; no missing-lot or new-lot credit.
  if (!rows.some(row => row.owner === 'Ranch Power' && row.status === expectedStatus)) throw new Error('Ranch Power custody is not confirmed for this lot and location.');
  const systemQuantity = rows.reduce((sum, row) => sum + Number(row.qty), 0);
  if (!Number.isSafeInteger(systemQuantity)) throw new Error('The recorded case balance is invalid.');
  const snapshot = { locationId, skuId, lot, systemQuantity, status: expectedStatus };
  return { ...snapshot, signature: JSON.stringify({ ...snapshot, master: [masters[0].id, masters[0].lifecycleStatus, masters[0].producedAt, masters[0].expiresAt] }) };
}

function inventoryCycleCountChoices(locationId) {
  const seen = new Set();
  return (RP.INV || []).filter(row => row.loc === locationId && String(row.unit || 'case').toLowerCase() === 'case').flatMap(row => {
    const skuId = rpCanonicalInventorySkuId(row.sku), lot = String(row.lot || ''), key = JSON.stringify([skuId, lot]);
    if (seen.has(key)) return [];
    seen.add(key);
    try { return [{ key, ...inventoryCycleCountSnapshot(locationId, skuId, lot), blocked: '' }]; }
    catch (error) { return [{ key, skuId, lot, blocked: error.message }]; }
  }).sort((a, b) => a.key.localeCompare(b.key));
}

function inventoryCycleCountReceiptMatches(response, pending) {
  const request = pending.request, line = request.lines[0], delta = pending.review.delta;
  if (!response || response.accepted !== true || response.ref !== request.ref || response.summary?.lines !== 1
    || response.summary.absoluteVariance !== Math.abs(delta) || response.summary.varianceCount !== (delta === 0 ? 0 : 1)
    || !Array.isArray(response.ledgerRows)) return false;
  if (delta === 0) return response.ledgerRows.length === 0;
  if (!response.ledgerRows.length) return false;
  return response.ledgerRows.every(row => row.event_type === 'Cycle Count' && row.related_type === 'cycle_count'
    && row.related_id === request.ref && row.sku_id === line.skuId && row.lot === line.lot && row.unit === 'case'
    && Number.isSafeInteger(Number(row.quantity)) && Number(row.quantity) > 0
    && (delta > 0 ? row.to_location_id === request.locationId && !row.from_location_id && row.owner_after === 'Ranch Power' && row.status_after === pending.review.status
      : row.from_location_id === request.locationId && !row.to_location_id && row.owner_before === 'Ranch Power' && row.status_before === pending.review.status))
    && response.ledgerRows.reduce((sum, row) => sum + Number(row.quantity), 0) === Math.abs(delta);
}

function inventoryCycleCountLoadPending(storageKey, actorId) {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;
  const pending = JSON.parse(raw), request = pending && pending.request, line = request && request.lines && request.lines[0];
  if (pending.version !== 1 || pending.actorId !== actorId || !request || !/^CC-[A-Z0-9-]{12,80}$/.test(request.ref)
    || !request.locationId || request.lines.length !== 1 || !line.skuId || !line.lot || !Number.isSafeInteger(line.countedQuantity) || line.countedQuantity < 0
    || !request.notes || !pending.review || !Number.isSafeInteger(pending.review.systemQuantity) || pending.review.delta !== line.countedQuantity - pending.review.systemQuantity) {
    throw new Error('The saved count request needs Operations review. Keep the browser record; do not start another count.');
  }
  return pending;
}

function InventoryCycleCountModal({ presetLocationId, presetSkuId, presetLot, onClose, onHistory }) {
  const actorId = rpAuthProfile().id, allowed = ['admin', 'leadership', 'ops', 'sales_manager'].includes(rpAuthProfile().role);
  const storageKey = `rp-inventory-cycle-count:${actorId || 'unavailable'}`;
  const [initial] = useState(() => { try { return { pending: inventoryCycleCountLoadPending(storageKey, actorId), error: '' }; } catch (error) { return { pending: null, error: error.message || 'Browser recovery storage is unavailable.' }; } });
  const [pending, setPending] = useState(initial.pending);
  const [locationId, setLocationId] = useState(initial.pending?.request.locationId || presetLocationId || '');
  const [selection, setSelection] = useState(initial.pending ? JSON.stringify([initial.pending.request.lines[0].skuId, initial.pending.request.lines[0].lot]) : presetSkuId ? JSON.stringify([presetSkuId, presetLot]) : '');
  const [count, setCount] = useState(initial.pending ? String(initial.pending.request.lines[0].countedQuantity) : '');
  const [notes, setNotes] = useState(initial.pending?.request.notes || '');
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState(initial.error), [saved, setSaved] = useState('');
  const inFlight = useRef(false);
  const choices = inventoryCycleCountChoices(locationId), chosen = choices.find(row => row.key === selection);
  const live = rpHasLiveAuth(), locked = busy || !!pending || !!saved;
  const validCount = /^\d+$/.test(count) && Number.isSafeInteger(Number(count));
  const review = pending?.review || (chosen && !chosen.blocked && validCount
    ? { ...chosen, delta: Number(count) - chosen.systemQuantity, countedQuantity: Number(count), notes: notes.trim() } : null);
  const invalidate = () => setError('');
  const validationError = !allowed ? 'Management access is required for this correction.'
    : !live || !actorId ? 'Sign in to your live account to save a count.'
    : initial.error ? initial.error
    : pending ? ''
    : !locationId ? 'Choose a count location.'
    : !chosen ? 'Choose the product and lot you counted.'
    : chosen.blocked ? chosen.blocked
    : !validCount ? 'Enter a whole number of cases, including zero if none remain.'
    : !notes.trim() ? 'Enter a count note or correction reason.'
    : !paused ? 'Confirm that movements for this stock are paused while you save the count.' : '';
  const submit = async () => {
    if (inFlight.current) return;
    if (validationError) { setError(validationError); return; }
    inFlight.current = true; setBusy(true); setError('');
    let submission = pending, sent = false;
    try {
      if (!submission) {
        if (!await rpRefreshLiveData('manual')) throw new Error('Live inventory could not be refreshed. Try saving again.');
        const fresh = inventoryCycleCountSnapshot(locationId, chosen.skuId, chosen.lot);
        if (fresh.signature !== review.signature) throw new Error('The recorded stock changed while this form was open. The difference is now updated; check your physical count and save again.');
        if (inventoryCycleCountLoadPending(storageKey, actorId)) throw new Error('Another pending count exists in this browser. Close and reopen Cycle Count to recover it.');
        submission = { version: 1, actorId, review, request: { ref: `CC-${crypto.randomUUID().toUpperCase()}`, locationId,
          lines: [{ skuId: chosen.skuId, lot: chosen.lot, countedQuantity: Number(count) }], notes: notes.trim() } };
        localStorage.setItem(storageKey, JSON.stringify(submission));
        if (localStorage.getItem(storageKey) !== JSON.stringify(submission)) throw new Error('The count recovery record could not be saved. No count was sent.');
        setPending(submission);
      }
      sent = true;
      const response = await rpApi('/api/inventory/cycle-count', { method: 'POST', body: JSON.stringify(submission.request) });
      if (!inventoryCycleCountReceiptMatches(response, submission)) throw new Error('The response did not confirm the matching reviewed count. Keep this request and check its history before another correction.');
      localStorage.removeItem(storageKey); setPending(null); setSaved(submission.request.ref);
      if (!await rpRefreshAfterMutation(`Count ${submission.request.ref} saved`)) setError('Count saved. Live inventory refresh failed; refresh before starting another count.');
    } catch (err) {
      // Only explicit pre-write validation/authorization failures permit editing.
      if (sent && [400, 403, 404, 422].includes(err.status)) { localStorage.removeItem(storageKey); setPending(null); }
      setError(err.message || 'The result is uncertain. Recover the same count request.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  return <Modal title="Reconcile Physical Case Count" sub="One existing product and exact lot at one location" width={720} onClose={() => !busy && onClose()}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>{pending ? 'Keep Pending and Close' : 'Close'}</button><button className="btn" disabled={busy} onClick={() => onHistory(saved || pending?.request.ref || '')}>View Count History</button>{!saved && <button className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? 'Saving...' : pending ? 'Recover Same Count' : 'Save Count'}</button>}</>}>
    {!allowed && <div role="alert">Management access is required for this correction.</div>}
    {!live && <div role="alert">Count corrections require a live account.</div>}
    {saved ? <div role="status">Count {saved} confirmed. The correction is recorded in history.</div> : <>
      {pending && <p role="status">The last save has not been confirmed. Select Recover Same Count to check it before making another change.</p>}
      <MField label="Count Location"><select className="field" value={locationId} disabled={locked} onChange={event => { setLocationId(event.target.value); setSelection(''); setCount(''); setPaused(false); invalidate(); }}><option value="">Choose location</option>{inventoryLocationOptions().map(row => <option key={row.id} value={row.id}>{row.name || row.id}</option>)}</select></MField>
      <MField label="Existing Product and Exact Lot"><select className="field" value={selection} disabled={locked} onChange={event => { setSelection(event.target.value); setCount(''); invalidate(); }}><option value="">Choose recorded stock</option>{choices.map(row => <option key={row.key} value={row.key}>{inventorySkuExternalLabel(row.skuId)} · {rpPrintedLotLabel(row.skuId, row.lot)}</option>)}</select></MField>
      {!pending && locationId && !choices.some(row => !row.blocked) && <p role="status">No eligible exact lots at this location. Review the lot master, ownership, QA and movement history; a new receipt is not a count correction.</p>}
      {chosen?.blocked && !pending && <p role="alert">{chosen.blocked}</p>}
      <MField label="Physical Cases Counted"><input className="field" type="number" min="0" step="1" value={count} disabled={locked} onChange={event => { setCount(event.target.value); invalidate(); }} placeholder="Enter the actual count, including zero" /></MField>
      <MField label="Count Evidence and Correction Reason"><textarea className="field" rows="2" maxLength="2000" value={notes} disabled={locked} onChange={event => { setNotes(event.target.value); invalidate(); }} placeholder="e.g. Wade counted at BevMax today" /></MField>
      {!pending && <label style={{ display: 'flex', gap: 8, margin: '12px 0' }}><input type="checkbox" checked={paused} disabled={locked} onChange={event => { setPaused(event.target.checked); invalidate(); }} />Movements for this stock are paused while I save this count.</label>}
      {review && <section aria-label="Count correction review" style={{ background: 'var(--surface-2)', padding: 14, borderRadius: 8, overflowWrap: 'anywhere' }}>
        <p>Recorded cases: <strong>{review.systemQuantity}</strong> · Counted cases: <strong>{review.countedQuantity}</strong> · Difference: <strong>{review.delta > 0 ? '+' : ''}{review.delta}</strong></p>
      </section>}
    </>}
    {error && <div role="alert" style={{ marginTop: 12, color: 'var(--danger)', overflowWrap: 'anywhere' }}>{error}</div>}
  </Modal>;
}

// Company stock stored with employees is available stock. Keep the historical
// custody code on the original row for transfers, counts and ledger evidence.
function inventoryBalanceStatus(row) {
  const location = (RP.LOCATIONS || []).find(item => item.id === row.loc);
  return row.owner === 'Ranch Power' && row.status === 'Fronted to Rep' && location?.type === 'Micro-Warehouse'
    ? 'Available' : row.status;
}

function inventorySkuName(skuId) {
  return rpSkuDisplayName(rpSkuItem(skuId) || skuId || 'Unknown SKU').replace(' 355ml', '');
}

function inventorySkuExternalLabel(skuId) {
  const sku = rpSkuItem(skuId);
  const name = sku ? rpSkuDisplayName(sku).replace(/ 355m[lL]/, '') : 'Unknown product';
  return `${name} - ${rpSkuExternalIdentity(sku || '')}`;
}

function inventoryLocationName(locationId) {
  const location = RP.loc(locationId);
  return location ? (location.name || location.id || locationId || 'Unknown location') : (locationId || 'Unknown location');
}

function inventoryLocationOptions() {
  const types = new Set(['Main Warehouse', 'Micro-Warehouse', 'Distributor', 'FBA', 'Production Facility', '3PL Hub', '3PL Warehouse']);
  return (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : [])
    .filter(location => location && location.active !== false && types.has(location.type))
    .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
}

function inventoryCommitmentPools({ includeUncommitted = false } = {}) {
  if (!deskCanUseNav('inventory', deskActiveRole())) return [];
  const locations = new Map((Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : []).map(location => [location.id, location]));
  const pools = new Map();
  const unitOf = row => String(row.unit || 'case').toLowerCase() === 'can' ? 'can' : 'case';
  const skuOf = row => unitOf(row) === 'can' ? String(row.sku || '').trim() : rpCanonicalInventorySkuId(row.sku);
  const lotOf = row => String(row.lot || '').trim().toUpperCase();
  const keyOf = (loc, row) => JSON.stringify([loc, unitOf(row), skuOf(row)]);
  // This aggregate already includes open orders and pending reservations. Do
  // not add RP.ORDERS or infer demand from the existence of inventory cards.
  (Array.isArray(RP.ORDER_STOCK_COMMITMENTS) ? RP.ORDER_STOCK_COMMITMENTS : []).forEach(row => {
    const quantity = Number(row.quantity);
    if (!locations.has(row.sourceLocationId) || !skuOf(row) || !Number.isFinite(quantity) || quantity <= 0) return;
    const key = keyOf(row.sourceLocationId, row);
    const pool = pools.get(key) || { key, sku: skuOf(row), loc: row.sourceLocationId, unit: unitOf(row), committed: 0, onHand: 0, exact: new Map(), holdings: new Map(), sourceSkus: new Set() };
    pool.committed += quantity;
    pool.sourceSkus.add(String(row.sku).trim());
    const lot = lotOf(row);
    if (lot) pool.exact.set(lot, (pool.exact.get(lot) || 0) + quantity);
    pools.set(key, pool);
  });
  (Array.isArray(RP.INV) ? RP.INV : []).forEach(row => {
    const key = keyOf(row.loc, row);
    const location = locations.get(row.loc);
    if (!location || !skuOf(row)) return;
    if (includeUncommitted && !pools.has(key)) pools.set(key, { key, sku: skuOf(row), loc: row.loc, unit: unitOf(row), committed: 0, onHand: 0, exact: new Map(), holdings: new Map(), sourceSkus: new Set([String(row.sku).trim()]) });
    const pool = pools.get(key);
    if (!pool) return;
    pool.physicalOnHand = (pool.physicalOnHand || 0) + (Number.isFinite(Number(row.qty)) ? Number(row.qty) : 0);
    if (location.active === false) return;
    // Match rp_order_stock_availability at every active stock location.
    // Order-source selection restrictions do not make stored stock unavailable. Loose cans are
    // never converted into sealed cases and placeholder lots cannot ship.
    const statuses = pool.unit === 'can' ? ['Open Event Stock'] : location.type === 'Micro-Warehouse' ? ['Available', 'Fronted to Rep', 'Purchased by Rep'] : ['Available'];
    const lot = lotOf(row);
    const quantity = Number(row.qty);
    if (!statuses.includes(row.status) || !lot || ['UNASSIGNED', 'CYCLE_COUNT'].includes(lot) || !Number.isFinite(quantity)) return;
    const onHand = Math.max(0, Math.floor(quantity));
    pool.onHand += onHand;
    pool.holdings.set(lot, (pool.holdings.get(lot) || 0) + onHand);
  });
  return [...pools.values()].map(pool => {
    const lotShortfalls = [...pool.exact].map(([lot, committed]) => ({ lot, needed: Math.max(0, committed - (pool.holdings.get(lot) || 0)) })).filter(row => row.needed > 0);
    // Exact commitments reserve their own lot first; the remaining stock can
    // cover open-lot demand. Taking the maximum avoids counting a gap twice.
    const needed = Math.max(0, pool.committed - pool.onHand, lotShortfalls.reduce((sum, row) => sum + row.needed, 0));
    return { key: pool.key, sourceSkus: [...pool.sourceSkus], sku: pool.sku, loc: pool.loc, unit: pool.unit, committed: pool.committed, onHand: pool.onHand, physicalOnHand: pool.physicalOnHand || 0, needed, lotShortfalls };
  }).sort((a, b) => inventoryLocationName(a.loc).localeCompare(inventoryLocationName(b.loc)) || inventorySkuExternalLabel(a.sku).localeCompare(inventorySkuExternalLabel(b.sku)) || a.unit.localeCompare(b.unit));
}

function InventoryCommitmentOrders({ row, go }) {
  const [result, setResult] = useState({ items: [], nextOffset: 0, loading: true, error: '' });
  const [request, setRequest] = useState({ offset: 0, source: 'all', attempt: 0 });
  const heading = useRef(null);
  useEffect(() => { heading.current?.focus(); }, []);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setResult(old => ({ ...old, loading: true, error: '' }));
    const params = new URLSearchParams({ location: row.loc, unit: row.unit, offset: String(request.offset), source: request.source });
    (row.sourceSkus || [row.sku]).forEach(sku => params.append('sku', sku));
    rpApi(`/api/orders/commitment-details?${params}`, { signal: controller.signal }).then(response => {
      if (!Array.isArray(response.items)) throw new Error('The order list could not be read.');
      if (active) setResult(old => ({ items: request.offset === 0 ? response.items : [...new Map([...old.items, ...response.items].map(item => [item.id, item])).values()], nextOffset: response.nextOffset, nextSource: response.nextSource, loading: false, error: '' }));
    }).catch(error => {
      if (active) setResult(old => ({ ...old, loading: false, error: error.message || 'The associated orders could not be loaded.' }));
    });
    return () => { active = false; controller.abort(); };
  }, [row.key, request.offset, request.source, request.attempt]);
  const unit = row.unit === 'can' ? 'cans' : 'cases';
  const total = result.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  return <div className="stock-needed-orders">
    <h3 ref={heading} tabIndex={-1}>Associated orders · {inventorySkuName(row.sku)}</h3>
    <p className="stock-needed-muted">{inventoryLocationName(row.loc)} · These are the commitments behind this row. Quantities are committed {unit}, not an allocation of the shortage to individual orders.</p>
    <div className="stock-needed-order-list">
      {result.items.map(item => <div className="stock-needed-order" key={item.id}>
        <div>{item.orderAvailable
          ? <button type="button" className="stock-needed-link" onClick={() => go('orders', { type: 'order', id: item.orderId })}>Open {item.orderId}</button>
          : <strong>{item.orderId}</strong>}
          <div className="stock-needed-muted">{item.customer || (item.orderAvailable ? 'Customer details in order' : 'Pending order · not yet created locally')}</div>
        </div>
        <div><strong>{Number(item.quantity).toLocaleString()} {unit}</strong><div className="stock-needed-muted">{item.lot ? rpPrintedLotLabel(row.sku, item.lot) : 'Any eligible lot'}</div></div>
        <div className="stock-needed-muted">{item.kind === 'reservation' ? 'Pending reservation · ' : ''}{item.status}</div>
      </div>)}
    </div>
    {result.loading && <p role="status">Loading associated orders…</p>}
    {result.error && <p role="alert">{result.error} <button className="btn btn-sm" onClick={() => setRequest(old => ({ offset: 0, source: 'all', attempt: old.attempt + 1 }))}>Try again</button></p>}
    {!result.loading && !result.error && result.nextOffset != null && <button className="btn btn-sm" onClick={() => setRequest({ offset: result.nextOffset, source: result.nextSource || 'all', attempt: 0 })}>Load more order lines</button>}
    {!result.loading && !result.error && result.nextOffset == null && <p className="stock-needed-muted">{total === row.committed ? `${total.toLocaleString()} ${unit} committed across the order lines above.` : `Commitments have changed since this inventory view loaded (${total.toLocaleString()} ${unit} now). Refresh inventory before making a stock decision.`}</p>}
  </div>;
}

function InventoryStockShortfalls({ pools, filters = {}, search = '', onClearFilters, go }) {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const scope = JSON.stringify([filters, search]);
  useEffect(() => { setPage(0); setSelected(null); }, [scope]);
  if (!deskCanUseNav('inventory', deskActiveRole())) return null;
  const term = String(search || '').trim().toLowerCase();
  const rows = pools.filter(row => row.needed > 0)
    .filter(row => !filters.SKU || inventorySkuExternalLabel(row.sku) === filters.SKU)
    .filter(row => !filters.Location || inventoryLocationName(row.loc) === filters.Location)
    .filter(row => !term || [inventorySkuExternalLabel(row.sku), row.sku, inventoryLocationName(row.loc), row.loc, ...row.lotShortfalls.flatMap(item => [item.lot, rpPrintedLotLabel(row.sku, item.lot)])].join(' ').toLowerCase().includes(term));
  if (!rows.length) return null;
  const holdingFilters = Boolean(filters.Owner || filters.Status || filters.Lot);
  const totals = ['case', 'can'].map(unit => ({ unit, qty: rows.filter(row => row.unit === unit).reduce((sum, row) => sum + row.needed, 0) })).filter(item => item.qty > 0);
  const currentPage = Math.min(page, Math.ceil(rows.length / 6) - 1);
  const visible = rows.slice(currentPage * 6, currentPage * 6 + 6);
  const activeRow = visible.find(row => row.key === selected);
  return <section className="card stock-needed" aria-labelledby="inventory-stock-needed-title">
    <div className="stock-needed-summary">
      <div><h2 id="inventory-stock-needed-title">Stock still needed</h2><p className="stock-needed-muted">{holdingFilters ? 'Clear holding filters to compare commitments.' : `${totals.map(item => `${item.qty.toLocaleString()} ${item.unit === 'can' ? 'loose cans' : 'cases'}`).join(' · ')} across ${new Set(rows.map(row => row.loc)).size} locations · Orders are unchanged.`}</p></div>
      <button type="button" className="btn btn-sm" aria-expanded={expanded} aria-controls="stock-needed-content" onClick={() => setExpanded(value => !value)}>{expanded ? 'Hide shortages' : `Review shortages (${rows.length})`}</button>
    </div>
    {expanded && <div id="stock-needed-content">
      {holdingFilters ? <>
        <p>Commitments apply to a product and source location, not an Owner, Status or displayed Lot filter. Clear these filters to compare the full eligible stock pool.</p>
        <button className="btn btn-sm" onClick={onClearFilters}>Clear filters to compare commitments</button>
      </> : <>
        <p className="stock-needed-muted">Open orders and pending reservations still need stock, even when the recorded balance is zero. Select Orders to see the commitments.</p>
        <div className="stock-needed-list">
          {visible.map(row => <article key={row.key} className="stock-needed-row" aria-label={`${inventorySkuExternalLabel(row.sku)} at ${inventoryLocationName(row.loc)} — ${row.unit === 'can' ? 'cans' : 'cases'} needed`}>
            <div className="stock-needed-product"><strong>{inventorySkuName(row.sku)}</strong><div className="stock-needed-muted">{rpSkuItem(row.sku)?.code || row.sku} · {inventoryLocationName(row.loc)}</div></div>
            <dl className="stock-needed-metrics">
              {[['On hand', row.onHand], ['Committed', row.committed], ['Still needed', row.needed]].map(([label, value]) => <div key={label}><dt>{label}</dt><dd className={label === 'Still needed' ? 'stock-needed-gap' : ''}>{value.toLocaleString()}</dd></div>)}
            </dl>
            <div className="stock-needed-action"><span className="stock-needed-muted">{row.unit === 'can' ? 'Loose cans' : 'Cases'}</span><button className="btn btn-sm" aria-expanded={selected === row.key} aria-label={`Orders for ${inventorySkuName(row.sku)} at ${inventoryLocationName(row.loc)}`} onClick={() => setSelected(selected === row.key ? null : row.key)}>Orders</button></div>
            {row.lotShortfalls.length > 0 && <p className="stock-needed-lots">Required lots still short: {row.lotShortfalls.map(item => `${rpPrintedLotLabel(row.sku, item.lot)} (${item.needed.toLocaleString()} ${row.unit === 'can' ? 'cans' : 'cases'})`).join('; ')}. Other lots cannot cover these commitments.</p>}
          </article>)}
        </div>
        {rows.length > 6 && <div className="stock-needed-pagination"><span>{currentPage * 6 + 1}–{Math.min(rows.length, currentPage * 6 + 6)} of {rows.length} shortages</span><button className="btn btn-sm" disabled={currentPage === 0} onClick={() => { setPage(currentPage - 1); setSelected(null); }}>Previous</button><button className="btn btn-sm" disabled={(currentPage + 1) * 6 >= rows.length} onClick={() => { setPage(currentPage + 1); setSelected(null); }}>Next</button></div>}
        {activeRow && <InventoryCommitmentOrders key={`${scope}:${activeRow.key}`} row={activeRow} go={go} />}
        <p className="stock-needed-muted stock-needed-footnote">On hand excludes restricted stock and placeholder lots. Confirm physical counts, source and lot before shipping.</p>
      </>}
    </div>}
  </section>;
}

function inventoryRowsWithEmptyLocations(rows, filters = {}, search = '') {
  const activeStockFilters = Boolean(filters.SKU || filters.Owner || filters.Status || filters.Lot);
  if (activeStockFilters) return rows;

  const existingLocationIds = new Set((rows || []).map(row => row && row.loc).filter(Boolean));
  const term = String(search || '').trim().toLowerCase();
  const emptyLocations = inventoryLocationOptions()
    .filter(location => !existingLocationIds.has(location.id))
    .filter(location => !filters.Location || (location.name || location.id) === filters.Location)
    .filter(location => {
      if (!term) return true;
      return `${location.name || ''} ${location.id || ''} ${location.territory || ''} ${location.type || ''}`.toLowerCase().includes(term);
    })
    .map(location => ({
      emptyLocation: true,
      exp: '',
      loc: location.id,
      lot: '',
      owner: '',
      qty: 0,
      sku: '',
      status: ''
    }));

  return [...(rows || []), ...emptyLocations];
}

function inventoryUserName(userId) {
  const profile = (RP.USER_PROFILES || []).find(user => user && user.id === userId);
  return profile ? (profile.name || profile.displayName || profile.email || userId) : (userId ? String(userId) : 'Unassigned rep');
}

function openCaseMovementCanDelta(movement) {
  const qty = Number(movement && movement.quantity || 0);
  const type = String(movement && (movement.type || movement.movementType) || '').toLowerCase();
  const unit = String(movement && movement.unit || '').toLowerCase();
  if (type === 'open_case') return unit === 'case' ? qty * 12 : qty;
  if (type === 'can_sale') return -qty;
  if (type === 'count_adjustment') return qty;
  return 0;
}

function openCaseBatchSummary(batch) {
  const movements = (RP.OPEN_CASE_MOVEMENTS || []).filter(movement => movement.batchId === batch.id);
  const openedCases = movements
    .filter(movement => movement.type === 'open_case')
    .reduce((sum, movement) => sum + Number(movement.quantity || 0), 0);
  const soldCans = movements
    .filter(movement => movement.type === 'can_sale')
    .reduce((sum, movement) => sum + Number(movement.quantity || 0), 0);
  const adjustmentCans = movements
    .filter(movement => movement.type === 'count_adjustment')
    .reduce((sum, movement) => sum + Number(movement.quantity || 0), 0);
  const remainingCans = Math.max(0, Math.round(movements.reduce((sum, movement) => sum + openCaseMovementCanDelta(movement), 0) * 100) / 100);
  return {
    adjustmentCans,
    movements,
    openedCases,
    remainingCans,
    soldCans
  };
}

function inventoryOpenCaseRows() {
  return (RP.OPEN_CASE_BATCHES || [])
    .map(batch => {
      const summary = openCaseBatchSummary(batch);
      const event = (RP.EVENTS || []).find(row => row && row.id === batch.eventId);
      return {
        ...batch,
        ...summary,
        eventName: event ? (event.name || event.id) : (batch.eventLabel || 'POS event stock'),
        locationName: inventoryLocationName(batch.sourceLocationId),
        repName: inventoryUserName(batch.assignedRepId)
      };
    })
    .sort((a, b) => String(b.eventDate || b.createdAt || '').localeCompare(String(a.eventDate || a.createdAt || '')) || inventorySkuName(a.canSkuId).localeCompare(inventorySkuName(b.canSkuId)));
}

function inventoryFilteredOpenCaseRows(rows, filters = {}, search = '') {
  let output = rows || [];
  if (filters.SKU) output = output.filter(row => inventorySkuExternalLabel(row.canSkuId) === filters.SKU);
  if (filters.Location) output = output.filter(row => row.locationName === filters.Location);
  if (filters.Rep) output = output.filter(row => row.repName === filters.Rep);
  if (filters.Event) output = output.filter(row => row.eventName === filters.Event);
  if (filters.Lot) output = output.filter(row => row.lot === filters.Lot);
  const term = String(search || '').trim().toLowerCase();
  if (term) {
    output = output.filter(row => [
      row.eventDate,
      row.eventName,
      row.eventLabel,
      row.eventId,
      row.caseSkuId,
      row.canSkuId,
      inventorySkuExternalLabel(row.canSkuId),
      row.lot,
      row.locationName,
      row.repName,
      row.notes
    ].join(' ').toLowerCase().includes(term));
  }
  return output;
}

function OpenCaseBatchesTable({ rows, onCount }) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <table className="rp-table">
        <thead><tr><th>Event</th><th>SKU</th><th>Location</th><th>Rep</th><th>Lot</th><th className="num">Opened</th><th className="num">Sold</th><th className="num">Adjust</th><th className="num">Remaining</th><th></th></tr></thead>
        <tbody>
          {(rows || []).map(row => (
            <tr key={row.id}>
              <td><div style={{ fontWeight: 700 }}>{row.eventName}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{row.eventDate}{row.eventId ? ` - ${row.eventId}` : ''}</div></td>
              <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={rpSkuItem(row.canSkuId) || row.canSkuId} size={22} /><div><div style={{ fontWeight: 700 }}>{rpSkuItem(row.canSkuId) ? inventorySkuName(row.canSkuId) : 'Unknown product'}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{rpSkuExternalIdentity(rpSkuItem(row.caseSkuId) || '')} {'->'} {rpSkuExternalIdentity(rpSkuItem(row.canSkuId) || '')}</div></div></div></td>
              <td style={{ color: 'var(--muted)' }}>{row.locationName}</td>
              <td style={{ color: 'var(--muted)' }}>{row.repName}</td>
              <td className="mono" style={{ color: 'var(--muted)', fontSize: 12 }}>{row.lot}</td>
              <td className="num">{Number(row.openedCases || 0).toLocaleString()} cs</td>
              <td className="num">{Number(row.soldCans || 0).toLocaleString()} can</td>
              <td className="num" style={{ color: Number(row.adjustmentCans || 0) ? 'var(--warn)' : 'var(--faint)' }}>{Number(row.adjustmentCans || 0).toLocaleString()}</td>
              <td className="num" style={{ fontWeight: 900 }}>{Number(row.remainingCans || 0).toLocaleString()} can</td>
              <td className="num"><button className="btn btn-sm" onClick={() => onCount && onCount(row)}><Icon name="check" size={14} />Count</button></td>
            </tr>
          ))}
          {!(rows || []).length && <tr><td colSpan="10" style={{ textAlign: 'center', color: 'var(--faint)', padding: 28 }}>No active open-can batches match these filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const INVENTORY_REPACK_CASE_CANS = 12;
const INVENTORY_REPACK_RECIPES = Object.freeze({
  'RP-MIX-TRI': Object.freeze({
    outputCasesPerBatch: 3,
    sourceSkuIds: Object.freeze(['RP-12-BR', 'RP-12-GC', 'RP-12-KL'])
  }),
  'RP-MIX-NEW3': Object.freeze({
    outputCasesPerBatch: 3,
    sourceSkuIds: Object.freeze(['RP-12-PM', 'RP-12-BO', 'RP-12-PL'])
  }),
  'RP-MIX-ALL6': Object.freeze({
    outputCasesPerBatch: 6,
    sourceSkuIds: Object.freeze(['RP-12-BR', 'RP-12-GC', 'RP-12-KL', 'RP-12-BO', 'RP-12-PM', 'RP-12-PL'])
  })
});
const INVENTORY_REPACK_TARGET_IDS = Object.freeze(Object.keys(INVENTORY_REPACK_RECIPES));
const INVENTORY_REPACK_PLACEHOLDER_LOTS = new Set(['UNASSIGNED', 'CYCLE_COUNT', 'UNKNOWN', 'TBD', 'PENDING', 'N/A', 'NA']);

function inventoryRepackOperationsRoleAllowed() {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const role = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role) : String(profile.role || '').trim().toLowerCase();
  return ['admin', 'leadership', 'ops', 'warehouse'].includes(role);
}

function inventoryRepackRecipe(targetSkuId) {
  return INVENTORY_REPACK_RECIPES[rpCanonicalInventorySkuId(targetSkuId)] || null;
}

function inventoryRepackTargetCatalog() {
  const supported = new Set(INVENTORY_REPACK_TARGET_IDS);
  return rpInventorySkus({ includeIds: INVENTORY_REPACK_TARGET_IDS })
    .filter(sku => supported.has(rpCanonicalInventorySkuId(sku.id)))
    .filter(sku => rpSkuIsMixer(sku) && inventoryRepackRecipe(sku.id));
}

function inventoryRepackFlavorName(flavorId) {
  const flavor = RP.flavor(flavorId);
  return flavor ? flavor.name : String(flavorId || 'Flavour').replace(/-/g, ' ');
}

function inventoryRepackTargetChoiceLabel(target) {
  if (!target) return 'Mixed pack';
  const targetSkuId = rpCanonicalInventorySkuId(target && target.id);
  if (targetSkuId === 'RP-MIX-ALL6') return `ALL 6 FLAVOURS - ${rpSkuDisplayName(target)}`;
  if (targetSkuId === 'RP-MIX-TRI') return `BR + GC + KL - ${rpSkuDisplayName(target)}`;
  if (targetSkuId === 'RP-MIX-NEW3') return `BO + PM + PL - ${rpSkuDisplayName(target)}`;
  return rpSkuDisplayName(target);
}

function inventoryRepackLocation(locationId) {
  return (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : []).find(location => location && location.id === locationId) || null;
}

function inventoryRepackEligibleStatuses(locationId) {
  const location = inventoryRepackLocation(locationId);
  return location && String(location.type || '').trim().toLowerCase() === 'micro-warehouse'
    ? ['fronted to rep', 'available']
    : ['available'];
}

function inventoryRepackStatusLabel(status) {
  return String(status || '').trim().toLowerCase() === 'fronted to rep'
    ? 'Fronted to Rep'
    : 'Available';
}

function inventoryRepackLotMastersByExactSku() {
  // Case and loose-can masters can share a lot code; their unit-level facts must never overwrite one another.
  return new Map((Array.isArray(RP.LOTS) ? RP.LOTS : [])
    .filter(Boolean)
    .map(lot => [`${String(lot.sku || '').trim().toUpperCase()}|${String(lot.lot || '').trim().toUpperCase()}`, lot]));
}

function inventoryRepackSourceHoldings(locationId) {
  const byKey = new Map();
  const today = receiveDateToday();
  const eligibleStatuses = inventoryRepackEligibleStatuses(locationId);
  const eligibleStatusSet = new Set(eligibleStatuses);
  const lotMasters = inventoryRepackLotMastersByExactSku();
  (Array.isArray(RP.INV) ? RP.INV : []).forEach(row => {
    if (!row || row.loc !== locationId || String(row.unit || 'case').toLowerCase() === 'can') return;
    const holdingStatusKey = String(row.status || 'Available').trim().toLowerCase() || 'available';
    if (!eligibleStatusSet.has(holdingStatusKey)) return;
    if (String(row.owner || 'Ranch Power').trim().toLowerCase() !== 'ranch power') return;
    const skuId = rpCanonicalInventorySkuId(row.sku);
    const sku = rpSkuItem(skuId);
    const flavorId = rpSkuFlavorId(sku || skuId);
    const rawLot = String(row.lot || '').trim().toUpperCase();
    const lot = rawLot.length <= 100 && /^[A-Z0-9._:-]+$/.test(rawLot) ? rawLot : '';
    const quantity = Number(row.qty || 0);
    if (!sku || rpSkuIsMixer(sku) || !/^RP-12-[A-Z0-9]+$/i.test(skuId) || !flavorId || !lot || INVENTORY_REPACK_PLACEHOLDER_LOTS.has(lot) || !(quantity > 0)) return;
    const lotKey = `${skuId}|${lot}`;
    const key = `${lotKey}|${holdingStatusKey}`;
    const lotMaster = lotMasters.get(lotKey);
    const expiresAt = lotMaster && lotMaster.expiresAt || row.lotExpiresAt || row.exp || '';
    const lifecycleStatus = lotMaster && lotMaster.lifecycleStatus || row.lotLifecycleStatus || '';
    if (String(lifecycleStatus).trim().toLowerCase() !== 'released' || (expiresAt && expiresAt < today)) return;
    const current = byKey.get(key) || {
      availableCases: 0,
      expiresAt,
      flavorId,
      holdingStatus: inventoryRepackStatusLabel(holdingStatusKey),
      holdingStatusKey,
      key,
      lot,
      sku,
      skuId
    };
    current.availableCases += quantity;
    current.expiresAt = current.expiresAt || expiresAt;
    byKey.set(key, current);
  });
  return Array.from(byKey.values())
    .sort((a, b) => inventoryRepackFlavorName(a.flavorId).localeCompare(inventoryRepackFlavorName(b.flavorId)) || eligibleStatuses.indexOf(a.holdingStatusKey) - eligibleStatuses.indexOf(b.holdingStatusKey) || String(a.expiresAt || '9999').localeCompare(String(b.expiresAt || '9999')) || a.lot.localeCompare(b.lot));
}

function inventoryRepackSourceBlockers(locationId, sourceSkuIds, eligibleHoldings) {
  const holdings = Array.isArray(eligibleHoldings) ? eligibleHoldings : inventoryRepackSourceHoldings(locationId);
  const eligibleStatusSet = new Set(inventoryRepackEligibleStatuses(locationId));
  const today = receiveDateToday();
  const lotMasters = inventoryRepackLotMastersByExactSku();
  const rows = (Array.isArray(RP.INV) ? RP.INV : [])
    .filter(row => row && row.loc === locationId && String(row.unit || 'case').toLowerCase() !== 'can' && Number(row.qty || 0) > 0);

  return (Array.isArray(sourceSkuIds) ? sourceSkuIds : []).flatMap(sourceSkuId => {
    if (holdings.some(row => row.skuId === sourceSkuId)) return [];
    const sku = rpSkuItem(sourceSkuId);
    const flavorName = inventoryRepackFlavorName(rpSkuFlavorId(sku || sourceSkuId));
    const sourceRows = rows.filter(row => rpCanonicalInventorySkuId(row.sku) === sourceSkuId);
    if (!sourceRows.length) return [`${flavorName}: no cases on hand`];

    const companyRows = sourceRows.filter(row => String(row.owner || 'Ranch Power').trim().toLowerCase() === 'ranch power');
    if (!companyRows.length) return [`${flavorName}: no Ranch Power-owned cases`];
    const custodyRows = companyRows.filter(row => eligibleStatusSet.has(String(row.status || 'Available').trim().toLowerCase() || 'available'));
    if (!custodyRows.length) return [`${flavorName}: no cases in eligible custody`];

    const issues = new Set();
    custodyRows.forEach(row => {
      const rawLot = String(row.lot || '').trim().toUpperCase();
      if (!rawLot || rawLot.length > 100 || !/^[A-Z0-9._:-]+$/.test(rawLot) || INVENTORY_REPACK_PLACEHOLDER_LOTS.has(rawLot)) {
        issues.add(`${flavorName}: exact source lot missing`);
        return;
      }
      const master = lotMasters.get(`${sourceSkuId}|${rawLot}`);
      const lifecycleStatus = String(master && master.lifecycleStatus || row.lotLifecycleStatus || '').trim();
      const dateStatus = String(master && master.dateStatus || row.lotDateStatus || '').trim();
      const expiresAt = String(master && master.expiresAt || row.lotExpiresAt || row.exp || '').trim();
      const reasons = [];
      if (!lifecycleStatus) reasons.push('lot master missing');
      else if (lifecycleStatus.toLowerCase() !== 'released') {
        reasons.push(`lifecycle ${lifecycleStatus}`);
        if (dateStatus && dateStatus.toLowerCase() !== 'verified') reasons.push(`date control ${dateStatus}`);
      }
      if (expiresAt && expiresAt < today) reasons.push(`expired ${expiresAt}`);
      if (reasons.length) issues.add(`${flavorName} ${rawLot}: ${reasons.join(', ')}`);
    });
    return issues.size ? Array.from(issues) : [`${flavorName}: no eligible Released lot`];
  });
}

function inventoryRepackTargetAvailability(locationId, target, availableHoldings) {
  const recipe = inventoryRepackRecipe(target && target.id);
  const holdings = Array.isArray(availableHoldings) ? availableHoldings : inventoryRepackSourceHoldings(locationId);
  const modes = inventoryRepackEligibleStatuses(locationId).map((holdingStatusKey, priority) => {
    const sourceOptions = recipe
      ? recipe.sourceSkuIds.map(sourceSkuId => holdings.filter(row => row.skuId === sourceSkuId && row.holdingStatusKey === holdingStatusKey))
      : [];
    const eligible = Boolean(recipe && sourceOptions.length === recipe.sourceSkuIds.length && sourceOptions.every(options => options.length));
    return {
      eligible,
      holdingStatus: inventoryRepackStatusLabel(holdingStatusKey),
      holdingStatusKey,
      maxBatchCount: eligible
        ? Math.floor(Math.min(...sourceOptions.map(options => Math.max(...options.map(row => row.availableCases)))))
        : 0,
      priority,
      sourceOptions
    };
  });
  const selectedMode = modes
    .filter(mode => mode.eligible && mode.maxBatchCount > 0)
    .sort((a, b) => b.maxBatchCount - a.maxBatchCount || a.priority - b.priority)[0]
    || modes[0]
    || { eligible: false, holdingStatus: 'Available', holdingStatusKey: 'available', maxBatchCount: 0, sourceOptions: [] };
  const blockedReasons = recipe
    ? inventoryRepackSourceBlockers(locationId, recipe.sourceSkuIds, holdings)
    : ['Canonical recipe is unavailable'];
  if (!blockedReasons.length && !modes.some(mode => mode.eligible && mode.maxBatchCount > 0)) {
    blockedReasons.push('No single custody mode has one full case of every required flavour');
  }
  return {
    blockedReasons,
    eligible: selectedMode.eligible && selectedMode.maxBatchCount > 0,
    holdingStatus: selectedMode.holdingStatus,
    holdingStatusKey: selectedMode.holdingStatusKey,
    holdings,
    maxBatchCount: selectedMode.maxBatchCount,
    modes,
    recipe,
    sourceOptions: selectedMode.sourceOptions
  };
}

function inventoryRepackTargetOptions(locationId) {
  const holdings = inventoryRepackSourceHoldings(locationId);
  return inventoryRepackTargetCatalog().map(target => ({
    ...target,
    repackAvailability: inventoryRepackTargetAvailability(locationId, target, holdings)
  }));
}

function inventoryRepackSuggestedLot(targetSkuId, requestRef) {
  const prefix = typeof rpSkuDefaultLotCode === 'function' ? rpSkuDefaultLotCode(targetSkuId) : 'RP-VP-';
  const suffix = String(requestRef || 'REPACK').replace(/^RPK-/i, '').replace(/[^A-Z0-9]/gi, '').slice(0, 6).toUpperCase();
  return desktopReceiveIdentifier(`${prefix}${receiveDateToday().replace(/-/g, '')}-${suffix}`);
}

function inventoryRepackTargetLotMatches(targetSkuId, targetLot) {
  const canonicalTargetSkuId = rpCanonicalInventorySkuId(targetSkuId);
  const lot = desktopReceiveIdentifier(targetLot);
  const prefix = typeof rpSkuDefaultLotCode === 'function' ? rpSkuDefaultLotCode(canonicalTargetSkuId) : 'RP-VP-';
  if (!lot || !prefix || !lot.startsWith(prefix)) return false;
  return canonicalTargetSkuId !== 'RP-MIX-TRI' || !/^RP-VP-[BC]-/.test(lot);
}

function inventoryRepackClientRef() {
  return window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `RPK-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`
    : `RPK-${Date.now().toString(36).toUpperCase()}`;
}

async function inventorySubmitFinishedGoodsRepack(input) {
  return rpApi('/api/inventory/repack', {
    method: 'POST',
    body: JSON.stringify({
      batchCount: input.batchCount,
      locationId: input.locationId,
      notes: input.notes || undefined,
      ref: input.ref,
      repackedOn: input.repackedOn || undefined,
      sourceLines: input.sourceLines.map(line => ({ lot: line.lot, skuId: line.skuId })),
      targetLot: input.targetLot,
      targetSkuId: input.targetSkuId,
      targetStatus: input.targetStatus
    })
  });
}

function inventoryRepackSalesRoleAllowed(role) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const roleKey = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role || role) : String(profile.role || role || '').trim().toLowerCase();
  return ['rep', 'partner', 'admin', 'leadership', 'ops', 'sales_manager'].includes(roleKey);
}

function inventoryRepackNoLocationMessage(role) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const roleKey = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role || role) : String(profile.role || role || '').trim().toLowerCase();
  return ['admin', 'leadership', 'sales_manager'].includes(roleKey)
    ? 'No active micro-warehouse is configured. Create or reactivate one in Operations before building mix packs.'
    : 'Mix-pack building needs an active micro-warehouse assigned to your profile or exact territory. Ask Operations to update the warehouse assignment.';
}

function inventoryRepackAuthorizedFieldLocations(role) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const roleKey = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role || role) : String(profile.role || role || '').trim().toLowerCase();
  if (!inventoryRepackSalesRoleAllowed(role)) return [];
  const profileId = String(profile.id || '').trim();
  const defaultLocationId = String(profile.defaultLocationId || profile.default_location_id || '').trim();
  const profileAreas = typeof rpProfileAreas === 'function' ? rpProfileAreas(profile) : [];
  const profileTerritories = new Set(profileAreas.map(area => String(area || '').trim().toLowerCase()).filter(Boolean));
  const micros = (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : [])
    .filter(location => location && location.active !== false && String(location.type || '').trim().toLowerCase() === 'micro-warehouse');
  const authorized = ['admin', 'leadership', 'sales_manager'].includes(roleKey)
    ? micros
    : micros.filter(micro => {
      const assignedUserId = String(micro.assignedUserId || micro.assigned_user_id || '').trim();
      const territory = String(micro.territory || '').trim();
      const matchesTerritory = Boolean(territory) && profileTerritories.has(territory.toLowerCase());
      return Boolean((profileId && assignedUserId === profileId) || matchesTerritory);
    });
  return authorized.slice().sort((a, b) => {
    const aDefault = defaultLocationId && String(a.id || '') === defaultLocationId ? 0 : 1;
    const bDefault = defaultLocationId && String(b.id || '') === defaultLocationId ? 0 : 1;
    return aDefault - bDefault || String(a.name || a.id || '').localeCompare(String(b.name || b.id || ''));
  });
}

function inventoryPersonalFieldLocations(role) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const roleKey = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role || role) : String(profile.role || role || '').trim().toLowerCase();
  if (!['rep', 'partner', 'admin', 'leadership', 'ops', 'sales_manager'].includes(roleKey)) return [];
  const profileId = String(profile.id || '').trim();
  const defaultLocationId = String(profile.defaultLocationId || profile.default_location_id || '').trim();
  const profileAreas = typeof rpProfileAreas === 'function' ? rpProfileAreas(profile) : [];
  const profileTerritories = new Set(profileAreas.map(area => String(area || '').trim().toLowerCase()).filter(Boolean));
  const micros = (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : [])
    .filter(location => location && location.active !== false && String(location.type || '').trim().toLowerCase() === 'micro-warehouse');
  const personal = micros.filter(micro => {
    const assignedUserId = String(micro.assignedUserId || micro.assigned_user_id || '').trim();
    const territory = String(micro.territory || '').trim().toLowerCase();
    return Boolean(
      (profileId && assignedUserId === profileId)
      || (territory && profileTerritories.has(territory))
    );
  });
  return personal.slice().sort((a, b) => {
    const rank = location => {
      if (defaultLocationId && String(location.id || '') === defaultLocationId) return 0;
      const assignedUserId = String(location.assignedUserId || location.assigned_user_id || '').trim();
      if (profileId && assignedUserId === profileId) return 1;
      return 2;
    };
    return rank(a) - rank(b) || String(a.name || a.id || '').localeCompare(String(b.name || b.id || ''));
  });
}

function inventoryPersonalResolveFieldLocation(locations, selectedLocationId) {
  const personalLocations = Array.isArray(locations) ? locations.filter(Boolean) : [];
  const selectedId = String(selectedLocationId || '').trim();
  return personalLocations.find(location => String(location.id || '') === selectedId)
    || (personalLocations.length === 1 ? personalLocations[0] : null);
}

function inventoryPersonalValidatedFieldLocationId(locations, requestedLocationId) {
  const requestedId = String(requestedLocationId || '').trim();
  if (!requestedId) return '';
  const exactLocation = (Array.isArray(locations) ? locations : [])
    .find(location => location && String(location.id || '') === requestedId);
  return exactLocation ? exactLocation.id : '';
}

function inventoryPersonalPreferredFieldLocationId(locations) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const profileId = String(profile.id || '').trim();
  const defaultLocationId = String(profile.defaultLocationId || profile.default_location_id || '').trim();
  let rememberedLocationId = '';
  if (profileId && typeof window !== 'undefined') {
    try {
      rememberedLocationId = String(window.sessionStorage.getItem(`rp:personal-field-location:${profileId}`) || '').trim();
    } catch (_) {}
  }
  const personalLocations = Array.isArray(locations) ? locations.filter(Boolean) : [];
  const remembered = personalLocations.find(location => String(location.id || '') === rememberedLocationId);
  if (remembered) return remembered.id;
  const configuredDefault = personalLocations.find(location => String(location.id || '') === defaultLocationId);
  if (configuredDefault) return configuredDefault.id;
  const assigned = profileId
    ? personalLocations.filter(location => String(location.assignedUserId || location.assigned_user_id || '').trim() === profileId)
    : [];
  if (assigned.length === 1) return assigned[0].id;
  return personalLocations.length === 1 ? personalLocations[0].id : '';
}

function inventoryPersonalRememberFieldLocation(locations, locationId) {
  const exactLocation = (Array.isArray(locations) ? locations : [])
    .find(location => location && String(location.id || '') === String(locationId || '').trim());
  if (!exactLocation) return '';
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const profileId = String(profile.id || '').trim();
  if (profileId && typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(`rp:personal-field-location:${profileId}`, exactLocation.id);
    } catch (_) {}
  }
  return exactLocation.id;
}

function inventoryPersonalNoLocationMessage() {
  return 'No personal micro-warehouse is assigned to your profile, default location, or exact territory. Ask Operations to update the association.';
}

function inventoryRepackResolveFieldLocation(locations, selectedLocationId) {
  const allowedLocations = Array.isArray(locations) ? locations.filter(Boolean) : [];
  const selectedId = String(selectedLocationId || '').trim();
  return allowedLocations.find(location => String(location.id || '') === selectedId)
    || allowedLocations[0]
    || null;
}

function inventoryRepackValidatedFieldLocationId(role, locations, requestedLocationId) {
  const requestedId = String(requestedLocationId || '').trim();
  if (!requestedId) return '';
  const eligibleLocation = (Array.isArray(locations) ? locations : [])
    .find(location => location && String(location.id || '') === requestedId);
  if (!eligibleLocation) return '';
  const authorizedLocationIds = new Set(inventoryRepackAuthorizedFieldLocations(role).map(location => String(location.id || '')));
  return authorizedLocationIds.has(requestedId) ? requestedId : '';
}

function inventoryRepackPreferredFieldLocationId(locations) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const profileId = String(profile.id || '').trim();
  let rememberedLocationId = '';
  if (profileId && typeof window !== 'undefined') {
    try {
      rememberedLocationId = String(window.sessionStorage.getItem(`rp:field-inventory-location:${profileId}`) || '').trim();
    } catch (_) {}
  }
  const location = inventoryRepackResolveFieldLocation(locations, rememberedLocationId);
  return location ? location.id : '';
}

function inventoryRepackRememberFieldLocation(locations, locationId) {
  const exactLocation = (Array.isArray(locations) ? locations : [])
    .find(location => location && String(location.id || '') === String(locationId || '').trim());
  if (!exactLocation) return '';
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const profileId = String(profile.id || '').trim();
  if (profileId && typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(`rp:field-inventory-location:${profileId}`, exactLocation.id);
    } catch (_) {}
  }
  return exactLocation.id;
}

function inventoryRepackCustodyHelp(target, modeCount) {
  if (!target) return 'Choose a mixed-pack composition to see its eligible inventory custody.';
  if (Number(modeCount) < 1) return 'No complete single-custody recipe is available. Review the blocked mix details above.';
  if (Number(modeCount) > 1) return 'Available and Fronted to Rep are separate ledger holdings. Choose the one matching the physical cases.';
  return 'Custody is fixed by the only complete eligible source recipe.';
}

function inventoryRepackSourceEmptyMessage(locationId, target) {
  if (!locationId) return 'Choose a physical location and mixed-pack composition to see the required source lots.';
  if (!target) return 'Choose a mixed-pack composition to see the required source lots.';
  return 'This selected recipe is unavailable at this location. Review the blocked mix details above.';
}

function inventoryRepackLocationPolicyHelp(location, holdingStatus) {
  if (!location) return 'Choose a physical location to see its eligible custody and source-lot rules.';
  if (String(location.type || '').trim().toLowerCase() === 'micro-warehouse') {
    return `This build uses one complete Ranch Power-owned ${holdingStatus || 'Available'} recipe and preserves that custody on the mixed cases. Available and Fronted to Rep stock are never combined in one build.`;
  }
  return 'Only Available, Ranch Power-owned cases at this non-micro location are eligible.';
}

function FinishedGoodsRepackModal({ onClose, onAdded, allowedLocationIds = null }) {
  const isCompact = useIsNarrow(760);
  const allLocations = inventoryLocationOptions();
  const hasLocationWhitelist = Array.isArray(allowedLocationIds);
  const allowedLocationIdSet = new Set(hasLocationWhitelist ? allowedLocationIds.map(id => String(id || '').trim()).filter(Boolean) : []);
  const locations = hasLocationWhitelist
    ? allLocations.filter(location => allowedLocationIdSet.has(String(location.id || '')))
    : allLocations;
  const fixedLocationId = hasLocationWhitelist && locations.length === 1 ? locations[0].id : '';
  const fixedLocation = fixedLocationId ? locations[0] || null : null;
  const requestRef = useRef(inventoryRepackClientRef());
  const [targetSkuId, setTargetSkuId] = useState('');
  const [locationId, setLocationId] = useState(fixedLocation ? fixedLocation.id : '');
  const [targetLot, setTargetLot] = useState('');
  const [holdingStatusKey, setHoldingStatusKey] = useState('');
  const [sourceKeys, setSourceKeys] = useState([]);
  const [batchCount, setBatchCount] = useState('1');
  const [repackedOn, setRepackedOn] = useState(receiveDateToday());
  const [notes, setNotes] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const targets = inventoryRepackTargetOptions(locationId);
  const unavailableTargets = targets.filter(row => !row.repackAvailability.eligible);
  const target = targets.find(row => inventorySkuMatches(row.id, targetSkuId)) || null;
  const targetAvailability = target && target.repackAvailability || null;
  const recipe = targetAvailability && targetAvailability.recipe || inventoryRepackRecipe(targetSkuId);
  const buildableHoldingModes = targetAvailability
    ? targetAvailability.modes.filter(mode => mode.eligible && mode.maxBatchCount > 0)
    : [];
  const activeHoldingMode = buildableHoldingModes.find(mode => mode.holdingStatusKey === holdingStatusKey)
    || (buildableHoldingModes.length === 1 ? buildableHoldingModes[0] : null)
    || null;
  const effectiveHoldingStatusKey = activeHoldingMode && activeHoldingMode.holdingStatusKey || '';
  const repackHoldingStatus = activeHoldingMode && activeHoldingMode.holdingStatus || 'Available';
  const requiredSourceSkuIds = recipe ? recipe.sourceSkuIds : [];
  const optionsBySource = activeHoldingMode ? activeHoldingMode.sourceOptions : [];
  const effectiveSourceKeys = optionsBySource.map((options, index) => options.some(row => row.key === sourceKeys[index]) ? sourceKeys[index] : '');
  const selectedSources = effectiveSourceKeys.map((key, index) => optionsBySource[index].find(row => row.key === key) || null);
  const batchQuantity = Number(batchCount);
  const maxBatchCount = selectedSources.length === requiredSourceSkuIds.length && selectedSources.every(Boolean)
    ? Math.floor(Math.min(...selectedSources.map(row => row.availableCases)))
    : 0;
  const batchIsWhole = Number.isInteger(batchQuantity) && batchQuantity > 0;
  const exactSources = requiredSourceSkuIds.length >= 3
    && selectedSources.length === requiredSourceSkuIds.length
    && selectedSources.every(Boolean)
    && new Set(effectiveSourceKeys).size === requiredSourceSkuIds.length;
  const targetLotValue = desktopReceiveIdentifier(targetLot);
  const targetLotIsExact = Boolean(
    targetLotValue
    && !INVENTORY_REPACK_PLACEHOLDER_LOTS.has(targetLotValue)
    && inventoryRepackTargetLotMatches(targetSkuId, targetLotValue)
  );
  const today = receiveDateToday();
  const repackDateValid = Boolean(repackedOn && repackedOn <= today);
  const overdrawn = batchIsWhole && maxBatchCount > 0 && batchQuantity > maxBatchCount;
  const locationAllowed = Boolean(locationId && (!hasLocationWhitelist || allowedLocationIdSet.has(locationId)));
  const valid = Boolean(locationAllowed && target && targetLotIsExact && repackDateValid && exactSources && batchIsWhole && batchQuantity <= maxBatchCount);
  const consumedCases = batchIsWhole ? batchQuantity * requiredSourceSkuIds.length : 0;
  const consumedCans = consumedCases * INVENTORY_REPACK_CASE_CANS;
  const producedCases = batchIsWhole && recipe ? batchQuantity * recipe.outputCasesPerBatch : 0;
  const producedCans = producedCases * INVENTORY_REPACK_CASE_CANS;
  const cansPerFlavorPerMixedCase = requiredSourceSkuIds.length
    ? INVENTORY_REPACK_CASE_CANS / requiredSourceSkuIds.length
    : 0;

  const chooseLocation = value => {
    setLocationId(value);
    setTargetSkuId('');
    setTargetLot('');
    setHoldingStatusKey('');
    setSourceKeys([]);
    setTried(false);
    setError('');
  };
  const chooseTarget = value => {
    setTargetSkuId(value);
    setTargetLot(value ? inventoryRepackSuggestedLot(value, requestRef.current) : '');
    setHoldingStatusKey('');
    setSourceKeys([]);
    setTried(false);
    setError('');
  };
  const chooseHoldingStatus = value => {
    setHoldingStatusKey(value);
    setSourceKeys([]);
    setTried(false);
    setError('');
  };
  const chooseSource = (index, value) => {
    setSourceKeys(() => {
      const next = [...effectiveSourceKeys];
      next[index] = value;
      return next;
    });
    setError('');
  };
  const dismiss = () => {
    if (!busy) onClose();
  };

  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    if (window.RP_AUTH && window.RP_AUTH.demo) {
      setError('Finished-goods repacking posts live inventory and is unavailable in demo data mode.');
      return;
    }
    setBusy(true);
    try {
      const response = await inventorySubmitFinishedGoodsRepack({
        batchCount: batchQuantity,
        locationId,
        notes: notes.trim(),
        ref: requestRef.current,
        repackedOn,
        sourceLines: selectedSources.map(row => ({ lot: row.lot, skuId: row.skuId })),
        targetLot: targetLotValue,
        targetSkuId,
        targetStatus: repackHoldingStatus
      });
      await rpRefreshAfterMutation(`Repack ${response.ref || requestRef.current}`);
      const savedResult = {
        ...response,
        locationId: response.locationId || locationId,
        targetLot: response.targetLot || targetLotValue,
        targetSkuId: response.targetSkuId || targetSkuId
      };
      const savedLocation = inventoryRepackLocation(savedResult.locationId);
      toast(`${savedResult.producedCases || producedCases} ${inventorySkuName(savedResult.targetSkuId)} cases built at ${savedLocation ? savedLocation.name : savedResult.locationId} - lot ${savedResult.targetLot}${savedResult.replayed ? ' - prior result confirmed' : ''}`, 'ok');
      onAdded && onAdded(savedResult);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not post this repack. No local inventory was changed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Build Mixed 12-Packs" sub="Convert exact single-flavour case lots into tracked mixed cases at one physical location" onClose={dismiss} width={920}
      footer={<><button type="button" className="btn" disabled={busy} onClick={dismiss}>Cancel</button><button type="button" className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.65 }}><Icon name="layers" size={15} />{busy ? 'Posting Repack...' : `Build ${producedCases || 0} Mix Packs`}</button></>}>
      <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 10, padding: '11px 13px', marginBottom: 16, fontSize: 12.5, fontWeight: 650, lineHeight: 1.45 }}>
        <div style={{ fontWeight: 850, marginBottom: 4 }}>{recipe ? inventoryRepackTargetChoiceLabel(target) : 'Build the pack that is physically being assembled'}</div>
        <div>{recipe
          ? `One complete build consumes one full 12-pack from each of ${requiredSourceSkuIds.length} required flavours and produces ${recipe.outputCasesPerBatch} mixed 12-packs, with ${cansPerFlavorPerMixedCase} cans of each flavour in every output case.`
          : 'Choose the physical location first, then choose the exact mixed-pack composition. For six-flavour cases, select “ALL 6 FLAVOURS - Variety Pack A”.'}</div>
        <div style={{ marginTop: 6, color: 'var(--muted)' }}>Then confirm custody and every source lot, enter how many cases of each flavour you will open, physically assemble the cases, and post the completed build.</div>
      </div>

      <div className="card" style={{ padding: 16, background: 'var(--surface-2)', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isCompact ? 'minmax(0,1fr)' : 'repeat(2,minmax(220px,1fr))', columnGap: 14 }}>
          <div style={{ marginBottom: 14 }}>
            <label className="field-label" htmlFor="inventory-repack-location">Repack location <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select id="inventory-repack-location" className="field" value={locationId} disabled={Boolean(fixedLocationId)} onChange={event => chooseLocation(event.target.value)} style={tried && !locationId ? { borderColor: 'var(--danger)' } : undefined}>
              <option value="">Choose one physical location</option>
              {locations.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}
            </select>
            {hasLocationWhitelist && <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 6 }}>{fixedLocationId ? 'This is your only authorized active micro-warehouse.' : 'Choose the authorized micro-warehouse where you are physically building these cases.'}</div>}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="field-label" htmlFor="inventory-repack-target">Mixed-pack target <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select id="inventory-repack-target" className="field" value={targetSkuId} onChange={event => chooseTarget(event.target.value)} style={tried && !target ? { borderColor: 'var(--danger)' } : undefined}>
              <option value="">Choose the mixed-pack SKU</option>
              {targets.map(sku => {
                const availability = sku.repackAvailability;
                const modes = availability.modes.filter(mode => mode.eligible && mode.maxBatchCount > 0);
                const custodyLabel = modes.map(mode => mode.holdingStatus).join(' / ');
                return <option key={sku.id} value={sku.id} disabled={!availability.eligible}>{inventoryRepackTargetChoiceLabel(sku)} - {rpSkuExternalIdentity(sku)}{availability.eligible ? ` - ${custodyLabel} - up to ${availability.maxBatchCount} build${availability.maxBatchCount === 1 ? '' : 's'}` : ' - blocked; details below'}</option>;
              })}
            </select>
            {!!locationId && !!unavailableTargets.length && <div role="status" aria-live="polite" style={{ marginTop: 8, border: '1px solid var(--warn)', borderRadius: 9, background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '9px 11px', fontSize: 11.5, lineHeight: 1.45 }}>
              <div style={{ fontWeight: 850, marginBottom: 4 }}>Why some mixes are blocked</div>
              {unavailableTargets.map(row => <div key={row.id} style={{ marginTop: 3 }}><b>{rpSkuDisplayName(row)}:</b> {(row.repackAvailability.blockedReasons || []).join('; ') || 'No complete eligible recipe at this location'}</div>)}
              <div style={{ color: 'var(--muted)', marginTop: 5 }}>Inventory is not changed until blocked lot or custody records are reviewed.</div>
            </div>}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="field-label" htmlFor="inventory-repack-custody">Inventory custody <span style={{ color: 'var(--danger)' }}>*</span></label>
            <select id="inventory-repack-custody" className="field" value={effectiveHoldingStatusKey} disabled={buildableHoldingModes.length <= 1} onChange={event => chooseHoldingStatus(event.target.value)}>
              {!buildableHoldingModes.length && <option value="">No complete custody mode</option>}
              {buildableHoldingModes.length > 1 && <option value="">Choose the custody you are physically opening</option>}
              {buildableHoldingModes.map(mode => <option key={mode.holdingStatusKey} value={mode.holdingStatusKey}>{mode.holdingStatus} - up to {mode.maxBatchCount} build{mode.maxBatchCount === 1 ? '' : 's'}</option>)}
            </select>
            <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 6 }}>{inventoryRepackCustodyHelp(target, buildableHoldingModes.length)}</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="field-label" htmlFor="inventory-repack-lot">New mixed-pack lot <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input id="inventory-repack-lot" className="field mono" value={targetLot} maxLength="80" onChange={event => setTargetLot(desktopReceiveIdentifier(event.target.value))} placeholder="Exact output lot" style={tried && !targetLotIsExact ? { borderColor: 'var(--danger)' } : undefined} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="field-label" htmlFor="inventory-repack-date">Repacked on <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input id="inventory-repack-date" className="field" type="date" max={today} value={repackedOn} onChange={event => setRepackedOn(event.target.value)} style={tried && !repackDateValid ? { borderColor: 'var(--danger)' } : undefined} />
          </div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 12 }}>
          {inventoryRepackLocationPolicyHelp(inventoryRepackLocation(locationId), repackHoldingStatus)} Purchased and sample stock is excluded. Every source must have an exact Released, unexpired finished-goods lot.
        </div>
      </div>

      <div className="card" style={{ overflowX: 'auto', marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
          <div style={{ fontSize: 13.5, fontWeight: 800 }}>Choose the {requiredSourceSkuIds.length || 'required'} source lots</div>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 3 }}>The target recipe fixes the required flavours; you choose the exact on-hand lot for each.</div>
        </div>
        <table className="rp-table" style={isCompact ? { minWidth: 660 } : undefined}>
          <thead><tr><th style={{ width: '28%' }}>Required flavour</th><th>Source SKU / lot</th><th className="num" style={{ width: '16%' }}>Available</th><th className="num" style={{ width: '16%' }}>After</th></tr></thead>
          <tbody>
            {requiredSourceSkuIds.map((sourceSkuId, index) => {
              const options = optionsBySource[index] || [];
              const selected = selectedSources[index];
              const controlId = `inventory-repack-source-${index}`;
              const sourceSku = rpSkuItem(sourceSkuId);
              const flavorId = rpSkuFlavorId(sourceSku || sourceSkuId);
              return (
                <tr key={sourceSkuId}>
                  <td><label htmlFor={controlId} style={{ display: 'flex', alignItems: 'center', gap: 9, fontWeight: 750 }}><FlavorChip sku={selected ? selected.sku : sourceSku || { flavor: flavorId }} size={24} /><span>{inventoryRepackFlavorName(flavorId)} <span className="mono" style={{ display: 'block', color: 'var(--faint)', fontSize: 10.5, marginTop: 2 }}>{rpSkuExternalIdentity(sourceSku || '')}</span></span></label></td>
                  <td>
                    <select id={controlId} className="field" value={effectiveSourceKeys[index] || ''} onChange={event => chooseSource(index, event.target.value)} style={tried && !selected ? { borderColor: 'var(--danger)' } : undefined}>
                      <option value="">{options.length ? 'Choose exact source lot' : 'No eligible lot at this location'}</option>
                      {options.map(row => <option key={row.key} value={row.key}>{rpSkuDisplayName(row.sku)} - {rpSkuExternalIdentity(row.sku)} - {rpPrintedLotLabel(row.skuId, row.lot)} - {row.holdingStatus}{row.expiresAt ? ` - exp ${row.expiresAt}` : ''}</option>)}
                    </select>
                  </td>
                  <td className="mono num">{selected ? `${selected.availableCases.toLocaleString()} cs` : '-'}</td>
                  <td className="mono num" style={{ color: selected && batchIsWhole && selected.availableCases - batchQuantity < 0 ? 'var(--danger)' : 'var(--muted)' }}>{selected && batchIsWhole ? `${(selected.availableCases - batchQuantity).toLocaleString()} cs` : '-'}</td>
                </tr>
              );
            })}
            {!requiredSourceSkuIds.length && <tr><td colSpan="4" style={{ textAlign: 'center', padding: 26, color: target ? 'var(--danger)' : 'var(--muted)' }}>{inventoryRepackSourceEmptyMessage(locationId, target)}</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? 'minmax(0,1fr)' : 'minmax(180px,0.55fr) minmax(300px,1.45fr)', gap: 16, alignItems: 'stretch', marginBottom: 16 }}>
        <div className="card" style={{ padding: 16 }}>
          <label className="field-label" htmlFor="inventory-repack-batches">Cases of each flavour to open <span style={{ color: 'var(--danger)' }}>*</span></label>
          <input id="inventory-repack-batches" className="field mono" type="number" min="1" max={Math.max(1, maxBatchCount)} step="1" value={batchCount} aria-describedby="inventory-repack-batch-help" onChange={event => setBatchCount(event.target.value)} style={{ fontSize: 18, fontWeight: 850, ...(tried && (!batchIsWhole || overdrawn) ? { borderColor: 'var(--danger)' } : {}) }} />
          <div id="inventory-repack-batch-help" style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 7 }}>{maxBatchCount ? `This is also the number of complete builds. The selected lots support up to ${maxBatchCount.toLocaleString()}.` : `Choose all ${requiredSourceSkuIds.length || 'required'} eligible source lots first.`}</div>
        </div>
        <div className="card" aria-live="polite" style={{ padding: 16, background: valid ? 'var(--ok-bg)' : 'var(--surface-2)', borderColor: valid ? 'var(--ok)' : 'var(--line)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isCompact ? 'minmax(0,1fr)' : '1fr auto 1fr', alignItems: 'center', gap: 14 }}>
            <div><div className="eyebrow" style={{ marginBottom: 5 }}>Consume</div><div style={{ fontSize: 22, fontWeight: 900 }}>{consumedCases.toLocaleString()} cases</div><div style={{ color: 'var(--muted)', fontSize: 12 }}>{consumedCans.toLocaleString()} cans - {batchIsWhole ? batchQuantity.toLocaleString() : 0} case{batchQuantity === 1 ? '' : 's'} of each flavour</div></div>
            {!isCompact && <Icon name="chevR" size={22} />}
            <div><div className="eyebrow" style={{ marginBottom: 5 }}>Produce</div><div style={{ fontSize: 22, fontWeight: 900 }}>{producedCases.toLocaleString()} mixed cases</div><div style={{ color: 'var(--muted)', fontSize: 12 }}>{producedCans.toLocaleString()} cans - lot {targetLotValue || 'required'}</div></div>
          </div>
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line)', color: consumedCans === producedCans && producedCans > 0 ? 'var(--ok)' : 'var(--muted)', fontSize: 12.5, fontWeight: 750 }}>
            {producedCans > 0 ? `${consumedCans.toLocaleString()} cans in = ${producedCans.toLocaleString()} cans out. No inventory disappears.` : 'Enter a whole batch count to preview the inventory movement.'}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 6 }}>
        <label className="field-label" htmlFor="inventory-repack-notes">Repack notes</label>
        <textarea id="inventory-repack-notes" className="field" rows="3" maxLength="1000" value={notes} onChange={event => setNotes(event.target.value)} placeholder="Optional: who packed it, work area, label detail, or other traceability note" style={{ resize: 'vertical' }} />
      </div>

      {tried && !locationAllowed && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Choose the exact physical location where the cases are being opened and rebuilt.</div>}
      {tried && locationAllowed && !target && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Choose the exact mixed-pack composition. A blocked target is missing at least one eligible source flavour at this location; review the reasons above or ask Operations to correct the lot or stock record.</div>}
      {tried && target && buildableHoldingModes.length > 1 && !activeHoldingMode && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Choose whether the physical source cases are in Available or Fronted to Rep custody. The two holdings cannot be combined.</div>}
      {tried && target && activeHoldingMode && !exactSources && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Confirm one exact eligible case lot for each of the {requiredSourceSkuIds.length} recipe flavours. Every selection must match the physical lot at this location.</div>}
      {tried && !batchIsWhole && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Cases of each flavour must be a positive whole number.</div>}
      {tried && overdrawn && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>The selected lots can support at most {maxBatchCount.toLocaleString()} complete builds. Reduce the case count or choose different lots.</div>}
      {tried && target && (!targetLotIsExact || !repackDateValid) && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Use the selected pack's canonical lot prefix and a real repack date no later than today. Variety Pack B (BR + GC + KL) uses RP-VP-, Variety Pack C (BO + PM + PL) uses RP-VP-B-, and Variety Pack A (all 6 flavours) uses RP-VP-C-.</div>}
      {error && <div role="alert" style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

const GIVEAWAY_STAFF_REASON = 'Staff / rep promo stock';
const GIVEAWAY_REASONS = ['New account sample', 'Reorder conversion', 'Promo placement', 'Event tasting', 'Staff trial', GIVEAWAY_STAFF_REASON, 'Customer make-good', 'Other'];

function giveawayAvailableCases(skuId, locationId) {
  return typeof rpInventoryGiveawayCases === 'function'
    ? rpInventoryGiveawayCases(skuId, locationId)
    : rpInventoryAvailableCases(skuId, locationId);
}

function inventorySkuMatches(left, right) {
  const normalize = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId : value => value;
  return normalize(left) === normalize(right);
}

function giveawaySkuOptions(locationId) {
  return rpInventorySkus({ includeIds: (RP.INV || []).filter(row => row.loc === locationId).map(row => row.sku) })
    .filter(sku => giveawayAvailableCases(sku.id, locationId) > 0);
}

function giveawayNewLine(locationId) {
  const sku = giveawaySkuOptions(locationId)[0];
  return { quantity: 1, skuId: sku ? sku.id : '' };
}

function giveawayRecipientOptions() {
  const customers = (RP.CUSTOMERS || [])
    .filter(customer => customer && customer.id)
    .map(customer => ({
      detail: [customer.type, customer.territory].filter(Boolean).join(' - '),
      id: customer.id,
      key: `customer:${customer.id}`,
      name: customer.name || customer.displayName || customer.id,
      type: 'customer'
    }));
  const prospects = (RP.LEADS || [])
    .filter(lead => lead && lead.id && !['won', 'lost'].includes(String(lead.stage || '').toLowerCase()))
    .map(lead => ({
      detail: ['Prospect', lead.stage, lead.territory].filter(Boolean).join(' - '),
      id: lead.id,
      key: `prospect:${lead.id}`,
      name: lead.name || lead.company || lead.id,
      type: 'prospect'
    }));
  return [...customers, ...prospects].sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
}

function addDemoGiveawayRows(input) {
  const ref = input.ref || `GW-${Date.now().toString().slice(-6)}`;
  const user = typeof rpDisplayName === 'function' ? rpDisplayName() : 'Ranch Power';
  const statusSet = new Set(['available', 'fronted to rep', 'purchased by rep', 'sample', 'sample / demo']);
  const lineItems = Array.isArray(input.lineItems) && input.lineItems.length ? input.lineItems : [{ quantity: input.quantity, skuId: input.skuId }];
  lineItems.forEach(line => {
    let remaining = Math.max(0, Math.floor(Number(line.quantity || 0) || 0));
    (RP.INV || [])
      .filter(row => inventorySkuMatches(row.sku, line.skuId) && row.loc === input.locationId && statusSet.has(String(row.status || 'Available').toLowerCase()))
      .forEach(row => {
        if (remaining <= 0) return;
        const used = Math.min(remaining, Number(row.qty || 0));
        if (!(used > 0)) return;
        row.qty -= used;
        remaining -= used;
        RP.LEDGER.unshift({
          id: `${ref}-${line.skuId}-${Math.random().toString(36).slice(2, 8)}`,
          date: new Date().toISOString(),
          type: 'Giveaway',
          sku: line.skuId,
          from: locName(input.locationId),
          fromLocationId: input.locationId,
          to: '',
          qty: used,
          ownerBefore: row.owner || 'Ranch Power',
          reasonCode: input.recipientType === 'staff' ? 'staff_promo_stock' : 'sales_giveaway',
          relatedId: ref,
          relatedType: 'inventory_giveaway',
          statusBefore: row.status || 'Available',
          user,
          ref,
          lot: row.lot || 'UNASSIGNED',
          note: [`Recipient: ${input.recipientName}.`, `Type: ${input.recipientType || 'other'}.`, `Reason: ${input.reason}.`, input.expectedOutcome ? `Expected outcome: ${input.expectedOutcome}.` : '', input.followUpDate ? `Follow-up: ${input.followUpDate}.` : '', input.notes || ''].filter(Boolean).join(' ').trim()
        });
      });
  });
  RP.INV = (RP.INV || []).filter(row => Number(row.qty || 0) > 0);
  return ref;
}

function GiveawayModal({ onClose, onAdded }) {
  const locations = inventoryLocationOptions();
  const [f, setF] = useState({
    expectedOutcome: '',
    followUpDate: '',
    locationId: locations[0] ? locations[0].id : '',
    notes: '',
    reason: 'New account sample',
    recipientKey: '',
    recipientMode: 'customer',
    recipientName: '',
    ref: ''
  });
  const [lines, setLines] = useState(() => [giveawayNewLine(locations[0] ? locations[0].id : '')]);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setF(state => ({ ...state, [key]: value }));
  const locationId = f.locationId;
  const skuOptions = giveawaySkuOptions(locationId);
  const recipientOptions = giveawayRecipientOptions();
  const selectedRecipient = recipientOptions.find(option => option.key === f.recipientKey) || null;
  const recipientType = f.recipientMode === 'staff' ? 'staff' : (selectedRecipient ? selectedRecipient.type : f.recipientMode === 'other' ? 'other' : 'customer');
  const staffName = typeof rpDisplayName === 'function' ? rpDisplayName() : 'Staff / rep';
  const recipientName = recipientType === 'staff' ? staffName : selectedRecipient ? selectedRecipient.name : f.recipientMode === 'other' ? f.recipientName.trim() : '';
  const customerId = selectedRecipient && selectedRecipient.type === 'customer' ? selectedRecipient.id : '';
  const prospectId = selectedRecipient && selectedRecipient.type === 'prospect' ? selectedRecipient.id : '';
  const lineItems = lines
    .map(line => ({
      quantity: Math.max(0, Math.floor(Number(line.quantity || 0) || 0)),
      skuId: line.skuId && skuOptions.some(sku => sku.id === line.skuId) ? line.skuId : ''
    }))
    .filter(line => line.skuId || line.quantity > 0);
  const quantityBySku = new Map();
  lineItems.forEach(line => quantityBySku.set(line.skuId, (quantityBySku.get(line.skuId) || 0) + line.quantity));
  const lineIssues = new Set(lineItems.filter(line => !line.skuId || !(line.quantity > 0) || (quantityBySku.get(line.skuId) || 0) > giveawayAvailableCases(line.skuId, locationId)).map(line => line.skuId || '__missing'));
  const totalQuantity = lineItems.reduce((sum, line) => sum + line.quantity, 0);
  const hasRationale = recipientType === 'staff' || Boolean(f.expectedOutcome.trim() || f.notes.trim());
  const valid = Boolean(locationId && lineItems.length && totalQuantity > 0 && !lineIssues.size && recipientName && hasRationale);

  useEffect(() => {
    setLines(current => current.length ? current.map(line => {
      if (line.skuId && skuOptions.some(sku => sku.id === line.skuId)) return line;
      return { ...line, skuId: skuOptions[0] ? skuOptions[0].id : '' };
    }) : [giveawayNewLine(locationId)]);
  }, [locationId, skuOptions.map(sku => sku.id).join('|')]);

  useEffect(() => {
    if (f.recipientMode === 'staff' && f.reason !== GIVEAWAY_STAFF_REASON) set('reason', GIVEAWAY_STAFF_REASON);
  }, [f.recipientMode]);

  const setLocation = value => {
    set('locationId', value);
    setLines([giveawayNewLine(value)]);
  };
  const setLine = (index, key, value) => setLines(current => current.map((line, lineIndex) => lineIndex === index ? { ...line, [key]: value } : line));
  const addLine = () => setLines(current => [...current, giveawayNewLine(locationId)]);
  const removeLine = index => setLines(current => current.length <= 1 ? current : current.filter((_, lineIndex) => lineIndex !== index));

  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      let ref = f.ref.trim();
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/inventory/giveaway', {
          method: 'POST',
          body: JSON.stringify({
            customerId: customerId || undefined,
            expectedOutcome: f.expectedOutcome.trim(),
            followUpDate: f.followUpDate || undefined,
            lineItems,
            locationId,
            notes: f.notes.trim(),
            prospectId: prospectId || undefined,
            reason: f.reason,
            recipientName,
            recipientType,
            ref: ref || undefined,
          })
        });
        ref = response.ref || ref;
        await rpRefreshAfterMutation(`Giveaway ${ref || 'logged'}`);
      } else {
        ref = addDemoGiveawayRows({ ...f, customerId, lineItems, locationId, prospectId, recipientName, recipientType });
      }
      toast(`Giveaway ${ref} logged - ${totalQuantity} cs`, 'ok');
      onAdded && onAdded(ref);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not log giveaway.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log Sales Giveaway" sub="Remove cases from inventory with recipient and rationale" onClose={onClose} width={780}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.55 }}><Icon name="package" size={15} />{busy ? 'Logging...' : `Log Giveaway - ${totalQuantity || 0} cs`}</button></>}>
      <MField label="Source location" required><select className="field" value={locationId} onChange={e => setLocation(e.target.value)}>{locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}</select></MField>
      <div className="card" style={{ boxShadow: 'none', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
          <span className="eyebrow">Products</span>
          <button className="btn btn-sm" type="button" onClick={addLine} disabled={!skuOptions.length}><Icon name="plus" size={14} />Add Product</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {lines.map((line, index) => {
            const rowSkuId = line.skuId && skuOptions.some(sku => sku.id === line.skuId) ? line.skuId : '';
            const rowQuantity = Math.max(0, Math.floor(Number(line.quantity || 0) || 0));
            const rowAvailable = rowSkuId ? giveawayAvailableCases(rowSkuId, locationId) || 0 : 0;
            const requested = quantityBySku.get(rowSkuId) || 0;
            const over = rowSkuId ? requested > rowAvailable : true;
            return (
              <div key={index} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 110px 90px 42px', gap: 10, alignItems: 'center', padding: '11px 12px', borderBottom: index < lines.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
                <select className="field" value={rowSkuId} onChange={e => setLine(index, 'skuId', e.target.value)} disabled={!skuOptions.length} style={tried && !rowSkuId ? { borderColor: 'var(--danger)' } : undefined}>
                  {skuOptions.map(sku => <option key={sku.id} value={sku.id}>{rpSkuDisplayName(sku)}</option>)}
                  {!skuOptions.length && <option value="">No giveaway stock here</option>}
                </select>
                <input className="field mono" type="number" min="1" step="1" value={line.quantity} onChange={e => setLine(index, 'quantity', e.target.value)} style={{ textAlign: 'right', borderColor: tried && (!(rowQuantity > 0) || over) ? 'var(--danger)' : undefined }} />
                <span className="mono" style={{ color: over ? 'var(--danger)' : 'var(--faint)', fontSize: 11.5, fontWeight: 800, textAlign: 'right' }}>{rowAvailable} cs</span>
                <button className="btn btn-sm" type="button" disabled={lines.length <= 1} onClick={() => removeLine(index)} style={{ padding: 0, justifyContent: 'center' }}><Icon name="x" size={14} /></button>
              </div>
            );
          })}
        </div>
      </div>
      <MField label="Recipient type" required>
        <Segmented size="sm" options={[{ value: 'customer', label: 'Customer / Prospect' }, { value: 'staff', label: 'Staff / Rep' }, { value: 'other', label: 'Other' }]} value={f.recipientMode} onChange={value => set('recipientMode', value)} />
      </MField>
      {f.recipientMode === 'customer' && <MField label="Customer / prospect" required><select className="field" value={f.recipientKey} onChange={e => set('recipientKey', e.target.value)} style={tried && !recipientName ? { borderColor: 'var(--danger)' } : undefined}><option value="">Select customer or prospect</option>{recipientOptions.map(option => <option key={option.key} value={option.key}>{option.name}{option.detail ? ` - ${option.detail}` : ''}</option>)}</select></MField>}
      {f.recipientMode === 'staff' && <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 13px', marginBottom: 12, fontSize: 12.5, color: 'var(--muted)' }}><b style={{ color: 'var(--ink)' }}>{staffName}</b> will be recorded as the recipient for staff promo stock.</div>}
      {f.recipientMode === 'other' && <MField label="Recipient name" required><input className="field" value={recipientName} onChange={e => set('recipientName', e.target.value)} placeholder="Event, buyer, walk-in, or organization" style={tried && !recipientName ? { borderColor: 'var(--danger)' } : undefined} /></MField>}
      <div style={{ display: 'grid', gridTemplateColumns: f.recipientMode === 'staff' ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
        <MField label="Reason" required><select className="field" value={f.reason} onChange={e => set('reason', e.target.value)}>{GIVEAWAY_REASONS.map(reason => <option key={reason}>{reason}</option>)}</select></MField>
        {f.recipientMode !== 'staff' && <MField label="Follow-up date"><input className="field" type="date" value={f.followUpDate} onChange={e => set('followUpDate', e.target.value)} /></MField>}
      </div>
      {f.recipientMode !== 'staff' && <MField label="Expected outcome" required={!f.notes.trim()}><input className="field" value={f.expectedOutcome} onChange={e => set('expectedOutcome', e.target.value)} placeholder="Example: win first order next week, secure cooler placement, save reorder..." style={tried && !hasRationale ? { borderColor: 'var(--danger)' } : undefined} /></MField>}
      <MField label={f.recipientMode === 'staff' ? 'Notes' : 'Notes / reasoning'} required={f.recipientMode !== 'staff' && !f.expectedOutcome.trim()}><textarea className="field" rows="3" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder={f.recipientMode === 'staff' ? 'Optional promo-use context...' : 'Why this giveaway makes sense, who approved verbally, what the next step is...'} style={{ resize: 'vertical', ...(tried && !hasRationale ? { borderColor: 'var(--danger)' } : {}) }} /></MField>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: lineIssues.size ? 'var(--danger)' : 'var(--muted)', fontSize: 12.5 }}>
        <span>{skuOptions.length ? `${skuOptions.length} giveaway-eligible products at ${locName(locationId)}.` : 'No giveaway-eligible stock at this location.'}</span>
        <span className="mono">{f.ref.trim() || 'Auto ref'}</span>
      </div>
      {tried && !valid && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Choose available stock and a recipient. Customer/prospect giveaways need expected outcome or notes; staff promo stock does not.</div>}
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function receiveDateToday() {
  return typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10);
}

function receiveLocalDateTime() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function desktopReceiveIdentifier(value) {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9._:-]/g, '');
}

function desktopReceiveTransferRef() {
  return window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `TO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`
    : `TO-${Date.now().toString(36).toUpperCase()}`;
}

function desktopReceiveHistoricalRef() {
  return window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `RCV-HIST-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 10).toUpperCase()}`
    : `RCV-HIST-${Date.now().toString(36).toUpperCase()}`;
}

function desktopReceiveAcknowledgementRef() {
  return window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `RCV-ACK-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 10).toUpperCase()}`
    : `RCV-ACK-${Date.now().toString(36).toUpperCase()}`;
}

function desktopUnacknowledgedInboundTransfers(toLocationId) {
  const acknowledged = new Set((RP.FIELD_RECEIPTS || [])
    .map(receipt => desktopReceiveIdentifier(receipt && receipt.linkedTransferRef))
    .filter(Boolean));
  return (RP.LOGISTICS_MOVEMENTS || [])
    .filter(row => row && !row.reconciliationOnly && row.movementType === 'inventory_transfer' && row.toLocationId === toLocationId && row.id && !acknowledged.has(desktopReceiveIdentifier(row.id)))
    .filter(row => Array.isArray(row.lines) && row.lines.some(line => Number(line.quantity || 0) > 0))
    .slice()
    .sort((a, b) => String(b.occurredAt || b.createdAt || '').localeCompare(String(a.occurredAt || a.createdAt || '')) || String(a.id).localeCompare(String(b.id)));
}

function addDemoReceiptRows(input) {
  const ref = input.ref || `RCV-${Date.now().toString().slice(-6)}`;
  const user = typeof rpDisplayName === 'function' ? rpDisplayName() : 'Ranch Power';
  const loc = RP.loc(input.toLocationId);
  const locNameText = loc ? loc.name : input.toLocationId;
  const lot = input.lot || 'UNASSIGNED';
  const addBalance = (status, quantity) => {
    if (!(quantity > 0)) return;
    const existing = RP.INV.find(row => inventorySkuMatches(row.sku, input.skuId) && row.loc === input.toLocationId && row.owner === 'Ranch Power' && row.status === status && row.lot === lot);
    if (existing) {
      existing.qty += quantity;
      existing.exp = existing.exp || input.expiresAt || '';
      existing.lotExpiresAt = existing.lotExpiresAt || input.expiresAt || '';
      existing.lotProducedAt = existing.lotProducedAt || input.producedAt || '';
      existing.lotReceivedAt = existing.lotReceivedAt || input.receivedAt || '';
    } else {
      RP.INV.push({
        sku: input.skuId,
        loc: input.toLocationId,
        owner: 'Ranch Power',
        status,
        lot,
        exp: input.expiresAt || '',
        lotExpiresAt: input.expiresAt || '',
        lotProducedAt: input.producedAt || '',
        lotReceivedAt: input.receivedAt || '',
        qty: quantity
      });
    }
    RP.LEDGER.unshift({
      date: new Date().toISOString(),
      type: status === 'Damaged' ? 'Damage' : 'Receipt',
      sku: input.skuId,
      from: '',
      to: locNameText,
      qty: quantity,
      user,
      ref,
      lot,
      note: input.notes || '',
      lotExpiresAt: input.expiresAt || '',
      lotProducedAt: input.producedAt || '',
      lotReceivedAt: input.receivedAt || ''
    });
  };
  addBalance('Available', input.quantity);
  addBalance('Damaged', input.damagedQuantity);
  return ref;
}

function ReceiveInventoryModal({ onClose, onAdded, presetLocationId }) {
  const receiveSkus = rpInventorySkus();
  const locations = inventoryLocationOptions();
  const initialLocation = locations.find(location => location.id === presetLocationId)
    || locations.find(location => desktopUnacknowledgedInboundTransfers(location.id).length)
    || locations[0]
    || null;
  const authRole = typeof rpNormalizeRole === 'function' ? rpNormalizeRole((rpAuthProfile() || {}).role) : '';
  const canHistoricalOpening = ['admin', 'leadership', 'ops'].includes(authRole);
  const [mode, setMode] = useState('planned');
  const [selectedTransferRef, setSelectedTransferRef] = useState(() => initialLocation
    ? ((desktopUnacknowledgedInboundTransfers(initialLocation.id)[0] || {}).id || '')
    : '');
  const [f, setF] = useState({
    acknowledgementRef: desktopReceiveAcknowledgementRef(), bol: '', carrier: '', chepUsage: '', expiresAt: '', expectedPallets: '', handledBy: '', historicalOpening: false,
    historicalRef: desktopReceiveHistoricalRef(), lot: '', movementMethod: 'internal_transfer', notes: '', occurredAt: receiveLocalDateTime(),
    pallets: '', producedAt: '', receivedAt: receiveDateToday(), ref: desktopReceiveTransferRef(), sourceLocationId: '', trackingNumber: '',
    toLocationId: initialLocation ? initialLocation.id : ''
  });
  const [lines, setLines] = useState(() => receiveSkus.map(sku => ({ skuId: sku.id, quantity: '', damagedQuantity: '', lot: '', producedAt: '', expiresAt: '' })));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setF(state => ({ ...state, [key]: value }));
  const setLine = (skuId, key, value) => setLines(rows => rows.map(row => row.skuId === skuId ? { ...row, [key]: value } : row));
  const clearLines = () => setLines(rows => rows.map(row => ({ ...row, quantity: '', damagedQuantity: '', lot: '', producedAt: '', expiresAt: '' })));
  const isPlanned = mode === 'planned';
  const isUnplanned = mode === 'unplanned';
  const isHistorical = mode === 'historical';
  const inboundTransfers = desktopUnacknowledgedInboundTransfers(f.toLocationId);
  const selectedTransfer = inboundTransfers.find(row => row.id === selectedTransferRef) || null;
  const selectedRef = selectedTransfer ? selectedTransfer.id : '';
  const cleanNumber = value => Number(value || 0);
  const sharedLot = desktopReceiveIdentifier(f.lot);
  const editableLines = lines.map(line => ({
    ...line,
    damagedQuantity: isHistorical ? cleanNumber(line.damagedQuantity) : 0,
    expiresAt: line.expiresAt || f.expiresAt || null,
    lot: rpResolvePrintedLotInput(line.skuId, desktopReceiveIdentifier(line.lot) || sharedLot),
    producedAt: line.producedAt || f.producedAt || null,
    quantity: cleanNumber(line.quantity),
    receivedAt: f.receivedAt || null
  })).filter(line => line.quantity > 0 || line.damagedQuantity > 0);
  const plannedLines = selectedTransfer ? (selectedTransfer.lines || []).map((line, index) => {
    const master = (RP.LOTS || []).find(lot => inventorySkuMatches(lot.sku, line.skuId) && lot.lot === line.lot) || {};
    return {
      damagedQuantity: 0,
      expiresAt: master.expiresAt || null,
      key: `${line.skuId}|${line.lot}|${index}`,
      lot: desktopReceiveIdentifier(line.lot),
      lotGroup: line.lotGroup || master.lotGroup || '',
      producedAt: master.producedAt || null,
      quantity: cleanNumber(line.quantity),
      receivedAt: f.receivedAt || null,
      skuId: line.skuId
    };
  }).filter(line => line.quantity > 0) : [];
  const activeLines = isPlanned ? plannedLines : editableLines;
  const sourceLocationId = isPlanned ? (selectedTransfer ? selectedTransfer.fromLocationId : '') : isHistorical ? '' : f.sourceLocationId;
  const movementMethod = isPlanned ? (selectedTransfer ? selectedTransfer.movementMethod : '') : isHistorical ? 'external_receipt' : f.movementMethod;
  const handledBy = isPlanned ? (selectedTransfer ? selectedTransfer.handledBy : '') : f.handledBy;
  const carrier = isPlanned ? (selectedTransfer ? selectedTransfer.carrier : '') : f.carrier;
  const bol = isPlanned ? (selectedTransfer ? selectedTransfer.bol : '') : f.bol;
  const trackingNumber = isPlanned ? (selectedTransfer ? selectedTransfer.trackingNumber : '') : f.trackingNumber;
  const chepUsage = isPlanned ? (selectedTransfer ? selectedTransfer.chepUsage : '') : f.chepUsage;
  const palletCount = isPlanned ? (selectedTransfer ? Number(selectedTransfer.chepPallets || 0) : 0) : Number(f.pallets || 0);
  const occurredAt = isPlanned ? (selectedTransfer ? selectedTransfer.occurredAt : '') : f.occurredAt;
  const totalQuantity = activeLines.reduce((sum, line) => sum + line.quantity, 0);
  const totalDamaged = activeLines.reduce((sum, line) => sum + line.damagedQuantity, 0);
  const hasInvalidQuantity = !isPlanned && lines.some(line => {
    const quantity = cleanNumber(line.quantity);
    const damaged = cleanNumber(line.damagedQuantity);
    return !Number.isInteger(quantity) || quantity < 0 || !Number.isInteger(damaged) || damaged < 0;
  });
  const missingLot = activeLines.some(line => !line.lot || ['UNASSIGNED', 'CYCLE_COUNT'].includes(line.lot));
  const sameLocation = Boolean(sourceLocationId && sourceLocationId === f.toLocationId);
  const pickupNeedsHandler = isUnplanned && ['direct_pickup', 'rep_pickup'].includes(movementMethod) && !handledBy.trim();
  const freightNeedsCarrier = isUnplanned && ['carrier_freight', 'courier'].includes(movementMethod) && !carrier.trim();
  const historicalInvalid = isHistorical && (!canHistoricalOpening || !f.historicalOpening || f.notes.trim().length < 20);
  const palletsInvalid = !isPlanned && (!Number.isInteger(Number(f.pallets || 0)) || Number(f.pallets || 0) < 0 || !Number.isInteger(Number(f.expectedPallets || 0)) || Number(f.expectedPallets || 0) < 0);
  const chepInvalid = isUnplanned && (!['used', 'not_used'].includes(chepUsage) || (chepUsage === 'used' ? !(palletCount > 0) : palletCount !== 0));
  const handoffInvalid = isUnplanned && !occurredAt;
  const refInvalid = isUnplanned && (!f.ref.startsWith('TO-') || f.ref !== desktopReceiveIdentifier(f.ref));
  const plannedInvalid = isPlanned && (!selectedTransfer || selectedTransfer.toLocationId !== f.toLocationId || !activeLines.length);
  const valid = Boolean(f.toLocationId && f.receivedAt && activeLines.length && !hasInvalidQuantity && !missingLot && !sameLocation && !pickupNeedsHandler && !freightNeedsCarrier && !historicalInvalid && !palletsInvalid && !chepInvalid && !handoffInvalid && !refInvalid && !plannedInvalid && (isPlanned || isHistorical || sourceLocationId));

  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      const requestedRef = isPlanned ? f.acknowledgementRef : isHistorical ? f.historicalRef : f.ref;
      let response = { ref: requestedRef };
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        response = await rpApi('/api/inventory/receive', {
          method: 'POST',
          body: JSON.stringify({
            bol: bol.trim() || undefined,
            carrier: carrier.trim() || undefined,
            chepPalletCount: sourceLocationId ? palletCount : undefined,
            chepUsage: sourceLocationId ? chepUsage : undefined,
            expectedPallets: isUnplanned && f.expectedPallets ? Number(f.expectedPallets) : undefined,
            handledBy: handledBy.trim() || undefined,
            historicalOpening: isHistorical ? f.historicalOpening : false,
            lineItems: activeLines.map(line => ({ damagedQuantity: line.damagedQuantity, expiresAt: line.expiresAt, lot: line.lot, producedAt: line.producedAt, quantity: line.quantity, receivedAt: line.receivedAt, skuId: line.skuId })),
            linkedTransferRef: isPlanned ? selectedRef : undefined,
            movementMethod,
            notes: f.notes.trim(),
            occurredAt: sourceLocationId && occurredAt ? new Date(occurredAt).toISOString() : undefined,
            pallets: sourceLocationId ? palletCount : undefined,
            receivedAt: f.receivedAt || null,
            recordUnplannedTransfer: isUnplanned ? true : undefined,
            ref: requestedRef,
            sourceLocationId: sourceLocationId || undefined,
            toLocationId: f.toLocationId,
            trackingNumber: trackingNumber.trim() || undefined
          })
        });
        await rpRefreshAfterMutation(`${isPlanned ? 'Transfer' : 'Inventory'} ${response.ref || selectedRef || requestedRef}`);
      } else if (isPlanned) {
        const demoReceipt = { id: f.acknowledgementRef, linkedTransferRef: selectedRef, location: f.toLocationId, sourceLocationId, receiptMode: 'transfer_acknowledgement', status: 'Approved', totalCases: totalQuantity };
        if (RP.upsertFieldReceipt) RP.upsertFieldReceipt(demoReceipt);
        else if (Array.isArray(RP.FIELD_RECEIPTS)) RP.FIELD_RECEIPTS.unshift(demoReceipt);
      } else {
        activeLines.forEach(line => addDemoReceiptRows({ damagedQuantity: line.damagedQuantity, expiresAt: line.expiresAt || '', lot: line.lot, notes: f.notes.trim(), producedAt: line.producedAt || '', quantity: line.quantity, receivedAt: line.receivedAt || '', ref: requestedRef, skuId: line.skuId, toLocationId: f.toLocationId }));
      }
      const finalRef = response.ref || selectedRef || requestedRef;
      toast(isPlanned ? `Transfer ${selectedRef} acknowledged - ${totalQuantity.toLocaleString()} exact cases` : `${isHistorical ? 'Historical opening' : 'Handoff'} ${finalRef} recorded - ${(totalQuantity + totalDamaged).toLocaleString()} cases`, totalDamaged ? 'warn' : 'ok');
      onAdded && onAdded(finalRef);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not receive inventory.');
    } finally {
      setBusy(false);
    }
  };

  const modeButton = (value, label, danger) => <button type="button" className={`btn btn-sm${mode === value ? ' btn-primary' : ''}`} onClick={() => { setMode(value); setTried(false); setError(''); }} disabled={value === 'historical' && !canHistoricalOpening} style={danger && mode !== value ? { color: 'var(--amber-deep)' } : undefined}>{label}</button>;
  return (
    <Modal title="Receive Inventory" sub="Acknowledge a signed completed transfer by default; record a new handoff only when no transfer exists" onClose={onClose} width={980}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn" onClick={clearLines} disabled={busy || isPlanned}><Icon name="x" size={15} />Clear Lines</button><button className="btn btn-primary" disabled={busy || !valid} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name={isPlanned ? 'check' : 'plus'} size={15} />{busy ? 'Saving...' : isPlanned ? 'Acknowledge Exact Transfer' : isHistorical ? 'Post Historical Opening' : 'Record Handoff Now'}</button></>}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        {modeButton('planned', 'Choose Recorded Inbound Transfer')}
        {modeButton('unplanned', 'No transfer was recorded - record this handoff now', true)}
        {modeButton('historical', 'Privileged Historical Opening')}
      </div>
      {isPlanned && <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 10, padding: '11px 13px', marginBottom: 14, fontSize: 12.5, fontWeight: 650 }}>This acknowledgement verifies the exact signed source, destination, SKU, lot, case, carrier, and CHEP evidence. It does not move inventory a second time.</div>}
      {isUnplanned && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', marginBottom: 14, fontSize: 12.5, fontWeight: 700 }}>Use this only when the physical handoff occurred but no transfer was recorded. Saving creates one completed TO-* custody transfer now.</div>}
      <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--surface-2)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(190px,1fr))', gap: 12 }}>
          <MField label="Receiving Into" required><select className="field" value={f.toLocationId} onChange={e => { const nextLocationId = e.target.value; set('toLocationId', nextLocationId); setSelectedTransferRef(((desktopUnacknowledgedInboundTransfers(nextLocationId)[0] || {}).id || '')); }} style={tried && (!f.toLocationId || sameLocation) ? { borderColor: 'var(--danger)' } : undefined}>{locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}</select></MField>
          {isPlanned ? <MField label="Completed Inbound Transfer" required><select className="field mono" value={selectedRef} onChange={e => setSelectedTransferRef(e.target.value)} style={tried && !selectedTransfer ? { borderColor: 'var(--danger)' } : undefined}><option value="">Select unacknowledged transfer</option>{inboundTransfers.map(row => <option key={row.id} value={row.id}>{row.id} - {row.caseCount || row.lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0)} cases - {String(row.occurredAt || '').slice(0, 10)}</option>)}</select></MField> : <MField label={isHistorical ? 'Historical Opening ID' : 'Transfer ID'} required><input className="field mono" value={isHistorical ? f.historicalRef : f.ref} onChange={e => set(isHistorical ? 'historicalRef' : 'ref', desktopReceiveIdentifier(e.target.value))} style={tried && refInvalid ? { borderColor: 'var(--danger)' } : undefined} /></MField>}
          <MField label="Received Date" required><input className="field" type="date" value={f.receivedAt} onChange={e => set('receivedAt', e.target.value)} style={tried && !f.receivedAt ? { borderColor: 'var(--danger)' } : undefined} /></MField>
        </div>
        {isPlanned && !inboundTransfers.length && <div style={{ color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 700, marginTop: 4 }}>No unacknowledged completed inbound transfers are available for this destination. Confirm the destination, or deliberately choose the unplanned-handoff mode.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(180px,1fr))', gap: 12, marginTop: 4 }}>
          <MField label="Received From" required={!isHistorical}>{isPlanned ? <input className="field" readOnly value={sourceLocationId ? inventoryLocationName(sourceLocationId) : ''} /> : isHistorical ? <input className="field" readOnly value="No source - evidence-backed historical opening" /> : <select className="field" value={f.sourceLocationId} onChange={e => set('sourceLocationId', e.target.value)}><option value="">Select recorded source</option>{locations.filter(location => location.id !== f.toLocationId).map(location => <option key={location.id} value={location.id}>{location.name}</option>)}</select>}</MField>
          <MField label="Movement"><select className="field" disabled={isPlanned || isHistorical} value={movementMethod || 'internal_transfer'} onChange={e => set('movementMethod', e.target.value)}><option value="carrier_freight">Carrier / Freight</option><option value="courier">Courier</option><option value="direct_pickup">Direct Pickup</option><option value="rep_pickup">Rep Pickup</option><option value="internal_transfer">Internal Transfer</option>{isHistorical && <option value="external_receipt">External Receipt</option>}</select></MField>
          <MField label="Actual Handoff Time" required={isUnplanned}>{isPlanned ? <input className="field" readOnly value={occurredAt ? rpFormatLocalDateTime(occurredAt) : '-'} /> : <input className="field" type="datetime-local" disabled={isHistorical} value={f.occurredAt} onChange={e => set('occurredAt', e.target.value)} style={tried && handoffInvalid ? { borderColor: 'var(--danger)' } : undefined} />}</MField>
          <MField label="Handled By"><input className="field" readOnly={isPlanned} disabled={isHistorical} value={handledBy} onChange={e => set('handledBy', e.target.value)} placeholder="Driver / rep" /></MField>
          <MField label="Carrier"><input className="field" readOnly={isPlanned} disabled={isHistorical} value={carrier} onChange={e => set('carrier', e.target.value)} placeholder="Required for freight" style={tried && freightNeedsCarrier ? { borderColor: 'var(--danger)' } : undefined} /></MField>
          <MField label="BOL"><input className="field mono" readOnly={isPlanned} disabled={isHistorical} value={bol} onChange={e => set('bol', desktopReceiveIdentifier(e.target.value))} placeholder="Bill of lading" /></MField>
          <MField label="Tracking / PRO"><input className="field mono" readOnly={isPlanned} disabled={isHistorical} value={trackingNumber} onChange={e => set('trackingNumber', desktopReceiveIdentifier(e.target.value))} placeholder="Optional" /></MField>
          <MField label="CHEP Used" required={isUnplanned}><select className="field" disabled={isPlanned || isHistorical} value={chepUsage} onChange={e => { set('chepUsage', e.target.value); if (e.target.value === 'not_used') set('pallets', ''); }} style={tried && chepInvalid ? { borderColor: 'var(--danger)' } : undefined}>{isPlanned && !['used', 'not_used'].includes(chepUsage) && <option value={chepUsage}>{chepUsage || 'Not recorded'}</option>}<option value="">Select Yes / No</option><option value="used">Yes - CHEP used</option><option value="not_used">No CHEP pallets</option></select></MField>
          <MField label="CHEP Pallet Count"><input className="field" type="number" min="0" step="1" readOnly={isPlanned} disabled={isHistorical || (isUnplanned && chepUsage !== 'used')} value={isPlanned ? palletCount : f.pallets} onChange={e => set('pallets', e.target.value)} /></MField>
          <MField label="Expected CHEP"><input className="field" type="number" min="0" step="1" disabled={isPlanned || isHistorical} value={f.expectedPallets} onChange={e => set('expectedPallets', e.target.value)} /></MField>
          {!isPlanned && <><MField label="Shared Lot" required><input className="field mono" value={f.lot} onChange={e => set('lot', desktopReceiveIdentifier(e.target.value))} placeholder="Exact BevMax lot code" /></MField><MField label="Produced Date"><input className="field" type="date" value={f.producedAt} onChange={e => set('producedAt', e.target.value)} /></MField><MField label="Expiry Date"><input className="field" type="date" value={f.expiresAt} onChange={e => set('expiresAt', e.target.value)} /></MField></>}
          <MField label="Notes"><input className="field" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder={isHistorical ? 'Documentary evidence reference (required)' : 'Handoff or receiving note'} /></MField>
        </div>
      </div>
      {isHistorical && <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', margin: '-4px 0 14px', border: '1px solid var(--warn)', borderRadius: 10, background: 'var(--warn-bg)' }}><input type="checkbox" checked={f.historicalOpening} onChange={e => set('historicalOpening', e.target.checked)} style={{ marginTop: 2 }} /><span><b style={{ display: 'block', fontSize: 13 }}>I am creating an evidence-backed historical opening, not new production</b><span style={{ display: 'block', marginTop: 2, fontSize: 11.5, color: 'var(--muted)' }}>Use only for inventory proven to predate this ledger; include the evidence reference in Notes.</span></span></label>}
      <ReqMsg show={tried && plannedInvalid}>Choose an unacknowledged completed inbound transfer with signed SKU/lot lines for this destination.</ReqMsg>
      <ReqMsg show={tried && !f.receivedAt}>Enter the date this inventory was received.</ReqMsg>
      <ReqMsg show={tried && isUnplanned && !sourceLocationId}>Choose the custody location that sent this inventory.</ReqMsg>
      <ReqMsg show={tried && historicalInvalid}>Historical openings require privilege, explicit confirmation, and an evidence note of at least 20 characters.</ReqMsg>
      <ReqMsg show={tried && freightNeedsCarrier}>Freight and courier handoffs require the carrier name.</ReqMsg>
      <ReqMsg show={tried && pickupNeedsHandler}>Direct and rep pickups require the driver or rep name.</ReqMsg>
      <ReqMsg show={tried && (palletsInvalid || chepInvalid)}>Confirm CHEP use with a valid whole pallet count.</ReqMsg>
      <ReqMsg show={tried && handoffInvalid}>Enter the actual completed handoff time.</ReqMsg>
      <div className="card" style={{ overflow: 'hidden', marginBottom: 12 }}>
        <table className="rp-table" style={{ tableLayout: 'fixed' }}>
          <thead><tr><th style={{ width: '34%' }}>Product</th><th className="num" style={{ width: '12%' }}>{isPlanned ? 'Signed Cases' : 'Sellable'}</th>{isHistorical && <th className="num" style={{ width: '12%' }}>Damaged</th>}<th style={{ width: '18%' }}>Lot</th><th style={{ width: '14%' }}>Produced</th><th style={{ width: '14%' }}>Expiry</th></tr></thead>
          <tbody>{isPlanned ? activeLines.map(line => { const sku = receiveSkus.find(item => inventorySkuMatches(item.id, line.skuId)) || RP.sku(line.skuId) || line.skuId; return <tr key={line.key}><td><div style={{ fontWeight: 700 }}>{rpSkuDisplayName(rpSkuItem(sku) || 'Unknown product')}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{rpSkuExternalIdentity(rpSkuItem(sku) || '')}{line.lotGroup ? ` - ${line.lotGroup}` : ''}</div></td><td className="mono num" style={{ fontWeight: 800 }}>{line.quantity.toLocaleString()}</td><td className="mono" style={{ overflowWrap: 'anywhere' }}>{rpPrintedLotLabel(line.skuId, line.lot)}</td><td>{line.producedAt || '-'}</td><td>{line.expiresAt || '-'}</td></tr>; }) : receiveSkus.map(sku => { const row = lines.find(line => line.skuId === sku.id) || {}; return <tr key={sku.id}><td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={sku} size={24} /><div><div style={{ fontWeight: 700 }}>{rpSkuDisplayName(sku)}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{rpSkuExternalIdentity(sku)}</div></div></div></td><td><input className="field mono" type="number" min="0" step="1" value={row.quantity || ''} onChange={e => setLine(sku.id, 'quantity', e.target.value)} style={{ textAlign: 'right' }} /></td>{isHistorical && <td><input className="field mono" type="number" min="0" step="1" value={row.damagedQuantity || ''} onChange={e => setLine(sku.id, 'damagedQuantity', e.target.value)} style={{ textAlign: 'right' }} /></td>}<td><input className="field mono" placeholder={sharedLot || 'Required'} value={row.lot || ''} onBlur={e => setLine(sku.id, 'lot', rpResolvePrintedLotInput(sku.id, desktopReceiveIdentifier(e.target.value)))} onChange={e => setLine(sku.id, 'lot', desktopReceiveIdentifier(e.target.value))} /></td><td><input className="field" type="date" value={row.producedAt || ''} onChange={e => setLine(sku.id, 'producedAt', e.target.value)} /></td><td><input className="field" type="date" value={row.expiresAt || ''} onChange={e => setLine(sku.id, 'expiresAt', e.target.value)} /></td></tr>; })}</tbody>
        </table>
      </div>
      {!isHistorical && <div style={{ background: 'var(--surface-2)', borderRadius: 9, padding: '10px 12px', color: 'var(--muted)', fontSize: 12.5 }}>Acknowledge or record the full exact handoff first. If any cases are damaged, use Report Damage / Loss afterward so the signed transfer remains exact.</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10, color: 'var(--muted)', fontSize: 12.5 }}><span>{activeLines.length ? `${activeLines.length} SKU/lot lines - ${totalQuantity.toLocaleString()} sellable${isHistorical ? `, ${totalDamaged.toLocaleString()} damaged` : ''}` : 'No cases loaded.'}</span>{isPlanned && <span className="mono">{selectedRef || 'Select transfer'}</span>}</div>
      {tried && !activeLines.length && <ReqMsg show>Enter or load at least one case.</ReqMsg>}
      {tried && missingLot && <ReqMsg show>Every line requires the exact finished-goods lot.</ReqMsg>}
      {tried && hasInvalidQuantity && <ReqMsg show>Case quantities must be non-negative whole numbers.</ReqMsg>}
      {sameLocation && <ReqMsg show>Source and destination must differ.</ReqMsg>}
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function inventoryLotTone(status) {
  if (status === 'Verified' || status === 'Released') return { background: 'var(--ok-bg)', color: 'var(--ok)' };
  if (status === 'Conflicted' || status === 'Quarantined') return { background: 'var(--danger-bg)', color: 'var(--danger)' };
  if (status === 'QA Hold') return { background: 'var(--warn-bg)', color: 'var(--amber-deep)' };
  return { background: 'var(--surface-2)', color: 'var(--muted)' };
}

function inventoryLotManagementAllowed() {
  return ['admin', 'leadership', 'ops', 'warehouse'].includes(rpAuthProfile().role);
}

function inventoryLotIdentity(lot) {
  // Lot masters include loose-can stock. Case catalog aliases must never change
  // the product shown for an existing record.
  const product = (RP.SKUS || []).find(row => row.id === lot.sku);
  const isCan = String(product && product.unit || '').toLowerCase() === 'can' || /^RP-CAN-/.test(lot.sku);
  const unit = isCan ? 'Loose cans' : String(product && product.unit || '').toLowerCase() === 'case' || /^RP-(12|MIX)-/.test(lot.sku) ? 'Cases' : product && product.unit || 'Unit unconfirmed';
  const labels = (RP.PRINTED_LOT_METADATA || []).filter(row => row.skuId === lot.sku && row.lot === lot.lot && typeof row.printedLot === 'string' && /^[A-Z0-9._:-]{1,80}$/.test(row.printedLot));
  const printed = labels.length === 1 ? labels[0] : null;
  // The generated shape alone is not proof. Require the exact open-can batch
  // and its recorded correction reference before describing source provenance.
  const generated = isCan && /^([A-Z0-9._:-]+)-OPEN-([A-Z0-9]{12})$/.exec(lot.lot);
  const batches = generated ? (RP.OPEN_CASE_BATCHES || []).filter(row => row.canSkuId === lot.sku && row.lot === lot.lot
    && row.inventoryStatus === 'Open Repack Stock' && row.eventLabel === `Repack correction RPK-${generated[2]}` && row.caseSkuId) : [];
  const sourceSkus = [...new Set(batches.map(row => row.caseSkuId))];
  const openSource = sourceSkus.length === 1 ? { sku: sourceSkus[0], lot: generated[1], repackId: `RPK-${generated[2]}` } : null;
  if (openSource) {
    const sourceLabels = (RP.PRINTED_LOT_METADATA || []).filter(row => row.skuId === openSource.sku && row.lot === openSource.lot && typeof row.printedLot === 'string' && /^[A-Z0-9._:-]{1,80}$/.test(row.printedLot));
    openSource.printedLot = sourceLabels.length === 1 ? sourceLabels[0].printedLot : '';
  }
  const externalIdentity = product ? rpSkuExternalIdentity(product) : '';
  return { product, isCan, unit, printed, openSource, externalIdentity, productLabel: `${product ? rpSkuDisplayName(product) : lot.sku} · ${externalIdentity && externalIdentity !== lot.sku ? `${externalIdentity} · recorded SKU ${lot.sku}` : lot.sku} · ${unit}` };
}

function InventoryLotsTable({ rows, onSelect }) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <table className="rp-table">
        <thead><tr><th>Product</th><th>Lot</th><th>Group</th><th>Origin</th><th>Produced</th><th>Expires</th><th>Lifecycle</th><th>Date Control</th></tr></thead>
        <tbody>
          {rows.map(row => { const identity = inventoryLotIdentity(row); return (
            <tr key={row.id || `${row.sku}-${row.lot}`} onClick={() => onSelect && onSelect(row)} style={{ cursor: 'pointer' }}>
              <td style={{ overflowWrap: 'anywhere' }}><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FlavorChip sku={identity.product || row.sku} size={22} /><div><div style={{ fontWeight: 650 }}>{identity.product ? rpSkuDisplayName(identity.product) : row.sku}</div><div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{identity.externalIdentity || row.sku} · {identity.unit}</div>{identity.externalIdentity && identity.externalIdentity !== row.sku && <div style={{ fontSize: 11, color: 'var(--faint)' }}>Recorded SKU: {row.sku}</div>}</div></div></td>
              <td style={{ overflowWrap: 'anywhere' }}><div className="mono" style={{ fontWeight: 700 }}>{identity.printed ? identity.printed.printedLot : identity.openSource ? `Loose cans from ${identity.openSource.printedLot || identity.openSource.lot}` : row.lot}</div>{identity.openSource && <div className="mono" style={{ fontSize: 11.5, color: 'var(--muted)' }}>Recorded lot: {row.lot}</div>}{identity.openSource && <div style={{ fontSize: 11.5 }}>Recovered through {identity.openSource.repackId}</div>}{onSelect && inventoryLotManagementAllowed() && <button className="btn btn-sm" style={{ display: 'block', marginTop: 6 }} aria-label={`Edit dates for ${row.sku}, lot ${row.lot}`} onClick={event => { event.stopPropagation(); onSelect(row); }}>Edit</button>}</td>
              <td>{row.lotGroup || '-'}</td>
              <td>{inventoryLocationName(row.originLocationId) || '-'}</td>
              <td className="mono" style={{ color: 'var(--muted)' }}>{row.producedAt || '-'}</td>
              <td className="mono" style={{ color: row.expiresAt && row.expiresAt < receiveDateToday() ? 'var(--danger)' : 'var(--muted)' }}>{row.expiresAt || '-'}{row.expiryConfirmationId && <div style={{ fontSize: 11 }}>Expiry confirmed</div>}</td>
              <td><span className="pill" style={inventoryLotTone(row.lifecycleStatus)}>{row.lifecycleStatus || 'Unverified'}</span></td>
              <td><span className="pill" style={inventoryLotTone(row.dateStatus)}>{row.dateStatus || 'Unverified'}</span></td>
            </tr>
          ); })}
          {!rows.length && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 32 }}>No lots match these filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function inventoryExpiryStorageKey(lot) {
  return `rp-inventory-expiry:${JSON.stringify([rpAuthProfile().id, lot.sku, lot.lot])}`;
}

function inventoryExpiryDateValid(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || '') && Number.isFinite(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}

function inventoryExpiryLoadPending(lot) {
  const raw = localStorage.getItem(inventoryExpiryStorageKey(lot));
  if (!raw) return null;
  const pending = JSON.parse(raw), request = pending && pending.request;
  if (pending.version !== 1 || pending.actorId !== rpAuthProfile().id || !request
    || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(request.id)
    || request.skuId !== lot.sku || request.lot !== lot.lot || !inventoryExpiryDateValid(request.expiresAt)
    || !(request.expectedExpiresAt === null || inventoryExpiryDateValid(request.expectedExpiresAt))
    || typeof request.reason !== 'string' || request.reason.trim().length < 10 || request.reason.length > 1000
    || typeof request.evidence !== 'string' || request.evidence.trim().length < 3 || request.evidence.length > 2000 || request.confirmed !== true) {
    throw new Error('The saved expiry request cannot be read safely. Keep this browser data and ask Operations to reconcile it before another confirmation.');
  }
  return pending;
}

function inventoryExpiryReceiptMatches(response, request) {
  const confirmation = response && response.confirmation;
  return response && response.accepted === true && typeof response.active === 'boolean' && confirmation
    && confirmation.id === request.id && confirmation.sku_id === request.skuId && confirmation.lot === request.lot
    && confirmation.expires_at === request.expiresAt && confirmation.reason === request.reason && confirmation.evidence === request.evidence
    && confirmation.actor_id === rpAuthProfile().id
    && (!response.active || (response.lot && response.lot.expiry_confirmation_id === request.id
      && response.lot.sku_id === request.skuId && response.lot.lot === request.lot && response.lot.expires_at === request.expiresAt));
}

function InventoryLotExpiryModal({ lot, onClose, onSaved, onBack }) {
  const [initial] = useState(() => {
    try { return { pending: inventoryExpiryLoadPending(lot), error: '' }; }
    catch (error) { return { pending: null, error: error.message || 'The saved expiry request is unreadable.' }; }
  });
  const [pending, setPending] = useState(initial.pending);
  const [f, setF] = useState(initial.pending ? initial.pending.request : { expiresAt: lot.expiresAt || '', reason: '', evidence: '' });
  const [review, setReview] = useState(initial.pending ? initial.pending.request : null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState(initial.error);
  const [result, setResult] = useState(null);
  const canManage = inventoryLotManagementAllowed();
  const locked = busy || Boolean(pending) || Boolean(result) || Boolean(initial.error) || !canManage;
  const valid = inventoryExpiryDateValid(f.expiresAt) && f.reason.trim().length >= 10 && f.reason.trim().length <= 1000 && f.evidence.trim().length >= 3 && f.evidence.trim().length <= 2000;
  const change = (key, value) => { if (locked) return; setF(state => ({ ...state, [key]: value })); setReview(null); setConfirmed(false); setError(''); };
  const preview = async () => {
    if (locked || !valid) return;
    setBusy(true); setError(''); setConfirmed(false); setReview(null);
    try {
      if (!await rpRefreshLiveData()) throw new Error('Live inventory could not refresh. Reconnect before reviewing expiry.');
      const matches = (RP.LOTS || []).filter(row => row.sku === lot.sku && row.lot === lot.lot);
      if (matches.length !== 1 || matches[0].id !== lot.id) throw new Error('The exact lot changed or could not be loaded. Close this dialog and review Lot Master again.');
      const current = matches[0];
      if (current.expiresAt && !inventoryExpiryDateValid(current.expiresAt)) throw new Error('The current expiry is invalid. Correct it through Edit production and expiry first.');
      if (current.producedAt && f.expiresAt < current.producedAt) throw new Error('Expiry cannot be earlier than the recorded production date. Review the production evidence before correcting it.');
      setReview({ skuId: lot.sku, lot: lot.lot, expiresAt: f.expiresAt, expectedExpiresAt: current.expiresAt || null, reason: f.reason.trim(), evidence: f.evidence.trim() });
    } catch (err) { setError(err.message || 'Could not refresh the current expiry.'); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (submitting.current || busy || !canManage || initial.error || result || (!pending && (!review || !confirmed))) return;
    if (window.RP_AUTH && window.RP_AUTH.demo) { setError('Expiry confirmations are available in live mode.'); return; }
    submitting.current = true; setBusy(true); setError('');
    let attempt = pending;
    try {
      if (!attempt) {
        attempt = { version: 1, actorId: rpAuthProfile().id, request: { ...review, id: crypto.randomUUID(), confirmed: true } };
        // Persist the exact request before POST. An uncertain response must reuse it unchanged.
        localStorage.setItem(inventoryExpiryStorageKey(lot), JSON.stringify(attempt));
        setPending(attempt);
      }
      const response = await rpApi('/api/inventory/lot/confirm-expiry', { method: 'POST', body: JSON.stringify(attempt.request) });
      if (!inventoryExpiryReceiptMatches(response, attempt.request)) throw new Error('The response did not confirm the matching expiry request. Recover the same confirmation before making another change.');
      setResult(response); setPending(null);
      localStorage.removeItem(inventoryExpiryStorageKey(lot));
      try { if (!await rpRefreshAfterMutation('Expiry confirmation saved')) throw new Error('Refresh unavailable'); }
      catch (err) { setError('The confirmation was saved, but inventory could not refresh. Refresh inventory before editing dates again.'); }
    } catch (err) {
      // These responses prove the request was rejected, so its fields may be corrected.
      if ([400, 403, 404, 409, 422].includes(err.status)) {
        try { localStorage.removeItem(inventoryExpiryStorageKey(lot)); setPending(null); setReview(null); setConfirmed(false); }
        catch { /* Keep the durable request locked if its receipt cannot be cleared. */ }
      }
      setError(err.message || 'Could not confirm expiry. Recover the same confirmation if its outcome is unknown.');
    } finally { submitting.current = false; setBusy(false); }
  };
  return <Modal title={`Confirm expiry: ${lot.lot}`} sub={inventoryLotIdentity(lot).productLabel} onClose={busy ? () => {} : onClose} width={720}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>Close</button>{result ? <button className="btn btn-primary" onClick={onSaved}>Done</button> : pending ? <button className="btn btn-primary" disabled={busy || !canManage} onClick={submit}>{busy ? 'Recovering...' : 'Recover same confirmation'}</button> : <button className="btn btn-primary" disabled={locked || !review || !confirmed} onClick={submit}>{busy ? 'Saving...' : 'Confirm expiry'}</button>}</>}>
    <p style={{ fontSize: 13, color: 'var(--muted)' }}>Confirm the supported expiry for this exact lot. This records expiry evidence only. Production date and joint Date Control remain unchanged.</p>
    {!canManage && <div role="alert">Admin, leadership, operations or warehouse access is required to confirm expiry.</div>}
    {pending && <p role="status">An expiry request is waiting for its receipt. Recover the same confirmation; its date, reason, evidence and reference are locked, including after closing or reloading.</p>}
    {!result && <>
      <button className="btn btn-sm" disabled={locked} onClick={onBack}>{inventoryLotIdentity(lot).isCan ? 'Back to lot details' : 'Edit production and expiry'}</button>
      <MField label="Expiry date" required><input aria-label="Expiry date" className="field" type="date" value={f.expiresAt} disabled={locked} onChange={e => change('expiresAt', e.target.value)} /></MField>
      <MField label="Correction or confirmation reason" required><textarea aria-label="Correction or confirmation reason" className="field" rows="2" maxLength={1000} value={f.reason} disabled={locked} onChange={e => change('reason', e.target.value)} placeholder="Explain the correction, or why the current date is supported (at least 10 characters)." /></MField>
      <MField label="Expiry evidence" required><textarea aria-label="Expiry evidence" className="field" rows="2" maxLength={2000} value={f.evidence} disabled={locked} onChange={e => change('evidence', e.target.value)} placeholder="Label photo, COA or document reference (at least 3 characters)." /></MField>
      {!pending && <button className="btn" disabled={locked || !valid} onClick={preview}>Refresh and review expiry</button>}
    </>}
    {review && <section role="region" aria-label="Expiry confirmation review" style={{ marginTop: 16, padding: 14, background: 'var(--surface-2)', borderRadius: 10, overflowWrap: 'anywhere' }}>
      <h3 style={{ marginTop: 0 }}>Review this expiry confirmation</h3>
      <p><b>Product / lot:</b> {inventoryLotIdentity(lot).productLabel} / {review.lot}</p>
      <p><b>Current expiry:</b> {review.expectedExpiresAt || 'Not recorded'}<br /><b>Proposed expiry:</b> {review.expiresAt}</p>
      <p><b>Reason:</b> {review.reason}<br /><b>Evidence:</b> {review.evidence}</p>
      <p>Production date and Date Control are unchanged. Quantities and historical ledger dates are unchanged.</p>
      {!pending && !result && <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><input type="checkbox" checked={confirmed} disabled={locked} onChange={e => setConfirmed(e.target.checked)} /><span>I checked the expiry evidence for this exact product and lot and confirm the proposed expiry.</span></label>}
    </section>}
    {result && <p role="status">{result.active ? `Expiry ${result.confirmation.expires_at} confirmed. Production date and Date Control are unchanged.` : 'This confirmation was recorded, but a later edit has superseded it. Review the current lot dates before making another change.'}</p>}
    {error && <p role="alert" style={{ color: 'var(--danger)', overflowWrap: 'anywhere' }}>{error}</p>}
  </Modal>;
}

function InventoryLotModal({ lot: suppliedLot, onClose, onSaved, go }) {
  // Keep the identity and version that supplied these form values. A background
  // refresh must never pair newer metadata with this editor's older draft.
  const [lot] = useState(() => suppliedLot ? { ...suppliedLot } : null);
  const [correctLot, setCorrectLot] = useState(false);
  const [expiryOnly, setExpiryOnly] = useState(() => {
    if (!lot) return false;
    try { return Boolean(localStorage.getItem(inventoryExpiryStorageKey(lot))); }
    catch { return true; }
  });
  const skus = rpInventorySkus();
  const locations = inventoryLocationOptions();
  const initialSku = lot && lot.sku || (skus[0] && skus[0].id) || '';
  const [f, setF] = useState({
    acknowledgeHistoricalConflicts: false,
    dateStatus: lot && lot.dateStatus || 'Unverified',
    expiresAt: lot && lot.expiresAt || '',
    lifecycleStatus: lot && lot.lifecycleStatus || 'Unverified',
    lot: lot && lot.lot || '',
    lotGroup: lot && lot.lotGroup || '',
    notes: lot && lot.notes || '',
    originLocationId: lot && lot.originLocationId || '',
    producedAt: lot && lot.producedAt || '',
    producer: lot && lot.producer || '',
    skuId: initialSku
  });
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  const set = (key, value) => setF(state => ({ ...state, [key]: value }));
  const cleanLot = value => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  const existingPrintedLot = !lot && rpExistingPrintedLotMessage(f.skuId, cleanLot(f.lot));
  const canManage = inventoryLotManagementAllowed();
  const identity = inventoryLotIdentity(lot || { sku: f.skuId, lot: f.lot });
  const supportsMasterEdit = !identity.isCan && (!lot || skus.some(sku => sku.id === lot.sku));
  const missingVersion = Boolean(lot && !lot.updatedAt);
  const canEditMaster = canManage && supportsMasterEdit && !missingVersion;
  const canSaveMaster = canEditMaster && !stale;
  const recordedLot = lot ? lot.lot : cleanLot(f.lot);
  const packagingRuns = lot ? (RP.PACKAGING_RUNS || []).filter(run => run.outputSkuId === lot.sku && run.lot === lot.lot && run.inventoryMode === 'post_to_inventory' && run.status !== 'Reversed') : [];
  const correctionRun = packagingRuns.length === 1 && !identity.printed ? packagingRuns[0] : null;
  const valid = Boolean(canSaveMaster && !existingPrintedLot && f.skuId && recordedLot && (f.dateStatus !== 'Verified' || (f.producedAt && f.expiresAt)));
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    if (window.RP_AUTH && window.RP_AUTH.demo) {
      toast('Lot master changes are available in live mode.', 'info');
      return;
    }
    setBusy(true);
    try {
      await rpApi('/api/inventory/lot/upsert', {
        method: 'POST',
        body: JSON.stringify({ ...f, lot: recordedLot, ...(lot ? { skuId: lot.sku, expectedLotId: lot.id, expectedUpdatedAt: lot.updatedAt } : {}) })
      });
      await rpRefreshAfterMutation(`Lot ${recordedLot} saved`);
      toast(`Lot ${recordedLot} saved`, f.dateStatus === 'Conflicted' ? 'warn' : 'ok');
      onSaved && onSaved();
    } catch (err) {
      const versionConflict = err && ['LOT_EDIT_CONFLICT', 'LOT_VERSION_REQUIRED'].includes(err.code);
      if (versionConflict) setStale(true);
      setError(versionConflict ? 'This lot changed or its edit version is unavailable. Your edits are still here and were not saved. Copy any details you need, then discard this draft, refresh the list and reopen the lot to review the latest dates.' : err && err.message ? err.message : 'Could not save lot master.');
    } finally {
      setBusy(false);
    }
  };
  const refreshLotList = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!await rpRefreshLiveData()) throw new Error('Live inventory could not refresh. Reconnect and try again.');
      onClose();
    } catch (err) { setError(err.message || 'Could not refresh the lot list. Your current draft is still open.'); }
    finally { setBusy(false); }
  };
  if (correctLot && correctionRun) return <PackagingCorrectionModal run={correctionRun} operation="correct_lot" onClose={() => setCorrectLot(false)} onSaved={onSaved || onClose} />;
  if (expiryOnly && lot) return <InventoryLotExpiryModal lot={lot} onClose={onClose} onSaved={onSaved} onBack={() => setExpiryOnly(false)} />;
  return (
    <Modal title={lot ? identity.printed ? `Lot ${identity.printed.printedLot}` : identity.openSource ? 'Recovered loose-can lot' : `Lot ${lot.lot}` : 'New Lot Master'} sub="Recorded stock identity and supported date corrections" onClose={onClose} width={720}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !canSaveMaster} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Save Lot'}</button></>}>
      {!supportsMasterEdit && <p role="status">{identity.isCan ? 'Use Confirm expiry only for this loose-can lot. Edit its production details from the can run.' : 'This product is unavailable. Refresh the product directory before editing.'}</p>}
      {lot && canManage && <button className="btn" disabled={busy} style={{ marginBottom: 16 }} onClick={() => setExpiryOnly(true)}>Confirm expiry only</button>}
      {!canManage && <div role="alert">Admin, leadership, operations or warehouse access is required to edit lot dates.</div>}
      {supportsMasterEdit && missingVersion && <p role="status">This lot was loaded without an edit version. Refresh the list and reopen the lot before editing so a newer change cannot be overwritten.</p>}
      {supportsMasterEdit && (missingVersion || stale) && <button className="btn" disabled={busy} onClick={refreshLotList} style={{ marginBottom: 16 }}>{stale ? 'Discard edits and refresh list' : 'Refresh lot list'}</button>}
      {lot && <details aria-label="Lot identity explained" style={{ marginBottom: 16, padding: 12, background: 'var(--surface-2)', borderRadius: 8, fontSize: 12.5, overflowWrap: 'anywhere' }}><summary>Lot identity and linked records</summary>
        <p><b>Recorded lot</b> is the protected reference used by stock movements. Saving dates or a lot group keeps that reference unchanged.</p>
        <p><b>Printed label</b> is the supported code on the physical packaging. {identity.printed ? 'The saved mapping links this label to the recorded lot. Its evidence can be reviewed in Production History.' : 'No separate printed label has been recorded for this exact product and lot.'}</p>
        <p><b>Lot group</b> is an organizational label. It does not change the recorded lot or printed label.</p>
        {identity.openSource && <p>This loose-can stock was recovered from source lot <b>{identity.openSource.lot}</b> ({identity.openSource.sku}) by repack correction <b>{identity.openSource.repackId}</b>. The OPEN reference keeps these cans separate from sealed cases.{identity.openSource.printedLot ? ` The source cases have the saved printed label ${identity.openSource.printedLot}.` : ''}</p>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {identity.printed && go && <button className="btn btn-sm" onClick={() => { onClose(); go('production', { type: 'production-history', id: identity.printed.packagingRunId }); }}>View printed-label history</button>}

          {identity.openSource && go && <button className="btn btn-sm" onClick={() => { onClose(); go('inventory', { type: 'inventory-view', view: 'open-cans' }); }}>View Open Cans</button>}
        </div>
        {correctionRun && <p>Correct Lot checks the original packaging, stock and sources before allowing a change. Moved stock can require a reviewed printed-label mapping instead.</p>}
      </details>}
      <fieldset disabled={!canEditMaster || busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 12 }}>
        <MField label="Product" required>{lot ? <div aria-label="Product" style={{ padding: '10px 0', overflowWrap: 'anywhere' }}>{identity.productLabel}</div> : <select aria-label="Product" className="field" value={f.skuId} onChange={e => set('skuId', e.target.value)}>{skus.map(sku => <option key={sku.id} value={sku.id}>{rpSkuDisplayName(sku)} - {rpSkuExternalIdentity(sku)}</option>)}</select>}</MField>
        <MField label="Recorded lot" required><input aria-label="Recorded lot" className="field mono" readOnly={Boolean(lot)} value={f.lot} onChange={e => set('lot', cleanLot(e.target.value))} placeholder="RP-GC-002" style={tried && !recordedLot ? { borderColor: 'var(--danger)' } : undefined} />{correctionRun && canManage && <button className="btn btn-sm" onClick={() => setCorrectLot(true)}>Review recorded-lot correction</button>}</MField>
        <MField label="Printed label"><div aria-label="Printed label" className="mono" style={{ padding: '10px 0', overflowWrap: 'anywhere' }}>{identity.printed ? identity.printed.printedLot : 'No separate printed label recorded'}</div></MField>
        <MField label="Lot group"><input aria-label="Lot group" className="field" value={f.lotGroup} onChange={e => set('lotGroup', e.target.value)} placeholder="Lot 2" /></MField>
        <MField label="Origin"><select className="field" value={f.originLocationId} onChange={e => set('originLocationId', e.target.value)}><option value="">Unconfirmed</option>{locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}</select></MField>
        <MField label="Producer"><input className="field" value={f.producer} onChange={e => set('producer', e.target.value)} placeholder="BevMax" /></MField>
        <MField label="Lifecycle"><select className="field" value={f.lifecycleStatus} onChange={e => set('lifecycleStatus', e.target.value)}>{['Draft', 'Scheduled', 'Produced', 'QA Hold', 'Released', 'Closed', 'Unverified', 'Quarantined'].map(status => <option key={status}>{status}</option>)}</select></MField>
        <MField label="Production date"><input aria-label="Production date" className="field" type="date" value={f.producedAt} onChange={e => set('producedAt', e.target.value)} style={tried && f.dateStatus === 'Verified' && !f.producedAt ? { borderColor: 'var(--danger)' } : undefined} /></MField>
        <MField label="Expiry date"><input aria-label="Expiry date" className="field" type="date" value={f.expiresAt} onChange={e => set('expiresAt', e.target.value)} style={tried && f.dateStatus === 'Verified' && !f.expiresAt ? { borderColor: 'var(--danger)' } : undefined} /></MField>
      </div>
      <MField label="Date Control"><select aria-label="Date Control" className="field" value={f.dateStatus} onChange={e => set('dateStatus', e.target.value)}><option>Unverified</option><option>Verified</option><option>Conflicted</option></select></MField>
      <MField label="Evidence / Notes"><textarea aria-label="Evidence / Notes" className="field" rows="3" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="COA, production record, correction rationale, or unresolved discrepancy..." style={{ resize: 'vertical' }} /></MField>
      {f.dateStatus === 'Verified' && <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4 }}><input type="checkbox" checked={f.acknowledgeHistoricalConflicts} onChange={e => set('acknowledgeHistoricalConflicts', e.target.checked)} style={{ marginTop: 2 }} /><span>If historical ledger dates differ, I reviewed the production/COA evidence and acknowledge that the ledger remains unchanged as historical evidence.</span></label>}
      </fieldset>
      {existingPrintedLot && <ReqMsg show>{existingPrintedLot}</ReqMsg>}
      {tried && !valid && <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>Product and lot are required. Verified lots also require produced and expiry dates.</div>}
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function InventoryAvailabilityTable({ pools, group, filters, search, go, onOpenLocation, onEdit }) {
  const [sorts,setSorts] = useState({}), [expanded,setExpanded] = useState({}), [selected,setSelected] = useState(null);
  const ready = Array.isArray(RP.ORDER_STOCK_COMMITMENTS);
  const term = String(search || '').trim().toLowerCase();
  const stockRows = pool => (RP.INV || []).filter(row => row.loc === pool.loc && String(row.unit || 'case') !== 'can' && rpCanonicalInventorySkuId(row.sku) === pool.sku);
  const visible = pools.filter(row => (!filters.SKU || inventorySkuExternalLabel(row.sku) === filters.SKU) && (!filters.Location || inventoryLocationName(row.loc) === filters.Location))
    .filter(row => !term || [inventorySkuExternalLabel(row.sku),inventoryLocationName(row.loc),...stockRows(row).map(item=>rpPrintedLotLabel(item.sku,item.lot))].join(' ').toLowerCase().includes(term));
  const groups = new Map();
  visible.forEach(row => { const key = group === 'sku' ? row.sku : row.loc; groups.set(key,[...(groups.get(key) || []),row]); });
  const value = (row,column) => column === 'name' ? group === 'sku' ? inventoryLocationName(row.loc) : inventorySkuExternalLabel(row.sku)
    : column === 'available' ? Math.max(0,row.onHand-row.committed) : column === 'onHand' ? row.physicalOnHand : row.committed;
  const header = (key,column,label) => <th className={column === 'name' ? '' : 'num'} aria-sort={sorts[key]?.column === column ? sorts[key].descending ? 'descending' : 'ascending' : 'none'}>
    <button className="rp-inventory-sort" aria-label={`Sort by ${label}`} onClick={()=>setSorts(old=>({...old,[key]:{column,descending:old[key]?.column === column ? !old[key].descending : false}}))}>{label}<span aria-hidden="true">{sorts[key]?.column === column ? sorts[key].descending ? '↓' : '↑' : '↕'}</span></button>
  </th>;
  return <div className="rp-inventory-availability">
    {!ready && <p role="status">Order commitments are unavailable. Refresh to check availability.</p>}
    {[...groups].map(([key,rows])=>{
      const title = group === 'sku' ? inventorySkuExternalLabel(key) : inventoryLocationName(key);
      const sort = sorts[key];
      const ordered = sort ? [...rows].sort((a,b)=>{const left=value(a,sort.column),right=value(b,sort.column);return (typeof left === 'number' ? left-right : left.localeCompare(right,undefined,{numeric:true}))*(sort.descending?-1:1);}) : rows;
      return <section className="card" key={key} aria-label={`${title} order availability`}>
        <div className="rp-inventory-group-head" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 18px',gap:12}}><strong>{title}</strong>{group === 'location' && <button className="btn btn-sm" aria-label={`Open ${title} inventory history`} onClick={()=>onOpenLocation(key)}>History</button>}</div>
        <table className="rp-table rp-availability-table"><thead><tr>{header(key,'name',group === 'sku'?'Location':'SKU')}{header(key,'onHand','On hand')}{header(key,'committed','Committed')}{header(key,'available','Available')}<th><span className="sr-only">Actions</span></th></tr></thead><tbody>
          {ordered.map(row=><React.Fragment key={row.key}>
            <tr>
              <td><strong>{group === 'sku' ? inventoryLocationName(row.loc) : inventorySkuName(row.sku)}</strong></td>
              <td className="num" data-label="On hand">{row.physicalOnHand.toLocaleString()}</td>
              <td className="num" data-label="Committed">{ready ? row.committed > 0 ? <button className="stock-needed-link" aria-label={`Orders for ${inventorySkuName(row.sku)} at ${inventoryLocationName(row.loc)}`} onClick={()=>setSelected(selected === row.key ? null : row.key)}>{row.committed.toLocaleString()}</button> : '0' : '—'}</td>
              <td className="num" data-label="Available" style={{fontWeight:800,color:'var(--ok)'}}>{ready ? Math.max(0,row.onHand-row.committed).toLocaleString() : '—'}</td>
              <td><button className="btn btn-sm" aria-expanded={Boolean(expanded[row.key])} aria-label={`Lots for ${inventorySkuName(row.sku)} at ${inventoryLocationName(row.loc)}`} onClick={()=>setExpanded(old=>({...old,[row.key]:!old[row.key]}))}>{expanded[row.key]?'Hide lots':'Lots'}</button></td>
            </tr>
            {expanded[row.key] && <tr className="rp-availability-detail"><td colSpan="5"><BalancesTable rows={stockRows(row)} group="sku" onOpenLocation={onOpenLocation} onEdit={onEdit}/></td></tr>}
            {selected === row.key && <tr className="rp-availability-detail"><td colSpan="5"><InventoryCommitmentOrders row={row} go={go}/></td></tr>}
          </React.Fragment>)}
        </tbody></table>
      </section>;
    })}
    {!visible.length && <div className="card" style={{padding:24}}>No inventory matches these filters.</div>}
  </div>;
}

function BalancesTable({ rows, group, onOpenLocation, onEdit }) {
  const [sorts, setSorts] = useState({});
  const sortValue = (row, column) => ({
    sku: () => inventorySkuExternalLabel(row.sku),
    location: () => inventoryLocationName(row.loc),
    owner: () => row.owner,
    status: () => inventoryBalanceStatus(row),
    lot: () => rpPrintedLotLabel(row.sku, row.lot),
    expiry: () => row.exp ? Date.parse(row.exp) : null,
    qty: () => Number(row.qty),
  }[column]());
  const sortedItems = (items, sort) => !sort ? items : [...items].sort((a, b) => {
    const left = sortValue(a, sort.column), right = sortValue(b, sort.column);
    const missing = value => value == null || value === '' || (typeof value === 'number' && !Number.isFinite(value));
    if (missing(left) || missing(right)) return Number(missing(left)) - Number(missing(right));
    const comparison = typeof left === 'number' && typeof right === 'number'
      ? left - right : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
    return comparison * (sort.descending ? -1 : 1);
  });
  const sortHeader = (tableKey, column, label) => {
    const sort = sorts[tableKey], selected = sort?.column === column;
    return <th className={column === 'qty' ? 'num' : undefined} aria-sort={selected ? (sort.descending ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="rp-inventory-sort" aria-label={`Sort by ${label}`} onClick={() => setSorts(current => ({ ...current, [tableKey]: { column, descending: current[tableKey]?.column === column ? !current[tableKey].descending : false } }))}>
        {label}<span aria-hidden="true">{selected ? (sort.descending ? '↓' : '↑') : '↕'}</span>
      </button>
    </th>;
  };
  const keyFn = {
    location: i => inventoryLocationName(i.loc),
    sku: i => inventorySkuExternalLabel(i.sku),
    status: i => inventoryBalanceStatus(i) || 'Unknown status'
  }[group];
  const groups = {};
  rows.forEach(i => { const k = keyFn(i); (groups[k] = groups[k] || []).push(i); });
  return (
    <div className="rp-inventory-balance-groups" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {Object.entries(groups).map(([k, items]) => {
        const total = items.reduce((s, i) => s + i.qty, 0);
        const locationId = group === 'location' ? (items[0] || {}).loc : '';
        const tableKey = JSON.stringify([group, k]);
        const activeItems = sortedItems(items.filter(i => !i.emptyLocation), sorts[tableKey]);
        return (
          <div key={k} className="card rp-inventory-balance-group" style={{ overflow: 'hidden' }}>
            <div className="rp-inventory-group-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)' }}>
              <div className="rp-inventory-group-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {group === 'status' ? <Pill status={k} /> : <span className="disp" style={{ fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</span>}
              </div>
              <div className="rp-inventory-group-actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {group === 'location' && locationId && <button className="btn btn-sm rp-inventory-history-button" aria-label={`Open ${k} inventory history`} onClick={() => onOpenLocation && onOpenLocation(locationId)}><Icon name="layers" size={14} /><span>History</span></button>}
                <span className="mono tnum" style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>{total} cases on hand</span>
              </div>
            </div>
            <table className="rp-table rp-inventory-balance-table">
              <thead><tr>
                {group !== 'sku' && sortHeader(tableKey, 'sku', 'SKU')}
                {group !== 'location' && sortHeader(tableKey, 'location', 'Location')}
                {sortHeader(tableKey, 'owner', 'Owner')}{group !== 'status' && sortHeader(tableKey, 'status', 'Status')}{sortHeader(tableKey, 'lot', 'Lot')}{sortHeader(tableKey, 'expiry', 'Expiry')}{sortHeader(tableKey, 'qty', 'On hand')}{onEdit && <th><span className="sr-only">Actions</span></th>}
              </tr></thead>
              <tbody>
                {activeItems.map((i, idx) => {
                  const s = rpSkuItem(i.sku);
                  return (
                    <tr key={idx}>
                      {group !== 'sku' && <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={s || i.sku} size={22} /><span style={{ fontWeight: 500 }}>{inventorySkuExternalLabel(i.sku)}</span></div></td>}
                      {group !== 'location' && <td style={{ color: 'var(--muted)' }}>{inventoryLocationName(i.loc)}</td>}
                      <td style={{ color: i.owner === 'Ranch Power' ? 'var(--muted)' : 'var(--ink)' }}>{i.owner}</td>
                      {group !== 'status' && <td><Pill status={inventoryBalanceStatus(i)} /></td>}
                      <td className="mono" style={{ fontSize: 12, color: 'var(--muted)', overflowWrap: 'anywhere' }}>{rpPrintedLotLabel(i.sku, i.lot)}</td>
                      <td className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>{i.exp}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{i.qty}</td>{onEdit && <td><button className="btn btn-sm" aria-label={`Edit count for ${rpPrintedLotLabel(i.sku,i.lot)} at ${inventoryLocationName(i.loc)}`} onClick={() => onEdit(i)}>Edit</button></td>}
                    </tr>
                  );
                })}
                {!activeItems.length && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--faint)', padding: 24 }}>
                      No current balance. Open history to audit prior movement.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="rp-inventory-mobile-balances" role="list" aria-label={`${k} inventory balances`}>
              {activeItems.map((i, idx) => {
                const sku = rpSkuItem(i.sku);
                const showSku = group !== 'sku';
                return (
                  <div className="rp-inventory-mobile-balance" role="listitem" key={`${i.sku || 'sku'}-${i.loc || 'location'}-${i.lot || idx}-${idx}`}>
                    <div className="rp-inventory-mobile-primary">
                      {showSku
                        ? <><FlavorChip sku={sku || i.sku} size={26} /><span><strong>{inventorySkuName(i.sku)}</strong><small className="mono">{rpSkuExternalIdentity(sku || i.sku)}</small></span></>
                        : <><Icon name="pin" size={16} /><span><strong>{inventoryLocationName(i.loc)}</strong></span></>}
                    </div>
                    <div className="rp-inventory-mobile-qty"><strong className="mono tnum">{i.qty}</strong><span>on hand</span></div>
                    <div className="rp-inventory-mobile-meta">
                      {group !== 'location' && <span><Icon name="pin" size={12} />{inventoryLocationName(i.loc)}</span>}
                      {i.owner && <span><Icon name="user" size={12} />{i.owner}</span>}
                      {group !== 'status' && <Pill status={inventoryBalanceStatus(i)} />}
                      {i.lot && <span className="mono" style={{ overflowWrap: 'anywhere' }}>Lot {rpPrintedLotLabel(i.sku, i.lot)}</span>}
                      {i.exp && <span className="mono">Exp {i.exp}</span>}
                      {onEdit && <button className="btn btn-sm" aria-label={`Edit count for ${rpPrintedLotLabel(i.sku,i.lot)} at ${inventoryLocationName(i.loc)}`} onClick={() => onEdit(i)}>Edit</button>}
                    </div>
                  </div>
                );
              })}
              {!activeItems.length && <div className="rp-inventory-mobile-empty">No current balance. Open history to audit prior movement.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function warehouseTextKey(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function warehouseCompactKey(value) {
  return warehouseTextKey(value).replace(/\s+/g, '');
}

function warehouseLocationAliases(location) {
  const strong = new Set();
  const weak = new Set();
  const addStrong = value => {
    const key = warehouseTextKey(value);
    if (key) strong.add(key);
  };
  const addWeak = value => {
    const key = warehouseTextKey(value);
    if (key) weak.add(key);
  };
  const name = String(location && location.name || '');
  const territory = String(location && location.territory || '');
  const type = String(location && location.type || '');
  addStrong(location && location.id);
  addStrong(name);
  addStrong(name.replace(/\bwarehouse\b/ig, 'WH'));
  addStrong(name.replace(/\bmicro[-\s]*warehouse\b/ig, '').replace(/\brep storage\b/ig, '').replace(/\bwarehouse\b/ig, '').trim());
  addStrong(territory && `${territory} warehouse`);
  addStrong(territory && `${territory} WH`);
  if (/main warehouse|warehouse/i.test(`${type} ${name}`) && !/micro/i.test(`${type} ${name}`)) {
    addWeak(territory);
    addWeak(territory && `${territory} inbound`);
  }
  return {
    strong: [...strong],
    strongCompact: [...strong].map(warehouseCompactKey),
    weak: [...weak],
    weakCompact: [...weak].map(warehouseCompactKey)
  };
}

function warehouseEndpointMatches(row, side, location) {
  if (!row || !location) return false;
  const idKey = side === 'from' ? 'fromLocationId' : 'toLocationId';
  const direct = String(row[idKey] || '').trim();
  if (direct && direct === location.id) return true;
  const label = String(row[side] || '').trim();
  if (!label) return false;
  const labelKey = warehouseTextKey(label);
  const labelCompact = warehouseCompactKey(label);
  const aliases = warehouseLocationAliases(location);
  if (aliases.strong.some(alias => labelKey === alias || (alias.split(' ').length > 1 && labelKey.startsWith(`${alias} `)))) return true;
  if (aliases.strongCompact.some(alias => alias && labelCompact === alias)) return true;
  const legacyStatus = /\b(available|reserved|allocated|fronted|purchased|sample|inbound|outbound|damaged|damage|qa|hold|warehouse|wh)\b/.test(labelKey);
  if (legacyStatus && aliases.weak.some(alias => labelKey === alias || labelKey.startsWith(`${alias} `))) return true;
  return legacyStatus && aliases.weakCompact.some(alias => alias && labelCompact === alias);
}

function warehouseMovement(row, location) {
  const qty = Math.abs(Number(row && row.qty || 0));
  const fromHere = warehouseEndpointMatches(row, 'from', location);
  const toHere = warehouseEndpointMatches(row, 'to', location);
  if (toHere && !fromHere) return { color: 'var(--ok)', direction: 'Inbound', label: `+${qty.toLocaleString()}`, signed: qty };
  if (fromHere && !toHere) return { color: 'var(--danger)', direction: 'Outbound', label: `-${qty.toLocaleString()}`, signed: -qty };
  if (fromHere && toHere) return { color: 'var(--ink-soft)', direction: 'Internal', label: `Move ${qty.toLocaleString()}`, signed: 0 };
  return { color: 'var(--muted)', direction: 'Other', label: `${qty.toLocaleString()}`, signed: 0 };
}

function warehouseLedgerRows(location) {
  return (RP.LEDGER || [])
    .filter(row => warehouseEndpointMatches(row, 'from', location) || warehouseEndpointMatches(row, 'to', location))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

function warehouseBalanceRows(locationId) {
  const grouped = {};
  (RP.INV || []).filter(row => row.loc === locationId && String(row.unit || 'case').toLowerCase() !== 'can').forEach(row => {
    const key = [row.sku, inventoryBalanceStatus(row) || 'Available', row.lot || 'UNASSIGNED'].join('|');
    if (!grouped[key]) grouped[key] = { sku: row.sku, status: inventoryBalanceStatus(row) || 'Available', lot: row.lot || 'UNASSIGNED', qty: 0, exp: row.exp || row.lotExpiresAt || '' };
    grouped[key].qty += Number(row.qty || 0);
    grouped[key].exp = grouped[key].exp || row.exp || row.lotExpiresAt || '';
  });
  return Object.values(grouped).sort((a, b) => String(inventorySkuName(a.sku)).localeCompare(String(inventorySkuName(b.sku))) || String(a.status).localeCompare(String(b.status)));
}

function WarehouseDetailModal({ id, go, onChange, onClose, onReceive, onTransferIn, onTransferOut, onCycleCount }) {
  const location = RP.loc(id);
  const [query, setQuery] = useState('');
  const [direction, setDirection] = useState('All');
  const [eventType, setEventType] = useState('All');
  const [skuId, setSkuId] = useState('All');
  const [status, setStatus] = useState('All');
  const [selected, setSelected] = useState(null);
  if (!location) return null;

  const balances = warehouseBalanceRows(location.id);
  const allMovements = warehouseLedgerRows(location);
  const totalCases = balances.reduce((sum, row) => sum + Number(row.qty || 0), 0);
  const pools = inventoryCommitmentPools({ includeUncommitted: true }).filter(pool => pool.loc === location.id && pool.unit === 'case');
  const commitmentsReady = Array.isArray(RP.ORDER_STOCK_COMMITMENTS);
  const committedCases = pools.reduce((sum, pool) => sum + pool.committed, 0);
  const availableCases = pools.reduce((sum, pool) => sum + Math.max(0, pool.onHand - pool.committed), 0);
  const inboundCases = allMovements.reduce((sum, row) => {
    const move = warehouseMovement(row, location);
    return move.signed > 0 ? sum + move.signed : sum;
  }, 0);
  const outboundCases = allMovements.reduce((sum, row) => {
    const move = warehouseMovement(row, location);
    return move.signed < 0 ? sum + Math.abs(move.signed) : sum;
  }, 0);
  const openRequests = (RP.STOCK_REQUESTS || []).filter(req => ['Pending', 'Approved'].includes(String(req.status || '')) && (req.source === location.id || req.destination === location.id));
  const pendingReceipts = (RP.FIELD_RECEIPTS || []).filter(receipt => String(receipt.status || '') === 'Pending' && (receipt.location === location.id || receiptSourceId(receipt) === location.id));
  const eventOptions = ['All', ...new Set(allMovements.map(row => row.type).filter(Boolean))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : String(a).localeCompare(String(b)));
  const skuOptions = ['All', ...new Set(allMovements.map(row => row.sku).filter(Boolean))];
  const statusOptions = ['All', ...new Set(allMovements.flatMap(row => [row.statusBefore, row.statusAfter]).filter(Boolean))].sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : String(a).localeCompare(String(b)));
  const term = query.trim().toLowerCase();
  const filteredMovements = allMovements.filter(row => {
    const move = warehouseMovement(row, location);
    if (direction !== 'All' && move.direction !== direction) return false;
    if (eventType !== 'All' && row.type !== eventType) return false;
    if (skuId !== 'All' && row.sku !== skuId) return false;
    if (status !== 'All' && row.statusBefore !== status && row.statusAfter !== status) return false;
    if (!term) return true;
    const haystack = [
      row.type,
      ledgerSkuLabel(row),
      ledgerEndpointLabel(row.fromLocationId || row.from, row.statusBefore),
      ledgerEndpointLabel(row.toLocationId || row.to, row.statusAfter),
      ledgerUserLabel(row),
      row.ref,
      row.lot,
      row.note
    ].join(' ').toLowerCase();
    return haystack.includes(term);
  });

  return (
    <Modal title={location.name || location.id} sub={`${location.type || 'Location'} - ${location.territory || 'Unassigned'} - ${allMovements.length} ledger movements`} onClose={onClose} width={1120}
      footer={<>
        <button className="btn" onClick={onClose}>Close</button>
        {onCycleCount && <button className="btn" onClick={() => onCycleCount(location.id)}><Icon name="refresh" size={15} />Reconcile Count</button>}
        <button className="btn" onClick={() => onReceive && onReceive(location.id)}><Icon name="plus" size={15} />Receive Here</button>
        <button className="btn" onClick={() => onTransferIn && onTransferIn(location.id)}><Icon name="download" size={15} />Transfer In</button>
        <button className="btn btn-primary" onClick={() => onTransferOut && onTransferOut(location.id)}><Icon name="layers" size={15} />Transfer Out</button>
      </>}>
      {selected && <LedgerDetailModal row={selected} go={go} onChange={onChange} onClose={() => setSelected(null)} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(0,1fr))', gap: 12, marginBottom: 16 }}>
        <KPICard label="On Hand" value={totalCases.toLocaleString()} unit="cases" icon="inventory" accent="var(--navy)" />
        <KPICard label="Available" value={commitmentsReady ? availableCases.toLocaleString() : '—'} unit="cases" icon="check" accent="var(--ok)" />
        <KPICard label="Committed" value={commitmentsReady ? committedCases.toLocaleString() : '—'} unit="cases" icon="alert" accent="var(--warn)" />
        <KPICard label="Inbound History" value={inboundCases.toLocaleString()} unit="cases" icon="download" accent="var(--ok)" />
        <KPICard label="Outbound History" value={outboundCases.toLocaleString()} unit="cases" icon="orders" accent="var(--danger)" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,.9fr) minmax(0,1.6fr)', gap: 14, marginBottom: 16 }}>
        <div className="card" style={{ padding: 16, boxShadow: 'none' }}>
          <SectionTitle>Warehouse Details</SectionTitle>
          <Row k="Location ID" v={location.id} muted />
          <Row k="Type" v={location.type || '-'} />
          <Row k="Territory" v={location.territory || '-'} />
          <Row k="Rep / owner" v={location.rep || '-'} />
          <Row k="Capacity" v={location.cap ? `${Number(location.cap).toLocaleString()} cases` : '-'} />
          <Row k="Open requests" v={`${openRequests.length} open`} />
          <Row k="Pending receipts" v={`${pendingReceipts.length} pending`} />
        </div>
        <div className="card" style={{ overflow: 'hidden', boxShadow: 'none' }}>
          <div style={{ padding: '14px 16px 0' }}><SectionTitle>Current Balance</SectionTitle></div>
          <table className="rp-table">
            <thead><tr><th>SKU</th><th>Status</th><th>Lot</th><th>Expiry</th><th className="num">On hand</th></tr></thead>
            <tbody>
              {balances.slice(0, 8).map(row => (
                <tr key={`${row.sku}-${row.status}-${row.lot}`}>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FlavorChip sku={rpSkuItem(row.sku) || row.sku} size={20} /><span style={{ fontWeight: 700 }}>{inventorySkuExternalLabel(row.sku)}</span></div></td>
                  <td><Pill status={row.status} /></td>
                  <td className="mono" style={{ color: 'var(--muted)', fontSize: 11.5 }}>{rpPrintedLotLabel(row.sku, row.lot)}</td>
                  <td className="mono" style={{ color: 'var(--faint)', fontSize: 11.5 }}>{row.exp || '-'}</td>
                  <td className="num" style={{ fontWeight: 800 }}>{Number(row.qty || 0).toLocaleString()}</td>
                </tr>
              ))}
              {!balances.length && <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--faint)', padding: 22 }}>No current inventory at this location.</td></tr>}
              {balances.length > 8 && <tr><td colSpan="5" style={{ color: 'var(--muted)', fontSize: 12.5, padding: '10px 16px' }}>Showing top 8 of {balances.length} balance rows. Use Inventory filters for the full balance table.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card" style={{ overflow: 'hidden', boxShadow: 'none' }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--line)', display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) repeat(4,minmax(130px,.5fr))', gap: 10, alignItems: 'end' }}>
          <MField label="Search"><input className="field" value={query} onChange={e => setQuery(e.target.value)} placeholder="Ref, SKU, note, user..." /></MField>
          <MField label="Direction"><select className="field" value={direction} onChange={e => setDirection(e.target.value)}>{['All', 'Inbound', 'Outbound', 'Internal'].map(option => <option key={option}>{option}</option>)}</select></MField>
          <MField label="Event"><select className="field" value={eventType} onChange={e => setEventType(e.target.value)}>{eventOptions.map(option => <option key={option}>{option}</option>)}</select></MField>
          <MField label="SKU"><select className="field" value={skuId} onChange={e => setSkuId(e.target.value)}>{skuOptions.map(option => <option key={option} value={option}>{option === 'All' ? 'All' : inventorySkuExternalLabel(option)}</option>)}</select></MField>
          <MField label="Status"><select className="field" value={status} onChange={e => setStatus(e.target.value)}>{statusOptions.map(option => <option key={option}>{option}</option>)}</select></MField>
        </div>
        <table className="rp-table">
          <thead><tr><th>Date</th><th>Event</th><th>SKU</th><th>Movement</th><th>Route</th><th>User</th><th>Ref</th><th>Note</th></tr></thead>
          <tbody>
            {filteredMovements.map((row, index) => {
              const move = warehouseMovement(row, location);
              const note = String(row.note || row.notes || '').trim();
              return (
                <tr key={row.id || `${row.date}-${row.ref}-${index}`} style={{ cursor: 'pointer' }} onClick={() => setSelected(row)}>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{String(row.date || '').replace('T', ' ').slice(0, 22)}</td>
                  <td><span className="pill" style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)', border: '1px solid var(--line)' }}>{row.type || '-'}</span></td>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FlavorChip sku={rpSkuItem(row.sku) || row.sku} size={20} /><span style={{ fontWeight: 600 }}>{ledgerSkuLabel(row)}</span></div></td>
                  <td><span className="mono" style={{ color: move.color, fontWeight: 900 }}>{move.label}</span><div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>{move.direction}</div></td>
                  <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{ledgerEndpointLabel(row.fromLocationId || row.from, row.statusBefore)} <span style={{ color: 'var(--faint)' }}>{'->'}</span> {ledgerEndpointLabel(row.toLocationId || row.to, row.statusAfter)}</td>
                  <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{ledgerUserLabel(row)}</td>
                  <td className="mono" style={{ color: 'var(--info)', fontSize: 12 }}>{row.ref || '-'}</td>
                  <td title={note} style={{ color: note ? 'var(--muted)' : 'var(--faint)', fontSize: 12.5, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note || '-'}</td>
                </tr>
              );
            })}
            {!filteredMovements.length && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 28 }}>No movement rows match this warehouse view.</td></tr>}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function ledgerSkuLabel(row) {
  return inventorySkuExternalLabel(row && row.sku);
}

function ledgerReasonLabel(value) {
  const text = String(value || '').trim();
  if (!text) return 'Unspecified';
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

function ledgerLocationLabel(value, fallback) {
  const id = String(value || '').trim();
  if (!id) return fallback || '';
  const location = RP.loc(id);
  return location ? location.name : id;
}

function ledgerLocationId(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (RP.loc(text)) return text;
  const key = warehouseTextKey(text);
  const compact = warehouseCompactKey(text);
  const found = (RP.LOCATIONS || []).find(location => {
    const nameKey = warehouseTextKey(location.name);
    const nameCompact = warehouseCompactKey(location.name);
    return key === nameKey || compact === nameCompact;
  });
  return found ? found.id : text;
}

function ledgerEndpointLabel(value, fallback) {
  return ledgerLocationLabel(value, fallback) || fallback || '-';
}

function ledgerLocationTerritories(row) {
  const territories = [];
  [row && row.fromLocationId || row && row.from, row && row.toLocationId || row && row.to].forEach(id => {
    const location = RP.loc(id);
    if (location && location.territory && !territories.includes(location.territory)) territories.push(location.territory);
  });
  return territories;
}

function ledgerUserLabel(row) {
  const user = String(row && row.user || '').trim();
  if (user && !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(user)) return user;
  const createdBy = String(row && row.createdBy || user || '').trim();
  const profile = (RP.USER_PROFILES || []).find(p => p.id === createdBy);
  return profile ? (profile.name || profile.display_name || profile.email || 'Unknown user') : (createdBy ? 'Unknown user' : 'System');
}

function ledgerMovement(row) {
  const qty = Number(row && row.qty || 0);
  const hasFrom = Boolean(String(row && (row.fromLocationId || row.from) || '').trim());
  const hasTo = Boolean(String(row && (row.toLocationId || row.to) || '').trim());
  if (hasFrom && !hasTo) return { color: 'var(--danger)', label: `-${qty.toLocaleString()}`, tone: 'out' };
  if (!hasFrom && hasTo) return { color: 'var(--ok)', label: `+${qty.toLocaleString()}`, tone: 'in' };
  if (hasFrom && hasTo) return { color: 'var(--ink-soft)', label: `Move ${qty.toLocaleString()}`, tone: 'move' };
  return { color: qty < 0 ? 'var(--danger)' : 'var(--ok)', label: `${qty > 0 ? '+' : ''}${qty.toLocaleString()}`, tone: qty < 0 ? 'out' : 'in' };
}

function ledgerOrderId(row) {
  const relatedType = String(row && row.relatedType || '').toLowerCase();
  const ref = String(row && (row.relatedId || row.ref) || '').trim();
  if (relatedType.includes('sales_order') || relatedType === 'order' || /^SO-/i.test(ref)) return ref;
  return '';
}

function ledgerGiveawayReference(row) {
  const relatedType = String(row && row.relatedType || '').toLowerCase();
  const ref = String(row && (row.relatedId || row.ref) || '').trim();
  if (relatedType === 'inventory_giveaway' || /^GW-/i.test(ref)) return ref;
  return '';
}

function ledgerCanReverseGiveaway(row) {
  const type = String(row && row.type || '').toLowerCase();
  const reason = String(row && row.reasonCode || '').toLowerCase();
  if (!ledgerGiveawayReference(row)) return false;
  if (type !== 'giveaway') return false;
  return !/reversal|void/.test(`${type} ${reason}`);
}

function reverseDemoGiveaway(row, note) {
  const ref = ledgerGiveawayReference(row);
  if (!ref) throw new Error('This giveaway has no reference to reverse.');
  const alreadyReversed = (RP.LEDGER || []).some(candidate =>
    ledgerGiveawayReference(candidate) === ref && String(candidate.type || '').toLowerCase() === 'giveaway reversal'
  );
  if (alreadyReversed) return { alreadyReversed: true, quantity: 0, ref };
  const rows = (RP.LEDGER || []).filter(candidate =>
    ledgerGiveawayReference(candidate) === ref
    && String(candidate.type || '').toLowerCase() === 'giveaway'
    && !/void/.test(String(candidate.reasonCode || '').toLowerCase())
  );
  if (!rows.length) return { alreadyReversed: true, quantity: 0, ref };
  const user = typeof rpDisplayName === 'function' ? rpDisplayName() : 'Ranch Power';
  let total = 0;
  rows.forEach(candidate => {
    const quantity = Math.abs(Number(candidate.qty || 0));
    const locationId = ledgerLocationId(candidate.fromLocationId || candidate.from);
    if (!locationId || !(quantity > 0)) return;
    const lot = candidate.lot || 'UNASSIGNED';
    const owner = candidate.ownerBefore || 'Ranch Power';
    const status = candidate.statusBefore || 'Available';
    const existing = (RP.INV || []).find(item => inventorySkuMatches(item.sku, candidate.sku) && item.loc === locationId && item.owner === owner && item.status === status && item.lot === lot);
    if (existing) existing.qty += quantity;
    else RP.INV.push({ sku: candidate.sku, loc: locationId, owner, status, lot, qty: quantity });
    total += quantity;
    RP.LEDGER.unshift({
      id: `REV-${ref}-${Math.random().toString(36).slice(2, 8)}`,
      date: new Date().toISOString(),
      type: 'Giveaway Reversal',
      sku: candidate.sku,
      from: '',
      to: locName(locationId),
      toLocationId: locationId,
      qty: quantity,
      ownerAfter: owner,
      reasonCode: 'giveaway_reversal',
      relatedId: ref,
      relatedType: 'inventory_giveaway',
      statusAfter: status,
      user,
      ref,
      lot,
      note: `Reversed giveaway ledger ${candidate.id || candidate.ref || ref}: ${note}`
    });
    candidate.reasonCode = 'giveaway_voided';
    candidate.note = [candidate.note || candidate.notes || '', `Reversed giveaway ${ref}: ${note}`].filter(Boolean).join('\n\n');
  });
  return { quantity: total, ref };
}

function ledgerMatchesFilter(row, label, value) {
  if (!value) return true;
  if (label === 'Event') return row.type === value;
  if (label === 'SKU') return ledgerSkuLabel(row) === value;
  if (label === 'Location') return [ledgerEndpointLabel(row.fromLocationId || row.from, row.statusBefore), ledgerEndpointLabel(row.toLocationId || row.to, row.statusAfter)].includes(value);
  if (label === 'Territory') return ledgerLocationTerritories(row).includes(value);
  if (label === 'User') return ledgerUserLabel(row) === value;
  if (label === 'Reason') return ledgerReasonLabel(row.reasonCode) === value;
  if (label === 'Method') return ledgerReasonLabel(row.movementMethod) === value;
  if (label === 'Status') return [row.statusBefore, row.statusAfter].filter(Boolean).includes(value);
  if (label === 'Lot') return String(row.lot || '') === value;
  return true;
}

function inventoryLedgerFilterDefs(rows) {
  const unique = values => [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
  return [
    { label: 'Event', options: unique(rows.map(row => row.type)) },
    { label: 'SKU', options: unique(rows.map(ledgerSkuLabel)) },
    { label: 'Location', options: unique(rows.flatMap(row => [ledgerEndpointLabel(row.fromLocationId || row.from, row.statusBefore), ledgerEndpointLabel(row.toLocationId || row.to, row.statusAfter)]).filter(value => value && value !== '-')) },
    { label: 'Territory', options: unique(rows.flatMap(ledgerLocationTerritories)) },
    { label: 'User', options: unique(rows.map(ledgerUserLabel)) },
    { label: 'Reason', options: unique(rows.map(row => ledgerReasonLabel(row.reasonCode))) },
    { label: 'Method', options: unique(rows.map(row => ledgerReasonLabel(row.movementMethod))) },
    { label: 'Status', options: unique(rows.flatMap(row => [row.statusBefore, row.statusAfter])) },
    { label: 'Lot', options: unique(rows.map(row => row.lot)) },
  ];
}

function inventoryFilteredLedgerRows(rows, filters, search) {
  const term = String(search || '').trim().toLowerCase();
  return [...rows]
    .filter(row => Object.entries(filters || {}).every(([label, value]) => ledgerMatchesFilter(row, label, value)))
    .filter(row => {
      if (!term) return true;
      const haystack = [
        row.type,
        ledgerSkuLabel(row),
        ledgerEndpointLabel(row.fromLocationId || row.from, row.statusBefore),
        ledgerEndpointLabel(row.toLocationId || row.to, row.statusAfter),
        ledgerLocationTerritories(row).join(' '),
        ledgerUserLabel(row),
        ledgerReasonLabel(row.reasonCode),
        row.ref,
        row.relatedType,
        row.movementMethod,
        row.handledBy,
        row.carrier,
        row.bol,
        row.trackingNumber,
        rpPrintedLotLabel(row.sku, row.lot),
        row.note
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
}

function LedgerDetailModal({ row, go, onChange, onClose }) {
  const movement = ledgerMovement(row);
  const orderId = ledgerOrderId(row);
  const giveawayRef = ledgerGiveawayReference(row);
  const canReverseGiveaway = ledgerCanReverseGiveaway(row);
  const note = String(row.note || '').trim();
  const fromLabel = ledgerEndpointLabel(row.fromLocationId || row.from, row.statusBefore);
  const toLabel = ledgerEndpointLabel(row.toLocationId || row.to, row.statusAfter);
  const provenance = Array.isArray(row.provenance) ? row.provenance : [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const openOrder = () => {
    if (!orderId || !go) return;
    onClose();
    go('orders', { type: 'order', id: orderId });
  };
  const reverseGiveaway = async () => {
    const defaultNote = 'Duplicate giveaway entered by mistake.';
    const reason = window.prompt(`Reverse giveaway ${giveawayRef}? Add a reason for the audit log.`, defaultNote);
    if (reason === null) return;
    const notes = reason.trim() || defaultNote;
    setBusy(true);
    setError('');
    try {
      let result;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/inventory/giveaway/reverse', {
          method: 'POST',
          body: JSON.stringify({ ledgerId: row.id, notes, ref: giveawayRef })
        });
        await rpRefreshAfterMutation(`Giveaway ${response.ref || giveawayRef} reversed`);
        result = response;
      } else {
        result = reverseDemoGiveaway(row, notes);
      }
      if (result && result.alreadyReversed) toast(`Giveaway ${result.ref || giveawayRef} was already reversed`, 'info');
      else toast(`Giveaway ${result && result.ref || giveawayRef} reversed - ${result && result.quantity || Math.abs(Number(row.qty || 0))} cs restored`, 'ok');
      onChange && onChange();
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not reverse giveaway.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Ledger Item" sub={`${row.type || 'Inventory event'} - ${row.ref || row.id || 'No reference'}`} onClose={onClose} width={760}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Close</button>{canReverseGiveaway && <button className="btn" disabled={busy} onClick={reverseGiveaway}><Icon name="refresh" size={15} />{busy ? 'Reversing...' : 'Reverse Giveaway'}</button>}{orderId && <button className="btn btn-primary" disabled={busy} onClick={openOrder}><Icon name="orders" size={15} />Open Order</button>}</>}>
      {row.reconciliationOnly && <InfoNote icon="alert"><strong>Bookkeeping reconciliation — no physical handoff.</strong> {row.reconciliationNote} The original entry is retained; location balances must be checked against physical counts.</InfoNote>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
        <div className="card" style={{ padding: 14, background: 'var(--surface-2)' }}>
          <div className="field-label">Movement</div>
          <div className="mono" style={{ fontSize: 24, fontWeight: 800, color: movement.color }}>{movement.label}</div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>{movement.tone === 'out' ? `Removed from ${fromLabel}` : movement.tone === 'in' ? `Added to ${toLabel}` : `${fromLabel} to ${toLabel}`}</div>
        </div>
        <div className="card" style={{ padding: 14, background: 'var(--surface-2)' }}>
          <div className="field-label">Product</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={rpSkuItem(row.sku) || row.sku} size={26} /><b>{ledgerSkuLabel(row)}</b></div>
          <div className="mono" style={{ color: 'var(--muted)', fontSize: 12, marginTop: 5 }}>Lot {rpPrintedLotLabel(row.sku, row.lot) || 'UNASSIGNED'}</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Row k="Date" v={String(row.date || '').replace('T', ' ').slice(0, 22)} />
        <Row k="Event" v={row.type || '-'} />
        <Row k="Lot group" v={row.lotGroup || '-'} />
        <Row k="Produced" v={row.lotProducedAt || row.producedAt || '-'} />
        <Row k="Expires" v={row.lotExpiresAt || row.expiresAt || '-'} />
        <Row k="Received" v={row.lotReceivedAt || row.receivedAt || '-'} />
        <Row k="From" v={fromLabel} />
        <Row k="To" v={toLabel} />
        <Row k="Status before" v={row.statusBefore || '-'} />
        <Row k="Status after" v={row.statusAfter || '-'} />
        <Row k="Owner before" v={row.ownerBefore || '-'} />
        <Row k="Owner after" v={row.ownerAfter || '-'} />
        <Row k="User" v={ledgerUserLabel(row)} />
        <Row k="Reason" v={ledgerReasonLabel(row.reasonCode)} />
        <Row k="Movement method" v={ledgerReasonLabel(row.movementMethod)} />
        <Row k="Handled by" v={row.handledBy || '-'} />
        <Row k="Carrier" v={row.carrier || '-'} />
        <Row k="BOL" v={row.bol || '-'} />
        <Row k="Tracking / PRO" v={row.trackingNumber || '-'} />
        <Row k="Reference" v={row.ref || '-'} />
        <Row k="Ledger ID" v={row.id || '-'} muted />
        {row.createdBy && <Row k="Raw user ID" v={row.createdBy} muted />}
      </div>
      <div style={{ marginTop: 16 }}>
        <div className="field-label">Notes</div>
        <div style={{ whiteSpace: 'pre-wrap', border: '1px solid var(--line)', borderRadius: 10, padding: 12, color: note ? 'var(--ink-soft)' : 'var(--faint)', background: 'var(--surface-2)', lineHeight: 1.45 }}>{note || 'No notes recorded.'}</div>
      </div>
      <div style={{ marginTop: 16 }}>
        <div className="field-label">Historical Provenance</div>
        {provenance.length ? <div style={{ display: 'grid', gap: 8 }}>{provenance.map(allocation => (
          <div key={allocation.id || allocation.allocationKey} className="card" style={{ padding: 12, boxShadow: 'none', background: 'var(--surface-2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}><b>{allocation.quantity.toLocaleString()} cases</b><span className="pill" style={inventoryLotTone(allocation.confidence === 'verified' ? 'Verified' : allocation.confidence === 'unknown' ? 'Unverified' : 'Released')}>{allocation.confidence}</span></div>
            <div style={{ marginTop: 6, color: 'var(--ink-soft)', fontSize: 12.5, fontWeight: 650 }}>{(allocation.routeLocationIds || []).map(inventoryLocationName).join(' -> ')}</div>
            <div style={{ marginTop: 4, color: 'var(--muted)', fontSize: 12 }}>{ledgerReasonLabel(allocation.movementMethod)}{allocation.handlerName ? ` - ${allocation.handlerName}` : ''}{allocation.movedAt ? ` - ${String(allocation.movedAt).slice(0, 10)}` : ''}</div>
            {allocation.notes && <div style={{ marginTop: 5, color: 'var(--muted)', fontSize: 12 }}>{allocation.notes}</div>}
          </div>
        ))}</div> : <div style={{ border: '1px dashed var(--line)', borderRadius: 10, padding: 12, color: 'var(--faint)', background: 'var(--surface-2)' }}>{!fromLabel || fromLabel === '-' ? 'No origin route has been allocated to this source-less inbound row.' : 'Immediate source and destination are recorded in the ledger; no historical provenance override is attached.'}</div>}
      </div>
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function LedgerTable({ rows, go, onChange }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {selected && <LedgerDetailModal row={selected} go={go} onChange={onChange} onClose={() => setSelected(null)} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--muted)' }}>
        <Icon name="layers" size={15} />The ledger is immutable. Click any row to audit source, destination, notes, user, and related order.
      </div>
      <table className="rp-table">
        <thead><tr><th>Date</th><th>Event</th><th>SKU</th><th>From</th><th>To</th><th className="num">Qty</th><th>User</th><th>Ref</th><th>Provenance</th><th>Note</th></tr></thead>
        <tbody>
          {rows.map((l, i) => {
            const s = rpSkuItem(l.sku);
            const note = String(l.note || l.notes || '').trim();
            const movement = ledgerMovement(l);
            return (
              <tr key={l.id || `${l.date}-${l.ref}-${i}`} style={{ cursor: 'pointer' }} onClick={() => setSelected(l)}>
                <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{String(l.date || '').replace('T', ' ').slice(0, 22)}</td>
                <td><span className="pill" style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)', border: '1px solid var(--line)' }} >{l.type}</span>{l.reconciliationOnly && <div style={{ color: 'var(--amber-deep)', fontSize: 11, marginTop: 4 }}>Bookkeeping reconciliation</div>}</td>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FlavorChip sku={s || l.sku} size={20} /><span style={{ fontWeight: 500 }}>{ledgerSkuLabel(l)}</span></div></td>
                <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{ledgerEndpointLabel(l.fromLocationId || l.from, l.statusBefore)}</td>
                <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{ledgerEndpointLabel(l.toLocationId || l.to, l.statusAfter)}</td>
                <td className="num" style={{ fontWeight: 800, color: movement.color }}>{movement.label}</td>
                <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{ledgerUserLabel(l)}</td>
                <td className="mono" style={{ fontSize: 12, color: 'var(--info)' }}>{l.ref}</td>
                <td>{Array.isArray(l.provenance) && l.provenance.length ? <span className="pill" style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}>{l.provenance.length} route{l.provenance.length === 1 ? '' : 's'}</span> : (!String(l.fromLocationId || l.from || '').trim() && String(l.toLocationId || l.to || '').trim() && /receipt|opening/i.test(String(l.type || '')) ? <span className="pill" style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)' }}>Needs review</span> : <span style={{ color: 'var(--faint)' }}>-</span>)}</td>
                <td title={note} style={{ color: note ? 'var(--muted)' : 'var(--faint)', fontSize: 12.5, maxWidth: 340, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note ? note.slice(0, 180) : '-'}</td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan="10" style={{ textAlign: 'center', color: 'var(--faint)', padding: 32 }}>No ledger rows match these filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function requestCases(req) {
  return req.totalCases || (req.lines || []).reduce((sum, line) => sum + (line.qty || 0), 0);
}
function locName(id) {
  const l = RP.loc(id);
  return l ? l.name : (id || 'Unknown');
}
function requestLineLabel(line) {
  return inventorySkuExternalLabel(line && line.sku);
}
function StockRequestsTable({ requests, onSelect }) {
  const rows = [...(requests || [])].sort((a, b) => {
    const rank = { Pending: 0, Approved: 1, Fulfilled: 2, Rejected: 3 };
    return (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || String(b.created || '').localeCompare(String(a.created || ''));
  });

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--muted)' }}>
        <Icon name="layers" size={15} />Field stock requests are approved here. Fulfilled requests post transfer rows to the immutable inventory ledger.
      </div>
      <table className="rp-table">
        <thead><tr><th>Request</th><th>Requested By</th><th>Route</th><th>Ownership</th><th className="num">Cases</th><th>Items</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map(req => (
            <tr key={req.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(req.id)}>
              <td><div className="mono" style={{ fontWeight: 700, color: 'var(--ink)' }}>{req.id}</div><div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 2 }}>{rpFormatLocalDateTime(req.created, { empty: '' })}</div></td>
              <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Avatar name={req.requester || req.requestedBy} size={26} color="var(--navy)" /><span style={{ fontSize: 12.5, fontWeight: 600 }}>{req.requester || req.requestedBy || 'Field rep'}</span></div></td>
              <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{locName(req.source)} <span style={{ color: 'var(--faint)' }}>{'->'}</span> {locName(req.destination)}</td>
              <td style={{ color: 'var(--muted)' }}>{req.ownership}</td>
              <td className="num" style={{ fontWeight: 700 }}>{requestCases(req)}</td>
              <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{(req.lines || []).slice(0, 2).map(requestLineLabel).join(', ')}{(req.lines || []).length > 2 ? ' +' + ((req.lines || []).length - 2) : ''}</td>
              <td><Pill status={req.status} /></td>
              <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No stock requests yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
function StockRequestModal({ id, onClose, onChange }) {
  const req = RP.stockRequest(id);
  const [notes, setNotes] = useState(req ? (req.reviewNotes || '') : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!req) return null;

  const reviewLabel = (action) => action === 'fulfill' ? 'fulfilled' : action + 'd';
  const notificationSuffix = (notification) => {
    if (!notification) return '';
    if (notification.sent) return '; rep notified';
    if (notification.reason === 'resend_not_configured') return '; email not configured';
    if (notification.reason === 'requester_lookup_failed') return '; requester lookup failed';
    if (notification.reason === 'requester_email_missing') return '; requester email missing';
    if (notification.reason === 'requester_email_invalid') return '; requester email invalid';
    if (notification.attempted) return '; email failed';
    return '';
  };

  const post = async (action) => {
    if (busy) return;
    setBusy(true);
    setError('');
    let notification = null;
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/inventory/stock-request/review', {
          method: 'POST',
          body: JSON.stringify({ action, id: req.id, notes })
        });
        notification = response.notification || null;
        RP.upsertStockRequest(response.request);
        await rpRefreshAfterMutation(`Stock request ${req.id} ${reviewLabel(action)}`);
      } else {
        if (action === 'fulfill') {
          (req.lines || []).forEach(line => RP.addTransfer({ sku: line.sku, from: req.source, to: req.destination, qty: line.qty, ref: req.id }));
          req.status = 'Fulfilled';
        } else {
          req.status = action === 'approve' ? 'Approved' : 'Rejected';
        }
        req.notes = notes;
      }
      const notificationIssue = notification && !notification.sent && !(window.RP_AUTH && window.RP_AUTH.demo);
      toast(`Stock request ${req.id} ${reviewLabel(action)}${notificationSuffix(notification)}`, notificationIssue || action === 'reject' ? 'warn' : 'ok');
      onChange && onChange();
      if (action !== 'approve') onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update stock request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={req.id} sub={`${locName(req.source)} -> ${locName(req.destination)}`} onClose={onClose} width={620}
      footer={<>
        <button className="btn" disabled={busy} onClick={onClose}>Close</button>
        {!['Rejected', 'Fulfilled'].includes(req.status) && <button className="btn" disabled={busy} style={{ color: 'var(--danger)' }} onClick={() => post('reject')}><Icon name="x" size={15} />Reject</button>}
        {req.status === 'Pending' && <button className="btn" disabled={busy} onClick={() => post('approve')}><Icon name="check" size={15} />Approve</button>}
        {['Pending', 'Approved'].includes(req.status) && <button className="btn btn-primary" disabled={busy} onClick={() => post('fulfill')}><Icon name="layers" size={15} />{busy ? 'Posting...' : 'Fulfill Transfer'}</button>}
      </>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Status</div><Pill status={req.status} /></div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Cases</div><span className="disp" style={{ fontSize: 22 }}>{requestCases(req)}</span></div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Ownership</div><span style={{ fontSize: 13, fontWeight: 700 }}>{req.ownership}</span></div>
      </div>
      <div className="card" style={{ overflow: 'hidden', boxShadow: 'none', marginBottom: 16 }}>
        <table className="rp-table">
          <thead><tr><th>SKU</th><th className="num">Requested</th><th className="num">Available at Source</th></tr></thead>
          <tbody>
            {(req.lines || []).map(line => {
              const s = rpSkuItem(line.sku);
              const available = rpInventoryAvailableCases(line.sku, req.source) || 0;
              return (
                <tr key={line.sku}>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={s || line.sku} size={24} /><span style={{ fontWeight: 600 }}>{requestLineLabel(line)}</span></div></td>
                  <td className="num" style={{ fontWeight: 700 }}>{line.qty}</td>
                  <td className="num" style={{ fontWeight: 700, color: available >= line.qty ? 'var(--ok)' : 'var(--danger)' }}>{available}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {req.requestNotes && <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '11px 13px', marginBottom: 14, color: 'var(--muted)', fontSize: 12.5 }}><div className="eyebrow" style={{ marginBottom: 5 }}>Requester note</div>{req.requestNotes}</div>}
      <MField label="Ops note">
        <textarea className="field" rows="3" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Approval, shortage, or fulfillment notes..." style={{ resize: 'none' }} />
      </MField>
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function receiptCases(receipt) {
  return receipt.totalCases || (receipt.lines || []).reduce((sum, line) => sum + (line.qty || 0) + (line.damagedQuantity || 0), 0);
}
function receiptPhotoCount(receipt) {
  if (receipt.photoCount != null) return receipt.photoCount;
  return (receipt.lines || []).reduce((sum, line) => sum + ((line.photos || []).length) + ((line.damagePhotos || []).length), 0);
}
function receiptLineLabel(line) {
  return inventorySkuExternalLabel(line && line.sku);
}
function receiptDate(receipt) {
  return rpFormatLocalDateTime(receipt.created || receipt.receivedAt, { empty: '' });
}
function receiptSourceId(receipt) {
  return receipt.sourceLocationId || receipt.source || receipt.fromLocationId || '';
}
function receiptSourceName(receipt) {
  const id = receiptSourceId(receipt);
  return id ? locName(id) : 'Missing source';
}
function isTransferAcknowledgement(receipt) {
  return receipt && receipt.receiptMode === 'transfer_acknowledgement';
}
function fieldReceiptSourceOptions(receipt) {
  const currentId = receiptSourceId(receipt);
  const destinationId = receipt && receipt.location;
  const sourceTypes = new Set(['Main Warehouse', 'Micro-Warehouse', 'Distributor', 'FBA']);
  return (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : [])
    .filter(location => location && location.id && location.id !== destinationId && location.active !== false && (sourceTypes.has(location.type) || location.id === currentId))
    .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
}
function FieldReceiptsTable({ receipts, onSelect }) {
  const rows = [...(receipts || [])].sort((a, b) => {
    const rank = { Pending: 0, Approved: 1, Rejected: 2 };
    return (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || String(b.created || '').localeCompare(String(a.created || ''));
  });

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--muted)' }}>
        <Icon name="download" size={15} />Transfer acknowledgements confirm inventory already moved by a completed transfer and never post it twice. Only older unplanned receipts stay pending for review.
      </div>
      <table className="rp-table">
        <thead><tr><th>Receipt</th><th>Submitted By</th><th>Transfer</th><th className="num">Cases</th><th className="num">Damage</th><th>Items</th><th>Photos</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map(receipt => (
            <tr key={receipt.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(receipt.id)}>
              <td><div className="mono" style={{ fontWeight: 700, color: 'var(--ink)' }}>{receipt.id}</div><div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 2 }}>{receiptDate(receipt)}</div></td>
              <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Avatar name={receipt.submitter || receipt.submittedBy} size={26} color="var(--navy)" /><span style={{ fontSize: 12.5, fontWeight: 600 }}>{receipt.submitter || receipt.submittedBy || 'Field rep'}</span></div></td>
              <td style={{ color: 'var(--muted)', fontSize: 12.5 }}><div style={{ color: receiptSourceId(receipt) ? 'var(--muted)' : 'var(--danger)', fontWeight: receiptSourceId(receipt) ? 500 : 800 }}>{receiptSourceName(receipt)}</div><div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>{receipt.linkedTransferRef ? `${receipt.linkedTransferRef} · ` : ''}to {locName(receipt.location)}</div></td>
              <td className="num" style={{ fontWeight: 700 }}>{receiptCases(receipt)}</td>
              <td className="num" style={{ fontWeight: 700, color: receipt.damagedCases ? 'var(--danger)' : 'var(--muted)' }}>{receipt.damagedCases || 0}</td>
              <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{(receipt.lines || []).slice(0, 2).map(receiptLineLabel).join(', ')}{(receipt.lines || []).length > 2 ? ' +' + ((receipt.lines || []).length - 2) : ''}</td>
              <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{receiptPhotoCount(receipt)} file{receiptPhotoCount(receipt) === 1 ? '' : 's'}</td>
              <td><Pill status={receipt.status} /></td>
              <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan="9" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No field receipts yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
function addDemoApprovedReceipt(receipt) {
  const user = typeof rpDisplayName === 'function' ? rpDisplayName() : 'Operations';
  const sourceId = receiptSourceId(receipt);
  (receipt.lines || []).forEach(line => {
    const lot = line.lot || 'UNASSIGNED';
    const addBalance = (status, quantity) => {
      if (!(quantity > 0)) return;
      if (sourceId) {
        const source = RP.INV.find(row => inventorySkuMatches(row.sku, line.sku) && row.loc === sourceId && row.owner === 'Ranch Power' && row.status === 'Available');
        if (source) source.qty = Math.max(0, (source.qty || 0) - quantity);
      }
      const existing = RP.INV.find(row => inventorySkuMatches(row.sku, line.sku) && row.loc === receipt.location && row.owner === 'Ranch Power' && row.status === status && row.lot === lot);
      if (existing) existing.qty += quantity;
      else RP.INV.push({ sku: line.sku, loc: receipt.location, owner: 'Ranch Power', status, lot, qty: quantity });
      RP.LEDGER.unshift({
        date: new Date().toISOString(),
        type: status === 'Damaged' ? 'Field Damage' : 'Field Receipt',
        sku: line.sku,
        from: sourceId,
        fromLocationId: sourceId,
        to: receipt.location,
        toLocationId: receipt.location,
        qty: quantity,
        statusBefore: 'Available',
        statusAfter: status,
        user,
        ref: receipt.id,
        lot,
        note: [receipt.notes || '', sourceId ? `${locName(sourceId)} -> ${locName(receipt.location)}` : 'Missing source'].filter(Boolean).join(' | ')
      });
    };
    addBalance('Fronted to Rep', line.qty || 0);
    addBalance('Damaged', line.damagedQuantity || 0);
  });
}
function adjustDemoAvailableInventory(skuId, locationId, lot, quantity) {
  if (!(quantity > 0) || !locationId) return;
  const available = (RP.INV || [])
    .filter(row => inventorySkuMatches(row.sku, skuId) && row.loc === locationId && row.owner === 'Ranch Power' && row.status === 'Available')
    .reduce((sum, row) => sum + Math.max(0, row.qty || 0), 0);
  if (available < quantity) throw new Error(`Only ${available} cases of ${inventorySkuName(skuId)} are available at ${locName(locationId)}.`);
  let remaining = quantity;
  (RP.INV || [])
    .filter(row => inventorySkuMatches(row.sku, skuId) && row.loc === locationId && row.owner === 'Ranch Power' && row.status === 'Available')
    .forEach(row => {
      if (remaining <= 0) return;
      const used = Math.min(remaining, Math.max(0, row.qty || 0));
      row.qty = Math.max(0, (row.qty || 0) - used);
      remaining -= used;
    });
  RP.INV = (RP.INV || []).filter(row => Number(row.qty || 0) !== 0);
}
function updateDemoFieldReceiptSource(receipt, sourceId, note) {
  const previousSourceId = receiptSourceId(receipt);
  if (!sourceId) throw new Error('Select the source location first.');
  if (sourceId === receipt.location) throw new Error('Source and destination locations must differ.');
  if (receipt.status !== 'Pending') throw new Error('Approved or rejected receipt sources are immutable. Add a provenance revision instead.');
  receipt.source = sourceId;
  receipt.sourceLocationId = sourceId;
  receipt.sourceName = locName(sourceId);
  receipt.reviewNotes = note ? [receipt.reviewNotes || '', note].filter(Boolean).join('\n') : receipt.reviewNotes;
  receipt.updated = new Date().toISOString();
  return receipt;
}
function FieldReceiptModal({ id, onClose, onChange }) {
  const receipt = (RP.fieldReceipt && RP.fieldReceipt(id)) || (RP.FIELD_RECEIPTS || []).find(x => x.id === id);
  const [notes, setNotes] = useState(receipt ? (receipt.reviewNotes || '') : '');
  const [sourceId, setSourceId] = useState(receipt ? receiptSourceId(receipt) : '');
  const [sourceNote, setSourceNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!receipt) return;
    setNotes(receipt.reviewNotes || '');
    setSourceId(receiptSourceId(receipt));
    setSourceNote('');
    setError('');
  }, [id]);
  if (!receipt) return null;

  const savedSourceId = receiptSourceId(receipt);
  const sourceOptions = fieldReceiptSourceOptions(receipt);
  const sourceChanged = Boolean(sourceId) && sourceId !== savedSourceId;
  const sourceConflict = Boolean(sourceId) && sourceId === receipt.location;
  const sourceCanSave = receipt.status === 'Pending' && !receipt.immutable && Boolean(sourceId) && sourceChanged && !sourceConflict;

  const post = async (action) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const reviewNotes = notes || receipt.reviewNotes || '';
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/inventory/field-receipt/review', {
          method: 'POST',
          body: JSON.stringify({ action, id: receipt.id, notes: reviewNotes })
        });
        if (RP.upsertFieldReceipt) RP.upsertFieldReceipt(response.receipt);
        await rpRefreshAfterMutation(`Field receipt ${receipt.id} ${action === 'approve' ? 'approved' : 'rejected'}`);
      } else {
        receipt.status = action === 'approve' ? 'Approved' : 'Rejected';
        receipt.reviewNotes = reviewNotes;
        receipt.updated = new Date().toISOString();
        if (action === 'approve') addDemoApprovedReceipt(receipt);
      }
      toast(`Field receipt ${receipt.id} ${action === 'approve' ? 'approved' : 'rejected'}`, action === 'approve' ? 'ok' : 'warn');
      onChange && onChange();
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not review field receipt.');
    } finally {
      setBusy(false);
    }
  };

  const saveSource = async () => {
    if (busy || !sourceCanSave) return;
    setBusy(true);
    setError('');
    const noteToSave = sourceNote;
    let savedReviewNotes = '';
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/inventory/field-receipt/source', {
          method: 'POST',
          body: JSON.stringify({ id: receipt.id, sourceLocationId: sourceId, notes: noteToSave })
        });
        if (RP.upsertFieldReceipt && response.receipt) RP.upsertFieldReceipt(response.receipt);
        savedReviewNotes = response.receipt && response.receipt.reviewNotes || '';
        await rpRefreshAfterMutation(`Field receipt ${receipt.id} source updated`);
      } else {
        updateDemoFieldReceiptSource(receipt, sourceId, noteToSave);
        savedReviewNotes = receipt.reviewNotes || '';
      }
      toast(`Source saved for ${receipt.id}`, 'ok');
      if (savedReviewNotes) setNotes(savedReviewNotes);
      else if (noteToSave) setNotes(prev => [prev || '', noteToSave].filter(Boolean).join('\n'));
      setSourceNote('');
      onChange && onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update field receipt source.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={receipt.id} sub={`${receiptSourceName(receipt)} -> ${locName(receipt.location)} - ${receiptCases(receipt)} cases`} onClose={onClose} width={700}
      footer={<>
        <button className="btn" disabled={busy} onClick={onClose}>Close</button>
        {receipt.status === 'Pending' && <button className="btn" disabled={busy} style={{ color: 'var(--danger)' }} onClick={() => post('reject')}><Icon name="x" size={15} />Reject</button>}
        {receipt.status === 'Pending' && <button className="btn btn-primary" disabled={busy || !savedSourceId || sourceChanged} onClick={() => post('approve')}><Icon name="check" size={15} />{busy ? 'Posting...' : 'Approve Legacy Handoff'}</button>}
      </>}>
      {isTransferAcknowledgement(receipt) && <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>Acknowledgement of completed transfer <span className="mono">{receipt.linkedTransferRef || '-'}</span>. Its exact SKU, lot, quantity, source, and destination were verified against the signed transfer; this acknowledgement posted zero inventory rows.</div>}
      {!isTransferAcknowledgement(receipt) && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>Legacy or unplanned handoff. Review its source and evidence before approving it into the historical ledger.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Status</div><Pill status={receipt.status} /></div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Sellable</div><span className="disp" style={{ fontSize: 22 }}>{receipt.sellableCases || 0}</span></div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Damaged</div><span className="disp" style={{ fontSize: 22, color: receipt.damagedCases ? 'var(--danger)' : 'var(--ink)' }}>{receipt.damagedCases || 0}</span></div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Photos</div><span className="disp" style={{ fontSize: 22 }}>{receiptPhotoCount(receipt)}</span></div>
      </div>
      <div className="card" style={{ overflow: 'hidden', boxShadow: 'none', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10, padding: 14, borderBottom: '1px solid var(--line)' }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 4 }}>Received From</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8, alignItems: 'center' }}>
              <select className="field" value={sourceId} onChange={e => setSourceId(e.target.value)} disabled={busy || receipt.status !== 'Pending' || receipt.immutable} style={{ color: sourceId ? 'var(--ink)' : 'var(--danger)', fontWeight: 800 }}>
                <option value="">Missing source</option>
                {sourceOptions.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}
              </select>
              <button className="btn btn-sm" disabled={busy || !sourceCanSave} onClick={saveSource}><Icon name="check" size={14} />Save</button>
            </div>
            {sourceConflict && <div style={{ marginTop: 6, color: 'var(--danger)', fontSize: 11.5, fontWeight: 800 }}>Source and receiving location must differ.</div>}
            {receipt.status === 'Pending' && receipt.immutable && <div style={{ marginTop: 6, color: 'var(--muted)', fontSize: 11.5, fontWeight: 700 }}>This signed receipt is immutable; reject and resubmit to change its source.</div>}
            {receipt.status !== 'Pending' && <div style={{ marginTop: 6, color: 'var(--muted)', fontSize: 11.5, fontWeight: 700 }}>After review, route corrections are recorded as append-only provenance.</div>}
          </div>
          <div><div className="eyebrow" style={{ marginBottom: 4 }}>Receiving Into</div><div style={{ fontWeight: 800 }}>{locName(receipt.location)}</div></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)', fontSize: 12 }}>
          <div><div className="eyebrow">Method</div><b>{ledgerReasonLabel(receipt.movementMethod)}</b></div>
          <div><div className="eyebrow">Handled By</div><b>{receipt.handledBy || receipt.submitter || '-'}</b></div>
          <div><div className="eyebrow">Carrier</div><b>{receipt.carrier || '-'}</b></div>
          <div><div className="eyebrow">BOL / Tracking</div><b className="mono">{receipt.bol || receipt.trackingNumber || '-'}</b></div>
        </div>
        <table className="rp-table">
          <thead><tr><th>SKU</th><th>Lot</th><th className="num">Sellable</th><th className="num">Damaged</th><th>Evidence</th></tr></thead>
          <tbody>
            {(receipt.lines || []).map((line, index) => {
              const s = rpSkuItem(line.sku);
              const photos = [...(line.photos || []), ...(line.damagePhotos || [])];
              return (
                <tr key={line.sku + index}>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={s || line.sku} size={24} /><span style={{ fontWeight: 600 }}>{receiptLineLabel(line)}</span></div></td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{line.lot || 'UNASSIGNED'}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{line.qty || 0}</td>
                  <td className="num" style={{ fontWeight: 700, color: line.damagedQuantity ? 'var(--danger)' : 'var(--muted)' }}>{line.damagedQuantity || 0}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {photos.map((photo, photoIndex) => (
                        <button key={(photo.path || photo.name || photoIndex) + photoIndex} className="btn btn-sm" disabled={!photo.path} onClick={e => { e.stopPropagation(); photo.path && rpOpenProtectedFile(photo.path); }}><Icon name="camera" size={14} />{photoIndex + 1}</button>
                      ))}
                      {!photos.length && <span style={{ color: 'var(--faint)', fontSize: 12 }}>No photos</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {receipt.notes && <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '11px 13px', color: 'var(--muted)', fontSize: 12.5, marginBottom: 14 }}><strong style={{ color: 'var(--ink)' }}>Rep note:</strong> {receipt.notes}</div>}
      {!savedSourceId && <div style={{ background: 'var(--danger-bg)', borderRadius: 10, padding: '11px 13px', color: 'var(--danger)', fontSize: 12.5, fontWeight: 800, marginBottom: 14 }}>This receipt is missing a source. Select the warehouse or storage location above and save it before approving or auditing the transfer.</div>}
      {receipt.status === 'Approved' && sourceChanged && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>Saving this approved receipt posts source correction rows so the ledger remains auditable and inventory balances move to the corrected source.</div>}
      {receipt.status === 'Pending' && sourceChanged && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>Save the source before approving so the ledger can transfer stock out of the right location.</div>}
      <MField label="Source correction note">
        <input className="field" value={sourceNote} onChange={e => setSourceNote(e.target.value)} placeholder="Why this source was added or corrected..." />
      </MField>
      <MField label="Review note">
        <textarea className="field" rows="3" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Approval, rejection, or inventory note..." style={{ resize: 'none' }} />
      </MField>
      {receipt.status === 'Pending' && <div style={{ marginTop: 12, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Legacy approval transfers cases out of the source location. Sellable cases become Fronted to Rep and damaged cases become Damaged at the receiving location.</div>}
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

/* ---- Elegant placeholder for phased sections ---- */
function StubView({ nav }) {
  const phases = {
    production: ['Production orders', 'Canning partner portal', 'BOM / formula', 'QA hold & release', 'Finished-goods receipt'],
    procurement: ['Supplier POs', 'Cans · lids · ingredients', 'Tariff / origin tracking', 'Reorder planning'],
    fba: ['Amazon shipments', 'FBA inventory & statuses', 'Fulfillment centers', 'Discrepancy & removal orders'],
    admin: ['Users & roles', 'Permissions matrix', 'Price lists', 'Approval rules', 'Audit log'],
  };
  const items = phases[nav.id] || ['Coming soon'];
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <PageHead eyebrow={`Roadmap`} title={nav.label} />
      <div className="card" style={{ padding: '48px 40px', textAlign: 'center', maxWidth: 640, margin: '40px auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
        <span style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--navy)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={nav.icon} size={30} /></span>
        <div>
          <h2 className="disp" style={{ margin: 0, fontSize: 24, textTransform: 'uppercase' }}>{nav.label}</h2>
          <p style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 420, margin: '8px auto 0', lineHeight: 1.5 }}>This module is mapped in the spec and scoped for a later phase. The screens below are the planned scope.</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 460 }}>
          {items.map(it => <span key={it} style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-soft)', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 100, padding: '7px 13px' }}>{it}</span>)}
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { InventoryView, StubView });


/* ===== desktop_ops.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Dispatch · Analytics · Trade Spend
   ============================================================ */

/* ============================================================ DISPATCH BOARD */
const DISPATCH_DRIVER_ROLES = new Set(['admin', 'leadership', 'ops', 'sales_manager', 'warehouse', 'dispatch', 'rep', 'partner']);
function dispatchTodayIso() {
  return typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10);
}
function dispatchStaffOptions() {
  return (Array.isArray(RP.USER_PROFILES) ? RP.USER_PROFILES : [])
    .filter(user => user && user.active !== false && DISPATCH_DRIVER_ROLES.has(rpNormalizeRole(user.role)))
    .map(user => ({
      id: String(user.id || user.email || user.name || '').trim(),
      name: String(user.name || user.displayName || user.display_name || user.email || user.id || '').trim(),
      role: rpRoleLabel(user.role),
      territory: user.territory || ''
    }))
    .filter(user => user.id && user.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}
function dispatchLaneSlug(value) {
  return String(value || 'lane').trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'lane';
}
function upsertDispatchLane(lane) {
  if (!lane || !lane.id) return null;
  if (!Array.isArray(RP.LANES)) RP.LANES = [];
  const next = { id: lane.id, name: lane.name || lane.id, driver: lane.driver || '', kind: lane.kind || 'Ranch Delivery' };
  const idx = RP.LANES.findIndex(item => item.id === next.id);
  if (idx >= 0) RP.LANES.splice(idx, 1, next);
  else RP.LANES.push(next);
  return next;
}

function dispatchJobsFromDirectory(items) {
  return (Array.isArray(items) ? items : []).map(rpDispatchJobFromItem).filter(Boolean);
}

function dispatchLiveManifests(jobs) {
  const groups = new Map();
  (Array.isArray(jobs) ? jobs : []).filter(job => job && job.routeId).forEach(job => {
    const current = groups.get(job.routeId) || {
      id: job.routeId,
      laneId: job.lane,
      laneName: job.laneName || job.lane,
      driver: job.driver || '',
      routeDate: job.routeDate || '',
      routeType: job.routeType || 'Ranch Delivery',
      status: job.routeStatus || 'Draft',
      loadCheck: job.routeLoadCheck || null,
      stops: []
    };
    current.stops.push({
      sequence: Number(job.stopSequence || 0),
      orderId: job.id,
      customer: job.label,
      territory: job.territory,
      cases: Number(job.cases || 0),
      window: job.window,
      orderStatus: job.status,
      stopStatus: job.stopStatus || 'Assigned',
      notes: job.stopNotes || ''
    });
    groups.set(job.routeId, current);
  });
  return Array.from(groups.values()).map(manifest => {
    manifest.stops.sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0) || String(left.orderId).localeCompare(String(right.orderId)));
    manifest.stopCount = manifest.stops.length;
    manifest.cases = manifest.stops.reduce((sum, stop) => sum + Number(stop.cases || 0), 0);
    return manifest;
  });
}

function DispatchView({ go, directory }) {
  const [view, setView] = useState('board');
  const [jobs, setJobs] = useState(() => (RP.DISPATCH || []).map(j => ({ ...j })));
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [optimizeBusy, setOptimizeBusy] = useState(false);
  const [laneModal, setLaneModal] = useState(false);
  const [, refreshLanes] = useState(0);

  useEffect(() => {
    if (directory.isLive) {
      if (directory.loaded) setJobs(dispatchJobsFromDirectory(directory.items));
      return;
    }
    setJobs((RP.DISPATCH || []).map(job => ({ ...job })));
  }, [directory.isLive, directory.loaded, directory.items]);

  const move = async (id, laneId) => {
    const job = jobs.find(j => j.id === id);
    const lane = lanes.find(l => l.id === laneId);
    if (!job || !lane || job.lane === laneId || busyId) return;

    const before = jobs.map(j => ({ ...j }));
    setJobs(js => js.map(j => j.id === id ? {
      ...j,
      driver: lane.driver,
      lane: laneId,
      laneName: lane.name,
      routeType: lane.kind || j.routeType,
      status: laneId === 'unassigned' || j.status === 'Delivered' ? j.status : 'Dispatched'
    } : j));

    if (rpIsDemoMode() || id.startsWith('TO')) return;

    setBusyId(id);
    try {
      const response = await rpApi('/api/dispatch/assign', {
        method: 'POST',
        body: JSON.stringify({
          orderId: id,
          laneId,
          laneName: lane.name,
          driverName: lane.driver,
          routeType: lane.kind || job.routeType || job.delivery || 'Ranch Delivery',
          routeDate: job.routeDate || job.window || (typeof rpTodayYmd === 'function' ? rpTodayYmd(job) : new Date().toISOString().slice(0, 10))
        })
      });
      if (response && response.orderStatus) {
        setJobs(js => js.map(j => j.id === id ? { ...j, status: response.orderStatus } : j));
      }
      await rpRefreshAfterMutation(`Route assignment ${id} saved`);
      toast(`${id} assigned to ${lane.name}`, 'ok');
    } catch (err) {
      setJobs(before);
      const message = err && err.message ? err.message : 'Route assignment could not be saved.';
      toast(message, 'danger');
    } finally {
      setBusyId(null);
    }
  };

  const optimizeRoutes = async () => {
    if (optimizeBusy) return;
    if (rpIsDemoMode()) {
      toast('Routes optimized in demo mode - live stop sequences were not changed.', 'info');
      return;
    }

    setOptimizeBusy(true);
    try {
      const result = await rpApi('/api/dispatch/optimize', {
        method: 'POST',
        body: JSON.stringify({ routeDate: dispatchTodayIso() })
      });
      const changed = result && Number.isFinite(Number(result.changedStops)) ? Number(result.changedStops) : 0;
      const routeCount = result && Number.isFinite(Number(result.routeCount)) ? Number(result.routeCount) : 0;
      const concurrentChanges = result && Number.isFinite(Number(result.concurrentChanges)) ? Number(result.concurrentChanges) : 0;
      await rpRefreshAfterMutation('Route sequence optimization saved');
      if (concurrentChanges) {
        toast(`${routeCount} routes optimized; ${concurrentChanges} stops changed assignment during optimization and were safely skipped.`, 'info');
      } else {
        toast(changed ? `${routeCount} routes optimized - ${changed} stop positions updated` : `${routeCount} routes already in optimized order`, 'ok');
      }
    } catch (err) {
      const message = err && err.message ? err.message : 'Routes could not be optimized.';
      toast(message, 'danger');
    } finally {
      setOptimizeBusy(false);
    }
  };
  const laneMap = new Map((RP.LANES || []).map(lane => [lane.id, lane]));
  jobs.filter(job => job && job.lane && job.lane !== 'unassigned').forEach(job => {
    laneMap.set(job.lane, {
      id: job.lane,
      name: job.laneName || job.lane,
      driver: job.driver || '',
      kind: job.routeType || 'Ranch Delivery'
    });
  });
  const lanes = [{ id: 'unassigned', name: 'Unassigned', driver: 'Drag to a lane →', kind: '' }, ...Array.from(laneMap.values())];

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {laneModal && <DispatchLaneModal onClose={() => setLaneModal(false)} onAdded={() => refreshLanes(x => x + 1)} />}
      <PageHead eyebrow="Tuesday · June 10, 2026" title="Dispatch & Logistics"
        sub="Drag jobs onto a driver or carrier lane to build the day."
        actions={<><Segmented size="sm" options={[{ value: 'board', label: 'Board' }, { value: 'chep', label: 'CHEP Pallets' }]} value={view} onChange={setView} />{view === 'board' && <button className="btn btn-sm" onClick={() => setLaneModal(true)}><Icon name="plus" size={15} />Add Dispatcher</button>}{view === 'board' && <button className="btn btn-sm btn-primary" disabled={optimizeBusy} style={{ opacity: optimizeBusy ? 0.7 : 1 }} onClick={optimizeRoutes}><Icon name="truck" size={15} />{optimizeBusy ? 'Optimizing all routes...' : 'Optimize Routes'}</button>}</>} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
        <button className={view === 'manifest' ? 'btn btn-sm btn-primary' : 'btn btn-sm'} onClick={() => setView('manifest')}><Icon name="doc" size={14} />Manifests</button>
      </div>
      {directory.isLive && directory.initialLoading && <div className="card" style={{ padding: '14px 16px', color: 'var(--muted)', marginBottom: 12 }}>Loading the live dispatch board...</div>}
      {directory.isLive && directory.error && !directory.loadingMore && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, fontWeight: 700 }}><span>{directory.error}</span><button className="btn btn-sm" onClick={directory.retry}>Retry</button></div>}
      {view === 'manifest' && <DispatchManifestView jobs={jobs} directory={directory} />}
      {view === 'chep' ? <div style={{ overflowY: 'auto' }}><ChepPanel /></div> : (
      <div className="rp-board" style={{ display: view === 'manifest' ? 'none' : 'grid', gridTemplateColumns: `repeat(${lanes.length}, minmax(220px, 1fr))`, gap: 14, flex: 1, minHeight: 0 }}>
        {lanes.map(lane => {
          const laneJobs = jobs.filter(j => j.lane === lane.id);
          const totCases = laneJobs.reduce((s, j) => s + j.cases, 0);
          const isOver = over === lane.id;
          return (
            <div key={lane.id}
              onDragOver={e => { e.preventDefault(); setOver(lane.id); }}
              onDragLeave={() => setOver(o => o === lane.id ? null : o)}
              onDrop={e => { e.preventDefault(); if (dragId) move(dragId, lane.id); setDragId(null); setOver(null); }}
              style={{ display: 'flex', flexDirection: 'column', background: lane.id === 'unassigned' ? 'var(--surface-2)' : 'var(--surface)', border: '1px solid ' + (isOver ? 'var(--amber)' : 'var(--line)'), borderRadius: 12, overflow: 'hidden', boxShadow: isOver ? '0 0 0 3px var(--amber-soft)' : 'var(--shadow-sm)', transition: 'box-shadow .15s, border-color .15s' }}>
              <div style={{ padding: '13px 15px', borderBottom: '1px solid var(--line)', background: lane.id === 'unassigned' ? 'transparent' : 'var(--navy)', color: lane.id === 'unassigned' ? 'var(--ink)' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="disp" style={{ fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{lane.name}</span>
                  {lane.id !== 'unassigned' && <Icon name="truck" size={16} style={{ opacity: 0.8 }} />}
                </div>
                <div style={{ fontSize: 11.5, marginTop: 2, opacity: lane.id === 'unassigned' ? 0.6 : 0.8, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{lane.driver}{lane.kind && ' · ' + lane.kind}</span>
                  {laneJobs.length > 0 && <span className="mono">{totCases} cs</span>}
                </div>
              </div>
              <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 9 }}>
                {laneJobs.map(j => {
                  const transfer = j.id.startsWith('TO');
                  const saving = busyId === j.id;
                  return (
                    <div key={j.id} draggable={!saving} onDragStart={() => !saving && setDragId(j.id)} onDragEnd={() => { setDragId(null); setOver(null); }}
                      style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 9, padding: '11px 12px', cursor: saving ? 'wait' : 'grab', boxShadow: dragId === j.id ? 'var(--shadow-lg)' : 'var(--shadow-sm)', opacity: dragId === j.id || saving ? 0.5 : 1, borderLeft: '3px solid ' + (transfer ? 'var(--info)' : 'var(--amber)') }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span className="mono nowrap" style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--ink)' }}>{j.id}</span>
                        <span className="nowrap" style={{ fontSize: 10.5, fontWeight: 600, color: transfer ? 'var(--info)' : 'var(--amber-deep)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{saving ? 'Saving' : transfer ? 'Transfer' : j.territory}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginTop: 4 }}>{j.label}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                        <span className="mono" style={{ fontSize: 11.5, color: 'var(--muted)' }}>{j.cases} cases</span>
                        {j.window !== '—' && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', background: 'var(--surface-2)', borderRadius: 6, padding: '2px 7px' }}>{j.window}</span>}
                      </div>
                    </div>
                  );
                })}
                {laneJobs.length === 0 && <div style={{ flex: 1, minHeight: 80, border: '1.5px dashed var(--line)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faint)', fontSize: 12.5 }}>{isOver ? 'Release to assign' : 'Drop jobs here'}</div>}
              </div>
            </div>
          );
        })}
      </div>
      )}
      {view !== 'chep' && directory.isLive && <div style={{ paddingTop: 12 }}><RpServerLoadMore error={directory.loadingMore ? directory.error : ''} items={directory.items} label="dispatch jobs" loadingMore={directory.loadingMore} onLoadMore={directory.loadMore} onRetry={directory.retry} page={directory.page} total={directory.total} /></div>}
    </div>
  );
}

function DispatchLaneModal({ onClose, onAdded }) {
  const staff = dispatchStaffOptions();
  const [profileId, setProfileId] = useState(staff[0] ? staff[0].id : 'custom');
  const [laneName, setLaneName] = useState('');
  const [driverName, setDriverName] = useState('');
  const [routeType, setRouteType] = useState('Ranch Delivery');
  const [routeDate, setRouteDate] = useState(dispatchTodayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const selected = profileId !== 'custom' ? staff.find(user => user.id === profileId) : null;
  const resolvedDriver = selected ? selected.name : driverName.trim();
  const resolvedLaneName = laneName.trim() || resolvedDriver;

  const save = async () => {
    if (busy) return;
    setError('');
    if (!resolvedLaneName) {
      setError('Name the dispatcher, driver, or carrier lane.');
      return;
    }
    setBusy(true);
    try {
      let lane;
      if (rpIsDemoMode()) {
        lane = upsertDispatchLane({
          id: selected ? `staff-${dispatchLaneSlug(selected.id)}` : `lane-${dispatchLaneSlug(resolvedLaneName)}`,
          name: resolvedLaneName,
          driver: resolvedDriver,
          kind: routeType
        });
      } else {
        const response = await rpApi('/api/dispatch/lane', {
          method: 'POST',
          body: JSON.stringify({
            driverName: resolvedDriver,
            driverProfileId: selected ? selected.id : undefined,
            laneName: resolvedLaneName,
            routeDate,
            routeType
          })
        });
        lane = upsertDispatchLane(response.lane);
        await rpRefreshAfterMutation(`${lane.name} dispatch lane added`);
      }
      toast(`${lane.name} added to dispatch`, 'ok');
      onAdded && onAdded(lane);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not add dispatcher.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add Dispatcher" sub="Create a lane for a staff hand-delivery route or carrier." onClose={onClose} width={540}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Adding...' : 'Add Lane'}</button></>}>
      <MField label="Dispatcher / driver">
        <select className="field" value={profileId} onChange={e => { setProfileId(e.target.value); setLaneName(''); setDriverName(''); }}>
          {staff.map(user => <option key={user.id} value={user.id}>{user.name} · {user.role}{user.territory ? ' · ' + user.territory : ''}</option>)}
          <option value="custom">Custom carrier / driver</option>
        </select>
      </MField>
      {profileId === 'custom' && <MField label="Driver or carrier name" required><input className="field" value={driverName} onChange={e => setDriverName(e.target.value)} placeholder="e.g. Wade, Darren, Local Courier" /></MField>}
      <MField label="Lane label" required><input className="field" value={laneName} onChange={e => setLaneName(e.target.value)} placeholder={resolvedDriver || 'e.g. Wade Hand Delivery'} /></MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Route type" half>
          <select className="field" value={routeType} onChange={e => setRouteType(e.target.value)}>
            <option>Ranch Delivery</option>
            <option>Rep Hand Delivery</option>
            <option>Carrier / Freight</option>
          </select>
        </MField>
        <MField label="Route date" half><input className="field" type="date" value={routeDate} onChange={e => setRouteDate(e.target.value)} /></MField>
      </div>
      {selected && <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}><Icon name="dispatch" size={15} />This lane is tied to {selected.name}'s active staff profile.</div>}
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

/* ============================================================ ANALYTICS — heat map */
function dispatchManifestText(manifest) {
  const lines = [
    `${manifest.laneName || 'Route'} - ${manifest.routeDate || 'Unscheduled'}`,
    `Driver: ${manifest.driver || 'Unassigned'} | Type: ${manifest.routeType || 'Delivery'} | Status: ${manifest.status || 'Draft'}`,
    `Stops: ${manifest.stopCount || 0} | Cases: ${manifest.cases || 0}`
  ];

  if (manifest.loadCheck) {
    lines.push(`Load check: ${manifest.loadCheck.status || 'Complete'} | Expected ${manifest.loadCheck.expectedCases || 0} | Counted ${manifest.loadCheck.countedCases || 0} | Variance ${manifest.loadCheck.varianceCases || 0}`);
  }

  lines.push('');
  (manifest.stops || []).forEach(stop => {
    lines.push(`${stop.sequence}. ${stop.orderId} - ${stop.customer} - ${stop.cases} cases - ${stop.window || 'Unscheduled'} - ${stop.stopStatus || stop.orderStatus || 'Assigned'}`);
  });

  return lines.join('\n');
}

function copyDispatchManifest(manifest) {
  try { navigator.clipboard && navigator.clipboard.writeText(dispatchManifestText(manifest)); } catch (e) { }
  toast('Route manifest copied', 'info');
}

function DispatchManifestView({ jobs, directory }) {
  const [copyBusyId, setCopyBusyId] = useState('');
  const live = directory && directory.isLive;
  const manifests = live ? dispatchLiveManifests(jobs) : (Array.isArray(RP.DISPATCH_MANIFESTS) ? RP.DISPATCH_MANIFESTS : []);
  const totalStops = live && directory.summary && directory.summary.total != null
    ? Number(directory.summary.total || 0)
    : manifests.reduce((sum, manifest) => sum + (manifest.stopCount || 0), 0);
  const totalCases = live && directory.summary && directory.summary.cases != null
    ? Number(directory.summary.cases || 0)
    : manifests.reduce((sum, manifest) => sum + (manifest.cases || 0), 0);
  const routeCount = live && directory.summary && directory.summary.routes != null
    ? Number(directory.summary.routes || 0)
    : manifests.length;

  const copyManifest = async manifest => {
    if (!live) {
      copyDispatchManifest(manifest);
      return;
    }
    if (copyBusyId || !manifest || !manifest.id) return;
    setCopyBusyId(manifest.id);
    try {
      const exact = await rpLoadDispatchManifest(manifest.id);
      copyDispatchManifest({
        ...manifest,
        ...exact,
        loadCheck: exact.loadCheck || manifest.loadCheck || null
      });
    } catch (err) {
      toast(err && err.message ? err.message : 'The complete route manifest could not be loaded.', 'danger');
    } finally {
      setCopyBusyId('');
    }
  };

  return (
    <div className="rp-scroll" style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: 2 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))', gap: 14, marginBottom: 18 }}>
        <KPICard label="Route Manifests" value={routeCount} unit="active" accent="var(--navy)" icon="truck" />
        <KPICard label="Manifest Stops" value={totalStops} unit="stops" accent="var(--info)" icon="orders" />
        <KPICard label="Manifest Cases" value={totalCases} unit="cs" accent="var(--amber)" icon="package" />
      </div>

      {!manifests.length && !(live && directory.initialLoading) && (
        <div className="card" style={{ padding: '22px 24px', color: 'var(--muted)', fontSize: 13.5 }}>
          No live route manifests yet. Assign approved orders to a dispatch lane to build one.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(520px, 1fr))', gap: 16 }}>
        {manifests.map(manifest => {
          const load = manifest.loadCheck;
          const variance = load ? Number(load.varianceCases || 0) : 0;
          return (
            <div key={manifest.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div className="eyebrow">{manifest.routeDate || 'Unscheduled'} / {manifest.routeType || 'Delivery'}</div>
                  <div className="disp" style={{ fontSize: 20, textTransform: 'uppercase', marginTop: 2 }}>{manifest.laneName || 'Route Manifest'}</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6, color: 'var(--muted)', fontSize: 12.5 }}>
                    <span><Icon name="truck" size={14} /> {manifest.driver || 'Driver unassigned'}</span>
                    <span className="mono">{manifest.stopCount || 0}{live && directory.page.hasMore ? ' loaded' : ''} stops</span>
                    <span className="mono">{manifest.cases || 0} cs</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Pill status={manifest.status || 'Draft'} />
                  <button className="btn btn-sm" disabled={Boolean(copyBusyId)} title="Copy the complete route manifest" onClick={() => copyManifest(manifest)}><Icon name="doc" size={14} />{copyBusyId === manifest.id ? 'Loading...' : 'Copy'}</button>
                </div>
              </div>

              <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)', display: 'grid', gridTemplateColumns: load ? '1fr auto' : '1fr', gap: 12, alignItems: 'center', background: 'var(--surface-2)' }}>
                {load ? (
                  <>
                    <div>
                      <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Latest vehicle load check</div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4, fontSize: 12.5 }}>
                        <span className="mono">Expected {load.expectedCases || 0} cs</span>
                        <span className="mono">Counted {load.countedCases || 0} cs</span>
                        <span className="mono">CHEP {load.palletCount || 0}</span>
                        {load.checkedAt && <span className="mono">{rpFormatLocalDateTime(load.checkedAt, { empty: '' })}</span>}
                      </div>
                    </div>
                    <Pill status={variance === 0 ? 'Complete' : 'Variance'} style={variance === 0 ? undefined : { color: 'var(--danger)', background: 'var(--danger-bg)' }}>{variance === 0 ? 'Balanced' : `${variance > 0 ? '+' : ''}${variance} cs`}</Pill>
                  </>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No vehicle load check recorded for this manifest.</div>
                )}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10.5 }}>
                      <th style={{ textAlign: 'left', padding: '11px 18px', width: 54 }}>Stop</th>
                      <th style={{ textAlign: 'left', padding: '11px 10px' }}>Order</th>
                      <th style={{ textAlign: 'left', padding: '11px 10px' }}>Customer</th>
                      <th style={{ textAlign: 'right', padding: '11px 10px' }}>Cases</th>
                      <th style={{ textAlign: 'left', padding: '11px 10px' }}>Window</th>
                      <th style={{ textAlign: 'right', padding: '11px 18px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(manifest.stops || []).map(stop => (
                      <tr key={`${manifest.id}-${stop.orderId}`} style={{ borderTop: '1px solid var(--line)' }}>
                        <td className="mono" style={{ padding: '12px 18px', color: 'var(--ink-soft)' }}>{stop.sequence}</td>
                        <td className="mono" style={{ padding: '12px 10px', fontWeight: 600 }}>{stop.orderId}</td>
                        <td style={{ padding: '12px 10px' }}>
                          <div style={{ fontWeight: 600 }}>{stop.customer}</div>
                          <div style={{ color: 'var(--faint)', fontSize: 11.5 }}>{stop.territory || 'Unassigned'}</div>
                        </td>
                        <td className="mono" style={{ padding: '12px 10px', textAlign: 'right' }}>{stop.cases || 0}</td>
                        <td className="mono" style={{ padding: '12px 10px', color: 'var(--muted)' }}>{stop.window || 'Unscheduled'}</td>
                        <td style={{ padding: '12px 18px', textAlign: 'right' }}><Pill status={stop.stopStatus || stop.orderStatus || 'Assigned'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TerritoryHeatTab({ A, go }) {
  const [metric, setMetric] = useState('revenue');
  // Map node positions (abstract BC layout) — x,y in %
  const NODE = { 'Northern BC': [38, 16], 'Kamloops': [62, 52], 'Kelowna': [70, 74], 'Squamish': [22, 64] };
  const stats = ((rpHasLiveAuth() && A && A.byTerritory) ? A.byTerritory : RP.TERRITORY_STATS)
    .filter(s => s && NODE[s.territory])
    .map(s => ({ reorderRisk: 0, ...s }));
  const maxRev = Math.max(1, ...stats.map(s => Number(s.revenue || 0)));
  const doorTotal = stats.reduce((sum, s) => sum + Number(s.doors || 0), 0);
  const riskCustomers = RP.CUSTOMERS.filter(c => c.risk === 'High' && NODE[c.territory]);
  const growthText = value => value == null ? 'New' : `${value >= 0 ? '+' : ''}${value}%`;
  const growthColor = value => value == null ? 'var(--info)' : value >= 0 ? 'var(--ok)' : 'var(--danger)';
  const growthHeat = value => value == null ? 0.45 : (value + 10) / 35;

  return (
    <div className="rp-fade">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <Segmented size="sm" options={[{ value: 'revenue', label: 'Revenue' }, { value: 'growth', label: 'Growth' }, { value: 'risk', label: 'Reorder Risk' }]} value={metric} onChange={setMetric} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 18 }}>
        {/* Map */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative', minHeight: 460, background: 'var(--navy-900)' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(oklch(1 0 0 / 0.05) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
          <div style={{ position: 'absolute', top: 16, left: 18, color: 'oklch(0.8 0.02 235)', zIndex: 2 }}>
            <div className="eyebrow" style={{ color: 'oklch(0.65 0.02 235)' }}>British Columbia</div>
            <div className="disp" style={{ fontSize: 18, color: '#fff', textTransform: 'uppercase' }}>{stats.length} Territories · {doorTotal} Doors</div>
          </div>
          {/* connection lines */}
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1 }}>
            {[['Squamish', 'Kamloops'], ['Kamloops', 'Kelowna'], ['Kamloops', 'Northern BC']].map(([a, b], i) => (
              <line key={i} x1={NODE[a][0] + '%'} y1={NODE[a][1] + '%'} x2={NODE[b][0] + '%'} y2={NODE[b][1] + '%'} stroke="oklch(1 0 0 / 0.10)" strokeWidth="1.5" strokeDasharray="3 4" />
            ))}
          </svg>
          {/* door dots */}
          {RP.CUSTOMERS.filter(c => NODE[c.territory]).map((c, i) => {
            const [nx, ny] = NODE[c.territory];
            const ang = (i * 67) % 360, r = 5 + (i % 3) * 2.5;
            const x = nx + Math.cos(ang * Math.PI / 180) * r, y = ny + Math.sin(ang * Math.PI / 180) * r;
            return <span key={c.id} title={c.name} style={{ position: 'absolute', left: x + '%', top: y + '%', width: 6, height: 6, borderRadius: '50%', background: c.risk === 'High' ? 'var(--danger)' : c.risk === 'Prospect' ? 'oklch(0.6 0.01 235)' : 'var(--ok)', transform: 'translate(-50%,-50%)', zIndex: 2, boxShadow: '0 0 0 2px oklch(0 0 0 / 0.3)' }} />;
          })}
          {/* territory heat bubbles */}
          {stats.map(s => {
            const [x, y] = NODE[s.territory];
            const val = metric === 'revenue' ? s.revenue / maxRev : metric === 'growth' ? growthHeat(s.growth) : s.reorderRisk / 4;
            const size = 54 + val * 78;
            const color = metric === 'growth' ? growthColor(s.growth) : metric === 'risk' ? 'var(--danger)' : 'var(--amber)';
            return (
              <div key={s.territory} style={{ position: 'absolute', left: x + '%', top: y + '%', transform: 'translate(-50%,-50%)', zIndex: 3, textAlign: 'center' }}>
                <div style={{ width: size, height: size, borderRadius: '50%', background: color, opacity: 0.22, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', filter: 'blur(2px)' }} />
                <div style={{ width: size * 0.5, height: size * 0.5, borderRadius: '50%', background: color, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', boxShadow: '0 4px 14px oklch(0 0 0 / 0.35)' }} />
                <div style={{ position: 'relative', paddingTop: size / 2 + 6, whiteSpace: 'nowrap' }}>
                  <div className="disp" style={{ fontSize: 13, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.territory}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'oklch(0.85 0.02 235)' }}>{metric === 'revenue' ? RP.money(s.revenue) : metric === 'growth' ? growthText(s.growth) : s.reorderRisk + ' at risk'}</div>
                </div>
              </div>
            );
          })}
          <div style={{ position: 'absolute', bottom: 14, left: 18, display: 'flex', gap: 16, zIndex: 4, fontSize: 11, color: 'oklch(0.8 0.02 235)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)' }} />Healthy door</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)' }} />Reorder risk</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'oklch(0.6 0.01 235)' }} />Prospect</span>
          </div>
        </div>

        {/* Side: ranking + reorder ops */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle>Territory Performance</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {[...stats].sort((a, b) => b.revenue - a.revenue).map(s => (
                <div key={s.territory}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{s.territory}</span>
                    <span style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                      <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{RP.money(s.revenue)}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: growthColor(s.growth), width: 38, textAlign: 'right' }}>{growthText(s.growth)}</span>
                    </span>
                  </div>
                  <Bar value={s.revenue} max={maxRev} color={s.growth != null && s.growth < 0 ? 'var(--danger)' : 'var(--amber)'} />
                </div>
              ))}
              {!stats.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 18, textAlign: 'center' }}>No live territory sales in this range yet.</div>}
            </div>
          </div>
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle right={<span className="pill" style={{ color: 'var(--danger)', background: 'var(--danger-bg)' }}>{riskCustomers.length} doors</span>}>Reorder Opportunities</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {riskCustomers.map(c => (
                <div key={c.id} onClick={() => go('customers', { type: 'customer', id: c.id })} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 8px', borderRadius: 8, cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Avatar name={c.name} size={28} color="var(--danger)" />
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{c.territory} · {c.daysSince} days since last order</div></div>
                  <button className="btn btn-sm btn-ghost" style={{ color: 'var(--amber-deep)' }} onClick={(e) => { e.stopPropagation(); toast('Reorder nudge sent to ' + c.name); }}>Nudge</button>
                </div>
              ))}
              {!riskCustomers.length && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 14, textAlign: 'center' }}>No high-risk live reorder opportunities right now.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ TRADE SPEND */
function finiteTradeAmount(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

function tradeMoney(value) {
  const amount = finiteTradeAmount(value);
  try {
    return RP && typeof RP.money === 'function' ? RP.money(amount) : '$' + Math.round(amount).toLocaleString('en-CA');
  } catch (error) {
    console.warn('Trade Spend money formatting failed', { value, error });
    return '$' + Math.round(amount).toLocaleString('en-CA');
  }
}

function tradeRows(value) {
  try {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value && typeof value === 'object') return Object.values(value).filter(Boolean);
  } catch (error) {
    console.warn('Trade Spend rows normalization failed', { error });
  }
  return [];
}

function tradeId(value) {
  try {
    return String(value == null ? '' : value).trim();
  } catch (error) {
    console.warn('Trade Spend value normalization failed', { error });
    return '';
  }
}

function tradeDataRows(key) {
  try {
    if (typeof RP === 'undefined' || !RP) return [];
    return tradeRows(RP[key]);
  } catch (error) {
    console.warn('Trade Spend data lookup failed', { key, error });
    return [];
  }
}

function tradeList(value) {
  if (Array.isArray(value)) return value.map(tradeId).filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    const text = value.trim();
    if (text.startsWith('[')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) return parsed.map(tradeId).filter(Boolean);
      } catch (error) {
        console.warn('Trade Spend list parse failed', { value: text, error });
      }
    }
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [];
}

function tradeCustomerById(id) {
  const customerId = tradeId(id);
  if (!customerId || typeof RP === 'undefined' || !RP) return null;
  try {
    if (typeof RP.cust === 'function') {
      const customer = RP.cust(customerId);
      if (customer && typeof customer === 'object') return customer;
    }
  } catch (error) {
    console.warn('Trade Spend customer lookup failed', { id: customerId, error });
  }
  return tradeDataRows('CUSTOMERS').find(c => c && tradeId(c.id) === customerId) || null;
}

function tradeCustomerName(id) {
  const c = tradeCustomerById(id);
  return (c && c.name) || tradeId(id) || 'Unassigned customer';
}

function tradeOrderId(order) {
  return tradeId(order && (order.id || order.order_id || order.orderId));
}

function tradeOrderCustomerId(order) {
  return tradeId(order && (order.cust || order.customer_id || order.customerId || order.customer));
}

function tradeRowKey(prefix, row, index) {
  const id = tradeId(row && (row.id || row.order_id || row.orderId));
  return id || `${prefix}-${index}`;
}

function tradeOrderTotals(order) {
  try {
    if (typeof rpOrderTotals !== 'function') throw new Error('Order total helper is unavailable.');
    const totals = rpOrderTotals(order) || {};
    return {
      sub: finiteTradeAmount(totals.sub),
      dep: finiteTradeAmount(totals.dep),
      fee: finiteTradeAmount(totals.fee),
      tax: finiteTradeAmount(totals.tax),
      total: finiteTradeAmount(totals.total),
      cases: finiteTradeAmount(totals.cases)
    };
  } catch (error) {
    console.warn('Trade Spend order total failed', { orderId: order && order.id, error });
    const lines = tradeRows(order && order.lines);
    const cases = lines.reduce((sum, line) => sum + finiteTradeAmount(line && (line.qty ?? line.quantity)), 0);
    const total = finiteTradeAmount(order && (order.total ?? order.total_amount ?? order.subtotal ?? order.subtotal_amount));
    return { sub: total, dep: 0, fee: 0, tax: 0, total, cases };
  }
}

function normalizeTradeDeduction(row, index) {
  try {
    const raw = row && typeof row === 'object' ? row : {};
    const customerId = raw.cust || raw.customer || raw.customer_id || raw.customerId || '';
    return {
      raw,
      id: tradeId(raw.id) || `TS-${String((index || 0) + 1).padStart(3, '0')}`,
      cust: tradeId(customerId),
      type: tradeId(raw.type || raw.deduction_type || raw.category) || 'Deduction',
      amount: finiteTradeAmount(raw.amount),
      status: tradeId(raw.status) || 'Pending',
      period: tradeId(raw.period || raw.month),
      skus: tradeList(raw.skus || raw.sku_ids || raw.skuIds),
      gl: tradeId(raw.gl || raw.gl_account || raw.glAccount || raw.account) || 'Trade Spend',
      owner: tradeId(raw.owner || raw.owner_name || raw.owner_id || raw.createdBy) || 'Unassigned',
      matched: tradeList(raw.matched || raw.matched_order_ids || raw.matchedOrderIds),
      note: tradeId(raw.note || raw.notes)
    };
  } catch (error) {
    console.warn('Trade Spend deduction normalization failed', { row, error });
    return null;
  }
}

function tradeSpendDeductions({ live } = {}) {
  const rows = tradeDataRows('DEDUCTIONS');
  return rows
    .map(normalizeTradeDeduction)
    .filter(Boolean)
    .filter(d => !live || !d.cust || tradeCustomerById(d.cust));
}

function tradeOrderTotalValue(order) {
  return finiteTradeAmount(tradeOrderTotals(order).total);
}

function TradeSpendRecordModal({ onClose, onAdded }) {
  const customerOptions = tradeDataRows('CUSTOMERS').filter(c => {
    try {
      return typeof rpCustomerOrderReady === 'function' ? rpCustomerOrderReady(c) : !!c;
    } catch (error) {
      console.warn('Trade Spend customer readiness check failed', { customerId: c && c.id, error });
      return false;
    }
  });
  const defaultCustomer = customerOptions[0] ? customerOptions[0].id : '';
  const types = { 'Slotting': 'Trade Spend - Slotting', 'Chargeback': 'Trade Spend - Chargeback', 'Promo Allowance': 'Trade Spend - Promo', 'Credit Memo': 'Trade Spend - Credit' };
  const [f, setF] = useState({ cust: defaultCustomer, type: 'Slotting', amount: '', period: 'Q2 2026' });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  useEffect(() => {
    if (!f.cust && defaultCustomer) set('cust', defaultCustomer);
  }, [defaultCustomer, f.cust]);

  const save = async () => {
    setTried(true);
    setError('');
    const amount = parseFloat(f.amount);
    if (!(amount > 0) || !f.cust || busy) return;
    if (rpHasLiveAuth()) {
      setBusy(true);
      try {
        const response = await rpApi('/api/trade-spend/upsert', {
          method: 'POST',
          body: JSON.stringify({
            amount,
            customerId: f.cust,
            gl: types[f.type],
            matched: [],
            period: f.period,
            skus: [],
            status: 'Pending',
            type: f.type
          })
        });
        if (!Array.isArray(RP.DEDUCTIONS)) RP.DEDUCTIONS = [];
        const deduction = response.deduction;
        if (!deduction || !deduction.id) throw new Error('Trade spend save did not return a deduction.');
        const idx = RP.DEDUCTIONS.findIndex(item => item && item.id === deduction.id);
        if (idx >= 0) RP.DEDUCTIONS.splice(idx, 1, deduction);
        else RP.DEDUCTIONS.unshift(deduction);
        toast(`Deduction ${deduction.id} recorded - ${tradeMoney(amount)}`, 'info');
        if (typeof onAdded === 'function') onAdded();
        onClose();
      } catch (err) {
        const msg = err && err.message ? err.message : 'Could not record deduction.';
        setError(msg);
        toast(msg, 'danger');
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!Array.isArray(RP.DEDUCTIONS)) RP.DEDUCTIONS = [];
    const id = 'TS-' + (505 + RP.DEDUCTIONS.length);
    RP.DEDUCTIONS.unshift({ id, cust: f.cust, type: f.type, amount, status: 'Pending', period: f.period, skus: [], gl: types[f.type], owner: 'S. Manager' });
    toast(`Deduction ${id} recorded - ${tradeMoney(amount)}`, 'info');
    if (typeof onAdded === 'function') onAdded();
    onClose();
  };

  return (
    <Modal title="Record Deduction" sub="Trade spend posts against gross-to-net" onClose={onClose} width={500}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !customerOptions.length} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Record'}</button></>}>
      <MField label="Customer" required>
        <select className="field" value={f.cust} onChange={e => set('cust', e.target.value)}>
          {customerOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </MField>
      {!customerOptions.length && <div style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 700, marginTop: -6, marginBottom: 10 }}>Add an active customer before recording trade spend.</div>}
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Type" half><select className="field" value={f.type} onChange={e => set('type', e.target.value)}>{Object.keys(types).map(t => <option key={t}>{t}</option>)}</select></MField>
        <MField label="Period" half><select className="field" value={f.period} onChange={e => set('period', e.target.value)}>{['Q2 2026', 'Jun 2026', 'May 2026', 'Q3 2026'].map(t => <option key={t}>{t}</option>)}</select></MField>
      </div>
      <MField label="Amount" required>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
          <input className="field" style={{ paddingLeft: 26, borderColor: tried && !(parseFloat(f.amount) > 0) ? 'var(--danger)' : undefined }} placeholder="0.00" type="number" value={f.amount} onChange={e => set('amount', e.target.value)} />
        </div>
        {tried && !(parseFloat(f.amount) > 0) && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>Enter an amount</div>}
      </MField>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5 }}><Icon name="trade" size={15} />Posts to <b style={{ marginLeft: 2 }}>{types[f.type]}</b> and routes to Sales Manager for approval.</div>
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

function TradeView({ go, leadership }) {
  const [modal, setModal] = useState(false);
  const [sel, setSel] = useState(null);
  const [, force] = useState(0);
  const live = rpHasLiveAuth();
  const salesAnalytics = useRpSalesAnalytics('mtd');
  const role = rpNormalizeRole((rpAuthProfile() || {}).role);
  const canEditTradeSpend = !live || ['admin', 'leadership', 'ops', 'sales_manager', 'accountant', 'marketing'].includes(role);
  const deductions = tradeSpendDeductions({ live });
  const deductionAmount = type => deductions
    .filter(d => String(d.type || '').toLowerCase().includes(type))
    .reduce((sum, d) => sum + finiteTradeAmount(d.amount), 0);
  const gross = live
    ? (salesAnalytics.data && salesAnalytics.data.summary ? dashboardExactNumber(salesAnalytics.data.summary.revenue) : null)
    : 58200;
  const slotting = live ? deductionAmount('slotting') : 2400;
  const charge = live ? deductionAmount('charge') : 1190;
  const promo = live ? deductionAmount('promo') : 640;
  const credit = live ? deductionAmount('credit') : 192;
  const totalSpend = deductions.length ? deductions.reduce((sum, d) => sum + finiteTradeAmount(d.amount), 0) : (live ? 0 : slotting + charge + promo + credit);
  const net = gross == null ? null : gross - totalSpend;
  const waterfall = gross == null ? [] : [
    { label: 'Gross Sales', value: gross, type: 'base' },
    { label: 'Slotting', value: -slotting, type: 'sub' },
    { label: 'Chargebacks', value: -charge, type: 'sub' },
    { label: 'Promo', value: -promo, type: 'sub' },
    { label: 'Credits', value: -credit, type: 'sub' },
    { label: 'Net Sales', value: net, type: 'net' },
  ];
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {modal && <TradeSpendRecordModal onClose={() => setModal(false)} onAdded={() => force(x => x + 1)} />}
      <PageHead eyebrow="Gross-to-Net · MTD" title="Trade Spend"
        sub="Slotting, chargebacks, promo allowances and deductions — and the true net underneath."
        actions={canEditTradeSpend && <button className="btn btn-sm btn-primary" onClick={() => setModal(true)}><Icon name="plus" size={15} />Record Deduction</button>} />
      {!leadership && <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--info-bg)', border: '1px solid oklch(0.56 0.1 245 / 0.3)', color: 'var(--info)', padding: '10px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13 }}><Icon name="admin" size={16} />Margin figures are visible to leadership, sales management, ops and accounting. Showing as Operations.</div>}
      <SalesAnalyticsStatus state={salesAnalytics} label="MTD gross sales" />
      {live && deductions.length === 0 && <LiveDataGuardNotice title="No live trade-spend deductions are recorded.">Developer data deductions are hidden in live mode. Record a live deduction to start tracking gross-to-net adjustments here.</LiveDataGuardNotice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
        <KPICard label="Gross Sales" value={gross == null ? '—' : tradeMoney(gross)} accent="var(--navy)" icon="trade" />
        <KPICard label="Total Trade Spend" value={tradeMoney(totalSpend)} delta={live ? null : -6} accent="var(--danger)" icon="trade" />
        <KPICard label="Net Sales" value={net == null ? '—' : tradeMoney(net)} delta={live || net == null ? null : 11} accent="var(--ok)" icon="check" />
        <KPICard label="Spend Rate" value={gross == null ? '—' : gross > 0 ? (totalSpend / gross * 100).toFixed(1) : '0.0'} unit={gross == null ? null : '% of gross'} accent="var(--amber)" icon="analytics" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 18 }}>
        <div className="card" style={{ padding: '20px 22px' }}>
          <SectionTitle>Gross-to-Net</SectionTitle>
          {gross == null
            ? <div style={{ minHeight: 220, display: 'grid', placeItems: 'center', color: 'var(--faint)', fontSize: 13 }}>Complete gross sales are not available yet.</div>
            : <Waterfall steps={waterfall} />}
        </div>
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}><SectionTitle>Deductions & Fees</SectionTitle></div>
          <table className="rp-table">
            <thead><tr><th>ID</th><th>Customer</th><th>Type</th><th>Period</th><th className="num">Amount</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {deductions.map((d, index) => (
                <tr key={tradeRowKey('deduction', d, index)} style={{ cursor: 'pointer' }} onClick={() => setSel(d.id)}>
                  <td className="mono" style={{ fontWeight: 600 }}>{d.id}</td>
                  <td>{tradeCustomerName(d.cust)}</td>
                  <td style={{ color: 'var(--muted)' }}>{d.type}</td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{d.period}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{tradeMoney(d.amount)}</td>
                  <td><Pill status={d.status} /></td>
                  <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
                </tr>
              ))}
              {!deductions.length && <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No trade-spend deductions found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {sel && <DeductionModal id={sel} onClose={() => setSel(null)} onChange={() => force(x => x + 1)} />}
    </div>
  );
}

/* ---- Deduction detail + matching modal ---- */
function DeductionModal({ id, onClose, onChange }) {
  const live = rpHasLiveAuth();
  const d = tradeSpendDeductions({ live }).find(x => x.id === id);
  const c = d ? (tradeCustomerById(d.cust) || { name: 'Unassigned customer', terms: 'Not set' }) : { name: 'Unassigned customer', terms: 'Not set' };
  const candidatePage = useRpServerPagedRows({ mode: 'orders', customerId: d && d.cust ? d.cust : '__missing__', limit: 100 });
  const candidates = live
    ? candidatePage.items.map(item => rpServerPagedOrder(item)).filter(Boolean)
    : (d ? tradeDataRows('ORDERS').filter(o => o && d.cust && tradeOrderCustomerId(o) === d.cust) : []);
  const candidateKey = candidates.map(tradeOrderId).filter(Boolean).join('|');
  const [status, setStatus] = useState(() => d ? d.status : 'Unavailable');
  const [matched, setMatched] = useState(() => d && tradeRows(d.matched).length ? d.matched : (candidates[0] ? [tradeOrderId(candidates[0])] : []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setStatus(d ? d.status : 'Unavailable');
  }, [d && d.id, d && d.status]);
  useEffect(() => {
    setMatched(d && tradeRows(d.matched).length ? d.matched : []);
  }, [d && d.id]);
  useEffect(() => {
    if (!candidates[0]) return;
    setMatched(current => tradeRows(current).length ? current : [tradeOrderId(candidates[0])].filter(Boolean));
  }, [candidateKey]);
  useEffect(() => {
    if (!live) return;
    candidates.forEach(order => rpUpsertOrder(order));
  }, [live, candidateKey]);

  if (!d) return (
    <Modal title="Deduction unavailable" sub="This deduction is not available in the current data set." onClose={onClose} width={420}
      footer={<button className="btn btn-primary" onClick={onClose}>Close</button>}>
      <div style={{ color: 'var(--muted)', fontSize: 13.5 }}>Refresh the workspace and try again.</div>
    </Modal>
  );
  const toggleMatch = oid => setMatched(m => {
    const ids = tradeRows(m).map(tradeId).filter(Boolean);
    return ids.includes(oid) ? ids.filter(x => x !== oid) : [...ids, oid];
  });
  const matchedTotal = tradeRows(matched).reduce((s, oid) => {
    const o = candidates.find(x => tradeOrderId(x) === tradeId(oid));
    return s + (o ? tradeOrderTotals(o).total : 0);
  }, 0);

  const act = async (newStatus, msg, tone) => {
    setError('');
    if (busy) return;
    if (rpHasLiveAuth()) {
      setBusy(true);
      try {
        const response = await rpApi('/api/trade-spend/upsert', {
          method: 'POST',
          body: JSON.stringify({
            amount: d.amount,
            customerId: d.cust,
            gl: d.gl,
            id: d.id,
            matched,
            note: d.note || '',
            period: d.period,
            skus: d.skus,
            status: newStatus,
            type: d.type
          })
        });
        if (response && response.deduction && Array.isArray(RP.DEDUCTIONS)) {
          const idx = RP.DEDUCTIONS.findIndex(item => item && item.id === response.deduction.id);
          if (idx >= 0) RP.DEDUCTIONS.splice(idx, 1, response.deduction);
          else RP.DEDUCTIONS.unshift(response.deduction);
        }
      } catch (err) {
        const nextError = err && err.message ? err.message : 'Could not update deduction.';
        setError(nextError);
        toast(nextError, 'danger');
        setBusy(false);
        return;
      } finally {
        setBusy(false);
      }
    } else if (d.raw && typeof d.raw === 'object') {
      d.raw.status = newStatus;
      d.raw.matched = matched;
    }
    setStatus(newStatus);
    toast(msg, tone);
    if (typeof onChange === 'function') onChange();
  };

  return (
    <Modal title={d.id} sub={`${c.name} · ${d.type}`} onClose={onClose} width={580}
      footer={<>
        <button className="btn" disabled={busy} onClick={onClose}>Close</button>
        {status === 'Disputed'
          ? <><button className="btn" onClick={() => act('Pending', `${d.id} dispute withdrawn`, 'info')}>Withdraw Dispute</button><button className="btn btn-primary" onClick={() => act('Closed', `${d.id} resolved & closed`)}><Icon name="check" size={15} />Resolve</button></>
          : status === 'Closed'
            ? <Pill status="Closed">Settled</Pill>
            : <><button className="btn" onClick={() => act('Disputed', `${d.id} marked disputed`, 'warn')}><Icon name="flag" size={15} />Dispute</button><button className="btn btn-primary" onClick={() => act('Approved', `${d.id} approved · ${tradeMoney(d.amount)}`)}><Icon name="check" size={15} />Approve</button></>}
      </>}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
        <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 11, padding: '14px 16px' }}>
          <div className="eyebrow nowrap" style={{ marginBottom: 6 }}>Amount</div>
          <div className="disp" style={{ fontSize: 26 }}>{tradeMoney(d.amount)}</div>
        </div>
        <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 11, padding: '14px 16px' }}>
          <div className="eyebrow nowrap" style={{ marginBottom: 6 }}>Status</div>
          <Pill status={status} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20, fontSize: 13 }}>
        <Row k="Period" v={d.period} /><Row k="GL Account" v={d.gl} /><Row k="Owner" v={d.owner} /><Row k="Customer terms" v={c.terms} />
      </div>
      {error && <div style={{ marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span className="eyebrow">Match to Orders / Invoices</span>
        <span className="mono nowrap" style={{ fontSize: 12, fontWeight: 600, color: matchedTotal >= d.amount ? 'var(--ok)' : 'var(--warn)' }}>{tradeMoney(matchedTotal)} matched</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
        {live && candidatePage.initialLoading && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 14, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 9 }}>Loading the complete account order history...</div>}
        {candidates.length === 0 && (!live || candidatePage.loaded) && !candidatePage.error && <div style={{ color: 'var(--faint)', fontSize: 13, padding: 14, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 9 }}>No orders on this account to match.</div>}
        {candidates.map((o, index) => {
          const oid = tradeOrderId(o);
          const on = tradeRows(matched).map(tradeId).includes(oid), t = tradeOrderTotals(o);
          return (
            <button key={tradeRowKey('order', o, index)} onClick={() => oid && toggleMatch(oid)} disabled={status === 'Closed' || !oid} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', border: '1.5px solid ' + (on ? 'var(--amber)' : 'var(--line)'), background: on ? 'var(--amber-soft)' : 'var(--surface)', borderRadius: 10, textAlign: 'left', cursor: status === 'Closed' || !oid ? 'default' : 'pointer' }}>
              <span style={{ width: 20, height: 20, borderRadius: 6, border: '2px solid ' + (on ? 'var(--amber)' : 'var(--faint)'), background: on ? 'var(--amber)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on && <Icon name="check" size={13} style={{ color: '#fff' }} />}</span>
              <span className="mono" style={{ fontWeight: 600, fontSize: 12.5 }}>{oid || 'Order'}</span>
              <span style={{ flex: 1, fontSize: 12.5, color: 'var(--muted)' }}>{rpOrderDateLabel(o, o.created || 'Unscheduled')} · {t.cases} cs</span>
              <Pill status={o.status} />
              <span className="mono" style={{ fontWeight: 600, fontSize: 13 }}>{tradeMoney(t.total)}</span>
            </button>
          );
        })}
        {live && <RpServerLoadMore
          error={candidatePage.error}
          items={candidatePage.items}
          label="orders"
          loadingMore={candidatePage.loadingMore}
          onLoadMore={candidatePage.loadMore}
          onRetry={candidatePage.retry}
          page={candidatePage.page}
          total={candidatePage.total}
        />}
      </div>
    </Modal>
  );
}

function Waterfall({ steps }) {
  const safeSteps = Array.isArray(steps) ? steps : [];
  const base = Math.max(Math.abs(finiteTradeAmount(safeSteps[0] && safeSteps[0].value)), 1);
  let running = 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
      {safeSteps.map((s, i) => {
        const value = finiteTradeAmount(s && s.value);
        const isTot = s.type === 'base' || s.type === 'net';
        const w = Math.max(0, Math.min(100, Math.abs(value) / base * 100));
        let offset = 0;
        if (s.type === 'sub') { running += Math.abs(value); offset = (1 - running / base) * 100 + 0; }
        const left = s.type === 'sub' ? Math.max(0, Math.min(100, (100 - w - (base - running) / base * 100))) : 0;
        const color = s.type === 'base' ? 'var(--navy)' : s.type === 'net' ? 'var(--ok)' : 'var(--danger)';
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
              <span style={{ fontWeight: isTot ? 700 : 500, color: isTot ? 'var(--ink)' : 'var(--muted)' }}>{s.label}</span>
              <span className="mono" style={{ fontWeight: 600, color: s.type === 'sub' ? 'var(--danger)' : 'var(--ink)' }}>{value < 0 ? '−' : ''}{tradeMoney(Math.abs(value))}</span>
            </div>
            <div style={{ height: isTot ? 16 : 11, background: 'var(--surface-2)', borderRadius: 5, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: left + '%', top: 0, bottom: 0, width: w + '%', background: color, borderRadius: 5, opacity: s.type === 'sub' ? 0.85 : 1 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

Object.assign(window, { DispatchView, TerritoryHeatTab, TradeView, Waterfall });


/* ===== desktop_analytics.jsx ===== */
/* ============================================================
   RANCH POWER — Analytics workspace (deep-dive)
   Tabs: Overview · Timing · Territory · Reps · Products · Customers
   ============================================================ */

const ANA_TABS = [
  { id: 'overview', label: 'Overview', icon: 'analytics' },
  { id: 'timing', label: 'Timing', icon: 'clock' },
  { id: 'territory', label: 'Territory', icon: 'pin' },
  { id: 'reps', label: 'Sales Reps', icon: 'customers' },
  { id: 'products', label: 'Products', icon: 'package' },
  { id: 'customers', label: 'Customer Type', icon: 'user' },
];

function rpEmptyAnalytics(range) {
  const hours = ['7a', '8a', '9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p', '6p'];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const trendBuckets = range === '1d'
    ? Array.from({ length: 24 }, (_, index) => ({ label: index === 0 ? '12a' : index < 12 ? `${index}a` : index === 12 ? '12p' : `${index - 12}p`, cases: 0, orders: 0, revenue: 0 }))
    : Array.from({ length: range === '7d' ? 7 : range === '4w' ? 4 : 12 }, (_, index) => ({ label: range === '7d' ? `D${index + 1}` : `W${index + 1}`, cases: 0, orders: 0, revenue: 0 }));
  return {
    HOURS: hours,
    byRep: [],
    byProduct: [],
    bySku: [],
    byTerritory: [],
    byTerritoryFlavor: [],
    byType: [],
    channelMix: [],
    dow: days.map(day => ({ day, cases: 0, orders: 0, revenue: 0 })),
    heatmap: days.map(day => ({ day, cells: hours.map(() => 0) })),
    insights: [{ kind: 'peak', icon: 'analytics', text: 'No live sales activity in this range yet', detail: 'Create or import live orders to populate analytics.' }],
    orderSizes: [],
    operational: {
      approvalOrders: null,
      arBalance: null,
      invoiceDraft: null,
      invoiceOverdue: null,
      invoiceUnpaid: null,
      openOrders: null,
      orderAging: null,
      overdueOrders: null
    },
    provenance: null,
    summary: {
      activeDoors: 0,
      aov: 0,
      aovGrowth: 0,
      cases: 0,
      casesGrowth: 0,
      orders: 0,
      ordersGrowth: 0,
      revenue: 0,
      revenueGrowth: 0
    },
    weeks: trendBuckets
  };
}

function rpAnalyticsData(range, filters) {
  if (rpHasLiveAuth()) return rpEmptyAnalytics(range);
  return RP.ANA;
}

function anaRangeLabel(range) {
  if (range === '1d') return 'daily';
  if (range === '7d') return '7-day';
  if (range === '4w') return '4-week';
  if (range === 'ytd') return 'YTD';
  return '12-week';
}

function anaRangeShortLabel(range) {
  if (range === '1d') return '1D';
  if (range === '7d') return '7D';
  if (range === '4w') return '4W';
  if (range === 'ytd') return 'YTD';
  return '12W';
}

function anaMetricLabel(metric, compact) {
  if (metric === 'cases') return compact ? 'Cases' : 'Cases Sold';
  return compact ? 'Dollars' : 'Dollars Sold';
}

function anaFilterAll(value) {
  return !value || value === 'all';
}

function anaGrowthText(value) {
  if (value == null) return 'New';
  return `${value >= 0 ? '+' : ''}${value}%`;
}

function anaGrowthColor(value) {
  if (value == null) return 'var(--info)';
  return value >= 0 ? 'var(--ok)' : 'var(--danger)';
}

function anaGrowthSortValue(value) {
  return value == null ? Number.POSITIVE_INFINITY : value;
}

function anaTrendDeltaPill(value) {
  return value == null ? 'New vs prior' : `${value >= 0 ? '+' : ''}${value}% vs prior`;
}

function analyticsLineFlavorIds(line) {
  const rawSku = line && (line.sku || line.inventorySku || line.inventorySkuId);
  const canonical = typeof rpCanonicalSkuId === 'function' ? rpCanonicalSkuId(rawSku) : rawSku;
  const sku = RP.sku(canonical) || (typeof rpSkuItem === 'function' ? rpSkuItem(canonical) : null) || {};
  if (Array.isArray(sku.tri) && sku.tri.length) return sku.tri.filter(flavor => RP.flavor(flavor));
  const flavor = sku.flavor || sku.visualFlavor || '';
  if (RP.flavor(flavor)) return [flavor];
  const inferred = typeof rpSkuFlavorId === 'function' ? rpSkuFlavorId(canonical || rawSku) : '';
  return inferred && RP.flavor(inferred) ? [inferred] : [];
}

function rpAnalyticsFacetValues(values, key) {
  return (Array.isArray(values) ? values : []).map(value => {
    if (value && typeof value === 'object') return String(value[key] || value.name || value.label || value.value || value.id || '').trim();
    return String(value || '').trim();
  }).filter(Boolean);
}

function rpAnalyticsFilterOptions(A, selectedFilters) {
  if (rpHasLiveAuth()) {
    const facets = A && A.facets && typeof A.facets === 'object' ? A.facets : {};
    const withSelected = (values, selected) => Array.from(new Set([
      ...values,
      anaFilterAll(selected) ? '' : String(selected || '').trim()
    ].filter(Boolean))).sort();
    return {
      territories: withSelected(rpAnalyticsFacetValues(facets.territories, 'territory'), selectedFilters && selectedFilters.territory),
      reps: withSelected(rpAnalyticsFacetValues(facets.reps, 'name'), selectedFilters && selectedFilters.rep),
      types: withSelected(rpAnalyticsFacetValues(facets.customerTypes, 'type'), selectedFilters && selectedFilters.customerType),
      flavors: withSelected(rpAnalyticsFacetValues(facets.flavors, 'flavor'), selectedFilters && selectedFilters.flavor)
    };
  }
  const customersById = new Map((RP.CUSTOMERS || []).map(customer => [customer.id, customer]));
  const orders = RP.ORDERS || [];
  const add = (set, value) => {
    const text = String(value || '').trim();
    if (text) set.add(text);
  };
  const territories = new Set(RP.TERRITORIES || []);
  const reps = new Set();
  const types = new Set();
  const flavors = new Set((RP.FLAVORS || []).map(flavor => flavor.id));
  (RP.CUSTOMERS || []).forEach(customer => {
    add(territories, customer.territory);
    add(reps, customer.rep);
    add(types, customer.type);
  });
  orders.forEach(order => {
    const customer = customersById.get(order.cust) || {};
    add(territories, order.territory || customer.territory);
    add(reps, order.rep || customer.rep);
    add(types, customer.type);
    (order.lines || []).forEach(line => analyticsLineFlavorIds(line).forEach(flavor => add(flavors, flavor)));
  });
  return {
    territories: Array.from(territories).sort(),
    reps: Array.from(reps).filter(rep => rep !== 'Unassigned rep').sort(),
    types: Array.from(types).sort(),
    flavors: Array.from(flavors).filter(flavor => RP.flavor(flavor)).sort((a, b) => analyticsFlavorMeta(a).name.localeCompare(analyticsFlavorMeta(b).name))
  };
}

function AnalyticsFilterSelect({ label, value, options, onChange }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 160 }}>
      <span style={{ color: 'var(--muted)', fontFamily: 'var(--ff-display)', fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase' }}>{label}</span>
      <select className="field" value={value || 'all'} onChange={e => onChange(e.target.value)} style={{ height: 38, paddingTop: 0, paddingBottom: 0 }}>
        <option value="all">All {label.toLowerCase()}</option>
        {(options || []).map(option => <option key={option} value={option}>{label === 'Flavor' ? analyticsFlavorMeta(option).name : option}</option>)}
      </select>
    </label>
  );
}

function anaPeakHour(A) {
  const hours = A.HOURS || [];
  const totals = hours.map((hour, index) => ({
    hour,
    orders: (A.heatmap || []).reduce((sum, row) => sum + Number((row.cells || [])[index] || 0), 0)
  }));
  return totals.sort((a, b) => b.orders - a.orders)[0] || { hour: 'None', orders: 0 };
}

function anaWeekendShare(A) {
  const rows = A.dow || [];
  const total = rows.reduce((sum, row) => sum + Number(row.orders || 0), 0);
  if (!total) return 0;
  const weekend = rows.filter(row => row.day === 'Sat' || row.day === 'Sun').reduce((sum, row) => sum + Number(row.orders || 0), 0);
  return Math.round((weekend / total) * 100);
}

function AnalyticsView({ go, leadership }) {
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState('12w');
  const [metric, setMetric] = useState('revenue'); // revenue | cases
  const [filters, setFilters] = useState({ territory: 'all', rep: 'all', customerType: 'all', flavor: 'all' });
  const salesAnalytics = useRpSalesAnalytics(range, filters);
  const A = salesAnalytics.isLive ? (salesAnalytics.data || rpEmptyAnalytics(range)) : rpAnalyticsData(range, filters);
  const filterOptions = rpAnalyticsFilterOptions(A, filters);
  const analyticsReady = !salesAnalytics.isLive || salesAnalytics.loaded;
  const filterCount = Object.values(filters).filter(value => !anaFilterAll(value)).length;
  const setFilter = (key, value) => setFilters(current => ({ ...current, [key]: value || 'all' }));
  const clearFilters = () => setFilters({ territory: 'all', rep: 'all', customerType: 'all', flavor: 'all' });

  const ctx = { A, filters, go, metric, money: metric === 'revenue', range };

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <PageHead eyebrow="Sales Intelligence" title="Analytics"
        sub="Dig into when, where, who and what drives Ranch Power sales."
        actions={<>
          <Segmented size="sm" options={[{ value: 'revenue', label: 'Dollars Sold' }, { value: 'cases', label: 'Cases Sold' }]} value={metric} onChange={setMetric} />
          <Segmented size="sm" options={[{ value: '1d', label: '1D' }, { value: '7d', label: '7D' }, { value: '4w', label: '4W' }, { value: '12w', label: '12W' }, { value: 'ytd', label: 'YTD' }]} value={range} onChange={setRange} />
          <button className="btn btn-sm" onClick={() => go('reports', { type: 'bundle', id: 'sales-finance' })}><Icon name="download" size={15} />Reports & Exports</button>
        </>} />

      <div className="card" style={{ padding: '14px 16px', marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <AnalyticsFilterSelect label="Territory" value={filters.territory} options={filterOptions.territories} onChange={value => setFilter('territory', value)} />
          <AnalyticsFilterSelect label="Rep" value={filters.rep} options={filterOptions.reps} onChange={value => setFilter('rep', value)} />
          <AnalyticsFilterSelect label="Customer Type" value={filters.customerType} options={filterOptions.types} onChange={value => setFilter('customerType', value)} />
          <AnalyticsFilterSelect label="Flavor" value={filters.flavor} options={filterOptions.flavors} onChange={value => setFilter('flavor', value)} />
          {filterCount > 0 && <button className="btn btn-sm" onClick={clearFilters} style={{ marginLeft: 'auto', color: 'var(--muted)' }}><Icon name="x" size={14} />Clear filters ({filterCount})</button>}
        </div>
      </div>

      <SalesAnalyticsStatus state={salesAnalytics} label="Sales analytics" />

      {/* Sub-tabs */}
      <div className="rp-scroll" style={{ display: 'flex', gap: 6, marginBottom: 22, borderBottom: '1px solid var(--line)', overflowX: 'auto' }}>
        {ANA_TABS.map(t => {
          const on = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 7, border: 'none', background: 'transparent', color: on ? 'var(--ink)' : 'var(--muted)', fontSize: 13.5, fontWeight: 600, padding: '10px 13px', borderBottom: '2px solid ' + (on ? 'var(--amber)' : 'transparent'), marginBottom: -1, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <Icon name={t.icon} size={16} />{t.label}
            </button>
          );
        })}
      </div>

      {!analyticsReady && <div className="card" style={{ minHeight: 260, display: 'grid', placeItems: 'center', padding: 24, color: 'var(--faint)', textAlign: 'center' }}>Complete analytics are not available yet. No bounded-cache values are shown.</div>}
      {analyticsReady && tab === 'overview' && <AnaOverview {...ctx} />}
      {analyticsReady && tab === 'timing' && <AnaTiming {...ctx} />}
      {analyticsReady && tab === 'territory' && <TerritoryHeatTab A={A} go={go} />}
      {analyticsReady && tab === 'reps' && <AnaReps {...ctx} />}
      {analyticsReady && tab === 'products' && <AnaProducts {...ctx} />}
      {analyticsReady && tab === 'customers' && <AnaCustomers {...ctx} />}
    </div>
  );
}

/* small card wrapper */
function AnaCard({ title, right, children, pad = true, style }) {
  return (
    <div className="card" style={{ padding: pad ? '18px 20px' : 0, ...style }}>
      {title && <SectionTitle right={right}>{title}</SectionTitle>}
      {children}
    </div>
  );
}
function Insight({ ins }) {
  const tone = ins.kind === 'risk' ? 'danger' : ins.kind === 'growth' ? 'ok' : ins.kind === 'peak' ? 'info' : 'amber';
  const [fg, bg] = statusColors(tone === 'danger' ? 'Damaged' : tone === 'ok' ? 'Approved' : tone === 'info' ? 'Submitted' : 'Fronted to Rep');
  return (
    <div style={{ display: 'flex', gap: 12, padding: '13px 14px', border: '1px solid var(--line)', borderRadius: 11, background: 'var(--surface)' }}>
      <span style={{ width: 34, height: 34, borderRadius: 9, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={ins.icon} size={17} /></span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3 }}>{ins.text}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{ins.detail}</div>
      </div>
    </div>
  );
}

/* ---------------- OVERVIEW ---------------- */
function analyticsFlavorMeta(id) {
  const inferred = typeof rpSkuFlavorId === 'function' ? rpSkuFlavorId(id) : '';
  return RP.flavor(id) || RP.flavor(inferred) || { name: id === 'unknown' ? 'Unmapped SKU' : (id || 'Unmapped SKU'), color: 'var(--faint)' };
}

function AnaOverview({ A, go, metric, money, range }) {
  const s = A.summary;
  const trendSeries = [{ name: money ? 'Revenue' : 'Cases', color: 'var(--amber)', data: A.weeks.map(w => money ? w.revenue : w.cases) }];
  const rangeLabel = anaRangeLabel(range);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <KPICard label="Revenue" value={RP.money(s.revenue)} delta={s.revenueGrowth} icon="trade" accent="var(--ok)" spark={A.weeks.map(w => w.revenue)} />
        <KPICard label="Cases Sold" value={s.cases.toLocaleString()} delta={s.casesGrowth} icon="package" accent="var(--amber)" spark={A.weeks.map(w => w.cases)} />
        <KPICard label="Orders" value={s.orders} delta={s.ordersGrowth} icon="orders" accent="var(--info)" spark={A.weeks.map(w => w.orders)} />
        <KPICard label="Avg Order Value" value={RP.money(s.aov)} delta={s.aovGrowth} icon="analytics" accent="var(--navy)" />
      </div>

      <AnaCard title={`${anaMetricLabel(metric)} · ${rangeLabel} trend`} right={<span className="pill" style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}>{anaTrendDeltaPill(money ? s.revenueGrowth : s.casesGrowth)}</span>}>
        <LineArea series={trendSeries} labels={A.weeks.map(w => w.label)} money={money} h={210} />
      </AnaCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 18 }}>
        <AnaCard title="What the numbers are telling you">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {A.insights.map((ins, i) => <Insight key={i} ins={ins} />)}
          </div>
        </AnaCard>
        <AnaCard title="Order Source Mix">
          <Donut centerValue={s.orders} centerLabel="orders" items={A.channelMix.map((c, i) => ({ label: c.channel, value: c.orders, color: ['var(--amber)', 'var(--navy)', 'var(--info)', 'var(--faint)'][i] }))} />
        </AnaCard>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <AnaCard title="Top Territories" right={<button className="btn btn-ghost btn-sm" onClick={() => go('analytics')}>Detail<Icon name="chevR" size={14} /></button>}>
          <RankBars money={money} showGrowth items={[...A.byTerritory].sort((a, b) => (money ? b.revenue - a.revenue : b.cases - a.cases)).map(t => ({ label: t.territory, value: money ? t.revenue : t.cases, growth: t.growth, sub: t.doors + ' doors' }))} />
        </AnaCard>
        <AnaCard title="Top Products">
          <RankBars money={money} showGrowth items={[...A.bySku].sort((a, b) => (money ? b.revenue - a.revenue : b.cases - a.cases)).map(p => {
            const flavor = analyticsFlavorMeta(p.flavor);
            return { label: flavor.name, value: money ? p.revenue : p.cases, growth: p.growth, swatch: flavor.color };
          })} />
        </AnaCard>
      </div>

      <TerritoryFlavorMatrix A={A} money={money} />
    </div>
  );
}

function TerritoryFlavorMatrix({ A, money }) {
  const rows = A.byTerritoryFlavor || [];
  const flavors = [...new Set(rows.flatMap(row => (row.flavors || []).map(flavor => flavor.flavor)))]
    .sort((a, b) => analyticsFlavorMeta(a).name.localeCompare(analyticsFlavorMeta(b).name))
    .slice(0, 6);
  const max = Math.max(...rows.flatMap(row => (row.flavors || []).map(flavor => money ? flavor.revenue : flavor.cases)), 0) || 1;
  return (
    <AnaCard title="Flavor Demand by Territory" right={<span style={{ fontSize: 12, color: 'var(--muted)' }}>{money ? 'Dollars sold' : 'Cases sold'}</span>} pad={false}>
      <table className="rp-table">
        <thead>
          <tr>
            <th>Territory</th>
            {flavors.map(flavor => <th key={flavor} className="num">{analyticsFlavorMeta(flavor).name}</th>)}
            <th>Top Trend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const flavorMap = new Map((row.flavors || []).map(flavor => [flavor.flavor, flavor]));
            const top = (row.flavors || [])[0];
            return (
              <tr key={row.territory}>
                <td style={{ fontWeight: 700 }}>{row.territory}</td>
                {flavors.map(flavor => {
                  const item = flavorMap.get(flavor) || { cases: 0, revenue: 0 };
                  const value = money ? item.revenue : item.cases;
                  const meta = analyticsFlavorMeta(flavor);
                  return (
                    <td key={flavor} className="num" style={{ minWidth: 116 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        <span className="mono" style={{ fontWeight: 700 }}>{money ? RP.money(value) : Math.round(value).toLocaleString()}</span>
                        <span style={{ width: 48, height: 7, borderRadius: 100, background: 'var(--surface-2)', overflow: 'hidden', display: 'inline-flex' }}>
                          <span style={{ width: `${Math.max(0, value / max * 100)}%`, background: meta.color, borderRadius: 100 }} />
                        </span>
                      </div>
                    </td>
                  );
                })}
                <td>{top ? <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip flavorId={top.flavor} size={22} /><span style={{ fontWeight: 700 }}>{analyticsFlavorMeta(top.flavor).name}</span></div> : <span style={{ color: 'var(--faint)' }}>No sales yet</span>}</td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan={flavors.length + 2} style={{ textAlign: 'center', color: 'var(--faint)', padding: 26 }}>No sales match the selected analytics filters.</td></tr>}
        </tbody>
      </table>
    </AnaCard>
  );
}

/* ---------------- TIMING ---------------- */
function AnaTiming({ A, money }) {
  const bestDay = [...A.dow].sort((a, b) => b.cases - a.cases)[0] || { day: 'None', cases: 0 };
  const peakHour = anaPeakHour(A);
  const weekendShare = anaWeekendShare(A);
  const staffingTip = A.summary.orders
    ? `${bestDay.day} is the busiest live order day in this range. ${peakHour.orders ? `${peakHour.hour} is the busiest order hour.` : 'Hourly volume is still sparse.'}`
    : 'Live order timing will populate once sales orders are created or imported.';
  const dowData = A.dow.map(d => ({ label: d.day, value: money ? d.revenue : d.cases }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <KPICard label="Peak Day" value={bestDay.day} unit={`${bestDay.cases} cs`} icon="calendar" accent="var(--amber)" />
        <KPICard label="Peak Hour" value={peakHour.hour} unit={`${peakHour.orders} orders`} icon="clock" accent="var(--info)" />
        <KPICard label="Weekend Share" value={weekendShare} unit="% of orders" icon="analytics" accent="var(--navy)" />
      </div>

      <AnaCard title="Order Volume · Hour × Day" right={<span style={{ fontSize: 12, color: 'var(--muted)' }}>Orders placed</span>}>
        <HeatGrid cols={A.HOURS} data={A.heatmap} color="var(--amber)" />
      </AnaCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <AnaCard title={`By Day of Week`}>
          <ColBars data={dowData} money={money} color="var(--navy)" h={200} />
        </AnaCard>
        <AnaCard title="By Hour of Day">
          <ColBars data={A.HOURS.map((hr, i) => ({ label: hr, value: A.heatmap.reduce((s, r) => s + r.cells[i], 0) }))} color="var(--amber)" h={200} highlightLast={false} />
        </AnaCard>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--info-bg)', color: 'var(--info)', padding: '12px 16px', borderRadius: 10, fontSize: 13.5 }}>
        <Icon name="clock" size={17} /><span><b>Staffing tip:</b> {staffingTip}</span>
      </div>
    </div>
  );
}

/* ---------------- REPS ---------------- */
function AnaReps({ A, money, go }) {
  const reps = [...A.byRep].sort((a, b) => (money ? b.revenue - a.revenue : b.cases - a.cases));
  const max = Math.max(...reps.map(r => money ? r.revenue : r.cases));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AnaCard title="Rep Leaderboard" pad={false}>
        <table className="rp-table">
          <thead><tr><th>Rep</th><th>Territory</th><th className="num">{money ? 'Revenue' : 'Cases'}</th><th className="num">Orders</th><th className="num">AOV</th><th className="num">Visits</th><th className="num">Win&nbsp;Rate</th><th className="num">New Doors</th><th>Trend</th></tr></thead>
          <tbody>
            {reps.map(r => (
              <tr key={r.rep}>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Avatar name={r.rep} size={28} color="var(--navy)" /><span style={{ fontWeight: 600 }}>{r.rep}</span></div></td>
                <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{r.territory}</td>
                <td className="num" style={{ fontWeight: 600 }}>{money ? RP.money(r.revenue) : r.cases.toLocaleString()}</td>
                <td className="num">{r.orders}</td>
                <td className="num">{RP.money(r.aov)}</td>
                <td className="num">{r.visits}</td>
                <td className="num">{r.winRate ? Math.round(r.winRate * 100) + '%' : '—'}</td>
                <td className="num"><span style={{ fontWeight: 700, color: 'var(--ok)' }}>{r.newDoors || '—'}</span></td>
                <td><Sparkline data={r.spark} color={anaGrowthColor(r.growth)} w={64} h={22} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </AnaCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <AnaCard title={money ? 'Revenue by Rep' : 'Cases by Rep'}>
          <RankBars money={money} showGrowth items={reps.map(r => ({ label: r.rep, value: money ? r.revenue : r.cases, growth: r.growth }))} />
        </AnaCard>
        <AnaCard title="Activity → Outcome">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {reps.filter(r => r.visits > 0).map(r => (
              <div key={r.rep}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{r.rep}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}><b style={{ color: 'var(--ink)' }}>{r.visits}</b> visits · <b style={{ color: 'var(--ink)' }}>{r.orders}</b> orders · <b style={{ color: 'var(--amber-deep)' }}>{Math.round(r.winRate * 100)}%</b> win</span>
                </div>
                <div style={{ display: 'flex', gap: 4, height: 8 }}>
                  <div style={{ flex: r.visits, background: 'var(--surface-2)', borderRadius: 100, position: 'relative', overflow: 'hidden' }}><div style={{ position: 'absolute', inset: 0, width: (r.winRate * 100) + '%', background: 'var(--amber)', borderRadius: 100 }} /></div>
                </div>
              </div>
            ))}
          </div>
        </AnaCard>
      </div>
    </div>
  );
}

/* ---------------- PRODUCTS ---------------- */
function AnaProducts({ A, money, range }) {
  const skus = [...A.bySku].sort((a, b) => (money ? b.revenue - a.revenue : b.cases - a.cases));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 18 }}>
        <AnaCard title="Flavor Mix">
          <Donut centerValue={skus.reduce((s, p) => s + p.cases, 0)} centerLabel="cases" items={skus.map(p => {
            const flavor = analyticsFlavorMeta(p.flavor);
            return { label: flavor.name, value: p.cases, color: flavor.color };
          })} />
        </AnaCard>
        <AnaCard title={money ? 'Revenue by Flavor' : 'Cases by Flavor'}>
          <RankBars money={money} showGrowth items={skus.map(p => {
            const flavor = analyticsFlavorMeta(p.flavor);
            return { label: flavor.name, value: money ? p.revenue : p.cases, growth: p.growth, swatch: flavor.color };
          })} />
        </AnaCard>
      </div>

      <TerritoryFlavorMatrix A={A} money={money} />

      <AnaCard title="Product Detail" pad={false}>
        <table className="rp-table">
          <thead><tr><th>Flavor</th><th className="num">Cases · {anaRangeShortLabel(range)}</th><th className="num">Revenue</th><th className="num">Growth</th><th className="num">Velocity</th><th className="num">Return&nbsp;%</th><th>Share</th></tr></thead>
          <tbody>
            {skus.map(p => {
              const f = analyticsFlavorMeta(p.flavor);
              const totalCases = skus.reduce((s, x) => s + x.cases, 0);
              return (
                <tr key={p.flavor}>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FlavorChip flavorId={p.flavor} size={24} /><span style={{ fontWeight: 500 }}>{f.name}</span></div></td>
                  <td className="num" style={{ fontWeight: 600 }}>{p.cases}</td>
                  <td className="num">{RP.money(p.revenue)}</td>
                  <td className="num" style={{ fontWeight: 700, color: anaGrowthColor(p.growth) }}>{anaGrowthText(p.growth)}</td>
                  <td className="num">{p.velocity}<span style={{ color: 'var(--faint)', fontSize: 11 }}>/wk</span></td>
                  <td className="num" style={{ color: p.returns > 0.7 ? 'var(--warn)' : 'var(--muted)' }}>{p.returns}%</td>
                  <td style={{ width: 120 }}><Bar value={p.cases} max={Math.max(...skus.map(x => x.cases))} color={f.color} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </AnaCard>

      <AnaCard title="Order Size Distribution" right={<span style={{ fontSize: 12, color: 'var(--muted)' }}>cases per order</span>}>
        <ColBars data={A.orderSizes.map(o => ({ label: o.band, value: o.count }))} color="var(--navy)" h={180} />
      </AnaCard>
    </div>
  );
}

/* ---------------- CUSTOMER TYPE ---------------- */
function AnaCustomers({ A, money, go }) {
  const types = [...A.byType].sort((a, b) => (money ? b.revenue - a.revenue : b.cases - a.cases));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 18 }}>
        <AnaCard title="Channel Mix">
          <Donut centerValue={A.summary.activeDoors} centerLabel="doors" items={types.map((t, i) => ({ label: t.type, value: t.revenue, color: ['var(--amber)', 'var(--navy)', 'var(--info)', 'var(--ok)', 'var(--f-pinklem)', 'var(--faint)'][i] }))} />
        </AnaCard>
        <AnaCard title="Fastest Growing Segments">
          <RankBars money={money} showGrowth color="var(--ok)" items={[...types].sort((a, b) => anaGrowthSortValue(b.growth) - anaGrowthSortValue(a.growth)).map(t => ({ label: t.type, value: money ? t.revenue : t.cases, growth: t.growth, sub: t.doors + ' doors' }))} />
        </AnaCard>
      </div>

      <AnaCard title="Customer Type Detail" pad={false}>
        <table className="rp-table">
          <thead><tr><th>Type</th><th className="num">Doors</th><th className="num">{money ? 'Revenue' : 'Cases'}</th><th className="num">Avg / Door</th><th className="num">Growth</th><th>Share</th></tr></thead>
          <tbody>
            {types.map(t => (
              <tr key={t.type}>
                <td style={{ fontWeight: 600 }}>{t.type}</td>
                <td className="num">{t.doors}</td>
                <td className="num" style={{ fontWeight: 600 }}>{money ? RP.money(t.revenue) : t.cases.toLocaleString()}</td>
                <td className="num">{RP.money(t.doors ? Math.round(t.revenue / t.doors) : 0)}</td>
                <td className="num" style={{ fontWeight: 700, color: anaGrowthColor(t.growth) }}>{anaGrowthText(t.growth)}</td>
                <td style={{ width: 120 }}><Bar value={t.revenue} max={Math.max(...types.map(x => x.revenue))} color="var(--amber)" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </AnaCard>
    </div>
  );
}

Object.assign(window, { AnalyticsView, AnaCard });


/* ===== desktop_reports.jsx ===== */
/* ============================================================
   RANCH POWER - Operations Reports & Exports
   Complete, server-scoped previews and Excel/CSV downloads.
   ============================================================ */

const REPORTS_PREVIEW_ROWS = 100;
const REPORTS_MAX_EXPORT_ROWS = 50000;
const REPORTS_TIMEZONE = 'America/Vancouver';

const REPORTS_DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'mtd', label: 'Month to date' },
  { id: 'qtd', label: 'Quarter to date' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'last-year', label: 'Last year' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' }
];

function reportsArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function reportsText(value, fallback = '') {
  const text = String(value == null ? '' : value).trim();
  return text || fallback;
}

function reportsPositiveInteger(value, fallback, maximum) {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 1) return fallback;
  return maximum ? Math.min(maximum, parsed) : parsed;
}

function reportsColumn(raw, index) {
  const id = reportsText(raw && (raw.id || raw.key), `column-${index + 1}`);
  const allowedTypes = new Set(['text', 'date', 'datetime', 'number', 'currency', 'percent', 'boolean']);
  const type = reportsText(raw && raw.type, 'text').toLowerCase();
  return {
    id,
    label: reportsText(raw && (raw.label || raw.name), id),
    type: allowedTypes.has(type) ? type : 'text',
    width: Number(raw && raw.width) > 0 ? Number(raw.width) : undefined,
    default: raw && raw.default === true
  };
}

function reportsFilter(raw, index) {
  const id = reportsText(raw && (raw.id || raw.key), `filter-${index + 1}`);
  const type = reportsText(raw && raw.type, 'text').toLowerCase();
  if (!['date', 'select', 'text', 'boolean'].includes(type)) return null;
  return {
    id,
    label: reportsText(raw && (raw.label || raw.name), id),
    type,
    maxLength: reportsPositiveInteger(raw && raw.maxLength, 160, 1000),
    options: reportsArray(raw && raw.options).map(option => {
      if (option && typeof option === 'object') {
        const value = reportsText(option.value);
        return value ? { value, label: reportsText(option.label, value) } : null;
      }
      const value = reportsText(option);
      return value ? { value, label: value } : null;
    }).filter(Boolean)
  };
}

function reportsReport(raw, index) {
  const id = reportsText(raw && (raw.id || raw.reportId), `report-${index + 1}`);
  const columns = reportsArray(raw && raw.columns).map(reportsColumn);
  const columnIds = new Set(columns.map(column => column.id));
  const requestedDefaults = reportsArray(raw && (raw.defaultColumns || raw.defaults)).map(reportsText).filter(idValue => columnIds.has(idValue));
  const declaredDefaults = columns.filter(column => column.default).map(column => column.id);
  const defaultColumns = requestedDefaults.length
    ? requestedDefaults
    : declaredDefaults.length
      ? declaredDefaults
      : columns.map(column => column.id);
  return {
    id,
    label: reportsText(raw && (raw.label || raw.name), id),
    description: reportsText(raw && raw.description, 'Export complete live records for this data set.'),
    category: reportsText(raw && raw.category, 'Operations'),
    columns,
    defaultColumns,
    filters: reportsArray(raw && raw.filters).map(reportsFilter).filter(Boolean),
    roles: reportsArray(raw && (raw.roles || raw.accessRoles)).map(value => reportsText(value).toLowerCase()).filter(Boolean)
  };
}

function reportsRoleVisible(report) {
  if (!report.roles.length) return true;
  const role = typeof rpAuthProfile === 'function' ? rpNormalizeRole(rpAuthProfile().role) : '';
  return role === 'admin' || role === 'leadership' || report.roles.includes(role);
}

function reportsDerivedBundle(id, label, description, reports, predicate) {
  return {
    id,
    label,
    description,
    reportIds: reports.filter(predicate).map(report => report.id)
  };
}

function reportsBundleGroup(report) {
  const text = `${report.id} ${report.label} ${report.category}`.toLowerCase();
  if (/inventory|ledger|stock|dispatch|logistics|warehouse|route/.test(text)) return 'inventory-logistics';
  if (/customer|lead|visit|event|quote|growth|marketing/.test(text)) return 'customer-growth';
  if (/order|invoice|payment|expense|trade|sales|finance/.test(text)) return 'sales-finance';
  return '';
}

function reportsNormalizeCatalog(payload) {
  const sourceReports = reportsArray(payload && (payload.reports || payload.items || payload.templates));
  const reports = sourceReports.map(reportsReport).filter(report => report.id && report.columns.length && reportsRoleVisible(report));
  const allowed = new Set(reports.map(report => report.id));
  const serverBundles = reportsArray(payload && payload.bundles).map((raw, index) => ({
    id: reportsText(raw && raw.id, `bundle-${index + 1}`),
    label: reportsText(raw && (raw.label || raw.name), 'Report bundle'),
    description: reportsText(raw && raw.description, 'Export these reports as one Excel workbook.'),
    reportIds: reportsArray(raw && (raw.reportIds || raw.reports)).map(value => reportsText(value && typeof value === 'object' ? value.id : value)).filter(id => allowed.has(id))
  })).filter(bundle => bundle.reportIds.length);
  const derived = [
    reportsDerivedBundle('full-operations', 'Full Operations', 'Every report available to your role in one workbook.', reports, () => true),
    reportsDerivedBundle('sales-finance', 'Sales & Finance', 'Orders, invoicing, payments, expenses, and related financial records.', reports, report => reportsBundleGroup(report) === 'sales-finance'),
    reportsDerivedBundle('inventory-logistics', 'Inventory & Logistics', 'Inventory movements, stock activity, and logistics records.', reports, report => reportsBundleGroup(report) === 'inventory-logistics'),
    reportsDerivedBundle('customer-growth', 'Customer & Growth', 'Customers, leads, visits, events, and retail quotes.', reports, report => reportsBundleGroup(report) === 'customer-growth')
  ].filter(bundle => bundle.reportIds.length);
  const bundleById = new Map();
  [...derived, ...serverBundles].forEach(bundle => {
    const previous = bundleById.get(bundle.id);
    bundleById.set(bundle.id, previous
      ? { ...previous, ...bundle, reportIds: Array.from(new Set(bundle.reportIds.filter(id => allowed.has(id)))) }
      : bundle);
  });
  const rawLimits = payload && payload.limits || {};
  return {
    generatedAt: payload && payload.generatedAt || null,
    reports,
    bundles: Array.from(bundleById.values()).filter(bundle => bundle.reportIds.length),
    limits: {
      preview: Math.min(REPORTS_PREVIEW_ROWS, reportsPositiveInteger(rawLimits.preview || rawLimits.previewRows, REPORTS_PREVIEW_ROWS, 250)),
      exportPage: reportsPositiveInteger(rawLimits.exportPage || rawLimits.pageRows, 1000, 1000),
      maxExportRows: Math.min(REPORTS_MAX_EXPORT_ROWS, reportsPositiveInteger(rawLimits.maxExportRows || rawLimits.exportRows, REPORTS_MAX_EXPORT_ROWS, REPORTS_MAX_EXPORT_ROWS))
    }
  };
}

function reportsYmd(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function reportsToday() {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: REPORTS_TIMEZONE,
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, part.value]));
    return new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day)));
  } catch {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }
}

function reportsAddDays(date, days) {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + Number(days || 0));
  return next;
}

function reportsDatePresetBounds(preset) {
  const today = reportsToday();
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  if (preset === 'today') return { from: reportsYmd(today), to: reportsYmd(today) };
  if (preset === '7d') return { from: reportsYmd(reportsAddDays(today, -6)), to: reportsYmd(today) };
  if (preset === '30d') return { from: reportsYmd(reportsAddDays(today, -29)), to: reportsYmd(today) };
  if (preset === '90d') return { from: reportsYmd(reportsAddDays(today, -89)), to: reportsYmd(today) };
  if (preset === 'mtd') return { from: reportsYmd(new Date(Date.UTC(year, month, 1))), to: reportsYmd(today) };
  if (preset === 'qtd') return { from: reportsYmd(new Date(Date.UTC(year, Math.floor(month / 3) * 3, 1))), to: reportsYmd(today) };
  if (preset === 'ytd') return { from: `${year}-01-01`, to: reportsYmd(today) };
  if (preset === 'last-year') return { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` };
  if (preset === 'all') return { from: '', to: '' };
  return null;
}

function reportsDefaultFilterState(report) {
  return Object.fromEntries(report.filters.map(filter => [filter.id, '']));
}

function reportsDefaultColumnState(report) {
  return report.defaultColumns.filter(id => report.columns.some(column => column.id === id));
}

function reportsCleanFilters(report, values) {
  const allowed = new Set(report.filters.map(filter => filter.id));
  return Object.fromEntries(Object.entries(values || {})
    .filter(([id, value]) => allowed.has(id) && reportsText(value))
    .map(([id, value]) => [id, reportsText(value)]));
}

function reportsQueryInputKey(reportId, filters, columns) {
  return JSON.stringify({ reportId, filters, columns });
}

function reportsApiError(error, fallback) {
  if (error && error.name === 'AbortError') return 'Cancelled.';
  return reportsText(error && error.message, fallback);
}

function reportsExportError(error) {
  const message = reportsApiError(error, 'The report could not be exported.');
  if (/50,?000|too many|maximum|max(?:imum)? export|narrow/i.test(message)) {
    return `${message} Narrow the date range or add filters, then try again.`;
  }
  return message;
}

function reportsFileStem(value) {
  return reportsText(value, 'operations-report')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 70) || 'operations-report';
}

function reportsWorkbookColumns(columns) {
  return reportsArray(columns).map(column => ({
    key: column.id || column.key,
    label: column.label || column.id || column.key,
    type: column.type === 'text' ? 'string' : column.type,
    width: column.width
  }));
}

function reportsWorkbookRows(columns, rows) {
  const percentIds = reportsArray(columns).filter(column => column.type === 'percent').map(column => column.id || column.key);
  if (!percentIds.length) return rows;
  return reportsArray(rows).map(row => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
    const next = { ...row };
    percentIds.forEach(id => {
      const value = next[id];
      const number = Number(value);
      if (value != null && value !== '' && Number.isFinite(number)) next[id] = number / 100;
    });
    return next;
  });
}

function reportsFileResult(result, fallbackFileName, mimeType) {
  const directBlob = typeof Blob !== 'undefined' && result instanceof Blob ? result : null;
  const nestedBlob = result && typeof Blob !== 'undefined' && result.blob instanceof Blob ? result.blob : null;
  const textBlob = typeof result === 'string'
    ? new Blob([result], { type: mimeType })
    : result && typeof result.text === 'string'
      ? new Blob([result.text], { type: mimeType })
      : null;
  const blob = directBlob || nestedBlob || textBlob;
  if (!blob) throw new Error('The completed report could not be prepared for download. No file was saved.');
  return { blob, fileName: reportsText(result && result.fileName, fallbackFileName) };
}

function reportsDisplayValue(value, column) {
  if (value == null || value === '') return '-';
  if (column && column.type === 'boolean') {
    return value === true || value === 1 || String(value).toLowerCase() === 'true' ? 'Yes' : 'No';
  }
  if (column && column.type === 'currency') {
    const number = Number(value);
    return Number.isFinite(number)
      ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(number)
      : String(value);
  }
  if (column && column.type === 'percent') {
    const number = Number(value);
    return Number.isFinite(number) ? `${number.toLocaleString('en-CA', { maximumFractionDigits: 2 })}%` : String(value);
  }
  if (column && column.type === 'number') {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString('en-CA', { maximumFractionDigits: 4 }) : String(value);
  }
  if (column && (column.type === 'date' || column.type === 'datetime')) {
    const date = new Date(column.type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T12:00:00Z` : value);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('en-CA', column.type === 'date'
        ? { timeZone: REPORTS_TIMEZONE, year: 'numeric', month: 'short', day: '2-digit' }
        : { timeZone: REPORTS_TIMEZONE, year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      ).format(date);
    }
  }
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

async function reportsQueryPage({ report, filters, columns, offset, limit, snapshotAt, purpose, signal }) {
  return rpApi('/api/reports/query', {
    method: 'POST',
    signal,
    body: JSON.stringify({
      reportId: report.id,
      filters: reportsCleanFilters(report, filters),
      columns,
      offset,
      limit,
      ...(snapshotAt ? { snapshotAt } : {}),
      purpose
    })
  });
}

async function reportsFetchComplete({ report, filters, columns, pageSize, remainingCap, snapshotAt: requestedSnapshotAt, signal, onProgress }) {
  const rows = [];
  let offset = 0;
  let snapshotAt = reportsText(requestedSnapshotAt);
  let responseColumns = [];
  let generatedAt = '';
  let total = null;
  let page = 0;
  while (true) {
    if (signal && signal.aborted) throw new DOMException('The export was cancelled.', 'AbortError');
    const response = await reportsQueryPage({
      report,
      filters,
      columns,
      offset,
      limit: pageSize,
      snapshotAt,
      purpose: 'export',
      signal
    });
    const pageRows = reportsArray(response && response.rows);
    const responseTotal = Math.max(0, Number(response && response.total) || 0);
    if (total == null) {
      total = responseTotal;
      if (total > remainingCap) {
        throw new Error(`${report.label} matches ${total.toLocaleString()} rows, which would exceed the ${REPORTS_MAX_EXPORT_ROWS.toLocaleString()}-row workbook limit.`);
      }
    } else if (responseTotal !== total) {
      throw new Error(`${report.label} changed while it was being exported. Retry to create one consistent file.`);
    }
    const nextSnapshot = reportsText(response && response.snapshotAt);
    if (!snapshotAt) snapshotAt = nextSnapshot;
    else if (nextSnapshot && nextSnapshot !== snapshotAt) throw new Error(`${report.label} returned an inconsistent data snapshot. Retry the export.`);
    if (!responseColumns.length) responseColumns = reportsArray(response && response.columns).map(reportsColumn);
    generatedAt = reportsText(response && response.generatedAt, generatedAt);
    rows.push(...pageRows);
    page += 1;
    if (onProgress) onProgress({ loaded: rows.length, total, page });
    const hasMore = response && response.hasMore === true;
    if (!hasMore) break;
    if (!pageRows.length) throw new Error(`${report.label} returned an empty page before the export was complete.`);
    offset += pageRows.length;
    if (offset >= total) break;
  }
  if (rows.length !== total) {
    throw new Error(`${report.label} returned ${rows.length.toLocaleString()} of ${total.toLocaleString()} rows. No partial file was created.`);
  }
  return {
    report,
    columns: responseColumns.length ? responseColumns : report.columns.filter(column => columns.includes(column.id)),
    rows,
    total,
    snapshotAt,
    generatedAt
  };
}

function ReportsFilterControl({ filter, value, onChange }) {
  if (filter.type === 'select') {
    return (
      <label className="reports-filter-control">
        <span>{filter.label}</span>
        <select className="field" value={value || ''} onChange={event => onChange(event.target.value)}>
          <option value="">All</option>
          {filter.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>
    );
  }
  if (filter.type === 'date') {
    return (
      <label className="reports-filter-control">
        <span>{filter.label}</span>
        <input className="field" type="date" value={value || ''} onChange={event => onChange(event.target.value)} />
      </label>
    );
  }
  if (filter.type === 'boolean') {
    return (
      <label className="reports-filter-control">
        <span>{filter.label}</span>
        <select className="field" value={value || ''} onChange={event => onChange(event.target.value)}>
          <option value="">All</option><option value="true">Yes</option><option value="false">No</option>
        </select>
      </label>
    );
  }
  return (
    <label className="reports-filter-control">
      <span>{filter.label}</span>
      <input className="field" value={value || ''} maxLength={filter.maxLength || 160} onChange={event => onChange(event.target.value)} placeholder={`Any ${filter.label.toLowerCase()}`} />
    </label>
  );
}

function ReportsView({ go, detail }) {
  const [catalogState, setCatalogState] = useState({ loading: true, error: '', data: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [activeReportId, setActiveReportId] = useState('');
  const [filtersByReport, setFiltersByReport] = useState({});
  const [columnsByReport, setColumnsByReport] = useState({});
  const [datePreset, setDatePreset] = useState('all');
  const [preview, setPreview] = useState({ status: 'idle', error: '', rows: [], columns: [], total: null, inputKey: '', snapshotAt: '', generatedAt: '' });
  const [exportState, setExportState] = useState({ status: 'idle', error: '', message: '', progress: 0 });
  const previewAbortRef = useRef(null);
  const exportAbortRef = useRef(null);

  useEffect(() => {
    let current = true;
    setCatalogState({ loading: true, error: '', data: null });
    rpApi('/api/reports/catalog')
      .then(payload => {
        if (!current) return;
        const data = reportsNormalizeCatalog(payload || {});
        if (!data.reports.length) throw new Error('No report data sets are available for this account.');
        const filters = {};
        const columns = {};
        data.reports.forEach(report => {
          filters[report.id] = reportsDefaultFilterState(report);
          columns[report.id] = reportsDefaultColumnState(report);
        });
        const requestedReportId = detail && detail.type === 'report' ? reportsText(detail.id) : '';
        const requestedBundleId = detail && detail.type === 'bundle' ? reportsText(detail.id) : '';
        const requestedReport = data.reports.find(report => report.id === requestedReportId);
        const requestedBundle = data.bundles.find(bundle => bundle.id === requestedBundleId);
        if (requestedReport && detail && detail.filters && typeof detail.filters === 'object') {
          const allowed = new Set(requestedReport.filters.map(filter => filter.id));
          Object.entries(detail.filters).forEach(([id, value]) => {
            if (allowed.has(id) && value != null) filters[requestedReport.id][id] = reportsText(value);
          });
        }
        const firstReport = requestedReport || data.reports.find(report => requestedBundle && requestedBundle.reportIds.includes(report.id)) || data.reports[0];
        setFiltersByReport(filters);
        setColumnsByReport(columns);
        setSelectedTarget(requestedBundle ? `bundle:${requestedBundle.id}` : `report:${firstReport.id}`);
        setActiveReportId(firstReport.id);
        setDatePreset('all');
        setCatalogState({ loading: false, error: '', data });
      })
      .catch(error => {
        if (current) setCatalogState({ loading: false, error: reportsApiError(error, 'Reports could not be loaded.'), data: null });
      });
    return () => { current = false; };
  }, [reloadKey]);

  useEffect(() => () => {
    if (previewAbortRef.current) previewAbortRef.current.abort();
    if (exportAbortRef.current) exportAbortRef.current.abort();
  }, []);

  const catalog = catalogState.data;
  const reports = catalog ? catalog.reports : [];
  const bundles = catalog ? catalog.bundles : [];
  const activeReport = reports.find(report => report.id === activeReportId) || reports[0] || null;
  const selectedBundle = selectedTarget.startsWith('bundle:')
    ? bundles.find(bundle => `bundle:${bundle.id}` === selectedTarget) || null
    : null;
  const selectedReportIds = selectedBundle
    ? selectedBundle.reportIds.filter(id => reports.some(report => report.id === id))
    : activeReport ? [activeReport.id] : [];
  const activeFilters = activeReport ? filtersByReport[activeReport.id] || reportsDefaultFilterState(activeReport) : {};
  const activeColumns = activeReport ? columnsByReport[activeReport.id] || reportsDefaultColumnState(activeReport) : [];
  const currentInputKey = activeReport ? reportsQueryInputKey(activeReport.id, reportsCleanFilters(activeReport, activeFilters), activeColumns) : '';
  const previewStale = Boolean(preview.inputKey && preview.inputKey !== currentInputKey);
  const exportRunning = exportState.status === 'running' || exportState.status === 'building';
  const hasDateRange = activeReport && activeReport.filters.some(filter => filter.id === 'from') && activeReport.filters.some(filter => filter.id === 'to');

  const selectTarget = value => {
    setSelectedTarget(value);
    const reportId = value.startsWith('report:')
      ? value.slice(7)
      : ((bundles.find(bundle => `bundle:${bundle.id}` === value) || {}).reportIds || [])[0];
    if (reportId) setActiveReportId(reportId);
    setPreview(current => ({ ...current, status: 'idle', error: '', rows: [], columns: [], total: null, inputKey: '' }));
  };

  const setFilter = (id, value) => {
    if (!activeReport) return;
    setFiltersByReport(current => ({
      ...current,
      [activeReport.id]: { ...(current[activeReport.id] || reportsDefaultFilterState(activeReport)), [id]: value }
    }));
    if (id === 'from' || id === 'to') setDatePreset('custom');
  };

  const clearFilters = () => {
    if (!activeReport) return;
    setFiltersByReport(current => {
      const next = { ...current, [activeReport.id]: reportsDefaultFilterState(activeReport) };
      if (selectedBundle) {
        selectedReportIds.forEach(reportId => {
          if (reportId === activeReport.id) return;
          const report = reports.find(item => item.id === reportId);
          if (!report) return;
          const saved = { ...(current[reportId] || reportsDefaultFilterState(report)) };
          if (report.filters.some(filter => filter.id === 'from')) saved.from = '';
          if (report.filters.some(filter => filter.id === 'to')) saved.to = '';
          next[reportId] = saved;
        });
      }
      return next;
    });
    setDatePreset('all');
  };

  const applyDatePreset = preset => {
    setDatePreset(preset);
    const bounds = reportsDatePresetBounds(preset);
    if (!bounds) return;
    const targetIds = selectedBundle ? selectedReportIds : activeReport ? [activeReport.id] : [];
    setFiltersByReport(current => {
      const next = { ...current };
      targetIds.forEach(reportId => {
        const report = reports.find(item => item.id === reportId);
        if (!report) return;
        const ids = new Set(report.filters.map(filter => filter.id));
        if (!ids.has('from') || !ids.has('to')) return;
        next[reportId] = { ...(current[reportId] || reportsDefaultFilterState(report)), from: bounds.from, to: bounds.to };
      });
      return next;
    });
  };

  const toggleColumn = columnId => {
    if (!activeReport) return;
    setColumnsByReport(current => {
      const selected = current[activeReport.id] || reportsDefaultColumnState(activeReport);
      const next = selected.includes(columnId) ? selected.filter(id => id !== columnId) : [...selected, columnId];
      return { ...current, [activeReport.id]: activeReport.columns.map(column => column.id).filter(id => next.includes(id)) };
    });
  };

  const setAllColumns = () => {
    if (activeReport) setColumnsByReport(current => ({ ...current, [activeReport.id]: activeReport.columns.map(column => column.id) }));
  };

  const resetColumns = () => {
    if (activeReport) setColumnsByReport(current => ({ ...current, [activeReport.id]: reportsDefaultColumnState(activeReport) }));
  };

  const runPreview = async () => {
    if (!catalog || !activeReport || !activeColumns.length) return;
    if (previewAbortRef.current) previewAbortRef.current.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;
    const inputKey = currentInputKey;
    setPreview(current => ({ ...current, status: 'loading', error: '', rows: [], columns: [], total: null, inputKey }));
    try {
      const response = await reportsQueryPage({
        report: activeReport,
        filters: activeFilters,
        columns: activeColumns,
        offset: 0,
        limit: catalog.limits.preview,
        purpose: 'preview',
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      setPreview({
        status: 'ready',
        error: '',
        rows: reportsArray(response && response.rows),
        columns: reportsArray(response && response.columns).map(reportsColumn),
        total: Math.max(0, Number(response && response.total) || 0),
        inputKey,
        snapshotAt: reportsText(response && response.snapshotAt),
        generatedAt: reportsText(response && response.generatedAt)
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      setPreview(current => ({ ...current, status: 'error', error: reportsApiError(error, 'The report preview could not be loaded.'), rows: [], columns: [], total: null, inputKey }));
    } finally {
      if (previewAbortRef.current === controller) previewAbortRef.current = null;
    }
  };

  useEffect(() => {
    if (!catalog || !activeReport || !activeColumns.length) return;
    runPreview();
  }, [catalog && catalog.generatedAt, selectedTarget, activeReportId]);

  const requester = () => {
    const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
    return reportsText(profile.display_name || profile.email, 'Ranch Power Operations');
  };

  const exportReports = async format => {
    const needsActiveColumns = format === 'csv' || !selectedBundle;
    if (!catalog || !activeReport || (needsActiveColumns && !activeColumns.length) || exportRunning) return;
    const reportIds = format === 'csv' ? [activeReport.id] : selectedReportIds;
    const exportReportsList = reportIds.map(id => reports.find(report => report.id === id)).filter(Boolean);
    if (!exportReportsList.length) return;
    const controller = new AbortController();
    exportAbortRef.current = controller;
    setExportState({ status: 'running', error: '', message: `Preparing ${exportReportsList[0].label}...`, progress: 0 });
    try {
      const exportSnapshotAt = new Date().toISOString();
      const sheets = [];
      const filterDetails = {};
      let totalRows = 0;
      let generatedAt = new Date().toISOString();
      for (let index = 0; index < exportReportsList.length; index += 1) {
        const report = exportReportsList[index];
        const selected = selectedBundle && format === 'xlsx'
          ? report.columns.map(column => column.id)
          : columnsByReport[report.id] || reportsDefaultColumnState(report);
        if (!selected.length) throw new Error(`Select at least one column for ${report.label}.`);
        const filters = filtersByReport[report.id] || reportsDefaultFilterState(report);
        setExportState({
          status: 'running', error: '', progress: Math.round((index / exportReportsList.length) * 100),
          message: `Loading ${report.label} (${index + 1} of ${exportReportsList.length})...`
        });
        const sheet = await reportsFetchComplete({
          report,
          filters,
          columns: selected,
          pageSize: catalog.limits.exportPage,
          remainingCap: catalog.limits.maxExportRows - totalRows,
          snapshotAt: exportSnapshotAt,
          signal: controller.signal,
          onProgress: ({ loaded, total }) => {
            const reportProgress = total ? loaded / total : 0;
            const overall = ((index + reportProgress) / exportReportsList.length) * 100;
            setExportState({ status: 'running', error: '', progress: Math.min(99, Math.round(overall)), message: `Loading ${report.label}: ${loaded.toLocaleString()} of ${total.toLocaleString()} rows...` });
          }
        });
        totalRows += sheet.total;
        generatedAt = sheet.generatedAt || generatedAt;
        filterDetails[`${report.label} - Data snapshot`] = sheet.snapshotAt || exportSnapshotAt;
        Object.entries(reportsCleanFilters(report, filters)).forEach(([id, value]) => {
          const descriptor = report.filters.find(filter => filter.id === id);
          filterDetails[`${report.label} - ${descriptor ? descriptor.label : id}`] = value;
        });
        sheets.push({
          name: report.label,
          columns: reportsWorkbookColumns(sheet.columns),
          rows: format === 'xlsx' ? reportsWorkbookRows(sheet.columns, sheet.rows) : sheet.rows,
          total: sheet.total
        });
      }
      if (controller.signal.aborted) throw new DOMException('The export was cancelled.', 'AbortError');
      setExportState({ status: 'building', error: '', progress: 100, message: format === 'csv' ? 'Building CSV file...' : 'Building Excel workbook...' });
      const title = format === 'csv'
        ? activeReport.label
        : selectedBundle ? selectedBundle.label : activeReport.label;
      const date = reportsYmd(reportsToday());
      if (format === 'csv') {
        const fileName = `ranch-power-${reportsFileStem(title)}-${date}.csv`;
        const result = buildReportCsv({
          title,
          fileName,
          columns: sheets[0].columns,
          rows: sheets[0].rows
        });
        if (controller.signal.aborted) throw new DOMException('The export was cancelled.', 'AbortError');
        const file = reportsFileResult(result, fileName, 'text/csv;charset=utf-8');
        downloadReportBlob(file.blob, file.fileName);
      } else {
        const fileName = `ranch-power-${reportsFileStem(title)}-${date}.xlsx`;
        const result = await buildReportWorkbook({
          title,
          fileName,
          generatedAt,
          requestedBy: requester(),
          timezone: REPORTS_TIMEZONE,
          filters: Object.keys(filterDetails).length ? filterDetails : { Scope: 'All matching records' },
          sheets
        });
        if (controller.signal.aborted) throw new DOMException('The export was cancelled.', 'AbortError');
        const file = reportsFileResult(result, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        downloadReportBlob(file.blob, file.fileName);
      }
      setExportState({ status: 'complete', error: '', progress: 100, message: `${totalRows.toLocaleString()} rows exported. Your download is ready.` });
      toast(`${title} exported`, 'ok');
    } catch (error) {
      const cancelled = controller.signal.aborted || (error && error.name === 'AbortError');
      setExportState({
        status: cancelled ? 'cancelled' : 'error',
        error: cancelled ? '' : reportsExportError(error),
        progress: 0,
        message: cancelled ? 'Export cancelled. No file was created.' : ''
      });
    } finally {
      if (exportAbortRef.current === controller) exportAbortRef.current = null;
    }
  };

  const cancelExport = () => {
    if (exportAbortRef.current) exportAbortRef.current.abort();
  };

  if (catalogState.loading) {
    return (
      <div className="rp-fade reports-page">
        <PageHead eyebrow="Business Intelligence" title="Reports & Exports" sub="Loading the reports available to your account..." />
        <div className="card reports-loading"><Icon name="refresh" size={22} /><strong>Loading report catalog...</strong></div>
      </div>
    );
  }

  if (catalogState.error || !catalog) {
    return (
      <div className="rp-fade reports-page">
        <PageHead eyebrow="Business Intelligence" title="Reports & Exports" sub="Build complete Excel and CSV exports from live Operations data." />
        <div className="card reports-catalog-error">
          <ErrorNotice error={catalogState.error} title="Reports catalog unavailable" source="Operations Reports">{catalogState.error}</ErrorNotice>
          <button className="btn btn-primary" onClick={() => setReloadKey(value => value + 1)}><Icon name="refresh" size={15} />Try Again</button>
        </div>
      </div>
    );
  }

  const targetLabel = selectedBundle ? selectedBundle.label : activeReport ? activeReport.label : 'Report';
  const previewColumns = preview.columns.length ? preview.columns : activeReport.columns.filter(column => activeColumns.includes(column.id));

  return (
    <div className="rp-fade reports-page" data-reports-center="true">
      <PageHead eyebrow="Business Intelligence" title="Reports & Exports"
        sub="Preview complete live data, choose exactly what you need, and export a true Excel workbook or CSV."
        actions={<button className="btn btn-sm" onClick={() => setReloadKey(value => value + 1)} disabled={exportRunning}><Icon name="refresh" size={15} />Refresh Catalog</button>} />

      <div className="reports-bundle-grid" aria-label="Report bundles">
        {bundles.map(bundle => {
          const selected = selectedTarget === `bundle:${bundle.id}`;
          return (
            <button key={bundle.id} className={`reports-bundle-card${selected ? ' is-selected' : ''}`} onClick={() => selectTarget(`bundle:${bundle.id}`)}>
              <span className="reports-bundle-icon"><Icon name={bundle.id === 'inventory-logistics' ? 'inventory' : bundle.id === 'customer-growth' ? 'customers' : bundle.id === 'sales-finance' ? 'trade' : 'analytics'} size={18} /></span>
              <span><strong>{bundle.label}</strong><small>{bundle.description}</small><em>{bundle.reportIds.length} sheet{bundle.reportIds.length === 1 ? '' : 's'}</em></span>
            </button>
          );
        })}
      </div>

      <div className="reports-workspace">
        <aside className="reports-builder">
          <section className="card reports-builder-section">
            <div className="reports-section-head"><div><span className="eyebrow">1. Report</span><h2>Choose data</h2></div></div>
            <label className="reports-filter-control">
              <span>Report or workbook</span>
              <select className="field" value={selectedTarget} onChange={event => selectTarget(event.target.value)}>
                <optgroup label="Excel workbooks">
                  {bundles.map(bundle => <option key={`bundle-${bundle.id}`} value={`bundle:${bundle.id}`}>{bundle.label} ({bundle.reportIds.length} sheets)</option>)}
                </optgroup>
                <optgroup label="Individual reports">
                  {reports.map(report => <option key={`report-${report.id}`} value={`report:${report.id}`}>{report.label}</option>)}
                </optgroup>
              </select>
            </label>
            {selectedBundle && (
              <label className="reports-filter-control">
                <span>Configure workbook sheet</span>
                <select className="field" value={activeReport.id} onChange={event => { setActiveReportId(event.target.value); setPreview(current => ({ ...current, status: 'idle', inputKey: '' })); }}>
                  {selectedReportIds.map(reportId => {
                    const report = reports.find(item => item.id === reportId);
                    return report ? <option key={report.id} value={report.id}>{report.label}</option> : null;
                  })}
                </select>
              </label>
            )}
            <div className="reports-report-description"><strong>{activeReport.label}</strong><span>{activeReport.description}</span><small>{activeReport.category}</small></div>
          </section>

          <section className="card reports-builder-section">
            <div className="reports-section-head"><div><span className="eyebrow">2. Filters</span><h2>Narrow the report</h2></div><button className="btn btn-ghost btn-sm" onClick={clearFilters}>Reset</button></div>
            {hasDateRange && (
              <div className="reports-date-presets">
                {REPORTS_DATE_PRESETS.map(preset => <button key={preset.id} className={datePreset === preset.id ? 'is-selected' : ''} onClick={() => applyDatePreset(preset.id)}>{preset.label}</button>)}
              </div>
            )}
            <div className="reports-filter-grid">
              {activeReport.filters.map(filter => <ReportsFilterControl key={filter.id} filter={filter} value={activeFilters[filter.id] || ''} onChange={value => setFilter(filter.id, value)} />)}
            </div>
            {!activeReport.filters.length && <div className="reports-muted-note">This data set exports all accessible records and has no additional filters.</div>}
            {selectedBundle && hasDateRange && <div className="reports-muted-note"><Icon name="calendar" size={13} />Date presets apply to every compatible sheet in this workbook. Other filters apply to the sheet being configured.</div>}
          </section>

          <section className="card reports-builder-section">
            <div className="reports-section-head"><div><span className="eyebrow">3. Columns</span><h2>Choose fields</h2></div><span className="pill">{activeColumns.length} of {activeReport.columns.length}</span></div>
            <div className="reports-column-actions"><button className="btn btn-sm" onClick={setAllColumns}>Select All</button><button className="btn btn-sm" onClick={resetColumns}>Defaults</button></div>
            <div className="reports-column-list rp-scroll">
              {activeReport.columns.map(column => (
                <label key={column.id} className={activeColumns.includes(column.id) ? 'is-selected' : ''}>
                  <input type="checkbox" checked={activeColumns.includes(column.id)} onChange={() => toggleColumn(column.id)} />
                  <span><strong>{column.label}</strong><small>{column.type}</small></span>
                </label>
              ))}
            </div>
            {selectedBundle && <div className="reports-muted-note"><Icon name="check" size={13} />The Excel workbook always includes every safe column declared for each sheet. This picker controls the active preview and its optional CSV.</div>}
            {!activeColumns.length && <div className="reports-inline-error">Select at least one column before previewing or exporting this sheet as CSV.</div>}
          </section>
        </aside>

        <div className="reports-results">
          <section className="card reports-export-panel">
            <div className="reports-export-copy">
              <span className="eyebrow">Export target</span>
              <h2>{targetLabel}</h2>
              <p>{selectedBundle ? `${selectedReportIds.length} accessible reports will be exported as separate Excel sheets.` : 'The selected columns and all matching records will be exported.'}</p>
            </div>
            <div className="reports-export-actions">
              <button className="btn btn-primary" disabled={exportRunning || (!selectedBundle && !activeColumns.length)} onClick={() => exportReports('xlsx')}><Icon name="download" size={16} />{selectedBundle ? 'Export Complete Workbook' : 'Export Excel'}</button>
              <button className="btn" disabled={exportRunning || !activeColumns.length} onClick={() => exportReports('csv')}><Icon name="doc" size={16} />{selectedBundle ? `Export ${activeReport.label} CSV` : 'Export CSV'}</button>
              {exportRunning && <button className="btn reports-cancel-btn" onClick={cancelExport}><Icon name="x" size={15} />Cancel</button>}
            </div>
            {exportState.status !== 'idle' && (
              <div className={`reports-export-status is-${exportState.status}`} role={exportState.error ? 'alert' : 'status'}>
                {exportRunning && <div className="reports-progress"><span style={{ width: `${Math.max(2, exportState.progress)}%` }} /></div>}
                <div><Icon name={exportState.error ? 'alert' : exportState.status === 'complete' ? 'check' : exportState.status === 'cancelled' ? 'x' : 'refresh'} size={15} /><span>{exportState.error || exportState.message}</span></div>
              </div>
            )}
            <div className="reports-cap-note"><Icon name="info" size={14} /><span>Exports include up to {catalog.limits.maxExportRows.toLocaleString()} rows across a workbook. If more records match, narrow the date range or add filters. Partial files are never downloaded.</span></div>
          </section>

          <section className="card reports-preview-card">
            <div className="reports-preview-head">
              <div><span className="eyebrow">Live preview</span><h2>{activeReport.label}</h2><p>{preview.total == null ? 'Run a preview to see an exact count.' : `${preview.total.toLocaleString()} matching row${preview.total === 1 ? '' : 's'} - showing the first ${preview.rows.length.toLocaleString()}.`}</p></div>
              <button className="btn btn-sm" onClick={runPreview} disabled={preview.status === 'loading' || !activeColumns.length}><Icon name="refresh" size={15} />{preview.status === 'loading' ? 'Loading...' : previewStale ? 'Update Preview' : 'Run Preview'}</button>
            </div>
            {previewStale && <div className="reports-preview-stale"><Icon name="info" size={14} />Filters or columns changed. Update the preview before relying on the count.</div>}
            {preview.status === 'error' && <ErrorNotice error={preview.error} title={`${activeReport.label} preview failed`} context={{ report: activeReport.id }} source="Operations Reports">{preview.error}</ErrorNotice>}
            {preview.status === 'loading' && <div className="reports-loading reports-preview-loading"><Icon name="refresh" size={20} /><strong>Loading a complete, server-scoped preview...</strong></div>}
            {preview.status === 'ready' && (
              <>
                <div className="reports-preview-table-wrap rp-scroll">
                  <table className="rp-table reports-preview-table">
                    <thead><tr>{previewColumns.map(column => <th key={column.id} className={['number', 'currency', 'percent'].includes(column.type) ? 'num' : ''}>{column.label}</th>)}</tr></thead>
                    <tbody>
                      {preview.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>{previewColumns.map(column => {
                          const display = reportsDisplayValue(row && row[column.id], column);
                          return <td key={column.id} title={display} className={['number', 'currency', 'percent'].includes(column.type) ? 'num' : ''}>{display}</td>;
                        })}</tr>
                      ))}
                      {!preview.rows.length && <tr><td colSpan={Math.max(1, previewColumns.length)} className="reports-empty-cell">No records match these filters.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="reports-preview-foot"><span>Exact total: <strong>{preview.total.toLocaleString()}</strong></span><span>Snapshot: <strong>{preview.snapshotAt ? reportsDisplayValue(preview.snapshotAt, { type: 'datetime' }) : 'Current'}</strong></span></div>
              </>
            )}
            {preview.status === 'idle' && <div className="reports-preview-empty"><Icon name="analytics" size={24} /><strong>Preview ready to run</strong><span>Choose filters and columns, then load the first {catalog.limits.preview} rows with an exact total.</span></div>}
          </section>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { ReportsView });


/* ===== desktop_accounting.jsx ===== */
/* Accounting reads complete server datasets; it never changes stock or sums currencies. */
const ACCOUNTING_SECTIONS = [
  ['overview', 'Month-end'], ['income', 'Income & taxes'], ['invoices', 'Invoices outstanding'],
  ['expenses', 'Expenses'], ['inventory', 'Inventory'], ['shopify', 'Shopify coverage']
];
const accountingText = value => value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
const accountingWarnings = value => (Array.isArray(value) ? value : value ? [value] : []).map(item => accountingText(item?.message || item)).filter(Boolean);
function accountingPreviousMonth(today = typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10)) {
  const end = new Date(`${today.slice(0, 7)}-01T12:00:00Z`); end.setUTCDate(0);
  const dateTo = end.toISOString().slice(0, 10);
  return { dateFrom: `${dateTo.slice(0, 7)}-01`, dateTo, asOf: dateTo };
}
function accountingMonthFilters(month, today = typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10)) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month) || month > today.slice(0, 7)) throw new Error('Choose this month or an earlier month.');
  const end = new Date(`${month}-01T12:00:00Z`); end.setUTCMonth(end.getUTCMonth() + 1); end.setUTCDate(0);
  const dateTo = [end.toISOString().slice(0, 10), today].sort()[0];
  return { dateFrom: `${month}-01`, dateTo, asOf: dateTo };
}
function accountingValidateDates(filters) {
  const valid = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') && !Number.isNaN(new Date(`${value}T12:00:00Z`).getTime()) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
  if (!['dateFrom', 'dateTo', 'asOf'].every(key => valid(filters[key])) || filters.dateFrom > filters.dateTo) throw new Error('Choose valid start, end and as-of dates. The start must be before the end.');
}
function accountingColumns(report) {
  const columns = (Array.isArray(report?.columns) ? report.columns : []).map(column => ({ ...column, key: column.key || column.id, label: column.label || column.key || column.id }));
  if (!columns.length || columns.some(column => !column.key) || new Set(columns.map(column => column.key)).size !== columns.length) throw new Error('This report has no valid export column definition. Refresh the report catalog.');
  // A generic dollar symbol would mislabel Shopify currencies. Every amount
  // remains beside its server currency column, with no conversion or netting.
  return columns.map(column => column.type === 'currency' ? { ...column, format: '#,##0.00;[Red](#,##0.00);-' } : column);
}
function accountingCheckCancelled(signal) { if (signal?.aborted) throw new DOMException('Export cancelled. No file was downloaded.', 'AbortError'); }
function accountingAppendRows(rows, page, seen, label) {
  for (const row of page) {
    const id = row && accountingText(row.id);
    if (!id || seen.has(id)) throw new Error(`${label}: a missing or duplicate record ID prevents a verified export. No partial file was created.`);
    seen.add(id); rows.push(row);
  }
}
function accountingCell(column, value) {
  if (value == null || value === '') return '—';
  if (column.type === 'boolean') return value === true ? 'Yes' : value === false ? 'No' : accountingText(value);
  if (['number', 'currency', 'percent'].includes(column.type) && Number.isFinite(Number(value))) return new Intl.NumberFormat('en-CA', { minimumFractionDigits: column.type === 'currency' ? 2 : 0, maximumFractionDigits: column.type === 'currency' ? 2 : 6 }).format(Number(value));
  if (column.type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  if (column.type === 'datetime' && Number.isFinite(Date.parse(value))) return new Intl.DateTimeFormat('en-CA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Vancouver' }).format(new Date(value));
  return accountingText(value);
}
function accountingSection(report) {
  const section = accountingText(report.section).toLowerCase();
  if (report.endpoint === 'shopify' || section.includes('shopify')) return 'shopify';
  if (section.includes('invent')) return 'inventory';
  if (section.includes('expense') || section.includes('purchase') || section.includes('procure')) return 'expenses';
  if (section.includes('invoice') || section.includes('receiv')) return 'invoices';
  return 'income';
}
async function accountingFetchNative({ report, filters, snapshotAt = '', cap, pageSize = 500, signal, onProgress, api = rpApi }) {
  const rows = [], seen = new Set(); let offset = 0, total = null, version = '', snapshot = snapshotAt; const warnings = new Set(); let metadata = {};
  while (true) {
    accountingCheckCancelled(signal);
    const page = await api('/api/accounting/report', { method: 'POST', signal, body: JSON.stringify({ reportId: report.id, filters, limit: pageSize, offset, ...(snapshot ? { snapshotAt: snapshot } : {}), ...(version ? { datasetVersion: version } : {}) }) });
    accountingCheckCancelled(signal);
    if (page.reportId !== report.id || !Array.isArray(page.rows) || !Number.isSafeInteger(page.total) || page.total < 0 || !page.snapshotAt || !page.datasetVersion) throw new Error(`${report.label}: invalid completeness evidence. No partial file was created.`);
    if (snapshot && page.snapshotAt !== snapshot) throw new Error(`${report.label}: the data cutoff changed. Start a fresh export.`);
    snapshot = page.snapshotAt;
    if (total !== null && (page.total !== total || page.datasetVersion !== version)) throw new Error(`${report.label}: source records changed during export. Start a fresh export.`);
    total = page.total; version = page.datasetVersion;
    if (total > cap) throw new Error(`${report.label} exceeds the remaining ${cap.toLocaleString()} row allowance. Narrow the period.`);
    if (page.completeness === 'incomplete' || page.complete === false && page.nextOffset == null || ['incomplete', 'unavailable'].includes(page.coverage?.status)) throw new Error(`${report.label}: source coverage is incomplete. ${accountingWarnings(page.warnings).join(' ')}`);
    accountingWarnings(page.warnings).forEach(warning => warnings.add(warning));
    accountingAppendRows(rows, page.rows, seen, report.label);
    if (rows.length > total || rows.length > cap) throw new Error(`${report.label}: unexpected extra rows. No partial file was created.`);
    metadata = { ...page, rows: undefined };
    onProgress?.({ label: report.label, loaded: rows.length, total });
    if (page.nextOffset == null) { if (rows.length !== total) throw new Error(`${report.label}: only ${rows.length} of ${total} rows arrived. No partial file was created.`); break; }
    if (!page.rows.length || page.nextOffset !== offset + page.rows.length || page.nextOffset >= total) throw new Error(`${report.label}: pagination stopped before all records arrived.`);
    offset = page.nextOffset;
  }
  return { report, rows, total, snapshotAt: snapshot, warnings: [...warnings], metadata, complete: true };
}
async function accountingFetchShopify({ reports, filters, snapshotAt, cap, signal, onProgress, api = rpApi }) {
  let index = null;
  try {
    const status = await api('/api/accounting/shopify-index', { method: 'POST', signal, body: JSON.stringify({ action: 'status', from: filters.dateFrom, to: filters.dateTo }) });
    if (status?.status === 'available' && status.snapshotId && status.snapshotAt) index = status;
    else if (status?.snapshotId && ['building', 'stale', 'failed'].includes(status.status)) throw new Error('This Shopify month has pending updates or needs a verified refresh. Retry after its prepared report is ready. No partial file was created.');
  } catch (error) {
    if (/pending updates or needs a verified refresh/.test(error?.message || '')) throw error;
    if ([401, 403].includes(error?.status)) throw error;
  }
  accountingCheckCancelled(signal);
  const sourceSnapshotAt = index?.snapshotAt || snapshotAt;
  const sheets = new Map(reports.map(report => [report.dataset, { report, rows: [], seen: new Set(), warnings: [], metadata: {}, snapshotAt: sourceSnapshotAt, complete: true }]));
  const modes = [...new Set(reports.map(report => report.mode || (report.dataset === 'fees' ? 'balances' : report.dataset === 'payouts' ? 'payouts' : 'orders')))];
  let loaded = 0;
  for (const mode of modes) {
    let cursor = ''; const seen = new Set(); let pages = 0;
    while (true) {
      accountingCheckCancelled(signal);
      const page = index
        ? await api('/api/accounting/shopify-index', { method: 'POST', signal, body: JSON.stringify({ action: 'page', mode, from: filters.dateFrom, to: filters.dateTo, snapshotId: index.snapshotId, ...(cursor ? { cursor } : {}) }) })
        : await api('/api/accounting/shopify', { method: 'POST', signal, body: JSON.stringify({ mode, from: filters.dateFrom, to: filters.dateTo, snapshotAt, ...(cursor ? { cursor } : {}) }) });
      accountingCheckCancelled(signal);
      if (page.source !== 'shopify' || page.mode !== mode || page.snapshotAt !== sourceSnapshotAt || index && (page.snapshotId !== index.snapshotId || page.indexStatus !== 'available') || !page.rows || typeof page.rows !== 'object') throw new Error('Shopify returned inconsistent source or date-cutoff evidence.');
      if (page.coverage?.status !== 'available') throw new Error(`Shopify ${mode} coverage is ${page.coverage?.status || 'unverified'}. ${accountingWarnings(page.warnings).join(' ')} Choose a clearly marked Ranch Power only export if needed.`);
      for (const sheet of sheets.values()) {
        if ((sheet.report.mode || (sheet.report.dataset === 'fees' ? 'balances' : sheet.report.dataset === 'payouts' ? 'payouts' : 'orders')) !== mode) continue;
        const values = page.rows[sheet.report.dataset];
        if (!Array.isArray(values)) throw new Error(`Shopify did not return the ${sheet.report.dataset} dataset.`);
        accountingAppendRows(sheet.rows, values, sheet.seen, sheet.report.label); loaded += values.length;
        sheet.warnings = [...new Set([...sheet.warnings, ...accountingWarnings(page.warnings)])];
        sheet.metadata = { coverage: page.coverage, source: page.source, shopDomain: page.shopDomain, timezone: page.timezone, dateBasis: page.dateBasis,
          indexStatus: page.indexStatus || 'live', snapshotId: page.snapshotId || '', completedAt: page.completedAt || '', freshUntil: page.freshUntil || '' };
      }
      if (loaded > cap) throw new Error('Shopify exceeds the workbook row allowance. Narrow the period.');
      onProgress?.({ label: `Shopify ${index ? 'prepared' : 'live'} ${mode}`, loaded, total: null });
      pages++;
      if (!page.nextCursor) { if (page.complete !== true) throw new Error(`Shopify ${mode} is incomplete. No partial file was created.`); break; }
      if (page.complete === true || seen.has(page.nextCursor) || pages >= 2000) throw new Error('Shopify pagination could not prove a complete extract.');
      seen.add(page.nextCursor); cursor = page.nextCursor;
    }
  }
  if (index) {
    const current = await api('/api/accounting/shopify-index', { method: 'POST', signal, body: JSON.stringify({ action: 'status', from: filters.dateFrom, to: filters.dateTo }) });
    accountingCheckCancelled(signal);
    if (current?.status !== 'available' || current.snapshotId !== index.snapshotId || current.snapshotAt !== index.snapshotAt) throw new Error('New Shopify updates arrived during export. Refresh the prepared month. No partial file was created.');
  }
  return [...sheets.values()].map(({ seen, ...sheet }) => ({ ...sheet, total: sheet.rows.length }));
}
function accountingReconciliationNotes(sheets) {
  const sales = sheets.find(sheet => sheet.report.endpoint === 'shopify' && sheet.report.dataset === 'sales');
  const normalize = value => /^(?:gid:\/\/shopify\/Order\/)?([1-9]\d*)$/.exec(accountingText(value).trim())?.[1] || '';
  const sourceIds = new Set((sales?.rows || []).map(row => normalize(row.shopifyOrderId)).filter(Boolean));
  const bridge = sheets.find(sheet => sheet.report.id === 'source_links')?.rows || [];
  const invoiceRefs = sheets.find(sheet => sheet.report.id === 'income')?.rows || [];
  const eventIds = new Set(bridge.filter(row => row.eventId).map(row => normalize(row.shopifyOrderId)).filter(Boolean));
  const notes = [];
  const uncertain = [...new Set(sheets.filter(sheet => sheet.report.endpoint !== 'shopify').flatMap(sheet => sheet.rows).filter(row => String(row.accountingTreatment || '').includes('unverified')).map(row => accountingText(row.orderId || row.id)).filter(Boolean))];
  if (uncertain.length) notes.push({ report: 'Unverified source links', source: 'Recorded source evidence', rows: uncertain.length, completeness: 'Source classification needs review', warnings: `${uncertain.length} native records have an unverified accounting treatment. Reconcile the source before recognizing income. Example record IDs: ${uncertain.slice(0, 10).join(', ')}${uncertain.length > 10 ? ' (first 10 shown)' : ''}.` });
  if (!sales) return notes;
  const hintedOrders = new Set(sales.rows.map(row => accountingText(row.nativeOrderIdHint).trim()).filter(Boolean));
  const hintedInvoices = new Set(sales.rows.map(row => accountingText(row.nativeInvoiceNumberHint).trim()).filter(Boolean));
  const hinted = [...new Set(invoiceRefs.filter(row => !normalize(row.shopifyOrderId) && (hintedOrders.has(accountingText(row.orderId).trim()) || hintedInvoices.has(accountingText(row.invoiceNumber).trim()))).map(row => accountingText(row.orderId || row.id)))];
  if (hinted.length) notes.push({ report: 'Shopify native-reference hints', source: 'Exact source custom-attribute references', rows: hinted.length, completeness: 'Possible mirrored invoices need review', warnings: `${hinted.length} native invoices without a Shopify order pointer are named by an exact Shopify order or invoice reference. These hints do not prove a match or authorize counting both sources. Example native order IDs: ${hinted.slice(0, 10).join(', ')}${hinted.length > 10 ? ' (first 10 shown)' : ''}.` });
  for (const [label, records] of [['Native invoice references', invoiceRefs], ['Recorded Shopify/event links', bridge]]) {
    const missing = [...new Set(records.map(row => normalize(row.shopifyOrderId)).filter(id => id && !sourceIds.has(id)))];
    if (missing.length) notes.push({ report: label, source: 'Exact ID reconciliation', rows: missing.length, completeness: 'Review matching source periods', warnings: `${missing.length} distinct Shopify order references are absent from the selected Shopify sales period. Invoice, link and processed dates may differ; this does not prove a missing sale. Example order IDs: ${missing.slice(0, 10).join(', ')}${missing.length > 10 ? ' (first 10 shown)' : ''}.` });
  }
  if (sheets.some(sheet => sheet.report.id === 'source_links')) {
    const unattributed = [...new Set(sales.rows.filter(row => row.recognizedSale !== false && (String(row.sourceChannel || '').toLowerCase() === 'pos' || row.posLocationId)).map(row => normalize(row.shopifyOrderId)).filter(id => id && !eventIds.has(id)))];
    if (unattributed.length) notes.push({ report: 'POS event attribution', source: 'Exact ID reconciliation', rows: unattributed.length, completeness: 'Event attribution needs review', warnings: `${unattributed.length} eligible POS sales have no exact recorded event link. POS activity may also be ordinary store sales; no event has been inferred. Example order IDs: ${unattributed.slice(0, 10).join(', ')}${unattributed.length > 10 ? ' (first 10 shown)' : ''}.` });
  }
  return notes;
}
function accountingEvidenceRows(sheets, filters, catalog, scope) {
  const rows = [{ report: 'Export scope', source: scope, rows: '', snapshotAt: '', timezone: catalog.timezone || 'America/Vancouver', dateBasis: `Period ${filters.dateFrom} through ${filters.dateTo}; as of ${filters.asOf}`, completeness: 'Only requested sources', warnings: scope === 'Ranch Power only' ? 'Shopify source records, payouts and fees are excluded. This is not a complete cross-source month-end workbook.' : 'Amounts retain their source currencies. Native Shopify mirrors are evidence; do not add them to Shopify source sales.' }];
  for (const sheet of sheets) rows.push({ report: sheet.report.label, source: sheet.metadata.source || sheet.report.source || '', rows: sheet.total, snapshotAt: sheet.snapshotAt, timezone: sheet.metadata.timezone || sheet.report.timezone || catalog.timezone || '', dateBasis: sheet.metadata.dateBasis || sheet.report.dateBasis || '', completeness: 'All matching rows retrieved', warnings: [...accountingWarnings(catalog.warnings), ...sheet.warnings, ...(sheet.report.endpoint === 'shopify' ? [sheet.metadata.indexStatus === 'available' ? `Prepared Shopify source snapshot ${sheet.metadata.snapshotId} completed ${sheet.metadata.completedAt}; valid through ${sheet.metadata.freshUntil}.` : 'Shopify is a live read with a cutoff, not an atomic historical archive.'] : [])].join(' | ') });
  return [...rows, ...accountingReconciliationNotes(sheets)];
}
const ACCOUNTING_EVIDENCE_COLUMNS = ['report', 'source', 'rows', 'snapshotAt', 'timezone', 'dateBasis', 'completeness', 'warnings'].map(key => ({ key, label: ({ snapshotAt: 'Data cutoff', dateBasis: 'Date basis' })[key] || key[0].toUpperCase() + key.slice(1), type: key === 'rows' ? 'number' : 'string', wrap: true }));
const ACCOUNTING_MONTHLY_COLUMNS = [
  { key: 'period', label: 'Period', type: 'string' }, { key: 'source', label: 'Source', type: 'string' }, { key: 'currency', label: 'Currency', type: 'string' },
  { key: 'grossRevenue', label: 'Gross before tax', type: 'currency', format: '#,##0.00;[Red](#,##0.00);-' },
  { key: 'pstCharged', label: 'PST charged', type: 'currency', format: '#,##0.00;[Red](#,##0.00);-' },
  { key: 'gstCharged', label: 'GST charged', type: 'currency', format: '#,##0.00;[Red](#,##0.00);-' },
  { key: 'recordCount', label: 'Invoices / orders', type: 'number' }
];
const ACCOUNTING_EXCEPTION_COLUMNS = [
  { key: 'source', label: 'Source', type: 'string' }, { key: 'currency', label: 'Currency', type: 'string' },
  { key: 'code', label: 'Review code', type: 'string' }, { key: 'count', label: 'Records', type: 'number' },
  { key: 'amount', label: 'Recorded amount', type: 'currency', format: '#,##0.00;[Red](#,##0.00);-' },
  { key: 'message', label: 'Review needed', type: 'string', wrap: true }, { key: 'examples', label: 'Example references', type: 'string', wrap: true }
];

const ACCOUNTING_REVIEW_REASONS = {
  unverified_native_link: 'This invoice has a Shopify draft or event reference without a verified Shopify order identity.',
  plausible_unlinked_overlap: 'An unlinked Shopify sale may match this invoice. The candidate is a review lead, not proof of a duplicate.',
  native_source_hint: 'A Shopify order names this native record, but the exact source link has not been verified.',
  event_link: 'An event link names a Shopify source that needs an exact identity check.'
};
const ACCOUNTING_CANDIDATE_REASONS = {
  same_total_near_date: 'Nearby date and equal amount only; this does not prove a duplicate.',
  native_source_hint: 'The Shopify order names this Ranch Power order or invoice. Check the original source link.',
  native_source_identifier: 'The Shopify source identifier names this Ranch Power record. Check the original source link.',
  draft_source_match: 'The Shopify order names the linked draft. Confirm the draft conversion and sale identity.',
  event_link: 'A recorded event link names this order. Check the original event sale record.',
  exact_order_lookup: 'Found by exact Shopify Order ID lookup; check the original source records before choosing it.'
};
const ACCOUNTING_SOURCE_BLOCKERS = {
  invalidSourceIdentity: 'A source order ID is missing or invalid.',
  repeatedShopifyOrder: 'The same Shopify order ID appears more than once in the indexed history.',
  missingDirectPointer: 'A Ranch Power invoice links to a Shopify order absent from indexed history.',
  ineligibleDirectPointer: 'A Ranch Power invoice links to a Shopify order that is not an eligible sale.',
  conflictingDirectPointer: 'Source links conflict, or more than one Ranch Power invoice points to one Shopify order.',
  unresolvedShopifyHint: 'A Shopify source hint cannot be matched to a Ranch Power invoice.',
  ineligible_linked_shopify_sale: 'A linked Shopify order is not an eligible CAD sale. Check its status, currency and source link.'
};
function accountingSourceBlocker(code) {
  return ACCOUNTING_SOURCE_BLOCKERS[code] || String(code || 'Unknown source issue').replace(/([a-z])([A-Z])/g, '$1 $2');
}
function accountingReviewProducts(lines) {
  return Array.isArray(lines) ? lines.map(line => typeof line === 'string' ? line
    : [line.quantity, line.title || line.name || line.sku].filter(Boolean).join(' × ')).filter(Boolean).join('; ') : '';
}
function accountingReviewReason(item) {
  if (item.reasonText || item.reason) return item.reasonText || item.reason;
  return (item.reasonCodes || []).map(code => ACCOUNTING_REVIEW_REASONS[code] || code).join(' ') || 'The invoice needs a source identity review.';
}
function accountingReviewOperationsLink(type, id) {
  const value = String(id || '').trim();
  if (type === 'invoice' ? !/^INV-[A-Za-z0-9-]{1,160}$/.test(value)
    : type === 'order' ? !/^SO-[A-Za-z0-9-]{1,160}$/.test(value) : true) return '';
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('workspace', type === 'invoice' ? 'invoicing' : 'orders');
  url.searchParams.set('detail', type);
  url.searchParams.set('record', value);
  return `${url.pathname}${url.search}`;
}
function accountingReviewShopifyLink(type, id) {
  const value = String(id || '').trim();
  const match = type === 'draft'
    ? /^(?:gid:\/\/shopify\/DraftOrder\/)?([1-9][0-9]*)$/.exec(value)
    : type === 'order' ? /^(?:gid:\/\/shopify\/Order\/)?([1-9][0-9]*)$/.exec(value) : null;
  return match ? `https://ranch-power.myshopify.com/admin/${type === 'draft' ? 'draft_orders' : 'orders'}/${match[1]}` : '';
}
function AccountingReviewNativeLinks({ item }) {
  const invoiceUrl = accountingReviewOperationsLink('invoice', item.invoiceNumber);
  const orderUrl = accountingReviewOperationsLink('order', item.nativeOrderId);
  const draftUrl = accountingReviewShopifyLink('draft', item.shopifyDraftOrderId);
  return <div className="accounting-actions" style={{ margin: '10px 0' }}>
    {invoiceUrl && <a className="btn btn-sm" href={invoiceUrl} target="_blank" rel="noopener noreferrer">Open invoice {item.invoiceNumber}</a>}
    {orderUrl && <a className="btn btn-sm" href={orderUrl} target="_blank" rel="noopener noreferrer">Open Ranch Power order {item.nativeOrderId}</a>}
    {draftUrl && <a className="btn btn-sm" href={draftUrl} target="_blank" rel="noopener noreferrer">Open Shopify draft</a>}
  </div>;
}
function AccountingReviewShopifyOrderLink({ candidate }) {
  const url = accountingReviewShopifyLink('order', candidate.shopifyOrderId);
  return url ? <a className="btn btn-sm" href={url} target="_blank" rel="noopener noreferrer">Open Shopify order {candidate.orderNumber || candidate.shopifyOrderId}</a> : null;
}
function accountingReviewPayload(value, month) {
  if (value?.month !== month || !['ready', 'reviewable', 'source_refresh_required', 'source_repair_required', 'awaiting_regeneration'].includes(value.status)
    || !Array.isArray(value.items) || value.items.some(item => !item.nativeOrderId || !item.invoiceNumber
      || !Array.isArray(item.candidates) || new Set(item.candidates.map(candidate => String(candidate.shopifyOrderId))).size !== item.candidates.length))
    throw new Error('The invoice review list could not be verified. Refresh and try again.');
  if (value.status === 'reviewable' && (!value.reviewToken?.nativeHistoryRevision || !value.reviewToken?.shopifyHistoryFingerprint))
    throw new Error('The source review version is missing. Refresh and try again.');
  return value;
}
function AccountingReconciliationReview({ month, pack, onRecorded }) {
  const [review, setReview] = useState({ phase: 'loading', data: null, error: '' });
  const [reload, setReload] = useState(0), [selectedOrderId, setSelectedOrderId] = useState('');
  const [decision, setDecision] = useState(''), [mirrorId, setMirrorId] = useState('');
  const [reviewedIds, setReviewedIds] = useState([]), [evidenceKind, setEvidenceKind] = useState(''), [evidenceReference, setEvidenceReference] = useState('');
  const [confirmed, setConfirmed] = useState(false), [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(''), [saveNotice, setSaveNotice] = useState('');
  const [lookupId, setLookupId] = useState(''), [lookupBusy, setLookupBusy] = useState(false), [lookupError, setLookupError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setReview({ phase: 'loading', data: null, error: '' });
    rpApi(`/api/accounting/reconciliation?month=${encodeURIComponent(month)}`, { signal: controller.signal }).then(value => {
      if (controller.signal.aborted) return;
      accountingReviewPayload(value, month);
      setReview({ phase: 'ready', data: value, error: '' });
      setSelectedOrderId(current => value.items.some(item => item.nativeOrderId === current) ? current : value.items[0]?.nativeOrderId || '');
      setDecision(''); setMirrorId(''); setReviewedIds([]); setEvidenceKind(''); setEvidenceReference(''); setConfirmed(false);
      setLookupId(''); setLookupError('');
    }).catch(error => { if (!controller.signal.aborted) setReview({ phase: 'error', data: null, error: error.message || 'Could not load the source review.' }); });
    return () => controller.abort();
  }, [month, reload]);
  const selected = review.data?.items.find(item => item.nativeOrderId === selectedOrderId);
  const candidates = selected?.candidates || [];
  const boundExactOrderId = String(review.data?.reviewToken?.exactShopifyOrderId || '');
  const allCandidatesReviewed = candidates.every(candidate => reviewedIds.includes(String(candidate.shopifyOrderId)));
  const chosenMirror = candidates.find(candidate => String(candidate.shopifyOrderId) === mirrorId);
  const evidence = evidenceReference.trim();
  const evidenceValid = evidence.length >= 20 && evidence.length <= 500 && !/[\r\n\0]/.test(evidence)
    && !/^(?:same amount|same sku|looks same|probably|guess)\b/i.test(evidence);
  const recordBlockers = [];
  if (!decision) recordBlockers.push('Choose what the original source records prove.');
  if (!allCandidatesReviewed) recordBlockers.push(`Review all ${candidates.length} listed Shopify orders.`);
  if (decision === 'shopify_mirror' && !chosenMirror?.shopifySnapshotId) recordBlockers.push('Select the exact Shopify order this invoice copies.');
  if (!evidenceKind) recordBlockers.push('Choose the type of source record you checked.');
  if (evidence.length < 20) recordBlockers.push(`Add a traceable evidence reference and explain what it proves (at least 20 characters; ${evidence.length} entered).`);
  else if (!evidenceValid) recordBlockers.push('Use one line of evidence, 500 characters or fewer, that explains what the original record proves.');
  if (!confirmed) recordBlockers.push('Confirm that you checked the independent source evidence.');
  const canRecord = Boolean(review.data?.status === 'reviewable' && selected && decision && confirmed && allCandidatesReviewed && evidenceKind && evidenceValid && !saving && !lookupBusy
    && (decision !== 'shopify_mirror' || chosenMirror?.shopifySnapshotId));
  const chooseInvoice = nativeOrderId => {
    setSelectedOrderId(nativeOrderId); setDecision(''); setMirrorId(''); setReviewedIds([]);
    setEvidenceKind(''); setEvidenceReference(''); setConfirmed(false); setSaveError(''); setSaveNotice(''); setLookupId(''); setLookupError('');
  };
  const lookUpExactOrder = async () => {
    const exactId = lookupId.trim();
    if (!selected || !/^(?:gid:\/\/shopify\/Order\/)?[1-9][0-9]*$/.test(exactId) || lookupBusy || saving) {
      setLookupError('Enter an exact Shopify Order ID from its source record.'); return;
    }
    setLookupBusy(true); setLookupError(''); setSaveError('');
    try {
      const numericOrderId = exactId.replace(/^gid:\/\/shopify\/Order\//, '');
      const query = new URLSearchParams({ month, nativeOrderId: selected.nativeOrderId, shopifyOrderId: numericOrderId });
      const value = accountingReviewPayload(await rpApi(`/api/accounting/reconciliation?${query}`), month);
      setReview({ phase: 'ready', data: value, error: '' });
      const candidate = value.items.find(item => item.nativeOrderId === selected.nativeOrderId)?.candidates.find(item =>
        String(item.shopifyOrderId).replace(/^gid:\/\/shopify\/Order\//, '') === exactId.replace(/^gid:\/\/shopify\/Order\//, ''));
      setDecision(''); setMirrorId(''); setReviewedIds([]); setEvidenceKind(''); setEvidenceReference(''); setConfirmed(false);
      if (value.status !== 'reviewable') throw new Error('Sources changed. Refresh them before recording a decision.');
      if (!candidate) throw new Error('This exact Shopify order was not found as an eligible indexed sale. Check the order ID or refresh Shopify first.');
      setDecision('shopify_mirror'); setMirrorId(String(candidate.shopifyOrderId));
    } catch (error) { setLookupError(error.message || 'The exact Shopify order could not be verified.'); }
    finally { setLookupBusy(false); }
  };
  const recordReview = async () => {
    if (!canRecord) return;
    setSaving(true); setSaveError(''); setSaveNotice('');
    try {
      const payload = await rpApi('/api/accounting/reconciliation', { method: 'POST', body: JSON.stringify({
        month, nativeOrderId: selected.nativeOrderId,
        decision, evidenceKind, evidenceReference: evidence, reviewToken: review.data.reviewToken,
        reviewedShopifyOrderIds: candidates.map(candidate => String(candidate.shopifyOrderId)),
        ...(decision === 'shopify_mirror' ? { shopifyOrderId: chosenMirror.shopifyOrderId,
          shopifySnapshotId: chosenMirror.shopifySnapshotId } : {})
      }) });
      if (payload?.status !== 'review_recorded' || payload.month !== month || payload.workbookStatus !== 'awaiting_regeneration')
        throw new Error('The decision receipt could not be verified. Refresh before trying again.');
      setSaveNotice(`Decision recorded for ${selected.invoiceNumber}. All prepared workbooks and review packs must be rebuilt and checked before download. No filing total is ready from this decision alone.`);
      onRecorded(); setReload(value => value + 1);
    } catch (error) {
      setSaveError(error.status === 409 ? 'Source records or another review changed. Refresh the invoice list and check the evidence again.' : error.message || 'The review could not be recorded.');
      if (error.status === 409) setReload(value => value + 1);
    } finally { setSaving(false); }
  };
  return <section className="accounting-review" aria-label={`${month} invoice source reconciliation`}>
    <div className="accounting-actions" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <h3 style={{ margin: 0 }}>{month} · resolve held invoices</h3>
      <button className="btn btn-sm" disabled={saving || review.phase === 'loading'} onClick={() => setReload(value => value + 1)}>Refresh invoice list</button>
    </div>
    <p className="accounting-note">Check the original paid receipt, transaction and order history for each invoice. For early event sales, check Shopify card processing, Stripe receipts, and manual post-event merges against the event sale record. A matching amount, date, product, customer, payment record, channel, or open Shopify draft is only a lead. Leave an invoice unresolved if its source identity cannot be proved. Each recorded decision invalidates every prepared month, including August, until reports are rebuilt.</p>
    {(review.data || pack) && <p className="accounting-note">Ranch Power cutoff: {accountingCell({ type: 'datetime' }, review.data?.nativeCutoff || pack?.nativeCutoff)} · Shopify cutoff: {accountingCell({ type: 'datetime' }, review.data?.shopifyCutoff || pack?.shopifyCutoff)}{review.data?.periodTo ? ` · Through ${review.data.periodTo}` : ''}</p>}
    {review.data?.kind === 'mtd' && <p className="accounting-note"><strong>Provisional month to date:</strong> source decisions expire when a new Vancouver day or source revision changes the review period. Recheck open items after each refresh.</p>}
    {review.phase === 'loading' ? <p role="status">Checking invoice source links…</p> : review.error ? <p role="alert" className="accounting-alert">{review.error}</p>
      : review.data.status === 'ready' ? <p role="status">This period has no unresolved source overlaps. A workbook is available only when it appears in the prepared reports list.</p>
      : review.data.status === 'source_refresh_required' ? <p role="status">A current Shopify or Ranch Power source refresh is required before these invoices can be reviewed.</p>
      : review.data.status === 'source_repair_required' ? <div>
        <div role="status" className="accounting-alert"><strong>Original source records need repair.</strong> Correct the invoice or Shopify identity at its source, then refresh the index and review list. Source decisions are unavailable while these issues remain.
          {Array.isArray(review.data.otherBlockingReasons) && review.data.otherBlockingReasons.length > 0 && <ul>{review.data.otherBlockingReasons.map(reason => <li key={reason.code}>{Number(reason.count || 0)} issue{Number(reason.count || 0) === 1 ? '' : 's'}: {accountingSourceBlocker(reason.code)}</li>)}</ul>}</div>
        {review.data.items.length > 0 && <div role="region" aria-label="Invoices needing source repair"><h4>Affected invoices and Shopify source leads</h4>
          <div className="accounting-review-candidates">{review.data.items.map(item => <div className="accounting-review-candidate" key={item.nativeOrderId}>
            <strong>{item.invoiceNumber}</strong> · {item.nativeOrderId}<br />
            {item.invoiceDate || 'Date unavailable'} · {accountingCell({ type: 'currency' }, item.total)} {item.currency || 'CAD'}
            {item.shopifyDraftOrderId && <><br />Linked draft: {item.shopifyDraftOrderId}</>}
            <AccountingReviewNativeLinks item={item} />
            <br />{accountingReviewReason(item)}
            {item.candidates.length > 0 ? <ul>{item.candidates.map(candidate => <li key={`${candidate.shopifyOrderId}-${candidate.shopifySnapshotId}`}>
              Shopify Order {candidate.orderNumber || candidate.shopifyOrderId} · ID {candidate.shopifyOrderId}
              {candidate.sourceChannel && <> · Channel: {candidate.sourceChannel}</>}
              {candidate.sourceIdentifier && <> · Source ID: {candidate.sourceIdentifier}</>}: {ACCOUNTING_CANDIDATE_REASONS[candidate.reason] || candidate.reason || 'Source identity needs review.'}
              {candidate.eligibleForMirror === false && <> <strong>Not an eligible CAD sale.</strong></>}
              <br /><AccountingReviewShopifyOrderLink candidate={candidate} />
            </li>)}</ul> : <p className="accounting-note">No indexed Shopify order is listed for this invoice. Check the original source reference.</p>}
          </div>)}</div>
        </div>}
      </div>
      : review.data.status === 'awaiting_regeneration' ? <p role="status">Source decisions are recorded. Verified workbooks and source review packs are awaiting a fresh rebuild. Refresh the prepared report list for the current downloads.</p>
      : !review.data.items.length ? <p role="status">No invoices remain in this review list. Prepared reports are being checked and rebuilt; use Refresh list above to see when downloads are available.</p>
      : <>
        <p className="accounting-note"><strong>{review.data.items.length} invoice{review.data.items.length === 1 ? '' : 's'} need source decisions.</strong> No duplicate is assumed from a candidate match.</p>
        <div className="accounting-review-items" role="group" aria-label="Invoices awaiting source review">{review.data.items.map(item =>
          <button key={item.nativeOrderId} className={`btn btn-sm ${selectedOrderId === item.nativeOrderId ? 'btn-navy' : ''}`}
            aria-pressed={selectedOrderId === item.nativeOrderId} disabled={saving} onClick={() => chooseInvoice(item.nativeOrderId)}>{item.invoiceNumber}</button>)}</div>
        {selected && <div className="accounting-review-form">
          <h4 style={{ margin: '0 0 6px' }}>{selected.invoiceNumber} · {selected.nativeOrderId}</h4>
          <p className="accounting-note">Invoice date {selected.invoiceDate || 'unknown'} · Recorded total {accountingCell({ type: 'currency' }, selected.total)} {selected.currency || 'CAD'}{selected.shopifyDraftOrderId ? ` · Shopify draft ${selected.shopifyDraftOrderId}` : ''}</p>
          <AccountingReviewNativeLinks item={selected} />
          {accountingReviewProducts(selected.nativeProductLines) && <p className="accounting-note">Ranch Power products: {accountingReviewProducts(selected.nativeProductLines)}</p>}
          <p><strong>Why this invoice is held:</strong> {accountingReviewReason(selected)}</p>
          <h4>Possible Shopify orders ({candidates.length})</h4>
          {candidates.length ? <div className="accounting-review-candidates">{candidates.map(candidate => <div className="accounting-review-candidate" key={`${candidate.shopifyOrderId}-${candidate.shopifySnapshotId}`}>
            <strong>{candidate.orderNumber || candidate.shopifyOrderId}</strong> · {candidate.shopifyOrderId}<br />
            {candidate.date || 'Date unavailable'} · {accountingCell({ type: 'currency' }, candidate.total)} {candidate.currency || 'CAD'}
            {accountingReviewProducts(candidate.productLines) && <><br />Products: {accountingReviewProducts(candidate.productLines)}</>}
            {candidate.sourceChannel && <><br />Channel: {candidate.sourceChannel}</>}
            {candidate.sourceIdentifier && <><br />Source ID: {candidate.sourceIdentifier}</>}
            {candidate.reason && <><br />{ACCOUNTING_CANDIDATE_REASONS[candidate.reason] || candidate.reason}</>}
            <div style={{ marginTop: 10 }}><AccountingReviewShopifyOrderLink candidate={candidate} /></div>
            <br /><label><input type="checkbox" checked={reviewedIds.includes(String(candidate.shopifyOrderId))} disabled={saving}
              onChange={event => setReviewedIds(current => event.target.checked ? [...new Set([...current, String(candidate.shopifyOrderId)])] : current.filter(id => id !== String(candidate.shopifyOrderId)))} /> I checked this Shopify order against the source records</label>
            {decision === 'shopify_mirror' && <label><input type="radio" name={`accounting-mirror-${month}`} checked={mirrorId === String(candidate.shopifyOrderId)} disabled={saving || Boolean(boundExactOrderId && boundExactOrderId !== String(candidate.shopifyOrderId))}
              onChange={() => setMirrorId(String(candidate.shopifyOrderId))} /> This is the exact Shopify order for {selected.invoiceNumber}</label>}
          </div>)}</div> : <p className="accounting-note">No indexed Shopify order is suggested. A linked draft is not itself a sale or proof that the native invoice is separate.</p>}
          {candidates.length > 1 && <p className="accounting-note" role="status">{reviewedIds.filter(id => candidates.some(candidate => String(candidate.shopifyOrderId) === id)).length} of {candidates.length} Shopify orders marked reviewed.</p>}
          {candidates.length > 1 && <label className="accounting-review-confirm"><input type="checkbox" checked={allCandidatesReviewed} disabled={saving}
            onChange={event => setReviewedIds(event.target.checked ? candidates.map(candidate => String(candidate.shopifyOrderId)) : [])} />
            I reviewed all {candidates.length} listed Shopify orders against their original source records.</label>}
          {boundExactOrderId && <p className="accounting-note">Exact lookup is bound to Shopify Order ID {boundExactOrderId}. Refresh the invoice list to choose another order.</p>}
          <label htmlFor={`accounting-exact-order-${month}`}>Have an exact Shopify Order ID that is not listed?</label>
          <div className="accounting-actions" style={{ alignItems: 'end' }}><input id={`accounting-exact-order-${month}`} className="field" style={{ maxWidth: 320 }} value={lookupId}
            disabled={saving || lookupBusy} onChange={event => setLookupId(event.target.value)} placeholder="Shopify Order ID" />
            <button className="btn btn-sm" disabled={saving || lookupBusy || !lookupId.trim()} onClick={lookUpExactOrder}>{lookupBusy ? 'Checking…' : 'Look up exact order'}</button></div>
          {lookupError && <p role="alert" className="accounting-alert">{lookupError}</p>}
          <label htmlFor={`accounting-decision-${month}`}>What do the original source records prove?</label>
          <select id={`accounting-decision-${month}`} className="field" value={decision} disabled={saving} onChange={event => { setDecision(event.target.value); setMirrorId(''); }}>
            <option value="">Leave unresolved</option><option value="native_only">A separate Ranch Power sale</option><option value="shopify_mirror" disabled={!candidates.length}>A copy of an exact Shopify sale</option>
          </select>
          <label htmlFor={`accounting-evidence-kind-${month}`}>Source record type</label>
          <select id={`accounting-evidence-kind-${month}`} className="field" value={evidenceKind} disabled={saving} onChange={event => setEvidenceKind(event.target.value)}>
            <option value="">Choose the record checked</option><option value="receipt">Original paid receipt</option><option value="payment">Payment with an independent sale reference</option><option value="order_history">Order history</option><option value="other">Other independent source record</option>
          </select>
          <label htmlFor={`accounting-evidence-${month}`}>Independent source evidence reference</label>
          <textarea id={`accounting-evidence-${month}`} className="field" rows="3" maxLength="500" value={evidenceReference} disabled={saving}
            onChange={event => setEvidenceReference(event.target.value)} placeholder="Identify the original paid receipt, transaction, or order-history record and explain how it proves this classification." />
          <p className="accounting-note">20–500 characters. Include a traceable reference and what it proves; no customer personal details. An amount/date match, SKU, open draft, or payment entry alone cannot establish a duplicate or a separate sale.</p>
          <label className="accounting-review-confirm"><input type="checkbox" checked={confirmed} disabled={saving} onChange={event => setConfirmed(event.target.checked)} /> I checked the independent source evidence, and this classification is supported by it.</label>
          {!canRecord && !saving && !lookupBusy && (decision || evidence || evidenceKind || confirmed) && <div className="accounting-alert" role="status" aria-live="polite">
            <strong>To record this decision:</strong><ul>{recordBlockers.map(blocker => <li key={blocker}>{blocker}</li>)}</ul>
          </div>}
          <div className="accounting-actions"><button className="btn btn-primary btn-sm" disabled={!canRecord} onClick={recordReview}>{saving ? 'Recording…' : 'Record source decision'}</button></div>
        </div>}
      </>}
    {saveError && <p role="alert" className="accounting-alert">{saveError}</p>}
    {saveNotice && <p role="status" className="accounting-alert">{saveNotice}</p>}
  </section>;
}

function AccountingView({ go }) {
  const [catalog, setCatalog] = useState(null), [catalogError, setCatalogError] = useState(''), [retry, setRetry] = useState(0);
  const [prepared, setPrepared] = useState({ phase: 'loading', reports: [], reviewPacks: [], currentMonthReviewPack: null, currentMonth: null, completeMonthStatuses: [], completeMonthHistoryStatus: 'unavailable', error: '' }), [preparedRetry, setPreparedRetry] = useState(0);
  const [downloadingMonth, setDownloadingMonth] = useState(''), [preparedNotice, setPreparedNotice] = useState('');
  const [reviewMonth, setReviewMonth] = useState('');
  const [filters, setFilters] = useState(accountingPreviousMonth), [section, setSection] = useState('overview'), [selected, setSelected] = useState('');
  const [includeShopify, setIncludeShopify] = useState(true), [preview, setPreview] = useState(null), [previewBusy, setPreviewBusy] = useState(false);
  const [status, setStatus] = useState({ phase: 'idle', message: '', error: '' }), [evidence, setEvidence] = useState([]), [monthlySummary, setMonthlySummary] = useState(null);
  const exportController = React.useRef(null), previewController = React.useRef(null);
  useEffect(() => { const controller = new AbortController(); setCatalogError('');
    rpApi('/api/accounting/catalog', { signal: controller.signal }).then(value => { if (controller.signal.aborted) return; if (!Array.isArray(value.reports) || !value.reports.length) throw new Error('The accounting catalog is unavailable.'); value.reports.forEach(accountingColumns); value = { ...value, reports: value.reports.map(report => ({ ...report, label: report.label || report.title || report.id })) }; setCatalog(value); setSelected(value.reports.find(report => report.id === 'income')?.id || value.reports[0].id); }).catch(error => { if (!controller.signal.aborted) setCatalogError(error.message || 'Could not load accounting reports.'); });
    return () => controller.abort();
  }, [retry]);
  useEffect(() => { const controller = new AbortController(); setPrepared(current => ({ ...current, phase: 'loading', error: '' }));
    rpApi('/api/accounting/prepared', { signal: controller.signal }).then(value => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(value?.reports)) throw new Error('The prepared report list could not be verified.');
      setPrepared({ phase: 'ready', reports: value.reports, reviewPacks: Array.isArray(value.reviewPacks) ? value.reviewPacks : [],
        currentMonthReviewPack: value.currentMonthReviewPack || null, currentMonth: value.currentMonth || null,
        completeMonthStatuses: Array.isArray(value.completeMonthStatuses) ? value.completeMonthStatuses : [],
        completeMonthHistoryStatus: value.completeMonthHistoryStatus === 'verified' ? 'verified' : 'unavailable',
        error: '', sourceStatus: value.sourceStatus || '' });
    }).catch(error => { if (!controller.signal.aborted) setPrepared(current => ({ ...current, phase: 'error', error: error.message || 'Prepared reports are unavailable.' })); });
    return () => controller.abort();
  }, [preparedRetry]);
  useEffect(() => () => { exportController.current?.abort(); previewController.current?.abort(); }, []);
  const reports = catalog?.reports || [], active = reports.find(report => report.id === selected);
  const canReviewSources = ['accountant', 'admin'].includes(deskActiveRole());
  const visible = section === 'overview' ? reports : reports.filter(report => accountingSection(report) === section);
  const running = ['loading', 'building'].includes(status.phase);
  const downloadPrepared = async report => {
    if (downloadingMonth) return;
    const { month, kind, periodTo } = report;
    setDownloadingMonth(month); setPreparedNotice('');
    try {
      const response = await rpFetchProtected(`/api/accounting/prepared/download?month=${encodeURIComponent(month)}${kind === 'mtd' ? '&kind=mtd' : ''}`, { headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } });
      const blob = await response.blob();
      if (!blob.size || !String(response.headers.get('Content-Type') || '').includes('spreadsheetml.sheet')) throw new Error('The prepared workbook could not be verified. Please retry.');
      downloadReportBlob(blob, kind === 'mtd' ? `ranch-power-month-to-date-${month}-through-${periodTo}.xlsx` : `ranch-power-monthly-summary-${month}.xlsx`);
      setPreparedNotice(`${month}${kind === 'mtd' ? ` through ${periodTo} (provisional)` : ''} report downloaded.`);
    } catch (error) {
      setPreparedNotice(error.message || 'The prepared report could not be downloaded.');
      setPreparedRetry(value => value + 1);
    } finally { setDownloadingMonth(''); }
  };
  const downloadSourceReview = async pack => {
    if (downloadingMonth) return;
    setDownloadingMonth(`review-${pack.month}`); setPreparedNotice('');
    try {
      const response = await rpFetchProtected(`/api/accounting/source-review/download?month=${encodeURIComponent(pack.month)}${pack.kind === 'mtd' ? '&kind=mtd' : ''}`, { headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } });
      const blob = await response.blob();
      if (!blob.size || !String(response.headers.get('Content-Type') || '').includes('spreadsheetml.sheet')) throw new Error('The source review pack could not be verified. Please retry.');
      downloadReportBlob(blob, `ranch-power-${pack.availableBasis ? 'AVAILABLE-BASIS' : 'SOURCE-REVIEW-NOT-FOR-FILING'}-${pack.month}${pack.kind === 'mtd' ? `-through-${pack.periodTo}` : ''}.xlsx`);
      setPreparedNotice(`${pack.month}${pack.kind === 'mtd' ? ` through ${pack.periodTo} (provisional)` : ''} ${pack.availableBasis ? 'accountant worksheet' : 'source review pack'} downloaded.`);
    } catch (error) {
      setPreparedNotice(error.message || 'The source review pack could not be downloaded.');
      setPreparedRetry(value => value + 1);
    } finally { setDownloadingMonth(''); }
  };
  const chooseReport = id => { previewController.current?.abort(); setPreviewBusy(false); setSelected(id); setPreview(null); };
  const chooseSection = value => { setSection(value); const first = value === 'overview' ? reports.find(report => report.id === 'income') || reports[0] : reports.find(report => accountingSection(report) === value); chooseReport(first?.id || ''); };
  const changeDate = (key, value) => { previewController.current?.abort(); setPreviewBusy(false); setFilters(current => ({ ...current, [key]: value, ...(key === 'dateTo' ? { asOf: value } : {}) })); setPreview(null); setEvidence([]); setMonthlySummary(null); setStatus({ phase: 'idle', message: '', error: '' }); };
  const chooseMonth = value => { previewController.current?.abort(); setPreviewBusy(false); try { setFilters(accountingMonthFilters(value)); setPreview(null); setEvidence([]); setMonthlySummary(null); setStatus({ phase: 'idle', message: '', error: '' }); } catch (error) { setStatus({ phase: 'error', message: '', error: error.message }); } };
  const loadPreview = async (offset = 0) => {
    if (!active || previewBusy) return;
    previewController.current?.abort(); const controller = new AbortController(); previewController.current = controller; setPreviewBusy(true);
    try {
      accountingValidateDates(filters);
      let page;
      if (active.endpoint === 'shopify') {
        const mode = active.mode || (active.dataset === 'fees' ? 'balances' : active.dataset === 'payouts' ? 'payouts' : 'orders');
        const data = await rpApi('/api/accounting/shopify', { method: 'POST', signal: controller.signal, body: JSON.stringify({ mode, from: filters.dateFrom, to: filters.dateTo, snapshotAt: new Date().toISOString() }) });
        if (data.source !== 'shopify' || data.mode !== mode || !Array.isArray(data.rows?.[active.dataset])) throw new Error('Shopify did not return a verified report preview.');
        page = { ...data, rows: data.rows?.[active.dataset] || [], total: null, nextOffset: null, sample: Boolean(data.nextCursor), offset: 0 };
      } else page = await rpApi('/api/accounting/report', { method: 'POST', signal: controller.signal, body: JSON.stringify({ reportId: active.id, filters, limit: 50, offset, ...(offset && preview?.snapshotAt ? { snapshotAt: preview.snapshotAt, datasetVersion: preview.datasetVersion } : {}) }) });
      if (!Array.isArray(page.rows) || active.endpoint !== 'shopify' && (page.reportId !== active.id || !Number.isSafeInteger(page.total) || !page.datasetVersion || !page.snapshotAt)) throw new Error('The report preview could not be verified.');
      accountingCheckCancelled(controller.signal); setPreview({ ...page, offset, reportId: active.id });
    } catch (error) { if (!controller.signal.aborted) setPreview({ reportId: active.id, error: error.message || 'Could not load rows.' }); }
    finally { if (!controller.signal.aborted) setPreviewBusy(false); }
  };
  const exportData = async format => {
    if (running || !catalog || !active) return;
    const controller = new AbortController(); exportController.current = controller; setEvidence([]); setMonthlySummary(null);
    setStatus({ phase: 'loading', message: 'Starting a complete source read…', error: '' });
    try {
      accountingValidateDates(filters);
      const summaryOnly = format === 'summary';
      const summaryNative = new Set(['income', 'charges']);
      const summaryShopify = new Set(['sales', 'lines', 'taxes', 'refunds']);
      const chosen = format === 'csv' ? [active] : summaryOnly
        ? reports.filter(report => report.endpoint === 'shopify' ? includeShopify && summaryShopify.has(report.dataset) : summaryNative.has(report.id))
        : reports.filter(report => includeShopify || report.endpoint !== 'shopify');
      if (!chosen.length) throw new Error('No reports are available for this export.');
      if (summaryOnly && (!['income', 'charges'].every(id => chosen.some(report => report.id === id))
        || includeShopify && !['sales', 'lines', 'taxes', 'refunds'].every(dataset => chosen.some(report => report.endpoint === 'shopify' && report.dataset === dataset)))) throw new Error('Monthly summary source definitions are unavailable. Retry the catalog.');
      if (format === 'xlsx' && includeShopify && !['sales', 'lines', 'taxes', 'refunds', 'payments', 'payouts', 'fees'].every(dataset => chosen.some(report => report.endpoint === 'shopify' && report.dataset === dataset))) throw new Error('Shopify report definitions are unavailable. Choose a clearly marked Ranch Power only export or retry after the source is ready.');
      const maxRows = Math.min(100000, Number(catalog.limits?.maxExportRows) || 50000), sheets = []; let remaining = maxRows, snapshotAt = '';
      const onProgress = progress => setStatus({ phase: 'loading', message: `${progress.label}: ${progress.loaded.toLocaleString()}${progress.total == null ? '' : ` of ${progress.total.toLocaleString()}`} rows read`, error: '' });
      for (const report of chosen.filter(report => report.endpoint !== 'shopify')) {
        const sheet = await accountingFetchNative({ report, filters, snapshotAt, cap: remaining, pageSize: Math.min(500, Number(catalog.limits?.pageSize) || 500), signal: controller.signal, onProgress });
        sheets.push(sheet); remaining -= sheet.total; snapshotAt = sheet.snapshotAt;
      }
      const shopify = chosen.filter(report => report.endpoint === 'shopify');
      if (shopify.length) sheets.push(...await accountingFetchShopify({ reports: shopify, filters, snapshotAt: snapshotAt || new Date().toISOString(), cap: remaining, signal: controller.signal, onProgress }));
      accountingCheckCancelled(controller.signal);
      const scope = format === 'csv' ? `${active.label} only` : includeShopify ? 'Ranch Power and Shopify' : 'Ranch Power only';
      const evidenceRows = accountingEvidenceRows(sheets, filters, catalog, scope);
      const summary = format === 'csv' ? null : buildAccountingMonthlySummary({ sheets, filters, includeShopify });
      const shopifySheet = sheets.find(sheet => sheet.report.endpoint === 'shopify');
      setEvidence(evidenceRows); setStatus({ phase: 'building', message: 'All requested rows received. Building your file…', error: '' });
      const fileStem = `${summaryOnly ? 'ranch-power-monthly-summary' : 'ranch-power-accounting'}-${filters.dateFrom}-${filters.dateTo}${format !== 'csv' && !includeShopify ? '-native-only' : ''}`;
      const csvEvidence = { exportDateFrom: filters.dateFrom, exportDateTo: filters.dateTo, exportAsOf: filters.asOf, exportSnapshotAt: sheets[0].snapshotAt, exportTimezone: sheets[0].metadata.timezone || active.timezone || catalog.timezone, exportDateBasis: sheets[0].metadata.dateBasis || active.dateBasis, exportScope: scope, exportWarnings: evidenceRows.map(row => row.warnings).filter(Boolean).join(' | ') };
      const file = format === 'csv' ? buildReportCsv({ title: active.label, fileName: `${fileStem}-${active.id}.csv`, columns: [...accountingColumns(active), ...Object.keys(csvEvidence).map(key => ({ key, label: key, type: 'string' }))], rows: sheets[0].rows.map(row => ({ ...row, ...csvEvidence })) })
        : await buildReportWorkbook({ title: `${summaryOnly ? 'Monthly sales and tax summary' : 'Accounting'} · ${scope}`, fileName: `${fileStem}.xlsx`, generatedAt: new Date().toISOString(), requestedBy: rpAuthProfile().display_name || 'Accounting', timezone: catalog.timezone || 'America/Vancouver', filters: { ...filters, Scope: scope, 'Ranch Power cutoff': snapshotAt, ...(shopifySheet ? { 'Shopify source cutoff': shopifySheet.snapshotAt, 'Shopify read': shopifySheet.metadata.indexStatus === 'available' ? 'Prepared local snapshot' : 'Live Shopify source scan' } : {}), 'Gross basis': 'Native: invoice total less tax, deposits and carried prior debt. Shopify: order total less provider tax, tips, and recorded tax/deposit/account/gift-card lines. Sale fees and shipping remain in gross.', 'PST basis': 'All explicitly recorded PST charged, including non-BC regions until reconciled.', 'Summary status': 'Recorded sales and tax charged for review; not filing-ready. Refunds, exemptions and exceptions require reconciliation.', 'Currency treatment': 'Only same-currency sources can be combined; no conversion.' }, sheets: [
          { name: 'Monthly summary', columns: ACCOUNTING_MONTHLY_COLUMNS, rows: summary.totals.map(row => ({ ...row, period: summary.periodLabel })) },
          { name: 'Review exceptions', columns: ACCOUNTING_EXCEPTION_COLUMNS, rows: summary.exceptions.map(item => ({ ...item, examples: item.examples.join(', ') })) },
          { name: 'Source coverage', columns: ACCOUNTING_EVIDENCE_COLUMNS, rows: evidenceRows },
          ...sheets.map(sheet => ({ name: sheet.report.label, columns: accountingColumns(sheet.report), rows: sheet.rows, total: sheet.total }))
        ] });
      accountingCheckCancelled(controller.signal); downloadReportBlob(file.blob, file.fileName);
      if (summary) setMonthlySummary(summary);
      setStatus({ phase: 'complete', message: `${sheets.reduce((sum, sheet) => sum + sheet.total, 0).toLocaleString()} rows downloaded · ${scope}.`, error: '' });
    } catch (error) { const cancelled = controller.signal.aborted || error.name === 'AbortError'; setEvidence([]); setMonthlySummary(null); setStatus({ phase: cancelled ? 'cancelled' : 'error', message: cancelled ? 'Cancelled. No file was downloaded.' : '', error: cancelled ? '' : error.message || 'Export failed. No partial file was downloaded.' }); }
    finally { exportController.current = null; }
  };
  return <div className="rp-accounting rp-fade">
    <style>{`.rp-accounting{padding:24px 28px 40px;max-width:1500px;margin:auto;width:100%;box-sizing:border-box}.rp-accounting *{box-sizing:border-box}.rp-accounting h1{margin:5px 0 8px}.rp-accounting p{line-height:1.5}.rp-accounting .accounting-actions,.rp-accounting .accounting-tabs,.rp-accounting .accounting-review-items{display:flex;flex-wrap:wrap;gap:8px}.rp-accounting .accounting-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:14px}.rp-accounting .accounting-panel{padding:18px;margin-top:18px;min-width:0}.rp-accounting label{display:block;font-size:12px;font-weight:650}.rp-accounting input,.rp-accounting select{width:100%;margin-top:6px;min-width:0}.rp-accounting .accounting-note{font-size:12px;color:var(--muted);overflow-wrap:anywhere}.rp-accounting .accounting-table{overflow:auto;max-width:100%;margin-top:16px}.rp-accounting td{max-width:320px;overflow-wrap:anywhere}.rp-accounting .accounting-alert{padding:12px;border-radius:8px;margin-top:12px;overflow-wrap:anywhere;background:var(--amber-soft);color:var(--amber-deep)}.rp-accounting .accounting-review{border-top:1px solid var(--line-2);margin-top:18px;padding-top:18px}.rp-accounting .accounting-review-form{border:1px solid var(--line-2);border-radius:9px;margin-top:14px;padding:16px}.rp-accounting .accounting-review-candidates{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:10px}.rp-accounting .accounting-review-candidate{border:1px solid var(--line-2);border-radius:8px;padding:12px;font-size:13px;overflow-wrap:anywhere}.rp-accounting .accounting-review-candidate label,.rp-accounting .accounting-review-confirm{display:flex;align-items:flex-start;gap:8px;margin-top:10px;line-height:1.4}.rp-accounting .accounting-review-candidate input,.rp-accounting .accounting-review-confirm input{width:auto;margin:2px 0 0;flex:none}.rp-accounting .accounting-review-form>label:not(.accounting-review-confirm){margin-top:14px}.rp-accounting .accounting-review-form textarea{width:100%;resize:vertical}@media(max-width:600px){.rp-accounting{padding:18px 14px 28px}.rp-accounting .accounting-panel{padding:14px}.rp-accounting .accounting-grid{grid-template-columns:1fr}.rp-accounting .btn{white-space:normal;text-align:left;min-height:40px}}`}</style>
    <div className="eyebrow">Accounting · Operations</div><h1 className="disp">Accounting</h1>
    <p style={{ color: 'var(--muted)', marginTop: 0 }}>Review income, expenses, receivables and stock. Prepare complete source extracts for your month-end reconciliation.</p>
    <div className="accounting-actions">{[['invoicing', 'Invoices'], ['expenses', 'Review expenses'], ['reports', 'Other reports'], ['procurement', 'Procurement']].filter(([id]) => deskCanUseNav(id, deskActiveRole())).map(([id, label]) => <button className="btn btn-sm" key={id} onClick={() => go(id)}>{label}</button>)}</div>
    <div className="accounting-tabs" role="group" aria-label="Accounting sections" style={{ marginTop: 22 }}>{ACCOUNTING_SECTIONS.map(([id, label]) => <button key={id} className={`btn btn-sm ${section === id ? 'btn-navy' : ''}`} aria-pressed={section === id} disabled={running} onClick={() => chooseSection(id)}>{label}</button>)}</div>
    <section className="card accounting-panel" aria-label="Prepared monthly reports">
      <div className="accounting-actions" style={{ alignItems: 'center', justifyContent: 'space-between' }}><h2 style={{ margin: 0, fontSize: 20 }}>Prepared monthly reports</h2><button className="btn btn-sm" disabled={prepared.phase === 'loading'} onClick={() => setPreparedRetry(value => value + 1)}>Refresh list</button></div>
      <p className="accounting-note">Use complete-month workbooks for filing review. The current month, when available, is marked provisional and covers only completed Vancouver days. Each workbook has gross revenue, PST and GST charged, source rows and review exceptions. Shopify figures use a dated local snapshot; downloads wait until source refresh and cross-source reconciliation finish.</p>
      {prepared.phase === 'loading' ? <p role="status">Checking prepared months…</p> : prepared.error ? <div role="alert" className="accounting-alert">{prepared.error} <button className="btn btn-sm" onClick={() => setPreparedRetry(value => value + 1)}>Retry</button></div> : prepared.reports.length ? <div className="accounting-table" tabIndex={0}><table className="rp-table"><thead><tr><th>Month</th><th>Gross sales before tax · CAD</th><th>PST charged · CAD</th><th>GST charged · CAD</th><th>Review</th><th>Prepared</th><th>Workbook</th></tr></thead><tbody>{prepared.reports.map(report => {
        const combined = report.summaryTotals?.find(row => row.source === 'Combined' && row.currency === 'CAD');
        return <tr key={report.month}><td><strong>{new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(new Date(`${report.month}-01T12:00:00Z`))}</strong>{report.provisional && <p className="accounting-note"><strong>Provisional month to date</strong><br />Through {report.periodTo} · last completed Vancouver day</p>}<details><summary style={{ cursor: 'pointer', fontSize: 12 }}>View source totals</summary>{(report.summaryTotals || []).map(row => <p className="accounting-note" key={`${row.source}-${row.currency}`}><strong>{row.source} · {row.currency}</strong><br />Gross {accountingCell({ type: 'currency' }, row.grossRevenue)} · PST {accountingCell({ type: 'currency' }, row.pstCharged)} · GST {accountingCell({ type: 'currency' }, row.gstCharged)}</p>)}</details><details><summary style={{ cursor: 'pointer', fontSize: 12 }}>Source cutoffs</summary><p className="accounting-note">Ranch Power: {accountingCell({ type: 'datetime' }, report.nativeCutoff)}<br />Shopify: {accountingCell({ type: 'datetime' }, report.shopifyCutoff)}</p></details></td>
          <td>{combined ? accountingCell({ type: 'currency' }, combined.grossRevenue) : 'Review sources'}</td><td>{combined ? accountingCell({ type: 'currency' }, combined.pstCharged) : 'Review sources'}</td><td>{combined ? accountingCell({ type: 'currency' }, combined.gstCharged) : 'Review sources'}</td>
          <td>{Number(report.exceptionCount || 0).toLocaleString()} exception{Number(report.exceptionCount || 0) === 1 ? '' : 's'}</td><td>{accountingCell({ type: 'datetime' }, report.preparedAt)}</td><td><button className="btn btn-primary btn-sm" disabled={Boolean(downloadingMonth)} onClick={() => downloadPrepared(report)}>{downloadingMonth === report.month ? 'Downloading…' : `Download ${report.month}${report.provisional ? ' to date' : ''} Excel`}</button></td></tr>;
      })}</tbody></table></div> : <p role="status">No verified monthly workbooks are ready yet. See the complete month status below for what is pending.</p>}
      {prepared.phase === 'ready' && prepared.reports.length > 0 && <p className="accounting-note">PST shown is charged PST across recorded regions. Check the workbook’s Review exceptions sheet for BC allocation and missing tax jurisdiction before filing.</p>}
      {prepared.phase === 'ready' && [...prepared.reviewPacks, prepared.currentMonthReviewPack].some(pack => pack?.availableBasis) && <div className="accounting-table" role="region" tabIndex={0} aria-label="Available monthly figures with deferred review">
        <h3 style={{ marginBottom: 6 }}>Available figures · unresolved invoices deferred</h3>
        <p className="accounting-note" style={{ marginTop: 0 }}>These accountant worksheets count eligible Shopify sales once and leave unresolved Ranch Power invoices and their recorded tax out of the current month. Each workbook lists the excluded invoices for next-filing review. The accountant must confirm the filing treatment, refunds and BC PST allocation.</p>
        <table className="rp-table"><thead><tr><th>Month</th><th>Gross revenue before tax · CAD</th><th>PST charged · CAD</th><th>GST charged · CAD</th><th>Deferred invoices</th><th>Workbook</th></tr></thead><tbody>{[...prepared.reviewPacks, prepared.currentMonthReviewPack].filter(pack => pack?.availableBasis).sort((a, b) => b.month.localeCompare(a.month)).map(pack => {
          const combined = pack.summaryTotals?.find(row => row.source === 'Combined' && row.currency === 'CAD');
          return <tr key={`${pack.kind}-${pack.month}`}><td><strong>{pack.month}</strong>{pack.provisional && <p className="accounting-note">Provisional through {pack.periodTo}</p>}</td>
            <td>{combined ? accountingCell({ type: 'currency' }, combined.grossRevenue) : 'Unavailable'}</td>
            <td>{combined ? accountingCell({ type: 'currency' }, combined.pstCharged) : 'Unavailable'}</td>
            <td>{combined ? accountingCell({ type: 'currency' }, combined.gstCharged) : 'Unavailable'}</td>
            <td>{pack.deferredCount} · see next-filing register in workbook</td><td><button className="btn btn-primary btn-sm" disabled={Boolean(downloadingMonth)} onClick={() => downloadSourceReview(pack)}>{downloadingMonth === `review-${pack.month}` ? 'Downloading…' : `Download ${pack.month} accountant worksheet`}</button></td></tr>;
        })}</tbody></table></div>}
      {prepared.phase === 'ready' && prepared.completeMonthStatuses.length > 0 && <div className="accounting-table" role="region" tabIndex={0} aria-label="Complete month status"><h3 style={{ marginBottom: 6 }}>Complete month status</h3><p className="accounting-note" style={{ marginTop: 0 }}>This list covers every complete month since sales history began. Verified workbooks appear above. Held months may have an accountant worksheet or source review pack above.</p><table className="rp-table"><thead><tr><th>Complete month</th><th>Status</th><th>Why a workbook may be unavailable</th><th>Review evidence</th></tr></thead><tbody>{prepared.completeMonthStatuses.map(item => { const pack = item.status === 'awaiting_review' ? prepared.reviewPacks.find(candidate => candidate.month === item.month) : null; return <tr key={item.month}><td><strong>{new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(new Date(`${item.month}-01T12:00:00Z`))}</strong></td><td>{({ ready: 'Ready', refreshing: 'Refreshing Shopify', refresh_needed: 'Source refresh needed', awaiting_review: pack?.availableBasis ? 'Accountant worksheet ready' : 'Awaiting review or preparation' })[item.status] || 'Unavailable'}</td><td>{item.reason}</td><td>{pack && <><strong>{pack.warning}</strong><br />{!pack.availableBasis && <button className="btn btn-sm" disabled={Boolean(downloadingMonth)} onClick={() => downloadSourceReview(pack)}>{downloadingMonth === `review-${item.month}` ? 'Downloading…' : `Download ${item.month} source review Excel`}</button>}<p className="accounting-note">Native cutoff: {accountingCell({ type: 'datetime' }, pack.nativeCutoff)}<br />Shopify cutoff: {accountingCell({ type: 'datetime' }, pack.shopifyCutoff)}<br />{(pack.holdReasons || []).map(reason => `${reason.reviewFlags} review flag${reason.reviewFlags === 1 ? '' : 's'}: ${reason.explanation || reason.code}`).join(' · ')}</p></>}{item.status === 'awaiting_review' && canReviewSources && <p><button className="btn btn-sm" aria-pressed={reviewMonth === item.month} onClick={() => setReviewMonth(current => current === item.month ? '' : item.month)}>Review {item.month} held invoices</button></p>}</td></tr>; })}</tbody></table></div>}
      {prepared.phase === 'ready' && prepared.completeMonthHistoryStatus !== 'verified' && <p role="status" className="accounting-note">The complete sales month history could not be verified right now. Ready workbooks above remain available; refresh this list to check held months.</p>}
      {prepared.phase === 'ready' && prepared.currentMonth?.status === 'awaiting_verified_refresh' &&
        <div className="accounting-note"><p>{prepared.currentMonth.month} to date through {prepared.currentMonth.to} is awaiting a verified source refresh and cross-source review. Refresh the list after preparation finishes.</p>
          {prepared.currentMonthReviewPack?.periodTo === prepared.currentMonth.to && <p><strong>{prepared.currentMonthReviewPack.warning} · PROVISIONAL through {prepared.currentMonth.to}</strong><br />{!prepared.currentMonthReviewPack.availableBasis && <button className="btn btn-sm" disabled={Boolean(downloadingMonth)} onClick={() => downloadSourceReview(prepared.currentMonthReviewPack)}>{downloadingMonth === `review-${prepared.currentMonth.month}` ? 'Downloading…' : `Download ${prepared.currentMonth.month} to-date source review Excel`}</button>}<br />Native cutoff: {accountingCell({ type: 'datetime' }, prepared.currentMonthReviewPack.nativeCutoff)} · Shopify cutoff: {accountingCell({ type: 'datetime' }, prepared.currentMonthReviewPack.shopifyCutoff)}<br />{(prepared.currentMonthReviewPack.holdReasons || []).map(reason => `${reason.reviewFlags} review flag${reason.reviewFlags === 1 ? '' : 's'}: ${reason.explanation || reason.code}`).join(' · ')}</p>}
          {canReviewSources && <p><button className="btn btn-sm" aria-pressed={reviewMonth === prepared.currentMonth.month} onClick={() => setReviewMonth(current => current === prepared.currentMonth.month ? '' : prepared.currentMonth.month)}>Review {prepared.currentMonth.month} held invoices</button></p>}</div>}
      {reviewMonth && canReviewSources && <AccountingReconciliationReview key={reviewMonth} month={reviewMonth}
        pack={prepared.reviewPacks.find(pack => pack.month === reviewMonth) || (prepared.currentMonthReviewPack?.month === reviewMonth ? prepared.currentMonthReviewPack : null)}
        onRecorded={() => { setPrepared(current => ({ ...current, phase: 'loading', reports: [], reviewPacks: [], currentMonthReviewPack: null })); setPreparedNotice('Source decision recorded. All prepared months are pending a fresh source check and rebuild; use Refresh list to see verified downloads.'); setPreparedRetry(value => value + 1); }} />}
      {preparedNotice && <p role={/downloaded\.$/.test(preparedNotice) ? 'status' : 'alert'} className="accounting-note">{preparedNotice}</p>}
      {prepared.reports.length > 0 && <p className="accounting-note">CAD combined figures appear only where the workbook can reconcile both sources. Open the Excel workbook for source breakdown, other currencies, refunds and exceptions. Amounts shown are recorded charges for review, not a filed return.</p>}
    </section>
    {catalogError ? <div role="alert" className="accounting-alert">{catalogError} <button className="btn btn-sm" onClick={() => setRetry(value => value + 1)}>Retry catalog</button></div> : !catalog ? <p role="status">Loading accounting sources…</p> : <>
      <section className="card accounting-panel" aria-label="Period and export">
        <div className="accounting-grid"><label>Monthly summary month<input className="field" type="month" value={filters.dateFrom.slice(0, 7)} max={(typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10)).slice(0, 7)} disabled={running} onChange={event => chooseMonth(event.target.value)} /></label>{[['dateFrom', 'Start date'], ['dateTo', 'End date']].map(([key, label]) => <label key={key}>{label}<input className="field" type="date" value={filters[key]} disabled={running} onChange={event => changeDate(key, event.target.value)} /></label>)}<label>As-of date<input className="field" type="date" value={filters.dateTo} readOnly aria-describedby="accounting-asof-basis" /></label></div>
        <p id="accounting-asof-basis" className="accounting-note">As-of balances use the selected period end.</p>
        <p className="accounting-note">Default: last complete calendar month. Dates use {catalog.timezone || 'America/Vancouver'}. Each sheet records its own date basis and data cutoff. Inventory is read-only; its report states whether it represents current stock or an as-of balance.</p>
        <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', margin: '15px 0' }}><input style={{ width: 'auto', marginTop: 2 }} type="checkbox" checked={includeShopify} disabled={running} onChange={event => { setIncludeShopify(event.target.checked); setMonthlySummary(null); setEvidence([]); }} /><span>Include Shopify source sales, refunds, payments, payouts and fees in the workbook</span></label>
        {!includeShopify && <div className="accounting-alert">Ranch Power only export: Shopify source records, payouts and fees are excluded. The filename and source sheet will identify this limitation.</div>}
        {accountingWarnings(catalog.warnings).length > 0 && <details style={{ marginTop: 14 }}><summary style={{ cursor: 'pointer', fontWeight: 650, fontSize: 13 }}>How these reports are prepared</summary>{accountingWarnings(catalog.warnings).map((warning, index) => <p key={index} className="accounting-note">{warning}</p>)}</details>}
        <div className="accounting-actions" style={{ marginTop: 16 }}><button className="btn" disabled={running || !active} onClick={() => exportData('summary')}>Build live monthly summary Excel</button><button className="btn" disabled={running || !active} onClick={() => exportData('xlsx')}>Export detailed live month-end Excel</button><button className="btn" disabled={running || !active} onClick={() => exportData('csv')}>Export selected CSV</button>{running && <button className="btn" onClick={() => exportController.current?.abort()}>Cancel export</button>}</div>
        <div role={status.error ? 'alert' : 'status'} aria-live="polite" style={{ marginTop: 12, color: status.error ? 'var(--danger)' : 'var(--muted)', overflowWrap: 'anywhere' }}>{status.error || status.message}</div>
        {running && <progress aria-label="Export in progress" style={{ width: '100%', marginTop: 8 }} />}
        <p className="accounting-note">For a complete month, use the prepared workbook above for the cross-source checked filing review. These on-demand exports can take longer and show source evidence for the selected dates; the complete export also includes balances, expenses, inventory, payments and payouts. Every requested page must arrive before a file downloads. Review refunds, exemptions, source links and other exceptions before filing.</p>
      </section>
      {monthlySummary && <section className="card accounting-panel" aria-label="Monthly sales and tax summary">
        <h2 style={{ marginTop: 0, fontSize: 20 }}>Monthly sales and tax charged · {monthlySummary.periodLabel}</h2>
        <p className="accounting-note">Recorded gross sales exclude tax, deposits and previous balances. PST includes all recorded regions and needs BC allocation review. Totals are before refunds and are not a filed return.</p>
        <div className="accounting-table" tabIndex={0}><table className="rp-table"><thead><tr><th>Source</th><th>Currency</th><th>Gross sales before tax</th><th>PST charged</th><th>GST charged</th><th>Invoices / orders</th></tr></thead><tbody>{monthlySummary.totals.map(row => <tr key={`${row.source}-${row.currency}`}><td>{row.source}</td><td>{row.currency}</td><td>{accountingCell({ type: 'currency' }, row.grossRevenue)}</td><td>{accountingCell({ type: 'currency' }, row.pstCharged)}</td><td>{accountingCell({ type: 'currency' }, row.gstCharged)}</td><td>{row.recordCount.toLocaleString()}</td></tr>)}</tbody></table></div>
        <h3 style={{ fontSize: 16, marginBottom: 4 }}>Review before filing</h3>
        {monthlySummary.exceptions.map(item => <p className="accounting-note" key={`${item.code}-${item.source}-${item.currency}-${item.message}`}><strong>{item.source} · {item.currency} · {item.count ? `${item.count} record${item.count === 1 ? '' : 's'}` : 'Required review'}</strong> — {item.message}{item.amount != null ? ` Recorded amount ${accountingCell({ type: 'currency' }, item.amount)} ${item.currency}.` : ''}{item.examples.length ? ` Examples: ${item.examples.join(', ')}.` : ''}</p>)}
      </section>}
      <section className="card accounting-panel" aria-label="Report preview">
        {section === 'shopify' && <p>Shopify coverage is checked from the connected store. Missing order history or payout permissions will block a combined export; a clearly marked Ranch Power only export remains available.</p>}
        <label htmlFor="accounting-report-choice">Report</label><select id="accounting-report-choice" className="field" value={selected} disabled={running || !visible.length} onChange={event => chooseReport(event.target.value)}>{visible.map(report => <option key={report.id} value={report.id}>{report.label}</option>)}</select>
        {active && <><p className="accounting-note">{active.description} {active.dateBasis ? `Date basis: ${active.dateBasis}.` : ''}</p><button className="btn btn-sm" disabled={previewBusy || running} onClick={() => loadPreview()}>{previewBusy ? 'Loading rows…' : 'Load rows'}</button></>}
        {!visible.length && <p>No reports are available in this section.</p>}
        {preview?.error && <div role="alert" className="accounting-alert">{preview.error}</div>}
        {preview && !preview.error && <>
          <p className="accounting-note">{preview.total == null ? `${preview.rows.length} source rows shown${preview.sample ? ' · first page only; export reads every page' : ''}` : `${preview.rows.length ? preview.offset + 1 : 0}–${preview.offset + preview.rows.length} of ${preview.total.toLocaleString()} matching rows`} · Data cutoff {preview.snapshotAt || 'unavailable'}</p>
          {preview.coverage && <div className="accounting-alert">Source coverage: {preview.coverage.status || 'See source evidence'}</div>}
          {accountingWarnings(preview.warnings).map((warning, index) => <div className="accounting-alert" key={index}>{warning}</div>)}
          <div className="accounting-table" tabIndex={0} aria-label="Accounting rows"><table className="rp-table"><thead><tr>{accountingColumns(active).map(column => <th key={column.key}>{column.label}</th>)}</tr></thead><tbody>{preview.rows.map((row, index) => <tr key={index}>{accountingColumns(active).map(column => <td key={column.key}>{column.key === 'receiptPath' && row[column.key] ? <button className="btn btn-sm" onClick={async () => { try { await rpOpenProtectedFile(`/api/files/view?path=${encodeURIComponent(row[column.key])}`); } catch (error) { setStatus({ phase: 'error', message: '', error: error.message || 'Could not open protected receipt.' }); } }}>View receipt</button> : accountingCell(column, row[column.key])}</td>)}</tr>)}</tbody></table></div>
          {preview.nextOffset != null && <button className="btn btn-sm" disabled={previewBusy || running} onClick={() => loadPreview(preview.nextOffset)}>Next page</button>}
        </>}
      </section>
      {evidence.length > 0 && <section className="card accounting-panel" aria-label="Export evidence"><h2 style={{ marginTop: 0, fontSize: 18 }}>Source coverage saved with your workbook</h2>{evidence.map((item, index) => <p key={index} className="accounting-note"><strong>{item.report}</strong> · {item.rows === '' ? item.source : `${item.rows} rows · ${item.completeness}`} {item.warnings}</p>)}</section>}
    </>}
  </div>;
}
Object.assign(window, { AccountingView, AccountingHelpers: { accountingPreviousMonth, accountingValidateDates, accountingColumns, accountingFetchNative, accountingFetchShopify, accountingEvidenceRows, accountingReconciliationNotes } });


/* ===== desktop_pipeline.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Sales Pipeline (ops / leadership)
   ============================================================ */

function desktopLeadDefaultDeliveryFromNotes(notes, fallback = 'Ranch Delivery') {
  if (typeof leadDefaultDeliveryFromNotes === 'function') return leadDefaultDeliveryFromNotes(notes, fallback);
  const match = String(notes || '').match(/Default delivery preference:\s*([^\n]+)/i);
  return typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(match && match[1], fallback) : (match && match[1]) || fallback;
}

function desktopLeadNotesWithDeliveryPreference(notes, method) {
  if (typeof leadNotesWithDeliveryPreference === 'function') return leadNotesWithDeliveryPreference(notes, method);
  const normalized = typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(method) : (method || 'Ranch Delivery');
  const base = String(notes || '').replace(/^Default delivery preference:\s*.*$/gim, '').trim();
  return [base, `Default delivery preference: ${normalized}`].filter(Boolean).join('\n');
}

function pipelineEmailMetric(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.round(number) : 0;
}

function PipelineEmailAnalytics({ onOpenLead }) {
  const live = typeof leadLiveMode !== 'function' || leadLiveMode();
  const [state, setState] = useState({ loading: live, error: '', payload: null });
  const [reload, setReload] = useState(0);
  const [suppression, setSuppression] = useState({
    busy: false,
    email: '',
    error: '',
    reason: '',
    source: 'recipient_request',
    success: '',
  });

  useEffect(() => {
    let cancelled = false;
    if (!live) {
      setState({ loading: false, error: '', payload: null });
      return () => { cancelled = true; };
    }
    setState({ loading: true, error: '', payload: null });
    rpApi('/api/leads/email/analytics?days=90')
      .then(payload => {
        if (!payload || payload.schemaVersion !== 1 || !payload.summary) throw new Error('Email analytics returned an unsupported response.');
        if (!cancelled) setState({ loading: false, error: '', payload });
      })
      .catch(error => {
        if (!cancelled) setState({ loading: false, error: error && error.message ? error.message : 'Could not load pipeline email analytics.', payload: null });
      });
    return () => { cancelled = true; };
  }, [live, reload]);

  const payload = state.payload;
  const summary = payload && payload.summary || {};
  const sampleSize = pipelineEmailMetric(summary.sampleSize);
  const webhookConfigured = Boolean(payload && payload.webhookConfigured);
  const engagementTrackingConfigured = Boolean(payload && payload.engagementTrackingConfigured);
  const providerMetric = (value, configured, offDetail) => {
    const count = pipelineEmailMetric(value);
    return {
      value,
      unavailable: !configured && count === 0,
      detail: !configured && count > 0 ? `Historical data · ${offDetail}` : !configured ? offDetail : '',
    };
  };
  const metrics = [
    { label: 'Sent', value: summary.sent, icon: 'mail', color: 'var(--ink)' },
    { label: 'Delivered', icon: 'check', color: 'var(--ok)', ...providerMetric(summary.delivered, webhookConfigured, 'Webhook unavailable') },
    { label: 'Replies', value: summary.replied, icon: 'tap', color: 'var(--ok)' },
    {
      label: 'Messages with any-recipient open estimate',
      icon: 'trend',
      color: 'var(--info)',
      ...providerMetric(summary.openSignalMessages, webhookConfigured && engagementTrackingConfigured, 'Engagement tracking unavailable'),
      detail: webhookConfigured && engagementTrackingConfigured && pipelineEmailMetric(summary.openSignals) > pipelineEmailMetric(summary.openSignalMessages)
        ? `${pipelineEmailMetric(summary.openSignals)} total signals`
        : providerMetric(summary.openSignalMessages, webhookConfigured && engagementTrackingConfigured, 'Engagement tracking unavailable').detail,
    },
    {
      label: 'Messages with any-recipient click estimate',
      icon: 'link',
      color: 'var(--info)',
      ...providerMetric(summary.clickSignalMessages, webhookConfigured && engagementTrackingConfigured, 'Engagement tracking unavailable'),
      detail: webhookConfigured && engagementTrackingConfigured && pipelineEmailMetric(summary.clickSignals) > pipelineEmailMetric(summary.clickSignalMessages)
        ? `${pipelineEmailMetric(summary.clickSignals)} total signals`
        : providerMetric(summary.clickSignalMessages, webhookConfigured && engagementTrackingConfigured, 'Engagement tracking unavailable').detail,
    },
    { label: 'Bounces', icon: 'alert', color: pipelineEmailMetric(summary.bounced) ? 'var(--danger)' : 'var(--muted)', ...providerMetric(summary.bounced, webhookConfigured, 'Webhook unavailable') },
    { label: 'Unsubscribes', value: summary.unsubscribed, icon: 'x', color: pipelineEmailMetric(summary.unsubscribed) ? 'var(--danger)' : 'var(--muted)' },
    { label: 'Provider suppressions', icon: 'alert', color: pipelineEmailMetric(summary.suppressed) ? 'var(--danger)' : 'var(--muted)', ...providerMetric(summary.suppressed, webhookConfigured, 'Webhook unavailable') },
    { label: 'Follow-ups overdue', value: summary.overdueFollowUps, icon: 'clock', color: pipelineEmailMetric(summary.overdueFollowUps) ? 'var(--danger)' : 'var(--muted)' },
  ];
  const attentionRows = [
    ...(Array.isArray(summary.overdueFollowUpLeads) ? summary.overdueFollowUpLeads : []),
    ...(Array.isArray(summary.goingColdLeads) ? summary.goingColdLeads : []),
    ...(Array.isArray(summary.coldLeads) ? summary.coldLeads : []),
    ...(Array.isArray(payload && payload.overdueFollowUps) ? payload.overdueFollowUps : []),
  ].filter((row, index, rows) => {
    const id = row && (row.leadId || row.lead_id || row.id);
    return row && rows.findIndex(candidate => (candidate.leadId || candidate.lead_id || candidate.id) === id) === index;
  }).slice(0, 6);
  const generatedAt = payload && payload.generatedAt ? leadEmailFormatDate(payload.generatedAt) : '';
  const submitSuppression = async event => {
    event.preventDefault();
    const email = String(suppression.email || '').trim().toLowerCase();
    const reason = String(suppression.reason || '').trim();
    if (!/^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(email)) {
      setSuppression(current => ({ ...current, error: 'Enter the exact valid email address to suppress.', success: '' }));
      return;
    }
    if (reason.length < 10 || reason.split(/\s+/).filter(Boolean).length < 2) {
      setSuppression(current => ({ ...current, error: 'Add a factual reason of at least 10 characters and two words.', success: '' }));
      return;
    }
    const confirmed = window.confirm(
      `Suppress ${email} from future Ranch Power pipeline and marketing email?\n\nThis preserves existing email evidence and does not provide an unsuppress shortcut.`
    );
    if (!confirmed) return;
    setSuppression(current => ({ ...current, busy: true, error: '', success: '' }));
    try {
      const response = await rpApi('/api/leads/email/suppress', {
        method: 'POST',
        body: JSON.stringify({
          email,
          reason,
          source: suppression.source,
        }),
      });
      if (!response || response.accepted !== true || response.email !== email) {
        throw new Error('The suppression response could not be verified.');
      }
      const warning = Array.isArray(response.bookkeepingWarnings) && response.bookkeepingWarnings.length
        ? ` ${response.bookkeepingWarnings.join(' ')}`
        : '';
      setSuppression(current => ({
        ...current,
        busy: false,
        email: '',
        error: '',
        reason: '',
        success: `${email} is now blocked from future pipeline and marketing email.${warning}`,
      }));
    } catch (error) {
      setSuppression(current => ({
        ...current,
        busy: false,
        error: error && error.message ? error.message : 'The email address could not be suppressed.',
        success: '',
      }));
    }
  };

  return (
    <div className="card" data-pipeline-email-analytics={live ? 'live' : 'preview'} style={{ padding: 15, marginBottom: 22, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: state.loading || state.error || !payload || sampleSize ? 12 : 0 }}>
        <div>
          <div className="eyebrow">Retailer email performance · 90 days</div>
          <div style={{ color: 'var(--faint)', fontSize: 11.25, lineHeight: 1.4, marginTop: 4 }}>
            Operations oversight and compliance controls{generatedAt ? ` · refreshed ${generatedAt}` : ''}
          </div>
        </div>
        {live && <button className="btn btn-sm" disabled={state.loading} onClick={() => setReload(value => value + 1)}><Icon name="refresh" size={13} />Refresh</button>}
      </div>

      {!live && <div style={{ padding: '10px 11px', borderRadius: 9, background: 'var(--info-bg)', color: 'var(--info)', fontSize: 11.75, fontWeight: 700 }}>Sign in to view provider-backed email analytics. Preview mode does not simulate results.</div>}
      {state.loading && <div role="status" style={{ padding: '11px 0', color: 'var(--faint)', fontSize: 12 }}><Icon name="refresh" size={14} style={{ marginRight: 7, verticalAlign: -2 }} />Loading tracked email performance…</div>}
      {state.error && (
        <div role="alert" style={{ padding: '10px 11px', borderRadius: 9, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 11.75, fontWeight: 700 }}>
          {state.error} <button className="btn btn-sm" onClick={() => setReload(value => value + 1)} style={{ marginLeft: 8 }}>Try again</button>
        </div>
      )}
      {!state.loading && !state.error && payload && sampleSize === 0 && <div style={{ padding: '10px 11px', borderRadius: 9, background: 'var(--surface-2)', color: 'var(--muted)', fontSize: 11.75 }}>No tracked pipeline emails were sent in this 90-day window.</div>}
      {!state.loading && !state.error && payload && sampleSize > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(105px, 1fr))', gap: 8 }}>
            {metrics.map(metric => (
              <div key={metric.label} style={{ minWidth: 0, padding: '10px 9px', border: '1px solid var(--line-2)', borderRadius: 10, background: 'var(--surface-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: metric.color }}>
                  <Icon name={metric.icon} size={13} />
                  <span className="disp" style={{ fontSize: 19 }}>{metric.unavailable ? '—' : pipelineEmailMetric(metric.value)}</span>
                </div>
                <div style={{ color: 'var(--ink-soft)', fontSize: 10.25, fontWeight: 700, marginTop: 4, lineHeight: 1.25 }}>{metric.label}</div>
                {metric.detail && <div style={{ color: 'var(--faint)', fontSize: 9.5, marginTop: 2 }}>{metric.detail}</div>}
              </div>
            ))}
          </div>
          <div style={{ color: 'var(--faint)', fontSize: 10.5, lineHeight: 1.45, marginTop: 9 }}>
            {engagementTrackingConfigured && webhookConfigured
              ? <><strong style={{ color: 'var(--info)' }}>Aggregate open/click estimates can come from any To or CC recipient and are not confirmed reads.</strong> {summary.openSignalLabel || 'Machine activity and privacy tools can produce open estimates.'}</>
              : <strong style={{ color: 'var(--amber-deep)' }}>Open/click reporting is unavailable until Resend engagement tracking and its signed webhook are configured.</strong>}
            {pipelineEmailMetric(summary.complained) > 0 ? <span style={{ color: 'var(--danger)', fontWeight: 700 }}> · {pipelineEmailMetric(summary.complained)} complaint{pipelineEmailMetric(summary.complained) === 1 ? '' : 's'}</span> : null}
            {payload.truncated ? <span style={{ color: 'var(--danger)', fontWeight: 700 }}> · This view reached its reporting limit; narrow the date window before comparing rates.</span> : null}
          </div>
          {attentionRows.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7, marginTop: 10 }}>
              <span style={{ color: 'var(--danger)', fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email follow-up attention</span>
              {attentionRows.map((row, index) => {
                const leadId = row.leadId || row.lead_id || row.id;
                const localLead = Array.isArray(RP.LEADS) ? RP.LEADS.find(lead => lead.id === leadId) : null;
                const label = row.leadName || row.lead_name || row.name || localLead && localLead.name || leadId || `Lead ${index + 1}`;
                return leadId && typeof onOpenLead === 'function'
                  ? <button key={leadId} className="btn btn-sm" onClick={() => onOpenLead(leadId)}>{label}</button>
                  : <span key={leadId || index} className="pill" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>{label}</span>;
              })}
            </div>
          )}
        </>
      )}
      {live && (
        <form data-lead-email-suppression-control onSubmit={submitSuppression} style={{ borderTop: '1px solid var(--line-2)', marginTop: 14, paddingTop: 14 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: 'var(--danger)', fontSize: 10.5, fontWeight: 850, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Manual email suppression</div>
            <div style={{ color: 'var(--faint)', fontSize: 10.75, lineHeight: 1.45, marginTop: 3 }}>Use a factual request or delivery reason. This blocks the exact address across pipeline and marketing sends while preserving history.</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(190px, 1fr) minmax(180px, 0.8fr)', gap: 9 }}>
            <label style={{ minWidth: 0 }}>
              <span style={{ display: 'block', color: 'var(--muted)', fontSize: 10.5, fontWeight: 750, marginBottom: 4 }}>Email address</span>
              <input className="field" type="email" autoComplete="off" required value={suppression.email} onChange={event => setSuppression(current => ({ ...current, email: event.target.value, error: '', success: '' }))} placeholder="buyer@retailer.ca" />
            </label>
            <label style={{ minWidth: 0 }}>
              <span style={{ display: 'block', color: 'var(--muted)', fontSize: 10.5, fontWeight: 750, marginBottom: 4 }}>Source</span>
              <select className="field" required value={suppression.source} onChange={event => setSuppression(current => ({ ...current, source: event.target.value, error: '', success: '' }))}>
                <option value="recipient_request">Recipient asked us to stop</option>
                <option value="retailer_staff_request">Retailer staff asked us to stop</option>
                <option value="complaint">Complaint received</option>
                <option value="invalid_or_bounced">Address invalid or bounced</option>
                <option value="other_documented">Other documented source</option>
              </select>
            </label>
          </div>
          <label style={{ display: 'block', marginTop: 9 }}>
            <span style={{ display: 'block', color: 'var(--muted)', fontSize: 10.5, fontWeight: 750, marginBottom: 4 }}>Factual reason</span>
            <textarea className="field" required maxLength="1000" rows="2" value={suppression.reason} onChange={event => setSuppression(current => ({ ...current, reason: event.target.value, error: '', success: '' }))} placeholder="e.g. Morgan replied on July 23 asking Ranch Power not to email this address again." style={{ resize: 'vertical' }} />
          </label>
          {suppression.error && <div role="alert" style={{ background: 'var(--danger-bg)', borderRadius: 8, color: 'var(--danger)', fontSize: 11.25, fontWeight: 700, marginTop: 9, padding: '8px 10px' }}>{suppression.error}</div>}
          {suppression.success && <div role="status" style={{ background: 'var(--ok-bg)', borderRadius: 8, color: 'var(--ok)', fontSize: 11.25, fontWeight: 700, marginTop: 9, padding: '8px 10px' }}>{suppression.success}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, justifyContent: 'space-between', marginTop: 9 }}>
            <span style={{ color: 'var(--faint)', fontSize: 10.25 }}>A confirmation is required. There is intentionally no one-click unsuppress action.</span>
            <button className="btn btn-sm" disabled={suppression.busy} type="submit" style={{ color: 'var(--danger)', flexShrink: 0 }}><Icon name="alert" size={13} />{suppression.busy ? 'Suppressing…' : 'Suppress address'}</button>
          </div>
        </form>
      )}
    </div>
  );
}

function PipelineView({ go }) {
  const [, force] = useState(0);
  const [sel, setSel] = useState(null);
  const [addLead, setAddLead] = useState(false);
  const [repFilter, setRepFilter] = useState('all');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const L = RP.LEADS_API;
  const reps = ['all', ...Array.from(new Set(RP.LEADS.map(l => l.rep)))];
  const leads = RP.LEADS.filter(l => repFilter === 'all' || l.rep === repFilter);
  const active = leads.filter(l => L.ACTIVE_STAGES.includes(l.stage));

  const funnel = L.ACTIVE_STAGES.map(s => {
    const items = active.filter(l => l.stage === s);
    return { s, st: L.stage(s), items, value: items.reduce((a, l) => a + l.potential, 0) };
  });
  const maxN = Math.max(1, ...funnel.map(f => f.items.length));
  const won = leads.filter(l => l.stage === 'won').length;
  const lost = leads.filter(l => l.stage === 'lost').length;
  const closeRate = (won + lost) ? Math.round(won / (won + lost) * 100) : 0;
  const overdue = active.filter(l => L.isOverdue(l));
  const pipeValue = active.reduce((a, l) => a + l.potential, 0);

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {addLead && <DesktopLeadModal onClose={() => setAddLead(false)} onAdded={(lead) => { force(x => x + 1); if (lead && lead.id) setSel(lead.id); }} />}
      {sel && <LeadDrawer id={sel} onClose={() => setSel(null)} onChange={() => force(x => x + 1)} go={go} />}
      <PageHead eyebrow="Sales Intelligence" title="Pipeline"
        sub="Every rep's lead-to-customer funnel in one view."
        actions={<div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <select className="field" style={{ width: 'auto' }} value={repFilter} onChange={e => setRepFilter(e.target.value)}>
            {reps.map(r => <option key={r} value={r}>{r === 'all' ? 'All reps' : r}</option>)}
          </select>
          <button className="btn btn-sm btn-primary" onClick={() => setAddLead(true)}><Icon name="plus" size={15} />Add Lead</button>
        </div>} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 22 }}>
        <KPICard label="Active Leads" value={active.length} icon="trend" accent="var(--info)" />
        <KPICard label="Pipeline Potential" value={'~' + pipeValue} unit="cs/mo" icon="package" accent="var(--amber)" />
        <KPICard label="Close Rate" value={closeRate} unit="%" icon="check" accent="var(--ok)" />
        <KPICard label="Overdue Follow-ups" value={overdue.length} icon="alert" accent="var(--danger)" onClick={() => setOnlyOverdue(v => !v)} active={onlyOverdue} />
      </div>

      <PipelineEmailAnalytics onOpenLead={leadId => setSel(leadId)} />

      {/* Funnel board */}
      <div className="rp-board" style={{ display: 'grid', gridTemplateColumns: `repeat(${funnel.length}, 1fr)`, gap: 14, marginBottom: 22 }}>
        {funnel.map(f => (
          <div key={f.s} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="disp" style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.st.label}</span>
                <span className="disp" style={{ fontSize: 18, color: f.st.color }}>{f.items.length}</span>
              </div>
              <div style={{ height: 3, borderRadius: 3, background: f.st.color, marginTop: 8, opacity: 0.3 + (f.items.length / maxN) * 0.7 }} />
              <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 6 }}>~{f.value} cs/mo</div>
            </div>
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 80 }}>
              {f.items.map(l => {
                const h = L.health(l), [fg, bg] = healthPill(h);
                return (
                  <button key={l.id} onClick={() => setSel(l.id)} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid ' + (L.isOverdue(l) ? 'oklch(0.57 0.16 27 / 0.35)' : 'var(--line)'), borderRadius: 9, padding: '10px 11px', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: tempColor(l.temp), flexShrink: 0 }} />
                      <span style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 3 }}>{l.type} · ~{l.potential} cs/mo</div>
                    <div style={{ marginTop: 7 }}><span className="pill" style={{ background: bg, color: fg, fontSize: 10.5 }}>{h.label}</span></div>
                  </button>
                );
              })}
              {f.items.length === 0 && <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faint)', fontSize: 12 }}>—</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Attention table */}
      <SectionTitle right={<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {onlyOverdue && <button className="btn btn-ghost btn-sm" onClick={() => setOnlyOverdue(false)} style={{ color: 'var(--muted)' }}><Icon name="x" size={13} />Show all</button>}
        {overdue.length ? <button onClick={() => setOnlyOverdue(v => !v)} className="pill" style={{ background: onlyOverdue ? 'var(--danger)' : 'var(--danger-bg)', color: onlyOverdue ? '#fff' : 'var(--danger)', border: 'none', cursor: 'pointer' }}>{overdue.length} overdue</button> : null}
      </div>}>{onlyOverdue ? 'Overdue Follow-ups' : 'Needs Attention'}</SectionTitle>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="rp-table">
          <thead><tr><th>Lead</th><th>Rep</th><th>Stage</th><th>Temp</th><th>Next Action</th><th>Health</th><th className="num">Score</th><th></th></tr></thead>
          <tbody>
            {[...active].filter(l => !onlyOverdue || L.isOverdue(l)).sort((a, b) => (L.isOverdue(b) - L.isOverdue(a)) || L.score(b) - L.score(a)).map(l => {
              const h = L.health(l), [fg, bg] = healthPill(h);
              return (
                <tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => setSel(l.id)}>
                  <td><div style={{ fontWeight: 600 }}>{l.name}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{l.type} · {l.city}</div></td>
                  <td style={{ color: 'var(--muted)' }}>{l.rep}</td>
                  <td><span style={{ fontWeight: 600, color: L.stage(l.stage).color === 'var(--faint)' ? 'var(--muted)' : L.stage(l.stage).color }}>{L.stage(l.stage).label}</span></td>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: tempColor(l.temp) }} />{l.temp}</span></td>
                  <td style={{ color: L.isOverdue(l) ? 'var(--danger)' : 'var(--muted)', fontWeight: L.isOverdue(l) ? 700 : 400, fontSize: 12.5 }}>{l.nextAction ? (L.isOverdue(l) ? 'Overdue · ' : '') + l.nextAction.note + (l.nextAction.due ? ` · ${L.formatDue ? L.formatDue(l.nextAction.due) : l.nextAction.due}` : '') : '—'}</td>
                  <td><span className="pill" style={{ background: bg, color: fg }}>{h.label}</span></td>
                  <td className="num" style={{ fontWeight: 700 }}>{L.score(l)}</td>
                  <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
                </tr>
              );
            })}
            {onlyOverdue && overdue.length === 0 && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No overdue follow-ups — everyone's on track.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function leadDetailClean(value) {
  return String(value || '').trim();
}

function leadDetailPhoneHref(phone) {
  const raw = leadDetailClean(phone);
  if (!raw) return '';
  const dial = raw.replace(/[^\d+]/g, '');
  return dial ? `tel:${dial}` : `tel:${encodeURIComponent(raw)}`;
}

/* ---- Lead detail drawer (desktop) ---- */
function LeadDrawer({ id, onClose, onChange, go }) {
  const L = RP.LEADS_API;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const lead = RP.LEADS.find(l => l.id === id);
  if (!lead) return null;
  const nba = L.nextBestAction(lead);
  const st = L.stage(lead.stage);
  const h = L.health(lead), [fg, bg] = healthPill(h);
  const curIdx = L.ACTIVE_STAGES.indexOf(lead.stage);
  const nextStage = L.ACTIVE_STAGES[curIdx + 1];
  const isActive = L.ACTIVE_STAGES.includes(lead.stage);
  const canDeleteDuplicate = typeof rpCanDeleteDuplicateLead === 'function' && rpCanDeleteDuplicateLead(lead);

  const refreshLead = async (message) => {
    const refreshed = await rpRefreshAfterMutation(message);
    if (!refreshed) onChange();
  };

  const advance = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (lead.stage === 'lost') {
        const nextAction = leadNextActionForStage('contacted', 1);
        if (leadLiveMode()) {
          await leadRequest('stage', { leadId: id, stage: 'contacted', nextAction });
          await refreshLead(`${lead.name} reopened`);
        } else {
          L.advanceStage(id, 'contacted', { lostReason: null, nextAction, revisit: null });
        }
        toast(`${lead.name} reopened`);
        onChange();
        return;
      }
      if (lead.stage === 'proposal') {
        setConvertOpen(true);
        return;
      }
      if (nextStage) {
        const type = L.actType(nba.channel).id;
        const nextAction = leadNextActionForStage(nextStage, 3);
        if (leadLiveMode()) {
          await leadRequest('activity', { leadId: id, type, note: nba.cta, nextAction, stage: nextStage });
          await refreshLead(`${lead.name} advanced`);
        } else {
          L.logActivity(id, { type, note: nba.cta, nextAction });
          L.advanceStage(id, nextStage);
        }
        toast(`${lead.name} advanced to ${L.stage(nextStage).label}`);
        onChange();
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update lead.');
    } finally {
      setBusy(false);
    }
  };
  const logQuick = async (type) => {
    if (busy) return;
    setBusy(true);
    setError('');
    const note = L.actType(type).label + ' logged';
    const nextAction = leadNextActionForStage(lead.stage, 3);
    try {
      if (leadLiveMode()) {
        await leadRequest('activity', { leadId: id, type, note, nextAction });
        await refreshLead('Touch logged');
      } else {
        L.logActivity(id, { type, note, nextAction });
      }
      toast('Touch logged · follow-up refreshed');
      onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not log touch.');
    } finally {
      setBusy(false);
    }
  };
  const markLost = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (leadLiveMode()) {
        await leadRequest('stage', { leadId: id, stage: 'lost', lostReason: 'Not interested', nextAction: null });
        await refreshLead(`${lead.name} marked lost`);
      } else {
        L.advanceStage(id, 'lost', { lostReason: 'Not interested', nextAction: null });
      }
      toast(`${lead.name} marked as lost`);
      onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update lead.');
    } finally {
      setBusy(false);
    }
  };
  const deleteLead = async () => {
    if (busy) return;
    const confirmed = window.confirm ? window.confirm(`Permanently delete ${lead.name} as a duplicate or test lead?\n\nIf this is a real lead that is no longer interested, cancel and use Mark as Lost so its history is preserved. Converted leads and leads with customer, contact, task, or email history cannot be deleted.`) : true;
    if (!confirmed) return;
    setBusy(true);
    setError('');
    try {
      if (leadLiveMode()) {
        await leadRequest('delete', { leadId: id, reason: 'Confirmed duplicate or test lead cleanup.' });
        await refreshLead(`${lead.name} deleted`);
      } else {
        L.deleteLead(id);
      }
      toast(`${lead.name} duplicate/test lead deleted`, 'warn');
      onChange();
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not delete lead.');
    } finally {
      setBusy(false);
    }
  };
  const saveContact = async (patch) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (leadLiveMode()) {
        await leadRequest('update', { leadId: id, ...patch });
        await refreshLead('Lead details updated');
      } else {
        L.upsertLead({ id, ...patch });
      }
      toast(`${lead.name} details updated`);
      setEditContactOpen(false);
      onChange();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not update lead contact.';
      setError(message);
      throw new Error(message);
    } finally {
      setBusy(false);
    }
  };
  const phoneHref = leadDetailPhoneHref(lead.phone);
  const leadAddressText = [lead.address, lead.city || lead.territory, lead.postal].filter(Boolean).join(', ');

  return (
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 8000, background: 'oklch(0.24 0.035 235 / 0.5)', backdropFilter: 'blur(3px)', display: 'flex', justifyContent: 'flex-end' }}>
      {convertOpen && <DesktopLeadConvertModal lead={lead} onClose={() => setConvertOpen(false)} onConverted={(customer, mode) => {
        setConvertOpen(false);
        toast(mode === 'existing' ? `${lead.name} linked to ${customer.name}` : `${lead.name} converted -> ${customer.id} - account created`);
        onChange();
      }} />}
      {editContactOpen && <DesktopLeadContactModal lead={lead} onClose={() => setEditContactOpen(false)} onSave={saveContact} />}
      {emailOpen && <LeadEmailComposer lead={lead} allowSend={true} onClose={() => setEmailOpen(false)} onSent={() => {
        rpRefreshAfterMutation('Lead email activity refreshed').then(refreshed => {
          if (!refreshed) onChange();
        });
      }} />}
      <div onMouseDown={e => e.stopPropagation()} className="rp-scroll" style={{ width: 460, maxWidth: '100%', height: '100%', background: 'var(--surface)', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', animation: 'rp-pop .2s ease both' }}>
        {/* header */}
        <div style={{ background: 'var(--navy)', color: '#fff', padding: '20px 22px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 className="disp" style={{ margin: 0, fontSize: 24, textTransform: 'uppercase' }}>{lead.name}</h2>
              <div style={{ fontSize: 13, opacity: 0.75, marginTop: 3 }}>{lead.type} · {lead.city} · {lead.rep}</div>
            </div>
            <button onClick={onClose} style={{ border: 'none', background: 'oklch(1 0 0 / 0.12)', color: '#fff', width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={17} /></button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <span className="pill" style={{ background: bg, color: fg }}>{h.label}</span>
            <span className="pill" style={{ background: 'oklch(1 0 0 / 0.1)', color: '#fff' }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: tempColor(lead.temp) }} />{lead.temp}</span>
            <span className="pill" style={{ background: 'oklch(1 0 0 / 0.1)', color: '#fff' }}>~{lead.potential} cs/mo</span>
            <span className="pill" style={{ background: 'oklch(1 0 0 / 0.1)', color: '#fff' }}>Score {L.score(lead)}</span>
          </div>
          {/* stage rail */}
          <div style={{ display: 'flex', gap: 4, marginTop: 16 }}>
            {L.ACTIVE_STAGES.map((s, i) => {
              const reached = isActive ? i <= curIdx : lead.stage === 'won';
              return <div key={s} style={{ flex: 1 }}><div style={{ height: 4, borderRadius: 3, background: reached ? (lead.stage === 'won' ? 'var(--ok)' : L.stage(s).color) : 'oklch(1 0 0 / 0.15)' }} /><div style={{ fontSize: 9, marginTop: 4, color: i === curIdx ? '#fff' : 'oklch(0.65 0.02 235)', fontWeight: i === curIdx ? 700 : 500, textTransform: 'uppercase' }}>{L.stage(s).short}</div></div>;
            })}
          </div>
        </div>

        <div style={{ padding: 22 }}>
          {/* coach card */}
          <div style={{ background: nba.urgent ? 'var(--danger-bg)' : 'var(--amber-soft)', border: '1px solid ' + (nba.urgent ? 'oklch(0.57 0.16 27 / 0.3)' : 'oklch(0.64 0.115 64 / 0.3)'), borderRadius: 12, padding: 15, marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}><Icon name={nba.urgent ? 'alert' : 'star'} size={15} style={{ color: nba.urgent ? 'var(--danger)' : 'var(--amber-deep)' }} /><span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: nba.urgent ? 'var(--danger)' : 'var(--amber-deep)' }}>{nba.urgent ? 'Act now' : 'Recommended next move'}</span></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.25 }}>{nba.title}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4, lineHeight: 1.45 }}>{nba.detail}</div>
            {(isActive || lead.stage === 'lost') && <button className="btn btn-primary" disabled={busy} style={{ width: '100%', marginTop: 12, opacity: busy ? 0.6 : 1 }} onClick={advance}><Icon name={lead.stage === 'proposal' ? 'check' : lead.stage === 'lost' ? 'refresh' : 'arrowR'} size={15} />{busy ? 'Working...' : nba.cta}</button>}
            {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{error}</div>}
          </div>

          {/* quick log */}
          {isActive && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              {L.ACTIVITY_TYPES.slice(0, 4).map(a => (
                <button key={a.id} disabled={busy} onClick={() => a.id === 'email' ? setEmailOpen(true) : logQuick(a.id)} className="btn" style={{ flex: 1, flexDirection: 'column', gap: 5, padding: '10px 4px', opacity: busy ? 0.6 : 1 }}><Icon name={a.icon} size={17} /><span style={{ fontSize: 10.5 }}>{a.label}</span></button>
              ))}
            </div>
          )}

          <div className="card" style={{ padding: 15, marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <div className="eyebrow">Contact Details</div>
              <button className="btn btn-sm" disabled={busy} onClick={() => setEditContactOpen(true)}><Icon name="pen" size={14} />Edit</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 9 }}>
              <Row k="Contact" v={lead.contact || 'Not set'} />
              <Row k="Phone" v={phoneHref ? <a href={phoneHref} style={{ color: 'var(--amber-deep)', fontWeight: 800, textDecoration: 'none' }}>{lead.phone}</a> : 'Not set'} />
              <Row k="Email" v={lead.email ? <span className="mono" style={{ overflowWrap: 'anywhere' }}>{lead.email}</span> : 'Not set'} />
              <Row k="Address" v={leadAddressText || 'Not set'} />
              <div>
                <div style={{ color: 'var(--faint)', fontSize: 12, marginBottom: 4 }}>Notes</div>
                <div style={{ color: lead.notes ? 'var(--ink-soft)' : 'var(--faint)', fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{lead.notes || 'No lead notes recorded yet.'}</div>
              </div>
            </div>
          </div>

          <LeadEmailPulse lead={lead} actionLabel="Review email" onOpen={() => setEmailOpen(true)} />

          {nba.tips && (
            <div className="card" style={{ padding: 15, marginBottom: 18 }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>{st.label} playbook</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {nba.tips.map((t, i) => <div key={i} style={{ display: 'flex', gap: 9 }}><span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--surface-2)', color: 'var(--amber-deep)', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</span><span style={{ fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.4 }}>{t}</span></div>)}
              </div>
            </div>
          )}

          {/* activity */}
          <div className="eyebrow" style={{ marginBottom: 12 }}>Activity</div>
          {lead.activities.map((a, i) => { const at = L.actType(a.type); return (
            <div key={i} style={{ display: 'flex', gap: 11 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', background: i === 0 ? 'var(--navy)' : 'var(--surface-2)', color: i === 0 ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={at.icon} size={14} /></span>
                {i < lead.activities.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--line-2)', minHeight: 10 }} />}
              </div>
              <div style={{ paddingBottom: 16, flex: 1 }}><div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 12.5, fontWeight: 600 }}>{at.label}</span><span style={{ fontSize: 11, color: 'var(--faint)' }}>{a.date}</span></div><div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 2, lineHeight: 1.4 }}>{a.note}</div></div>
            </div>
          ); })}
          {isActive && (
            <button className="btn" disabled={busy} onClick={markLost} style={{ width: '100%', marginTop: 8, color: 'var(--danger)', opacity: busy ? 0.6 : 1 }}><Icon name="x" size={15} />Mark as Lost - keep history</button>
          )}
          {canDeleteDuplicate && <button className="btn" disabled={busy} onClick={deleteLead} style={{ width: '100%', marginTop: 8, color: 'var(--danger)', opacity: busy ? 0.6 : 1 }}><Icon name="trash" size={15} />Delete duplicate / test lead</button>}
        </div>
      </div>
    </div>
  );
}

function DesktopLeadContactModal({ lead, onClose, onSave }) {
  const [f, setF] = useState({
    address: lead.address || '',
    city: lead.city || '',
    contact: lead.contact || '',
    email: lead.email || '',
    name: lead.name || '',
    notes: lead.notes || '',
    phone: lead.phone || '',
    postal: lead.postal || ''
  });
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setF(state => ({ ...state, [key]: value }));
  const name = f.name.trim();
  const email = f.email.trim().toLowerCase();
  const emailOk = !email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const canSave = !busy && !!name && emailOk;

  const save = async () => {
    setTried(true);
    setError('');
    if (!canSave) return;
    setBusy(true);
    try {
      await onSave({
        address: f.address.trim(),
        city: f.city.trim(),
        contact: f.contact.trim(),
        email,
        name,
        notes: f.notes.trim(),
        phone: f.phone.trim(),
        postal: f.postal.trim()
      });
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update contact details.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit Lead" sub={lead.name} onClose={onClose} width={560}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!canSave} style={{ opacity: canSave ? 1 : 0.55 }} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Save Lead'}</button></>}>
      <MField label="Business Name" required>
        <input className="field" placeholder="e.g. Sea to Sky Bottle Co." value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !name)} />
        <ReqMsg show={tried && !name}>Business name is required.</ReqMsg>
      </MField>
      <MField label="Street Address">
        <input className="field" placeholder="e.g. 38005 Cleveland Ave" value={f.address} onChange={e => set('address', e.target.value)} />
      </MField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <MField label="City">
          <input className="field" placeholder="e.g. Squamish" value={f.city} onChange={e => set('city', e.target.value)} />
        </MField>
        <MField label="Postal">
          <input className="field" placeholder="V8B 0A1" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} />
        </MField>
      </div>
      <MField label="Contact Name / Role">
        <input className="field" placeholder="e.g. Owner - Ray" value={f.contact} onChange={e => set('contact', e.target.value)} />
      </MField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <MField label="Phone">
          <input className="field" type="tel" placeholder="(604) 555-0100" value={f.phone} onChange={e => set('phone', e.target.value)} />
        </MField>
        <MField label="Email">
          <input className="field" type="email" placeholder="buyer@example.com" value={f.email} onChange={e => set('email', e.target.value)} style={reqStyle(tried && !emailOk)} />
          <ReqMsg show={tried && !emailOk}>Enter a valid email address or leave it blank.</ReqMsg>
        </MField>
      </div>
      <MField label="Lead Notes">
        <textarea className="field" rows="5" placeholder="Buying context, contacts, hours, objections, next details..." value={f.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />
      </MField>
      <InfoNote icon="user">These details stay with the pipeline lead and become the default ship-to details when a new customer is created.</InfoNote>
      {error && <ReqMsg show>{error}</ReqMsg>}
    </Modal>
  );
}

function leadExistingCustomerOptions(lead, search) {
  const scopedCustomers = typeof rpFieldCustomers === 'function' ? rpFieldCustomers() : rpVisibleCustomers(RP.CUSTOMERS);
  const duplicateMatches = typeof rpCustomerDuplicateCandidates === 'function'
    ? rpCustomerDuplicateCandidates({ name: lead.name, email: lead.email, phone: lead.phone, address: lead.address, city: lead.city, postal: lead.postal, territory: lead.territory })
    : [];
  const matchIds = new Set(duplicateMatches.map(match => match.customer && match.customer.id).filter(Boolean));
  const query = String(search || '').trim().toLowerCase();
  return (scopedCustomers || [])
    .filter(customer => customer && customer.id)
    .filter(customer => {
      if (!query) return true;
      return [customer.name, customer.banner, customer.address, customer.city, customer.postal, customer.territory, customer.type, customer.tier, customer.email, customer.phone]
        .join(' ')
        .toLowerCase()
        .includes(query);
    })
    .sort((a, b) => (matchIds.has(b.id) ? 1 : 0) - (matchIds.has(a.id) ? 1 : 0) || String(a.name || '').localeCompare(String(b.name || '')))
    .slice(0, 80);
}

function DesktopLeadConvertModal({ lead, onClose, onConverted }) {
  const accountDefaultTier = rpRetailAccountDefaultPriceListName();
  const canSetCustomAccountPricing = rpCanSetCustomAccountPricing();
  const duplicateMatches = typeof rpCustomerDuplicateCandidates === 'function'
    ? rpCustomerDuplicateCandidates({ name: lead.name, email: lead.email, phone: lead.phone, address: lead.address, city: lead.city, postal: lead.postal, territory: lead.territory })
    : [];
  const initialCustomerId = (duplicateMatches[0] && duplicateMatches[0].customer && duplicateMatches[0].customer.id) || '';
  const [mode, setMode] = useState(initialCustomerId ? 'existing' : 'new');
  const [search, setSearch] = useState(lead.name || '');
  const options = leadExistingCustomerOptions(lead, search);
  const [existingCustomerId, setExistingCustomerId] = useState(initialCustomerId || (options[0] && options[0].id) || '');
  const [tier, setTier] = useState(canSetCustomAccountPricing ? rpRetailAccountNewPriceListName(lead.tier) : accountDefaultTier);
  const [terms, setTerms] = useState('Prepaid');
  const [defaultDeliveryMethod, setDefaultDeliveryMethod] = useState(() => desktopLeadDefaultDeliveryFromNotes(lead.notes));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const selectedCustomer = RP.CUSTOMERS.find(customer => customer.id === existingCustomerId);
  const canConvert = mode === 'existing' ? !!existingCustomerId : true;
  const deliveryNeedsAddress = typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(defaultDeliveryMethod) : defaultDeliveryMethod !== 'Customer Pickup';

  useEffect(() => {
    if (mode !== 'existing') return;
    if (existingCustomerId && options.some(customer => customer.id === existingCustomerId)) return;
    setExistingCustomerId(initialCustomerId || (options[0] && options[0].id) || '');
  }, [mode, search]);

  const convert = async () => {
    if (busy || !canConvert) return;
    setBusy(true);
    setError('');
    try {
      let customer = null;
      if (leadLiveMode()) {
        const response = await leadRequest('convert', mode === 'existing'
          ? { leadId: lead.id, existingCustomerId }
          : { leadId: lead.id, defaultDeliveryMethod, tier, terms });
        if (response.approvalTask) RP.upsertTask(response.approvalTask);
        customer = RP.upsertCustomer({ ...response.customer, skipCreds: true, persisted: true });
        await rpRefreshAfterMutation(mode === 'existing' ? `${lead.name} linked` : `${customer.name} converted`);
      } else if (mode === 'existing') {
        customer = selectedCustomer;
        if (!customer) throw new Error('Choose a customer to link.');
        LA().advanceStage(lead.id, 'won', { convertedTo: customer.id, nextAction: null });
      } else {
        customer = LA().convertLead(lead.id, { defaultDeliveryMethod, tier, terms });
      }
      if (customer) onConverted && onConverted(customer, mode);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not convert lead.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Convert Lead" sub={lead.name} onClose={onClose} width={620}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !canConvert} style={{ opacity: busy || !canConvert ? 0.55 : 1 }} onClick={convert}><Icon name="check" size={15} />{busy ? 'Converting...' : mode === 'existing' ? 'Link Customer' : 'Create Prospect'}</button></>}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button type="button" onClick={() => setMode('existing')} style={{ flex: 1, border: '1px solid ' + (mode === 'existing' ? 'var(--amber)' : 'var(--line)'), background: mode === 'existing' ? 'var(--amber-soft)' : 'var(--surface)', color: mode === 'existing' ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 800 }}>Link existing customer</button>
        <button type="button" onClick={() => setMode('new')} style={{ flex: 1, border: '1px solid ' + (mode === 'new' ? 'var(--amber)' : 'var(--line)'), background: mode === 'new' ? 'var(--amber-soft)' : 'var(--surface)', color: mode === 'new' ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 800 }}>Create new prospect</button>
      </div>

      {mode === 'existing' ? <>
        {duplicateMatches.length > 0 && <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.4, marginBottom: 14 }}>
          Possible existing account: {duplicateMatches.slice(0, 2).map(match => match.customer.name).join(', ')}.
        </div>}
        <MField label="Find Customer">
          <input className="field" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, city, territory, email..." />
        </MField>
        <MField label="Customer to Link" required>
          <select className="field" value={existingCustomerId} onChange={e => setExistingCustomerId(e.target.value)}>
            <option value="">Choose an existing customer...</option>
            {options.map(customer => <option key={customer.id} value={customer.id}>{customer.name} - {customer.territory} - {customer.type}</option>)}
          </select>
        </MField>
        {selectedCustomer && <div className="card" style={{ padding: 13, marginBottom: 14 }}>
          <div style={{ fontWeight: 800 }}>{selectedCustomer.name}</div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 3 }}>{selectedCustomer.type} - {selectedCustomer.tier || 'No tier'} - {selectedCustomer.status || 'No status'}</div>
          <div style={{ color: 'var(--faint)', fontSize: 12, marginTop: 3 }}>{selectedCustomer.city || selectedCustomer.territory}{selectedCustomer.email ? ` - ${selectedCustomer.email}` : ''}</div>
        </div>}
        <InfoNote icon="link">This marks the lead won and points it at the existing customer. If that account is missing a ship-to address, this lead's address fills it in.</InfoNote>
      </> : <>
        <MField label="Price List / Tier">
          <select className="field" value={tier} disabled={!canSetCustomAccountPricing} onChange={e => setTier(e.target.value)}>
            {rpAccountPriceListOptionNames({ currentName: tier, excludeConsumer: true }).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}
          </select>
        </MField>
        <MField label="Payment Terms">
          <select className="field" value={terms} onChange={e => setTerms(e.target.value)}>
            {['Prepaid', 'COD', 'Net 30'].map(t => <option key={t}>{t}</option>)}
          </select>
        </MField>
        <MField label="Default Delivery">
          <select className="field" value={defaultDeliveryMethod} onChange={e => setDefaultDeliveryMethod(e.target.value)}>
            {(window.RP_DELIVERY_METHOD_OPTIONS || ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight']).map(method => <option key={method}>{method}</option>)}
          </select>
        </MField>
        {deliveryNeedsAddress && !String(lead.address || '').trim() && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, marginBottom: 14 }}>
          This prospect will need a ship-to address before delivery orders can be submitted. Choose Customer Pickup if they should be able to order without one.
        </div>}
        <InfoNote icon="user">A new prospect account and approval task will be created. The lead address becomes the default ship-to address when present.</InfoNote>
      </>}
      {error && <ReqMsg show>{error}</ReqMsg>}
    </Modal>
  );
}

function DesktopLeadModal({ onClose, onAdded }) {
  const L = RP.LEADS_API;
  const defaultTerritory = (RP.TERRITORIES || [])[0] || '';
  const accountDefaultTier = rpRetailAccountDefaultPriceListName();
  const canSetCustomAccountPricing = rpCanSetCustomAccountPricing();
  const [f, setF] = useState({ address: '', city: '', name: '', type: 'Retailer', tier: accountDefaultTier, defaultDeliveryMethod: 'Ranch Delivery', postal: '', territory: defaultTerritory, temp: 'Warm', source: 'Rep idea', potential: 10, contact: '', email: '', phone: '', notes: '' });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const name = f.name.trim();
  const email = f.email.trim().toLowerCase();
  const emailOk = !email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const potential = Math.max(1, Math.round(Number(f.potential || 0)));
  const deliveryNeedsAddress = typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(f.defaultDeliveryMethod) : f.defaultDeliveryMethod !== 'Customer Pickup';
  const canSave = name && f.territory && emailOk && Number.isFinite(potential) && potential > 0 && !busy;

  const save = async () => {
    setTried(true);
    setError('');
    if (!canSave) return;
    setBusy(true);
    try {
      let lead;
      const notes = desktopLeadNotesWithDeliveryPreference(f.notes, f.defaultDeliveryMethod);
      if (leadLiveMode()) {
        const response = await leadRequest('create', {
          address: f.address,
          city: f.city || `${f.territory}, BC`,
          contact: f.contact,
          email,
          name,
          notes,
          phone: f.phone,
          postal: f.postal,
          potential,
          source: f.source,
          temp: f.temp,
          tier: f.tier,
          territory: f.territory,
          type: f.type
        });
        lead = response.lead;
        await rpRefreshAfterMutation(`${lead.name} added`);
      } else {
        lead = L.addLead({ ...f, address: f.address.trim(), city: f.city.trim() || `${f.territory}, BC`, email, name, notes, postal: f.postal.trim(), potential, rep: rpDisplayName() });
      }
      toast(`Lead "${lead.name}" added`, 'ok');
      onAdded && onAdded(lead);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create lead.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add Lead" sub="Create an operations-visible sales pipeline lead" onClose={onClose} width={620}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!canSave} style={{ opacity: canSave ? 1 : 0.55 }} onClick={save}><Icon name="plus" size={15} />{busy ? 'Adding...' : 'Add Lead'}</button></>}>
      <MField label="Business Name" required>
        <input className="field" placeholder="e.g. Sea to Sky Bottle Co." value={f.name} onChange={e => set('name', e.target.value)} style={reqStyle(tried && !name)} />
        <ReqMsg show={tried && !name}>Lead name is required.</ReqMsg>
      </MField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <MField label="Type">
          <select className="field" value={f.type} onChange={e => set('type', e.target.value)}>
            {(window.RP_LEAD_TYPES || ['Retailer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor', 'Other']).map(type => <option key={type}>{type}</option>)}
          </select>
        </MField>
        <MField label="Retailer Type / Price List">
          <select className="field" value={f.tier} disabled={!canSetCustomAccountPricing} onChange={e => set('tier', e.target.value)}>
            {rpAccountPriceListOptionNames({ currentName: f.tier, excludeConsumer: true }).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}
          </select>
        </MField>
        <MField label="Territory" required>
          <select className="field" value={f.territory} onChange={e => set('territory', e.target.value)} style={reqStyle(tried && !f.territory)}>
            {!defaultTerritory && <option value="">No territories configured</option>}
            {(RP.TERRITORIES || []).map(territory => <option key={territory}>{territory}</option>)}
          </select>
          <ReqMsg show={tried && !f.territory}>Territory is required.</ReqMsg>
        </MField>
        <MField label="Temperature">
          <select className="field" value={f.temp} onChange={e => set('temp', e.target.value)}>
            {['Hot', 'Warm', 'Cold'].map(temp => <option key={temp}>{temp}</option>)}
          </select>
        </MField>
        <MField label="Source">
          <select className="field" value={f.source} onChange={e => set('source', e.target.value)}>
            {['Rep idea', 'Referral', 'Walk-in', 'Event', 'Inbound', 'Operations'].map(source => <option key={source}>{source}</option>)}
          </select>
        </MField>
        <MField label="Default Delivery">
          <select className="field" value={f.defaultDeliveryMethod} onChange={e => set('defaultDeliveryMethod', e.target.value)}>
            {(window.RP_DELIVERY_METHOD_OPTIONS || ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight']).map(method => <option key={method}>{method}</option>)}
          </select>
        </MField>
      </div>
      {deliveryNeedsAddress && !f.address.trim() && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, marginBottom: 14 }}>
        Delivery accounts need a ship-to address before orders can be submitted. Choose Customer Pickup if this lead should be allowed to order without one.
      </div>}
      <MField label="Street Address">
        <input className="field" placeholder="e.g. 38005 Cleveland Ave" value={f.address} onChange={e => set('address', e.target.value)} />
      </MField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <MField label="City">
          <input className="field" placeholder="e.g. Squamish" value={f.city} onChange={e => set('city', e.target.value)} />
        </MField>
        <MField label="Postal">
          <input className="field" placeholder="V8B 0A1" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} />
        </MField>
      </div>
      <MField label="Estimated Potential">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <input type="range" min="2" max="120" step="2" value={f.potential} onChange={e => set('potential', Number(e.target.value))} style={{ flex: 1, accentColor: 'var(--amber)' }} />
          <input className="field" type="number" min="1" step="1" value={f.potential} onChange={e => set('potential', e.target.value)} style={Object.assign({ width: 100 }, reqStyle(tried && (!Number.isFinite(potential) || potential <= 0)))} />
          <span className="mono" style={{ color: 'var(--muted)', fontWeight: 700 }}>cs/mo</span>
        </div>
        <ReqMsg show={tried && (!Number.isFinite(potential) || potential <= 0)}>Enter a positive case estimate.</ReqMsg>
      </MField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <MField label="Contact">
          <input className="field" placeholder="Name / role" value={f.contact} onChange={e => set('contact', e.target.value)} />
        </MField>
        <MField label="Phone">
          <input className="field" type="tel" placeholder="Phone" value={f.phone} onChange={e => set('phone', e.target.value)} />
        </MField>
      </div>
      <MField label="Email">
        <input className="field" type="email" placeholder="buyer@example.com" value={f.email} onChange={e => set('email', e.target.value)} style={reqStyle(tried && !emailOk)} />
        <ReqMsg show={tried && !emailOk}>Enter a valid email address or leave it blank.</ReqMsg>
      </MField>
      <MField label="Notes">
        <textarea className="field" rows="3" placeholder="What to know before the first touch..." value={f.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />
      </MField>
      <InfoNote icon="trend">New leads start in Idea with a first-contact follow-up and are available immediately in Operations and Sales after refresh.</InfoNote>
      {error && <ReqMsg show>{error}</ReqMsg>}
    </Modal>
  );
}

Object.assign(window, { PipelineView, LeadDrawer, DesktopLeadModal });


/* ===== desktop_supply.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Production · Procurement · FBA · Admin
   ============================================================ */

/* ============================================================ PRODUCTION */
function rpProductionSku(run) {
  return (run && RP.sku(run.sku)) || {
    id: run && run.sku,
    flavor: (run && run.flavor) || 'ginger-citrus',
    name: (run && (run.sku || run.flavor)) || 'Production SKU'
  };
}

function rpProductionNextStatus(status) {
  const map = {
    Draft: 'Scheduled',
    Scheduled: 'Inputs Allocated',
    'Inputs Allocated': 'In Production',
    'In Production': 'Produced',
    Produced: 'QA Hold',
    'QA Hold': 'Released',
    Released: 'Closed'
  };
  return map[status] || '';
}

function rpProductionStatusLabel(status) {
  return status === 'Inputs Allocated' ? 'Inputs Checked' : status;
}

function rpProductionOutputCases(run) {
  return run && run.output != null
    ? run.output / (RP.FINISHED_GOODS_CASE_PACK_SIZE || 12)
    : Number(run && run.cases) || 0;
}

function rpProductionActionLabel(status) {
  if (status === 'Scheduled') return 'Check Inputs';
  if (status === 'QA Hold') return 'Release to Sellable';
  if (status === 'Released') return 'Close Run';
  return 'Advance Status';
}

function rpProductionLocation(run) {
  const id = run && run.outputLocationId;
  const location = id && RP.loc ? RP.loc(id) : null;
  return (location && location.name) || id || 'Default warehouse';
}

const PRODUCTION_DOC_DEFS = [
  { type: 'coa', label: 'Certificate of Analysis' },
  { type: 'qa_report', label: 'QA Report' },
  { type: 'batch_record', label: 'Batch Record' }
];

function rpProductionQtyLabel(value, unit) {
  const qty = Number(value) || 0;
  if (!qty) return '-';
  return qty.toLocaleString(undefined, { maximumFractionDigits: 3 }) + (unit ? ` ${unit}` : '');
}

function rpProductionToday() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Edmonton', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const byType = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

function rpCanSourceReconciliationHold(canRunId) {
  return (RP.CAN_SOURCE_RECONCILIATION_HOLDS || []).find(row => row.canRunId === canRunId && !row.countId) || null;
}

function rpCanCountManagementAllowed() {
  return ['admin', 'leadership', 'ops'].includes(String(rpAuthProfile()?.role || '').toLowerCase());
}
function rpPackagingHistoryProtected(run) {
  return (run.allocations || []).some(allocation => (RP.CAN_SOURCE_RECONCILIATION_HOLDS || []).some(hold => hold.canRunId === allocation.canRunId && (!hold.countedAt || !run.createdAt || run.createdAt <= hold.countedAt)));
}
function CanSourceCountModal({ run, onClose, onSaved }) {
  const key = `rp-can-source-count:v1:${rpAuthProfile()?.id || 'unknown'}:${run.id}`;
  const [pending, setPending] = useState(() => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (_) { return null; } });
  const [remaining, setRemaining] = useState('');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const count = Number(remaining);
  const valid = remaining.trim() !== '' && Number.isInteger(count) && count >= 0 && count <= 1000000 && reason.trim().length >= 10 && evidence.trim().length >= 3 && confirmed && preview;
  const review = async () => {
    setBusy(true); setError(''); setConfirmed(false);
    try { setPreview(await rpApi(`/api/production/can-reconciliation?runId=${encodeURIComponent(run.id)}`)); }
    catch (err) { setError(err.message); setPreview(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (!pending) review(); }, []);
  const save = async () => {
    if (busy || (!pending && !valid)) return;
    const request = pending || { id: crypto.randomUUID(), runId: run.id, remainingCans: count, reason: reason.trim(), evidence: evidence.trim(), snapshotHash: preview.snapshotHash, confirmed: true };
    try {
      localStorage.setItem(key, JSON.stringify(request));
    } catch (_) { setError('This browser could not preserve the count request for safe retry. Enable local storage before confirming.'); return; }
    setPending(request); setBusy(true); setError('');
    try {
      const response = await rpApi('/api/production/can-reconciliation', { method: 'POST', body: JSON.stringify(request) });
      if (!response.accepted || response.receipt?.id !== request.id || response.receipt.can_run_id !== request.runId || Number(response.receipt.remaining_cans) !== request.remainingCans) throw new Error('The matching count receipt was not returned. Retry the same count.');
      await rpRefreshAfterMutation('Loose-can count reconciled');
      localStorage.removeItem(key); setPending(null);
      onSaved(); onClose();
    } catch (err) {
      if (err.payload?.reviewAgain) { localStorage.removeItem(key); setPending(null); setPreview(null); setConfirmed(false); }
      setError(err.message || 'Could not confirm the count. Retry the same request.');
    } finally { setBusy(false); }
  };
  return <Modal title="Reconcile remaining loose cans" sub={`${run.id} · ${rpSkuDisplayName(rpSkuItem(run.canSkuId) || 'Unknown product')}`} width={720} onClose={onClose}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>{pending ? 'Keep pending and close' : 'Cancel'}</button>{!pending && <button className="btn" disabled={busy} onClick={review}>Refresh source review</button>}<button className="btn btn-primary" disabled={busy || rpIsDemoMode() || (!pending && !valid)} onClick={save}>{busy ? 'Working…' : pending ? 'Recover same count' : 'Confirm remaining cans'}</button></>}>
    <InfoNote icon="info">Enter the loose cans physically remaining at BevMax now, confirmed by a count or a current BevMax report. Do not copy the calculated unallocated number without checking it. This sets the balance available for future packing and keeps the older production and allocation records in history. It does not add cases or resolve unknown historical batch usage.</InfoNote>
    {preview && <p>Calculated remainder: <strong>{Number(preview.balance.calculated_remaining).toLocaleString()} cans</strong>. {preview.balance.count_id ? `Last verified balance remaining: ${Number(preview.balance.verified_remaining).toLocaleString()} cans.` : 'No physical remaining count has been recorded.'}</p>}
    {pending ? <p role="status">Recover the saved count of <strong>{pending.remainingCans} cans</strong> before starting another correction. The request is preserved across refreshes.</p> : <>
      <MField label="Verified loose cans remaining" required><input className="field" type="number" min="0" max="1000000" step="1" value={remaining} onChange={e => { setRemaining(e.target.value); setConfirmed(false); }} /></MField>
      <p style={{fontSize:12,color:'var(--muted)'}}>Enter 0 only when the count confirms no loose cans remain. A blank field makes no adjustment.</p>
      <MField label="Reason for reconciliation" required><textarea className="field" maxLength={1000} value={reason} onChange={e => { setReason(e.target.value); setConfirmed(false); }} /></MField>
      <MField label="How did you confirm this count?" required><input className="field" placeholder="e.g. Wade counted it on Sept 23" maxLength={2000} value={evidence} onChange={e => { setEvidence(e.target.value); setConfirmed(false); }} /></MField>
      <label style={{display:'flex',gap:10,marginTop:14}}><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />I verified this physical remaining count for this exact can batch. It is current and excludes cans already packed into cases.</label>
    </>}
    {error && <p role="alert" style={{color:'var(--danger)'}}>{error}</p>}
  </Modal>;
}

function rpHistoricalPackingReport(packaging) {
  const confirmations = [...new Map((RP.HISTORICAL_PACKING_CONFIRMATIONS || []).map(row => [row.id, row])).values()].map(row => {
    const edited = (row.coveredRunIds || []).length === 1 && packaging.find(run => run.id === row.coveredRunIds[0] && run.recordEditId);
    return edited ? {...row, totalCases: rpPackingCases(edited)} : row;
  });
  const coveredRunIds = new Set(confirmations.flatMap(row => row.coveredRunIds || []));
  const confirmedCases = confirmations.reduce((sum, row) => sum + Number(row.totalCases || 0), 0);
  const uncoveredRuns = packaging.filter(run => run.status !== 'Reversed' && !coveredRunIds.has(run.id));
  const otherCases = uncoveredRuns.reduce((sum, run) => sum + rpPackingCases(run), 0);
  return { confirmations, confirmedCases, otherCases, uncoveredRuns, totalCases: confirmedCases + otherCases };
}

function rpPackingCases(run) {
  if (run.displayCases != null) return Number(run.displayCases);
  const confirmed = (RP.HISTORICAL_PACKING_CONFIRMATIONS || []).find(row => row.coveredRunIds?.length === 1 && row.coveredRunIds[0] === run.id && run.status !== 'Reversed');
  return Number(confirmed ? confirmed.totalCases : run.cases || 0);
}

function rpPackingDate(run) {
  if (Object.prototype.hasOwnProperty.call(run, 'displayPackedOn')) return run.displayPackedOn || '';
  const confirmed = (RP.HISTORICAL_PACKING_CONFIRMATIONS || []).find(row => row.coveredRunIds?.length === 1 && row.coveredRunIds[0] === run.id && run.status !== 'Reversed');
  return confirmed ? confirmed.packedOn || '' : run.packedOn || '';
}

function PackagingEditModal({run, onClose, onSaved, onStockCorrection, onSourceCorrection, onLotDetails, onInventory}) {
  const key = `rp-packaging-edit:${rpAuthProfile().id}:${run.id}`;
  const [stored] = useState(() => { try { const request=JSON.parse(localStorage.getItem(key) || 'null'); if(request && (request.runId!==run.id || !Number.isInteger(request.cases) || !/^[a-f0-9]{64}$/.test(request.snapshotHash || '') || typeof request.reason!=='string')) throw new Error('Invalid saved edit');return {request}; } catch { return {error:'The saved edit cannot be read. Review History before another change.'}; } });
  const [pending, setPending] = useState(stored.request || null);
  const [source, setSource] = useState(null), [busy, setBusy] = useState(!pending && !stored.error), [error, setError] = useState(stored.error || '');
  const [f, setF] = useState({cases:String(pending ? pending.cases : rpPackingCases(run)),packedOn:pending ? pending.packedOn || '' : rpPackingDate(run),notes:pending ? pending.notes : run.displayNotes ?? run.notes ?? '',reason:pending ? pending.reason : ''});
  const [reviewed, setReviewed] = useState(false), [identityIssue, setIdentityIssue] = useState(false);
  const set = (name, value) => {setF(old => ({...old,[name]:value}));setReviewed(false);setError('');};
  const load = async () => {
    if (pending || stored.error) return;
    setBusy(true);setError('');setReviewed(false);
    try {
      const result = await rpApi(`/api/production/packaging-edit?runId=${encodeURIComponent(run.id)}`);
      setSource(result);
      setF(old => ({...old,cases:String(result.record.display_case_quantity),packedOn:result.record.display_packaged_at || '',notes:result.record.display_notes || ''}));
    } catch(err) {setError(err.message || 'Could not load the packing record.');}
    finally {setBusy(false);}
  };
  useEffect(() => {load();}, []);
  const review = () => {
    if (!source) {setError('Load the current record before reviewing changes.');return;}
    if (!/^\d+$/.test(f.cases) || Number(f.cases)>1000000) {setError('Enter a whole case total from 0 to 1,000,000.');return;}
    if (f.reason.trim().length<3) {setError('Enter a short reason for this edit.');return;}
    setError('');setReviewed(true);
  };
  const save = async () => {
    if (busy || stored.error || (!pending && !reviewed) || rpIsDemoMode()) return;
    const request = pending || {id:crypto.randomUUID(),runId:run.id,cases:Number(f.cases),packedOn:f.packedOn || null,notes:f.notes,reason:f.reason.trim(),snapshotHash:source.snapshotHash,confirmed:true};
    setBusy(true);setError('');
    try {
      localStorage.setItem(key,JSON.stringify(request));setPending(request);
      const result = await rpApi('/api/production/packaging-edit',{method:'POST',body:JSON.stringify(request)});
      if (result?.accepted !== true || result.receipt?.id !== request.id || result.receipt?.run_id !== run.id) throw new Error('Save receipt was not confirmed. Retry the same save.');
      if (await rpRefreshAfterMutation('Packing record updated') === false) throw new Error('The edit was saved, but the list could not refresh. Retry the same save to reload it.');
      localStorage.removeItem(key);setPending(null);onSaved();onClose();
    } catch(err) {
      if ([400,403,404,409].includes(err.status)) {localStorage.removeItem(key);setPending(null);setReviewed(false);setSource(null);}
      setError(err.message || 'Save not confirmed. Retry the same save.');
    } finally {setBusy(false);}
  };
  return <Modal title="Edit packing" sub={`${rpSkuDisplayName(RP.sku(run.outputSkuId) || run.outputSkuId)} · ${rpPrintedLotLabel(run.outputSkuId,run.lot)}`} onClose={() => !busy && onClose()} width={650}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button>{pending ? <button className="btn btn-primary" disabled={busy} onClick={save}>Retry same save</button> : <button className="btn btn-primary" disabled={busy || !!stored.error || rpIsDemoMode()} onClick={reviewed ? save : review}>{busy ? 'Working…' : reviewed ? 'Save changes' : 'Review changes'}</button>}</>}>
    <fieldset disabled={busy || !!pending} style={{border:0,padding:0,margin:0,minWidth:0}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,220px),1fr))',gap:14}}>
        <MField label="Cases packed" required><input className="field" type="number" min="0" max="1000000" step="1" value={f.cases} onChange={e => set('cases',e.target.value)} /></MField>
        <MField label="Packed date"><input className="field" type="date" value={f.packedOn} onChange={e => set('packedOn',e.target.value)} /></MField>
      </div>
      <MField label="Notes"><textarea className="field" rows="2" maxLength={4000} value={f.notes} onChange={e => set('notes',e.target.value)} /></MField>
      <MField label="Reason for edit" required><input className="field" maxLength={1000} value={f.reason} onChange={e => set('reason',e.target.value)} /></MField>
      {!reviewed && <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:14}}>
        <button className="btn btn-sm" onClick={onLotDetails}>Edit lot / expiry</button>
        <button className="btn btn-sm" onClick={() => setIdentityIssue(true)}>Change product</button>
        <button className="btn btn-sm" onClick={onSourceCorrection}>Edit source allocations</button>
      </div>}
    </fieldset>
    {identityIssue && <div role="alert" className="rp-edit-review">Changing the product also changes its recipe, source cans and stock identity. This record uses {run.recipeId} and {(run.allocations || []).map(a => a.canRunId).join(', ') || 'recorded source allocations'}. Review the stock correction before replacing the product.<button className="btn btn-sm" onClick={onStockCorrection}>Review affected stock</button></div>}
    {reviewed && <section className="rp-edit-review" aria-label="Change preview">
      <strong>Changes to this packing record</strong>
      <p>Cases: {Number(source.record.display_case_quantity).toLocaleString()} → {Number(f.cases).toLocaleString()}<br />Packed date: {source.record.display_packaged_at || 'Not confirmed'} → {f.packedOn || 'Not confirmed'}</p>
      <p>Warehouse quantities, source-can allocations and orders stay unchanged. Previous values stay in History.</p>
      <button className="btn btn-sm" onClick={onInventory}>Edit warehouse count instead</button>
    </section>}
    {error && <p role="alert" style={{color:'var(--danger)'}}>{error}</p>}
    {!source && !busy && !pending && !stored.error && <button className="btn" onClick={load}>Reload current record</button>}
  </Modal>;
}

function rpHistoricalPackingConfirmation(skuId, lot) {
  const key = String(lot || '').trim().toUpperCase();
  return key ? (RP.HISTORICAL_PACKING_CONFIRMATIONS || []).find(row => row.skuId === skuId
    && [row.lot, row.printedLot].some(value => String(value || '').trim().toUpperCase() === key)) || null : null;
}

function rpCanRunUi(row) {
  return {
    id: row.id,
    canSkuId: row.can_sku_id || row.canSkuId,
    producerLocationId: row.facility_location_id || row.producer_location_id || row.producerLocationId || '',
    invoiceDate: row.invoice_date || row.invoiceDate || '',
    productionDate: row.production_date || row.productionDate || '',
    productionDateBasis: row.date_basis || row.production_date_basis || row.productionDateBasis || 'invoice_estimate',
    totalCans: Number(row.produced_cans == null ? (row.total_cans == null ? row.totalCans : row.total_cans) : row.produced_cans) || 0,
    allocatedCans: Number(row.allocated_cans == null ? row.allocatedCans : row.allocated_cans) || 0,
    availableCans: Number(row.available_cans == null ? row.availableCans : row.available_cans) || 0,
    countStatus: row.count_status || row.countStatus || 'reported',
    notes: row.notes || '',
    revision: Number(row.current_revision || row.revision) || 1,
    calculatedRemaining: row.calculatedRemaining == null ? Number(row.available_cans ?? row.availableCans) || 0 : Number(row.calculatedRemaining),
    countId: row.countId || null,
    sourceHold: rpCanSourceReconciliationHold(row.id)
  };
}

function CanProductionRunModal({ run, onClose, onSaved, onCount }) {
  const existing = run && run.id ? rpCanRunUi(run) : null;
  const canSkus = (RP.SKUS || []).filter(s => s && s.active !== false && (s.unit === 'can' || /^RP-CAN-/.test(s.id || '')));
  const bevmax = (RP.LOCATIONS || []).find(l => l && l.active !== false && (/BEVMAX-AIRDRIE/i.test(l.facilityCode || '') || /bevmax/i.test(`${l.id || ''} ${l.name || ''} ${l.organization || ''}`))) || {};
  const [f, setF] = useState({
    id: existing ? existing.id : '',
    canSkuId: existing ? existing.canSkuId : ((canSkus[0] || {}).id || ''),
    invoiceDate: existing ? existing.invoiceDate : rpProductionToday(),
    productionDate: existing ? existing.productionDate : rpProductionToday(),
    productionDateBasis: existing ? existing.productionDateBasis : 'invoice_estimate',
    totalCans: existing ? String(existing.totalCans) : '',
    countStatus: existing ? existing.countStatus : 'reported',
    notes: existing ? existing.notes : '',
    correctionReason: ''
  });
  const [tried, setTried] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const totalCans = Number(f.totalCans);
  const valid = /^[A-Z0-9][A-Z0-9._:-]{0,99}$/.test(f.id) && f.canSkuId && f.invoiceDate && f.productionDate
    && Number.isInteger(totalCans) && totalCans > 0 && (!existing || f.correctionReason.trim());
  const set = (key, value) => {setReviewed(false);setError('');setF(current => ({ ...current, [key]: value }));};
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    if (existing && (f.id !== existing.id || f.canSkuId !== existing.canSkuId)) {
      const linked = (RP.PACKAGING_RUNS || []).filter(row => (row.allocations || []).some(a => a.canRunId === existing.id));
      setError(`Changing this batch identity would disconnect its revisions${linked.length ? ' and packing records ' + linked.map(row => row.id).join(', ') : ''}. Its ID and product cannot be reassigned through a production-detail edit. Review the linked packing records before replacing this batch.`);return;
    }
    if (existing && totalCans !== existing.totalCans && (RP.CAN_SOURCE_RECONCILIATION_HOLDS || []).some(h => h.canRunId === existing.id)) {
      setError(`This batch has ${existing.allocatedCans.toLocaleString()} cans linked to historical packing. Changing its produced total would alter that unresolved balance. Use Edit remaining cans to enter today's verified count; production dates and notes can still be saved here.`);return;
    }
    if (existing && !reviewed) {setReviewed(true);return;}
    setBusy(true);
    try {
      const response = await rpApi('/api/production/can-run', {
        method: 'POST',
        body: JSON.stringify({
          id: f.id,
          canSkuId: f.canSkuId,
          producerLocationId: existing ? existing.producerLocationId : bevmax.id,
          invoiceDate: f.invoiceDate,
          productionDate: f.productionDate,
          productionDateBasis: f.productionDateBasis,
          totalCans,
          countStatus: f.countStatus,
          notes: f.notes.trim() || undefined,
          expectedRevision: existing ? existing.revision : 0,
          correctionReason: existing ? f.correctionReason.trim() : undefined
        })
      });
      await rpRefreshAfterMutation(`${f.id} can run saved`);
      onSaved && onSaved(response.run);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save the can run.');
    } finally {
      setBusy(false);
    }
  };
  return <Modal title={existing ? `Correct ${existing.id}` : 'Add BevMax Can Run'} sub="Authoritative loose cans produced before case packing" onClose={onClose} width={600}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name="check" size={15} />{busy ? 'Saving...' : existing ? reviewed ? 'Save changes' : 'Review changes' : 'Record Can Run'}</button></>}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      <MField label="BevMax Run ID" required><input className="field mono" value={f.id} onChange={e => set('id', e.target.value.toUpperCase().replace(/[^A-Z0-9._:-]/g, ''))} placeholder="RP-BR-001" style={reqStyle(tried && !f.id)} /></MField>
      <MField label="Can SKU" required><select className="field" value={f.canSkuId} onChange={e => set('canSkuId', e.target.value)}>{canSkus.map(s => <option key={s.id} value={s.id}>{rpSkuDisplayName(s)} - {rpSkuExternalIdentity(s)}</option>)}</select></MField>
      <MField label="BevMax Invoice Date" required><input className="field" type="date" value={f.invoiceDate} onChange={e => set('invoiceDate', e.target.value)} />{existing && <div style={{ marginTop: 5, color: 'var(--muted)', fontSize: 11.5 }}>Correct this date from the invoice. Your reason, the old date, and the new date stay in Production History.</div>}</MField>
      <MField label="Best-Known Production Date" required><input className="field" type="date" value={f.productionDate} onChange={e => set('productionDate', e.target.value)} /></MField>
      <MField label="Date Confidence"><select className="field" value={f.productionDateBasis} onChange={e => set('productionDateBasis', e.target.value)}><option value="invoice_estimate">Invoice-date estimate</option><option value="bevmax_reported">Reported by BevMax</option><option value="bevmax_confirmed">Confirmed by BevMax</option></select></MField>
      <MField label="Can Count Confidence"><select className="field" value={f.countStatus} onChange={e => set('countStatus', e.target.value)}><option value="reported">BevMax reported</option><option value="verified">Verified count</option><option value="estimated">Estimated / count pending</option></select></MField>
    </div>
    <MField label="Total Cans Produced" required><input className="field mono" type="number" min="1" step="1" value={f.totalCans} onChange={e => set('totalCans', e.target.value)} style={reqStyle(tried && !(Number.isInteger(totalCans) && totalCans > 0))} /><ReqMsg show={tried && !(Number.isInteger(totalCans) && totalCans > 0)}>Enter the whole-can total from BevMax.</ReqMsg></MField>
    {existing && onCount && (RP.CAN_SOURCE_RECONCILIATION_HOLDS || []).some(h => h.canRunId === existing.id) && <button className="btn btn-sm" onClick={() => onCount(existing)}>Edit remaining cans</button>}
    <MField label="Notes"><textarea className="field" rows="2" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Invoice, count, or production-date context" /></MField>
    {existing && reviewed && <section className="rp-edit-review" aria-label="Can edit impact"><strong>Change preview</strong><p>Produced cans: {existing.totalCans.toLocaleString()} → {totalCans.toLocaleString()}<br />Production date: {existing.productionDate} → {f.productionDate}</p><p>{existing.allocatedCans.toLocaleString()} cans already allocated. This changes the production record and calculated remainder; it does not move cases or change orders.</p></section>}
    {existing && <MField label="Correction Reason" required><textarea className="field" rows="2" value={f.correctionReason} onChange={e => set('correctionReason', e.target.value)} placeholder="What evidence changed and why" style={reqStyle(tried && !f.correctionReason.trim())} /><ReqMsg show={tried && !f.correctionReason.trim()}>Every edit creates an audit revision and requires a reason.</ReqMsg></MField>}

    {error && <ReqMsg show>{error}</ReqMsg>}
  </Modal>;
}

function rpPackagingAllocationPlan(recipe, cases, facilityId, allowCounted = true) {
  const canRuns = (RP.CAN_PRODUCTION_RUNS || []).map(rpCanRunUi);
  const allocations = [];
  const shortages = [];
  (recipe && recipe.components || []).forEach(component => {
    let remaining = cases * Number(component.cansPerCase || 0);
    canRuns.filter(run => !run.sourceHold && (allowCounted || !run.countId) && run.canSkuId === component.canSkuId && run.availableCans > 0 && (!facilityId || run.producerLocationId === facilityId))
      .sort((a, b) => `${a.productionDate}|${a.id}`.localeCompare(`${b.productionDate}|${b.id}`))
      .forEach(run => {
        if (remaining <= 0) return;
        const cansAllocated = Math.min(remaining, run.availableCans);
        if (cansAllocated > 0) allocations.push({ canRunId: run.id, canSkuId: component.canSkuId, cansAllocated });
        remaining -= cansAllocated;
      });
    if (remaining > 0) shortages.push({ canSkuId: component.canSkuId, shortage: remaining, required: cases * Number(component.cansPerCase || 0) });
  });
  return { allocations, shortages };
}

function PackagingRunModal({ inventoryMode, initialParentRun, onClose, onSaved, onReviewStock, onEditOriginal }) {
  const recipes = (RP.PACK_RECIPES || []).filter(r => r && r.active !== false && (r.components || []).length).sort((a, b) => rpSkuDisplayName(RP.sku(a.outputSkuId) || a.outputSkuId).localeCompare(rpSkuDisplayName(RP.sku(b.outputSkuId) || b.outputSkuId)));
  const bevmax = (RP.LOCATIONS || []).find(l => l && l.active !== false && (/BEVMAX-AIRDRIE/i.test(l.facilityCode || '') || /bevmax/i.test(`${l.id || ''} ${l.name || ''} ${l.organization || ''}`))) || {};
  const [ref] = useState(() => window.crypto && window.crypto.randomUUID ? `PKG-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}` : `PKG-${Date.now().toString(36).toUpperCase()}`);
  const [parentRun, setParentRun] = useState(initialParentRun || null);
  const [f, setF] = useState({ outputSkuId: initialParentRun ? initialParentRun.outputSkuId : (recipes[0] || {}).outputSkuId || '', lot: initialParentRun ? initialParentRun.lot : '', lotGroup: initialParentRun ? initialParentRun.lotGroup : '', packedOn: initialParentRun ? '' : rpProductionToday(), cases: '', reconciliationReference: '', notes: '', sessionReason: '', sessionEvidence: '' });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sourcesConfirmed, setSourcesConfirmed] = useState(false);
  const [customAllocations, setCustomAllocations] = useState(null);
  const [newSessionConfirmed, setNewSessionConfirmed] = useState(false);
  const [historyRun, setHistoryRun] = useState('');
  const storageKey = `rp-packaging-pending:${rpAuthProfile().id || 'demo'}`;
  const [recovery] = useState(() => {
    try {
      const request = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (request && (!/^PKG-[A-Z0-9-]+$/.test(request.id) || !Array.isArray(request.allocations) || !request.outputSkuId || !request.lot)) throw new Error('The saved packaging request needs review. Keep its browser record; do not create another run.');
      return { request, error: '' };
    } catch (err) { return { request: null, error: err.message || 'Recovery storage is unavailable.' }; }
  });
  const [pending, setPending] = useState(recovery.request);
  const set = (key, value) => { setSourcesConfirmed(false); setNewSessionConfirmed(false); if (['cases', 'outputSkuId'].includes(key)) setCustomAllocations(null); setF(current => ({ ...current, [key]: value })); };
  const recipe = parentRun ? recipes.find(r => r.outputSkuId === parentRun.outputSkuId && r.id === parentRun.recipeId) : recipes.filter(r => r.outputSkuId === f.outputSkuId).sort((a, b) => Number(b.recipeVersion) - Number(a.recipeVersion))[0];
  const cases = Number(f.cases);
  const facilityId = parentRun ? parentRun.outputLocationId : bevmax.id;
  const historical = inventoryMode === 'reconcile_existing';
  const suggested = rpPackagingAllocationPlan(recipe, Number.isInteger(cases) && cases > 0 ? cases : 0, facilityId, !historical);
  const facilityRuns = (RP.CAN_PRODUCTION_RUNS || []).map(rpCanRunUi).filter(run => run.producerLocationId === facilityId);
  const availableRuns = facilityRuns.filter(run => !run.sourceHold && (!historical || !run.countId) && run.availableCans > 0);
  const heldRuns = facilityRuns.filter(run => run.sourceHold && (recipe?.components || []).some(component => component.canSkuId === run.canSkuId));
  const allocations = customAllocations || suggested.allocations;
  const plan = { allocations, shortages: (recipe && recipe.components || []).flatMap(component => {
    const used = allocations.filter(row => row.canSkuId === component.canSkuId).reduce((sum, row) => sum + Number(row.cansAllocated || 0), 0);
    const required = cases * component.cansPerCase;
    return used === required ? [] : [{ canSkuId: component.canSkuId, shortage: required - used }];
  }) };
  const invalidSources = allocations.some(row => { const source = availableRuns.find(run => run.id === row.canRunId); return !source || source.canSkuId !== row.canSkuId || !Number.isInteger(Number(row.cansAllocated)) || Number(row.cansAllocated) <= 0 || Number(row.cansAllocated) > source.availableCans; });
  const setSource = (run, value) => { setSourcesConfirmed(false); setNewSessionConfirmed(false); setCustomAllocations([...allocations.filter(row => row.canRunId !== run.id), ...(value === '' || Number(value) === 0 ? [] : [{ canRunId: run.id, canSkuId: run.canSkuId, cansAllocated: Number(value) }])]); };
  const existingPrintedLot = rpExistingPrintedLotMessage(f.outputSkuId, f.lot);
  const conflict = rpPackagingLotConflict(f.outputSkuId, f.lot, historical);
  const additional = !!parentRun && !historical;
  const confirmedOriginal = rpHistoricalPackingConfirmation(f.outputSkuId, f.lot);
  const valid = !recovery.error && !invalidSources && (additional || (!conflict && !existingPrintedLot && !confirmedOriginal)) && sourcesConfirmed && !!recipe && !!bevmax.id && f.lot.trim() && f.lotGroup.trim() && f.packedOn && Number.isInteger(cases) && cases > 0 && !plan.shortages.length && plan.allocations.length && (!historical || (f.reconciliationReference.trim() && f.notes.trim())) && (!additional || (newSessionConfirmed && f.sessionReason.trim().length >= 10 && f.sessionEvidence.trim().length >= 3));
  const chooseAdditional = () => {
    const candidate = conflict && conflict.run;
    const root = candidate && (candidate.additionalParentRunId ? (RP.PACKAGING_RUNS || []).find(row => row.id === candidate.additionalParentRunId) : candidate);
    if (!root || root.inventoryMode !== 'post_to_inventory') return;
    setParentRun(root); setSourcesConfirmed(false); setNewSessionConfirmed(false); setCustomAllocations(null);
    setF(current => ({ ...current, lot: root.lot, lotGroup: root.lotGroup, cases: '', packedOn: '', sessionReason: '', sessionEvidence: '' }));
  };
  const save = async () => {
    setTried(true);
    setError('');
    if ((!pending && !valid) || busy || recovery.error) return;
    setBusy(true);
    try {
      const request = pending || {
          id: ref,
          outputSkuId: f.outputSkuId,
          outputLocationId: additional ? parentRun.outputLocationId : bevmax.id,
          lot: f.lot,
          lotGroup: f.lotGroup.trim(),
          packedOn: f.packedOn,
          cases,
          recipeVersion: recipe.recipeVersion,
          inventoryMode,
          ...(additional ? { additionalParentRunId: parentRun.id, sessionReason: f.sessionReason.trim(), sessionEvidence: f.sessionEvidence.trim() } : {}),
          reconciliationReference: historical ? f.reconciliationReference.trim() : undefined,
          notes: f.notes.trim() || undefined,
          allocations: plan.allocations
      };
      const otherPending = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (otherPending && otherPending.id !== request.id) throw new Error('Another packaging request is pending in this browser. Close and reopen Pack Cases to recover it.');
      localStorage.setItem(storageKey, JSON.stringify(request));
      if (localStorage.getItem(storageKey) !== JSON.stringify(request)) throw new Error('Recovery details could not be saved. No packaging request was sent.');
      setPending(request);
      const response = await rpApi('/api/production/packaging', { method: 'POST', body: JSON.stringify(request) });
      if (!response || !response.run || response.run.id !== request.id) throw new Error('The response did not confirm this packaging run. Recover the same request before recording another session.');
      await rpRefreshAfterMutation(`${request.id} packaging recorded`);
      localStorage.removeItem(storageKey); setPending(null);
      onSaved && onSaved(response.run);
      onClose();
    } catch (err) {
      if ([400, 403, 404, 409].includes(err.status)) { localStorage.removeItem(storageKey); setPending(null); setSourcesConfirmed(false); setNewSessionConfirmed(false); }
      setError(err && err.message ? err.message : 'Could not record packaging.');
    } finally {
      setBusy(false);
    }
  };
  if (pending) return <Modal title="Confirm Pending Packaging" sub={pending.id} onClose={() => !busy && onClose()} width={650} footer={<><button className="btn" disabled={busy} onClick={onClose}>Keep Pending and Close</button><button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Checking...' : 'Recover Same Packaging Request'}</button></>}><p>{pending.cases} cases · {pending.lot} · packed {pending.packedOn}</p><p>The request was sent or prepared to send. Its details are frozen so a lost response cannot create another packing session. Recover this same request before starting another.</p>{error && <ReqMsg show>{error}</ReqMsg>}</Modal>;
  if (historyRun) return <ProductionHistoryModal runId={historyRun} onClose={() => setHistoryRun('')} />;
  return <Modal title={additional ? 'Record Another Packing Session' : historical ? 'Reconcile Historical Packaging' : 'Record New Case Packaging'} sub={historical ? 'Allocate source cans without adding duplicate case inventory' : 'Consume exact source cans and place sealed cases in QA hold at BevMax'} onClose={() => !busy && onClose()} width={760}
    footer={<><div style={{ flex: 1, color: 'var(--muted)', fontSize: 12 }}>{cases > 0 ? `${(cases * 12).toLocaleString()} cans → ${cases.toLocaleString()} sealed cases` : ''}</div><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy || !valid} onClick={save}><Icon name="check" size={15} />{busy ? 'Recording...' : historical ? 'Record Without Inventory' : 'Post to QA Hold'}</button></>}>
    {historical ? <InfoNote icon="alert">This mode records how cans became cases but creates no inventory ledger quantity. Use it for Lot 1 or any packaging already represented by opening receipts, field receipts, or current balances. Current verified loose-can counts support new packing only and cannot fill missing historical allocations.</InfoNote> : <InfoNote icon="production">This mode creates new case inventory at BevMax in QA Hold. Release it before recording a transfer to Jori, Apps Cargo, Wade, or another custodian.</InfoNote>}
    {additional && <InfoNote icon="alert">Existing lot: {rpPrintedLotLabel(parentRun.outputSkuId, parentRun.lot)} · original run {parentRun.id}. Enter only cases physically packed in this additional session. Earlier sessions remain unchanged. This is not a way to correct their totals or dates.</InfoNote>}
    {!additional && confirmedOriginal && <div role="alert" style={{margin:'12px 0'}}><strong>{Number(confirmedOriginal.totalCases).toLocaleString()} original cases are already recorded.</strong> {onEditOriginal&&<button className="btn btn-sm" onClick={()=>onEditOriginal(confirmedOriginal)}>Edit original packing</button>}</div>}
    {!!heldRuns.length && !confirmedOriginal && <InfoNote icon="alert">Source reconciliation is pending for {heldRuns.map(row => row.id).join(', ')}. These runs cannot supply another packing session until their original allocations are reconciled. Their calculated unallocated totals are not verified loose-can counts. A separate, unheld can run remains available when supported by the actual packing evidence.</InfoNote>}
    <div className="rp-packaging-form-grid">
      <MField label="Finished Product" required><select className="field" disabled={additional || busy} value={f.outputSkuId} onChange={e => set('outputSkuId', e.target.value)}>{recipes.map(r => <option key={`${r.outputSkuId}-${r.recipeVersion}`} value={r.outputSkuId}>{rpSkuDisplayName(RP.sku(r.outputSkuId) || r.name)}</option>)}</select></MField>
      <MField label="Cases / Flats" required><input className="field mono" type="number" min="1" step="1" value={f.cases} onChange={e => set('cases', e.target.value)} style={reqStyle(tried && !(Number.isInteger(cases) && cases > 0))} /></MField>
      <MField label="Packed Date" required><input className="field" type="date" value={f.packedOn} onChange={e => set('packedOn', e.target.value)} /></MField>
      <MField label="Finished Goods Lot" required><input className="field mono" disabled={additional || busy} value={f.lot} onChange={e => set('lot', e.target.value.toUpperCase().replace(/[^A-Z0-9._:-]/g, ''))} placeholder="Exact BevMax lot" /></MField>
      <MField label="Lot Group" required><input className="field" disabled={additional || busy} value={f.lotGroup} onChange={e => set('lotGroup', e.target.value)} placeholder="Lot 1 / Lot 2" /></MField>
      <MField label="Recipe"><input className="field" disabled value={recipe ? `v${recipe.recipeVersion} · ${recipe.packSize} cans` : 'No active recipe'} /></MField>
    </div>
    <div className="card rp-packaging-allocation" style={{ overflowX: 'auto', margin: '8px 0 14px' }}>
      <table className="rp-table"><thead><tr><th>Recipe Component</th><th className="num">Per Case</th><th>Source Can Run(s)</th><th className="num">Required</th></tr></thead><tbody>
        {(recipe && recipe.components || []).map(component => {
          const shortage = plan.shortages.find(s => s.canSkuId === component.canSkuId);
          return <tr key={component.canSkuId}><td>{rpSkuDisplayName(RP.sku(component.canSkuId) || component.canSkuId)}</td><td className="num">{component.cansPerCase}</td><td>{availableRuns.filter(run => run.canSkuId === component.canSkuId).map(run => <label key={run.id} style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>{run.id} · {run.availableCans.toLocaleString()} available<input className="field mono" aria-label={`Cans from ${run.id}`} type="number" min="0" max={run.availableCans} step="1" value={(allocations.find(row => row.canRunId === run.id) || {}).cansAllocated || ''} onChange={e => setSource(run, e.target.value)} style={{ maxWidth: 150 }} /></label>)}{shortage && <span style={{ display: 'block', color: 'var(--danger)', fontSize: 11.5, fontWeight: 700 }}>{shortage.shortage > 0 ? 'Short' : 'Over by'} {Math.abs(shortage.shortage).toLocaleString()} cans</span>}</td><td className="num">{(cases > 0 ? cases * component.cansPerCase : 0).toLocaleString()}</td></tr>;
        })}
      </tbody></table>
    </div>
    {historical && <MField label="Prior Inventory / Case-Sheet Reference" required><input className="field mono" value={f.reconciliationReference} onChange={e => set('reconciliationReference', e.target.value.toUpperCase().replace(/[^A-Z0-9._:-]/g, ''))} placeholder="LOT1-CASE-SHEET or ledger / receipt ref" /></MField>}
    {additional && <><MField label="Why These Are Additional Cases" required><textarea className="field" rows="2" maxLength="1000" value={f.sessionReason} onChange={e => set('sessionReason', e.target.value)} placeholder="Explain this separate packing session, not a replacement total" /></MField><MField label="Packing Report / Evidence Reference" required><input className="field" maxLength="2000" value={f.sessionEvidence} onChange={e => set('sessionEvidence', e.target.value)} /></MField><label style={{ display: 'flex', gap: 10, margin: '14px 0' }}><input type="checkbox" checked={newSessionConfirmed} onChange={e => setNewSessionConfirmed(e.target.checked)} />These are additional physically packed cases that have not been recorded in any earlier session or stock receipt.</label></>}
    <MField label={historical ? 'Historical Evidence Note' : 'Packaging Note'} required={historical}><textarea className="field" rows="2" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder={historical ? 'Identify the case sheet, opening receipts, or inventory records this packaging explains.' : 'Optional BevMax packing or QA context'} /></MField>
    <label style={{ display: 'flex', gap: 10, margin: '14px 0' }}><input type="checkbox" checked={sourcesConfirmed} onChange={e => setSourcesConfirmed(e.target.checked)} />I checked every source run and quantity above against the actual packing report.</label>
    <p style={{ color: 'var(--muted)', fontSize: 12 }}>Sources are suggested oldest first. Adjust the cans from each run to match the actual report; use zero for a run that was not used. Available cans from the same source run can be used in later packing sessions. Do not invent another finished lot code to bypass an existing record.</p>
    {!additional && !confirmedOriginal && (conflict || existingPrintedLot) && <div role="alert"><ReqMsg show>{conflict ? conflict.message : existingPrintedLot}</ReqMsg><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>{conflict && conflict.run && <button className="btn btn-sm" onClick={() => setHistoryRun(conflict.run.id)}>View Existing Packaging History</button>}{!historical && conflict && conflict.run && conflict.run.inventoryMode === 'post_to_inventory' && <button className="btn btn-sm" onClick={chooseAdditional}>Record Another Packing Session</button>}{onReviewStock && <button className="btn btn-sm" onClick={onReviewStock}>Review Stock / Reconcile Count</button>}</div><p style={{ fontSize: 12 }}>For an incorrect physical balance, use Inventory → Reconcile Count. For dates or other lot details, use Lot Master. Corrections retain the original history.</p></div>}
    {tried && plan.shortages.length > 0 && <ReqMsg show>Not enough unallocated cans exist in the selected recipe components.</ReqMsg>}
    {tried && historical && (!f.reconciliationReference.trim() || !f.notes.trim()) && <ReqMsg show>Historical reconciliation requires both the prior record reference and an evidence note.</ReqMsg>}
    {error && <ReqMsg show>{error}</ReqMsg>}
    {recovery.error && <ReqMsg show>{recovery.error}</ReqMsg>}
    {invalidSources && <ReqMsg show>Each source quantity must be a whole number within that can run's available balance.</ReqMsg>}
  </Modal>;
}

function PackagingReverseModal({ run, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const valid = reason.trim().length >= 10 && confirmed;
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      await rpApi('/api/production/packaging-status', {
        method: 'POST',
        body: JSON.stringify({ id: run.id, expectedStatus: run.status, status: 'Reversed', note: reason.trim() })
      });
      await rpRefreshAfterMutation(`${run.id} reversed; its can allocations are available for a corrected packaging run.`);
      onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError && requestError.message ? requestError.message : 'Could not reverse the packaging run.');
    } finally {
      setBusy(false);
    }
  };
  return <Modal title="Reverse Packaging Run" sub={`${run.id} · ${rpSkuDisplayName(RP.sku(run.outputSkuId) || run.outputSkuId)} · ${Number(run.cases || 0).toLocaleString()} cases`} onClose={() => !busy && onClose()} width={620}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name="refresh" size={15} />{busy ? 'Reversing...' : 'Reverse Signed Run'}</button></>}>
    <InfoNote icon="alert">{run.inventoryMode === 'reconcile_existing' ? 'This cancels the historical source-can allocation. It does not remove any existing case inventory.' : `This removes ${Number(run.cases || 0).toLocaleString()} cases of lot ${run.lot} from the producer and makes the original source cans available again. It records a permanent reversal and fails if the required cases are no longer there.`} Use Close when the work is simply finished. Use Correct Lot for a mistaken lot code. An accidental reversal can be restored only after the system proves the original cans and stock have not been reused.</InfoNote>
    <MField label="Correction Reason" required><textarea className="field" rows="4" value={reason} onChange={e => setReason(e.target.value)} placeholder="Describe the evidence and what must be corrected (minimum 10 characters)." /></MField>
    <label style={{ display: 'flex', gap: 10, padding: '10px 0' }}><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />I intend to cancel this packaging and release its source cans.</label>
    {tried && !valid && <ReqMsg show>Enter a reason of at least 10 characters and confirm the effect on stock.</ReqMsg>}
    {error && <ReqMsg show>{error}</ReqMsg>}
  </Modal>;
}

function rpPendingProductionCorrection(runId) {
  for (const operation of ['restore', 'correct_lot', 'restore_with_lot', 'correct_plan']) {
    try {
      const request = JSON.parse(localStorage.getItem(`rp-production-correction:${rpAuthProfile().id || 'demo'}:${runId}:${operation}`) || 'null');
      if (request && request.runId === runId && request.operation === operation && request.correctionId) return request;
    } catch (_) {}
  }
  return null;
}

function PackagingCorrectionModal({ run, operation, onClose, onSaved }) {
  const restore = operation === 'restore';
  const combined = operation === 'restore_with_lot';
  const storageKey = `rp-production-correction:${rpAuthProfile().id || 'demo'}:${run.id}:${operation}`;
  const [pending, setPending] = useState(() => { try { return JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch (_) { return null; } });
  const [lot, setLot] = useState(pending && pending.correctedLot || '');
  const [reason, setReason] = useState(pending && pending.reason || '');
  const [evidence, setEvidence] = useState(pending && pending.evidence || '');
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const demo = rpIsDemoMode();
  const check = async () => {
    if (busy || demo) return;
    const heldSources = (run.allocations || []).filter(row => rpCanSourceReconciliationHold(row.canRunId));
    if (heldSources.length) {
      setConfirmed(false); setError('');
      setPreview({ready:false,effect:'The original signed record is preserved while can-source reconciliation is pending.',blockers:[`Needs reconciliation: ${[...new Set(heldSources.map(row => row.canRunId))].join(', ')}. Review the historical packing confirmation before a stock-changing correction.`]});
      return;
    }
    setBusy(true); setError(''); setPreview(null); setConfirmed(false);
    try { setPreview(await rpApi('/api/production/packaging-correction', { method: 'POST', body: JSON.stringify({ preview: true, runId: run.id, operation, correctedLot: restore ? null : lot }) })); }
    catch (err) { setError(err.message || 'Could not check this correction.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (restore && !pending) check(); }, []);
  const save = async () => {
    if (busy || demo || (!pending && (!preview || !preview.ready || !confirmed || reason.trim().length < 10 || evidence.trim().length < 3))) return;
    setBusy(true); setError('');
    const request = pending || { correctionId: crypto.randomUUID(), runId: run.id, operation, correctedLot: restore ? null : lot, expectedStatus: preview.run.status, reason: reason.trim(), evidence: evidence.trim(), confirmed: true };
    try {
      // Save the exact request before sending; a lost response always retries
      // the same signed correction, even after a browser reload.
      localStorage.setItem(storageKey, JSON.stringify(request)); setPending(request);
      const response = await rpApi('/api/production/packaging-correction', { method: 'POST', body: JSON.stringify(request) });
      if (!response || response.accepted !== true || response.correctionId !== request.correctionId || response.operation !== request.operation || !response.run || response.run.id !== (restore ? request.runId : response.replacementRunId)) throw new Error('The server did not return the matching completed correction.');
      localStorage.removeItem(storageKey); setPending(null);
      await rpRefreshAfterMutation(restore ? `${run.id} restored; its reversal remains in history.` : `Lot corrected in ${response.replacementRunId}. Original ${run.id} remains in history.`);
      onSaved(); onClose();
    } catch (err) {
      if ([400, 403, 404, 409].includes(err.status)) {
        localStorage.removeItem(storageKey); setPending(null); setPreview(null); setConfirmed(false);
        setError((err.message || 'The correction was rejected.') + ' Check the current stock and evidence before trying again.');
      } else setError((err.message || 'The correction response could not be confirmed.') + ' Retry the same correction to safely recover its result.');
    }
    finally { setBusy(false); }
  };
  const invalidate = () => { setPreview(null); setConfirmed(false); };
  return <Modal title={restore ? 'Restore Accidental Reversal' : combined ? 'Restore with Corrected Lot' : 'Correct Packaging Lot'} sub={`${run.id} · ${rpPrintedLotLabel(run.outputSkuId, run.lot, run.additionalParentRunId || run.id)} · ${Number(run.cases || 0).toLocaleString()} cases`} width={740} onClose={() => !busy && onClose()}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>{pending ? 'Keep Pending and Close' : 'Cancel'}</button>{!pending && <button className="btn" disabled={busy || demo} onClick={check}>{busy ? 'Checking...' : 'Check Stock and Sources'}</button>}<button className="btn btn-primary" disabled={busy || demo || (!pending && (!preview || !preview.ready || !confirmed || reason.trim().length < 10 || evidence.trim().length < 3))} onClick={save}>{busy ? 'Working...' : pending ? 'Recover Same Correction' : restore ? 'Confirm Restore' : combined ? 'Confirm Restore with Corrected Lot' : 'Confirm Lot Correction'}</button></>}>
    <InfoNote icon="alert">{restore ? 'Restore returns this run to its status before reversal, recreates exactly the removed cases, and allocates the original cans again. The system must prove the source cans were not reused and no replacement stock was recorded. The original reversal stays in history. If the lot also needs correcting, cancel and choose Restore with Corrected Lot instead.' : combined ? 'This restores the removed cases under a corrected lot in one action. The original run stays reversed. A linked replacement uses the same case quantity, exact source cans, recipe, packed date and status from before reversal. It fails if the original cans were reused, stock remains, or another movement needs reconciliation.' : 'A lot correction preserves the original signed run and replaces it with a linked run using the corrected lot. The cases, recipe, source cans, packed date and status stay the same. This is available only while all original cases remain unmoved at the producer. Stock with other movement records needs a reviewed reconciliation.'}</InfoNote>
    {demo && <ReqMsg show>Production corrections require live records. Demo data cannot be corrected here.</ReqMsg>}
    {pending && <InfoNote icon="alert">A correction was sent and its result still needs confirmation. Its details are frozen below. Recover the same request; do not start another adjustment.</InfoNote>}
    {!restore && <MField label="Correct Lot from Production Evidence" required><input className="field mono" value={lot} disabled={!!pending || busy} onChange={e => { setLot(e.target.value.toUpperCase().replace(/[^A-Z0-9._:-]/g, '').slice(0, 80)); invalidate(); }} placeholder="Exact printed lot code" /></MField>}
    <MField label="Why This Correction Is Needed" required><textarea className="field" rows="2" maxLength="1000" value={reason} disabled={!!pending || busy} onChange={e => setReason(e.target.value)} /></MField>
    <MField label="Evidence Reference" required><textarea className="field" rows="2" maxLength="2000" value={evidence} disabled={!!pending || busy} onChange={e => setEvidence(e.target.value)} placeholder="Case sheet, invoice, production confirmation, or verified count and who confirmed it" /></MField>
    {preview && <div style={{ margin: '12px 0' }}><p style={{ fontWeight: 700 }}>{preview.ready ? 'The current stock and sources support this correction.' : 'This correction needs further review.'}</p><p>{preview.effect}</p>{(preview.blockers || []).map((item,i) => <ReqMsg key={i} show>{item}</ReqMsg>)}
      <details><summary>Stock and source evidence</summary><div style={{ overflowX: 'auto' }}><table className="rp-table"><thead><tr><th>Source Can Run</th><th>Original Allocation</th><th>Now Available</th></tr></thead><tbody>{(preview.sourceAllocations || []).map(row => <tr key={row.can_run_id}><td className="mono">{row.can_run_id}</td><td>{Number(row.can_quantity).toLocaleString()} cans</td><td>{Number(row.available_cans).toLocaleString()} cans</td></tr>)}</tbody></table></div>{(preview.holdings || []).map((row,i) => <p key={i}>{(RP.loc(row.location_id) || {}).name || row.location_id}: {row.quantity} cases · {row.owner} · {row.status}</p>)}{(preview.downstreamEvidence || []).map(row => <p key={row.id}>{row.event_type} · {row.related_id || row.id} · {row.quantity} cases</p>)}</details>
      {preview.ready && !pending && <label style={{ display: 'flex', gap: 10, marginTop: 14 }}><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />I checked the run, quantity, exact lot, source cans and the stock effect above.</label>}
    </div>}
    {error && <ReqMsg show>{error}</ReqMsg>}
  </Modal>;
}

function PackagingPlanCorrectionModal({ run, onClose, onSaved }) {
  const storageKey = `rp-production-correction:${rpAuthProfile().id || 'demo'}:${run.id}:correct_plan`;
  const [initial] = useState(() => { try { return { request: JSON.parse(localStorage.getItem(storageKey) || 'null'), error: '' }; } catch (_) { return { request: null, error: 'The stored correction cannot be read. Keep the browser record and review its history before another correction.' }; } });
  const [pending, setPending] = useState(initial.request);
  const [cases, setCases] = useState(String(initial.request ? initial.request.cases : run.cases));
  const [packedOn, setPackedOn] = useState(initial.request ? initial.request.packedOn : run.packedOn);
  const [allocations, setAllocations] = useState(initial.request ? initial.request.allocations : (run.allocations || []).map(row => ({canRunId:row.canRunId,canSkuId:row.canSkuId,cansAllocated:row.cansAllocated})));
  const [reason, setReason] = useState(initial.request ? initial.request.reason : ''), [evidence, setEvidence] = useState(initial.request ? initial.request.evidence : '');
  const [preview, setPreview] = useState(null), [confirmed, setConfirmed] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState(initial.error);
  const feedbackRef = useRef(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const feedbackId = `packing-correction-feedback-${run.id}`;
  const recipe = (RP.PACK_RECIPES || []).find(row => row.id === run.recipeId);
  const sourceRuns = (RP.CAN_PRODUCTION_RUNS || []).map(rpCanRunUi).filter(row => row.producerLocationId === run.outputLocationId).map(row => ({ ...row, usableCans: row.availableCans + (run.allocations || []).filter(item => item.canRunId === row.id).reduce((sum,item) => sum + Number(item.cansAllocated || 0),0) }));
  const heldOriginalSources = (run.allocations || []).filter(row => rpCanSourceReconciliationHold(row.canRunId));
  const valid = !heldOriginalSources.length && /^\d+$/.test(cases) && Number(cases)>0 && Number(cases)<=100000 && packedOn && recipe && allocations.length>0 && (recipe.components || []).every(component => allocations.filter(row => row.canSkuId===component.canSkuId).reduce((sum,row)=>sum+Number(row.cansAllocated),0)===Number(cases)*component.cansPerCase) && allocations.every(row => Number.isInteger(row.cansAllocated) && row.cansAllocated>0 && sourceRuns.some(source => !source.sourceHold && source.id===row.canRunId && source.canSkuId===row.canSkuId && source.usableCans>=row.cansAllocated));
  const planIssues = [];
  if (heldOriginalSources.length) planIssues.push(`Original source reconciliation is pending for ${[...new Set(heldOriginalSources.map(row => row.canRunId))].join(', ')}. This signed packing plan must be reconciled with its historical confirmation before another stock-changing correction.`);
  const caseCountValid = /^\d+$/.test(cases) && Number(cases)>0 && Number(cases)<=100000;
  if (!caseCountValid) planIssues.push('Enter a whole corrected case total from 1 to 100,000.');
  if (!packedOn) planIssues.push('Enter the corrected packed date.');
  if (!recipe) planIssues.push('The original recipe is unavailable. Refresh production data before checking this correction.');
  if (caseCountValid && recipe) (recipe.components || []).forEach(component => {
    const required = Number(cases)*component.cansPerCase;
    const allocated = allocations.filter(row => row.canSkuId===component.canSkuId).reduce((sum,row)=>sum+Number(row.cansAllocated),0);
    if (allocated !== required) planIssues.push(`${rpSkuDisplayName(RP.sku(component.canSkuId)||component.canSkuId)}: ${Number.isFinite(allocated) ? allocated.toLocaleString() : 'invalid'} of ${required.toLocaleString()} cans allocated${Number.isFinite(allocated) ? ` (${Math.abs(required-allocated).toLocaleString()} ${allocated<required?'still needed':'too many'})` : ''}.`);
  });
  allocations.forEach(row => {
    const source = sourceRuns.find(item => item.id===row.canRunId && item.canSkuId===row.canSkuId);
    if (!Number.isInteger(row.cansAllocated) || row.cansAllocated<=0) planIssues.push(`${row.canRunId}: enter a positive whole number of cans, or clear the unused source.`);
    else if (!source) planIssues.push(`${row.canRunId}: this original source is unavailable. Review the source record before checking the correction.`);
    else if (source.sourceHold && !heldOriginalSources.some(item => item.canRunId === row.canRunId)) planIssues.push(`${row.canRunId}: source reconciliation is pending; this run cannot be allocated.`);
    else if (row.cansAllocated>source.usableCans) planIssues.push(`${row.canRunId}: ${row.cansAllocated.toLocaleString()} entered exceeds the ${source.usableCans.toLocaleString()} cans available including its original allocation.`);
  });
  if (!allocations.length && !planIssues.length) planIssues.push('Enter the exact cans used from the supported source runs.');
  const feedback = error ? [error] : [];
  if (rpIsDemoMode()) feedback.push('Packing corrections are available in live mode.');
  if (pending) feedback.push('Recover the same frozen correction to confirm its result. The entered details are preserved.');
  else {
    feedback.push(...planIssues);
    if (!valid) feedback.push('Check the packing evidence and enter the actual source quantities. Changing the case total does not change these allocations automatically.');
    else if (!preview) feedback.push('Choose Check Correction to review the current stock and source records.');
    else if (!preview.ready) feedback.push(...((preview.blockers || []).length ? preview.blockers : ['The current stock or source records do not support this correction. Review the original record before continuing.']));
    if (reason.trim().length<10) feedback.push('Enter a correction reason of at least 10 characters.');
    if (evidence.trim().length<3) feedback.push('Enter an evidence reference of at least 3 characters.');
    if (preview?.ready && !confirmed) feedback.push('Check the confirmation box after reviewing the total, date, sources, evidence and stock effect.');
  }
  const focusFeedback = () => { feedbackRef.current?.focus(); feedbackRef.current?.scrollIntoView({block:'nearest'}); };
  const locked = busy || !!pending;
  const invalidate = () => { setShowFeedback(true); setPreview(null); setConfirmed(false); setError(''); };
  const plan = {runId:run.id,cases:Number(cases),packedOn,allocations};
  const check = async () => {
    setShowFeedback(true);focusFeedback();requestAnimationFrame(focusFeedback);
    if (busy || pending || !valid || initial.error || rpIsDemoMode()) return;
    setBusy(true); invalidate();
    try { setPreview(await rpApi('/api/production/packaging-plan-correction',{method:'POST',body:JSON.stringify({...plan,preview:true})})); }
    catch(err) { setError(err.message || 'Could not check the original packing correction.'); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (busy || initial.error || rpIsDemoMode() || (!pending && (!valid || !preview?.ready || !confirmed || reason.trim().length<10 || evidence.trim().length<3))) return;
    setBusy(true); setError('');
    const request = pending || {...plan,operation:'correct_plan',correctionId:crypto.randomUUID(),expectedStatus:preview.run.status,reason:reason.trim(),evidence:evidence.trim(),confirmed:true};
    try {
      localStorage.setItem(storageKey,JSON.stringify(request)); setPending(request);
      const result=await rpApi('/api/production/packaging-plan-correction',{method:'POST',body:JSON.stringify(request)});
      if(result?.accepted!==true || result.correctionId!==request.correctionId || !result.run || result.run.id!==result.replacementRunId) throw new Error('The matching correction was not confirmed. Recover the same request before another change.');
      await rpRefreshAfterMutation(`Original packing corrected in ${result.replacementRunId}; previous history retained.`);
      localStorage.removeItem(storageKey);setPending(null);onSaved();onClose();
    } catch(err) {
      if([400,403,404,409].includes(err.status)){localStorage.removeItem(storageKey);setPending(null);invalidate();}
      setError(err.message || 'Correction result is uncertain. Recover the same request.');
    } finally {setBusy(false);}
  };
  return <Modal title="Correct Original Packed Details" sub={`${run.id} · ${rpPrintedLotLabel(run.outputSkuId,run.lot)}`} width={760} onClose={()=>!busy&&onClose()} footer={<>
    <div hidden={!showFeedback && !error && !pending} id={feedbackId} ref={feedbackRef} tabIndex={-1} role="status" aria-live="polite" aria-label="Packing correction status" style={{flexBasis:'100%',minWidth:0,maxHeight:'min(28vh, 190px)',overflowY:'auto',overflowWrap:'anywhere',fontSize:12.5,lineHeight:1.4,color:error?'var(--danger)':'var(--muted)'}}>
      <strong>{busy ? 'Checking or saving the correction…' : pending ? 'Correction receipt pending' : feedback.length ? 'Before this correction can be confirmed' : 'Ready to confirm the reviewed correction'}</strong>
      {feedback.length>0 && <ul style={{margin:'6px 0 0',paddingLeft:18}}>{feedback.map((item,index)=><li key={index}>{item}</li>)}</ul>}
    </div>
    <button className="btn" disabled={busy} onClick={onClose}>{pending?'Keep Pending and Close':'Cancel'}</button>
    {!pending&&<button className="btn" aria-describedby={feedbackId} disabled={busy||!!initial.error||rpIsDemoMode()} onClick={check}>Check Correction</button>}
    <button className="btn btn-primary" aria-describedby={feedbackId} style={{whiteSpace:'normal',textAlign:'center',maxWidth:'100%'}} disabled={busy||!!initial.error||rpIsDemoMode()||(!pending&&(!preview?.ready||!confirmed||!valid||reason.trim().length<10||evidence.trim().length<3))} onClick={save}>{busy?'Working...':pending?'Recover Same Correction':'Confirm Original Packing Correction'}</button>
  </>}>
    <p>Review the case-stock change and source cans before saving.</p>
    {pending&&<InfoNote icon="alert">The original correction request is frozen until its result is confirmed.</InfoNote>}
    <div className="rp-packaging-form-grid"><MField label="Correct Total Cases" required><input className="field" type="number" min="1" max="100000" step="1" value={cases} disabled={locked} onChange={e=>{setCases(e.target.value);invalidate();}}/></MField><MField label="Correct Packed Date" required><input className="field" type="date" value={packedOn} disabled={locked} onChange={e=>{setPackedOn(e.target.value);invalidate();}}/></MField></div>
    {(recipe?.components||[]).map(component=><div key={component.canSkuId} style={{margin:'14px 0'}}><strong>{rpSkuDisplayName(RP.sku(component.canSkuId)||component.canSkuId)} · requires {Number(cases||0)*component.cansPerCase} cans</strong>{sourceRuns.filter(row=>row.canSkuId===component.canSkuId&&row.usableCans>0).map(source=><MField key={source.id} label={`${source.id} · ${source.usableCans.toLocaleString()} including the original allocation${source.sourceHold ? ' · Needs reconciliation' : ''}`}><input className="field" aria-label={`Correct cans from ${source.id}`} type="number" min="0" max={source.usableCans} step="1" disabled={locked || !!source.sourceHold} value={allocations.find(row=>row.canRunId===source.id)?.cansAllocated||''} onChange={e=>{const qty=Number(e.target.value);setAllocations(current=>[...current.filter(row=>row.canRunId!==source.id),...(qty?[{canRunId:source.id,canSkuId:source.canSkuId,cansAllocated:qty}]:[])]);invalidate();}}/></MField>)}</div>)}
    <MField label="Correction Reason" required><textarea className="field" maxLength="1000" value={reason} disabled={locked} onChange={e=>{setReason(e.target.value);setConfirmed(false);}}/></MField>
    <MField label="Evidence Reference" required><textarea className="field" maxLength="2000" value={evidence} disabled={locked} onChange={e=>{setEvidence(e.target.value);setConfirmed(false);}}/></MField>
    {preview&&<section aria-label="Original packing correction review"><p>{preview.effect}</p>{preview.ready&&<label style={{display:'flex',gap:10,margin:'14px 0'}}><input type="checkbox" checked={confirmed} disabled={locked} onChange={e=>setConfirmed(e.target.checked)}/>I verified the corrected total, date, exact sources, evidence and stock effect.</label>}</section>}
  </Modal>;
}

function ProductionHistoryModal({ runId, onClose }) {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [printedLabels, setPrintedLabels] = useState([]);
  const [labelsBusy, setLabelsBusy] = useState(false);
  const [labelsLoaded, setLabelsLoaded] = useState(false);
  const [labelsError, setLabelsError] = useState('');
  const load = async more => {
    if (busy) return; setBusy(true); setError('');
    try {
      if (rpIsDemoMode()) throw new Error('Production History shows signed records from the live database. Demo actions do not create this history.');
      const params = new URLSearchParams({ limit: '40' });
      if (runId) params.set('runId', runId);
      if (more && cursor) { params.set('beforeAt', cursor.beforeAt); params.set('beforeId', cursor.beforeId); }
      const result = await rpApi(`/api/production/history?${params}`);
      setEvents(current => more ? [...current, ...result.events] : result.events); setCursor(result.nextCursor); setLoaded(true);
    } catch (err) { setError(err.message || 'Could not load production history.'); }
    finally { setBusy(false); }
  };
  const loadPrintedLabels = async () => {
    if (labelsBusy) return; setLabelsBusy(true); setLabelsError('');
    try {
      if (rpIsDemoMode()) throw new Error('Printed lot labels are available with live production records.');
      const params = new URLSearchParams();
      if (runId) params.set('runId', runId);
      const result = await rpApi(`/api/production/printed-lot${params.size ? `?${params}` : ''}`);
      if (!Array.isArray(result.metadata)) throw new Error('The saved label details could not be read.');
      setPrintedLabels(result.metadata); setLabelsLoaded(true);
    } catch (err) { setLabelsError(err.message || 'Could not load printed lot labels.'); }
    finally { setLabelsBusy(false); }
  };
  useEffect(() => { load(false); loadPrintedLabels(); }, []);
  const snapshot = value => {
    if (!value) return 'No previous record';
    const labels = { display_case_quantity: 'Previously displayed cases', display_packaged_at: 'Previously displayed packed date', reason: 'Reason', invoice_date: 'Invoice date', production_date: 'Production date', date_basis: 'Date confidence', produced_cans: 'Cans produced', count_status: 'Count confidence', revision_no: 'Revision', status: 'Status', lot: 'Lot', cases: 'Cases', case_quantity: 'Cases', product: 'Product', case_sku_id: 'Product', packedOn: 'Packed date', packaged_at: 'Packed date', mode: 'Inventory mode', id: 'Record', notes: 'Notes' };
    return Object.entries(labels).filter(([key]) => value[key] != null).map(([key,label]) => `${label}: ${value[key]}`).join(' · ') || JSON.stringify(value);
  };
  return <Modal title="Production History" sub={runId || 'Recorded work, corrections, status changes and the people who made them'} width={980} onClose={onClose} footer={<><button className="btn" disabled={busy || labelsBusy} onClick={() => { load(false); loadPrintedLabels(); }}>Refresh</button>{cursor && <button className="btn" disabled={busy} onClick={() => load(true)}>Load Older History</button>}<button className="btn" onClick={onClose}>Close</button></>}>
    <InfoNote icon="doc">History includes original records and every saved can revision, packaging release, closure, reversal and correction. A restore leaves both the original reversal and the compensating action visible.</InfoNote>
    {error && <ReqMsg show>{error}</ReqMsg>}{busy && <p>Loading history...</p>}{loaded && !events.length && <p>No production history was found for this selection.</p>}
    {events.map(event => <article key={event.event_id} style={{ padding: '16px 0', borderBottom: '1px solid var(--line)', overflowWrap: 'anywhere' }}><div style={{ fontWeight: 750 }}>{event.action} · <span className="mono">{event.run_id}</span></div><div style={{ color: 'var(--muted)', margin: '4px 0 10px', fontSize: 12 }}>{new Date(event.occurred_at).toLocaleString()} · {event.actor_name}</div><div><strong>Before: </strong>{snapshot(event.before)}</div><div style={{ marginTop: 6 }}><strong>After: </strong>{snapshot(event.after)}</div>{event.evidence && <p><strong>Reason / evidence: </strong>{event.evidence}</p>}</article>)}
    <section aria-label="Printed lot labels" style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--line)', minWidth: 0, overflowWrap: 'anywhere' }}>
      <h3 style={{ margin: '0 0 8px' }}>Printed lot labels</h3>
      <p style={{ color: 'var(--muted)' }}>These saved labels identify existing recorded lots. Adding a label does not create, move or rename inventory.</p>
      {labelsError && <ReqMsg show>Printed lot labels could not be refreshed: {labelsError} Production events above remain available. Use Refresh to try again.</ReqMsg>}
      {labelsBusy && <p>Loading printed lot labels...</p>}
      {labelsLoaded && !printedLabels.length && !labelsError && <p>No printed lot labels were saved for this selection.</p>}
      {printedLabels.length >= 1000 && <p>Showing the newest 1,000 labels. Open a packaging run's History to see its saved label.</p>}
      {printedLabels.map(label => <article key={label.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--line)', minWidth: 0 }}>
        <div style={{ fontWeight: 750 }}>Printed label: <span className="mono">{label.printed_lot}</span></div>
        <div style={{ marginTop: 6 }}><strong>Recorded lot: </strong><span className="mono" style={{ whiteSpace: 'pre-wrap' }}>{label.lot}</span></div>
        <div style={{ marginTop: 6 }}><strong>Product: </strong>{rpSkuDisplayName(RP.sku(label.sku_id) || label.sku_id)} · <strong>Packaging run: </strong><span className="mono">{label.packaging_run_id}</span></div>
        <div style={{ color: 'var(--muted)', marginTop: 6, fontSize: 12 }}>{new Date(label.created_at).toLocaleString()} · Saved by {label.actor_name || label.actor_id || 'Unknown user'}</div>
        <p style={{ whiteSpace: 'pre-wrap' }}><strong>Reason: </strong>{label.reason}</p>
        <p style={{ whiteSpace: 'pre-wrap' }}><strong>Evidence: </strong>{label.evidence}</p>
      </article>)}
    </section>
  </Modal>;
}

function CompleteOriginalPackingModal({confirmation,onClose,onSaved,onEditRelated}) {
  const key=`rp-complete-original:${rpAuthProfile().id}:${confirmation.id}`;
  const [pending,setPending]=useState(()=>{try{return JSON.parse(localStorage.getItem(key)||'null');}catch{return null;}});
  const [review,setReview]=useState(null),[cases,setCases]=useState(String(confirmation.totalCases)),[packedOn,setPackedOn]=useState(confirmation.packedOn||'');
  const [quantities,setQuantities]=useState({}),[evidence,setEvidence]=useState(''),[checked,setChecked]=useState(false);
  const [busy,setBusy]=useState(false),[error,setError]=useState('');
  const load=async()=>{setBusy(true);setError('');setChecked(false);try{setReview(await rpApi(`/api/production/complete-original?confirmationId=${encodeURIComponent(confirmation.id)}`));}catch(e){setError(e.message);}finally{setBusy(false);}};
  useEffect(()=>{if(!pending)load();},[]);
  const snapshot=review?.snapshot, sources=snapshot?.sources||[], components=snapshot?.components||[];
  const count=Number(cases),selected=sources.filter(s=>Number(quantities[s.run.id])>0).map(s=>({canRunId:s.run.id,cans:Number(quantities[s.run.id])}));
  const capacity=s=>Math.max(0,Number(s.run.produced_cans)-Number(s.allocated)-Number(s.count?.verified_remaining||0));
  const sourceErrors=sources.filter(s=>quantities[s.run.id]!==undefined&&quantities[s.run.id]!==''&&(!Number.isInteger(Number(quantities[s.run.id]))||Number(quantities[s.run.id])<0||Number(quantities[s.run.id])>capacity(s)));
  const matched=components.length>0&&components.every(c=>sources.filter(s=>s.run.can_sku_id===c.can_sku_id).reduce((n,s)=>n+Number(quantities[s.run.id]||0),0)===count*c.cans_per_case);
  const existingId=snapshot?.completion?.run_id||snapshot?.confirmation?.covered_run_ids?.[0]||snapshot?.existingRuns?.[0]?.id;
  const valid=review&&!existingId&&Number.isInteger(count)&&count>0&&count<=1000000&&matched&&!sourceErrors.length&&evidence.trim().length>=3;
  const save=async()=>{setBusy(true);setError('');try{
    const request=pending||{id:crypto.randomUUID(),confirmationId:confirmation.id,cases:count,packedOn:packedOn||null,sources:selected,evidence,snapshotHash:review.snapshotHash,confirmed:true};
    localStorage.setItem(key,JSON.stringify(request));setPending(request);
    const result=await rpApi('/api/production/complete-original',{method:'POST',body:JSON.stringify(request)});
    if(result?.accepted!==true||!result.runId)throw new Error('The save was not confirmed. Retry the same request.');
    if(await rpRefreshAfterMutation('Original packing completed')===false)throw new Error('Saved, but the list could not refresh. Retry the same request.');
    localStorage.removeItem(key);setPending(null);onSaved();onClose();
  }catch(e){setError(e.message);if([400,403,409].includes(e.status)){localStorage.removeItem(key);setPending(null);setChecked(false);}}finally{setBusy(false);}};
  const edit=id=>{if(onEditRelated)onEditRelated(id);};
  return <Modal title="Complete original packing" sub={`${rpSkuDisplayName(RP.sku(confirmation.skuId)||confirmation.skuId)} · ${confirmation.printedLot||confirmation.lot}`} onClose={()=>!busy&&onClose()} footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button>{!pending&&<button className="btn" disabled={busy} onClick={load}>Reload</button>}<button className="btn btn-primary" disabled={busy||(!pending&&!valid)} onClick={()=>pending||checked?save():setChecked(true)}>{busy?'Please wait...':pending?'Retry save':checked?'Save original packing':'Review changes'}</button></>}>
    {error&&<p role="alert" style={{color:'var(--danger)'}}>{error}</p>}
    {pending?<p>{pending.cases.toLocaleString()} original cases. Retry this saved request to confirm whether it completed.</p>:<>
    <p>Link the cans used for this original packing. No cases will be added to inventory.</p>
    {existingId?<p role="alert">This original already has a packing record. <button className="btn btn-sm" onClick={()=>edit(existingId)}>Edit existing record</button></p>:<>
    <div className="rp-packaging-form-grid"><MField label="Original cases" required><input className="field" aria-label="Original cases" type="number" min="1" max="1000000" step="1" disabled={busy} value={cases} onChange={e=>{setCases(e.target.value);setChecked(false);}}/></MField><MField label="Packed date (if known)"><input className="field" aria-label="Original packed date" type="date" disabled={busy} value={packedOn} onChange={e=>{setPackedOn(e.target.value);setChecked(false);}}/></MField></div>
    {components.map(c=>{const assigned=sources.filter(s=>s.run.can_sku_id===c.can_sku_id).reduce((n,s)=>n+Number(quantities[s.run.id]||0),0);return <section key={c.can_sku_id} style={{margin:'16px 0'}}><strong>{rpSkuDisplayName(RP.sku(c.can_sku_id)||c.can_sku_id)}</strong><p style={{fontSize:12,margin:'4px 0 10px'}}>{assigned.toLocaleString()} / {(count*c.cans_per_case).toLocaleString()} cans assigned</p>
      {sources.filter(s=>s.run.can_sku_id===c.can_sku_id).map(s=><div key={s.run.id} style={{marginBottom:12}}><label style={{display:'block'}}>{s.run.id} · {capacity(s).toLocaleString()} cans not yet linked<input className="field" aria-label={`Historical cans from ${s.run.id}`} type="number" min="0" step="1" disabled={busy} value={quantities[s.run.id]||''} onChange={e=>{setQuantities(q=>({...q,[s.run.id]:e.target.value}));setChecked(false);}}/></label>
      {!!s.packingLinks?.length&&<details><summary style={{fontSize:12,cursor:'pointer'}}>{Number(s.allocated).toLocaleString()} cans linked to other packing</summary>{s.packingLinks.map(link=><div key={link.runId} style={{fontSize:12,marginTop:6}}>{link.lot} · {Number(link.cans).toLocaleString()} cans <button className="btn btn-sm" onClick={()=>edit(link.runId)}>Edit {link.runId}</button></div>)}</details>}</div>)}
      {!sources.some(s=>s.run.can_sku_id===c.can_sku_id)&&<p role="alert">No matching can batches are recorded at BevMax.</p>}
    </section>;})}
    {!!sourceErrors.length&&<p role="alert" style={{color:'var(--danger)'}}>Review {sourceErrors.map(s=>s.run.id).join(', ')}: enter whole cans within the amount not yet linked. Open its other packing links to correct an existing allocation.</p>}
    <MField label="How were these sources confirmed?" required><input className="field" aria-label="Original packing evidence" disabled={busy} maxLength={2000} value={evidence} onChange={e=>{setEvidence(e.target.value);setChecked(false);}}/></MField>
    {checked&&<section aria-label="Original packing review"><strong>{count.toLocaleString()} original cases · {packedOn||'Packing date unknown'}</strong><ul>{selected.map(s=><li key={s.canRunId}>{s.canRunId}: {s.cans.toLocaleString()} cans</li>)}</ul><p>Links historical can usage. Current case stock, orders and verified loose-can counts stay unchanged.</p></section>}
    </>}
    </>}
  </Modal>;
}

function PackagingSourcesModal({run,onClose,onSaved}) {
  const key=`rp-packaging-sources:${rpAuthProfile().id}:${run.id}`;
  const [pending,setPending]=useState(()=>{try{return JSON.parse(localStorage.getItem(key)||'null');}catch{return null;}});
  const [review,setReview]=useState(null),[sources,setSources]=useState([]),[reason,setReason]=useState(''),[evidence,setEvidence]=useState('');
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[checked,setChecked]=useState(false);
  const load=async()=>{setBusy(true);setError('');try{const data=await rpApi(`/api/production/packaging-sources?runId=${encodeURIComponent(run.id)}`);setReview(data);setSources(data.snapshot.allocations.map(a=>({allocationId:a.id,canRunId:a.can_run_id})));setChecked(false);}catch(e){setError(e.message);}finally{setBusy(false);}};
  useEffect(()=>{load();},[]);
  const save=async()=>{setBusy(true);setError('');try{const request=pending||{id:crypto.randomUUID(),runId:run.id,sources,reason,evidence,snapshotHash:review.snapshotHash,confirmed:true};localStorage.setItem(key,JSON.stringify(request));setPending(request);await rpApi('/api/production/packaging-sources',{method:'POST',body:JSON.stringify(request)});localStorage.removeItem(key);setPending(null);await rpRefreshAfterMutation('Source batches corrected');onSaved();onClose();}catch(e){setError(e.message);if(/changed|cover|same flavour|different source|another edit|cannot target/.test(e.message)){localStorage.removeItem(key);setPending(null);setChecked(false);}}finally{setBusy(false);}};
  const valid=review&&reason.trim().length>=3&&evidence.trim().length>=3&&sources.some(a=>review.snapshot.allocations.find(b=>b.id===a.allocationId)?.can_run_id!==a.canRunId);
  return <Modal title="Edit can sources" sub={run.id} onClose={()=>!busy&&onClose()} footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button>{!pending&&<button className="btn" disabled={busy} onClick={load}>Reload</button>}<button className="btn btn-primary" disabled={busy||(!pending&&!valid)} onClick={()=>pending||checked?save():setChecked(true)}>{busy?'Please wait...':pending?'Retry save':checked?'Save sources':'Review changes'}</button></>}>
    {error&&<p role="alert" style={{color:'var(--danger)'}}>{error}</p>}
    {review?.snapshot.allocations.map(a=><MField key={a.id} label={`${Number(a.can_quantity).toLocaleString()} cans - ${rpSkuDisplayName(RP.sku(a.can_sku_id)||a.can_sku_id)}`}><select className="field" aria-label={`Source for ${a.can_run_id}`} disabled={busy||!!pending} value={sources.find(x=>x.allocationId===a.id)?.canRunId||a.can_run_id} onChange={e=>{setSources(current=>current.map(x=>x.allocationId===a.id?{...x,canRunId:e.target.value}:x));setChecked(false);}}>{review.snapshot.sources.filter(x=>x.run.can_sku_id===a.can_sku_id).map(x=><option key={x.run.id} value={x.run.id}>{x.run.id}</option>)}</select></MField>)}
    <MField label="Reason" required><input className="field" value={reason} disabled={busy||!!pending} maxLength={1000} onChange={e=>{setReason(e.target.value);setChecked(false);}}/></MField>
    <MField label="How was the source confirmed?" required><input className="field" value={evidence} disabled={busy||!!pending} maxLength={2000} onChange={e=>{setEvidence(e.target.value);setChecked(false);}}/></MField>
    {checked&&<section aria-label="Source correction review"><ul>{sources.filter(a=>review.snapshot.allocations.find(b=>b.id===a.allocationId)?.can_run_id!==a.canRunId).map(a=>{const old=review.snapshot.allocations.find(b=>b.id===a.allocationId);return <li key={a.allocationId}>{old.can_quantity.toLocaleString()} cans: {old.can_run_id} to {a.canRunId}</li>;})}</ul><p>Updates batch allocations. Case stock, orders and verified loose-can counts stay unchanged.</p></section>}
  </Modal>;
}

function ProductionView({ go, detail }) {
  const [sel,setLocalSel]=useState(detail?.type==='production-run'?detail.id:null);
  const setSel=id=>{setLocalSel(id);if(go)go('production',id?{type:'production-run',id}:null);};
  const [newRun, setNewRun] = useState(false);
  const [canRunEditor, setCanRunEditor] = useState(null);
  const [packagingMode, setPackagingMode] = useState('');
  const [packagingParent, setPackagingParent] = useState(null);
  const [reversePackaging, setReversePackaging] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [closePackaging, setClosePackaging] = useState(null);
  const [history,setLocalHistory]=useState(detail?.type==='production-history'?detail.id||'':null);
  const setHistory=id=>{setLocalHistory(id);if(go)go('production',id!==null?{type:'production-history',id}:null);};
  const [canCount, setCanCount] = useState(null);
  const [packingEditor, setPackingEditor] = useState(null);
  const [packingLot, setPackingLot] = useState(null);
  const [sourceEditor,setSourceEditor]=useState(null);
  const [originalEditor,setOriginalEditor]=useState(null);
  const [packagingBusy, setPackagingBusy] = useState('');
  const [, force] = useState(0);
  useEffect(() => {
    setLocalSel(detail?.type==='production-run'?detail.id:null);
    setLocalHistory(detail?.type==='production-history'?detail.id||'':null);
  }, [detail]);
  if (sel) return <ProductionDetail run={(RP.PRODUCTION || []).find(p => p.id === sel)} back={() => setSel(null)} />;
  const production = Array.isArray(RP.PRODUCTION) ? RP.PRODUCTION : [];
  const canRuns = (Array.isArray(RP.CAN_PRODUCTION_RUNS) ? RP.CAN_PRODUCTION_RUNS : []).map(rpCanRunUi).sort((a, b) => `${b.productionDate}|${b.id}`.localeCompare(`${a.productionDate}|${a.id}`));
  const packaging = Array.isArray(RP.PACKAGING_RUNS) ? RP.PACKAGING_RUNS : [];
  const totalCans = canRuns.reduce((sum, run) => sum + run.totalCans, 0);
  const allocatedCans = canRuns.reduce((sum, run) => sum + run.allocatedCans, 0);
  const availableCans = canRuns.reduce((sum, run) => sum + (run.sourceHold ? 0 : run.availableCans), 0);
  const packingReport = rpHistoricalPackingReport(packaging);
  const hasPackingConfirmations = packingReport.confirmations.length > 0;
  const packagedCases = packingReport.totalCases;
  const advancePackaging = async run => {
    const target = run.status === 'QA Hold' ? 'Released' : run.status === 'Released' ? 'Closed' : '';
    if (!target || packagingBusy) return;
    setPackagingBusy(run.id);
    try {
      await rpApi('/api/production/packaging-status', { method: 'POST', body: JSON.stringify({ id: run.id, expectedStatus: run.status, status: target, note: target === 'Released' ? 'Released by production operations after QA review.' : undefined }) });
      await rpRefreshAfterMutation(`${run.id} moved to ${target}`);
      setClosePackaging(null);
      force(value => value + 1);
    } catch (error) {
      toast(error && error.message ? error.message : 'Could not update packaging status.', 'error');
    } finally {
      setPackagingBusy('');
    }
  };
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {packingEditor && <PackagingEditModal run={packingEditor} onClose={() => setPackingEditor(null)} onSaved={() => force(x => x + 1)}
        onStockCorrection={() => {setCorrection({run:packingEditor,operation:'correct_plan'});setPackingEditor(null);}}
        onSourceCorrection={() => {setSourceEditor(packingEditor);setPackingEditor(null);}}
        onLotDetails={() => {const lot=(RP.LOTS || []).find(row => row.sku===packingEditor.outputSkuId && row.lot===packingEditor.lot);if(lot){setPackingLot(lot);setPackingEditor(null);}else{setCorrection({run:packingEditor,operation:'correct_lot'});setPackingEditor(null);}}}
        onInventory={() => {setPackingEditor(null);go('inventory');}} />}
      {originalEditor && <CompleteOriginalPackingModal confirmation={originalEditor} onClose={()=>setOriginalEditor(null)} onSaved={()=>force(x=>x+1)} onEditRelated={id=>{const run=(RP.PACKAGING_RUNS||[]).find(r=>r.id===id);if(run){setOriginalEditor(null);setPackingEditor(run);}else toast('Refresh Production to load this record.','error');}}/>}
      {sourceEditor && <PackagingSourcesModal run={sourceEditor} onClose={()=>setSourceEditor(null)} onSaved={()=>force(x=>x+1)}/>}
      {packingLot && <InventoryLotModal lot={packingLot} go={go} onClose={() => setPackingLot(null)} onSaved={() => {setPackingLot(null);force(x => x + 1);}} />}
      {canCount && <CanSourceCountModal run={canCount} onClose={() => setCanCount(null)} onSaved={() => force(x => x + 1)} />}
      {newRun && <ScheduleProductionModal onClose={() => setNewRun(false)} onAdded={() => force(x => x + 1)} />}
      {canRunEditor !== null && <CanProductionRunModal run={canRunEditor && canRunEditor.id ? canRunEditor : null} onClose={() => setCanRunEditor(null)} onCount={run => {setCanRunEditor(null);setCanCount(run);}} onSaved={() => force(x => x + 1)} />}
      {packagingMode && <PackagingRunModal inventoryMode={packagingMode} initialParentRun={packagingParent} onEditOriginal={confirmation=>{setPackagingMode('');setPackagingParent(null);const run=(RP.PACKAGING_RUNS||[]).find(r=>(confirmation.coveredRunIds||[]).includes(r.id));if(run)setPackingEditor(run);else setOriginalEditor(confirmation);}} onClose={() => { setPackagingMode(''); setPackagingParent(null); }} onSaved={() => force(x => x + 1)} onReviewStock={() => { setPackagingMode(''); setPackagingParent(null); go('inventory'); }} />}
      {reversePackaging && <PackagingReverseModal run={reversePackaging} onClose={() => setReversePackaging(null)} onSaved={() => force(x => x + 1)} />}
      {correction && correction.operation !== 'correct_plan' && <PackagingCorrectionModal run={correction.run} operation={correction.operation} onClose={() => setCorrection(null)} onSaved={() => force(x => x + 1)} />}
      {correction && correction.operation === 'correct_plan' && <PackagingPlanCorrectionModal run={correction.run} onClose={() => setCorrection(null)} onSaved={() => force(x => x + 1)} />}
      {history !== null && <ProductionHistoryModal runId={history} onClose={() => setHistory(null)} />}
      {closePackaging && <Modal title="Close Completed Packaging" sub={closePackaging.id} onClose={() => !packagingBusy && setClosePackaging(null)} footer={<><button className="btn" disabled={!!packagingBusy} onClick={() => setClosePackaging(null)}>Cancel</button><button className="btn btn-primary" disabled={!!packagingBusy} onClick={() => advancePackaging(closePackaging)}>{packagingBusy ? 'Closing...' : 'Confirm Close Run'}</button></>}><p>Close marks the production paperwork complete. The {Number(closePackaging.cases || 0).toLocaleString()} released cases of lot <strong>{rpPrintedLotLabel(closePackaging.outputSkuId, closePackaging.lot, closePackaging.id)}</strong> remain in inventory and can still be transferred or sold.</p><p>No cases are added, removed or moved, and no source cans are released. The closure and your name are recorded in Production History.</p></Modal>}
      <PageHead eyebrow="BevMax → Cases → Custody" title="Production"
        sub="Cans, packing and production history."
        actions={<><button className="btn btn-sm" onClick={() => setHistory('')}><Icon name="doc" size={15} />History</button><button className="btn btn-sm btn-primary" onClick={() => go('move-stock')}><Icon name="layers" size={15} />Move Stock</button><button className="btn btn-sm" onClick={() => setPackagingMode('reconcile_existing')}><Icon name="doc" size={15} />Historical Packaging</button><button className="btn btn-sm" onClick={() => setCanRunEditor({})}><Icon name="plus" size={15} />Can Run</button><button className="btn btn-sm" onClick={() => setPackagingMode('post_to_inventory')}><Icon name="package" size={15} />Pack Cases</button></>} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 22 }}>
        <KPICard label="Cans Produced" value={totalCans.toLocaleString()} unit="cans" icon="production" accent="var(--info)" />
        <KPICard label="Allocated to Packs" value={allocatedCans.toLocaleString()} unit="cans" icon="layers" accent="var(--amber)" />
        <KPICard label="Available for packing" value={availableCans.toLocaleString()} unit="cans" icon="inventory" accent="var(--warn)" />
        <KPICard label={hasPackingConfirmations ? 'Packed cases' : 'Cases Recorded'} value={packagedCases.toLocaleString()} unit="cases" icon="package" accent="var(--ok)" />
      </div>
      <SectionTitle>1. BevMax Can Runs</SectionTitle>
      <div className="card rp-production-table-card">
        <table className="rp-table rp-production-table rp-can-runs-table" aria-label="BevMax can runs" role="table">
          <thead role="rowgroup"><tr role="row"><th scope="col">Run</th><th scope="col">Can SKU</th><th scope="col">Production Date</th><th scope="col" className="num">Produced</th><th scope="col" className="num">Allocated</th><th scope="col" className="num">Remaining cans</th><th scope="col">Production count</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
          <tbody role="rowgroup">
            {!canRuns.length && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 26 }}>No BevMax can runs are loaded.</td></tr>}
            {canRuns.map(run => {
              const runSku = rpSkuItem(run.canSkuId);
              return <tr key={run.id} role="row">
                <td role="cell" data-label="Run" className="rp-production-reference"><span className="mono" style={{ fontWeight: 700 }}>{run.id}</span></td>
                <td role="cell" data-label="Can SKU" className="rp-production-product"><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={runSku || run.canSkuId} size={24} /><div><div style={{ fontWeight: 700 }}>{rpSkuDisplayName(runSku || 'Unknown product')}</div><div className="mono" style={{ color: 'var(--faint)', fontSize: 10.5, marginTop: 2 }}>{rpSkuExternalIdentity(runSku || '')}</div></div></div></td>
                <td role="cell" data-label="Production Date"><span className="mono rp-production-date">{run.productionDate}</span><span style={{ display: 'block', fontSize: 10.5, color: run.productionDateBasis === 'bevmax_confirmed' ? 'var(--ok)' : 'var(--warn)' }}>{run.productionDateBasis === 'bevmax_confirmed' ? 'Confirmed by BevMax' : run.productionDateBasis === 'bevmax_reported' ? 'Reported by BevMax' : `Invoice estimate · ${run.invoiceDate}`}</span></td>
                <td role="cell" data-label="Produced" className="num">{run.totalCans.toLocaleString()}</td>
                <td role="cell" data-label="Allocated" className="num">{run.allocatedCans.toLocaleString()}</td>
                <td role="cell" data-label="Unallocated" className="num">{run.sourceHold ? <><strong>Needs count</strong><span className="rp-production-secondary">{run.calculatedRemaining.toLocaleString()} calculated · unavailable</span></> : <><strong>{run.availableCans.toLocaleString()}</strong><span className="rp-production-secondary">{run.countId ? 'Verified balance remaining' : 'Calculated'}</span></>}</td>
                <td role="cell" data-label="Production count"><Pill status={run.countStatus === 'estimated' ? 'Pending' : 'Approved'}>{run.countStatus === 'estimated' ? 'Estimated' : run.countStatus === 'verified' ? 'Verified' : 'Reported'}</Pill></td>
                <td role="cell" data-label="Actions" className="rp-production-actions"><div className="rp-production-action-list"><button className="btn btn-sm" onClick={() => setCanRunEditor(run)}>Edit</button>{rpCanCountManagementAllowed() && (RP.CAN_SOURCE_RECONCILIATION_HOLDS || []).some(h => h.canRunId === run.id) && <button className="btn btn-sm" onClick={() => setCanCount(run)}>Reconcile loose cans</button>}<button className="btn btn-sm" onClick={() => setHistory(run.id)}>History</button></div></td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
      <SectionTitle>2. Case Packaging from Cans</SectionTitle>
      <div className="card rp-production-table-card"><table className="rp-table rp-production-table rp-packaging-table" aria-label="Case packaging from cans" role="table"><thead role="rowgroup"><tr role="row"><th scope="col">Packaging Run</th><th scope="col">Finished Product</th><th scope="col">Lot</th><th scope="col">Packed</th><th scope="col" className="num">Cases</th><th scope="col">Inventory Effect</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead><tbody role="rowgroup">
        {!packaging.length && !packingReport.confirmations.length && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 26 }}>No case packaging has been allocated yet. Historical entries will not duplicate inventory.</td></tr>}
        {packingReport.confirmations.filter(c=>!(c.coveredRunIds||[]).length).map(c=><tr key={`confirmed-${c.id}`} role="row">
          <td data-label="Packaging Run">BevMax record</td><td data-label="Finished Product">{rpSkuDisplayName(RP.sku(c.skuId)||c.skuId)}</td>
          <td data-label="Lot" className="mono">{c.printedLot||c.lot}</td><td data-label="Packed">{c.packedOn||'—'}</td><td data-label="Cases" className="num">{Number(c.totalCases).toLocaleString()}</td>
          <td data-label="Inventory Effect">No stock change</td><td data-label="Status">Sources unlinked</td><td data-label="Actions">{rpCanCountManagementAllowed()&&<button className="btn btn-sm" onClick={()=>setOriginalEditor(c)}>Edit</button>} <button className="btn btn-sm" onClick={()=>setHistory('')}>History</button></td>
        </tr>)}
        {packaging.map(run => <tr key={run.id} role="row">
          <td role="cell" data-label="Packaging Run" className="mono rp-production-reference" style={{ fontWeight: 700 }}>{run.id}</td>
          <td role="cell" data-label="Finished Product" className="rp-production-product">{rpSkuDisplayName(RP.sku(run.outputSkuId) || run.outputSkuId)}</td>
          <td role="cell" data-label="Lot" className="rp-production-lot"><span className="mono">{rpPrintedLotLabel(run.outputSkuId, run.lot, run.additionalParentRunId || run.id)}</span><span className="rp-production-secondary">{run.lotGroup}{run.additionalParentRunId && ` · Additional session for ${run.additionalParentRunId}`}</span></td>
          <td role="cell" data-label="Packed" className="mono rp-production-date">{rpPackingDate(run) || "—"}</td>
          <td role="cell" data-label="Cases" className="num">{rpPackingCases(run).toLocaleString()}</td>
          <td role="cell" data-label="Inventory Effect">{run.inventoryMode === 'post_to_inventory' ? 'Posted at BevMax' : 'Reconciliation only'}</td>
          <td role="cell" data-label="Status"><Pill status={run.status}>{run.status}</Pill></td>
          <td role="cell" data-label="Actions" className="rp-production-actions"><div className="rp-production-action-list">{rpCanCountManagementAllowed() && run.status !== "Reversed" && <button className="btn btn-sm" onClick={() => setPackingEditor(run)}>Edit</button>}{['QA Hold', 'Released'].includes(run.status) && !rpPendingProductionCorrection(run.id) && <button className="btn btn-sm" disabled={packagingBusy === run.id} onClick={() => run.status === 'Released' ? setClosePackaging(run) : advancePackaging(run)}>{packagingBusy === run.id ? 'Saving...' : run.status === 'QA Hold' ? 'Release' : 'Close'}</button>}<button className="btn btn-sm" onClick={() => setHistory(run.id)}>History</button>{run.inventoryMode === 'post_to_inventory' && run.status !== 'Reversed' && <button className="btn btn-sm" onClick={() => { const root = run.additionalParentRunId ? packaging.find(item => item.id === run.additionalParentRunId) : run; if (root) { setPackagingParent(root); setPackagingMode('post_to_inventory'); } }}>Pack More</button>}{!rpPackagingHistoryProtected(run) && run.inventoryMode === 'post_to_inventory' && run.status !== 'Reversed' && !rpPendingProductionCorrection(run.id) && <button className="btn btn-sm" onClick={() => setCorrection({ run, operation: 'correct_plan' })}>Correct Packed Details</button>}{!rpPackagingHistoryProtected(run) && run.inventoryMode === 'post_to_inventory' && <button className="btn btn-sm" onClick={() => setCorrection({ run, operation: (rpPendingProductionCorrection(run.id) || {}).operation || (run.status === 'Reversed' ? 'restore' : 'correct_lot') })}>{rpPendingProductionCorrection(run.id) ? 'Recover Correction' : run.status === 'Reversed' ? 'Restore' : 'Correct Lot'}</button>}{!rpPackagingHistoryProtected(run) && run.inventoryMode === 'post_to_inventory' && run.status === 'Reversed' && !rpPendingProductionCorrection(run.id) && <button className="btn btn-sm" onClick={() => setCorrection({ run, operation: 'restore_with_lot' })}>Restore with Corrected Lot</button>}{!rpPackagingHistoryProtected(run) && run.status !== 'Reversed' && !rpPendingProductionCorrection(run.id) && <button className="btn btn-sm" disabled={Boolean(packagingBusy)} onClick={() => setReversePackaging(run)} style={{ color: 'var(--danger)' }}>Reverse</button>}</div></td>
        </tr>)}
      </tbody></table></div>
      {production.length > 0 && <><SectionTitle>Legacy Sealed-Flat Runs</SectionTitle><div className="card rp-production-table-card"><table className="rp-table rp-production-table" aria-label="Legacy sealed-flat runs" role="table"><thead role="rowgroup"><tr role="row"><th scope="col">Run</th><th scope="col">Product</th><th scope="col">Partner</th><th scope="col">Run Date</th><th scope="col" className="num">Output</th><th scope="col">QA</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Details</span></th></tr></thead><tbody role="rowgroup">{production.map(p => { const s = rpProductionSku(p), shortInput = (p.inputs || []).find(i => !i.ok); return <tr key={p.id} role="row" style={{ cursor: 'pointer' }} onClick={() => setSel(p.id)}><td role="cell" data-label="Run" className="rp-production-reference"><span className="mono" style={{ fontWeight: 600 }}>{p.id}</span>{shortInput && <Icon name="alert" size={13} style={{ color: 'var(--danger)', marginLeft: 6 }} />}</td><td role="cell" data-label="Product" className="rp-production-product">{rpSkuDisplayName(s || p.sku)}</td><td role="cell" data-label="Partner">{p.partner}</td><td role="cell" data-label="Run Date" className="mono rp-production-date">{p.runDate}</td><td role="cell" data-label="Output" className="num">{rpProductionOutputCases(p).toLocaleString()} cs</td><td role="cell" data-label="QA">{p.qa}</td><td role="cell" data-label="Status"><Pill status={p.status}>{rpProductionStatusLabel(p.status)}</Pill></td><td role="cell" data-label="Details" className="rp-production-actions"><button className="btn btn-sm" onClick={event => { event.stopPropagation(); setSel(p.id); }}>View Run<Icon name="chevR" size={16} /></button></td></tr>; })}</tbody></table><div style={{ padding: 12, borderTop: '1px solid var(--line)' }}><button className="btn btn-sm" onClick={() => setNewRun(true)}>Record Legacy Sealed-Flat Run</button></div></div></>}
    </div>
  );
}

function ProductionDetail({ run, back }) {
  const [current, setCurrent] = useState(run);
  const [actualOutput, setActualOutput] = useState(String(run && (run.output != null ? run.output / (RP.FINISHED_GOODS_CASE_PACK_SIZE || 12) : run.cases) || ''));
  const [wasteCans, setWasteCans] = useState(String(run && run.waste != null ? run.waste : 0));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [docBusy, setDocBusy] = useState('');
  const [docType, setDocType] = useState('');
  const docInput = useRef(null);
  useEffect(() => {
    setCurrent(run);
    setActualOutput(String(run && (run.output != null ? run.output / (RP.FINISHED_GOODS_CASE_PACK_SIZE || 12) : run.cases) || ''));
    setWasteCans(String(run && run.waste != null ? run.waste : 0));
  }, [run && run.id, run && run.status]);
  if (!current) {
    return <div className="rp-fade" style={{ padding: '26px 30px 40px' }}><button className="btn btn-ghost btn-sm" onClick={back}><Icon name="arrowL" size={16} />Back to Production</button><div className="card" style={{ marginTop: 18, padding: 24, color: 'var(--muted)' }}>Production run was not found.</div></div>;
  }
  const s = rpProductionSku(current);
  const inputs = current.inputs || [];
  const idx = Math.max(0, RP.PROD_STATUSES.indexOf(current.status));
  const nextStatus = rpProductionNextStatus(current.status);
  const documents = Array.isArray(current.documents) ? current.documents : [];
  const lotEvidence = (RP.LOTS || []).find(lot => lot.sku === current.sku && lot.lot === current.lot);
  const verifiedLotDates = Boolean(lotEvidence && lotEvidence.dateStatus === 'Verified' && lotEvidence.producedAt && lotEvidence.expiresAt);
  const qaEvidence = documents.some(doc => ['coa', 'qa_report'].includes(doc.type) && (doc.path || doc.url));
  const releaseBlocked = nextStatus === 'Released' && (!verifiedLotDates || !qaEvidence);
  const canAdvance = Boolean(nextStatus) && current.status !== 'Closed';
  const latestDoc = type => documents.filter(doc => doc.type === type).sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0] || null;

  const mergeRun = async (updated, message) => {
    if (!updated) return;
    const merged = {
      ...current,
      ...updated,
      documents: Array.isArray(updated.documents) && updated.documents.length
        ? updated.documents
        : current.documents || []
    };
    const refreshed = await rpRefreshAfterMutation(message);
    if (!refreshed) {
      const i = RP.PRODUCTION.findIndex(p => p.id === merged.id);
      if (i >= 0) RP.PRODUCTION.splice(i, 1, merged);
    }
    setCurrent(merged);
  };

  const advance = async () => {
    if (!canAdvance || busy) return;
    if (nextStatus === 'Closed' && !window.confirm(`Close ${current.id}? This marks production paperwork complete. Released cases remain in inventory for transfer or sale. It adds, removes and moves no stock.`)) return;
    setErr('');
    if (releaseBlocked) {
      setErr('Verify the lot production/expiry dates and attach a COA or QA report before release.');
      return;
    }
    const outputCases = Number(actualOutput);
    const output = outputCases * (RP.FINISHED_GOODS_CASE_PACK_SIZE || 12);
    const waste = Number(wasteCans);
    if (nextStatus === 'Produced' && (!Number.isInteger(outputCases) || outputCases <= 0 || !Number.isInteger(waste) || waste < 0 || output + waste !== Number(current.cans))) {
      setErr(`Enter whole sealed flats and waste so output plus waste exactly equals the signed plan of ${Number(current.cans).toLocaleString()} cans.`);
      return;
    }
    setBusy(true);
    try {
      let updated = { ...current, status: nextStatus };
      if (nextStatus === 'Released' || nextStatus === 'Closed') updated.qa = 'Passed';
      if (nextStatus === 'Produced') {
        updated.output = output;
        updated.waste = waste;
      }
      if (nextStatus === 'Produced' || nextStatus === 'QA Hold' || nextStatus === 'Released') {
        updated.output = updated.output || updated.cans;
        updated.waste = updated.waste == null ? 0 : updated.waste;
      }
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/production/status', {
          method: 'POST',
          body: JSON.stringify({ id: current.id, status: nextStatus, ...(nextStatus === 'Produced' ? { output, outputCases, waste } : {}) })
        });
        updated = response.run || updated;
        await mergeRun(updated, 'Production run updated');
      } else {
        const i = RP.PRODUCTION.findIndex(p => p.id === updated.id);
        if (i >= 0) RP.PRODUCTION.splice(i, 1, updated);
        toast(`${updated.id} moved to ${rpProductionStatusLabel(nextStatus)}`);
        setCurrent(updated);
      }
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not update production status.');
    } finally {
      setBusy(false);
    }
  };

  const chooseProductionDocument = (type) => {
    setErr('');
    setDocType(type);
    if (window.RP_AUTH && window.RP_AUTH.demo) {
      const def = PRODUCTION_DOC_DEFS.find(d => d.type === type) || PRODUCTION_DOC_DEFS[0];
      const doc = { id: `demo-${Date.now()}`, type, label: def.label, name: 'Demo upload', createdAt: new Date().toISOString() };
      const updated = { ...current, documents: [doc].concat(current.documents || []), coa: type === 'coa' ? doc.name : current.coa };
      const i = RP.PRODUCTION.findIndex(p => p.id === updated.id);
      if (i >= 0) RP.PRODUCTION.splice(i, 1, updated);
      setCurrent(updated);
      toast(`${def.label} attached in demo mode`, 'info');
      return;
    }
    if (docInput.current) {
      docInput.current.value = '';
      docInput.current.click();
    }
  };

  const onProductionDocumentFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    const type = docType;
    if (!file || !type) return;
    const def = PRODUCTION_DOC_DEFS.find(d => d.type === type) || PRODUCTION_DOC_DEFS[0];
    setDocBusy(type);
    setErr('');
    try {
      const uploaded = await rpUploadPrivateFile('production_document', file, `${current.id}-${type}`);
      const response = await rpApi('/api/production/document', {
        method: 'POST',
        body: JSON.stringify({
          contentType: uploaded.type,
          documentType: type,
          fileName: uploaded.name,
          filePath: uploaded.path,
          fileSize: uploaded.size,
          label: def.label,
          runId: current.id
        })
      });
      const document = response.document;
      await mergeRun({
        ...current,
        documents: [document].concat(current.documents || []),
        coa: type === 'coa' ? (document.name || document.label) : current.coa
      }, `${current.id} document uploaded`);
      toast(`${def.label} uploaded`, 'ok');
    } catch (error) {
      setErr(error && error.message ? error.message : 'Could not upload production document.');
    } finally {
      setDocBusy('');
      setDocType('');
    }
  };
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      <input ref={docInput} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={onProductionDocumentFile} />
      <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />Back to Production</button>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className="disp" style={{ margin: 0, fontSize: 32, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{current.id}</h1>
            <Pill status={current.status}>{rpProductionStatusLabel(current.status)}</Pill>
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}><FlavorChip sku={s || { flavor: current.flavor }} size={20} />{rpSkuDisplayName(s || current.sku || 'Production SKU')} - {current.partner}</div>
        </div>
        <div style={{ display: 'flex', gap: 9 }}>
          {current.immutable ? <span className="pill" style={{ color: 'var(--muted)' }}><Icon name="lock" size={13} />Signed plan · immutable</span> : <>
          <button className="btn btn-sm" onClick={() => toast('Opening run editor…', 'info')}><Icon name="pen" size={15} />Edit</button>
          </>}
          {canAdvance && <button className="btn btn-sm btn-primary" disabled={busy || releaseBlocked} onClick={advance} style={{ opacity: busy || releaseBlocked ? 0.55 : 1 }}><Icon name="check" size={15} />{busy ? 'Saving...' : rpProductionActionLabel(current.status)}</button>}
        </div>
      </div>
      {err && <div style={{ marginTop: -10, marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{err}</div>}
      {nextStatus === 'Released' && <div style={{ marginTop: -10, marginBottom: 14, background: releaseBlocked ? 'var(--warn-bg)' : 'var(--ok-bg)', color: releaseBlocked ? 'var(--amber-deep)' : 'var(--ok)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 650 }}>
        Release evidence: {verifiedLotDates ? 'verified production and expiry dates' : 'verify production and expiry dates'} · {qaEvidence ? 'COA/QA report attached' : 'attach a COA or QA report'}.
      </div>}

      {/* Stepper */}
      <div className="card" style={{ padding: '18px 22px', marginBottom: 18, overflowX: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', minWidth: 720 }}>
          <div style={{ position: 'absolute', top: 11, left: 10, right: 10, height: 2, background: 'var(--line)' }} />
          <div style={{ position: 'absolute', top: 11, left: 10, height: 2, background: current.status === 'QA Hold' ? 'var(--warn)' : 'var(--ok)', width: `calc(${(idx / (RP.PROD_STATUSES.length - 1)) * 100}% - 20px)` }} />
          {RP.PROD_STATUSES.map((st, i) => {
            const done = i <= idx, hold = current.status === 'QA Hold' && st === 'QA Hold';
            return (
              <div key={st} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, position: 'relative', zIndex: 1 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: done ? (hold ? 'var(--warn)' : 'var(--ok)') : 'var(--surface)', border: '2px solid ' + (done ? (hold ? 'var(--warn)' : 'var(--ok)') : 'var(--line)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{done && (hold ? <Icon name="alert" size={13} /> : <Icon name="check" size={13} />)}</div>
                <span style={{ fontSize: 11, fontWeight: i === idx ? 700 : 500, color: i === idx ? 'var(--ink)' : 'var(--muted)', whiteSpace: 'nowrap' }}>{rpProductionStatusLabel(st)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 18 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}><SectionTitle>Bill of Materials</SectionTitle></div>
          <table className="rp-table">
            <thead><tr><th>Input</th><th>Supplier Lot</th><th className="num">Required</th><th className="num">Available</th><th className="num">Consumed</th><th>Status</th></tr></thead>
            <tbody>
              {inputs.length === 0 ? <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--faint)', padding: 24 }}>{current.finishedGoodsOnly ? 'No material inputs — historical finished-goods-only record.' : 'No verified BOM inputs are attached.'}</td></tr> : inputs.map((inp, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{inp.name}</td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--info)' }}>{inp.lot}</td>
                  <td className="num">{inp.req}</td>
                  <td className="num" style={{ color: inp.ok ? 'var(--muted)' : 'var(--danger)', fontWeight: inp.ok ? 400 : 700 }}>{inp.avail}</td>
                  <td className="num" style={{ color: inp.consumedQty ? 'var(--ok)' : 'var(--faint)', fontWeight: inp.consumedQty ? 700 : 400 }}>{rpProductionQtyLabel(inp.consumedQty, inp.unit)}</td>
                  <td><Pill status={inp.ok ? 'Available' : 'Short'}>{inp.ok ? 'OK' : 'Short'}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
          {inputs.some(i => !i.ok) && <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '11px 18px', background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 13, fontWeight: 600 }}><Icon name="alert" size={16} />Inputs are short or missing lots. Resolve the shortage before checking inputs.</div>}
          {!current.finishedGoodsOnly && ['Scheduled', 'Inputs Allocated'].includes(current.status) && <div style={{ padding: '10px 18px', background: 'var(--info-bg)', color: 'var(--muted)', fontSize: 12.5 }}>Inputs Checked is a point-in-time availability check, not a reservation. Availability is checked again before any consumption.</div>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle>Run Plan</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
              <Row k="Planned cans" v={(current.cans || 0).toLocaleString()} /><Row k="Planned cases" v={(current.cases || 0) + ' cs'} /><Row k="Finished lot" v={<span className="mono">{current.lot || 'Lot pending'}</span>} /><Row k="Lot group" v={current.lotGroup || '-'} /><Row k="Run date" v={<span className="mono">{current.runDate}</span>} /><Row k="Partner" v={current.partner} /><Row k="QA hold location" v={rpProductionLocation(current)} /><Row k="Material tracking" v={current.finishedGoodsOnly ? 'Historical finished goods only' : 'Exact input lots'} />
              {current.finishedGoodsOnly && <Row k="Tracking exception" v={current.materialTrackingNote || 'Exception note retained in the production record'} />}
            </div>
          </div>
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle>Actuals & QA</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
              {['In Production', 'Produced'].includes(current.status) && <><MField label="Actual Sealed Cases / Flats"><input className="field mono" type="number" min="1" step="1" value={actualOutput} disabled={current.status === 'Produced'} onChange={e => setActualOutput(e.target.value)} /></MField><MField label="Waste Cans"><input className="field mono" type="number" min="0" step="1" value={wasteCans} disabled={current.status === 'Produced'} onChange={e => setWasteCans(e.target.value)} /></MField></>}
              <Row k="Actual output" v={current.output ? `${(current.output / (RP.FINISHED_GOODS_CASE_PACK_SIZE || 12)).toLocaleString()} cs · ${current.output.toLocaleString()} cans` : 'TBD'} />
              <Row k="Loose sellable cans" v={current.output ? '0 · remainder must be recorded as waste' : 'TBD'} />
              <Row k="Waste" v={current.waste != null ? current.waste + ' cans' : 'TBD'} />
              <Row k="QA status" v={<Pill status={current.qa === 'Passed' ? 'Approved' : current.qa === 'Pending' ? 'Pending' : 'QA Hold'}>{current.qa}</Pill>} />
            </div>
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PRODUCTION_DOC_DEFS.map(def => {
                const doc = latestDoc(def.type);
                const busyDoc = docBusy === def.type;
                return (
                  <div key={def.type} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 11px', border: '1px solid var(--line)', borderRadius: 9, background: 'var(--surface-2)' }}>
                    <Icon name="doc" size={15} style={{ color: doc ? 'var(--ok)' : 'var(--faint)' }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600 }}>
                      {def.label}
                      {doc && <div className="mono" style={{ marginTop: 2, color: 'var(--faint)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name || doc.label}</div>}
                    </span>
                    {doc && doc.url && <button className="btn btn-ghost btn-sm" style={{ padding: 0, color: 'var(--amber-deep)' }} onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}>View</button>}
                    <button className="btn btn-sm" disabled={busyDoc} onClick={() => chooseProductionDocument(def.type)}><Icon name={busyDoc ? 'refresh' : 'doc'} size={14} />{busyDoc ? 'Uploading...' : doc ? 'Replace' : 'Upload'}</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ PROCUREMENT */
function ProcurementView({ go }) {
  const [tab, setTab] = useState('po'); // po | suppliers | tariff
  const [sel, setSel] = useState(null);
  const [newPO, setNewPO] = useState(null); // false | {} | presetItem
  const [newSupplier, setNewSupplier] = useState(false);
  const [newMaterial, setNewMaterial] = useState(false);
  const [, force] = useState(0);
  if (sel) return <PODetail po={RP.PURCHASE_ORDERS.find(p => p.id === sel)} back={() => setSel(null)} />;
  const bump = () => force(x => x + 1);
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {newPO !== null && <NewPurchaseOrderModal presetItem={newPO || undefined} onClose={() => setNewPO(null)} onAdded={bump} />}
      {newSupplier && <AddSupplierModal onClose={() => setNewSupplier(false)} onAdded={bump} />}
      {newMaterial && <AddMaterialModal onClose={() => setNewMaterial(false)} onAdded={bump} />}
      <PageHead eyebrow="Materials & Suppliers" title="Procurement"
        actions={<>
          {tab === 'suppliers' && <button className="btn btn-sm" onClick={() => setNewSupplier(true)}><Icon name="plus" size={15} />Supplier</button>}
          {tab === 'tariff' && <button className="btn btn-sm" onClick={() => setNewMaterial(true)}><Icon name="plus" size={15} />Material</button>}
          <button className="btn btn-sm btn-primary" onClick={() => setNewPO({})}><Icon name="plus" size={15} />Purchase Order</button>
        </>} />

      {/* Reorder alerts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {RP.REORDER_ALERTS.map((a, i) => {
          const [fg, bg] = statusColors(a.sev === 'high' ? 'High' : 'Pending');
          return <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, background: bg, border: '1px solid ' + fg + '40', borderRadius: 10, padding: '11px 15px' }}>
            <Icon name="alert" size={17} style={{ color: fg }} />
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{a.item}</span>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>— {a.msg}</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: fg }}>{a.cover} cover</span>
            <button className="btn btn-sm" onClick={() => setNewPO({ name: a.item, category: /lid/i.test(a.item) ? 'Lids' : /can/i.test(a.item) ? 'Cans' : 'Ingredient' })}>Reorder</button>
          </div>;
        })}
      </div>

      <div style={{ marginBottom: 14 }}><Segmented options={[{ value: 'po', label: 'Purchase Orders' }, { value: 'suppliers', label: 'Suppliers' }, { value: 'tariff', label: 'Aluminum Origin & Tariff' }]} value={tab} onChange={setTab} /></div>

      {tab === 'po' ? (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>PO</th><th>Supplier</th><th>Category</th><th>ETA</th><th className="num">Value</th><th>Status</th><th>Risk</th><th></th></tr></thead>
            <tbody>
              {RP.PURCHASE_ORDERS.map(p => {
                const val = p.items.reduce((s, it) => s + it.ordered * it.unit, 0);
                return (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setSel(p.id)}>
                    <td className="mono nowrap" style={{ fontWeight: 600 }}>{p.id}</td>
                    <td style={{ fontWeight: 500 }}>{p.supplier}</td>
                    <td style={{ color: 'var(--muted)' }}>{p.category}</td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{p.eta}</td>
                    <td className="num" style={{ fontWeight: 600 }}>{RP.money(val)}</td>
                    <td><Pill status={p.status} /></td>
                    <td><Pill status={p.risk} /></td>
                    <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : tab === 'suppliers' ? (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Supplier</th><th>Supplies</th><th>Origin</th><th>Terms</th><th>Contact</th><th className="num">Open POs</th><th>Status</th></tr></thead>
            <tbody>
              {RP.SUPPLIERS.map(s => (
                <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => setNewPO({ category: s.category === 'Co-Packer' ? 'Other' : s.category, name: '', supplier: s.name })}>
                  <td style={{ fontWeight: 600 }}>{s.name}<div className="mono" style={{ fontSize: 11, color: 'var(--faint)', fontWeight: 400 }}>{s.id}</div></td>
                  <td style={{ color: 'var(--muted)' }}>{s.category}</td>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: s.country === 'Canada' ? 'var(--ok)' : s.country === 'China' ? 'var(--danger)' : 'var(--warn)' }} />{s.country}</span></td>
                  <td className="mono" style={{ fontSize: 12.5 }}>{s.terms}</td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{s.contact}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{s.openPOs || 0}</td>
                  <td><Pill status={s.status === 'Watch' ? 'Watch' : 'Active'}>{s.status}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Co-packers double as production partners. Click a supplier to start a PO.</span>
            <button className="btn btn-sm btn-primary" onClick={() => setNewSupplier(true)}><Icon name="plus" size={14} />Add Supplier</button>
          </div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--muted)' }}>
            <Icon name="layers" size={15} />Country of origin and HS codes flow to finished-goods lots for tariff exposure.
          </div>
          <table className="rp-table">
            <thead><tr><th>Material Lot</th><th>Supplier</th><th>Material</th><th className="num">On Hand</th><th className="num">Damaged</th><th>Origin</th><th>HS Code</th><th>Duty</th><th>Docs</th></tr></thead>
            <tbody>
              {RP.MATERIALS.map(m => (
                <tr key={m.lot}>
                  <td className="mono" style={{ fontWeight: 600, color: 'var(--info)' }}>{m.lot}</td>
                  <td>{m.supplier}</td>
                  <td style={{ color: 'var(--muted)' }}>{m.material}</td>
                  <td className="num" style={{ fontWeight: 700 }}>{(m.onHand || 0).toLocaleString()}</td>
                  <td className="num" style={{ color: m.damaged ? 'var(--danger)' : 'var(--muted)', fontWeight: m.damaged ? 700 : 400 }}>{(m.damaged || 0).toLocaleString()}</td>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: m.origin === 'Canada' ? 'var(--ok)' : m.origin === 'China' ? 'var(--danger)' : 'var(--warn)' }} />{m.origin}</span></td>
                  <td className="mono" style={{ fontSize: 12 }}>{m.hs}</td>
                  <td className="mono" style={{ fontWeight: 600, color: m.duty === '0%' || m.duty === 'N/A' ? 'var(--muted)' : 'var(--danger)' }}>{m.duty}</td>
                  <td><Pill status={m.docs === 'Complete' ? 'Approved' : m.docs === 'Missing' ? 'Damaged' : 'Pending'}>{m.docs}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <button className="btn btn-sm btn-primary" onClick={() => setNewMaterial(true)}><Icon name="plus" size={14} />Add Material</button>
          </div>
        </div>
      )}
    </div>
  );
}

const PO_DOC_DEFS = [
  { type: 'supplier_invoice', label: 'Supplier Invoice' },
  { type: 'origin_docs', label: 'COO / Origin Docs' },
  { type: 'tariff_notes', label: 'Tariff Notes' }
];

function PODetail({ po, back }) {
  const [, force] = useState(0);
  const [status, setStatus] = useState(po.status);
  const [receiving, setReceiving] = useState(false);
  const [statusBusy, setStatusBusy] = useState('');
  const [docBusy, setDocBusy] = useState('');
  const [docType, setDocType] = useState('');
  const [docError, setDocError] = useState('');
  const docInput = useRef(null);
  const val = po.items.reduce((s, it) => s + it.ordered * it.unit, 0);
  const fullyReceived = po.items.every(it => (it.recvd || 0) >= it.ordered);
  const anyReceived = po.items.some(it => (it.recvd || 0) > 0);
  const authRole = rpNormalizeRole(window.RP_AUTH && window.RP_AUTH.profile && window.RP_AUTH.profile.role);
  const canUpdatePOStatus = !rpHasLiveAuth() || ['admin', 'leadership', 'ops', 'accountant'].includes(authRole);
  const canReceivePO = !rpHasLiveAuth() || ['admin', 'leadership', 'ops', 'warehouse'].includes(authRole);
  const onReceived = (newStatus) => { po.status = newStatus; setStatus(newStatus); force(x => x + 1); };
  const documents = Array.isArray(po.documents) ? po.documents : [];
  const latestDoc = type => documents.filter(doc => doc.type === type).sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0] || null;

  const syncPO = async (updated, message) => {
    if (updated) {
      const merged = { ...po, ...updated, documents: updated.documents || po.documents || [] };
      Object.assign(po, merged);
      const idx = RP.PURCHASE_ORDERS.findIndex(x => x.id === updated.id);
      if (idx >= 0) RP.PURCHASE_ORDERS.splice(idx, 1, merged);
    }
    const refreshed = await rpRefreshAfterMutation(message);
    if (!refreshed) force(x => x + 1);
  };

  const updatePOStatus = async (nextStatus) => {
    if (statusBusy) return;
    setStatusBusy(nextStatus);
    setDocError('');
    try {
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/procurement/status', {
          method: 'POST',
          body: JSON.stringify({ id: po.id, status: nextStatus })
        });
        const updated = response.purchaseOrder;
        if (updated) {
          setStatus(updated.status);
          await syncPO(updated, `${po.id} ${updated.status}`);
        }
      } else {
        po.status = nextStatus;
        setStatus(nextStatus);
        force(x => x + 1);
      }
      toast(`${po.id} marked ${nextStatus}`, nextStatus === 'Delayed' || nextStatus === 'Cancelled' ? 'warn' : 'ok');
    } catch (err) {
      setDocError(err && err.message ? err.message : 'Could not update purchase order status.');
    } finally {
      setStatusBusy('');
    }
  };

  const chooseDocument = (type) => {
    setDocError('');
    setDocType(type);
    if (window.RP_AUTH && window.RP_AUTH.demo) {
      const def = PO_DOC_DEFS.find(d => d.type === type);
      po.documents = [{ id: `demo-${Date.now()}`, type, label: def ? def.label : 'Document', name: 'Demo upload', createdAt: new Date().toISOString() }].concat(po.documents || []);
      force(x => x + 1);
      toast('Document attached in demo mode', 'info');
      return;
    }
    if (docInput.current) {
      docInput.current.value = '';
      docInput.current.click();
    }
  };

  const onDocumentFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    const type = docType;
    if (!file || !type) return;
    const def = PO_DOC_DEFS.find(d => d.type === type) || PO_DOC_DEFS[0];
    setDocBusy(type);
    setDocError('');
    try {
      const uploaded = await rpUploadPrivateFile('procurement_document', file, `${po.id}-${type}`);
      const response = await rpApi('/api/procurement/document', {
        method: 'POST',
        body: JSON.stringify({
          contentType: uploaded.type,
          documentType: type,
          fileName: uploaded.name,
          filePath: uploaded.path,
          fileSize: uploaded.size,
          label: def.label,
          poId: po.id
        })
      });
      po.documents = [response.document].concat(po.documents || []);
      await syncPO(po, `${po.id} document uploaded`);
      toast(`${def.label} uploaded`, 'ok');
    } catch (err) {
      setDocError(err && err.message ? err.message : 'Could not upload procurement document.');
    } finally {
      setDocBusy('');
      setDocType('');
    }
  };
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {receiving && <ReceivePOModal po={po} onClose={() => setReceiving(false)} onDone={onReceived} />}
      <input ref={docInput} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={onDocumentFile} />
      <button className="btn btn-ghost btn-sm" onClick={back} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />Back to Procurement</button>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}><h1 className="disp" style={{ margin: 0, fontSize: 32, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{po.id}</h1><Pill status={status} /><Pill status={po.risk}>{po.risk} risk</Pill></div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 4 }}>{po.supplier} · {po.category}</div>
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canUpdatePOStatus && (status === 'Draft' || status === 'Open' || status === 'Submitted' || status === 'Pending') && <button className="btn btn-sm" disabled={Boolean(statusBusy)} onClick={() => updatePOStatus('Approved')}><Icon name={statusBusy === 'Approved' ? 'refresh' : 'check'} size={15} />{statusBusy === 'Approved' ? 'Saving...' : 'Approve'}</button>}
          {canUpdatePOStatus && status === 'Approved' && <button className="btn btn-sm" disabled={Boolean(statusBusy)} onClick={() => updatePOStatus('Shipped')}><Icon name={statusBusy === 'Shipped' ? 'refresh' : 'truck'} size={15} />{statusBusy === 'Shipped' ? 'Saving...' : 'Mark Shipped'}</button>}
          {canUpdatePOStatus && ['Open', 'Approved', 'Shipped'].includes(status) && <button className="btn btn-sm" disabled={Boolean(statusBusy)} onClick={() => updatePOStatus('Delayed')}><Icon name={statusBusy === 'Delayed' ? 'refresh' : 'alert'} size={15} />Delay</button>}
          {canUpdatePOStatus && status === 'Delayed' && <button className="btn btn-sm" disabled={Boolean(statusBusy)} onClick={() => updatePOStatus('Shipped')}><Icon name={statusBusy === 'Shipped' ? 'refresh' : 'truck'} size={15} />Mark Shipped</button>}
          {canUpdatePOStatus && (status === 'Received' || status === 'Partial') && <button className="btn btn-sm" disabled={Boolean(statusBusy)} onClick={() => updatePOStatus('Closed')}><Icon name={statusBusy === 'Closed' ? 'refresh' : 'check'} size={15} />Close</button>}
          {canReceivePO && !fullyReceived && <button className="btn btn-sm btn-primary" onClick={() => setReceiving(true)}><Icon name="download" size={15} />{anyReceived ? 'Receive Remaining' : 'Receive'}</button>}
          {fullyReceived && <Pill status="Delivered">Fully Received</Pill>}
        </div>
      </div>
      {status === 'Delayed' && <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13.5, fontWeight: 600 }}><Icon name="alert" size={17} />Delayed — this PO blocks the PRD-118 Ginger Citrus run (short on lids).</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 18 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Item</th><th>Lot</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Unit Cost</th><th>Origin</th><th>Tariff</th></tr></thead>
            <tbody>
              {po.items.map((it, i) => {
                const recvd = it.recvd || 0, partial = recvd > 0 && recvd < it.ordered, full = recvd >= it.ordered;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{it.name}{it.dmg ? <span style={{ fontSize: 11, color: 'var(--danger)', marginLeft: 7, fontWeight: 600 }}>{it.dmg} dmg</span> : null}</td>
                    <td className="mono" style={{ fontSize: 12, color: it.lot ? 'var(--info)' : 'var(--faint)' }}>{it.lot || 'Pending'}</td>
                    <td className="num">{it.ordered.toLocaleString()}</td>
                    <td className="num" style={{ color: full ? 'var(--ok)' : partial ? 'var(--warn)' : 'var(--muted)', fontWeight: recvd ? 700 : 400 }}>{recvd.toLocaleString()}</td>
                    <td className="num">{RP.money2(it.unit)}</td>
                    <td>{it.origin}</td>
                    <td className="mono" style={{ color: it.tariff === 'N/A' ? 'var(--muted)' : 'var(--danger)' }}>{it.tariff}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr style={{ background: 'var(--surface-2)' }}><td colSpan="6" style={{ fontWeight: 600, padding: '12px 14px' }}>PO Value</td><td className="num" style={{ fontWeight: 700, fontSize: 15, padding: '12px 14px' }}>{RP.money(val)}</td></tr></tfoot>
          </table>
        </div>
        <div className="card" style={{ padding: '18px 20px' }}>
          <SectionTitle>Documents</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {PO_DOC_DEFS.map(def => {
              const doc = latestDoc(def.type);
              const busy = docBusy === def.type;
              return (
                <div key={def.type} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', border: '1px solid var(--line)', borderRadius: 9, background: 'var(--surface-2)' }}>
                  <Icon name="doc" size={16} style={{ color: doc ? 'var(--ok)' : 'var(--faint)' }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500 }}>
                    {def.label}
                    {doc && <div className="mono" style={{ marginTop: 2, color: 'var(--faint)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name || doc.label}</div>}
                  </span>
                  {doc && doc.url && <button className="btn btn-ghost btn-sm" style={{ padding: 0, color: 'var(--amber-deep)' }} onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}>View</button>}
                  <button className="btn btn-sm" disabled={busy} onClick={() => chooseDocument(def.type)}><Icon name={busy ? 'refresh' : 'doc'} size={14} />{busy ? 'Uploading...' : doc ? 'Replace' : 'Upload'}</button>
                </div>
              );
            })}
          </div>
          {docError && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{docError}</div>}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
            <Row k="Payment terms" v={po.terms} /><Row k="ETA" v={<span className="mono">{po.eta}</span>} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- PO Receiving modal ---- */
function DemoReceivePOModal({ po, onClose, onDone }) {
  const [rows, setRows] = useState(() => po.items.map(it => {
    const remaining = it.ordered - (it.recvd || 0);
    return { recv: remaining, dmg: 0 };
  }));
  const [docsOk, setDocsOk] = useState(false);
  const set = (i, k, v) => setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: Math.max(0, v) } : r));

  const totalRecv = rows.reduce((s, r) => s + r.recv, 0);
  const totalDmg = rows.reduce((s, r) => s + r.dmg, 0);
  const anyVariance = po.items.some((it, i) => (it.recvd || 0) + rows[i].recv !== it.ordered) || totalDmg > 0;

  const confirm = () => {
    po.items.forEach((it, i) => {
      it.recvd = (it.recvd || 0) + rows[i].recv;
      if (rows[i].dmg) it.dmg = (it.dmg || 0) + rows[i].dmg;
    });
    const full = po.items.every(it => (it.recvd || 0) >= it.ordered);
    const status = full ? 'Received' : 'Partial';
    toast(`${po.id} ${full ? 'fully received' : 'partially received'} · ${totalRecv.toLocaleString()} units to stock${totalDmg ? `, ${totalDmg} damaged` : ''}`, totalDmg ? 'warn' : 'ok');
    onDone(status);
    onClose();
  };

  return (
    <Modal title={`Receive ${po.id}`} sub={`${po.supplier} · against ordered quantities`} onClose={onClose} width={620}
      footer={<>
        <div style={{ flex: 1, alignSelf: 'center', fontSize: 12.5, color: 'var(--muted)' }}>{totalRecv.toLocaleString()} to receive{totalDmg ? ` · ${totalDmg} damaged` : ''}</div>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={confirm} style={{ opacity: docsOk || !anyVariance ? 1 : 0.5 }} disabled={anyVariance && !docsOk}><Icon name="check" size={15} />Confirm Receipt</button>
      </>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface-2)', borderRadius: 9, padding: '10px 13px', marginBottom: 16, fontSize: 12.5, color: 'var(--muted)' }}>
        <Icon name="layers" size={15} />Received quantities post to the material lot and the ledger. Damages route to a write-off review.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {po.items.map((it, i) => {
          const already = it.recvd || 0, remaining = it.ordered - already;
          const over = rows[i].recv > remaining;
          return (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 11, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{it.name}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{already.toLocaleString()} / {it.ordered.toLocaleString()} received</span>
              </div>
              <div style={{ display: 'flex', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <label className="field-label">Received now</label>
                  <NumStepper value={rows[i].recv} onChange={v => set(i, 'recv', v)} />
                  {over && <div style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 600, marginTop: 5 }}>Over remaining ({remaining.toLocaleString()}) — overage flagged</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="field-label">Damaged</label>
                  <NumStepper value={rows[i].dmg} onChange={v => set(i, 'dmg', v)} danger />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {anyVariance && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'var(--warn-bg)', cursor: 'pointer' }}>
          <input type="checkbox" checked={docsOk} onChange={e => setDocsOk(e.target.checked)} style={{ width: 17, height: 17, accentColor: 'var(--amber)' }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--amber-deep)' }}>Variance present — I confirm the count and packing slip have been checked.</span>
        </label>
      )}
    </Modal>
  );
}

function NumStepper({ value, onChange, danger }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', width: 'fit-content' }}>
      <button className="btn btn-ghost" style={{ padding: '7px 11px', borderRadius: 0 }} onClick={() => onChange(value - 1)}><Icon name="minus" size={14} /></button>
      <input className="mono tnum" value={value} onChange={e => onChange(parseInt(e.target.value.replace(/\D/g, '')) || 0)} style={{ width: 70, textAlign: 'center', border: 'none', borderLeft: '1px solid var(--line)', borderRight: '1px solid var(--line)', padding: '7px 0', fontWeight: 600, fontSize: 14, color: danger && value > 0 ? 'var(--danger)' : 'var(--ink)', background: 'var(--surface)' }} />
      <button className="btn btn-ghost" style={{ padding: '7px 11px', borderRadius: 0, color: danger ? 'var(--danger)' : 'var(--amber-deep)' }} onClick={() => onChange(value + 1)}><Icon name="plus" size={14} /></button>
    </div>
  );
}

/* ---- Live PO receiving override ---- */
function ReceivePOModal({ po, onClose, onDone }) {
  const [rows, setRows] = useState(() => po.items.map(it => {
    const remaining = Math.max(0, it.ordered - (it.recvd || 0));
    return { recv: remaining, dmg: 0 };
  }));
  const [docsOk, setDocsOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (i, k, v) => setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: Math.max(0, v) } : r));

  const totalRecv = rows.reduce((s, r) => s + r.recv, 0);
  const totalDmg = rows.reduce((s, r) => s + r.dmg, 0);
  const anyVariance = po.items.some((it, i) => (it.recvd || 0) + rows[i].recv !== it.ordered) || totalDmg > 0;
  const canConfirm = !busy && (docsOk || !anyVariance) && (totalRecv > 0 || totalDmg > 0);

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setError('');
    try {
      if (rpHasLiveAuth()) {
        const response = await rpApi('/api/procurement/receive-po', {
          method: 'POST',
          body: JSON.stringify({
            id: po.id,
            lines: po.items.map((it, i) => ({
              damaged: rows[i].dmg,
              lineId: it.lineId || it.id,
              received: rows[i].recv
            }))
          })
        });
        const updated = response.purchaseOrder;
        const refreshed = await rpRefreshAfterMutation(`${po.id} received`);
        if (!refreshed && updated) {
          Object.assign(po, updated);
          const idx = RP.PURCHASE_ORDERS.findIndex(x => x.id === updated.id);
          if (idx >= 0) RP.PURCHASE_ORDERS.splice(idx, 1, updated);
        }
        toast(`${po.id} received - ${totalRecv.toLocaleString()} units${totalDmg ? `, ${totalDmg} damaged` : ''}`, totalDmg ? 'warn' : 'ok');
        onDone(updated ? updated.status : 'Received');
        onClose();
        return;
      }

      po.items.forEach((it, i) => {
        it.recvd = (it.recvd || 0) + rows[i].recv;
        if (rows[i].dmg) it.dmg = (it.dmg || 0) + rows[i].dmg;
      });
      const full = po.items.every(it => (it.recvd || 0) >= it.ordered);
      const status = full ? 'Received' : 'Partial';
      toast(`${po.id} ${full ? 'fully received' : 'partially received'} - ${totalRecv.toLocaleString()} units${totalDmg ? `, ${totalDmg} damaged` : ''}`, totalDmg ? 'warn' : 'ok');
      onDone(status);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not receive purchase order.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Receive ${po.id}`} sub={`${po.supplier} - against ordered quantities`} onClose={onClose} width={620}
      footer={<>
        <div style={{ flex: 1, alignSelf: 'center', fontSize: 12.5, color: 'var(--muted)' }}>{totalRecv.toLocaleString()} to receive{totalDmg ? ` - ${totalDmg} damaged` : ''}</div>
        <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={confirm} style={{ opacity: canConfirm ? 1 : 0.5 }} disabled={!canConfirm}><Icon name={busy ? 'refresh' : 'check'} size={15} />{busy ? 'Saving...' : 'Confirm Receipt'}</button>
      </>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface-2)', borderRadius: 9, padding: '10px 13px', marginBottom: 16, fontSize: 12.5, color: 'var(--muted)' }}>
        <Icon name="layers" size={15} />Received quantities post to the raw-material ledger. Damages are captured for write-off review.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {po.items.map((it, i) => {
          const already = it.recvd || 0, remaining = Math.max(0, it.ordered - already);
          const over = rows[i].recv > remaining;
          return (
            <div key={it.lineId || it.id || i} style={{ border: '1px solid var(--line)', borderRadius: 11, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{it.name}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{already.toLocaleString()} / {it.ordered.toLocaleString()} received</span>
              </div>
              <div style={{ display: 'flex', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <label className="field-label">Received now</label>
                  <NumStepper value={rows[i].recv} onChange={v => set(i, 'recv', v)} />
                  {over && <div style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 600, marginTop: 5 }}>Over remaining ({remaining.toLocaleString()}) - overage flagged</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="field-label">Damaged</label>
                  <NumStepper value={rows[i].dmg} onChange={v => set(i, 'dmg', v)} danger />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {anyVariance && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'var(--warn-bg)', cursor: 'pointer' }}>
          <input type="checkbox" checked={docsOk} onChange={e => setDocsOk(e.target.checked)} style={{ width: 17, height: 17, accentColor: 'var(--amber)' }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--amber-deep)' }}>Variance present - I confirm the count and packing slip have been checked.</span>
        </label>
      )}
      {error && <div style={{ marginTop: 10, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

Object.assign(window, { ProductionView, ProcurementView });


/* ===== inventory_workflow.jsx ===== */
/* Guided stock workflow. Drafts never change stock; the server freezes and posts
   the reviewed request once. All retries use that same saved workflow identity. */
const WF_ACTIONS = {
  can_run: { title: 'Cans were produced', description: 'Record BevMax’s output and the report you have.', icon: 'production', next: 'pack_cases', confirm: 'Record produced cans' },
  pack_cases: { title: 'Cases were packed', description: 'Turn exact can runs into sealed single or mixed cases.', icon: 'package', next: 'release', confirm: 'Create cases in QA Hold' },
  release: { title: 'Cases passed quality checks', description: 'Release an existing packaging run when it is cleared.', icon: 'check', next: 'transfer', confirm: 'Release these cases' },
  transfer: { title: 'Stock changed hands', description: 'Record a completed warehouse move, pickup or rep handoff.', icon: 'dispatch', next: 'acknowledge', confirm: 'Record this completed transfer' },
  acknowledge: { title: 'Stock arrived', description: 'Find the recorded transfer and confirm what was received.', icon: 'inventory', next: 'transfer', confirm: 'Acknowledge this exact transfer' },
  repack: { title: 'Build mixed packs', description: 'Open finished cases and rebuild them at one location.', icon: 'layers', next: 'transfer', confirm: 'Record these mixed packs' }
};
const WF_DEMO_KEY = 'rp-inventory-workflow-demo-v1';
function wfUuid() { return window.crypto.randomUUID(); }
function wfToday() { return typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10); }
function wfLabel(id) { const sku = (RP.SKUS || []).find(row => row.id === id); return sku ? rpSkuDisplayName(sku) : id || 'Product not confirmed'; }
function wfLocation(id) { const row = (RP.LOCATIONS || []).find(item => item.id === id); return row ? row.name : id || 'Location not confirmed'; }
function wfQuantity(value) { return Number.isInteger(Number(value)) && Number(value) > 0; }
function wfExactLot(value) { return /^[A-Z0-9._:-]+$/.test(String(value || '')) && !['UNASSIGNED', 'CYCLE_COUNT', 'UNKNOWN', 'TBD', 'PENDING', 'NA', 'N/A'].includes(value); }
function wfDate(value) { if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false; const date = new Date(`${value}T00:00:00Z`); return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value && value <= wfToday(); }
function wfLocalDateTime(value) { if (!value || !/Z$|[+-]\d\d:\d\d$/.test(value)) return value || ''; const date = new Date(value); if (Number.isNaN(date.getTime())) return ''; const pad = part => String(part).padStart(2, '0'); return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`; }
function wfCanRuns() { return (RP.CAN_PRODUCTION_RUNS || []).map(rpCanRunUi); }
function wfRecipe(id) { return (RP.PACK_RECIPES || []).filter(row => row.outputSkuId === id && row.active !== false).sort((a, b) => Number(b.recipeVersion) - Number(a.recipeVersion))[0]; }
function wfBevmax() { return (RP.LOCATIONS || []).find(row => row.active !== false && (/BEVMAX-AIRDRIE/i.test(row.facilityCode || '') || /bevmax/i.test(`${row.id} ${row.name} ${row.organization}`))); }
function wfTransferOptions(locationId) { return desktopUnacknowledgedInboundTransfers(locationId).filter(row => !row.reconciliationOnly); }
function wfBookkeepingTransfer(reference, facts) { return (RP.LOGISTICS_MOVEMENTS || []).some(row => row.id === reference && row.reconciliationOnly) || Boolean(facts && facts.transferSnapshot && facts.transferSnapshot.id === reference && facts.transferSnapshot.reconciliationOnly); }
function wfReceiptChoices(payload, facts) { const rows = wfTransferOptions(payload.toLocationId); const found = facts && facts.transferSnapshot; return found && !wfBookkeepingTransfer(found.id, facts) && !found.acknowledged && found.toLocationId === payload.toLocationId && !rows.some(row => row.id === found.id) ? [...rows, found] : rows; }
function wfPackagingRun(payload, facts) { return (RP.PACKAGING_RUNS || []).find(row => row.id === payload.id) || (facts && facts.packagingSnapshot && facts.packagingSnapshot.id === payload.id ? facts.packagingSnapshot : null); }
function wfHoldingRows(locationId, skuId) {
  const location = (RP.LOCATIONS || []).find(row => row.id === locationId);
  const statuses = location && location.type === 'Micro-Warehouse' ? ['Available', 'Fronted to Rep'] : ['Available'];
  const rows = new Map();
  (RP.INV || []).forEach(row => {
    if (row.loc !== locationId || row.unit === 'can' || !statuses.includes(row.status || 'Available') || (row.owner || 'Ranch Power') !== 'Ranch Power') return;
    const canonical = rpCanonicalInventorySkuId(row.sku);
    if (skuId && canonical !== skuId || !wfExactLot(row.lot)) return;
    const master = (RP.LOTS || []).find(lot => rpCanonicalInventorySkuId(lot.sku) === canonical && lot.lot === row.lot);
    if (master && (['Quarantined', 'QA Hold'].includes(master.lifecycleStatus) || master.expiresAt && master.expiresAt < wfToday())) return;
    const key = `${canonical}|${row.lot}`;
    const current = rows.get(key) || { skuId: canonical, lot: row.lot, quantity: 0 };
    current.quantity += Number(row.qty || 0); rows.set(key, current);
  });
  return Array.from(rows.values()).filter(row => row.quantity > 0);
}
function wfNewDraft(action) {
  const facility = wfBevmax();
  const payload = action === 'can_run' ? { id: '', canSkuId: '', producerLocationId: facility && facility.id || '', invoiceDate: '', productionDate: '', productionDateBasis: 'invoice_estimate', countStatus: 'reported', totalCans: '', expectedRevision: 0 }
    : action === 'pack_cases' ? { outputSkuId: '', outputLocationId: facility && facility.id || '', lot: '', lotGroup: '', packedOn: '', cases: '', recipeVersion: 0, inventoryMode: 'post_to_inventory', allocations: [] }
    : action === 'release' ? { id: '', expectedStatus: 'QA Hold', status: 'Released', note: '' }
    : action === 'transfer' ? { fromLocationId: '', toLocationId: '', movementMethod: '', handledBy: '', carrier: '', occurredAt: '', chepUsage: '', chepPalletCount: '', bol: '', trackingNumber: '', lines: [{ skuId: '', lot: '', quantity: '' }] }
    : action === 'acknowledge' ? { toLocationId: '', linkedTransferRef: '', receivedAt: '', lineItems: [] }
    : { locationId: '', targetSkuId: '', targetLot: '', repackedOn: '', targetStatus: '', batchCount: '', sourceLines: [] };
  return { id: `WF-${wfUuid()}`, revision: 0, status: 'draft', action, title: WF_ACTIONS[action].title, payload, facts: { history: '', notes: '', evidenceReferences: '', followupName: '', nextDetail: '', attachments: [] }, canEdit: true };
}
function wfReceiptPayload(transfer, previous) {
  if (!transfer) return { ...previous, linkedTransferRef: '', sourceLocationId: '', lineItems: [] };
  return { ...previous, linkedTransferRef: transfer.id, sourceLocationId: transfer.fromLocationId, toLocationId: transfer.toLocationId,
    movementMethod: transfer.movementMethod, handledBy: transfer.handledBy || '', carrier: transfer.carrier || '', bol: transfer.bol || '', trackingNumber: transfer.trackingNumber || '', occurredAt: transfer.occurredAt,
    chepUsage: transfer.chepUsage, chepPalletCount: Number(transfer.chepPallets || 0), pallets: Number(transfer.chepPallets || 0), historicalOpening: false,
    lineItems: (transfer.lines || []).map(line => ({ skuId: line.skuId, lot: line.lot, quantity: Number(line.quantity), damagedQuantity: 0 })) };
}
function wfPreparedDraft(draft) {
  const payload = JSON.parse(JSON.stringify(draft.payload || {}));
  const facts = { ...draft.facts };
  payload.notes = [facts.notes, facts.evidenceReferences && `Evidence: ${facts.evidenceReferences}`].filter(Boolean).join('\n');
  if (draft.action === 'release') payload.note = payload.notes || 'Quality release confirmed in Move Stock.';
  if (draft.action === 'transfer' && payload.occurredAt) { const date = new Date(payload.occurredAt); if (!Number.isNaN(date.getTime())) payload.occurredAt = date.toISOString(); }
  if (draft.action === 'acknowledge') payload.lineItems = (payload.lineItems || []).map(line => ({ ...line, receivedAt: payload.receivedAt }));
  const title = draft.action === 'transfer' ? `${(payload.lines || []).reduce((sum, row) => sum + Number(row.quantity || 0), 0) || 'Unconfirmed'} cases · ${wfLocation(payload.fromLocationId)} → ${wfLocation(payload.toLocationId)}`
    : draft.action === 'can_run' ? `${payload.totalCans || 'Unconfirmed'} cans · ${wfLabel(payload.canSkuId)}`
    : draft.action === 'pack_cases' ? `${payload.cases || 'Unconfirmed'} packed cases · ${wfLabel(payload.outputSkuId)}`
    : draft.action === 'release' ? `Quality release · ${payload.id || 'run to confirm'}`
    : draft.action === 'acknowledge' ? `Arrival · ${payload.linkedTransferRef || wfLocation(payload.toLocationId)}`
    : draft.action === 'repack' ? `Mixed packs · ${wfLocation(payload.locationId)}` : draft.title;
  return { ...draft, title: String(title || draft.title).slice(0, 200), payload, facts };
}
function wfMissingDetails(draft) {
  const p = draft.payload || {}, f = draft.facts || {}, errors = [];
  const add = (condition, message) => { if (!condition) errors.push(message); };
  if (['can_run', 'pack_cases', 'transfer', 'repack'].includes(draft.action)) add(f.history === 'new', 'Confirm this event still needs recording. If it is historical or uncertain, save it for review.');
  if (draft.action === 'can_run') {
    add(wfExactLot(p.id), 'Enter the exact BevMax can-run ID.'); add(Boolean(p.canSkuId), 'Choose the can flavour.');
    add(wfDate(p.invoiceDate) && wfDate(p.productionDate), 'Confirm the invoice and best-known production dates, no later than today.');
    add(wfQuantity(p.totalCans) && Number(p.totalCans) <= 1000000, 'Enter a whole-can total from 1 to 1,000,000.');
    add(Boolean(p.producerLocationId), 'An active BevMax production location is needed.');
    add(!(RP.CAN_PRODUCTION_RUNS || []).some(row => row.id === p.id), 'This can run already exists. Review its recorded revision instead of creating another.');
  }
  if (draft.action === 'pack_cases') {
    const recipe = wfRecipe(p.outputSkuId), allocations = p.allocations || [], runs = wfCanRuns();
    add(Boolean(recipe && p.outputLocationId), 'Choose a published case recipe and confirm the BevMax location.');
    add(wfQuantity(p.cases) && Number(p.cases) <= 100000, 'Enter the actual whole-case quantity from 1 to 100,000.');
    add(wfExactLot(p.lot) && Boolean(String(p.lotGroup || '').trim()), 'Confirm the exact finished-case lot and lot group.');
    add(wfDate(p.packedOn), 'Confirm the actual packed date, no later than today.');
    add(f.sourcesConfirmed === true, 'Confirm the source can runs match the physical packing report.');
    add(!rpExistingPrintedLotMessage(p.outputSkuId, p.lot), rpExistingPrintedLotMessage(p.outputSkuId, p.lot));
    const existingPackaging = rpPackagingLotConflict(p.outputSkuId, p.lot, false);
    add(!existingPackaging, existingPackaging && existingPackaging.message);
    add(!rpHistoricalPackingConfirmation(p.outputSkuId, p.lot), 'Original packing for this product/lot is already confirmed. Review Confirmed original packing in Production; its source allocation needs separate audited reconciliation. Do not record those original cases again.');
    add(!((RP.LOTS || []).some(row => rpCanonicalInventorySkuId(row.sku) === p.outputSkuId && row.lot === p.lot)), 'This finished product/lot already exists. Save it for review; new packing must not duplicate recorded cases.');
    add(allocations.length > 0 && new Set(allocations.map(row => row.canRunId)).size === allocations.length, 'Choose each exact source can run only once.');
    (recipe && recipe.components || []).forEach(component => {
      const total = allocations.filter(row => row.canSkuId === component.canSkuId).reduce((sum, row) => sum + Number(row.cansAllocated || 0), 0);
      add(total === Number(p.cases) * component.cansPerCase, `${wfLabel(component.canSkuId)} needs exactly ${Number(p.cases || 0) * component.cansPerCase} allocated cans.`);
    });
    allocations.forEach(row => { const run = runs.find(item => item.id === row.canRunId); add(!run?.sourceHold, `${row.canRunId}: Needs reconciliation. Its calculated unallocated cans cannot supply packing until its historical source allocations are reconciled. Keep this draft for review.`); add(run && !run.sourceHold && run.canSkuId === row.canSkuId && run.producerLocationId === p.outputLocationId && wfQuantity(row.cansAllocated) && Number(row.cansAllocated) <= run.availableCans, `Check the selected run and available cans for ${row.canRunId || 'each source'}.`); });
  }
  if (draft.action === 'release') {
    const run = wfPackagingRun(p, f);
    add(run && run.status === 'QA Hold', 'Choose a packaging run currently in QA Hold.');
    add(f.qualityConfirmed === true, 'Confirm the quality clearance, product, lot and case quantity.');
    add(Boolean(String(f.evidenceReferences || '').trim()), 'Add the quality-release evidence reference.');
  }
  if (draft.action === 'transfer') {
    add(p.fromLocationId && p.toLocationId && p.fromLocationId !== p.toLocationId, 'Choose different source and receiving locations.');
    add(['direct_pickup', 'rep_pickup', 'carrier_freight', 'courier', 'internal_transfer'].includes(p.movementMethod), 'Confirm how the physical handoff happened.');
    if (['direct_pickup', 'rep_pickup', 'internal_transfer'].includes(p.movementMethod)) add(Boolean(String(p.handledBy || '').trim()), 'Enter who handled the handoff.');
    if (['carrier_freight', 'courier'].includes(p.movementMethod)) add(Boolean(String(p.carrier || '').trim()), 'Enter the carrier or courier name.');
    const date = new Date(p.occurredAt); add(Boolean(p.occurredAt) && !Number.isNaN(date.getTime()) && date.getTime() <= Date.now() + 300000, 'Confirm the completed handoff date and time.');
    add(['used', 'not_used'].includes(p.chepUsage), 'Confirm whether CHEP pallets were handed over.');
    if (p.chepUsage === 'used') add(wfQuantity(p.chepPalletCount) && Number(p.chepPalletCount) <= 10000, 'Enter the whole CHEP pallet count.');
    const lines = p.lines || []; add(lines.length > 0 && lines.length <= 25, 'Add between one and 25 product/lot lines.');
    add(new Set(lines.map(row => `${row.skuId}|${row.lot}`)).size === lines.length, 'Combine duplicate product/lot lines.');
    lines.forEach(row => { const holding = wfHoldingRows(p.fromLocationId, row.skuId).find(item => item.lot === row.lot); add(row.skuId && wfExactLot(row.lot) && wfQuantity(row.quantity) && holding && Number(row.quantity) <= holding.quantity, `Confirm the exact lot and enough usable cases for ${wfLabel(row.skuId)}.`); });
    if (p.chepUsage === 'used') { const chep = (RP.CHEP_BALANCES || []).find(row => row.locationId === p.fromLocationId); add(chep && Number(chep.balance) >= Number(p.chepPalletCount), 'Verify the source CHEP balance before moving these pallets.'); }
  }
  if (draft.action === 'acknowledge') {
    add(!wfBookkeepingTransfer(p.linkedTransferRef, f), 'This transfer was a bookkeeping reset, not a physical handoff. Keep the draft for review; it cannot be acknowledged as received.');
    add(wfReceiptChoices(p, f).some(row => row.id === p.linkedTransferRef), 'Choose an existing, unacknowledged completed transfer for this destination.');
    add(wfDate(p.receivedAt), 'Confirm the actual received date, no later than today.');
    add(f.receiptMatches === true, 'Confirm the physical product, lots and full quantities match. Save discrepancies for review.');
  }
  if (draft.action === 'repack') {
    const recipe = inventoryRepackRecipe(p.targetSkuId), sources = p.sourceLines || [], holdings = inventoryRepackSourceHoldings(p.locationId);
    add(Boolean(p.locationId && recipe), 'Choose the physical repack location and mixed-pack composition.');
    add(wfQuantity(p.batchCount), 'Enter the number of cases of EACH flavour to open.');
    add(wfDate(p.repackedOn), 'Confirm the real repack date, no later than today.');
    add(wfExactLot(p.targetLot) && inventoryRepackTargetLotMatches(p.targetSkuId, p.targetLot), 'Confirm a new exact mixed-pack lot with the displayed prefix.');
    add(f.sourcesConfirmed === true, 'Confirm the physical source lots and completed mixed packs.');
    add(recipe && sources.length === recipe.sourceSkuIds.length, 'Choose one exact source lot for every recipe flavour.');
    (recipe && recipe.sourceSkuIds || []).forEach(skuId => { const source = sources.find(row => row.skuId === skuId); add(source && holdings.some(row => row.skuId === skuId && row.lot === source.lot && row.holdingStatus === p.targetStatus && row.availableCases >= Number(p.batchCount)), `Confirm enough Released ${wfLabel(skuId)} cases in the selected custody.`); });
  }
  return Array.from(new Set(errors));
}
function wfEffectLines(draft, finalized = false) {
  const p = draft.payload || {};
  if (draft.action === 'can_run') return [`${Number(p.totalCans || 0).toLocaleString()} ${wfLabel(p.canSkuId)} cans become recorded production output.`, 'Finished cases added: 0. These cans still need packing.'];
  if (draft.action === 'pack_cases') return [`${Number(p.cases || 0) * 12} exact source cans allocated → ${p.cases || 0} sealed cases in QA Hold at ${wfLocation(p.outputLocationId)}.`, 'Cases are not released or transferred by this action.'];
  if (draft.action === 'release') { const run = wfPackagingRun(p, draft.facts); return run ? [`${run.cases} ${wfLabel(run.outputSkuId)} cases, lot ${rpPrintedLotLabel(run.outputSkuId, run.lot, run.id)}: QA Hold → Available.`, `Same stock, same location: ${wfLocation(run.outputLocationId)}. No transfer.`] : ['Choose the packaging run to see its exact stock effect.']; }
  if (draft.action === 'transfer') return (p.lines || []).map(row => { const holding = wfHoldingRows(p.fromLocationId, row.skuId).find(item => item.lot === row.lot); return `${wfLabel(row.skuId)} · ${rpPrintedLotLabel(row.skuId, row.lot) || 'lot needed'}: ${row.quantity || 0} cases · ${wfLocation(p.fromLocationId)} → ${wfLocation(p.toLocationId)}.${finalized ? '' : ` Source usable cases: ${holding ? holding.quantity : 'unconfirmed'} → ${holding ? holding.quantity - Number(row.quantity || 0) : 'unconfirmed'}.`}`; }).concat([finalized ? 'The receiver acknowledges this same transfer; no second receipt quantity.' : 'Posting changes both locations immediately. The receiver then acknowledges this same transfer; no second receipt quantity.']);
  if (draft.action === 'acknowledge') return [`Confirm ${p.linkedTransferRef || 'the selected transfer'}: ${(p.lineItems || []).reduce((sum, row) => sum + Number(row.quantity), 0)} cases.`, 'No new cases or pallets. This confirms the existing transfer only.'];
  const recipe = inventoryRepackRecipe(p.targetSkuId), quantity = Number(p.batchCount || 0) * (recipe ? recipe.outputCasesPerBatch : 0);
  return [`${p.batchCount || 0} cases of EACH required flavour → ${quantity} mixed cases (${quantity * 12} cans in and out).`, `At ${wfLocation(p.locationId)}, custody remains ${p.targetStatus || 'to be confirmed'}.`];
}
function wfDemoRead() { const rows = JSON.parse(localStorage.getItem(WF_DEMO_KEY) || '[]'); if (!Array.isArray(rows)) throw new Error('Demo drafts could not be read.'); return rows; }
function wfFieldId(name) { return `wf-${name}`; }
function WFField({ name, label, help, children, wide }) { return <div className={`wf-field${wide ? ' wf-wide' : ''}`}><label htmlFor={wfFieldId(name)}>{label}</label>{children}{help && <small>{help}</small>}</div>; }
function WFCheck({ checked, onChange, children, disabled }) { return <label className="wf-check"><input type="checkbox" checked={!!checked} disabled={disabled} onChange={event => onChange(event.target.checked)} /><span>{children}</span></label>; }

function InventoryWorkflowView({ go, detail, onBack }) {
  const demo = Boolean(window.RP_AUTH && window.RP_AUTH.demo);
  const [screen, setScreen] = useState('home'), [draft, setDraft] = useState(null), [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false), [uploading, setUploading] = useState(false), [error, setError] = useState(''), [flash, setFlash] = useState(''), [loading, setLoading] = useState(true);
  const [preflight, setPreflight] = useState(null), [confirmed, setConfirmed] = useState(false), [conflict, setConflict] = useState(false), [scope, setScope] = useState('mine'), [dirty, setDirty] = useState(false);
  const [nextOffset, setNextOffset] = useState(null), [lookupRef, setLookupRef] = useState(''), [stockRefreshError, setStockRefreshError] = useState('');
  const openRef = useRef(''), focusRef = useRef(null);
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const role = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile.role) : profile.role;
  const allowedActions = ['admin', 'leadership', 'ops', 'warehouse'].includes(role) ? Object.keys(WF_ACTIONS) : role === 'dispatch' ? ['transfer', 'acknowledge'] : [];
  const canEdit = draft && draft.status === 'draft' && allowedActions.includes(draft.action) && draft.canEdit !== false && (!draft.creatorId || draft.creatorId === profile.id || demo);
  const frozen = draft && draft.status === 'posting';
  const canRecover = draft && frozen && allowedActions.includes(draft.action) && (draft.canRecover === true || demo) && (!draft.creatorId || draft.creatorId === profile.id || demo);
  const completed = draft && draft.status === 'completed';
  const locked = busy || uploading || frozen || completed || !canEdit;
  const p = draft && draft.payload || {}, facts = draft && draft.facts || {};
  const locations = inventoryLocationOptions(), skus = rpInventorySkus(), runs = wfCanRuns();
  const recipe = wfRecipe(p.outputSkuId), repackRecipe = inventoryRepackRecipe(p.targetSkuId);
  const localIssues = draft ? wfMissingDetails(draft) : [];
  const request = body => rpApi('/api/inventory/workflow', { method: 'POST', body: JSON.stringify(body) });
  const refreshList = async (selectedScope = scope) => {
    setLoading(true);
    try { const response = demo ? { drafts: wfDemoRead(), nextOffset: null } : await rpApi(`/api/inventory/workflow?scope=${selectedScope}`); setDrafts(Array.isArray(response.drafts) ? response.drafts.filter(row => row.status !== 'discarded') : []); setNextOffset(response.nextOffset == null ? null : response.nextOffset); }
    catch (err) { setError(err.message || 'Saved work could not be loaded. Live drafts need the workflow service; nothing is saved only in this browser.'); }
    finally { setLoading(false); }
  };
  async function loadMore() {
    if (loading || nextOffset == null) return; setLoading(true); setError('');
    try { const response = await rpApi(`/api/inventory/workflow?scope=${scope}&offset=${nextOffset}`); setDrafts(current => [...current, ...(response.drafts || []).filter(row => row.status !== 'discarded' && !current.some(existing => existing.id === row.id))]); setNextOffset(response.nextOffset == null ? null : response.nextOffset); }
    catch (err) { setError(err.message || 'Older saved work could not be loaded.'); } finally { setLoading(false); }
  }
  useEffect(() => { refreshList(scope); }, [scope, demo]);
  useEffect(() => {
    let active = true;
    const key = detail && (detail.id || detail.action);
    if (!key || key === openRef.current) return;
    openRef.current = key;
    if (detail.id) {
      setBusy(true);
      (async () => {
        try { const response = demo ? { draft: wfDemoRead().find(row => row.id === detail.id) } : await rpApi(`/api/inventory/workflow?scope=team&id=${encodeURIComponent(detail.id)}`); const row = response.draft || (response.drafts || [])[0]; if (!row) throw new Error('This saved workflow was not found.'); if (active) openDraft(row); }
        catch (err) { if (active) setError(err.message); }
        finally { if (active) setBusy(false); }
      })();
    } else if (allowedActions.includes(detail.action)) start(detail.action);
    return () => { active = false; };
  }, [detail && detail.id, detail && detail.action]);
  useEffect(() => { if (focusRef.current) focusRef.current.focus(); }, [screen, draft && draft.id]);
  useEffect(() => {
    if (typeof deskWriteInventoryWorkflowRoute !== 'function') return;
    const saved = draft && draft.revision > 0 && ['edit', 'review', 'done'].includes(screen);
    deskWriteInventoryWorkflowRoute('move-stock', saved ? { type: 'inventory-workflow', id: draft.id } : null);
  }, [screen, draft && draft.id, draft && draft.revision]);
  useEffect(() => {
    const canLeave = () => !busy && !uploading && (!dirty || window.confirm('Leave without saving your latest changes? Any previously saved draft will remain in Saved work.'));
    window.RP_INVENTORY_WORKFLOW_CAN_LEAVE = canLeave;
    return () => { if (window.RP_INVENTORY_WORKFLOW_CAN_LEAVE === canLeave) delete window.RP_INVENTORY_WORKFLOW_CAN_LEAVE; };
  }, [dirty, busy, uploading]);
  useEffect(() => {
    if (!dirty && !busy && !uploading) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, busy, uploading]);
  function canLeave() { return !busy && !uploading && (!dirty || window.confirm('Leave without saving your latest changes? Any previously saved draft will remain in Saved work.')); }
  function rememberDraft(row) {
    if (!row || row.revision < 1) return;
    if (scope === 'mine' && !demo && row.creatorId !== profile.id) return;
    setDrafts(current => row.status === 'discarded' ? current.filter(item => item.id !== row.id)
      : current.some(item => item.id === row.id) ? current.map(item => item.id === row.id ? row : item) : [row, ...current]);
  }
  function savedDraft(row) { setDraft(row); rememberDraft(row); }
  function openDraft(row) { savedDraft(JSON.parse(JSON.stringify(row))); setDirty(false); setLookupRef(''); setPreflight(null); setConfirmed(false); setConflict(false); setError(''); setScreen(row.status === 'discarded' ? 'discarded' : row.status === 'completed' ? 'done' : row.status === 'posting' ? 'review' : 'edit'); }
  function cancelEdit() {
    if (!canLeave()) return;
    setDraft(null); setDirty(false); setPreflight(null); setConfirmed(false); setConflict(false); setError(''); setFlash(''); setScreen('home');
  }
  function canDiscard(row) {
    return row && row.status === 'draft' && row.revision > 0 && row.canEdit !== false && row.canDiscard !== false && allowedActions.includes(row.action) && (demo || row.creatorId === profile.id);
  }
  function discardSucceeded(row) {
    setDraft(null); setDirty(false); setPreflight(null); setConfirmed(false); setConflict(false); setError('');
    setDrafts(current => current.filter(item => item.id !== row.id));
    setFlash('Draft discarded. Inventory unchanged.'); setScreen('home');
  }
  async function discardDraft(row) {
    if (busy || uploading || !canDiscard(row)) return;
    if (!window.confirm(`Discard draft “${row.title || (WF_ACTIONS[row.action] || {}).title || 'Saved inventory work'}”?\n${row.id}\n\nThis removes it from saved work, including any unsaved edits. No inventory will change. This cannot be undone.`)) return;
    setBusy(true); setError(''); setConflict(false);
    try {
      let removed;
      if (demo) {
        const rows = wfDemoRead(), existing = rows.find(item => item.id === row.id);
        if (!existing || existing.status !== 'draft' || existing.revision !== row.revision) throw new Error('This draft changed in another browser tab. Reload the saved version before discarding.');
        removed = { ...existing, status: 'discarded', revision: existing.revision + 1, canEdit: false, canRecover: false, canDiscard: false, discardedAt: new Date().toISOString() };
        localStorage.setItem(WF_DEMO_KEY, JSON.stringify(rows.map(item => item.id === row.id ? removed : item)));
      } else {
        const response = await request({ operation: 'discard', id: row.id, expectedRevision: row.revision });
        removed = response.discarded === true && response.draft;
        if (!removed || removed.id !== row.id || removed.status !== 'discarded') throw new Error('Discard was not confirmed. Check the saved status before retrying.');
      }
      discardSucceeded(removed); await refreshList();
    } catch (err) {
      if (!demo) {
        try {
          const response = await rpApi(`/api/inventory/workflow?id=${encodeURIComponent(row.id)}`);
          if (response.draft?.id === row.id && response.draft.status === 'discarded') { discardSucceeded(response.draft); await refreshList(); return; }
          if (response.draft?.id === row.id && ['posting', 'completed'].includes(response.draft.status)) {
            openDraft(response.draft);
            setError(response.draft.status === 'posting' ? 'This workflow has started posting and cannot be discarded. Recover its original request.' : 'This workflow has already completed and cannot be discarded. Its original result is shown below.');
            return;
          }
        } catch (_) { /* Preserve the original draft until its status is known. */ }
      }
      if (!draft || draft.id !== row.id) openDraft(row);
      setError(`${err.message || 'Discard could not be confirmed.'} Check saved status before continuing.`);
      setConflict(/revision|changed|conflict/i.test(err.message || ''));
    } finally { setBusy(false); }
  }
  function start(action) { openDraft(wfNewDraft(action)); }
  function startNext() {
    const action = WF_ACTIONS[draft.action] && WF_ACTIONS[draft.action].next;
    if (!allowedActions.includes(action)) return;
    const next = wfNewDraft(action);
    if (action === 'release') next.payload.id = p.id || '';
    if (action === 'transfer') {
      const packaged = (RP.PACKAGING_RUNS || []).find(row => row.id === p.id);
      next.payload.fromLocationId = draft.action === 'repack' ? p.locationId : packaged && packaged.outputLocationId || p.toLocationId || '';
      if (draft.action === 'repack') next.payload.lines = [{ skuId: p.targetSkuId, lot: p.targetLot, quantity: Number(p.batchCount) * (inventoryRepackRecipe(p.targetSkuId) || {}).outputCasesPerBatch }];
    }
    if (action === 'acknowledge') {
      next.payload.toLocationId = p.toLocationId;
      const transfer = wfTransferOptions(p.toLocationId).find(row => row.id === p.ref);
      if (transfer) next.payload = wfReceiptPayload(transfer, next.payload);
    }
    openDraft(next);
  }
  function update(key, value) { setDraft(current => ({ ...current, payload: { ...current.payload, [key]: value }, facts: { ...current.facts, sourcesConfirmed: false, ...(key === 'id' && current.action === 'release' ? { qualityConfirmed: false } : {}) } })); setDirty(true); setPreflight(null); setConfirmed(false); }
  function fact(key, value) { setDraft(current => ({ ...current, facts: { ...current.facts, [key]: value } })); setDirty(true); setPreflight(null); setConfirmed(false); }
  function setPayload(payload) { setDraft(current => ({ ...current, payload, facts: { ...current.facts, sourcesConfirmed: false, receiptMatches: false, qualityConfirmed: false } })); setDirty(true); setPreflight(null); setConfirmed(false); }
  async function saveCurrent() {
    const prepared = wfPreparedDraft(draft);
    if (demo) {
      const rows = wfDemoRead(), existing = rows.find(row => row.id === prepared.id);
      if ((existing && existing.revision || 0) !== prepared.revision) throw new Error('This draft changed in another browser tab. Reload the saved version before continuing.');
      const row = { ...prepared, revision: prepared.revision + 1, creatorId: profile.id || 'demo', creatorName: rpDisplayName(), canEdit: true, updatedAt: new Date().toISOString() };
      localStorage.setItem(WF_DEMO_KEY, JSON.stringify([row, ...rows.filter(item => item.id !== row.id)])); savedDraft(row); setDirty(false); return row;
    }
    const response = await request({ operation: 'save', id: prepared.id, expectedRevision: prepared.revision, title: prepared.title, action: prepared.action, payload: prepared.payload, facts: prepared.facts });
    if (!response.draft) throw new Error('The saved draft was not confirmed. Reload saved work before retrying.');
    savedDraft(response.draft); setDirty(false); return response.draft;
  }
  async function saveLater() {
    if (locked) return; setBusy(true); setError('');
    try { await saveCurrent(); setFlash(demo ? 'Demo saved on this browser. Inventory unchanged.' : 'Saved to your account. Inventory unchanged.'); setScreen('home'); await refreshList(); }
    catch (err) { setError(err.message); setConflict(/revision|changed|conflict/i.test(err.message)); }
    finally { setBusy(false); }
  }
  async function review() {
    if (locked) return; setBusy(true); setError('');
    try {
      const saved = await saveCurrent();
      const check = demo ? { draft: saved, ready: wfMissingDetails(saved).length === 0, blockers: wfMissingDetails(saved).map(message => ({ message })), warnings: [], matches: {} } : await request({ operation: 'preflight', id: saved.id, expectedRevision: saved.revision });
      if (check.draft) savedDraft(check.draft); setPreflight(check); setConfirmed(false); setScreen('review');
    } catch (err) { setError(err.message); setConflict(/revision|changed|conflict/i.test(err.message)); }
    finally { setBusy(false); }
  }
  async function reloadSaved() {
    if (busy || uploading || (dirty && !window.confirm('Reload saved status and replace your unsaved changes?'))) return;
    setBusy(true); setError('');
    try { const response = demo ? { draft: wfDemoRead().find(row => row.id === draft.id) } : await rpApi(`/api/inventory/workflow?scope=team&id=${encodeURIComponent(draft.id)}`); const row = response.draft || (response.drafts || [])[0]; if (!row) throw new Error('Saved draft not found. Your details are still on screen.'); openDraft(row); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function post() {
    if (busy || uploading || !(frozen ? canRecover : canEdit) || completed || (!frozen && (!confirmed || !preflight || !preflight.ready || localIssues.length))) return;
    const pending = { ...draft, status: 'posting', canRecover: true };
    savedDraft(pending); setBusy(true); setError('');
    try {
      let row;
      if (demo) { row = { ...pending, status: 'completed', revision: pending.revision + 1, result: { simulated: true, ref: pending.payload.ref || pending.payload.id || pending.id }, updatedAt: new Date().toISOString() }; const rows = wfDemoRead(); localStorage.setItem(WF_DEMO_KEY, JSON.stringify([row, ...rows.filter(item => item.id !== row.id)])); }
      else { const response = await request({ operation: 'post', id: pending.id, expectedRevision: pending.revision }); row = response.draft; if (!row) throw new Error('The posting result is unclear. Recover this original workflow; do not create a replacement.'); }
      savedDraft(row); setScreen(row.status === 'completed' ? 'done' : 'review');
      if (!demo && row.status === 'completed') { try { await rpRefreshAfterMutation('Stock workflow completed'); } catch (err) { setError('The action completed, but the latest balances could not be refreshed. Reload before another movement.'); } }
      await refreshList();
    } catch (err) {
      setError(`${err.message || 'The result is unclear.'} Keep this workflow reference. Recover the original request before starting another.`);
      if (!demo) { try { const response = await rpApi(`/api/inventory/workflow?id=${encodeURIComponent(pending.id)}`); const row = response.draft || (response.drafts || [])[0]; if (row) { savedDraft(row); if (row.status === 'completed') setScreen('done'); } } catch (_) { /* Remain frozen until the durable state can be recovered. */ } }
    } finally { setBusy(false); }
  }
  async function lookupRecord() {
    if (locked || !lookupRef.trim()) return; setBusy(true); setError('');
    try {
      const receipt = draft.action === 'acknowledge', key = receipt ? 'transferRef' : 'packagingRunId';
      const response = demo ? receipt ? { transfer: (RP.LOGISTICS_MOVEMENTS || []).find(row => row.id === lookupRef.trim()) } : { packagingRun: (RP.PACKAGING_RUNS || []).find(row => row.id === lookupRef.trim()) } : await rpApi(`/api/inventory/workflow?${key}=${encodeURIComponent(lookupRef.trim())}`);
      const record = receipt ? response.transfer : response.packagingRun;
      if (!record) throw new Error('That exact record was not found. Keep a draft and confirm its reference.');
      if (receipt && (record.reconciliationOnly || wfBookkeepingTransfer(record.id, facts))) throw new Error('This transfer was a bookkeeping reset, not a physical handoff. Review its reconciliation note in Inventory Ledger.');
      if (receipt && record.acknowledged) throw new Error(`This transfer is already acknowledged${record.acknowledgementId ? ` by ${record.acknowledgementId}` : ''}. Do not receive it again.`);
      if (!receipt && record.status !== 'QA Hold') throw new Error(`This packaging run is ${record.status}; only QA Hold can be released.`);
      setDraft(current => ({ ...current, payload: receipt ? wfReceiptPayload(record, current.payload) : { ...current.payload, id: record.id }, facts: { ...current.facts, [receipt ? 'transferSnapshot' : 'packagingSnapshot']: record, receiptMatches: false, qualityConfirmed: false } }));
      setDirty(true); setPreflight(null); setConfirmed(false);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function refreshStock() {
    if (busy || uploading) return; setBusy(true); setStockRefreshError('');
    try { if (!demo) { const refreshed = await rpRefreshLiveData(); if (refreshed === false) throw new Error('The latest stock data could not be confirmed.'); } setPreflight(null); setConfirmed(false); setFlash(demo ? 'Using the existing demo catalog and stock.' : 'Stock data refreshed. Review again before posting.'); }
    catch (err) { setStockRefreshError(err.message || 'Stock data could not be refreshed. Keep a draft; an empty list is not a verified zero balance.'); }
    finally { setBusy(false); }
  }
  const exactLookup = draft && ['release', 'acknowledge'].includes(draft.action) && <section className="wf-lookup"><WFField name="lookup-reference" label={draft.action === 'acknowledge' ? 'Find an older transfer by its exact TO reference' : 'Find an older packaging run by its exact PKG reference'} help="Recent lists may not include every record. This lookup reads the original signed record."><input id="wf-lookup-reference" value={lookupRef} disabled={locked} onChange={event => setLookupRef(event.target.value.toUpperCase())} placeholder={draft.action === 'acknowledge' ? 'TO-…' : 'PKG-…'} /></WFField><button className="wf-button secondary" disabled={locked || !lookupRef.trim()} onClick={lookupRecord}>Find exact record</button></section>;
  async function upload(event) {
    const files = Array.from(event.target.files || []); event.target.value = ''; if (!files.length || locked) return;
    if (demo) { setError('Demo mode does not upload files. Add an evidence reference in the notes instead.'); return; }
    setUploading(true); setError('');
    const added = [];
    try { for (const file of files.slice(0, Math.max(0, 8 - (facts.attachments || []).length))) { const result = await rpUploadPrivateFile('inventory_photo', file, 'inventory-workflow'); if (!result || !result.path) throw new Error('The uploaded file path could not be verified.'); added.push({ path: result.path, name: file.name, contentType: file.type, size: file.size }); } }
    catch (err) { setError(err.message || 'Evidence could not be uploaded.'); }
    finally { if (added.length) { setDraft(current => ({ ...current, facts: { ...current.facts, attachments: [...(current.facts.attachments || []), ...added] } })); setDirty(true); } setUploading(false); }
  }
  function input(name, label, type = 'text', help = '', extra = {}) { return <WFField name={name} label={label} help={help}><input id={wfFieldId(name)} type={type} value={type === 'datetime-local' ? wfLocalDateTime(p[name]) : p[name] == null ? '' : p[name]} disabled={locked} onChange={event => update(name, ['id', 'lot', 'targetLot'].includes(name) ? event.target.value.toUpperCase() : event.target.value)} {...extra} /></WFField>; }
  function select(name, label, options, help = '', callback) { return <WFField name={name} label={label} help={help}><select id={wfFieldId(name)} value={p[name] || ''} disabled={locked} onChange={event => callback ? callback(event.target.value) : update(name, event.target.value)}><option value="">I still need to confirm this</option>{name === 'linkedTransferRef' && p[name] && !options.some(option => option.value === p[name]) && <option value={p[name]} disabled>{p[name]} · {wfBookkeepingTransfer(p[name], facts) ? 'Bookkeeping reconciliation — blocked' : 'Not available'}</option>}{options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></WFField>; }
  const locationOptions = locations.map(row => ({ value: row.id, label: row.name }));
  const skuOptions = skus.map(row => ({ value: row.id, label: rpSkuDisplayName(row) }));
  const evidenceFields = draft && <section className="wf-section"><h2>Keep the details together</h2><div className="wf-form-grid">
    <WFField name="notes" label="Anything else to remember?" wide><textarea id="wf-notes" value={facts.notes || ''} disabled={locked} maxLength="3000" rows="3" onChange={event => fact('notes', event.target.value)} placeholder="What happened, what is uncertain, or what the paperwork says…" /></WFField>
    <WFField name="evidence" label="Report, label or handoff reference" wide help="Keep the source document’s name, reference or link. Quality release needs its evidence reference."><input id="wf-evidence" value={facts.evidenceReferences || ''} disabled={locked} maxLength="2000" onChange={event => fact('evidenceReferences', event.target.value)} /></WFField>
    <WFField name="followup" label="Who can confirm the missing detail?" help="A named follow-up kept with this draft; no message is sent."><input id="wf-followup" value={facts.followupName || ''} disabled={locked} maxLength="150" onChange={event => fact('followupName', event.target.value)} placeholder="Person or team" /></WFField>
    <WFField name="next-detail" label="What needs to happen next?"><input id="wf-next-detail" value={facts.nextDetail || ''} disabled={locked} maxLength="500" onChange={event => fact('nextDetail', event.target.value)} placeholder="For example, confirm the lot from the case label" /></WFField>
    <WFField name="attachment" label="Attach a label photo or paperwork" wide help={demo ? 'Uploads are disabled in demo mode.' : 'PDF or image, up to 8 MB each; maximum eight files. Save the draft to keep the uploaded evidence linked.'}><input id="wf-attachment" type="file" accept=".pdf,.heic,.heif,.jpg,.jpeg,.png,.webp" multiple disabled={locked || demo || (facts.attachments || []).length >= 8} onChange={upload} /></WFField>
  </div>{uploading && <p role="status">Uploading evidence…</p>}<div className="wf-attachments">{(facts.attachments || []).map((attachment, index) => <div key={attachment.path}><button className="wf-link" type="button" onClick={() => rpOpenProtectedFile(attachment.path).catch(err => setError(err.message))}>{attachment.name || 'Open evidence'}</button>{!locked && <button className="wf-link" onClick={() => fact('attachments', facts.attachments.filter((_, position) => position !== index))} aria-label={`Remove ${attachment.name || 'evidence'} from this draft`}>Remove link</button>}</div>)}</div></section>;
  const historyField = draft && ['can_run', 'pack_cases', 'transfer', 'repack'].includes(draft.action) && <WFField name="history" label="Is this event already recorded?" wide help="Already-counted or uncertain history stays a draft for review; it never creates more stock."><select id="wf-history" value={facts.history || ''} disabled={locked} onChange={event => fact('history', event.target.value)}><option value="">I’m not sure yet</option><option value="new">I checked — this event still needs recording</option><option value="recorded">Yes / these quantities may already be recorded</option></select></WFField>;
  const fieldContent = draft && <>
    {draft.action === 'can_run' && <div className="wf-form-grid">{input('id', 'BevMax run ID', 'text', 'Use the exact run identity on the report.', { maxLength: 100 })}{select('canSkuId', 'Which flavour was produced?', (RP.SKUS || []).filter(row => row.active !== false && (row.unit === 'can' || /^RP-CAN-/.test(row.id))).map(row => ({ value: row.id, label: rpSkuDisplayName(row) })))}{input('totalCans', 'Total cans produced', 'number', 'Whole cans, not cases.', { min: 1, max: 1000000, step: 1 })}{input('invoiceDate', 'BevMax invoice date', 'date', '', { max: wfToday() })}{input('productionDate', 'Best-known production date', 'date', '', { max: wfToday() })}{select('productionDateBasis', 'How certain is that date?', [{ value: 'invoice_estimate', label: 'Invoice-date estimate' }, { value: 'bevmax_reported', label: 'Reported by BevMax' }, { value: 'bevmax_confirmed', label: 'Confirmed by BevMax' }])}{select('countStatus', 'How certain is the can count?', [{ value: 'reported', label: 'BevMax reported' }, { value: 'verified', label: 'Verified count' }, { value: 'estimated', label: 'Estimated / count pending' }])}{historyField}</div>}
    {draft.action === 'pack_cases' && <><div className="wf-form-grid">{select('outputSkuId', 'What finished product was packed?', Array.from(new Set((RP.PACK_RECIPES || []).filter(row => row.active !== false).map(row => row.outputSkuId))).map(id => ({ value: id, label: wfLabel(id) })), '', value => { const selected = wfRecipe(value); setPayload({ ...p, outputSkuId: value, recipeVersion: selected ? selected.recipeVersion : 0, allocations: [] }); })}{input('cases', 'Sealed cases / flats packed', 'number', 'Actual packing totals; every case contains 12 cans.', { min: 1, max: 100000, step: 1 })}{input('lot', 'Finished-case lot on the label')}{input('lotGroup', 'Lot group', 'text', 'For example, Lot 2. This does not replace the exact lot.')}{input('packedOn', 'Actual packed date', 'date', '', { max: wfToday() })}{historyField}</div><section className="wf-section"><div className="wf-row"><h2>Which cans went into these cases?</h2><button className="wf-button secondary" disabled={locked || !recipe || !wfQuantity(p.cases)} onClick={() => update('allocations', rpPackagingAllocationPlan(recipe, Number(p.cases), p.outputLocationId).allocations)}>Suggest oldest runs</button></div><p className="wf-muted">Choose the exact runs on the packing report. Suggestions exclude sources awaiting reconciliation. A saved blocked selection stays visible for review; its calculated unallocated balance is not verified loose stock.</p>{!recipe && <p className="wf-notice">Choose a finished product to see its recipe. If no recipes load, keep a draft and check the production data connection.</p>}{(recipe && recipe.components || []).map(component => { const matching = (p.allocations || []).map((row, index) => ({ ...row, index })).filter(row => row.canSkuId === component.canSkuId); return <div className="wf-allocation" key={component.canSkuId}><h3>{wfLabel(component.canSkuId)} <span className="wf-muted">{Number(p.cases || 0) * Number(component.cansPerCase)} cans needed · {component.cansPerCase} per case</span></h3>{matching.map(row => <div className="wf-line" key={row.index}><WFField name={`run-${row.index}`} label="Source can run"><select id={`wf-run-${row.index}`} disabled={locked} value={row.canRunId} onChange={event => update('allocations', p.allocations.map((item, index) => index === row.index ? { ...item, canRunId: event.target.value } : item))}><option value="">Choose exact run</option>{runs.filter(run => run.id === row.canRunId || (!run.sourceHold && run.canSkuId === component.canSkuId && run.producerLocationId === p.outputLocationId && run.availableCans > 0)).map(run => <option key={run.id} value={run.id} disabled={!!run.sourceHold}>{run.id} · {run.sourceHold ? 'Needs reconciliation — blocked' : `${run.availableCans.toLocaleString()} unallocated · ${run.productionDate}`}</option>)}</select></WFField><WFField name={`cans-${row.index}`} label="Cans used"><input id={`wf-cans-${row.index}`} type="number" min="1" step="1" disabled={locked} value={row.cansAllocated} onChange={event => update('allocations', p.allocations.map((item, index) => index === row.index ? { ...item, cansAllocated: event.target.value } : item))} /></WFField><button className="wf-link" disabled={locked} onClick={() => update('allocations', p.allocations.filter((_, index) => index !== row.index))}>Remove</button></div>)}<button className="wf-link" disabled={locked} onClick={() => update('allocations', [...(p.allocations || []), { canRunId: '', canSkuId: component.canSkuId, cansAllocated: '' }])}>+ Add a source run for this flavour</button></div>; })}<WFCheck checked={facts.sourcesConfirmed} disabled={locked} onChange={value => fact('sourcesConfirmed', value)}>These exact source runs and can quantities match the physical packing report.</WFCheck></section></>}
    {draft.action === 'release' && <><div className="wf-form-grid">{select('id', 'Which packaging run passed quality checks?', [...(RP.PACKAGING_RUNS || []).filter(row => row.status === 'QA Hold'), ...(facts.packagingSnapshot && !(RP.PACKAGING_RUNS || []).some(row => row.id === facts.packagingSnapshot.id) ? [facts.packagingSnapshot] : [])].map(row => ({ value: row.id, label: `${row.id} · ${wfLabel(row.outputSkuId)} · ${rpPrintedLotLabel(row.outputSkuId, row.lot, row.id)} · ${row.cases} cases` })))}</div><div className="wf-notice">Check the product, printed lot, count, production/expiry evidence and quality clearance. Release keeps the cases at the same location. It never happens automatically.</div><WFCheck checked={facts.qualityConfirmed} disabled={locked} onChange={value => fact('qualityConfirmed', value)}>The quality review is complete and this exact packaging run is cleared for release.</WFCheck></>}
    {draft.action === 'transfer' && <><div className="wf-form-grid">{select('fromLocationId', 'Where did it leave from?', locationOptions, '', value => setPayload({ ...p, fromLocationId: value, lines: [{ skuId: '', lot: '', quantity: '' }] }))}{select('toLocationId', 'Who has custody now?', locationOptions.filter(row => row.value !== p.fromLocationId), 'Choose the actual warehouse or rep storage, not just the driver.')}{select('movementMethod', 'How did it move?', [{ value: 'direct_pickup', label: 'Direct pickup' }, { value: 'rep_pickup', label: 'Rep pickup' }, { value: 'carrier_freight', label: 'Carrier / freight' }, { value: 'courier', label: 'Courier' }, { value: 'internal_transfer', label: 'Internal transfer' }])}{input('occurredAt', 'Actual handoff date and time', 'datetime-local')}{['carrier_freight', 'courier'].includes(p.movementMethod) ? input('carrier', 'Carrier / courier name') : input('handledBy', 'Who handled the handoff?')}{select('chepUsage', 'Were CHEP pallets handed over?', [{ value: 'used', label: 'Yes' }, { value: 'not_used', label: 'No' }], '', value => setPayload({ ...p, chepUsage: value, chepPalletCount: value === 'not_used' ? 0 : '' }))}{p.chepUsage === 'used' && input('chepPalletCount', 'CHEP pallets moved', 'number', 'Whole pallets. Check the source CHEP balance.', { min: 1, max: 10000, step: 1 })}{input('bol', 'Bill of lading reference (if available)')}{input('trackingNumber', 'Tracking / PRO (if available)')}{historyField}</div><section className="wf-section"><h2>What changed hands?</h2><p className="wf-muted">Only recorded Ranch Power-owned usable case lots appear here. If a lot is missing, save the details and check the earlier handoff. A blank list does not prove there is no physical stock.</p>{(p.lines || []).map((line, index) => <div className="wf-stock-line" key={index}><WFField name={`product-${index}`} label="Product"><select id={`wf-product-${index}`} disabled={locked} value={line.skuId} onChange={event => update('lines', p.lines.map((row, position) => position === index ? { ...row, skuId: event.target.value, lot: '' } : row))}><option value="">Choose product</option>{skuOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></WFField><WFField name={`lot-${index}`} label="Exact case lot"><select id={`wf-lot-${index}`} disabled={locked} value={line.lot} onChange={event => update('lines', p.lines.map((row, position) => position === index ? { ...row, lot: event.target.value } : row))}><option value="">Lot not confirmed yet</option>{wfHoldingRows(p.fromLocationId, line.skuId).map(row => <option key={row.lot} value={row.lot}>{rpPrintedLotLabel(line.skuId, row.lot)} · {row.quantity} usable cases</option>)}</select></WFField><WFField name={`quantity-${index}`} label="Cases"><input id={`wf-quantity-${index}`} type="number" min="1" step="1" disabled={locked} value={line.quantity} onChange={event => update('lines', p.lines.map((row, position) => position === index ? { ...row, quantity: event.target.value } : row))} /></WFField><button className="wf-link" disabled={locked || p.lines.length === 1} onClick={() => update('lines', p.lines.filter((_, position) => position !== index))}>Remove</button></div>)}<button className="wf-button secondary" disabled={locked || p.lines.length >= 25} onClick={() => update('lines', [...p.lines, { skuId: '', lot: '', quantity: '' }])}>+ Add product / lot</button></section><div className="wf-notice">A completed transfer updates both locations immediately. Pending stock requests and existing transfers are checked before posting, so the same handoff is not fulfilled twice.</div></>}
    {draft.action === 'acknowledge' && <><div className="wf-form-grid">{select('toLocationId', 'Where did the stock arrive?', locationOptions, '', value => setPayload(wfReceiptPayload(null, { ...p, toLocationId: value })))}{select('linkedTransferRef', 'Completed inbound transfer', wfReceiptChoices(p, facts).map(row => ({ value: row.id, label: `${row.id} · from ${wfLocation(row.fromLocationId)} · ${row.caseCount || (row.lines || []).reduce((sum, line) => sum + Number(line.quantity), 0)} cases` })), 'Choose the TO reference already recorded for this handoff.', value => setPayload(wfReceiptPayload(wfReceiptChoices(p, facts).find(row => row.id === value), p)))}{input('receivedAt', 'Actual received date', 'date', '', { max: wfToday() })}</div>{p.linkedTransferRef && <div className="wf-notice good"><strong>{p.linkedTransferRef} · {wfLocation(p.sourceLocationId)} → {wfLocation(p.toLocationId)}</strong><ul>{(p.lineItems || []).map((line, index) => <li key={index}>{line.quantity} cases · {wfLabel(line.skuId)} · {rpPrintedLotLabel(line.skuId, line.lot)}</li>)}</ul><p>These quantities are already in the records. Acknowledging them adds no stock.</p></div>}<WFCheck checked={facts.receiptMatches} disabled={locked} onChange={value => fact('receiptMatches', value)}>The physical product, exact lots and all case quantities match this transfer.</WFCheck><p className="wf-muted">Missing transfer or a mismatch? Keep a draft with the count and who can confirm it. Do not create a new receipt to make the quantities match.</p></>}
    {draft.action === 'repack' && <><div className="wf-form-grid">{select('locationId', 'Where were the cases rebuilt?', locationOptions, '', value => setPayload({ ...p, locationId: value, targetStatus: '', sourceLines: [] }))}{select('targetSkuId', 'Which mixed-pack composition?', inventoryRepackTargetCatalog().map(row => ({ value: row.id, label: inventoryRepackTargetChoiceLabel(row) })), '', value => setPayload({ ...p, targetSkuId: value, targetLot: '', sourceLines: (inventoryRepackRecipe(value) || { sourceSkuIds: [] }).sourceSkuIds.map(skuId => ({ skuId, lot: '' })) }))}{select('targetStatus', 'Which custody are you opening?', inventoryRepackEligibleStatuses(p.locationId).map(status => ({ value: inventoryRepackStatusLabel(status), label: inventoryRepackStatusLabel(status) })), 'Use a complete recipe in one custody. Purchased and sample stock is excluded.', value => setPayload({ ...p, targetStatus: value, sourceLines: (p.sourceLines || []).map(row => ({ ...row, lot: '' })) }))}{input('batchCount', 'Cases of EACH flavour to open', 'number', repackRecipe ? `One of each flavour makes ${repackRecipe.outputCasesPerBatch} mixed cases.` : 'This is not the total number of mixed cases.', { min: 1, step: 1 })}{input('targetLot', 'New mixed-pack lot', 'text', p.targetSkuId ? `Use a new physical label starting ${rpSkuDefaultLotCode(p.targetSkuId)}.` : 'Choose the target composition first.')}{input('repackedOn', 'Actual repack date', 'date', '', { max: wfToday() })}{historyField}</div><section className="wf-section"><h2>Exact source cases</h2><div className="wf-form-grid">{(p.sourceLines || []).map((line, index) => <WFField key={line.skuId} name={`source-${index}`} label={wfLabel(line.skuId)}><select id={`wf-source-${index}`} disabled={locked} value={line.lot} onChange={event => update('sourceLines', p.sourceLines.map((row, position) => position === index ? { ...row, lot: event.target.value } : row))}><option value="">Choose exact source lot</option>{inventoryRepackSourceHoldings(p.locationId).filter(row => row.skuId === line.skuId && row.holdingStatus === p.targetStatus).map(row => <option key={row.key} value={row.lot}>{rpPrintedLotLabel(line.skuId, row.lot)} · {row.availableCases} cases{row.expiresAt ? ` · expires ${row.expiresAt}` : ''}</option>)}</select></WFField>)}</div><WFCheck checked={facts.sourcesConfirmed} disabled={locked} onChange={value => fact('sourcesConfirmed', value)}>These source lots were physically opened and the mixed cases are complete with the new label.</WFCheck></section></>}
  </>;
  const workflowTitle = draft && WF_ACTIONS[draft.action] ? WF_ACTIONS[draft.action].title : 'Saved inventory work';
  const activeRows = drafts.filter(row => !['completed', 'discarded'].includes(row.status));
  function draftCards(rows) { return rows.filter(row => row.status !== 'discarded').map(row => <article className="wf-draft-card" key={row.id}><div><span className={`wf-pill${row.status === 'completed' ? ' good' : ''}`}>{row.status === 'posting' ? 'Recover original request' : row.status === 'completed' ? 'Completed' : row.facts && row.facts.nextDetail || 'Saved for later'}</span><h3>{row.title || (WF_ACTIONS[row.action] || {}).title || 'Saved inventory work'}</h3><p className="wf-muted">{row.creatorName ? `${row.creatorName} · ` : ''}{row.facts && row.facts.followupName ? `Follow up with ${row.facts.followupName} · ` : ''}{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : ''}</p><small className="wf-reference">{row.payload && (row.payload.ref || row.payload.id || row.payload.linkedTransferRef) || row.id}</small></div><div className="wf-draft-actions"><button className="wf-button secondary" disabled={busy || uploading} onClick={() => openDraft(row)}>{row.canEdit === false ? 'View details' : row.status === 'posting' ? 'Recover' : row.status === 'completed' ? 'View result' : 'Continue'}</button>{canDiscard(row) && <button className="wf-button danger" disabled={busy || uploading} onClick={() => discardDraft(row)}>Discard draft</button>}</div></article>); }
  const exitButtons = <><button className="wf-button secondary" disabled={busy || uploading} onClick={cancelEdit}>Cancel</button>{canDiscard(draft) && <button className="wf-button danger" disabled={busy || uploading} onClick={() => discardDraft(draft)}>Discard draft</button>}</>;
  const reviewIssues = Array.from(new Set([...localIssues, ...((preflight && preflight.blockers) || []).map(row => typeof row === 'string' ? row : row.message)]));
  const pipelineWarnings = !demo && window.RP_BOOTSTRAP && Array.isArray(window.RP_BOOTSTRAP.warnings) ? window.RP_BOOTSTRAP.warnings.filter(message => /inventory|packaging|production|lot|pipeline/i.test(String(message))) : [];
  return <main className="inventory-workflow" data-inventory-workflow={demo ? 'demo' : 'live'}>
    {demo && <div className="wf-demo" role="status">Demo · Existing example catalog and stock · Drafts stay in this browser · No live inventory changes</div>}
    <div className="wf-topbar"><button className="wf-link" disabled={busy || uploading} onClick={cancelEdit}>Move Stock</button><button className="wf-link" disabled={busy || uploading} onClick={() => { if (!canLeave()) return; setDraft(null); setDirty(false); setError(''); setConflict(false); setScreen('drafts'); refreshList(); }}>Saved work ({activeRows.length})</button>{onBack && <button className="wf-link" disabled={busy || uploading} onClick={() => { if (canLeave()) onBack(); }}>Back</button>}</div>
    {error && <div className="wf-error" role="alert">{error}{conflict ? <><p>Your entered details are still on screen. Reloading uses the saved version and discards this unsaved copy.</p><button className="wf-button secondary" disabled={busy || uploading} onClick={reloadSaved}>Reload saved version</button></> : draft && draft.revision > 0 && draft.status === 'draft' && <button className="wf-button secondary" disabled={busy || uploading} onClick={reloadSaved}>Check saved status</button>}</div>}
    {(pipelineWarnings.length > 0 || stockRefreshError) && <div className="wf-notice" role="status"><strong>Stock lists may be incomplete.</strong><p>{stockRefreshError || pipelineWarnings.map(String).join(' ')}</p><p>An empty list is not a verified zero balance. Keep the known details in a draft and refresh before choosing source stock.</p></div>}
    {screen !== 'done' && <div className="wf-data-status"><span>{demo ? 'Using the existing example stock records.' : window.RP_BOOTSTRAP && window.RP_BOOTSTRAP.loadedAt ? `Stock lists loaded ${new Date(window.RP_BOOTSTRAP.loadedAt).toLocaleString()}. Review checks the current records again.` : 'Recent stock lists are suggestions. Review verifies the current records before posting.'}</span><button className="wf-link" disabled={busy || uploading || frozen} onClick={refreshStock}>Refresh stock data</button></div>}
    {screen === 'home' && <><p className="wf-eyebrow">One place to keep stock moving</p><h1 ref={focusRef} tabIndex="-1">What happened to the stock?</h1><p className="wf-sub">Start with the part you know. Keep uncertain details for later, then review the exact effect before anything changes.</p>{flash && <div className="wf-notice good" role="status">{flash}</div>}<nav className="wf-path" aria-label="Start at any inventory stage">{allowedActions.filter(action => action !== 'repack').map((action, index) => <React.Fragment key={action}>{index > 0 && <span aria-hidden="true">›</span>}<button onClick={() => start(action)}>{({ can_run: 'Cans produced', pack_cases: 'Cases packed', release: 'Quality release', transfer: 'Warehouse / rep', acknowledge: 'Arrival confirmed' })[action]}</button></React.Fragment>)}</nav><div className="wf-choice-grid">{allowedActions.map(action => <button className="wf-choice" key={action} onClick={() => start(action)}><span className="wf-choice-icon"><Icon name={WF_ACTIONS[action].icon} size={23} /></span><span><strong>{WF_ACTIONS[action].title}</strong><small>{WF_ACTIONS[action].description}</small></span></button>)}</div><section className="wf-section"><div className="wf-row"><h2>Pick up where you left off</h2><button className="wf-link" onClick={() => setScreen('drafts')}>View all saved work →</button></div>{loading ? <p role="status">Loading saved work…</p> : activeRows.length ? draftCards(activeRows.slice(0, 3)) : <div className="wf-panel wf-muted">No unfinished work loaded. Start at any stage; incomplete information can be saved safely.</div>}</section><p className="wf-footnote">Use one record for each real event. An acknowledgement confirms a transfer already posted; it does not add another quantity.</p></>}
    {screen === 'drafts' && <><p className="wf-eyebrow">Keep every next step visible</p><h1 ref={focusRef} tabIndex="-1">Finish when you know more.</h1><p className="wf-sub">Drafts preserve the known facts, evidence and person to follow up with. Completed work retains its original result.</p><div className="wf-tabs"><button className={`wf-button ${scope === 'mine' ? '' : 'secondary'}`} onClick={() => setScope('mine')}>My work</button>{!demo && <button className={`wf-button ${scope === 'team' ? '' : 'secondary'}`} onClick={() => setScope('team')}>Team visibility</button>}<button className="wf-link" onClick={() => refreshList()}>Refresh</button></div>{scope === 'team' && <p className="wf-muted">Other people’s drafts are visible for coordination. Only the creator can edit or post their saved action.</p>}{loading ? <p role="status">Loading saved work…</p> : drafts.length ? draftCards(drafts) : <div className="wf-panel">No saved workflows loaded.</div>}</>}
    {draft && ['edit', 'review'].includes(screen) && <><p className="wf-eyebrow">{draft.revision ? 'Saved workflow' : 'Start with what you know'}</p><h1 ref={focusRef} tabIndex="-1">{workflowTitle}</h1><p className="wf-sub">{canRecover ? 'Recover the original saved action to confirm its result. Its details remain locked.' : canEdit ? 'You can save with missing details. Inventory changes only after review and an explicit confirmation.' : `Read-only work saved by ${draft.creatorName || 'another operator'}.`}</p><div className="wf-steps"><span className={screen === 'edit' ? 'current' : ''}><b>1</b> What you know</span><span className={screen === 'review' ? 'current' : ''}><b>2</b> Check the result</span><span><b>3</b> Next step</span></div>{draft.revision > 0 && <p className="wf-reference">Workflow {draft.id} · revision {draft.revision}{p.ref ? ` · ${p.ref}` : ''}</p>}
      {screen === 'edit' && <div className="wf-panel">{fieldContent}{exactLookup}{evidenceFields}<div className="wf-actions">{exitButtons}<button className="wf-button secondary" disabled={locked} onClick={saveLater}>{busy ? 'Saving…' : 'Save for later'}</button><button className="wf-button" disabled={locked} onClick={review}>Check next step →</button></div><p className="wf-footnote">Cancel leaves without saving your latest changes. Use Discard draft to remove an already saved draft. Checking the next step saves a draft; it does not post stock.</p></div>}
      {screen === 'review' && <div className="wf-panel">{frozen ? <><span className="wf-pill">Original request preserved</span><h2>This action needs its final result.</h2><p>Its exact details and original reference are locked. Recover this same action; do not create another movement for it.</p><ul className="wf-effects">{wfEffectLines(draft, true).map((line, index) => <li key={index}>{line}</li>)}</ul>{draft.lastError && <div className="wf-notice">{typeof draft.lastError === 'string' ? draft.lastError : 'The previous posting attempt did not return a confirmed result.'}</div>}<div className="wf-actions"><button className="wf-button secondary" disabled={busy} onClick={reloadSaved}>Check saved status</button><button className="wf-button" disabled={busy || !canRecover} onClick={post}>{busy ? 'Recovering…' : 'Recover original request'}</button></div></> : <><span className={`wf-pill${reviewIssues.length ? '' : ' good'}`}>{reviewIssues.length ? 'Needs a detail' : 'Ready for your review'}</span><h2>{reviewIssues.length ? 'Keep the progress. Resolve the gap.' : 'Here is what this will do.'}</h2>{reviewIssues.length > 0 && <div className="wf-notice"><ul>{reviewIssues.map(message => <li key={message}>{message}</li>)}</ul><p>No inventory changes while these details are unresolved.</p></div>}{preflight && preflight.effect && <div className="wf-notice good"><strong>Current record check</strong><p>{typeof preflight.effect === 'string' ? preflight.effect : 'The current stock effect was checked by the inventory service.'}</p></div>}<ul className="wf-effects">{wfEffectLines(draft).map((line, index) => <li key={index}>{line}</li>)}</ul>{((preflight && preflight.warnings) || []).length > 0 && <div className="wf-notice">{preflight.warnings.map((warning, index) => <p key={index}>{typeof warning === 'string' ? warning : warning.message}</p>)}</div>}{preflight && preflight.matches && <div className="wf-matches">{['transfers', 'stockRequests'].map(key => (preflight.matches[key] || []).length > 0 && <div className="wf-notice" key={key}><strong>{key === 'transfers' ? 'Related transfers to check' : 'Related stock requests to resolve'}</strong><ul>{preflight.matches[key].map((row, index) => <li key={index}>{row.id || row.ref || row.transfer_reference || row.reference || 'Existing record'}{row.status ? ` · ${row.status}` : ''}</li>)}</ul></div>)}</div>}<div className="wf-summary"><p><strong>Evidence:</strong> {facts.evidenceReferences || 'No reference recorded'}</p><p><strong>Notes:</strong> {facts.notes || 'None'}</p>{facts.followupName && <p><strong>Follow up with:</strong> {facts.followupName} — {facts.nextDetail || 'confirm the missing details'}</p>}</div>{!reviewIssues.length && preflight && preflight.ready && <WFCheck checked={confirmed} disabled={busy || !canEdit} onChange={setConfirmed}>I checked the physical event, exact lots, quantities and existing records. I authorize this specific action.</WFCheck>}<div className="wf-actions">{exitButtons}<button className="wf-button secondary" disabled={busy} onClick={() => { setPreflight(null); setConfirmed(false); setScreen('edit'); }}>Back to details</button>{reviewIssues.length ? <button className="wf-button" disabled={busy} onClick={() => { setFlash(demo ? 'Demo draft retained. Inventory unchanged.' : 'Draft retained in your account. Inventory unchanged.'); setScreen('home'); refreshList(); }}>Keep saved for later</button> : <button className="wf-button" disabled={busy || !canEdit || !confirmed || !preflight || !preflight.ready} onClick={post}>{busy ? 'Confirming…' : demo ? 'Complete demo example' : WF_ACTIONS[draft.action] && WF_ACTIONS[draft.action].confirm || 'Confirm action'}</button>}</div></>}</div>}
    </>}
    {screen === 'discarded' && draft && <section className="wf-panel"><h1 ref={focusRef} tabIndex="-1">Draft discarded</h1><p>This draft was removed from saved work. No inventory was changed by discarding it, and it cannot be continued or posted.</p><p className="wf-reference">{draft.id}</p><button className="wf-button secondary" onClick={cancelEdit}>Back to Move Stock</button></section>}
    {screen === 'done' && draft && <><div className="wf-success"><Icon name="check" size={30} /></div><p className="wf-eyebrow">{demo ? 'Demo completed' : 'Action confirmed'}</p><h1 ref={focusRef} tabIndex="-1">One step finished.<br />The next one is clear.</h1><p className="wf-sub">{demo ? 'This was a simulation. No live inventory or example balances were changed.' : `${workflowTitle}. The saved result stays linked to this workflow.`}</p><div className="wf-panel"><strong>Reference: {draft.payload.ref || draft.payload.id || draft.payload.linkedTransferRef || draft.id}</strong><ul className="wf-effects">{wfEffectLines(draft, true).map((line, index) => <li key={index}>{line}</li>)}</ul><p className="wf-reference">Workflow {draft.id}</p><h2>Suggested next step</h2><p>{({ can_run: 'Record packing when the actual case output is known.', pack_cases: 'Confirm quality clearance before releasing these cases.', release: 'Record the next physical handoff when it happens.', transfer: 'The receiver acknowledges this same completed transfer.', acknowledge: 'Record another handoff only if stock physically moves again.', repack: 'Record a handoff if the mixed cases move to another location.' })[draft.action]}</p><div className="wf-actions"><button className="wf-button secondary" onClick={() => { setScreen('home'); setDraft(null); }}>Back to Move Stock</button>{WF_ACTIONS[draft.action] && allowedActions.includes(WF_ACTIONS[draft.action].next) && <button className="wf-button" onClick={startNext}>Start the next step →</button>}</div></div></>}
    {screen === 'drafts' && nextOffset != null && <div className="wf-actions"><button className="wf-button secondary" disabled={loading} onClick={loadMore}>{loading ? 'Loading…' : 'Load older saved work'}</button></div>}
    <p className="wf-footnote">{demo ? 'Demo drafts are local to this browser.' : 'Saved work follows your signed-in account across devices. Save before leaving. If data or posting is unavailable, keep the original workflow and recover its status.'}</p>
  </main>;
}
Object.assign(window, { InventoryWorkflowView, InventoryWorkflowHelpers: { newDraft: wfNewDraft, preparedDraft: wfPreparedDraft, missingDetails: wfMissingDetails, receiptPayload: wfReceiptPayload, effectLines: wfEffectLines, holdingRows: wfHoldingRows } });


/* ===== location_retirement.jsx ===== */
const LOCATION_REFERENCE_LABELS = {
  rp_inventory_ledger: 'Inventory movements', rp_sales_orders: 'Orders', rp_stock_requests: 'Stock requests',
  rp_events: 'Events', rp_event_inventory_allocations: 'Event stock allocations', rp_user_profiles: 'Staff default locations',
  rp_packaging_runs: 'Packaging runs', rp_can_production_runs: 'Can production runs', rp_can_production_run_revisions: 'Production revisions',
  rp_inventory_transfer_logistics: 'Completed transfers', rp_chep_pallet_ledger: 'CHEP movements',
  rp_field_inventory_receipts: 'Receiving records', rp_order_stock_reservations: 'Stock reservations',
  rp_location_retirements: 'Previously reassigned history'
};
function locationReferenceLabel(table) { return LOCATION_REFERENCE_LABELS[table] || String(table || '').replace(/^rp_/, '').replace(/_/g, ' '); }
function SafeLocationRemovalModal({ location, onClose, onRemoved }) {
  const [plan, setPlan] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const [replacementId, setReplacementId] = useState(''), [reason, setReason] = useState(''), [confirmed, setConfirmed] = useState(false);
  const [frozen, setFrozen] = useState(null);
  const requestId = useRef(`LR-${crypto.randomUUID().toUpperCase()}`);
  const live = rpHasLiveAuth();
  const inFlight = useRef(false);
  async function check() {
    setBusy(true); setError(''); setConfirmed(false);
    try {
      if (!live) throw new Error('Safe removal uses live references and balances. The demo cannot prove a location is unused.');
      const response = await rpApi(`/api/locations/remove?id=${encodeURIComponent(location.id)}`);
      if (!response.plan) throw new Error('The removal check did not return a result.');
      setPlan(response.plan);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  useEffect(() => { check(); }, [location.id]);
  async function remove() {
    if (busy || inFlight.current) return;
    if (!frozen && (!confirmed || reason.trim().length < 5)) { setError('Enter a removal reason and check the confirmation box.'); return; }
    inFlight.current = true; setBusy(true); setError('');
    try {
      if (!live) throw new Error('Sign in to your live account before removing a location.');
      let payload = frozen;
      if (!payload) {
        const response = await rpApi(`/api/locations/remove?id=${encodeURIComponent(location.id)}`);
        if (!response.plan) throw new Error('The removal check did not return a result.');
        const fresh = response.plan; setPlan(fresh);
        if (!fresh.ready) throw new Error('This location still has the active records or stock listed below. Resolve them, then try again.');
        if (fresh.requiresReplacement && !replacementId) throw new Error('Choose the location that should hold this warehouse’s history.');
        payload = { locationId: location.id, expectedUpdatedAt: fresh.location.updated_at, requestId: requestId.current, replacementId, reason: reason.trim(), confirmed: true };
        setFrozen(payload);
      }
      const result = await rpApi('/api/locations/remove', { method: 'POST', body: JSON.stringify(payload) });
      if (!result.accepted) throw new Error('The saved result was not confirmed. Retry the same request.');
      await rpRefreshAfterMutation('Location removed safely');
      if (onRemoved) onRemoved(); onClose();
    } catch (err) {
      if ([400, 403, 404, 409].includes(err.status)) setFrozen(null);
      setError(err.message || 'Removal could not be confirmed. Retry the same request.');
    }
    finally { inFlight.current = false; setBusy(false); }
  }
  return <Modal title={`Remove ${location.name} safely`} sub="Keep stock and historical records accounted for" width={680} onClose={busy ? () => {} : onClose}
    footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={remove}>{busy ? 'Checking…' : frozen ? 'Retry original removal' : 'Confirm safe removal'}</button></>}>
    {error && <div role="alert" style={{ color: 'var(--danger)', marginBottom: 14 }}>{error}</div>}
    {busy && !plan && <p role="status">Checking stock, pallets and linked records…</p>}
    {plan && <>
      <InfoNote>{plan.requiresReplacement ? 'Choose where to attach this location’s history. Active orders and stock must be reassigned separately.' : 'This unused location can be removed.'}</InfoNote>
      {!!plan.references.length && <><h3>Linked records</h3><ul>{plan.references.map(row => <li key={`${row.table}.${row.field}`}>{locationReferenceLabel(row.table)}: {row.count} ({row.field.replace(/_/g, ' ')})</li>)}</ul></>}
      {!!plan.blockers.length && <div role="alert" style={{ background: 'var(--warn-bg)', padding: 14, borderRadius: 10 }}><strong>Resolve these first</strong><ul>{plan.blockers.map((item,index) => <li key={index}>{item.message}{item.table ? ` ${locationReferenceLabel(item.table)}: ${item.count}.` : ''}{item.quantity != null ? ` Balance: ${item.quantity}.` : ''}{item.balances && <ul>{item.balances.map((row,i) => <li key={i}>{row.sku_id} · {row.lot} · {row.owner} · {row.status}: {row.quantity} {row.unit}</li>)}</ul>}</li>)}</ul>{!frozen && <button className="btn" disabled={busy} onClick={check}>Check again</button>}</div>}
      {!!plan.blockingOrders?.length && <div><p>These orders still use this warehouse as their source. Open the order and change its source warehouse.</p>{plan.blockingOrders.map(order => <p key={order.id}><a href={`?workspace=orders&detail=order&record=${encodeURIComponent(order.id)}`} target="_blank" rel="noopener noreferrer">Open {order.id}</a> · {order.status}</p>)}{plan.moreOrders && <p>More linked orders remain. Resolve these and check again.</p>}</div>}
      {plan.requiresReplacement && <MField label="Attach this location’s history to" required><select className="field" disabled={busy || !!frozen} value={replacementId} onChange={event => { setReplacementId(event.target.value); setConfirmed(false); }}><option value="">Choose an active replacement</option>{RP.LOCATIONS.filter(row => row.id !== location.id && row.active !== false).map(row => <option key={row.id} value={row.id}>{row.name}</option>)}</select></MField>}
      <MField label="Reason for removal" required><textarea className="field" rows="3" maxLength="1000" value={reason} disabled={busy || !!frozen} onChange={event => { setReason(event.target.value); setConfirmed(false); }} placeholder="For example, this warehouse was created but rep storage is used instead." /></MField>
      <label style={{ display: 'flex', gap: 10, alignItems: 'start', marginTop: 14 }}><input type="checkbox" checked={confirmed} disabled={busy || !!frozen} onChange={event => setConfirmed(event.target.checked)} /><span>I reviewed the linked records and replacement. Remove this location from active use and preserve its removal history.</span></label>
    </>}
  </Modal>;
}
function LocationRemovalHistory({ refreshKey }) {
  const [rows,setRows] = useState([]), [offset,setOffset] = useState(null), [error,setError] = useState(''), [busy,setBusy] = useState(false);
  async function load(next = 0) {
    if (!rpHasLiveAuth()) return;
    setBusy(true); setError('');
    try { const result = await rpApi(`/api/locations/remove?history=1&offset=${next}`); setRows(current => next ? [...current,...result.history.filter(row => !current.some(item => item.id===row.id))] : result.history); setOffset(result.nextOffset); }
    catch(err) { setError(err.message); } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, [refreshKey]);
  return <section style={{ padding: 18, borderTop: '1px solid var(--line)' }}><h3>Removed locations and reassigned history</h3><p style={{ color: 'var(--muted)' }}>Original records retain the location where the event happened. The replacement below keeps that history associated with your current location.</p>{error && <p role="alert">{error}</p>}{!rows.length && !error && <p>{busy ? 'Loading history…' : 'No location removals recorded.'}</p>}{rows.map(row => <details key={row.id} style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}><summary><strong>{row.source_snapshot.name}</strong> → {row.replacement_location_id ? (RP.loc(row.replacement_location_id) || {}).name || row.replacement_location_id : 'Unused location deleted'} · {new Date(row.created_at).toLocaleDateString()}</summary><p>{row.reason}</p><p className="mono">{row.source_location_id} · {row.id}</p><ul>{row.reference_counts.map((reference,index) => <li key={index}>{locationReferenceLabel(reference.table)}: {reference.count} ({reference.field.replace(/_/g, ' ')})</li>)}</ul></details>)}{offset != null && <button className="btn" disabled={busy} onClick={() => load(offset)}>Load older removals</button>}</section>;
}


/* ===== desktop_admin.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : FBA · CHEP · Admin
   ============================================================ */

/* ============================================================ FBA */
const FBA_REIMBURSEMENTS = [
  { id: 'RB-441', shipment: 'FBA-8813', reason: 'Lost in transit', units: 10, amount: 38.40, status: 'Filed', opened: '2026-05-28' },
  { id: 'RB-438', shipment: 'FBA-8809', reason: 'Warehouse damaged', units: 6, amount: 23.04, status: 'Approved', opened: '2026-05-19' },
  { id: 'RB-432', shipment: 'FBA-8806', reason: 'Customer return — disposed', units: 3, amount: 11.52, status: 'Reimbursed', opened: '2026-05-08' },
];

function NewFbaShipmentModal({ onClose }) {
  const [f, setF] = useState({ flavor: 'ginger-citrus', dest: 'YVR2 · Delta', units: '', source: 'WH-KAM' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const dests = ['YVR2 · Delta', 'YYZ4 · Toronto', 'YYC1 · Calgary', 'Let Amazon assign'];
  const save = () => {
    setTried(true);
    const u = parseInt(f.units, 10);
    if (!u || u <= 0) return;
    const id = 'FBA-' + (8815 + RP.FBA_SHIPMENTS.length - 3);
    RP.FBA_SHIPMENTS.unshift({ id, dest: f.dest === 'Let Amazon assign' ? 'TBD' : f.dest, flavor: f.flavor, sent: u, recvd: 0, status: 'Pending' });
    toast(`FBA shipment ${id} created · ${u} units inbound`, 'info');
    onClose();
  };
  return (
    <Modal title="New FBA Shipment" sub="Send Ranch Power inventory into Amazon's network" onClose={onClose} width={520}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Create Shipment</button></>}>
      <MField label="Product" required><select className="field" value={f.flavor} onChange={e => set('flavor', e.target.value)}>{RP.FLAVOR_MIX.map(m => { const sku = rpSkuItem(m.flavor); return <option key={m.flavor} value={m.flavor}>{rpSkuDisplayName(sku || m.flavor)} - {rpSkuExternalIdentity(sku || '')}</option>; })}</select></MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Ship From" half><select className="field" value={f.source} onChange={e => set('source', e.target.value)}>{[['WH-KAM', 'Kamloops WH'], ['WH-SQ', 'Squamish WH'], ['WH-NBC', 'Northern BC WH'], ['WH-KEL', 'Kelowna WH']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></MField>
        <MField label="Destination FC" half><select className="field" value={f.dest} onChange={e => set('dest', e.target.value)}>{dests.map(d => <option key={d}>{d}</option>)}</select></MField>
      </div>
      <MField label="Units" required>
        <input className="field" type="number" placeholder="e.g. 600" value={f.units} onChange={e => set('units', e.target.value)} style={{ borderColor: tried && !(parseInt(f.units, 10) > 0) ? 'var(--danger)' : undefined }} />
        {tried && !(parseInt(f.units, 10) > 0) && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>Enter a unit count</div>}
      </MField>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5 }}><Icon name="fba" size={15} />Units stay owned by Ranch Power until Amazon scans them at the FC.</div>
    </Modal>
  );
}

function FbaShipmentModal({ sh, onClose }) {
  const variance = sh.recvd - sh.sent;
  const sku = rpSkuItem(sh.sku || sh.flavor);
  const flavorId = rpSkuVisualFlavor(sku || sh.flavor);
  const fl = RP.flavor(flavorId) || { name: rpSkuDisplayName(sku || sh.flavor) };
  const steps = [
    { k: 'Created', done: true },
    { k: 'In transit', done: sh.status !== 'Pending' && sh.status !== 'Planned' },
    { k: 'Receiving', done: sh.recvd > 0 },
    { k: 'Closed', done: sh.status === 'Closed' },
  ];
  return (
    <Modal title={sh.id} sub={`${fl.name} · ${sh.dest}`} onClose={onClose} width={520}
      footer={<>
        {sh.status === 'Disputed' && <button className="btn" style={{ color: 'var(--danger)', marginRight: 'auto' }} onClick={() => { toast(`Reimbursement case opened for ${sh.id}`, 'info'); onClose(); }}><Icon name="flag" size={15} />File Reimbursement</button>}
        <button className="btn" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={() => { toast(`Tracking link copied for ${sh.id}`, 'info'); }}><Icon name="truck" size={15} />Track</button>
      </>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 20 }}>
        <FlavorChip flavorId={flavorId} size={40} />
        <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: 15 }}>{fl.name}</div><div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 2 }}>{rpSkuExternalIdentity(sku || '')}</div><div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{sh.dest}</div></div>
        <Pill status={sh.status === 'Disputed' ? 'Disputed' : sh.status === 'Closed' ? 'Delivered' : sh.status === 'Pending' ? 'Submitted' : sh.status}>{sh.status}</Pill>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {[['Sent', sh.sent.toLocaleString()], ['Received', sh.recvd ? sh.recvd.toLocaleString() : '—'], ['Variance', sh.status === 'Pending' ? '—' : (variance === 0 ? '0' : variance)]].map(([k, v], i) => (
          <div key={k} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px' }}>
            <div className="eyebrow" style={{ marginBottom: 5 }}>{k}</div>
            <div className="disp" style={{ fontSize: 22, color: i === 2 && variance < 0 ? 'var(--danger)' : 'var(--ink)' }}>{v}</div>
          </div>
        ))}
      </div>
      <div className="eyebrow" style={{ marginBottom: 12 }}>Progress</div>
      <div style={{ display: 'flex', gap: 0, marginBottom: 4 }}>
        {steps.map((s, i) => (
          <div key={s.k} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, position: 'relative' }}>
            {i < steps.length - 1 && <div style={{ position: 'absolute', top: 10, left: '50%', width: '100%', height: 2, background: steps[i + 1].done ? 'var(--ok)' : 'var(--line)' }} />}
            <span style={{ width: 22, height: 22, borderRadius: '50%', background: s.done ? 'var(--ok)' : 'var(--surface-2)', border: '1px solid ' + (s.done ? 'var(--ok)' : 'var(--line)'), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>{s.done && <Icon name="check" size={13} />}</span>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: s.done ? 'var(--ink)' : 'var(--faint)' }}>{s.k}</span>
          </div>
        ))}
      </div>
      {variance < 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, marginTop: 18 }}><Icon name="alert" size={15} />{Math.abs(variance)} units short on receiving — eligible for reimbursement.</div>}
    </Modal>
  );
}

function FbaView({ go }) {
  const f = RP.FBA_SUMMARY;
  const [tab, setTab] = useState('shipments');
  const [sel, setSel] = useState(null);
  const [newShip, setNewShip] = useState(false);
  const [, force] = useState(0);
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {newShip && <NewFbaShipmentModal onClose={() => { setNewShip(false); force(x => x + 1); }} />}
      {sel && <FbaShipmentModal sh={sel} onClose={() => setSel(null)} />}
      <PageHead eyebrow="Amazon Channel" title="FBA"
        sub="Fenced inventory owned by Ranch Power until Amazon receives it."
        actions={<button className="btn btn-sm btn-primary" onClick={() => setNewShip(true)}><Icon name="plus" size={15} />FBA Shipment</button>} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 22 }}>
        <KPICard label="Available" value={f.available.toLocaleString()} unit="units" icon="check" accent="var(--ok)" />
        <KPICard label="Inbound" value={f.inbound.toLocaleString()} unit="units" icon="fba" accent="var(--info)" />
        <KPICard label="Reserved" value={f.reserved} unit="units" icon="package" accent="var(--amber)" />
        <KPICard label="Damaged / Lost" value={f.damaged} unit="units" icon="alert" accent="var(--danger)" onClick={() => setTab('reimburse')} active={tab === 'reimburse'} />
      </div>
      <div style={{ marginBottom: 18 }}><Segmented options={[{ value: 'shipments', label: 'Shipments' }, { value: 'reimburse', label: 'Reimbursements' }]} value={tab} onChange={setTab} /></div>

      {tab === 'shipments' ? (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Shipment</th><th>Destination</th><th>SKU</th><th className="num">Sent</th><th className="num">Received</th><th className="num">Variance</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {RP.FBA_SHIPMENTS.map(sh => {
                const variance = sh.recvd - sh.sent;
                const sku = rpSkuItem(sh.sku || sh.flavor);
                const flavorId = rpSkuVisualFlavor(sku || sh.flavor);
                return (
                  <tr key={sh.id} style={{ cursor: 'pointer' }} onClick={() => setSel(sh)}>
                    <td className="mono nowrap" style={{ fontWeight: 600 }}>{sh.id}</td>
                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: sh.dest === 'TBD' ? 'var(--faint)' : 'var(--ink)' }}><Icon name="pin" size={14} style={{ color: 'var(--muted)' }} />{sh.dest}</span></td>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip flavorId={flavorId} size={22} /><span><span style={{ display: 'block', fontWeight: 500 }}>{rpSkuDisplayName(sku || sh.flavor)}</span><span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--faint)', marginTop: 2 }}>{rpSkuExternalIdentity(sku || '')}</span></span></div></td>
                    <td className="num">{sh.sent.toLocaleString()}</td>
                    <td className="num" style={{ color: sh.recvd === 0 ? 'var(--faint)' : 'var(--ink)' }}>{sh.recvd.toLocaleString()}</td>
                    <td className="num" style={{ fontWeight: 700, color: variance < 0 ? 'var(--danger)' : 'var(--muted)' }}>{sh.status === 'Planned' || sh.status === 'Pending' ? '—' : variance === 0 ? '0' : variance}</td>
                    <td><Pill status={sh.status === 'Discrepancy' ? 'Disputed' : sh.status === 'Closed' ? 'Delivered' : sh.status === 'Planned' || sh.status === 'Pending' ? 'Submitted' : sh.status}>{sh.status}</Pill></td>
                    <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Case</th><th>Shipment</th><th>Reason</th><th className="num">Units</th><th className="num">Est. Value</th><th>Opened</th><th>Status</th></tr></thead>
            <tbody>
              {FBA_REIMBURSEMENTS.map(r => (
                <tr key={r.id}>
                  <td className="mono nowrap" style={{ fontWeight: 600 }}>{r.id}</td>
                  <td className="mono" style={{ color: 'var(--muted)' }}>{r.shipment}</td>
                  <td style={{ fontWeight: 500 }}>{r.reason}</td>
                  <td className="num">{r.units}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{RP.money2(r.amount)}</td>
                  <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{r.opened}</td>
                  <td><Pill status={r.status === 'Reimbursed' ? 'Delivered' : r.status === 'Approved' ? 'Approved' : 'Submitted'}>{r.status}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Open cases recover value for units Amazon lost or damaged.</span>
            <button className="btn btn-sm btn-primary" onClick={() => toast('Reimbursement case drafted', 'info')}><Icon name="plus" size={14} />File Case</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 26 }}><SectionTitle>SKU Identifiers</SectionTitle></div>
      <div className="card" style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
        {[['Marketplace', 'Amazon.ca'], ['ASIN', 'B0RP12GC55'], ['FNSKU', 'X002RP9GC1'], ['Seller SKU', 'RP-12-GC-FBA']].map(([k, v]) => (
          <div key={k}><div className="eyebrow nowrap" style={{ marginBottom: 4 }}>{k}</div><div className="mono" style={{ fontSize: 13.5, fontWeight: 600 }}>{v}</div></div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================ CHEP — folded into Dispatch as a sub-tab, but standalone too */
function chepLocalDateTime() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function chepClientRef() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return `CHEP-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
  return `CHEP-${Date.now().toString(36).toUpperCase()}`;
}

function ChepMovementModal({ movementType, onClose }) {
  const locations = (RP.LOCATIONS || []).filter(location => location && location.id);
  const [f, setF] = useState({ adjustmentDirection: 'increase', evidenceReference: '', locationId: (locations[0] || {}).id || '', occurredAt: chepLocalDateTime(), palletCount: '', reason: '', ref: chepClientRef() });
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setF(current => ({ ...current, [key]: value }));
  const count = Number(f.palletCount);
  const title = movementType === 'pool_issue' ? 'Receive CHEP Pallets' : movementType === 'pool_return' ? 'Return CHEP Pallets' : 'Adjust Physical CHEP Count';
  const valid = Boolean(f.locationId && Number.isInteger(count) && count > 0 && f.occurredAt && f.evidenceReference.trim() && f.reason.trim().length >= 8);
  const save = async () => {
    setTried(true);
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/inventory/chep', {
        method: 'POST',
        body: JSON.stringify({ adjustmentDirection: movementType === 'count_adjustment' ? f.adjustmentDirection : undefined, evidenceReference: f.evidenceReference.trim(), locationId: f.locationId, movementType, occurredAt: new Date(f.occurredAt).toISOString(), palletCount: count, reason: f.reason.trim(), ref: f.ref })
      });
      await rpRefreshAfterMutation(`CHEP movement ${response.ref || f.ref} recorded`);
      toast(`CHEP movement ${response.ref || f.ref} recorded`, 'ok');
      onClose();
    } catch (err) {
      const message = err && err.message ? err.message : 'CHEP movement could not be recorded.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  return <Modal title={title} sub="Evidence-backed CHEP custody movement" onClose={onClose} width={600}
    footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Record Movement'}</button></>}>
    <InfoNote icon="layers">{movementType === 'pool_issue' ? 'Use when CHEP issues pallets from its pool into Ranch Power custody.' : movementType === 'pool_return' ? 'Use when pallets physically leave a Ranch Power location and are returned to the CHEP pool.' : 'Use only to reconcile an evidence-backed physical count. This does not move finished goods.'}</InfoNote>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
      <MField label="Custody Location" required><select className="field" value={f.locationId} onChange={e => set('locationId', e.target.value)} style={tried && !f.locationId ? { borderColor: 'var(--danger)' } : undefined}>{locations.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}</select></MField>
      <MField label="Occurred At" required><input className="field" type="datetime-local" value={f.occurredAt} onChange={e => set('occurredAt', e.target.value)} style={tried && !f.occurredAt ? { borderColor: 'var(--danger)' } : undefined} /></MField>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: movementType === 'count_adjustment' ? '1fr 1fr' : '1fr', gap: 14 }}>
      {movementType === 'count_adjustment' && <MField label="Direction" required><select className="field" value={f.adjustmentDirection} onChange={e => set('adjustmentDirection', e.target.value)}><option value="increase">Increase tracked balance</option><option value="decrease">Decrease tracked balance</option></select></MField>}
      <MField label="Pallet Count" required><input className="field mono" type="number" min="1" step="1" value={f.palletCount} onChange={e => set('palletCount', e.target.value)} placeholder="Whole pallets" style={tried && !(Number.isInteger(count) && count > 0) ? { borderColor: 'var(--danger)' } : undefined} /></MField>
    </div>
    <MField label="Evidence Reference" required><input className="field" value={f.evidenceReference} onChange={e => set('evidenceReference', e.target.value)} placeholder="CHEP docket, photo, count sheet, or ticket" style={tried && !f.evidenceReference.trim() ? { borderColor: 'var(--danger)' } : undefined} /></MField>
    <MField label="Reason" required><textarea className="field" rows="3" value={f.reason} onChange={e => set('reason', e.target.value)} placeholder="Why this pallet movement occurred" style={{ resize: 'vertical', ...(tried && f.reason.trim().length < 8 ? { borderColor: 'var(--danger)' } : {}) }} /></MField>
    <div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>Immutable reference: {f.ref}</div>
    {tried && !valid && <div style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 700, marginTop: 10 }}>Complete every required field with a positive whole-pallet count and a clear reason.</div>}
    {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 10 }}>{error}</div>}
  </Modal>;
}

function ChepPanel() {
  const balances = (RP.CHEP_BALANCES || []).slice().sort((a, b) => String(a.locationName || a.locationId).localeCompare(String(b.locationName || b.locationId)));
  const movements = (RP.CHEP_MOVEMENTS || []).slice().sort((a, b) => String(b.occurredAt || b.createdAt || '').localeCompare(String(a.occurredAt || a.createdAt || '')));
  const [movementType, setMovementType] = useState('');
  const total = balances.reduce((sum, row) => sum + Number(row.balance || 0), 0);
  const transferCount = movements.filter(row => row.movementType === 'inventory_transfer').length;
  const label = type => type === 'pool_issue' ? 'Pool issue' : type === 'pool_return' ? 'Pool return' : type === 'count_adjustment' ? 'Count adjustment' : 'Inventory transfer';
  const endpoint = row => row.movementType === 'inventory_transfer'
    ? `${(RP.loc(row.fromLocationId) || {}).name || row.fromLocationId} -> ${(RP.loc(row.toLocationId) || {}).name || row.toLocationId}`
    : (RP.loc(row.adjustmentLocationId || row.toLocationId || row.fromLocationId) || {}).name
      || row.adjustmentLocationId || row.toLocationId || row.fromLocationId || '-';
  return (
    <div>
      {movementType && <ChepMovementModal movementType={movementType} onClose={() => setMovementType('')} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 18 }}>
        <KPICard label="Tracked Custody" value={total} unit="pallets" icon="layers" accent="var(--navy)" />
        <KPICard label="Locations Holding CHEP" value={balances.filter(row => Number(row.balance || 0) > 0).length} unit="locations" icon="inventory" accent="var(--info)" />
        <KPICard label="Recorded CHEP Transfers" value={transferCount} unit="movements" icon="truck" accent="var(--ok)" />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-sm" onClick={() => setMovementType('count_adjustment')}><Icon name="refresh" size={14} />Physical Count</button>
        <button className="btn btn-sm" onClick={() => setMovementType('pool_return')}><Icon name="arrowR" size={14} />Return to Pool</button>
        <button className="btn btn-sm btn-primary" onClick={() => setMovementType('pool_issue')}><Icon name="plus" size={14} />Receive from Pool</button>
      </div>
      <div className="card" style={{ overflow: 'hidden', marginBottom: 18 }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--line)' }}><SectionTitle>CHEP Pallets by Custody Location</SectionTitle></div>
        <table className="rp-table">
          <thead><tr><th>Location</th><th className="num">Tracked Balance</th><th>Status</th></tr></thead>
          <tbody>
            {balances.map(row => <tr key={row.locationId}><td style={{ fontWeight: 600 }}>{row.locationName || row.locationId}</td><td className="num mono" style={{ fontWeight: 800 }}>{Number(row.balance || 0).toLocaleString()}</td><td><Pill status={Number(row.balance || 0) > 0 ? 'Active' : 'Pending'}>{Number(row.balance || 0) > 0 ? 'In custody' : 'Clear'}</Pill></td></tr>)}
            {!balances.length && <tr><td colSpan="3" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No CHEP opening balance has been recorded. Start with an evidence-backed pool issue or physical count.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--line)' }}><SectionTitle>CHEP Movement Journal</SectionTitle></div>
        <table className="rp-table">
          <thead><tr><th>Occurred</th><th>Reference</th><th>Movement</th><th>Route / Location</th><th className="num">Pallets</th><th>Reason</th></tr></thead>
          <tbody>
            {movements.slice(0, 100).map(row => <tr key={row.id}><td className="mono nowrap">{String(row.occurredAt || row.createdAt || '').replace('T', ' ').slice(0, 16)}</td><td className="mono" style={{ fontWeight: 600 }}>{row.id}</td><td>{label(row.movementType)}</td><td>{endpoint(row)}</td><td className="num mono" style={{ fontWeight: 800 }}>{row.adjustmentDelta || row.palletCount}</td><td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{row.reason || '-'}</td></tr>)}
            {!movements.length && <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No CHEP custody movements recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ ADMIN */
function shopifySyncSummary(run) {
  const summary = run && run.summary ? run.summary : {};
  const parts = [
    summary.products != null ? `${summary.products} products` : null,
    summary.variants != null ? `${summary.variants} variants` : null,
    summary.companies != null ? `${summary.companies} companies` : null,
    summary.b2bOrders != null ? `${summary.b2bOrders} B2B orders` : null,
    summary.customers != null ? `${summary.customers} customers` : null,
    summary.scopedPreview && summary.scopedPreview.includedOrderCount != null ? `${summary.scopedPreview.includedOrderCount} scoped orders` : null,
    summary.orders != null ? `${summary.orders} orders` : null,
    summary.orderLines != null ? `${summary.orderLines} lines` : null,
    Array.isArray(summary.warnings) && summary.warnings.length ? `${summary.warnings.length} warnings` : null,
    summary.mode ? String(summary.mode) : null
  ].filter(Boolean);
  return parts.length ? parts.join(' / ') : (run && run.error ? run.error : 'No summary recorded');
}

function shopifySyncStatus(run) {
  if (!run) return 'Pending';
  return String(run.status || '').toLowerCase() === 'success' ? 'Active' : 'Damaged';
}

function shopifyWarnings(run) {
  if (!run) return [];
  if (Array.isArray(run.warnings)) return run.warnings.filter(Boolean);
  const summary = run.summary || {};
  return Array.isArray(summary.warnings) ? summary.warnings.filter(Boolean) : [];
}

function shopifyPullSummaryFromData(data) {
  const products = data && data.products && Array.isArray(data.products.nodes) ? data.products.nodes : [];
  const orders = data && data.orders && Array.isArray(data.orders.nodes) ? data.orders.nodes : [];
  const companies = data && data.companies && Array.isArray(data.companies.nodes) ? data.companies.nodes : [];
  return {
    companies: companies.length,
    b2bOrders: orders.filter(order => order && order.purchasingEntity && order.purchasingEntity.company).length,
    orders: orders.length,
    products: products.length,
    variants: products.reduce((total, product) => total + ((product.variants && Array.isArray(product.variants.nodes)) ? product.variants.nodes.length : 0), 0)
  };
}

const SCOPED_SHOPIFY_IMPORT_CHUNK_SIZE = 10;

function scopedImportOrderName(order) {
  return String(order && (order.name || order.id) || '').trim();
}

function chunkList(items, size) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

function scopedSyncSummaryFromResponses(responses, includedCount, warnings, filters) {
  const summary = {
    batches: responses.length,
    scoped: true,
    scopedPreview: {
      filters: filters || {},
      includedOrderCount: includedCount,
      skippedOrderCount: 0
    },
    warnings: warnings || []
  };
  responses.forEach(response => {
    const source = response && response.sync && response.sync.summary ? response.sync.summary : {};
    Object.entries(source).forEach(([key, value]) => {
      if (typeof value === 'number') summary[key] = Number(summary[key] || 0) + value;
      else if (summary[key] == null && value != null && !['warnings', 'scopedPreview'].includes(key)) summary[key] = value;
    });
  });
  return { persisted: true, summary, warnings: warnings || [] };
}

function setupDetailLines(row) {
  const lines = [];
  if (Array.isArray(row.missing) && row.missing.length) {
    lines.push(`Missing: ${row.missing.join(', ')}`);
  }
  if (Array.isArray(row.failed) && row.failed.length) {
    lines.push(`Failed: ${row.failed.map(item => item.table || item).join(', ')}`);
  }
  if (Array.isArray(row.wrongEndpoint) && row.wrongEndpoint.length) {
    lines.push(`Wrong endpoint: ${row.wrongEndpoint.map(item => item.topic || item).join(', ')}`);
  }
  if (row.expectedUrl && (Array.isArray(row.missing) || Array.isArray(row.wrongEndpoint))) {
    lines.push(`Expected URL: ${row.expectedUrl}`);
  }
  return lines;
}

function IntegrationsView() {
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [scopedBusy, setScopedBusy] = useState(false);
  const [scopedSyncBusy, setScopedSyncBusy] = useState(false);
  const [scopedImportProgress, setScopedImportProgress] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scopedPreview, setScopedPreview] = useState(null);
  const [scopedPull, setScopedPull] = useState({ from: '', to: '', orderNames: '', posOnly: true, saleableOnly: true });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [setup, setSetup] = useState(null);
  const [setupError, setSetupError] = useState('');
  const fullSyncRequestId = useRef('');
  const scopedSyncRequestIds = useRef({});
  const configured = !(window.RP_AUTH && window.RP_AUTH.demo);
  const syncRuns = (RP.SHOPIFY_SYNC_RUNS || []).slice().sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  const latestSync = syncRuns[0] || null;
  const latestWarnings = shopifyWarnings(latestSync);
  const resultWarnings = shopifyWarnings(result);
  const scopedIncludedOrders = scopedPreview && Array.isArray(scopedPreview.includedOrders) ? scopedPreview.includedOrders : [];
  const scopedIncludedCount = Number(scopedPreview && scopedPreview.includedOrderCount || 0);
  const scopedSkippedCount = Number(scopedPreview && scopedPreview.skippedOrderCount || 0);
  const scopedImportOrderNames = scopedIncludedOrders.map(scopedImportOrderName).filter(Boolean);
  const scopedImportBatchCount = Math.ceil(scopedImportOrderNames.length / SCOPED_SHOPIFY_IMPORT_CHUNK_SIZE);

  const setScoped = (key, value) => {
    setScopedPull(current => Object.assign({}, current, { [key]: value }));
    setScopedPreview(null);
    setScopedImportProgress(null);
    scopedSyncRequestIds.current = {};
  };

  const scopedPayload = (confirm, overrides = {}) => ({
    confirm: !!confirm,
    dryRun: !confirm,
    expectedSourceHash: confirm ? overrides.expectedSourceHash : undefined,
    from: scopedPull.from,
    includeCompanies: false,
    includeProducts: false,
    limit: overrides.limit,
    orderNames: overrides.orderNames != null ? overrides.orderNames : scopedPull.orderNames,
    posOnly: scopedPull.posOnly,
    requestId: overrides.requestId,
    saleableOnly: scopedPull.saleableOnly,
    scoped: true,
    to: scopedPull.to
  });

  const previewShopify = async () => {
    setPreviewBusy(true);
    setError('');
    setPreview(null);
    try {
      const res = await rpApi('/api/shopify/pull');
      const nextPreview = {
        pulledAt: res.pulledAt,
        shop: res.data && res.data.shop ? (res.data.shop.myshopifyDomain || res.data.shop.name) : '',
        summary: shopifyPullSummaryFromData(res.data || {}),
        warnings: Array.isArray(res.warnings) ? res.warnings : []
      };
      setPreview(nextPreview);
      toast(nextPreview.warnings.length ? 'Shopify preview loaded with warnings' : 'Shopify preview loaded', nextPreview.warnings.length ? 'warn' : 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Shopify preview failed.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setPreviewBusy(false);
    }
  };

  const syncShopify = async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      if (!fullSyncRequestId.current) fullSyncRequestId.current = crypto.randomUUID();
      const res = await rpApi('/api/shopify/pull', {
        method: 'POST',
        body: JSON.stringify({ requestId: fullSyncRequestId.current })
      });
      fullSyncRequestId.current = '';
      setResult(Object.assign({}, res.sync || res, { warnings: res.warnings || [] }));
      await rpRefreshAfterMutation('Shopify sync completed');
      toast((res.warnings || []).length ? 'Shopify sync completed with warnings' : 'Shopify sync completed', (res.warnings || []).length ? 'warn' : 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Shopify sync failed.';
      setError(message);
      await rpRefreshAfterMutation('Shopify sync failed');
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const previewScopedShopify = async () => {
    setScopedBusy(true);
    setError('');
    setScopedPreview(null);
    setScopedImportProgress(null);
    scopedSyncRequestIds.current = {};
    try {
      const res = await rpApi('/api/shopify/pull', {
        method: 'POST',
        body: JSON.stringify(scopedPayload(false))
      });
      setScopedPreview(Object.assign({}, res.preview || {}, {
        sourceHash: res.sourceHash || res.preview && res.preview.sourceHash || ''
      }));
      const count = Number(res.preview && res.preview.includedOrderCount || 0);
      toast(count ? `${count} scoped Shopify POS orders ready to review` : 'No scoped Shopify POS orders matched', count ? 'ok' : 'warn');
    } catch (err) {
      const message = err && err.message ? err.message : 'Scoped Shopify preview failed.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setScopedBusy(false);
    }
  };

  const syncScopedShopify = async () => {
    setScopedSyncBusy(true);
    setError('');
    setResult(null);
    setScopedImportProgress(null);
    try {
      if (!scopedImportOrderNames.length) throw new Error('Run a scoped preview before importing so the order numbers can be batched safely.');
      const chunks = chunkList(scopedImportOrderNames, SCOPED_SHOPIFY_IMPORT_CHUNK_SIZE);
      const sourceBatches = Array.isArray(scopedPreview && scopedPreview.sourceBatches) ? scopedPreview.sourceBatches : [];
      if (Number(scopedPreview && scopedPreview.sourceBatchSize) !== SCOPED_SHOPIFY_IMPORT_CHUNK_SIZE) {
        throw new Error('The scoped preview batch contract changed. Run the preview again before importing.');
      }
      if (sourceBatches.length !== chunks.length) throw new Error('The scoped preview proof is incomplete. Run the preview again before importing.');
      const previewLimit = Number(scopedPreview && scopedPreview.filters && scopedPreview.filters.orderFirst || 100);
      const responses = [];
      const warnings = [];
      let imported = 0;
      for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index];
        const sourceProof = sourceBatches[index];
        if (!sourceProof || !sourceProof.sourceHash) throw new Error('The scoped preview proof is missing for this batch. Run the preview again before importing.');
        if (JSON.stringify(sourceProof.orderNames || []) !== JSON.stringify(chunk)) {
          throw new Error('The scoped preview order set changed locally. Run the preview again before importing.');
        }
        const batchKey = JSON.stringify({
          chunk,
          from: scopedPull.from,
          posOnly: scopedPull.posOnly,
          saleableOnly: scopedPull.saleableOnly,
          to: scopedPull.to
        });
        if (!scopedSyncRequestIds.current[batchKey]) {
          scopedSyncRequestIds.current[batchKey] = crypto.randomUUID();
        }
        setScopedImportProgress({ done: imported, total: scopedImportOrderNames.length, batch: index + 1, batches: chunks.length });
        const res = await rpApi('/api/shopify/pull', {
          method: 'POST',
          body: JSON.stringify(scopedPayload(true, {
            expectedSourceHash: sourceProof.sourceHash,
            limit: previewLimit,
            orderNames: chunk.join(', '),
            requestId: scopedSyncRequestIds.current[batchKey]
          }))
        });
        responses.push(res);
        if (Array.isArray(res.warnings)) warnings.push(...res.warnings);
        imported += chunk.length;
        setScopedImportProgress({ done: imported, total: scopedImportOrderNames.length, batch: index + 1, batches: chunks.length });
      }
      scopedSyncRequestIds.current = {};
      setResult(scopedSyncSummaryFromResponses(responses, scopedImportOrderNames.length, warnings, scopedPreview && scopedPreview.filters));
      await rpRefreshAfterMutation('Scoped Shopify POS import completed');
      toast(warnings.length ? `Scoped Shopify import completed in ${chunks.length} batches with warnings` : `Scoped Shopify import completed in ${chunks.length} batches`, warnings.length ? 'warn' : 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Scoped Shopify import failed.';
      setError(message);
      await rpRefreshAfterMutation('Scoped Shopify import failed');
      toast(message, 'danger');
    } finally {
      setScopedSyncBusy(false);
    }
  };

  const checkSetup = async () => {
    setChecking(true);
    setSetupError('');
    try {
      const res = await rpApi('/api/setup/check');
      setSetup(res);
      toast(res.ready ? 'Setup check passed' : 'Setup check found missing items', res.ready ? 'ok' : 'warn');
    } catch (err) {
      const message = err && err.message ? err.message : 'Setup check failed.';
      setSetupError(message);
      toast(message, 'danger');
    } finally {
      setChecking(false);
    }
  };

  const setupRows = setup && setup.results ? Object.entries(setup.results) : [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1fr)', gap: 16 }}>
      <div className="card" style={{ padding: '18px 20px', gridColumn: '1 / -1' }}>
        <SectionTitle right={setup ? <Pill status={setup.ready ? 'Active' : 'Pending'}>{setup.ready ? 'Ready' : 'Needs Setup'}</Pill> : null}>Deployment Readiness</SectionTitle>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.45, marginBottom: 14 }}>
          Checks the server-side pieces required for production: Supabase Auth, all live Supabase data tables, Shopify Admin API, and Resend email configuration.
        </div>
        <button className="btn btn-navy" disabled={checking || !configured} onClick={checkSetup} style={{ opacity: checking || !configured ? 0.65 : 1 }}>
          <Icon name={checking ? 'refresh' : 'check'} size={15} />{checking ? 'Checking...' : 'Check Setup'}
        </button>
        {!configured && <span style={{ marginLeft: 12, fontSize: 12.5, color: 'var(--faint)' }}>Live checks require a signed-in Supabase session.</span>}
        {setupError && <div style={{ marginTop: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{setupError}</div>}
        {setupRows.length > 0 && (
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
            {setupRows.map(([key, row]) => {
              const status = row.status === 'ok' ? 'Active' : row.status === 'missing' ? 'Pending' : 'Damaged';
              const details = setupDetailLines(row);
              return (
                <div key={key} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 9, padding: '11px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                    <span className="eyebrow">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <Pill status={status}>{row.status}</Pill>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.35 }}>{row.message}</div>
                  {details.map(detail => <div key={detail} style={{ marginTop: 7, fontSize: 11.5, color: 'var(--ink-soft)', lineHeight: 1.35, wordBreak: 'break-word' }}>{detail}</div>)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '18px 20px' }}>
        <SectionTitle right={<Pill status={configured ? 'Active' : 'Invited'}>{configured ? 'Protected' : 'Demo'}</Pill>}>Shopify</SectionTitle>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.45, marginBottom: 16 }}>
          Pulls products, variants, prices, B2B companies, contacts, recent orders and line items through the server-side Shopify Admin API. Synced records are merged into Supabase SKUs, customers and order tracking tables when live credentials are configured.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <button className="btn btn-navy" disabled={previewBusy || busy || !configured} onClick={previewShopify} style={{ opacity: previewBusy || busy || !configured ? 0.65 : 1 }}>
            <Icon name={previewBusy ? 'refresh' : 'search'} size={15} />{previewBusy ? 'Previewing...' : 'Preview Pull'}
          </button>
          <button className="btn btn-primary" disabled={busy || previewBusy || !configured} onClick={syncShopify} style={{ opacity: busy || previewBusy || !configured ? 0.65 : 1 }}>
            <Icon name={busy ? 'refresh' : 'download'} size={15} />{busy ? 'Syncing...' : 'Sync Shopify'}
          </button>
        </div>
        {!configured && <div style={{ marginTop: 10, color: 'var(--faint)', fontSize: 12.5 }}>Live Shopify checks require a signed-in Supabase session.</div>}
        <div style={{ marginTop: 18, borderTop: '1px solid var(--line)', paddingTop: 16, display: 'grid', gap: 12 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 5 }}>Scoped POS Import</div>
            <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.45 }}>
              Use this for event backfills. It pulls only matching Shopify POS orders, skips non-saleable line items, and defaults to a dry-run preview.
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <label style={{ display: 'grid', gap: 5 }}>
              <span className="eyebrow">Start Date</span>
              <input className="field" type="date" value={scopedPull.from} onChange={e => setScoped('from', e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 5 }}>
              <span className="eyebrow">End Date</span>
              <input className="field" type="date" value={scopedPull.to} onChange={e => setScoped('to', e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 5, gridColumn: 'span 2' }}>
              <span className="eyebrow">Order Numbers</span>
              <input className="field" value={scopedPull.orderNames} onChange={e => setScoped('orderNames', e.target.value)} placeholder="#1066, #1067 (optional)" />
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <label className="btn" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={scopedPull.posOnly} onChange={e => setScoped('posOnly', e.target.checked)} /> POS only
            </label>
            <label className="btn" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={scopedPull.saleableOnly} onChange={e => setScoped('saleableOnly', e.target.checked)} /> Saleable Ranch Power lines only
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button className="btn btn-navy" disabled={scopedBusy || scopedSyncBusy || !configured} onClick={previewScopedShopify} style={{ opacity: scopedBusy || scopedSyncBusy || !configured ? 0.65 : 1 }}>
              <Icon name={scopedBusy ? 'refresh' : 'search'} size={15} />{scopedBusy ? 'Previewing...' : 'Preview Scoped POS'}
            </button>
            <button className="btn btn-primary" disabled={scopedSyncBusy || scopedBusy || !configured || !scopedIncludedCount || !scopedImportOrderNames.length} onClick={syncScopedShopify} style={{ opacity: scopedSyncBusy || scopedBusy || !configured || !scopedIncludedCount || !scopedImportOrderNames.length ? 0.65 : 1 }}>
              <Icon name={scopedSyncBusy ? 'refresh' : 'download'} size={15} />{scopedSyncBusy && scopedImportProgress ? `Importing ${scopedImportProgress.done}/${scopedImportProgress.total}` : scopedSyncBusy ? 'Importing...' : `Import ${scopedIncludedCount || ''} Orders`.trim()}
            </button>
          </div>
          {scopedPreview && scopedIncludedCount > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 650 }}>
              Imports run in {scopedImportBatchCount || 1} batch{(scopedImportBatchCount || 1) === 1 ? '' : 'es'} of up to {SCOPED_SHOPIFY_IMPORT_CHUNK_SIZE} orders to stay under API limits.
              {scopedImportProgress ? ` Batch ${scopedImportProgress.batch}/${scopedImportProgress.batches}, ${scopedImportProgress.done}/${scopedImportProgress.total} orders imported.` : ''}
            </div>
          )}
          {scopedPreview && (
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 9, padding: '12px 13px', display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <div className="eyebrow" style={{ marginBottom: 4 }}>Scoped Preview</div>
                  <div style={{ fontWeight: 750 }}>{scopedIncludedCount} orders included · {scopedSkippedCount} skipped</div>
                </div>
                <Pill status={scopedIncludedCount ? 'Active' : 'Pending'}>{scopedIncludedCount ? 'Ready' : 'No matches'}</Pill>
              </div>
              <div style={{ display: 'grid', gap: 8, maxHeight: 260, overflow: 'auto' }}>
                {scopedIncludedOrders.slice(0, 20).map(order => (
                  <div key={order.id || order.name} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '9px 10px', background: 'var(--surface)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontWeight: 750 }}>{order.name || order.id}</span>
                      <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{String(order.createdAt || '').slice(0, 10)}</span>
                    </div>
                    <div style={{ marginTop: 5, fontSize: 12.5, color: 'var(--muted)' }}>
                      {(order.lines || []).map(line => `${line.title} · ${line.quantity} ${line.unit}`).join(' / ') || 'No saleable lines'}
                    </div>
                    {Array.isArray(order.droppedLines) && order.droppedLines.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--amber-deep)' }}>
                        Dropped: {order.droppedLines.map(line => line.title || line.sku).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {preview && (
          <div style={{ marginTop: 16, background: 'var(--info-bg)', border: '1px solid rgba(44, 127, 181, 0.22)', borderRadius: 9, padding: '12px 13px', display: 'grid', gap: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div className="eyebrow" style={{ marginBottom: 4 }}>Preview Pull</div>
                <div style={{ fontWeight: 700 }}>{preview.shop || 'Shopify Admin API'}</div>
              </div>
              <Pill status={preview.warnings.length ? 'Pending' : 'Active'}>{preview.warnings.length ? 'Warnings' : 'Ready'}</Pill>
            </div>
            <Row k="Counts" v={<span style={{ fontSize: 12.5 }}>{shopifySyncSummary({ summary: preview.summary })}</span>} />
            {preview.pulledAt && <Row k="Pulled" v={rpFormatAccountTime(preview.pulledAt)} />}
            {preview.warnings.length > 0 && (
              <div style={{ display: 'grid', gap: 7 }}>
                {preview.warnings.slice(0, 3).map((warning, index) => (
                  <div key={index} style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700 }}>{warning}</div>
                ))}
              </div>
            )}
          </div>
        )}
        {latestSync && (
          <div style={{ marginTop: 16, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 9, padding: '12px 13px', display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div className="eyebrow" style={{ marginBottom: 4 }}>Last Shopify sync</div>
                <div style={{ fontWeight: 700 }}>{rpFormatAccountTime(latestSync.createdAt)}</div>
              </div>
              <Pill status={shopifySyncStatus(latestSync)}>{latestSync.status || 'unknown'}</Pill>
            </div>
            <Row k="Direction" v={latestSync.topic ? `${latestSync.direction} / ${latestSync.topic}` : latestSync.direction} />
            <Row k="Summary" v={<span style={{ fontSize: 12.5 }}>{shopifySyncSummary(latestSync)}</span>} />
            {latestSync.error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700 }}>{latestSync.error}</div>}
            {latestWarnings.length > 0 && (
              <div style={{ display: 'grid', gap: 7 }}>
                {latestWarnings.slice(0, 3).map((warning, index) => (
                  <div key={index} style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700 }}>{warning}</div>
                ))}
              </div>
            )}
          </div>
        )}
        {error && <div style={{ marginTop: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
        {result && (
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10 }}>
            {[
              ['Products', result.summary ? result.summary.products : '—'],
              ['Variants', result.summary ? result.summary.variants : '—'],
              ['Orders', result.summary ? result.summary.orders : '—'],
              ['B2B Orders', result.summary ? result.summary.b2bOrders : '—'],
              ['Companies', result.summary ? result.summary.companies : '--'],
            ].map(([k, v]) => (
              <div key={k} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 9, padding: '10px 12px' }}>
                <div className="eyebrow" style={{ marginBottom: 4 }}>{k}</div>
                <div className="disp" style={{ fontSize: 22 }}>{v}</div>
              </div>
            ))}
            {resultWarnings.length > 0 && (
              <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 7 }}>
                {resultWarnings.slice(0, 3).map((warning, index) => (
                  <div key={index} style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 700 }}>{warning}</div>
                ))}
              </div>
            )}
          </div>
        )}
        {syncRuns.length > 1 && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
            <SectionTitle right={<span className="eyebrow">{syncRuns.length} runs</span>}>Recent Sync Runs</SectionTitle>
            <div style={{ display: 'grid', gap: 8 }}>
              {syncRuns.slice(0, 6).map(run => (
                <div key={run.id} style={{ display: 'grid', gridTemplateColumns: '120px 74px 1fr', alignItems: 'center', gap: 10, fontSize: 12.5, border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px' }}>
                  <span className="mono" style={{ color: 'var(--muted)' }}>{rpFormatAccountTime(run.createdAt)}</span>
                  <Pill status={shopifySyncStatus(run)}>{run.status || 'unknown'}</Pill>
                  <span style={{ color: run.error ? 'var(--danger)' : 'var(--muted)', fontWeight: run.error ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shopifySyncSummary(run)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '18px 20px' }}>
        <SectionTitle right={<Pill status={configured ? 'Active' : 'Invited'}>{configured ? 'Protected' : 'Setup'}</Pill>}>Email Invites</SectionTitle>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.45, marginBottom: 14 }}>
          Resend is wired through a protected server endpoint. Customer portal credentials and team invites can be sent without exposing email API keys in the browser.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
          <Row k="Provider" v="Resend" />
          <Row k="Endpoint" v={<span className="mono">/api/email/invite</span>} />
          <Row k="Secret scope" v="Cloudflare Function only" />
        </div>
      </div>
    </div>
  );
}

function DeveloperDataSettings() {
  const [pending, setPending] = useState(false);
  const active = !!(window.RP_AUTH && window.RP_AUTH.developerData);
  const preferred = rpDeveloperDataPreference();
  const allowed = active || rpCanUseDeveloperData();
  const liveProfile = (window.RP_AUTH && window.RP_AUTH.liveProfile) || rpAuthProfile();
  const label = active ? 'Developer Data' : 'Live Data';
  const next = !active;

  const toggle = () => {
    if (!allowed) return;
    setPending(true);
    rpSwitchDeveloperData(next);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 0.9fr) minmax(320px, 1.1fr)', gap: 16 }}>
      <div className="card" style={{ padding: '18px 20px' }}>
        <SectionTitle right={<Pill status={active ? 'Invited' : 'Active'}>{label}</Pill>}>Data Mode</SectionTitle>
        <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          <Row k="Portal" v="Operations" />
          <Row k="Profile" v={liveProfile.display_name || liveProfile.email || 'Administrator'} />
          <Row k="Browser preference" v={preferred ? 'Developer data' : 'Live data'} />
          <Row k="Server writes" v={active ? 'Blocked' : 'Enabled'} />
          <Row k="Shopify sync" v={active ? 'Blocked' : 'Enabled'} />
        </div>
        <button className={active ? 'btn btn-navy' : 'btn btn-primary'} disabled={!allowed || pending} onClick={toggle} style={{ width: '100%', marginTop: 18, opacity: !allowed || pending ? 0.65 : 1 }}>
          <Icon name={pending ? 'refresh' : active ? 'arrowL' : 'layers'} size={15} />{pending ? 'Switching...' : active ? 'Return to Live Data' : 'Use Developer Data'}
        </button>
        {!allowed && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>Administrator access required.</div>}
      </div>

      <div className="card" style={{ padding: '18px 20px' }}>
        <SectionTitle right={<Pill status={active ? 'Invited' : 'Active'}>{active ? 'Local' : 'Supabase'}</Pill>}>Current Scope</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 14 }}>
          {[
            ['Customers', active ? 'Seed records' : `${RP.CUSTOMERS.length} live records`],
            ['Orders', active ? 'Seed records' : `${RP.ORDERS.length} operational rows loaded — full ledger is paged`],
            ['Inventory', active ? 'Seed ledger' : `${RP.INV.length} live balances`],
            ['Tasks', active ? 'Seed tasks' : `${RP.TASKS.length} live tasks`],
          ].map(([k, v]) => (
            <div key={k} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8, padding: '11px 12px' }}>
              <div className="eyebrow" style={{ marginBottom: 5 }}>{k}</div>
              <div style={{ fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductShopifyPushCell({ sku, onSynced }) {
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const live = rpHasLiveAuth();
  const profile = rpAuthProfile();
  const canPush = ['admin', 'leadership', 'ops'].includes(rpNormalizeRole(profile.role));
  const linked = !!(sku.shopifyVariantId || sku.shopifyProductId || (preview && preview.sku && preview.sku.shopifyVariantId));

  const previewSku = async () => {
    setBusy('preview');
    try {
      const response = await rpApi('/api/shopify/push-sku', {
        method: 'POST',
        body: JSON.stringify({ skuId: sku.id, dryRun: true })
      });
      const nextPreview = response.preview || null;
      setPreview(nextPreview);
      const mode = nextPreview && nextPreview.mode ? nextPreview.mode : 'push';
      const price = nextPreview && nextPreview.sku ? RP.money2(nextPreview.sku.price) : RP.money2(sku.price);
      const warningText = nextPreview && nextPreview.warnings && nextPreview.warnings.length ? ` - ${nextPreview.warnings[0]}` : '';
      toast(`Shopify ${mode}: ${rpSkuCode(sku)} at ${price}${warningText}`, warningText ? 'warn' : 'ok');
    } catch (error) {
      toast(error && error.message ? error.message : 'Could not preview Shopify SKU push.', 'danger');
    } finally {
      setBusy('');
    }
  };

  const pushSku = async () => {
    setBusy('push');
    try {
      const response = await rpApi('/api/shopify/push-sku', {
        method: 'POST',
        body: JSON.stringify({ skuId: sku.id, dryRun: false })
      });
      if (response.sku) {
        sku.shopifyProductId = response.sku.shopifyProductId || sku.shopifyProductId;
        sku.shopifyVariantId = response.sku.shopifyVariantId || sku.shopifyVariantId;
      }
      await rpRefreshAfterMutation(response.message || 'Shopify SKU pushed');
      setPreview(response.preview || preview);
      onSynced && onSynced();
      toast(response.message || 'SKU pushed to Shopify', 'ok');
    } catch (error) {
      toast(error && error.message ? error.message : 'Could not push SKU to Shopify.', 'danger');
    } finally {
      setBusy('');
    }
  };

  if (!live) return <span style={{ color: 'var(--faint)', fontSize: 12.5 }}>Demo only</span>;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      <Pill status={linked ? 'Active' : 'Invited'}>{linked ? 'Linked' : 'New'}</Pill>
      <button className="btn btn-ghost btn-sm" disabled={!canPush || !!busy} onClick={previewSku} title="Preview Shopify product push" style={{ opacity: !canPush || busy ? 0.65 : 1 }}>
        <Icon name={busy === 'preview' ? 'refresh' : 'search'} size={14} />Preview
      </button>
      <button className="btn btn-primary btn-sm" disabled={!canPush || !!busy} onClick={pushSku} title="Push this SKU to Shopify" style={{ opacity: !canPush || busy ? 0.65 : 1 }}>
        <Icon name={busy === 'push' ? 'refresh' : 'package'} size={14} />{busy === 'push' ? 'Pushing' : 'Push'}
      </button>
    </div>
  );
}

function ProductCustomerVisibilityCell({ sku, onSaved }) {
  const [busy, setBusy] = useState(false);
  const live = rpHasLiveAuth();
  const shown = rpSkuCustomerVisible(sku);
  const canSave = ['admin', 'leadership', 'ops'].includes(rpNormalizeRole(rpAuthProfile().role));
  const save = async () => {
    if (busy || !canSave) return;
    const nextVisible = !shown;
    setBusy(true);
    try {
      if (live) {
        const response = await rpApi('/api/skus/upsert', {
          method: 'POST',
          body: JSON.stringify({
            active: sku.active !== false,
            customerVisible: nextVisible,
            deposit: sku.deposit || 0,
            flavor: sku.flavor || null,
            id: sku.id,
            name: sku.name,
            pack: sku.pack,
            price: sku.price,
            recyclingFee: sku.recyclingFee || 0,
            sku: sku.code || sku.id,
            visualColor: sku.visualColor || null,
            visualLabel: sku.visualLabel || null
          })
        });
        Object.assign(sku, response.sku || { customerVisible: nextVisible });
        await rpRefreshAfterMutation(nextVisible ? 'Product shown in customer shop' : 'Product hidden from customer shop');
      } else {
        sku.customerVisible = nextVisible;
      }
      onSaved && onSaved();
      toast(`${rpSkuDisplayName(sku)} ${nextVisible ? 'shown in' : 'hidden from'} customer shop`, 'ok');
    } catch (error) {
      toast(error && error.message ? error.message : 'Could not update customer shop visibility.', 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button className="btn btn-ghost btn-sm" disabled={!canSave || busy} onClick={save} title={shown ? 'Hide from customer ordering' : 'Show on customer ordering page'} style={{ minWidth: 86, justifyContent: 'center', color: shown ? 'var(--ok)' : 'var(--muted)', opacity: !canSave || busy ? 0.65 : 1 }}>
      <Icon name={busy ? 'refresh' : shown ? 'check' : 'x'} size={14} />{shown ? 'Shown' : 'Hidden'}
    </button>
  );
}

const ADMIN_TERRITORY_EXCLUSIONS = new Set(['', 'all', 'fba', 'transfer', 'unassigned']);

function adminTerritoryId(value) {
  return String(value || 'TERRITORY')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64) || 'TERRITORY';
}

function adminAddTerritoryRecord(map, row, unmanaged) {
  const names = rpAreaNames(row && row.name || row);
  names.forEach(name => {
    if (!name || ADMIN_TERRITORY_EXCLUSIONS.has(name.toLowerCase())) return;
    const key = name.toLowerCase();
    const record = {
      active: unmanaged ? false : row.active !== false,
      centerLat: row.centerLat == null ? null : Number(row.centerLat),
      centerLng: row.centerLng == null ? null : Number(row.centerLng),
      id: row.id || adminTerritoryId(name),
      locatorGroupToleranceMeters: Number(row.locatorGroupToleranceMeters || 35),
      name,
      sortOrder: Number(row.sortOrder || row.sort_order || 0),
      unmanaged: !!unmanaged
    };
    if (!map.has(key) || (!unmanaged && map.get(key).unmanaged)) map.set(key, record);
  });
}

function adminTerritoryRows() {
  const rows = new Map();
  (Array.isArray(RP.TERRITORY_RECORDS) ? RP.TERRITORY_RECORDS : []).forEach(row => adminAddTerritoryRecord(rows, row, false));
  (Array.isArray(RP.TERRITORIES) ? RP.TERRITORIES : []).forEach(name => adminAddTerritoryRecord(rows, { name, active: true }, false));
  ['CUSTOMERS', 'LOCATIONS', 'LEADS', 'ORDERS', 'USER_PROFILES'].forEach(key => {
    (Array.isArray(RP[key]) ? RP[key] : []).forEach(row => adminAddTerritoryRecord(rows, row && row.territory, true));
  });
  return Array.from(rows.values()).sort((a, b) => (Number(a.sortOrder || 0) - Number(b.sortOrder || 0)) || String(a.name || '').localeCompare(String(b.name || '')));
}

function adminTerritoryUsage(name) {
  const match = value => rpAreaMatches(value, name);
  const count = key => (Array.isArray(RP[key]) ? RP[key] : []).filter(row => row && match(row.territory)).length;
  return {
    customers: count('CUSTOMERS'),
    leads: count('LEADS'),
    locations: count('LOCATIONS'),
    orders: count('ORDERS'),
    staff: (Array.isArray(RP.USER_PROFILES) ? RP.USER_PROFILES : []).filter(row => row && match(row.territory)).length
  };
}

function adminTerritoryCenterLabel(row) {
  if (!row || row.centerLat == null || row.centerLng == null) return 'Not set';
  return `${Number(row.centerLat).toFixed(5)}, ${Number(row.centerLng).toFixed(5)}`;
}

function adminTerritoryOptions(current) {
  const base = (Array.isArray(RP.TERRITORIES) ? RP.TERRITORIES : []).filter(Boolean);
  const missing = rpAreaNames(current).filter(value => value && !base.some(name => String(name || '').trim().toLowerCase() === value.toLowerCase()));
  return [...missing, ...base];
}

function AdminAreaPicker({ value, onChange, options }) {
  const selected = rpAreaNames(value);
  const has = name => selected.some(area => String(area || '').trim().toLowerCase() === String(name || '').trim().toLowerCase());
  const emit = next => onChange(next.filter(Boolean));
  const toggle = name => {
    if (has(name)) emit(selected.filter(area => String(area || '').trim().toLowerCase() !== String(name || '').trim().toLowerCase()));
    else emit([...selected, name]);
  };
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 9, padding: 8, minHeight: 52, background: 'var(--surface)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {options.map(name => {
          const active = has(name);
          return (
            <button key={name} type="button" onClick={() => toggle(name)} style={{ border: '1px solid ' + (active ? 'var(--amber)' : 'var(--line)'), background: active ? 'var(--amber-soft)' : 'var(--surface-2)', color: active ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 999, padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700 }}>
              {active && <Icon name="check" size={13} />}{name}
            </button>
          );
        })}
        {selected.length > 0 && (
          <button type="button" onClick={() => emit([])} style={{ border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--muted)', borderRadius: 999, padding: '6px 10px', fontSize: 12.5, fontWeight: 700 }}>
            Clear
          </button>
        )}
      </div>
      {!selected.length && <div style={{ color: 'var(--faint)', fontSize: 12.5, padding: '8px 3px 2px' }}>All / unassigned</div>}
    </div>
  );
}

function AdminView({ go, detail }) {
  const routeTab=detail?.type==='admin-tab'&&['users','audit','locations','territories','products','pricing','rules','public','settings','integrations','perms'].includes(detail.view)?detail.view:'users';
  const [tab,setLocalTab]=useState(routeTab);
  const setTab=value=>{setLocalTab(value);if(go)go('admin',{type:'admin-tab',view:value});};
  useEffect(()=>setLocalTab(routeTab),[routeTab]);
  const [selUser, setSelUser] = useState(null);
  const [selLocation, setSelLocation] = useState(null);
  const [selTerritory, setSelTerritory] = useState(null);
  const [selPriceList, setSelPriceList] = useState(null);
  const [selProduct, setSelProduct] = useState(null);
  const [selApprovalRule, setSelApprovalRule] = useState(null);
  const [modal, setModal] = useState(null); // invite | location | territory | product | pricelist | rule
  const [refreshTick, setRefreshTick] = useState(0);
  const bump = () => setRefreshTick(x => x + 1);
  const live = rpHasLiveAuth();
  const canInviteUsers = !live || rpCanInviteUsers();
  const localOnlyTabs = new Set([]);
  const localOnlyAdminTab = live && localOnlyTabs.has(tab);
  const tabs = [['users', 'Users'], ['audit', 'Audit Log'], ['locations', 'Locations'], ['territories', 'Territories'], ['products', 'Products'], ['pricing', 'Price Lists'], ['rules', 'Approval Rules'], ['public', 'Public Retailers'], ['settings', 'Settings'], ['integrations', 'Integrations'], ['perms', 'Permissions']];
  const rawAddAction = {
    users: ['Invite User', 'mail', () => setModal('invite')],
    locations: ['Add Location', 'plus', () => { setSelLocation(null); setModal('location'); }],
    territories: ['Add Territory', 'plus', () => { setSelTerritory(null); setModal('territory'); }],
    products: ['Add Product', 'plus', () => { setSelProduct(null); setModal('product'); }],
    pricing: ['Add Price List', 'plus', () => { setSelPriceList(null); setModal('pricelist'); }],
    rules: ['Add Rule', 'plus', () => { setSelApprovalRule(null); setModal('rule'); }],
  }[tab];
  const addAction = localOnlyAdminTab || (tab === 'users' && !canInviteUsers) ? null : rawAddAction;
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {selUser && (selUser.liveDiagnostic
        ? <UserDiagnosticModal user={selUser} onClose={() => setSelUser(null)} onUpdated={bump} />
        : <UserModal user={selUser} onClose={() => setSelUser(null)} onChange={bump} />)}
      {modal === 'invite' && canInviteUsers && <InviteUserModal onClose={() => setModal(null)} onAdded={bump} />}
      {modal === 'location' && <AddLocationModal location={selLocation} onClose={() => { setModal(null); setSelLocation(null); }} onAdded={bump} />}
      {modal === 'remove-location' && selLocation && <SafeLocationRemovalModal location={selLocation} onClose={() => { setModal(null); setSelLocation(null); }} onRemoved={bump} />}
      {modal === 'territory' && <AddTerritoryModal territory={selTerritory} onClose={() => { setModal(null); setSelTerritory(null); }} onAdded={bump} />}
      {modal === 'product' && <AddProductModal product={selProduct} onClose={() => { setModal(null); setSelProduct(null); }} onAdded={bump} />}
      {modal === 'pricelist' && <AddPriceListModal priceList={selPriceList} onClose={() => { setModal(null); setSelPriceList(null); }} onAdded={bump} />}
      {modal === 'rule' && <AddApprovalRuleModal approvalRule={selApprovalRule} onClose={() => { setModal(null); setSelApprovalRule(null); }} onAdded={bump} />}
      <PageHead eyebrow="Configuration" title="Admin"
        actions={addAction ? <button className="btn btn-sm btn-primary" onClick={addAction[2]}><Icon name={addAction[1]} size={15} />{addAction[0]}</button> : null} />
      <div className="rp-admin-tabs rp-scroll" style={{ marginBottom: 18, overflowX: 'auto', overflowY: 'hidden', paddingBottom: 4 }}>
        <Segmented options={tabs.map(([v, l]) => ({ value: v, label: l }))} value={tab} onChange={setTab} />
      </div>

      {localOnlyAdminTab && (
        <LiveDataGuardNotice title="This admin section is read-only in live mode.">
          The records shown here are useful for review, but create/edit actions for this section still need protected Supabase APIs before they can be treated as production writes.
        </LiveDataGuardNotice>
      )}

      {tab === 'users' && <UsersAdminPanel refreshKey={refreshTick} onSelect={setSelUser} />}

      {tab === 'audit' && <AuditLogView />}

      {tab === 'locations' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Location</th><th>Type</th><th>Territory</th><th></th><th className="num">Capacity</th><th>Utilization</th><th>Rep</th></tr></thead>
            <tbody>
              {RP.LOCATIONS.map(l => {
                const pct = l.cap ? Math.round((l.used || 0) / l.cap * 100) : null;
                return (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 600 }}>{l.name}<div className="mono" style={{ fontSize: 11, color: 'var(--faint)', fontWeight: 400 }}>{l.id}</div></td>
                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Icon name={l.type === 'FBA' ? 'fba' : l.type === 'Distributor' ? 'truck' : l.type === 'Micro-Warehouse' ? 'package' : 'inventory'} size={15} style={{ color: 'var(--muted)' }} />{l.type}</span></td>
                    <td style={{ color: 'var(--muted)' }}>{l.territory}</td>
                    <td className="num"><button className="btn btn-ghost btn-sm" onClick={() => { setSelLocation(l); setModal('location'); }}><Icon name="pen" size={14} />Edit</button><button className="btn btn-ghost btn-sm" onClick={() => { setSelLocation(l); setModal('remove-location'); }} style={{ color: 'var(--danger)' }}>Remove safely</button></td>
                    <td className="num">{l.cap ? l.cap.toLocaleString() + ' cs' : '—'}</td>
                    <td style={{ minWidth: 140 }}>{pct != null ? <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><div style={{ flex: 1, maxWidth: 90 }}><Bar value={l.used || 0} max={l.cap} color={pct > 85 ? 'var(--warn)' : 'var(--navy)'} /></div><span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{pct}%</span></div> : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                    <td style={{ color: 'var(--muted)' }}>{l.rep || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Warehouses, rep micro-warehouses, distributor DCs and FBA nodes.</span>
            {!localOnlyAdminTab && <button className="btn btn-sm btn-primary" onClick={() => { setSelLocation(null); setModal('location'); }}><Icon name="plus" size={14} />Add Location</button>}
          </div>
          <LocationRemovalHistory refreshKey={refreshTick} />
        </div>
      )}

      {tab === 'territories' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Territory</th><th>ID</th><th>Map Center</th><th className="num">Group</th><th className="num">Staff</th><th className="num">Customers</th><th className="num">Locations</th><th className="num">Leads</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {adminTerritoryRows().map(t => {
                const usage = adminTerritoryUsage(t.name);
                return (
                  <tr key={t.id || t.name} style={{ cursor: 'pointer' }} onClick={() => { setSelTerritory(t); setModal('territory'); }}>
                    <td style={{ fontWeight: 650 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}><Icon name="pin" size={15} style={{ color: 'var(--amber)' }} />{t.name}</span>
                      {t.unmanaged && <div style={{ color: 'var(--faint)', fontSize: 11.5, marginTop: 3, fontWeight: 500 }}>Used by live records but not yet configured</div>}
                    </td>
                    <td className="mono" style={{ color: 'var(--muted)' }}>{t.id}</td>
                    <td className="mono" style={{ color: t.centerLat == null || t.centerLng == null ? 'var(--faint)' : 'var(--muted)', fontSize: 11.5 }}>{adminTerritoryCenterLabel(t)}</td>
                    <td className="num mono" style={{ color: 'var(--muted)' }}>{Number(t.locatorGroupToleranceMeters || 35)}m</td>
                    <td className="num">{usage.staff}</td>
                    <td className="num">{usage.customers}</td>
                    <td className="num">{usage.locations}</td>
                    <td className="num">{usage.leads}</td>
                    <td><Pill status={t.unmanaged ? 'Invited' : t.active === false ? 'Disabled' : 'Active'}>{t.unmanaged ? 'Unmanaged' : t.active === false ? 'Inactive' : 'Active'}</Pill></td>
                    <td className="num"><button className="btn btn-ghost btn-sm" onClick={(event) => { event.stopPropagation(); setSelTerritory(t); setModal('territory'); }}><Icon name="pen" size={14} />Edit</button></td>
                  </tr>
                );
              })}
              {!adminTerritoryRows().length && (
                <tr><td colSpan="10" style={{ color: 'var(--muted)', textAlign: 'center', padding: 28 }}>No territories configured yet.</td></tr>
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Active territories populate assignment dropdowns for users, customers, leads and locations.</span>
            {!localOnlyAdminTab && <button className="btn btn-sm btn-primary" onClick={() => { setSelTerritory(null); setModal('territory'); }}><Icon name="plus" size={14} />Add Territory</button>}
          </div>
        </div>
      )}

      {tab === 'products' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Product</th><th>Visual</th><th>SKU</th><th>UPC</th><th>Pack</th><th>Customer Shop</th><th className="num">Default Price</th><th className="num">Container Fee</th><th className="num">Shopify</th><th></th></tr></thead>
            <tbody>
              {RP.SKUS.map(s => {
                const visualColor = rpSkuVisualColor(s);
                const visualLabel = rpSkuVisualLabel(s);
                const containerFee = Number(s.deposit || 0) + Number(s.recyclingFee || 0);
                return (
                  <tr key={s.id}>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FlavorChip sku={s} size={24} /><span style={{ fontWeight: 500 }}>{rpSkuDisplayName(s)}</span></div></td>
                    <td><span className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 11.5 }}><span style={{ width: 14, height: 14, borderRadius: 4, border: '1px solid var(--line)', background: visualColor, display: 'inline-block' }} />{visualLabel}</span></td>
                    <td className="mono" style={{ fontWeight: 600 }}>{rpSkuCode(s)}</td>
                    <td className="mono" style={{ color: 'var(--muted)' }}>{rpSkuUpc(s) || '—'}</td>
                    <td style={{ color: 'var(--muted)' }}>{s.pack}</td>
                    <td><ProductCustomerVisibilityCell sku={s} onSaved={bump} /></td>
                    <td className="num" style={{ fontWeight: 600 }}>{RP.money2(rpCustomerSkuPrice({ tier: rpDefaultPriceListName() }, s))}</td>
                    <td className="num" style={{ color: 'var(--muted)' }}>{RP.money2(containerFee)}</td>
                    <td className="num"><ProductShopifyPushCell sku={s} onSynced={bump} /></td>
                    <td className="num"><button className="btn btn-ghost btn-sm" onClick={() => { setSelProduct(s); setModal('product'); }}><Icon name="pen" size={14} />Edit</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Every SKU is a unit inventory, orders and production hang on.</span>
            {!localOnlyAdminTab && <button className="btn btn-sm btn-primary" onClick={() => { setSelProduct(null); setModal('product'); }}><Icon name="plus" size={14} />Add Product</button>}
          </div>
        </div>
      )}

      {tab === 'public' && <PublicRetailersView go={go} />}

      {tab === 'settings' && <DeveloperDataSettings />}

      {tab === 'integrations' && <IntegrationsView />}

      {tab === 'perms' && (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="rp-table">
            <thead><tr><th>Capability</th>{RP.PERM_ROLES.map(r => <th key={r} className="num" style={{ textAlign: 'center' }}>{r}</th>)}</tr></thead>
            <tbody>
              {RP.PERM_CAPS.map(c => (
                <tr key={c.cap}>
                  <td style={{ fontWeight: 500 }}>{c.cap}</td>
                  {c.vals.map((v, i) => (
                    <td key={i} style={{ textAlign: 'center' }}>
                      {v ? <span style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: 6, background: 'var(--ok-bg)', color: 'var(--ok)', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={14} /></span>
                        : <span style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: 6, background: 'var(--surface-2)', color: 'var(--faint)', alignItems: 'center', justifyContent: 'center' }}><Icon name="minus" size={12} /></span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'pricing' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Price List</th><th className="num">12-Pack Price</th><th>Default Terms</th><th className="num">Assigned Doors</th><th></th></tr></thead>
            <tbody>
              {RP.PRICE_LISTS.map(p => (
                <tr key={p.name} style={{ cursor: 'pointer' }} onClick={() => { setSelPriceList(p); setModal('pricelist'); }}>
                  <td style={{ fontWeight: 600 }}>{p.name}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{RP.money2(p.skuPrice)}</td>
                  <td style={{ color: 'var(--muted)' }}>{p.terms}</td>
                  <td className="num">{p.doors}</td>
                  <td><button className="btn btn-ghost btn-sm" onClick={(event) => { event.stopPropagation(); setSelPriceList(p); setModal('pricelist'); }}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            {!localOnlyAdminTab && <button className="btn btn-sm btn-primary" onClick={() => { setSelPriceList(null); setModal('pricelist'); }}><Icon name="plus" size={14} />Add Price List</button>}
          </div>
        </div>
      )}

      {tab === 'rules' && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>When</th><th>Routes To</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {RP.APPROVAL_RULES.map(r => (
                <tr key={r.id || r.rule} style={{ cursor: 'pointer' }} onClick={() => { setSelApprovalRule(r); setModal('rule'); }}>
                  <td style={{ fontWeight: 500 }}>{r.rule}</td>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Icon name="arrowR" size={14} style={{ color: 'var(--amber)' }} />{r.route}</span></td>
                  <td><Pill status={r.active === false ? 'Disabled' : 'Active'}>{r.active === false ? 'Disabled' : 'Active'}</Pill></td>
                  <td><button className="btn btn-ghost btn-sm" onClick={(event) => { event.stopPropagation(); setSelApprovalRule(r); setModal('rule'); }}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            {!localOnlyAdminTab && <button className="btn btn-sm btn-primary" onClick={() => { setSelApprovalRule(null); setModal('rule'); }}><Icon name="plus" size={14} />Add Rule</button>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- User detail / edit modal ---- */
function UsersAdminPanel({ refreshKey, onSelect }) {
  const live = rpHasLiveAuth();
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filters, onFilter] = useFilters();
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [localRefresh, setLocalRefresh] = useState(0);
  const canInviteUsers = rpCanInviteUsers();

  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    rpApi('/api/users/diagnostics')
      .then(data => { if (!cancelled) setDiagnostics(data); })
      .catch(err => { if (!cancelled) setError(err && err.message ? err.message : 'Could not load user diagnostics.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [live, refreshKey, localRefresh]);

  if (!live) {
    return (
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="rp-table">
          <thead><tr><th>User</th><th>Role</th><th>Area</th><th>Status</th><th>Last Active</th><th></th></tr></thead>
          <tbody>
            {RP.USERS.map(u => (
              <tr key={u.email} style={{ cursor: 'pointer' }} onClick={() => onSelect(u)}>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 11 }}><Avatar name={u.name} size={30} color="var(--navy)" /><div><div style={{ fontWeight: 600 }}>{u.name}</div><div className="mono" style={{ fontSize: 11, color: 'var(--faint)' }}>{u.email}</div></div></div></td>
                <td><span style={{ fontWeight: 500 }}>{u.role}</span></td>
                <td style={{ color: 'var(--muted)' }}>{u.area}</td>
                <td><Pill status={u.status === 'Suspended' ? 'On Hold' : 'Active'}>{u.status}</Pill></td>
                <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{u.last}</td>
                <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const summary = diagnostics && diagnostics.summary ? diagnostics.summary : { total: 0, active: 0, authOnly: 0, blocked: 0, needsReview: 0, signedIn: 0, invited: 0, setupComplete: 0, setupNeeded: 0 };
  const users = diagnostics && diagnostics.users ? diagnostics.users : [];
  const orphanedAuthUsers = diagnostics && Array.isArray(diagnostics.orphanedAuthUsers) ? diagnostics.orphanedAuthUsers : [];
  const accountTypeForUser = user => user.role === 'customer' ? (user.customer && user.customer.type || 'Customer') : 'Staff';
  const portalTypeForUser = user => {
    const portal = String(user.portalUrl || '').toLowerCase();
    if (portal.includes('order.')) return 'Order Portal';
    if (portal.includes('sales.')) return 'Sales App';
    if (portal.includes('operations.')) return 'Operations Hub';
    return user.portalUrl ? 'Other Portal' : 'No Portal';
  };
  const setupStatusForUser = user => user.setupComplete ? 'Complete' : 'Needed';
  const duplicateStatusForUser = user => user.duplicate && user.duplicate.isDuplicate ? 'Duplicate' : 'Unique';
  const searchText = search.trim().toLowerCase();
  const filterDefs = [
    { label: 'Account Type', options: distinct(users, accountTypeForUser) },
    { label: 'Role', options: distinct(users, u => u.roleLabel) },
    { label: 'Portal', options: distinct(users, portalTypeForUser) },
    { label: 'Setup', options: distinct(users, setupStatusForUser) },
    { label: 'Health', options: distinct(users, u => u.health) },
    { label: 'Duplicate', options: distinct(users, duplicateStatusForUser) }
  ];
  const filteredUsers = users.filter(user => {
    if (filters['Account Type'] && accountTypeForUser(user) !== filters['Account Type']) return false;
    if (filters.Role && user.roleLabel !== filters.Role) return false;
    if (filters.Portal && portalTypeForUser(user) !== filters.Portal) return false;
    if (filters.Setup && setupStatusForUser(user) !== filters.Setup) return false;
    if (filters.Health && user.health !== filters.Health) return false;
    if (filters.Duplicate && duplicateStatusForUser(user) !== filters.Duplicate) return false;
    if (!searchText) return true;
    const haystack = [
      user.name,
      user.email,
      user.roleLabel,
      user.area,
      user.portalUrl,
      user.authStatus,
      user.health,
      duplicateStatusForUser(user),
      user.customer && user.customer.name,
      user.customer && user.customer.type,
      user.customer && user.customer.tier,
      user.customer && user.customer.territory,
      ...(user.territories || [])
    ].map(value => String(value || '').toLowerCase()).join(' ');
    return haystack.includes(searchText);
  });
  const selectedIds = Object.entries(selected).filter(([, on]) => on).map(([id]) => id).filter(id => users.some(u => u.id === id));
  const visibleSetupNeeded = filteredUsers.filter(u => u.active !== false && !u.setupComplete && u.authStatus !== 'Missing Auth User');
  const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every(u => selected[u.id]);
  const toggleUser = (id, on) => setSelected(s => ({ ...s, [id]: on }));
  const toggleVisible = on => setSelected(s => {
    const next = { ...s };
    filteredUsers.forEach(u => { next[u.id] = on; });
    return next;
  });
  const selectSetupNeeded = () => setSelected(Object.fromEntries(visibleSetupNeeded.map(u => [u.id, true])));
  const clearSelected = () => setSelected({});
  const sendBulkSetup = async () => {
    if (!canInviteUsers) {
      const msg = 'Only darren@ranchpower.com can send setup links.';
      setBulkError(msg);
      toast(msg, 'warn');
      return;
    }
    if (!selectedIds.length || bulkBusy) return;
    setBulkBusy(true);
    setBulkError('');
    setBulkMessage('');
    try {
      const response = await rpApi('/api/users/bulk-invite', {
        method: 'POST',
        body: JSON.stringify({ ids: selectedIds, includeSignedIn: true })
      });
      const s = response.summary || {};
      const failed = (response.results || []).filter(r => r.status === 'failed');
      setBulkMessage(`Setup links sent: ${s.sent || 0} sent, ${s.skipped || 0} skipped, ${s.failed || 0} failed.`);
      if (failed.length) setBulkError(failed.slice(0, 3).map(r => `${r.email || r.id}: ${r.error}`).join('  '));
      toast(`Setup links: ${s.sent || 0} sent`, (s.failed || 0) ? 'warn' : 'ok');
      clearSelected();
      setLocalRefresh(x => x + 1);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not send setup links.';
      setBulkError(msg);
      toast(msg, 'danger');
    } finally {
      setBulkBusy(false);
    }
  };
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: 12, marginBottom: 14 }}>
        <ProfileHealthCard label="Profiles" value={summary.total} tone="var(--navy)" />
        <ProfileHealthCard label="Active" value={summary.active} tone="var(--ok)" />
        <ProfileHealthCard label="Signed In" value={summary.signedIn} tone="var(--info)" />
        <ProfileHealthCard label="Invited" value={summary.invited} tone="var(--amber)" />
        <ProfileHealthCard label="Setup Done" value={summary.setupComplete} tone="var(--ok)" />
        <ProfileHealthCard label="Setup Needed" value={summary.setupNeeded} tone="var(--warn)" />
        <ProfileHealthCard label="Auth Only" value={summary.authOnly} tone="var(--danger)" />
        <ProfileHealthCard label="Needs Review" value={summary.needsReview} tone="var(--warn)" />
        <ProfileHealthCard label="Blocked" value={summary.blocked} tone="var(--danger)" />
      </div>
      {diagnostics && diagnostics.authStatusAvailable === false && (
        <div style={{ marginBottom: 14, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>
          Supabase profile rows loaded, but Auth user status was unavailable: {diagnostics.authStatusError || 'Unknown auth status error.'}
        </div>
      )}
      {error && <div style={{ marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      {orphanedAuthUsers.length > 0 && (
        <div style={{ marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>
          {orphanedAuthUsers.length} Supabase Auth {orphanedAuthUsers.length === 1 ? 'user has' : 'users have'} no Ranch Power profile and will be blocked by invite-only auth.
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {orphanedAuthUsers.slice(0, 6).map(user => <span key={user.id || user.email} className="mono" style={{ background: '#fff', border: '1px solid var(--danger)', borderRadius: 100, padding: '4px 8px', fontSize: 11.5 }}>{user.email || user.id}</span>)}
          </div>
        </div>
      )}
      <div className="card" style={{ padding: '12px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13.5 }}>Bulk setup links</div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 3 }}>{canInviteUsers ? (selectedIds.length ? `${selectedIds.length} selected` : `${visibleSetupNeeded.length} visible users need account setup`) : 'Only darren@ranchpower.com can send user setup links.'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-sm" disabled={!canInviteUsers || bulkBusy || !visibleSetupNeeded.length} onClick={selectSetupNeeded}><Icon name="filter" size={14} />Select setup needed</button>
          <button className="btn btn-sm" disabled={bulkBusy || !selectedIds.length} onClick={clearSelected}><Icon name="x" size={14} />Clear</button>
          <button className="btn btn-sm btn-primary" disabled={!canInviteUsers || bulkBusy || !selectedIds.length} style={{ opacity: !canInviteUsers || bulkBusy || !selectedIds.length ? 0.65 : 1 }} onClick={sendBulkSetup}><Icon name="mail" size={14} />{bulkBusy ? 'Sending...' : 'Send setup links'}</button>
        </div>
      </div>
      {bulkMessage && <div style={{ marginBottom: 14, background: 'var(--ok-bg)', color: 'var(--ok)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{bulkMessage}</div>}
      {bulkError && <div style={{ marginBottom: 14, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{bulkError}</div>}
      <FilterBar defs={filterDefs} value={filters} onChange={onFilter} search={search} onSearch={setSearch} searchPlaceholder="Search users, email, account, territory..." />
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 18px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--muted)' }}>
          <span><Icon name="admin" size={15} style={{ verticalAlign: -3, marginRight: 7 }} />Live Supabase profiles, customer links, and portal readiness. <span className="mono" style={{ marginLeft: 8, color: 'var(--faint)' }}>{filteredUsers.length} / {users.length}</span></span>
          <span className="mono">{diagnostics && diagnostics.generatedAt ? rpFormatLocalDateTime(diagnostics.generatedAt, { empty: '' }) : loading ? 'Loading...' : 'Not loaded'}</span>
        </div>
        <table className="rp-table">
          <thead><tr><th style={{ width: 34 }}><input type="checkbox" checked={allVisibleSelected} disabled={!canInviteUsers || !filteredUsers.length || bulkBusy} onChange={e => toggleVisible(e.target.checked)} /></th><th>User</th><th>Role</th><th>Area / Customer</th><th>Portal</th><th>Auth</th><th>Setup</th><th>Health</th><th>Last Sign-in</th><th></th></tr></thead>
          <tbody>
            {loading && !users.length && <tr><td colSpan="10" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>Loading live user profiles...</td></tr>}
            {!loading && !users.length && <tr><td colSpan="10" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No live user profiles found.</td></tr>}
            {!loading && users.length > 0 && filteredUsers.length === 0 && <tr><td colSpan="10" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No user profiles match these filters.</td></tr>}
            {filteredUsers.map(u => (
              <tr key={u.id || u.email} style={{ cursor: 'pointer' }} onClick={() => onSelect({ ...u, customerOptions: diagnostics.customers || [], liveDiagnostic: true })}>
                <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={!!selected[u.id]} disabled={!canInviteUsers || bulkBusy} onChange={e => toggleUser(u.id, e.target.checked)} /></td>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 11 }}><Avatar name={u.name} size={30} color={u.health === 'Blocked' ? 'var(--danger)' : 'var(--navy)'} /><div><div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}><span style={{ fontWeight: 600 }}>{u.name}</span>{u.duplicate && u.duplicate.isDuplicate ? <Pill status="Pending">Duplicate</Pill> : null}</div><div className="mono" style={{ fontSize: 11, color: 'var(--faint)' }}>{u.email}</div></div></div></td>
                <td><span style={{ fontWeight: 500 }}>{u.roleLabel}</span></td>
                <td style={{ color: 'var(--muted)' }}><div>{u.area}</div>{u.customer && (u.customer.type || u.customer.tier) && <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--faint)' }}>{[u.customer.type, u.customer.tier].filter(Boolean).join(' - ')}</div>}</td>
                <td><span className="mono" style={{ fontSize: 11.5, color: 'var(--muted)' }}>{String(u.portalUrl || '').replace('https://', '')}</span></td>
                <td><Pill status={u.authStatus === 'Signed In' || u.authStatus === 'Accepted' ? 'Active' : u.authStatus === 'Invited' ? 'Invited' : u.authStatus === 'Missing Auth User' ? 'Damaged' : 'Pending'}>{u.authStatus || 'Unknown'}</Pill></td>
                <td><Pill status={u.setupComplete ? 'Active' : 'Pending'}>{u.setupComplete ? 'Complete' : 'Needed'}</Pill></td>
                <td><Pill status={u.health}>{u.health}</Pill>{u.issues && u.issues.length ? <span className="mono" style={{ marginLeft: 8, color: 'var(--faint)', fontSize: 11 }}>{u.issues.length}</span> : null}</td>
                <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>{rpFormatAccountTime(u.lastSignInAt)}</td>
                <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function ProfileHealthCard({ label, value, tone }) {
  return <div className="card" style={{ padding: '14px 16px' }}><div className="eyebrow" style={{ marginBottom: 7 }}>{label}</div><div className="disp" style={{ color: tone, fontSize: 27 }}>{Number(value || 0).toLocaleString()}</div></div>;
}

function AuditLogView() {
  const live = rpHasLiveAuth();
  const [kind, setKind] = useState('security');
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const kinds = [
    { value: 'security', label: 'Security' },
    { value: 'users', label: 'Users' },
    { value: 'orders', label: 'Orders' },
    { value: 'inventory', label: 'Inventory' },
    { value: 'shopify', label: 'Shopify' },
    { value: 'all', label: 'All' }
  ];
  const load = () => {
    if (!live) return;
    setLoading(true);
    setError('');
    rpApi(`/api/audit/logs?kind=${encodeURIComponent(kind)}&limit=100`)
      .then(data => setLogs(data))
      .catch(err => setError(err && err.message ? err.message : 'Could not load audit logs.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [kind, live]);

  if (!live) {
    return <div className="card" style={{ padding: 24, color: 'var(--muted)', lineHeight: 1.45 }}>Audit logs are available in live mode after Supabase service-role access is configured. Demo mode keeps writes in the local seed only.</div>;
  }

  const rows = logs && logs.rows ? logs.rows : [];
  return (
    <div>
      {selected && <AuditLogModal event={selected} onClose={() => setSelected(null)} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <Segmented options={kinds} value={kind} onChange={setKind} />
        <button className="btn btn-sm" onClick={load} disabled={loading}><Icon name="refresh" size={15} />{loading ? 'Refreshing...' : 'Refresh'}</button>
      </div>
      {error && <div style={{ marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 18px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--muted)' }}>
          <span><Icon name="receipt" size={15} style={{ verticalAlign: -3, marginRight: 7 }} />Server audit events from Supabase.</span>
          <span className="mono">{logs && logs.generatedAt ? rpFormatLocalDateTime(logs.generatedAt, { empty: '' }) : loading ? 'Loading...' : 'Not loaded'}</span>
        </div>
        <table className="rp-table">
          <thead><tr><th>Time</th><th>Action</th><th>Actor</th><th>Entity</th><th>IP</th><th></th></tr></thead>
          <tbody>
            {loading && !rows.length && <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>Loading audit log...</td></tr>}
            {!loading && !rows.length && <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No audit events found for this filter.</td></tr>}
            {rows.map(event => (
              <tr key={event.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(event)}>
                <td><div className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{event.createdAt ? rpFormatLocalDateTime(event.createdAt, { empty: '' }) : '-'}</div></td>
                <td><div style={{ fontWeight: 700 }}>{event.action}</div><div style={{ color: 'var(--faint)', fontSize: 11.5 }}>{event.entityType}</div></td>
                <td><div style={{ fontWeight: 600 }}>{event.actorEmail}</div><div className="mono" style={{ color: 'var(--faint)', fontSize: 11 }}>{event.actorId ? event.actorId.slice(0, 8) : 'system'}</div></td>
                <td><span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{event.entityId || '-'}</span></td>
                <td><span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{event.ip || '-'}</span></td>
                <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditLogModal({ event, onClose }) {
  const pretty = value => value ? JSON.stringify(value, null, 2) : 'null';
  const copy = () => {
    try { navigator.clipboard && navigator.clipboard.writeText(JSON.stringify(event, null, 2)); } catch (e) { }
    toast('Audit event copied', 'info');
  };
  return (
    <Modal title={event.action} sub={`${event.actorEmail} · ${event.createdAt ? String(event.createdAt).replace('T', ' ').slice(0, 19) : ''}`} onClose={onClose} width={760}
      footer={<><button className="btn" onClick={copy}><Icon name="doc" size={15} />Copy Event</button><button className="btn btn-primary" onClick={onClose}>Done</button></>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '11px 13px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Entity</div><div className="mono" style={{ fontSize: 12 }}>{event.entityType} · {event.entityId || '-'}</div></div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '11px 13px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>IP</div><div className="mono" style={{ fontSize: 12 }}>{event.ip || '-'}</div></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <AuditJsonBlock label="Before" value={pretty(event.before)} />
        <AuditJsonBlock label="After" value={pretty(event.after)} />
      </div>
    </Modal>
  );
}

function AuditJsonBlock({ label, value }) {
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 8 }}>{label}</div>
      <pre style={{ margin: 0, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: 12, color: 'var(--ink-soft)', fontSize: 11.5, lineHeight: 1.45, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{value}</pre>
    </div>
  );
}

function UserModal({ user, onClose, onChange }) {
  const [role, setRole] = useState(() => RP.PERM_ROLES.find(label => rpNormalizeRole(label) === rpNormalizeRole(user.role)) || user.role);
  const [area, setArea] = useState(user.area);
  const [status, setStatus] = useState(user.status || 'Active');
  const territoryOptions = adminTerritoryOptions(area);
  const roleCol = RP.PERM_ROLES.indexOf(role);
  const caps = roleCol >= 0 ? RP.PERM_CAPS.filter(c => c.vals[roleCol]) : [];
  const setAreas = areas => setArea((areas || []).join(' | '));

  const save = () => { user.role = role; user.area = area; user.status = status; toast(`${user.name} updated`); onChange(); onClose(); };
  const toggleStatus = () => setStatus(s => s === 'Active' ? 'Suspended' : 'Active');

  return (
    <Modal title={user.name} sub={user.email} onClose={onClose} width={560}
      footer={<>
        <button className="btn" onClick={toggleStatus} style={{ color: status === 'Active' ? 'var(--danger)' : 'var(--ok)', marginRight: 'auto' }}><Icon name={status === 'Active' ? 'x' : 'check'} size={15} />{status === 'Active' ? 'Suspend' : 'Reactivate'}</button>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}><Icon name="check" size={15} />Save Changes</button>
      </>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <Avatar name={user.name} size={50} color="var(--navy)" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{user.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}><Pill status={status === 'Active' ? 'Active' : 'On Hold'}>{status}</Pill><span style={{ fontSize: 12, color: 'var(--faint)' }}>Last active {user.last}</span></div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Role" half><select className="field" value={role} onChange={e => setRole(e.target.value)}>{RP.PERM_ROLES.map(r => <option key={r}>{r}</option>)}</select></MField>
        <MField label="Responsibility Areas" half>
          <AdminAreaPicker value={area} onChange={setAreas} options={territoryOptions} />
        </MField>
      </div>
      <div style={{ marginTop: 6 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Effective Permissions · {role}</div>
        {caps.length === 0 ? <div style={{ fontSize: 13, color: 'var(--faint)' }}>No elevated permissions.</div> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {caps.map(c => (
              <span key={c.cap} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 500, color: 'var(--ink-soft)', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 100, padding: '6px 12px' }}>
                <Icon name="check" size={13} style={{ color: 'var(--ok)' }} />{c.cap}
              </span>
            ))}
          </div>
        )}
        <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 10 }}>Permissions are derived from the role — change the matrix under the Permissions tab.</div>
      </div>
    </Modal>
  );
}

function UserDiagnosticModal({ user, onClose, onUpdated }) {
  const actorProfile = rpAuthProfile() || {};
  const actorRole = rpNormalizeRole(actorProfile.role);
  const canInviteUsers = rpCanInviteUsers();
  const roleGrants = {
    admin: ['admin', 'leadership', 'ops', 'sales_manager', 'rep', 'warehouse', 'dispatch', 'accountant', 'marketing', 'partner', 'customer'],
    leadership: ['leadership', 'ops', 'sales_manager', 'rep', 'warehouse', 'dispatch', 'accountant', 'marketing', 'partner', 'customer'],
    ops: ['ops', 'rep', 'warehouse', 'dispatch', 'partner', 'customer'],
    sales_manager: ['rep', 'partner', 'customer']
  };
  const roleOptions = Array.from(new Set([user.role || 'rep', ...(roleGrants[actorRole] || ['rep', 'partner', 'customer'])]));
  const [displayName, setDisplayName] = useState(user.name || '');
  const [role, setRole] = useState(user.role || 'rep');
  const [territory, setTerritory] = useState(user.territory || '');
  const [areas, setAreas] = useState(rpAreaNames(user.territory || ''));
  const [customerId, setCustomerId] = useState(user.customerId || '');
  const [featureFlags, setFeatureFlags] = useState(rpProfileFeatureFlags(user));
  const [retailQuoteDefaultPriceListName, setRetailQuoteDefaultPriceListName] = useState(rpRetailAccountNewPriceListName(user.retailQuoteDefaultPriceListName));
  const [retailQuoteCustomPricingEnabled, setRetailQuoteCustomPricingEnabled] = useState(user.retailQuoteCustomPricingEnabled === true);
  const [active, setActive] = useState(user.active !== false);
  const [busy, setBusy] = useState(false);
  const [setupBusy, setSetupBusy] = useState(false);
  const [tempBusy, setTempBusy] = useState(false);
  const [tempResult, setTempResult] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const customers = user.customerOptions || [];
  const isDuplicateProfile = Boolean(user.duplicate && user.duplicate.isDuplicate);
  const canDeleteDuplicate = actorRole === 'admin' && isDuplicateProfile && user.id !== actorProfile.id;
  const roleLabel = value => rpRoleLabel(value);
  const isCustomer = role === 'customer';
  const isFieldRole = ['rep', 'partner'].includes(role);
  const isRetailQuoteRole = ['admin', 'leadership', 'ops', 'sales_manager', 'rep', 'partner'].includes(role);
  const retailQuoteCustomPricingIncludedByRole = ['admin', 'leadership', 'ops', 'sales_manager'].includes(role);
  const retailQuotePriceModels = (() => {
    const byName = new Map((Array.isArray(RP.PRICE_LISTS) ? RP.PRICE_LISTS : [])
      .filter(model => model && model.name && ['default retail', 'slopeside retail'].includes(retailPriceListIdentity(model)))
      .map(model => [String(model.name).trim().toLowerCase(), {
        id: model.id, aliases: model.aliases, policyKey: model.policyKey,
        name: String(model.name).trim(),
        skuPrice: Number(model.skuPrice ?? model.sku_price ?? model.caseCost)
      }]));
    if (!findRetailPriceList(Array.from(byName.values()), 'Default Retail')) byName.set('default retail', { name: 'Default Retail', skuPrice: 36.50 });
    return Array.from(byName.values()).sort((left, right) => left.name.localeCompare(right.name));
  })();
  const dashboardWorkflowFeatureRoles = ['admin', 'leadership', 'ops', 'sales_manager', 'marketing', 'rep', 'partner'];
  const canEditDashboardWorkflowFlag = dashboardWorkflowFeatureRoles.includes(role);
  const salesAuthorityOptions = [
    { key: 'approveRetailCustomers', label: 'Approve retail customers', detail: 'Can approve assigned or territory retail accounts from the sales app.' },
    { key: 'sendRetailInvoices', label: 'Send retail invoices', detail: 'Can issue, email, and mark retail invoices paid for their accounts.' },
    { key: 'sendRetailPortalInvites', label: 'Send retail portal invites', detail: 'Can send order.ranchpower.com access to approved retail customers.' }
  ];
  const dashboardWorkflowOption = { key: 'dashboardWorkflowIntelligence', label: 'Dashboard workflow intelligence', detail: 'Can preview guided dashboard actions, expanded notifications, sales coaching, and territory health before general rollout.' };
  const territoryOptions = adminTerritoryOptions(isCustomer ? territory : areas);
  const toggleFeatureFlag = key => setFeatureFlags(flags => ({ ...flags, [key]: !flags[key] }));
  const setResponsibilityAreas = next => {
    const cleanAreas = (next || []).filter(Boolean);
    setAreas(cleanAreas);
    setTerritory(cleanAreas.join(' | '));
  };
  const copy = (text, label) => {
    try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (e) { }
    toast((label || 'Copied') + ' to clipboard', 'info');
  };
  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const savedFeatureFlags = isFieldRole
        ? featureFlags
        : canEditDashboardWorkflowFlag && featureFlags.dashboardWorkflowIntelligence === true
          ? { dashboardWorkflowIntelligence: true }
          : {};
      if (featureFlags.performance_read_efficiency === true) savedFeatureFlags.performance_read_efficiency = true;
      await rpApi('/api/users/update', {
        method: 'POST',
        body: JSON.stringify({
          active,
          customerId: isCustomer ? customerId : '',
          displayName,
          featureFlags: savedFeatureFlags,
          id: user.id,
          retailQuoteCustomPricingEnabled,
          retailQuoteDefaultPriceListName,
          role,
          territory: isCustomer ? territory : areas.join(' | ')
        })
      });
      setMessage('Profile updated.');
      toast('User profile updated', 'ok');
      onUpdated && onUpdated();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not update user profile.';
      setError(msg);
      toast(msg, 'danger');
    } finally {
      setBusy(false);
    }
  };
  const sendSetupLink = async () => {
    if (setupBusy) return;
    if (!canInviteUsers) {
      const msg = 'Only darren@ranchpower.com can send setup links.';
      setError(msg);
      toast(msg, 'warn');
      return;
    }
    setSetupBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await rpApi('/api/users/bulk-invite', {
        method: 'POST',
        body: JSON.stringify({ ids: [user.id], includeSignedIn: true })
      });
      const result = (response.results || [])[0] || {};
      if (result.status === 'sent') {
        setMessage(`Setup link sent to ${user.email}.`);
        toast('Setup link sent', 'ok');
      } else if (result.status === 'skipped') {
        const msg = result.reason || 'Setup link was skipped.';
        setMessage(msg);
        toast(msg, 'info');
      } else {
        const msg = result.error || 'Could not send setup link.';
        setError(msg);
        toast(msg, 'danger');
      }
      onUpdated && onUpdated();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not send setup link.';
      setError(msg);
      toast(msg, 'danger');
    } finally {
      setSetupBusy(false);
    }
  };
  const issueTempPassword = async () => {
    if (tempBusy) return;
    if (!canInviteUsers) {
      const msg = 'Only darren@ranchpower.com can issue temporary passwords.';
      setError(msg);
      toast(msg, 'warn');
      return;
    }
    setTempBusy(true);
    setTempResult(null);
    setError('');
    setMessage('');
    try {
      const response = await rpApi('/api/users/temp-password', {
        method: 'POST',
        body: JSON.stringify({ id: user.id })
      });
      setTempResult(response);
      setMessage(response.emailResult && response.emailResult.sent ? `Temporary password emailed to ${user.email}.` : 'Temporary password issued. Copy it before closing.');
      toast('Temporary password issued', 'ok');
      onUpdated && onUpdated();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not issue temporary password.';
      setError(msg);
      toast(msg, 'danger');
    } finally {
      setTempBusy(false);
    }
  };
  const copyTempPassword = () => {
    if (!tempResult || !tempResult.temporaryPassword) return;
    try { navigator.clipboard && navigator.clipboard.writeText(tempResult.temporaryPassword); } catch (e) { }
    toast('Temporary password copied', 'info');
  };
  const deleteDuplicate = async () => {
    if (!canDeleteDuplicate || deleteBusy) return;
    if (!deleteArmed) {
      setDeleteArmed(true);
      setError('');
      setMessage('Click Confirm Delete Duplicate to permanently remove this duplicate portal profile.');
      return;
    }
    setDeleteBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await rpApi('/api/users/delete', {
        method: 'POST',
        body: JSON.stringify({
          confirmSignedInDelete: true,
          id: user.id,
          reason: `Duplicate portal profile removed from Admin Users: ${user.name || user.email}`
        })
      });
      toast(response.deletedAuthUser ? 'Duplicate auth user and profile deleted' : 'Duplicate profile deleted', 'ok');
      onUpdated && onUpdated();
      onClose();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not delete duplicate profile.';
      setError(msg);
      toast(msg, 'danger');
    } finally {
      setDeleteBusy(false);
    }
  };
  return (
    <Modal title={user.name} sub={user.email} onClose={onClose} width={620}
      footer={<><button className="btn" onClick={() => copy(user.id, 'User id')}><Icon name="doc" size={15} />Copy ID</button><button className="btn" disabled={!canDeleteDuplicate || deleteBusy} style={{ color: 'var(--danger)', opacity: !canDeleteDuplicate || deleteBusy ? 0.55 : 1 }} title={canDeleteDuplicate ? 'Permanently remove this duplicate portal profile' : 'Only admins can delete profiles flagged as duplicates'} onClick={deleteDuplicate}><Icon name="trash" size={15} />{deleteBusy ? 'Deleting...' : deleteArmed ? 'Confirm Delete Duplicate' : 'Delete Duplicate'}</button><button className="btn" disabled={!canInviteUsers || setupBusy} style={{ opacity: !canInviteUsers || setupBusy ? 0.6 : 1 }} onClick={sendSetupLink}><Icon name="mail" size={15} />{setupBusy ? 'Sending...' : 'Send Setup Link'}</button><button className="btn" disabled={!canInviteUsers || tempBusy} style={{ opacity: !canInviteUsers || tempBusy ? 0.6 : 1 }} onClick={issueTempPassword}><Icon name="mail" size={15} />{tempBusy ? 'Issuing...' : 'Issue Temp Password'}</button><button className="btn" onClick={onClose}>Close</button><button className="btn btn-primary" disabled={busy || !displayName.trim() || (isCustomer && !customerId)} style={{ opacity: busy ? 0.65 : 1 }} onClick={save}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Save Repair'}</button></>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <Avatar name={user.name} size={50} color={user.health === 'Blocked' ? 'var(--danger)' : 'var(--navy)'} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{user.roleLabel}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, flexWrap: 'wrap' }}><Pill status={user.health}>{user.health}</Pill><span className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{String(user.portalUrl || '').replace('https://', '')}</span></div>
        </div>
      </div>
      {isDuplicateProfile && (
        <div style={{ marginBottom: 16, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.45 }}>
          This profile matches another profile by email or linked customer. Delete only the unused duplicate portal login; the customer record and order history stay intact.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5, marginBottom: 18 }}>
        <Row k="Profile id" v={<span className="mono">{user.id}</span>} />
        <Row k="Auth status" v={<Pill status={user.authStatus === 'Signed In' || user.authStatus === 'Accepted' ? 'Active' : user.authStatus === 'Invited' ? 'Invited' : user.authStatus === 'Missing Auth User' ? 'Damaged' : 'Pending'}>{user.authStatus || 'Unknown'}</Pill>} />
        <Row k="Setup status" v={<Pill status={user.setupComplete ? 'Active' : 'Pending'}>{user.setupComplete ? 'Complete' : 'Needed'}</Pill>} />
        <Row k="Setup completed" v={rpFormatAccountTime(user.profileCompletedAt)} />
        <Row k="Last sign-in" v={rpFormatAccountTime(user.lastSignInAt)} />
        <Row k="Invite sent" v={rpFormatAccountTime(user.invitedAt)} />
        <Row k="Email confirmed" v={rpFormatAccountTime(user.emailConfirmedAt)} />
        <Row k="Password reset sent" v={rpFormatAccountTime(user.recoverySentAt)} />
        <Row k="Setup links" v={canInviteUsers ? 'Enabled for this admin' : 'Darren only'} />
        {isDuplicateProfile && <Row k="Duplicate matches" v={`${user.duplicate.sameEmailCount || 0} by email / ${user.duplicate.sameCustomerCount || 0} by customer`} />}
        <Row k={rpAreaNames(user.territory).length > 1 ? 'Responsibility areas' : 'Territory'} v={rpAreaLabel(user.territory, 'None')} />
        {['rep', 'partner'].includes(user.role) && <Row k="Sales authority" v={Object.entries(rpProfileFeatureFlags(user)).filter(([, enabled]) => enabled).map(([key]) => salesAuthorityOptions.find(option => option.key === key)?.label || key).join(', ') || 'Standard'} />}
        {['admin', 'leadership', 'ops', 'sales_manager', 'rep', 'partner'].includes(user.role) && <Row k="Quote pricing" v={`${rpRetailAccountNewPriceListName(user.retailQuoteDefaultPriceListName)}${user.retailQuoteCustomPricingEnabled || ['admin', 'leadership', 'ops', 'sales_manager'].includes(user.role) ? ' · custom enabled' : ''}`} />}
        <Row k="Customer link" v={user.customer ? `${user.customer.name} (${user.customer.status})` : (user.customerId || 'None')} />
        <Row k="Assigned customers" v={user.assignedCustomerCount || 0} />
      </div>
      <div className="eyebrow" style={{ marginBottom: 10 }}>Repair Profile</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
        <MField label="Display Name">
          <input className="field" value={displayName} onChange={e => setDisplayName(e.target.value)} />
        </MField>
        <MField label="Role">
          <select className="field" value={role} onChange={e => setRole(e.target.value)}>
            {roleOptions.map(value => <option key={value} value={value}>{roleLabel(value)}</option>)}
          </select>
        </MField>
        <MField label={isCustomer ? 'Customer Territory' : 'Responsibility Areas'}>
          {isCustomer ? (
            <input className="field" disabled value={territory} onChange={e => setTerritory(e.target.value)} placeholder="Auto-filled from linked customer when saved" />
          ) : (
            <AdminAreaPicker value={areas} onChange={setResponsibilityAreas} options={territoryOptions} />
          )}
        </MField>
        <MField label="Status">
          <select className="field" value={active ? 'active' : 'inactive'} onChange={e => setActive(e.target.value === 'active')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </MField>
      </div>
      {isCustomer && (
        <MField label="Linked Customer">
          <select className="field" value={customerId} onChange={e => setCustomerId(e.target.value)}>
            <option value="">Select customer...</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.status}</option>)}
          </select>
        </MField>
      )}
      {isFieldRole && (
        <div style={{ marginTop: 6, marginBottom: 16 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Sales Authority</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {salesAuthorityOptions.map(option => (
              <label key={option.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: featureFlags[option.key] ? 'var(--amber-soft)' : 'var(--surface-2)', border: '1px solid ' + (featureFlags[option.key] ? 'var(--amber)' : 'var(--line)'), borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={featureFlags[option.key] === true} onChange={() => toggleFeatureFlag(option.key)} style={{ marginTop: 2 }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{option.label}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>{option.detail}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {isRetailQuoteRole && (
        <div style={{ marginTop: 6, marginBottom: 16 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Retail Quote Pricing</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            <MField label="Default quote price">
              <select className="field" value={retailQuoteDefaultPriceListName} onChange={event => setRetailQuoteDefaultPriceListName(event.target.value)}>
                {retailQuotePriceModels.map(model => <option key={model.name} value={model.name}>{model.name}{Number.isFinite(model.skuPrice) ? ` · $${model.skuPrice.toFixed(2)} / case` : ''}</option>)}
              </select>
            </MField>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: retailQuoteCustomPricingEnabled || retailQuoteCustomPricingIncludedByRole ? 'var(--amber-soft)' : 'var(--surface-2)', border: '1px solid ' + (retailQuoteCustomPricingEnabled || retailQuoteCustomPricingIncludedByRole ? 'var(--amber)' : 'var(--line)'), borderRadius: 10, padding: '10px 12px', cursor: retailQuoteCustomPricingIncludedByRole ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={retailQuoteCustomPricingEnabled || retailQuoteCustomPricingIncludedByRole} disabled={retailQuoteCustomPricingIncludedByRole} onChange={event => setRetailQuoteCustomPricingEnabled(event.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>Custom quote pricing</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>{retailQuoteCustomPricingIncludedByRole ? 'Included with this manager role.' : 'Allows this user to enter arbitrary case pricing without a manager changing this permission first.'}</span>
              </span>
            </label>
          </div>
        </div>
      )}
      <label style={{ display: 'flex', gap: 10, padding: '10px 0', marginBottom: 12 }}>
        <input type="checkbox" checked={featureFlags.performance_read_efficiency === true} onChange={() => toggleFeatureFlag('performance_read_efficiency')} />
        <span><strong>Efficient live refresh preview</strong><small style={{ display: 'block', color: 'var(--muted)' }}>Combine repeated updates and pause background refreshes for this user.</small></span>
      </label>
      {canEditDashboardWorkflowFlag && (
        <div style={{ marginTop: 6, marginBottom: 16 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Dashboard Preview</div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: featureFlags[dashboardWorkflowOption.key] ? 'var(--amber-soft)' : 'var(--surface-2)', border: '1px solid ' + (featureFlags[dashboardWorkflowOption.key] ? 'var(--amber)' : 'var(--line)'), borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={featureFlags[dashboardWorkflowOption.key] === true} onChange={() => toggleFeatureFlag(dashboardWorkflowOption.key)} style={{ marginTop: 2 }} />
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{dashboardWorkflowOption.label}</span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>{dashboardWorkflowOption.detail}</span>
            </span>
          </label>
        </div>
      )}
      {!isCustomer && user.customerId && <div style={{ marginTop: 10, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>Saving this non-customer profile will clear the customer link.</div>}
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      {message && <div style={{ marginTop: 12, background: 'var(--ok-bg)', color: 'var(--ok)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{message}</div>}
      {tempResult && tempResult.temporaryPassword && (
        <div style={{ marginTop: 12, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 13px', fontSize: 12.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ fontWeight: 800, color: 'var(--ink)' }}>Temporary password</div>
            <button className="btn btn-sm" onClick={copyTempPassword}><Icon name="doc" size={14} />Copy</button>
          </div>
          <div className="mono" style={{ marginTop: 8, fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>{tempResult.temporaryPassword}</div>
          <div style={{ marginTop: 8, color: 'var(--muted)', lineHeight: 1.45 }}>The user will be asked to choose their own password after sign-in. This password is only shown now.</div>
        </div>
      )}
      <div style={{ height: 18 }} />
      <div className="eyebrow" style={{ marginBottom: 10 }}>Diagnostics</div>
      {user.issues && user.issues.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {user.issues.map((issue, index) => (
            <div key={index} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', background: issue.severity === 'blocked' ? 'var(--danger-bg)' : 'var(--warn-bg)', color: issue.severity === 'blocked' ? 'var(--danger)' : 'var(--amber-deep)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.35 }}>
              <Icon name="alert" size={15} style={{ flexShrink: 0, marginTop: 1 }} />{issue.message}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>No profile-link issues detected.</div>
      )}
    </Modal>
  );
}

Object.assign(window, { FBAView: FbaView, ChepPanel, AdminView });


/* ===== desktop_tasks.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Task Manager
   Assign, track & organize work across the team.
   ============================================================ */

function taskTodayIso() {
  return typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10);
}
const TODAY = taskTodayIso();
function dueMeta(due) {
  const today = taskTodayIso();
  if (!due) return { label: '—', tone: 'muted' };
  if (due < today) return { label: 'Overdue', tone: 'danger' };
  if (due === today) return { label: 'Due today', tone: 'warn' };
  // within 2 days = soon
  const d = (new Date(due) - new Date(today)) / 86400000;
  if (d <= 2) return { label: 'Due ' + due.slice(5), tone: 'warn' };
  return { label: 'Due ' + due.slice(5), tone: 'muted' };
}
const PRIO_COLOR = { Urgent: 'var(--danger)', High: 'var(--amber-deep)', Medium: 'var(--info)', Low: 'var(--muted)' };
function PriorityTag({ p }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: PRIO_COLOR[p] }}><span style={{ width: 7, height: 7, borderRadius: 2, background: PRIO_COLOR[p] }} />{p}</span>;
}
function taskLiveMode() { return !(window.RP_AUTH && window.RP_AUTH.demo); }
const TASK_PLACEHOLDER_NAMES = new Set(['Dana Whitfield', 'Sam Okoro', 'Marco Ferreira', 'J. Park', 'L. Tran', 'A. Reyes', 'C. Hale', 'Accounting']);
const TASK_ASSIGNABLE_ROLES = new Set(['admin', 'leadership', 'ops', 'sales_manager', 'warehouse', 'dispatch', 'accountant', 'marketing', 'rep', 'partner']);
function taskAssigneeName(user) {
  return String(user && (user.name || user.displayName || user.display_name || user.email) || '').trim();
}
function taskAssigneeOptions(currentOwner) {
  if (taskLiveMode()) {
    const seen = new Set();
    const live = (RP.USER_PROFILES || [])
      .filter(user => user && user.active !== false && TASK_ASSIGNABLE_ROLES.has(rpNormalizeRole(user.role)))
      .map(user => ({ name: taskAssigneeName(user), role: rpRoleLabel(user.role), area: user.territory || '' }))
      .filter(user => user.name && !TASK_PLACEHOLDER_NAMES.has(user.name))
      .filter(user => {
        const key = user.name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    const owner = String(currentOwner || '').trim();
    if (owner && !TASK_PLACEHOLDER_NAMES.has(owner) && !seen.has(owner.toLowerCase())) {
      live.unshift({ name: owner, role: 'Current assignee', area: '' });
    }
    return live;
  }
  return RP.TEAM || [];
}
function taskOwnerLabel(owner) {
  const text = String(owner || '').trim();
  return taskLiveMode() && TASK_PLACEHOLDER_NAMES.has(text) ? 'Unassigned' : text;
}
function patchLocalTask(id, patch) {
  const t = RP.taskById(id);
  if (!t) return null;
  if (patch.status) t.status = patch.status;
  if (patch.ownerName) t.owner = patch.ownerName;
  return t;
}
async function persistTaskUpdate(id, patch) {
  if (taskLiveMode()) {
    const currentTask = RP.taskById(id);
    const response = await rpApi('/api/tasks/update', {
      method: 'POST',
      body: JSON.stringify({ id, expectedUpdatedAt: currentTask && currentTask.updatedAt || undefined, ...patch })
    });
    const task = RP.upsertTask(response.task);
    if (response.auditWarning) toast(response.auditWarning, 'warn');
    if (response.notificationWarning) toast(response.notificationWarning, 'warn');
    await rpRefreshAfterMutation(`Task ${task.id} updated`);
    return RP.taskById(task.id) || task;
  }
  return patchLocalTask(id, patch);
}
async function persistTaskCreate(input) {
  if (taskLiveMode()) {
    const response = await rpApi('/api/tasks/create', {
      method: 'POST',
      body: JSON.stringify(input)
    });
    const task = RP.upsertTask(response.task);
    await rpRefreshAfterMutation(`Task ${task.id} created`);
    return RP.taskById(task.id) || task;
  }
  return RP.addTask({ title: input.title, owner: input.ownerName, assigner: input.assignerName, priority: input.priority, cat: input.category, due: input.dueDate, cust: input.customerId || undefined, note: input.note || undefined });
}
const SUPPORT_TICKET_TYPES = ['Bug', 'Feature request', 'Order support', 'Billing', 'Portal help', 'Other'];
function supportTicketDefaultPriority(type) {
  return ['Bug', 'Order support', 'Billing'].includes(type) ? 'High' : 'Medium';
}
function supportTicketDue(priority) {
  if (typeof rpTodayYmd === 'function') return rpTodayYmd(null, priority === 'Urgent' || priority === 'High' ? 1 : 3);
  const d = new Date();
  d.setDate(d.getDate() + (priority === 'Urgent' || priority === 'High' ? 1 : 3));
  return d.toISOString().slice(0, 10);
}
function supportTicketActorName() {
  if (typeof rpDisplayName === 'function') return rpDisplayName();
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  return profile.display_name || profile.email || 'Ranch Power';
}
function supportTicketPage() {
  try {
    return `${window.location.hostname}${window.location.pathname}${window.location.hash || ''}`;
  } catch {
    return '';
  }
}
function supportTicketOrdersFor(customerId) {
  return (RP.ORDERS || []).filter(order => !customerId || order.cust === customerId);
}
function supportTicketTitle(type, customerId, orderId) {
  const customer = customerId ? RP.cust(customerId) : null;
  return [type, customer && customer.name, orderId].filter(Boolean).join(' - ');
}
async function submitSupportTicket(input) {
  const type = input.type || input.category || 'Other';
  const priority = input.priority || supportTicketDefaultPriority(type);
  const title = (input.title || supportTicketTitle(type, input.customerId, input.orderId) || type).trim();
  const message = String(input.message || '').trim();
  if (taskLiveMode()) {
    const response = await rpApi('/api/support/ticket', {
      method: 'POST',
      body: JSON.stringify({
        customerId: input.customerId || undefined,
        attachments: input.attachments || [],
        message,
        orderId: input.orderId || undefined,
        page: input.page || supportTicketPage(),
        priority,
        source: input.source || 'Operations portal',
        title,
        type
      })
    });
    const task = RP.upsertTask(response.task);
    await rpRefreshAfterMutation(`Support ticket ${task.id} created`);
    return RP.taskById(task.id) || task;
  }
  const note = [
    `Ticket type: ${type}`,
    `Submitted by: ${supportTicketActorName()}`,
    input.source ? `Source: ${input.source}` : '',
    input.orderId ? `Order: ${input.orderId}` : '',
    input.page ? `Page: ${input.page}` : '',
    '',
    message
  ].filter(Boolean).join('\n');
  return RP.addTask({ title: `Support: ${title}`, owner: 'Operations', assigner: supportTicketActorName(), priority, cat: 'Support', due: supportTicketDue(priority), cust: input.customerId || undefined, note, supportAttachments: input.attachments || [] });
}
function taskIsSupportTicket(task) {
  const category = String(task && task.cat || '').trim().toLowerCase();
  const title = String(task && task.title || '').trim();
  const note = String(task && task.note || '');
  return category === 'support' || /^support\s*:/i.test(title) || /\bticket type\s*:/i.test(note);
}
function supportTicketMeta(task) {
  const note = String(task && task.note || '');
  const lines = note.split(/\r?\n/);
  const labels = supportTicketLabels(task);
  const blankAt = lines.findIndex(line => !String(line || '').trim());
  const knownLabels = new Set(['ticket type', 'submitted by', 'source', 'customer', 'order', 'page', 'recipient', 'subject', 'invoice', 'provider message', 'reason']);
  const bodyLines = blankAt >= 0
    ? lines.slice(blankAt + 1)
    : lines.filter(line => {
      const match = String(line || '').match(/^([^:]{2,40}):\s*(.*)$/);
      return !match || !knownLabels.has(match[1].trim().toLowerCase());
    });
  const title = String(task && task.title || '');
  const type = labels['ticket type'] || SUPPORT_TICKET_TYPES.find(t => title.toLowerCase().includes(t.toLowerCase())) || 'Other';
  return {
    customer: labels.customer || '',
    message: bodyLines.join('\n').trim() || note.trim(),
    order: labels.order || '',
    page: labels.page || '',
    source: labels.source || '',
    submittedBy: labels['submitted by'] || String(task && task.assigner || ''),
    type
  };
}
function supportTicketLabels(task) {
  const labels = {};
  String(task && task.note || '').split(/\r?\n/).forEach(line => {
    const match = String(line || '').match(/^([^:]{2,40}):\s*(.*)$/);
    if (match) labels[match[1].trim().toLowerCase()] = match[2].trim();
  });
  return labels;
}
function supportTicketExcerpt(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > 170 ? clean.slice(0, 167) + '...' : clean;
}
function supportTicketLinkedOrder(task, meta) {
  const orderId = String((meta && meta.order) || '').trim();
  if (!orderId) return null;
  return (RP.ORDERS || []).find(order => String(order.id || '').trim() === orderId) || null;
}
function supportTicketOrderLineLabel(line) {
  const sku = line && line.sku ? RP.sku(line.sku) : null;
  const name = sku ? rpSkuDisplayName(sku) : (line && (line.name || line.sku)) || 'Unknown item';
  return `${name} x ${Number(line && line.qty || 0)} cs`;
}
function supportTicketCustomerSummary(customer) {
  if (!customer) return [];
  return [
    `- Customer ID: ${customer.id || '-'}`,
    `- Name: ${customer.name || '-'}`,
    `- Type/tier: ${[customer.type, customer.tier].filter(Boolean).join(' / ') || '-'}`,
    `- Territory/city: ${[customer.territory, customer.city].filter(Boolean).join(' / ') || '-'}`,
    `- Rep: ${customer.rep || '-'}`,
    `- Buyer/contact: ${customer.buyer || customer.contactName || '-'}`,
    `- Email: ${customer.email || customer.buyerEmail || '-'}`,
    `- Phone: ${customer.phone || customer.buyerPhone || '-'}`
  ];
}
function supportTicketOrderSummary(order) {
  if (!order) return [];
  let total = '';
  try {
    const totals = typeof rpOrderTotals === 'function' ? rpOrderTotals(order) : null;
    if (totals && Number.isFinite(Number(totals.total))) total = `$${Number(totals.total).toFixed(2)}`;
  } catch {}
  return [
    `- Order ID: ${order.id || '-'}`,
    `- Status: ${order.status || '-'}`,
    `- Customer ID: ${order.cust || order.customerId || order.customer_id || '-'}`,
    `- Territory: ${order.territory || '-'}`,
    `- Rep: ${order.rep || '-'}`,
    `- Delivery/date: ${[order.delivery, order.date].filter(Boolean).join(' / ') || '-'}`,
    total ? `- Total: ${total}` : '',
    `- Lines: ${(order.lines || []).map(supportTicketOrderLineLabel).join('; ') || '-'}`
  ].filter(Boolean);
}
function supportTicketCodexPrompt(task, exactLinkedOrder = null) {
  const meta = supportTicketMeta(task);
  const labels = supportTicketLabels(task);
  const customer = task && task.cust ? RP.cust(task.cust) : null;
  const order = exactLinkedOrder || supportTicketLinkedOrder(task, meta);
  const labelLines = Object.entries(labels)
    .filter(([key]) => !['ticket type', 'submitted by', 'source', 'customer', 'order', 'page'].includes(key))
    .map(([key, value]) => `- ${key.replace(/\b\w/g, c => c.toUpperCase())}: ${value || '-'}`);
  return [
    'Please tackle this Ranch Power support ticket end-to-end.',
    '',
    'Workspace:',
    '- Repository: D:\\Websites\\RanchPower',
    '- Primary app: apps/ops-platform',
    '',
    'Support ticket:',
    `- ID: ${task.id || '-'}`,
    `- Title: ${task.title || '-'}`,
    `- Type: ${meta.type || '-'}`,
    `- Status: ${task.status || '-'}`,
    `- Priority: ${task.priority || '-'}`,
    `- Due: ${task.due || '-'}`,
    `- Assignee: ${taskOwnerLabel(task.owner) || '-'}`,
    `- Submitted by: ${meta.submittedBy || task.assigner || '-'}`,
    `- Source: ${meta.source || '-'}`,
    `- Page: ${meta.page || '-'}`,
    ...(labelLines.length ? ['', 'Extracted fields:'] : []),
    ...labelLines,
    ...(customer ? ['', 'Linked customer:'] : []),
    ...supportTicketCustomerSummary(customer),
    ...(order ? ['', 'Linked order:'] : []),
    ...supportTicketOrderSummary(order),
    '',
    'Submitted details:',
    meta.message || 'No details provided.',
    '',
    'What I need from Codex:',
    '1. Inspect the relevant code paths and data shape for this ticket.',
    '2. Implement a focused fix or improvement that fits the existing Ranch Power patterns.',
    '3. Run the appropriate checks/builds, and verify behavior where practical.',
    '4. Commit, push, and deploy if the change is safe to ship; otherwise report the blocker clearly.',
    '',
    'Do not ask me to re-explain the ticket unless the copied context is genuinely insufficient.'
  ].filter(line => line !== null && line !== undefined).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}
async function copySupportTicketCodexPrompt(task) {
  try {
    const meta = supportTicketMeta(task);
    const orderId = String(meta.order || '').trim();
    let order = supportTicketLinkedOrder(task, meta);
    if (orderId && !order && typeof rpHasLiveAuth === 'function' && rpHasLiveAuth()) {
      order = await rpLoadExactOrder({ orderId });
      if (!order) throw new Error(`Linked order ${orderId} could not be loaded.`);
    }
    await copyTextToClipboard(supportTicketCodexPrompt(task, order));
    toast(`Copied Codex prompt for ${task.id}`, 'ok');
  } catch (err) {
    toast(err && err.message ? err.message : 'Could not copy the Codex prompt.', 'danger');
  }
}
function taskSearchText(task) {
  const cust = task && task.cust ? RP.cust(task.cust) : null;
  const meta = taskIsSupportTicket(task) ? supportTicketMeta(task) : {};
  return [
    task && task.id,
    task && task.title,
    task && task.cat,
    task && task.priority,
    task && task.status,
    task && task.due,
    taskOwnerLabel(task && task.owner),
    task && task.assigner,
    cust && cust.name,
    meta.type,
    meta.submittedBy,
    meta.source,
    meta.customer,
    meta.order,
    meta.page,
    meta.message,
    task && task.note
  ].filter(Boolean).join(' ').toLowerCase();
}
function SupportTicketForm({ customerId, customerLocked, source, onCreated, onCancel, compact, onStateChange }) {
  const initialType = source === 'Customer portal' ? 'Order support' : 'Bug';
  const [f, setF] = useState({
    type: initialType,
    priority: supportTicketDefaultPriority(initialType),
    customerId: customerId || '',
    orderId: '',
    title: '',
    message: '',
    page: supportTicketPage()
  });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  // Report before the browser can deliver Escape or another close action.
  // A passive effect can leave the parent guard one interaction behind.
  React.useLayoutEffect(() => {
    if (onStateChange) onStateChange({ busy: busy || uploading, dirty: !created && Boolean(f.title || f.message || f.orderId || f.customerId !== (customerId || '') || attachments.length) });
  }, [busy, uploading, created, f, attachments, customerId, onStateChange]);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const customers = customerLocked && f.customerId ? [RP.cust(f.customerId)].filter(Boolean) : (RP.CUSTOMERS || []);
  const liveOrders = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const orderPage = useRpServerPagedRows({ mode: 'orders', limit: 50, customerId: f.customerId, search: orderSearch });
  const pagedOrders = liveOrders
    ? orderPage.items.map(item => item && item.order).filter(order => order && order.id)
    : supportTicketOrdersFor(f.customerId);
  const selectedOrder = f.orderId ? (RP.ORDERS || []).find(order => order.id === f.orderId) : null;
  const orders = Array.from(new Map([...(selectedOrder ? [selectedOrder] : []), ...pagedOrders].map(order => [order.id, order])).values());
  const valid = f.message.trim().length >= 8;
  const uploadFiles = async event => {
    const files = Array.from(event.target.files || []); event.target.value = '';
    if (!files.length || busy || uploading) return;
    setError('');
    if (attachments.length + files.length > 6) { setError('Attach at most six images or PDFs.'); return; }
    if (!taskLiveMode()) { setError('Attachments are available when signed in. Demo files are not uploaded.'); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const uploaded = await rpUploadPrivateFile('support_attachment', file, 'support');
        if (!uploaded || !uploaded.path) throw new Error(`Upload of ${file.name} was not confirmed. Retry that file before submitting.`);
        const evidence = { path: uploaded.path, name: uploaded.name, type: uploaded.type, size: uploaded.size };
        setAttachments(current => [...current, evidence]);
      }
    } catch (err) { setError(err.message || 'The file could not be uploaded. Successfully uploaded files are still attached below.'); }
    finally { setUploading(false); }
  };
  const submit = async () => {
    setTried(true);
    setError('');
    if (!valid || busy || uploading) return;
    setBusy(true);
    try {
      const task = await submitSupportTicket({ ...f, source, attachments });
      setCreated(task);
      toast(`Support ticket ${task.id} created`, 'ok');
      onCreated && onCreated(task);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create support ticket.');
    } finally {
      setBusy(false);
    }
  };
  if (created) {
    return (
      <div style={{ background: 'var(--ok-bg)', border: '1px solid var(--ok)', borderRadius: 12, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 8 }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--ok)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={18} /></span>
          <div>
            <div style={{ fontWeight: 800, color: 'var(--ink)' }}>Support ticket created</div>
            <div className="mono" style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>{created.id}</div>
          </div>
        </div>
        <div style={{ color: 'var(--ink-soft)', fontSize: 13, lineHeight: 1.45 }}>Operations can now triage this from Tasks under the Support category.</div>
        <SupportTicketAttachments attachments={created.supportAttachments || attachments} />
        {onCancel && <button className="btn btn-sm" style={{ marginTop: 14 }} onClick={onCancel}>Close</button>}
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: compact ? 0 : 14 }}>
        <MField label="Ticket type" half={!compact}>
          <select className="field" value={f.type} onChange={e => setF(s => ({ ...s, type: e.target.value, priority: supportTicketDefaultPriority(e.target.value) }))}>
            {SUPPORT_TICKET_TYPES.map(type => <option key={type}>{type}</option>)}
          </select>
        </MField>
        <MField label="Priority" half={!compact}>
          <select className="field" value={f.priority} onChange={e => set('priority', e.target.value)}>
            {RP.TASK_PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </MField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: compact ? 0 : 14 }}>
        <MField label="Account" half={!compact}>
          <select className="field" value={f.customerId} disabled={customerLocked} onChange={e => { setOrderSearch(''); setF(s => ({ ...s, customerId: e.target.value, orderId: '' })); }}>
            {!customerLocked && <option value="">No account link</option>}
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </MField>
        <MField label="Order / invoice" half={!compact}>
          {liveOrders && <input className="field" value={orderSearch} onChange={e => setOrderSearch(e.target.value)} placeholder="Search order, invoice, PO, or customer..." style={{ marginBottom: 8 }} />}
          <select className="field" value={f.orderId} disabled={liveOrders && orderPage.initialLoading} onChange={e => { const next = e.target.value; const order = orders.find(row => row.id === next); if (order) rpUpsertOrder(order); set('orderId', next); }}>
            <option value="">Not tied to an order</option>
            {orders.map(o => <option key={o.id} value={o.id}>{o.id} - {RP.cust(o.cust)?.name || o.cust}</option>)}
          </select>
          {liveOrders && orderPage.initialLoading && <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 6 }}>Loading orders...</div>}
          {liveOrders && orderPage.error && <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 6 }}>{orderPage.error} <button type="button" className="btn btn-sm" onClick={orderPage.retry}>Retry</button></div>}
          {liveOrders && orderPage.loaded && orderPage.page.hasMore && <button type="button" className="btn btn-sm" disabled={orderPage.loadingMore} onClick={orderPage.loadMore} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>{orderPage.loadingMore ? 'Loading...' : 'Load More Orders'}</button>}
        </MField>
      </div>
      <MField label="Short title">
        <input className="field" placeholder="e.g. Invoice PDF is wrong on mobile" value={f.title} onChange={e => set('title', e.target.value)} />
      </MField>
      <MField label="Details" required>
        <textarea className="field" rows={compact ? 4 : 5} placeholder="What happened, what did you expect, and what should Operations look at?" value={f.message} onChange={e => set('message', e.target.value)} style={{ resize: 'vertical', minHeight: 110, borderColor: tried && !valid ? 'var(--danger)' : undefined }} />
        <ReqMsg show={tried && !valid}>Add a few details so Operations can act on it.</ReqMsg>
      </MField>
      <MField label="Page / context">
        <input className="field" value={f.page} onChange={e => set('page', e.target.value)} />
      </MField>
      <MField label="Screenshots or files">
        <input className="field" type="file" aria-label="Attach screenshots or files" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" multiple disabled={busy || uploading} onChange={uploadFiles} />
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>Up to six images or PDFs, 8 MB each. Uploads stay private; Operations can open them from this ticket.</div>
        {uploading && <div role="status" style={{ marginTop: 8 }}>Uploading files…</div>}
        <SupportTicketAttachments attachments={attachments} onRemove={index => setAttachments(rows => rows.filter((_, position) => position !== index))} disabled={busy || uploading} />
      </MField>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
        {onCancel && <button className="btn" disabled={busy || uploading} onClick={onCancel}>Cancel</button>}
        <button className="btn btn-primary" disabled={busy || uploading} onClick={submit}><Icon name="mail" size={15} />{busy ? 'Submitting...' : 'Submit Ticket'}</button>
      </div>
    </div>
  );
}
function SupportTicketAttachments({ attachments, onRemove, disabled }) {
  const [fileError, setFileError] = useState('');
  const open = async file => { setFileError(''); try { await rpOpenProtectedFile(`/api/files/view?path=${encodeURIComponent(file.path)}`); } catch (err) { setFileError(err.message || 'Could not open this attachment.'); } };
  if (!Array.isArray(attachments) || !attachments.length) return null;
  return <div aria-label="Ticket attachments" style={{ marginTop: 10 }}>
    {attachments.map((file, index) => <div key={file.path} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '7px 0' }}><button type="button" className="btn btn-sm" disabled={disabled} style={{ minWidth: 0, maxWidth: '100%', whiteSpace: 'normal', overflowWrap: 'anywhere', textAlign: 'left' }} onClick={() => open(file)}><Icon name="doc" size={14} />{file.name || 'Open attachment'} · {Math.max(1, Math.round(Number(file.size || 0) / 1024))} KB</button>{onRemove && <button type="button" className="btn btn-sm" disabled={disabled} aria-label={`Remove ${file.name || 'attachment'}`} onClick={() => onRemove(index)}>Remove</button>}</div>)}
    {fileError && <div role="alert" style={{ color: 'var(--danger)', fontSize: 12 }}>{fileError}</div>}
  </div>;
}
function SupportTicketModal({ customerId, customerLocked, onClose, onAdded, source }) {
  return (
    <Modal title="Support Ticket" sub="Send bugs, feature requests, or customer support to Operations" onClose={onClose} width={620}>
      <SupportTicketForm customerId={customerId} customerLocked={customerLocked} source={source || 'Operations portal'} onCreated={onAdded} onCancel={onClose} />
    </Modal>
  );
}
function isCustomerApprovalTask(task, customer) {
  return Boolean(task && customer && /^Approve customer:/i.test(String(task.title || '')));
}
function taskRemovalRequest(task) {
  if (!task || !/^RMV-/i.test(String(task.id || ''))) return null;
  const note = String(task.note || '');
  const entityLine = note.split(/\n+/).find(line => /^Entity:/i.test(line)) || '';
  const actionLine = note.split(/\n+/).find(line => /^Requested action:/i.test(line)) || '';
  const entityMatch = entityLine.match(/^Entity:\s*([a-z_]+)\s+([^\s(]+)(?:\s+\((.*)\))?/i);
  const actionMatch = actionLine.match(/^Requested action:\s*(.+)$/i);
  return {
    action: (actionMatch && actionMatch[1]) || 'remove',
    entityId: entityMatch ? entityMatch[2] : '',
    entityLabel: entityMatch ? entityMatch[3] || '' : '',
    entityType: entityMatch ? entityMatch[1] : '',
  };
}
function taskCanResolveRemoval() {
  const role = rpNormalizeRole(rpAuthProfile().role);
  return ['admin', 'leadership', 'ops', 'sales_manager'].includes(role);
}
function taskCustomerBuyerEmail(customer) {
  return String(customer && (customer.email || customer.buyerEmail || customer.buyer_email) || '').trim().toLowerCase();
}
function taskIsPortalInviteTask(task) {
  const text = `${task && task.id || ''} ${task && task.title || ''} ${task && task.note || ''}`.toLowerCase();
  return /portal|setup link|customer invite|invite/.test(text) && /invite|portal|setup/.test(text);
}
function taskPortalInviteState(task, customer) {
  const buyerEmail = taskCustomerBuyerEmail(customer);
  const portalTask = taskIsPortalInviteTask(task) && !!customer;
  const ready = portalTask && rpCustomerOrderReady(customer);
  const inviteReady = portalTask && (typeof rpCustomerPortalInviteReady === 'function' ? rpCustomerPortalInviteReady(customer) : ready);
  const intakeOnly = Boolean(portalTask && inviteReady && !ready && typeof rpCustomerQuoteProspect === 'function' && rpCustomerQuoteProspect(customer));
  const allowed = portalTask && (typeof rpCanSendCustomerPortalAccess === 'function' ? rpCanSendCustomerPortalAccess(customer) : rpCanInviteUsers());
  let reason = '';
  if (portalTask && !inviteReady) reason = customer.onboardingSubmittedAt ? 'Approve this customer before sending portal access.' : 'Customer portal access is not ready.';
  else if (portalTask && !buyerEmail) reason = 'Add a buyer email before sending portal access.';
  else if (portalTask && !allowed) reason = 'Your profile cannot send portal access for this customer.';
  return {
    allowed,
    buyerEmail,
    canSend: Boolean(portalTask && inviteReady && buyerEmail && allowed),
    intakeOnly,
    inviteReady,
    portalTask,
    ready,
    reason
  };
}
async function sendPortalInviteFromTask(task, customer) {
  if (!task || !customer) throw new Error('Portal invite task is missing its linked customer.');
  const state = taskPortalInviteState(task, customer);
  if (!state.portalTask) throw new Error('This task is not a portal invite task.');
  if (!state.canSend) throw new Error(state.reason || 'Portal access cannot be sent yet.');

  if (taskLiveMode()) {
    await rpApi('/api/users/invite', {
      method: 'POST',
      body: JSON.stringify({
        customerId: customer.id,
        email: state.buyerEmail,
        name: customer.buyer && customer.buyer !== 'Buyer' ? customer.buyer : customer.name,
        role: 'customer',
        ...(state.intakeOnly ? { purpose: 'retailer_intake' } : {})
      })
    });
    await persistTaskUpdate(task.id, { status: 'Done' });
    await rpRefreshAfterMutation(`${state.intakeOnly ? 'Intake link' : 'Portal access'} sent to ${customer.name}`);
  } else {
    patchLocalTask(task.id, { status: 'Done' });
  }
  return RP.taskById(task.id) || { ...task, status: 'Done' };
}
async function resolveRemovalFromTask(task, decision, note) {
  if (!task) throw new Error('Removal task is missing.');
  if (!taskCanResolveRemoval()) throw new Error('Only Admin, Leadership, Operations, or Sales Manager can resolve removal requests.');

  if (taskLiveMode()) {
    const response = await rpApi('/api/removal/resolve', {
      method: 'POST',
      body: JSON.stringify({ taskId: task.id, decision, note })
    });
    const updatedTask = RP.upsertTask(response.task);
    await rpRefreshAfterMutation(`Removal request ${task.id} ${decision}`);
    return updatedTask;
  }

  return patchLocalTask(task.id, { status: 'Done' });
}
async function approveCustomerFromTask(task, customer) {
  if (typeof rpCustomerQuoteProspect === 'function' && rpCustomerQuoteProspect(customer) && !customer.onboardingSubmittedAt) {
    throw new Error('Send the intake link and approve after the retailer submits the form.');
  }
  if (!task || !customer) throw new Error('Approval task is missing its linked customer.');
  if (!rpCanApproveCustomers(null, customer)) throw new Error('Your profile is not enabled to approve this customer.');

  let updatedCustomer = null;
  if (taskLiveMode()) {
    const response = await rpApi('/api/customers/update', {
      method: 'POST',
      body: JSON.stringify({
        creditStatus: customer.credit === 'Watch' || customer.credit === 'Hold' ? customer.credit : 'Good',
        id: customer.id,
        status: 'Approved'
      })
    });
    updatedCustomer = RP.upsertCustomer({ ...customer, ...response.customer, status: 'Approved', risk: 'Low', credit: response.customer && response.customer.credit ? response.customer.credit : 'Good', portalInviteTaskId: response.portalInviteTaskId || '', portalInviteTaskError: response.portalInviteTaskError || '', approvalTaskCloseoutWarning: response.approvalTaskCloseoutWarning || '', closedApprovalTaskIds: Array.isArray(response.closedApprovalTaskIds) ? response.closedApprovalTaskIds : [], skipCreds: true, persisted: true });
    // /api/customers/update closes every matching approval task server-side.
    // Do not issue a second task write with the pre-approval updatedAt token.
    await rpRefreshAfterMutation(`${updatedCustomer.name} approved`);
  } else {
    updatedCustomer = RP.upsertCustomer({ ...customer, status: 'Approved', risk: 'Low', credit: customer.credit === 'Watch' || customer.credit === 'Hold' ? customer.credit : 'Good', approvalTaskCloseoutWarning: '', closedApprovalTaskIds: [task.id] });
    patchLocalTask(task.id, { status: 'Done', ownerName });
  }
  return updatedCustomer;
}

function SupportTicketsView({ tickets, onOpen, onMove }) {
  const today = taskTodayIso();
  const open = tickets.filter(t => t.status !== 'Done').length;
  const overdue = tickets.filter(t => t.due && t.due < today && t.status !== 'Done').length;
  const blocked = tickets.filter(t => t.status === 'Blocked').length;
  const closed = tickets.filter(t => t.status === 'Done').length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, flexShrink: 0 }}>
        <KPICard label="Open Tickets" value={open} icon="mail" accent="var(--navy)" />
        <KPICard label="Overdue" value={overdue} icon="clock" accent="var(--danger)" />
        <KPICard label="Blocked" value={blocked} icon="alert" accent="var(--warn)" />
        <KPICard label="Closed" value={closed} icon="check" accent="var(--ok)" />
      </div>
      <div className="card rp-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table className="rp-table" style={{ minWidth: 980 }}>
          <thead><tr><th>Ticket</th><th>Type</th><th>Submitted by</th><th>Linked</th><th>Assignee</th><th>Priority</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {tickets.map(t => {
              const meta = supportTicketMeta(t);
              const cust = t.cust ? RP.cust(t.cust) : null;
              const dm = dueMeta(t.due);
              return (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(t.id)}>
                  <td style={{ maxWidth: 360 }}>
                    <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{t.title}</div>
                    <div style={{ color: 'var(--ink-soft)', fontSize: 12.5, lineHeight: 1.35 }}>{supportTicketExcerpt(meta.message)}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 5 }}>{t.id}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 700, color: 'var(--ink-soft)' }}>{meta.type}</div>
                    {meta.source && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{meta.source}</div>}
                  </td>
                  <td style={{ color: 'var(--ink-soft)', fontSize: 12.5 }}>{meta.submittedBy || t.assigner || 'Unknown'}</td>
                  <td style={{ color: 'var(--muted)', fontSize: 12.5 }}>
                    {cust ? <div style={{ fontWeight: 700, color: 'var(--ink-soft)' }}>{cust.name}</div> : meta.customer || '-'}
                    {meta.order && <div className="mono" style={{ marginTop: 3, color: 'var(--info)' }}>{meta.order}</div>}
                  </td>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Avatar name={taskOwnerLabel(t.owner)} size={26} color="var(--navy)" /><span style={{ fontSize: 12.5 }}>{taskOwnerLabel(t.owner)}</span></div></td>
                  <td><PriorityTag p={t.priority} /><div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--' + dm.tone + ')', marginTop: 5 }}>{dm.label}</div></td>
                  <td><Pill status={t.status} /></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                      <button className="btn btn-sm" title="Copy Codex-ready prompt" onClick={e => { e.stopPropagation(); copySupportTicketCodexPrompt(t); }}><Icon name="clipboard" size={14} />Codex</button>
                      {t.status === 'Done'
                        ? <button className="btn btn-sm" onClick={e => { e.stopPropagation(); onMove(t.id, 'In Progress'); }}><Icon name="refresh" size={14} />Reopen</button>
                        : <button className="btn btn-sm btn-primary" onClick={e => { e.stopPropagation(); onMove(t.id, 'Done'); }}><Icon name="check" size={14} />Close</button>}
                      <Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} />
                    </div>
                  </td>
                </tr>
              );
            })}
            {tickets.length === 0 && <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No support tickets match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TasksView({ go, role, detail }) {
  const me = rpFieldUserName(role);
  const [view, setView] = useState('board');
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [filters, onFilter] = useFilters();
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(false);
  const [supportModal, setSupportModal] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const [, force] = useState(0);
  const bump = () => force(x => x + 1);

  useEffect(() => {
    if (detail && detail.type === 'task' && detail.id) setSel(detail.id);
  }, [detail && detail.type, detail && detail.id]);

  const allTasks = RP.TASKS || [];
  const supportTasks = allTasks.filter(taskIsSupportTicket);
  const supportMode = tab === 'support';
  const defs = supportMode
    ? [
      { label: 'Assignee', options: distinct(supportTasks, t => taskOwnerLabel(t.owner)) },
      { label: 'Type', options: distinct(supportTasks, t => supportTicketMeta(t).type) },
      { label: 'Status', options: RP.TASK_STATUSES },
      { label: 'Priority', options: RP.TASK_PRIORITIES },
    ]
    : [
      { label: 'Assignee', options: distinct(allTasks, t => taskOwnerLabel(t.owner)) },
      { label: 'Category', options: RP.TASK_CATEGORIES },
      { label: 'Priority', options: RP.TASK_PRIORITIES },
    ];
  let rows = supportMode ? supportTasks : allTasks;
  if (!supportMode && tab === 'mine') rows = rows.filter(t => t.owner === me);
  if (!supportMode && tab === 'assigned') rows = rows.filter(t => t.assigner === me);
  if (!supportMode && tab === 'open') rows = rows.filter(t => t.status !== 'Done');
  if (filters.Assignee) rows = rows.filter(t => taskOwnerLabel(t.owner) === filters.Assignee);
  if (!supportMode && filters.Category) rows = rows.filter(t => t.cat === filters.Category);
  if (supportMode && filters.Type) rows = rows.filter(t => supportTicketMeta(t).type === filters.Type);
  if (supportMode && filters.Status) rows = rows.filter(t => t.status === filters.Status);
  if (filters.Priority) rows = rows.filter(t => t.priority === filters.Priority);
  if (search) {
    const q = search.toLowerCase().trim();
    rows = rows.filter(t => taskSearchText(t).includes(q));
  }

  const today = taskTodayIso();
  const counts = {
    open: allTasks.filter(t => t.status !== 'Done').length,
    today: allTasks.filter(t => t.due === today && t.status !== 'Done').length,
    overdue: allTasks.filter(t => t.due && t.due < today && t.status !== 'Done').length,
    blocked: allTasks.filter(t => t.status === 'Blocked').length,
    supportOpen: supportTasks.filter(t => t.status !== 'Done').length,
  };
  const taskTabs = [['all', 'All'], ['mine', 'My Tasks'], ['assigned', 'Assigned by Me'], ['open', 'Open'], ['support', 'Support Tickets']];
  const visibleFilters = supportMode
    ? { Assignee: filters.Assignee, Type: filters.Type, Status: filters.Status, Priority: filters.Priority }
    : { Assignee: filters.Assignee, Category: filters.Category, Priority: filters.Priority };
  const move = async (id, status) => {
    try {
      await persistTaskUpdate(id, { status });
      bump();
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not update task.', 'danger');
    }
  };

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {modal && <AssignTaskModal me={me} onClose={() => setModal(false)} onAdded={bump} go={go} />}
      {supportModal && <SupportTicketModal onClose={() => setSupportModal(false)} onAdded={bump} source="Operations portal" />}
      {sel && <TaskModal id={sel} me={me} onClose={() => setSel(null)} onChange={bump} go={go} />}
      <PageHead eyebrow="Team Workboard" title="Tasks"
        sub="Assign, track and organize work for yourself and the team."
        actions={<>
          {!supportMode && <Segmented size="sm" options={[{ value: 'board', label: 'Board' }, { value: 'list', label: 'List' }]} value={view} onChange={setView} />}
          <button className="btn btn-sm" onClick={() => setSupportModal(true)}><Icon name="mail" size={15} />Support Ticket</button>
          <button className="btn btn-sm btn-primary" onClick={() => setModal(true)}><Icon name="plus" size={15} />Assign Task</button>
        </>} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 18 }}>
        <KPICard label="Open Tasks" value={counts.open} icon="tasks" accent="var(--navy)" />
        <KPICard label="Due Today" value={counts.today} icon="calendar" accent="var(--amber)" />
        <KPICard label="Overdue" value={counts.overdue} icon="clock" accent="var(--danger)" />
        <KPICard label="Blocked" value={counts.blocked} icon="alert" accent="var(--warn)" />
        <KPICard label="Open Support" value={counts.supportOpen} icon="mail" accent="var(--info)" onClick={() => setTab('support')} active={supportMode} />
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {taskTabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid ' + (tab === k ? 'var(--navy)' : 'var(--line)'), background: tab === k ? 'var(--navy)' : 'var(--surface)', color: tab === k ? '#fff' : 'var(--ink-soft)', borderRadius: 8, padding: '7px 13px', fontSize: 13, fontWeight: 600 }}>
            {l}
            {k === 'support' && <span className="mono" style={{ fontSize: 11, padding: '1px 6px', borderRadius: 99, background: tab === k ? 'rgba(255,255,255,.18)' : 'var(--amber-soft)', color: tab === k ? '#fff' : 'var(--amber-deep)' }}>{counts.supportOpen}</span>}
          </button>
        ))}
      </div>
      <FilterBar defs={defs} value={visibleFilters} onChange={onFilter} search={search} onSearch={setSearch} searchPlaceholder={supportMode ? 'Search tickets, submitter, message, order...' : 'Search tasks...'} />

      {supportMode ? (
        <SupportTicketsView tickets={rows} onOpen={setSel} onMove={move} />
      ) : view === 'board' ? (
        <div className="rp-board" style={{ display: 'grid', gridTemplateColumns: `repeat(${RP.TASK_STATUSES.length}, minmax(220px, 1fr))`, gap: 14, flex: 1, minHeight: 0 }}>
          {RP.TASK_STATUSES.map(st => {
            const col = rows.filter(t => t.status === st);
            const isOver = over === st;
            const tone = st === 'Done' ? 'var(--ok)' : st === 'Blocked' ? 'var(--danger)' : st === 'In Progress' ? 'var(--amber)' : 'var(--muted)';
            return (
              <div key={st}
                onDragOver={e => { e.preventDefault(); setOver(st); }}
                onDragLeave={() => setOver(o => o === st ? null : o)}
                onDrop={e => { e.preventDefault(); if (dragId) move(dragId, st); setDragId(null); setOver(null); }}
                style={{ display: 'flex', flexDirection: 'column', background: 'var(--surface-2)', border: '1px solid ' + (isOver ? 'var(--amber)' : 'var(--line)'), borderRadius: 12, overflow: 'hidden', boxShadow: isOver ? '0 0 0 3px var(--amber-soft)' : 'none', transition: 'box-shadow .15s, border-color .15s' }}>
                <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--line)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: tone }} /><span className="disp" style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{st}</span></span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--faint)', fontWeight: 600 }}>{col.length}</span>
                </div>
                <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {col.map(t => {
                    const dm = dueMeta(t.due);
                    const cust = t.cust ? RP.cust(t.cust) : null;
                    return (
                      <div key={t.id} draggable onDragStart={() => setDragId(t.id)} onDragEnd={() => { setDragId(null); setOver(null); }} onClick={() => setSel(t.id)}
                        style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 13px', cursor: 'pointer', boxShadow: dragId === t.id ? 'var(--shadow-lg)' : 'var(--shadow-sm)', opacity: dragId === t.id ? 0.5 : 1, borderLeft: '3px solid ' + PRIO_COLOR[t.priority] }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                          <PriorityTag p={t.priority} />
                          <span className="mono" style={{ fontSize: 10.5, color: 'var(--faint)' }}>{t.id}</span>
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.32, textDecoration: st === 'Done' ? 'line-through' : 'none', opacity: st === 'Done' ? 0.6 : 1 }}>{t.title}</div>
                        {cust && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="customers" size={12} />{cust.name}</div>}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Avatar name={taskOwnerLabel(t.owner)} size={22} color="var(--navy)" /><span style={{ fontSize: 11.5, color: 'var(--ink-soft)', fontWeight: 500 }}>{taskOwnerLabel(t.owner).split(' ')[0] || 'Open'}</span></span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--' + dm.tone + ')' }}>{dm.label}</span>
                        </div>
                      </div>
                    );
                  })}
                  {col.length === 0 && <div style={{ minHeight: 60, border: '1.5px dashed var(--line)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faint)', fontSize: 12 }}>{isOver ? 'Release here' : 'No tasks'}</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="rp-table">
            <thead><tr><th>Task</th><th>Assignee</th><th>Category</th><th>Priority</th><th>Due</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map(t => {
                const dm = dueMeta(t.due);
                return (
                  <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setSel(t.id)}>
                    <td><div style={{ fontWeight: 600, color: 'var(--ink)' }}>{t.title}</div><div className="mono" style={{ fontSize: 11, color: 'var(--faint)' }}>{t.id}{t.cust ? ' · ' + RP.cust(t.cust).name : ''}</div></td>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Avatar name={taskOwnerLabel(t.owner)} size={26} color="var(--navy)" /><span style={{ fontSize: 12.5 }}>{taskOwnerLabel(t.owner)}</span></div></td>
                    <td style={{ color: 'var(--muted)' }}>{t.cat}</td>
                    <td><PriorityTag p={t.priority} /></td>
                    <td><span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--' + dm.tone + ')' }}>{dm.label}</span></td>
                    <td><Pill status={t.status} /></td>
                    <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>No tasks match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---- Task detail / status + reassign ---- */
function TaskModal({ id, me, onClose, onChange, go }) {
  const t = RP.taskById(id);
  if (!t) return null;
  const assigneeOptions = taskAssigneeOptions(t.owner);
  const initialOwner = assigneeOptions.some(p => p.name === t.owner) ? t.owner : '';
  const [status, setStatus] = useState(t.status);
  const [owner, setOwner] = useState(initialOwner);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dm = dueMeta(t.due);
  const cust = t.cust ? RP.cust(t.cust) : null;
  const supportTicket = taskIsSupportTicket(t);
  const supportMeta = supportTicket ? supportTicketMeta(t) : null;
  const approvalTask = isCustomerApprovalTask(t, cust);
  const removalRequest = taskRemovalRequest(t);
  const canResolveRemoval = removalRequest && taskCanResolveRemoval();
  const approvalReady = rpCustomerOrderReady(cust);
  const approvalNeedsIntake = Boolean(approvalTask && typeof rpCustomerQuoteProspect === 'function' && rpCustomerQuoteProspect(cust) && !cust.onboardingSubmittedAt);
  const canApproveCustomer = approvalTask && !approvalReady && !approvalNeedsIntake && rpCanApproveCustomers(null, cust);
  const portalInvite = taskPortalInviteState(t, cust);
  const openLinkedCustomer = () => {
    if (!cust) return;
    onClose();
    go('customers', { type: 'customer', id: cust.id });
  };
  const save = async (st) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const patch = { status: st };
      if (owner && owner !== t.owner) patch.ownerName = owner;
      const next = await persistTaskUpdate(id, patch);
      setStatus(next.status);
      setOwner(next.owner);
      toast(`${t.id} → ${next.status}`, next.status === 'Done' ? 'ok' : 'info');
      onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update task.');
    } finally {
      setBusy(false);
    }
  };
  const reassign = async (name) => {
    setOwner(name);
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (!name) return;
      const next = await persistTaskUpdate(id, { ownerName: name });
      setOwner(next.owner);
      toast(`${t.id} reassigned to ${next.owner}`, 'info');
      onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not reassign task.');
    } finally {
      setBusy(false);
    }
  };
  const approveCustomer = async () => {
    if (busy || !approvalTask || approvalReady) return;
    setBusy(true);
    setError('');
    try {
      const customer = await approveCustomerFromTask(t, cust);
      const refreshedTask = RP.taskById(t.id);
      const taskClosed = (Array.isArray(customer.closedApprovalTaskIds) && customer.closedApprovalTaskIds.includes(t.id))
        || (refreshedTask && refreshedTask.status === 'Done');
      if (taskClosed) setStatus('Done');
      const closeoutWarning = customer.approvalTaskCloseoutWarning || (!taskClosed ? `Customer approved, but task ${t.id} still needs closure verification.` : '');
      if (closeoutWarning) setError(closeoutWarning);
      toast(
        closeoutWarning
          ? `${customer.name} approved; approval task needs review`
          : customer.portalInviteTaskId
            ? `${customer.name} approved - invite task queued`
            : `${customer.name} approved and task closed`,
        closeoutWarning || customer.portalInviteTaskError ? 'warn' : 'ok'
      );
      onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not approve customer.');
    } finally {
      setBusy(false);
    }
  };
  const sendPortalInvite = async () => {
    if (busy || !portalInvite.portalTask) return;
    setBusy(true);
    setError('');
    try {
      const next = await sendPortalInviteFromTask(t, cust);
      setStatus(next.status || 'Done');
      toast(`${portalInvite.intakeOnly ? 'Intake link' : 'Portal invite'} sent to ${portalInvite.buyerEmail}`, 'ok');
      onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not send portal invite.');
    } finally {
      setBusy(false);
    }
  };
  const resolveRemoval = async (decision) => {
    if (busy || !removalRequest) return;
    setBusy(true);
    setError('');
    try {
      const next = await resolveRemovalFromTask(t, decision);
      setStatus(next.status);
      toast(`Removal request ${t.id} ${decision}`, decision === 'approved' ? 'ok' : 'info');
      onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not resolve removal request.');
    } finally {
      setBusy(false);
    }
  };
  const footerActions = status !== 'Done'
    ? (removalRequest
      ? <><button className="btn" disabled={busy} onClick={onClose}>Close</button><button className="btn" disabled={busy || !canResolveRemoval} style={{ color: 'var(--danger)', opacity: !canResolveRemoval ? 0.6 : 1 }} onClick={() => resolveRemoval('rejected')}><Icon name="x" size={15} />Reject</button><button className="btn btn-primary" disabled={busy || !canResolveRemoval} style={{ opacity: !canResolveRemoval ? 0.6 : 1 }} onClick={() => resolveRemoval('approved')}><Icon name="check" size={15} />{busy ? 'Approving...' : 'Approve Removal'}</button></>
      : <><button className="btn" disabled={busy} onClick={onClose}>Close</button>{status === 'Blocked' ? <button className="btn btn-primary" disabled={busy} onClick={() => save('In Progress')}><Icon name="refresh" size={15} />Unblock</button> : <button className="btn" disabled={busy} style={{ color: 'var(--danger)' }} onClick={() => save('Blocked')}><Icon name="alert" size={15} />Block</button>}{portalInvite.portalTask && portalInvite.canSend ? <button className="btn btn-primary" disabled={busy} onClick={sendPortalInvite}><Icon name="mail" size={15} />{busy ? 'Sending...' : portalInvite.intakeOnly ? 'Send Intake Link' : 'Send Invite'}</button> : approvalNeedsIntake ? <button className="btn btn-primary" disabled={busy} onClick={openLinkedCustomer}><Icon name="customers" size={15} />Open Customer</button> : canApproveCustomer ? <button className="btn btn-primary" disabled={busy} onClick={approveCustomer}><Icon name="check" size={15} />{busy ? 'Approving...' : 'Approve Customer'}</button> : <button className="btn btn-primary" disabled={busy} onClick={() => save('Done')}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Mark Done'}</button>}</>)
    : <><button className="btn" disabled={busy} onClick={onClose}>Close</button><button className="btn" disabled={busy} onClick={() => save('In Progress')}><Icon name="refresh" size={15} />{busy ? 'Saving...' : 'Reopen'}</button></>;

  return (
    <Modal title={t.title} sub={`${t.id} - ${t.cat}`} onClose={onClose} width={560} footer={footerActions}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 11, padding: '13px 15px' }}><div className="eyebrow nowrap" style={{ marginBottom: 6 }}>Status</div><Pill status={status} /></div>
        <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 11, padding: '13px 15px' }}><div className="eyebrow nowrap" style={{ marginBottom: 6 }}>Priority</div><PriorityTag p={t.priority} /></div>
        <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 11, padding: '13px 15px' }}><div className="eyebrow nowrap" style={{ marginBottom: 6 }}>Due</div><span style={{ fontSize: 14, fontWeight: 700, color: 'var(--' + dm.tone + ')' }}>{t.due ? t.due.slice(5) : '—'}</span></div>
      </div>

      {supportTicket && (
        <div style={{ background: 'var(--surface-2)', borderRadius: 11, padding: '13px 15px', marginBottom: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 13 }}>
            <Row k="Ticket type" v={supportMeta.type} />
            <Row k="Submitted by" v={supportMeta.submittedBy || t.assigner || 'Unknown'} />
            <Row k="Source" v={supportMeta.source || '-'} />
            <Row k="Page" v={supportMeta.page || '-'} />
            <Row k="Customer" v={cust ? cust.name : (supportMeta.customer || '-')} />
            <Row k="Order" v={supportMeta.order || '-'} />
          </div>
          <div className="eyebrow nowrap" style={{ marginBottom: 7 }}>Submitted details</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{supportMeta.message || 'No details provided.'}</div>
          <SupportTicketAttachments attachments={t.supportAttachments} />
          <div style={{ display: 'flex', gap: 8, marginTop: 13, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => copySupportTicketCodexPrompt(t)}><Icon name="clipboard" size={14} />Copy Codex Prompt</button>
            {cust && <button className="btn btn-sm" onClick={openLinkedCustomer}><Icon name="customers" size={14} />Open Account</button>}
            {supportMeta.order && <button className="btn btn-sm" onClick={() => { onClose(); go('orders', { type: 'order', id: supportMeta.order }); }}><Icon name="orders" size={14} />Open Order</button>}
          </div>
        </div>
      )}
      {!supportTicket && t.note && <div style={{ background: 'var(--surface-2)', borderRadius: 11, padding: '13px 15px', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5, marginBottom: 18 }}>{t.note}</div>}
      {cust && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: '12px 14px', marginBottom: 18, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 11 }}>
            <Icon name={portalInvite.portalTask ? 'mail' : 'user'} size={16} style={{ color: 'var(--amber-deep)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{portalInvite.portalTask ? portalInvite.intakeOnly ? 'Retailer intake action' : 'Portal invite action' : 'Linked account action'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{portalInvite.portalTask ? `${portalInvite.intakeOnly ? 'Send secure intake access' : 'Send order portal access'} to ${portalInvite.buyerEmail || 'the buyer'}.` : `Open ${cust.name} to complete this task in context.`}</div>
            </div>
            <Pill status={cust.status || 'Account'} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" style={{ flex: 1 }} onClick={openLinkedCustomer}><Icon name="customers" size={14} />Open Account</button>
            {portalInvite.portalTask && <button className="btn btn-sm btn-primary" disabled={busy || !portalInvite.canSend} style={{ flex: 1, opacity: busy || !portalInvite.canSend ? 0.6 : 1 }} onClick={sendPortalInvite}><Icon name="mail" size={14} />{busy ? 'Sending...' : portalInvite.intakeOnly ? 'Send Intake Link' : 'Send Invite'}</button>}
          </div>
          {portalInvite.portalTask && portalInvite.reason && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 9, fontWeight: 700 }}>{portalInvite.reason}</div>}
        </div>
      )}
      {approvalTask && (
        <div style={{ background: approvalReady ? 'var(--ok-bg)' : 'var(--warn-bg)', border: '1px solid ' + (approvalReady ? 'var(--ok)' : 'var(--amber)'), borderRadius: 11, padding: '12px 14px', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Icon name={approvalReady ? 'check' : 'flag'} size={16} style={{ color: approvalReady ? 'var(--ok)' : 'var(--amber-deep)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{approvalReady ? 'Customer is already order-ready' : 'Customer approval request'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{approvalNeedsIntake ? 'Send the secure intake link from the customer account before approval.' : `${cust.name} is currently ${cust.status || 'Prospect'} with ${cust.credit || 'New'} credit.`}</div>
            </div>
            <Pill status={cust.status || 'Prospect'} />
          </div>
          {!approvalReady && !rpCanApproveCustomers(null, cust) && <div style={{ color: 'var(--amber-deep)', fontSize: 11.5, fontWeight: 700, marginTop: 9 }}>Open the linked account or ask Operations/Sales Manager to approve this customer.</div>}
        </div>
      )}
      {removalRequest && (
        <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--amber)', borderRadius: 11, padding: '12px 14px', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Icon name="alert" size={16} style={{ color: 'var(--amber-deep)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>Removal approval request</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                {removalRequest.action} {removalRequest.entityType || 'record'} {removalRequest.entityId || ''}{removalRequest.entityLabel ? ` - ${removalRequest.entityLabel}` : ''}
              </div>
            </div>
            <Pill status={status} />
          </div>
          {!canResolveRemoval && <div style={{ color: 'var(--amber-deep)', fontSize: 11.5, fontWeight: 700, marginTop: 9 }}>Admin, Leadership, Operations, or Sales Manager can approve or reject this request.</div>}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label className="field-label">Assignee</label>
          <select className="field" value={owner} onChange={e => reassign(e.target.value)} disabled={!assigneeOptions.length}>{!owner && <option value="">Unassigned</option>}{assigneeOptions.map(p => <option key={p.name} value={p.name}>{p.name} · {p.role}</option>)}</select>
        </div>
        <div>
          <label className="field-label">Move to status</label>
          <select className="field" value={status} onChange={e => save(e.target.value)}>{RP.TASK_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
        <Row k="Assigned by" v={t.assigner} />
        <Row k="Created" v={<span className="mono">{t.created}</span>} />
        {cust && <Row k="Linked account" v={<button className="btn btn-ghost btn-sm" style={{ color: 'var(--amber-deep)', padding: 0 }} onClick={openLinkedCustomer}>{cust.name}<Icon name="chevR" size={13} /></button>} />}
      </div>
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

/* ---- Assign a new task ---- */
function AssignTaskModal({ me, onClose, onAdded, go }) {
  const assigneeOptions = taskAssigneeOptions(me);
  const defaultOwner = assigneeOptions.find(p => p.name === me)?.name || (assigneeOptions[0] && assigneeOptions[0].name) || (taskLiveMode() ? '' : me);
  const [f, setF] = useState({ title: '', owner: defaultOwner, priority: 'Medium', cat: 'Sales', due: '2026-06-09', cust: '', note: '' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setTried(true);
    setError('');
    if (!f.title.trim() || busy) return;
    if (!f.owner) {
      setError('Invite or activate a Ranch Power staff profile before assigning a task.');
      return;
    }
    setBusy(true);
    try {
      const t = await persistTaskCreate({ title: f.title.trim(), ownerName: f.owner, assignerName: me, priority: f.priority, category: f.cat, dueDate: f.due, customerId: f.cust || undefined, note: f.note.trim() || undefined });
      toast(`Task ${t.id} assigned to ${t.owner.split(' ')[0]}`);
      onAdded && onAdded();
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not assign task.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Assign Task" sub="Create work for yourself or a teammate" onClose={onClose} width={560}
      footer={<><button className="btn" disabled={busy} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={busy} onClick={save}><Icon name="check" size={15} />{busy ? 'Assigning...' : 'Assign Task'}</button></>}>
      <MField label="What needs doing?" required>
        <input className="field" placeholder="e.g. Follow up with Superette on Mango promo" value={f.title} onChange={e => set('title', e.target.value)} style={reqStyle(tried && !f.title.trim())} />
        <ReqMsg show={tried && !f.title.trim()}>A task title is required</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Assign to" half><select className="field" value={f.owner} onChange={e => set('owner', e.target.value)} disabled={!assigneeOptions.length}>{!f.owner && <option value="">No active staff</option>}{assigneeOptions.map(p => <option key={p.name} value={p.name}>{p.name} · {p.role}</option>)}</select></MField>
        <MField label="Category" half><select className="field" value={f.cat} onChange={e => set('cat', e.target.value)}>{RP.TASK_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Priority" half><select className="field" value={f.priority} onChange={e => set('priority', e.target.value)}>{RP.TASK_PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></MField>
        <MField label="Due date" half><input className="field" type="date" value={f.due} onChange={e => set('due', e.target.value)} /></MField>
      </div>
      <MField label="Link to account (optional)"><select className="field" value={f.cust} onChange={e => set('cust', e.target.value)}><option value="">None</option>{RP.CUSTOMERS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></MField>
      <MField label="Notes"><textarea className="field" rows="2" placeholder="Context, links, what 'done' looks like…" value={f.note} onChange={e => set('note', e.target.value)} style={{ resize: 'none' }} /></MField>
      {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
    </Modal>
  );
}

Object.assign(window, { TasksView, TaskModal, AssignTaskModal, SupportTicketModal, SupportTicketForm, submitSupportTicket, PriorityTag, dueMeta, taskTodayIso, TODAY, isCustomerApprovalTask, taskRemovalRequest, taskCanResolveRemoval, taskIsPortalInviteTask, taskPortalInviteState, sendPortalInviteFromTask, approveCustomerFromTask, resolveRemovalFromTask });


/* ===== desktop_contacts.jsx ===== */
/* Shared website inquiries. The server owns access, assignment and change history. */

const CONTACT_INBOX_STATUSES = [
  { id: 'new', label: 'New', tone: 'info' },
  { id: 'in_progress', label: 'In progress', tone: 'warn' },
  { id: 'waiting', label: 'Waiting', tone: 'muted' },
  { id: 'resolved', label: 'Resolved', tone: 'ok' },
  { id: 'spam', label: 'Spam', tone: 'muted' }
];
const CONTACT_INBOX_PAGE_SIZE = 25;

function contactInboxStatus(status) {
  return CONTACT_INBOX_STATUSES.find(item => item.id === status) || { label: 'Unknown status', tone: 'muted' };
}

function contactInboxDate(value) {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime())
    ? date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'Time unavailable';
}

function contactInboxOwner(id, assignees) {
  if (!id) return 'Unassigned';
  const owner = assignees.find(person => person.id === id);
  return owner ? owner.name || owner.email : 'Former team member';
}

function contactInboxReplyLink(submission) {
  const email = String(submission.email || '').trim();
  if (!/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email)) return '';
  // Encode the address and subject separately so message content cannot add headers.
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(`Re: ${submission.topic || 'Your Ranch Power inquiry'}`)}`;
}

function contactInboxQuery(filters, userId) {
  const query = new URLSearchParams({
    status: filters.status,
    assignee: filters.assignee === 'mine' ? userId : filters.assignee,
    search: filters.search.trim(),
    page: String(filters.page),
    pageSize: String(CONTACT_INBOX_PAGE_SIZE)
  });
  return query.toString();
}

function ContactInboxStatus({ status }) {
  const spec = contactInboxStatus(status);
  return <span className={`contact-inbox-status contact-inbox-status-${spec.tone}`}>{spec.label}</span>;
}

function ContactInboxDetail({ id, assignees, userId, note, onNoteChange, onUpdated, onClose, refreshKey }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  const [notice, setNotice] = useState('');
  const requestRef = useRef(0);
  const savingRef = useRef(false);
  const reviewRef = useRef(false);
  const refreshRef = useRef(refreshKey);
  const mountedRef = useRef(true);
  const titleRef = useRef(null);

  async function load({ review = false, quiet = false } = {}) {
    if (savingRef.current || (reviewRef.current && !review)) return;
    const request = ++requestRef.current;
    if (!quiet) setLoading(true);
    try {
      const response = await rpApi(`/api/contact/detail?id=${encodeURIComponent(id)}`);
      if (!mountedRef.current || request !== requestRef.current) return;
      setRecord(response);
      setError('');
      if (review) { reviewRef.current = false; setNeedsReview(false); setNotice('Latest details loaded. Your unsaved note has been kept.'); }
    } catch (failure) {
      if (mountedRef.current && request === requestRef.current) setError(failure.message || 'Unable to load this inquiry.');
    } finally {
      if (mountedRef.current && request === requestRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    load();
    titleRef.current && titleRef.current.focus();
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, [id]);

  useEffect(() => {
    if (refreshRef.current === refreshKey) return;
    refreshRef.current = refreshKey;
    load({ quiet: true });
  }, [refreshKey]);

  async function update(patch) {
    if (savingRef.current || loading || needsReview || !record || !record.submission.updatedAt) return;
    savingRef.current = true;
    requestRef.current += 1;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await rpApi('/api/contact/update', {
        method: 'POST',
        body: JSON.stringify({ id, expectedUpdatedAt: record.submission.updatedAt, ...patch })
      });
      if (!mountedRef.current) return;
      setRecord(response);
      if (patch.note) onNoteChange('');
      setNotice(patch.note ? 'Team note added.' : patch.status === 'spam' ? 'Moved to Spam. You can restore this inquiry at any time.' : record.submission.status === 'spam' && patch.status === 'new' ? 'Restored to the inbox.' : 'Inquiry updated.');
      onUpdated();
    } catch (failure) {
      if (!mountedRef.current) return;
      const conflict = failure.status === 409;
      const uncertain = !failure.status || failure.status >= 500;
      reviewRef.current = conflict || uncertain;
      setNeedsReview(conflict || uncertain);
      setError(conflict
        ? 'Someone else updated this inquiry. Reload the latest details before making another change. Your note has been kept.'
        : uncertain
          ? 'The update could not be confirmed. Reload the latest details to check whether it saved. Your note has been kept.'
          : failure.message || 'Unable to update this inquiry.');
      if (conflict || uncertain) onUpdated();
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  }

  const submission = record && record.submission;
  const disabled = saving || loading || needsReview;
  const missingOwner = submission && submission.assignedTo && !assignees.some(person => person.id === submission.assignedTo);
  return (
    <section className="card contact-inbox-detail" aria-labelledby="contact-inbox-detail-title" aria-busy={loading || saving}>
      <div className="contact-inbox-detail-head">
        <h2 id="contact-inbox-detail-title" tabIndex={-1} ref={titleRef}>Inquiry details</h2>
        <button type="button" className="btn btn-sm" disabled={saving} onClick={onClose} aria-label="Close inquiry details"><Icon name="x" size={16} /></button>
      </div>
      {error && <div className="contact-inbox-alert" role="alert"><p>{error}</p><button type="button" className="btn btn-sm" disabled={loading || saving} onClick={() => load({ review: true })}>Reload latest details</button></div>}
      {notice && <p className="contact-inbox-notice" role="status">{notice}</p>}
      {loading && !submission && <p className="contact-inbox-empty" role="status">Loading inquiry...</p>}
      {submission && <>
        <div className="contact-inbox-sender">
          <ContactInboxStatus status={submission.status} />
          <h3>{submission.topic || 'Website inquiry'}</h3>
          <strong>{submission.name}</strong>
          <span>{submission.email}</span>
          <time dateTime={submission.createdAt}>Received {contactInboxDate(submission.createdAt)}</time>
        </div>
        <div className="contact-inbox-message">{submission.message}</div>
        <div className="contact-inbox-reply">
          {contactInboxReplyLink(submission) ? <><a className="btn btn-sm" href={contactInboxReplyLink(submission)}><Icon name="mail" size={15} />Reply by email</a><span>Opens your email app. Record the outcome here after replying.</span></> : <span>No reply email is available. Check the original submission for another way to respond.</span>}
        </div>
        <div className="contact-inbox-management">
          <label>Status<select aria-label="Status" value={submission.status} disabled={disabled} onChange={event => update({ status: event.target.value })}>{CONTACT_INBOX_STATUSES.map(status => <option key={status.id} value={status.id}>{status.label}</option>)}</select></label>
          <label>Assigned to<select aria-label="Assigned to" value={submission.assignedTo || ''} disabled={disabled} onChange={event => update({ assignedTo: event.target.value || null })}>
            <option value="">Unassigned</option>
            {missingOwner && <option value={submission.assignedTo} disabled>Former team member</option>}
            {assignees.map(person => <option key={person.id} value={person.id}>{person.name || person.email}{person.id === userId ? ' (you)' : ''}</option>)}
          </select></label>
          <div className="contact-inbox-management-actions">
            {userId && assignees.some(person => person.id === userId) && submission.assignedTo !== userId && <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => update({ assignedTo: userId })}>Assign to me</button>}
            {submission.status === 'spam' ? <button type="button" className="btn btn-sm btn-primary" disabled={disabled} onClick={() => update({ status: 'new' })}><Icon name="refresh" size={15} />Restore to inbox</button> : <>
              <button type="button" className="btn btn-sm btn-primary" disabled={disabled} onClick={() => update({ status: submission.status === 'resolved' ? 'new' : 'resolved' })}><Icon name={submission.status === 'resolved' ? 'refresh' : 'check'} size={15} />{submission.status === 'resolved' ? 'Reopen inquiry' : 'Mark resolved'}</button>
              <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => update({ status: 'spam' })}><Icon name="flag" size={15} />Mark as spam</button>
            </>}
          </div>
          <p className="contact-inbox-help">{submission.status === 'spam' ? 'Spam stays out of the open inbox. The message and its history are kept, and you can restore it if needed.' : submission.resolvedAt ? `Resolved ${contactInboxDate(submission.resolvedAt)}` : 'New: needs review. In progress: being handled. Waiting: awaiting a reply or next step.'}</p>
        </div>
        <form className="contact-inbox-note-form" onSubmit={event => { event.preventDefault(); if (note.trim()) update({ note: note.trim() }); }}>
          <label htmlFor="contact-inbox-note">Team note</label>
          <textarea id="contact-inbox-note" rows={3} maxLength={4000} value={note} disabled={saving} onChange={event => onNoteChange(event.target.value)} placeholder="Record your reply, a handoff, or the next step..." />
          <div><span>Internal only. Visible to the operations team.</span><button type="submit" className="btn btn-sm" disabled={disabled || !note.trim()}>{saving ? 'Saving...' : 'Add note'}</button></div>
        </form>
        <div className="contact-inbox-history">
          <h3>Activity</h3>
          <ol>{(record.activity || []).slice().reverse().map(item => <li key={item.id} className={item.kind === 'note' ? 'contact-inbox-activity-note' : ''}>
            <div><strong>{item.actorName || 'Website'}</strong><time dateTime={item.createdAt}>{contactInboxDate(item.createdAt)}</time></div>
            <p>{item.body}</p>
          </li>)}</ol>
          <p className="contact-inbox-help">Last updated {contactInboxDate(submission.updatedAt)}</p>
        </div>
      </>}
    </section>
  );
}

function ContactInboxView() {
  const userId = String(rpAuthProfile().id || '');
  const demo = rpIsDemoMode();
  const [filters, setFilters] = useState({ status: 'open', assignee: 'all', search: '', page: 1 });
  const [searchInput, setSearchInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(!demo);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [refresh, setRefresh] = useState(0);
  const [loadedAt, setLoadedAt] = useState(null);
  const requestRef = useRef(0);
  const listTitleRef = useRef(null);
  const query = contactInboxQuery(filters, userId);

  useEffect(() => {
    const timer = window.setTimeout(() => setFilters(current => current.search === searchInput ? current : { ...current, search: searchInput, page: 1 }), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (demo) return;
    const controller = new AbortController();
    const request = ++requestRef.current;
    setLoading(true);
    rpApi(`/api/contact/list?${query}`, { signal: controller.signal }).then(response => {
      if (request !== requestRef.current || controller.signal.aborted) return;
      const lastPage = Math.max(1, Math.ceil(response.total / (response.pageSize || CONTACT_INBOX_PAGE_SIZE)));
      if (filters.page > lastPage) { setFilters(current => ({ ...current, page: lastPage })); return; }
      setResult(response);
      setLoadedAt(new Date().toISOString());
      setError('');
    }).catch(failure => {
      if (request === requestRef.current && !controller.signal.aborted) setError(failure.message || 'Unable to load the shared inbox.');
    }).finally(() => {
      if (request === requestRef.current && !controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [query, refresh, demo]);

  useEffect(() => {
    if (demo) return;
    const reload = () => { if (!document.hidden) setRefresh(current => current + 1); };
    const timer = window.setInterval(reload, 30000);
    window.addEventListener('focus', reload);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', reload); };
  }, [demo]);

  function filter(patch) { setFilters(current => ({ ...current, ...patch, page: 1 })); }
  const rows = result && result.submissions || [];
  const assignees = result && result.assignees || [];
  const counts = result && result.counts;
  const total = result && result.total || 0;
  const pageSize = result && result.pageSize || CONTACT_INBOX_PAGE_SIZE;
  const displayedPage = result && result.page || filters.page;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="rp-fade contact-inbox-page">
      <PageHead eyebrow="Shared operations" title="Website Inbox" sub="Every inquiry, one shared owner and a clear next step." actions={<button type="button" className="btn btn-sm" disabled={loading || demo} onClick={() => setRefresh(current => current + 1)}><Icon name="refresh" size={15} />Refresh</button>} />
      {demo ? <LiveDataGuardNotice title="Sign in to see website inquiries.">The shared inbox uses live contact form submissions and your operations team accounts.</LiveDataGuardNotice> : <>
        <div className="contact-inbox-counts" aria-label="Shared inbox totals">
          {[{ label: 'Open inquiries', value: counts && counts.open, status: 'open', assignee: 'all' }, { label: 'Open & unassigned', value: counts && counts.unassigned, status: 'open', assignee: 'unassigned' }, { label: 'Resolved', value: counts && counts.resolved, status: 'resolved', assignee: 'all' }, { label: 'Spam', value: counts && counts.spam, status: 'spam', assignee: 'all' }].map(item => <button type="button" key={item.label} className="card" onClick={() => { setSearchInput(''); filter({ status: item.status, assignee: item.assignee, search: '' }); }}><span>{item.label}</span><strong>{item.value == null ? '\u2014' : item.value}</strong></button>)}
        </div>
        <div className="contact-inbox-toolbar">
          <label className="contact-inbox-search">Search inquiries<input type="search" value={searchInput} maxLength={200} onChange={event => setSearchInput(event.target.value)} placeholder="Name, email, topic or message" /></label>
          <label>Show<select aria-label="Show" value={filters.status} onChange={event => filter({ status: event.target.value })}><option value="open">All open</option>{CONTACT_INBOX_STATUSES.map(status => <option key={status.id} value={status.id}>{status.label}</option>)}<option value="all">All statuses</option></select></label>
          <label>Owner<select aria-label="Owner" value={filters.assignee} onChange={event => filter({ assignee: event.target.value })}><option value="all">Everyone</option><option value="unassigned">Unassigned</option>{userId && <option value="mine">Assigned to me</option>}{assignees.map(person => <option key={person.id} value={person.id}>{person.name || person.email}</option>)}</select></label>
        </div>
        {error && <div className="contact-inbox-alert" role="alert"><p>{error}{result ? ' The list below may be out of date.' : ''}</p><button type="button" className="btn btn-sm" disabled={loading} onClick={() => setRefresh(current => current + 1)}>Try again</button></div>}
        <div className={`contact-inbox-workspace${selectedId ? ' contact-inbox-has-detail' : ''}`}>
          <section className="card contact-inbox-list" aria-labelledby="contact-inbox-list-title" aria-busy={loading}>
            <div className="contact-inbox-list-head"><h2 id="contact-inbox-list-title" tabIndex={-1} ref={listTitleRef}>Inquiries <span>{result ? total : ''}</span></h2><span role="status">{loading ? 'Updating...' : loadedAt ? 'Refreshes every 30 seconds' : ''}</span></div>
            {!loading && !rows.length && !error && <div className="contact-inbox-empty"><Icon name="mail" size={30} /><h3>{filters.status === 'open' && filters.assignee === 'all' && !filters.search ? 'No open inquiries' : 'No matching inquiries'}</h3><p>{filters.search || filters.assignee !== 'all' || filters.status !== 'open' ? 'Try changing the search or filters.' : 'New website messages will appear here for the team to pick up.'}</p></div>}
            {loading && !result && <p className="contact-inbox-empty" role="status">Loading shared inquiries...</p>}
            <ul>{rows.map(submission => <li key={submission.id}><button type="button" className={`contact-inbox-row${selectedId === submission.id ? ' is-selected' : ''}`} aria-pressed={selectedId === submission.id} onClick={() => setSelectedId(submission.id)}>
              <div className="contact-inbox-row-top"><strong>{submission.name}</strong><ContactInboxStatus status={submission.status} /></div>
              <span className="contact-inbox-row-topic">{submission.topic || 'Website inquiry'}</span>
              <span className="contact-inbox-row-preview">{submission.message}</span>
              <div className="contact-inbox-row-bottom"><span className={!submission.assignedTo ? 'contact-inbox-unassigned' : ''}>{contactInboxOwner(submission.assignedTo, assignees)}</span><time dateTime={submission.createdAt}>{contactInboxDate(submission.createdAt)}</time></div>
            </button></li>)}</ul>
            {result && <div className="contact-inbox-pagination"><span>{total ? `${(displayedPage - 1) * pageSize + 1}\u2013${Math.min(displayedPage * pageSize, total)} of ${total}` : '0 inquiries'}</span><div><button type="button" className="btn btn-sm" disabled={loading || filters.page <= 1} onClick={() => setFilters(current => ({ ...current, page: current.page - 1 }))}>Previous</button><span>Page {displayedPage} of {pageCount}</span><button type="button" className="btn btn-sm" disabled={loading || filters.page >= pageCount} onClick={() => setFilters(current => ({ ...current, page: current.page + 1 }))}>Next</button></div></div>}
          </section>
          {selectedId && <ContactInboxDetail key={selectedId} id={selectedId} assignees={assignees} userId={userId} refreshKey={refresh} note={noteDrafts[selectedId] || ''} onNoteChange={value => setNoteDrafts(current => ({ ...current, [selectedId]: value }))} onUpdated={() => setRefresh(current => current + 1)} onClose={() => { setSelectedId(null); listTitleRef.current && listTitleRef.current.focus(); }} />}
        </div>
      </>}
    </div>
  );
}


/* ===== desktop_goals.jsx ===== */
/* ============================================================
   RANCH POWER - Desktop : Sales Goals
   Management sets targets per team member per period; tracked to pace.
   ============================================================ */

/* progress bar with a "pace" marker showing where the team member should be today */
function GoalBar({ actual, target, elapsed, color }) {
  const pct = Math.min(100, target > 0 ? (actual / target) * 100 : 0);
  const pace = Math.min(100, elapsed * 100);
  return (
    <div style={{ position: 'relative', height: 9, background: 'var(--surface-2)', borderRadius: 100, overflow: 'visible' }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: 100, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: 100, transition: 'width .5s ease' }} />
      </div>
      <div title={'On-pace target today'} style={{ position: 'absolute', left: pace + '%', top: -3, bottom: -3, width: 2, background: 'var(--ink)', opacity: 0.55, borderRadius: 2 }} />
    </div>
  );
}

function GoalMetricRow({ icon, label, actual, target, fmt, elapsed }) {
  const st = RP.goalStatus(actual, target, elapsed);
  const color = st === 'Behind' ? 'var(--danger)' : st === 'At Risk' ? 'var(--warn)' : st === 'Ahead' ? 'var(--info)' : 'var(--ok)';
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--ink-soft)' }}><Icon name={icon} size={14} style={{ color: 'var(--muted)' }} />{label}</span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{fmt(actual)}</span>
          <span className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>/ {fmt(target)}</span>
          <span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color, width: 38, textAlign: 'right' }}>{pct}%</span>
        </span>
      </div>
      <GoalBar actual={actual} target={target} elapsed={elapsed} color={color} />
    </div>
  );
}

const SALES_GOAL_EDIT_ROLES = ['admin', 'leadership', 'ops', 'sales_manager'];
const SALES_GOAL_ASSIGNEE_ROLES = new Set(['admin', 'leadership', 'ops', 'sales_manager', 'rep', 'partner']);
const SALES_GOAL_COMPANY_SCOPE_ROLES = new Set(['admin', 'leadership', 'ops', 'sales_manager']);

function salesGoalCanEdit(leadership) {
  if (!rpHasLiveAuth()) return !!leadership;
  return SALES_GOAL_EDIT_ROLES.includes(rpNormalizeRole(rpAuthProfile().role));
}

function salesGoalMonthName(index) {
  return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][index] || 'June';
}

function salesGoalDefaultPeriod() {
  const d = new Date();
  return `${salesGoalMonthName(d.getMonth())} ${d.getFullYear()}`;
}

function salesGoalQuarterPeriod() {
  const d = new Date();
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
}

function salesGoalPeriodOptions(goals, current) {
  const values = new Set([current || salesGoalDefaultPeriod(), salesGoalDefaultPeriod(), salesGoalQuarterPeriod()]);
  (goals || []).forEach(g => { if (g && g.period) values.add(g.period); });
  return Array.from(values).map(value => {
    const month = String(value).match(/^([A-Za-z]+)\s+\d{4}$/);
    return { value, label: month ? month[1].slice(0, 3) : value };
  });
}

function salesGoalAssigneeScope(user) {
  const role = rpNormalizeRole(user && user.role);
  if (user && user.territory) return user.territory;
  return SALES_GOAL_COMPANY_SCOPE_ROLES.has(role) ? 'Ranch Power' : 'Unassigned';
}

function salesGoalRepOptions() {
  if (rpHasLiveAuth()) {
    const options = new Map();
    const add = user => {
      if (!user || user.active === false) return;
      const role = rpNormalizeRole(user.role);
      if (!SALES_GOAL_ASSIGNEE_ROLES.has(role)) return;
      const id = user.id || '';
      const name = user.name || user.display_name || user.email || id || 'Team member';
      const key = id || name;
      if (!key) return;
      options.set(key, {
        id,
        name,
        role,
        roleLabel: rpRoleLabel(role),
        territory: salesGoalAssigneeScope(user)
      });
    };
    (RP.USER_PROFILES || []).forEach(add);
    add(rpAuthProfile());
    return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name));
  }
  return (RP.TEAM || [])
    .filter(p => ['Sales Rep', 'Operations', 'Leadership', 'Administrator', 'Admin'].includes(p.role))
    .map(p => ({ id: p.name, name: p.name, roleLabel: p.role, territory: p.area || 'Unassigned' }));
}

function salesGoalRepByForm(reps, id, name) {
  return reps.find(rep => rep.id === id) || reps.find(rep => rep.name === name) || reps[0] || { id: '', name: name || '', territory: '' };
}

function SalesGoalsView({ go, role, leadership }) {
  const me = rpFieldUserName(role);
  const [period, setPeriod] = useState(salesGoalDefaultPeriod());
  const [modal, setModal] = useState(null); // {rep} for edit, true for new
  const [, force] = useState(0);
  const bump = () => force(x => x + 1);
  const elapsed = RP.PERIOD_ELAPSED[period] || 0.17;
  const canEdit = salesGoalCanEdit(leadership);
  const allGoals = (RP.SALES_GOALS || []).filter(g => g && g.active !== false);
  const goals = allGoals.filter(g => g.period === period);
  const periodOptions = salesGoalPeriodOptions(allGoals, period);

  const teamRevTarget = goals.reduce((s, g) => s + g.revTarget, 0);
  const teamRevActual = goals.reduce((s, g) => s + g.revActual, 0);
  const attainment = teamRevTarget > 0 ? Math.round(teamRevActual / teamRevTarget * 100) : 0;
  const behind = goals.filter(g => RP.goalOverall(g, elapsed) === 'Behind').length;
  const money = RP.money;

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {modal && <SetGoalModal preset={modal === true ? null : modal} period={period} me={me} onClose={() => setModal(null)} onSaved={bump} />}
      <PageHead eyebrow="Targets & Attainment" title="Sales Goals"
        sub="Set targets for each team member and track attainment to pace through the period."
        actions={<>
          <Segmented size="sm" options={periodOptions} value={period} onChange={setPeriod} />
          {canEdit && <button className="btn btn-sm btn-primary" onClick={() => setModal(true)}><Icon name="plus" size={15} />Set Goal</button>}
        </>} />

      {!canEdit && <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--info-bg)', border: '1px solid oklch(0.56 0.1 245 / 0.3)', color: 'var(--info)', padding: '10px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13 }}><Icon name="target" size={16} />Goals are set by sales management and leadership. Team members see relevant targets in their app views.</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 22 }}>
        <KPICard label="Team Revenue Target" value={money(teamRevTarget)} icon="target" accent="var(--navy)" />
        <KPICard label="Booked to Date" value={money(teamRevActual)} icon="trade" accent="var(--ok)" />
        <KPICard label="Attainment" value={attainment} unit={`% - pace ${Math.round(elapsed * 100)}%`} icon="analytics" accent={attainment >= elapsed * 100 ? 'var(--ok)' : 'var(--warn)'} />
        <KPICard label="Goals Behind Pace" value={behind} unit={`of ${goals.length}`} icon="alert" accent={behind ? 'var(--danger)' : 'var(--ok)'} />
      </div>

      {goals.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--faint)' }}>
          No goals set for {period}.{canEdit && <div style={{ marginTop: 12 }}><button className="btn btn-sm btn-primary" onClick={() => setModal(true)}><Icon name="plus" size={14} />Set a Goal</button></div>}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 16 }}>
          {goals.map(g => {
            const overall = RP.goalOverall(g, elapsed);
            return (
              <div key={g.id} className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar name={g.rep} size={42} color="var(--navy)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15.5, color: 'var(--ink)' }}>{g.rep}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.territory}</div>
                  </div>
                  <Pill status={overall} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                  <GoalMetricRow icon="trade" label="Revenue" actual={g.revActual} target={g.revTarget} fmt={money} elapsed={elapsed} />
                  <GoalMetricRow icon="package" label="Cases" actual={g.casesActual} target={g.casesTarget} fmt={v => v.toLocaleString()} elapsed={elapsed} />
                  <GoalMetricRow icon="customers" label="New Doors" actual={g.doorsActual} target={g.doorsTarget} fmt={v => v} elapsed={elapsed} />
                </div>
                {g.note && <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45, background: 'var(--surface-2)', borderRadius: 9, padding: '10px 12px' }}>{g.note}</div>}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--line-2)', paddingTop: 12 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>Set by {g.setBy}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--amber-deep)' }} onClick={() => go && go('analytics')}>Territory<Icon name="chevR" size={13} /></button>
                    {canEdit && <button className="btn btn-sm" onClick={() => setModal(g)}><Icon name="pen" size={13} />Edit</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card" style={{ marginTop: 18, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}><SectionTitle>Attainment Leaderboard - {period}</SectionTitle></div>
        <table className="rp-table">
          <thead><tr><th>Team Member</th><th>Scope</th><th className="num">Rev Target</th><th className="num">Booked</th><th>Attainment</th><th>Status</th></tr></thead>
          <tbody>
            {[...goals].sort((a, b) => ((b.revTarget ? b.revActual / b.revTarget : 0) - (a.revTarget ? a.revActual / a.revTarget : 0))).map(g => {
              const pct = g.revTarget > 0 ? Math.round(g.revActual / g.revTarget * 100) : 0;
              return (
                <tr key={g.id}>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Avatar name={g.rep} size={26} color="var(--navy)" /><span style={{ fontWeight: 600 }}>{g.rep}</span></div></td>
                  <td style={{ color: 'var(--muted)' }}>{g.territory}</td>
                  <td className="num">{money(g.revTarget)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{money(g.revActual)}</td>
                  <td style={{ minWidth: 150 }}><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><div style={{ flex: 1, maxWidth: 100 }}><Bar value={pct} max={100} color={pct >= elapsed * 100 ? 'var(--ok)' : 'var(--warn)'} /></div><span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{pct}%</span></div></td>
                  <td><Pill status={RP.goalOverall(g, elapsed)} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- Set / edit a team member's goal ---- */
function SetGoalModal({ preset, period, me, onClose, onSaved }) {
  const live = rpHasLiveAuth();
  const reps = salesGoalRepOptions();
  const defaultRep = preset
    ? salesGoalRepByForm(reps, preset.repId || preset.rep, preset.rep)
    : salesGoalRepByForm(reps, reps[0] && reps[0].id, reps[0] && reps[0].name);
  const [f, setF] = useState(() => preset ? {
    repId: preset.repId || defaultRep.id, rep: preset.rep, period: preset.period, revTarget: String(preset.revTarget), casesTarget: String(preset.casesTarget), doorsTarget: String(preset.doorsTarget), note: preset.note || '',
  } : { repId: defaultRep.id, rep: defaultRep.name, period, revTarget: '', casesTarget: '', doorsTarget: '', note: '' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rev = parseInt(f.revTarget, 10), cases = parseInt(f.casesTarget, 10), doors = parseInt(f.doorsTarget, 10);
  const valid = rev > 0 && !!f.rep && !!f.period && (!live || !!f.repId);
  const repObj = salesGoalRepByForm(reps, f.repId, f.rep);
  const save = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;

    const localGoal = {
      active: true,
      id: preset && preset.id,
      rep: f.rep,
      repId: f.repId,
      territory: repObj.territory,
      period: f.period,
      setBy: me,
      revTarget: rev,
      casesTarget: cases || 0,
      doorsTarget: doors || 0,
      note: f.note.trim() || undefined,
      revActual: preset ? preset.revActual : 0,
      casesActual: preset ? preset.casesActual : 0,
      doorsActual: preset ? preset.doorsActual : 0
    };

    try {
      setBusy(true);
      let g;
      if (live) {
        const response = await rpApi('/api/sales-goals/upsert', {
          method: 'POST',
          body: JSON.stringify({
            active: true,
            casesTarget: localGoal.casesTarget,
            doorsTarget: localGoal.doorsTarget,
            id: preset && preset.id,
            note: localGoal.note || '',
            period: localGoal.period,
            repId: localGoal.repId,
            repName: localGoal.rep,
            revTarget: localGoal.revTarget,
            territory: localGoal.territory
          })
        });
        g = response.salesGoal || localGoal;
        if (RP.upsertGoal) RP.upsertGoal(g);
        await rpRefreshAfterMutation(`Goal ${preset ? 'updated' : 'set'} for ${g.rep.split(' ')[0]}`);
      } else {
        g = RP.addGoal(localGoal);
      }
      toast(`Goal ${preset ? 'updated' : 'set'} for ${g.rep.split(' ')[0]} - ${f.period}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save sales goal.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={preset ? 'Edit Goal' : 'Set Sales Goal'} sub={`${f.period} target`} onClose={onClose} width={540}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy} style={{ opacity: valid && !busy ? 1 : 0.5 }}><Icon name="target" size={15} />{busy ? 'Saving...' : preset ? 'Update Goal' : 'Set Goal'}</button></>}>
      {live && reps.length === 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--warn-bg)', color: 'var(--warn)', border: '1px solid oklch(0.78 0.14 75 / 0.35)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12.5, fontWeight: 600 }}><Icon name="alert" size={15} />Invite or activate eligible staff profiles before setting live goals.</div>}
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Team Member" half><select className="field" value={f.repId || f.rep} onChange={e => {
          const rep = salesGoalRepByForm(reps, e.target.value, e.target.value);
          setF(s => ({ ...s, repId: rep.id, rep: rep.name }));
        }} disabled={!!preset || reps.length === 0}>{reps.map(p => <option key={p.id || p.name} value={p.id || p.name}>{p.name}{p.roleLabel ? ` - ${p.roleLabel}` : ''}</option>)}</select></MField>
        <MField label="Period" half><select className="field" value={f.period} onChange={e => set('period', e.target.value)} disabled={!!preset}>{salesGoalPeriodOptions(RP.SALES_GOALS || [], f.period).map(p => <option key={p.value} value={p.value}>{p.value}</option>)}</select></MField>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface-2)', borderRadius: 11, padding: '11px 14px', marginBottom: 16, fontSize: 12.5, color: 'var(--muted)' }}>
        <Icon name="pin" size={15} />Scope: <b style={{ color: 'var(--ink)' }}>{repObj.territory || 'Unassigned'}</b>
      </div>
      <MField label="Revenue Target" required>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--muted)', fontWeight: 600 }}>$</span>
          <input className="field" style={Object.assign({ paddingLeft: 26 }, reqStyle(tried && !valid))} type="number" placeholder="e.g. 16000" value={f.revTarget} onChange={e => set('revTarget', e.target.value)} />
        </div>
        <ReqMsg show={tried && !valid}>Select a team member and enter a revenue target</ReqMsg>
      </MField>
      <div style={{ display: 'flex', gap: 14 }}>
        <MField label="Cases Target" half><input className="field" type="number" placeholder="e.g. 480" value={f.casesTarget} onChange={e => set('casesTarget', e.target.value)} /></MField>
        <MField label="New Doors Target" half><input className="field" type="number" placeholder="e.g. 3" value={f.doorsTarget} onChange={e => set('doorsTarget', e.target.value)} /></MField>
      </div>
      <MField label="Coaching note"><textarea className="field" rows="2" placeholder="Where to focus this period..." value={f.note} onChange={e => set('note', e.target.value)} style={{ resize: 'none' }} /></MField>
      {error && <ReqMsg show>{error}</ReqMsg>}
    </Modal>
  );
}

Object.assign(window, { SalesGoalsView, SetGoalModal, GoalBar, GoalMetricRow });


/* ===== desktop_expenses.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Expenses & Accounting
   Field/staff submissions → accountant sets tax code + GL,
   approves and reimburses. Posts to the books.
   ============================================================ */

const EXP_TABS = [['review', 'To Review'], ['approved', 'Approved'], ['reimbursed', 'Reimbursed'], ['all', 'All']];

function ExpensesView({ go, reportsEnabled }) {
  const [tab, setTab] = useState('review');
  const [search, setSearch] = useState('');
  const [filters, onFilter] = useFilters();
  const [sel, setSel] = useState(null);
  const [, force] = useState(0);
  const bump = () => force(x => x + 1);

  const all = RP.EXPENSES;
  const defs = [
    { label: 'Person', options: distinct(all, e => e.who) },
    { label: 'Category', options: distinct(all, e => e.cat) },
    { label: 'Status', options: RP.EXP_STATUSES },
  ];
  let rows = all;
  if (tab === 'review') rows = rows.filter(e => e.status === 'Submitted' || e.status === 'Needs Info');
  if (tab === 'approved') rows = rows.filter(e => e.status === 'Approved');
  if (tab === 'reimbursed') rows = rows.filter(e => e.status === 'Reimbursed');
  if (filters.Person) rows = rows.filter(e => e.who === filters.Person);
  if (filters.Category) rows = rows.filter(e => e.cat === filters.Category);
  if (filters.Status) rows = rows.filter(e => e.status === filters.Status);
  if (search) rows = rows.filter(e => (e.id + e.who + e.merchant + e.cat).toLowerCase().includes(search.toLowerCase()));

  const toReview = all.filter(e => e.status === 'Submitted' || e.status === 'Needs Info');
  const pendingAmt = toReview.reduce((s, e) => s + e.amount, 0);
  const approvedAmt = all.filter(e => e.status === 'Approved').reduce((s, e) => s + e.amount, 0);
  const reimbursedAmt = all.filter(e => e.status === 'Reimbursed').reduce((s, e) => s + e.amount, 0);
  const itc = all.filter(e => e.status === 'Approved' || e.status === 'Reimbursed').reduce((s, e) => s + RP.expTax(e), 0);

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px' }}>
      {sel && <ExpenseReviewModal id={sel} onClose={() => setSel(null)} onChange={bump} go={go} />}
      <PageHead eyebrow="Accounting · Operations" title="Expenses"
        sub="Staff & field submissions land here. Set tax codes and GL accounts, then approve to post."
        actions={reportsEnabled ? <button className="btn btn-sm" onClick={() => go('reports', { type: 'report', id: 'expenses', filters: { ...(filters.Status ? { status: filters.Status } : {}), ...(filters.Category ? { category: filters.Category } : {}), ...(search ? { search } : {}) } })}><Icon name="download" size={15} />Export Expenses</button> : null} />

      {typeof rpHasLiveAuth === 'function' && rpHasLiveAuth() && <div style={{ border: '1px solid var(--line)', borderRadius: 9, padding: '12px 14px', marginBottom: 18, fontSize: 13, lineHeight: 1.5, color: 'var(--muted)' }}>This review inbox contains the most recent {all.length} loaded expenses, up to 250. Its counts and totals cover those records only. {deskCanUseNav('accounting', deskActiveRole()) ? <button className="btn btn-sm" onClick={() => go('accounting')}>Open Accounting for complete period records</button> : 'Use Reports for a complete period export.'}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 22 }}>
        <KPICard label="Awaiting Review" value={RP.money(pendingAmt)} unit={`· ${toReview.length} items`} icon="receipt" accent="var(--amber)" onClick={() => setTab('review')} active={tab === 'review'} />
        <KPICard label="Approved · Unpaid" value={RP.money(approvedAmt)} icon="check" accent="var(--info)" onClick={() => setTab('approved')} active={tab === 'approved'} />
        <KPICard label="Reimbursed · MTD" value={RP.money(reimbursedAmt)} icon="trade" accent="var(--ok)" onClick={() => setTab('reimbursed')} active={tab === 'reimbursed'} />
        <KPICard label="Recoverable Tax (ITC)" value={RP.money2(itc)} icon="analytics" accent="var(--navy)" />
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {EXP_TABS.map(([k, l]) => {
          const n = k === 'review' ? toReview.length : k === 'approved' ? all.filter(e => e.status === 'Approved').length : k === 'reimbursed' ? all.filter(e => e.status === 'Reimbursed').length : all.length;
          return <button key={k} onClick={() => setTab(k)} style={{ border: '1px solid ' + (tab === k ? 'var(--navy)' : 'var(--line)'), background: tab === k ? 'var(--navy)' : 'var(--surface)', color: tab === k ? '#fff' : 'var(--ink-soft)', borderRadius: 8, padding: '7px 13px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>{l}<span style={{ fontSize: 11, opacity: 0.7 }}>{n}</span></button>;
        })}
      </div>
      <FilterBar defs={defs} value={filters} onChange={onFilter} search={search} onSearch={setSearch} searchPlaceholder="Search expense, person, merchant…" />

      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="rp-table">
          <thead><tr><th>Expense</th><th>Submitted By</th><th>Date</th><th>Category</th><th className="num">Amount</th><th>Tax Code</th><th>GL Account</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(e => {
              const c = RP.EXP_CATS[e.cat] || {};
              return (
                <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => setSel(e.id)}>
                  <td><span className="mono" style={{ fontWeight: 600, color: 'var(--ink)' }}>{e.id}</span>{e.flag && <Icon name="alert" size={13} style={{ color: 'var(--danger)', marginLeft: 6, verticalAlign: 'middle' }} />}{!e.receipt && <Icon name="camera" size={13} style={{ color: 'var(--faint)', marginLeft: 6, verticalAlign: 'middle' }} />}</td>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Avatar name={e.who} size={26} color="var(--navy)" /><div><div style={{ fontWeight: 500, fontSize: 12.5 }}>{e.who}</div><div style={{ fontSize: 11, color: 'var(--faint)' }}>{e.role}</div></div></div></td>
                  <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{e.date}</td>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Icon name={c.icon || 'box'} size={14} style={{ color: 'var(--muted)' }} />{e.cat}</span></td>
                  <td className="num" style={{ fontWeight: 600 }}>{RP.money2(e.amount)}</td>
                  <td>{e.taxCode ? <span className="mono" style={{ fontSize: 11.5 }}>{e.taxCode}</span> : <span style={{ fontSize: 11.5, color: 'var(--amber-deep)', fontWeight: 600 }}>Set code</span>}</td>
                  <td style={{ color: e.gl ? 'var(--muted)' : 'var(--faint)', fontSize: 12 }}>{e.gl ? e.gl.split(' · ')[0] : '—'}</td>
                  <td><Pill status={e.status} /></td>
                  <td><Icon name="chevR" size={16} style={{ color: 'var(--faint)' }} /></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan="9" style={{ textAlign: 'center', color: 'var(--faint)', padding: 30 }}>Nothing here — inbox clear.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- Accountant review: tax code + GL + approve/reimburse ---- */
function ExpenseReviewModal({ id, onClose, onChange, go }) {
  const e = RP.expById(id);
  const cat = RP.EXP_CATS[e.cat] || {};
  const [status, setStatus] = useState(e.status);
  const [taxCode, setTaxCode] = useState(e.taxCode || cat.tax || RP.TAX_CODES[0]);
  const [gl, setGl] = useState(e.gl || cat.gl || RP.EXP_GL[0]);
  const [reimb, setReimb] = useState(e.reimbursable !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const c = e.cust ? RP.cust(e.cust) : null;
  const taxRate = { 'GST 5%': 0.05, 'GST + PST 12%': 0.12, 'PST 7%': 0.07, 'HST 13%': 0.13, 'Meals (50% GST)': 0.025 }[taxCode] || 0;
  const taxAmt = taxRate ? e.amount - e.amount / (1 + taxRate) : 0;
  const net = e.amount - taxAmt;

  const post = async (newStatus, msg, tone) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      let reviewed = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/expenses/review', {
          method: 'POST',
          body: JSON.stringify({
            gl,
            id,
            reimbursable: reimb,
            status: newStatus,
            taxCode
          })
        });
        reviewed = response.expense;
      }
      const expense = reviewed
        ? RP.upsertExpense(reviewed)
        : RP.reviewExpense(id, { status: newStatus, taxCode, gl, reimbursable: reimb, by: 'Accounting' });
      if (reviewed) await rpRefreshAfterMutation(`Expense ${expense.id} updated`);
      setStatus(newStatus); toast(msg, tone); onChange();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update expense.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={e.id} sub={`${e.who} · ${e.cat}`} onClose={onClose} width={600}
      footer={<>
        {status === 'Submitted' || status === 'Needs Info'
          ? <><button className="btn" disabled={busy} style={{ color: 'var(--danger)', marginRight: 'auto' }} onClick={() => post('Rejected', `${e.id} rejected`, 'danger')}><Icon name="x" size={15} />Reject</button>
            <button className="btn" disabled={busy} onClick={() => post('Needs Info', `Info requested from ${e.who.split(' ')[0]}`, 'warn')}><Icon name="mail" size={15} />Request Info</button>
            <button className="btn btn-primary" disabled={busy} onClick={() => post('Approved', `${e.id} approved · coded ${taxCode}`)}><Icon name="check" size={15} />{busy ? 'Saving...' : 'Approve & Code'}</button></>
          : status === 'Approved'
            ? <><button className="btn" disabled={busy} onClick={onClose}>Close</button><button className="btn btn-primary" disabled={busy} onClick={() => post('Reimbursed', `${e.id} marked reimbursed to ${e.who.split(' ')[0]}`)}><Icon name="trade" size={15} />{busy ? 'Saving...' : 'Mark Reimbursed'}</button></>
            : <button className="btn btn-primary" onClick={onClose}>Done</button>}
      </>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 18 }}>
        {/* Receipt */}
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Receipt</div>
          {e.receipt ? (
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)', background: 'var(--surface-2)' }}>
              <div style={{ height: 200, background: 'repeating-linear-gradient(45deg, var(--surface-2), var(--surface-2) 10px, var(--surface) 10px, var(--surface) 20px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--faint)', gap: 8 }}>
                <Icon name="receipt" size={30} /><span className="mono" style={{ fontSize: 11 }}>{e.id}-receipt.jpg</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 12px', borderTop: '1px solid var(--line)' }}><span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{e.merchant}</span><button className="btn btn-ghost btn-sm" style={{ padding: 0, color: e.receiptUrl ? 'var(--amber-deep)' : 'var(--faint)' }} onClick={async () => { if (!e.receiptUrl) { toast('Receipt file was not uploaded for this older record', 'warn'); return; } try { await rpOpenProtectedFile(e.receiptUrl); } catch (err) { setError(err?.message || 'Could not open receipt.'); } }}>View</button></div>
            </div>
          ) : (
            <div style={{ borderRadius: 12, border: '1.5px dashed var(--danger)', background: 'var(--danger-bg)', padding: '28px 16px', textAlign: 'center', color: 'var(--danger)' }}><Icon name="camera" size={26} /><div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 8 }}>No receipt attached</div></div>
          )}
          {e.note && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 12, lineHeight: 1.45 }}>{e.note}</div>}
        </div>

        {/* Coding */}
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
            <span className="disp" style={{ fontSize: 34 }}>{RP.money2(e.amount)}</span>
            <Pill status={status} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 12.5 }}>
            <Row k="Date" v={<span className="mono">{e.date}</span>} />
            <Row k="Submitted by" v={e.who} />
            {c && <Row k="Account" v={c.name} />}
            <Row k="Reimbursable" v={reimb ? 'Yes' : 'Company card'} />
          </div>

          <MField label="Tax Code">
            <select className="field" value={taxCode} onChange={ev => setTaxCode(ev.target.value)}>{RP.TAX_CODES.map(t => <option key={t}>{t}</option>)}</select>
          </MField>
          <MField label="GL Account">
            <select className="field" value={gl} onChange={ev => setGl(ev.target.value)}>{RP.EXP_GL.map(g => <option key={g}>{g}</option>)}</select>
          </MField>

          <div style={{ background: 'var(--surface-2)', borderRadius: 11, padding: '12px 14px', marginTop: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}><span style={{ color: 'var(--muted)' }}>Net (pre-tax)</span><span className="mono" style={{ fontWeight: 600 }}>{RP.money2(net)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}><span style={{ color: 'var(--muted)' }}>Recoverable tax (ITC)</span><span className="mono" style={{ fontWeight: 600, color: 'var(--ok)' }}>{RP.money2(taxAmt)}</span></div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, cursor: 'pointer' }}>
            <button onClick={() => setReimb(r => !r)} style={{ width: 42, height: 24, borderRadius: 100, border: 'none', background: reimb ? 'var(--ok)' : 'var(--line)', position: 'relative', flexShrink: 0 }}><span style={{ position: 'absolute', top: 3, left: reimb ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 2px rgba(0,0,0,.3)' }} /></button>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Reimburse to employee</span>
          </label>
          {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
        </div>
      </div>
    </Modal>
  );
}

Object.assign(window, { ExpensesView, ExpenseReviewModal });


/* ===== desktop_locator.jsx ===== */
/* ============================================================
   RANCH POWER — Desktop : Public Retail Locator
   Customer address + map pin → "mark as public retailer" pushes
   the door to the public store-locator on ranchpower.com.
   ============================================================ */

/* project BC lat/lng into a 0–100 box for the abstract map */
const GEO_BOX = { minLat: 49.4, maxLat: 54.2, minLng: -123.6, maxLng: -119.0 };
const LOCATOR_GROUP_TOLERANCE_METERS = 35;
function locatorCoord(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && !(parsed === 0) ? parsed : null;
}
function locatorCoordLabel(value) {
  const coord = locatorCoord(value);
  return coord == null ? '—' : coord.toFixed(6);
}
function geoXY(lat, lng) {
  const x = ((lng - GEO_BOX.minLng) / (GEO_BOX.maxLng - GEO_BOX.minLng)) * 100;
  const y = ((GEO_BOX.maxLat - lat) / (GEO_BOX.maxLat - GEO_BOX.minLat)) * 100;
  return [Math.max(4, Math.min(96, x)), Math.max(6, Math.min(94, y))];
}
function locatorDistanceMeters(a, b) {
  const lat1 = locatorCoord(a && a.lat);
  const lng1 = locatorCoord(a && a.lng);
  const lat2 = locatorCoord(b && b.lat);
  const lng2 = locatorCoord(b && b.lng);
  if ([lat1, lng1, lat2, lng2].some(value => value == null)) return Infinity;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const rLat1 = lat1 * Math.PI / 180;
  const rLat2 = lat2 * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}
function locatorGroupedPins(pins, toleranceMeters = LOCATOR_GROUP_TOLERANCE_METERS) {
  const groups = [];
  (Array.isArray(pins) ? pins : [])
    .map(pin => ({ ...pin, lat: locatorCoord(pin && pin.lat), lng: locatorCoord(pin && pin.lng) }))
    .filter(pin => pin.lat != null && pin.lng != null)
    .forEach(pin => {
      const group = groups.find(item => locatorDistanceMeters(item, pin) <= toleranceMeters);
      if (group) {
        group.pins.push(pin);
        group.lat = group.pins.reduce((sum, item) => sum + item.lat, 0) / group.pins.length;
        group.lng = group.pins.reduce((sum, item) => sum + item.lng, 0) / group.pins.length;
        group.public = group.public || pin.public;
      } else {
        groups.push({ ...pin, pins: [pin] });
      }
    });
  return groups.map((group, index) => group.pins.length === 1
    ? group.pins[0]
    : {
        id: `locator-group-${index}`,
        name: group.pins.map(pin => pin.name).join(', '),
        lat: group.lat,
        lng: group.lng,
        public: group.public,
        count: group.pins.length,
        pins: group.pins
      });
}
function customerLocatorPins(customers) {
  return (Array.isArray(customers) ? customers : []).flatMap(c => {
    const locations = (Array.isArray(c.locations) ? c.locations : [])
      .filter(location => locatorCoord(location && location.lat) != null && locatorCoord(location && location.lng) != null);
    const source = locations.length ? locations : [c];
    return source.map((location, index) => ({
      id: `${c.id}:${location.id || index}`,
      customerId: c.id,
      name: index === 0 ? c.name : `${c.name} - ${location.name || 'Location'}`,
      lat: location.lat ?? c.lat,
      lng: location.lng ?? c.lng,
      public: c.public,
      address: location.address || c.address,
      city: location.city || c.city
    }));
  });
}
function locatorPublicEligibleCustomer(c) {
  if (!c) return false;
  if (String(c.type || '').trim().toLowerCase() === 'distributor') return false;
  if (typeof rpIsConsumerCustomer === 'function') return !rpIsConsumerCustomer(c);
  const text = [c.type, c.tier].map(value => String(value || '').toLowerCase()).join(' ');
  return !(/\bconsumer\b|direct/.test(text));
}

/* ---- Mini map used on the customer detail + locator ---- */
function PinMap({ pins, height = 230, active, legend = true }) {
  const displayPins = locatorGroupedPins(pins);
  return (
    <div style={{ position: 'relative', height, borderRadius: 12, overflow: 'hidden', background: 'var(--navy-900)', border: '1px solid var(--line)' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(oklch(1 0 0 / 0.05) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <path d="M28,12 C22,24 30,30 24,44 C20,56 30,66 26,80 C40,86 46,74 60,78 C72,82 70,64 80,58 C90,52 82,38 84,26 C72,20 64,28 52,22 C42,17 38,8 28,12 Z"
          fill="oklch(1 0 0 / 0.04)" stroke="oklch(1 0 0 / 0.1)" strokeWidth="0.5" transform="scale(1,1)" vectorEffect="non-scaling-stroke"
          style={{ transformOrigin: 'center' }} />
      </svg>
      {displayPins.map(p => {
        const [x, y] = geoXY(p.lat, p.lng);
        const on = active === p.id || active === p.customerId || (p.pins || []).some(pin => pin.customerId === active || pin.id === active);
        const grouped = (p.count || 0) > 1;
        return (
          <div key={p.id} title={p.name} style={{ position: 'absolute', left: x + '%', top: y + '%', transform: 'translate(-50%,-100%)', zIndex: on ? 4 : 2 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ color: p.public ? 'var(--amber)' : 'oklch(0.6 0.01 235)', filter: 'drop-shadow(0 2px 3px oklch(0 0 0 / 0.5))', transform: on ? 'scale(1.25)' : 'none', transition: 'transform .15s' }}><Icon name="pin" size={on ? 24 : 19} sw={2.2} /></span>
              {grouped && <span className="mono" style={{ marginTop: -8, minWidth: 20, height: 20, borderRadius: 999, background: p.public ? 'var(--amber)' : 'var(--navy-700)', color: '#fff', border: '2px solid var(--navy-900)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900 }}>{p.count}</span>}
            </div>
          </div>
        );
      })}
      {legend && <div style={{ position: 'absolute', bottom: 10, left: 12, display: 'flex', gap: 14, fontSize: 10.5, color: 'oklch(0.8 0.02 235)', zIndex: 5 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="pin" size={12} style={{ color: 'var(--amber)' }} />Public retailer</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="pin" size={12} style={{ color: 'oklch(0.6 0.01 235)' }} />Not listed</span>
      </div>}
    </div>
  );
}

/* ---- Customer detail: location + public listing control ---- */
function LocationPublicCard({ c, force }) {
  const eligible = locatorPublicEligibleCustomer(c);
  const [pub, setPub] = useState(eligible && !!c.public);
  const [busy, setBusy] = useState(false);
  useEffect(() => setPub(eligible && !!c.public), [c && c.id, c && c.public, eligible]);
  const toggle = async () => {
    if (busy) return;
    if (!eligible) {
      toast('Consumer and direct accounts are not public store locator listings.', 'warn');
      return;
    }
    const next = !pub;
    setBusy(true);
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/update', {
          method: 'POST',
          body: JSON.stringify({ id: c.id, publicLocator: next })
        });
        if (response.customer) RP.upsertCustomer({ ...c, ...response.customer, skipCreds: true, persisted: true });
        c.public = response.customer ? !!response.customer.public : next;
        await rpRefreshAfterMutation(next ? `${c.name} published` : `${c.name} hidden`);
      } else {
        c.public = next;
      }
      setPub(!!c.public);
      toast(c.public ? `${c.name} published to the public store locator` : `${c.name} removed from the public site`, c.public ? 'ok' : 'info');
      force && force();
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not update public listing.', 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <SectionTitle right={pub ? <Pill status="Public">Live on site</Pill> : <span className="pill" style={{ color: 'var(--muted)', background: 'var(--surface-2)' }}>Private</span>}>Location & Public Listing</SectionTitle>
      <PinMap pins={[{ id: c.id, name: c.name, lat: c.lat, lng: c.lng, public: pub }]} height={150} active={c.id} legend={false} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13, marginTop: 14 }}>
        <Row k="Address" v={c.address || '—'} />
        <Row k="City / Postal" v={`${c.city}${c.postal ? ' · ' + c.postal : ''}`} />
        {c.phone && <Row k="Phone" v={<span className="mono" style={{ fontSize: 12.5 }}>{c.phone}</span>} />}
        <Row k="Coordinates" v={<span className="mono" style={{ fontSize: 12 }}>{locatorCoordLabel(c.lat)}, {locatorCoordLabel(c.lng)}</span>} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, padding: '12px 14px', background: pub ? 'var(--amber-soft)' : 'var(--surface-2)', borderRadius: 11, border: '1px solid ' + (pub ? 'var(--amber)' : 'var(--line)') }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: pub ? 'var(--amber-deep)' : 'var(--ink)' }}>Mark as public retailer</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{eligible ? 'Show this door on ranchpower.com/find' : 'Only business doors can be listed publicly'}</div>
        </div>
        <button onClick={toggle} disabled={busy || !eligible} style={{ width: 46, height: 27, borderRadius: 100, border: 'none', background: pub ? 'var(--amber)' : 'var(--line)', position: 'relative', flexShrink: 0, transition: 'background .18s', opacity: busy || !eligible ? 0.55 : 1 }}><span style={{ position: 'absolute', top: 3, left: pub ? 22 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} /></button>
      </div>

      {pub && (
        <div style={{ marginTop: 12, border: '1px solid var(--line)', borderRadius: 11, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: 'var(--navy)', color: '#fff', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="pin" size={13} style={{ color: 'var(--amber)' }} />Public site preview</div>
          <div style={{ padding: '13px 14px' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{c.publicName || c.name}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{c.address}, {c.city}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{c.hours}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}><span className="pill" style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)' }}>Carries Ranch Power</span><span className="pill" style={{ background: 'var(--surface-2)', color: 'var(--ink-soft)' }}>{c.type}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Public Retailers management (Admin tab) ---- */
function PublicRetailersView({ go }) {
  const [, force] = useState(0);
  const bump = () => force(x => x + 1);
  const [active, setActive] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const all = RP.CUSTOMERS.filter(locatorPublicEligibleCustomer);
  const publicOnes = all.filter(c => c.public);
  const locatorPins = customerLocatorPins(all);
  const togglePub = async c => {
    if (busyId || refreshing) return;
    const next = !c.public;
    setBusyId(c.id);
    setError('');
    try {
      let published = next;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/update', { method: 'POST', body: JSON.stringify({ id: c.id, publicLocator: next }) });
        if (response.customer) {
          published = response.customer.public === true;
          RP.upsertCustomer({ ...c, ...response.customer, skipCreds: true, persisted: true });
        }
        await rpRefreshAfterMutation(`${c.name} listing updated`);
      } else c.public = next;
      toast(next && !published ? `${c.name} saved privately. Confirm its location pin in Edit Customer to publish.` : published ? `${c.name} listed on Find Us` : `${c.name} removed from Find Us`, published ? 'ok' : 'info');
      bump();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not save this listing.');
    } finally { setBusyId(''); }
  };
  const refreshListings = async () => {
    if (refreshing || busyId) return;
    setRefreshing(true);
    setError('');
    try {
      await rpRefreshAfterMutation('Retailer listings refreshed');
      bump();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not refresh retailer listings.');
    } finally { setRefreshing(false); }
  };

  return (
    <div className="rp-fade">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--info-bg)', border: '1px solid oklch(0.56 0.1 245 / 0.3)', color: 'var(--info)', padding: '10px 15px', borderRadius: 9, marginBottom: 18, fontSize: 13 }}>
        <Icon name="pin" size={16} />These retailers appear on <b style={{ margin: '0 3px' }}>ranchpower.com/find</b>. {publicOnes.length} listed. Saved changes appear as the public site's cached listing refreshes.
      </div>
      {error && <div role="alert" style={{ color: 'var(--danger)', background: 'var(--danger-bg)', padding: 12, borderRadius: 9, marginBottom: 14 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 18 }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <SectionTitle>Store Locator Map</SectionTitle>
            <button className="btn btn-sm btn-primary" disabled={refreshing || !!busyId} onClick={refreshListings}><Icon name="refresh" size={14} />{refreshing ? 'Refreshing...' : 'Refresh Listings'}</button>
          </div>
          <div style={{ padding: 16 }}><PinMap pins={locatorPins} height={380} active={active} /></div>
        </div>
        <div className="card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}><SectionTitle right={<span className="pill" style={{ color: 'var(--amber-deep)', background: 'var(--amber-soft)' }}>{publicOnes.length} live</span>}>Listed Retailers</SectionTitle></div>
          <div className="rp-scroll" style={{ overflowY: 'auto', maxHeight: 412 }}>
            {all.map(c => (
              <div key={c.id} onMouseEnter={() => setActive(c.id)} onMouseLeave={() => setActive(null)} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderBottom: '1px solid var(--line-2)' }}>
                <span style={{ color: c.public ? 'var(--amber)' : 'var(--faint)', flexShrink: 0 }}><Icon name="pin" size={18} /></span>
                <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => go('customers', { type: 'customer', id: c.id })}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.publicName || c.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.address}, {c.city}</div>
                </div>
                <button onClick={() => togglePub(c)} disabled={!!busyId || refreshing} aria-label={`${c.public ? 'Unpublish' : 'Publish'} ${c.name}`} aria-pressed={!!c.public} title={busyId === c.id ? 'Saving...' : c.public ? 'Unpublish' : 'Publish'} style={{ width: 40, height: 23, borderRadius: 100, border: 'none', background: c.public ? 'var(--amber)' : 'var(--line)', position: 'relative', flexShrink: 0, opacity: busyId || refreshing ? 0.6 : 1 }}><span style={{ position: 'absolute', top: 3, left: c.public ? 20 : 3, width: 17, height: 17, borderRadius: '50%', background: '#fff', transition: 'left .18s' }} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { PinMap, LocationPublicCard, PublicRetailersView, customerLocatorPins, locatorPublicEligibleCustomer, geoXY, locatorGroupedPins });


/* ===== mobile_shell.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile field app : phone frame + nav + shared
   ============================================================ */

window.MOBILE_SCREENS = window.MOBILE_SCREENS || {};

const ROLE_TABS = {
  rep: [
    { id: 'rep-home', label: 'Home', icon: 'home' },
    { id: 'rep-pipeline', label: 'Pipeline', icon: 'trend' },
    { id: 'rep-customers', label: 'Customers', icon: 'customers' },
    { id: 'rep-orders', label: 'Orders', icon: 'orders' },
    { id: 'more', label: 'More', icon: 'more' },
  ],
  warehouse: [
    { id: 'wh-home', label: 'Tasks', icon: 'home' },
    { id: 'wh-pick', label: 'Pick', icon: 'package' },
    { id: 'wh-receive', label: 'Receive', icon: 'download' },
    { id: 'more', label: 'More', icon: 'more' },
  ],
  dispatch: [
    { id: 'dp-home', label: 'Route', icon: 'truck' },
    { id: 'dp-map', label: 'Map', icon: 'pin' },
    { id: 'more', label: 'More', icon: 'more' },
  ],
};
const ROLE_HOME = { rep: 'rep-home', warehouse: 'wh-home', dispatch: 'dp-home' };
const MOBILE_PARENT_ROUTES = {
  'rep-customer': 'rep-customers',
  'edit-customer': 'rep-customers',
  'rep-order-detail': 'rep-orders',
  'order-exceptions': 'rep-orders',
  'create-order': 'rep-home',
  'quick-retail-quote': 'rep-home',
  'retail-quotes': 'rep-home',
  'retail-quote-edit': 'retail-quotes',
  'add-customer': 'rep-customers',
  'lead-detail': 'rep-pipeline',
  'edit-lead-contact': 'lead-detail',
  'log-touch': 'rep-pipeline',
  'convert-lead': 'rep-pipeline',
  'add-lead': 'rep-pipeline',
  'rep-inventory': 'more',
  'request-stock': 'rep-inventory',
  'stock-requests': 'rep-inventory',
  'stock-request-detail': 'stock-requests',
  'field-receipt': 'rep-inventory',
  'giveaway-log': 'rep-inventory',
  'damage-report': 'rep-inventory',
  'rep-events': 'more',
  'event-detail': 'rep-events',
  'event-new': 'rep-events',
  'event-add-cases': 'event-detail',
  'event-open-cases': 'event-detail',
  'visit-log': 'rep-home',
  'documents': 'more',
  'settings': 'more',
  'notifications': 'more',
  'transfer-stock': 'more',
  'cycle-count': 'more',
  'my-tasks': 'more',
  'task-detail': 'my-tasks',
  'submit-expense': 'more',
  'my-expenses': 'more',
  'my-goals': 'more',
  'pick-order': 'wh-pick',
  'load-check': 'dp-home',
  'delivery-pod': 'dp-home'
};

function mobileParentRoute(screen, params, role, home) {
  const parent = MOBILE_PARENT_ROUTES[screen];
  if (!parent || parent === screen) return null;
  if (screen === 'edit-lead-contact' && params && params.id) return { s: parent, p: { id: params.id } };
  if (parent === 'stock-requests' && params && params.id) return { s: parent, p: {} };
  return { s: parent || home || ROLE_HOME[role] || 'rep-home', p: {} };
}

function mobilePreviewFrameEnabled() {
  if (typeof window === 'undefined') return false;
  if (!(window.RP_AUTH && window.RP_AUTH.demo)) return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('frame') === 'phone' || params.get('device') === 'phone';
}

const MOBILE_DEEP_LINK_IDS = {
  'task-detail': 'taskId',
  'rep-order-detail': 'orderId',
  'stock-request-detail': 'stockRequestId',
  'rep-customer': 'customerId',
  'edit-customer': 'customerId',
  'visit-log': 'customerId',
  'quick-retail-quote': 'customerId',
  'retail-quotes': 'customerId',
  'retail-quote-edit': 'quoteId',
  'lead-detail': 'leadId',
  'edit-lead-contact': 'leadId',
  'event-detail': 'eventId',
  'my-expenses': 'expenseId'
};
const MOBILE_DEEP_LINK_QUERY_KEYS = Array.from(new Set(['salesScreen', 'entityId', ...Object.values(MOBILE_DEEP_LINK_IDS)]));

function mobileRouteFromLocation(home, options = {}) {
  if (typeof window === 'undefined') return { s: home, p: {} };
  const query = new URLSearchParams(window.location.search);
  const stateRoute = window.history && window.history.state && window.history.state.ranchPowerSalesRoute;
  const screen = String(query.get('salesScreen') || stateRoute && stateRoute.s || '').trim();
  const allowScreen = typeof options.allowScreen === 'function' ? options.allowScreen : candidate => Boolean(MOBILE_SCREENS[candidate]);
  if (!screen || !allowScreen(screen)) return { s: home, p: {} };
  const idKey = MOBILE_DEEP_LINK_IDS[screen];
  const stateId = stateRoute && stateRoute.s === screen && stateRoute.p && stateRoute.p.id;
  const id = idKey ? String(query.get(idKey) || query.get('entityId') || stateId || '').trim() : '';
  return { s: screen, p: id ? { id } : {} };
}

function mobileWriteRoute(route, mode = 'push') {
  if (typeof window === 'undefined' || !window.history) return;
  const url = new URL(window.location.href);
  MOBILE_DEEP_LINK_QUERY_KEYS.forEach(key => url.searchParams.delete(key));
  if (route && route.s) {
    url.searchParams.set('salesScreen', route.s);
    const idKey = MOBILE_DEEP_LINK_IDS[route.s];
    if (idKey && route.p && route.p.id) url.searchParams.set(idKey, route.p.id);
  }
  const state = { ...(window.history.state || {}), ranchPowerSalesRoute: route };
  if (mode === 'replace') window.history.replaceState(state, '', url.toString());
  else window.history.pushState(state, '', url.toString());
}

function MobileApp({ role }) {
  const home = ROLE_HOME[role] || 'rep-home';
  const [stack, setStack] = useState(() => [mobileRouteFromLocation(home)]);
  const cur = stack[stack.length - 1];
  const nav = useMemo(() => ({
    push: (s, p = {}) => {
      const route = { s, p };
      setStack(st => [...st, route]);
      mobileWriteRoute(route);
    },
    pop: () => setStack(st => {
      if (st.length > 1) {
        const next = st[st.length - 2];
        mobileWriteRoute(next, 'replace');
        return st.slice(0, -1);
      }
      const current = st[0] || { s: home, p: {} };
      const parent = mobileParentRoute(current.s, current.p, role, home);
      if (parent && parent.s !== current.s) mobileWriteRoute(parent, 'replace');
      return parent && parent.s !== current.s ? [parent] : st;
    }),
    tab: (s) => {
      const route = { s, p: {} };
      setStack([route]);
      mobileWriteRoute(route);
    },
    reset: (s, p = {}) => {
      const route = { s, p };
      setStack([route]);
      mobileWriteRoute(route, 'replace');
    },
  }), [home, role]);
  useEffect(() => {
    const restoreRoute = () => setStack([mobileRouteFromLocation(home)]);
    window.addEventListener('popstate', restoreRoute);
    return () => window.removeEventListener('popstate', restoreRoute);
  }, [home]);
  useEffect(() => {
    const openAccount = () => nav.push('settings');
    window.RP_OPEN_ACCOUNT = openAccount;
    return () => { if (window.RP_OPEN_ACCOUNT === openAccount) window.RP_OPEN_ACCOUNT = null; };
  }, [nav]);

  const tabs = ROLE_TABS[role] || ROLE_TABS.rep;
  const tabRoot = stack.length === 1;
  const Comp = MOBILE_SCREENS[cur.s] || (() => <div style={{ padding: 40, textAlign: 'center', color: 'var(--faint)' }}>Screen: {cur.s}</div>);
  const activeTab = tabs.find(t => t.id === cur.s)?.id;
  const previewFrame = mobilePreviewFrameEnabled();
  const homeTone = cur.s === home && role === 'rep';
  const viewport = (
    <div className="mobile-app-viewport" style={{ width: '100%', height: '100%', background: 'var(--paper)', borderRadius: previewFrame ? 36 : 0, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {previewFrame && (
        <div style={{ height: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '0 26px 8px', flexShrink: 0, background: homeTone ? 'var(--navy)' : 'var(--surface)', color: homeTone ? '#fff' : 'var(--ink)', position: 'relative', zIndex: 5 }}>
          <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>9:41</span>
          <div style={{ position: 'absolute', left: '50%', top: 9, transform: 'translateX(-50%)', width: 96, height: 26, background: '#0a0e12', borderRadius: 20 }} />
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <Icon name="trend" size={13} /><span style={{ fontSize: 11, fontWeight: 700 }}>5G</span>
            <span style={{ width: 22, height: 11, border: '1.5px solid currentColor', borderRadius: 3, position: 'relative', display: 'inline-block', opacity: 0.9 }}><span style={{ position: 'absolute', inset: 1.5, right: 6, background: 'currentColor', borderRadius: 1 }} /></span>
          </div>
        </div>
      )}
      <div className="rp-scroll mobile-app-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
        <PageHelp page={cur.s} detail={cur.p} role={role} portal="field" />
        <Comp key={cur.s + JSON.stringify(cur.p)} nav={nav} role={role} {...cur.p} />
      </div>
      {tabRoot && activeTab && (
        <div style={{ flexShrink: 0, display: 'flex', borderTop: '1px solid var(--line)', background: 'var(--surface)', padding: '8px 6px calc(12px + env(safe-area-inset-bottom))' }}>
          {tabs.map(t => {
            const on = t.id === activeTab;
            return (
              <button key={t.id} onClick={() => nav.tab(t.id)} style={{ flex: 1, border: 'none', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, color: on ? 'var(--amber-deep)' : 'var(--faint)', padding: '2px 0' }}>
                <Icon name={t.icon} size={21} sw={on ? 2.1 : 1.8} />
                <span style={{ fontSize: 10, fontWeight: on ? 700 : 500 }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (previewFrame) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 20, background: 'radial-gradient(120% 90% at 50% 0%, var(--navy-800), var(--navy-900))' }}>
        <div style={{ width: 390, height: 'min(844px, calc(100vh - 40px))', maxHeight: 844, background: '#0a0e12', borderRadius: 46, padding: 11, boxShadow: '0 40px 90px oklch(0 0 0 / 0.5), 0 0 0 2px oklch(1 0 0 / 0.04), inset 0 0 0 1.5px oklch(1 0 0 / 0.06)', position: 'relative', flexShrink: 0 }}>
          {viewport}
        </div>
      </div>
    );
  }

  return viewport;
}

/* ---- Shared mobile primitives ---- */
function MHeader({ title, sub, nav, back, backLabel, dark, right, tall }) {
  return (
    <div style={{ background: dark ? 'var(--navy)' : 'var(--surface)', color: dark ? '#fff' : 'var(--ink)', padding: tall ? '6px 18px 18px' : '10px 16px 12px', borderBottom: dark ? 'none' : '1px solid var(--line)', position: 'sticky', top: 0, zIndex: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 34 }}>
        {back && <button onClick={() => nav.pop()} aria-label={backLabel || undefined} style={{ border: 'none', background: dark ? 'oklch(1 0 0 / 0.12)' : 'var(--surface-2)', width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'inherit', flexShrink: 0 }}><Icon name="arrowL" size={18} /></button>}
        <div style={{ flex: 1, minWidth: 0 }}>
          {sub && <div style={{ fontSize: 11.5, opacity: dark ? 0.7 : 0.55, fontWeight: 500 }}>{sub}</div>}
          <div className="disp" style={{ fontSize: tall ? 26 : 19, textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        </div>
        {right}
      </div>
    </div>
  );
}

function MSection({ title, children, right, pad = true }) {
  return (
    <div style={{ padding: pad ? '0 16px' : 0, marginTop: 20 }}>
      {title && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span className="eyebrow">{title}</span>{right}
      </div>}
      {children}
    </div>
  );
}

function MStat({ label, value, unit, accent }) {
  return (
    <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
      <div className="disp" style={{ fontSize: 21, lineHeight: 1, color: accent || 'inherit', whiteSpace: 'nowrap' }}>{value}<span style={{ fontSize: 10.5, fontWeight: 600, opacity: 0.6, marginLeft: 3 }}>{unit}</span></div>
      <div style={{ fontSize: 10, opacity: 0.7, marginTop: 5, fontWeight: 500, lineHeight: 1.15 }}>{label}</div>
    </div>
  );
}

/* Big bottom action button bar */
function MActionBar({ children }) {
  return <div style={{ position: 'sticky', bottom: 0, padding: '12px 16px', background: 'linear-gradient(to top, var(--paper) 70%, transparent)', display: 'flex', gap: 10 }}>{children}</div>;
}

/* Quick action tile */
function MTile({ icon, label, onClick, accent }) {
  return (
    <button onClick={onClick} style={{ flex: 1, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 12, padding: '13px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, boxShadow: 'var(--shadow-sm)' }}>
      <span style={{ width: 38, height: 38, borderRadius: 11, background: (accent || 'var(--amber)') + '', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Icon name={icon} size={19} /></span>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink)' }}>{label}</span>
    </button>
  );
}

/* Stepper input */
function Stepper({ value, onChange, max }) {
  const maxValue = Number(max);
  const hasMax = Number.isFinite(maxValue);
  const atMax = hasMax && value >= maxValue;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1px solid var(--line)', borderRadius: 9, overflow: 'hidden', background: 'var(--surface)' }}>
      <button onClick={() => onChange(Math.max(0, value - 1))} style={{ width: 36, height: 36, border: 'none', background: 'var(--surface-2)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="minus" size={16} /></button>
      <span className="mono tnum" style={{ width: 38, textAlign: 'center', fontWeight: 600, fontSize: 15 }}>{value}</span>
      <button disabled={atMax} onClick={() => onChange(hasMax ? Math.min(maxValue, value + 1) : value + 1)} style={{ width: 36, height: 36, border: 'none', background: atMax ? 'var(--line)' : 'var(--amber)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: atMax ? 0.65 : 1 }}><Icon name="plus" size={16} /></button>
    </div>
  );
}

Object.assign(window, { MobileApp, MHeader, MSection, MStat, MActionBar, MTile, Stepper });


/* ===== mobile_quote.jsx ===== */
/* ============================================================
   RANCH POWER - Quick retail quote
   Fast, mobile-first retailer pricing with a printable sell sheet.
   ============================================================ */

const RETAIL_QUOTE_DEFAULT_MSRP = 4.49;
const RETAIL_QUOTE_DEFAULT_CANS = 12;
const RETAIL_QUOTE_DEFAULT_CASE_COST = 36.50;
const RETAIL_QUOTE_DEFAULT_PRICE_MODEL = 'Default Retail';
const RETAIL_QUOTE_DEFAULT_TERMS = 'Prepaid';
const RETAIL_QUOTE_PAYMENT_TERMS = ['Prepaid', 'COD', 'Net 30'];
const RETAIL_QUOTE_PRINT_BODY_CLASS = 'rp-retail-quote-printing';
const RETAIL_QUOTE_ORIGINAL_SKUS = ['RP-12-GC', 'RP-12-KL', 'RP-12-BR', 'RP-12-BO', 'RP-12-PL', 'RP-12-PM'];
const RETAIL_QUOTE_MIX_SKUS = ['RP-MIX-TRI', 'RP-MIX-NEW3', 'RP-MIX-ALL6'];
const RETAIL_QUOTE_MESSAGE_PRESETS = [
  { label: 'After a visit', text: 'It was great meeting you today. I put together the retail pricing we discussed and would be happy to help with your opening order.' },
  { label: 'After samples', text: 'Thanks for trying Ranch Power. Here is the retail pricing we discussed, and I would be happy to drop by again or help build your first order.' },
  { label: 'New account intro', text: 'I thought Ranch Power could be a strong fit for your customers. Here is a quick look at the lineup and your retailer economics.' },
];
const RETAIL_QUOTE_DEFAULT_SUBJECT_PRESET_ID = 'pricing_for';
const RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID = 'custom';
const RETAIL_QUOTE_SUBJECT_PRESETS = [
  { id: 'pricing_for', joiner: ' for ', label: 'Pricing for', subject: 'Ranch Power pricing' },
  { id: 'retail_quote', joiner: ' - ', label: 'Retail quote', subject: 'Your Ranch Power retail quote' },
  { id: 'wholesale_details', joiner: ' - ', label: 'Wholesale details', subject: 'Ranch Power wholesale details' },
  { id: 'pricing_follow_up', joiner: ' - ', label: 'Pricing follow-up', subject: 'Following up with Ranch Power pricing' },
  { id: 'pricing_discussed', joiner: ' - ', label: 'Pricing discussed', subject: 'Ranch Power pricing we discussed' },
];
const RETAIL_QUOTE_FALLBACK_CLAIMS = [
  { id: 'caffeine', text: 'Each 355 mL can contains 100 mg of caffeine from green tea.' },
  { id: 'formula', text: "Ranch Power includes Lion's Mane, L-Theanine, electrolytes, and vitamins B6 and B12." },
  { id: 'sugar', text: 'Ranch Power has no added sugar.' },
  { id: 'sweeteners', text: 'Ranch Power has no artificial sweeteners.' },
];

function retailQuoteClean(value, limit = 240) {
  return String(value || '').replace(/[\r\n]+/g, ' ').trim().slice(0, limit).trim();
}

function retailQuoteMessage(value, limit = 500) {
  return String(value || '').replace(/\r\n?/g, '\n').trim().slice(0, limit).trim();
}

function retailQuotePresetSubject(presetId, retailerName) {
  const preset = RETAIL_QUOTE_SUBJECT_PRESETS.find(row => row.id === presetId)
    || RETAIL_QUOTE_SUBJECT_PRESETS.find(row => row.id === RETAIL_QUOTE_DEFAULT_SUBJECT_PRESET_ID);
  const retailer = retailQuoteClean(retailerName, 180);
  return retailQuoteClean(`${preset.subject}${retailer ? `${preset.joiner}${retailer}` : ''}`, 200);
}

function retailQuoteSubjectPresetId(subject, retailerName) {
  const value = retailQuoteClean(subject, 200);
  const match = RETAIL_QUOTE_SUBJECT_PRESETS.find(row => retailQuotePresetSubject(row.id, retailerName) === value);
  return match ? match.id : '';
}

function retailQuoteSubjectError(value) {
  const raw = String(value || '');
  const subject = raw.trim();
  if (!subject) return 'Add an email subject.';
  if (/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/.test(raw)) return 'Use one subject line without control characters.';
  if (/^(?:re|fw|fwd)\s*:/i.test(subject)) return 'Start a new subject instead of Re:, Fw:, or Fwd:. This is a new email, not an existing thread.';
  if (subject.length > 200) return 'Keep the email subject to 200 characters or fewer.';
  return '';
}

function retailQuoteMoney(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : '-';
}

function retailQuotePercent(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(1)}%` : '-';
}

function retailQuoteDateLabel(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return retailQuoteClean(value) || '-';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  return new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}

function retailQuoteFrozenBasisDate(currentValue, todayValue) {
  const current = String(currentValue || '').trim().slice(0, 10);
  const today = String(todayValue || '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(current) ? current : today;
}

function retailQuoteRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `retail-quote-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function retailQuoteRecipientDraft(values = {}) {
  return {
    id: retailQuoteRequestId(),
    name: retailQuoteClean(values.name, 120),
    email: retailQuoteClean(values.email, 320).toLowerCase(),
    role: retailQuoteClean(values.role, 120),
    basis: values.basis === 'provided_directly' || values.basis === 'retailer_staff_direction' ? values.basis : '',
    directionSource: retailQuoteClean(values.directionSource, 120),
    orderingAccessConfirmed: values.orderingAccessConfirmed === true,
  };
}

function retailQuoteValidEmail(value) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(retailQuoteClean(value, 320).toLowerCase());
}

function retailQuoteStaffDirectionSource(value) {
  const source = retailQuoteClean(value, 120);
  if (!source) return '';
  if (/\b(?:manager|owner|clerk|cashier|associate|staff|employee|counter|receiving|front[- ]desk)\b/i.test(source) || /\b(?:at|from)\s+(?:the\s+)?(?:store|retailer|location)\b/i.test(source)) return source;
  return `${source} at the store`;
}

function retailQuoteRecipientEvidence(recipient) {
  const email = retailQuoteClean(recipient && recipient.email, 320).toLowerCase();
  const name = retailQuoteClean(recipient && recipient.contactName, 120);
  const role = retailQuoteClean(recipient && recipient.role, 120);
  if (recipient && recipient.contactBasis === 'retailer_staff_direction') {
    const source = retailQuoteStaffDirectionSource(recipient.directionSource);
    const destination = name || email;
    return `${source} asked me to email ${destination}${role ? ` (${role})` : ''} with this pricing quote.`;
  }
  return `${name || email || 'The retailer contact'} provided this email address directly in person and asked to receive this pricing quote.`;
}

function retailQuoteModelRows(rows, customerPriceModelName = '') {
  const authorizedCustomerModel = retailQuoteClean(customerPriceModelName, 160).toLowerCase();
  const models = (Array.isArray(rows) ? rows : []).map(row => ({
    ...(row.id ? { id: row.id } : {}), ...(row.aliases ? { aliases: row.aliases } : {}),
    ...((row.policyKey || row.policy_key) ? { policyKey: row.policyKey || row.policy_key } : {}),
    name: retailQuoteClean(row && row.name, 160),
    caseCost: Number(row && (row.caseCost ?? row.skuPrice ?? row.sku_price)),
    terms: retailQuoteClean(row && row.terms, 120) || RETAIL_QUOTE_DEFAULT_TERMS,
  })).filter(row => row.name
    && (['default retail', 'slopeside retail'].includes(retailPriceListIdentity(row))
      || (authorizedCustomerModel && row.name.toLowerCase() === authorizedCustomerModel))
    && Number.isFinite(row.caseCost)
    && row.caseCost > 0);
  const byName = new Map(models.map(model => [model.name.toLowerCase(), model]));
  if (!findRetailPriceList(models, RETAIL_QUOTE_DEFAULT_PRICE_MODEL)) {
    byName.set(RETAIL_QUOTE_DEFAULT_PRICE_MODEL.toLowerCase(), {
      name: RETAIL_QUOTE_DEFAULT_PRICE_MODEL,
      caseCost: RETAIL_QUOTE_DEFAULT_CASE_COST,
      terms: RETAIL_QUOTE_DEFAULT_TERMS,
    });
  }
  return Array.from(byName.values()).sort((left, right) => left.name.localeCompare(right.name));
}

function retailQuoteProductRows(rows) {
  return (Array.isArray(rows) ? rows : []).map(row => {
    const id = retailQuoteClean(row && row.id, 120);
    const sourceSku = typeof RP !== 'undefined' && typeof RP.sku === 'function' ? RP.sku(id) : null;
    return {
      id,
      skuCode: retailQuoteClean(row && (row.skuCode ?? row.sku_code ?? row.sku), 120) || (typeof rpSkuCode === 'function' ? rpSkuCode(sourceSku || id) : ''),
      upc: retailQuoteClean(row && row.upc, 12) || (typeof rpSkuUpc === 'function' ? rpSkuUpc(sourceSku || id) : ''),
      name: retailQuoteClean(row && row.name, 160) || (typeof rpSkuDisplayName === 'function' ? rpSkuDisplayName(sourceSku || id) : id),
      pack: retailQuoteClean(row && row.pack, 80) || '12-Pack',
      containerFeePerCase: Number(row && (row.containerFeePerCase ?? row.deposit ?? 0)) || 0,
      visualColor: retailQuoteClean(row && (row.visualColor ?? row.visual_color), 80) || (typeof rpSkuVisualColor === 'function' ? rpSkuVisualColor(sourceSku || id) : 'var(--amber)'),
      visualLabel: retailQuoteClean(row && (row.visualLabel ?? row.visual_label), 12) || (typeof rpSkuVisualLabel === 'function' ? rpSkuVisualLabel(sourceSku || id) : id.slice(-3)),
    };
  }).filter(row => row.id && row.name);
}

function retailQuoteFulfillmentRows(rows) {
  return (Array.isArray(rows) ? rows : []).map(row => ({
    id: retailQuoteClean(row && row.id, 80),
    territory: retailQuoteClean(row && row.territory, 160),
    label: retailQuoteClean(row && row.label, 120),
    mode: retailQuoteClean(row && row.mode, 40) || 'custom',
    providerName: retailQuoteClean(row && (row.providerName ?? row.provider_name), 120),
    customerCopy: retailQuoteClean(row && (row.customerCopy ?? row.customer_copy), 400),
    sourceLocationId: retailQuoteClean(row && (row.sourceLocationId ?? row.source_location_id), 120) || null,
    isDefault: Boolean(row && (row.isDefault ?? row.is_default)),
    sortOrder: Number(row && (row.sortOrder ?? row.sort_order)) || 100,
  })).filter(row => row.id && row.territory && row.label && row.providerName && row.customerCopy);
}

function retailQuoteFulfillmentChoice(profileRow = null) {
  return {
    id: profileRow && profileRow.id || `custom-${retailQuoteRequestId()}`,
    profileId: profileRow && profileRow.id || '',
    label: profileRow && profileRow.label || 'Custom option',
    mode: profileRow && profileRow.mode || 'custom',
    providerName: profileRow && profileRow.providerName || '',
    customerCopy: profileRow && profileRow.customerCopy || '',
    sourceLocationId: profileRow && profileRow.sourceLocationId || null,
  };
}

function retailQuoteTerritoryMatches(allowed, territory) {
  const target = retailQuoteClean(territory, 160).toLowerCase();
  if (!target) return false;
  return String(allowed || '').split(/\s*(?:\||;|,|\u00b7|\u2022)\s*/).some(value => value.trim().toLowerCase() === target);
}

function retailQuoteDemoOptions() {
  const canonicalIds = new Set([...RETAIL_QUOTE_ORIGINAL_SKUS, ...RETAIL_QUOTE_MIX_SKUS]);
  const skus = (RP.SKUS || []).filter(sku => sku && canonicalIds.has(sku.id));
  const territory = typeof rpFieldTerritory === 'function' ? rpFieldTerritory('rep') : '';
  return {
    claims: RETAIL_QUOTE_FALLBACK_CLAIMS,
    containerFeePolicy: { containerFeePerCase: 1.20, containerFeeNote: 'An additional $1.20 container fee applies per case; taxes are extra.' },
    msrpPresets: [4.49, 5, 5.25],
    priceModels: retailQuoteModelRows(RP.PRICE_LISTS || []),
    pricingPolicy: {
      customPricingEnabled: false,
      defaultCaseCost: RETAIL_QUOTE_DEFAULT_CASE_COST,
      defaultPriceModelName: RETAIL_QUOTE_DEFAULT_PRICE_MODEL,
    },
    paymentTerms: RETAIL_QUOTE_PAYMENT_TERMS,
    products: retailQuoteProductRows(skus),
    fulfillmentProfiles: [
      { id: 'squamish-slopeside', territory: 'Squamish', label: 'Slopeside Supply', mode: 'distributor', providerName: 'Slopeside Supply', customerCopy: 'Available through Slopeside Supply. Orders placed by 4:00 p.m. are scheduled for next-business-day delivery.', sourceLocationId: 'MW-SQ', isDefault: true, sortOrder: 10 },
      { id: 'squamish-whistler-local', territory: 'Squamish', label: 'Personal delivery from your sales rep', mode: 'rep_delivery', providerName: 'Your Ranch Power sales representative', customerCopy: 'Personal delivery is also available through your Ranch Power sales representative. Timing can be arranged directly with your rep based on your preference.', sourceLocationId: 'MW-SQ', isDefault: false, sortOrder: 20 },
      { id: 'kamloops-rep-delivery', territory: 'Kamloops', label: 'Kamloops rep delivery', mode: 'rep_delivery', providerName: 'Your Ranch Power representative', customerCopy: 'Delivered by your Ranch Power representative. Next-business-day delivery is typically available; timing is confirmed when you order.', sourceLocationId: 'WH-KAM', isDefault: true, sortOrder: 10 },
    ],
    territory,
    territories: territory ? [territory] : [],
  };
}

function retailQuoteLocalPricing({ caseCost, customPricingEnabled, model, msrp, priceMode, products, selectedSkuIds, terms, territory }) {
  const quotedCaseCost = Number(caseCost);
  const suggestedRetail = Number(msrp);
  const cansPerCase = RETAIL_QUOTE_DEFAULT_CANS;
  const costPerCan = quotedCaseCost / cansPerCase;
  const grossProfitPerCan = suggestedRetail - costPerCan;
  const grossMarginPercent = suggestedRetail > 0 ? (grossProfitPerCan / suggestedRetail) * 100 : NaN;
  const baselineCaseCost = Number(model && model.caseCost) || quotedCaseCost;
  const discountPercent = baselineCaseCost > 0 ? Math.max(0, ((baselineCaseCost - quotedCaseCost) / baselineCaseCost) * 100) : 0;
  const chosenProducts = products.filter(product => selectedSkuIds.includes(product.id));
  const fees = Array.from(new Set(chosenProducts.map(product => Number(product.containerFeePerCase) || 0)));
  const containerFeePerCase = fees.length === 1 ? fees[0] : null;
  const issueDate = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory }) : new Date().toISOString().slice(0, 10);
  const validUntil = typeof rpAddDaysYmd === 'function'
    ? rpAddDaysYmd(issueDate, 14, { territory })
    : new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  return {
    approval: {
      code: priceMode === 'custom' && !customPricingEnabled ? 'custom_price_approval_required' : '',
      required: priceMode === 'custom' && !customPricingEnabled,
      reason: priceMode === 'custom' && !customPricingEnabled ? 'Request one-time manager approval for this exact quote draft before printing or sending.' : '',
      route: priceMode === 'custom' && !customPricingEnabled ? 'Sales Manager' : '',
    },
    baselineCaseCost,
    cansPerCase,
    containerFeeNote: containerFeePerCase == null
      ? 'Applicable container fees and taxes are extra and are calculated by product.'
      : containerFeePerCase > 0
        ? `An additional ${retailQuoteMoney(containerFeePerCase)} container fee applies per case; taxes are extra.`
        : 'Taxes are extra.',
    containerFeePerCase,
    costPerCan,
    currency: 'CAD',
    discountPercent,
    grossMarginPercent,
    grossProfitPerCan,
    issueDate,
    lines: chosenProducts.map(product => ({
      ...product,
      productName: product.name,
      skuId: product.id,
      suggestedRetailPrice: suggestedRetail,
      wholesaleCaseCost: quotedCaseCost,
      wholesaleUnitCost: costPerCan,
    })),
    msrp: suggestedRetail,
    priceMode: priceMode === 'custom' ? 'custom' : 'price_list',
    priceModelName: model && model.name || '',
    quotedCaseCost,
    terms: retailQuoteClean(terms, 80) || RETAIL_QUOTE_DEFAULT_TERMS,
    validUntil,
  };
}

function RetailQuoteMatchChoice({ match, onChoose, busy }) {
  const name = retailQuoteClean(match && (match.displayName || match.display_name || match.legalName || match.legal_name || match.name)) || 'Existing customer';
  const email = retailQuoteClean(match && (match.primaryEmail || match.primary_email || match.email));
  return (
    <button type="button" className="retail-quote-match" disabled={busy} onClick={() => onChoose(match)}>
      <span className="retail-quote-match-icon"><Icon name="customers" size={17} /></span>
      <span><strong>{name}</strong><small>{[email, match && match.territory, match && match.status].filter(Boolean).join(' - ')}</small></span>
      <span>Use account <Icon name="chevR" size={14} /></span>
    </button>
  );
}

function retailQuoteHistoryRows(response) {
  return Array.isArray(response && response.quotes) ? response.quotes : [];
}

function retailQuoteHistoryCustomer(row) {
  const customer = row && row.customer && typeof row.customer === 'object' ? row.customer : {};
  return {
    id: retailQuoteClean(customer.id || row && (row.customerId || row.customer_id), 120),
    name: retailQuoteClean(customer.name || customer.displayName || customer.display_name || row && row.retailerName, 180),
  };
}

function retailQuoteHistoryArchived(row) {
  return Boolean(row && (row.archivedAt || row.archived_at));
}

function retailQuoteHistoryCapabilities(row) {
  const capabilities = row && row.capabilities && typeof row.capabilities === 'object' ? row.capabilities : {};
  return {
    canArchive: capabilities.canArchive === true,
    canRestore: capabilities.canRestore === true,
    canPermanentlyDelete: capabilities.canPermanentlyDelete === true,
    canArchiveLinkedProspect: capabilities.canArchiveLinkedProspect === true,
  };
}

function retailQuoteLinkedProspectCleanup(row) {
  const cleanup = row && row.linkedProspectCleanup && typeof row.linkedProspectCleanup === 'object'
    ? row.linkedProspectCleanup
    : {};
  return {
    customerId: retailQuoteClean(cleanup.customerId || cleanup.customer_id, 120),
    status: retailQuoteClean(cleanup.status, 40),
    eligible: cleanup.eligible === true,
    reason: retailQuoteClean(cleanup.reason, 280),
  };
}

function retailQuoteHistoryStatus(row) {
  if (retailQuoteHistoryArchived(row)) return { label: 'Archived', tone: 'archived' };
  const summary = row && row.deliverySummary || {};
  const needsAttention = Number(summary.needsAttention || 0);
  const pending = Number(summary.pending || 0);
  const status = retailQuoteClean(row && row.status, 40).toLowerCase();
  if (needsAttention > 0 || ['failed', 'bounced', 'complained', 'suppressed'].includes(status)) {
    return { label: 'Needs attention', tone: 'attention' };
  }
  if (pending > 0 || status === 'pending') return { label: 'Pending', tone: 'pending' };
  if (status === 'sent' || status === 'delivered') return { label: status === 'delivered' ? 'Delivered' : 'Sent', tone: 'sent' };
  return { label: status ? status.replace(/_/g, ' ') : 'Saved', tone: 'saved' };
}

function RetailQuoteArchiveModal({ row, busy, onClose, onArchive, onPermanentlyDelete }) {
  const [reasonChoice, setReasonChoice] = useState('duplicate_test');
  const [otherReason, setOtherReason] = useState('');
  const [archiveLinkedProspect, setArchiveLinkedProspect] = useState(false);
  const [linkedProspectConfirmed, setLinkedProspectConfirmed] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [confirmQuoteNumber, setConfirmQuoteNumber] = useState('');
  const customer = retailQuoteHistoryCustomer(row);
  const quoteNumber = retailQuoteClean(row && (row.quoteNumber || row.quote_number), 80) || 'Retail quote';
  const quoteStatus = retailQuoteHistoryStatus(row);
  const capabilities = retailQuoteHistoryCapabilities(row);
  const prospectCleanup = retailQuoteLinkedProspectCleanup(row);
  const canArchiveProspect = capabilities.canArchiveLinkedProspect && prospectCleanup.eligible;
  const canPermanentlyDelete = capabilities.canPermanentlyDelete && !retailQuoteHistoryArchived(row);
  const reasons = {
    duplicate_test: 'Duplicate or test quote',
    replaced: 'Replaced by a corrected quote',
    inactive: 'Prospect is no longer active',
  };
  const reason = reasonChoice === 'other' ? otherReason.trim() : reasons[reasonChoice] || '';
  const statusNote = quoteStatus.tone === 'sent'
    ? 'The sent email, PDF, and delivery evidence will remain preserved.'
    : quoteStatus.tone === 'pending' || quoteStatus.tone === 'attention'
      ? 'Existing delivery attempts and audit evidence will remain preserved.'
      : 'The saved quote and its history will remain preserved.';
  const submitDisabled = busy || reason.length < 8 || (archiveLinkedProspect && !linkedProspectConfirmed);
  const permanentDeleteDisabled = busy || confirmQuoteNumber.trim() !== quoteNumber || deleteReason.trim().length < 8;
  const close = () => { if (!busy) onClose(); };

  return (
    <Modal
      title={deleteMode ? 'Delete Unused Draft' : capabilities.canArchive ? 'Archive Quote' : 'Clean Up Quote'}
      sub={`${quoteNumber} - ${customer.name || 'Retail customer'}`}
      onClose={close}
      width={560}
      footer={<><button type="button" className="btn" disabled={busy} onClick={close}>Cancel</button>{capabilities.canArchive && !deleteMode && <button type="button" className="btn btn-primary" data-retail-quote-confirm-archive disabled={submitDisabled} onClick={() => onArchive({ archiveLinkedProspect, reason })}><Icon name="trash" size={15} />{busy ? 'Archiving...' : 'Archive Quote'}</button>}</>}
    >
      {capabilities.canArchive && !deleteMode && <div className="retail-quote-cleanup-warning">
        <Icon name="doc" size={18} />
        <div><strong>This removes the quote from the active Saved Quotes list.</strong><span>{statusNote} While archived, this quote no longer sets active customer pricing; the newest active sent quote or account default applies. Restoring makes it active again.</span></div>
      </div>}

      {capabilities.canArchive && !deleteMode && <>
        <label className="field-label" htmlFor="retail-quote-archive-reason">Why are you archiving this quote?</label>
        <select id="retail-quote-archive-reason" className="field" value={reasonChoice} onChange={event => setReasonChoice(event.target.value)}>
          <option value="duplicate_test">Duplicate or test quote</option>
          <option value="replaced">Replaced by a corrected quote</option>
          <option value="inactive">Prospect is no longer active</option>
          <option value="other">Other reason</option>
        </select>
        {reasonChoice === 'other' && <input className="field" autoFocus aria-label="Other archive reason" maxLength="180" value={otherReason} onChange={event => setOtherReason(event.target.value)} placeholder="Brief cleanup reason" style={{ marginTop: 9 }} />}
      </>}

      {canArchiveProspect && !deleteMode && (
        <div className="retail-quote-linked-cleanup" data-retail-quote-linked-prospect-cleanup>
          <label>
            <input type="checkbox" checked={archiveLinkedProspect} onChange={event => { setArchiveLinkedProspect(event.target.checked); setLinkedProspectConfirmed(false); }} />
            <span><strong>Also mark this quote-created prospect inactive</strong><small>{customer.name || 'This prospect'} will leave active customer and prospect lists. No orders or other protected business history will be deleted, but unused ordering links will be permanently disabled.</small></span>
          </label>
          {archiveLinkedProspect && <label className="retail-quote-linked-cleanup-confirm"><input type="checkbox" checked={linkedProspectConfirmed} onChange={event => setLinkedProspectConfirmed(event.target.checked)} /><span>I understand this also changes the linked prospect account. Restoring can reactivate the prospect, but old ordering links stay disabled; send a new quote revision if ordering access is needed again.</span></label>}
        </div>
      )}
      {!canArchiveProspect && customer.id && !deleteMode && (
        <p className="retail-quote-customer-preserved"><strong>Customer account unchanged.</strong> {prospectCleanup.reason || 'Archiving this quote will not change the linked customer or prospect.'}</p>
      )}

      {canPermanentlyDelete && (
        <section className="retail-quote-permanent-delete" data-retail-quote-permanent-delete>
          <div><strong>Permanent deletion - unused draft only</strong><span>This option is only available because the draft was never submitted and has no provider-started, access, approval, revision, or protected audit evidence. It cannot be undone and never changes the linked prospect.</span></div>
          {!deleteMode
            ? <button type="button" className="btn btn-sm retail-quote-delete-draft-action" disabled={busy} onClick={() => { setDeleteMode(true); setArchiveLinkedProspect(false); setLinkedProspectConfirmed(false); }}><Icon name="trash" size={14} />Permanently delete unused draft</button>
            : <div className="retail-quote-permanent-delete-confirm">
              <label className="field-label" htmlFor="retail-quote-delete-reason">Cleanup reason</label>
              <textarea id="retail-quote-delete-reason" className="field" rows="2" maxLength="180" value={deleteReason} onChange={event => setDeleteReason(event.target.value)} placeholder="Why this unused draft must be permanently removed" />
              <label className="field-label" htmlFor="retail-quote-delete-confirm">Type {quoteNumber} to confirm</label>
              <input id="retail-quote-delete-confirm" className="field mono" autoComplete="off" value={confirmQuoteNumber} onChange={event => setConfirmQuoteNumber(event.target.value)} />
              <div className="retail-quote-permanent-delete-buttons"><button type="button" className="btn btn-sm" disabled={busy} onClick={() => { setDeleteMode(false); setDeleteReason(''); setConfirmQuoteNumber(''); }}>Keep Draft</button><button type="button" className="btn btn-sm retail-quote-delete-draft-action" data-retail-quote-confirm-permanent-delete disabled={permanentDeleteDisabled} onClick={() => onPermanentlyDelete({ confirmQuoteNumber: confirmQuoteNumber.trim(), reason: deleteReason.trim() })}><Icon name="trash" size={14} />{busy ? 'Deleting...' : 'Delete Permanently'}</button></div>
            </div>}
        </section>
      )}
    </Modal>
  );
}

MOBILE_SCREENS['retail-quotes'] = function SavedRetailQuotes({ nav, id }) {
  const [emailQuoteId, setEmailQuoteId] = useState('');
  const isNarrow = useIsNarrow(760);
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const customerId = retailQuoteClean(id, 120);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState(live ? '' : 'Sign in to view saved quotes.');
  const [actionError, setActionError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [openingId, setOpeningId] = useState('');
  const [mutatingId, setMutatingId] = useState('');
  const [cleanupTarget, setCleanupTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [cleanupNotice, setCleanupNotice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!live) return undefined;
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    if (showArchived) params.set('includeArchived', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    rpApi(`/api/retail-quotes/history${query}`).then(response => {
      if (cancelled) return;
      setQuotes(retailQuoteHistoryRows(response));
    }).catch(loadError => {
      if (cancelled) return;
      setQuotes([]);
      setError(loadError && loadError.message ? loadError.message : 'Could not load saved quotes.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [live, customerId, showArchived, refreshKey]);

  const openPdf = async quoteId => {
    if (!quoteId || openingId) return;
    setOpeningId(quoteId);
    setError('');
    try {
      await rpOpenProtectedFile(`/api/retail-quotes/pdf?id=${encodeURIComponent(quoteId)}`);
    } catch (pdfError) {
      setError(pdfError && pdfError.message ? pdfError.message : 'Could not open the saved quote PDF.');
    } finally {
      setOpeningId('');
    }
  };

  const newQuote = () => nav.push('quick-retail-quote', customerId ? { id: customerId } : {});
  const headerAction = <button type="button" className="btn btn-primary btn-sm" onClick={newQuote}><Icon name="plus" size={14} />New Quote</button>;

  const runCleanup = async (row, action, options = {}) => {
    const quoteId = retailQuoteClean(row && row.id, 80);
    const expectedUpdatedAt = retailQuoteClean(row && (row.updatedAt || row.updated_at), 80);
    if (!quoteId || !expectedUpdatedAt || mutatingId) {
      if (!expectedUpdatedAt) setActionError('This quote changed or is missing its update timestamp. Refresh the list and try again.');
      return;
    }
    setMutatingId(quoteId);
    setActionError('');
    const actionLabel = action === 'permanently_delete' ? 'permanently delete' : action;
    try {
      const response = await rpApi('/api/retail-quotes/cleanup', {
        method: 'POST',
        body: JSON.stringify({
          action,
          quoteId,
          expectedUpdatedAt,
          ...(action === 'archive' ? {
            reason: retailQuoteClean(options.reason, 180),
            archiveLinkedProspect: options.archiveLinkedProspect === true,
          } : action === 'permanently_delete' ? {
            reason: retailQuoteClean(options.reason, 180),
            confirmQuoteNumber: retailQuoteClean(options.confirmQuoteNumber, 80),
          } : {}),
        }),
      });
      if (!response || response.accepted !== true || response.quoteId !== quoteId || response.action !== action) {
        throw new Error(`The quote ${actionLabel} response could not be verified.`);
      }
      if (action === 'permanently_delete' && response.permanentlyDeleted !== true) {
        throw new Error('The permanent deletion response could not be verified.');
      }
      const responseUpdatedAt = retailQuoteClean(response.updatedAt, 80);
      if (action !== 'permanently_delete' && (!responseUpdatedAt || (action === 'archive' && !response.archivedAt))) {
        throw new Error(`The quote ${actionLabel} result was incomplete. Refresh the list before trying again.`);
      }
      const nextRow = {
        ...row,
        updatedAt: responseUpdatedAt,
        archivedAt: action === 'archive' ? response.archivedAt : null,
      };
      if (action === 'archive') {
        const customerNote = response.customerArchived ? ' The linked quote-created prospect was also marked inactive and unused ordering links were disabled. Use Show archived to review restore availability.' : '';
        setCleanupNotice({ message: `${retailQuoteClean(row.quoteNumber || row.quote_number, 80) || 'Quote'} archived.${customerNote}`, row: nextRow, undo: response.customerArchived !== true });
        if (typeof toast === 'function') toast('Quote archived', 'ok');
      } else if (action === 'restore') {
        const customerNote = response.customerRestored ? ' The linked prospect was reactivated; old ordering links remain disabled.' : '';
        setCleanupNotice({ message: `${retailQuoteClean(row.quoteNumber || row.quote_number, 80) || 'Quote'} restored.${customerNote}`, row: nextRow, undo: false });
        if (typeof toast === 'function') toast('Quote restored', 'ok');
      } else {
        setCleanupNotice({ message: `${retailQuoteClean(row.quoteNumber || row.quote_number, 80) || 'Unused draft'} permanently deleted.`, row: null, undo: false });
        if (typeof toast === 'function') toast('Unused quote draft permanently deleted', 'warn');
      }
      setCleanupTarget(null);
      setRefreshKey(value => value + 1);
    } catch (cleanupError) {
      setActionError(cleanupError && cleanupError.message ? cleanupError.message : `Could not ${actionLabel} the quote.`);
    } finally {
      setMutatingId('');
    }
  };

  return (
    <div className="rp-fade retail-quote-history-screen" data-retail-quote-history="true">
      {cleanupTarget && <RetailQuoteArchiveModal row={cleanupTarget} busy={mutatingId === cleanupTarget.id} onClose={() => setCleanupTarget(null)} onArchive={options => runCleanup(cleanupTarget, 'archive', options)} onPermanentlyDelete={options => runCleanup(cleanupTarget, 'permanently_delete', options)} />}
      {isNarrow
        ? <MHeader title="Saved Quotes" sub={customerId ? 'Customer quote history' : 'Retail quote history'} nav={nav} back backLabel="Back to sales" right={headerAction} />
        : <div className="retail-quote-history-head"><PageHead eyebrow="Retail pricing" title="Saved Quotes" sub={customerId ? 'Review this customer’s quotes, open a sell sheet, or create a new revision.' : 'Review sent and saved retailer quotes, then open or revise them safely.'} actions={<div className="retail-quote-history-head-actions"><button type="button" className="btn" onClick={() => nav.pop()}><Icon name="arrowL" size={15} />Back</button>{headerAction}</div>} /></div>}

      <main className="retail-quote-history-main">
        {error && <div className="retail-quote-callout retail-quote-callout-danger" role="alert"><Icon name="alert" size={16} /><span>{error}</span>{live && <button type="button" className="btn btn-sm" onClick={() => setRefreshKey(value => value + 1)}>Try Again</button>}</div>}
        {actionError && <div className="retail-quote-callout retail-quote-callout-danger" role="alert"><Icon name="alert" size={16} /><span>{actionError}</span><button type="button" className="btn btn-sm" onClick={() => setActionError('')}>Dismiss</button></div>}
        {cleanupNotice && <div className="retail-quote-cleanup-notice" role="status"><Icon name="check" size={16} /><span>{cleanupNotice.message}</span>{cleanupNotice.undo && <button type="button" className="btn btn-sm" data-retail-quote-undo disabled={Boolean(mutatingId)} onClick={() => runCleanup(cleanupNotice.row, 'restore')}>Undo</button>}<button type="button" className="btn btn-ghost btn-sm" aria-label="Dismiss cleanup notice" onClick={() => setCleanupNotice(null)}><Icon name="x" size={14} /></button></div>}
        <div className="retail-quote-history-controls">
          <div><strong>{showArchived ? 'Active and archived quotes' : 'Active quotes'}</strong><span>Archived quotes keep their PDFs and delivery history and can be restored.</span></div>
          <label className="retail-quote-archive-toggle"><input type="checkbox" checked={showArchived} onChange={event => { setShowArchived(event.target.checked); setCleanupNotice(null); setActionError(''); }} /><span>Show archived</span></label>
        </div>
        {loading && <div className="retail-quote-history-loading" role="status"><Icon name="refresh" size={18} /><span>Loading saved quotes...</span></div>}
        {!loading && !error && quotes.length === 0 && <div className="retail-quote-history-empty"><span className="retail-quote-history-empty-icon"><Icon name="doc" size={28} /></span><h2>{showArchived ? 'No saved quotes yet' : 'No active saved quotes'}</h2><p>{showArchived ? (customerId ? 'Create the first retail quote for this customer.' : 'Quotes will appear here after the first send is reserved.') : 'Archived quotes are preserved out of the way. Turn on Show archived to review or restore them.'}</p>{showArchived ? <button type="button" className="btn btn-primary" onClick={newQuote}><Icon name="plus" size={15} />New Quote</button> : <button type="button" className="btn" onClick={() => setShowArchived(true)}><Icon name="refresh" size={15} />Show Archived</button>}</div>}
        {!loading && quotes.length > 0 && <div className="retail-quote-history-list">{quotes.map(row => {
          const quoteId = retailQuoteClean(row && row.id, 80);
          const quoteNumber = retailQuoteClean(row && (row.quoteNumber || row.quote_number), 80) || 'Retail quote';
          const customer = retailQuoteHistoryCustomer(row);
          const status = retailQuoteHistoryStatus(row);
          const summary = row && row.deliverySummary || {};
          const total = Number(summary.total || 0);
          const sent = Number(summary.sent || 0);
          const createdAt = row && (row.sentAt || row.sent_at || row.createdAt || row.created_at);
          const validUntil = row && (row.validUntil || row.valid_until);
          const archived = retailQuoteHistoryArchived(row);
          const capabilities = retailQuoteHistoryCapabilities(row);
          const updatedAt = row && (row.updatedAt || row.updated_at);
          return <article className={`retail-quote-history-card ${archived ? 'is-archived' : ''}`} key={quoteId || quoteNumber}>
            <div className="retail-quote-history-card-head">
              <div><span className="eyebrow">{quoteNumber}</span><h2>{customer.name || retailQuoteClean(row && row.retailerName, 180) || 'Retail customer'}</h2></div>
              <span className={`retail-quote-history-status is-${status.tone}`}>{status.label}</span>
            </div>
            <div className="retail-quote-history-meta">
              <span><Icon name="calendar" size={13} />{createdAt ? retailQuoteDateLabel(createdAt) : 'Date unavailable'}</span>
              {validUntil && <span><Icon name="clock" size={13} />Valid to {retailQuoteDateLabel(validUntil)}</span>}
              {total > 0 && <span><Icon name="mail" size={13} />{sent} of {total} sent</span>}
              {archived && <span><Icon name="clock" size={13} />Archived {retailQuoteDateLabel(row.archivedAt || row.archived_at)}</span>}
            </div>
            <p className="retail-quote-history-revision-note">{archived ? `Archived${row.archiveReason || row.archive_reason ? ` - ${retailQuoteClean(row.archiveReason || row.archive_reason, 180)}` : ''}. Restore it to create a new revision.` : 'Edit & Resend creates a new revision. This saved quote stays unchanged.'}</p>
            {emailQuoteId === quoteId && typeof EmailDeliveryStatus === 'function' && <EmailDeliveryStatus entityType="quote" entityId={quoteId} />}
            <div className="retail-quote-history-actions">
              {typeof EmailDeliveryStatus === 'function' && <button type="button" className="btn btn-sm" onClick={() => setEmailQuoteId(current => current === quoteId ? '' : quoteId)} aria-expanded={emailQuoteId === quoteId}><Icon name="mail" size={14} />Email delivery</button>}
              <button type="button" className="btn btn-primary btn-sm" disabled={!quoteId || Boolean(openingId)} onClick={() => openPdf(quoteId)}><Icon name="doc" size={14} />{openingId === quoteId ? 'Opening...' : 'View PDF'}</button>
              {!archived && <button type="button" className="btn btn-sm" disabled={!quoteId} onClick={() => nav.push('retail-quote-edit', { id: quoteId })}><Icon name="pen" size={14} />Edit &amp; Resend</button>}
              {customer.id && <button type="button" className="btn btn-sm btn-ghost" onClick={() => nav.push('rep-customer', { id: customer.id })}><Icon name="customers" size={14} />Open Customer</button>}
              {!archived && (capabilities.canArchive || capabilities.canPermanentlyDelete) && <button type="button" className="btn btn-sm btn-ghost retail-quote-archive-action" disabled={!quoteId || !updatedAt || Boolean(mutatingId)} onClick={() => setCleanupTarget(row)}><Icon name="trash" size={14} />{capabilities.canArchive ? 'Archive' : 'Clean Up'}</button>}
              {archived && capabilities.canRestore && <button type="button" className="btn btn-sm retail-quote-restore-action" disabled={!quoteId || !updatedAt || Boolean(mutatingId)} onClick={() => runCleanup(row, 'restore')}><Icon name="refresh" size={14} />{mutatingId === quoteId ? 'Restoring...' : 'Restore'}</button>}
            </div>
          </article>;
        })}</div>}
      </main>
    </div>
  );
};

const QuickRetailQuote = MOBILE_SCREENS['quick-retail-quote'] = function QuickRetailQuote({ nav, role, id, sourceQuoteId }) {
  const resolvedSourceQuoteId = retailQuoteClean(sourceQuoteId, 80);
  const isNarrow = useIsNarrow(760);
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const exactCustomer = useRpExactCustomer({ customerId: resolvedSourceQuoteId ? '' : id || '', enabled: Boolean(id) && !resolvedSourceQuoteId });
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  const repName = typeof rpDisplayName === 'function' ? rpDisplayName() : retailQuoteClean(profile && (profile.display_name || profile.name)) || 'Ranch Power Sales';
  const repPhone = retailQuoteClean(profile && profile.quote_phone, 40);
  const repLocation = retailQuoteClean(profile && profile.quote_location, 160);
  const authenticatedProfileEmail = retailQuoteClean(profile && profile.email, 254).toLowerCase();
  const testRecipientEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(authenticatedProfileEmail) ? authenticatedProfileEmail : '';
  const repEmail = testRecipientEmail || 'sales@ranchpower.com';
  const [options, setOptions] = useState(() => retailQuoteDemoOptions());
  const [optionsReady, setOptionsReady] = useState(!live);
  const [optionsError, setOptionsError] = useState('');
  const [retailerName, setRetailerName] = useState('');
  const [contactName, setContactName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [additionalRecipients, setAdditionalRecipients] = useState([]);
  const [primaryOrderingAccess, setPrimaryOrderingAccess] = useState(false);
  const [testPersonaId, setTestPersonaId] = useState('primary');
  const [customerId, setCustomerId] = useState(id || '');
  const [territory, setTerritory] = useState(() => retailQuoteDemoOptions().territory || '');
  const [priceModelName, setPriceModelName] = useState('');
  const [priceMode, setPriceMode] = useState('price_list');
  const [customCaseCost, setCustomCaseCost] = useState('');
  const [terms, setTerms] = useState(RETAIL_QUOTE_DEFAULT_TERMS);
  const [msrp, setMsrp] = useState(String(RETAIL_QUOTE_DEFAULT_MSRP));
  const [selectedSkuIds, setSelectedSkuIds] = useState([]);
  const [fulfillmentOpen, setFulfillmentOpen] = useState(false);
  const [fulfillmentChoices, setFulfillmentChoices] = useState([]);
  const [note, setNote] = useState('');
  const [subjectPresetId, setSubjectPresetId] = useState(RETAIL_QUOTE_DEFAULT_SUBJECT_PRESET_ID);
  const [customEmailSubject, setCustomEmailSubject] = useState('');
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);
  const [formAttempted, setFormAttempted] = useState(false);
  const [recipientAttempted, setRecipientAttempted] = useState(false);
  const [sendAttempted, setSendAttempted] = useState(false);
  const [reviewedPricing, setReviewedPricing] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [testStatus, setTestStatus] = useState(null);
  const [matches, setMatches] = useState([]);
  const [approval, setApproval] = useState(null);
  const [priceApprovalQueue, setPriceApprovalQueue] = useState([]);
  const [sent, setSent] = useState(null);
  const [sourceRecord, setSourceRecord] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(Boolean(resolvedSourceQuoteId && live));
  const [sourceError, setSourceError] = useState(resolvedSourceQuoteId && !live ? 'Sign in to load this saved quote.' : '');
  const [sourceWarning, setSourceWarning] = useState('');
  const [recoveryQuoteId, setRecoveryQuoteId] = useState('');
  const [recoveryAvailable, setRecoveryAvailable] = useState(false);
  const requestIdRef = useRef(retailQuoteRequestId());
  const basisObtainedAtRef = useRef(typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory }) : new Date().toISOString().slice(0, 10));
  const testRequestIdRef = useRef(retailQuoteRequestId());
  const prefilledCustomerRef = useRef('');
  const retailerRef = useRef(null);
  const emailRef = useRef(null);
  const recipientsRef = useRef(null);
  const customCaseCostRef = useRef(null);
  const msrpRef = useRef(null);
  const productsRef = useRef(null);
  const fulfillmentRef = useRef(null);
  const subjectRef = useRef(null);
  const consentRef = useRef(null);
  const printCleanupRef = useRef(null);
  const sendInFlightRef = useRef(false);
  const testInFlightRef = useRef(false);
  const sourceHydratedRef = useRef('');

  const priceModels = useMemo(() => retailQuoteModelRows(
    options.priceModels,
    options && options.pricingPolicy && options.pricingPolicy.customerPriceModelName
  ), [options.priceModels, options.pricingPolicy]);
  const products = useMemo(() => retailQuoteProductRows(options.products), [options.products]);
  const territories = useMemo(() => Array.from(new Set((Array.isArray(options.territories) ? options.territories : [options.territory]).map(value => retailQuoteClean(value, 160)).filter(Boolean))), [options.territories, options.territory]);
  const territoryChoices = useMemo(() => Array.from(new Set([...territories, retailQuoteClean(territory, 160)].filter(Boolean))), [territories, territory]);
  const fulfillmentProfiles = useMemo(() => retailQuoteFulfillmentRows(options.fulfillmentProfiles)
    .filter(profileRow => retailQuoteTerritoryMatches(profileRow.territory, territory))
    .sort((left, right) => left.sortOrder - right.sortOrder || left.label.localeCompare(right.label)), [options.fulfillmentProfiles, territory]);
  const defaultFulfillmentProfile = fulfillmentProfiles.find(profileRow => profileRow.isDefault) || fulfillmentProfiles[0] || null;
  const fulfillmentEnabled = fulfillmentChoices.length > 0;
  const fulfillment = useMemo(() => fulfillmentEnabled ? {
    enabled: true,
    options: fulfillmentChoices.map(choice => {
      const profileRow = fulfillmentProfiles.find(profile => profile.id === choice.profileId) || null;
      return {
        profileId: profileRow && profileRow.id || null,
        customized: Boolean(profileRow && (
          retailQuoteClean(choice.providerName, 120) !== profileRow.providerName
          || retailQuoteClean(choice.customerCopy, 400) !== profileRow.customerCopy
        )),
        mode: profileRow && profileRow.mode || choice.mode || 'custom',
        providerName: retailQuoteClean(choice.providerName, 120),
        customerCopy: retailQuoteClean(choice.customerCopy, 400),
        sourceLocationId: profileRow && profileRow.sourceLocationId || null,
        territory,
      };
    }),
  } : { enabled: false }, [fulfillmentEnabled, fulfillmentChoices, fulfillmentProfiles, territory]);
  const msrpPresets = useMemo(() => (Array.isArray(options.msrpPresets) ? options.msrpPresets : [4.49, 5, 5.25]).map(Number).filter(value => Number.isFinite(value) && value > 0), [options.msrpPresets]);
  const paymentTerms = useMemo(() => {
    const available = Array.isArray(options.paymentTerms) ? options.paymentTerms : RETAIL_QUOTE_PAYMENT_TERMS;
    const authorizedCustomerTerms = retailQuoteClean(options && options.pricingPolicy && options.pricingPolicy.customerPriceModelTerms, 80);
    const normalized = available.map(value => retailQuoteClean(value, 80)).filter(value => RETAIL_QUOTE_PAYMENT_TERMS.includes(value) || (authorizedCustomerTerms && value === authorizedCustomerTerms));
    return normalized.length ? Array.from(new Set(normalized)) : RETAIL_QUOTE_PAYMENT_TERMS;
  }, [options.paymentTerms, options.pricingPolicy]);
  const pricingPolicy = options && options.pricingPolicy || {};
  const customPricingEnabled = pricingPolicy.customPricingEnabled === true;
  const priceApprovalManager = ['admin', 'leadership', 'ops', 'sales_manager'].includes(
    typeof rpNormalizeRole === 'function' ? rpNormalizeRole(profile && profile.role) : retailQuoteClean(profile && profile.role, 80).toLowerCase()
  );
  const selectedModel = priceModels.find(model => model.name === priceModelName) || priceModels[0] || { name: '', caseCost: 0, terms: '' };
  const effectiveCaseCost = priceMode === 'custom' ? Number(customCaseCost) : Number(selectedModel.caseCost);
  const localPricing = useMemo(() => retailQuoteLocalPricing({
    caseCost: effectiveCaseCost,
    customPricingEnabled,
    model: selectedModel,
    msrp: Number(msrp),
    priceMode,
    products,
    selectedSkuIds,
    terms,
    territory,
  }), [customPricingEnabled, effectiveCaseCost, msrp, priceMode, products, selectedModel.name, selectedModel.caseCost, selectedSkuIds, terms, territory]);
  const displayPricing = reviewedPricing || localPricing;
  const displayClaims = Array.isArray(reviewedPricing && reviewedPricing.claims) && reviewedPricing.claims.length
    ? reviewedPricing.claims
    : options.claims;
  const selectedProducts = products.filter(product => selectedSkuIds.includes(product.id));
  const subjectForRetailer = value => subjectPresetId === RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID
    ? String(customEmailSubject || '').trim()
    : retailQuotePresetSubject(subjectPresetId, value);
  const emailSubject = subjectForRetailer(retailerName);
  const subjectFieldValue = subjectPresetId === RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID ? customEmailSubject : emailSubject;
  const emailSubjectError = retailQuoteSubjectError(subjectFieldValue);
  const retailerInvalid = retailerName.trim().length < 2;
  const emailValid = retailQuoteValidEmail(recipientEmail);
  const emailSubjectInvalid = Boolean(emailSubjectError);
  const caseCostInvalid = priceMode === 'custom'
    ? !Number.isFinite(effectiveCaseCost) || effectiveCaseCost < 1 || effectiveCaseCost > 250
    : !Number.isFinite(effectiveCaseCost) || effectiveCaseCost <= 0;
  const msrpInvalid = !Number.isFinite(Number(msrp)) || Number(msrp) < 1 || Number(msrp) > 25;
  const quoteRecipients = useMemo(() => [{
    id: 'primary',
    contactName: contactName.trim(),
    email: recipientEmail.trim().toLowerCase(),
    role: '',
    contactBasis: 'provided_directly',
    directionSource: '',
    orderingAccessConfirmed: primaryOrderingAccess,
  }, ...additionalRecipients.map(recipient => ({
    id: recipient.id,
    contactName: retailQuoteClean(recipient.name, 120),
    email: retailQuoteClean(recipient.email, 320).toLowerCase(),
    role: retailQuoteClean(recipient.role, 120),
    contactBasis: recipient.basis,
    directionSource: retailQuoteClean(recipient.directionSource, 120),
    orderingAccessConfirmed: recipient.orderingAccessConfirmed === true,
  }))], [additionalRecipients, contactName, primaryOrderingAccess, recipientEmail]);
  const testPersonas = quoteRecipients.filter(recipient => retailQuoteValidEmail(recipient.email));
  const selectedTestPersona = testPersonas.find(recipient => recipient.id === testPersonaId) || testPersonas[0] || quoteRecipients[0];
  const savedContactSuggestions = useMemo(() => {
    const customer = exactCustomer.customer;
    const contacts = typeof rpCustomerContacts === 'function' ? rpCustomerContacts(customer) : (Array.isArray(customer && customer.contacts) ? customer.contacts : []);
    const used = new Set(quoteRecipients.map(recipient => recipient.email).filter(Boolean));
    return contacts.map(row => ({
      name: retailQuoteClean(row && row.name, 120),
      email: retailQuoteClean(row && row.email, 320).toLowerCase(),
      role: retailQuoteClean(row && (row.role || row.title), 120),
    })).filter(row => retailQuoteValidEmail(row.email) && !used.has(row.email));
  }, [exactCustomer.customer, quoteRecipients]);
  const fulfillmentInvalid = fulfillmentChoices.some(choice => retailQuoteClean(choice.providerName, 120).length < 2 || retailQuoteClean(choice.customerCopy, 400).length < 10);

  const invalidate = (basisTerritory = territory) => {
    setReviewedPricing(null);
    setError('');
    setNotice('');
    setTestStatus(null);
    setMatches([]);
    setApproval(null);
    setRecoveryQuoteId('');
    setRecoveryAvailable(false);
    requestIdRef.current = retailQuoteRequestId();
    basisObtainedAtRef.current = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: basisTerritory }) : new Date().toISOString().slice(0, 10);
    testRequestIdRef.current = retailQuoteRequestId();
  };
  const update = (setter, value) => {
    setter(value);
    invalidate();
  };
  const chooseSubjectPreset = presetId => {
    if (presetId === RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID
      && subjectPresetId !== RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID
      && !customEmailSubject) {
      setCustomEmailSubject(emailSubject);
    }
    setSubjectPresetId(presetId);
    invalidate();
    if (presetId === RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID) {
      window.requestAnimationFrame(() => subjectRef.current && subjectRef.current.focus());
    }
  };
  const editEmailSubject = value => {
    setCustomEmailSubject(value);
    setSubjectPresetId(RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID);
    invalidate();
  };
  const revokeRecipientPermission = () => {
    setPermissionConfirmed(false);
    setSendAttempted(false);
  };
  const updateRetailerIdentity = value => {
    setRetailerName(value);
    if (!resolvedSourceQuoteId) setCustomerId('');
    setPrimaryOrderingAccess(false);
    setAdditionalRecipients(current => current.map(recipient => ({ ...recipient, orderingAccessConfirmed: false })));
    revokeRecipientPermission();
    invalidate();
  };
  const updateRecipientEmail = value => {
    setRecipientEmail(value);
    setPrimaryOrderingAccess(false);
    revokeRecipientPermission();
    invalidate();
  };
  const updateContactIdentity = value => {
    setContactName(value);
    revokeRecipientPermission();
    invalidate();
  };
  const updateAdditionalRecipient = (recipientId, values) => {
    setAdditionalRecipients(current => current.map(recipient => {
      if (recipient.id !== recipientId) return recipient;
      const identityChanged = Object.prototype.hasOwnProperty.call(values, 'email');
      return { ...recipient, ...values, ...(identityChanged ? { orderingAccessConfirmed: false } : {}) };
    }));
    revokeRecipientPermission();
    invalidate();
  };
  const addAdditionalRecipient = (values = {}) => {
    setAdditionalRecipients(current => current.length >= 2 ? current : [...current, retailQuoteRecipientDraft(values)]);
    revokeRecipientPermission();
    invalidate();
  };
  const removeAdditionalRecipient = recipientId => {
    setAdditionalRecipients(current => current.filter(recipient => recipient.id !== recipientId));
    if (testPersonaId === recipientId) setTestPersonaId('primary');
    revokeRecipientPermission();
    invalidate();
  };
  const clearFulfillment = ({ close = false } = {}) => {
    setFulfillmentChoices([]);
    if (close) setFulfillmentOpen(false);
  };
  const toggleFulfillmentProfile = profileRow => {
    setFulfillmentOpen(true);
    setFulfillmentChoices(current => current.some(choice => choice.profileId === profileRow.id)
      ? current.filter(choice => choice.profileId !== profileRow.id)
      : current.length >= 4
        ? current
        : [...current, retailQuoteFulfillmentChoice(profileRow)].sort((left, right) => {
            const leftIndex = fulfillmentProfiles.findIndex(profile => profile.id === left.profileId);
            const rightIndex = fulfillmentProfiles.findIndex(profile => profile.id === right.profileId);
            return (leftIndex < 0 ? 999 : leftIndex) - (rightIndex < 0 ? 999 : rightIndex);
          }));
    invalidate();
  };
  const addCustomFulfillment = () => {
    setFulfillmentOpen(true);
    setFulfillmentChoices(current => current.length >= 4 ? current : [...current, retailQuoteFulfillmentChoice()]);
    invalidate();
  };
  const updateFulfillmentChoice = (choiceId, values) => {
    setFulfillmentChoices(current => current.map(choice => choice.id === choiceId ? { ...choice, ...values } : choice));
    invalidate();
  };
  const removeFulfillmentChoice = choiceId => {
    setFulfillmentChoices(current => current.filter(choice => choice.id !== choiceId));
    invalidate();
  };
  const openFulfillment = () => {
    setFulfillmentOpen(true);
    if (!fulfillmentEnabled) {
      if (defaultFulfillmentProfile) setFulfillmentChoices([retailQuoteFulfillmentChoice(defaultFulfillmentProfile)]);
      invalidate();
    }
  };
  const updateTerritory = value => {
    setTerritory(value);
    clearFulfillment();
    if (!resolvedSourceQuoteId) setCustomerId('');
    revokeRecipientPermission();
    invalidate(value);
  };

  useEffect(() => {
    let cancelled = false;
    if (!live) return undefined;
    setOptionsReady(false);
    setOptionsError('');
    const optionsPath = customerId
      ? `/api/retail-quotes/options?customerId=${encodeURIComponent(customerId)}`
      : '/api/retail-quotes/options';
    rpApi(optionsPath).then(response => {
      if (cancelled) return;
      const nextModels = retailQuoteModelRows(
        response && response.priceModels,
        response && response.pricingPolicy && response.pricingPolicy.customerPriceModelName
      );
      const nextProducts = retailQuoteProductRows(response && response.products);
      if (!nextModels.length || !nextProducts.length) throw new Error('Current quote products or pricing are unavailable.');
      setOptions(response);
      if (!resolvedSourceQuoteId) {
        const customerModelName = retailQuoteClean(response && response.pricingPolicy && response.pricingPolicy.customerPriceModelName, 160);
        const defaultModelName = retailQuoteClean(response && response.pricingPolicy && response.pricingPolicy.defaultPriceModelName, 160);
        const defaultModel = (customerId && nextModels.find(model => model.name === customerModelName))
          || nextModels.find(model => model.name === defaultModelName)
          || nextModels.find(model => model.name === RETAIL_QUOTE_DEFAULT_PRICE_MODEL)
          || nextModels[0];
        setPriceModelName(defaultModel.name);
        setPriceMode('price_list');
        setCustomCaseCost('');
        if (!customerId) setTerms(RETAIL_QUOTE_DEFAULT_TERMS);
      }
      const responseTerritories = Array.isArray(response && response.territories) ? response.territories.map(value => retailQuoteClean(value, 160)).filter(Boolean) : [];
      setTerritory(current => current && (!responseTerritories.length || responseTerritories.some(value => value.toLowerCase() === current.toLowerCase()))
        ? current
        : retailQuoteClean(response && response.territory, 160) || responseTerritories[0] || '');
      setOptionsReady(true);
    }).catch(loadError => {
      if (cancelled) return;
      setOptionsReady(false);
      setOptionsError(loadError && loadError.message ? loadError.message : 'Could not load current quote pricing.');
    });
    return () => { cancelled = true; };
  }, [customerId, id, live, resolvedSourceQuoteId]);

  useEffect(() => {
    let cancelled = false;
    if (!resolvedSourceQuoteId || !live) return undefined;
    setSourceLoading(true);
    setSourceError('');
    rpApi(`/api/retail-quotes/history?id=${encodeURIComponent(resolvedSourceQuoteId)}`).then(response => {
      if (cancelled) return;
      const quote = response && response.quote && typeof response.quote === 'object' ? response.quote : null;
      const draft = quote && quote.draft && typeof quote.draft === 'object'
        ? quote.draft
        : response && response.draft && typeof response.draft === 'object'
          ? response.draft
          : null;
      if (!quote || !draft) throw new Error('The saved quote could not be loaded for editing.');
      setSourceRecord({ quote, draft });
    }).catch(loadError => {
      if (cancelled) return;
      setSourceRecord(null);
      setSourceLoading(false);
      setSourceError(loadError && loadError.message ? loadError.message : 'Could not load this saved quote.');
    });
    return () => { cancelled = true; };
  }, [live, resolvedSourceQuoteId]);

  useEffect(() => {
    if (!sourceRecord || !resolvedSourceQuoteId || sourceHydratedRef.current === resolvedSourceQuoteId) return;
    if (live && !optionsReady && !optionsError) return;
    const quote = sourceRecord.quote || {};
    const draft = sourceRecord.draft || {};
    const quoteSnapshot = quote.pricingSnapshot && typeof quote.pricingSnapshot === 'object'
      ? quote.pricingSnapshot
      : quote.pricing_snapshot && typeof quote.pricing_snapshot === 'object'
        ? quote.pricing_snapshot
        : {};
    const sourceRecipients = Array.isArray(draft.recipients) ? draft.recipients : [];
    const primary = sourceRecipients[0] || {};
    const sourceCustomer = quote.customer && typeof quote.customer === 'object' ? quote.customer : {};
    const nextCustomerId = retailQuoteClean(draft.customerId || quote.customerId || quote.customer_id || sourceCustomer.id, 120);
    if (live && nextCustomerId && customerId !== nextCustomerId) {
      // A saved quote can use an account-only schedule (including a
      // distributor agreement). Load that exact customer's authorized options
      // before freezing the edit draft as a custom-price fallback.
      setCustomerId(nextCustomerId);
      return;
    }
    const optionsCustomerId = retailQuoteClean(pricingPolicy.selectedCustomerId, 120);
    if (live && nextCustomerId && !optionsError && optionsCustomerId !== nextCustomerId) return;
    const nextTerritory = retailQuoteClean(draft.territory || quote.territory, 160) || territory;
    const nextModelName = retailQuoteClean(draft.priceModelName || draft.price_model_name, 160);
    const modelExists = priceModels.some(model => model.name === nextModelName);
    const requestedMode = retailQuoteClean(draft.priceMode || draft.price_mode, 40).toLowerCase();
    const caseCost = Number(draft.caseCost ?? draft.case_cost ?? draft.quotedCaseCost ?? draft.quoted_case_cost);
    const sourceSkuIds = (Array.isArray(draft.skuIds) ? draft.skuIds : [])
      .map(value => retailQuoteClean(value, 120))
      .filter(Boolean);
    const availableSourceSkuIds = sourceSkuIds.filter(skuId => products.some(product => product.id === skuId));
    const sourceFulfillment = draft.fulfillment && typeof draft.fulfillment === 'object' ? draft.fulfillment : {};
    const nextRetailerName = retailQuoteClean(draft.retailerName || draft.retailer_name || quote.retailerName || quote.retailer_name, 180);
    const sourceEmailSubject = retailQuoteClean(
      draft.emailSubject || draft.email_subject || quote.emailSubject || quote.email_subject || quoteSnapshot.emailSubject || quoteSnapshot.email_subject,
      200
    );
    const sourceSubjectPresetId = retailQuoteSubjectPresetId(sourceEmailSubject, nextRetailerName);
    const fulfillmentOptions = sourceFulfillment.enabled === false || !Array.isArray(sourceFulfillment.options)
      ? []
      : sourceFulfillment.options.slice(0, 4).map(choice => ({
          id: retailQuoteRequestId(),
          profileId: retailQuoteClean(choice && (choice.profileId || choice.profile_id), 120),
          label: retailQuoteClean(choice && choice.label, 120) || 'Saved delivery option',
          mode: retailQuoteClean(choice && choice.mode, 40) || 'custom',
          providerName: retailQuoteClean(choice && (choice.providerName || choice.provider_name), 120),
          customerCopy: retailQuoteClean(choice && (choice.customerCopy || choice.customer_copy), 400),
          sourceLocationId: retailQuoteClean(choice && (choice.sourceLocationId || choice.source_location_id), 120) || null,
        }));

    sourceHydratedRef.current = resolvedSourceQuoteId;
    setRetailerName(nextRetailerName);
    setContactName(retailQuoteClean(draft.contactName || draft.contact_name || primary.contactName || primary.contact_name || primary.name, 120));
    setRecipientEmail(retailQuoteClean(primary.email || primary.toEmail || primary.to_email || draft.recipientEmail || draft.recipient_email, 320).toLowerCase());
    setAdditionalRecipients(sourceRecipients.slice(1, 3).map(recipient => retailQuoteRecipientDraft({
      name: recipient && (recipient.contactName || recipient.contact_name || recipient.name),
      email: recipient && (recipient.email || recipient.toEmail || recipient.to_email),
      role: recipient && recipient.role,
      basis: recipient && (recipient.contactBasis || recipient.contact_basis || recipient.basis),
      directionSource: recipient && (recipient.directionSource || recipient.direction_source),
      orderingAccessConfirmed: false,
    })));
    setCustomerId(nextCustomerId);
    setTerritory(nextTerritory);
    setPriceModelName(modelExists ? nextModelName : (priceModels[0] && priceModels[0].name || ''));
    setPriceMode(requestedMode === 'custom' || !modelExists ? 'custom' : 'price_list');
    setCustomCaseCost(Number.isFinite(caseCost) && caseCost > 0 ? String(caseCost) : '');
    setTerms(paymentTerms.includes(draft.terms) ? draft.terms : RETAIL_QUOTE_DEFAULT_TERMS);
    setMsrp(String(Number(draft.msrp) || RETAIL_QUOTE_DEFAULT_MSRP));
    setSelectedSkuIds(availableSourceSkuIds);
    setSourceWarning(availableSourceSkuIds.length < sourceSkuIds.length
      ? 'One or more products from the saved quote are no longer available. Review the product selection before sending this revision.'
      : '');
    setFulfillmentChoices(fulfillmentOptions);
    setFulfillmentOpen(fulfillmentOptions.length > 0);
    setNote(retailQuoteMessage(draft.note, 500));
    setSubjectPresetId(sourceSubjectPresetId || (sourceEmailSubject ? RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID : RETAIL_QUOTE_DEFAULT_SUBJECT_PRESET_ID));
    setCustomEmailSubject(sourceSubjectPresetId ? '' : sourceEmailSubject);
    setPrimaryOrderingAccess(false);
    setPermissionConfirmed(false);
    setTestPersonaId('primary');
    setFormAttempted(false);
    setRecipientAttempted(false);
    setSendAttempted(false);
    setReviewedPricing(null);
    setTestStatus(null);
    setMatches([]);
    setApproval(null);
    setRecoveryQuoteId('');
    setRecoveryAvailable(false);
    setError('');
    setNotice('');
    requestIdRef.current = retailQuoteRequestId();
    basisObtainedAtRef.current = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: nextTerritory }) : new Date().toISOString().slice(0, 10);
    testRequestIdRef.current = retailQuoteRequestId();
    setSourceLoading(false);
  }, [sourceRecord, resolvedSourceQuoteId, live, optionsReady, optionsError, paymentTerms, priceModels, pricingPolicy.selectedCustomerId, products, territory]);

  useEffect(() => {
    if (!priceModels.length) return;
    setPriceModelName(current => current && priceModels.some(model => model.name === current)
      ? current
      : (priceModels.find(model => model.name === retailQuoteClean(pricingPolicy.defaultPriceModelName, 160))
        || priceModels.find(model => model.name === RETAIL_QUOTE_DEFAULT_PRICE_MODEL)
        || priceModels[0]).name);
  }, [priceModels, pricingPolicy.defaultPriceModelName]);

  useEffect(() => {
    if (!products.length || selectedSkuIds.length || (resolvedSourceQuoteId && (sourceLoading || sourceHydratedRef.current === resolvedSourceQuoteId))) return;
    const originals = RETAIL_QUOTE_ORIGINAL_SKUS.filter(skuId => products.some(product => product.id === skuId));
    setSelectedSkuIds(originals.length ? originals : products.slice(0, 6).map(product => product.id));
  }, [products, selectedSkuIds.length, resolvedSourceQuoteId, sourceLoading]);

  useEffect(() => {
    const customer = exactCustomer.customer;
    if (!customer || prefilledCustomerRef.current === customer.id) return;
    prefilledCustomerRef.current = customer.id;
    const contacts = typeof rpCustomerContacts === 'function' ? rpCustomerContacts(customer) : (Array.isArray(customer.contacts) ? customer.contacts : []);
    const primary = contacts.find(row => row && row.primary) || contacts[0] || {};
    setRetailerName(retailQuoteClean(customer.name || customer.displayName || customer.display_name || customer.legalName || customer.legal_name));
    setContactName(retailQuoteClean(primary.name || customer.buyer));
    setRecipientEmail(typeof repCustomerPrimaryEmail === 'function' ? repCustomerPrimaryEmail(customer) : retailQuoteClean(primary.email || customer.email).toLowerCase());
    setAdditionalRecipients([]);
    setPrimaryOrderingAccess(false);
    setTestPersonaId('primary');
    setCustomerId(customer.id);
    const customerTerritory = retailQuoteClean(customer.territory, 160);
    if (customerTerritory) {
      setTerritory(customerTerritory);
      clearFulfillment();
    }
    setPermissionConfirmed(false);
    setRecipientAttempted(false);
    setSendAttempted(false);
    setReviewedPricing(null);
    setTestStatus(null);
    setApproval(null);
    requestIdRef.current = retailQuoteRequestId();
    basisObtainedAtRef.current = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: customerTerritory || territory }) : new Date().toISOString().slice(0, 10);
    testRequestIdRef.current = retailQuoteRequestId();
    const customerTier = retailQuoteClean(customer.tier || customer.priceListName || customer.price_list_name);
    if (customerTier && priceModels.some(model => model.name === customerTier)) setPriceModelName(customerTier);
    const customerTerms = retailQuoteClean(customer.terms, 80);
    if (paymentTerms.includes(customerTerms)) setTerms(customerTerms);
  }, [exactCustomer.customer, paymentTerms, priceModels]);

  const pricingInput = () => ({
    priceMode,
    priceModelName: selectedModel.name,
    ...(priceMode === 'custom' ? { caseCost: Number(customCaseCost) } : {}),
    msrp: Number(msrp),
    skuIds: selectedSkuIds,
    terms,
  });

  const priceApprovalRecipients = () => quoteRecipients.map(recipient => ({
    contactName: recipient.contactName || undefined,
    email: recipient.email,
    orderingAccessConfirmed: recipient.orderingAccessConfirmed === true,
  }));

  const priceApprovalDraft = (customerOverride) => {
    const chosenCustomerId = retailQuoteClean(customerOverride && customerOverride.id, 120) || customerId || undefined;
    const chosenRetailerName = retailQuoteClean(customerOverride && (customerOverride.displayName || customerOverride.display_name || customerOverride.legalName || customerOverride.legal_name || customerOverride.name), 180) || retailerName.trim();
    return {
      approvalRecipients: priceApprovalRecipients(),
      contactName: contactName.trim() || undefined,
      ...(chosenCustomerId ? { customerId: chosenCustomerId } : {}),
      emailSubject: subjectForRetailer(chosenRetailerName),
      fulfillment,
      note: note.trim() || undefined,
      orderingAccessConfirmed: primaryOrderingAccess,
      pricing: pricingInput(),
      recipientEmail: recipientEmail.trim().toLowerCase(),
      retailerName: chosenRetailerName,
      ...(resolvedSourceQuoteId ? { sourceQuoteId: resolvedSourceQuoteId } : {}),
      territory: territory || undefined,
    };
  };

  const validationIssue = ({ requirePermission = false, requireRecipient = true } = {}) => {
    if (resolvedSourceQuoteId && sourceLoading) return { field: '', message: 'The saved quote is still loading.' };
    if (resolvedSourceQuoteId && sourceError) return { field: '', message: sourceError };
    if (retailerInvalid) return { field: 'retailer', message: 'Add a retailer or store name with at least two characters.' };
    if (requireRecipient && !emailValid) return { field: 'email', message: 'Enter a valid retailer email address.' };
    if (requireRecipient) {
      const invalidAdditional = additionalRecipients.find(recipient => !retailQuoteValidEmail(recipient.email));
      if (invalidAdditional) return { field: 'recipients', message: 'Enter a valid email address for every additional contact.' };
      const normalizedEmails = quoteRecipients.map(recipient => recipient.email);
      if (new Set(normalizedEmails).size !== normalizedEmails.length) return { field: 'recipients', message: 'Each contact needs a different email address.' };
      const missingBasis = additionalRecipients.find(recipient => !['provided_directly', 'retailer_staff_direction'].includes(recipient.basis));
      if (missingBasis) return { field: 'recipients', message: 'Choose how each additional contact asked to receive the quote.' };
      const incompleteDirection = additionalRecipients.find(recipient => recipient.basis === 'retailer_staff_direction' && !retailQuoteClean(recipient.directionSource, 120));
      if (incompleteDirection) return { field: 'recipients', message: 'For a staff-directed contact, add who directed you.' };
    }
    if (caseCostInvalid) return { field: 'caseCost', message: priceMode === 'custom' ? 'Enter a custom case cost from $1.00 to $250.00.' : 'Choose a current wholesale price preset.' };
    if (msrpInvalid) return { field: 'msrp', message: 'Enter a suggested retail price from $1.00 to $25.00.' };
    if (!selectedSkuIds.length) return { field: 'products', message: 'Choose at least one product.' };
    if (fulfillmentInvalid) return { field: 'fulfillment', message: 'Complete both the delivery provider and the customer-facing delivery note, or remove delivery details.' };
    if (emailSubjectInvalid) return { field: 'subject', message: emailSubjectError };
    if (requirePermission && !permissionConfirmed) return { field: 'consent', message: 'Confirm that the retailer asked you to send this quote.' };
    if (live && !optionsReady) return { field: '', message: optionsError || 'Current pricing is still loading.' };
    return null;
  };

  const focusValidationIssue = issue => {
    if (!issue || !issue.field) return;
    const target = {
      retailer: retailerRef.current,
      email: emailRef.current,
      recipients: recipientsRef.current,
      caseCost: customCaseCostRef.current,
      msrp: msrpRef.current,
      products: productsRef.current,
      fulfillment: fulfillmentRef.current,
      subject: subjectRef.current,
      consent: consentRef.current,
    }[issue.field];
    if (!target) return;
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => target.focus({ preventScroll: true }), 180);
    });
  };

  const chooseCustomerMatch = match => {
    const matchId = retailQuoteClean(match && match.id, 120);
    const matchName = retailQuoteClean(match && (match.displayName || match.display_name || match.legalName || match.legal_name || match.name), 180);
    setCustomerId(matchId);
    setPermissionConfirmed(false);
    if (matchName) setRetailerName(matchName);
    const matchTerritory = retailQuoteClean(match && match.territory, 160);
    if (matchTerritory && matchTerritory.toLowerCase() !== territory.toLowerCase()) {
      setTerritory(matchTerritory);
      clearFulfillment();
    }
    setReviewedPricing(null);
    setSendAttempted(true);
    setMatches([]);
    setApproval(null);
    setNotice('');
    setError('Review the matched customer, then confirm the retailer requested this quote before sending.');
    requestIdRef.current = retailQuoteRequestId();
    basisObtainedAtRef.current = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: matchTerritory || territory }) : new Date().toISOString().slice(0, 10);
    testRequestIdRef.current = retailQuoteRequestId();
    focusValidationIssue({ field: 'consent' });
  };

  const scheduleBrowserPrint = () => {
    if (printCleanupRef.current) printCleanupRef.current();
    let launchTimer = 0;
    let fallbackTimer = 0;
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      window.clearTimeout(launchTimer);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('afterprint', cleanup);
      document.body.classList.remove(RETAIL_QUOTE_PRINT_BODY_CLASS);
      if (printCleanupRef.current === cleanup) printCleanupRef.current = null;
    };
    printCleanupRef.current = cleanup;
    document.body.classList.add(RETAIL_QUOTE_PRINT_BODY_CLASS);
    window.addEventListener('afterprint', cleanup, { once: true });
    launchTimer = window.setTimeout(() => {
      try {
        window.print();
      } finally {
        fallbackTimer = window.setTimeout(cleanup, 1000);
      }
    }, 80);
  };

  useEffect(() => () => {
    if (printCleanupRef.current) printCleanupRef.current();
  }, []);

  const approvalReadRef = useRef(null);
  const priceApprovalReadScope = () => {
    const profile = rpAuthProfile();
    return JSON.stringify([profile.id, profile.role, profile.territory, profile.customer_id, profile.active]);
  };
  const loadPriceApprovals = async ({ silent = false, fresh = false } = {}) => {
    if (!live) return [];
    const scopeAtStart = priceApprovalReadScope();
    try {
      // A completed review must not reuse a read started before that write.
      if (fresh && approvalReadRef.current?.scope === scopeAtStart) await approvalReadRef.current.promise.catch(() => {});
      if (scopeAtStart !== priceApprovalReadScope()) return [];
      if (approvalReadRef.current?.scope !== scopeAtStart) {
        const pending = { scope: scopeAtStart, promise: null };
        pending.promise = rpApi('/api/retail-quotes/price-approvals').finally(() => {
          if (approvalReadRef.current === pending) approvalReadRef.current = null;
        });
        approvalReadRef.current = pending;
      }
      const response = await approvalReadRef.current.promise;
      if (scopeAtStart !== priceApprovalReadScope()) return [];
      const rows = Array.isArray(response && response.approvals) ? response.approvals : [];
      if (priceApprovalManager) setPriceApprovalQueue(rows.filter(row => ['pending', 'approved'].includes(row.status)));
      if (approval && approval.id) {
        const current = rows.find(row => row.id === approval.id);
        if (current) {
          setApproval(current);
          if (current.status === 'approved' && approval.status !== 'approved') {
            setNotice(`Manager approved ${retailQuoteMoney(current.quotedCaseCost)} per case for this exact draft.`);
            setError('');
          }
        }
      }
      return rows;
    } catch (approvalError) {
      if (!silent && scopeAtStart === priceApprovalReadScope()) setError(approvalError && approvalError.message ? approvalError.message : 'Could not refresh price approvals.');
      return [];
    }
  };

  const requestPriceApproval = async () => {
    setFormAttempted(true);
    setRecipientAttempted(true);
    const issue = validationIssue();
    if (issue) {
      setError(issue.message);
      focusValidationIssue(issue);
      return;
    }
    setBusy('approval-request');
    setError('');
    setNotice('');
    try {
      const response = await rpApi('/api/retail-quotes/price-approvals', {
        method: 'POST',
        body: JSON.stringify({ action: 'request', ...priceApprovalDraft() }),
      });
      setApproval(response.approval);
      setNotice(response.approval && response.approval.status === 'approved'
        ? 'This exact draft already has active manager approval.'
        : 'Price approval requested. This draft is locked to the amount, products, contacts, terms, subject, and message shown here.');
    } catch (approvalError) {
      setError(approvalError && approvalError.message ? approvalError.message : 'Could not request manager price approval.');
    } finally {
      setBusy('');
    }
  };

  const reviewPriceApproval = async (approvalId, action) => {
    if (!priceApprovalManager || !approvalId) return;
    setBusy(`approval-${action}-${approvalId}`);
    setError('');
    try {
      await rpApi('/api/retail-quotes/price-approvals', {
        method: 'POST',
        body: JSON.stringify({ action, approvalId }),
      });
      await loadPriceApprovals({ silent: true, fresh: true });
      setNotice(action === 'approve' ? 'The exact custom quote draft is approved for 24 hours.' : 'The custom price request was declined.');
    } catch (approvalError) {
      setError(approvalError && approvalError.message ? approvalError.message : 'Could not review this price request.');
    } finally {
      setBusy('');
    }
  };

  const efficientApprovalPolling = rpProfileHasFeature(null, 'performance_read_efficiency');
  useEffect(() => {
    if (!live || (!priceApprovalManager && !(approval && approval.id && approval.status === 'pending'))) return undefined;
    let cancelled = false;
    const efficient = efficientApprovalPolling;
    const refresh = async () => {
      if (!cancelled && (!efficient || document.visibilityState !== 'hidden')) await loadPriceApprovals({ silent: true });
    };
    refresh();
    const timer = window.setInterval(refresh, efficient ? 60000 : 15000);
    if (efficient) document.addEventListener('visibilitychange', refresh);
    return () => { cancelled = true; window.clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [live, priceApprovalManager, approval && approval.id, approval && approval.status, efficientApprovalPolling]);

  const previewPricing = async (validationOptions = {}) => {
    const issue = validationIssue(validationOptions);
    if (issue) throw new Error(issue.message);
    if (!live) return localPricing;
    const response = await rpApi('/api/retail-quotes/preview', {
      method: 'POST',
      body: JSON.stringify({
        ...priceApprovalDraft(),
        ...(approval && approval.status === 'approved' ? { approvalId: approval.id } : {}),
      }),
    });
    if (!response || !response.pricing) throw new Error('The pricing preview was incomplete.');
    setReviewedPricing(response.pricing);
    return response.pricing;
  };

  const printQuote = async () => {
    setError('');
    setNotice('');
    setFormAttempted(true);
    setRecipientAttempted(true);
    const issue = validationIssue();
    if (issue) {
      setError(issue.message);
      focusValidationIssue(issue);
      return;
    }
    const previewWindow = live ? window.open('', '_blank') : null;
    if (previewWindow) previewWindow.opener = null;
    setBusy('print');
    try {
      const pricing = await previewPricing();
      if (pricing && pricing.approval && pricing.approval.required) {
        if (previewWindow) previewWindow.close();
        setApproval(pricing.approval);
        setError('Request one-time manager approval for this exact draft before printing or sending.');
        focusValidationIssue({ field: 'caseCost' });
        return;
      }
      if (live && (!Array.isArray(pricing && pricing.claims) || !pricing.claims.length)) {
        if (previewWindow) previewWindow.close();
        setError('Current approved product claims could not be verified. Refresh the quote before printing.');
        return;
      }
      if (!live) {
        setNotice('Demo print preview is ready.');
        scheduleBrowserPrint();
        return;
      }
      const response = await rpFetchProtected('/api/retail-quotes/preview-pdf', {
        method: 'POST',
        headers: { Accept: 'application/pdf', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(approval && approval.status === 'approved' ? { approvalId: approval.id } : {}),
          approvalRecipients: priceApprovalRecipients(),
          retailerName: retailerName.trim(),
          contactName: contactName.trim() || undefined,
          customerId: customerId || undefined,
          emailSubject,
          recipientEmail: recipientEmail.trim().toLowerCase(),
          note: note.trim() || undefined,
          fulfillment,
          orderingAccessEnabled: primaryOrderingAccess,
          pricing: pricingInput(),
          ...(resolvedSourceQuoteId ? { sourceQuoteId: resolvedSourceQuoteId } : {}),
          territory: territory || undefined,
        }),
        timeoutMs: 60000,
      });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      if (previewWindow) previewWindow.location.href = objectUrl;
      else window.open(objectUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 300000);
      setNotice('Your polished sell sheet opened in a new tab.');
    } catch (printError) {
      if (previewWindow) previewWindow.close();
      const printPayload = printError && printError.payload || {};
      const printErrorCode = retailQuoteClean(printError && printError.code || printPayload.code, 80);
      if (['approval_required', 'custom_price_approval_required'].includes(printErrorCode)) {
        setApproval(printPayload.approval || { required: true, route: 'Sales Manager' });
        focusValidationIssue({ field: 'caseCost' });
      }
      setError(printError && printError.message ? printError.message : 'Could not prepare the printable quote.');
    } finally {
      setBusy('');
    }
  };

  const sendTestEmail = async () => {
    setTestStatus(null);
    setApproval(null);
    setFormAttempted(true);
    const issue = validationIssue({ requireRecipient: false });
    if (issue) {
      setTestStatus({ kind: 'error', message: issue.message });
      focusValidationIssue(issue);
      return;
    }
    if (!live) {
      setTestStatus({ kind: 'error', message: 'Sign in to send a test email.' });
      return;
    }
    if (!testRecipientEmail) {
      setTestStatus({ kind: 'error', message: 'Add a valid email address to your Ranch Power profile before sending a test.' });
      return;
    }
    if (testInFlightRef.current) return;
    testInFlightRef.current = true;
    setBusy('test');
    try {
      const pricing = await previewPricing({ requireRecipient: false });
      if (pricing && pricing.approval && pricing.approval.required) {
        setApproval(pricing.approval);
        setTestStatus({ kind: 'error', message: 'Request one-time manager approval for this exact draft before sending a test.' });
        focusValidationIssue({ field: 'caseCost' });
        return;
      }
      if (!Array.isArray(pricing && pricing.claims) || !pricing.claims.length) {
        setTestStatus({ kind: 'error', message: 'Current approved product claims could not be verified. Refresh the quote before sending a test.' });
        return;
      }
      const response = await rpApi('/api/retail-quotes/test-email', {
        method: 'POST',
        headers: { 'Idempotency-Key': testRequestIdRef.current },
        body: JSON.stringify({
          ...(approval && approval.status === 'approved' ? { approvalId: approval.id } : {}),
          approvalRecipients: priceApprovalRecipients(),
          clientRequestId: testRequestIdRef.current,
          retailerName: retailerName.trim(),
          contactName: selectedTestPersona.contactName || undefined,
          customerId: customerId || undefined,
          emailSubject,
          ...(retailQuoteValidEmail(selectedTestPersona.email) ? { recipientEmail: selectedTestPersona.email } : {}),
          territory: territory || undefined,
          note: note.trim() || undefined,
          fulfillment,
          orderingAccessEnabled: selectedTestPersona.orderingAccessConfirmed === true,
          pricing: pricingInput(),
          pricingSnapshot: pricing,
          ...(resolvedSourceQuoteId ? { sourceQuoteId: resolvedSourceQuoteId } : {}),
        }),
      });
      const recordsNotCreated = response && response.customerCreated === false && response.quoteCreated === false;
      setTestStatus({ kind: 'success', message: `Test sent to ${testRecipientEmail}. The retailer was not contacted.${recordsNotCreated ? ' No customer or quote was created.' : ''}` });
      testRequestIdRef.current = retailQuoteRequestId();
    } catch (testError) {
      const payload = testError && testError.payload || {};
      if (testError && ['approval_required', 'custom_price_approval_required'].includes(testError.code)) {
        setApproval(payload.approval || { required: true, route: 'Sales Manager' });
      }
      const failureMessage = testError && testError.message
        ? testError.message
        : 'Could not send the test email.';
      setTestStatus({
        kind: 'error',
        message: /retailer (?:was|will) not contacted/i.test(failureMessage)
          ? failureMessage
          : `${failureMessage} The retailer was not contacted.`,
      });
      // Keep this id after every failure. Network and provider errors can be
      // ambiguous, so a retry must reconcile the same provider request.
    } finally {
      testInFlightRef.current = false;
      setBusy('');
    }
  };

  const sendQuote = async (customerOverride) => {
    setError('');
    setNotice('');
    setMatches([]);
    setApproval(null);
    setRecoveryQuoteId('');
    setRecoveryAvailable(false);
    setFormAttempted(true);
    setRecipientAttempted(true);
    setSendAttempted(true);
    const issue = validationIssue({ requirePermission: true });
    if (issue) {
      setError(issue.message);
      focusValidationIssue(issue);
      return;
    }
    if (!live) {
      setError('Sign in to send a retailer quote. You can still review and print this demo.');
      return;
    }
    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    setBusy('send');
    try {
      const pricing = await previewPricing();
      if (pricing && pricing.approval && pricing.approval.required) {
        setApproval(pricing.approval);
        setError('Request one-time manager approval for this exact draft before sending.');
        return;
      }
      const chosenCustomerId = retailQuoteClean(customerOverride && customerOverride.id, 120) || customerId || undefined;
      const chosenRetailerName = retailQuoteClean(customerOverride && (customerOverride.displayName || customerOverride.display_name || customerOverride.legalName || customerOverride.legal_name || customerOverride.name), 180) || retailerName.trim();
      const currentTerritoryDate = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory }) : new Date().toISOString().slice(0, 10);
      const basisObtainedAt = retailQuoteFrozenBasisDate(basisObtainedAtRef.current, currentTerritoryDate);
      basisObtainedAtRef.current = basisObtainedAt;
      const recipientPayload = quoteRecipients.map(recipient => ({
        contactName: recipient.contactName || undefined,
        email: recipient.email,
        role: recipient.role || undefined,
        contactBasis: recipient.contactBasis,
        contactBasisDetail: retailQuoteRecipientEvidence(recipient),
        basisObtainedAt,
        permissionConfirmed: true,
        orderingAccessConfirmed: recipient.orderingAccessConfirmed === true,
      }));
      const response = await rpApi('/api/retail-quotes/send', {
        method: 'POST',
        headers: { 'Idempotency-Key': requestIdRef.current },
        body: JSON.stringify({
          ...(approval && approval.status === 'approved' ? { approvalId: approval.id } : {}),
          approvalRecipients: priceApprovalRecipients(),
          clientRequestId: requestIdRef.current,
          ...(chosenCustomerId ? { customerId: chosenCustomerId } : {}),
          retailerName: chosenRetailerName,
          contactName: contactName.trim() || undefined,
          emailSubject: subjectForRetailer(chosenRetailerName),
          recipientEmail: recipientEmail.trim().toLowerCase(),
          territory: territory || undefined,
          note: note.trim() || undefined,
          fulfillment,
          contactBasis: 'provided_directly',
          contactBasisDetail: recipientPayload[0].contactBasisDetail,
          basisObtainedAt,
          permissionConfirmed: true,
          orderingAccessConfirmed: recipientPayload[0].orderingAccessConfirmed,
          ...(recipientPayload.length > 1 ? { recipients: recipientPayload } : {}),
          ...(resolvedSourceQuoteId ? { sourceQuoteId: resolvedSourceQuoteId } : {}),
          pricing: pricingInput(),
          pricingSnapshot: pricing,
        }),
      });
      setSent(response);
      const responseSummary = response && response.deliverySummary;
      const responseTotal = Number(responseSummary && responseSummary.total || recipientPayload.length);
      const responseSent = Number(responseSummary && responseSummary.sent || (response && response.sent ? responseTotal : 0));
      const responseNeedsAttention = Number(responseSummary && responseSummary.needsAttention || 0);
      setNotice(response && response.partial
        ? `Sent to ${responseSent} of ${responseTotal}; ${responseNeedsAttention || Math.max(0, responseTotal - responseSent)} need${(responseNeedsAttention || Math.max(0, responseTotal - responseSent)) === 1 ? 's' : ''} attention.`
        : `Quote sent to ${responseTotal} contact${responseTotal === 1 ? '' : 's'}.`);
      if (typeof rpRefreshAfterMutation === 'function') await rpRefreshAfterMutation('Retail quote sent');
    } catch (sendError) {
      const payload = sendError && sendError.payload || {};
      const errorCode = retailQuoteClean(sendError && sendError.code || payload.code, 80);
      if (errorCode === 'customer_match_required') {
        setMatches(Array.isArray(payload.matches) ? payload.matches : []);
        setError('This retailer or one of these contacts matches an existing customer. Choose the right account before sending.');
      } else if (['approval_required', 'custom_price_approval_required'].includes(errorCode)) {
        setApproval(payload.approval || { required: true, route: 'Sales Manager' });
        setError(sendError.message || 'Request one-time manager approval for this exact draft before sending.');
      } else if (errorCode === 'customer_assignment_conflict') {
        setError('A matching customer is outside your assigned sales scope. Ask a sales manager to send the quote.');
      } else if (['duplicate_quote', 'quote_retry_available'].includes(errorCode)) {
        const savedQuoteId = retailQuoteClean(payload.quoteId || payload.quote_id || payload.quote && payload.quote.id, 80);
        setRecoveryQuoteId(savedQuoteId);
        setRecoveryAvailable(true);
        setError(savedQuoteId
          ? 'A saved version of this quote already exists. Open it, or edit it as a new revision instead of starting over.'
          : (sendError.message || 'A saved version of this quote already exists. Open Saved Quotes to continue.'));
      } else {
        setError(sendError && sendError.message ? sendError.message : 'Could not send this quote. Nothing was sent.');
      }
    } finally {
      sendInFlightRef.current = false;
      setBusy('');
    }
  };

  const resetQuote = () => {
    setRetailerName('');
    setContactName('');
    setRecipientEmail('');
    setAdditionalRecipients([]);
    setPrimaryOrderingAccess(false);
    setTestPersonaId('primary');
    setCustomerId('');
    setTerritory(retailQuoteClean(options.territory, 160) || territories[0] || '');
    setPriceMode('price_list');
    setCustomCaseCost('');
    setMsrp(String(RETAIL_QUOTE_DEFAULT_MSRP));
    setSelectedSkuIds(RETAIL_QUOTE_ORIGINAL_SKUS.filter(skuId => products.some(product => product.id === skuId)));
    clearFulfillment({ close: true });
    setNote('');
    setSubjectPresetId(RETAIL_QUOTE_DEFAULT_SUBJECT_PRESET_ID);
    setCustomEmailSubject('');
    setPermissionConfirmed(false);
    setFormAttempted(false);
    setRecipientAttempted(false);
    setSendAttempted(false);
    setReviewedPricing(null);
    setError('');
    setNotice('');
    setTestStatus(null);
    setMatches([]);
    setApproval(null);
    setSent(null);
    requestIdRef.current = retailQuoteRequestId();
    basisObtainedAtRef.current = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory }) : new Date().toISOString().slice(0, 10);
    testRequestIdRef.current = retailQuoteRequestId();
  };

  const sentQuote = sent && sent.quote || {};
  const sentCustomer = sent && sent.customer || {};
  const sentQuoteId = retailQuoteClean(sentQuote.id || sent && (sent.quoteId || sent.quote_id), 80);
  const sentCustomerId = retailQuoteClean(sentCustomer.id || sentQuote.customerId || sentQuote.customer_id, 120);
  const pdfUrl = retailQuoteClean(sent && sent.pdfUrl, 1000);
  const sourceQuoteNumber = retailQuoteClean(sourceRecord && sourceRecord.quote && (sourceRecord.quote.quoteNumber || sourceRecord.quote.quote_number), 80);
  const sentDeliveries = Array.isArray(sent && sent.deliveries) && sent.deliveries.length
    ? sent.deliveries
    : (sent ? [{ email: recipientEmail.trim().toLowerCase(), contactName: contactName.trim(), status: 'sent', sent: true }] : []);
  const sentCount = sent && sent.deliverySummary ? Number(sent.deliverySummary.sent || 0) : sentDeliveries.filter(delivery => delivery.sent).length;
  const sentTotal = sent && sent.deliverySummary ? Number(sent.deliverySummary.total || sentDeliveries.length) : sentDeliveries.length;
  const sentNeedsAttention = sent && sent.deliverySummary ? Number(sent.deliverySummary.needsAttention || 0) : sentDeliveries.filter(delivery => !delivery.sent).length;
  const sentPending = sent && sent.deliverySummary ? Number(sent.deliverySummary.pending || 0) : 0;
  const sentPartial = Boolean(sent && sent.partial) || sentCount < sentTotal;
  const hasRetryableDeliveries = sentDeliveries.some(delivery => delivery.retryable === true || (!Object.hasOwn(delivery, 'retryable') && !delivery.sent));
  const openSentPdf = async () => {
    if (!pdfUrl || busy) return;
    setError('');
    setBusy('pdf');
    try {
      await rpOpenProtectedFile(pdfUrl);
    } catch (pdfError) {
      setError(pdfError && pdfError.message ? pdfError.message : 'Could not open the quote PDF.');
    } finally {
      setBusy('');
    }
  };
  const openRecoveryPdf = async () => {
    if (!recoveryQuoteId || busy) return;
    setBusy('recovery-pdf');
    try {
      await rpOpenProtectedFile(`/api/retail-quotes/pdf?id=${encodeURIComponent(recoveryQuoteId)}`);
    } catch (pdfError) {
      setError(pdfError && pdfError.message ? pdfError.message : 'Could not open the saved quote PDF.');
    } finally {
      setBusy('');
    }
  };
  const openSavedQuotes = preferredCustomerId => {
    const filterCustomerId = retailQuoteClean(preferredCustomerId || customerId, 120);
    nav.push('retail-quotes', filterCustomerId ? { id: filterCustomerId } : {});
  };

  if (sent) return (
    <div className="rp-fade retail-quote-screen" data-retail-quote-success="true">
      {isNarrow ? <MHeader title={sentPartial ? 'Delivery Update' : 'Quote Sent'} sub="Retail pricing" nav={nav} back backLabel="Back to sales" /> : <div className="retail-quote-desktop-head"><PageHead eyebrow="Retail pricing" title={sentPartial ? 'Some emails need attention' : 'Quote sent'} sub="The customer record and quote history are ready for follow-up." /></div>}
      <div className="retail-quote-success">
        <div className={`retail-quote-success-icon ${sentPartial ? 'is-partial' : ''}`}><Icon name={sentPartial ? 'alert' : 'check'} size={34} sw={2.4} /></div>
        <span className="eyebrow">{sentPartial ? 'Delivery update' : 'Sent'}</span>
        <h1>{retailQuoteClean(sentQuote.quoteNumber || sentQuote.quote_number) || 'Retail quote'}</h1>
        {sentQuoteId && typeof EmailDeliveryStatus === 'function' && <EmailDeliveryStatus entityType="quote" entityId={sentQuoteId} />}
        <p>{sentPartial ? <><strong>Sent to {sentCount} of {sentTotal} contacts.</strong> {sentNeedsAttention ? `${sentNeedsAttention} need${sentNeedsAttention === 1 ? 's' : ''} attention.` : `${sentPending} remain${sentPending === 1 ? 's' : ''} pending.`}</> : <><strong>Sent a private copy to all {sentTotal} contact{sentTotal === 1 ? '' : 's'}.</strong></>} {sent.customerCreated ? 'A prospect customer was created. Open the customer to review intake and approval.' : 'The existing customer account was reused.'}</p>
        {sentDeliveries.length > 1 && <div className="retail-quote-delivery-results" aria-label="Recipient delivery status">{sentDeliveries.map(delivery => {
          const status = retailQuoteClean(delivery.status, 40).toLowerCase();
          const needsAttention = ['failed', 'bounced', 'complained', 'suppressed'].includes(status);
          const successful = Boolean(delivery.sent) && !needsAttention;
          const statusLabel = status === 'delivered' ? 'Delivered'
            : status === 'delayed' ? 'Delayed'
              : status === 'bounced' ? 'Bounced'
                : status === 'complained' ? 'Complaint'
                  : status === 'suppressed' ? 'Suppressed'
                    : status === 'failed' ? 'Needs attention'
                      : successful ? 'Sent' : 'Pending';
          return <div key={delivery.deliveryId || delivery.email} className={successful ? 'is-sent' : 'is-attention'}><Icon name={successful ? 'check' : 'alert'} size={15} /><span><strong>{delivery.contactName || delivery.email}</strong><small>{delivery.contactName ? delivery.email : ''}</small></span><b>{statusLabel}</b></div>;
        })}</div>}
        {Array.isArray(sent.warnings) && sent.warnings.length > 0 && <div className="retail-quote-delivery-warning" role="status">{sent.warnings.map((warning, index) => <p key={`${warning}-${index}`}>{warning}</p>)}</div>}
        {error && <div className="retail-quote-message retail-quote-message-error" role="alert" style={{ width: '100%', marginTop: 16 }}><Icon name="alert" size={17} /><span>{error}</span></div>}
        <div className="retail-quote-success-actions">
          {sentPartial && (sentPending > 0 || hasRetryableDeliveries) && <button type="button" className="btn btn-primary" disabled={Boolean(busy)} onClick={() => sendQuote()}><Icon name="refresh" size={16} />{busy === 'send' ? 'Retrying...' : 'Retry remaining'}</button>}
          {pdfUrl && <button type="button" className="btn btn-primary" disabled={Boolean(busy)} onClick={openSentPdf}><Icon name="download" size={16} />{busy === 'pdf' ? 'Opening...' : 'Download PDF'}</button>}
          {sentQuoteId && <button type="button" className="btn" onClick={() => nav.push('retail-quote-edit', { id: sentQuoteId })}><Icon name="pen" size={16} />Edit &amp; Resend</button>}
          {sentCustomerId && <button type="button" className="btn" onClick={() => nav.push('rep-customer', { id: sentCustomerId })}><Icon name="customers" size={16} />Open Customer</button>}
          <button type="button" className="btn" onClick={resolvedSourceQuoteId ? () => nav.push('quick-retail-quote') : resetQuote}><Icon name="plus" size={16} />New Quote</button>
          <button type="button" className="btn btn-ghost" onClick={() => openSavedQuotes(sentCustomerId)}><Icon name="doc" size={16} />Saved Quotes</button>
          <button type="button" className="btn btn-ghost" onClick={() => nav.pop()}>Done</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="rp-fade retail-quote-screen" data-retail-quote-screen="true">
      {isNarrow ? (
        <MHeader title={resolvedSourceQuoteId ? 'Edit & Resend' : 'Quick Retail Quote'} sub={resolvedSourceQuoteId ? 'Send a new revision' : 'Build and send in under a minute'} nav={nav} back backLabel="Back to sales" dark right={<button type="button" className="retail-quote-header-action" onClick={() => openSavedQuotes()}><Icon name="doc" size={15} /><span>Saved</span></button>} />
      ) : (
        <div className="retail-quote-desktop-head">
          <PageHead eyebrow="Sales tool" title={resolvedSourceQuoteId ? 'Edit & Resend' : 'Quick Retail Quote'} sub={resolvedSourceQuoteId ? 'Review this saved quote, then send a new revision. The original stays unchanged.' : 'Create a professional retailer price sheet, send it, and capture the prospect in one fast workflow.'} actions={<div className="retail-quote-history-head-actions"><button type="button" className="btn" onClick={() => openSavedQuotes()}><Icon name="doc" size={15} />Saved Quotes</button><button type="button" className="btn" onClick={() => nav.pop()}><Icon name="arrowL" size={15} />Back</button></div>} />
        </div>
      )}

      <div className="retail-quote-workspace">
        <main className="retail-quote-builder" aria-label="Retail quote details">
          <form id="retail-quote-form" className="retail-quote-builder-form" noValidate aria-busy={sourceLoading || undefined} onSubmit={event => { event.preventDefault(); if (!busy) sendQuote(); }}>
          {sourceLoading && <div className="retail-quote-callout"><Icon name="refresh" size={16} /><span>Loading the saved quote as a new revision...</span></div>}
          {sourceError && <div className="retail-quote-callout retail-quote-callout-danger" role="alert"><Icon name="alert" size={16} /><span>{sourceError}</span><button type="button" className="btn btn-sm" onClick={() => openSavedQuotes()}>Open Saved Quotes</button></div>}
          {resolvedSourceQuoteId && sourceRecord && !sourceLoading && <div className="retail-quote-revision-banner" role="status"><Icon name="refresh" size={17} /><span><strong>New revision{sourceQuoteNumber ? ` of ${sourceQuoteNumber}` : ''}</strong><small>This stays linked to the existing customer. The saved quote remains unchanged, ordering access is off, and permission must be confirmed again.</small></span></div>}
          {sourceWarning && <div className="retail-quote-callout retail-quote-callout-warning" role="status"><Icon name="alert" size={16} /><span>{sourceWarning}</span></div>}
          {exactCustomer.loading && <div className="retail-quote-callout"><Icon name="refresh" size={16} /><span>Loading customer details...</span></div>}
          {exactCustomer.error && <div className="retail-quote-callout retail-quote-callout-danger"><Icon name="alert" size={16} /><span>The linked customer could not load. Email matching will still prevent a duplicate account.</span></div>}
          {optionsError && <div className="retail-quote-callout retail-quote-callout-danger" role="alert"><Icon name="alert" size={16} /><span><strong>Current pricing unavailable.</strong> {optionsError} Sending is disabled until it reloads.</span></div>}

          {priceApprovalManager && priceApprovalQueue.some(row => row.status === 'pending') && <section className="retail-quote-form-card" aria-label="Custom price approval queue">
            <div className="retail-quote-step-head"><span><Icon name="flag" size={16} /></span><div><strong>Custom price approvals</strong><small>Approve only the exact retailer draft and amount shown. Approval expires after 24 hours and one quote send.</small></div></div>
            <div className="retail-quote-delivery-results">{priceApprovalQueue.filter(row => row.status === 'pending').map(row => <div key={row.id} className="is-attention"><Icon name="flag" size={15} /><span><strong>{row.retailerName || 'Retail quote'} · {retailQuoteMoney(row.quotedCaseCost)} / case</strong><small>{row.requester && row.requester.name || 'Sales rep'} · {row.terms || 'Prepaid'} · SRP {retailQuoteMoney(row.msrp)} · {(row.products || []).map(product => product.skuCode || product.name).join(', ')}</small>{row.emailSubject && <small>Subject: {row.emailSubject}</small>}<small>{(row.recipientEmails || []).join(', ')}{row.note ? ` · “${row.note}”` : ''}</small></span><div className="retail-quote-recovery-actions"><button type="button" className="btn btn-primary btn-sm" disabled={Boolean(busy)} onClick={() => reviewPriceApproval(row.id, 'approve')}>{busy === `approval-approve-${row.id}` ? 'Approving...' : 'Approve exact draft'}</button><button type="button" className="btn btn-sm" disabled={Boolean(busy)} onClick={() => reviewPriceApproval(row.id, 'reject')}>{busy === `approval-reject-${row.id}` ? 'Declining...' : 'Decline'}</button></div></div>)}</div>
          </section>}

          <section className="retail-quote-form-card">
            <div className="retail-quote-step-head"><span>1</span><div><strong>Who is this for?</strong><small>Send each contact a private copy. Add a manager or buyer only when they asked to receive it.</small></div></div>
            <div className="retail-quote-field-grid">
              <label><span className="field-label">Retailer / store <b aria-hidden="true">*</b><span className="sr-only"> required</span></span><input ref={retailerRef} className="field" required minLength="2" aria-invalid={formAttempted && retailerInvalid} aria-describedby={formAttempted && retailerInvalid ? 'retail-quote-retailer-error' : undefined} autoComplete="organization" value={retailerName} onChange={event => updateRetailerIdentity(event.target.value)} placeholder="e.g. Mountain Market" maxLength="180" autoFocus={!id} />{formAttempted && retailerInvalid && <small id="retail-quote-retailer-error" className="retail-quote-field-error">Enter at least two characters.</small>}</label>
              <label><span className="field-label">Primary contact name <em>optional</em></span><input className="field" autoComplete="name" value={contactName} onChange={event => updateContactIdentity(event.target.value)} placeholder="Buyer or owner name" maxLength="120" /></label>
              <label className="retail-quote-field-wide"><span className="field-label">Primary email <b aria-hidden="true">*</b><span className="sr-only"> required when sending to the retailer</span></span><input ref={emailRef} className="field" type="email" required inputMode="email" aria-invalid={recipientAttempted && !emailValid} aria-describedby={recipientAttempted && !emailValid ? 'retail-quote-email-error' : undefined} autoComplete="email" autoCapitalize="none" spellCheck="false" value={recipientEmail} onChange={event => updateRecipientEmail(event.target.value)} placeholder="buyer@store.ca" maxLength="320" />{recipientAttempted && !emailValid && <small id="retail-quote-email-error" className="retail-quote-field-error">Enter a valid email address.</small>}</label>
              {territoryChoices.length > 1 && <label className="retail-quote-field-wide"><span className="field-label">Sales territory</span><select className="field" value={territory} disabled={Boolean(customerId)} onChange={event => updateTerritory(event.target.value)}>{territoryChoices.map(value => <option key={value} value={value}>{value}</option>)}</select><small>{customerId ? 'This follows the existing customer account.' : 'Used for local delivery options and customer assignment.'}</small></label>}
            </div>
            <label className={`retail-quote-order-access-toggle ${primaryOrderingAccess ? 'is-active' : ''}`}><input type="checkbox" checked={primaryOrderingAccess} onChange={event => { setPrimaryOrderingAccess(event.target.checked); invalidate(); }} /><span><strong>Give this person secure ordering access</strong><small>Adds a private Start your order button to their email and a verification-safe QR code to their PDF. They can finish account setup if needed.</small></span></label>
            <div ref={recipientsRef} className="retail-quote-extra-contacts" tabIndex="-1">
              {(savedContactSuggestions.length > 0 && additionalRecipients.length < 2) && <div className="retail-quote-contact-suggestions"><span>Saved contacts</span>{savedContactSuggestions.slice(0, 3).map(contact => <button type="button" className="btn btn-sm" key={contact.email} onClick={() => addAdditionalRecipient(contact)}><Icon name="plus" size={13} />{contact.name || contact.email}</button>)}</div>}
              {additionalRecipients.map((recipient, index) => {
                const invalidEmail = sendAttempted && !retailQuoteValidEmail(recipient.email);
                const missingBasis = sendAttempted && !['provided_directly', 'retailer_staff_direction'].includes(recipient.basis);
                const incompleteDirection = sendAttempted && recipient.basis === 'retailer_staff_direction' && !retailQuoteClean(recipient.directionSource, 120);
                return <div className="retail-quote-contact-card" key={recipient.id}>
                  <div className="retail-quote-contact-card-head"><span><strong>Additional contact {index + 1}</strong><small>Receives a separate private email</small></span><button type="button" className="btn btn-sm btn-ghost" onClick={() => removeAdditionalRecipient(recipient.id)} aria-label={`Remove additional contact ${index + 1}`}><Icon name="close" size={14} />Remove</button></div>
                  <div className="retail-quote-contact-fields">
                    <label><span className="field-label">Name <em>optional</em></span><input className="field" autoComplete="name" value={recipient.name} onChange={event => updateAdditionalRecipient(recipient.id, { name: event.target.value })} placeholder="Manager or buyer" maxLength="120" /></label>
                    <label><span className="field-label">Email <b aria-hidden="true">*</b></span><input className="field" type="email" required inputMode="email" autoCapitalize="none" spellCheck="false" value={recipient.email} aria-invalid={invalidEmail} onChange={event => updateAdditionalRecipient(recipient.id, { email: event.target.value })} placeholder="manager@store.ca" maxLength="320" />{invalidEmail && <small className="retail-quote-field-error">Enter a valid email address.</small>}</label>
                    <label className="retail-quote-contact-role"><span className="field-label">Role <em>optional</em></span><input className="field" value={recipient.role} onChange={event => updateAdditionalRecipient(recipient.id, { role: event.target.value })} placeholder="Purchasing manager" maxLength="120" /></label>
                  </div>
                  <fieldset className="retail-quote-contact-basis"><legend>How did they ask to receive it? <b aria-hidden="true">*</b></legend><div><button type="button" className={recipient.basis === 'provided_directly' ? 'is-active' : ''} aria-pressed={recipient.basis === 'provided_directly'} onClick={() => updateAdditionalRecipient(recipient.id, { basis: 'provided_directly', directionSource: '' })}>They gave me their email</button><button type="button" className={recipient.basis === 'retailer_staff_direction' ? 'is-active' : ''} aria-pressed={recipient.basis === 'retailer_staff_direction'} onClick={() => updateAdditionalRecipient(recipient.id, { basis: 'retailer_staff_direction', directionSource: recipient.directionSource || contactName.trim() })}>Store staff directed me</button></div>{missingBasis && <small className="retail-quote-field-error">Choose one option.</small>}</fieldset>
                  {recipient.basis === 'retailer_staff_direction' && <label className="retail-quote-direction-source"><span className="field-label">Who directed you? <b aria-hidden="true">*</b></span><input className="field" value={recipient.directionSource} onChange={event => updateAdditionalRecipient(recipient.id, { directionSource: event.target.value })} placeholder="Store manager or Jamie at the store" maxLength="120" /><small>One requested introduction only; follow up again after they reply.</small>{incompleteDirection && <small className="retail-quote-field-error">Add who directed you.</small>}</label>}
                  <label className={`retail-quote-order-access-toggle ${recipient.orderingAccessConfirmed ? 'is-active' : ''}`}><input type="checkbox" checked={recipient.orderingAccessConfirmed === true} onChange={event => updateAdditionalRecipient(recipient.id, { orderingAccessConfirmed: event.target.checked })} /><span><strong>Give this person secure ordering access</strong><small>This is separate from sending the quote. Only enable it for someone authorized to use this retailer's ordering account.</small></span></label>
                </div>;
              })}
              {additionalRecipients.length < 2 && <button type="button" className="btn retail-quote-add-contact" onClick={() => addAdditionalRecipient()}><Icon name="plus" size={15} />Add another contact <small>optional</small></button>}
              {sendAttempted && quoteRecipients.some((recipient, index) => quoteRecipients.findIndex(row => row.email === recipient.email) !== index) && <small className="retail-quote-field-error">Each contact needs a different email address.</small>}
            </div>
          </section>

          <section className="retail-quote-form-card">
            <div className="retail-quote-step-head"><span>2</span><div><strong>Set their pricing</strong><small>Use this account's default case price, or enter a custom amount when authorized.</small></div></div>
            <fieldset className="retail-quote-fieldset"><legend>Wholesale price</legend>
              <div className="retail-quote-choice-grid">
                <button type="button" className={`retail-quote-choice ${priceMode === 'price_list' ? 'is-active' : ''}`} aria-pressed={priceMode === 'price_list'} onClick={() => { setPriceMode('price_list'); setCustomCaseCost(''); invalidate(); }}><strong>{selectedModel.name || RETAIL_QUOTE_DEFAULT_PRICE_MODEL}</strong><span>{retailQuoteMoney(selectedModel.caseCost)} / case</span><small>Default for this {customerId ? 'customer' : 'sales account'}</small></button>
                <button type="button" className={`retail-quote-choice retail-quote-custom-choice ${priceMode === 'custom' ? 'is-active' : ''}`} aria-pressed={priceMode === 'custom'} onClick={() => { setPriceMode('custom'); if (!customCaseCost) setCustomCaseCost(String(selectedModel.caseCost || '')); invalidate(); }}><strong>Custom price</strong><span>Enter case cost</span><small>{customPricingEnabled ? 'Authorized for your account' : 'One-time manager approval'}</small></button>
              </div>
            </fieldset>
            {priceMode === 'custom' && <label className="retail-quote-money-input"><span className="field-label">Custom wholesale case cost <span className="sr-only">required</span></span><span><b>$</b><input ref={customCaseCostRef} className="field tnum" type="number" required inputMode="decimal" min="1" max="250" step="0.01" value={customCaseCost} onChange={event => update(setCustomCaseCost, event.target.value)} aria-invalid={formAttempted && caseCostInvalid} aria-describedby={`retail-quote-custom-help${formAttempted && caseCostInvalid ? ' retail-quote-custom-error' : ''}`} /></span><small id="retail-quote-custom-help">{customPricingEnabled ? `Your account is authorized to set a custom amount. Default: ${retailQuoteMoney(selectedModel.caseCost)}.` : 'Request one-time manager approval after the retailer, contacts, products, terms, and message are ready. Any draft change requires fresh approval.'}</small>{formAttempted && caseCostInvalid && <small id="retail-quote-custom-error" className="retail-quote-field-error">Enter $1.00 to $250.00.</small>}</label>}

            <div role="note" style={{ background: 'var(--surface-2)', borderRadius: 10, padding: 12, fontSize: 12.5, lineHeight: 1.55, marginBottom: 14 }}><strong>What this quote includes</strong><div>Case cost covers product only. {displayPricing.containerFeeNote} Applicable tax is itemized on the order using the customer’s tax region and exemptions. Credit card payments add 2.4% to the amount paid by card; that conditional fee is excluded from the case cost. This price sheet has no order quantity or final payable total.</div></div>
            <fieldset className="retail-quote-fieldset"><legend>Payment terms</legend>
              <div className="retail-quote-choice-grid">
                {paymentTerms.map(value => <button type="button" key={value} className={`retail-quote-choice ${terms === value ? 'is-active' : ''}`} aria-pressed={terms === value} onClick={() => update(setTerms, value)}><strong>{value}</strong><span>{value === 'Prepaid' ? 'Paid before fulfillment' : value === 'COD' ? 'Due on delivery' : value === 'Net 30' ? 'Due within 30 days' : 'Uses this customer\'s account terms'}</span><small>Shown on the quote and saved to new prospects</small></button>)}
              </div>
            </fieldset>

            <fieldset className="retail-quote-fieldset retail-quote-msrp"><legend>Suggested retail price per can</legend>
              <div className="retail-quote-msrp-row">
                {msrpPresets.map(value => <button type="button" key={value} className={`retail-quote-msrp-chip ${Number(msrp) === value ? 'is-active' : ''}`} aria-pressed={Number(msrp) === value} onClick={() => update(setMsrp, String(value))}>{retailQuoteMoney(value)}</button>)}
                <label><span>Custom SRP <span className="sr-only">required</span></span><span><b>$</b><input ref={msrpRef} className="field tnum" type="number" required inputMode="decimal" min="1" max="25" step="0.01" value={msrp} onChange={event => update(setMsrp, event.target.value)} aria-invalid={formAttempted && msrpInvalid} aria-describedby={formAttempted && msrpInvalid ? 'retail-quote-msrp-error' : undefined} /></span>{formAttempted && msrpInvalid && <small id="retail-quote-msrp-error" className="retail-quote-field-error">Enter $1.00 to $25.00.</small>}</label>
              </div>
            </fieldset>

            <div className="retail-quote-live-economics" aria-live="polite">
              <RetailQuoteMetric label="Case cost" value={retailQuoteMoney(displayPricing.quotedCaseCost)} sub={terms} accent="var(--amber)" />
              <RetailQuoteMetric label="Cost / can" value={retailQuoteMoney(displayPricing.costPerCan)} sub={`${displayPricing.cansPerCase || 12} per case`} />
              <RetailQuoteMetric label="Profit / can" value={retailQuoteMoney(displayPricing.grossProfitPerCan)} sub="at suggested retail" accent={displayPricing.grossProfitPerCan >= 0 ? 'var(--ok)' : 'var(--danger)'} />
              <RetailQuoteMetric label="Retail margin" value={retailQuotePercent(displayPricing.grossMarginPercent)} sub="before store costs" accent={displayPricing.grossMarginPercent >= 0 ? 'var(--info)' : 'var(--danger)'} />
            </div>
            {localPricing.approval.required && <div className="retail-quote-callout retail-quote-callout-warning"><Icon name="flag" size={16} /><span><strong>{approval && approval.status === 'approved' ? 'Exact draft approved.' : approval && approval.status === 'pending' ? 'Manager review pending.' : 'Manager approval required.'}</strong> {approval && approval.status === 'approved' ? 'This custom amount can be printed and sent while the approval is active.' : approval && approval.status === 'pending' ? 'Keep this draft unchanged while a manager reviews it.' : 'Request a one-time approval for this exact quote draft.'}</span>{!customPricingEnabled && (!approval || !['pending', 'approved'].includes(approval.status)) && <button type="button" className="btn btn-sm" disabled={Boolean(busy)} onClick={requestPriceApproval}>{busy === 'approval-request' ? 'Requesting...' : 'Request approval'}</button>}{approval && approval.status === 'pending' && <button type="button" className="btn btn-sm" disabled={Boolean(busy)} onClick={() => loadPriceApprovals()}><Icon name="refresh" size={13} />Check status</button>}</div>}
          </section>

          <section className="retail-quote-form-card">
            <div className="retail-quote-step-head"><span>3</span><div><strong>Choose products</strong><small>Six original flavours are selected by default.</small></div></div>
            <div className="retail-quote-product-actions">
              <button type="button" className="btn btn-sm" onClick={() => update(setSelectedSkuIds, products.map(product => product.id))}>Select all</button>
              <button type="button" className="btn btn-sm" onClick={() => update(setSelectedSkuIds, RETAIL_QUOTE_ORIGINAL_SKUS.filter(skuId => products.some(product => product.id === skuId)))}>Original six</button>
              <button type="button" className="btn btn-sm" onClick={() => update(setSelectedSkuIds, RETAIL_QUOTE_MIX_SKUS.filter(skuId => products.some(product => product.id === skuId)))}>Mixed packs</button>
            </div>
            <fieldset ref={productsRef} className="retail-quote-products" tabIndex="-1" aria-invalid={formAttempted && !selectedSkuIds.length} aria-describedby={formAttempted && !selectedSkuIds.length ? 'retail-quote-products-error' : undefined}><legend className="sr-only">Products included in quote</legend>
              {products.map(product => {
                const selected = selectedSkuIds.includes(product.id);
                return <label key={product.id} className={`retail-quote-product-choice ${selected ? 'is-active' : ''}`}><input type="checkbox" checked={selected} onChange={() => update(setSelectedSkuIds, selected ? selectedSkuIds.filter(value => value !== product.id) : [...selectedSkuIds, product.id])} /><RetailQuoteProductBadge product={product} /><span><strong>{product.name.replace(' 355ml', '')}</strong><small>{product.pack} · SKU {product.skuCode}{product.upc ? ` · UPC ${product.upc}` : ''}</small></span><Icon name={selected ? 'check' : 'plus'} size={15} /></label>;
              })}
            </fieldset>
            <div className="retail-quote-selection-count">{selectedProducts.length} product{selectedProducts.length === 1 ? '' : 's'} included</div>
            {formAttempted && !selectedSkuIds.length && <small id="retail-quote-products-error" className="retail-quote-field-error">Choose at least one product.</small>}
          </section>

          <section ref={fulfillmentRef} className={`retail-quote-form-card retail-quote-fulfillment-card ${fulfillmentEnabled ? 'is-enabled' : ''}`} tabIndex="-1" aria-invalid={formAttempted && fulfillmentInvalid}>
            <button type="button" className="retail-quote-fulfillment-toggle" aria-expanded={fulfillmentOpen} onClick={() => fulfillmentOpen ? setFulfillmentOpen(false) : openFulfillment()}>
              <span className="retail-quote-fulfillment-icon"><Icon name="truck" size={19} /></span>
              <span><strong>{fulfillmentEnabled ? `${fulfillmentChoices.length} ordering & delivery option${fulfillmentChoices.length === 1 ? '' : 's'} added` : 'Add ordering & delivery details'}</strong><small>{fulfillmentEnabled ? `${fulfillmentChoices.map(choice => choice.label || choice.providerName).join(' or ')} - included on the quote` : 'Optional - offer one method or let the retailer choose'}</small></span>
              <Icon name={fulfillmentOpen ? 'chevU' : 'chevD'} size={17} />
            </button>
            {fulfillmentOpen && <div className="retail-quote-fulfillment-body">
              <p>Select every option you want to offer. The retailer will see them as choices, and you can adjust the wording below.</p>
              <div className="retail-quote-fulfillment-presets" role="group" aria-label="Ordering and delivery options">
                {fulfillmentProfiles.map(profileRow => {
                  const selected = fulfillmentChoices.some(choice => choice.profileId === profileRow.id);
                  return <button type="button" key={profileRow.id} className={`btn btn-sm ${selected ? 'is-active' : ''}`} aria-pressed={selected} disabled={!selected && fulfillmentChoices.length >= 4} onClick={() => toggleFulfillmentProfile(profileRow)}><Icon name={selected ? 'check' : 'plus'} size={14} />{profileRow.label}{profileRow.isDefault ? ' (default)' : ''}</button>;
                })}
                <button type="button" className="btn btn-sm" disabled={fulfillmentChoices.length >= 4} onClick={addCustomFulfillment}><Icon name="plus" size={14} />Custom option</button>
              </div>
              {fulfillmentChoices.length > 0 && <div className="retail-quote-fulfillment-choice-list">
                {fulfillmentChoices.map((choice, index) => <div className="retail-quote-fulfillment-choice" key={choice.id}>
                  <div className="retail-quote-fulfillment-choice-head"><span><b>Option {index + 1}</b><small>{choice.label}</small></span><button type="button" className="icon-btn" aria-label={`Remove ${choice.label}`} onClick={() => removeFulfillmentChoice(choice.id)}><Icon name="x" size={15} /></button></div>
                  <div className="retail-quote-fulfillment-fields">
                    <label><span className="field-label">Distributor / delivery provider</span><input className="field" value={choice.providerName} onChange={event => updateFulfillmentChoice(choice.id, { providerName: event.target.value })} placeholder="e.g. Slopeside Supply" maxLength="120" /></label>
                    <label><span className="field-label">What the retailer should know</span><textarea className="field" value={choice.customerCopy} onChange={event => updateFulfillmentChoice(choice.id, { customerCopy: event.target.value })} placeholder="Orders placed by 4 p.m. are delivered the next business day." maxLength="400" rows="3" /><small className="retail-quote-note-meta"><span>Shown on the sell sheet and in the email.</span><span className="tnum">{choice.customerCopy.length}/400</span></small></label>
                  </div>
                </div>)}
              </div>}
              {formAttempted && fulfillmentInvalid && <small className="retail-quote-field-error">Complete the provider and customer-facing note for every selected option.</small>}
              {fulfillmentEnabled && <button type="button" className="btn btn-sm retail-quote-fulfillment-remove" onClick={() => { clearFulfillment({ close: true }); invalidate(); }}><Icon name="x" size={14} />Remove all delivery details</button>}
            </div>}
          </section>

          <section className="retail-quote-form-card retail-quote-message-card">
            <div className="retail-quote-step-head"><span>4</span><div><strong>Customize the email</strong><small>Choose the subject, then add an optional note that also appears on the sell sheet.</small></div></div>
            <fieldset className="retail-quote-subject-fieldset">
              <legend>Email subject <b aria-hidden="true">*</b><span className="sr-only"> required</span></legend>
              <div className="retail-quote-subject-presets" role="group" aria-label="Email subject presets">
                {RETAIL_QUOTE_SUBJECT_PRESETS.map(preset => <button type="button" key={preset.id} className={`btn btn-sm ${subjectPresetId === preset.id ? 'is-active' : ''}`} aria-pressed={subjectPresetId === preset.id} onClick={() => chooseSubjectPreset(preset.id)}>{preset.label}</button>)}
                <button type="button" className={`btn btn-sm ${subjectPresetId === RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID ? 'is-active' : ''}`} aria-pressed={subjectPresetId === RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID} onClick={() => chooseSubjectPreset(RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID)}>Custom</button>
              </div>
              <label><span className="field-label">Final subject</span><input ref={subjectRef} className="field" required value={subjectFieldValue} onChange={event => editEmailSubject(event.target.value)} maxLength="200" aria-invalid={formAttempted && emailSubjectInvalid} aria-describedby={`retail-quote-subject-help${formAttempted && emailSubjectInvalid ? ' retail-quote-subject-error' : ''}`} /><small id="retail-quote-subject-help" className="retail-quote-note-meta"><span>{subjectPresetId === RETAIL_QUOTE_CUSTOM_SUBJECT_PRESET_ID ? 'Write any clear, accurate subject. Do not make it look like an existing reply thread.' : 'This updates with the retailer name. Editing it switches to Custom.'}</span><span className="tnum">{subjectFieldValue.length}/200</span></small>{formAttempted && emailSubjectInvalid && <small id="retail-quote-subject-error" className="retail-quote-field-error">{emailSubjectError}</small>}</label>
            </fieldset>
            <div className="retail-quote-message-presets" aria-label="Message starters">
              {RETAIL_QUOTE_MESSAGE_PRESETS.map(preset => <button type="button" className="btn btn-sm" key={preset.label} onClick={() => update(setNote, preset.text)}>{preset.label}</button>)}
            </div>
            <label><span className="field-label">Your message to the retailer <em>optional</em></span><textarea className="field retail-quote-note-input" value={note} onChange={event => update(setNote, event.target.value)} placeholder="Great meeting you today. I put together the pricing we discussed and would be happy to help with your opening order." maxLength="500" rows="5" /><small className="retail-quote-note-meta"><span>Keep it personal and useful.</span><span className="tnum">{note.length}/500</span></small></label>
            <div className="retail-quote-test-email" aria-labelledby="retail-quote-test-email-title">
              <div className="retail-quote-test-email-copy">
                <span className="retail-quote-test-email-icon" aria-hidden="true"><Icon name="mail" size={18} /></span>
                <div>
                  <strong id="retail-quote-test-email-title">Test before sending</strong>
                  <p>Send this draft and attached PDF to <b id="retail-quote-test-email-destination">{testRecipientEmail || 'your Ranch Power inbox'}</b>.</p>
                  <small id="retail-quote-test-email-help">{!live ? 'Sign in to send a private test.' : testRecipientEmail ? 'The retailer will not be contacted and no customer will be created.' : 'Add a valid email address to your Ranch Power profile to send tests.'}</small>
                  {testPersonas.length > 1 && <label className="retail-quote-test-persona"><span>Preview as</span><select className="field" value={selectedTestPersona.id} onChange={event => { setTestPersonaId(event.target.value); setTestStatus(null); testRequestIdRef.current = retailQuoteRequestId(); }}>{testPersonas.map((recipient, index) => <option key={recipient.id} value={recipient.id}>{recipient.contactName || recipient.email || `Contact ${index + 1}`}</option>)}</select></label>}
                </div>
              </div>
              <button type="button" className="btn retail-quote-test-email-button" disabled={Boolean(busy) || !live || !optionsReady || !testRecipientEmail} aria-busy={busy === 'test'} aria-describedby="retail-quote-test-email-destination retail-quote-test-email-help" onClick={sendTestEmail}><Icon name="mail" size={16} />{busy === 'test' ? 'Sending Test...' : 'Send Test to Me'}</button>
              {testStatus && <div className={`retail-quote-test-email-status is-${testStatus.kind}`} role={testStatus.kind === 'error' ? 'alert' : 'status'}><Icon name={testStatus.kind === 'error' ? 'alert' : 'check'} size={15} /><span>{testStatus.message}</span></div>}
            </div>
            <label className={`retail-quote-confirm ${permissionConfirmed ? 'is-active' : ''} ${sendAttempted && !permissionConfirmed ? 'is-invalid' : ''}`}><input ref={consentRef} type="checkbox" required checked={permissionConfirmed} aria-invalid={sendAttempted && !permissionConfirmed} aria-describedby={`retail-quote-consent-help${sendAttempted && !permissionConfirmed ? ' retail-quote-consent-error' : ''}`} onChange={event => { setPermissionConfirmed(event.target.checked); setError(''); }} /><span><strong>Each listed contact asked for this quote, or retailer staff explicitly directed me to them.</strong><small id="retail-quote-consent-help">Each person receives a separate private email. This confirmation and the contact source are recorded before delivery.</small>{sendAttempted && !permissionConfirmed && <small id="retail-quote-consent-error" className="retail-quote-field-error">Required before these emails can be sent.</small>}</span></label>
          </section>

          {matches.length > 0 && <section className="retail-quote-resolution" aria-live="polite"><div className="retail-quote-resolution-head"><Icon name="customers" size={19} /><span><strong>Choose the existing customer</strong><small>This keeps one clean account instead of creating a duplicate.</small></span></div>{matches.map(match => <RetailQuoteMatchChoice key={match.id} match={match} busy={Boolean(busy)} onChoose={chooseCustomerMatch} />)}</section>}
          {recoveryAvailable && <section className="retail-quote-resolution retail-quote-saved-recovery" aria-live="polite"><div className="retail-quote-resolution-head"><Icon name="doc" size={19} /><span><strong>Continue from the saved quote</strong><small>Open the existing PDF, or use it to create a new revision. The saved version will not be changed.</small></span></div><div className="retail-quote-recovery-actions">{recoveryQuoteId && <button type="button" className="btn btn-primary btn-sm" disabled={Boolean(busy)} onClick={openRecoveryPdf}><Icon name="doc" size={14} />{busy === 'recovery-pdf' ? 'Opening...' : 'View PDF'}</button>}{recoveryQuoteId && <button type="button" className="btn btn-sm" onClick={() => nav.push('retail-quote-edit', { id: recoveryQuoteId })}><Icon name="pen" size={14} />Edit &amp; Resend</button>}<button type="button" className="btn btn-sm btn-ghost" onClick={() => openSavedQuotes()}><Icon name="doc" size={14} />Saved Quotes</button></div></section>}
          {approval && <section className="retail-quote-resolution retail-quote-resolution-warning"><div className="retail-quote-resolution-head"><Icon name={approval.status === 'approved' ? 'check' : 'flag'} size={19} /><span><strong>{approval.status === 'approved' ? 'Custom price approved' : approval.status === 'pending' ? 'Price approval requested' : 'Price approval needed'}</strong><small>{approval.status === 'approved' ? `Approved for this exact draft until ${new Date(approval.expiresAt).toLocaleString()}.` : approval.status === 'pending' ? 'A sales manager can approve or decline this exact draft from their Quick Retail Quote page. Changes to the draft require a new request.' : (approval.reason || 'Request one-time approval for this exact draft before printing or sending.')}</small></span></div>{approval.status === 'pending' && <div className="retail-quote-recovery-actions"><button type="button" className="btn btn-sm" disabled={Boolean(busy)} onClick={() => loadPriceApprovals()}><Icon name="refresh" size={14} />Check status</button></div>}{!approval.status && <div className="retail-quote-recovery-actions"><button type="button" className="btn btn-primary btn-sm" disabled={Boolean(busy)} onClick={requestPriceApproval}>{busy === 'approval-request' ? 'Requesting...' : 'Request approval'}</button></div>}</section>}
          {error && <div className="retail-quote-message retail-quote-message-error" role="alert"><Icon name="alert" size={17} /><span>{error}</span></div>}
          {notice && !sent && <div className="retail-quote-message retail-quote-message-ok" role="status"><Icon name="check" size={17} /><span>{notice}</span></div>}

          {isNarrow && <div className="retail-quote-mobile-preview">
            <div className="retail-quote-preview-label"><span className="eyebrow">Customer preview</span><span>Print-ready</span></div>
            <RetailQuoteSheet claims={displayClaims} contactName={contactName} fulfillment={fulfillment} note={note} orderingAccessEnabled={primaryOrderingAccess} pricing={displayPricing} products={selectedProducts} repEmail={repEmail} repLocation={repLocation} repName={repName} repPhone={repPhone} retailerName={retailerName} />
          </div>}
          <div className="retail-quote-bottom-space" />
          </form>
        </main>

        {!isNarrow && <aside className="retail-quote-preview-panel" aria-label="Quote preview">
          <div className="retail-quote-preview-label"><span className="eyebrow">Customer preview</span><span>Updates live</span></div>
          <RetailQuoteSheet claims={displayClaims} contactName={contactName} fulfillment={fulfillment} note={note} orderingAccessEnabled={primaryOrderingAccess} pricing={displayPricing} products={selectedProducts} repEmail={repEmail} repLocation={repLocation} repName={repName} repPhone={repPhone} retailerName={retailerName} />
        </aside>}
      </div>

      <div className="retail-quote-action-bar" data-retail-quote-actions="true">
        <button type="button" className="btn" disabled={Boolean(busy) || sourceLoading || Boolean(sourceError)} onClick={printQuote}><Icon name="doc" size={16} />{busy === 'print' ? 'Preparing...' : 'Open Sell Sheet'}</button>
        <button type="submit" form="retail-quote-form" className="btn btn-primary" disabled={Boolean(busy) || sourceLoading || Boolean(sourceError) || (live && !optionsReady)}><Icon name="mail" size={16} />{busy === 'send' ? 'Sending...' : resolvedSourceQuoteId ? `Send New Revision to ${quoteRecipients.length}` : `Send to ${quoteRecipients.length} contact${quoteRecipients.length === 1 ? '' : 's'}`}</button>
      </div>
    </div>
  );
};

MOBILE_SCREENS['retail-quote-edit'] = function RetailQuoteEdit(props) {
  return <QuickRetailQuote {...props} sourceQuoteId={props.id} id="" />;
};


/* ===== mobile_rep.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : Sales Rep screens
   ============================================================ */

function repHealthDate(value) {
  const raw = String(value || '').trim();
  if (!raw || raw.toLowerCase() === 'recently') return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

function repDaysSince(value) {
  const iso = repHealthDate(value);
  if (!iso) return null;
  const date = new Date(`${iso}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - date.getTime()) / 86400000));
}

function repCustomerVisits(customerOrId) {
  const id = String(typeof customerOrId === 'object' ? (customerOrId && customerOrId.id) : customerOrId || '').trim();
  if (!id) return [];
  return (RP.VISITS || [])
    .filter(visit => String(visit.cust || visit.customerId || visit.customer_id || '') === id)
    .slice()
    .sort((a, b) => String(repHealthDate(b.date || b.createdAt || b.created_at) || '').localeCompare(String(repHealthDate(a.date || a.createdAt || a.created_at) || '')));
}

function repShelfHealth(visit) {
  const shelf = visit && (visit.shelf || visit.shelfCounts || visit.shelf_counts);
  if (!shelf || typeof shelf !== 'object') return { low: 0, empty: 0, total: null };
  const counts = Object.values(shelf).map(value => Number(value)).filter(Number.isFinite);
  if (!counts.length) return { low: 0, empty: 0, total: null };
  return {
    empty: counts.filter(value => value <= 0).length,
    low: counts.filter(value => value <= 2).length,
    total: counts.reduce((sum, value) => sum + value, 0)
  };
}

function repCustomerHealth(customer) {
  const c = customer || {};
  const restock = typeof rpCustomerRestockSignal === 'function' ? rpCustomerRestockSignal(c) : {};
  const summary = typeof rpCustomerOrderSummary === 'function' ? rpCustomerOrderSummary(c) : {};
  const orders = typeof rpCustomerOrders === 'function' ? rpCustomerOrders(c, { includeCancelled: true }) : [];
  const visits = repCustomerVisits(c);
  const latestVisit = visits[0] || null;
  const shelf = repShelfHealth(latestVisit);
  const orderReady = typeof rpCustomerOrderReady === 'function' ? rpCustomerOrderReady(c) : c.status !== 'Prospect';
  const creditHold = typeof rpCustomerOnCreditHold === 'function'
    ? rpCustomerOnCreditHold(c)
    : ['Hold', 'Watch', 'Credit Hold'].includes(String(c.credit || c.status || ''));
  const daysSinceOrder = Number.isFinite(Number(restock.daysSinceOrder)) ? Number(restock.daysSinceOrder) : (Number.isFinite(Number(summary.daysSince)) ? Number(summary.daysSince) : (Number.isFinite(Number(c.daysSince)) ? Number(c.daysSince) : null));
  const daysSinceVisit = latestVisit ? repDaysSince(latestVisit.date || latestVisit.createdAt || latestVisit.created_at) : null;
  const exactUnpaidCount = Number(c.unpaidCount);
  const unpaidCount = c.unpaidCount != null && Number.isFinite(exactUnpaidCount)
    ? Math.max(0, exactUnpaidCount)
    : orders.filter(order => ['Unpaid', 'Partial', 'Overdue'].includes(repInvoicePaymentStatus(order))).length;
  const reasons = Array.isArray(restock.reasons) ? [...restock.reasons] : [];
  let score = Number(restock.score || 0);
  const addReason = reason => {
    if (reason && !reasons.includes(reason)) reasons.push(reason);
  };

  if (!orderReady) {
    score += 32;
    addReason(c.status === 'Prospect' ? 'Approval needed' : 'Ordering blocked');
  }
  if (creditHold) {
    score += 28;
    addReason(c.credit === 'Watch' ? 'Credit watch' : 'Credit hold');
  }
  if (unpaidCount > 0) {
    score += unpaidCount > 1 ? 20 : 14;
    addReason(`${unpaidCount} unpaid`);
  }
  if (latestVisit && latestVisit.followUp) {
    score += 16;
    addReason('Follow-up flagged');
  }
  if (shelf.low) {
    score += shelf.empty ? 22 : 14;
    addReason(shelf.empty ? `${shelf.empty} SKU out` : `${shelf.low} shelf low`);
  }
  if (!latestVisit && orderReady) {
    score += 8;
    addReason('No visit logged');
  } else if (daysSinceVisit >= 21) {
    score += 10;
    addReason(`${daysSinceVisit}d since visit`);
  }
  if (!reasons.length) addReason('Healthy account');

  const tone = score >= 55 ? 'High' : score >= 28 ? 'Medium' : 'Low';
  return {
    customer: c,
    daysSinceOrder,
    daysSinceVisit,
    label: tone === 'High' ? 'Priority' : tone === 'Medium' ? 'Watch' : 'Healthy',
    latestVisit,
    lastOrder: summary.lastOrder || null,
    reasons,
    restock,
    score,
    shelf,
    tone,
    unpaidCount
  };
}

function repGoalPacing(goal, elapsed) {
  const safeElapsed = Math.min(1, Math.max(0.01, Number(elapsed) || 0.01));
  const remainingShare = Math.max(0, 1 - safeElapsed);
  const revenueActual = Number(goal && goal.revActual || 0);
  const revenueTarget = Math.max(0, Number(goal && goal.revTarget || 0));
  const casesActual = Number(goal && goal.casesActual || 0);
  const casesTarget = Math.max(0, Number(goal && goal.casesTarget || 0));
  const doorsActual = Number(goal && goal.doorsActual || 0);
  const doorsTarget = Math.max(0, Number(goal && goal.doorsTarget || 0));
  return {
    casesGap: Math.max(0, casesTarget - casesActual),
    doorsGap: Math.max(0, doorsTarget - doorsActual),
    expectedRevenue: revenueTarget * safeElapsed,
    periodLeftPct: Math.round(remainingShare * 100),
    projectedRevenue: safeElapsed ? revenueActual / safeElapsed : revenueActual,
    revenueGap: Math.max(0, revenueTarget - revenueActual)
  };
}

const REP_MY_DAY_OPEN_TASK_STATUSES = new Set(['To Do', 'In Progress', 'Blocked']);
const REP_MY_DAY_OPEN_LEAD_STAGES = new Set(['idea', 'contacted', 'sampling', 'proposal']);

function repMyDayYmd(value) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const ymd = `${match[1]}-${match[2]}-${match[3]}`;
  const parsed = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(parsed.getTime()) || rpFormatUtcYmd(parsed) !== ymd) return '';
  return ymd;
}

function repMyDayDateDistance(due, today) {
  if (!due || !today) return 0;
  return Math.round((Date.parse(`${due}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86400000);
}

function repMyDayDueLabel(due, today) {
  const distance = repMyDayDateDistance(due, today);
  if (distance < 0) return `${Math.abs(distance)}d overdue`;
  if (distance === 0) return 'Due today';
  return `Due ${due.slice(5)}`;
}

function repMyDayScopedLeads(role) {
  if (!window.RP) return [];
  if (rpHasLiveAuth()) return Array.isArray(RP.LEADS) ? RP.LEADS : [];
  const api = RP.LEADS_API;
  if (!api) return [];
  return typeof api.forRep === 'function' ? api.forRep(rpFieldUserName(role)) : [];
}

function repMyDayItems(role) {
  const today = typeof rpTodayYmd === 'function'
    ? rpTodayYmd({ territory: rpFieldTerritory(role) })
    : new Date().toISOString().slice(0, 10);
  const taskPriority = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
  const items = [];

  rpFieldTasks(role)
    .filter(task => REP_MY_DAY_OPEN_TASK_STATUSES.has(String(task.status || '')))
    .forEach(task => {
      const due = repMyDayYmd(task.due);
      if (!due || due > today) return;
      const overdue = due < today;
      items.push({
        date: due,
        icon: 'tasks',
        id: task.id,
        key: `task:${task.id}`,
        meta: [repMyDayDueLabel(due, today), task.priority || task.status].filter(Boolean).join(' - '),
        rank: (overdue ? 0 : 10) + (taskPriority[task.priority] ?? 3) / 10,
        task,
        title: task.title || task.id,
        tone: overdue || task.priority === 'Urgent' || task.status === 'Blocked' ? 'danger' : 'warn',
        type: 'task'
      });
    });

  repMyDayScopedLeads(role)
    .filter(lead => REP_MY_DAY_OPEN_LEAD_STAGES.has(String(lead.stage || '')) && lead.nextAction)
    .forEach(lead => {
      const due = repMyDayYmd(lead.nextAction && lead.nextAction.due);
      if (!due || due > today) return;
      const overdue = due < today;
      const activity = RP.LEADS_API && typeof RP.LEADS_API.actType === 'function' ? RP.LEADS_API.actType(lead.nextAction.type) : null;
      items.push({
        date: due,
        icon: activity && activity.icon || 'trend',
        id: lead.id,
        key: `lead:${lead.id}`,
        lead,
        meta: [repMyDayDueLabel(due, today), lead.nextAction.note].filter(Boolean).join(' - '),
        rank: overdue ? 5 : 15,
        title: `${activity && activity.label || 'Follow up'} - ${lead.name}`,
        tone: overdue ? 'danger' : 'warn',
        type: 'lead'
      });
    });

  rpFieldOrders(role)
    .filter(order => ['Needs Approval', 'On Hold'].includes(String(order.status || '')))
    .forEach(order => {
      const customer = RP.cust(order.cust);
      items.push({
        icon: 'flag',
        id: order.id,
        key: `order:${order.id}`,
        meta: [order.status, order.flag || order.reason, customer && customer.name].filter(Boolean).join(' - '),
        order,
        rank: order.status === 'On Hold' ? 20 : 21,
        title: `Review ${order.id}`,
        tone: order.status === 'On Hold' ? 'danger' : 'warn',
        type: 'order'
      });
    });

  const customers = rpFieldCustomers(role);
  const restock = typeof rpRestockRecommendations === 'function' ? rpRestockRecommendations(customers, { limit: 12 }) : [];
  restock
    .filter(row => {
      const checkInDate = repMyDayYmd(row && row.checkInDate);
      return Boolean(row && row.customer && row.status !== 'Submitted' && checkInDate && checkInDate <= today);
    })
    .forEach(row => {
      items.push({
        customer: row.customer,
        icon: 'refresh',
        id: row.customer.id,
        key: `restock:${row.customer.id}`,
        meta: [row.label, ...(row.reasons || []).slice(0, 2)].filter(Boolean).join(' - '),
        rank: row.status === 'High' ? 30 : 31,
        restock: row,
        title: `${row.lastOrder ? 'Reorder check' : 'First-order check'} - ${row.customer.name}`,
        tone: row.status === 'High' ? 'danger' : 'warn',
        type: 'restock'
      });
    });

  return items.sort((a, b) => a.rank - b.rank || String(a.date || today).localeCompare(String(b.date || today)) || a.title.localeCompare(b.title));
}

function RepMyDayQueue({ nav, role, limit = 6, desktop = false }) {
  const [, force] = useState(0);
  const [saving, setSaving] = useState('');
  const [emailLead, setEmailLead] = useState(null);
  const live = rpHasLiveAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!live) {
      setPayload(null);
      setLoading(false);
      setError('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setPayload(null);
      try {
        const response = await rpApi('/api/sales/my-day?limit=100');
        const supported = response
          && response.schemaVersion === 1
          && response.summary
          && response.scans
          && Array.isArray(response.items)
          && response.items.every(item => item && item.queueId && item.entity && item.entity.id && Array.isArray(item.actions) && item.actions.every(action => action && action.id && action.label && action.kind));
        if (!supported) throw new Error('My Day returned an unsupported response.');
        if (!cancelled) setPayload(response);
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(err && err.message ? err.message : 'Could not load My Day.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [live, reload]);

  const allItems = live
    ? (payload && Array.isArray(payload.items) ? payload.items : [])
    : repMyDayItems(role);
  const items = allItems.slice(0, limit);
  const itemKind = item => live ? item.kind : item.type;
  const itemId = item => live ? item.entity && item.entity.id : item.id;
  const returnedActions = item => live && Array.isArray(item.actions) ? item.actions : [];
  const primaryAction = item => {
    if (!live) return null;
    const actions = returnedActions(item);
    const preferredId = item.primaryAction && item.primaryAction.id;
    return actions.find(action => preferredId && action.id === preferredId)
      || actions.find(action => action.kind === 'navigate')
      || null;
  };
  const navigateFromAction = action => {
    const navigation = action && action.kind === 'navigate' && action.navigation;
    if (!navigation || !navigation.screen) {
      toast('This My Day action is not available.', 'danger');
      return false;
    }
    nav.push(navigation.screen, { ...(navigation.params || {}) });
    return true;
  };
  const openItem = item => {
    if (live) {
      navigateFromAction(primaryAction(item));
      return;
    }
    if (item.type === 'task') nav.push('task-detail', { id: item.id });
    else if (item.type === 'lead') nav.push('lead-detail', { id: item.id });
    else if (item.type === 'order') nav.push('rep-order-detail', { id: item.id });
    else if (item.type === 'restock') nav.push('rep-customer', { id: item.id });
  };
  const updateTask = async (item, action) => {
    const kind = itemKind(item);
    const id = itemId(item);
    const actionId = live ? action && action.id : action;
    if (!item || kind !== 'task' || !id || saving || !['complete', 'done', 'snooze'].includes(actionId)) return;
    setSaving(`${id}:${actionId}`);
    try {
      if (actionId === 'complete' || actionId === 'done') {
        const status = live ? action.request && action.request.fixedBody && action.request.fixedBody.status : 'Done';
        if (!status) throw new Error('My Day did not provide a completion status.');
        await saveMobileTaskStatus(id, status, item.updatedAt);
        toast(live ? 'Task completed' : 'Demo task completed locally', 'ok');
      } else {
        const dueDate = live
          ? action.request && action.request.fixedBody && action.request.fixedBody.dueDate
          : typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: rpFieldTerritory(role) }, 1) : repMyDayYmd(new Date(Date.now() + 86400000).toISOString());
        if (!dueDate) throw new Error('My Day did not provide a snooze date.');
        await saveMobileTaskDueDate(id, dueDate, item.updatedAt);
        toast(live ? `Task snoozed until ${dueDate}` : `Demo task snoozed locally until ${dueDate}`, 'info');
      }
      if (live) {
        setPayload(null);
        setLoading(true);
        setReload(value => value + 1);
      } else {
        force(value => value + 1);
      }
    } catch (error) {
      toast(error && error.message ? error.message : 'Could not update task.', 'danger');
    } finally {
      setSaving('');
    }
  };
  const runItemAction = item => {
    if (item.type === 'lead') nav.push('log-touch', { id: item.id, preset: item.lead.nextAction && item.lead.nextAction.type });
    if (item.type === 'order') nav.push('rep-order-detail', { id: item.id });
    if (item.type === 'restock') {
      if (item.restock.lastOrder) nav.push('create-order', { cust: item.customer.id, reorder: item.restock.lastOrder.id });
      else if (rpCustomerOrderReady(item.customer)) nav.push('create-order', { cust: item.customer.id });
      else nav.push('visit-log', { id: item.customer.id });
    }
  };
  const summary = live && payload && payload.summary || {};
  const truncatedScans = live && payload
    ? Object.entries(payload.scans || {}).filter(([, scan]) => scan && scan.truncated).map(([name]) => name)
    : [];
  const queueTruncated = live && Boolean(summary.queueTruncated);
  const sourceDataIncomplete = live && truncatedScans.length > 0;
  const ready = !live || Boolean(payload);
  const matchedCount = Number(summary.matched);
  const totalPriorities = live && Number.isFinite(matchedCount) && matchedCount >= allItems.length
    ? matchedCount
    : allItems.length;
  const showWorkloadCount = !loading && !error && ready && totalPriorities > items.length;
  return (
    <>
    {emailLead && <LeadEmailComposer lead={emailLead} allowSend={true} onClose={() => setEmailLead(null)} onSent={() => {
      if (live) setReload(value => value + 1);
      else force(value => value + 1);
    }} />}
    <div data-my-day-queue="true" data-my-day-count={items.length} data-my-day-source={live ? 'live-api-scoped' : 'demo-local'} data-my-day-provenance={live ? 'live-api' : 'demo-local'} data-my-day-demo={live ? undefined : 'local-only'} data-my-day-schema-version={live && payload ? payload.schemaVersion : ''}>
      {!live && <div style={{ color: 'var(--info)', fontSize: 11.5, fontWeight: 700, lineHeight: 1.4, marginBottom: 9 }}>Preview mode - changes aren't saved.</div>}
      {loading && <div data-my-day-loading="true" role="status" style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: desktop ? 22 : 18, textAlign: 'center', color: 'var(--muted)', fontSize: 12.5 }}><Icon name="refresh" size={20} /><div style={{ marginTop: 7, color: 'var(--ink)', fontWeight: 750 }}>Loading your priorities...</div></div>}
      {!loading && error && <div data-my-day-error="true" role="alert" style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: 12, fontSize: 12, fontWeight: 650, lineHeight: 1.4 }}><div style={{ color: 'var(--ink)', fontWeight: 800 }}>My Day is unavailable</div><div style={{ marginTop: 3 }}>We couldn't load your priorities. Check your connection and try again.</div><button data-my-day-retry="true" className="btn" onClick={() => setReload(value => value + 1)} style={{ width: '100%', justifyContent: 'center', marginTop: 9 }}>Try again</button></div>}
      {showWorkloadCount && <div data-my-day-workload-count="true" data-my-day-truncated={queueTruncated ? 'true' : undefined} data-my-day-view-truncated="true" style={{ color: 'var(--faint)', fontSize: 11.5, fontWeight: 650, lineHeight: 1.4, marginBottom: 9 }}>Top {items.length.toLocaleString()} of {totalPriorities.toLocaleString()} priorities</div>}
      {!loading && !error && ready && <div style={{ display: 'grid', gap: 8 }}>
        {items.map(item => {
          const kind = itemKind(item);
          const id = itemId(item);
          const itemPrimaryAction = primaryAction(item);
          const apiActions = returnedActions(item);
          const apiTaskActions = kind === 'task' ? apiActions.filter(action => action.kind === 'request' && ['complete', 'snooze'].includes(action.id)) : [];
          const secondaryNavigationActions = kind !== 'task' ? apiActions.filter(action => action.kind === 'navigate' && (!itemPrimaryAction || action.id !== itemPrimaryAction.id)) : [];
          const apiNavigationActions = secondaryNavigationActions;
          const leadRecord = kind === 'lead' && window.RP && Array.isArray(RP.LEADS) ? RP.LEADS.find(lead => lead.id === id) : null;
          const hasApiEmailAction = apiNavigationActions.some(action => /email/i.test(`${action.id || ''} ${action.label || ''}`));
          const urgent = live ? item.isOverdue || ['critical', 'high'].includes(String(item.priority || '').toLowerCase()) : item.tone === 'danger';
          const [fg, bg] = statusColors(urgent ? 'High' : 'Pending');
          const itemSaving = saving.startsWith(`${id}:`);
          const icon = live ? ({ task: 'tasks', lead: 'trend', order_exception: 'flag', restock: 'refresh' }[kind] || 'tasks') : item.icon;
          const title = item.title;
          const detail = live ? item.detail : item.meta;
          const rowKey = live ? item.queueId : item.key;
          const restockAction = !live && item.type === 'restock' ? (item.restock.lastOrder ? 'Reorder' : rpCustomerOrderReady(item.customer) ? 'New order' : 'Log visit') : '';
          const primaryActionAriaLabel = live && itemPrimaryAction ? `${itemPrimaryAction.label}: ${title}` : `Open ${title}`;
          const hasActionButtons = live ? Boolean(apiTaskActions.length || apiNavigationActions.length || leadRecord) : true;
          return (
            <div key={rowKey} data-my-day-item={kind} data-my-day-record-id={id} data-my-day-rank={live ? item.rank : undefined} style={{ display: desktop ? 'flex' : 'grid', gridTemplateColumns: desktop ? undefined : 'minmax(0, 1fr)', alignItems: desktop ? 'center' : 'stretch', gap: 8, padding: '10px 11px', background: 'var(--surface)', border: '1px solid var(--line)', borderLeft: `3px solid ${fg}`, borderRadius: 10, minWidth: 0 }}>
              <button data-my-day-open={kind} data-my-day-primary-action={live && itemPrimaryAction ? itemPrimaryAction.id : undefined} aria-label={primaryActionAriaLabel} onClick={() => openItem(item)} style={{ flex: desktop ? '1 1 220px' : undefined, width: '100%', minWidth: 0, minHeight: 44, border: 'none', background: 'transparent', padding: 0, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, background: bg, color: fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={icon} size={16} /></span>
                <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: desktop ? 1 : 2, color: 'var(--ink)', fontSize: 13, fontWeight: 750, lineHeight: 1.3, overflow: 'hidden' }}>{title}</span><span style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: desktop ? 1 : 2, color: 'var(--faint)', fontSize: 11.25, lineHeight: 1.35, marginTop: 2, overflow: 'hidden' }}>{detail}</span></span>
                <span aria-hidden="true" style={{ color: 'var(--faint)', display: 'inline-flex', flexShrink: 0 }}><Icon name="chevR" size={15} /></span>
              </button>
              {hasActionButtons && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: desktop ? 'flex-end' : 'flex-start', flex: desktop ? '0 0 auto' : undefined, width: desktop ? 'auto' : '100%', minWidth: 0 }}>
                {live && apiTaskActions.map(action => <button key={action.id} data-my-day-task-action={action.id} data-my-day-action-label={action.label} className="btn btn-sm" disabled={itemSaving} onClick={() => updateTask(item, action)}><Icon name={action.id === 'complete' ? 'check' : 'calendar'} size={13} />{action.label}</button>)}
                {!live && item.type === 'task' && <>
                  <button data-my-day-task-done={item.id} className="btn btn-sm" disabled={itemSaving} onClick={() => updateTask(item, 'done')}><Icon name="check" size={13} />Done</button>
                  <button data-my-day-task-snooze={item.id} className="btn btn-sm" disabled={itemSaving} onClick={() => updateTask(item, 'snooze')}><Icon name="calendar" size={13} />Snooze</button>
                </>}
                {live && apiNavigationActions.map(action => {
                  const emailAction = /email/i.test(`${action.id || ''} ${action.label || ''}`) && leadRecord;
                  return <button key={action.id} data-my-day-action={kind} data-my-day-action-id={action.id} data-my-day-action-label={action.label} className="btn btn-sm" onClick={() => emailAction ? setEmailLead(leadRecord) : navigateFromAction(action)}>{action.label}</button>;
                })}
                {live && leadRecord && !hasApiEmailAction && <button data-my-day-action="lead-email" className="btn btn-sm" onClick={() => setEmailLead(leadRecord)}><Icon name="mail" size={13} />Build email</button>}
                {!live && item.type !== 'task' && <button data-my-day-action={item.type} className="btn btn-sm" onClick={() => runItemAction(item)}>{item.type === 'lead' ? 'Log touch' : item.type === 'order' ? 'Review' : restockAction}</button>}
                {!live && item.type === 'lead' && item.lead && <button data-my-day-action="lead-email" className="btn btn-sm" onClick={() => setEmailLead(item.lead)}><Icon name="mail" size={13} />Build email</button>}
              </div>}
            </div>
          );
        })}
        {!items.length && <div data-my-day-empty="true" style={{ border: '1px dashed var(--line)', borderRadius: 10, padding: desktop ? 20 : 16, textAlign: 'center', color: 'var(--faint)', fontSize: 12.5 }}><div style={{ color: 'var(--ink)', fontWeight: 800 }}>You're all caught up</div><div style={{ marginTop: 3 }}>Nothing needs your attention right now.</div></div>}
      </div>}
      {!loading && !error && sourceDataIncomplete && <div data-my-day-incomplete="true" role="status" style={{ color: 'var(--amber-deep)', fontSize: 11.5, fontWeight: 650, lineHeight: 1.4, marginTop: 9 }}>Some records couldn't be checked. Review Tasks, Pipeline, Customers, and Orders for the full picture.</div>}
    </div>
    </>
  );
}

/* ---------- REP HOME ---------- */
MOBILE_SCREENS['rep-home'] = function RepHome({ nav, role }) {
  const name = rpFieldUserName(role);
  const firstName = rpFieldFirstName(role);
  const territory = rpFieldTerritory(role);
  const personalLocations = inventoryPersonalFieldLocations(role);
  const salesRepackAllowed = inventoryRepackSalesRoleAllowed(role);
  const [inventoryLocationId, setInventoryLocationId] = useState(() => inventoryPersonalPreferredFieldLocationId(personalLocations));
  const micro = salesRepackAllowed ? inventoryPersonalResolveFieldLocation(personalLocations, inventoryLocationId) : rpFieldMicroWarehouse(role);
  const inventoryRouteParams = micro ? { loc: micro.id } : {};
  const [newRepack, setNewRepack] = useState(false);
  const customers = rpFieldCustomers(role);
  const orders = rpFieldOrders(role);
  const salesAnalytics = useRpSalesAnalytics('mtd');
  const myGoals = (RP.SALES_GOALS || []).filter(g => g && g.rep === name && g.active !== false);
  const defaultPeriod = typeof salesGoalDefaultPeriod === 'function' ? salesGoalDefaultPeriod() : 'June 2026';
  const goal = myGoals.find(g => g.period === defaultPeriod) || myGoals[0];
  const gElapsed = goal ? (RP.PERIOD_ELAPSED[goal.period] || 0.17) : 0.17;
  const demoSales = typeof rpSalesSummary === 'function'
    ? rpSalesSummary(orders, { range: 'mtd' })
    : orders.reduce((summary, o) => {
      if (['Cancelled', 'Canceled', 'Closed'].includes(String(o.status || ''))) return summary;
      const t = orderTotals(o);
      summary.revenue += t.sub ?? t.total;
      summary.cases += t.cases;
      summary.orders += 1;
      return summary;
    }, { cases: 0, revenue: 0, orders: 0 });
  const liveSummary = salesAnalytics.data && salesAnalytics.data.summary;
  const liveOperational = salesAnalytics.data && salesAnalytics.data.operational;
  const total = salesAnalytics.isLive ? (liveSummary ? dashboardExactNumber(liveSummary.revenue) : null) : demoSales.revenue;
  const cases = salesAnalytics.isLive ? (liveSummary ? dashboardExactNumber(liveSummary.cases) : null) : Math.round(demoSales.cases);
  const openOrderCount = salesAnalytics.isLive
    ? (liveOperational ? dashboardExactNumber(liveOperational.openOrders) : null)
    : orders.filter(o => typeof rpOrderIsOpenForField === 'function' ? rpOrderIsOpenForField(o) : !['Delivered', 'Invoiced', 'Paid', 'Closed', 'Cancelled', 'Canceled'].includes(o.status)).length;
  const deliveryQueueCount = salesAnalytics.isLive
    ? (liveOperational ? dashboardExactNumber(liveOperational.pickQueueOrders) : null)
    : orders.filter(o => ['Picked', 'Dispatched'].includes(String(o.status || ''))).length;
  const customerHealthRows = customers.map(repCustomerHealth)
    .sort((a, b) => b.score - a.score || String(a.customer.name || '').localeCompare(String(b.customer.name || '')));
  const priorityCustomers = customerHealthRows.filter(row => row.tone !== 'Low').slice(0, 3);
  const approvalOrders = orders.filter(o => ['Needs Approval', 'On Hold'].includes(o.status));
  const fieldInventory = rpFieldInventory(role, micro);
  const lowStock = fieldInventory.find(m => m.available <= 5);
  const visitCustomerId = rpFieldFirstCustomerId(role);
  const invUsed = rpFieldInventoryUsedCases(fieldInventory);
  const invCap = Math.max(rpFiniteNumber(micro && micro.cap, 100), invUsed, 1);
  const invValue = rpFieldInventoryFrontedValue(fieldInventory);
  const goalOverall = goal && typeof RP.goalOverall === 'function' ? RP.goalOverall(goal, gElapsed) : '';
  const goalPacing = goal ? repGoalPacing(goal, gElapsed) : null;
  const workflowPreview = typeof rpDashboardWorkflowIntelligenceEnabled === 'function' && rpDashboardWorkflowIntelligenceEnabled(role);
  const reorderSuggestion = priorityCustomers.find(row => rpCustomerOrderReady(row.customer) && ((row.restock && row.restock.lastOrder) || row.lastOrder));
  const reorderSuggestionOrder = reorderSuggestion && ((reorderSuggestion.restock && reorderSuggestion.restock.lastOrder) || reorderSuggestion.lastOrder);
  const suggestedActions = workflowPreview ? [
    reorderSuggestion ? {
      go: () => nav.push('create-order', { cust: reorderSuggestion.customer.id, reorder: reorderSuggestionOrder.id }),
      icon: 'refresh',
      meta: ((reorderSuggestion.restock && reorderSuggestion.restock.reasons) || reorderSuggestion.reasons).slice(0, 2).join(' - '),
      status: 'Pending',
      title: `Build reorder for ${reorderSuggestion.customer.name}`
    } : null,
    lowStock ? {
      go: () => nav.push('request-stock', inventoryRouteParams),
      icon: 'layers',
      meta: `${lowStock.available} cases left in field storage`,
      status: 'Pending',
      title: `Request restock for ${rpSkuDisplayName(RP.sku(lowStock.sku) || lowStock.sku)}`
    } : null,
    approvalOrders[0] ? {
      go: () => nav.push('rep-order-detail', { id: approvalOrders[0].id }),
      icon: 'flag',
      meta: approvalOrders[0].flag || approvalOrders[0].reason || approvalOrders[0].status,
      status: 'High',
      title: `Clear held order ${approvalOrders[0].id}`
    } : null,
    goal && goalPacing && (goalPacing.revenueGap > 0 || goalPacing.casesGap > 0) ? {
      go: () => nav.push('my-goals'),
      icon: 'target',
      meta: `${RP.money(Math.round(goalPacing.revenueGap))} gap - ${goalPacing.casesGap} cases left`,
      status: goalOverall === 'Behind' ? 'High' : 'Pending',
      title: 'Review goal recovery plan'
    } : null
  ].filter(Boolean).slice(0, 4) : [];
  return (
    <div className="rp-fade">
      {newRepack && personalLocations.length > 0 && <FinishedGoodsRepackModal
        allowedLocationIds={personalLocations.map(location => location.id)}
        onClose={() => setNewRepack(false)}
        onAdded={result => {
          const savedLocationId = inventoryPersonalRememberFieldLocation(personalLocations, result && result.locationId);
          if (savedLocationId) setInventoryLocationId(savedLocationId);
          setNewRepack(false);
        }}
      />}
      {/* Navy hero */}
      <div style={{ background: 'var(--navy)', color: '#fff', padding: 'calc(16px + env(safe-area-inset-top)) 18px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Logo color="#fff" />
          <button onClick={() => nav.push('notifications')} style={{ border: 'none', background: 'oklch(1 0 0 / 0.12)', width: 34, height: 34, borderRadius: 10, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <Icon name="bell" size={18} /><span style={{ position: 'absolute', top: 7, right: 8, width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)' }} />
          </button>
        </div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>{territory} territory</div>
        <div className="disp" style={{ fontSize: 25, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Hi, {firstName}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', marginTop: 16, background: 'oklch(1 0 0 / 0.07)', borderRadius: 14, padding: '14px 10px' }}>
          {[
            ['Sales', total == null ? '—' : RP.money(total)],
            ['Cases', cases == null ? '—' : cases.toLocaleString()],
            ['Open', openOrderCount == null ? '—' : openOrderCount.toLocaleString()],
            [salesAnalytics.isLive ? 'Pick queue' : 'Deliveries', deliveryQueueCount == null ? '—' : deliveryQueueCount.toLocaleString()],
          ].map(([label, value], i) => (
            <div key={label} style={{ minWidth: 0, borderLeft: i ? '1px solid oklch(1 0 0 / 0.12)' : 'none', padding: '0 6px' }}>
              <MStat label={label} value={value} accent="#fff" />
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '12px 14px 0' }}><SalesAnalyticsStatus state={salesAnalytics} label="my MTD and operational sales totals" /></div>

      <div style={{ padding: '10px 14px 0' }}>
        <button
          data-retail-quote-home-cta="true"
          onClick={() => nav.push('quick-retail-quote')}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', border: '1px solid var(--amber)', borderRadius: 13, background: 'var(--amber-soft)', color: 'var(--ink)', textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}
        >
          <span style={{ width: 38, height: 38, flex: '0 0 38px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, background: 'var(--navy)', color: '#fff' }}><Icon name="doc" size={18} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ display: 'block', fontSize: 14, lineHeight: 1.2 }}>Quick Retail Quote</strong>
            <small style={{ display: 'block', marginTop: 3, color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.35 }}>Price, print, and email a retailer in under a minute.</small>
          </span>
          <Icon name="chevR" size={17} style={{ color: 'var(--amber-deep)', flexShrink: 0 }} />
        </button>
        <button data-retail-quote-history-cta="true" className="btn" onClick={() => nav.push('retail-quotes')} style={{ width: '100%', justifyContent: 'space-between', marginTop: 8 }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="doc" size={16} />Saved Quotes</span><Icon name="chevR" size={15} /></button>
      </div>

      <MSection title="My Day" right={<button aria-label="Open task list" onClick={() => nav.push('my-tasks')} style={{ border: 'none', background: 'none', color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 600 }}>Tasks</button>}>
        <RepMyDayQueue nav={nav} role={role} limit={6} />
      </MSection>

      <MSection title="Customer Priority" right={<button onClick={() => nav.tab('rep-customers')} style={{ border: 'none', background: 'none', color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 600 }}>View all</button>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {priorityCustomers.map(row => {
            const [fg, bg] = statusColors(row.tone);
            return (
              <button key={row.customer.id} onClick={() => nav.push('rep-customer', { id: row.customer.id })} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 13px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
                <Avatar name={row.customer.name} size={34} color={row.tone === 'High' ? 'var(--danger)' : 'var(--amber-deep)'} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 750, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.customer.name}</span>
                    <span className="pill" style={{ color: fg, background: bg, fontSize: 10.5, flexShrink: 0 }}>{row.label}</span>
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--faint)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.reasons.slice(0, 3).join(' - ')}</span>
                </span>
                <Icon name="chevR" size={17} style={{ color: 'var(--faint)', flexShrink: 0 }} />
              </button>
            );
          })}
          {!priorityCustomers.length && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px', textAlign: 'center', fontSize: 12.5, color: 'var(--faint)' }}>No priority customer risks right now.</div>}
        </div>
      </MSection>

      {workflowPreview && (
        <MSection title="Guided Actions" right={<span className="pill" style={{ color: 'var(--info)', background: 'var(--info-bg)' }}>Preview</span>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {suggestedActions.map((item, i) => {
              const [fg, bg] = statusColors(item.status);
              return (
                <button key={`${item.title}-${i}`} onClick={item.go} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 13px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
                  <span style={{ width: 34, height: 34, borderRadius: 10, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={item.icon} size={17} /></span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 750, color: 'var(--ink)', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--faint)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.meta}</span>
                  </span>
                  <Icon name="chevR" size={17} style={{ color: 'var(--faint)', flexShrink: 0 }} />
                </button>
              );
            })}
            {!suggestedActions.length && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px', textAlign: 'center', fontSize: 12.5, color: 'var(--faint)' }}>No guided actions from current data.</div>}
          </div>
        </MSection>
      )}

      {/* My Goal */}
      {goal && (() => {
        const overall = RP.goalOverall(goal, gElapsed);
        const pct = Math.round(goal.revActual / goal.revTarget * 100);
        const color = overall === 'Behind' ? 'var(--danger)' : overall === 'At Risk' ? 'var(--warn)' : overall === 'Ahead' ? 'var(--info)' : 'var(--ok)';
        const pacing = goalPacing;
        return (
          <MSection title={`My Goal - ${goal.period}`} right={<button onClick={() => nav.push('my-goals')} style={{ border: 'none', background: 'none', color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 600 }}>Details</button>}>
            <button onClick={() => nav.push('my-goals')} style={{ width: '100%', textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 9 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}><span className="disp" style={{ fontSize: 22 }}>{RP.money(goal.revActual)}</span><span className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>/ {RP.money(goal.revTarget)}</span></span>
                <Pill status={overall} />
              </div>
              <div style={{ position: 'relative' }}>
                <Bar value={pct} max={100} color={color} h={9} />
                <div style={{ position: 'absolute', left: Math.min(100, gElapsed * 100) + '%', top: -3, bottom: -3, width: 2, background: 'var(--ink)', opacity: 0.5, borderRadius: 2 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11.5 }}>
                <span style={{ color: 'var(--muted)' }}>{goal.casesActual}/{goal.casesTarget} cases · {goal.doorsActual}/{goal.doorsTarget} new doors</span>
                <span style={{ fontWeight: 700, color }}>{pct}%</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginTop: 12, paddingTop: 11, borderTop: '1px solid var(--line-2)' }}>
                {[
                  ['Pace now', RP.money(Math.round(pacing.expectedRevenue))],
                  ['Gap', RP.money(Math.round(pacing.revenueGap))],
                  ['Projected', RP.money(Math.round(pacing.projectedRevenue))],
                ].map(([label, value]) => (
                  <span key={label} style={{ minWidth: 0 }}>
                    <span className="mono" style={{ display: 'block', color: 'var(--ink)', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
                    <span style={{ display: 'block', color: 'var(--faint)', fontSize: 10.5, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                  </span>
                ))}
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.35, marginTop: 9 }}>{pacing.periodLeftPct}% of period left - {pacing.casesGap} cases and {pacing.doorsGap} doors remaining.</div>
            </button>
          </MSection>
        );
      })()}

      {/* Quick actions */}
      <MSection title="Quick Actions">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 9 }}>
          <MTile icon="plus" label="New Order" accent="var(--amber)" onClick={() => nav.push('create-order')} />
          <MTile icon="doc" label="Quick Quote" accent="var(--navy)" onClick={() => nav.push('quick-retail-quote')} />
          <MTile icon="doc" label="Saved Quotes" accent="var(--info)" onClick={() => nav.push('retail-quotes')} />
          <MTile icon="tasks" label="Tasks" accent="var(--info)" onClick={() => nav.push('my-tasks')} />
          <MTile icon="calendar" label="Events" accent="var(--info)" onClick={() => nav.push('rep-events')} />
          <MTile icon="wallet" label="Expense" accent="var(--ok)" onClick={() => nav.push('submit-expense')} />
          <MTile icon="customers" label="Add Customer" accent="var(--navy)" onClick={() => nav.push('add-customer')} />
          <MTile icon="layers" label="Stock Requests" accent="var(--amber)" onClick={() => nav.push('stock-requests')} />
          <MTile icon="package" label="Giveaway" accent="var(--navy)" onClick={() => micro ? nav.push('giveaway-log', inventoryRouteParams) : toast(inventoryPersonalNoLocationMessage(), 'info')} />
          <MTile icon="trend" label="Pipeline" accent="var(--info)" onClick={() => nav.tab('rep-pipeline')} />
          <MTile icon="pin" label="Log Visit" accent="var(--ok)" onClick={() => visitCustomerId ? nav.push('visit-log', { id: visitCustomerId }) : nav.tab('rep-customers')} />
          {salesRepackAllowed && <MTile icon="layers" label="Build Mix Packs" accent="var(--ok)" onClick={() => personalLocations.length ? setNewRepack(true) : toast(inventoryPersonalNoLocationMessage(), 'info')} />}
        </div>
        {salesRepackAllowed && !personalLocations.length && <div role="status" style={{ marginTop: 10, border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 10, padding: '10px 12px', color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.4 }}>{inventoryPersonalNoLocationMessage()}</div>}
      </MSection>

      {/* My inventory */}
      <MSection title={micro ? `My Micro-Warehouse - ${micro.name || micro.id}` : 'My Micro-Warehouse'} right={<button onClick={() => nav.push('rep-inventory', inventoryRouteParams)} style={{ border: 'none', background: 'none', color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 600 }}>Details</button>}>
        {!micro && <div role="status" style={{ marginBottom: 10, border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 10, padding: '10px 12px', color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.4 }}>{personalLocations.length > 1 ? 'Choose one of your associated storage locations in Details.' : inventoryPersonalNoLocationMessage()}</div>}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 12 }}>
            <span style={{ color: 'var(--muted)' }}>{invUsed} / {invCap} cases used</span>
            <span className="mono" style={{ fontWeight: 600 }}>{RP.money(invValue)} fronted</span>
          </div>
          <Bar value={invUsed} max={invCap} color="var(--amber)" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 6 }}>
            {fieldInventory.length > 0 ? fieldInventory.map((m, i) => {
              const s = RP.sku(m.sku);
              const details = [
                m.fronted ? `${m.fronted} fronted` : null,
                m.purchased ? `${m.purchased} purchased` : null,
                m.sample ? `${m.sample} sample` : null
              ].filter(Boolean).join(' · ') || `${m.available} available`;
              return (
                <div key={m.sku || i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderBottom: i < fieldInventory.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
                  <FlavorChip sku={s} size={30} />
                  <div style={{ flex: 1 }}><div style={{ fontSize: 13.5, fontWeight: 500 }}>{rpSkuDisplayName(s || m.sku)}</div><div style={{ fontSize: 11, color: 'var(--faint)' }}>{details}</div></div>
                  <div style={{ textAlign: 'right' }}><span className="disp" style={{ fontSize: 19, color: m.available <= 5 ? 'var(--danger)' : 'var(--ink)' }}>{m.available}</span><div style={{ fontSize: 10, color: 'var(--faint)' }}>of {m.limit}</div></div>
                </div>
              );
            }) : <div style={{ padding: '16px 0 4px', color: 'var(--faint)', fontSize: 12.5, textAlign: 'center' }}>No micro-warehouse stock assigned.</div>}
          </div>
        </div>
      </MSection>
      <div style={{ height: 20 }} />
    </div>
  );
};

/* ---------- MY INVENTORY (micro-warehouse) ---------- */
MOBILE_SCREENS['rep-inventory'] = function RepInventory({ nav, role, loc }) {
  const personalLocations = inventoryPersonalFieldLocations(role);
  const salesRepackAllowed = inventoryRepackSalesRoleAllowed(role);
  const [inventoryLocationId, setInventoryLocationId] = useState(() => (
    personalLocations.some(location => String(location.id || '') === String(loc || '').trim())
      ? String(loc || '').trim()
      : inventoryPersonalPreferredFieldLocationId(personalLocations)
  ));
  const micro = salesRepackAllowed ? inventoryPersonalResolveFieldLocation(personalLocations, inventoryLocationId) : rpFieldMicroWarehouse(role);
  const inventoryRouteParams = micro ? { loc: micro.id } : {};
  const [newRepack, setNewRepack] = useState(false);
  const [, forceInventory] = useState(0);
  const fieldInventory = rpFieldInventory(role, micro);
  const invUsed = rpFieldInventoryUsedCases(fieldInventory);
  const invCap = Math.max(rpFiniteNumber(micro && micro.cap, 100), invUsed, 1);
  const invValue = rpFieldInventoryFrontedValue(fieldInventory);
  const valueCap = rpFieldInventoryValueCap(micro);
  const valueRemaining = Math.max(0, valueCap - invValue);
  const receipts = rpFieldReceipts(role).slice().sort((a, b) => String(b.created || '').localeCompare(String(a.created || '')));
  const pendingReceipts = micro ? receipts.filter(receipt => (
    String(receipt.status || '') === 'Pending'
    && String(receipt.location || receipt.locationId || receipt.location_id || '') === String(micro.id || '')
  )) : [];
  return (
    <div className="rp-fade">
      {newRepack && personalLocations.length > 0 && <FinishedGoodsRepackModal
        allowedLocationIds={personalLocations.map(location => location.id)}
        onClose={() => setNewRepack(false)}
        onAdded={result => {
          const savedLocationId = inventoryPersonalRememberFieldLocation(personalLocations, result && result.locationId);
          if (savedLocationId) setInventoryLocationId(savedLocationId);
          setNewRepack(false);
          forceInventory(value => value + 1);
        }}
      />}
      <MHeader title="My Inventory" sub={micro ? micro.name : `${rpFieldTerritory(role)} Rep Storage`} nav={nav} back right={<button onClick={() => nav.push('stock-requests')} style={{ border: 'none', background: 'var(--surface-2)', color: 'var(--navy)', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="clock" size={17} /></button>} />
      <div style={{ padding: 16 }}>
        {salesRepackAllowed && personalLocations.length > 1 && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px', marginBottom: 12, boxShadow: 'var(--shadow-sm)' }}>
          <label className="field-label" htmlFor="sales-inventory-location">Inventory location</label>
          <select
            id="sales-inventory-location"
            className="field"
            value={micro ? micro.id : ''}
            onChange={event => {
              const savedLocationId = inventoryPersonalRememberFieldLocation(personalLocations, event.target.value);
              if (savedLocationId) setInventoryLocationId(savedLocationId);
            }}
          >
            {!micro && <option value="">Select storage...</option>}
            {personalLocations.map(location => <option key={location.id} value={location.id}>{location.name || location.id}</option>)}
          </select>
          <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 6 }}>Balances below belong only to the selected associated storage location.</div>
        </div>}
        {!micro && <div role="status" style={{ marginBottom: 16, border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 10, padding: '11px 12px', color: 'var(--muted)', fontSize: 12, lineHeight: 1.45 }}>{personalLocations.length > 1 ? 'Choose one of your associated storage locations above.' : inventoryPersonalNoLocationMessage()}</div>}
        <div style={{ background: 'var(--navy)', color: '#fff', borderRadius: 14, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div><div style={{ fontSize: 11.5, opacity: 0.7 }}>Capacity</div><div className="disp" style={{ fontSize: 26 }}>{invUsed} <span style={{ fontSize: 14, opacity: 0.6 }}>/ {invCap} cs</span></div></div>
            <div style={{ textAlign: 'right' }}><div style={{ fontSize: 11.5, opacity: 0.7 }}>Fronted value</div><div className="disp" style={{ fontSize: 26 }}>{RP.money(invValue)}</div></div>
          </div>
          <div style={{ marginTop: 12 }}><Bar value={invValue} max={valueCap} color="var(--amber)" bg="oklch(1 0 0 / 0.15)" /></div>
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>{RP.money(valueRemaining)} of {RP.money(valueCap)} fronted limit remaining</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 16 }}>
          <MTile icon="download" label="Record Received" accent="var(--ok)" onClick={() => micro ? nav.push('field-receipt', inventoryRouteParams) : toast(inventoryPersonalNoLocationMessage(), 'info')} />
          <MTile icon="alert" label="Report Damage" accent="var(--danger)" onClick={() => micro ? nav.push('damage-report', inventoryRouteParams) : toast(inventoryPersonalNoLocationMessage(), 'info')} />
          <MTile icon="calendar" label="Events" accent="var(--info)" onClick={() => nav.push('rep-events')} />
          <MTile icon="layers" label="Request Stock" accent="var(--amber)" onClick={() => micro ? nav.push('request-stock', inventoryRouteParams) : toast(inventoryPersonalNoLocationMessage(), 'info')} />
          <MTile icon="package" label="Giveaway" accent="var(--navy)" onClick={() => micro ? nav.push('giveaway-log', inventoryRouteParams) : toast(inventoryPersonalNoLocationMessage(), 'info')} />
          {salesRepackAllowed && <MTile icon="layers" label="Build Mix Packs" accent="var(--ok)" onClick={() => personalLocations.length ? setNewRepack(true) : toast(inventoryPersonalNoLocationMessage(), 'info')} />}
        </div>
        {salesRepackAllowed && !personalLocations.length && <div role="status" style={{ marginBottom: 16, border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 10, padding: '11px 12px', color: 'var(--muted)', fontSize: 12, lineHeight: 1.45 }}>{inventoryPersonalNoLocationMessage()}</div>}

        {pendingReceipts.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div><div className="eyebrow" style={{ marginBottom: 4 }}>Pending approval</div><div style={{ fontWeight: 700, fontSize: 13.5 }}>{pendingReceipts.length} inventory receipt{pendingReceipts.length === 1 ? '' : 's'}</div></div>
              <Pill status="Pending" />
            </div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {pendingReceipts.slice(0, 3).map(receipt => (
                <div key={receipt.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, color: 'var(--muted)' }}>
                  <span className="mono" style={{ color: 'var(--ink)', fontWeight: 700 }}>{receipt.id}</span>
                  <span>{receipt.totalCases || 0} cs · {String(receipt.created || receipt.receivedAt || '').slice(0, 10)}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--faint)' }}>These cases are not orderable until operations approves them.</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {fieldInventory.length > 0 ? fieldInventory.map((m, i) => {
            const s = RP.sku(m.sku);
            const low = m.available <= 5;
            return (
              <div key={m.sku || i} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <CanThumb sku={s} h={52} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14.5 }}>{rpSkuDisplayName(s || m.sku)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--faint)' }}>355ml · 12-Pack</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                      {m.fronted > 0 && <span className="pill" style={{ background: 'var(--amber-soft)', color: 'var(--amber-deep)' }}>{m.fronted} fronted</span>}
                      {m.purchased > 0 && <span className="pill" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>{m.purchased} purchased</span>}
                      {m.sample > 0 && <span className="pill" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>{m.sample} sample</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}><span className="disp" style={{ fontSize: 26, color: low ? 'var(--danger)' : 'var(--ink)' }}>{m.available}</span><div style={{ fontSize: 10, color: 'var(--faint)' }}>cap {m.limit}</div></div>
                </div>
                {low && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 11.5, color: 'var(--danger)', fontWeight: 600 }}><Icon name="alert" size={14} />Below safety stock — request restock</div>}
              </div>
            );
          }) : <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 18, textAlign: 'center', color: 'var(--faint)', fontSize: 12.5 }}>No micro-warehouse stock assigned.</div>}
        </div>
      </div>
      <MActionBar>
        <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.push('field-receipt', inventoryRouteParams)}><Icon name="download" size={16} />Received</button>
        <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.push('giveaway-log', inventoryRouteParams)}><Icon name="package" size={16} />Giveaway</button>
        <button className="btn btn-lg btn-primary" style={{ flex: 1 }} onClick={() => nav.push('request-stock', inventoryRouteParams)}><Icon name="layers" size={16} />Request Stock</button>
      </MActionBar>
    </div>
  );
};

/* ---------- CUSTOMER LIST ---------- */
const REP_CUSTOMER_SEGMENTS = [
  { value: 'business', label: 'Businesses' },
  { value: 'consumer', label: 'Consumers' },
  { value: 'all', label: 'All' }
];
const REP_CUSTOMER_FOCUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'due', label: 'Check-in due' },
  { value: 'active', label: 'Active' },
  { value: 'prospect', label: 'Prospects' },
  { value: 'unpaid', label: 'Unpaid' }
];
let repCustomerSegmentPreference = 'business';
let repCustomerFocusPreference = 'all';

function repCustomerMatchesSegment(customer, segment) {
  if (segment === 'all') return true;
  const consumer = rpIsConsumerCustomer(customer);
  return segment === 'consumer' ? consumer : !consumer;
}

function repCustomerNeedsCheckIn(health) {
  const restock = health && health.restock || {};
  const daysUntilCheckIn = Number(restock.daysUntilCheckIn);
  return restock.eligible !== false && (
    ['High', 'Pending'].includes(String(restock.status || ''))
    || (Number.isFinite(daysUntilCheckIn) && daysUntilCheckIn <= 7)
  );
}

function repCustomerMatchesFocus(row, focus) {
  if (focus === 'all') return true;
  if (focus === 'due') return repCustomerNeedsCheckIn(row.health);
  if (focus === 'active') return rpCustomerOrderReady(row.c);
  if (focus === 'prospect') return ['Prospect', 'Draft'].includes(String(row.c && row.c.status || ''));
  if (focus === 'unpaid') return Number(row.health && row.health.unpaidCount || 0) > 0;
  return true;
}

MOBILE_SCREENS['rep-customers'] = function RepCustomers({ nav, role }) {
  const [q, setQ] = useState('');
  const [segment, setSegmentValue] = useState(() => repCustomerSegmentPreference);
  const [focus, setFocusValue] = useState(() => repCustomerFocusPreference);
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const customerPage = useRpServerPagedCustomers({
    limit: 50,
    search: q,
    filters: { segment, focus, sort: 'priority' }
  });
  const customers = live ? customerPage.items : rpFieldCustomers(role);
  const summaryCounts = customerPage.summary && customerPage.summary.counts || {};
  const consumerCount = customers.filter(rpIsConsumerCustomer).length;
  const segmentCounts = live
    ? { business: summaryCounts.business, consumer: summaryCounts.consumer, all: summaryCounts.all }
    : { business: customers.length - consumerCount, consumer: consumerCount, all: customers.length };
  const setSegment = value => {
    repCustomerSegmentPreference = value;
    setSegmentValue(value);
  };
  const setFocus = value => {
    repCustomerFocusPreference = value;
    setFocusValue(value);
  };
  const customerRows = customers.map(c => ({ c, health: repCustomerHealth(c) }));
  const segmentRows = live ? customerRows : customerRows.filter(row => repCustomerMatchesSegment(row.c, segment));
  const serverFocusCounts = customerPage.summary && customerPage.summary.focusBySegment && customerPage.summary.focusBySegment[segment] || {};
  const focusCounts = live
    ? Object.fromEntries(REP_CUSTOMER_FOCUS_FILTERS.map(option => [option.value, serverFocusCounts[option.value]]))
    : Object.fromEntries(REP_CUSTOMER_FOCUS_FILTERS.map(option => [
        option.value,
        segmentRows.filter(row => repCustomerMatchesFocus(row, option.value)).length
      ]));
  let rows = live ? segmentRows : segmentRows.filter(row => repCustomerMatchesFocus(row, focus));
  const query = q.trim().toLowerCase();
  if (!live && query) {
    rows = rows.filter(({ c: customer }) => [customer.name, customer.banner, customer.city, customer.type, customer.tier, customer.email, customer.phone, customer.address, customer.postal]
      .some(value => String(value || '').toLowerCase().includes(query)));
  }
  if (!live) rows = rows.sort((a, b) => b.health.score - a.health.score || String(a.c.name || '').localeCompare(String(b.c.name || '')));
  const countLabel = value => value == null || value === '' || !Number.isFinite(Number(value)) ? '\u2026' : Number(value).toLocaleString();
  const openCustomer = customer => {
    if (live && RP.upsertCustomer) RP.upsertCustomer({ ...customer, skipCreds: true, persisted: true });
    nav.push('rep-customer', { id: customer.id });
  };
  const emptyGroup = segment === 'consumer' ? 'consumers' : segment === 'business' ? 'businesses' : 'customers';
  let emptyCopy = `No ${emptyGroup} are assigned to this profile.`;
  if (query) emptyCopy = `No ${emptyGroup} match "${q.trim()}".`;
  else if (focus === 'due') emptyCopy = `No ${emptyGroup} need a check-in right now.`;
  else if (focus === 'active') emptyCopy = `No active ${emptyGroup} match this view.`;
  else if (focus === 'prospect') emptyCopy = `No ${emptyGroup} are waiting for approval.`;
  else if (focus === 'unpaid') emptyCopy = `No ${emptyGroup} have unpaid invoices.`;
  return (
    <div className="rp-fade">
      <MHeader title="Customers" right={<button onClick={() => nav.push('add-customer')} style={{ border: 'none', background: 'var(--amber)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={19} /></button>} />
      <div style={{ padding: '12px 16px 0' }}>
        <div style={{ position: 'relative' }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--faint)' }} />
          <input className="field" style={{ paddingLeft: 36 }} placeholder="Search customers…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <div className="eyebrow" style={{ marginTop: 12, marginBottom: 6 }}>Account type</div>
        <div role="group" aria-label="Customer account type" style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 4 }}>
          {REP_CUSTOMER_SEGMENTS.map(option => {
            const selected = segment === option.value;
            return (
              <button type="button" key={option.value} data-customer-segment={option.value} aria-pressed={selected} onClick={() => setSegment(option.value)} style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 100, border: selected ? '1px solid var(--navy)' : '1px solid var(--line)', background: selected ? 'var(--navy)' : 'var(--surface)', color: selected ? '#fff' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                {option.label} <span style={{ opacity: selected ? 0.75 : 0.65 }}>{countLabel(segmentCounts[option.value])}</span>
              </button>
            );
          })}
        </div>
        <div className="eyebrow" style={{ marginTop: 8, marginBottom: 6 }}>Focus</div>
        <div role="group" aria-label="Customer focus" style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 4 }}>
          {REP_CUSTOMER_FOCUS_FILTERS.map(option => {
            const selected = focus === option.value;
            return (
              <button type="button" key={option.value} data-customer-focus={option.value} aria-pressed={selected} onClick={() => setFocus(option.value)} style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 100, border: selected ? '1px solid var(--amber-deep)' : '1px solid var(--line)', background: selected ? 'var(--amber-soft)' : 'var(--surface)', color: selected ? 'var(--amber-deep)' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                {option.label} <span style={{ opacity: selected ? 0.75 : 0.65 }}>{countLabel(focusCounts[option.value])}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {live && customerPage.initialLoading && <div data-rep-customers-loading="true" style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 12, padding: 28, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}><Icon name="refresh" size={22} /><div style={{ marginTop: 8, fontWeight: 700, color: 'var(--ink)' }}>Loading customers...</div><div style={{ marginTop: 3 }}>Fetching your complete customer directory.</div></div>}
        {live && !customerPage.initialLoading && !customerPage.loaded && customerPage.error && <div data-rep-customers-error="true" style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 12, padding: 14, fontSize: 12.5, fontWeight: 650 }}>{customerPage.error}<button type="button" className="btn" onClick={customerPage.retry} style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>Try Again</button></div>}
        {rows.map(({ c, health }) => {
          const orderReady = rpCustomerOrderReady(c);
          const consumerAccount = rpIsConsumerCustomer(c);
          const unpaidCount = health.unpaidCount;
          const historyLabel = rpCustomerOrderHistoryLabel(c);
          const [healthFg, healthBg] = statusColors(health.tone);
          const restock = health.restock || {};
          return (
          <button key={c.id} data-customer-kind={consumerAccount ? 'consumer' : 'business'} onClick={() => openCustomer(c)} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Avatar name={c.name} size={40} color={c.status === 'Prospect' ? 'var(--faint)' : 'var(--navy)'} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14.5 }}>{c.name}</span>
                  <span className="pill" style={{ color: healthFg, background: healthBg, flexShrink: 0 }}>{health.label}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
                  <span style={{ fontSize: 12, color: 'var(--faint)' }}>{[c.city, c.type].filter(Boolean).join(' · ')}</span>
                  <span className="pill" style={{ fontSize: 10.5, color: consumerAccount ? 'var(--amber-deep)' : 'var(--info)', background: consumerAccount ? 'var(--amber-soft)' : 'var(--info-bg)' }}>{consumerAccount ? 'Consumer' : 'Business'}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{[restock.label, restock.checkInDate ? `by ${restock.checkInDate.slice(5)}` : '', restock.cadenceLabel].filter(Boolean).join(' - ') || historyLabel}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                  {health.reasons.slice(0, 3).map(reason => <span key={reason} className="pill" style={{ fontSize: 10.5, color: 'var(--muted)', background: 'var(--surface-2)' }}>{reason}</span>)}
                  {unpaidCount > 0 && <Pill status="Unpaid" style={{ fontSize: 11 }}>{unpaidCount} unpaid invoice{unpaidCount === 1 ? '' : 's'}</Pill>}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <span onClick={e => { e.stopPropagation(); if (live && RP.upsertCustomer) RP.upsertCustomer({ ...c, skipCreds: true, persisted: true }); orderReady ? nav.push('create-order', { cust: c.id }) : nav.push('rep-customer', { id: c.id }); }} className={'btn btn-sm' + (orderReady ? ' btn-primary' : '')} style={{ flex: 1 }}>{orderReady ? 'Order' : 'Approval'}</span>
              <span onClick={e => { e.stopPropagation(); if (live && RP.upsertCustomer) RP.upsertCustomer({ ...c, skipCreds: true, persisted: true }); nav.push('visit-log', { id: c.id }); }} className="btn btn-sm" style={{ flex: 1 }}>Visit</span>
            </div>
          </button>
          );
        })}
        {(!live || customerPage.loaded) && rows.length === 0 && <MEmpty icon="customers" title="No customers found" sub={emptyCopy} action={<button className="btn btn-primary btn-sm" onClick={() => nav.push('add-customer')}><Icon name="plus" size={15} />Add Customer</button>} />}
        {live && customerPage.loaded && <div data-rep-customers-pagination style={{ border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}><RpServerLoadMore {...customerPage} label="customers" onLoadMore={customerPage.loadMore} onRetry={customerPage.loadMore} /></div>}
      </div>
    </div>
  );
};

function mobileClean(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}
function mobilePublicLocatorEligibleCustomer(c) {
  if (!c) return false;
  if (typeof locatorPublicEligibleCustomer === 'function') return locatorPublicEligibleCustomer(c);
  if (typeof rpPublicLocatorEligibleType === 'function') return rpPublicLocatorEligibleType(c.type);
  const text = [c.type, c.tier].map(value => String(value || '').trim().toLowerCase()).join(' ');
  return String(c.type || '').trim().toLowerCase() !== 'distributor' && !(/\bconsumer\b|direct/.test(text));
}
function mobilePublicLocatorName(c, location, locationCount) {
  const customerName = mobileClean(c && (c.publicName || c.name || c.banner));
  const locationName = mobileClean(location && location.name);
  if (!locationName || locationName === 'Location' || locationName === 'Ship-to') return customerName;
  if (locationCount <= 1 || locationName.toLowerCase() === customerName.toLowerCase()) return locationName;
  return `${customerName} - ${locationName}`;
}
function mobilePublicLocatorRows(c) {
  if (!c) return [];
  const fallbackPhone = typeof rpPrimaryCustomerPhone === 'function' ? rpPrimaryCustomerPhone(c) : c.phone;
  const locations = typeof rpCustomerLocations === 'function'
    ? rpCustomerLocations(c)
    : [{
        id: `${c.id || 'customer'}-MAIN`,
        name: c.name || c.banner || 'Location',
        address: c.address || '',
        city: c.city || '',
        postal: c.postal || '',
        phone: fallbackPhone || '',
        active: true
      }];
  const active = locations.filter(location => location && location.active !== false);
  const rows = active.length ? active : locations;
  return rows.map((location, index) => {
    const ship = location.shippingAddress || location.shipping_address || {};
    const address = mobileClean(location.address || ship.address || ship.address1 || c.address);
    const city = mobileClean(location.city || ship.city || c.city || c.territory);
    const postal = mobileClean(location.postal || location.zip || ship.postal || ship.zip || c.postal);
    const province = mobileClean(location.province || ship.province || c.province);
    const phone = mobileClean(location.phone || ship.phone || fallbackPhone);
    return {
      id: location.id || `${c.id || 'customer'}-${index}`,
      name: mobilePublicLocatorName(c, location, rows.length),
      address,
      city,
      province,
      postal,
      phone,
      active: location.active !== false
    };
  });
}
function mobilePublicLocatorReadiness(c) {
  const missing = [];
  const rows = mobilePublicLocatorRows(c);
  const eligible = mobilePublicLocatorEligibleCustomer(c);
  const status = mobileClean(c && c.status).toLowerCase();
  if (!eligible) missing.push('eligible retail account type');
  if (status === 'draft' || status === 'inactive') missing.push('active customer status');
  if (!mobileClean(c && (c.publicName || c.name || c.banner))) missing.push('public-facing business name');
  if (!rows.length) missing.push('street address');
  rows.forEach((row, index) => {
    const label = row.name || `location ${index + 1}`;
    if (!row.address) missing.push(`${label} street address`);
    if (!row.city) missing.push(`${label} city`);
  });
  return { eligible, missing: Array.from(new Set(missing)), rows };
}
function MobilePublicLocatorCard({ c, nav, onUpdated }) {
  const [, force] = useState(0);
  const [busy, setBusy] = useState(false);
  const readiness = mobilePublicLocatorReadiness(c);
  const canEdit = typeof rpCanEditCustomerProfile === 'function' ? rpCanEditCustomerProfile(c) : rpCustomerInProfileScope(rpAuthProfile(), c);
  const currentlyPublic = Boolean(c && c.public);
  const listed = Boolean(currentlyPublic && readiness.eligible);
  const firstRow = readiness.rows[0] || {};
  if (!c || (!readiness.eligible && !c.public)) return null;
  const toggle = async () => {
    if (busy || !canEdit) return;
    const next = !currentlyPublic;
    if (next && readiness.missing.length) {
      toast('Review public listing details before publishing.', 'warn');
      return;
    }
    setBusy(true);
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/update', {
          method: 'POST',
          body: JSON.stringify({ id: c.id, publicLocator: next })
        });
        if (response.customer) RP.upsertCustomer({ ...c, ...response.customer, skipCreds: true, persisted: true });
        await rpRefreshAfterMutation(next ? `${c.name} listed on Find Us` : `${c.name} removed from Find Us`);
      } else {
        RP.upsertCustomer({ ...c, public: next });
      }
      toast(next ? `${c.name} listed on Find Us` : `${c.name} removed from Find Us`, next ? 'ok' : 'info');
      force(x => x + 1);
      onUpdated && onUpdated();
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not update public listing.', 'danger');
    } finally {
      setBusy(false);
    }
  };
  const editButton = (
    <button className="btn" disabled={!canEdit} onClick={() => nav.push('edit-customer', { id: c.id })} style={{ width: '100%', justifyContent: 'center', opacity: !canEdit ? 0.65 : 1 }}>
      <Icon name="pen" size={14} />Edit Public Details
    </button>
  );
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div>
          <span className="eyebrow">Find Us Listing</span>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>Public listing on ranchpower.com/find.</div>
        </div>
        <span className="pill" style={{ color: listed ? 'var(--ok)' : readiness.missing.length ? 'var(--amber-deep)' : 'var(--muted)', background: listed ? 'var(--ok-bg)' : readiness.missing.length ? 'var(--amber-soft)' : 'var(--surface-2)' }}>{listed ? 'Live' : readiness.missing.length ? 'Review' : 'Private'}</span>
      </div>

      <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 12, background: listed ? 'var(--amber-soft)' : 'var(--surface-2)', marginBottom: 11 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{firstRow.name || c.name}</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>{[firstRow.address, firstRow.city, firstRow.province, firstRow.postal].filter(Boolean).join(', ') || 'Address missing'}</div>
        <div style={{ fontSize: 12.5, color: firstRow.phone ? 'var(--ink-soft)' : 'var(--faint)', marginTop: 4 }}>{firstRow.phone || 'No public phone'}</div>
        {readiness.rows.length > 1 && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>{readiness.rows.length} active locations will publish.</div>}
      </div>

      {readiness.missing.length > 0 && (
        <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--amber)', borderRadius: 10, padding: '10px 12px', marginBottom: 11 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 800 }}><Icon name="alert" size={14} />Public details need review</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5 }}>{readiness.missing.slice(0, 3).join(', ')}{readiness.missing.length > 3 ? ` +${readiness.missing.length - 3} more` : ''}</div>
        </div>
      )}

      {readiness.missing.length && !currentlyPublic ? editButton : (
        <div style={{ display: 'grid', gridTemplateColumns: currentlyPublic ? '1fr 1fr' : '1fr', gap: 8 }}>
          <button className={currentlyPublic ? 'btn' : 'btn btn-primary'} disabled={busy || !canEdit || (!currentlyPublic && readiness.missing.length > 0)} onClick={toggle} style={{ justifyContent: 'center', opacity: busy || !canEdit || (!currentlyPublic && readiness.missing.length > 0) ? 0.65 : 1 }}>
            <Icon name={currentlyPublic ? 'x' : 'pin'} size={14} />{busy ? 'Saving...' : currentlyPublic ? 'Remove Listing' : 'List on Find Us'}
          </button>
          {currentlyPublic && editButton}
        </div>
      )}
    </div>
  );
}

function repCustomerPrimaryEmail(customer) {
  const contacts = typeof rpCustomerContacts === 'function' ? rpCustomerContacts(customer) : (Array.isArray(customer && customer.contacts) ? customer.contacts : []);
  const primaryContact = contacts.find(contact => contact && contact.primary) || contacts[0] || {};
  const emailRows = Array.isArray(customer && customer.emailAddresses) ? customer.emailAddresses : [];
  const primaryEmail = emailRows.find(row => row && row.primary) || emailRows[0] || {};
  return [customer && customer.email, customer && customer.buyerEmail, primaryContact.email, primaryEmail.value || primaryEmail.email, ...contacts.map(contact => contact && contact.email), ...emailRows.map(row => row && (row.value || row.email))]
    .map(value => String(value || '').replace(/[\r\n]/g, '').trim().toLowerCase())
    .find(value => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) || '';
}

function repCustomerCoordinatePair(source) {
  if (!source || source.lat == null || source.lng == null || String(source.lat).trim() === '' || String(source.lng).trim() === '') return null;
  const lat = Number(source.lat);
  const lng = Number(source.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180 || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

function repCustomerStoredText(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const text = String(value).trim();
  return text && text !== '[object Object]' ? text : '';
}

function repCustomerDirectionsDestination(customer) {
  const locations = Array.isArray(customer && customer.locations) ? customer.locations.filter(location => location && location.active !== false) : [];
  const coordinate = [customer, ...locations].map(repCustomerCoordinatePair).find(Boolean);
  if (coordinate) return { kind: 'coordinates', query: `${coordinate.lat},${coordinate.lng}` };

  for (const source of [...locations, customer]) {
    if (!source) continue;
    const shipping = source.shippingAddress && typeof source.shippingAddress === 'object' ? source.shippingAddress : (source.shipping_address && typeof source.shipping_address === 'object' ? source.shipping_address : {});
    const street = [source.address, shipping.address, shipping.address1].map(repCustomerStoredText).find(Boolean);
    if (!street) continue;
    const parts = [street, source.address2 || shipping.address2, source.city || shipping.city, source.province || shipping.province, source.postal || source.zip || shipping.postal || shipping.zip, source.country || shipping.country]
      .map(repCustomerStoredText)
      .filter((part, index, values) => part && values.findIndex(value => value.toLowerCase() === part.toLowerCase()) === index)
      .reduce((result, part) => result.some(existing => existing.toLowerCase().includes(part.toLowerCase())) ? result : [...result, part], []);
    return { kind: 'address', query: parts.join(', ') };
  }
  return null;
}

/* ---------- CUSTOMER PROFILE ---------- */
MOBILE_SCREENS['rep-customer'] = function RepCustomer({ nav, id }) {
  const exactCustomer = useRpExactCustomer({ customerId: id });
  if (exactCustomer.loading) return (
    <div className="rp-fade" data-exact-customer-loading="true">
      <MHeader title="Loading Customer" sub={id} nav={nav} back />
      <div style={{ padding: 16 }}><MEmpty icon="refresh" title="Loading customer" sub="Fetching this account from the complete customer directory." /></div>
    </div>
  );
  if (exactCustomer.error) return (
    <div className="rp-fade" data-exact-customer-error="true">
      <MHeader title="Customer Unavailable" sub={id} nav={nav} back />
      <div style={{ padding: 16 }}><MEmpty icon="alert" title="Customer could not load" sub={exactCustomer.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactCustomer.retry}>Try Again</button>} /></div>
    </div>
  );
  if (!exactCustomer.customer) return (
    <div className="rp-fade" data-exact-customer-missing="true" data-customer-deep-link-missing="true">
      <MHeader title="Customer Unavailable" nav={nav} back />
      <div style={{ padding: 16 }}><MEmpty icon="customers" title="Customer not found" sub={`Customer ${id || ''} was not found in your current sales scope.`} /></div>
    </div>
  );
  return <RepCustomerFound key={exactCustomer.customer.id} nav={nav} id={id} customer={exactCustomer.customer} />;
};

function RepCustomerFound({ nav, id, customer }) {
  const c = customer;
  const directoryOnly = c.supplyChannel === 'distributor';
  const [tab, setTab] = useState('overview');
  const [, force] = useState(0);
  const [cardSetupOpen, setCardSetupOpen] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [approvalError, setApprovalError] = useState('');
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalMessage, setPortalMessage] = useState('');
  const [portalError, setPortalError] = useState('');
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const liveOrderHistory = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const customerOrderPage = useRpServerPagedRows({ mode: 'orders', limit: 50, customerId: id });
  const aggregateLastOrderId = String(c && c.lastOrderId || '').trim();
  const exactLastOrder = useRpExactOrder({ orderId: aggregateLastOrderId, enabled: liveOrderHistory && Boolean(aggregateLastOrderId) });
  const customerOrders = liveOrderHistory
    ? customerOrderPage.items.map(item => item.order).filter(order => order && order.cust === id)
    : rpCustomerOrders(id);
  const orderSummary = rpCustomerOrderSummary(c, { orders: customerOrders });
  const lastOrder = liveOrderHistory
    ? (exactLastOrder.order && String(exactLastOrder.order.cust || '') === String(id || '') ? exactLastOrder.order : null)
    : orderSummary.lastOrder;
  const reorderLoading = liveOrderHistory && Boolean(aggregateLastOrderId) && exactLastOrder.loading;
  const reorderUnavailable = liveOrderHistory && Boolean(aggregateLastOrderId) && !exactLastOrder.loading && !lastOrder
    && (Boolean(exactLastOrder.error) || exactLastOrder.notFound || exactLastOrder.loaded);
  const lastOrderDate = orderSummary.lastOrderDate || (lastOrder ? rpOrderDateLabel(lastOrder) : '');
  const approvalReady = rpCustomerOrderReady(c);
  const quoteProspect = typeof rpCustomerQuoteProspect === 'function' && rpCustomerQuoteProspect(c);
  const intakePending = Boolean(quoteProspect && !c.onboardingSubmittedAt);
  const approvalRole = rpNormalizeRole(rpAuthProfile() && rpAuthProfile().role);
  const managementApprovalOverride = ['admin', 'leadership', 'ops', 'sales_manager'].includes(approvalRole);
  const intakeBlocksApproval = Boolean(intakePending && !managementApprovalOverride);
  const archivedCustomer = Boolean(c.archivedAt || c.archived_at || String(c.status || '').trim().toLowerCase() === 'inactive');
  const portalInviteReady = typeof rpCustomerPortalInviteReady === 'function' ? rpCustomerPortalInviteReady(c) : approvalReady;
  const intakeOnly = Boolean(!approvalReady && portalInviteReady && quoteProspect);
  const canApproveCustomer = rpCanApproveCustomers(null, c);
  const buyerEmail = repCustomerPrimaryEmail(c);
  const primaryPhone = rpPrimaryCustomerPhone(c);
  const canEditCustomer = typeof rpCanEditCustomerProfile === 'function' ? rpCanEditCustomerProfile(c) : rpCustomerInProfileScope(rpAuthProfile(), c);
  const canSendPortalAccess = typeof rpCanSendCustomerPortalAccess === 'function' ? rpCanSendCustomerPortalAccess(c) : rpCanInviteUsers();
  const customerSavedStripeCard = typeof rpStripeDefaultCard === 'function' ? rpStripeDefaultCard(c) : null;
  const customerHealth = repCustomerHealth(c);
  const [customerHealthFg, customerHealthBg] = statusColors(customerHealth.tone);
  const restockSignal = customerHealth.restock || {};
  const startReorder = () => {
    if (reorderLoading) return;
    if (reorderUnavailable) {
      exactLastOrder.retry();
      return;
    }
    if (!lastOrder || !lastOrder.id) {
      nav.push('create-order', { cust: id });
      return;
    }
    if (liveOrderHistory) rpUpsertOrder(lastOrder);
    nav.push('create-order', { cust: id, reorder: lastOrder.id });
  };
  const quickOrderLabel = !approvalReady
    ? 'Request Approval'
    : reorderLoading ? 'Loading Reorder' : reorderUnavailable ? 'Retry Reorder' : lastOrder ? 'Reorder' : 'New Order';
  const quickOrderIcon = !approvalReady ? 'flag' : (aggregateLastOrderId || lastOrder ? 'refresh' : 'plus');
  const quickOrder = () => {
    if (!approvalReady) {
      requestApproval();
      return;
    }
    startReorder();
  };
  const callCustomer = () => {
    if (!primaryPhone) {
      toast('No phone on file', 'warn');
      return;
    }
    window.location.href = `tel:${primaryPhone}`;
  };
  const emailCustomer = () => {
    if (!buyerEmail) {
      toast('No valid email on file', 'warn');
      return;
    }
    window.location.href = `mailto:${buyerEmail}`;
  };
  const directCustomer = () => {
    const destination = repCustomerDirectionsDestination(c);
    if (!destination) {
      toast('No saved GPS pin or street address for this customer', 'warn');
      return;
    }
    window.location.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination.query)}`;
  };
  const sendPortalInvite = async () => {
    if (portalBusy) return;
    setPortalMessage('');
    setPortalError('');
    if (!portalInviteReady) {
      setPortalError('Approve this customer before sending normal portal access.');
      return;
    }
    if (!buyerEmail) {
      setPortalError('Add a buyer email before sending portal access.');
      return;
    }
    if (!canSendPortalAccess) {
      setPortalError('You can only send portal access for your assigned or covered customers.');
      return;
    }
    setPortalBusy(true);
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        await rpApi('/api/users/invite', {
          method: 'POST',
          body: JSON.stringify({
            customerId: c.id,
            email: buyerEmail,
            name: c.buyer && c.buyer !== 'Buyer' ? c.buyer : c.name,
            role: 'customer',
            ...(intakeOnly ? { purpose: 'retailer_intake' } : {})
          })
        });
        await rpRefreshAfterMutation(`${intakeOnly ? 'Intake link' : 'Portal access'} sent to ${c.name}`);
      }
      setPortalMessage(`${intakeOnly ? 'Intake' : 'Setup'} link sent to ${buyerEmail}.`);
      toast(`${intakeOnly ? 'Intake link' : 'Portal access'} sent to ${buyerEmail}`, 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not send portal access.';
      setPortalError(message);
      toast(message, 'danger');
    } finally {
      setPortalBusy(false);
    }
  };
  const approveCustomer = async () => {
    if (approvalBusy || approvalReady) return;
    if (archivedCustomer) {
      setApprovalError('This archived account is unavailable for approval. Restore its saved quote first, or use the active customer account.');
      return;
    }
    if (intakeBlocksApproval) {
      setApprovalError('Send the intake link and approve after the retailer submits the form.');
      return;
    }
    setApprovalBusy(true);
    setApprovalMessage('');
    setApprovalError('');
    try {
      let updated = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/update', {
          method: 'POST',
          body: JSON.stringify({
            creditStatus: c.credit === 'Watch' || c.credit === 'Hold' ? c.credit : 'Good',
            id: c.id,
            status: 'Approved'
          })
        });
        updated = RP.upsertCustomer({ ...c, ...response.customer, status: 'Approved', risk: 'Low', credit: response.customer && response.customer.credit ? response.customer.credit : 'Good', approvalTaskCloseoutWarning: response.approvalTaskCloseoutWarning || '', closedApprovalTaskIds: Array.isArray(response.closedApprovalTaskIds) ? response.closedApprovalTaskIds : [], skipCreds: true, persisted: true });
        await rpRefreshAfterMutation(`${updated.name} approved`);
        updated.portalInviteTaskId = response.portalInviteTaskId || '';
        updated.portalInviteTaskError = response.portalInviteTaskError || '';
      } else {
        updated = RP.upsertCustomer({ ...c, status: 'Approved', risk: 'Low', credit: c.credit === 'Watch' || c.credit === 'Hold' ? c.credit : 'Good' });
      }
      const approvalWarning = updated.approvalTaskCloseoutWarning || updated.portalInviteTaskError || '';
      setApprovalMessage(`${updated.portalInviteTaskId ? `${updated.name} approved. Portal setup is ready from this screen.` : `${updated.name} approved for ordering.`}${approvalWarning ? ` ${approvalWarning}` : ''}`);
      toast(updated.portalInviteTaskId ? `${updated.name} approved - invite task queued` : `${updated.name} approved`, approvalWarning ? 'warn' : 'ok');
      force(x => x + 1);
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not approve customer.';
      setApprovalError(message);
      toast(message, 'danger');
    } finally {
      setApprovalBusy(false);
    }
  };
  const requestApproval = async () => {
    if (approvalBusy || approvalReady) return;
    setApprovalBusy(true);
    setApprovalMessage('');
    setApprovalError('');
    try {
      let task = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/request-approval', {
          method: 'POST',
          body: JSON.stringify({
            customerId: c.id,
            note: `Sales request from ${rpDisplayName()} for ${c.name}.`
          })
        });
        if (response.task) task = RP.upsertTask(response.task);
        await rpRefreshAfterMutation(response.requested ? `${c.name} approval requested` : `${c.name} approval already requested`);
        setApprovalMessage(response.message || (response.requested ? 'Approval request sent.' : 'Approval request already open.'));
      } else {
        task = RP.addTask({ title: `Approve customer: ${c.name}`, owner: 'Sales Manager', assigner: rpDisplayName(), priority: 'High', cat: 'Sales', due: typeof rpTodayYmd === 'function' ? rpTodayYmd(c, 1) : new Date(Date.now() + 86400000).toISOString().slice(0, 10), cust: c.id, note: `Review ${c.name} for customer approval.` });
        setApprovalMessage('Approval request sent.');
      }
      if (task) force(x => x + 1);
      toast(`Approval requested for ${c.name}`, 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not request approval.';
      setApprovalError(message);
      toast(message, 'danger');
    } finally {
      setApprovalBusy(false);
    }
  };
  return (
    <div className="rp-fade">
      {onboardingOpen && <CustomerOnboardingEntryModal c={c} onClose={() => setOnboardingOpen(false)} onSaved={() => force(x => x + 1)} />}
      {cardSetupOpen && <StripeCardSetupModal customer={c} onClose={() => setCardSetupOpen(false)} onSaved={() => force(x => x + 1)} />}
      <MHeader title={c.name} sub={`${directoryOnly ? 'Distributor-supplied' : c.status} · ${c.city}`} nav={nav} back dark
        right={<div style={{ display: 'flex', gap: 7 }}>
          {canEditCustomer && <button onClick={() => nav.push('edit-customer', { id: c.id })} aria-label="Edit customer" style={{ border: 'none', background: 'oklch(1 0 0 / 0.14)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="pen" size={16} /></button>}
          <button onClick={callCustomer} aria-label="Call customer" style={{ border: 'none', background: 'oklch(1 0 0 / 0.14)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="phone" size={17} /></button>
        </div>} />
      <div style={{ background: 'var(--navy)', padding: '0 16px 14px', display: 'flex', gap: 8 }}>
        {['Overview', 'Orders', 'Visits', 'Photos', 'Fees'].map((t, i) => (
          <button key={t} onClick={() => setTab(t.toLowerCase())} style={{ border: 'none', background: 'transparent', color: tab === t.toLowerCase() ? '#fff' : 'oklch(0.75 0.02 235)', fontSize: 12.5, fontWeight: 600, padding: '6px 0', borderBottom: '2px solid ' + (tab === t.toLowerCase() ? 'var(--amber)' : 'transparent'), flexShrink: 0 }}>{t}</button>
        ))}
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {tab === 'overview' && <>
          {directoryOnly && <div data-distributor-directory="true" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14 }}>
            <div className="eyebrow" style={{ marginBottom: 9 }}>Distributor-supplied</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>{c.distributorName ? `Supplied by ${c.distributorName}. ` : ''}Saved for your customer directory and Find Us.</div>
            <div style={{ marginTop: 9, fontWeight: 700, color: c.public ? 'var(--ok)' : 'var(--muted)' }}>{c.public ? 'Listed on Find Us' : c.coordinateVerification || (c.locations || []).some(location => location.coordinateVerification) ? 'Location confirmed; listing is private' : 'Confirm the store location to publish on Find Us'}</div>
            {canEditCustomer && <div style={{ marginTop: 7, fontSize: 12, color: 'var(--muted)' }}>Edit this record to update the location or change its supplier to Ranch Power directly.</div>}
          </div>}
          {/* Contact + account */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="eyebrow">Account</span>{!directoryOnly && <Pill status={c.credit} />}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field2 k="Buyer" v={c.buyer} />{!directoryOnly && <Field2 k="Terms" v={c.terms} />}
              {!directoryOnly && <Field2 k="Price list" v={c.tier} />}<Field2 k="Rep" v={c.rep} />
              {!directoryOnly && <div style={{ gridColumn: '1 / -1' }}><Field2 k="Case price" v={rpCustomerPricingLabel(c)} /></div>}
              <Field2 k="Email" v={buyerEmail || (directoryOnly ? 'Not provided' : 'Missing')} /><Field2 k="Phone" v={primaryPhone || (directoryOnly ? 'Not provided' : 'Missing')} />
            </div>
          </div>

          {!directoryOnly && <div data-sales-card-vault="true" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <div><span className="eyebrow">Card on File</span><div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>Securely stored with Stripe for approved invoice charges.</div></div>
              <Pill status={customerSavedStripeCard ? 'Paid' : 'Pending'}>{customerSavedStripeCard ? 'Saved' : 'No card'}</Pill>
            </div>
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 12px', fontSize: 12.5, fontWeight: 700, color: customerSavedStripeCard ? 'var(--ink)' : 'var(--muted)', marginBottom: 10 }}>
              {customerSavedStripeCard ? rpStripeCardLabel(customerSavedStripeCard) : 'No saved card connected.'}
            </div>
            {canEditCustomer && <button type="button" className="btn btn-primary" onClick={() => setCardSetupOpen(true)} style={{ width: '100%', justifyContent: 'center' }}><Icon name="card" size={15} />{customerSavedStripeCard ? 'Replace Saved Card' : 'Add Card on File'}</button>}
          </div>}

          <MobilePublicLocatorCard c={c} nav={nav} onUpdated={() => force(x => x + 1)} />

          {!directoryOnly && <>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span className="eyebrow">Sales Health</span>
              <span className="pill" style={{ color: customerHealthFg, background: customerHealthBg }}>{customerHealth.label}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {customerHealth.reasons.slice(0, 4).map(reason => <span key={reason} className="pill" style={{ color: 'var(--muted)', background: 'var(--surface-2)' }}>{reason}</span>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field2 k="Check-in" v={[restockSignal.label, restockSignal.checkInDate ? restockSignal.checkInDate.slice(5) : ''].filter(Boolean).join(' ') || 'Healthy'} />
              <Field2 k="Cadence" v={restockSignal.cadenceLabel || 'Learning'} />
              <Field2 k="Last visit" v={customerHealth.daysSinceVisit == null ? 'Not logged' : customerHealth.daysSinceVisit === 0 ? 'Today' : `${customerHealth.daysSinceVisit}d ago`} />
              <Field2 k="Shelf signal" v={customerHealth.shelf.total == null ? 'No shelf count' : customerHealth.shelf.empty ? `${customerHealth.shelf.empty} SKU out` : customerHealth.shelf.low ? `${customerHealth.shelf.low} SKU low` : `${customerHealth.shelf.total} on shelf`} />
              <Field2 k="Confidence" v={restockSignal.confidence || 'Low'} />
              <Field2 k="Priority score" v={customerHealth.score} />
            </div>
          </div>

          </>}

          {!directoryOnly && (approvalReady || intakeOnly) && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <span className="eyebrow">Portal Setup</span>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>{intakeOnly ? 'Secure intake form access. Ordering stays locked until approval.' : 'order.ranchpower.com account setup link.'}</div>
                </div>
                <Pill status={c.onboardingSubmittedAt ? 'Active' : 'Pending'}>{c.onboardingSubmittedAt ? 'Done' : 'Required'}</Pill>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5, color: 'var(--ink-soft)', marginBottom: 12 }}>
                <Field2 k="Buyer email" v={buyerEmail || 'Missing'} />
                <Field2 k="Access" v={canSendPortalAccess ? 'Allowed' : 'Not allowed'} />
                {intakeOnly && <Field2 k="Portal" v="Intake only - ordering locked" />}
              </div>
              <button className="btn btn-primary" disabled={portalBusy || !buyerEmail || !canSendPortalAccess} onClick={sendPortalInvite} style={{ width: '100%', justifyContent: 'center', opacity: portalBusy || !buyerEmail || !canSendPortalAccess ? 0.65 : 1 }}><Icon name="mail" size={15} />{portalBusy ? 'Sending...' : intakeOnly ? 'Send Intake Link' : 'Send Setup Link'}</button>
              <button className="btn" disabled={!canEditCustomer} onClick={() => setOnboardingOpen(true)} style={{ width: '100%', justifyContent: 'center', marginTop: 8, opacity: !canEditCustomer ? 0.65 : 1 }}><Icon name="doc" size={15} />{c.onboardingSubmittedAt ? 'Update Onboarding' : 'Record Paper Form'}</button>
              {portalMessage && <div style={{ fontSize: 11.5, color: 'var(--ok)', marginTop: 9, fontWeight: 700 }}>{portalMessage}</div>}
              {portalError && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 9, fontWeight: 700 }}>{portalError}</div>}
            </div>
          )}

          {!directoryOnly && !approvalReady && (
            <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--amber)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
                <Icon name="alert" size={17} style={{ color: 'var(--amber-deep)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{archivedCustomer ? 'Archived account unavailable' : intakeBlocksApproval ? 'Retailer intake needed' : 'Approval needed'}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>{archivedCustomer ? 'Restore its saved quote first, or use the active customer account.' : intakeBlocksApproval ? 'Send the intake link above, then approve after submission.' : intakePending && managementApprovalOverride ? 'Intake is incomplete; managers can approve now and follow up.' : `${c.status} accounts need approval before ordering.`}</div>
                </div>
                <Pill status={c.status}>{c.status}</Pill>
              </div>
              <button className="btn btn-primary" disabled={approvalBusy || intakeBlocksApproval || archivedCustomer} onClick={intakeBlocksApproval || archivedCustomer ? undefined : (canApproveCustomer ? approveCustomer : requestApproval)} style={{ width: '100%', justifyContent: 'center', opacity: approvalBusy || intakeBlocksApproval || archivedCustomer ? 0.65 : 1 }}><Icon name={canApproveCustomer ? 'check' : 'flag'} size={15} />{approvalBusy ? 'Working...' : archivedCustomer ? 'Archived Account' : intakeBlocksApproval ? 'Waiting for Intake' : canApproveCustomer ? 'Approve Customer' : 'Request Approval'}</button>
              {approvalMessage && <div style={{ fontSize: 11.5, color: 'var(--ok)', marginTop: 9, fontWeight: 700 }}>{approvalMessage}</div>}
              {approvalError && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 9, fontWeight: 700 }}>{approvalError}</div>}
            </div>
          )}

          {/* Last order + reorder */}
          {liveOrderHistory && customerOrderPage.initialLoading && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 18, boxShadow: 'var(--shadow-sm)', textAlign: 'center', color: 'var(--muted)' }}>
              <Icon name="refresh" size={18} /><div style={{ marginTop: 7, fontWeight: 700, color: 'var(--ink)' }}>Loading recent order history...</div>
            </div>
          )}
          {liveOrderHistory && !customerOrderPage.initialLoading && !customerOrderPage.loaded && customerOrderPage.error && (
            <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 12, padding: 14, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
              <div>{customerOrderPage.error}</div><button type="button" className="btn btn-sm" onClick={customerOrderPage.retry} style={{ marginTop: 9 }}>Try Again</button>
            </div>
          )}
          {reorderLoading && (
            <div data-exact-reorder-loading="true" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, color: 'var(--muted)', fontSize: 12.5, fontWeight: 700 }}>
              <Icon name="refresh" size={16} /> <span style={{ marginLeft: 6 }}>Loading the exact previous order...</span>
            </div>
          )}
          {reorderUnavailable && (
            <div data-exact-reorder-error="true" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 12, padding: 14, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
              <div>Reorder could not load the exact previous order. No substitute order was selected.</div><button type="button" className="btn btn-sm" onClick={exactLastOrder.retry} style={{ marginTop: 9 }}>Try Again</button>
            </div>
          )}
          {lastOrder && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span className="eyebrow">Last Order · {lastOrderDate}</span><span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{lastOrder.id}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {(lastOrder.lines || []).map((l, i) => { const s = RP.sku(l.sku); return (
                  <div key={i} style={{ flex: 1, textAlign: 'center', background: 'var(--surface-2)', borderRadius: 10, padding: '10px 4px' }}>
                    <FlavorChip sku={s} size={26} />
                    <div className="disp" style={{ fontSize: 18, marginTop: 5 }}>{l.qty}</div>
                    <div style={{ fontSize: 9.5, color: 'var(--faint)' }}>{rpSkuDisplayName(s || l.sku).split(' ')[0]}</div>
                  </div>
                ); })}
              </div>
              {!directoryOnly && <button className="btn btn-primary" style={{ width: '100%' }} onClick={approvalReady ? startReorder : requestApproval}><Icon name={approvalReady ? 'refresh' : 'flag'} size={16} />{approvalReady ? 'Reorder these' : 'Request Approval'}</button>}
            </div>
          )}

          {c.note && <div style={{ background: 'var(--amber-soft)', borderRadius: 12, padding: 14 }}><div className="eyebrow" style={{ color: 'var(--amber-deep)', marginBottom: 6 }}>Note</div><div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{c.note}</div></div>}

          <div data-customer-quick-actions="true" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 9 }}>
            <MTile icon="phone" label="Call" accent="var(--ok)" onClick={callCustomer} />
            <MTile icon="mail" label="Email" accent="var(--info)" onClick={emailCustomer} />
            {!directoryOnly && <MTile icon="doc" label="Quote" accent="var(--amber)" onClick={() => nav.push('quick-retail-quote', { id })} />}
            <MTile icon="doc" label="Saved Quotes" accent="var(--info)" onClick={() => nav.push('retail-quotes', { id })} />
            <MTile icon="pin" label="Directions" accent="var(--navy)" onClick={directCustomer} />
            {!directoryOnly && <MTile icon={quickOrderIcon} label={quickOrderLabel} accent={approvalReady ? 'var(--amber)' : 'var(--amber-deep)'} onClick={quickOrder} />}
            <MTile icon="pin" label="Log Visit" accent="var(--ok)" onClick={() => nav.push('visit-log', { id })} />
            <MTile icon="camera" label="Photos" accent="var(--navy)" onClick={() => setTab('photos')} />
          </div>
        </>}

        {tab === 'orders' && <CustomerOrdersTab id={id} nav={nav} live={liveOrderHistory} orders={customerOrders} page={customerOrderPage} />}
        {tab === 'visits' && <CustomerVisitsTab c={c} nav={nav} />}
        {tab === 'photos' && <CustomerPhotosTab nav={nav} id={id} />}
        {tab === 'fees' && <CustomerFeesTab id={id} />}
      </div>
    </div>
  );
}

function MEmpty({ icon, title, sub, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 54, height: 54, borderRadius: 14, background: 'var(--surface-2)', color: 'var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={icon} size={26} /></span>
      <div><div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{title}</div><div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>{sub}</div></div>
      {action}
    </div>
  );
}

function CustomerOrdersTab({ id, nav, live = false, orders: providedOrders, page }) {
  const c = RP.cust(id);
  const ready = rpCustomerOrderReady(c);
  const orders = live ? (Array.isArray(providedOrders) ? providedOrders : []) : rpCustomerOrders(id);
  if (live && page && page.initialLoading) return <MEmpty icon="refresh" title="Loading order history" sub="Fetching this customer's complete order history." />;
  if (live && page && !page.loaded && page.error) return <MEmpty icon="alert" title="Order history unavailable" sub={page.error} action={<button type="button" className="btn btn-sm" onClick={page.retry}>Try Again</button>} />;
  if (!orders.length && c?.supplyChannel === 'distributor') return <MEmpty icon="customers" title="Distributor-supplied" sub={c.distributorName ? `This location is supplied by ${c.distributorName}.` : 'This location is supplied through a distributor.'} />;
  if (!orders.length && !ready) return <MEmpty icon="orders" title="Approval needed" sub="Approve this account before pitching a first PO." action={<button className="btn btn-primary btn-sm" onClick={() => nav.push('rep-customer', { id })}><Icon name="flag" size={15} />Request Approval</button>} />;
  if (!orders.length) return <MEmpty icon="orders" title="No orders yet" sub="This account hasn't ordered — pitch a first PO." action={<button className="btn btn-primary btn-sm" onClick={() => nav.push('create-order', { cust: id })}><Icon name="plus" size={15} />New Order</button>} />;
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {orders.map(o => { const t = orderTotals(o), canEdit = rpOrderEditableBySales(o), invoiceStatus = repInvoiceStatus(o), paymentStatus = repInvoicePaymentStatus(o); return (
          <div key={o.id} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 13, boxShadow: 'var(--shadow-sm)' }}>
            <button onClick={() => { if (live) rpUpsertOrder(o); nav.push('rep-order-detail', { id: o.id }); }} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span className="mono nowrap" style={{ fontWeight: 600, fontSize: 12.5 }}>{o.id}</span><Pill status={o.status} /></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{t.cases} cases - {rpOrderDateLabel(o)}</span><span className="disp" style={{ fontSize: 17 }}>{RP.money(t.total)}</span>
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 10 }}>
                <Pill status={invoiceStatus} style={{ fontSize: 11 }}>{invoiceStatus}</Pill>
                <Pill status={paymentStatus} style={{ fontSize: 11 }}>{paymentStatus}</Pill>
              </div>
            </button>
            {canEdit && <button className="btn btn-sm" onClick={() => { if (live) rpUpsertOrder(o); nav.push('create-order', { editId: o.id }); }} style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}><Icon name="pen" size={14} />Edit Order</button>}
          </div>
        ); })}
      </div>
      {live && page && page.loaded && <div style={{ marginTop: 10, border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}><RpServerLoadMore {...page} label="orders" onLoadMore={page.loadMore} onRetry={page.loadMore} /></div>}
    </>
  );
}

function CustomerVisitsTab({ c, nav }) {
  const fallbackVisits = [
    { type: 'Reorder check', when: c.daysSince ? c.daysSince + ' days ago' : 'Recently', note: 'Shelf running low on Ginger Citrus. Buyer keen on Mango promo.', who: c.rep },
    { type: 'Merchandising', when: '3 weeks ago', note: 'Built end-cap display. Took photos.', who: c.rep },
    { type: 'New account pitch', when: '6 weeks ago', note: 'First meeting — sampled the line, positive.', who: c.rep },
  ];
  const visits = (RP.VISITS || []).filter(v => v.cust === c.id);
  const rows = visits.length ? visits : (rpHasLiveAuth() ? [] : fallbackVisits);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <button className="btn btn-primary" style={{ marginBottom: 14 }} onClick={() => nav.push('visit-log', { id: c.id })}><Icon name="pin" size={16} />Log a Visit</button>
      {rows.map((v, i) => (
        <div key={i} style={{ display: 'flex', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: i === 0 ? 'var(--amber)' : 'var(--line)', marginTop: 5 }} />
            {i < rows.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--line-2)' }} />}
          </div>
          <div style={{ paddingBottom: 18, flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 13.5, fontWeight: 600 }}>{v.type}</span><span style={{ fontSize: 11, color: 'var(--faint)' }}>{v.when}</span></div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 4, lineHeight: 1.45 }}>{v.note}</div>
            <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 5 }}>{v.who}</div>
          </div>
        </div>
      ))}
      {!rows.length && <MEmpty icon="pin" title="No visits yet" sub="Only visits saved to this customer will appear here." />}
    </div>
  );
}

function CustomerPhotosTab({ nav, id }) {
  const [, force] = useState(0);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const [error, setError] = useState('');
  const visitPhotos = (RP.VISITS || [])
    .filter(v => v.cust === id && v.photos)
    .flatMap(v => Object.entries(v.photos || {})
      .filter(([, value]) => value === true || (value && typeof value === 'object' && value.attached !== false))
      .map(([kind, value]) => ({
        id: `${v.id}-${kind}`,
        customerId: id,
        label: value && typeof value === 'object' ? (value.label || value.name || kind.replace(/_/g, ' ')) : (kind === 'shelf' ? 'Shelf photo' : kind === 'display' ? 'Display photo' : kind.replace(/_/g, ' ')),
        location: value && typeof value === 'object' ? (value.locationName || value.category || '') : '',
        path: value && typeof value === 'object' ? (value.path || '') : '',
        photoKey: kind,
        url: value && typeof value === 'object' ? value.url : '',
        visitId: v.id,
        when: v.when || v.date || 'Recently'
      })))
    .filter(photo => photo.url);
  const live = !(window.RP_AUTH && window.RP_AUTH.demo);
  const removePhoto = async photo => {
    if (!photo || deletingId) return;
    if (!live) { toast('Photo removal requires live data.', 'warn'); return; }
    if (!window.confirm(`Remove ${photo.label || 'this photo'}?`)) return;
    setDeletingId(photo.id);
    setError('');
    try {
      const response = await rpApi('/api/customers/photo', {
        method: 'DELETE',
        body: JSON.stringify({ customerId: id, visitId: photo.visitId, photoKey: photo.photoKey })
      });
      if (response.visit && RP.upsertVisit) RP.upsertVisit(response.visit);
      await rpRefreshAfterMutation('Customer photo removed');
      setLightboxPhoto(null);
      toast(response.storageWarning ? 'Photo removed; storage cleanup needs review.' : 'Customer photo removed', response.storageWarning ? 'warn' : 'ok');
      force(x => x + 1);
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not remove customer photo.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setDeletingId('');
    }
  };
  return (
    <div>
      <button className="btn btn-primary" style={{ width: '100%', marginBottom: 14 }} onClick={() => nav.push('visit-log', { id })}><Icon name="camera" size={16} />Add Photo</button>
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
      {visitPhotos.length === 0 ? (
        <MEmpty icon="camera" title="No photos yet" sub="Uploaded account, storefront, and visit photos will appear here." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {visitPhotos.map((p, i) => (
            <button key={p.id || i} onClick={() => setLightboxPhoto(p)} style={{ textAlign: 'left', padding: 0, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)', background: 'var(--surface)', cursor: 'zoom-in' }}>
              <ProtectedImage src={p.url} alt={p.label} style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
              <div style={{ padding: '8px 11px', background: 'var(--surface)' }}><div style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{p.label}</div><div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>{p.location || 'Customer photo'}</div><div className="mono" style={{ fontSize: 9.5, color: 'var(--faint)', marginTop: 2 }}>{/(ago|Recently|Just now)/i.test(String(p.when)) ? p.when : `${p.when} ago`}</div></div>
            </button>
          ))}
        </div>
      )}
      <ProtectedImageLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} onDelete={live && lightboxPhoto ? () => removePhoto(lightboxPhoto) : null} deleting={Boolean(lightboxPhoto && deletingId === lightboxPhoto.id)} />
    </div>
  );
}

function CustomerFeesTab({ id }) {
  const fees = (Array.isArray(RP.DEDUCTIONS) ? RP.DEDUCTIONS : []).filter(d => d && d.cust === id);
  if (!fees.length) return <MEmpty icon="trade" title="No trade spend" sub="No slotting, promos or deductions on this account." />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {fees.map(d => (
        <div key={d.id} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 13, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{d.type}</span><Pill status={d.status} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
            <span className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>{d.id} · {d.period}</span><span className="disp" style={{ fontSize: 19 }}>{RP.money(d.amount)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
function Field2({ k, v }) { return <div><div style={{ fontSize: 10.5, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{k}</div><div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 2 }}>{v}</div></div>; }

const REP_ORDER_EXCEPTION_OPTIONS = [
  { code: 'approval', label: 'Approval', tone: 'Pending' },
  { code: 'on_hold', label: 'On hold', tone: 'High' },
  { code: 'backorder', label: 'Backorder', tone: 'Pending' },
  { code: 'unpaid', label: 'Unpaid', tone: 'Submitted' },
  { code: 'overdue', label: 'Overdue', tone: 'High' },
  { code: 'stale', label: 'Stale', tone: 'Pending' },
];

function repOrderExceptionOption(code) {
  return REP_ORDER_EXCEPTION_OPTIONS.find(option => option.code === code) || { code, label: code || 'Exception', tone: 'Pending' };
}

function repOrderExceptionReason(code, detail, options = {}) {
  const definition = repOrderExceptionOption(code);
  const priorities = { approval: 90, on_hold: 80, backorder: 70, unpaid: 60, overdue: 100, stale: 50 };
  return {
    ageDays: options.ageDays,
    code,
    detail,
    dueDate: options.dueDate || null,
    label: definition.label,
    priority: priorities[code] || 0,
    source: options.source || 'workflow'
  };
}

function repOrderExceptionNextAction(reason) {
  if (!reason) return null;
  if (reason.code === 'approval') return { code: 'review_approval', dueDate: reason.dueDate || null, label: 'Review approval' };
  if (reason.code === 'on_hold') return { code: 'resolve_hold', dueDate: reason.dueDate || null, label: 'Resolve hold' };
  if (reason.code === 'backorder') return { code: 'check_inventory', dueDate: reason.dueDate || null, label: 'Check inventory' };
  if (reason.code === 'unpaid') return { code: 'collect_payment', dueDate: reason.dueDate || null, label: 'Follow up on payment' };
  if (reason.code === 'overdue' && reason.source === 'invoice') return { code: 'collect_payment', dueDate: reason.dueDate || null, label: 'Follow up on overdue payment' };
  if (reason.code === 'overdue') return { code: 'resolve_delivery', dueDate: reason.dueDate || null, label: 'Resolve overdue delivery' };
  return { code: 'update_order', dueDate: reason.dueDate || null, label: 'Update next action' };
}

function repDemoOrderExceptionItems(role, search, mineOnly) {
  const today = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: rpFieldTerritory(role) }) : new Date().toISOString().slice(0, 10);
  const repName = rpFieldUserName(role).toLowerCase();
  const closed = new Set(['Delivered', 'Fulfilled', 'Invoiced', 'Paid', 'Closed', 'Complete', 'Completed', 'Cancelled', 'Canceled', 'Refunded', 'Void']);
  const query = String(search || '').trim().toLowerCase();
  const items = rpFieldOrders(role)
    .filter(order => !mineOnly || String(order.rep || order.owner || '').trim().toLowerCase() === repName)
    .map(order => {
      const customer = RP.cust(order.cust || order.customerId || order.customer_id) || {};
      const status = String(order.status || 'Unknown').trim() || 'Unknown';
      const open = !closed.has(status);
      const billing = typeof rpOrderInvoiceScan === 'function' ? rpOrderInvoiceScan(order) : { open: false, paymentStatus: 'Not invoiced' };
      const totals = rpOrderTotals(order);
      const invoiceDueDate = repMyDayYmd(order.invoiceDueDate || order.invoice_due_date);
      const deliveryDueDate = repMyDayYmd(order.requestedDeliveryDate || order.requested_delivery_date || order.date);
      const updatedDate = repMyDayYmd(order.updatedAt || order.updated_at || order.updated || order.createdAt || order.created_at || order.created || order.date);
      const unpaid = Number(totals.total || 0) > 0.005 && Boolean(billing.open);
      const reasons = [];

      if (status === 'Needs Approval') reasons.push(repOrderExceptionReason('approval', order.flag || order.reason || 'Approval is required before the order can advance.', { source: 'approval' }));
      if (status === 'On Hold') reasons.push(repOrderExceptionReason('on_hold', order.flag || order.reason || 'The order is on hold and needs review before it can advance.'));
      if (typeof rpOrderIsBackorder === 'function' && rpOrderIsBackorder(order)) reasons.push(repOrderExceptionReason('backorder', 'Inventory was intentionally deferred and availability needs review.', { source: 'inventory' }));
      if (unpaid) reasons.push(repOrderExceptionReason('unpaid', 'The issued invoice has not been marked paid.', { dueDate: invoiceDueDate, source: 'invoice' }));
      if (unpaid && invoiceDueDate && invoiceDueDate < today) {
        reasons.push(repOrderExceptionReason('overdue', 'The invoice due date has passed and payment is still outstanding.', { ageDays: Math.max(1, -repMyDayDateDistance(invoiceDueDate, today)), dueDate: invoiceDueDate, source: 'invoice' }));
      } else if (open && deliveryDueDate && deliveryDueDate < today) {
        reasons.push(repOrderExceptionReason('overdue', 'The requested delivery date has passed while the order remains open.', { ageDays: Math.max(1, -repMyDayDateDistance(deliveryDueDate, today)), dueDate: deliveryDueDate, source: 'delivery' }));
      }
      const workflowAge = updatedDate ? Math.max(0, -repMyDayDateDistance(updatedDate, today)) : 0;
      if (open && workflowAge >= 14) reasons.push(repOrderExceptionReason('stale', `The open order has not changed in ${workflowAge} days.`, { ageDays: workflowAge, dueDate: typeof rpAddDaysYmd === 'function' ? rpAddDaysYmd(updatedDate, 14, order) : null }));

      reasons.sort((a, b) => b.priority - a.priority || a.code.localeCompare(b.code));
      if (!reasons.length) return null;
      const primaryException = reasons[0];
      return {
        assignedRepId: order.repId || order.rep_id || null,
        createdAt: order.createdAt || order.created_at || order.created || null,
        customerId: order.cust || order.customerId || order.customer_id || null,
        customerName: customer.name || order.customer || order.cust || 'Unknown customer',
        deliveryMethod: order.deliveryMethod || order.delivery_method || order.delivery || null,
        exceptionCodes: reasons.map(reason => reason.code),
        exceptions: reasons,
        financialStatus: billing.paymentStatus || 'Not invoiced',
        id: order.id,
        invoiceDueDate,
        invoiceNumber: order.invoiceNumber || order.invoice_number || null,
        nextAction: repOrderExceptionNextAction(primaryException),
        poNumber: order.poNumber || order.po_number || order.po || null,
        primaryException,
        requestedDeliveryDate: deliveryDueDate,
        status,
        subtotal: Number(totals.sub || 0),
        territory: order.territory || customer.territory || null,
        total: Number(totals.total || 0),
        updatedAt: order.updatedAt || order.updated_at || order.updated || order.createdAt || order.created_at || order.created || null
      };
    })
    .filter(Boolean)
    .filter(item => !query || [
      item.id, item.customerId, item.customerName, item.territory, item.status, item.financialStatus, item.invoiceNumber, item.poNumber, item.deliveryMethod,
      ...item.exceptionCodes, ...item.exceptions.map(reason => `${reason.label} ${reason.detail}`)
    ].some(value => String(value || '').toLowerCase().includes(query)))
    .sort((a, b) => Number(b.primaryException.priority || 0) - Number(a.primaryException.priority || 0) || String(a.primaryException.dueDate || '9999-12-31').localeCompare(String(b.primaryException.dueDate || '9999-12-31')) || String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));

  const byReason = Object.fromEntries(REP_ORDER_EXCEPTION_OPTIONS.map(option => [option.code, 0]));
  items.forEach(item => item.exceptionCodes.forEach(code => { byReason[code] = Number(byReason[code] || 0) + 1; }));
  return {
    filters: { search: String(search || '').trim(), staleDays: 14 },
    items,
    page: { hasMore: false, limit: items.length, offset: 0 },
    scope: { kind: 'demo', mineOnly },
    summary: { byReason, matched: items.length, returned: items.length, scanned: rpFieldOrders(role).length, scanTruncated: false }
  };
}

function SalesOrderExceptionsWorkspace({ nav, role, desktop = false }) {
  const live = rpHasLiveAuth();
  const roleKey = rpNormalizeRole((rpAuthProfile() && rpAuthProfile().role) || role);
  const canFilterMine = ['rep', 'partner'].includes(roleKey);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [mineOnly, setMineOnly] = useState(canFilterMine);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(live);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [openingOrderId, setOpeningOrderId] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);
  const exceptionQueryKey = JSON.stringify({ canFilterMine, mineOnly, search: debouncedSearch, selectedReason });

  useEffect(() => {
    if (!live) {
      setLoading(false);
      setLoadingMore(false);
      setError('');
      setLoadMoreError('');
      setPayload(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setLoadMoreError('');
      try {
        const params = new URLSearchParams({ limit: '100' });
        if (debouncedSearch) params.set('q', debouncedSearch);
        if (canFilterMine && mineOnly) params.set('mine', '1');
        if (selectedReason) params.set('exception', selectedReason);
        const response = await rpApi(`/api/orders/exceptions?${params.toString()}`);
        if (!response || response.schemaVersion !== 1 || !Array.isArray(response.items)) throw new Error('Order exceptions returned an unsupported response.');
        if (!cancelled) setPayload({ ...response, _queryKey: exceptionQueryKey });
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(err && err.message ? err.message : 'Could not load order exceptions.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [live, exceptionQueryKey, reload]);

  const loadMore = async () => {
    const cursor = payload && payload.page && payload.page.nextCursor;
    if (!live || loading || loadingMore || !cursor) return;
    setLoadingMore(true);
    setLoadMoreError('');
    try {
      const params = new URLSearchParams({ limit: '100', cursor });
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (canFilterMine && mineOnly) params.set('mine', '1');
      if (selectedReason) params.set('exception', selectedReason);
      const response = await rpApi(`/api/orders/exceptions?${params.toString()}`);
      if (!response || response.schemaVersion !== 1 || !Array.isArray(response.items)) throw new Error('Order exceptions returned an unsupported response.');
      setPayload(previous => {
        if (!previous || previous._queryKey !== exceptionQueryKey) return previous;
        const existing = previous && Array.isArray(previous.items) ? previous.items : [];
        const merged = [];
        const byId = new Map();
        [...existing, ...response.items].forEach(item => {
          const id = String(item && item.id || '').trim();
          if (!id) return;
          if (byId.has(id)) merged[byId.get(id)] = item;
          else {
            byId.set(id, merged.length);
            merged.push(item);
          }
        });
        return { ...response, items: merged, _queryKey: exceptionQueryKey };
      });
    } catch (err) {
      setLoadMoreError(err && err.message ? err.message : 'Could not load more order exceptions.');
    } finally {
      setLoadingMore(false);
    }
  };

  const activePayload = live ? payload : repDemoOrderExceptionItems(role, debouncedSearch, mineOnly);
  const summary = activePayload && activePayload.summary || { byReason: {}, matched: 0, returned: 0, scanned: 0, scanTruncated: false };
  const page = activePayload && activePayload.page || { hasMore: false };
  const allItems = activePayload && Array.isArray(activePayload.items) ? activePayload.items : [];
  const items = !live && selectedReason ? allItems.filter(item => (item.exceptionCodes || []).includes(selectedReason)) : allItems;
  const countsReady = Boolean(!loading && !error && activePayload);
  const countLabel = value => countsReady ? Number(value || 0) : '-';
  const openExceptionOrder = async item => {
    const id = String(item && item.id || '').trim();
    if (!id || openingOrderId) return;
    const cached = (RP.ORDERS || []).find(order => String(order && order.id || '').trim() === id);
    if (cached) {
      nav.push('rep-order-detail', { id });
      return;
    }
    setOpeningOrderId(id);
    try {
      const order = await rpLoadExactOrder({ orderId: id });
      if (!order) throw new Error('The exact order could not be loaded.');
      nav.push('rep-order-detail', { id });
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not open this order.';
      if (typeof toast === 'function') toast(message, 'danger');
      else setLoadMoreError(message);
    } finally {
      setOpeningOrderId('');
    }
  };
  const content = (
    <div data-order-exceptions-source={live ? 'live-api-scoped' : 'demo-local-derived'} data-order-exceptions-debounce="300" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div data-order-exceptions-provenance={live ? 'live' : 'demo'} style={{ background: live ? 'var(--ok-bg)' : 'var(--info-bg)', color: live ? 'var(--ok)' : 'var(--info)', borderRadius: 10, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.4 }}>
        {live ? 'Live mode - results come from the server-scoped order exceptions service.' : 'Demo data - exceptions are derived locally from sample orders; no live API request was made.'}
      </div>

      <div className={desktop ? 'card' : undefined} style={{ background: desktop ? undefined : 'var(--surface)', border: desktop ? undefined : '1px solid var(--line)', borderRadius: 12, padding: desktop ? '16px 18px' : 12, boxShadow: desktop ? undefined : 'var(--shadow-sm)' }}>
        <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 260px' }}>
            <Icon name="search" size={15} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--faint)' }} />
            <input data-order-exceptions-search className="field" value={search} maxLength={120} onChange={event => setSearch(event.target.value)} placeholder="Search order, customer, PO, invoice..." style={{ paddingLeft: 36 }} />
          </div>
          {canFilterMine && <label data-order-exceptions-mine style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 40, padding: '0 10px', border: '1px solid var(--line)', borderRadius: 9, color: 'var(--ink)', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}><input type="checkbox" checked={mineOnly} onChange={event => setMineOnly(event.target.checked)} />Mine only</label>}
        </div>
        <div data-order-exception-filters style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingTop: 11, paddingBottom: 2 }}>
          <button data-order-exception-filter="all" onClick={() => setSelectedReason('')} style={{ border: '1px solid ' + (!selectedReason ? 'var(--navy)' : 'var(--line)'), background: !selectedReason ? 'var(--navy)' : 'var(--surface)', color: !selectedReason ? '#fff' : 'var(--ink)', borderRadius: 999, padding: '7px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: 750 }}>All <span className="mono">{countLabel(summary.matched)}</span></button>
          {REP_ORDER_EXCEPTION_OPTIONS.map(option => {
            const active = selectedReason === option.code;
            const [fg, bg] = statusColors(option.tone);
            return <button key={option.code} data-order-exception-filter={option.code} onClick={() => setSelectedReason(active ? '' : option.code)} style={{ border: '1px solid ' + (active ? fg : 'var(--line)'), background: active ? bg : 'var(--surface)', color: active ? fg : 'var(--ink)', borderRadius: 999, padding: '7px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: 750 }}>{option.label} <span className="mono">{countLabel(summary.byReason && summary.byReason[option.code])}</span></button>;
          })}
        </div>
        <div style={{ color: 'var(--faint)', fontSize: 10.75, marginTop: 7 }}>{live && activePayload && !summary.complete ? 'Counts update as more scoped orders are scanned. ' : 'Counts cover the current search and scope. '}One order can have multiple reasons.</div>
      </div>

      {loading && <div data-order-exceptions-loading="true" style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 12, padding: 28, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}><Icon name="refresh" size={22} /><div style={{ marginTop: 8, fontWeight: 700, color: 'var(--ink)' }}>Loading order exceptions...</div><div style={{ marginTop: 3 }}>Checking the latest scoped orders.</div></div>}
      {!loading && error && <div data-order-exceptions-error="true" style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 12, padding: 14, fontSize: 12.5, fontWeight: 650 }}>{error}<button className="btn" onClick={() => setReload(value => value + 1)} style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>Try again</button></div>}
      {!loading && !error && live && activePayload && !summary.complete && <div data-order-exceptions-progress="true" style={{ border: '1px solid var(--info)', background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 10, padding: '10px 12px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.4 }}>Scanned {Number(summary.scanned || 0).toLocaleString()} scoped orders so far. Use Load More until the scan is complete; no later exceptions are discarded.</div>}
      {!loading && !error && !items.length && <div data-order-exceptions-empty="true" style={{ border: '1px dashed var(--line)', background: 'var(--surface)', borderRadius: 12 }}><MEmpty icon="check" title={page.hasMore ? 'No matches in this scan window' : 'No order exceptions found'} sub={page.hasMore ? 'Continue with Load More; later scoped orders are still reachable.' : (search.trim() || selectedReason ? 'Try a different search, reason, or scope.' : 'There are no approval, hold, backorder, payment, overdue, or stale-order exceptions in this scope.')} /></div>}
      {!loading && !error && items.length > 0 && <div data-order-exceptions-results={items.length} style={{ display: 'grid', gridTemplateColumns: desktop ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 10 }}>
        {items.map(item => {
          const primary = item.primaryException || item.exceptions && item.exceptions[0] || {};
          const definition = repOrderExceptionOption(primary.code);
          const [fg, bg] = statusColors(definition.tone);
          const nextAction = item.nextAction && item.nextAction.label || 'Open order and review';
          const nextDue = item.nextAction && item.nextAction.dueDate;
          return (
            <button key={item.id} type="button" disabled={Boolean(openingOrderId)} data-order-exception-id={item.id} data-order-exception-reasons={(item.exceptionCodes || []).join(',')} onClick={() => openExceptionOrder(item)} style={{ width: '100%', textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderLeft: `4px solid ${fg}`, borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', opacity: openingOrderId && openingOrderId !== item.id ? 0.72 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 9 }}><span className="mono" style={{ color: 'var(--ink)', fontWeight: 800, fontSize: 12.5 }}>{item.id}</span><Pill status={item.status}>{item.status}</Pill></div>
              <div style={{ color: 'var(--ink)', fontSize: 14, fontWeight: 800, marginTop: 7 }}>{item.customerName || item.customerId || 'Unknown customer'}</div>
              <div style={{ color: 'var(--faint)', fontSize: 11.5, marginTop: 2 }}>{[item.territory, Number(item.total || item.subtotal || 0) ? RP.money2(Number(item.total || item.subtotal || 0)) : ''].filter(Boolean).join(' - ')}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>{(item.exceptions || []).map(reason => { const reasonDefinition = repOrderExceptionOption(reason.code); const [reasonFg, reasonBg] = statusColors(reasonDefinition.tone); return <span key={reason.code} className="pill" style={{ color: reasonFg, background: reasonBg }}>{reason.label || reasonDefinition.label}{reason.ageDays ? ` - ${reason.ageDays}d` : ''}</span>; })}</div>
              <div style={{ background: bg, color: fg, borderRadius: 9, padding: '9px 10px', marginTop: 10 }}><div style={{ fontSize: 11.5, fontWeight: 800 }}>Next: {nextAction}{nextDue ? ` by ${nextDue}` : ''}</div><div style={{ fontSize: 11.25, lineHeight: 1.4, marginTop: 3 }}>{primary.detail || 'Review this order exception.'}</div></div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 5, color: 'var(--amber-deep)', fontSize: 11.5, fontWeight: 800, marginTop: 10 }}>{openingOrderId === item.id ? 'Loading exact order...' : 'Open exact order'}<Icon name={openingOrderId === item.id ? 'refresh' : 'chevR'} size={14} /></div>
            </button>
          );
        })}
      </div>}
      {!loading && !error && live && activePayload && <div data-order-exceptions-pagination style={{ border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}><RpServerLoadMore error={loadMoreError} items={allItems} label="exceptions" loadingMore={loadingMore} onLoadMore={loadMore} onRetry={loadMore} page={page} total={{ value: summary.complete ? Number(summary.matched || 0) : null }} /></div>}
    </div>
  );

  if (desktop) {
    return <div className="rp-fade" data-sales-order-exceptions="desktop" style={{ padding: '26px 30px 40px', maxWidth: 1400, margin: '0 auto' }}><PageHead eyebrow="Sales Operations" title="Order Exceptions" sub="Search and prioritize approvals, holds, backorders, unpaid invoices, overdue work, and stale orders." actions={<button className="btn btn-primary btn-sm" onClick={() => nav.push('create-order')}><Icon name="plus" size={15} />New Order</button>} />{content}</div>;
  }
  return <div className="rp-fade" data-sales-order-exceptions="mobile"><MHeader title="Order Exceptions" sub="Orders needing action" nav={nav} back /><div style={{ padding: 16 }}>{content}</div></div>;
}

MOBILE_SCREENS['order-exceptions'] = function RepOrderExceptions({ nav, role }) {
  return <SalesOrderExceptionsWorkspace nav={nav} role={role} />;
};

/* ---------- REP ORDERS LIST ---------- */
MOBILE_SCREENS['rep-orders'] = function RepOrders({ nav, role }) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const serverPage = useRpServerPagedRows({ mode: 'orders', limit: 50, search, tab });
  const sourceRows = live
    ? serverPage.items.map(item => ({ order: item.order, customer: item.customer })).filter(row => row.order && row.order.id)
    : rpFieldOrders(role).map(order => ({ order, customer: RP.cust(order.cust) }));
  const isOpen = order => !['Delivered', 'Invoiced', 'Paid', 'Closed', 'Cancelled', 'Canceled', 'Void'].includes(String(order && order.status || '').trim());
  const needsApproval = order => ['Needs Approval', 'On Hold'].includes(String(order && order.status || '').trim());
  let list = sourceRows;
  if (!live && tab === 'open') list = list.filter(row => isOpen(row.order));
  if (!live && tab === 'approval') list = list.filter(row => needsApproval(row.order));
  if (!live && search.trim()) {
    const query = search.trim().toLowerCase();
    list = list.filter(({ order, customer }) => [
      order.id,
      order.invoiceNumber,
      order.po,
      order.territory,
      order.rep,
      customer && customer.name,
      customer && customer.legalName,
      customer && customer.buyer,
      customer && customer.email
    ].filter(Boolean).join(' ').toLowerCase().includes(query));
  }
  const localCounts = {
    all: sourceRows.length,
    open: sourceRows.filter(row => isOpen(row.order)).length,
    approval: sourceRows.filter(row => needsApproval(row.order)).length
  };
  const totalRepresentsAll = tab === 'all' && !search.trim();
  const counts = live
    ? Object.fromEntries(Object.keys(localCounts).map(key => [key, rpServerSummaryValue(serverPage.summary, key, key === 'all' && totalRepresentsAll && serverPage.total.value != null ? serverPage.total.value : null)]))
    : localCounts;
  const openOrder = order => {
    if (live) rpUpsertOrder(order);
    nav.push('rep-order-detail', { id: order.id });
  };
  return (
    <div className="rp-fade" data-rep-orders-source={live ? 'server' : 'demo'}>
      <MHeader title="My Orders" right={<button data-open-order-exceptions onClick={() => nav.push('order-exceptions')} style={{ border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--amber-deep)', borderRadius: 9, padding: '7px 9px', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 800 }}><Icon name="alert" size={14} />Exceptions</button>} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button onClick={() => nav.push('order-exceptions')} style={{ border: '1px solid var(--amber)', background: 'var(--amber-soft)', color: 'var(--ink)', borderRadius: 11, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}><span style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--amber)', color: 'var(--navy)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="alert" size={16} /></span><span style={{ flex: 1, minWidth: 0 }}><span style={{ display: 'block', fontSize: 12.5, fontWeight: 800 }}>Review order exceptions</span><span style={{ display: 'block', color: 'var(--muted)', fontSize: 11.25, marginTop: 2 }}>Search approvals, holds, backorders, payment issues, and stale orders.</span></span><Icon name="chevR" size={15} style={{ color: 'var(--amber-deep)' }} /></button>
        <div data-rep-orders-search style={{ position: 'relative' }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--faint)', pointerEvents: 'none' }} />
          <input className="field" value={search} maxLength={120} onChange={event => setSearch(event.target.value)} placeholder="Search order, customer, PO, invoice..." style={{ paddingLeft: 36 }} />
        </div>
        <div data-rep-orders-tabs style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 2 }}>
          {[['all', 'All'], ['open', 'Open'], ['approval', 'Needs Approval']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} style={{ border: '1px solid ' + (tab === key ? 'var(--navy)' : 'var(--line)'), background: tab === key ? 'var(--navy)' : 'var(--surface)', color: tab === key ? '#fff' : 'var(--ink-soft)', borderRadius: 999, padding: '7px 10px', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: 750 }}>{label}<span className="mono" style={{ opacity: 0.72 }}>{counts[key] == null ? '\u2026' : Number(counts[key]).toLocaleString()}</span></button>
          ))}
        </div>
        {live && serverPage.initialLoading && <div data-rep-orders-loading="true" style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 12, padding: 28, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}><Icon name="refresh" size={22} /><div style={{ marginTop: 8, fontWeight: 700, color: 'var(--ink)' }}>Loading orders...</div><div style={{ marginTop: 3 }}>Fetching your complete scoped order history.</div></div>}
        {live && !serverPage.initialLoading && !serverPage.loaded && serverPage.error && <div data-rep-orders-error="true" style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 12, padding: 14, fontSize: 12.5, fontWeight: 650 }}>{serverPage.error}<button type="button" className="btn" onClick={serverPage.retry} style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>Try Again</button></div>}
        {list.map(({ order: o, customer }) => { const t = orderTotals(o), c = customer || RP.cust(o.cust) || { name: o.customer || o.cust || 'Customer' }, canEdit = rpOrderEditableBySales(o), invoiceStatus = repInvoiceStatus(o), paymentStatus = repInvoicePaymentStatus(o); return (
          <div key={o.id} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <button onClick={() => openOrder(o)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="mono" style={{ fontWeight: 600, fontSize: 13 }}>{o.id}</span><Pill status={o.status} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6 }}>{c.name}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{t.cases} cases - {rpOrderDateLabel(o)}</span>
                <span className="disp" style={{ fontSize: 18 }}>{RP.money(t.total)}</span>
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 10 }}>
                {rpOrderIsBackorder(o) && <Pill status="On Hold" style={{ fontSize: 11 }}>Advance order</Pill>}
                <Pill status={invoiceStatus} style={{ fontSize: 11 }}>{invoiceStatus}</Pill>
                <Pill status={paymentStatus} style={{ fontSize: 11 }}>{paymentStatus}</Pill>
              </div>
            </button>
            {canEdit && <button className="btn btn-sm" onClick={() => { if (live) rpUpsertOrder(o); nav.push('create-order', { editId: o.id }); }} style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}><Icon name="pen" size={14} />Edit Order</button>}
          </div>
        ); })}
        {(!live || serverPage.loaded) && list.length === 0 && <MEmpty icon="orders" title={search.trim() || tab !== 'all' ? 'No matching orders' : 'No orders yet'} sub={search.trim() || tab !== 'all' ? 'Try a different search or order view.' : 'Orders submitted by or assigned to this profile will appear here.'} action={<button className="btn btn-primary btn-sm" onClick={() => nav.push('create-order')}><Icon name="plus" size={15} />New Order</button>} />}
        {live && serverPage.loaded && <div data-rep-orders-pagination style={{ border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}><RpServerLoadMore {...serverPage} label="orders" onLoadMore={serverPage.loadMore} onRetry={serverPage.loadMore} /></div>}
      </div>
    </div>
  );
};
const REP_ORDER_FULFILLMENT_STAGES = ['Submitted', 'Approved', 'Allocated', 'Picked', 'Dispatched', 'Delivered'];
const REP_ORDER_ADVANCE_ACTIONS = {
  Approved: { icon: 'check', label: 'Approve Order' },
  Allocated: { icon: 'layers', label: 'Allocate From My Supply' },
  Picked: { icon: 'inventory', label: 'Mark Picked' },
  Dispatched: { icon: 'truck', label: 'Mark Dispatched' },
  Delivered: { icon: 'check', label: 'Mark Delivered' }
};
const REP_ORDER_ADVANCE_NOTES = {
  Approved: 'Approved from the sales app.',
  Allocated: 'Allocated from sales rep supply.',
  Picked: 'Picked by sales rep.',
  Dispatched: 'Hand delivery dispatched by sales rep.',
  Delivered: 'Hand delivery completed by sales rep.'
};
const REP_INVOICE_PAYMENT_METHODS = [
  { code: 'account_terms', label: 'Account terms' },
  { code: 'bank_transfer', label: 'Bank transfer' },
  { code: 'e_transfer', label: 'e-Transfer' },
  { code: 'cheque', label: 'Cheque' },
  { code: 'cash', label: 'Cash' },
  { code: 'credit_card', label: 'Credit card' }
];
function repInvoicePaymentMethodLabel(code) {
  return (REP_INVOICE_PAYMENT_METHODS.find(method => method.code === code) || {}).label || code || 'Not set';
}
function repInvoiceIsStaleCancelled(order) {
  if (!order || !order.invoiceNumber) return false;
  const status = String(order.status || '').trim();
  if (!['Cancelled', 'Canceled', 'Void'].includes(status)) return false;
  const inv = typeof RP.invoiceForOrder === 'function' ? RP.invoiceForOrder(order.id) : null;
  return !inv;
}
function repInvoiceStatus(order) {
  if (repInvoiceIsStaleCancelled(order)) return 'Not issued';
  if (!order || !order.invoiceNumber) return 'Not issued';
  if (order.invoiceStatus === 'Paid' || order.financialStatus === 'Paid') return 'Paid';
  if (order.invoiceStatus) return order.invoiceStatus;
  return order.invoiceLastSentAt ? 'Sent' : 'Issued';
}
function repInvoicePaymentStatus(order) {
  if (repInvoiceIsStaleCancelled(order)) return 'Not invoiced';
  if (!order || !order.invoiceNumber) return 'Not invoiced';
  if (order.invoiceStatus === 'Paid' || order.financialStatus === 'Paid') return 'Paid';
  const inv = typeof RP.invoiceForOrder === 'function' ? RP.invoiceForOrder(order.id) : null;
  const totals = inv && typeof RP.invoiceTotals === 'function' ? RP.invoiceTotals(inv) : null;
  if (totals) {
    if (totals.balance <= 0.005 && totals.paid > 0) return 'Paid';
    if (totals.paid > 0) return 'Partial';
  }
  if (order.invoiceStatus === 'Partial' || order.financialStatus === 'Partial') return 'Partial';
  return 'Unpaid';
}
function repInvoiceBalanceDue(order, fallbackTotal) {
  if (repInvoiceIsStaleCancelled(order)) return Number(fallbackTotal || order?.total || 0);
  const inv = order && typeof RP.invoiceForOrder === 'function' ? RP.invoiceForOrder(order.id) : null;
  const totals = inv && typeof RP.invoiceTotals === 'function' ? RP.invoiceTotals(inv) : null;
  return Number(totals && totals.balance > 0.005 ? totals.balance : fallbackTotal || order?.total || 0);
}
function repInvoicePaymentAmountInput(order, fallbackTotal) {
  const balance = repInvoiceBalanceDue(order, fallbackTotal);
  return balance > 0 ? balance.toFixed(2) : '';
}
function repInvoiceEmail(order, customer) {
  const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
  return String(order?.invoiceEmailTo || overrides.invoiceEmail || overrides.invoice_email || customer?.email || customer?.buyerEmail || '').trim().toLowerCase();
}
function repInvoiceNote(order, customer) {
  const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
  return String(order?.invoiceNote || overrides.invoiceNote || overrides.invoice_note || '').trim();
}
function repInvoicePolicy(customer) {
  const accepted = customer && customer.acceptedPaymentMethods && typeof customer.acceptedPaymentMethods === 'object' ? customer.acceptedPaymentMethods : {};
  return {
    account_terms: accepted.account_terms !== false,
    bank_transfer: accepted.bank_transfer !== false,
    e_transfer: accepted.e_transfer !== false,
    cheque: true,
    cash: accepted.cash !== false,
    credit_card: accepted.credit_card === true
  };
}
function repInvoiceAllowedMethods(customer) {
  const policy = repInvoicePolicy(customer);
  return REP_INVOICE_PAYMENT_METHODS.filter(method => policy[method.code]);
}
function repInvoiceDefaultMethod(customer, order) {
  if (order && order.invoicePaymentMethod) return order.invoicePaymentMethod;
  const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object' ? customer.invoiceOverrides : {};
  const preferred = overrides.defaultPaymentMethod || overrides.default_payment_method || '';
  const allowed = repInvoiceAllowedMethods(customer);
  if (allowed.some(method => method.code === preferred)) return preferred;
  if (allowed.some(method => method.code === 'e_transfer')) return 'e_transfer';
  return (allowed[0] || {}).code || 'e_transfer';
}
function repOrderNextStatus(status) {
  const value = String(status || '').trim();
  if (['Needs Approval', 'On Hold', 'Cancelled', 'Invoiced', 'Paid', 'Closed'].includes(value)) return null;
  const normalized = value === 'Requested' ? 'Submitted' : value;
  const idx = REP_ORDER_FULFILLMENT_STAGES.indexOf(normalized);
  if (idx < 0 || idx >= REP_ORDER_FULFILLMENT_STAGES.length - 1) return null;
  return REP_ORDER_FULFILLMENT_STAGES[idx + 1];
}
MOBILE_SCREENS['rep-order-detail'] = function RepOrderDetail({ nav, id }) {
  const exactOrder = useRpExactOrder({ orderId: id });
  if (exactOrder.loading) return <div data-order-deep-link-loading="true"><MEmpty icon="refresh" title="Loading order" sub={`Fetching ${id} from your complete order history.`} /></div>;
  if (exactOrder.error) return <div data-order-deep-link-error="true"><MEmpty icon="alert" title="Order could not load" sub={exactOrder.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactOrder.retry}>Try Again</button>} /></div>;
  if (!exactOrder.order) return <div data-order-deep-link-missing="true"><MEmpty icon="orders" title="Order not found" sub="This order does not exist or is outside your current sales scope." action={<button className="btn btn-primary btn-sm" onClick={() => nav.pop()}><Icon name="arrowL" size={15} />Back</button>} /></div>;
  return <RepOrderDetailFound key={exactOrder.order.id} nav={nav} id={id} order={exactOrder.order} />;
};

function RepOrderDetailFound({ nav, id, order }) {
  const o = order;
  const c = RP.cust(o.cust) || { name: o.customer || o.cust || 'Customer' }, t = orderTotals(o);
  const timeline = rpOrderTimeline(o);
  const [status, setStatus] = useState(o.status);
  const backorder = rpOrderIsBackorder(o);
  const cur = Math.max(0, REP_ORDER_FULFILLMENT_STAGES.indexOf(rpOrderFulfillmentStage(status, REP_ORDER_FULFILLMENT_STAGES)));
  const reasonLabel = typeof rpOrderReasonDisplay === 'function' ? rpOrderReasonDisplay(o, 'Live order') : '';
  const editable = rpOrderEditableBySales({ ...o, status });
  const nextStatus = repOrderNextStatus(status);
  const advanceAction = nextStatus ? REP_ORDER_ADVANCE_ACTIONS[nextStatus] : null;
  const canUndoDelivery = ['Delivered', 'Invoiced'].includes(String(status || '').trim());
  const [advanceBusy, setAdvanceBusy] = useState(false);
  const [advanceError, setAdvanceError] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('Customer requested');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelled, setCancelled] = useState(false);
  const [, bumpInvoiceVersion] = useState(0);
  const [invoiceBusy, setInvoiceBusy] = useState('');
  const [invoiceNotice, setInvoiceNotice] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const invoiceIssue = useRpInvoiceIssueRequest(o.id);
  const missingRequiredPo = rpInvoiceMissingRequiredPo(o, c);
  const [cardSetupOpen, setCardSetupOpen] = useState(false);
  const [invoiceMethod, setInvoiceMethod] = useState(() => repInvoiceDefaultMethod(c, o));
  const [invoiceEmail, setInvoiceEmail] = useState(() => repInvoiceEmail(o, c));
  const [invoiceNote, setInvoiceNote] = useState(() => repInvoiceNote(o, c));
  const [invoiceSendEmail, setInvoiceSendEmail] = useState(() => Boolean(repInvoiceEmail(o, c)));
  const [invoicePaymentAmount, setInvoicePaymentAmount] = useState(() => repInvoicePaymentAmountInput(o, t.total));
  const [invoicePaymentDate, setInvoicePaymentDate] = useState(() => typeof rpTodayYmd === 'function' ? rpTodayYmd(c || o) : new Date().toISOString().slice(0, 10));
  const [invoicePaymentReference, setInvoicePaymentReference] = useState('');
  const [invoicePaymentNote, setInvoicePaymentNote] = useState('');
  const invoicePaymentRequestRef = useRef('');
  const invoiceOrderState = { ...o, status };
  const invoiceIssued = Boolean(o.invoiceNumber) && !repInvoiceIsStaleCancelled(invoiceOrderState);
  const invoiceStatus = repInvoiceStatus(invoiceOrderState);
  const invoicePaymentStatus = repInvoicePaymentStatus(invoiceOrderState);
  const invoiceBalanceDue = repInvoiceBalanceDue(invoiceOrderState, t.total);
  const invoicePaymentAmountText = String(invoicePaymentAmount || '').trim();
  const invoicePaymentAmountValue = Number(invoicePaymentAmountText.replace(/[$,\s]/g, ''));
  const invoicePaymentAmountExceedsBalance = Number.isFinite(invoicePaymentAmountValue) && invoicePaymentAmountValue > invoiceBalanceDue + 0.005;
  const invoicePaymentAmountValid = invoicePaymentAmountValue > 0 && !invoicePaymentAmountExceedsBalance;
  const invoicePaymentInFull = !invoicePaymentAmountText || (Number.isFinite(invoicePaymentAmountValue) && invoicePaymentAmountValue >= invoiceBalanceDue - 0.005);
  const savedStripeCard = typeof rpStripeDefaultCard === 'function' ? rpStripeDefaultCard(c) : null;
  const invoiceActionsAllowed = rpCanIssueCustomerInvoices(c) && !['Cancelled', 'Canceled', 'Void'].includes(String(status || '').trim());
  const invoiceAllowedMethods = repInvoiceAllowedMethods(c);
  const invoiceDocument = 'invoice';
  const refreshInvoiceOrder = async (response, message) => {
    const saved = rpUpsertOrder(rpOrderFromApi(response, { ...o, ...(response && response.order || {}) }));
    Object.assign(o, saved);
    if (o.status) setStatus(o.status);
    await rpRefreshAfterMutation(message);
    const refreshed = RP.ORDERS.find(x => x.id === id);
    if (refreshed) {
      Object.assign(o, refreshed);
      if (refreshed.status) setStatus(refreshed.status);
    }
    bumpInvoiceVersion(value => value + 1);
  };
  const issueInvoice = async (retryOriginal = false, cancelUnstarted = false) => {
    if (invoiceBusy || invoiceIssue.busy || invoiceIssue.loading || (!retryOriginal && !invoiceActionsAllowed)) return;
    if (!retryOriginal && missingRequiredPo) { setInvoiceError('Save the required PO number before issuing the invoice.'); return; }
    setInvoiceBusy('issue');
    setInvoiceNotice('');
    setInvoiceError('');
    try {
      const warnings = [];
      if (rpIsDemoMode()) {
        const inv = RP.generateInvoice(o.id, { send: invoiceSendEmail });
        Object.assign(o, { invoiceNumber: inv && inv.id, invoiceStatus: invoiceSendEmail ? 'Sent' : 'Issued', invoicePaymentMethod: invoiceMethod, invoiceEmailTo: invoiceEmail, invoiceNote: invoiceNote.trim() });
        rpUpsertOrder(o);
        bumpInvoiceVersion(value => value + 1);
      } else {
        const response = cancelUnstarted
          ? await invoiceIssue.cancelUnstarted()
          : retryOriginal ? await invoiceIssue.retry()
          : await invoiceIssue.run({
            paymentMethod: invoiceMethod,
            invoiceNote: invoiceNote.trim(),
            emailTo: invoiceSendEmail ? invoiceEmail.trim() : '',
            sendEmail: invoiceSendEmail
          });
        if (response.cancelled === true && response.definitivelyNotCommitted === true) {
          const message = 'The unstarted request is cancelled. No invoice was issued by that request. You can correct the details and choose Issue Invoice explicitly.';
          setInvoiceNotice(message);
          toast(message, 'ok');
          return;
        }
        warnings.push(...rpInvoiceIssueWarnings(response));
        if (cancelUnstarted) warnings.push('The invoice was already saved and could not be cancelled. Its original request was recovered.');
        try {
          await refreshInvoiceOrder(response, `Invoice ${response.invoice && response.invoice.invoiceNumber || o.id} saved`);
        } catch {
          warnings.push('Invoice saved, but the latest order could not be refreshed. Reload the order to confirm its current details.');
          bumpInvoiceVersion(value => value + 1);
        }
      }
      const successMessage = `Invoice ${o.invoiceNumber || o.id} ${retryOriginal ? 'recovered' : 'issued'}`;
      if (warnings.length) setInvoiceNotice(warnings.join(' '));
      toast(warnings.length ? `${successMessage}. ${warnings.join(' ')}` : successMessage, warnings.length ? 'warn' : 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not issue invoice.';
      setInvoiceError(msg);
      toast(msg, 'danger');
    } finally {
      setInvoiceBusy('');
    }
  };
  const emailInvoice = async () => {
    if (invoiceBusy || !invoiceIssued || !invoiceActionsAllowed || invoiceIssue.pending) return;
    setInvoiceBusy('email');
    setInvoiceNotice('');
    setInvoiceError('');
    try {
      if (rpIsDemoMode()) {
        o.invoiceLastSentAt = new Date().toISOString();
        o.invoiceEmailTo = invoiceEmail;
        rpUpsertOrder(o);
        bumpInvoiceVersion(value => value + 1);
      } else {
        const response = await rpApi('/api/invoices/email', {
          method: 'POST',
          body: JSON.stringify({ orderId: o.id, emailTo: invoiceEmail.trim() })
        });
        if (response.invoice) {
          o.invoiceLastSentAt = response.invoice.lastSentAt || o.invoiceLastSentAt;
          o.invoiceEmailTo = response.invoice.emailTo || invoiceEmail;
        }
        rpUpsertOrder(o);
        bumpInvoiceVersion(value => value + 1);
      }
      toast(`${invoicePaymentStatus === 'Paid' ? 'Receipt' : 'Invoice'} ${o.invoiceNumber || o.id} emailed`, 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not email invoice.';
      setInvoiceError(msg);
      toast(msg, 'danger');
    } finally {
      setInvoiceBusy('');
    }
  };
  const saveInvoiceSettings = async () => {
    if (invoiceBusy || !invoiceIssued || !invoiceActionsAllowed) return;
    setInvoiceBusy('settings');
    setInvoiceNotice('');
    setInvoiceError('');
    try {
      if (rpIsDemoMode()) {
        Object.assign(o, { invoicePaymentMethod: invoiceMethod, invoiceEmailTo: invoiceEmail, invoiceNote: invoiceNote.trim() });
        rpUpsertOrder(o);
        bumpInvoiceVersion(value => value + 1);
      } else {
        const response = await rpApi('/api/invoices/update', {
          method: 'POST',
          body: JSON.stringify({
            orderId: o.id,
            paymentMethod: invoiceMethod,
            emailTo: invoiceEmail.trim(),
            invoiceNote: invoiceNote.trim()
          })
        });
        await refreshInvoiceOrder(response, `Invoice ${o.invoiceNumber || o.id} updated`);
      }
      toast(`Invoice ${o.invoiceNumber || o.id} updated`, 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not update invoice.';
      setInvoiceError(msg);
      toast(msg, 'danger');
    } finally {
      setInvoiceBusy('');
    }
  };
  const markInvoicePaid = async () => {
    if (invoiceBusy || !invoiceIssued || !invoiceActionsAllowed || invoicePaymentStatus === 'Paid' || invoiceMethod === 'credit_card') return;
    if (!invoicePaymentAmountValid) {
      setInvoiceError(invoicePaymentAmountExceedsBalance ? `Payment amount cannot exceed the open balance of ${RP.money2(invoiceBalanceDue)}.` : 'Enter a payment amount greater than zero.');
      return;
    }
    setInvoiceBusy('payment');
    setInvoiceNotice('');
    setInvoiceError('');
    try {
      let deliveryWarning = '';
      if (rpIsDemoMode()) {
        Object.assign(o, { financialStatus: invoicePaymentInFull ? 'Paid' : 'Partial', invoiceStatus: invoicePaymentInFull ? 'Paid' : 'Partial', invoicePaymentMethod: invoiceMethod });
        rpUpsertOrder(o);
        bumpInvoiceVersion(value => value + 1);
      } else {
        const clientRequestId = invoicePaymentRequestRef.current || crypto.randomUUID();
        invoicePaymentRequestRef.current = clientRequestId;
        const response = await rpApi('/api/invoices/payment', {
          method: 'POST',
          body: JSON.stringify({
            clientRequestId,
            orderId: o.id,
            paymentMethod: invoiceMethod,
            paymentAmount: invoicePaymentAmount.trim(),
            paymentDate: invoicePaymentDate,
            paymentReference: invoicePaymentReference.trim(),
            paymentNote: invoicePaymentNote.trim(),
            emailTo: invoiceSendEmail ? invoiceEmail.trim() : '',
            sendEmail: invoiceSendEmail
          })
        });
        await refreshInvoiceOrder(response, `Payment recorded for ${o.invoiceNumber || o.id}`);
        invoicePaymentRequestRef.current = '';
        if (invoiceSendEmail && !(response.email && response.email.sent)) {
          deliveryWarning = response.email && response.email.warning
            ? response.email.warning
            : 'The customer email was not sent. Use Email to try again.';
        }
      }
      const successMessage = `${invoicePaymentInFull ? 'Receipt' : 'Payment'} recorded for ${o.invoiceNumber || o.id}`;
      if (deliveryWarning) setInvoiceError(`${successMessage}, but ${deliveryWarning}`);
      toast(deliveryWarning ? `${successMessage}, but ${deliveryWarning}` : successMessage, deliveryWarning ? 'warn' : 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not record payment.';
      setInvoiceError(msg);
      toast(msg, 'danger');
    } finally {
      setInvoiceBusy('');
    }
  };
  const chargeSavedCard = async () => {
    if (invoiceBusy || !invoiceActionsAllowed || invoicePaymentStatus === 'Paid' || !savedStripeCard) return;
    setInvoiceBusy('stripe');
    setInvoiceNotice('');
    setInvoiceError('');
    try {
      const response = await rpApi('/api/invoices/stripe-charge', {
        method: 'POST',
        body: JSON.stringify({
          orderId: o.id,
          paymentMethodId: savedStripeCard.id,
          emailTo: invoiceSendEmail ? invoiceEmail.trim() : '',
          sendReceipt: invoiceSendEmail,
          paymentNote: `Stripe saved card charge from sales app (${rpStripeCardLabel(savedStripeCard)}).`
        })
      });
      if (response.processing) {
        const message = response.message || 'Stripe is still processing this payment. No receipt has been issued yet.';
        setInvoiceNotice(message);
        toast('Card payment is still processing', 'info');
        return;
      }
      await refreshInvoiceOrder(response, `Stripe card charged for ${o.invoiceNumber || o.id}`);
      const amount = RP.money2(response.payment && response.payment.amount || o.total || 0);
      const receiptEmailed = Boolean(response.email && response.email.sent);
      if (invoiceSendEmail && !receiptEmailed) {
        const warning = `The card was charged, but the receipt email was not sent${response.email && response.email.warning ? `: ${response.email.warning}` : '. Use Email or PDF to send the receipt.'}`;
        setInvoiceError(warning);
        toast(`Card charged for ${amount}, but the receipt email was not sent`, 'warn');
      } else {
        toast(`Card charged for ${amount}. ${receiptEmailed ? 'Receipt emailed.' : 'Receipt ready.'}`, 'ok');
      }
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not charge saved card.';
      setInvoiceError(msg);
      toast(msg, 'danger');
    } finally {
      setInvoiceBusy('');
    }
  };
  const openInvoicePdf = async () => {
    try {
      await rpOpenInvoicePdf(o.id);
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not open invoice PDF.', 'danger');
    }
  };
  const advanceOrder = async () => {
    if (!nextStatus || advanceBusy) return;
    setAdvanceBusy(true);
    setAdvanceError('');
    try {
      if (!rpIsDemoMode()) {
        const response = await rpApi('/api/orders/status', {
          method: 'POST',
          body: JSON.stringify({ id: o.id, status: nextStatus, note: REP_ORDER_ADVANCE_NOTES[nextStatus] || '' })
        });
        const savedOrder = rpUpsertOrder(rpOrderFromApi(response, { ...o, status: nextStatus }));
        Object.assign(o, savedOrder);
        setStatus(savedOrder.status || nextStatus);
        await rpRefreshAfterMutation(`Order ${o.id} updated`);
        const refreshed = RP.ORDERS.find(x => x.id === id);
        if (refreshed) {
          Object.assign(o, refreshed);
          setStatus(refreshed.status);
        }
      } else {
        o.status = nextStatus;
        setStatus(nextStatus);
      }
      toast(`${o.id} moved to ${nextStatus}`, 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not update order status.';
      setAdvanceError(msg);
      toast(msg, 'danger');
    } finally {
      setAdvanceBusy(false);
    }
  };
  const undoDelivery = async () => {
    if (!canUndoDelivery || advanceBusy) return;
    const ok = window.confirm(`Move ${o.id} back to Dispatched and restore its fulfillment inventory?`);
    if (!ok) return;
    setAdvanceBusy(true);
    setAdvanceError('');
    try {
      if (!rpIsDemoMode()) {
        const response = await rpApi('/api/orders/status', {
          method: 'POST',
          body: JSON.stringify({ id: o.id, status: 'Dispatched', note: 'Delivery marked in error; reopened to dispatched.' })
        });
        const savedOrder = rpUpsertOrder(rpOrderFromApi(response, { ...o, status: 'Dispatched' }));
        Object.assign(o, savedOrder);
        setStatus(savedOrder.status || 'Dispatched');
        await rpRefreshAfterMutation(`Order ${o.id} corrected`);
        const refreshed = RP.ORDERS.find(x => x.id === id);
        if (refreshed) {
          Object.assign(o, refreshed);
          setStatus(refreshed.status || 'Dispatched');
        }
      } else {
        o.status = 'Dispatched';
        setStatus('Dispatched');
      }
      toast(`${o.id} moved back to Dispatched`, 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not undo delivery.';
      setAdvanceError(msg);
      toast(msg, 'danger');
    } finally {
      setAdvanceBusy(false);
    }
  };
  const cancelOrder = async () => {
    if (cancelBusy) return;
    setCancelBusy(true);
    setCancelError('');
    try {
      if (!rpIsDemoMode()) {
        const response = await rpApi('/api/orders/status', {
          method: 'POST',
          body: JSON.stringify({ id: o.id, status: 'Cancelled', note: cancelReason })
        });
        const savedOrder = rpUpsertOrder(rpOrderFromApi(response, { ...o, status: 'Cancelled' }));
        Object.assign(o, savedOrder);
        setStatus(savedOrder.status || 'Cancelled');
        await rpRefreshAfterMutation(`Order ${o.id} cancelled`);
      } else {
        o.status = 'Cancelled';
        setStatus('Cancelled');
      }
      setConfirmCancel(false);
      setCancelled(true);
      toast(`Order ${o.id} cancelled`, 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not cancel order.';
      setCancelError(msg);
      toast(msg, 'danger');
    } finally {
      setCancelBusy(false);
    }
  };
  if (cancelled) return <Success nav={nav} title="Order Cancelled" sub={`${o.id} - ${c.name}`} note="Operations will confirm any allocation release and customer follow-up." />;
  return (
    <div className="rp-fade">
      {cardSetupOpen && <StripeCardSetupModal customer={c} onClose={() => setCardSetupOpen(false)} onSaved={() => { setInvoiceMethod('credit_card'); bumpInvoiceVersion(value => value + 1); }} />}
      <MHeader title={o.id} sub={c.name} nav={nav} back right={<Pill status={status} />} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {backorder && <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}><Icon name="inventory" size={16} />Advance order. Incoming stock is reserved here and inventory is deducted only after fulfillment.</div>}
        {!editable && <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--surface-2)', borderRadius: 10, padding: '10px 13px', fontSize: 12, color: 'var(--muted)' }}><Icon name="clock" size={15} />This order is {String(status || 'open').toLowerCase()} and can no longer be edited.</div>}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px 14px', boxShadow: 'var(--shadow-sm)' }}>
          {REP_ORDER_FULFILLMENT_STAGES.map((s, i) => {
            const done = i <= cur, active = i === cur;
            return (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: i < REP_ORDER_FULFILLMENT_STAGES.length - 1 ? 4 : 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: done ? 'var(--ok)' : 'var(--surface)', border: '2px solid ' + (done ? 'var(--ok)' : 'var(--line)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{done && <Icon name="check" size={12} />}</span>
                  {i < REP_ORDER_FULFILLMENT_STAGES.length - 1 && <span style={{ width: 2, height: 18, background: i < cur ? 'var(--ok)' : 'var(--line)' }} />}
                </div>
                <span style={{ fontSize: 13.5, fontWeight: active ? 700 : 500, color: done ? 'var(--ink)' : 'var(--faint)' }}>{s}</span>
              </div>
            );
          })}
        </div>
        {advanceAction && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <span className="eyebrow">Fulfillment</span>
            <button className="btn btn-lg btn-primary" disabled={advanceBusy} style={{ width: '100%', marginTop: 10 }} onClick={advanceOrder}><Icon name={advanceAction.icon} size={17} />{advanceBusy ? 'Saving...' : advanceAction.label}</button>
            {advanceError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 12 }}>{advanceError}</div>}
          </div>
        )}
        {canUndoDelivery && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <span className="eyebrow">Delivery Correction</span>
            <button className="btn btn-lg" disabled={advanceBusy} style={{ width: '100%', marginTop: 10, color: 'var(--amber-deep)' }} onClick={undoDelivery}><Icon name="refresh" size={17} />{advanceBusy ? 'Saving...' : 'Undo Delivered'}</button>
            {advanceError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 12 }}>{advanceError}</div>}
          </div>
        )}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
          <span className="eyebrow">Timeline</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 10 }}>
            {timeline.map((a, i, arr) => (
              <div key={a.key} style={{ display: 'flex', gap: 11 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: a.tone === 'danger' ? 'var(--danger)' : a.tone === 'warn' ? 'var(--amber)' : i === arr.length - 1 ? 'var(--amber)' : 'var(--ok)', marginTop: 5 }} />
                  {i < arr.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--line-2)' }} />}
                </div>
                <div style={{ paddingBottom: 13 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{a.title}</div>
                  {a.detail && <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>{a.detail}</div>}
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 3 }}>{a.when}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span className="eyebrow">Invoice</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Pill status={invoiceStatus}>{invoiceStatus}</Pill>
              <Pill status={invoicePaymentStatus}>{invoicePaymentStatus}</Pill>
            </div>
          </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <Field2 k="Number" v={invoiceIssued ? o.invoiceNumber : 'Not issued'} />
                <Field2 k="Payment" v={invoiceIssued ? invoicePaymentStatus : 'Pending'} />
                <Field2 k="Method" v={invoiceIssued ? repInvoicePaymentMethodLabel(o.invoicePaymentMethod) : repInvoicePaymentMethodLabel(invoiceMethod)} />
                <Field2 k="Due" v={o.invoiceDueDate || '-'} />
                {savedStripeCard && <Field2 k="Saved card" v={rpStripeCardLabel(savedStripeCard)} />}
                {o.invoiceLastSentAt && <Field2 k="Last sent" v={rpFormatLocalDateTime(o.invoiceLastSentAt, { source: c || o, empty: '' })} />}
                {o.invoiceLastOpenedAt && <Field2 k="Last read" v={rpFormatLocalDateTime(o.invoiceLastOpenedAt, { source: c || o, empty: '' })} />}
                {Number(o.invoiceOpenCount || 0) > 0 && <Field2 k="Reads" v={Number(o.invoiceOpenCount || 0)} />}
          </div>
          {typeof EmailDeliveryStatus === 'function' && <EmailDeliveryStatus entityType="order" entityId={o.id} />}
          {invoiceIssue.pending && <div data-invoice-issue-recovery="sales" role="status" style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.45, marginTop: 12 }}>
            The original invoice request still needs recovery. Retry its original details, even if the invoice already exists. Changed fields cannot replace that request, and closing this screen keeps it available. Cancel unstarted request cannot cancel an invoice already saved.
            <div style={{ marginTop: 9, display: 'flex', gap: 8, flexWrap: 'wrap' }}><button type="button" className="btn btn-sm btn-primary" disabled={!!invoiceBusy || invoiceIssue.busy || invoiceIssue.loading || !!invoiceIssue.error} onClick={() => issueInvoice(true)}><Icon name="refresh" size={14} />{invoiceBusy === 'issue' ? 'Checking...' : 'Retry Original Request'}</button><button type="button" className="btn btn-sm" disabled={!!invoiceBusy || invoiceIssue.busy || invoiceIssue.loading || !!invoiceIssue.error} onClick={() => issueInvoice(true, true)}>Cancel unstarted request</button></div>
          </div>}
          {invoiceIssue.error && <div role="alert" style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 12 }}>{invoiceIssue.error}</div>}
          {invoiceActionsAllowed && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
              <InvoicePoRequirement order={o} customer={c} disabled={!!invoiceBusy || invoiceIssue.pending} onSaved={() => { bumpInvoiceVersion(value => value + 1); setInvoiceError(''); }} />
              <div data-sales-invoice-card="true" style={{ background: savedStripeCard ? 'var(--ok-bg)' : 'var(--surface-2)', border: `1px solid ${savedStripeCard ? 'oklch(0.6 0.11 150 / 0.3)' : 'var(--line)'}`, borderRadius: 10, padding: '11px 12px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 9 }}>
                  <div style={{ minWidth: 0 }}><div style={{ fontSize: 12.5, fontWeight: 800 }}>{savedStripeCard ? rpStripeCardLabel(savedStripeCard) : 'No card on file'}</div><div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 2 }}>{savedStripeCard ? 'Ready for an approved Stripe invoice charge.' : 'Add a card securely without leaving the invoice.'}</div></div>
                  <button type="button" className="btn btn-sm" disabled={!!invoiceBusy} onClick={() => setCardSetupOpen(true)}><Icon name="card" size={14} />{savedStripeCard ? 'Replace' : 'Add Card'}</button>
                </div>
              </div>
              <Lbl>{invoiceIssued && invoicePaymentStatus !== 'Paid' ? 'Payment method' : 'Invoice method'}</Lbl>
              <select className="field" disabled={invoiceBusy === 'issue' || invoiceIssue.pending} value={invoiceMethod} onChange={e => setInvoiceMethod(e.target.value)} style={{ marginBottom: 12 }}>
                {invoiceAllowedMethods.map(method => <option key={method.code} value={method.code}>{method.label}</option>)}
              </select>
              <Lbl>Email</Lbl>
              <input className="field" type="email" disabled={invoiceBusy === 'issue' || invoiceIssue.pending} value={invoiceEmail} onChange={e => { setInvoiceEmail(e.target.value); if (e.target.value.trim()) setInvoiceSendEmail(true); }} placeholder="customer@example.com" style={{ marginBottom: 10 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                <input type="checkbox" disabled={invoiceBusy === 'issue' || invoiceIssue.pending} checked={invoiceSendEmail} onChange={e => setInvoiceSendEmail(e.target.checked)} />
                Email invoice or payment receipt to customer
              </label>
              <Lbl>Customer invoice note</Lbl>
              <textarea className="field" rows="2" maxLength={600} disabled={invoiceBusy === 'issue' || invoiceIssue.pending} value={invoiceNote} onChange={e => setInvoiceNote(e.target.value)} placeholder="Meet at receiving, delivery instructions, thank you..." style={{ resize: 'none', marginBottom: 12 }} />
              {invoiceIssued && invoicePaymentStatus !== 'Paid' ? (
                <><Lbl>Amount paid</Lbl><input className="field" type="number" min="0.01" max={Number(invoiceBalanceDue || 0).toFixed(2)} step="0.01" inputMode="decimal" aria-invalid={invoicePaymentAmountExceedsBalance ? 'true' : 'false'} value={invoicePaymentAmount} onChange={e => setInvoicePaymentAmount(e.target.value)} placeholder={RP.money2(invoiceBalanceDue)} style={{ marginBottom: invoicePaymentAmountExceedsBalance ? 5 : 12, borderColor: invoicePaymentAmountExceedsBalance ? 'var(--danger)' : undefined }} />{invoicePaymentAmountExceedsBalance && <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 750, marginBottom: 12 }}>Payment amount cannot exceed the open balance of {RP.money2(invoiceBalanceDue)}.</div>}</>
              ) : null}
              {invoiceIssued && invoicePaymentStatus !== 'Paid' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)', gap: 9, marginBottom: 12 }}>
                  <div><Lbl>Paid date</Lbl><input className="field" type="date" value={invoicePaymentDate} onChange={e => setInvoicePaymentDate(e.target.value)} /></div>
                  <div><Lbl>{invoiceMethod === 'cheque' ? 'Cheque number' : 'Reference'}</Lbl><input className="field" value={invoicePaymentReference} onChange={e => setInvoicePaymentReference(e.target.value)} placeholder={invoiceMethod === 'cheque' ? 'Cheque #' : 'Interac ref, cash, etc.'} /></div>
                </div>
              ) : null}
              {invoiceIssued && invoicePaymentStatus !== 'Paid' ? (
                <><Lbl>Payment note</Lbl><textarea className="field" rows="2" value={invoicePaymentNote} onChange={e => setInvoicePaymentNote(e.target.value)} placeholder="Optional internal note" style={{ resize: 'none', marginBottom: 12 }} /></>
              ) : null}
              <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                {invoiceIssued && savedStripeCard && invoicePaymentStatus !== 'Paid' && <button className="btn btn-sm btn-primary" disabled={!!invoiceBusy} onClick={chargeSavedCard}><Icon name="card" size={14} />{invoiceBusy === 'stripe' ? 'Charging...' : 'Charge Card & Issue Receipt'}</button>}
                {!invoiceIssued && <button className="btn btn-sm btn-primary" disabled={!!invoiceBusy || !invoiceMethod || missingRequiredPo || invoiceIssue.pending || invoiceIssue.loading || !!invoiceIssue.error} onClick={() => issueInvoice()}><Icon name="receipt" size={14} />{invoiceBusy === 'issue' ? 'Issuing...' : `Issue ${invoiceDocument}`}</button>}
                {invoiceIssued && <button className="btn btn-sm" disabled={!!invoiceBusy || !invoiceMethod || invoiceIssue.pending} onClick={saveInvoiceSettings}><Icon name="check" size={14} />{invoiceBusy === 'settings' ? 'Saving...' : 'Save'}</button>}
                {invoiceIssued && <button className="btn btn-sm" onClick={openInvoicePdf}><Icon name="doc" size={14} />PDF</button>}
                {invoiceIssued && <button className="btn btn-sm" disabled={!!invoiceBusy || !invoiceEmail || invoiceIssue.pending || invoiceIssue.loading || !!invoiceIssue.error} onClick={emailInvoice}><Icon name="mail" size={14} />{invoiceBusy === 'email' ? 'Emailing...' : 'Email'}</button>}
                {invoiceIssued && invoicePaymentStatus !== 'Paid' && invoiceMethod !== 'credit_card' && <button className="btn btn-sm btn-primary" disabled={!!invoiceBusy || !invoiceMethod || !invoicePaymentAmountValid} onClick={markInvoicePaid}><Icon name="check" size={14} />{invoiceBusy === 'payment' ? 'Saving...' : invoiceMethod === 'cheque' ? 'Mark Paid by Cheque' : 'Record Payment'}</button>}
              </div>
            </div>
          )}
          {!invoiceActionsAllowed && invoiceIssued && <div style={{ marginTop: 14 }}><button className="btn btn-sm" onClick={openInvoicePdf}><Icon name="doc" size={14} />PDF</button></div>}
          {invoiceNotice && <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 12 }}>{invoiceNotice}</div>}
          {invoiceError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 12 }}>{invoiceError}</div>}
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
          <span className="eyebrow">Items</span>
          <div style={{ marginTop: 10 }}>
            {o.lines.map((l, i) => { const s = RP.sku(l.sku); return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', borderBottom: i < o.lines.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
                <FlavorChip sku={s} size={26} />
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{rpSkuDisplayName(s || l.sku)}</span>
                <span className="mono" style={{ fontWeight: 600 }}>{l.qty} cs</span>
              </div>
            ); })}
          </div>
          <PricingBreakdown order={o} totals={t} />
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field2 k="Source" v={RP.loc(o.source).name} /><Field2 k="Delivery" v={o.delivery} /><Field2 k="ETA" v={rpOrderDateLabel(o) === 'ASAP' ? 'ASAP' : `${rpOrderDateLabel(o)} AM`} /><Field2 k="Reason" v={reasonLabel || o.reason} />
        </div>
        {editable && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-lg" style={{ flex: 1, color: 'var(--danger)' }} onClick={() => setConfirmCancel(true)}><Icon name="x" size={16} />Cancel Order</button>
            <button className="btn btn-lg btn-primary" style={{ flex: 1 }} onClick={() => nav.push('create-order', { editId: o.id })}><Icon name="pen" size={16} />Edit Order</button>
          </div>
        )}
      </div>

      {confirmCancel && (
        <div style={{ position: 'absolute', inset: 0, background: 'oklch(0.2 0.03 240 / 0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 20 }} onClick={() => setConfirmCancel(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', background: 'var(--surface)', borderRadius: '20px 20px 0 0', padding: '22px 18px 26px', animation: 'rp-pop .2s ease' }}>
            <div style={{ width: 40, height: 4, borderRadius: 4, background: 'var(--line)', margin: '0 auto 16px' }} />
            <div className="disp" style={{ fontSize: 21, textTransform: 'uppercase', textAlign: 'center' }}>Cancel {o.id}?</div>
            <div style={{ fontSize: 13.5, color: 'var(--muted)', textAlign: 'center', marginTop: 6, lineHeight: 1.45 }}>This marks {t.cases} cases as cancelled for {c.name}. Operations will confirm any allocation release from {RP.loc(o.source).name}.</div>
            <div style={{ marginTop: 16 }}>
              <Lbl>Reason for cancellation</Lbl>
              <select className="field" value={cancelReason} onChange={e => setCancelReason(e.target.value)}><option>Customer requested</option><option>Duplicate order</option><option>Out of stock</option><option>Credit hold</option><option>Entered in error</option></select>
            </div>
            {cancelError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 12 }}>{cancelError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="btn btn-lg" disabled={cancelBusy} style={{ flex: 1 }} onClick={() => setConfirmCancel(false)}>Keep Order</button>
              <button className="btn btn-lg btn-primary" disabled={cancelBusy} style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={cancelOrder}>{cancelBusy ? 'Cancelling...' : 'Cancel Order'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- MORE ---------- */
MOBILE_SCREENS['more'] = function More({ nav, role }) {
  const visitCustomerId = rpFieldFirstCustomerId(role);
  const items = [
    { i: 'pin', l: 'Store Visit Log', go: () => visitCustomerId ? nav.push('visit-log', { id: visitCustomerId }) : nav.tab('rep-customers') },
    { i: 'alert', l: 'Report Damage / Loss', go: () => nav.push('damage-report') },
    { i: 'layers', l: 'Request Stock Transfer', go: () => nav.push('request-stock') },
    { i: 'package', l: 'Log Giveaway', go: () => nav.push('giveaway-log') },
    { i: 'clock', l: 'Stock Request History', go: () => nav.push('stock-requests') },
    { i: 'doc', l: 'Documents', go: () => { } },
    { i: 'admin', l: 'Settings', go: () => { } },
  ];
  return (
    <div className="rp-fade">
      <MHeader title="More" />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(it => (
          <button key={it.l} onClick={it.go} style={{ display: 'flex', alignItems: 'center', gap: 13, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '14px 15px', textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
            <span style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--surface-2)', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={it.i} size={18} /></span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{it.l}</span>
            <Icon name="chevR" size={17} style={{ color: 'var(--faint)' }} />
          </button>
        ))}
      </div>
    </div>
  );
};

Object.assign(window, { Field2 });


/* ===== mobile_order.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : Create Order wizard + Request Stock + Visit
   ============================================================ */

const DEMO_SELLABLE_AVAIL = { 'RP-12-GC': 120, 'RP-12-KL': 44, 'RP-12-BR': 36, 'RP-12-PM': 64, 'RP-12-PL': 12, 'RP-12-BO': 8, 'RP-MIX-TRI': 18 };
const MOBILE_EVENT_CAN_PRICE = 4.37;
const MOBILE_ORDER_INVOICE_PAYMENT_METHODS = [
  { code: 'account_terms', label: 'Account terms' },
  { code: 'bank_transfer', label: 'Bank transfer' },
  { code: 'e_transfer', label: 'e-Transfer' },
  { code: 'cheque', label: 'Cheque' },
  { code: 'cash', label: 'Cash' },
  { code: 'credit_card', label: 'Credit card' }
];
function mobileOrderAvailableCases(skuId, source, options = {}) {
  const liveQty = rpOrderAvailableCases(skuId, source, options);
  if (liveQty != null) return liveQty;
  return Object.prototype.hasOwnProperty.call(DEMO_SELLABLE_AVAIL, skuId) ? DEMO_SELLABLE_AVAIL[skuId] : null;
}

function mobileOrderSkuUnit(skuOrId) {
  const sku = typeof skuOrId === 'string' ? rpSkuItem(skuOrId) : skuOrId;
  return String(sku && sku.unit || sku && sku.pack || '').toLowerCase().includes('single') || String(sku && sku.id || skuOrId || '').toUpperCase().startsWith('RP-CAN-')
    ? 'can'
    : 'case';
}

function mobileOrderLineCaseQty(skuId, quantity) {
  const qty = Number(quantity || 0);
  return mobileOrderSkuUnit(skuId) === 'can' ? Math.round((qty / 12) * 10000) / 10000 : qty;
}

function mobileOrderUnitPrice(customer, skuOrId) {
  const sku = typeof skuOrId === 'string' ? rpSkuItem(skuOrId) : skuOrId;
  if (mobileOrderSkuUnit(sku) !== 'can') return rpCustomerSkuPrice(customer, sku);
  const direct = rpFiniteNumber(sku && sku.price, 0);
  return rpMoneyNumber(direct > 0 ? direct : MOBILE_EVENT_CAN_PRICE);
}

function mobileOrderQtyLabel(skuId, qty) {
  const value = Number(qty || 0);
  if (mobileOrderSkuUnit(skuId) === 'can') return `${value} can${value === 1 ? '' : 's'}`;
  return `${value} cs`;
}

function mobileOrderPriceSuffix(skuId) {
  return mobileOrderSkuUnit(skuId) === 'can' ? '/can' : '/case';
}

function mobileOrderSummaryQty(lines) {
  let caseCount = 0;
  let canCount = 0;
  (lines || []).forEach(([skuId, qty]) => {
    if (mobileOrderSkuUnit(skuId) === 'can') canCount += Number(qty || 0);
    else caseCount += Number(qty || 0);
  });
  return [caseCount ? `${caseCount} cs` : '', canCount ? `${canCount} can${canCount === 1 ? '' : 's'}` : ''].filter(Boolean).join(' + ');
}

function mobileEventSaleDefaultSource(event, role) {
  const batches = (RP.OPEN_CASE_BATCHES || []).filter(batch => batch && batch.status !== 'Closed' && (!event || eventBatchMatchesEvent(batch, event)) && eventOpenBatchRemaining(batch) > 0);
  if (batches[0] && batches[0].sourceLocationId) return batches[0].sourceLocationId;
  if (event && event.defaultSourceLocationId) return event.defaultSourceLocationId;
  const micro = rpFieldMicroWarehouse(role);
  return micro && micro.id || '';
}

function mobileOpenCanAvailable(skuId, source, options = {}) {
  const event = options.eventId ? (RP.EVENTS || []).find(row => row && row.id === options.eventId) : null;
  const canonical = rpCanonicalSkuId(skuId);
  const batches = (RP.OPEN_CASE_BATCHES || []).filter(batch => {
    if (!batch || batch.status === 'Closed') return false;
    if (canonical && batch.canSkuId !== canonical) return false;
    if (source && batch.sourceLocationId !== source) return false;
    if (event && !eventBatchMatchesEvent(batch, event)) return false;
    return true;
  });
  if (!batches.length) return 0;
  return Math.max(0, Math.round(batches.reduce((sum, batch) => sum + eventOpenBatchRemaining(batch), 0) * 100) / 100);
}

function mobileSingleCanOrderSkus(eventId = '', includeIds = []) {
  const event = eventId ? (RP.EVENTS || []).find(row => row && row.id === eventId) : null;
  const ids = new Set();
  (RP.OPEN_CASE_BATCHES || []).forEach(batch => {
    if (!batch || batch.status === 'Closed' || !batch.canSkuId) return;
    if (event && !eventBatchMatchesEvent(batch, event)) return;
    ids.add(batch.canSkuId);
  });
  ['RP-CAN-BO', 'RP-CAN-BR', 'RP-CAN-GC', 'RP-CAN-KL', 'RP-CAN-PL', 'RP-CAN-PM'].forEach(id => ids.add(id));
  includeIds.forEach(id => {
    if (mobileOrderSkuUnit(id) === 'can') ids.add(id);
  });
  return Array.from(ids)
    .map(id => rpSkuItem(id))
    .filter(Boolean)
    .map(sku => ({ ...sku, unit: 'can', pack: sku.pack || 'Single Can', price: rpFiniteNumber(sku.price, 0) > 0 ? sku.price : MOBILE_EVENT_CAN_PRICE }))
    .sort((a, b) => rpSkuDisplayName(a).localeCompare(rpSkuDisplayName(b)));
}

function mobileOrderAvailableQty(skuId, source, options = {}) {
  return mobileOrderSkuUnit(skuId) === 'can'
    ? mobileOpenCanAvailable(skuId, source, options)
    : mobileOrderAvailableCases(skuId, source, options);
}

function mobileCustomerLocations(c) {
  const rows = Array.isArray(c && c.locations) ? c.locations.filter(location => location && location.active !== false) : [];
  if (rows.length) return rows;
  if (!c) return [];
  return [{ id: `${c.id || 'customer'}-MAIN`, name: c.name || 'Location', address: c.address || '', city: c.city || '', postal: c.postal || '', active: true }];
}

function mobileAddressObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function mobileCleanText(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function mobileCustomerLocationAddress(location, customer) {
  const loc = mobileAddressObject(location);
  const locShip = mobileAddressObject(loc.shippingAddress || loc.shipping_address);
  const cust = mobileAddressObject(customer);
  const custShip = mobileAddressObject(cust.shippingAddress || cust.shipping_address);
  return mobileCleanText(loc.address || loc.address1 || locShip.address || locShip.address1 || cust.address || cust.address1 || custShip.address || custShip.address1);
}

function mobileCustomerLocationCity(location, customer) {
  const loc = mobileAddressObject(location);
  const locShip = mobileAddressObject(loc.shippingAddress || loc.shipping_address);
  const cust = mobileAddressObject(customer);
  const custShip = mobileAddressObject(cust.shippingAddress || cust.shipping_address);
  return mobileCleanText(loc.city || locShip.city || cust.city || custShip.city || cust.territory || '');
}

function mobileCustomerLocationPostal(location, customer) {
  const loc = mobileAddressObject(location);
  const locShip = mobileAddressObject(loc.shippingAddress || loc.shipping_address);
  const cust = mobileAddressObject(customer);
  const custShip = mobileAddressObject(cust.shippingAddress || cust.shipping_address);
  return mobileCleanText(loc.postal || loc.zip || locShip.postal || locShip.zip || cust.postal || cust.zip || custShip.postal || custShip.zip);
}

function mobileSourceLocationTypes() {
  return new Set(['Main Warehouse', 'Micro-Warehouse', 'Distributor']);
}

function mobileLocationSellableCases(locationId) {
  const skus = rpManualOrderSkus();
  let known = false;
  const total = skus.reduce((sum, sku) => {
    const value = rpOrderAvailableCases(sku.id, locationId);
    if (value == null) return sum;
    known = true;
    return sum + value;
  }, 0);
  return known ? Math.round(total) : null;
}

function mobileStockLocationOptions(role, current, options = {}) {
  const types = mobileSourceLocationTypes();
  const excludeIds = new Set((options.excludeIds || []).filter(Boolean));
  const includeMicro = options.includeMicro !== false;
  const personalSalesScope = typeof inventoryRepackSalesRoleAllowed === 'function' && inventoryRepackSalesRoleAllowed(role);
  const personalMicroIds = new Set(personalSalesScope && typeof inventoryPersonalFieldLocations === 'function'
    ? inventoryPersonalFieldLocations(role).map(location => location.id)
    : []);
  const territory = options.territory || rpFieldTerritory(role);
  const customer = options.customer || null;
  const canFulfill = location => customer
    ? rpLocationCanFulfillCustomer(location, customer, { deliveryMethod: options.deliveryMethod })
    : rpLocationCanFulfillTerritory(location, territory, { deliveryMethod: options.deliveryMethod });
  const assignedMicro = rpFieldMicroWarehouse(role);
  const all = (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : []).filter(location =>
    location
    && location.active !== false
    && types.has(location.type)
    && !excludeIds.has(location.id)
    && (includeMicro || location.type !== 'Micro-Warehouse')
    && (!personalSalesScope || location.type !== 'Micro-Warehouse' || personalMicroIds.has(location.id))
  );
  const ordered = [];
  const add = location => {
    if (!location || !location.id || excludeIds.has(location.id) || ordered.some(item => item.id === location.id)) return;
    if (!includeMicro && location.type === 'Micro-Warehouse') return;
    if (personalSalesScope && location.type === 'Micro-Warehouse' && !personalMicroIds.has(location.id)) return;
    ordered.push(location);
  };
  add(RP.loc(current));
  if (includeMicro) add(assignedMicro);
  all.filter(location => location.type !== 'Micro-Warehouse' && canFulfill(location)).forEach(add);
  all.filter(location => location.type !== 'Micro-Warehouse' && !canFulfill(location)).forEach(add);
  if (includeMicro) all.filter(location => location.type === 'Micro-Warehouse' && canFulfill(location)).forEach(add);
  if (includeMicro) all.filter(location => location.type === 'Micro-Warehouse' && !canFulfill(location)).forEach(add);

  return ordered.map(location => {
    const cases = mobileLocationSellableCases(location.id);
    const isAssignedMicro = assignedMicro && assignedMicro.id === location.id;
    return {
      v: location.id,
      l: isAssignedMicro ? 'My Micro-Warehouse' : (location.name || location.id),
      m: cases == null
        ? `${location.name || location.type || 'Inventory source'}${location.territory ? ` - ${location.territory}` : ''}`
        : `${location.type === 'Micro-Warehouse' ? 'On hand' : 'Sellable'}: ${cases.toLocaleString()} cases`
    };
  });
}

function mobileDefaultOrderSource(role, editOrder) {
  if (editOrder && editOrder.source && mobileStockLocationOptions(role, editOrder.source).some(option => option.v === editOrder.source)) return editOrder.source;
  const assignedMicro = rpFieldMicroWarehouse(role);
  if (assignedMicro && assignedMicro.id) return assignedMicro.id;
  const first = mobileStockLocationOptions(role, '').find(option => option && option.v);
  return first ? first.v : 'WH-SQ';
}

function mobileDefaultStockSource(role, destinationLocationId) {
  const first = mobileStockLocationOptions(role, '', { excludeIds: [destinationLocationId], includeMicro: false }).find(option => option && option.v);
  if (first) return first.v;
  const fallback = (Array.isArray(RP.LOCATIONS) ? RP.LOCATIONS : []).find(location => location && location.id !== destinationLocationId && mobileSourceLocationTypes().has(location.type));
  return fallback ? fallback.id : 'WH-SQ';
}

function mobileOrderInvoicePolicy(customer) {
  const accepted = customer && customer.acceptedPaymentMethods && typeof customer.acceptedPaymentMethods === 'object'
    ? customer.acceptedPaymentMethods
    : {};
  const overrides = customer && customer.invoiceOverrides && typeof customer.invoiceOverrides === 'object'
    ? customer.invoiceOverrides
    : {};
  return {
    acceptedPaymentMethods: {
      account_terms: accepted.account_terms !== false,
      bank_transfer: accepted.bank_transfer !== false,
      e_transfer: accepted.e_transfer !== false,
      cheque: true,
      cash: accepted.cash !== false,
      credit_card: accepted.credit_card === true
    },
    invoiceOverrides: overrides
  };
}

function mobileOrderInvoiceAllowedMethods(customer) {
  const policy = mobileOrderInvoicePolicy(customer);
  return MOBILE_ORDER_INVOICE_PAYMENT_METHODS.filter(method => policy.acceptedPaymentMethods[method.code]);
}

function mobileOrderInvoiceDefaultMethod(customer) {
  const policy = mobileOrderInvoicePolicy(customer);
  const preferred = policy.invoiceOverrides.defaultPaymentMethod || policy.invoiceOverrides.default_payment_method || '';
  const allowed = mobileOrderInvoiceAllowedMethods(customer);
  if (allowed.some(method => method.code === preferred)) return preferred;
  if (allowed.some(method => method.code === 'e_transfer')) return 'e_transfer';
  return (allowed[0] || {}).code || 'e_transfer';
}

function mobileOrderInvoiceEmail(customer) {
  const policy = mobileOrderInvoicePolicy(customer);
  return String(policy.invoiceOverrides.invoiceEmail || policy.invoiceOverrides.invoice_email || customer?.email || customer?.buyerEmail || '').trim().toLowerCase();
}

function mobileOrderInvoiceNote(customer) {
  const policy = mobileOrderInvoicePolicy(customer);
  return String(policy.invoiceOverrides.invoiceNote || policy.invoiceOverrides.invoice_note || '').trim();
}

function mobileLocalYmd(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function mobileBusinessToday(source) {
  return typeof rpTodayYmd === 'function' ? rpTodayYmd(source) : mobileLocalYmd();
}

function mobileDefaultDeliveryDate(source) {
  if (typeof rpTodayYmd === 'function') return rpTodayYmd(source, 7);
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return mobileLocalYmd(date);
}

function mobileDefaultFollowUpDate(source) {
  if (typeof rpTodayYmd === 'function') return rpTodayYmd(source, 3);
  const date = new Date();
  date.setDate(date.getDate() + 3);
  return mobileLocalYmd(date);
}

function mobileValidYmd(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const parsed = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return parsed.toISOString().slice(0, 10) === value;
}

function mobileScheduledDeliveryError(value, source) {
  if (!mobileValidYmd(value)) return 'Choose a valid requested delivery date.';
  if (value < mobileBusinessToday(source)) return 'Requested delivery cannot be before today.';
  return '';
}

MOBILE_SCREENS['create-order'] = function CreateOrder(props) {
  const { nav, editId, reorder } = props;
  const sourceOrderId = editId || reorder || '';
  const exactOrder = useRpExactOrder({ orderId: sourceOrderId, enabled: Boolean(sourceOrderId) });
  const customerId = (exactOrder.order && exactOrder.order.cust) || props.cust || '';
  const exactCustomer = useRpExactCustomer({ customerId, enabled: Boolean(customerId) });
  const action = editId ? 'edit' : 'reorder';
  const editInvoiceLocked = Boolean(editId && exactOrder.order && typeof rpOrderInvoiceLocked === 'function' && rpOrderInvoiceLocked(exactOrder.order));
  const editBlocked = Boolean(editId && exactOrder.order && typeof rpOrderEditableBySales === 'function' && !rpOrderEditableBySales(exactOrder.order));
  if (exactOrder.loading) return (
    <div className="rp-fade" data-exact-order-loading="true">
      <MHeader title={editId ? 'Edit Order' : 'Reorder'} sub={`Loading ${sourceOrderId}`} nav={nav} back />
      <MEmpty icon="refresh" title="Loading order" sub={`Fetching ${sourceOrderId} from your complete order history.`} />
    </div>
  );
  if (exactOrder.error) return (
    <div className="rp-fade" data-exact-order-error="true">
      <MHeader title={editId ? 'Edit Order' : 'Reorder'} sub="Order unavailable" nav={nav} back />
      <MEmpty icon="alert" title={`Could not ${action} this order`} sub={exactOrder.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactOrder.retry}>Try Again</button>} />
    </div>
  );
  if (sourceOrderId && !exactOrder.order) return (
    <div className="rp-fade" data-exact-order-missing="true">
      <MHeader title={editId ? 'Edit Order' : 'Reorder'} sub="Order not found" nav={nav} back />
      <MEmpty icon="orders" title="Order not found" sub="This order does not exist or is outside your sales scope." />
    </div>
  );
  if (editBlocked) return (
    <div className="rp-fade" data-order-edit-locked="true">
      <MHeader title="Edit Order" sub={sourceOrderId} nav={nav} back />
      <MEmpty icon="lock" title="Order cannot be edited" sub={editInvoiceLocked ? `Invoice ${exactOrder.order.invoiceNumber || exactOrder.order.invoice_number || 'history'} locks this order's items and pricing. Void or reconcile the invoice before editing.` : `This order is ${String(exactOrder.order.status || 'locked').toLowerCase()} and can no longer be edited.`} />
    </div>
  );
  if (exactCustomer.loading) return (
    <div className="rp-fade" data-exact-customer-loading="true">
      <MHeader title={editId ? 'Edit Order' : reorder ? 'Reorder' : 'New Order'} sub={`Loading ${customerId}`} nav={nav} back />
      <MEmpty icon="refresh" title="Loading customer" sub="Fetching this account from the complete customer directory." />
    </div>
  );
  if (exactCustomer.error) return (
    <div className="rp-fade" data-exact-customer-error="true">
      <MHeader title={editId ? 'Edit Order' : reorder ? 'Reorder' : 'New Order'} sub="Customer unavailable" nav={nav} back />
      <MEmpty icon="alert" title="Customer could not load" sub={exactCustomer.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactCustomer.retry}>Try Again</button>} />
    </div>
  );
  if (customerId && !exactCustomer.customer) return (
    <div className="rp-fade" data-exact-customer-missing="true">
      <MHeader title={editId ? 'Edit Order' : reorder ? 'Reorder' : 'New Order'} sub="Customer not found" nav={nav} back />
      <MEmpty icon="customers" title="Customer not found" sub="This customer does not exist or is outside your sales scope." />
    </div>
  );
  return <CreateOrderForm key={sourceOrderId || customerId || 'new-order'} {...props} exactSourceOrder={exactOrder.order} exactCustomer={exactCustomer.customer} />;
};

function CreateOrderForm({ nav, cust, reorder, editId, role, eventId, eventName, eventDate, singles, exactSourceOrder, exactCustomer }) {
  const editOrder = editId ? (exactSourceOrder || RP.ORDERS.find(x => x.id === editId)) : null;
  const reorderOrder = reorder ? (exactSourceOrder || RP.ORDERS.find(x => x.id === reorder)) : null;
  const selectedEvent = eventId ? (RP.EVENTS || []).find(event => event && event.id === eventId) : null;
  const eventQuickSale = Boolean(!editOrder && (selectedEvent || eventName || singles));
  const defaultSource = editOrder ? mobileDefaultOrderSource(role, editOrder) : (mobileEventSaleDefaultSource(selectedEvent, role) || mobileDefaultOrderSource(role, editOrder));
  const initialOrderCustomer = exactCustomer || (cust ? RP.cust(cust) : (editOrder ? RP.cust(editOrder.cust) : null));
  const initialDeliveryMethod = editOrder
    ? (editOrder.delivery || 'Ranch Delivery')
    : (typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(initialOrderCustomer) : 'Ranch Delivery');
  const initialDeliveryDate = mobileDefaultDeliveryDate(initialOrderCustomer || { territory: rpFieldTerritory(role) });
  const [step, setStep] = useState(editOrder ? 2 : 1);
  const [quickSaleMode, setQuickSaleMode] = useState(eventQuickSale);
  const quickSale = !editOrder && quickSaleMode;
  const [custId, setCustId] = useState(cust || (editOrder ? editOrder.cust : ''));
  const [customerLocationId, setCustomerLocationId] = useState(editOrder ? (editOrder.customerLocationId || '') : '');
  const [source, setSource] = useState(editOrder ? editOrder.source : defaultSource);
  const [backorderMode, setBackorderMode] = useState(() => Boolean(editOrder && typeof rpOrderIsBackorder === 'function' && rpOrderIsBackorder(editOrder)));
  const [method, setMethod] = useState(initialDeliveryMethod);
  const [methodTouched, setMethodTouched] = useState(Boolean(editOrder));
  const [deliveryTiming, setDeliveryTiming] = useState(editOrder && rpOrderIsAsapDelivery(editOrder) ? 'asap' : 'date');
  const [date, setDate] = useState(() => {
    const requested = editOrder ? rpOrderDateInputValue(editOrder, initialDeliveryDate) : initialDeliveryDate;
    return mobileScheduledDeliveryError(requested, initialOrderCustomer || { territory: rpFieldTerritory(role) }) ? initialDeliveryDate : requested;
  });
  const [qty, setQty] = useState(() => {
    const seed = editOrder || reorderOrder;
    if (seed) { const m = {}; seed.lines.forEach(l => { const item = rpSkuItem(l.sku); if (item && rpSkuIsManualOrderable(item)) m[item.id] = l.qty; }); return m; }
    return {};
  });
  const [discount, setDiscount] = useState('None');
  const [reason, setReason] = useState(editOrder ? editOrder.reason : selectedEvent ? 'Event' : 'Normal reorder');
  const [submitted, setSubmitted] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [submitOrderId] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `SO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 20).toUpperCase()}`
    : `SO-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`);
  const [updateRequestId] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `ORDER-UPDATE-${window.crypto.randomUUID()}`
    : `ORDER-UPDATE-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
  const [expectedUpdatedAt] = useState(() => {
    if (!editOrder) return '';
    const raw = String(editOrder.updated || editOrder.updatedAt || editOrder.updated_at || '').trim();
    const timestamp = Date.parse(raw);
    return Number.isFinite(timestamp) && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/.test(raw)
      ? raw
      : '';
  });
  const [search, setSearch] = useState('');
  const [customerSegment, setCustomerSegment] = useState(() => {
    const initialCustomer = cust ? RP.cust(cust) : null;
    return initialCustomer && rpIsConsumerCustomer(initialCustomer) ? 'consumer' : 'business';
  });
  const liveCustomerDirectory = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const customerPage = useRpServerPagedCustomers({
    limit: 50,
    search: step === 1 && !custId && !quickSale ? search : '',
    filters: { focus: 'active', segment: customerSegment, sort: 'name' }
  });
  const [po, setPo] = useState(editOrder ? (editOrder.po || '') : '');
  const [note, setNote] = useState(editOrder ? (editOrder.note || '') : selectedEvent ? `Event sale - ${selectedEvent.name}` : '');
  const [quickSaleLabel, setQuickSaleLabel] = useState(selectedEvent ? selectedEvent.name : (eventName || ''));
  const [quickSalePaymentMethod, setQuickSalePaymentMethod] = useState('cash');
  const [quickSalePaymentReference, setQuickSalePaymentReference] = useState('');
  const [issueConsumerInvoice, setIssueConsumerInvoice] = useState(false);
  const [consumerInvoiceMethod, setConsumerInvoiceMethod] = useState('e_transfer');
  const [consumerInvoiceEmail, setConsumerInvoiceEmail] = useState('');
  const [consumerInvoiceNote, setConsumerInvoiceNote] = useState('');
  const [consumerInvoiceSendEmail, setConsumerInvoiceSendEmail] = useState(false);
  const [consumerInvoiceAlreadyPaid, setConsumerInvoiceAlreadyPaid] = useState(false);
  const [consumerPaymentAmount, setConsumerPaymentAmount] = useState('');
  const [consumerPaymentDate, setConsumerPaymentDate] = useState(() => typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10));
  const [consumerPaymentReference, setConsumerPaymentReference] = useState('');
  const [consumerPaymentNote, setConsumerPaymentNote] = useState('');
  const [submittedInvoice, setSubmittedInvoice] = useState(null);
  const [submittedInvoiceError, setSubmittedInvoiceError] = useState('');
  const invoiceIssue = useRpInvoiceIssueRequest(submittedOrder?.id || editOrder?.id || submitOrderId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [addressCaptureOpen, setAddressCaptureOpen] = useState(false);
  const [addressDraft, setAddressDraft] = useState({ address: '', city: '', postal: '' });
  const [addressBusy, setAddressBusy] = useState(false);
  const [addressError, setAddressError] = useState('');
  const invoiceActorRole = typeof rpNormalizeRole === 'function' ? rpNormalizeRole(role) : String(role || '').trim().toLowerCase();
  const canIssueAlreadyPaid = !['rep', 'partner'].includes(invoiceActorRole);
  const effectiveConsumerAlreadyPaid = canIssueAlreadyPaid && consumerInvoiceAlreadyPaid;
  const selectedDirectoryCustomer = custId
    ? (exactCustomer && exactCustomer.id === custId ? exactCustomer : RP.cust(custId))
    : null;
  const directoryCustomerRows = liveCustomerDirectory ? customerPage.items : rpFieldCustomers(role);
  const customerOptions = Array.from(new Map(
    [selectedDirectoryCustomer, ...directoryCustomerRows]
      .filter(Boolean)
      .map(customer => [customer.id, customer])
  ).values()).filter(customer => rpCustomerOrderReady(customer) || customer.id === custId);
  const customerConsumerCount = customerOptions.filter(rpIsConsumerCustomer).length;
  const serverSegmentFocus = customerPage.summary && customerPage.summary.focusBySegment || {};
  const customerSegmentCounts = liveCustomerDirectory
    ? {
        business: serverSegmentFocus.business && serverSegmentFocus.business.active,
        consumer: serverSegmentFocus.consumer && serverSegmentFocus.consumer.active,
        all: serverSegmentFocus.all && serverSegmentFocus.all.active
      }
    : { business: customerOptions.length - customerConsumerCount, consumer: customerConsumerCount, all: customerOptions.length };
  const customerCountLabel = value => value == null || value === '' || !Number.isFinite(Number(value)) ? '…' : Number(value).toLocaleString();
  const customerQuery = search.trim().toLowerCase();
  const visibleCustomerOptions = liveCustomerDirectory
    ? customerOptions
    : customerOptions
      .filter(customer => repCustomerMatchesSegment(customer, customerSegment))
      .filter(customer => !customerQuery || [customer.name, customer.banner, customer.city, customer.type, customer.tier, customer.email, customer.phone, customer.address, customer.postal]
        .some(value => String(value || '').toLowerCase().includes(customerQuery)));
  const selectedCanSkuIds = Object.keys(qty).filter(id => mobileOrderSkuUnit(id) === 'can');
  const caseOrderSkus = rpManualOrderSkus({ includeIds: Object.keys(qty).filter(id => mobileOrderSkuUnit(id) !== 'can') });
  const canOrderSkus = quickSale ? mobileSingleCanOrderSkus(selectedEvent && selectedEvent.id, selectedCanSkuIds) : [];
  const orderSkus = [...caseOrderSkus, ...canOrderSkus.filter(sku => !caseOrderSkus.some(row => row.id === sku.id))];

  const sourceLocation = RP.loc(source) || { name: source || 'No source selected' };
  const quickSaleCustomer = { id: RP_QUICK_SALE_CUSTOMER_ID, name: 'Quick cash sale', city: sourceLocation.territory || rpFieldTerritory(role), type: 'Consumer', tier: 'Consumer', territory: sourceLocation.territory || rpFieldTerritory(role), taxRegion: 'BC' };
  const c = quickSale ? quickSaleCustomer : custId ? RP.cust(custId) : null;
  const customerOrderReady = quickSale || (c ? rpCustomerOrderReady(c) : false);
  const customerApprovalBlocked = Boolean(c && !customerOrderReady);
  const customerLocations = quickSale ? [] : mobileCustomerLocations(c);
  const selectedCustomerLocationId = customerLocationId || (customerLocations[0] || {}).id || '';
  const selectedCustomerLocation = customerLocations.find(location => location.id === selectedCustomerLocationId) || customerLocations[0] || null;
  const selectedCustomerAddress = mobileCustomerLocationAddress(selectedCustomerLocation, c);
  const selectedCustomerCity = mobileCustomerLocationCity(selectedCustomerLocation, c);
  const selectedCustomerPostal = mobileCustomerLocationPostal(selectedCustomerLocation, c);
  const deliveryNeedsCustomerAddress = Boolean(!quickSale && c && (typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(method) : method !== 'Customer Pickup'));
  const customerAddressIssueText = deliveryNeedsCustomerAddress && !selectedCustomerAddress
    ? (typeof rpCustomerDeliveryWarning === 'function' ? rpCustomerDeliveryWarning(c, method, selectedCustomerLocation) : `${c.name} needs a business or ship-to address before ${method} orders can be submitted. Add the customer address first.`)
    : '';
  const lines = Object.entries(qty).filter(([k, v]) => v > 0);
  const sourceOptions = mobileStockLocationOptions(role, source, { territory: c && c.territory, customer: c, deliveryMethod: method });
  const sourceTerritoryBlocked = Boolean(!quickSale && !backorderMode && c && !rpLocationCanFulfillCustomer(sourceLocation, c, { deliveryMethod: method }));
  const stockIssues = lines.map(([sku, q]) => {
    const available = mobileOrderAvailableQty(sku, source, { excludeOrderId: editOrder && editOrder.id, eventId: selectedEvent && selectedEvent.id });
    return available != null && q > available ? { sku, qty: q, available, unit: mobileOrderSkuUnit(sku) === 'can' ? 'cans' : 'cases' } : null;
  }).filter(Boolean);
  const stockIssueText = sourceTerritoryBlocked
    ? `${sourceLocation.name || source} is not assigned to ${c.territory}. Choose a source in this customer's territory.`
    : stockIssues.length
      ? `${rpSkuDisplayName(rpSkuItem(stockIssues[0].sku) || stockIssues[0].sku)} has only ${stockIssues[0].available} ${stockIssues[0].unit} available from ${sourceLocation.name || source}.`
      : '';
  const blockingStockIssueText = backorderMode ? '' : stockIssueText;
  const estimatedLines = lines.map(([sku, qty]) => {
    const item = rpSkuItem(sku);
    return {
      caseQty: mobileOrderLineCaseQty(sku, qty),
      deposit: item && item.deposit || 0,
      price: mobileOrderUnitPrice(c, item || sku),
      qty,
      recyclingFee: item && item.recyclingFee || 0,
      sku,
      unit: mobileOrderSkuUnit(sku)
    };
  });
  const estimateOrder = { customer: quickSale ? quickSaleCustomer : undefined, cust: quickSale ? '' : custId, lines: estimatedLines };
  const estimate = rpOrderTotals(estimateOrder);
  const sub = estimate.sub;
  const containerFee = estimate.containerFee;
  const tax = estimate.tax;
  const cases = estimate.cases;
  const requestedDelivery = deliveryTiming === 'asap' ? 'ASAP' : date;
  const businessToday = mobileBusinessToday(c || { territory: rpFieldTerritory(role) });
  const deliveryDateError = !quickSale && deliveryTiming === 'date'
    ? mobileScheduledDeliveryError(date, c || { territory: rpFieldTerritory(role) })
    : '';
  const consumerInvoiceAvailable = Boolean(c && !quickSale && !editOrder && rpCanIssueCustomerInvoices(c));
  const consumerInvoiceAllowedMethods = c ? mobileOrderInvoiceAllowedMethods(c) : [];
  const consumerPaymentAmountText = String(consumerPaymentAmount || '').trim();
  const consumerPaymentAmountValue = Number(consumerPaymentAmountText.replace(/[$,\s]/g, ''));
  const consumerPaymentAmountExceedsTotal = Boolean(effectiveConsumerAlreadyPaid && consumerPaymentAmountText && Number.isFinite(consumerPaymentAmountValue) && consumerPaymentAmountValue > estimate.total + 0.005);
  const consumerPaymentAmountInvalid = Boolean(effectiveConsumerAlreadyPaid && consumerPaymentAmountText && (!(consumerPaymentAmountValue > 0) || consumerPaymentAmountExceedsTotal));
  const consumerPaidInFull = effectiveConsumerAlreadyPaid && (!consumerPaymentAmountText || (Number.isFinite(consumerPaymentAmountValue) && consumerPaymentAmountValue >= estimate.total - 0.005));
  const consumerInvoiceDocument = consumerPaidInFull ? 'receipt' : 'invoice';

  const setQ = (k, v) => setQty(q => ({ ...q, [k]: v }));

  useEffect(() => {
    if (!canIssueAlreadyPaid && consumerInvoiceAlreadyPaid) setConsumerInvoiceAlreadyPaid(false);
  }, [canIssueAlreadyPaid, consumerInvoiceAlreadyPaid]);

  useEffect(() => {
    if (quickSale) {
      setCustomerLocationId('');
      return;
    }
    const locations = mobileCustomerLocations(custId ? RP.cust(custId) : null);
    if (!locations.some(location => location.id === customerLocationId)) {
      setCustomerLocationId((locations[0] || {}).id || '');
    }
    const customer = custId ? RP.cust(custId) : null;
    const currentSource = RP.loc(source);
    const currentSourceCases = currentSource ? rpLocationSellableCases(currentSource.id) : 0;
    if (!backorderMode && customer && currentSource && (!rpLocationCanFulfillCustomer(currentSource, customer, { deliveryMethod: method }) || currentSourceCases <= 0)) {
      setSource(rpDefaultOrderSourceForCustomer(customer, source, { deliveryMethod: method }));
    }
  }, [custId, quickSale, method, backorderMode]);

  useEffect(() => {
    if (quickSale || editOrder || methodTouched || !c) return;
    const next = typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(c) : 'Ranch Delivery';
    if (next && next !== method) setMethod(next);
  }, [custId, quickSale, methodTouched]);

  useEffect(() => {
    if (quickSale) {
      setIssueConsumerInvoice(false);
      return;
    }
    const customer = custId ? RP.cust(custId) : null;
    const canInvoice = Boolean(customer && !editOrder && rpIsConsumerCustomer(customer));
    setIssueConsumerInvoice(canInvoice);
    if (!canInvoice) return;
    const defaultMethod = mobileOrderInvoiceDefaultMethod(customer);
    const email = mobileOrderInvoiceEmail(customer);
    setConsumerInvoiceMethod(defaultMethod);
    setConsumerInvoiceEmail(email);
    setConsumerInvoiceNote(mobileOrderInvoiceNote(customer));
    setConsumerInvoiceSendEmail(Boolean(email));
  }, [custId, editId, quickSale]);

  useEffect(() => {
    setSearch('');
    setAddressCaptureOpen(false);
    setAddressError('');
  }, [step, custId]);

  if (submitted) {
    const doneOrder = submittedOrder || editOrder;
    return (
      <div className="rp-fade" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30, textAlign: 'center', gap: 18 }}>
        <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'rp-pop .4s ease' }}><Icon name="check" size={40} /></div>
        <div>
          <div className="disp" style={{ fontSize: 26, textTransform: 'uppercase' }}>{editOrder ? 'Order Updated' : quickSale ? 'Sale Recorded' : 'Order Submitted'}</div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 6 }}>{doneOrder ? doneOrder.id : 'SO-1048'} · {c.name}</div>
        </div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 12, padding: '14px 18px', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}><span style={{ color: 'var(--muted)' }}>Status</span><Pill status={doneOrder ? doneOrder.status : 'Allocated'} /></div>
          <PricingBreakdown order={doneOrder || estimateOrder} showCardOption={!quickSale} />
        </div>
        {submittedInvoice && <div style={{ background: submittedInvoice.paid ? 'var(--ok-bg)' : 'var(--info-bg)', color: submittedInvoice.paid ? 'var(--ok)' : 'var(--info)', borderRadius: 12, padding: '12px 16px', width: '100%', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}>
          {submittedInvoice.paid ? 'Receipt' : 'Invoice'} {submittedInvoice.invoiceNumber || submittedInvoice.id || ''} issued{submittedInvoice.sent ? ' and emailed' : ''}.
        </div>}
        {submittedInvoiceError && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 12, padding: '12px 16px', width: '100%', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}>{submittedInvoiceError}</div>}
        {doneOrder && typeof EmailDeliveryStatus === 'function' && <EmailDeliveryStatus entityType="order" entityId={doneOrder.id} />}
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.tab('rep-home')}>Done</button>
          <button className="btn btn-lg btn-primary" style={{ flex: 1 }} onClick={() => nav.push('rep-order-detail', { id: doneOrder ? doneOrder.id : 'SO-1042' })}>View Order</button>
        </div>
      </div>
    );
  }

  const titles = ['', 'Customer & Source', 'Add Products', 'Discounts & Notes', 'Review'];
  const canNext = step === 1 ? (quickSale || custId) && !customerApprovalBlocked && !customerAddressIssueText && !deliveryDateError : step === 2 ? lines.length > 0 && !blockingStockIssueText : true;
  const liveSession = !(window.RP_AUTH && window.RP_AUTH.demo);
  const approvalPreview = quickSale
    ? { required: false, status: 'Invoiced', reason: '' }
    : rpOrderApprovalPreview(c, discount, editOrder ? (editOrder.status || 'Pending') : (liveSession ? 'Submitted / Requested' : 'Allocated'));
  const reviewStatus = quickSale ? 'Paid' : approvalPreview.required ? approvalPreview.status : backorderMode ? 'Submitted / Backorder' : (editOrder ? (editOrder.status || 'Pending') : liveSession ? 'Submitted / Requested' : 'Allocated');

  const openAddressCapture = () => {
    setAddressDraft({
      address: selectedCustomerAddress,
      city: selectedCustomerCity || (c && c.territory ? `${c.territory}, BC` : ''),
      postal: selectedCustomerPostal
    });
    setAddressError('');
    setAddressCaptureOpen(true);
  };

  const saveAddressCapture = async () => {
    if (!c || addressBusy) return;
    const address = mobileCleanText(addressDraft.address);
    const city = mobileCleanText(addressDraft.city) || selectedCustomerCity || c.city || c.territory || '';
    const postal = mobileCleanText(addressDraft.postal).toUpperCase();
    if (!address) {
      setAddressError('Street address is required for delivery.');
      return;
    }
    setAddressBusy(true);
    setAddressError('');
    const baseLocation = selectedCustomerLocation || { id: `${c.id}-MAIN`, name: c.name || 'Location', type: 'Ship-to', active: true };
    const baseShipping = mobileAddressObject(baseLocation.shippingAddress || baseLocation.shipping_address);
    const savedLocation = {
      ...baseLocation,
      name: baseLocation.name || c.name || 'Location',
      type: baseLocation.type || 'Ship-to',
      address,
      city,
      postal,
      shippingAddress: {
        ...baseShipping,
        address,
        address1: address,
        city,
        postal
      },
      active: true
    };
    const existingLocations = customerLocations.length ? customerLocations : [savedLocation];
    let matched = false;
    const nextLocations = existingLocations.map(location => {
      if (location && location.id === savedLocation.id) {
        matched = true;
        return savedLocation;
      }
      return location;
    });
    if (!matched) nextLocations.unshift(savedLocation);

    try {
      let updated = null;
      if (liveSession) {
        const response = await rpApi('/api/customers/update', {
          method: 'POST',
          body: JSON.stringify({
            id: c.id,
            address,
            city,
            postal,
            locations: nextLocations
          })
        });
        updated = RP.upsertCustomer({ ...c, ...response.customer, skipCreds: true, persisted: true });
        await rpRefreshAfterMutation(`${updated.name} delivery address saved`);
      } else {
        updated = RP.upsertCustomer({
          ...c,
          address,
          city,
          postal,
          locations: nextLocations
        });
      }
      const refreshed = RP.cust(c.id) || updated;
      const refreshedLocations = mobileCustomerLocations(refreshed);
      const refreshedLocation = refreshedLocations.find(location => location.id === savedLocation.id) || refreshedLocations[0];
      if (refreshedLocation && refreshedLocation.id) setCustomerLocationId(refreshedLocation.id);
      toast(`${updated.name} delivery address saved`, 'ok');
      setAddressCaptureOpen(false);
    } catch (err) {
      setAddressError(err && err.message ? err.message : 'Could not save the delivery address.');
    } finally {
      setAddressBusy(false);
    }
  };

  const issueInvoiceAfterSave = async (order) => {
    if (!consumerInvoiceAvailable || !issueConsumerInvoice || !order || !order.id) return order;
    if (rpInvoiceMissingRequiredPo(order, c)) {
      const message = 'Order saved. Add its required PO number before issuing the invoice.';
      setSubmittedInvoiceError(message); toast(message, 'warn'); return order;
    }
    if (!liveSession) {
      const inv = RP.generateInvoice(order.id, { send: consumerInvoiceSendEmail });
      const demoOrder = { ...order, invoiceNumber: inv && inv.id, invoiceStatus: effectiveConsumerAlreadyPaid ? (consumerPaidInFull ? 'Paid' : 'Partial') : (consumerInvoiceSendEmail ? 'Sent' : 'Issued'), invoicePaymentMethod: consumerInvoiceMethod, invoiceEmailTo: consumerInvoiceEmail, invoiceNote: consumerInvoiceNote.trim() };
      const saved = rpUpsertOrder(demoOrder);
      setSubmittedInvoice({ invoiceNumber: demoOrder.invoiceNumber, paid: consumerPaidInFull, sent: consumerInvoiceSendEmail });
      return saved;
    }
    let response;
    try {
      response = await invoiceIssue.run({
        paymentMethod: consumerInvoiceMethod,
        invoiceNote: consumerInvoiceNote.trim(),
        emailTo: consumerInvoiceEmail.trim(),
        sendEmail: consumerInvoiceSendEmail && !effectiveConsumerAlreadyPaid
      }, { orderId: order.id });
    } catch (err) {
      const detail = err && err.message ? err.message : 'The invoice request was not confirmed.';
      const message = `Order saved. ${detail} Open the order to recover its original invoice request before issuing again.`;
      setSubmittedInvoiceError(message);
      toast(message, 'warn');
      return order;
    }
    const warnings = rpInvoiceIssueWarnings(response);
    let saved = rpUpsertOrder(rpOrderFromApi(response, { ...order, ...(response.order || {}) }));
    if (effectiveConsumerAlreadyPaid && !response.recoveryRequired && !response.replayed) {
      try {
        const paymentResponse = await rpApi('/api/invoices/payment', {
          method: 'POST',
          body: JSON.stringify({
            clientRequestId: crypto.randomUUID(),
            orderId: order.id,
            paymentMethod: consumerInvoiceMethod,
            paymentAmount: consumerPaymentAmount.trim(),
            paymentDate: consumerPaymentDate,
            paymentReference: consumerPaymentReference.trim(),
            paymentNote: consumerPaymentNote.trim(),
            emailTo: consumerInvoiceEmail.trim(),
            sendEmail: consumerInvoiceSendEmail
          })
        });
        response = { ...response, ...paymentResponse, invoice: paymentResponse.invoice || response.invoice, order: paymentResponse.order || response.order };
        saved = rpUpsertOrder(rpOrderFromApi(response, { ...saved, ...(response.order || {}) }));
        if (paymentResponse.email && paymentResponse.email.warning) warnings.push(paymentResponse.email.warning);
      } catch (err) {
        warnings.push(`Invoice issued, but the payment result was not confirmed. Check its payment history before recording another payment. ${err && err.message || ''}`.trim());
      }
    } else if (effectiveConsumerAlreadyPaid) {
      warnings.push('No additional payment was submitted while recovering the original invoice request. Check payment history and record any outstanding payment separately.');
    }
    try {
      await rpRefreshAfterMutation(`${response.paid ? 'Receipt' : 'Invoice'} ${response.invoice && response.invoice.invoiceNumber || order.id} issued`);
      saved = RP.ORDERS.find(x => x.id === order.id) || saved;
    } catch {
      warnings.push('Invoice saved, but the latest order could not be refreshed. Reload the order to confirm its current details.');
    }
    setSubmittedInvoice({
      invoiceNumber: response.invoice && response.invoice.invoiceNumber || saved.invoiceNumber,
      paid: response.paid || false,
      sent: response.email && response.email.sent
    });
    if (warnings.length) {
      const message = [...new Set(warnings)].join(' ');
      setSubmittedInvoiceError(message);
      toast(message, 'warn');
    }
    return saved;
  };
  const submitOrder = async () => {
    if (busy || !c || !lines.length) return;
    setError('');
    if (editOrder && typeof rpOrderEditableBySales === 'function' && !rpOrderEditableBySales(editOrder)) {
      setError(typeof rpOrderInvoiceLocked === 'function' && rpOrderInvoiceLocked(editOrder) ? 'This order has an issued invoice. Void or reconcile the invoice before editing.' : 'This order can no longer be edited.');
      return;
    }
    if (!quickSale && !rpCustomerOrderReady(c)) {
      setError('Customer must be approved before orders can be submitted.');
      return;
    }
    if (consumerInvoiceAvailable && issueConsumerInvoice && c.requiresPo && !po.trim()) {
      setError('Add a PO number, or turn off Issue customer invoice to save the order without issuing.');
      return;
    }
    if (blockingStockIssueText) {
      setError(blockingStockIssueText);
      return;
    }
    if (customerAddressIssueText) {
      setError(customerAddressIssueText);
      return;
    }
    if (deliveryDateError) {
      setError(deliveryDateError);
      return;
    }
    if (consumerPaymentAmountInvalid) {
      setError(consumerPaymentAmountExceedsTotal ? `Payment amount cannot exceed the invoice total of ${RP.money2(estimate.total)}.` : 'Enter a payment amount greater than zero.');
      return;
    }
    setBusy(true);

    const orderLines = lines.map(([sku, q]) => {
      const item = rpSkuItem(sku);
      return {
        caseQty: mobileOrderLineCaseQty(sku, q),
        deposit: item && item.deposit || 0,
        inventorySku: sku,
        price: mobileOrderUnitPrice(c, item || sku),
        qty: q,
        recyclingFee: item && item.recyclingFee || 0,
        sku,
        unit: mobileOrderSkuUnit(sku)
      };
    });
    const lineItems = lines.map(([sku, q]) => {
      const item = rpSkuItem(sku);
      const unit = mobileOrderSkuUnit(sku);
      return {
        caseQuantity: mobileOrderLineCaseQty(sku, q),
        inventorySkuId: sku,
        sku,
        title: item ? item.name : sku,
        quantity: q,
        unit,
        originalUnitPrice: mobileOrderUnitPrice(c, item || sku).toFixed(2),
        deposit: item && item.deposit || 0
      };
    });
    const localId = editOrder ? editOrder.id : submitOrderId;
    let id = localId;
    let external = null;
    let response = null;
    let status = quickSale ? 'Invoiced' : approvalPreview.required ? approvalPreview.status : backorderMode ? 'Submitted' : 'Allocated';
    const quickSaleDate = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: sourceLocation.territory || rpFieldTerritory(role) }) : new Date().toISOString().slice(0, 10);
    const finalRequestedDelivery = quickSale ? quickSaleDate : requestedDelivery;
    const orderNote = quickSale
      ? [quickSaleLabel.trim() || 'Quick cash sale', note.trim()].filter(Boolean).join('\n')
      : [backorderMode ? 'Advance/backorder order' : '', reason, discount !== 'None' ? `Discount / promo: ${discount}` : '', note.trim()].filter(Boolean).join('\n');
    const discountPercent = rpDiscountPercent(discount);

    try {
      if (editOrder) {
        if (liveSession) {
          if (!expectedUpdatedAt) {
            throw new Error('This order is missing its server revision. Refresh the order before saving changes.');
          }
          response = await rpApi('/api/orders/update', {
            method: 'POST',
            body: JSON.stringify({
              id: editOrder.id,
              expectedUpdatedAt,
              requestId: updateRequestId,
              customerId: custId,
              customerLocationId: selectedCustomerLocationId || undefined,
              deliveryDate: requestedDelivery,
              deliveryMethod: method,
              lineItems,
              discountLabel: discount,
              discountPercent,
              allowBackorder: backorderMode,
              note: orderNote,
              po,
              sourceLocationId: source
            })
          });
          if (response && response.code === 'shopify_mutation_in_progress') {
            throw new Error(response.error || 'This exact update is still being confirmed. Wait a moment, then retry without changing the order.');
          }
          external = response;
          status = (response.order && response.order.status) || editOrder.status || status;
          const order = { ...editOrder, cust: custId, customerLocationId: selectedCustomerLocationId || null, territory: c.territory, source, delivery: method, status, date: requestedDelivery, deliveryPreference: deliveryTiming === 'asap' ? 'ASAP' : 'Scheduled', reason: orderNote, po, note, external, lines: orderLines };
          const savedOrder = rpUpsertOrder(rpOrderFromApi(response, order));
          await rpRefreshAfterMutation(response.shopifyDraftWarning ? `Order ${savedOrder.id} updated - Shopify draft sync warning` : `Order ${savedOrder.id} updated`);
          const syncedOrder = RP.ORDERS.find(x => x.id === savedOrder.id) || savedOrder;
          setSubmittedOrder(syncedOrder);
          toast(response.shopifyDraftWarning || `Order ${syncedOrder.id} updated`, response.shopifyDraftWarning ? 'warn' : 'ok');
          setSubmitted(true);
          return;
        }
        Object.assign(editOrder, { source, delivery: method, status, date: requestedDelivery, deliveryPreference: deliveryTiming === 'asap' ? 'ASAP' : 'Scheduled', reason, po, note, lines: orderLines });
        setSubmittedOrder(editOrder);
        setSubmitted(true);
        return;
      }

      if (liveSession) {
        response = await rpApi('/api/orders/submit', {
          method: 'POST',
          body: JSON.stringify({
            id: submitOrderId,
            quickSale,
            orderType: quickSale ? 'quick_sale' : undefined,
            customerId: quickSale ? undefined : custId,
            customerLocationId: quickSale ? undefined : selectedCustomerLocationId || undefined,
            deliveryDate: finalRequestedDelivery,
            deliveryMethod: quickSale ? 'Customer Pickup' : method,
            eventId: quickSale && selectedEvent ? selectedEvent.id : undefined,
            eventName: quickSale ? quickSaleLabel.trim() || undefined : undefined,
            lineItems,
            discountLabel: quickSale ? 'None' : discount,
            discountPercent: quickSale ? 0 : discountPercent,
            allowBackorder: !quickSale && backorderMode,
            note: orderNote,
            paymentMethod: quickSale ? quickSalePaymentMethod : undefined,
            paymentReference: quickSale ? quickSalePaymentReference.trim() || undefined : undefined,
            po: quickSale ? '' : po,
            sourceLocationId: source
          })
        });
        id = response.orderId || id;
        external = response.draftOrder || response;
        status = (response.order && response.order.status) || (response.mode === 'shopify-draft-order' ? 'Submitted' : 'Requested');
      }

      const order = { id, cust: quickSale ? RP_QUICK_SALE_CUSTOMER_ID : custId, customerLocationId: quickSale ? null : selectedCustomerLocationId || null, quickSale, backorder: !quickSale && backorderMode, territory: c.territory, rep: quickSale ? rpFieldUserName(role) : c.rep, source, delivery: quickSale ? 'Customer Pickup' : method, status, date: finalRequestedDelivery, deliveryPreference: quickSale ? 'Scheduled' : deliveryTiming === 'asap' ? 'ASAP' : 'Scheduled', created: typeof rpNowLocalInput === 'function' ? rpNowLocalInput(c) : new Date().toISOString().slice(0, 16).replace('T', ' '), reason: quickSale ? (quickSaleLabel.trim() || 'Quick cash sale') : reason, po: quickSale ? '' : po, note: orderNote, external, financialStatus: quickSale ? 'Paid' : undefined, invoicePaymentMethod: quickSale ? quickSalePaymentMethod : undefined, invoiceStatus: quickSale ? 'Paid' : undefined, lines: orderLines };
      const savedOrder = rpUpsertOrder(rpOrderFromApi(response, order));
      await rpRefreshAfterMutation(`Order ${savedOrder.id} submitted`);
      const syncedOrder = RP.ORDERS.find(x => x.id === savedOrder.id) || savedOrder;
      const finalOrder = await issueInvoiceAfterSave(syncedOrder);
      const completedOrder = rpUpsertOrder(finalOrder || syncedOrder);
      toast(quickSale ? `Quick sale ${syncedOrder.id} recorded` : `Order ${syncedOrder.id} submitted for ${c.name}`, 'ok');
      nav.reset('rep-order-detail', { id: completedOrder.id });
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not submit order.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title={titles[step]} sub={`${editOrder ? 'Edit ' + editOrder.id : 'New Order'} · Step ${step} of 4`} nav={nav} back={step === 1} />
      {/* progress */}
      <div style={{ display: 'flex', gap: 5, padding: '12px 16px 4px' }}>
        {[1, 2, 3, 4].map(n => <div key={n} style={{ flex: 1, height: 4, borderRadius: 4, background: n <= step ? 'var(--amber)' : 'var(--line)', transition: 'background .25s' }} />)}
      </div>

      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px 16px' }}>
        {step === 1 && <>
          {!editOrder && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 16 }}>
            <button type="button" onClick={() => setQuickSaleMode(false)} style={{ border: '1.5px solid ' + (!quickSale ? 'var(--amber)' : 'var(--line)'), background: !quickSale ? 'var(--amber-soft)' : 'var(--surface)', color: !quickSale ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 11, padding: '12px 10px', fontSize: 13, fontWeight: 800 }}>Customer</button>
            <button type="button" onClick={() => setQuickSaleMode(true)} style={{ border: '1.5px solid ' + (quickSale ? 'var(--amber)' : 'var(--line)'), background: quickSale ? 'var(--amber-soft)' : 'var(--surface)', color: quickSale ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 11, padding: '12px 10px', fontSize: 13, fontWeight: 800 }}>Quick cash</button>
          </div>}
          {!quickSale && <>
          <Lbl>Customer</Lbl>
          {c ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', border: '1px solid var(--navy)', borderRadius: 12, padding: 13, marginBottom: 18 }}>
              <Avatar name={c.name} size={38} /><div style={{ flex: 1 }}><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 12, color: 'var(--faint)' }}>{c.city} · {c.tier}</div><div style={{ marginTop: 5, fontSize: 13, fontWeight: 700 }}>{rpCustomerPricingLabel(c)}</div></div>
              <button onClick={() => setCustId('')} style={{ border: 'none', background: 'none', color: 'var(--muted)' }}><Icon name="x" size={18} /></button>
            </div>
          ) : (
            <div style={{ marginBottom: 18 }}>
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--faint)' }} />
                <input data-order-customer-server-search="mobile" className="field" style={{ paddingLeft: 36 }} placeholder="Search customer…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div role="group" aria-label="Order customer account type" style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 10 }}>
                {REP_CUSTOMER_SEGMENTS.map(option => {
                  const selected = customerSegment === option.value;
                  return (
                    <button type="button" key={option.value} data-order-customer-segment={option.value} aria-pressed={selected} onClick={() => setCustomerSegment(option.value)} style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 100, border: selected ? '1px solid var(--navy)' : '1px solid var(--line)', background: selected ? 'var(--navy)' : 'var(--surface)', color: selected ? '#fff' : 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {option.label} <span style={{ opacity: selected ? 0.75 : 0.65 }}>{customerCountLabel(customerSegmentCounts[option.value])}</span>
                    </button>
                  );
                })}
              </div>
              {visibleCustomerOptions.map(x => {
                const consumerAccount = rpIsConsumerCustomer(x);
                return (
                <button key={x.id} data-order-customer-kind={consumerAccount ? 'consumer' : 'business'} onClick={() => { setCustId(x.id); setSearch(''); }} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, marginBottom: 7 }}>
                  <Avatar name={x.name} size={32} color={x.status === 'Prospect' ? 'var(--faint)' : 'var(--navy)'} />
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 13.5 }}>{x.name}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{x.type}</div></div>
                  <span className="pill" style={{ fontSize: 10.5, color: consumerAccount ? 'var(--amber-deep)' : 'var(--info)', background: consumerAccount ? 'var(--amber-soft)' : 'var(--info-bg)' }}>{consumerAccount ? 'Consumer' : 'Business'}</span>
                  {x.hold && <Pill status="Hold">Credit hold</Pill>}
                </button>
                );
              })}
              {liveCustomerDirectory && customerPage.initialLoading && <div data-order-customer-loading="mobile" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 12px', marginBottom: 7, fontSize: 12.5, color: 'var(--muted)', fontWeight: 700 }}>Searching the complete scoped customer directory...</div>}
              {liveCustomerDirectory && !customerPage.initialLoading && !customerPage.loaded && customerPage.error && <div data-order-customer-error="mobile" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 10, padding: '12px', marginBottom: 7, fontSize: 12.5, color: 'var(--danger)', fontWeight: 700 }}>{customerPage.error}<button type="button" className="btn btn-sm" onClick={customerPage.retry} style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}>Try Again</button></div>}
              {(!liveCustomerDirectory || customerPage.loaded) && visibleCustomerOptions.length === 0 && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 12px', marginBottom: 7, fontSize: 12.5, color: 'var(--faint)' }}>{customerQuery ? `No ${customerSegment === 'all' ? 'customers' : customerSegment === 'consumer' ? 'consumers' : 'businesses'} match "${search.trim()}".` : `No approved ${customerSegment === 'all' ? 'customers' : customerSegment === 'consumer' ? 'consumers' : 'businesses'} are assigned to this profile.`}</div>}
              {liveCustomerDirectory && customerPage.loaded && <div data-order-customer-pagination="mobile" style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', marginTop: 9, marginBottom: 9 }}><RpServerLoadMore {...customerPage} label="customers" onLoadMore={customerPage.loadMore} onRetry={customerPage.loadMore} /></div>}
              <button onClick={() => nav.push('add-customer')} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, padding: '11px 12px', background: 'var(--surface-2)', border: '1px dashed var(--line)', borderRadius: 10, marginTop: 3 }}>
                <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--amber-soft)', color: 'var(--amber-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={18} /></span>
                <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--amber-deep)' }}>New customer</span>
              </button>
            </div>
          )}
          </>}
          {quickSale && <>
            {selectedEvent && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--info-bg)', color: 'var(--info)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}><Icon name="calendar" size={16} />Event sale linked to {selectedEvent.name}</div>}
            <Lbl>Quick Sale Label</Lbl>
            <input className="field" value={quickSaleLabel} onChange={e => setQuickSaleLabel(e.target.value)} placeholder="Event name or short note" style={{ marginBottom: 14 }} />
            <Lbl>Payment Method</Lbl>
            <select className="field" value={quickSalePaymentMethod} onChange={e => setQuickSalePaymentMethod(e.target.value)} style={{ marginBottom: quickSalePaymentMethod === 'cash' ? 18 : 10 }}>
              <option value="cash">Cash</option>
              <option value="e_transfer">e-Transfer</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="cheque">Cheque</option>
            </select>
            <div style={{ color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.45, marginTop: -8, marginBottom: 12 }}>Credit cards must be processed from a customer invoice using Charge Card. {RP_CREDIT_CARD_TERMS_NOTE}</div>
            {quickSalePaymentMethod !== 'cash' && <input className="field" value={quickSalePaymentReference} onChange={e => setQuickSalePaymentReference(e.target.value)} placeholder="Optional payment reference" style={{ marginBottom: 18 }} />}
          </>}
          {!quickSale && c && rpCustomerOnCreditHold(c) && rpActiveApprovalRule('CREDIT_HOLD_CUSTOMER') && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 600, marginBottom: 16 }}><Icon name="alert" size={16} />Customer on credit hold — fulfillment needs override.</div>}

          {!quickSale && customerApprovalBlocked && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, marginBottom: 16 }}><Icon name="flag" size={16} />{c.name} must be approved before orders or portal access are enabled.</div>}

          {!quickSale && c && customerLocations.length > 0 && <>
            <Lbl>Ship-to Location</Lbl>
            <select className="field" value={selectedCustomerLocationId} onChange={e => setCustomerLocationId(e.target.value)} style={{ marginBottom: 18 }}>
              {customerLocations.map(location => <option key={location.id} value={location.id}>{location.name || 'Location'}{location.city ? ` - ${location.city}` : ''}</option>)}
            </select>
          </>}
          {!quickSale && c && customerAddressIssueText && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: '12px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
              <Icon name="alert" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ flex: 1 }}>{customerAddressIssueText}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <button type="button" className="btn btn-sm" onClick={openAddressCapture}>Add delivery address</button>
              <button type="button" className="btn btn-sm" onClick={() => nav.push('edit-customer', { id: c.id })}>Full profile</button>
              <button type="button" className="btn btn-sm" onClick={() => { setMethod('Customer Pickup'); setMethodTouched(true); setAddressCaptureOpen(false); setError(''); }}>Use pickup</button>
            </div>
            {addressCaptureOpen && <div style={{ marginTop: 12, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: 12, color: 'var(--ink)' }}>
              <Lbl>Street Address</Lbl>
              <input className="field" value={addressDraft.address} onChange={e => setAddressDraft(current => ({ ...current, address: e.target.value }))} placeholder="Street address" style={{ marginBottom: 10 }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.8fr)', gap: 9, marginBottom: 10 }}>
                <div>
                  <Lbl>City</Lbl>
                  <input className="field" value={addressDraft.city} onChange={e => setAddressDraft(current => ({ ...current, city: e.target.value }))} placeholder={c.territory || 'City'} />
                </div>
                <div>
                  <Lbl>Postal</Lbl>
                  <input className="field" value={addressDraft.postal} onChange={e => setAddressDraft(current => ({ ...current, postal: e.target.value.toUpperCase() }))} placeholder="V8B 0A1" />
                </div>
              </div>
              {addressError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '9px 10px', fontSize: 12, fontWeight: 700, marginBottom: 10 }}>{addressError}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn" disabled={addressBusy} style={{ flex: 1 }} onClick={() => { setAddressCaptureOpen(false); setAddressError(''); }}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={addressBusy} style={{ flex: 1.3, opacity: addressBusy ? 0.65 : 1 }} onClick={saveAddressCapture}><Icon name="check" size={15} />{addressBusy ? 'Saving...' : 'Save address'}</button>
              </div>
            </div>}
          </div>}
          {!quickSale && c && !customerAddressIssueText && deliveryNeedsCustomerAddress && selectedCustomerAddress && <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: -10, marginBottom: 16 }}>Ship-to: {selectedCustomerAddress}</div>}

          {!quickSale && <><Lbl>Requested Delivery</Lbl>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 8 }}>
            {[{ value: 'asap', label: 'ASAP' }, { value: 'date', label: 'Pick date' }].map(option => (
              <button key={option.value} type="button" onClick={() => setDeliveryTiming(option.value)} style={{ border: '1.5px solid ' + (deliveryTiming === option.value ? 'var(--amber)' : 'var(--line)'), background: deliveryTiming === option.value ? 'var(--amber-soft)' : 'var(--surface)', color: deliveryTiming === option.value ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 11, padding: '11px 12px', fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                {option.label}
              </button>
            ))}
          </div>
          {deliveryTiming === 'date' && <>
            <input className="field" type="date" min={businessToday} value={date} onChange={e => setDate(e.target.value)} style={{ marginBottom: deliveryDateError ? 6 : 18, borderColor: deliveryDateError ? 'var(--danger)' : undefined }} />
            {deliveryDateError && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginBottom: 18 }}>{deliveryDateError}</div>}
          </>}
          {deliveryTiming === 'asap' && <div style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.4, marginTop: 2, marginBottom: 18 }}>Timing confirmed by ops or the assigned rep.</div>}</>}

          <Lbl>Fulfillment Source</Lbl>
          <RadioCards value={source} onChange={setSource} options={sourceOptions} />
          <div style={{ height: 14 }} />
          {!quickSale && <><Lbl>Delivery Method</Lbl>
          <RadioCards value={method} onChange={value => { setMethod(value); setMethodTouched(true); }} options={[
            { v: 'Ranch Delivery', l: 'Ranch Delivery' }, { v: 'Customer Pickup', l: 'Customer Pickup' }, { v: 'Carrier / Freight', l: 'Carrier / Freight' },
          ]} /></>}
        </>}

        {step === 2 && <>
          {!quickSale && c && <div style={{ padding: '10px 0', marginBottom: 10, fontSize: 14, fontWeight: 700 }}>{rpCustomerPricingLabel(c)}</div>}
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12 }}>Fulfilled from <b style={{ color: 'var(--ink)' }}>{sourceLocation.name}</b> - {quickSale && selectedEvent ? 'loose cans come from open event stock' : 'FEFO lots auto-selected'}</div>
          {!quickSale && <button type="button" onClick={() => setBackorderMode(!backorderMode)} style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 11, border: '1.5px solid ' + (backorderMode ? 'var(--amber)' : 'var(--line)'), background: backorderMode ? 'var(--amber-soft)' : 'var(--surface)', color: backorderMode ? 'var(--amber-deep)' : 'var(--ink)', borderRadius: 11, padding: '12px 13px', textAlign: 'left', marginBottom: 14 }}>
            <span style={{ width: 22, height: 22, borderRadius: 7, border: '2px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{backorderMode && <Icon name="check" size={13} />}</span>
            <span><span style={{ display: 'block', fontSize: 13.5, fontWeight: 800 }}>Advance order</span><span style={{ display: 'block', fontSize: 11.5, lineHeight: 1.4, marginTop: 2, color: 'var(--muted)' }}>Allows quantities above current stock. Cases are committed at {sourceLocation.name} and deducted only when fulfilled.</span></span>
          </button>}
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--faint)' }} />
            <input className="field" style={{ paddingLeft: 36 }} placeholder="Search product, SKU, or UPC…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orderSkus.filter(s => [rpSkuDisplayName(s), rpSkuExternalCode(s), rpSkuExternalUpc(s), s.pack].join(' ').toLowerCase().includes(search.toLowerCase())).map(s => {
              const unit = mobileOrderSkuUnit(s);
              const av = mobileOrderAvailableQty(s.id, source, { excludeOrderId: editOrder && editOrder.id, eventId: selectedEvent && selectedEvent.id }), low = av != null && av > 0 && av < (unit === 'can' ? 12 : 15), q = qty[s.id] || 0;
              const over = av != null && q > av;
              const empty = av != null && av <= 0;
              const price = mobileOrderUnitPrice(c, s);
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', border: '1px solid ' + (q > 0 ? 'var(--amber)' : 'var(--line)'), borderRadius: 12, padding: 12, boxShadow: 'var(--shadow-sm)' }}>
                  <CanThumb sku={s} h={46} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{rpSkuDisplayName(s)}</div>
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 2 }}>{rpSkuExternalIdentity(s)}</div>
                    <div style={{ fontSize: 11.5, color: empty || low ? 'var(--danger)' : 'var(--faint)', fontWeight: empty || low ? 600 : 400 }}>{av == null ? 'Availability pending' : `${av} ${unit === 'can' ? 'cans' : 'cases'} available`} - {RP.money2(price)} {mobileOrderPriceSuffix(s.id)}</div>
                    {over && <div style={{ fontSize: 11, color: backorderMode ? 'var(--amber-deep)' : 'var(--danger)', fontWeight: 600, marginTop: 2 }}>{backorderMode ? 'Will be reserved from incoming stock' : 'Not enough stock at this source'}</div>}
                    {unit === 'can' && selectedEvent && av <= 0 && <button type="button" className="btn btn-sm" onClick={() => nav.push('event-open-cases', { id: selectedEvent.id })} style={{ marginTop: 7 }}><Icon name="box" size={13} />Open cases</button>}
                  </div>
                  <Stepper value={q} max={backorderMode || av == null ? undefined : av} onChange={v => setQ(s.id, v)} />
                </div>
              );
            })}
          </div>
        </>}

        {step === 3 && <>
          <Lbl>Discount / Promo</Lbl>
          <select className="field" value={discount} onChange={e => setDiscount(e.target.value)} style={{ marginBottom: 18 }}>
            <option>None</option><option>5% volume</option><option>10% promo</option><option>14% rep push (needs approval)</option>
          </select>
          {rpDiscountPercent(discount) > 10 && rpActiveApprovalRule('DISCOUNT_ABOVE_10') && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 600, marginTop: -8, marginBottom: 16 }}><Icon name="flag" size={16} />Above your authority — routes to sales manager.</div>}
          <Lbl>Reason for Order</Lbl>
          <select className="field" value={reason} onChange={e => setReason(e.target.value)} style={{ marginBottom: 18 }}>
            {['Normal reorder', 'New account', 'Promo', 'Event', 'Sampling conversion', 'Rep push', 'Customer requested', 'Emergency stockout'].map(r => <option key={r}>{r}</option>)}
          </select>
          <Lbl>Delivery Instructions</Lbl>
          <textarea className="field" rows="2" placeholder="Back door receiving Tue/Thu…" value={note} onChange={e => setNote(e.target.value)} style={{ marginBottom: 18, resize: 'none' }} />
          <Lbl>PO Number <span style={{ color: 'var(--faint)', fontWeight: 400 }}>{c && c.requiresPo ? '(required before invoicing)' : '(optional)'}</span></Lbl>
          <input className="field" placeholder="—" value={po} onChange={e => setPo(e.target.value)} style={{ marginBottom: 18 }} />
        </>}

        {step === 4 && <>
          <ReviewBlock title={quickSale ? 'Sale' : 'Customer'}><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{quickSale ? (quickSaleLabel || 'Walk-up cash sale') : `${c.city} · ${c.tier}`}</div></ReviewBlock>
          <ReviewBlock title="Fulfillment"><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}><Field2 k="Source" v={sourceLocation.name} /><Field2 k="Method" v={quickSale ? 'Customer Pickup' : method} /><Field2 k="Requested" v={quickSale ? 'Immediate' : requestedDelivery} /><Field2 k="Reason" v={quickSale ? (quickSaleLabel || 'Quick cash sale') : reason} /></div></ReviewBlock>
          <ReviewBlock title="Items">
            {lines.map(([k, v]) => { const s = rpSkuItem(k); return (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line-2)' }}>
                <FlavorChip sku={s} size={24} />
                <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: 'block', fontSize: 13.5 }}>{rpSkuDisplayName(s)}</span><span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--faint)', marginTop: 2 }}>{rpSkuExternalIdentity(s || '')}</span></span>
                <span className="mono" style={{ fontWeight: 600 }}>{mobileOrderQtyLabel(k, v)}</span>
              </div>
            ); })}
              <PricingBreakdown order={estimateOrder} totals={estimate} estimated showCardOption={!quickSale} paymentMethod={consumerInvoiceMethod} />
          </ReviewBlock>
          {stockIssueText && <div style={{ background: backorderMode ? 'var(--warn-bg)' : 'var(--danger-bg)', color: backorderMode ? 'var(--amber-deep)' : 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}><Icon name="alert" size={16} />{backorderMode ? `Advance order: ${stockIssueText} Stock must be received or transferred to ${sourceLocation.name} before fulfillment.` : stockIssueText}</div>}
          <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}><Icon name="check" size={16} />Status after submit: {reviewStatus}{approvalPreview.required ? ` - ${approvalPreview.reason}` : ''}</div>
          {consumerInvoiceAvailable && (
            <ReviewBlock title="Customer Invoice">
              <button type="button" onClick={() => setIssueConsumerInvoice(!issueConsumerInvoice)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, border: '1.5px solid ' + (issueConsumerInvoice ? 'var(--amber)' : 'var(--line)'), background: issueConsumerInvoice ? 'var(--amber-soft)' : 'var(--surface)', color: issueConsumerInvoice ? 'var(--amber-deep)' : 'var(--ink)', borderRadius: 11, padding: '12px 13px', textAlign: 'left', marginBottom: issueConsumerInvoice ? 14 : 0 }}>
                <span style={{ width: 22, height: 22, borderRadius: 7, border: '2px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{issueConsumerInvoice && <Icon name="check" size={13} />}</span>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 800 }}>Issue customer {consumerInvoiceDocument}</span>
              </button>
              {issueConsumerInvoice && <>
                {c && c.requiresPo && !po.trim() && <div role="status" style={{ color: 'var(--amber-deep)', fontSize: 12.5, marginBottom: 12 }}>This customer requires a PO number. <button type="button" className="btn btn-sm" onClick={() => setStep(3)}>Add PO number</button> Or turn off invoice issuance to save only the order.</div>}
                <Lbl>Payment Method</Lbl>
                <select className="field" value={consumerInvoiceMethod} onChange={e => { const method = e.target.value; setConsumerInvoiceMethod(method); if (method === 'credit_card') setConsumerInvoiceAlreadyPaid(false); }} style={{ marginBottom: 14 }}>
                  {consumerInvoiceAllowedMethods.map(method => <option key={method.code} value={method.code}>{method.label}</option>)}
                </select>
                {canIssueAlreadyPaid && consumerInvoiceMethod !== 'credit_card' && <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                  <input type="checkbox" checked={consumerInvoiceAlreadyPaid} onChange={e => setConsumerInvoiceAlreadyPaid(e.target.checked)} />
                  Already paid
                </label>}
                {effectiveConsumerAlreadyPaid && <><Lbl>Amount paid</Lbl><input className="field" type="number" min="0.01" max={Number(estimate.total || 0).toFixed(2)} step="0.01" inputMode="decimal" aria-invalid={consumerPaymentAmountInvalid ? 'true' : 'false'} value={consumerPaymentAmount} onChange={e => setConsumerPaymentAmount(e.target.value)} placeholder={RP.money2(estimate.total)} style={{ marginBottom: consumerPaymentAmountInvalid ? 5 : 12, borderColor: consumerPaymentAmountInvalid ? 'var(--danger)' : undefined }} />{consumerPaymentAmountInvalid && <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, fontWeight: 750, marginBottom: 12 }}>{consumerPaymentAmountExceedsTotal ? `Payment amount cannot exceed the invoice total of ${RP.money2(estimate.total)}.` : 'Enter a payment amount greater than zero.'}</div>}</>}
                {effectiveConsumerAlreadyPaid && <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)', gap: 9, marginBottom: 12 }}>
                  <div><Lbl>Paid date</Lbl><input className="field" type="date" value={consumerPaymentDate} onChange={e => setConsumerPaymentDate(e.target.value)} /></div>
                  <div><Lbl>{consumerInvoiceMethod === 'cheque' ? 'Cheque number' : 'Reference'}</Lbl><input className="field" value={consumerPaymentReference} onChange={e => setConsumerPaymentReference(e.target.value)} placeholder={consumerInvoiceMethod === 'cheque' ? 'Cheque #' : 'Interac ref, cash, etc.'} /></div>
                </div>}
                {effectiveConsumerAlreadyPaid && <><Lbl>Payment note</Lbl><textarea className="field" rows="2" value={consumerPaymentNote} onChange={e => setConsumerPaymentNote(e.target.value)} placeholder="Optional internal note" style={{ resize: 'none', marginBottom: 14 }} /></>}
                <Lbl>Customer invoice note</Lbl>
                <textarea className="field" rows="2" maxLength={600} value={consumerInvoiceNote} onChange={e => setConsumerInvoiceNote(e.target.value)} placeholder="Meet at receiving, delivery instructions, thank you..." style={{ resize: 'none', marginBottom: 14 }} />
                <Lbl>Email {consumerInvoiceDocument} to</Lbl>
                <input className="field" type="email" value={consumerInvoiceEmail} onChange={e => setConsumerInvoiceEmail(e.target.value)} placeholder="customer@example.com" style={{ marginBottom: 12 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 700 }}>
                  <input type="checkbox" checked={consumerInvoiceSendEmail} onChange={e => setConsumerInvoiceSendEmail(e.target.checked)} />
                  Email {consumerInvoiceDocument} to customer
                </label>
              </>}
            </ReviewBlock>
          )}
          {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
        </>}
      </div>

      {/* Footer */}
      <div style={{ flexShrink: 0, padding: '12px 16px', borderTop: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12 }}>
        {lines.length > 0 && step < 4 && <div style={{ flex: 1 }}><div style={{ fontSize: 11, color: 'var(--faint)' }}>{mobileOrderSummaryQty(lines) || `${cases} cases`}</div><div className="disp" style={{ fontSize: 20 }}>{RP.money(estimate.total)}</div></div>}
        {step > 1 && <button className="btn btn-lg" onClick={() => setStep(step - 1)} style={{ flex: lines.length > 0 && step < 4 ? '0 0 auto' : 1 }}>Back</button>}
        {step < 4
          ? <button className="btn btn-lg btn-primary" disabled={!canNext} style={{ flex: lines.length > 0 ? '0 0 auto' : 1, opacity: canNext ? 1 : 0.4, paddingLeft: 28, paddingRight: 28 }} onClick={() => setStep(step + 1)}>{step === 3 ? 'Review' : 'Next'}<Icon name="arrowR" size={16} /></button>
          : <button className="btn btn-lg btn-primary" disabled={busy || !!blockingStockIssueText || !!customerAddressIssueText || !!deliveryDateError || !!consumerPaymentAmountInvalid} style={{ flex: 1, opacity: busy || blockingStockIssueText || customerAddressIssueText || deliveryDateError || consumerPaymentAmountInvalid ? 0.65 : 1 }} onClick={submitOrder}><Icon name="check" size={17} />{busy ? 'Submitting...' : (editOrder ? 'Save Changes' : quickSale ? 'Record Sale' : backorderMode ? 'Submit Advance Order' : 'Submit Order')}</button>}
      </div>
    </div>
  );
};

function Lbl({ children }) { return <label className="field-label">{children}</label>; }
function RadioCards({ value, onChange, options }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{options.map(o => {
    const on = o.v === value;
    return (
      <button key={o.v} onClick={() => onChange(o.v)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 11, border: '1.5px solid ' + (on ? 'var(--amber)' : 'var(--line)'), background: on ? 'var(--amber-soft)' : 'var(--surface)', textAlign: 'left' }}>
        <span style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid ' + (on ? 'var(--amber)' : 'var(--faint)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on && <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--amber)' }} />}</span>
        <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{o.l}</div>{o.m && <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{o.m}</div>}</div>
      </button>
    );
  })}</div>;
}
function ReviewBlock({ title, children }) {
  return <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 12, boxShadow: 'var(--shadow-sm)' }}><div className="eyebrow" style={{ marginBottom: 9 }}>{title}</div>{children}</div>;
}

function stockRequestTotal(r) {
  return Number(r.totalCases || (r.lines || []).reduce((s, l) => s + Number(l.qty || l.quantity || 0), 0));
}
function stockRequestDate(r) {
  const raw = r.updated || r.created || '';
  if (!raw) return 'Pending';
  return rpFormatLocalDate(raw, { source: r, empty: String(raw).slice(0, 16) });
}
function stockRequestProgress(status) {
  if (status === 'Rejected') return ['Rejected'];
  return ['Pending', 'Approved', 'Fulfilled'];
}
function StockRequestLines({ lines }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {(lines || []).map((line, i) => { const s = rpSkuItem(line.sku); return (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: i < lines.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
          <FlavorChip sku={s} size={26} />
          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{rpSkuDisplayName(s || line.sku)}</span>
          <span className="mono" style={{ fontWeight: 700 }}>{Number(line.qty || 0)} cs</span>
        </div>
      ); })}
    </div>
  );
}

/* ---------- REQUEST STOCK ---------- */
MOBILE_SCREENS['request-stock'] = function RequestStock({ nav, role, loc }) {
  const authorizedDestinationLocations = inventoryPersonalFieldLocations(role);
  const requestedDestinationLocationId = inventoryPersonalValidatedFieldLocationId(authorizedDestinationLocations, loc);
  const preferredDestinationLocationId = inventoryPersonalPreferredFieldLocationId(authorizedDestinationLocations);
  const destinationMicro = authorizedDestinationLocations.find(location => String(location.id || '') === requestedDestinationLocationId)
    || authorizedDestinationLocations.find(location => String(location.id || '') === preferredDestinationLocationId)
    || (authorizedDestinationLocations.length === 1 ? authorizedDestinationLocations[0] : null);
  const destinationLocationId = (destinationMicro && destinationMicro.id) || '';
  const [source, setSource] = useState(() => mobileDefaultStockSource(role, destinationLocationId));
  const [own, setOwn] = useState('Fronted to Rep');
  const [qty, setQty] = useState(() => {
    const defaults = {};
    rpInventorySkus().slice(0, 3).forEach((sku, index) => { defaults[sku.id] = rpHasLiveAuth() ? 0 : (index === 2 ? 6 : 12); });
    return defaults;
  });
  const [notes, setNotes] = useState('');
  const [requestRef] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `SR-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`
    : `SR-${Date.now().toString(36).toUpperCase()}`);
  const [result, setResult] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const stockSkus = rpInventorySkus({ includeIds: Object.keys(qty) });
  const total = Object.values(qty).reduce((s, v) => s + v, 0);
  const sourceOptions = mobileStockLocationOptions(role, source, { excludeIds: [destinationLocationId], includeMicro: false });
  const selectedSource = sourceOptions.some(option => option.v === source) ? source : ((sourceOptions[0] || {}).v || '');
  const destinationName = destinationMicro ? (destinationMicro.name || destinationMicro.id) : 'No micro-warehouse assigned';
  const canSubmit = !busy && total > 0 && Boolean(destinationLocationId) && Boolean(selectedSource);
  const submit = async () => {
    if (!destinationLocationId) {
      setError('No micro-warehouse is assigned to this profile yet.');
      return;
    }
    if (!selectedSource) {
      setError('No source warehouse is available for this request.');
      return;
    }
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    let ref = requestRef;
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/inventory/stock-request', {
          method: 'POST',
          body: JSON.stringify({
            destinationLocationId,
            lineItems: Object.entries(qty).filter(([, v]) => v > 0).map(([sku, quantity]) => ({ sku, quantity })),
            notes,
            ownershipType: own,
            requestId: requestRef,
            sourceLocationId: selectedSource
          })
        });
        ref = response.ref || ref;
        RP.upsertStockRequest({
          id: ref,
          requestedBy: rpAuthProfile().id || rpFieldUserName(role),
          requester: rpDisplayName(),
          source: selectedSource,
          destination: destinationLocationId,
          territory: rpFieldTerritory(role),
          ownership: own,
          status: 'Pending',
          notes,
          created: new Date().toISOString(),
          updated: new Date().toISOString(),
          totalCases: total,
          lines: Object.entries(qty).filter(([, v]) => v > 0).map(([sku, qty]) => ({ sku, qty })),
          persisted: true
        });
        await rpRefreshAfterMutation(`Stock request ${ref} submitted`);
      }
      setResult({ ref, total });
      toast(`Stock request ${ref} submitted`, 'ok');
      setDone(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not submit stock request.');
    } finally {
      setBusy(false);
    }
  };
  if (done) return <Success nav={nav} title="Request Sent" sub={`${result ? result.total : total} cases - ${own}`} note={`${result ? result.ref + ' - ' : ''}Pending ops approval. Track review and fulfillment from Stock Requests.`} actionLabel="View Requests" onAction={() => nav.reset('stock-requests')} />;
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Request Stock" sub={`To: ${destinationName}`} nav={nav} back right={<button onClick={() => nav.push('stock-requests')} style={{ border: 'none', background: 'var(--surface-2)', color: 'var(--navy)', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="clock" size={17} /></button>} />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        {!destinationLocationId && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>No micro-warehouse is assigned to this profile yet. Add one in Operations Admin &gt; Locations.</div>}
        <Lbl>Source</Lbl>
        <select className="field" value={selectedSource} onChange={e => setSource(e.target.value)} disabled={!sourceOptions.length} style={{ marginBottom: 18, opacity: sourceOptions.length ? 1 : 0.65 }}>
          {sourceOptions.map(option => <option key={option.v} value={option.v}>{option.l}</option>)}
          {!sourceOptions.length && <option value="">No source locations available</option>}
        </select>
        <Lbl>Ownership Type</Lbl>
        <RadioCards value={own} onChange={setOwn} options={[{ v: 'Fronted to Rep', l: 'Fronted to Rep', m: 'Company-owned, settle on sell-through' }, { v: 'Purchased by Rep', l: 'Purchased by Rep' }, { v: 'Sample / Demo', l: 'Sample / Demo', m: 'Hits marketing expense' }]} />
        <div style={{ height: 16 }} />
        <Lbl>Items</Lbl>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {stockSkus.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: 11 }}>
              <FlavorChip sku={s} size={30} /><span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{rpSkuDisplayName(s)}</span>
              <Stepper value={qty[s.id] || 0} onChange={v => setQty(q => ({ ...q, [s.id]: v }))} />
            </div>
          ))}
        </div>
        <Lbl style={{ marginTop: 16 }}>Notes</Lbl>
        <textarea className="field" rows="2" placeholder="Weekend retailer push…" value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'none' }} />
        {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy || total <= 0} style={{ flex: 1, opacity: busy || total <= 0 ? 0.6 : 1 }} onClick={submit}>{busy ? 'Submitting...' : `Submit Request · ${total} cases`}</button></MActionBar>
    </div>
  );
};

MOBILE_SCREENS['stock-requests'] = function StockRequests({ nav, role }) {
  const requests = rpFieldStockRequests(role).slice().sort((a, b) => String(b.created || '').localeCompare(String(a.created || '')));
  const open = requests.filter(r => ['Pending', 'Approved'].includes(r.status)).length;
  return (
    <div className="rp-fade">
      <MHeader title="Stock Requests" sub={open ? `${open} open` : 'All caught up'} nav={nav} back right={<button onClick={() => nav.push('request-stock')} style={{ border: 'none', background: 'var(--amber)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={18} /></button>} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {requests.map(r => (
          <button key={r.id} onClick={() => nav.push('stock-request-detail', { id: r.id })} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span className="mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{r.id}</span>
              <Pill status={r.status} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 9 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{RP.loc(r.source).name} to {RP.loc(r.destination).name}</span>
              <span className="disp" style={{ fontSize: 18 }}>{stockRequestTotal(r)} cs</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {(r.lines || []).slice(0, 3).map(line => {
                const s = rpSkuItem(line.sku);
                return <span key={line.sku} className="pill" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>{rpSkuDisplayName(s || line.sku).split(' ')[0]} {line.qty}</span>;
              })}
              {(r.lines || []).length > 3 && <span className="pill">+{r.lines.length - 3}</span>}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 9 }}>{stockRequestDate(r)} - {r.ownership || 'Fronted to Rep'}</div>
          </button>
        ))}
        {!requests.length && <MEmpty icon="layers" title="No stock requests" sub="Requests you submit will appear here with approval and fulfillment status." action={<button className="btn btn-primary btn-sm" onClick={() => nav.push('request-stock')}><Icon name="plus" size={15} />Request Stock</button>} />}
      </div>
    </div>
  );
};

MOBILE_SCREENS['stock-request-detail'] = function StockRequestDetail({ nav, id }) {
  const r = (RP.STOCK_REQUESTS || []).find(x => x.id === id);
  if (!r) return <MEmpty icon="layers" title="Request not found" sub="This stock request is not available in the current data set." action={<button className="btn btn-primary btn-sm" onClick={() => nav.pop()}><Icon name="arrowL" size={15} />Back</button>} />;
  const stages = stockRequestProgress(r.status);
  const cur = Math.max(0, stages.indexOf(r.status));
  return (
    <div className="rp-fade">
      <MHeader title={r.id} sub={`${stockRequestTotal(r)} cases`} nav={nav} back right={<Pill status={r.status} />} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px 14px', boxShadow: 'var(--shadow-sm)' }}>
          {stages.map((s, i) => {
            const done = r.status === 'Rejected' ? true : i <= cur;
            const active = s === r.status;
            return (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: i < stages.length - 1 ? 4 : 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: done ? (r.status === 'Rejected' ? 'var(--danger)' : 'var(--ok)') : 'var(--surface)', border: '2px solid ' + (done ? (r.status === 'Rejected' ? 'var(--danger)' : 'var(--ok)') : 'var(--line)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{done && <Icon name={r.status === 'Rejected' ? 'x' : 'check'} size={12} />}</span>
                  {i < stages.length - 1 && <span style={{ width: 2, height: 18, background: i < cur ? 'var(--ok)' : 'var(--line)' }} />}
                </div>
                <span style={{ fontSize: 13.5, fontWeight: active ? 700 : 500, color: done ? 'var(--ink)' : 'var(--faint)' }}>{s}</span>
              </div>
            );
          })}
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
          <span className="eyebrow">Items</span>
          <div style={{ marginTop: 9 }}><StockRequestLines lines={r.lines || []} /></div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field2 k="Source" v={RP.loc(r.source).name} />
          <Field2 k="Destination" v={RP.loc(r.destination).name} />
          <Field2 k="Ownership" v={r.ownership || 'Fronted to Rep'} />
          <Field2 k="Updated" v={stockRequestDate(r)} />
        </div>

        {r.notes && <div style={{ background: 'var(--amber-soft)', borderRadius: 12, padding: 14 }}><div className="eyebrow" style={{ color: 'var(--amber-deep)', marginBottom: 6 }}>Notes</div><div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{r.notes}</div></div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" style={{ flex: 1 }} onClick={() => nav.push('request-stock')}><Icon name="plus" size={16} />New Request</button></MActionBar>
    </div>
  );
};

/* ---------- VISIT LOG ---------- */
let mobileVisitRequestSequence = 0;
function mobileVisitClientRequestId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return `visit-${window.crypto.randomUUID()}`;
  mobileVisitRequestSequence += 1;
  return `visit-${Date.now().toString(36)}-${mobileVisitRequestSequence.toString(36)}`;
}

MOBILE_SCREENS['visit-log'] = function VisitLog({ nav, id, role }) {
  const customerId = id || rpFieldFirstCustomerId(role);
  const exactCustomer = useRpExactCustomer({ customerId, enabled: Boolean(customerId) });
  if (exactCustomer.loading) return (
    <div className="rp-fade" data-exact-customer-loading="true">
      <MHeader title="Log Visit" sub={`Loading ${customerId}`} nav={nav} back />
      <MEmpty icon="refresh" title="Loading customer" sub="Fetching this account from the complete customer directory." />
    </div>
  );
  if (exactCustomer.error) return (
    <div className="rp-fade" data-exact-customer-error="true">
      <MHeader title="Log Visit" sub="Customer unavailable" nav={nav} back />
      <MEmpty icon="alert" title="Customer could not load" sub={exactCustomer.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactCustomer.retry}>Try Again</button>} />
    </div>
  );
  if (!exactCustomer.customer) return <Success nav={nav} title="No Customer Selected" sub="Visit log" note="This customer does not exist, is outside your sales scope, or no customer was selected." />;
  return <VisitLogForm key={exactCustomer.customer.id} nav={nav} id={id} role={role} customer={exactCustomer.customer} />;
};

function VisitLogForm({ nav, role, customer }) {
  const c = customer;
  const [type, setType] = useState('Sales visit');
  const [shelf, setShelf] = useState(() => {
    const defaults = {};
    const summary = typeof rpCustomerOrderSummary === 'function' ? rpCustomerOrderSummary(c) : {};
    const orderedSkuIds = summary.lastOrder && Array.isArray(summary.lastOrder.lines)
      ? summary.lastOrder.lines.map(line => line.sku).filter(Boolean)
      : [];
    const skuIds = Array.from(new Set(orderedSkuIds)).slice(0, 5);
    const seedSkus = skuIds.length
      ? skuIds.map(id2 => RP.sku(id2) || { id: id2 })
      : rpCasePackSkus().slice(0, 3);
    seedSkus.forEach(sku => { defaults[sku.id] = ''; });
    return defaults;
  });
  const [followUp, setFollowUp] = useState('No');
  const [followUpDueDate, setFollowUpDueDate] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpChannel, setFollowUpChannel] = useState('');
  const [photos, setPhotos] = useState({ shelf: false, display: false });
  const [photoFiles, setPhotoFiles] = useState({ shelf: null, display: null });
  const [feedback, setFeedback] = useState('');
  const [result, setResult] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [clientRequestId] = useState(mobileVisitClientRequestId);
  const shelfPhotoInput = useRef(null);
  const displayPhotoInput = useRef(null);
  const photoInputFor = key => key === 'shelf' ? shelfPhotoInput : displayPhotoInput;
  const followUpMinDate = mobileBusinessToday(c);
  const setFollowUpChoice = value => {
    setFollowUp(value);
    if (value === 'Yes') {
      setFollowUpDueDate(current => current || mobileDefaultFollowUpDate(c));
      return;
    }
    setFollowUpDueDate('');
    setFollowUpNote('');
    setFollowUpChannel('');
  };
  const save = async () => {
    if (busy || !c) return;
    setError('');
    if (followUp === 'Yes' && (!mobileValidYmd(followUpDueDate) || followUpDueDate < followUpMinDate)) {
      setError('Choose a valid follow-up due date that is not in the past.');
      return;
    }
    setBusy(true);
    let visit = null;
    const shelfCounts = Object.fromEntries(Object.entries(shelf)
      .filter(([, value]) => value !== '' && Number.isFinite(Number(value)))
      .map(([sku, value]) => [sku, Math.max(0, Math.floor(Number(value)))]));
    const followUpDetails = [followUpChannel ? `Channel: ${followUpChannel}` : '', followUpNote.trim()].filter(Boolean).join(' - ');
    try {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const uploadedPhotos = { ...photos };
        for (const key of Object.keys(photoFiles)) {
          const file = photoFiles[key];
          if (file) {
            const uploaded = await rpUploadPrivateFile('visit_photo', file, key);
            uploadedPhotos[key] = uploaded ? {
              attached: true,
              name: uploaded.name,
              path: uploaded.path,
              size: uploaded.size,
              type: uploaded.type
            } : false;
          }
        }
        const response = await rpApi('/api/visits/log', {
          method: 'POST',
          body: JSON.stringify({
            buyerFeedback: feedback.trim(),
            clientRequestId,
            customerId: c.id,
            followUpDueDate: followUp === 'Yes' ? followUpDueDate : undefined,
            followUpNeeded: followUp === 'Yes',
            followUpNote: followUp === 'Yes' ? followUpDetails : undefined,
            photos: uploadedPhotos,
            shelfCounts,
            visitType: type
          })
        });
        visit = response.visit;
      }
      const row = visit
        ? RP.upsertVisit({ ...visit, persisted: true })
        : RP.addVisit({ cust: c.id, type, note: feedback.trim(), who: rpFieldUserName(role) || c.rep, followUp: followUp === 'Yes', followUpDueDate: followUp === 'Yes' ? followUpDueDate : '', followUpNote: followUp === 'Yes' ? followUpDetails : '', shelf: shelfCounts, photos });
      if (visit) await rpRefreshAfterMutation(`Visit ${row.id} logged`);
      setResult(row);
      toast(`Visit ${row.id} logged`, 'ok');
      setDone(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not log visit.');
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    const counted = Object.values(shelf).some(value => value !== '');
    return <Success nav={nav} title="Visit Logged" sub={c.name} note={`${result ? result.id + ' · ' : ''}${counted ? 'Shelf counts' : 'Visit details'}${photos.shelf || photos.display ? ' and photos' : ''} saved to the customer record.${followUp === 'Yes' ? ` Follow-up due ${followUpDueDate}.` : ''}`} />;
  }
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Store Visit" sub={c.name} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <Lbl>Visit Type</Lbl>
        <select className="field" value={type} onChange={e => setType(e.target.value)} style={{ marginBottom: 18 }}>
          {['Sales visit', 'Reorder check', 'New account pitch', 'Sampling / demo', 'Merchandising', 'Display verification', 'Collection follow-up'].map(t => <option key={t}>{t}</option>)}
        </select>
        <Lbl>Shelf Stock Count <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -6, marginBottom: 10 }}>Leave a SKU blank when it was not counted. Enter 0 only when the shelf is confirmed empty.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 18 }}>
          {Object.keys(shelf).map(id2 => { const s = rpSkuItem(id2); return (
            <div key={id2} style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: 11 }}>
              <FlavorChip sku={s || id2} size={28} /><span style={{ flex: 1, minWidth: 0 }}><span style={{ display: 'block', fontSize: 13.5, fontWeight: 500 }}>{rpSkuDisplayName(s || id2)}</span><span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--faint)', marginTop: 2 }}>{rpSkuExternalIdentity(s || '')}</span></span>
              {shelf[id2] === 0 && <span className="pill" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>Out</span>}
              <input aria-label={`${rpSkuDisplayName(s || id2)} shelf count`} className="field mono" type="number" inputMode="numeric" min="0" step="1" placeholder="Not counted" value={shelf[id2]} onChange={event => { const value = event.target.value; const parsed = Number(value); setShelf(current => ({ ...current, [id2]: value === '' || !Number.isFinite(parsed) ? '' : Math.max(0, Math.floor(parsed)) })); }} style={{ width: 112, flex: '0 0 112px', textAlign: 'right' }} />
            </div>
          ); })}
        </div>
        <Lbl>Photos</Lbl>
        <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
          {[['shelf', 'Shelf Photo'], ['display', 'Display Photo']].map(([k, label]) => {
            const on = photos[k];
            const file = photoFiles[k];
            const inputRef = photoInputFor(k);
            return (
              <div key={k} style={{ flex: 1 }}>
                <input ref={inputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => { const selected = e.target.files && e.target.files[0]; if (selected) { setPhotoFiles(p => ({ ...p, [k]: selected })); setPhotos(p => ({ ...p, [k]: true })); } }} />
                <button className="btn" onClick={() => (window.RP_AUTH && window.RP_AUTH.demo) ? setPhotos(p => ({ ...p, [k]: !p[k] })) : inputRef.current && inputRef.current.click()} style={{ width: '100%', flexDirection: 'column', minHeight: 80, gap: 6, borderColor: on ? 'var(--ok)' : undefined, background: on ? 'var(--ok-bg)' : undefined, color: on ? 'var(--ok)' : undefined, padding: '10px 8px' }}>
                  <Icon name={on ? 'check' : 'camera'} size={22} /><span style={{ fontSize: 11.5, textAlign: 'center', lineHeight: 1.25 }}>{file ? rpUploadLabel(file) : on ? 'Captured' : label}</span>
                </button>
                {file && <button className="btn btn-ghost btn-sm" onClick={() => { setPhotoFiles(p => ({ ...p, [k]: null })); setPhotos(p => ({ ...p, [k]: false })); if (inputRef.current) inputRef.current.value = ''; }} style={{ width: '100%', paddingTop: 5, color: 'var(--danger)' }}>Remove</button>}
              </div>
            );
          })}
        </div>
        <Lbl>Buyer Feedback</Lbl>
        <textarea className="field" rows="3" placeholder="Interested in Mango promo. Wants Tue delivery…" value={feedback} onChange={e => setFeedback(e.target.value)} style={{ resize: 'none', marginBottom: 18 }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: '13px 14px' }}>
          <span style={{ fontSize: 13.5, fontWeight: 500 }}>Follow-up needed?</span>
          <Segmented size="sm" options={['No', 'Yes']} value={followUp} onChange={setFollowUpChoice} />
        </div>
        {followUp === 'Yes' && <div style={{ marginTop: 10, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: '13px 14px' }}>
          <Lbl>Follow-up Due <span style={{ color: 'var(--danger)' }}>*</span></Lbl>
          <input className="field" type="date" min={followUpMinDate} value={followUpDueDate} onChange={event => setFollowUpDueDate(event.target.value)} style={{ marginBottom: 12 }} />
          <Lbl>Channel <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
          <select className="field" value={followUpChannel} onChange={event => setFollowUpChannel(event.target.value)} style={{ marginBottom: 12 }}>
            <option value="">Not specified</option>
            {['Call', 'Email', 'Text', 'In person'].map(channel => <option key={channel}>{channel}</option>)}
          </select>
          <Lbl>Follow-up Note <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
          <textarea className="field" rows="2" placeholder="What needs to happen next?" value={followUpNote} onChange={event => setFollowUpNote(event.target.value)} style={{ resize: 'none' }} />
        </div>}
        {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy} style={{ flex: 1, opacity: busy ? 0.65 : 1 }} onClick={save}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Save Visit'}</button></MActionBar>
    </div>
  );
}

function Success({ nav, title, sub, note, actionLabel, onAction }) {
  return (
    <div className="rp-fade" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30, textAlign: 'center', gap: 16 }}>
      <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'rp-pop .4s ease' }}><Icon name="check" size={38} /></div>
      <div><div className="disp" style={{ fontSize: 25, textTransform: 'uppercase' }}>{title}</div><div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 5 }}>{sub}</div></div>
      {note && <div style={{ background: 'var(--surface-2)', borderRadius: 11, padding: '12px 16px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4 }}>{note}</div>}
      {actionLabel && onAction && <button className="btn btn-lg btn-primary" style={{ width: '100%' }} onClick={onAction}>{actionLabel}</button>}
      <button className={actionLabel ? 'btn btn-lg' : 'btn btn-lg btn-primary'} style={{ width: '100%' }} onClick={() => nav.pop()}>Done</button>
    </div>
  );
}

Object.assign(window, { Lbl, RadioCards, ReviewBlock, Success });


/* ===== mobile_events.jsx ===== */
/* ============================================================
   RANCH POWER - Mobile : Events + open-case singles
   ============================================================ */

function mobileEventIsActive(event, today) {
  const status = String(event && event.status || '').toLowerCase();
  if (['cancelled', 'lost', 'reconciled', 'settled'].includes(status)) return false;
  const end = eventEndDateValue(event) || eventDateValue(event);
  return !end || end >= today;
}

function mobileActiveEventCount(role) {
  const today = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: rpFieldTerritory(role) }) : new Date().toISOString().slice(0, 10);
  return mobileEventRows(role).filter(event => mobileEventIsActive(event, today)).length;
}

function mobileEventRows(role) {
  const profile = rpAuthProfile ? rpAuthProfile() : {};
  const profileId = String(profile.id || '').trim();
  const profileName = String(profile.name || profile.displayName || rpFieldUserName(role) || '').trim().toLowerCase();
  const isRep = rpNormalizeRole(role) === 'rep';
  const rows = eventRows().slice().sort(eventSort);
  if (!isRep) return rows;
  return rows.filter(event => {
    const team = eventTeamRows(event.id);
    if (!team.length) return true;
    if (rpFieldCoversTerritory(role, event.territory)) return true;
    return team.some(member => {
      const memberId = String(member.userId || '').trim();
      const memberName = String(member.name || '').trim().toLowerCase();
      return (profileId && memberId === profileId) || (profileName && memberName === profileName);
    });
  });
}

function mobileEventDateSummary(event) {
  return [eventDateDisplay(event), eventTimeDisplay(event)].filter(Boolean).join(' - ') || 'Unscheduled';
}

function mobileEventVenueSummary(event) {
  return [event.venueName, event.territory].filter(Boolean).join(' - ') || event.territory || 'Field event';
}

function mobileEventTeamSummary(event) {
  const team = eventTeamRows(event.id);
  if (!team.length) return 'No team assigned';
  return team.slice(0, 3).map(member => member.name || member.userId).filter(Boolean).join(', ') + (team.length > 3 ? ` +${team.length - 3}` : '');
}

function mobileEventSourceOptionsForAdd(event, role) {
  const current = (rpFieldMicroWarehouse(role) || {}).id || (event && event.defaultSourceLocationId) || eventDefaultSourceLocationId(event);
  const base = mobileStockLocationOptions(role, current, { includeMicro: true }).map(option => ({
    id: option.v,
    name: option.l,
    meta: option.m
  }));
  const seen = new Set(base.map(option => option.id));
  const add = location => {
    if (!location || !location.id || seen.has(location.id)) return;
    seen.add(location.id);
    base.push({ id: location.id, name: location.name || location.id, meta: location.territory || location.type || '' });
  };
  add(RP.loc(event && event.defaultSourceLocationId));
  eventSourceCaseHoldings(current).forEach(row => add(RP.loc(row.sourceLocationId)));
  if (!base.length) eventLocationOptions().slice(0, 4).forEach(add);
  return base.filter(option => option.id);
}

function mobileEventAllocatedSourceOptions(event) {
  const grouped = new Map();
  const add = id => {
    if (!id || grouped.has(id)) return;
    grouped.set(id, { id, name: eventLocationName(id) });
  };
  eventAllocatedCaseHoldings(event).forEach(row => add(row.sourceLocationId));
  add(event && event.defaultSourceLocationId);
  return Array.from(grouped.values());
}

function mobileEventRepOptions(event, role) {
  const options = eventTeamRows(event.id).filter(row => row.userId).map(row => ({
    id: row.userId,
    label: row.name || row.userId,
    role: row.role || 'Team'
  }));
  const profile = rpAuthProfile ? rpAuthProfile() : {};
  const profileId = profile.id || rpFieldUserName(role);
  if (profileId && !options.some(option => option.id === profileId)) {
    options.unshift({ id: profileId, label: rpDisplayName ? rpDisplayName() : rpFieldUserName(role), role: 'Sales' });
  }
  return options;
}

function mobileEventDefaultRepId(event, role) {
  const defaultId = eventDefaultRepId(event);
  if (defaultId) return defaultId;
  const profile = rpAuthProfile ? rpAuthProfile() : {};
  return profile.id || rpFieldUserName(role) || '';
}

function mobileEventAllocatedCases(event) {
  return eventAllocatedCaseHoldings(event).reduce((sum, row) => sum + Number(row.quantity || 0), 0);
}

function mobileEventOpenRows(event) {
  return eventOpenCaseBatches(event.id)
    .map(batch => ({ ...batch, remainingCans: eventOpenBatchRemaining(batch) }))
    .sort((a, b) => rpSkuDisplayName(rpSkuItem(a.canSkuId) || a.canSkuId).localeCompare(rpSkuDisplayName(rpSkuItem(b.canSkuId) || b.canSkuId)));
}

function mobileEventHoldingLabel(row, kind = 'available') {
  const sku = rpSkuItem(row.skuId) || row.skuId;
  const quantity = Number(row.qty ?? row.quantity ?? 0);
  return `${rpSkuDisplayName(sku)} - ${row.lot || 'UNASSIGNED'} - ${quantity.toLocaleString()} cs ${kind}`;
}

function mobileEventSearchValue(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_./,:-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function mobileEventMatchesSearch(event, normalizedQuery) {
  if (!normalizedQuery) return true;
  const team = eventTeamRows(event.id).map(member => member.name || member.userId).filter(Boolean).join(' ');
  const sourceLocationId = event.defaultSourceLocationId || event.sourceLocationId || '';
  const source = sourceLocationId
    ? (typeof eventLocationName === 'function' ? eventLocationName(sourceLocationId) : ((RP.loc(sourceLocationId) || {}).name || sourceLocationId))
    : '';
  const customer = event.customerId ? RP.cust(event.customerId) : null;
  const values = [
    event.name,
    event.venueName,
    mobileEventVenueSummary(event),
    event.territory,
    event.status,
    eventStatusLabel(event.status),
    event.eventType,
    eventDateValue(event),
    eventEndDateValue(event),
    mobileEventDateSummary(event),
    team,
    source,
    customer && customer.name
  ];
  return mobileEventSearchValue(values.filter(Boolean).join(' ')).includes(normalizedQuery);
}

MOBILE_SCREENS['rep-events'] = function RepEvents({ nav, role }) {
  const [search, setSearch] = useState('');
  const [view, setView] = useState('total');
  const rows = mobileEventRows(role);
  const today = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: rpFieldTerritory(role) }) : new Date().toISOString().slice(0, 10);
  const openByEventId = new Map(rows.map(event => [event.id, eventOpenCanSummary(event.id)]));
  const activeRows = rows.filter(event => mobileEventIsActive(event, today));
  const openRows = rows.filter(event => (openByEventId.get(event.id) || {}).remainingCans > 0);
  const openCans = openRows.reduce((sum, event) => sum + Number((openByEventId.get(event.id) || {}).remainingCans || 0), 0);
  const viewRows = view === 'active' ? activeRows : view === 'open' ? openRows : rows;
  const query = mobileEventSearchValue(search);
  const filtered = viewRows.filter(event => mobileEventMatchesSearch(event, query));
  const viewOptions = [
    { value: 'active', label: 'Active', count: activeRows.length, detail: 'events' },
    { value: 'open', label: 'Open', count: openRows.length, detail: `${openCans} cans` },
    { value: 'total', label: 'Total', count: rows.length, detail: 'events' }
  ];
  const emptyGroup = view === 'active' ? 'active events' : view === 'open' ? 'events with open cans' : 'events';
  const emptyTitle = view === 'active' ? 'No active events' : view === 'open' ? 'No open event stock' : 'No events found';
  const emptySub = query
    ? `No ${emptyGroup} match "${search.trim()}".`
    : view === 'active'
      ? 'No current or upcoming events are assigned to this view.'
      : view === 'open'
        ? 'No events have loose cans remaining.'
        : 'Create an event to start planning field activity.';
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Events" sub="Field marketing and POS singles" nav={nav} back right={<button onClick={() => nav.push('event-new')} style={{ border: 'none', background: 'var(--amber)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={18} /></button>} />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div role="group" aria-label="Filter events" data-event-view-filters="true" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginBottom: 14 }}>
          {viewOptions.map(option => {
            const selected = view === option.value;
            return (
              <button type="button" key={option.value} data-event-view-filter={option.value} aria-pressed={selected} onClick={() => setView(option.value)} style={{ minWidth: 0, textAlign: 'left', color: selected ? '#fff' : 'var(--ink)', background: selected ? 'var(--navy)' : 'var(--surface)', border: '1px solid ' + (selected ? 'var(--navy)' : 'var(--line)'), borderRadius: 12, padding: 12 }}>
                <div className="eyebrow" style={{ color: selected ? 'var(--amber)' : undefined }}>{option.label}</div>
                <div className="disp" style={{ fontSize: 24 }}>{option.count}</div>
                <div style={{ fontSize: 10.5, color: selected ? 'oklch(0.82 0.02 235)' : 'var(--faint)' }}>{option.detail}</div>
              </button>
            );
          })}
        </div>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--faint)' }} />
          <input className="field" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events, dates, status, team..." style={{ paddingLeft: 36 }} />
        </div>
        <div data-event-results="true" data-result-count={filtered.length} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(event => {
            const open = openByEventId.get(event.id) || eventOpenCanSummary(event.id);
            const allocated = mobileEventAllocatedCases(event);
            return (
              <button key={event.id} data-event-row={event.id} data-event-status={String(event.status || '')} onClick={() => nav.push('event-detail', { id: event.id })} style={{ width: '100%', textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 13, padding: 13, boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{mobileEventVenueSummary(event)}</div>
                  </div>
                  <Pill status={eventStatusTone(event.status)}>{eventStatusLabel(event.status)}</Pill>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr', gap: 8, marginTop: 12, fontSize: 11.5, color: 'var(--muted)' }}>
                  <div><b style={{ color: 'var(--ink)' }}>{mobileEventDateSummary(event)}</b><div>{mobileEventTeamSummary(event)}</div></div>
                  <div><b className="mono" style={{ color: 'var(--ink)' }}>{allocated}</b><div>case stock</div></div>
                  <div><b className="mono" style={{ color: open.remainingCans ? 'var(--ok)' : 'var(--ink)' }}>{open.remainingCans}</b><div>open cans</div></div>
                </div>
              </button>
            );
          })}
          {!filtered.length && <div data-event-empty="true"><MEmpty icon="calendar" title={emptyTitle} sub={emptySub} action={<button className="btn btn-primary btn-sm" onClick={() => nav.push('event-new')}><Icon name="plus" size={15} />New Event</button>} /></div>}
        </div>
      </div>
    </div>
  );
};

MOBILE_SCREENS['event-detail'] = function MobileEventDetail({ nav, role, id }) {
  const event = eventRows().find(row => row.id === id);
  if (!event) return <MEmpty icon="calendar" title="Event not found" sub="Refresh events and try again." action={<button className="btn btn-primary btn-sm" onClick={() => nav.pop()}>Back</button>} />;
  const allocatedRows = eventAllocatedCaseHoldings(event);
  const openRows = mobileEventOpenRows(event);
  const openSummary = eventOpenCanSummary(event.id);
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title={event.name} sub={mobileEventDateSummary(event)} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 14, boxShadow: 'var(--shadow-sm)', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{event.name}</div>
              <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>{mobileEventVenueSummary(event)}</div>
            </div>
            <Pill status={eventStatusTone(event.status)}>{eventStatusLabel(event.status)}</Pill>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 14 }}>
            <Field2 k="Team" v={mobileEventTeamSummary(event)} />
            <Field2 k="Type" v={event.eventType || 'Event'} />
            <Field2 k="Source" v={eventLocationName(event.defaultSourceLocationId)} />
            <Field2 k="Closeout" v={EVENT_CLOSEOUT_LABELS[String(event.closeoutStatus || 'not_started')] || event.closeoutStatus || 'Not Started'} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginBottom: 14 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 12 }}><div className="eyebrow">Cases</div><div className="disp" style={{ fontSize: 24 }}>{mobileEventAllocatedCases(event)}</div></div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 12 }}><div className="eyebrow">Opened</div><div className="disp" style={{ fontSize: 24 }}>{openSummary.openedCases}</div></div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 12 }}><div className="eyebrow">Loose</div><div className="disp" style={{ fontSize: 24 }}>{openSummary.remainingCans}</div><div style={{ fontSize: 10.5, color: 'var(--faint)' }}>cans</div></div>
        </div>

        <MSection title="Inventory" pad={false}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginBottom: 12 }}>
            <button className="btn btn-primary" disabled={!eventCanAllocateInventory(event)} style={{ minWidth: 0, padding: '10px 6px', fontSize: 12, opacity: eventCanAllocateInventory(event) ? 1 : 0.55 }} onClick={() => nav.push('event-add-cases', { id: event.id })}><Icon name="plus" size={14} />Add Cases</button>
            <button className="btn" style={{ minWidth: 0, padding: '10px 6px', fontSize: 12 }} onClick={() => nav.push('event-open-cases', { id: event.id })}><Icon name="package" size={14} />Open Cases</button>
            <button className="btn" style={{ minWidth: 0, padding: '10px 6px', fontSize: 12 }} onClick={() => nav.push('create-order', { eventId: event.id, eventName: event.name, eventDate: eventDateValue(event), singles: true })}><Icon name="receipt" size={14} />Sell Singles</button>
          </div>

          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-sm)', marginBottom: 12 }}>
            <div className="eyebrow" style={{ padding: '11px 12px', borderBottom: '1px solid var(--line-2)' }}>Event Case Stock</div>
            {allocatedRows.length ? allocatedRows.map((row, index) => (
              <div key={`${row.skuId}-${row.sourceLocationId}-${row.lot}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: index ? '1px solid var(--line-2)' : 'none' }}>
                <FlavorChip sku={rpSkuItem(row.skuId) || row.skuId} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 13 }}>{rpSkuDisplayName(rpSkuItem(row.skuId) || row.skuId)}</div><div style={{ fontSize: 11, color: 'var(--faint)' }}>{eventLocationName(row.sourceLocationId)} - {row.lot}</div></div>
                <span className="mono" style={{ fontWeight: 800 }}>{Number(row.quantity || 0)} cs</span>
              </div>
            )) : <div style={{ padding: 14, color: 'var(--faint)', fontSize: 12.5 }}>No checked-in event cases yet.</div>}
          </div>

          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
            <div className="eyebrow" style={{ padding: '11px 12px', borderBottom: '1px solid var(--line-2)' }}>Open Loose Cans</div>
            {openRows.length ? openRows.map((batch, index) => (
              <div key={batch.id || index} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: index ? '1px solid var(--line-2)' : 'none' }}>
                <FlavorChip sku={rpSkuItem(batch.canSkuId) || batch.canSkuId} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 13 }}>{rpSkuDisplayName(rpSkuItem(batch.canSkuId) || batch.canSkuId)}</div><div style={{ fontSize: 11, color: 'var(--faint)' }}>{eventLocationName(batch.sourceLocationId)} - {batch.lot}</div></div>
                <span className="mono" style={{ fontWeight: 800, color: batch.remainingCans ? 'var(--ok)' : 'var(--faint)' }}>{batch.remainingCans} can</span>
              </div>
            )) : <div style={{ padding: 14, color: 'var(--faint)', fontSize: 12.5 }}>No cases have been opened into loose cans.</div>}
          </div>
        </MSection>
      </div>
    </div>
  );
};

MOBILE_SCREENS['event-new'] = function MobileEventNew({ nav, role }) {
  const today = typeof rpTodayYmd === 'function' ? rpTodayYmd({ territory: rpFieldTerritory(role) }) : new Date().toISOString().slice(0, 10);
  const micro = rpFieldMicroWarehouse(role);
  const [name, setName] = useState('');
  const [venueName, setVenueName] = useState('');
  const [territory, setTerritory] = useState(rpFieldTerritory(role) || '');
  const [eventType, setEventType] = useState('Sampling');
  const [status, setStatus] = useState('booked');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [sourceLocationId, setSourceLocationId] = useState((micro && micro.id) || (eventLocationOptions()[0] || {}).id || '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sourceOptions = mobileStockLocationOptions(role, sourceLocationId, { includeMicro: true });
  const valid = name.trim() && startDate && endDate && endDate >= startDate;
  const submit = async () => {
    if (busy || !valid) return;
    setBusy(true);
    setError('');
    const profile = rpAuthProfile ? rpAuthProfile() : {};
    const payload = {
      name: name.trim(),
      venueName: venueName.trim(),
      territory,
      eventType,
      status,
      eventDate: startDate,
      endDate,
      defaultSourceLocationId: sourceLocationId || undefined,
      notes,
      team: (profile.id || rpFieldUserName(role)) ? [{ userId: profile.id || rpFieldUserName(role), role: 'lead' }] : []
    };
    try {
      let savedId = '';
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        savedId = `EV-DEMO-${Date.now().toString().slice(-6)}`;
        RP.EVENTS = [{ id: savedId, ...payload, closeoutStatus: 'not_started' }, ...(RP.EVENTS || [])];
        if (payload.team.length) RP.EVENT_TEAM = [{ eventId: savedId, userId: payload.team[0].userId, name: rpDisplayName ? rpDisplayName() : rpFieldUserName(role), role: 'Lead' }, ...(RP.EVENT_TEAM || [])];
        toast('Event created', 'ok');
      } else {
        const response = await rpApi('/api/events/upsert', { method: 'POST', body: JSON.stringify(payload) });
        savedId = response.event && response.event.id || response.id || '';
        await rpRefreshAfterMutation('Event saved');
        toast('Event created', 'ok');
      }
      nav.reset(savedId ? 'event-detail' : 'rep-events', savedId ? { id: savedId } : {});
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not create event.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="New Event" sub="Create a field event" nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>{error}</div>}
        <Lbl>Event Name</Lbl>
        <input className="field" value={name} onChange={e => setName(e.target.value)} placeholder="Squamish Pop-up" style={{ marginBottom: 14 }} />
        <Lbl>Venue</Lbl>
        <input className="field" value={venueName} onChange={e => setVenueName(e.target.value)} placeholder="Store, festival, venue..." style={{ marginBottom: 14 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 14 }}>
          <div><Lbl>Start</Lbl><input className="field" type="date" value={startDate} onChange={e => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} /></div>
          <div><Lbl>End</Lbl><input className="field" type="date" value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)} /></div>
        </div>
        <Lbl>Status</Lbl>
        <select className="field" value={status} onChange={e => setStatus(e.target.value)} style={{ marginBottom: 14 }}>
          {['lead', 'evaluating', 'booked', 'needs_staffing', 'staffed', 'inventory_planning', 'packed', 'in_progress'].map(value => <option key={value} value={value}>{eventStatusLabel(value)}</option>)}
        </select>
        <Lbl>Type</Lbl>
        <select className="field" value={eventType} onChange={e => setEventType(e.target.value)} style={{ marginBottom: 14 }}>
          {EVENT_TYPE_OPTIONS.map(value => <option key={value} value={value}>{value}</option>)}
        </select>
        <Lbl>Territory</Lbl>
        <input className="field" value={territory} onChange={e => setTerritory(e.target.value)} placeholder="Territory" style={{ marginBottom: 14 }} />
        <Lbl>Default Source</Lbl>
        <select className="field" value={sourceLocationId} onChange={e => setSourceLocationId(e.target.value)} style={{ marginBottom: 14 }}>
          {sourceOptions.map(option => <option key={option.v} value={option.v}>{option.l}</option>)}
        </select>
        <Lbl>Notes</Lbl>
        <textarea className="field" rows="3" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional setup, inventory, or staffing notes" style={{ resize: 'none' }} />
      </div>
      <MActionBar>
        <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.pop()}>Cancel</button>
        <button className="btn btn-lg btn-primary" disabled={!valid || busy} style={{ flex: 1, opacity: valid && !busy ? 1 : 0.55 }} onClick={submit}><Icon name="check" size={16} />{busy ? 'Saving...' : 'Create'}</button>
      </MActionBar>
    </div>
  );
};

MOBILE_SCREENS['event-add-cases'] = function MobileEventAddCases({ nav, role, id }) {
  const event = eventRows().find(row => row.id === id);
  const requestStorageKey = eventAllocationRequestStorageKey(id);
  const [pendingRequest, setPendingRequest] = useState(() => eventLoadPendingActionRequest(requestStorageKey));
  const sourceOptions = mobileEventSourceOptionsForAdd(event, role);
  const initialSource = pendingRequest && pendingRequest.payload.sourceLocationId || (rpFieldMicroWarehouse(role) || {}).id || (event && event.defaultSourceLocationId) || (sourceOptions[0] || {}).id || '';
  const [sourceLocationId, setSourceLocationId] = useState(initialSource);
  const [purpose, setPurpose] = useState(() => pendingRequest && pendingRequest.payload.lines && pendingRequest.payload.lines[0] && pendingRequest.payload.lines[0].purpose || 'sell_singles');
  const [qty, setQty] = useState(() => pendingRequest && Array.isArray(pendingRequest.payload.lines)
    ? Object.fromEntries(pendingRequest.payload.lines.map(line => [`${line.skuId}|${line.lot}`, Number(line.quantity || 0)]))
    : {});
  const [notes, setNotes] = useState(() => pendingRequest && pendingRequest.payload.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!event) return <MEmpty icon="calendar" title="Event not found" sub="Refresh events and try again." action={<button className="btn btn-primary btn-sm" onClick={() => nav.pop()}>Back</button>} />;
  if (!eventCanAllocateInventory(event)) return <MEmpty icon="package" title="Inventory is locked" sub={`Cases cannot be added while this event is ${eventStatusLabel(event.status)}.`} action={<button className="btn btn-primary btn-sm" onClick={() => nav.pop()}>Back</button>} />;
  const availableRows = eventSourceCaseHoldings(sourceLocationId);
  const selectedLines = availableRows
    .map(row => ({ row, quantity: Number(qty[eventHoldingKey(row)] || 0) }))
    .filter(item => item.quantity > 0)
    .map(item => ({ lot: item.row.lot, purpose, quantity: item.quantity, skuId: item.row.skuId, unit: 'case' }));
  const total = selectedLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const setLineQty = (key, value) => setQty(prev => ({ ...prev, [key]: value }));
  const submit = async () => {
    if (busy || !selectedLines.length || pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        toast('Event case check-in posts to the live ledger only.', 'info');
        nav.pop();
      } else {
        const payload = { eventId: event.id, sourceLocationId, lines: selectedLines, notes };
        const clientRequestId = eventPreparePendingActionRequest(requestStorageKey, payload);
        const preparedRequest = eventLoadPendingActionRequest(requestStorageKey);
        setPendingRequest(preparedRequest);
        const response = await rpApi('/api/events/allocate', {
          method: 'POST',
          body: JSON.stringify({ clientRequestId, ...payload })
        });
        if (!eventReceiptMatchesPending(response && response.receipt, preparedRequest, event.id)) {
          throw new Error('The allocation may have committed, but its actor-bound receipt could not be verified. Recover this exact saved request.');
        }
        await rpRefreshAfterMutation('Event cases added');
        eventClearPendingActionRequest(requestStorageKey, clientRequestId);
        setPendingRequest(null);
        toast(`Cases added to event stock - receipt ${response.receipt.id}`, 'ok');
        nav.pop();
      }
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not add cases to the event.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  const recoverPending = async () => {
    if (busy || !pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/events/allocate', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId: pendingRequest.clientRequestId, ...pendingRequest.payload })
      });
      if (!eventReceiptMatchesPending(response && response.receipt, pendingRequest, event.id)) {
        throw new Error('The immutable allocation receipt could not be recovered. Keep this exact request and ask Operations to inspect it.');
      }
      await rpRefreshAfterMutation('Event allocation receipt recovered');
      eventClearPendingActionRequest(requestStorageKey, pendingRequest.clientRequestId);
      setPendingRequest(null);
      toast(`Allocation receipt recovered - ${response.receipt.id}`, 'ok');
      nav.pop();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not recover the pending allocation.';
      setError(message);
      toast(message, 'warn');
    } finally { setBusy(false); }
  };
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Add Cases" sub={event.name} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {pendingRequest && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}><div>An actor-bound allocation request is saved on this device. Its payload cannot be replaced until the exact receipt is recovered.</div><button className="btn btn-sm" disabled={busy} onClick={recoverPending} style={{ marginTop: 8 }}>Recover Exact Allocation</button></div>}
        {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>{error}</div>}
        <Lbl>Source</Lbl>
        <select className="field" value={sourceLocationId} onChange={e => { setSourceLocationId(e.target.value); setQty({}); }} style={{ marginBottom: 14 }}>
          {sourceOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
        </select>
        <Lbl>Purpose</Lbl>
        <select className="field" value={purpose} onChange={e => setPurpose(e.target.value)} style={{ marginBottom: 14 }}>
          {EVENT_PURPOSE_OPTIONS.map(value => <option key={value} value={value}>{eventPurposeLabel(value)}</option>)}
        </select>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {availableRows.map(row => {
            const key = eventHoldingKey(row);
            const sku = rpSkuItem(row.skuId) || row.skuId;
            const max = Math.floor(Number(row.qty || 0));
            const value = Number(qty[key] || 0);
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface)', border: '1px solid ' + (value ? 'var(--amber)' : 'var(--line)'), borderRadius: 12, padding: 12, boxShadow: 'var(--shadow-sm)' }}>
                <FlavorChip sku={sku} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 13.5 }}>{rpSkuDisplayName(sku)}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{row.lot || 'UNASSIGNED'} - {max.toLocaleString()} cs available</div>
                </div>
                <Stepper value={value} max={max} onChange={next => setLineQty(key, next)} />
              </div>
            );
          })}
          {!availableRows.length && <MEmpty icon="package" title="No case stock" sub="This source has no saleable case stock available." />}
        </div>
        <Lbl>Note</Lbl>
        <textarea className="field" rows="2" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional check-in note" style={{ resize: 'none', marginTop: 14 }} />
      </div>
      <MActionBar>
        <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.pop()}>Cancel</button>
        <button className="btn btn-lg btn-primary" disabled={!total || busy || Boolean(pendingRequest)} style={{ flex: 1, opacity: total && !busy && !pendingRequest ? 1 : 0.55 }} onClick={submit}><Icon name="check" size={16} />{busy ? 'Adding...' : `Add ${total} cs`}</button>
      </MActionBar>
    </div>
  );
};

MOBILE_SCREENS['event-open-cases'] = function MobileEventOpenCases({ nav, role, id }) {
  const event = eventRows().find(row => row.id === id);
  const requestStorageKey = eventOpenCaseRequestStorageKey(id);
  const [pendingRequest, setPendingRequest] = useState(() => eventLoadPendingOpenCaseRequest(requestStorageKey));
  const sourceOptions = event ? mobileEventAllocatedSourceOptions(event) : [];
  const [sourceLocationId, setSourceLocationId] = useState(pendingRequest && pendingRequest.payload.sourceLocationId || (sourceOptions[0] || {}).id || (event && event.defaultSourceLocationId) || '');
  const [assignedRepId, setAssignedRepId] = useState(pendingRequest && pendingRequest.payload.assignedRepId || (event ? mobileEventDefaultRepId(event, role) : ''));
  const [qty, setQty] = useState(() => pendingRequest && Array.isArray(pendingRequest.payload.lines)
    ? Object.fromEntries(pendingRequest.payload.lines.map(line => [`${line.caseSkuId}|${line.lot}`, Number(line.quantity || 0)]))
    : {});
  const [damaged, setDamaged] = useState(() => pendingRequest && Array.isArray(pendingRequest.payload.lines)
    ? Object.fromEntries(pendingRequest.payload.lines.map(line => [`${line.caseSkuId}|${line.lot}`, Number(line.damagedCans || 0)]))
    : {});
  const [notes, setNotes] = useState(pendingRequest && pendingRequest.payload.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!event) return <MEmpty icon="calendar" title="Event not found" sub="Refresh events and try again." action={<button className="btn btn-primary btn-sm" onClick={() => nav.pop()}>Back</button>} />;
  const repOptions = mobileEventRepOptions(event, role);
  const allocatedRows = eventAllocatedCaseHoldings(event, sourceLocationId);
  const availableRows = allocatedRows.filter(row => eventCaseCanSkuId(row.skuId));
  const unsupportedRows = allocatedRows.filter(row => !eventCaseCanSkuId(row.skuId));
  const selectedLines = availableRows
    .map(row => ({ row, quantity: Number(qty[eventHoldingKey(row)] || 0), damagedCans: Number(damaged[eventHoldingKey(row)] || 0) }))
    .filter(item => item.quantity > 0)
    .map(item => ({ caseSkuId: item.row.skuId, damagedCans: item.damagedCans, lot: item.row.lot, quantity: item.quantity }));
  const invalidDamage = selectedLines.some(line => line.damagedCans < 0 || line.damagedCans > line.quantity * EVENT_CASE_PACK_SIZE);
  const totalCases = selectedLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const totalSaleableCans = selectedLines.reduce((sum, line) => sum + Math.max(0, line.quantity * EVENT_CASE_PACK_SIZE - Number(line.damagedCans || 0)), 0);
  const setLineQty = (key, value) => setQty(prev => ({ ...prev, [key]: value }));
  const setDamagedCans = (key, value) => setDamaged(prev => ({ ...prev, [key]: value }));
  const submit = async () => {
    if (busy || !selectedLines.length || invalidDamage) return;
    setBusy(true);
    setError('');
    try {
      if (window.RP_AUTH && window.RP_AUTH.demo) {
        toast('Opening event cases posts to the live ledger only.', 'info');
        nav.pop();
      } else {
        const payload = {
          assignedRepId: assignedRepId || null,
          eventId: event.id,
          lines: selectedLines,
          notes,
          sourceLocationId
        };
        const clientRequestId = eventPreparePendingOpenCaseRequest(requestStorageKey, payload);
        const preparedRequest = eventLoadPendingOpenCaseRequest(requestStorageKey);
        setPendingRequest(preparedRequest);
        const response = await rpApi('/api/events/open-cases', {
          method: 'POST',
          body: JSON.stringify({ clientRequestId, ...payload })
        });
        const receipt = response && response.receipt;
        if (!eventReceiptMatchesPending(receipt, preparedRequest, event.id)) {
          throw new Error('The open-case write may have committed, but its immutable receipt could not be verified. Retry without changing the form to recover it safely.');
        }
        await rpRefreshAfterMutation('Event cases opened');
        eventClearPendingActionRequest(requestStorageKey, clientRequestId);
        setPendingRequest(null);
        toast(`Cases opened into loose cans - receipt ${receipt.id}`, 'ok');
        nav.pop();
      }
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not open event cases.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  const recoverPending = async () => {
    if (busy || !pendingRequest) return;
    setBusy(true);
    setError('');
    try {
      const response = await rpApi('/api/events/open-cases', {
        method: 'POST',
        body: JSON.stringify({ clientRequestId: pendingRequest.clientRequestId, ...pendingRequest.payload })
      });
      const receipt = response && response.receipt;
      if (!eventReceiptMatchesPending(receipt, pendingRequest, event.id)) {
        throw new Error('The immutable open-case receipt could not be recovered. Keep this request and ask Operations to inspect it.');
      }
      await rpRefreshAfterMutation('Event open-case receipt recovered');
      eventClearPendingActionRequest(requestStorageKey, pendingRequest.clientRequestId);
      setPendingRequest(null);
      toast(`Open-case receipt recovered - ${receipt.id}`, 'ok');
      nav.pop();
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not recover the pending open-case request.';
      setError(message);
      toast(message, 'warn');
    } finally { setBusy(false); }
  };
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Open Cases" sub={event.name} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {pendingRequest && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}><div>An actor-bound open-case request is saved on this device. Its payload cannot be replaced until the exact immutable receipt is recovered.</div><button className="btn btn-sm" disabled={busy} onClick={recoverPending} style={{ marginTop: 8 }}>Recover Exact Request</button></div>}
        {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>{error}</div>}
        {invalidDamage && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>Damaged cans cannot exceed opened cans.</div>}
        <Lbl>Event Stock Source</Lbl>
        <select className="field" value={sourceLocationId} onChange={e => { setSourceLocationId(e.target.value); setQty({}); setDamaged({}); }} style={{ marginBottom: 14 }}>
          {sourceOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
        </select>
        <Lbl>Rep Holding Open Cans</Lbl>
        <select className="field" value={assignedRepId} onChange={e => setAssignedRepId(e.target.value)} style={{ marginBottom: 14 }}>
          <option value="">Unassigned</option>
          {repOptions.map(option => <option key={option.id} value={option.id}>{option.label} - {option.role}</option>)}
        </select>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {availableRows.map(row => {
            const key = eventHoldingKey(row);
            const sku = rpSkuItem(row.skuId) || row.skuId;
            const max = Math.floor(Number(row.quantity || 0));
            const value = Number(qty[key] || 0);
            const damagedValue = String(damaged[key] || '');
            const saleable = Math.max(0, value * EVENT_CASE_PACK_SIZE - Number(damagedValue || 0));
            return (
              <div key={key} style={{ background: 'var(--surface)', border: '1px solid ' + (value ? 'var(--amber)' : 'var(--line)'), borderRadius: 12, padding: 12, boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <FlavorChip sku={sku} size={32} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 13.5 }}>{rpSkuDisplayName(sku)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{row.lot || 'UNASSIGNED'} - {max.toLocaleString()} cs checked in</div>
                  </div>
                  <Stepper value={value} max={max} onChange={next => setLineQty(key, next)} />
                </div>
                {value > 0 && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginTop: 10, alignItems: 'end' }}>
                  <div>
                    <Lbl>Damaged Cans</Lbl>
                    <input className="field" type="number" min="0" max={value * EVENT_CASE_PACK_SIZE} step="1" value={damagedValue} onChange={e => setDamagedCans(key, e.target.value)} placeholder="0" />
                  </div>
                  <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '9px 10px' }}>
                    <div className="eyebrow">Saleable</div>
                    <div className="disp" style={{ fontSize: 20 }}>{saleable}<span style={{ fontSize: 11, marginLeft: 4, color: 'var(--muted)' }}>cans</span></div>
                  </div>
                </div>}
              </div>
            );
          })}
          {!availableRows.length && <MEmpty icon="package" title="No openable cases" sub={allocatedRows.length ? 'Mixer packs stay as case stock in this workflow.' : 'Add event cases before opening them for singles.'} action={!allocatedRows.length ? <button className="btn btn-primary btn-sm" onClick={() => nav.push('event-add-cases', { id: event.id })}><Icon name="plus" size={15} />Add Cases</button> : null} />}
          {unsupportedRows.length > 0 && <div style={{ color: 'var(--faint)', fontSize: 12.5, lineHeight: 1.4 }}>Mixer packs remain sealed case stock for now. Single-can opening supports single-flavour 12-packs.</div>}
        </div>
        <Lbl>Note</Lbl>
        <textarea className="field" rows="2" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional opening note" style={{ resize: 'none', marginTop: 14 }} />
      </div>
      <MActionBar>
        <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.pop()}>Cancel</button>
        <button className="btn btn-lg btn-primary" disabled={!totalCases || invalidDamage || busy || Boolean(pendingRequest)} style={{ flex: 1, opacity: totalCases && !invalidDamage && !busy && !pendingRequest ? 1 : 0.55 }} onClick={submit}><Icon name="check" size={16} />{busy ? 'Opening...' : `${totalSaleableCans} cans`}</button>
      </MActionBar>
    </div>
  );
};


/* ===== mobile_field.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : Warehouse + Dispatcher field screens
   ============================================================ */

const MOBILE_PICK_QUEUE_STATUSES = new Set(['Allocated', 'Approved', 'Picked']);
const MOBILE_DISPATCH_ROUTE_STATUSES = new Set(['Approved', 'Allocated', 'Picked', 'Dispatched', 'Delivered']);
const MOBILE_DISPATCH_ACTION_STATUSES = new Set(['Picked', 'Dispatched']);

function mobileOrderTotals(order) {
  if (!order || !Array.isArray(order.lines)) return { cases: 0, total: 0 };
  try {
    return orderTotals(order);
  } catch {
    let cases = 0;
    order.lines.forEach(line => {
      const qty = Number(line.qty || 0);
      if (Number.isFinite(qty)) cases += qty;
    });
    return { cases, total: Number(order.total || 0) };
  }
}

function mobileCustomerName(order, fallback) {
  if (!order) return fallback || 'Customer';
  const customer = RP.cust(order.cust);
  return (customer && customer.name) || fallback || order.cust || 'Customer';
}

function mobileDisplayOrderStatus(status) {
  if (status === 'Dispatched') return 'In Transit';
  if (status === 'Picked') return 'Ready';
  return status || 'Scheduled';
}

function mobileDeliveryLines(order) {
  if (!order || !Array.isArray(order.lines) || !order.lines.length) {
    return [
      { flavor: 'ginger-citrus', name: 'Ginger Citrus', qty: 12 },
      { flavor: 'key-lime', name: 'Key Lime', qty: 8 },
      { flavor: 'peach-mango', name: 'Peach Mango', qty: 4 }
    ];
  }

  return order.lines.map(line => {
    const sku = RP.sku(line.sku);
    return {
      flavor: rpSkuVisualFlavor(sku),
      name: rpSkuDisplayName(sku || line.sku),
      qty: Number(line.qty || 0)
    };
  });
}

function mobileDispatchStops() {
  const assignedJobs = (RP.DISPATCH || []).filter(job => job && job.lane && job.lane !== 'unassigned');
  if (assignedJobs.length) {
    return assignedJobs
      .slice()
      .sort((a, b) => Number(a.stopSequence || 0) - Number(b.stopSequence || 0))
      .map(job => {
        const order = (RP.ORDERS || []).find(x => x.id === job.id);
        const status = (order && order.status) || job.status || 'Assigned';
        const totals = order ? mobileOrderTotals(order) : { cases: Number(job.cases || 0), total: 0 };
        return {
          id: job.id,
          cust: mobileCustomerName(order, job.label),
          window: job.window || (order && order.date) || 'Unscheduled',
          cases: totals.cases || Number(job.cases || 0),
          rawStatus: status,
          status: mobileDisplayOrderStatus(status),
          lane: job.lane,
          laneName: job.laneName || ((RP.LANES || []).find(l => l.id === job.lane) || {}).name || 'Assigned Route',
          driver: job.driver || ((RP.LANES || []).find(l => l.id === job.lane) || {}).driver || '',
          routeId: job.routeId || null,
          routeType: job.routeType || 'Ranch Delivery',
          order
        };
      });
  }

  return (RP.ORDERS || [])
    .filter(order => MOBILE_DISPATCH_ROUTE_STATUSES.has(order.status) && order.delivery !== 'Customer Pickup')
    .map(order => {
      const totals = mobileOrderTotals(order);
      return {
        id: order.id,
        cust: mobileCustomerName(order),
        window: order.date || 'Unscheduled',
        cases: totals.cases,
        rawStatus: order.status,
        status: mobileDisplayOrderStatus(order.status),
        lane: null,
        laneName: 'Unassigned Route',
        driver: '',
        routeId: null,
        routeType: order.delivery || 'Ranch Delivery',
        order
      };
    });
}

function mobileDispatchStopsFromDirectory(items) {
  return (Array.isArray(items) ? items : [])
    .map(rpDispatchJobFromItem)
    .filter(Boolean)
    .map(job => {
      const order = job.order;
      return {
        id: job.id,
        cust: job.label || mobileCustomerName(order),
        window: job.window || (order && order.date) || 'Unscheduled',
        cases: Number(job.cases || 0),
        rawStatus: job.status || (order && order.status) || 'Assigned',
        status: mobileDisplayOrderStatus(job.status || (order && order.status)),
        lane: job.lane,
        laneName: job.laneName || 'Assigned Route',
        driver: job.driver || '',
        routeDate: job.routeDate || null,
        routeId: job.routeId || null,
        routeStatus: job.routeStatus || null,
        routeType: job.routeType || (order && order.delivery) || 'Ranch Delivery',
        stopSequence: Number(job.stopSequence || 0),
        stopStatus: job.stopStatus || '',
        order
      };
    });
}

function MobileDispatchPager({ directory, label = 'route stops' }) {
  if (!directory || !directory.isLive) return null;
  if (directory.loadingMore) return <div style={{ color: 'var(--muted)', fontSize: 12.5, textAlign: 'center', padding: '10px 0' }}>Loading more {label}...</div>;
  if (directory.page && directory.page.hasMore) {
    return <button className="btn" style={{ width: '100%', marginTop: 10 }} onClick={directory.loadMore}>Load More {label}</button>;
  }
  if (directory.items && directory.items.length) {
    return <div style={{ color: 'var(--faint)', fontSize: 11.5, textAlign: 'center', padding: '10px 0' }}>All {directory.items.length.toLocaleString()} {label} loaded.</div>;
  }
  return null;
}

function MobileDispatchError({ directory }) {
  if (!directory || !directory.isLive || !directory.error || directory.loadingMore) return null;
  return (
    <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 12, padding: 13, marginBottom: 12, fontSize: 12.5, fontWeight: 600 }}>
      <div>{directory.error}</div>
      <button className="btn btn-sm" style={{ marginTop: 9 }} onClick={directory.retry}>Retry</button>
    </div>
  );
}

function mobileRouteLoadLines(stops) {
  const bySku = new Map();
  (stops || []).forEach(stop => {
    const order = stop.order || (RP.ORDERS || []).find(x => x.id === stop.id);
    (order && order.lines ? order.lines : []).forEach(line => {
      const skuId = line.sku;
      if (!skuId) return;
      bySku.set(skuId, (bySku.get(skuId) || 0) + Number(line.qty || 0));
    });
  });
  return Array.from(bySku.entries()).map(([skuId, expected]) => {
    const sku = RP.sku(skuId);
    return {
      expected,
      name: rpSkuDisplayName(sku || skuId),
      skuId
    };
  });
}

async function mobilePersistOrderStatus(order, status, note) {
  if (!order || !order.id) throw new Error('Order was not found.');

  if (!rpIsDemoMode()) {
    const response = await rpApi('/api/orders/status', {
      method: 'POST',
      body: JSON.stringify({ id: order.id, status, note })
    });
    const savedOrder = rpUpsertOrder(rpOrderFromApi(response, { ...order, status }));
    Object.assign(order, savedOrder);
    await rpRefreshAfterMutation(`Order ${order.id} updated`);
    const refreshed = RP.ORDERS.find(x => x.id === order.id);
    if (refreshed) Object.assign(order, refreshed);
    return order;
  }

  order.status = status;
  if (note) order.note = [order.note, note].filter(Boolean).join('\n\n');
  return order;
}

function mobileSkuOptions() {
  if (typeof rpInventorySkus === 'function') return rpInventorySkus();
  return rpOrderableSkus().filter(sku => /^RP-(12|MIX)-/i.test(String(sku && sku.id || '')));
}

function mobileInventorySkuMatches(left, right) {
  const normalize = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId : value => value;
  return normalize(left) === normalize(right);
}

function mobileWarehouseLocations() {
  const defaultLocation = typeof rpFieldDefaultLocation === 'function' ? rpFieldDefaultLocation('warehouse') : null;
  const live = (RP.LOCATIONS || []).filter(loc => loc && loc.id && loc.type !== 'Micro-Warehouse' && /warehouse|production facility|3pl hub/i.test(loc.type || loc.name || ''));
  if (defaultLocation && defaultLocation.type !== 'Micro-Warehouse') {
    return [defaultLocation, ...live.filter(loc => loc.id !== defaultLocation.id)];
  }
  return live.length ? live : (RP.LOCATIONS || []).filter(loc => loc && loc.id).slice(0, 4);
}

function mobileInventoryLocations(role) {
  const defaultLocation = typeof rpFieldDefaultLocation === 'function' ? rpFieldDefaultLocation(role) : null;
  const micro = rpFieldMicroWarehouse(role);
  const list = (RP.LOCATIONS || []).filter(loc => loc && loc.id);
  if (defaultLocation && !list.find(loc => loc.id === defaultLocation.id)) return [defaultLocation, ...list];
  if (micro && !list.find(loc => loc.id === micro.id)) return [micro, ...list];
  return list.length ? list : [{ id: 'WH-SQ', name: 'Squamish Warehouse', type: 'Warehouse' }];
}

function mobileDefaultInventoryLocation(role) {
  const authRole = rpNormalizeRole(rpAuthProfile().role || role);
  const defaultLocation = typeof rpFieldDefaultLocation === 'function' ? rpFieldDefaultLocation(role) : null;
  if (defaultLocation) return defaultLocation.id;
  if (['rep', 'partner'].includes(authRole)) {
    const micro = rpFieldMicroWarehouse(role);
    if (micro) return micro.id;
  }
  const warehouse = mobileWarehouseLocations()[0];
  return warehouse ? warehouse.id : 'WH-SQ';
}

const MOBILE_FIELD_RECEIPT_SOURCE_TYPES = new Set(['Main Warehouse', 'Micro-Warehouse', 'Distributor', 'FBA', 'Production Facility', '3PL Hub', '3PL Warehouse']);

function mobileFieldReceiptSourceLocations(role, destinationId) {
  const destination = RP.loc(destinationId);
  const territory = destination?.territory || rpFieldTerritory(role);
  const sources = mobileInventoryLocations(role)
    .filter(loc => loc && loc.id && loc.id !== destinationId && loc.active !== false && MOBILE_FIELD_RECEIPT_SOURCE_TYPES.has(loc.type));
  const nearby = sources.filter(loc =>
    loc.type === 'Main Warehouse'
    || loc.type === 'Distributor'
    || !territory
    || loc.territory === territory
    || loc.territory === 'All'
    || (typeof rpAreaMatches === 'function' && rpAreaMatches([loc.territory], territory))
  );
  return nearby.length ? nearby : sources;
}

function mobileFieldReceiptDefaultSourceId(role, destinationId) {
  const destination = RP.loc(destinationId);
  const sources = mobileFieldReceiptSourceLocations(role, destinationId);
  const sameTerritoryWarehouse = sources.find(loc => loc.type === 'Main Warehouse' && loc.territory === destination?.territory);
  const warehouse = sameTerritoryWarehouse || sources.find(loc => loc.type === 'Main Warehouse');
  return (warehouse || sources[0] || { id: '' }).id;
}

function mobileLotsFor(locationId, skuId) {
  const canonicalSkuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(skuId) : skuId;
  const lots = Array.from(new Set((RP.INV || [])
    .filter(row => {
      return String(row.unit || 'case').toLowerCase() !== 'can' && (!locationId || row.loc === locationId) && (!canonicalSkuId || mobileInventorySkuMatches(row.sku, canonicalSkuId));
    })
    .map(row => row.lot)
    .filter(Boolean)));
  return lots.length ? lots : ['UNASSIGNED'];
}

const MOBILE_GIVEAWAY_STAFF_REASON = 'Staff / rep promo stock';
const MOBILE_GIVEAWAY_REASONS = ['New account sample', 'Reorder conversion', 'Promo placement', 'Event tasting', 'Staff trial', MOBILE_GIVEAWAY_STAFF_REASON, 'Customer make-good', 'Other'];
const MOBILE_GIVEAWAY_STOCK_STATUSES = new Set(['available', 'fronted to rep', 'purchased by rep', 'sample', 'sample / demo']);

function mobileGiveawayLocations(role) {
  const authRole = rpNormalizeRole(rpAuthProfile().role || role);
  const allLocations = mobileInventoryLocations(role);
  const fieldLocations = allLocations.filter(loc => loc.type === 'Micro-Warehouse');
  if (['rep', 'partner'].includes(authRole) && fieldLocations.length) return fieldLocations;
  return allLocations;
}

function mobileGiveawayAvailableCases(skuId, locationId) {
  return typeof rpInventoryGiveawayCases === 'function'
    ? rpInventoryGiveawayCases(skuId, locationId)
    : rpInventoryAvailableCases(skuId, locationId);
}

function mobileGiveawaySkus(locationId) {
  const ids = Array.from(new Set((RP.INV || [])
    .filter(row => row && String(row.unit || 'case').toLowerCase() !== 'can' && row.loc === locationId)
    .map(row => row.sku)
    .filter(Boolean)));
  return rpInventorySkus({ includeIds: ids }).filter(sku => mobileGiveawayAvailableCases(sku.id, locationId) > 0);
}

function mobileGiveawayNewLine(locationId) {
  const sku = mobileGiveawaySkus(locationId)[0];
  return { quantity: 1, skuId: sku ? sku.id : '' };
}

function mobileGiveawayProspects(role) {
  const name = rpFieldUserName(role);
  return (RP.LEADS || [])
    .filter(lead => lead && lead.id && !['won', 'lost'].includes(String(lead.stage || '').toLowerCase()))
    .filter(lead => rpHasLiveAuth() || lead.rep === name || rpFieldCoversTerritory(role, lead.territory));
}

function mobileGiveawayRecipientOptions(role) {
  const customers = rpFieldCustomers(role)
    .filter(customer => customer && customer.id)
    .map(customer => ({
      detail: [customer.type, customer.territory].filter(Boolean).join(' - '),
      id: customer.id,
      key: `customer:${customer.id}`,
      name: customer.name || customer.id,
      type: 'customer'
    }));
  const prospects = mobileGiveawayProspects(role)
    .map(lead => ({
      detail: ['Prospect', lead.stage, lead.territory].filter(Boolean).join(' - '),
      id: lead.id,
      key: `prospect:${lead.id}`,
      name: lead.name || lead.company || lead.id,
      type: 'prospect'
    }));
  return [...customers, ...prospects].sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
}

function mobileApplyDemoGiveaway(input) {
  const ref = input.ref || 'GW-' + Date.now().toString().slice(-8);
  const location = RP.loc(input.locationId);
  const user = typeof rpDisplayName === 'function' ? rpDisplayName() : 'Ranch Power';
  const lineItems = Array.isArray(input.lineItems) && input.lineItems.length ? input.lineItems : [{ quantity: input.quantity, skuId: input.skuId }];
  lineItems.forEach(line => {
    let remaining = Math.max(0, Math.floor(Number(line.quantity || 0) || 0));
    (RP.INV || [])
      .filter(row => row && String(row.unit || 'case').toLowerCase() !== 'can' && row.loc === input.locationId && mobileInventorySkuMatches(row.sku, line.skuId) && MOBILE_GIVEAWAY_STOCK_STATUSES.has(String(row.status || 'Available').toLowerCase()))
      .forEach(row => {
        if (remaining <= 0) return;
        const used = Math.min(remaining, Math.max(0, Number(row.qty || 0)));
        if (!(used > 0)) return;
        row.qty = Number(row.qty || 0) - used;
        remaining -= used;
        RP.LEDGER.unshift({
          date: new Date().toISOString(),
          from: location ? location.name : input.locationId,
          lot: row.lot || 'UNASSIGNED',
          note: [`Recipient: ${input.recipientName}.`, `Type: ${input.recipientType || 'other'}.`, `Reason: ${input.reason}.`, input.expectedOutcome ? `Expected outcome: ${input.expectedOutcome}.` : '', input.followUpDate ? `Follow-up: ${input.followUpDate}.` : '', input.notes ? `Notes: ${input.notes}` : ''].filter(Boolean).join(' '),
          qty: used,
          ref,
          sku: line.skuId,
          to: '',
          type: 'Giveaway',
          user
        });
      });
  });
  RP.INV = (RP.INV || []).filter(row => Number(row.qty || 0) > 0);
  return ref;
}

/* ---------- WAREHOUSE HOME ---------- */
MOBILE_SCREENS['wh-home'] = function WhHome({ nav }) {
  const who = useRpIdentity('warehouse');
  const warehouseName = rpFieldMicroWarehouse('warehouse')?.name || rpFieldTerritory('warehouse') || 'Warehouse';
  return (
    <div className="rp-fade">
      <div style={{ background: 'var(--navy)', color: '#fff', padding: '4px 18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}><Logo color="#fff" /><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><button onClick={() => nav.push('notifications')} style={{ border: 'none', background: 'oklch(1 0 0 / 0.12)', width: 34, height: 34, borderRadius: 10, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}><Icon name="bell" size={18} /><span style={{ position: 'absolute', top: 7, right: 8, width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)' }} /></button><button onClick={() => nav.push('settings')} style={{ border: 'none', background: 'none', padding: 0 }}><Avatar name={who.name} color="var(--amber)" /></button></div></div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>{warehouseName}</div>
        <div className="disp" style={{ fontSize: 24, textTransform: 'uppercase' }}>Today's Work</div>
        <div style={{ display: 'flex', gap: 4, marginTop: 14, background: 'oklch(1 0 0 / 0.07)', borderRadius: 14, padding: '14px 12px' }}>
          <MStat label="Picks" value="14" accent="#fff" /><span style={{ width: 1, background: 'oklch(1 0 0 / 0.12)' }} />
          <MStat label="Receipts" value="3" accent="#fff" /><span style={{ width: 1, background: 'oklch(1 0 0 / 0.12)' }} />
          <MStat label="Transfers" value="5" accent="#fff" /><span style={{ width: 1, background: 'oklch(1 0 0 / 0.12)' }} />
          <MStat label="Counts" value="2" accent="#fff" />
        </div>
      </div>
      <MSection title="Exceptions">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[{ t: '1 receiving discrepancy', m: 'SH-330 · 8 expected, 7 received', tone: 'warn' }, { t: '3 damaged cases pending write-off', m: 'Ginger Citrus · RP-GC-001', tone: 'danger' }].map((e, i) => {
            const [fg, bg] = statusColors(e.tone === 'danger' ? 'Damaged' : 'Pending');
            return <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 13px', boxShadow: 'var(--shadow-sm)' }}>
              <span style={{ width: 34, height: 34, borderRadius: 10, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="alert" size={17} /></span>
              <div style={{ flex: 1 }}><div style={{ fontSize: 13.5, fontWeight: 600 }}>{e.t}</div><div className="mono" style={{ fontSize: 11, color: 'var(--faint)' }}>{e.m}</div></div>
            </div>;
          })}
        </div>
      </MSection>
      <MSection title="Tasks">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[{ i: 'package', l: 'Pick Orders', m: '14 open', go: () => nav.tab('wh-pick') }, { i: 'download', l: 'Receive Stock', m: '3 inbound', go: () => nav.tab('wh-receive') }, { i: 'layers', l: 'Transfer Stock', m: '5 queued', go: () => nav.push('transfer-stock') }, { i: 'refresh', l: 'Cycle Count', m: '2 due', go: () => nav.push('cycle-count', { loc: 'WH-SQ' }) }, { i: 'alert', l: 'Report Damage / Loss', go: () => nav.push('damage-report') }].map(t => (
            <button key={t.l} onClick={t.go} style={{ display: 'flex', alignItems: 'center', gap: 13, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '14px 15px', textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
              <span style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={t.i} size={18} /></span>
              <span style={{ flex: 1, fontSize: 14.5, fontWeight: 600 }}>{t.l}</span>
              {t.m && <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{t.m}</span>}
              <Icon name="chevR" size={17} style={{ color: 'var(--faint)' }} />
            </button>
          ))}
        </div>
      </MSection>
      <div style={{ height: 20 }} />
    </div>
  );
};

/* ---------- PICK LIST + PICK ORDER ---------- */
MOBILE_SCREENS['wh-pick'] = function WhPickList({ nav }) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const serverPage = useRpServerPagedRows({ mode: 'orders', limit: 50, tab: 'pickqueue' });
  const list = live
    ? serverPage.items.map(item => ({ c: item.customer, o: item.order })).filter(row => row.o && row.o.id)
    : RP.ORDERS.filter(o => MOBILE_PICK_QUEUE_STATUSES.has(o.status)).map(o => ({ c: RP.cust(o.cust), o }));
  const openOrder = order => {
    if (live) rpUpsertOrder(order);
    nav.push('pick-order', { id: order.id });
  };
  return (
    <div className="rp-fade">
      <MHeader title="Pick Queue" sub="Squamish Warehouse" />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {live && serverPage.initialLoading && <div data-pick-queue-loading="true" style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 12, padding: 24, textAlign: 'center', color: 'var(--muted)' }}><Icon name="refresh" size={20} /><div style={{ marginTop: 7, fontWeight: 700, color: 'var(--ink)' }}>Loading the complete pick queue...</div></div>}
        {live && !serverPage.initialLoading && !serverPage.loaded && serverPage.error && <div data-pick-queue-error="true" role="alert" style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 12, padding: 13, fontSize: 12.5, fontWeight: 700 }}>{serverPage.error}<button type="button" className="btn" onClick={serverPage.retry} style={{ width: '100%', justifyContent: 'center', marginTop: 9 }}>Try Again</button></div>}
        {list.map(({ c, o }) => { const t = mobileOrderTotals(o); return (
          <button key={o.id} onClick={() => openOrder(o)} disabled={o.status === 'Picked'} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', opacity: o.status === 'Picked' ? 0.55 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="mono" style={{ fontWeight: 600, fontSize: 13 }}>{o.id}</span><Pill status={o.status} />
            </div>
            <div style={{ fontWeight: 600, fontSize: 14.5, marginTop: 5 }}>{c ? c.name : o.cust}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 9 }}>
              <div style={{ display: 'flex', gap: 4 }}>{o.lines.slice(0, 5).map((l, i) => { const sku = RP.sku(l.sku); const flavor = sku && RP.flavor(sku.flavor) ? sku.flavor : 'blue-raspberry'; return <FlavorChip key={i} flavorId={flavor} size={22} />; })}</div>
              <span className="mono" style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>{t.cases} cases</span>
            </div>
          </button>
        ); })}
        {(!live || serverPage.loaded) && !list.length && <div data-pick-queue-empty="true" style={{ border: '1px dashed var(--line)', background: 'var(--surface)', borderRadius: 12, padding: 24, textAlign: 'center', color: 'var(--faint)' }}><div style={{ color: 'var(--ink)', fontWeight: 800 }}>Pick queue clear</div><div style={{ marginTop: 3, fontSize: 12.5 }}>No approved, allocated, or picked orders are waiting.</div></div>}
        {live && serverPage.loaded && <div data-pick-queue-pagination="true" style={{ border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}><RpServerLoadMore {...serverPage} label="orders" onLoadMore={serverPage.loadMore} onRetry={serverPage.loadMore} /></div>}
      </div>
    </div>
  );
};

MOBILE_SCREENS['pick-order'] = function PickOrder({ nav, id }) {
  const exactOrder = useRpExactOrder({ orderId: id });
  const o = exactOrder.order;
  const lines = o && Array.isArray(o.lines) ? o.lines : [];
  const c = o ? RP.cust(o.cust) : null;
  const customerName = c ? c.name : mobileCustomerName(o);
  const [picked, setPicked] = useState(() => lines.map(() => 0));
  const [chep, setChep] = useState(1);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (picked.length !== lines.length) setPicked(lines.map(() => 0));
  }, [id, lines.length, picked.length]);
  const allDone = !!o && lines.length > 0 && picked.length === lines.length && picked.every((p, i) => p >= lines[i].qty);
  const completePick = async () => {
    if (!o || !allDone || busy) return;
    setBusy(true);
    setError('');
    try {
      await mobilePersistOrderStatus(o, 'Picked', `Picked ${mobileOrderTotals(o).cases} cases. CHEP pallets out: ${chep}.`);
      setDone(true);
      toast(`${o.id} marked Picked`, 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Pick could not be saved.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  if (exactOrder.loading) return (
    <div className="rp-fade" data-exact-order-loading="true">
      <MHeader title="Pick Order" sub={`Loading ${id}`} nav={nav} back />
      <MEmpty icon="refresh" title="Loading pick order" sub="Fetching the complete order and line details." />
    </div>
  );
  if (exactOrder.error) return (
    <div className="rp-fade" data-exact-order-error="true">
      <MHeader title="Pick Order" sub="Order unavailable" nav={nav} back />
      <MEmpty icon="alert" title="Pick order could not load" sub={exactOrder.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactOrder.retry}>Try Again</button>} />
    </div>
  );
  if (!o) return (
    <div className="rp-fade">
      <MHeader title="Pick Order" sub="Order not found" nav={nav} back />
      <div style={{ padding: 16, color: 'var(--muted)', fontSize: 13.5 }}>This order is no longer available in the current queue.</div>
    </div>
  );
  if (done) return <Success nav={nav} title="Pick Complete" sub={`${o.id} - ${customerName}`} note={`Order moved to Picked. CHEP pallet count recorded: ${chep}.`} />;
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title={`Pick ${o.id}`} sub={customerName} nav={nav} back right={<button style={{ border: 'none', background: 'var(--navy)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="scan" size={18} /></button>} />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)', borderRadius: 10, padding: '10px 13px', marginBottom: 14, fontSize: 12.5, color: 'var(--muted)' }}><Icon name="pin" size={15} />Pick location <b style={{ color: 'var(--ink)' }}>Aisle A · Bay 02</b> · FEFO lots</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {lines.map((l, i) => { const s = RP.sku(l.sku); const inv = RP.INV.find(x => mobileInventorySkuMatches(x.sku, l.sku)); const complete = picked[i] >= l.qty; return (
            <div key={i} style={{ background: 'var(--surface)', border: '1.5px solid ' + (complete ? 'var(--ok)' : 'var(--line)'), borderRadius: 12, padding: 13, boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <CanThumb sku={s} h={46} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{rpSkuDisplayName(s || l.sku)}</div>
                  <div className="mono" style={{ fontSize: 11.5, color: 'var(--faint)' }}>Lot {inv ? rpPrintedLotLabel(inv.sku, inv.lot) : 'Lot pending'}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Need <b style={{ color: 'var(--ink)' }}>{l.qty}</b> cases</div>
                </div>
                {complete ? <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--ok)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={17} /></span>
                  : <Stepper value={picked[i]} onChange={v => setPicked(p => p.map((x, j) => j === i ? v : x))} max={l.qty + 2} />}
              </div>
            </div>
          ); })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '13px 14px', marginTop: 12 }}>
          <span style={{ fontSize: 13.5, fontWeight: 500 }}>CHEP pallets out</span><Stepper value={chep} onChange={v => setChep(Math.max(0, v))} />
        </div>
      </div>
      <MActionBar>
        <button className="btn btn-lg" style={{ flex: '0 0 auto' }} onClick={() => nav.push('damage-report')}><Icon name="alert" size={16} /></button>
        <button className="btn btn-lg btn-primary" style={{ flex: 1, opacity: allDone && !busy ? 1 : 0.5 }} disabled={!allDone || busy} onClick={completePick}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Complete Pick'}</button>
      </MActionBar>
      {error && <div style={{ padding: '0 16px 12px', color: 'var(--danger)', fontSize: 12.5, fontWeight: 600 }}>{error}</div>}
    </div>
  );
};

/* ---------- RECEIVE STOCK ---------- */
function mobileReceiveIdentifier(value) {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9._:-]/g, '');
}

function mobileReceiveTransferRef() {
  return window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `TO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`
    : `TO-${Date.now().toString(36).toUpperCase()}`;
}

function mobileReceiveAcknowledgementRef() {
  return window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `RCV-ACK-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 10).toUpperCase()}`
    : `RCV-ACK-${Date.now().toString(36).toUpperCase()}`;
}

function mobileUnacknowledgedInboundTransfers(toLocationId) {
  const acknowledged = new Set((RP.FIELD_RECEIPTS || []).map(receipt => mobileReceiveIdentifier(receipt && receipt.linkedTransferRef)).filter(Boolean));
  return (RP.LOGISTICS_MOVEMENTS || [])
    .filter(row => row && !row.reconciliationOnly && row.movementType === 'inventory_transfer' && row.toLocationId === toLocationId && row.id && !acknowledged.has(mobileReceiveIdentifier(row.id)))
    .filter(row => Array.isArray(row.lines) && row.lines.some(line => Number(line.quantity || 0) > 0))
    .slice()
    .sort((a, b) => String(b.occurredAt || b.createdAt || '').localeCompare(String(a.occurredAt || a.createdAt || '')) || String(a.id).localeCompare(String(b.id)));
}


MOBILE_SCREENS['wh-receive'] = function WhReceive({ nav }) {
  const skus = mobileSkuOptions();
  const locations = mobileWarehouseLocations();
  const sourceLocations = mobileInventoryLocations('warehouse');
  const initialDestination = (locations.find(loc => mobileUnacknowledgedInboundTransfers(loc.id).length)
    || locations.find(loc => loc.id === 'WH-SQ')
    || locations[0]
    || { id: 'WH-SQ' }).id;
  const [mode, setMode] = useState('planned');
  const [selectedTransferRef, setSelectedTransferRef] = useState(() => ((mobileUnacknowledgedInboundTransfers(initialDestination)[0] || {}).id || ''));
  const [toLocationId, setToLocationId] = useState(initialDestination);
  const [sourceLocationId, setSourceLocationId] = useState((sourceLocations.find(loc => loc.id !== initialDestination) || {}).id || '');
  const [acknowledgementRef] = useState(mobileReceiveAcknowledgementRef);
  const [unplannedRef, setUnplannedRef] = useState(mobileReceiveTransferRef);
  const [movementMethod, setMovementMethod] = useState('carrier_freight');
  const [handledBy, setHandledBy] = useState('');
  const [carrier, setCarrier] = useState('');
  const [bol, setBol] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [skuId, setSkuId] = useState(skus[0] ? skus[0].id : 'RP-12-GC');
  const [lot, setLot] = useState('');
  const [receivedAt, setReceivedAt] = useState(typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10));
  const [occurredAt, setOccurredAt] = useState(() => { const now = new Date(); return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16); });
  const [producedAt, setProducedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [recv, setRecv] = useState(0);
  const [pallets, setPallets] = useState(0);
  const [chepUsage, setChepUsage] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const isPlanned = mode === 'planned';
  const inboundTransfers = mobileUnacknowledgedInboundTransfers(toLocationId);
  const selectedTransfer = inboundTransfers.find(row => row.id === selectedTransferRef) || null;
  const selectedRef = selectedTransfer ? selectedTransfer.id : '';
  const sourceId = isPlanned && selectedTransfer ? selectedTransfer.fromLocationId : sourceLocationId;
  const method = isPlanned && selectedTransfer ? selectedTransfer.movementMethod : movementMethod;
  const handler = isPlanned && selectedTransfer ? selectedTransfer.handledBy : handledBy;
  const signedCarrier = isPlanned && selectedTransfer ? selectedTransfer.carrier : carrier;
  const signedBol = isPlanned && selectedTransfer ? selectedTransfer.bol : bol;
  const signedTracking = isPlanned && selectedTransfer ? selectedTransfer.trackingNumber : trackingNumber;
  const signedChepUsage = isPlanned && selectedTransfer ? selectedTransfer.chepUsage : chepUsage;
  const signedPallets = isPlanned && selectedTransfer ? Number(selectedTransfer.chepPallets || 0) : pallets;
  const handoffAt = isPlanned && selectedTransfer ? selectedTransfer.occurredAt : occurredAt;
  const plannedLines = selectedTransfer ? (selectedTransfer.lines || []).map((line, index) => {
    const master = (RP.LOTS || []).find(item => mobileInventorySkuMatches(item.sku, line.skuId) && item.lot === line.lot) || {};
    return { damagedQuantity: 0, expiresAt: master.expiresAt || null, key: `${line.skuId}|${line.lot}|${index}`, lot: mobileReceiveIdentifier(line.lot), lotGroup: line.lotGroup || master.lotGroup || '', producedAt: master.producedAt || null, quantity: Number(line.quantity || 0), receivedAt: receivedAt || null, skuId: line.skuId };
  }).filter(line => line.quantity > 0) : [];
  const manualLot = rpResolvePrintedLotInput(skuId, mobileReceiveIdentifier(lot));
  const manualLines = recv > 0 ? [{ damagedQuantity: 0, expiresAt: expiresAt || null, lot: manualLot, producedAt: producedAt || null, quantity: recv, receivedAt: receivedAt || null, skuId }] : [];
  const activeLines = isPlanned ? plannedLines : manualLines;
  const totalCases = activeLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const sameLocation = Boolean(sourceId && sourceId === toLocationId);
  const pickupNeedsHandler = !isPlanned && ['direct_pickup', 'rep_pickup'].includes(method) && !handler.trim();
  const freightNeedsCarrier = !isPlanned && ['carrier_freight', 'courier'].includes(method) && !signedCarrier.trim();
  const refInvalid = !isPlanned && (!unplannedRef.startsWith('TO-') || unplannedRef !== mobileReceiveIdentifier(unplannedRef));
  const chepInvalid = !isPlanned && (!['used', 'not_used'].includes(signedChepUsage) || (signedChepUsage === 'used' ? !(signedPallets > 0) : signedPallets !== 0));
  const valid = isPlanned
    ? Boolean(receivedAt && selectedTransfer && selectedTransfer.toLocationId === toLocationId && activeLines.length && !sameLocation)
    : Boolean(receivedAt && sourceId && !sameLocation && activeLines.length && Number.isInteger(recv) && manualLot && !['UNASSIGNED', 'CYCLE_COUNT'].includes(manualLot) && handoffAt && !pickupNeedsHandler && !freightNeedsCarrier && !refInvalid && !chepInvalid);

  const submit = async () => {
    if (busy || !valid) return;
    setBusy(true);
    setError('');
    try {
      let response = { ref: isPlanned ? acknowledgementRef : unplannedRef, mode: isPlanned ? 'transfer_acknowledgement' : 'unplanned_completed_transfer' };
      if (!rpIsDemoMode()) {
        response = await rpApi('/api/inventory/receive', {
          method: 'POST',
          body: JSON.stringify({
            bol: signedBol || undefined,
            carrier: signedCarrier || undefined,
            chepPalletCount: signedPallets,
            chepUsage: signedChepUsage,
            handledBy: handler || undefined,
            lineItems: activeLines,
            linkedTransferRef: isPlanned ? selectedRef : undefined,
            movementMethod: method,
            notes,
            occurredAt: handoffAt ? new Date(handoffAt).toISOString() : undefined,
            pallets: signedPallets,
            receivedAt: receivedAt || null,
            recordUnplannedTransfer: isPlanned ? undefined : true,
            ref: isPlanned ? acknowledgementRef : unplannedRef,
            sourceLocationId: sourceId,
            toLocationId,
            trackingNumber: signedTracking || undefined
          })
        });
        await rpRefreshAfterMutation(`${isPlanned ? 'Transfer' : 'Handoff'} ${response.ref || selectedRef || unplannedRef}`);
      } else if (isPlanned) {
        const demoReceipt = { id: acknowledgementRef, linkedTransferRef: selectedRef, location: toLocationId, sourceLocationId: sourceId, receiptMode: 'transfer_acknowledgement', status: 'Approved', totalCases };
        if (RP.upsertFieldReceipt) RP.upsertFieldReceipt(demoReceipt);
        else if (Array.isArray(RP.FIELD_RECEIPTS)) RP.FIELD_RECEIPTS.unshift(demoReceipt);
      }
      setResult(response);
      setDone(true);
      toast(isPlanned ? `${selectedRef} acknowledged` : `${response.ref || unplannedRef} handoff recorded`, 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Receipt could not be saved.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success nav={nav} title={isPlanned ? 'Transfer Acknowledged' : 'Handoff Recorded'} sub={result && result.ref ? result.ref : selectedRef || unplannedRef} note={isPlanned ? `${totalCases} exact cases confirmed. The completed transfer already moved inventory, so this acknowledgement added zero inventory rows.` : `${totalCases} cases were recorded in one completed custody transfer. Record any damage separately.`} />;
  const evidence = selectedTransfer ? [
    ['From', (RP.loc(sourceId) || {}).name || sourceId],
    ['Movement', String(method || '').replace(/_/g, ' ')],
    ['Handled by', handler || '-'],
    ['Carrier', signedCarrier || '-'],
    ['BOL', signedBol || '-'],
    ['Tracking / PRO', signedTracking || '-'],
    ['Handoff', handoffAt ? rpFormatLocalDateTime(handoffAt, { empty: '-' }) : '-'],
    ['CHEP', `${signedChepUsage === 'used' ? 'Yes' : signedChepUsage === 'not_used' ? 'No' : 'Legacy / unknown'} - ${signedPallets} pallet${signedPallets === 1 ? '' : 's'}`]
  ] : [];
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Receive Stock" sub={isPlanned ? 'Choose completed inbound transfer' : 'Record missing handoff now'} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button type="button" className={`btn btn-sm${isPlanned ? ' btn-primary' : ''}`} onClick={() => { setMode('planned'); setError(''); }} style={{ flex: 1 }}>Recorded Transfer</button>
          <button type="button" className={`btn btn-sm${!isPlanned ? ' btn-primary' : ''}`} onClick={() => { setMode('unplanned'); setError(''); }} style={{ flex: 1, color: isPlanned ? 'var(--amber-deep)' : undefined }}>No transfer was recorded</button>
        </div>
        <div style={{ background: isPlanned ? 'var(--info-bg)' : 'var(--warn-bg)', color: isPlanned ? 'var(--info)' : 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>{isPlanned ? 'Confirm the exact signed transfer below. This acknowledgement does not add inventory again.' : 'Use only when the handoff happened but no transfer exists. This creates one completed TO-* transfer now.'}</div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, boxShadow: 'var(--shadow-sm)' }}>
          <Lbl>Destination</Lbl>
          <select className="field" value={toLocationId} onChange={e => { const nextLocationId = e.target.value; setToLocationId(nextLocationId); setSelectedTransferRef(((mobileUnacknowledgedInboundTransfers(nextLocationId)[0] || {}).id || '')); }} style={{ marginBottom: 12 }}>{locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}</select>
          <Lbl>Received Date</Lbl>
          <input className="field" type="date" value={receivedAt} onChange={e => setReceivedAt(e.target.value)} style={{ marginBottom: 12, borderColor: !receivedAt ? 'var(--danger)' : undefined }} />
          {isPlanned ? <>
            <Lbl>Completed Inbound Transfer</Lbl>
            <select className="field mono" value={selectedRef} onChange={e => setSelectedTransferRef(e.target.value)} style={{ marginBottom: 6 }}><option value="">Select unacknowledged transfer</option>{inboundTransfers.map(row => <option key={row.id} value={row.id}>{row.id} - {row.caseCount || 0} cases</option>)}</select>
            <div style={{ color: 'var(--faint)', fontSize: 11.5, marginBottom: 14 }}>{inboundTransfers.length ? `${inboundTransfers.length} unacknowledged inbound transfer${inboundTransfers.length === 1 ? '' : 's'} available.` : 'No unacknowledged completed transfer is available for this destination. Confirm the destination or deliberately use the unplanned mode.'}</div>
            {selectedTransfer && <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 14 }}>{evidence.map(([label, value]) => <div key={label} style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '9px 10px' }}><div style={{ color: 'var(--faint)', fontSize: 10.5 }}>{label}</div><div className={['BOL', 'Tracking / PRO'].includes(label) ? 'mono' : ''} style={{ fontSize: 12.5, fontWeight: 700, marginTop: 2, textTransform: label === 'Movement' ? 'capitalize' : undefined }}>{value}</div></div>)}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}><Lbl style={{ margin: 0 }}>Signed SKU / lot lines</Lbl><b className="mono">{totalCases} cases</b></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>{activeLines.map(line => { const sku = skus.find(item => mobileInventorySkuMatches(item.id, line.skuId)) || RP.sku(line.skuId) || null; return <div key={line.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, background: 'var(--surface-2)', borderRadius: 8, padding: '9px 10px' }}><div><b>{rpSkuDisplayName(sku || 'Unknown product')}</b><div className="mono" style={{ color: 'var(--faint)', fontSize: 10.5 }}>{rpSkuExternalIdentity(sku || '')} - {rpPrintedLotLabel(line.skuId, line.lot)}{line.lotGroup ? ` - ${line.lotGroup}` : ''}</div></div><b className="mono">{line.quantity}</b></div>; })}</div>
            </>}
          </> : <>
            <Lbl>Transfer ID</Lbl><input className="field mono" value={unplannedRef} onChange={e => setUnplannedRef(mobileReceiveIdentifier(e.target.value))} style={{ marginBottom: 12, borderColor: refInvalid ? 'var(--danger)' : undefined }} />
            <Lbl>Received From</Lbl><select className="field" value={sourceLocationId} onChange={e => setSourceLocationId(e.target.value)} style={{ marginBottom: 12, borderColor: sameLocation ? 'var(--danger)' : undefined }}><option value="">Select recorded source</option>{sourceLocations.filter(loc => loc.id !== toLocationId).map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}</select>
            <Lbl>Movement</Lbl><select className="field" value={movementMethod} onChange={e => setMovementMethod(e.target.value)} style={{ marginBottom: 12 }}><option value="carrier_freight">Carrier / Freight</option><option value="courier">Courier</option><option value="direct_pickup">Direct Pickup</option><option value="rep_pickup">Rep Pickup</option><option value="internal_transfer">Internal Transfer</option></select>
            <Lbl>Handled By / Driver</Lbl><input className="field" value={handledBy} onChange={e => setHandledBy(e.target.value)} placeholder="Required for direct pickup" style={{ marginBottom: 12, borderColor: pickupNeedsHandler ? 'var(--danger)' : undefined }} />
            <Lbl>Carrier</Lbl><input className="field" value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="Required for freight / courier" style={{ marginBottom: 12, borderColor: freightNeedsCarrier ? 'var(--danger)' : undefined }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}><div><Lbl>BOL</Lbl><input className="field mono" value={bol} onChange={e => setBol(mobileReceiveIdentifier(e.target.value))} /></div><div><Lbl>Tracking / PRO</Lbl><input className="field mono" value={trackingNumber} onChange={e => setTrackingNumber(mobileReceiveIdentifier(e.target.value))} /></div></div>
            <Lbl>Product</Lbl><select className="field" value={skuId} onChange={e => setSkuId(e.target.value)} style={{ marginBottom: 12 }}>{skus.map(s => <option key={s.id} value={s.id}>{rpSkuDisplayName(s)} - {rpSkuExternalIdentity(s)}</option>)}</select>
            <Lbl>Exact Lot</Lbl><input className="field mono" value={lot} onChange={e => setLot(mobileReceiveIdentifier(e.target.value))} placeholder="RP-GC-001" style={{ marginBottom: 8 }} /><div className="mono" style={{ fontSize: 11.5, marginBottom: 8, overflowWrap: 'anywhere' }}>{rpPrintedLotLabel(skuId, manualLot)}</div>
            <Lbl>Produced</Lbl><input className="field" type="date" value={producedAt} onChange={e => setProducedAt(e.target.value)} style={{ marginBottom: 12 }} />
            <Lbl>Actual Handoff Time</Lbl><input className="field" type="datetime-local" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} style={{ marginBottom: 12 }} />
            <Lbl>Expiry</Lbl><input className="field" type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} style={{ marginBottom: 8 }} />
            <CountRow label="Cases in exact handoff" value={recv} onMinus={() => setRecv(Math.max(0, recv - 1))} onPlus={() => setRecv(recv + 1)} onChange={setRecv} quick />
            <Lbl>CHEP Used</Lbl><select className="field" value={chepUsage} onChange={e => { setChepUsage(e.target.value); if (e.target.value === 'not_used') setPallets(0); }} style={{ marginBottom: 4 }}><option value="">Select Yes / No</option><option value="used">Yes - CHEP used</option><option value="not_used">No CHEP pallets</option></select>
            <CountRow label="CHEP pallets" value={pallets} warn={chepUsage === 'used' && pallets <= 0} onMinus={() => setPallets(Math.max(0, pallets - 1))} onPlus={() => setPallets(pallets + 1)} onChange={setPallets} quick />
          </>}
          {sameLocation && <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 12, fontWeight: 700 }}>Source and destination must differ.</div>}
        </div>
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', background: 'var(--surface-2)', color: 'var(--muted)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, marginTop: 12 }}><Icon name="alert" size={16} />Acknowledge or record the full exact handoff. If cases arrived damaged, use Report Damage / Loss afterward with the exact SKU and lot.</div>
        <Lbl style={{ marginTop: 16 }}>Notes</Lbl><textarea className="field" rows="2" placeholder="Handoff or receiving evidence notes..." value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'none' }} />
        {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy || !valid} style={{ flex: 1, opacity: busy || !valid ? 0.5 : 1 }} onClick={submit}><Icon name="check" size={17} />{busy ? 'Saving...' : isPlanned ? `Acknowledge ${totalCases} Cases` : `Record ${totalCases} Case Handoff`}</button></MActionBar>
    </div>
  );
};

function CountRow({ label, value, sub, danger, warn, onMinus, onPlus, onChange, quick, allowBlank }) {
  const quicks = Array.isArray(quick) ? quick : quick ? [5, 10] : [];
  const setInputValue = raw => {
    if (!onChange) return;
    if (allowBlank && String(raw) === '') {
      onChange(null);
      return;
    }
    const next = Math.max(0, Math.floor(Number(raw || 0) || 0));
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid var(--line-2)' }}>
      <div><div style={{ fontSize: 13.5, fontWeight: 500, color: danger ? 'var(--danger)' : warn ? 'var(--amber-deep)' : 'var(--ink)' }}>{label}</div>{sub && <div style={{ fontSize: 11, color: 'var(--faint)' }}>{sub}</div>}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1px solid var(--line)', borderRadius: 9, overflow: 'hidden' }}>
          <button onClick={onMinus} style={{ width: 36, height: 36, border: 'none', background: 'var(--surface-2)', color: 'var(--ink)' }}><Icon name="minus" size={16} /></button>
          {onChange ? <input className="mono tnum" type="number" min="0" step="1" inputMode="numeric" value={value == null ? '' : value} onFocus={e => e.target.select()} onChange={e => setInputValue(e.target.value)} style={{ width: 54, height: 36, textAlign: 'center', fontWeight: 700, fontSize: 16, border: 'none', borderLeft: '1px solid var(--line)', borderRight: '1px solid var(--line)', color: 'var(--ink)', background: 'var(--surface)' }} /> : <span className="mono tnum" style={{ width: 46, textAlign: 'center', fontWeight: 700, fontSize: 16 }}>{value}</span>}
          <button onClick={onPlus} style={{ width: 36, height: 36, border: 'none', background: 'var(--navy)', color: '#fff' }}><Icon name="plus" size={16} /></button>
        </div>
        {quicks.map(amount => <button key={amount} type="button" onClick={() => onChange ? onChange(Math.max(0, Number(value || 0) + amount)) : onPlus && onPlus()} style={{ height: 32, minWidth: 42, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface-2)', color: 'var(--ink)', fontSize: 12, fontWeight: 800 }}>+{amount}</button>)}
      </div>
    </div>
  );
}

function FieldReceiptPhotoInput({ label, files, onChange, required }) {
  const fileList = Array.isArray(files) ? files : [];
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  const addFiles = event => {
    const selected = Array.from(event.target.files || []);
    if (selected.length) onChange([...fileList, ...selected]);
    event.target.value = '';
  };
  const clearFiles = () => onChange([]);
  return (
    <div style={{ marginBottom: 12 }}>
      <Lbl>{label}{required ? ' *' : ''}</Lbl>
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={addFiles} />
      <input ref={galleryInput} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={addFiles} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
        <button type="button" className="btn" onClick={() => cameraInput.current && cameraInput.current.click()} style={{ minHeight: 70, flexDirection: 'column', gap: 6, borderStyle: 'dashed', justifyContent: 'center' }}><Icon name="camera" size={23} /><span style={{ fontSize: 12 }}>Take Photo</span></button>
        <button type="button" className="btn" onClick={() => galleryInput.current && galleryInput.current.click()} style={{ minHeight: 70, flexDirection: 'column', gap: 6, borderStyle: 'dashed', justifyContent: 'center' }}><Icon name="doc" size={23} /><span style={{ fontSize: 12 }}>Gallery</span></button>
      </div>
      {fileList.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 7 }}>
        {fileList.slice(0, 4).map((file, index) => <div key={file.name + index} style={{ fontSize: 11.5, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="check" size={12} />{file.name}</div>)}
        <button type="button" className="btn btn-ghost btn-sm" onClick={clearFiles} style={{ justifyContent: 'flex-start', paddingLeft: 0, color: 'var(--danger)' }}>Remove selected photos</button>
      </div>}
    </div>
  );
}

function mobileFieldReceiptLine(skuId, seed) {
  const suffix = seed || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  return { rowId: `${skuId}-${suffix}`, skuId, lot: '', quantity: 0, damagedQuantity: 0 };
}

MOBILE_SCREENS['field-receipt'] = function FieldInventoryReceipt({ nav, role, loc }) {
  const salesPersonalLocations = inventoryPersonalFieldLocations(role);
  const salesPersonalScope = inventoryRepackSalesRoleAllowed(role);
  const preferredReceiptLocationId = inventoryPersonalPreferredFieldLocationId(salesPersonalLocations);
  const micro = salesPersonalScope
    ? inventoryPersonalResolveFieldLocation(salesPersonalLocations, preferredReceiptLocationId)
    : rpFieldMicroWarehouse(role);
  const skus = mobileSkuOptions();
  const microLocations = mobileInventoryLocations(role).filter(loc => loc.type === 'Micro-Warehouse');
  const receiptLocations = salesPersonalScope
    ? salesPersonalLocations
    : (microLocations.length ? microLocations : mobileInventoryLocations(role));
  const requestedReceiptLocationId = salesPersonalScope
    ? inventoryPersonalValidatedFieldLocationId(receiptLocations, loc)
    : inventoryRepackValidatedFieldLocationId(role, receiptLocations, loc);
  const defaultReceiptLocationId = requestedReceiptLocationId
    || (receiptLocations.find(location => location.id === preferredReceiptLocationId) || micro || { id: salesPersonalScope ? '' : mobileDefaultInventoryLocation(role) }).id;
  const [locationId, setLocationId] = useState(defaultReceiptLocationId);
  const [sourceLocationId, setSourceLocationId] = useState(() => mobileFieldReceiptDefaultSourceId(role, defaultReceiptLocationId));
  const [receivedAt, setReceivedAt] = useState(typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10));
  const [linkedTransferRef, setLinkedTransferRef] = useState('');
  const [lines, setLines] = useState(() => skus.map((sku, index) => mobileFieldReceiptLine(sku.id, `base-${index}`)));
  const [receiptRef] = useState(() => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return `FR-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
    return `FR-${Date.now().toString(36).toUpperCase()}`;
  });
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const location = RP.loc(locationId);
  const sourceLocations = mobileFieldReceiptSourceLocations(role, locationId);
  const acknowledgedTransferRefs = new Set((RP.FIELD_RECEIPTS || []).map(receipt => receipt && receipt.linkedTransferRef).filter(Boolean));
  const inboundTransfers = (RP.LOGISTICS_MOVEMENTS || [])
    .filter(transfer => transfer && !transfer.reconciliationOnly && transfer.toLocationId === locationId && !acknowledgedTransferRefs.has(transfer.id))
    .sort((a, b) => String(b.occurredAt || '').localeCompare(String(a.occurredAt || '')));
  const selectedTransfer = inboundTransfers.find(row => row.id === linkedTransferRef) || null;
  const effectiveSourceLocationId = selectedTransfer && selectedTransfer.fromLocationId
    ? selectedTransfer.fromLocationId
    : sourceLocations.some(loc => loc.id === sourceLocationId)
      ? sourceLocationId
      : mobileFieldReceiptDefaultSourceId(role, locationId);
  const sourceLocation = RP.loc(effectiveSourceLocationId);
  const effectiveSourceLocations = sourceLocation && !sourceLocations.some(loc => loc.id === sourceLocation.id)
    ? [sourceLocation, ...sourceLocations]
    : sourceLocations;
  const updateTransferReference = value => {
    const cleaned = String(value || '').toUpperCase().replace(/[^A-Z0-9._:-]/g, '');
    setLinkedTransferRef(cleaned);
    const transfer = inboundTransfers.find(row => row.id === cleaned);
    if (transfer && transfer.fromLocationId) {
      setSourceLocationId(transfer.fromLocationId);
      if (Array.isArray(transfer.lines) && transfer.lines.length) {
        setLines(transfer.lines.map((line, index) => ({
          ...mobileFieldReceiptLine(line.skuId, `transfer-${index}`),
          lot: String(line.lot || '').toUpperCase(),
          quantity: Math.max(0, Math.floor(Number(line.quantity || 0) || 0))
        })));
      }
    }
  };
  const cleanLot = value => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9._:-]/g, '');
  const clampCount = value => Math.max(0, Math.floor(Number(value || 0) || 0));
  const setLine = (rowId, key, value) => setLines(rows => rows.map(line => line.rowId === rowId ? { ...line, [key]: key === 'lot' ? rpResolvePrintedLotInput(line.skuId, cleanLot(value)) : clampCount(value) } : line));
  const adjustLine = (rowId, key, amount) => setLines(rows => rows.map(line => line.rowId === rowId ? { ...line, [key]: clampCount((line[key] || 0) + amount) } : line));
  const addLotLine = skuId => setLines(rows => [...rows, mobileFieldReceiptLine(skuId)]);
  const removeLotLine = rowId => setLines(rows => rows.filter(line => line.rowId !== rowId));
  const activeLines = lines
    .map(line => ({
      damagedQuantity: clampCount(line.damagedQuantity),
      lot: rpResolvePrintedLotInput(line.skuId, cleanLot(line.lot)),
      quantity: clampCount(line.quantity),
      skuId: line.skuId
    }))
    .filter(line => line.quantity > 0 || line.damagedQuantity > 0);
  const totalSellable = activeLines.reduce((sum, line) => sum + line.quantity, 0);
  const totalDamaged = activeLines.reduce((sum, line) => sum + line.damagedQuantity, 0);
  const total = totalSellable + totalDamaged;
  const missingLot = activeLines.some(line => !line.lot || line.lot === 'UNASSIGNED' || line.lot === 'CYCLE_COUNT');
  const activeLineKeys = activeLines.map(line => `${line.skuId}|${line.lot}`);
  const duplicateLotLine = new Set(activeLineKeys).size !== activeLineKeys.length;
  const availableLotsForSku = skuId => Array.from(new Set((RP.INV || [])
    .filter(row => row && row.loc === locationId && Number(row.qty || 0) > 0 && (typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(row.sku) : row.sku) === skuId)
    .map(row => String(row.lot || '').trim().toUpperCase())
    .filter(lot => lot && lot !== 'UNASSIGNED' && lot !== 'CYCLE_COUNT'))).sort();
  const uploadEvidence = async (files, label) => {
    const selected = Array.isArray(files) ? files : [];
    if (rpIsDemoMode()) {
      return selected.map(file => ({ name: file.name, path: '', size: file.size, type: file.type, url: '' }));
    }
    const uploaded = [];
    for (const file of selected) {
      const saved = await rpUploadPrivateFile('inventory_photo', file, label);
      if (saved) uploaded.push(saved);
    }
    return uploaded;
  };
  const submit = async () => {
    if (busy) return;
    if (total <= 0) {
      setError('Add at least one received or damaged case.');
      return;
    }
    if (missingLot) {
      setError('Enter the exact lot printed on every case received.');
      return;
    }
    if (duplicateLotLine) {
      setError('Combine duplicate SKU and lot lines before acknowledging the transfer.');
      return;
    }
    if (!linkedTransferRef.trim()) {
      setError('Enter the completed transfer reference from the handoff paperwork.');
      return;
    }
    if (totalDamaged > 0 && damagePhotos.length === 0) {
      setError('Add at least one damage photo for damaged goods.');
      return;
    }
    if (!locationId) {
      setError('No micro-warehouse is assigned to this profile yet.');
      return;
    }
    if (!effectiveSourceLocationId) {
      setError('Choose where the inventory was received from.');
      return;
    }
    if (effectiveSourceLocationId === locationId) {
      setError('The source and receiving location must be different.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const receiptPhotos = await uploadEvidence(photos, 'field-receipt');
      const damagedEvidence = await uploadEvidence(damagePhotos, 'field-damage');
      const lineItems = activeLines.map((line, index) => ({
        damagePhotos: line.damagedQuantity > 0 ? damagedEvidence : [],
        damagedQuantity: line.damagedQuantity,
        lot: line.lot,
        photos: index === 0 ? receiptPhotos : [],
        quantity: line.quantity,
        skuId: line.skuId
      }));
      let receipt = {
        id: receiptRef,
        submittedBy: rpAuthProfile().id || rpFieldUserName(role),
        submitter: rpDisplayName(),
        location: locationId,
        source: effectiveSourceLocationId,
        sourceLocationId: effectiveSourceLocationId,
        sourceName: sourceLocation ? sourceLocation.name : '',
        territory: rpFieldTerritory(role),
        status: 'Approved',
        receiptMode: 'transfer_acknowledgement',
        linkedTransferRef: linkedTransferRef.trim(),
        receivedAt,
        notes,
        movementMethod: selectedTransfer ? selectedTransfer.movementMethod : '',
        handledBy: selectedTransfer ? selectedTransfer.handledBy : '',
        carrier: selectedTransfer ? selectedTransfer.carrier : '',
        trackingNumber: selectedTransfer ? selectedTransfer.trackingNumber : '',
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        sellableCases: totalSellable,
        damagedCases: totalDamaged,
        totalCases: total,
        photoCount: lineItems.reduce((sum, line) => sum + (line.photos || []).length + (line.damagePhotos || []).length, 0),
        lines: lineItems.map(line => ({ sku: line.skuId, qty: line.quantity, damagedQuantity: line.damagedQuantity, lot: line.lot, photos: line.photos, damagePhotos: line.damagePhotos })),
        persisted: true
      };
      if (!rpIsDemoMode()) {
        const response = await rpApi('/api/inventory/field-receipt', {
          method: 'POST',
          body: JSON.stringify({
            lineItems,
            linkedTransferRef: linkedTransferRef.trim(),
            locationId,
            notes,
            ref: receiptRef,
            receivedAt,
            sourceLocationId: effectiveSourceLocationId
          })
        });
        receipt = response.receipt || receipt;
        if (RP.upsertFieldReceipt) RP.upsertFieldReceipt(receipt);
        await rpRefreshAfterMutation(`Transfer ${linkedTransferRef.trim()} acknowledged`);
      } else if (RP.upsertFieldReceipt) {
        RP.upsertFieldReceipt(receipt);
      }
      setResult(receipt);
      toast(`Transfer ${linkedTransferRef.trim()} acknowledged`, 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Field receipt could not be submitted.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  if (result) {
    const sourceLabel = result.sourceName || (RP.loc(result.source || result.sourceLocationId)?.name) || 'the source location';
    return <Success nav={nav} title="Transfer Acknowledged" sub={`${result.totalCases || total} cases confirmed`} note={`${result.linkedTransferRef || linkedTransferRef} confirms the completed transfer from ${sourceLabel}. Inventory was already posted by that transfer, so this receipt did not add it again.`} actionLabel="My Inventory" onAction={() => nav.reset('rep-inventory', { loc: locationId })} />;
  }
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Record Received" sub={location ? location.name : 'Micro-warehouse'} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        {!micro && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>No assigned micro-warehouse was found. Choose an available field location before submitting.</div>}
        <div style={{ background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 650, marginBottom: 14 }}>This confirms a completed transfer; it does not add inventory again. If there is no transfer reference yet, Operations must record the custody handoff first.</div>
        <Lbl>Completed Transfer Reference</Lbl>
        <input className="field mono" list="field-receipt-transfer-refs" value={linkedTransferRef} onChange={e => updateTransferReference(e.target.value)} placeholder="TO-..." style={{ marginBottom: 5, borderColor: error && !linkedTransferRef.trim() ? 'var(--danger)' : undefined }} />
        <datalist id="field-receipt-transfer-refs">{inboundTransfers.map(transfer => <option key={transfer.id} value={transfer.id}>{`${(RP.loc(transfer.fromLocationId) || {}).name || transfer.fromLocationId} - ${transfer.caseCount || 0} cases - ${transfer.chepPallets || 0} CHEP - ${String(transfer.occurredAt || '').slice(0, 10)}`}</option>)}</datalist>
        <div style={{ color: 'var(--faint)', fontSize: 11.5, marginBottom: 14 }}>{inboundTransfers.length ? `${inboundTransfers.length} unacknowledged inbound transfer${inboundTransfers.length === 1 ? '' : 's'} available.` : 'Enter the reference from the completed transfer record.'}</div>
        {selectedTransfer && <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', borderRadius: 10, padding: '10px 12px', fontSize: 12, fontWeight: 700, marginBottom: 14 }}>{selectedTransfer.caseCount || 0} signed cases across {selectedTransfer.rowCount || (selectedTransfer.lines || []).length} SKU/lot line{(selectedTransfer.rowCount || (selectedTransfer.lines || []).length) === 1 ? '' : 's'} loaded from the completed transfer. Count and confirm these exact cases.</div>}
        <Lbl>Received from</Lbl>
        <select className="field" disabled={!!selectedTransfer} value={effectiveSourceLocationId} onChange={e => setSourceLocationId(e.target.value)} style={{ marginBottom: 14 }}>
          {effectiveSourceLocations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
        </select>
        <Lbl>Receiving into</Lbl>
        <select className="field" value={locationId} onChange={e => setLocationId(e.target.value)} style={{ marginBottom: 16 }}>
          {receiptLocations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
        </select>
        <Lbl>Received</Lbl>
        <input className="field" type="date" value={receivedAt} onChange={e => setReceivedAt(e.target.value)} style={{ marginBottom: 14 }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <Lbl style={{ margin: 0 }}>Flavors Received</Lbl>
          <span className="mono" style={{ fontSize: 12, color: total ? 'var(--ink)' : 'var(--faint)', fontWeight: 800 }}>{activeLines.length} lot lines / {total} cases</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
          {lines.map((line, lineIndex) => {
            const sku = skus.find(s => s.id === line.skuId) || RP.sku(line.skuId) || null;
            const lineTotal = clampCount(line.quantity) + clampCount(line.damagedQuantity);
            const firstForSku = lines.findIndex(candidate => candidate.skuId === line.skuId) === lineIndex;
            return (
              <div key={line.rowId} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px 2px', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 11 }}>
                  <FlavorChip sku={sku} size={30} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rpSkuDisplayName(sku || 'Unknown product')}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 1 }}>{rpSkuExternalIdentity(sku || '')}</div>
                  </div>
                  <span className="pill" style={{ background: lineTotal ? 'var(--ok-bg)' : 'var(--surface-2)', color: lineTotal ? 'var(--ok)' : 'var(--faint)' }}>{lineTotal} cs</span>
                  {!firstForSku && <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeLotLine(line.rowId)} style={{ color: 'var(--danger)', padding: 4 }}><Icon name="x" size={14} /></button>}
                </div>
                <div style={{ marginBottom: 2 }}><Lbl>Lot</Lbl><input className="field mono" list={`field-receipt-lots-${line.rowId}`} value={line.lot} onChange={e => setLine(line.rowId, 'lot', e.target.value)} placeholder="Exact lot required" style={lineTotal && !cleanLot(line.lot) ? { borderColor: 'var(--danger)' } : undefined} /><datalist id={`field-receipt-lots-${line.rowId}`}>{availableLotsForSku(line.skuId).map(lot => <option key={lot} value={lot}>{rpPrintedLotLabel(line.skuId, lot)}</option>)}</datalist><div className="mono" style={{ fontSize: 11.5, overflowWrap: 'anywhere' }}>{rpPrintedLotLabel(line.skuId, line.lot)}</div></div>
                <CountRow label="Cases received" value={line.quantity} onMinus={() => adjustLine(line.rowId, 'quantity', -1)} onPlus={() => adjustLine(line.rowId, 'quantity', 1)} onChange={value => setLine(line.rowId, 'quantity', value)} quick />
                {firstForSku && <button type="button" className="btn btn-ghost btn-sm" onClick={() => addLotLine(line.skuId)} style={{ marginBottom: 8, paddingLeft: 0 }}><Icon name="plus" size={13} />Add another lot of this SKU</button>}
                <div style={{ color: 'var(--faint)', fontSize: 11.5, paddingBottom: 10 }}>If cases arrived damaged, acknowledge the full transfer first, then use Report Damage / Loss with photos.</div>
              </div>
            );
          })}
        </div>
        <FieldReceiptPhotoInput label="Receipt / stock photos" files={photos} onChange={setPhotos} />
        <Lbl>Notes</Lbl>
        <textarea className="field" rows="2" placeholder="Handoff or receipt evidence notes..." value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'none' }} />
        <div style={{ marginTop: 12, background: 'var(--surface-2)', borderRadius: 10, padding: '11px 13px', color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.45 }}>The SKU, lot, and case totals must exactly match the signed completed transfer. A mismatch is rejected for Operations to investigate.</div>
        {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 700, marginTop: 10 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy || total <= 0} style={{ flex: 1, opacity: busy || total <= 0 ? 0.55 : 1 }} onClick={submit}><Icon name="check" size={17} />{busy ? 'Confirming...' : `Acknowledge Transfer - ${total} cases`}</button></MActionBar>
    </div>
  );
};

/* ---------- SALES GIVEAWAY ---------- */
MOBILE_SCREENS['giveaway-log'] = function SalesGiveaway({ nav, role, loc }) {
  const salesPersonalScope = inventoryRepackSalesRoleAllowed(role);
  const personalLocations = inventoryPersonalFieldLocations(role);
  const locations = salesPersonalScope ? personalLocations : mobileGiveawayLocations(role);
  const requestedGiveawayLocationId = salesPersonalScope
    ? inventoryPersonalValidatedFieldLocationId(locations, loc)
    : inventoryRepackValidatedFieldLocationId(role, locations, loc);
  const preferredGiveawayLocationId = salesPersonalScope ? inventoryPersonalPreferredFieldLocationId(locations) : '';
  const defaultLocationId = requestedGiveawayLocationId
    || (locations.find(location => location.id === preferredGiveawayLocationId) || (salesPersonalScope ? null : locations.find(loc => loc.id === mobileDefaultInventoryLocation(role))) || (locations.length === 1 ? locations[0] : null) || { id: '' }).id;
  const [locationId, setLocationId] = useState(defaultLocationId);
  const skuOptions = mobileGiveawaySkus(locationId);
  const [lines, setLines] = useState(() => [mobileGiveawayNewLine(defaultLocationId)]);
  const [recipientMode, setRecipientMode] = useState('customer');
  const [recipientKey, setRecipientKey] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [reason, setReason] = useState('New account sample');
  const [expectedOutcome, setExpectedOutcome] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [notes, setNotes] = useState('');
  const [requestRef] = useState(() => 'GW-' + Date.now().toString().slice(-8));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const recipientOptions = mobileGiveawayRecipientOptions(role);
  const selectedRecipient = recipientOptions.find(option => option.key === recipientKey) || null;
  const recipientType = recipientMode === 'staff' ? 'staff' : (selectedRecipient ? selectedRecipient.type : recipientMode === 'other' ? 'other' : 'customer');
  const staffName = typeof rpDisplayName === 'function' ? rpDisplayName() : rpFieldUserName(role);
  const recipient = recipientType === 'staff' ? staffName : selectedRecipient ? selectedRecipient.name : recipientMode === 'other' ? recipientName.trim() : '';
  const customerId = selectedRecipient && selectedRecipient.type === 'customer' ? selectedRecipient.id : '';
  const prospectId = selectedRecipient && selectedRecipient.type === 'prospect' ? selectedRecipient.id : '';
  const lineItems = lines
    .map(line => ({
      quantity: Math.max(0, Math.floor(Number(line.quantity || 0) || 0)),
      skuId: line.skuId && skuOptions.some(sku => sku.id === line.skuId) ? line.skuId : ''
    }))
    .filter(line => line.skuId || line.quantity > 0);
  const quantityBySku = new Map();
  lineItems.forEach(line => quantityBySku.set(line.skuId, (quantityBySku.get(line.skuId) || 0) + line.quantity));
  const lineIssues = new Set(lineItems.filter(line => !line.skuId || !(line.quantity > 0) || (quantityBySku.get(line.skuId) || 0) > mobileGiveawayAvailableCases(line.skuId, locationId)).map(line => line.skuId || '__missing'));
  const totalQuantity = lineItems.reduce((sum, line) => sum + line.quantity, 0);
  const hasRationale = recipientType === 'staff' || Boolean(expectedOutcome.trim() || notes.trim());
  const valid = Boolean(locationId && lineItems.length && totalQuantity > 0 && !lineIssues.size && recipient && hasRationale);
  const locationName = (RP.loc(locationId) || {}).name || locationId || 'Field inventory';

  useEffect(() => {
    setLines(current => current.length ? current.map(line => {
      if (line.skuId && skuOptions.some(sku => sku.id === line.skuId)) return line;
      return { ...line, skuId: skuOptions[0] ? skuOptions[0].id : '' };
    }) : [mobileGiveawayNewLine(locationId)]);
  }, [locationId, skuOptions.map(sku => sku.id).join('|')]);

  useEffect(() => {
    if (recipientMode === 'staff' && reason !== MOBILE_GIVEAWAY_STAFF_REASON) setReason(MOBILE_GIVEAWAY_STAFF_REASON);
  }, [recipientMode]);

  const changeLocation = value => {
    setLocationId(value);
    setLines([mobileGiveawayNewLine(value)]);
  };
  const setLine = (index, key, value) => setLines(current => current.map((line, lineIndex) => lineIndex === index ? { ...line, [key]: value } : line));
  const addLine = () => setLines(current => [...current, mobileGiveawayNewLine(locationId)]);
  const removeLine = index => setLines(current => current.length <= 1 ? current : current.filter((_, lineIndex) => lineIndex !== index));

  const submit = async () => {
    setTried(true);
    setError('');
    if (!valid || busy) return;
    setBusy(true);
    try {
      let ref = requestRef;
      if (!rpIsDemoMode()) {
        const response = await rpApi('/api/inventory/giveaway', {
          method: 'POST',
          body: JSON.stringify({
            customerId: customerId || undefined,
            expectedOutcome: expectedOutcome.trim(),
            followUpDate: followUpDate || undefined,
            lineItems,
            locationId,
            notes: notes.trim(),
            prospectId: prospectId || undefined,
            ref: requestRef,
            reason,
            recipientName: recipient,
            recipientType
          })
        });
        ref = response.ref || ref;
        await rpRefreshAfterMutation(`Giveaway ${ref} logged`);
      } else {
        ref = mobileApplyDemoGiveaway({
          expectedOutcome: expectedOutcome.trim(),
          followUpDate,
          lineItems,
          locationId,
          notes: notes.trim(),
          prospectId,
          reason,
          recipientName: recipient,
          recipientType
        });
      }
      setResult({ ref, quantity: totalQuantity, reason });
      toast(`Giveaway ${ref} logged`, 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not log giveaway.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  if (result) return <Success nav={nav} title="Giveaway Logged" sub={`${result.quantity} cases - ${result.reason}`} note={`${result.ref} posted to the inventory ledger.`} actionLabel="My Inventory" onAction={() => nav.reset('rep-inventory', { loc: locationId })} />;

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Log Giveaway" sub={locationName} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <Lbl>Source Location</Lbl>
        <select className="field" value={locationId} onChange={e => changeLocation(e.target.value)} disabled={!locations.length} style={{ marginBottom: 16, opacity: locations.length ? 1 : 0.65 }}>
          {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
          {!locations.length && <option value="">No inventory locations</option>}
        </select>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, marginBottom: 16, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 13px', borderBottom: '1px solid var(--line)' }}>
            <span className="eyebrow">Products</span>
            <button className="btn btn-sm" onClick={addLine} disabled={!skuOptions.length}><Icon name="plus" size={14} />Add</button>
          </div>
          {lines.map((line, index) => {
            const rowSkuId = line.skuId && skuOptions.some(sku => sku.id === line.skuId) ? line.skuId : '';
            const rowQuantity = Math.max(0, Math.floor(Number(line.quantity || 0) || 0));
            const rowAvailable = rowSkuId ? mobileGiveawayAvailableCases(rowSkuId, locationId) || 0 : 0;
            const requested = quantityBySku.get(rowSkuId) || 0;
            const over = rowSkuId ? requested > rowAvailable : true;
            return (
              <div key={index} style={{ padding: 13, borderBottom: index < lines.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <select className="field" value={rowSkuId} onChange={e => setLine(index, 'skuId', e.target.value)} disabled={!skuOptions.length} style={{ flex: 1, opacity: skuOptions.length ? 1 : 0.65, borderColor: tried && !rowSkuId ? 'var(--danger)' : undefined }}>
                    {skuOptions.map(sku => <option key={sku.id} value={sku.id}>{rpSkuDisplayName(sku)} - {mobileGiveawayAvailableCases(sku.id, locationId)} cs</option>)}
                    {!skuOptions.length && <option value="">No giveaway stock here</option>}
                  </select>
                  <button className="btn btn-sm" disabled={lines.length <= 1} onClick={() => removeLine(index)} style={{ width: 38, padding: 0, justifyContent: 'center' }}><Icon name="x" size={14} /></button>
                </div>
                <CountRow label="Cases" sub={over ? `${requested}/${rowAvailable} requested` : `${rowAvailable} available`} value={rowQuantity} warn={over} onMinus={() => setLine(index, 'quantity', Math.max(0, rowQuantity - 1))} onPlus={() => setLine(index, 'quantity', rowQuantity + 1)} onChange={value => setLine(index, 'quantity', value)} quick />
              </div>
            );
          })}
        </div>

        <Lbl>Recipient Type</Lbl>
        <div style={{ marginBottom: 16 }}><Segmented size="sm" options={[{ value: 'customer', label: 'Customer' }, { value: 'staff', label: 'Staff' }, { value: 'other', label: 'Other' }]} value={recipientMode} onChange={setRecipientMode} /></div>

        {recipientMode === 'customer' && <>
          <Lbl>Customer / Prospect</Lbl>
          <select className="field" value={recipientKey} onChange={e => setRecipientKey(e.target.value)} style={{ marginBottom: 16, borderColor: tried && !recipient ? 'var(--danger)' : undefined }}>
            <option value="">Select customer or prospect</option>
            {recipientOptions.map(option => <option key={option.key} value={option.key}>{option.name}{option.detail ? ` - ${option.detail}` : ''}</option>)}
          </select>
        </>}

        {recipientMode === 'staff' && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 13, marginBottom: 16, color: 'var(--muted)', fontSize: 12.5 }}><b style={{ color: 'var(--ink)' }}>{staffName}</b> will be recorded as the staff promo-stock recipient.</div>}

        {recipientMode === 'other' && <>
          <Lbl>Recipient Name</Lbl>
          <input className="field" value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="Buyer, event, walk-in, or organization" style={{ marginBottom: 16, borderColor: tried && !recipient ? 'var(--danger)' : undefined }} />
        </>}

        <Lbl>Reason</Lbl>
        <select className="field" value={reason} onChange={e => setReason(e.target.value)} style={{ marginBottom: 16 }}>
          {MOBILE_GIVEAWAY_REASONS.map(option => <option key={option}>{option}</option>)}
        </select>

        {recipientMode !== 'staff' && <>
          <Lbl>Expected Outcome</Lbl>
          <input className="field" value={expectedOutcome} onChange={e => setExpectedOutcome(e.target.value)} placeholder="First order, reorder save, placement, event trial..." style={{ marginBottom: 16, borderColor: tried && !hasRationale ? 'var(--danger)' : undefined }} />

          <Lbl>Follow-up Date</Lbl>
          <input className="field" type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} style={{ marginBottom: 16 }} />
        </>}

        <Lbl>Notes</Lbl>
        <textarea className="field" rows="3" value={notes} onChange={e => setNotes(e.target.value)} placeholder={recipientMode === 'staff' ? 'Optional promo-use context...' : 'Context, approval, customer promise, or next step...'} style={{ resize: 'none', borderColor: tried && !hasRationale ? 'var(--danger)' : undefined }} />

        {tried && !valid && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>Choose available stock and a recipient. Customer/prospect giveaways need expected outcome or notes; staff promo stock does not.</div>}
        {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy} style={{ flex: 1, opacity: valid && !busy ? 1 : 0.55 }} onClick={submit}><Icon name="package" size={17} />{busy ? 'Logging...' : `Log Giveaway - ${totalQuantity || 0} cs`}</button></MActionBar>
    </div>
  );
};

/* ---------- DAMAGE / LOSS REPORT ---------- */
MOBILE_SCREENS['damage-report'] = function DamageReport({ nav, role, loc }) {
  const authRole = rpNormalizeRole(rpAuthProfile().role || role);
  const salesPersonalScope = inventoryRepackSalesRoleAllowed(role);
  const personalLocations = inventoryPersonalFieldLocations(role);
  const skus = mobileSkuOptions();
  const allLocations = mobileInventoryLocations(role);
  const fieldLocations = allLocations.filter(loc => loc.type === 'Micro-Warehouse');
  const locations = salesPersonalScope ? personalLocations : (['rep', 'partner'].includes(authRole) && fieldLocations.length ? fieldLocations : allLocations);
  const requestedDamageLocationId = salesPersonalScope
    ? inventoryPersonalValidatedFieldLocationId(locations, loc)
    : inventoryRepackValidatedFieldLocationId(role, locations, loc);
  const preferredDamageLocationId = salesPersonalScope ? inventoryPersonalPreferredFieldLocationId(locations) : '';
  const [incident, setIncident] = useState('Damaged');
  const [locationId, setLocationId] = useState(requestedDamageLocationId || (locations.find(location => location.id === preferredDamageLocationId) || (salesPersonalScope ? null : locations.find(loc => loc.id === mobileDefaultInventoryLocation(role))) || (locations.length === 1 ? locations[0] : null) || { id: '' }).id);
  const [skuId, setSkuId] = useState(skus[0] ? skus[0].id : 'RP-12-GC');
  const lots = mobileLotsFor(locationId, skuId);
  const [lot, setLot] = useState(lots[0] || 'UNASSIGNED');
  const [reason, setReason] = useState('Crushed during handling');
  const [notes, setNotes] = useState('');
  const [qty, setQty] = useState(2);
  const [ref] = useState(() => 'DMG-' + Date.now().toString().slice(-8));
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!lots.includes(lot)) setLot(lots[0] || 'UNASSIGNED');
  }, [locationId, skuId, lots.join('|')]);
  const hasExactLot = !!lot && !['UNASSIGNED', 'CYCLE_COUNT'].includes(String(lot).toUpperCase()) && lots.includes(lot);
  const submit = async () => {
    if (busy || qty <= 0) return;
    if (!hasExactLot) {
      setError('Choose an exact on-hand lot before reporting damage or loss.');
      return;
    }
    if (damagePhotos.length === 0) {
      setError('Add at least one damage photo.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      let response = { ref, summary: { status: incident === 'Lost / Missing' ? 'Removed' : 'Non-sellable' } };
      if (!rpIsDemoMode()) {
        const photos = [];
        for (const file of damagePhotos) {
          const saved = await rpUploadPrivateFile('inventory_photo', file, 'damage-report');
          if (saved) photos.push(saved);
        }
        response = await rpApi('/api/inventory/damage', {
          method: 'POST',
          body: JSON.stringify({
            incident,
            locationId,
            lot,
            notes,
            photos,
            quantity: qty,
            ref,
            reason,
            skuId
          })
        });
        await rpRefreshAfterMutation(`Damage report ${response.ref} submitted`);
      }
      setResult(response);
      setDone(true);
      toast(`Damage report ${response.ref} submitted`, 'warn');
    } catch (err) {
      const message = err && err.message ? err.message : 'Damage report could not be saved.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    const statusNote = result && result.summary && result.summary.status ? `Status: ${result.summary.status}.` : '';
    const approvalNote = result && result.summary && result.summary.approvalStatus ? `${result.summary.approvalStatus}.` : '';
    return <Success nav={nav} title="Report Submitted" sub={`${result && result.ref ? result.ref : qty + ' cases'} - ${incident}`} note={['Inventory moved out of sellable stock.', statusNote, approvalNote].filter(Boolean).join(' ')} />;
  }
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Damage / Loss" nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <Lbl>Incident Type</Lbl>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 18 }}>
          {['Damaged', 'Lost / Missing', 'Expired', 'Quality Concern'].map(t => (
            <button key={t} onClick={() => setIncident(t)} style={{ padding: '12px 10px', borderRadius: 11, border: '1.5px solid ' + (incident === t ? 'var(--danger)' : 'var(--line)'), background: incident === t ? 'var(--danger-bg)' : 'var(--surface)', fontSize: 13, fontWeight: 600, color: incident === t ? 'var(--danger)' : 'var(--ink)' }}>{t}</button>
          ))}
        </div>
        <Lbl>Location</Lbl>
        <select className="field" value={locationId} onChange={e => setLocationId(e.target.value)} style={{ marginBottom: 18 }}>
          {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
          <div style={{ flex: 1 }}><Lbl>Product</Lbl><select className="field" value={skuId} onChange={e => setSkuId(e.target.value)}>{skus.map(s => <option key={s.id} value={s.id}>{rpSkuDisplayName(s)} - {rpSkuExternalIdentity(s)}</option>)}</select></div>
          <div style={{ flex: 1 }}><Lbl>Lot</Lbl><select className="field" value={lot} onChange={e => setLot(e.target.value)}>{lots.map(x => <option key={x} value={x}>{rpPrintedLotLabel(skuId, x)}</option>)}</select></div>
        </div>
        {!hasExactLot && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, marginTop: -10, marginBottom: 14 }}>No exact lot is available for this SKU at the selected location. Reconcile the location first.</div>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <Lbl>Quantity (cases)</Lbl><Stepper value={qty} onChange={v => setQty(Math.max(0, v))} />
        </div>
        <Lbl>Reason Code</Lbl>
        <select className="field" value={reason} onChange={e => setReason(e.target.value)} style={{ marginBottom: 18 }}><option>Crushed during handling</option><option>Freight damage</option><option>Temperature excursion</option><option>Count shortage</option><option>Expired on shelf</option></select>
        <Lbl>Notes</Lbl>
        <textarea className="field" rows="2" placeholder="Add report notes..." value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'none', marginBottom: 18 }} />
        <FieldReceiptPhotoInput label="Photo Evidence" files={damagePhotos} onChange={setDamagePhotos} required />
        {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy || qty <= 0 || !hasExactLot} style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)', opacity: busy || qty <= 0 || !hasExactLot ? 0.5 : 1 }} onClick={submit}>{busy ? 'Saving...' : 'Submit Report'}</button></MActionBar>
    </div>
  );
};

/* ---------- DISPATCHER ROUTE ---------- */
MOBILE_SCREENS['dp-home'] = function DpHome({ nav }) {
  const who = useRpIdentity('dispatch');
  const directory = useRpDispatchRows({ limit: 50, scope: 'assigned' });
  const stops = directory.isLive ? mobileDispatchStopsFromDirectory(directory.items) : mobileDispatchStops();
  const activeIndex = stops.findIndex(s => MOBILE_DISPATCH_ACTION_STATUSES.has(s.rawStatus));
  const totalCases = directory.isLive && directory.summary && directory.summary.cases != null
    ? Number(directory.summary.cases || 0)
    : stops.reduce((sum, stop) => sum + Number(stop.cases || 0), 0);
  const totalStops = directory.isLive && directory.summary && directory.summary.total != null
    ? Number(directory.summary.total || 0)
    : stops.length;
  const doneCount = directory.isLive && directory.summary && directory.summary.done != null
    ? Number(directory.summary.done || 0)
    : stops.filter(stop => stop.rawStatus === 'Delivered').length;
  const openCount = directory.isLive && directory.summary && directory.summary.open != null
    ? Number(directory.summary.open || 0)
    : Math.max(0, totalStops - doneCount);
  const routeLabel = (stops[0] && stops[0].laneName) || 'Ranch Van 1';
  const routeSub = who.live ? who.name : ((stops[0] && (stops[0].driver || stops[0].routeType)) || 'Squamish');
  return (
    <div className="rp-fade">
      <div style={{ background: 'var(--navy)', color: '#fff', padding: '4px 18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}><Logo color="#fff" /><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><button onClick={() => nav.push('notifications')} style={{ border: 'none', background: 'oklch(1 0 0 / 0.12)', width: 34, height: 34, borderRadius: 10, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}><Icon name="bell" size={18} /><span style={{ position: 'absolute', top: 7, right: 8, width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)' }} /></button><button onClick={() => nav.push('settings')} style={{ border: 'none', background: 'none', padding: 0 }}><Avatar name={who.name} color="var(--amber)" /></button></div></div>
        <div style={{ fontSize: 13, opacity: 0.7 }}>{routeLabel} · {routeSub}</div>
        <div className="disp" style={{ fontSize: 24, textTransform: 'uppercase' }}>Today's Route</div>
        <div style={{ display: 'flex', gap: 4, marginTop: 14, background: 'oklch(1 0 0 / 0.07)', borderRadius: 14, padding: '14px 12px' }}>
          <MStat label="Stops" value={totalStops.toLocaleString()} accent="#fff" /><span style={{ width: 1, background: 'oklch(1 0 0 / 0.12)' }} />
          <MStat label="Cases" value={totalCases.toLocaleString()} accent="#fff" /><span style={{ width: 1, background: 'oklch(1 0 0 / 0.12)' }} />
          <MStat label="Done" value={doneCount.toLocaleString()} accent="#fff" /><span style={{ width: 1, background: 'oklch(1 0 0 / 0.12)' }} />
          <MStat label="Open" value={openCount.toLocaleString()} accent="#fff" />
        </div>
      </div>
      <MSection title="Stops">
        {directory.isLive && directory.initialLoading && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, color: 'var(--muted)', fontSize: 13.5, marginBottom: 12 }}>Loading the live route...</div>}
        <MobileDispatchError directory={directory} />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {stops.map((s, i) => {
            const active = i === activeIndex && MOBILE_DISPATCH_ACTION_STATUSES.has(s.rawStatus);
            return (
              <div key={s.id} style={{ display: 'flex', gap: 13 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', marginTop: 18, background: s.status === 'Delivered' ? 'var(--ok)' : active ? 'var(--amber)' : 'var(--surface)', border: '2px solid ' + (s.status === 'Delivered' ? 'var(--ok)' : active ? 'var(--amber)' : 'var(--line)'), boxShadow: active ? '0 0 0 4px var(--amber-soft)' : 'none' }} />
                  {i < stops.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--line)' }} />}
                </div>
                <button onClick={() => active && nav.push('delivery-pod', { id: s.id, cust: s.cust })} style={{ flex: 1, textAlign: 'left', background: active ? 'var(--surface)' : 'transparent', border: active ? '1.5px solid var(--amber)' : '1px solid var(--line)', borderRadius: 12, padding: 13, marginBottom: 12, boxShadow: active ? 'var(--shadow)' : 'none', opacity: s.rawStatus === 'Delivered' ? 0.6 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: 14.5 }}>{s.cust}</span><Pill status={s.status} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12.5, color: 'var(--muted)' }}>
                    <span className="mono">{s.id} · {s.cases} cases</span><span style={{ fontWeight: 600 }}>{s.window}</span>
                  </div>
                  {active && <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 700, color: 'var(--amber-deep)', display: 'flex', alignItems: 'center', gap: 5 }}>Capture POD<Icon name="arrowR" size={15} /></div>}
                </button>
              </div>
            );
          })}
          {stops.length === 0 && !directory.initialLoading && !directory.error && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, color: 'var(--muted)', fontSize: 13.5 }}>No picked or dispatched deliveries are ready for this route.</div>}
        </div>
        <MobileDispatchPager directory={directory} />
      </MSection>
      <div style={{ height: 20 }} />
    </div>
  );
};

MOBILE_SCREENS['dp-map'] = function DpMap({ nav }) {
  const directory = useRpDispatchRows({ limit: 50, scope: 'assigned' });
  const stops = directory.isLive ? mobileDispatchStopsFromDirectory(directory.items) : mobileDispatchStops();
  const nextStop = stops.find(s => MOBILE_DISPATCH_ACTION_STATUSES.has(s.rawStatus));
  const routeLabel = (stops[0] && stops[0].laneName) || 'Ranch Van 1';
  const totalStops = directory.isLive && directory.summary && directory.summary.total != null
    ? Number(directory.summary.total || 0)
    : stops.length;
  return (
    <div className="rp-fade">
      <MHeader title="Route Map" sub={`${routeLabel} - ${totalStops} stops`} />
      <div style={{ padding: 16 }}>
        {directory.isLive && directory.initialLoading && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, color: 'var(--muted)', fontSize: 13.5, marginBottom: 12 }}>Loading the live route...</div>}
        <MobileDispatchError directory={directory} />
        <div style={{ position: 'relative', height: 360, borderRadius: 16, overflow: 'hidden', background: 'var(--navy-900)', border: '1px solid var(--line)' }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(oklch(1 0 0 / 0.05) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
            <polyline points="60,300 130,210 230,250 280,120" fill="none" stroke="var(--amber)" strokeWidth="2.5" strokeDasharray="6 5" />
          </svg>
          {[['60', '300', 'Delivered', 'ok'], ['130', '210', 'In Transit', 'amber'], ['230', '250', 'Next', 'line'], ['280', '120', 'Next', 'line']].map((p, i) => (
            <span key={i} style={{ position: 'absolute', left: p[0] + 'px', top: p[1] + 'px', transform: 'translate(-50%,-50%)', width: 26, height: 26, borderRadius: '50%', background: p[3] === 'ok' ? 'var(--ok)' : p[3] === 'amber' ? 'var(--amber)' : 'var(--surface)', border: '2px solid ' + (p[3] === 'line' ? 'var(--line)' : 'transparent'), color: p[3] === 'line' ? 'var(--ink)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, boxShadow: '0 3px 8px oklch(0 0 0 / 0.35)' }}>{i + 1}</span>
          ))}
        </div>
        <button className="btn btn-lg btn-primary" disabled={!nextStop} style={{ width: '100%', marginTop: 14, opacity: nextStop ? 1 : 0.5 }} onClick={() => nextStop && nav.push('delivery-pod', { id: nextStop.id, cust: nextStop.cust })}><Icon name="pin" size={16} />{nextStop ? `Next Stop - ${nextStop.cust}` : 'No Stops Ready'}</button>
        <MobileDispatchPager directory={directory} />
      </div>
    </div>
  );
};

MOBILE_SCREENS['load-check'] = function LoadCheck({ nav }) {
  const discovery = useRpDispatchRows({ limit: 10, scope: 'assigned' });
  const discoveredRouteId = discovery.isLive
    ? (((discovery.items || []).find(item => item && item.route && item.route.id) || {}).route || {}).id || ''
    : '';
  const routeDirectory = useRpDispatchRows({
    enabled: !rpIsDemoMode() && Boolean(discoveredRouteId),
    limit: 50,
    routeId: discoveredRouteId,
    scope: 'assigned'
  });
  const [manifestState, setManifestState] = useState({ error: '', loading: false, manifest: null });
  const [manifestRetry, setManifestRetry] = useState(0);
  useEffect(() => {
    if (!discovery.isLive || !discoveredRouteId) {
      setManifestState({ error: '', loading: false, manifest: null });
      return undefined;
    }
    const controller = new AbortController();
    setManifestState({ error: '', loading: true, manifest: null });
    rpLoadDispatchManifest(discoveredRouteId, { signal: controller.signal })
      .then(exact => {
        if (controller.signal.aborted) return;
        setManifestState({
          error: '',
          loading: false,
          manifest: {
            ...exact,
            expectedCases: Number(exact.cases || 0),
            route: {
              id: exact.id,
              laneId: exact.laneId,
              laneName: exact.laneName,
              driver: exact.driver,
              date: exact.routeDate,
              type: exact.routeType,
              status: exact.status
            }
          }
        });
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setManifestState({
          error: err && err.message ? err.message : 'The complete route manifest could not be loaded.',
          loading: false,
          manifest: null
        });
      });
    return () => controller.abort();
  }, [discovery.isLive, discoveredRouteId, manifestRetry]);
  const activeDirectory = discovery.isLive && discoveredRouteId ? routeDirectory : discovery;
  const stops = discovery.isLive
    ? mobileDispatchStopsFromDirectory(activeDirectory.items)
    : mobileDispatchStops();
  const manifest = discovery.isLive ? manifestState.manifest : null;
  const routeId = discoveredRouteId || (stops.find(stop => stop.routeId) || {}).routeId || '';
  const routeLabel = (manifest && manifest.route && manifest.route.laneName) || (stops[0] && stops[0].laneName) || 'Vehicle';
  const loadLines = manifest && Array.isArray(manifest.lines)
    ? manifest.lines.map(line => {
        const skuId = line.skuId || line.sku_id;
        const sku = RP.sku(skuId);
        return {
          expected: Number(line.expectedQuantity ?? line.expected_quantity ?? 0),
          name: rpSkuDisplayName(sku || skuId),
          skuId
        };
      }).filter(line => line.skuId)
    : (discovery.isLive ? [] : mobileRouteLoadLines(stops));
  const loadLineKey = JSON.stringify(loadLines.map(line => [line.skuId, line.expected]));
  const [counts, setCounts] = useState([]);
  const [pallets, setPallets] = useState(1);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const totalExpected = manifest && manifest.expectedCases != null
    ? Number(manifest.expectedCases || 0)
    : loadLines.reduce((sum, line) => sum + Number(line.expected || 0), 0);
  const stopCount = manifest && manifest.stopCount != null ? Number(manifest.stopCount || 0) : stops.length;
  useEffect(() => {
    const expected = loadLines.map(line => Number(line.expected || 0));
    setCounts(expected);
    setPallets(Math.max(1, Math.ceil(expected.reduce((sum, value) => sum + value, 0) / 60)));
  }, [loadLineKey]);
  const countedCases = counts.reduce((sum, value) => sum + Number(value || 0), 0);
  const variance = countedCases - totalExpected;
  const submit = async () => {
    if (busy || !loadLines.length) return;
    setBusy(true);
    setError('');
    try {
      if (!rpIsDemoMode()) {
        if (!routeId) throw new Error('Assign orders to a route before submitting a vehicle load check.');
        await rpApi('/api/dispatch/load-check', {
          method: 'POST',
          body: JSON.stringify({
            countedCases,
            lines: loadLines.map((line, index) => ({
              countedQuantity: counts[index],
              expectedQuantity: line.expected,
              skuId: line.skuId
            })),
            notes,
            palletCount: pallets,
            routeId
          })
        });
        await rpRefreshAfterMutation('Vehicle load check saved');
      }
      setDone(true);
      toast('Vehicle load check submitted', 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Load check could not be saved.';
      setError(message);
      toast(message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success nav={nav} title="Load Confirmed" sub={routeLabel} note={`${countedCases} cases checked across ${loadLines.length} SKU${loadLines.length === 1 ? '' : 's'}.`} />;

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Vehicle Load Check" sub={`${routeLabel} - ${stopCount} stops`} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        {activeDirectory.isLive && (activeDirectory.initialLoading || manifestState.loading) && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, color: 'var(--muted)', fontSize: 13.5, marginBottom: 12 }}>Loading the complete route manifest...</div>}
        <MobileDispatchError directory={activeDirectory} />
        {manifestState.error && <div style={{ background: 'var(--surface)', border: '1px solid var(--danger)', borderRadius: 12, padding: 16, color: 'var(--danger)', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{manifestState.error}<button className="btn btn-sm btn-ghost" style={{ marginLeft: 10 }} onClick={() => setManifestRetry(value => value + 1)}>Retry manifest</button></div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
          <MStat label="Stops" value={stopCount} />
          <MStat label="Expected" value={totalExpected} />
          <MStat label="Variance" value={variance === 0 ? '0' : (variance > 0 ? '+' : '') + variance} />
        </div>
        {!loadLines.length && !activeDirectory.initialLoading && !manifestState.loading && !activeDirectory.error && !manifestState.error && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, color: 'var(--muted)', fontSize: 13.5 }}>No assigned route manifest is available yet.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loadLines.map((line, index) => {
            const sku = RP.sku(line.skuId);
            const diff = Number(counts[index] || 0) - Number(line.expected || 0);
            return (
              <div key={line.skuId} style={{ background: 'var(--surface)', border: '1px solid ' + (diff ? 'var(--warn)' : 'var(--line)'), borderRadius: 12, padding: 13, boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FlavorChip flavorId={sku && sku.flavor} size={30} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{line.name}</div>
                    <div style={{ fontSize: 11.5, color: diff ? 'var(--warn)' : 'var(--faint)', fontWeight: diff ? 700 : 400, marginTop: 2 }}>{diff ? `${diff > 0 ? '+' : ''}${diff} vs manifest (${line.expected})` : `Manifest ${line.expected} cases`}</div>
                  </div>
                  <Stepper value={counts[index]} onChange={value => setCounts(current => current.map((x, i) => i === index ? Math.max(0, value) : x))} />
                </div>
              </div>
            );
          })}
        </div>
        <MobileDispatchPager directory={activeDirectory} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '13px 14px', marginTop: 14 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>CHEP pallets</span>
          <Stepper value={pallets} onChange={value => setPallets(Math.max(0, value))} />
        </div>
        <Lbl style={{ marginTop: 16 }}>Notes</Lbl>
        <textarea className="field" rows="2" placeholder="Seal number, variance notes, or driver handoff..." value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'none' }} />
        {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{error}</div>}
      </div>
      <MActionBar>
        <div style={{ flex: 1, alignSelf: 'center', paddingLeft: 4 }}><div style={{ fontSize: 11, color: 'var(--faint)' }}>Counted</div><div className="disp" style={{ fontSize: 20, color: variance ? 'var(--warn)' : 'var(--ok)' }}>{countedCases} cs</div></div>
        <button className="btn btn-lg btn-primary" disabled={busy || !loadLines.length || activeDirectory.initialLoading || manifestState.loading || Boolean(activeDirectory.error) || Boolean(manifestState.error)} style={{ flex: '0 0 auto', opacity: busy || !loadLines.length || activeDirectory.initialLoading || manifestState.loading || activeDirectory.error || manifestState.error ? 0.5 : 1 }} onClick={submit}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Submit'}</button>
      </MActionBar>
    </div>
  );
};

/* ---------- DELIVERY / POD ---------- */
MOBILE_SCREENS['delivery-pod'] = function DeliveryPOD({ nav, id, cust }) {
  const exactOrder = useRpExactOrder({ orderId: id });
  const order = exactOrder.order;
  const customerName = mobileCustomerName(order, cust);
  const lineItems = mobileDeliveryLines(order);
  const totalCases = mobileOrderTotals(order).cases || lineItems.reduce((sum, line) => sum + Number(line.qty || 0), 0);
  const [recv, setRecv] = useState(() => order ? (totalCases || 24) : 0);
  const [dmg, setDmg] = useState(0);
  const [name, setName] = useState('');
  const [signed, setSigned] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (order) setRecv(totalCases || 24);
  }, [id, Boolean(order), totalCases]);
  const markDelivered = async () => {
    if (!order || busy || !signed || !name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const note = `POD captured by ${name.trim()}. Received ${recv} cases. Damaged ${dmg} cases.`;
      await mobilePersistOrderStatus(order, 'Delivered', note);
      setDone(true);
      toast(`${order.id} marked Delivered`, 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Delivery could not be saved.';
      setError(message);
      toast(message, 'warn');
    } finally {
      setBusy(false);
    }
  };
  if (exactOrder.loading) return (
    <div className="rp-fade" data-exact-order-loading="true">
      <MHeader title="Delivery" sub={`Loading ${id}`} nav={nav} back />
      <MEmpty icon="refresh" title="Loading delivery" sub="Fetching the complete order and delivery details." />
    </div>
  );
  if (exactOrder.error) return (
    <div className="rp-fade" data-exact-order-error="true">
      <MHeader title="Delivery" sub="Order unavailable" nav={nav} back />
      <MEmpty icon="alert" title="Delivery could not load" sub={exactOrder.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactOrder.retry}>Try Again</button>} />
    </div>
  );
  if (!order && !rpIsDemoMode()) return (
    <div className="rp-fade">
      <MHeader title="Delivery" sub="Order not found" nav={nav} back />
      <div style={{ padding: 16, color: 'var(--muted)', fontSize: 13.5 }}>This delivery is no longer available in the current route.</div>
    </div>
  );
  if (done) return <Success nav={nav} title="Delivered" sub={`${id} - ${customerName}`} note="Proof of delivery captured. Order moved to Delivered." />;
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title={`Delivery ${id}`} sub={customerName} nav={nav} back right={<Pill status={mobileDisplayOrderStatus(order && order.status)} />} />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 14, boxShadow: 'var(--shadow-sm)' }}>
          <span className="eyebrow">Delivery Items</span>
          <div style={{ marginTop: 10 }}>
            {lineItems.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 0', borderBottom: i < lineItems.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
                <FlavorChip flavorId={l.flavor} size={24} /><span style={{ flex: 1, fontSize: 13.5 }}>{l.name}</span><span className="mono" style={{ fontWeight: 600 }}>{l.qty} cs</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 9, marginTop: 3, fontSize: 12.5, color: 'var(--muted)' }}><span>CHEP pallets</span><span className="mono">1</span></div>
          </div>
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: 'var(--shadow-sm)' }}>
          <span className="eyebrow">Receiving Confirmation</span>
          <div style={{ marginTop: 8 }}>
            <CountRow label="Cases received" value={recv} onMinus={() => setRecv(Math.max(0, recv - 1))} onPlus={() => setRecv(recv + 1)} />
            <CountRow label="Damaged cases" value={dmg} danger onMinus={() => setDmg(Math.max(0, dmg - 1))} onPlus={() => setDmg(dmg + 1)} />
          </div>
        </div>
        <Lbl>Receiver Name</Lbl>
        <input className="field" placeholder="Who received it?" value={name} onChange={e => setName(e.target.value)} style={{ marginBottom: 16 }} />
        <Lbl>Signature</Lbl>
        <button onClick={() => setSigned(true)} style={{ width: '100%', height: 100, borderRadius: 12, border: '1.5px ' + (signed ? 'solid var(--ok)' : 'dashed var(--line)'), background: signed ? 'var(--ok-bg)' : 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: signed ? 'var(--ok)' : 'var(--faint)', marginBottom: 16 }}>
          {signed ? <><svg width="120" height="40" viewBox="0 0 120 40"><path d="M6 28 Q20 6 32 24 T58 22 Q72 4 86 26 L110 14" fill="none" stroke="var(--ink)" strokeWidth="2.5" strokeLinecap="round" /></svg><span style={{ fontSize: 11.5, fontWeight: 600 }}>Signed</span></> : <><Icon name="pen" size={24} /><span style={{ fontSize: 12.5, fontWeight: 600 }}>Tap to capture signature</span></>}
        </button>
        <Lbl>Delivery Photo</Lbl>
        <button className="btn" style={{ width: '100%', height: 70, flexDirection: 'column', gap: 5, borderStyle: 'dashed' }}><Icon name="camera" size={22} /><span style={{ fontSize: 11.5 }}>Add Photo</span></button>
        {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" style={{ flex: 1, opacity: signed && name && !busy ? 1 : 0.5 }} disabled={!signed || !name || busy || !order} onClick={markDelivered}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Mark Delivered'}</button></MActionBar>
    </div>
  );
};

Object.assign(window, { CountRow });


/* ===== mobile_more.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : More menus + Notifications · Settings ·
   Documents · Transfer Stock · Cycle Count
   ============================================================ */

/* ---------- Notifications (per role) ---------- */
const NOTES = {
  rep: [
    { i: 'refresh', tone: 'danger', t: 'Superette Market is due to reorder', m: '12 days since last order', when: '8m ago', unread: true, go: n => n.push('rep-customer', { id: 'C-001' }) },
    { i: 'flag', tone: 'warn', t: 'SO-1046 needs manager approval', m: '14% discount over your authority', when: '1h ago', unread: true, go: n => n.push('rep-order-detail', { id: 'SO-1046' }) },
    { i: 'check', tone: 'ok', t: 'SO-1041 was delivered', m: 'Mountain Gym · signed by Priya N.', when: '2h ago', go: n => n.push('rep-order-detail', { id: 'SO-1041' }) },
    { i: 'inventory', tone: 'warn', t: 'Peach Mango low in your storage', m: '5 cases left — below safety stock', when: '3h ago', go: n => n.push('rep-inventory') },
  ],
  warehouse: [
    { i: 'download', tone: 'warn', t: 'SH-330 arriving today', m: '180 cases from Northern BC', when: '15m ago', unread: true, go: n => n.tab('wh-receive') },
    { i: 'alert', tone: 'danger', t: '3 damaged cases pending write-off', m: 'Ginger Citrus · RP-GC-001', when: '1h ago', unread: true, go: n => n.push('damage-report') },
    { i: 'package', tone: 'info', t: '14 orders in the pick queue', m: '5 ready to start', when: '2h ago', go: n => n.tab('wh-pick') },
    { i: 'refresh', tone: 'warn', t: '2 cycle counts due', m: 'Aisle A · Aisle C', when: 'Today', go: n => n.push('cycle-count') },
  ],
  dispatch: [
    { i: 'truck', tone: 'info', t: 'Route assigned — 4 stops', m: 'Ranch Van 1 · 50 cases', when: '30m ago', unread: true, go: n => n.tab('dp-home') },
    { i: 'pin', tone: 'warn', t: 'Next delivery window', m: 'Open the active route stop', when: '1h ago', go: n => { const stop = mobileDispatchStops().find(s => MOBILE_DISPATCH_ACTION_STATUSES.has(s.rawStatus)); stop ? n.push('delivery-pod', { id: stop.id, cust: stop.cust }) : n.tab('dp-home'); } },
    { i: 'check', tone: 'ok', t: 'Mountain Gym delivered', m: 'POD captured · 12 cases', when: '2h ago' },
  ],
};

function mobileNotificationItems(role) {
  const demo = (NOTES[role] || NOTES.rep).map(x => ({ ...x }));
  if (rpHasLiveAuth() && role === 'dispatch') {
    const stops = mobileDispatchStops();
    const next = stops.find(s => MOBILE_DISPATCH_ACTION_STATUSES.has(s.rawStatus));
    const delivered = stops.find(s => s.rawStatus === 'Delivered');
    const items = [
      {
        i: 'truck',
        tone: 'info',
        t: `${stops.length} route stop${stops.length === 1 ? '' : 's'} assigned`,
        m: `${stops.reduce((sum, stop) => sum + Number(stop.cases || 0), 0)} cases`,
        when: 'Now',
        unread: !!next,
        go: n => n.tab('dp-home')
      }
    ];
    if (next) items.push({ i: 'pin', tone: 'warn', t: `${next.cust} is ready for POD`, m: `${next.id} - ${next.cases} cases`, when: 'Now', unread: true, go: n => n.push('delivery-pod', { id: next.id, cust: next.cust }) });
    if (delivered) items.push({ i: 'check', tone: 'ok', t: `${delivered.cust} delivered`, m: `${delivered.id} - POD captured`, when: 'Recent', go: null });
    return stops.length ? items : [{ i: 'check', tone: 'ok', t: 'No active route notifications', m: 'No picked or dispatched deliveries are ready.', when: 'Now', go: null }];
  }
  if (rpHasLiveAuth() && role === 'warehouse') {
    const items = [];
    const pickQueue = (RP.ORDERS || []).filter(order => MOBILE_PICK_QUEUE_STATUSES.has(String(order.status || '')));
    const pendingStock = (RP.STOCK_REQUESTS || []).filter(request => ['Requested', 'Pending', 'Approved'].includes(String(request.status || '')));
    const lowStock = typeof dashboardStockoutRisks === 'function' ? dashboardStockoutRisks()[0] : null;
    const me = rpFieldUserName(role);
    const task = (RP.TASKS || []).find(row => row.owner === me && row.status !== 'Done');
    if (pickQueue.length) items.push({ i: 'package', tone: 'info', t: `${pickQueue.length} order${pickQueue.length === 1 ? '' : 's'} in the pick queue`, m: `${pickQueue.reduce((sum, order) => sum + mobileOrderTotals(order).cases, 0)} cases`, when: 'Now', unread: true, go: n => n.tab('wh-pick') });
    if (pendingStock.length) items.push({ i: 'layers', tone: 'warn', t: `${pendingStock.length} field stock request${pendingStock.length === 1 ? '' : 's'} pending`, m: pendingStock.slice(0, 3).map(request => request.id).join(' - '), when: 'Now', unread: true, go: n => n.push('request-stock') });
    if (lowStock) items.push({ i: 'inventory', tone: 'warn', t: `${lowStock.sku.name || lowStock.sku.id} is low`, m: `${lowStock.qty} cases available`, when: 'Now', unread: true, go: n => n.push('cycle-count') });
    if (task) items.push({ i: 'tasks', tone: task.status === 'Blocked' || task.priority === 'Urgent' ? 'danger' : 'warn', t: task.title || task.id, m: task.due ? `Due ${String(task.due).slice(5)}` : task.status, when: task.status || 'Open task', unread: true, go: n => n.push('my-tasks') });
    return items.length ? items : [{ i: 'check', tone: 'ok', t: 'No active warehouse notifications', m: 'Warehouse work is clear for your profile.', when: 'Now', go: null }];
  }
  if (!rpHasLiveAuth() || role !== 'rep') return demo;
  const customers = rpFieldCustomers(role);
  const orders = rpFieldOrders(role);
  const items = [];
  const workflowPreview = typeof rpDashboardWorkflowIntelligenceEnabled === 'function' && rpDashboardWorkflowIntelligenceEnabled(role);
  const reorder = customers.find(c => {
    const summary = rpCustomerOrderSummary(c);
    return c.risk === 'High' || Number(summary.daysSince ?? c.daysSince ?? 0) >= 12;
  });
  if (reorder) {
    const summary = rpCustomerOrderSummary(reorder);
    items.push({ i: 'refresh', tone: 'danger', t: `${reorder.name} is due to reorder`, m: summary.daysSince ? `${summary.daysSince} days since last order` : 'High reorder risk', when: 'Now', unread: true, go: n => n.push('rep-customer', { id: reorder.id }) });
  }
  const approval = orders.find(o => ['Needs Approval', 'On Hold'].includes(o.status));
  if (approval) items.push({ i: 'flag', tone: 'warn', t: `${approval.id} needs attention`, m: approval.reason || approval.status, when: 'Now', unread: true, go: n => n.push('rep-order-detail', { id: approval.id }) });
  const delivered = orders.find(o => o.status === 'Delivered');
  if (delivered) items.push({ i: 'check', tone: 'ok', t: `${delivered.id} was delivered`, m: delivered.date || 'Recently delivered', when: 'Recent', go: n => n.push('rep-order-detail', { id: delivered.id }) });
  const lowStock = rpFieldInventory(role).find(m => m.available <= 5);
  if (lowStock) items.push({ i: 'inventory', tone: 'warn', t: `${rpSkuDisplayName(RP.sku(lowStock.sku) || lowStock.sku)} low in your storage`, m: `${lowStock.available} cases left`, when: 'Now', go: n => n.push('rep-inventory') });
  if (workflowPreview) {
    const stockOutcome = rpFieldStockRequests(role).find(request => ['Approved', 'Fulfilled', 'Rejected'].includes(String(request.status || '')));
    if (stockOutcome) items.push({ i: 'layers', tone: stockOutcome.status === 'Rejected' ? 'danger' : 'info', t: `Stock request ${stockOutcome.status.toLowerCase()}`, m: `${stockOutcome.id} - ${stockRequestTotal(stockOutcome)} cases`, when: 'Recent', unread: stockOutcome.status !== 'Fulfilled', go: n => n.push('stock-request-detail', { id: stockOutcome.id }) });

    const approvedDoor = customers.find(c => {
      const summary = rpCustomerOrderSummary(c);
      return rpCustomerOrderReady(c) && !summary.orderCount && !summary.lastOrder && !summary.lastOrderDate;
    });
    if (approvedDoor) items.push({ i: 'customers', tone: 'info', t: `${approvedDoor.name} is ready for a first order`, m: approvedDoor.city || approvedDoor.type || 'Approved customer', when: 'Now', unread: true, go: n => n.push('rep-customer', { id: approvedDoor.id }) });

    const invoiceOrder = orders.find(order => ['Overdue', 'Unpaid', 'Partial'].includes(String(order.invoiceStatus || order.financialStatus || '')));
    if (invoiceOrder) items.push({ i: 'receipt', tone: String(invoiceOrder.invoiceStatus || invoiceOrder.financialStatus) === 'Overdue' ? 'danger' : 'warn', t: `${invoiceOrder.id} invoice needs follow-up`, m: invoiceOrder.invoiceStatus || invoiceOrder.financialStatus || 'Payment pending', when: 'Now', unread: true, go: n => n.push('rep-order-detail', { id: invoiceOrder.id }) });

    const task = rpFieldTasks(role).find(row => row.status !== 'Done' && ['Urgent', 'High'].includes(String(row.priority || '')));
    if (task) items.push({ i: 'tasks', tone: task.priority === 'Urgent' ? 'danger' : 'warn', t: task.title || task.id, m: task.due ? `Due ${String(task.due).slice(5)}` : task.status || 'Assigned task', when: 'Now', unread: true, go: n => n.push('task-detail', { id: task.id }) });
  }
  return items.length ? items : [{ i: 'check', tone: 'ok', t: 'No active notifications', m: 'Everything assigned to your profile is clear.', when: 'Now', go: null }];
}

function mobileUnreadNotificationCount(role) {
  if (rpHasLiveAuth() && Number.isFinite(Number(window.RP_NOTIFICATION_UNREAD_COUNT))) {
    return Number(window.RP_NOTIFICATION_UNREAD_COUNT);
  }
  return mobileNotificationItems(role).filter(item => item && item.unread).length;
}

function mobileNotificationWhen(value) {
  const timestamp = new Date(value || '');
  if (!Number.isFinite(timestamp.getTime())) return 'Recent';
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp.getTime()) / 60000));
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d ago` : timestamp.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function mobileNotificationVisual(item) {
  const kind = String(item && item.kind || '');
  if (kind.startsWith('task.')) return { i: 'tasks', tone: item.priority === 'urgent' ? 'danger' : 'warn' };
  if (kind.startsWith('order.')) return { i: 'orders', tone: item.priority === 'high' || item.priority === 'urgent' ? 'danger' : 'info' };
  if (kind.startsWith('stock_request.')) return { i: 'layers', tone: kind.endsWith('.rejected') ? 'danger' : 'info' };
  if (kind.startsWith('expense.')) return { i: 'receipt', tone: 'warn' };
  return { i: 'bell', tone: item.priority === 'high' || item.priority === 'urgent' ? 'warn' : 'info' };
}

function mobileOpenNotification(nav, item) {
  const id = item && item.entityId;
  if (item.entityType === 'task' && id) return nav.push('task-detail', { id });
  if (item.entityType === 'sales_order' && id) return nav.push('rep-order-detail', { id });
  if (item.entityType === 'stock_request' && id) return nav.push('stock-request-detail', { id });
  if (item.entityType === 'customer' && id) return nav.push('rep-customer', { id });
  if (item.entityType === 'lead' && id) return nav.push('lead-detail', { id });
  if (item.entityType === 'expense' && id) return nav.push('my-expenses', { id });
  return null;
}

function mobileSignOut(nav, role) {
  if (window.RP_SIGN_OUT) window.RP_SIGN_OUT();
  else nav.tab(ROLE_HOME[role]);
}

MOBILE_SCREENS['notifications'] = function Notifications({ nav, role }) {
  const live = rpHasLiveAuth();
  const [items, setItems] = useState(() => live ? [] : mobileNotificationItems(role).map((item, index) => ({ ...item, id: `demo-${index}` })));
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState('');
  const load = async () => {
    if (!live) return;
    setLoading(true);
    setError('');
    try {
      const payload = await rpApi('/api/notifications/list?limit=100');
      const next = Array.isArray(payload.notifications) ? payload.notifications : [];
      setItems(next);
      window.RP_NOTIFICATION_UNREAD_COUNT = Number(payload.unreadCount || 0);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not load notifications.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [live]);
  const update = async (ids, action) => {
    const targetIds = ids.filter(Boolean);
    if (!targetIds.length) return;
    if (!live) {
      setItems(current => current.map(item => targetIds.includes(item.id) ? { ...item, unread: false } : item));
      return;
    }
    const previous = items;
    if (action === 'dismiss') setItems(current => current.filter(item => !targetIds.includes(item.id)));
    else setItems(current => current.map(item => targetIds.includes(item.id) ? { ...item, readAt: action === 'read' ? new Date().toISOString() : null } : item));
    try {
      const payload = await rpApi('/api/notifications/update', { method: 'POST', body: JSON.stringify({ action, ids: targetIds }) });
      const updatedById = new Map((payload.notifications || []).map(item => [item.id, item]));
      setItems(current => current.map(item => updatedById.get(item.id) || item));
      const nextUnread = (action === 'read' || action === 'dismiss')
        ? Math.max(0, Number(window.RP_NOTIFICATION_UNREAD_COUNT || 0) - previous.filter(item => targetIds.includes(item.id) && !item.readAt && !item.dismissedAt).length)
        : Number(window.RP_NOTIFICATION_UNREAD_COUNT || 0);
      window.RP_NOTIFICATION_UNREAD_COUNT = nextUnread;
    } catch (err) {
      setItems(previous);
      toast(err && err.message ? err.message : 'Could not update notification.', 'danger');
    }
  };
  const markAll = () => update(items.filter(item => live ? !item.readAt : item.unread).map(item => item.id), 'read');
  const hasUnread = items.some(item => live ? !item.readAt : item.unread);
  return (
    <div className="rp-fade">
      <MHeader title="Notifications" nav={nav} back right={hasUnread ? <button onClick={markAll} style={{ border: 'none', background: 'none', color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 700 }}>Mark all read</button> : null} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading your inbox…</div>}
        {error && <div style={{ padding: 14, border: '1px solid var(--danger)', borderRadius: 10, color: 'var(--danger)', fontSize: 12.5 }}>{error}<button className="btn" onClick={load} style={{ width: '100%', marginTop: 10 }}>Try again</button></div>}
        {!loading && !error && !items.length && <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}><Icon name="check" size={24} /><div style={{ marginTop: 8, fontWeight: 650, color: 'var(--ink)' }}>You’re all caught up</div><div style={{ marginTop: 3 }}>Assigned work and status updates will appear here.</div></div>}
        {items.map((a, i) => {
          const visual = live ? mobileNotificationVisual(a) : a;
          const unread = live ? !a.readAt : a.unread;
          const [fg, bg] = statusColors(visual.tone === 'danger' ? 'High' : visual.tone === 'warn' ? 'Pending' : visual.tone === 'ok' ? 'Approved' : 'Submitted');
          return (
            <div key={a.id || i} style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <button onClick={async () => { if (unread) await update([a.id], 'read'); if (live) mobileOpenNotification(nav, a); else a.go && a.go(nav); }}
              style={{ flex: 1, display: 'flex', gap: 12, alignItems: 'flex-start', background: unread ? 'var(--surface)' : 'transparent', border: '1px solid ' + (unread ? 'var(--line)' : 'var(--line-2)'), borderRadius: 12, padding: '13px 13px', textAlign: 'left', boxShadow: unread ? 'var(--shadow-sm)' : 'none' }}>
              <span style={{ width: 36, height: 36, borderRadius: 10, background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={visual.i} size={17} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3 }}>{live ? a.title : a.t}</span>
                  {unread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0, marginTop: 4 }} />}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{live ? a.body : a.m}</div>
                <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 4 }}>{live ? mobileNotificationWhen(a.createdAt) : a.when}</div>
              </div>
            </button>
            {live && <button onClick={() => update([a.id], 'dismiss')} aria-label="Dismiss notification" title="Dismiss" style={{ width: 38, border: '1px solid var(--line-2)', borderRadius: 10, background: 'transparent', color: 'var(--faint)' }}><Icon name="x" size={15} /></button>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- Role-specific MORE menu ---------- */
const openTasksFor = nm => {
  if (rpHasLiveAuth()) return RP.TASKS.filter(t => t.status !== 'Done').length;
  return RP.TASKS.filter(t => t.owner === nm && t.status !== 'Done').length;
};
const MORE_MENUS = {
  rep: [
    { sec: 'Workflows', items: [
      { i: 'tasks', l: 'My Tasks', badge: r => openTasksFor(rpFieldUserName(r)), go: n => n.push('my-tasks') },
      { i: 'plus', l: 'New Order', go: n => n.push('create-order') },
      { i: 'trend', l: 'Sales Pipeline', go: n => n.tab('rep-pipeline') },
      { i: 'inventory', l: 'My Inventory', go: n => n.push('rep-inventory') },
      { i: 'calendar', l: 'Events', badge: r => mobileActiveEventCount(r), go: n => n.push('rep-events') },
      { i: 'pin', l: 'Store Visit Log', go: (n, r) => { const id = rpFieldFirstCustomerId(r); id ? n.push('visit-log', { id }) : n.tab('rep-customers'); } },
      { i: 'layers', l: 'Request Stock Transfer', go: n => n.push('request-stock') },
      { i: 'package', l: 'Log Giveaway', go: n => n.push('giveaway-log') },
      { i: 'refresh', l: 'Count My Storage', go: (n, r) => { const micro = rpFieldMicroWarehouse(r); micro ? n.push('cycle-count', { loc: micro.id }) : toast(inventoryPersonalNoLocationMessage(), 'info'); } },
      { i: 'alert', l: 'Report Damage / Loss', go: n => n.push('damage-report') },
    ] },
    { sec: 'Performance & Expenses', items: [
      { i: 'target', l: 'My Goals', go: n => n.push('my-goals') },
      { i: 'wallet', l: 'Submit Expense', go: n => n.push('submit-expense') },
      { i: 'receipt', l: 'My Expenses', go: n => n.push('my-expenses') },
    ] },
    { sec: 'Resources', items: [
      { i: 'mail', l: 'Support / Feedback', go: n => n.push('support-ticket') },
      { i: 'bell', l: 'Notifications', badge: r => mobileUnreadNotificationCount(r), go: n => n.push('notifications') },
    ] },
    { sec: 'Account', items: [
      { i: 'admin', l: 'Desktop Workspace', go: () => window.RP_SET_SALES_LAYOUT && window.RP_SET_SALES_LAYOUT('desktop') },
      { i: 'user', l: 'My Account', go: n => n.push('settings') },
      { i: 'admin', l: 'Settings', go: n => n.push('settings') },
    ] },
  ],
  warehouse: [
    { sec: 'Workflows', items: [
      { i: 'tasks', l: 'My Tasks', badge: r => openTasksFor(rpFieldUserName(r)), go: n => n.push('my-tasks') },
      { i: 'package', l: 'Pick Orders', go: n => n.tab('wh-pick') },
      { i: 'download', l: 'Receive Stock', go: n => n.tab('wh-receive') },
      { i: 'layers', l: 'Transfer Stock', go: n => n.push('transfer-stock') },
      { i: 'refresh', l: 'Cycle Count', go: n => n.push('cycle-count', { loc: 'WH-SQ' }) },
      { i: 'alert', l: 'Report Damage / Loss', go: n => n.push('damage-report') },
    ] },
    { sec: 'Expenses', items: [
      { i: 'wallet', l: 'Submit Expense', go: n => n.push('submit-expense') },
      { i: 'receipt', l: 'My Expenses', go: n => n.push('my-expenses') },
    ] },
    { sec: 'Resources', items: [
      { i: 'doc', l: 'Documents & SOPs', go: n => n.push('documents') },
      { i: 'mail', l: 'Support / Feedback', go: n => n.push('support-ticket') },
      { i: 'bell', l: 'Notifications', badge: r => mobileUnreadNotificationCount(r), go: n => n.push('notifications') },
    ] },
    { sec: 'Account', items: [{ i: 'user', l: 'My Account', go: n => n.push('settings') }] },
  ],
  dispatch: [
    { sec: 'Workflows', items: [
      { i: 'tasks', l: 'My Tasks', badge: r => openTasksFor(rpFieldUserName(r)), go: n => n.push('my-tasks') },
      { i: 'truck', l: "Today's Route", go: n => n.tab('dp-home') },
      { i: 'pin', l: 'Route Map', go: n => n.tab('dp-map') },
      { i: 'box', l: 'Vehicle Load Check', go: n => n.push('load-check') },
      { i: 'alert', l: 'Report Damage / Loss', go: n => n.push('damage-report') },
    ] },
    { sec: 'Expenses', items: [
      { i: 'wallet', l: 'Submit Expense', go: n => n.push('submit-expense') },
      { i: 'receipt', l: 'My Expenses', go: n => n.push('my-expenses') },
    ] },
    { sec: 'Resources', items: [
      { i: 'doc', l: 'BOLs & Documents', go: n => n.push('documents') },
      { i: 'mail', l: 'Support / Feedback', go: n => n.push('support-ticket') },
      { i: 'bell', l: 'Notifications', badge: r => mobileUnreadNotificationCount(r), go: n => n.push('notifications') },
    ] },
    { sec: 'Account', items: [{ i: 'user', l: 'My Account', go: n => n.push('settings') }] },
  ],
};

MOBILE_SCREENS['more'] = function More({ nav, role }) {
  const who = useRpIdentity(role);
  const menu = MORE_MENUS[role] || MORE_MENUS.rep;
  const [, refreshUnread] = useState(0);
  useEffect(() => {
    if (!rpHasLiveAuth()) return;
    let cancelled = false;
    rpApi('/api/notifications/list?limit=1')
      .then(payload => {
        if (cancelled) return;
        window.RP_NOTIFICATION_UNREAD_COUNT = Number(payload.unreadCount || 0);
        refreshUnread(value => value + 1);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [role]);
  return (
    <div className="rp-fade">
      <MHeader title="More" />
      <div style={{ padding: '14px 16px 0' }}>
        {/* profile card */}
        <button onClick={() => nav.push('settings')} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, background: 'var(--navy)', borderRadius: 14, padding: 15, border: 'none', textAlign: 'left', color: '#fff' }}>
          <Avatar name={who.name} size={46} color="var(--amber)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{who.name}</div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>{who.role} · {who.desc}</div>
          </div>
          <Icon name="chevR" size={18} style={{ opacity: 0.7 }} />
        </button>
      </div>
      {menu.map(group => (
        <div key={group.sec} style={{ padding: '0 16px', marginTop: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>{group.sec}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.items.map(it => {
              const badge = typeof it.badge === 'function' ? it.badge(role) : it.badge;
              return (
              <button key={it.l} onClick={() => it.go(nav, role)} style={{ display: 'flex', alignItems: 'center', gap: 13, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '14px 15px', textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--surface-2)', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={it.i} size={18} /></span>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{it.l}</span>
                {badge && <span style={{ background: 'var(--amber)', color: '#fff', fontSize: 10.5, fontWeight: 700, borderRadius: 100, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{badge}</span>}
                <Icon name="chevR" size={17} style={{ color: 'var(--faint)' }} />
              </button>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ padding: 16, marginTop: 8 }}>
        <button onClick={() => mobileSignOut(nav, role)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, background: 'transparent', border: '1px solid var(--line)', borderRadius: 12, padding: '13px', color: 'var(--danger)', fontSize: 14, fontWeight: 600 }}>
          <Icon name="arrowL" size={16} />Sign Out
        </button>
        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--faint)', marginTop: 14 }}>Ranch Power Ops · v1.0 · Grit. Driven.</div>
      </div>
    </div>
  );
};

/* ---------- SETTINGS / MY ACCOUNT ---------- */
MOBILE_SCREENS['settings'] = function Settings({ nav, role }) {
  const who = useRpIdentity(role);
  const live = rpHasLiveAuth();
  const authProfile = rpAuthProfile();
  const authDetails = rpAuthDetails();
  const [displayName, setDisplayName] = useState(who.name || '');
  const [quotePhone, setQuotePhone] = useState(authProfile.quote_phone || '');
  const [quoteLocation, setQuoteLocation] = useState(authProfile.quote_location || '');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [notificationPrefs, setNotificationPrefs] = useState({
    pushEnabled: true,
    quietHoursEnabled: false,
    quietHoursStart: '21:00',
    quietHoursEnd: '07:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Vancouver',
    categories: { order: true, task: true, stock_request: true }
  });
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  useEffect(() => {
    setDisplayName(who.name || '');
  }, [who.name]);
  useEffect(() => {
    setQuotePhone(authProfile.quote_phone || '');
    setQuoteLocation(authProfile.quote_location || '');
  }, [authProfile.quote_phone, authProfile.quote_location]);
  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    setNotificationBusy(true);
    rpApi('/api/notifications/preferences')
      .then(payload => {
        if (cancelled || !payload.preferences) return;
        setNotificationPrefs(current => ({ ...current, ...payload.preferences, categories: { ...current.categories, ...(payload.preferences.categories || {}) } }));
      })
      .catch(err => { if (!cancelled) setNotificationMessage(err && err.message ? err.message : 'Could not load notification preferences.'); })
      .finally(() => { if (!cancelled) setNotificationBusy(false); });
    return () => { cancelled = true; };
  }, [live]);
  const toggleNotificationCategory = key => setNotificationPrefs(current => ({
    ...current,
    categories: { ...current.categories, [key]: current.categories[key] === false }
  }));
  const saveNotificationPrefs = async () => {
    if (!live || notificationBusy) return;
    setNotificationBusy(true);
    setNotificationMessage('');
    try {
      const payload = await rpApi('/api/notifications/preferences', { method: 'POST', body: JSON.stringify(notificationPrefs) });
      setNotificationPrefs(current => ({ ...current, ...(payload.preferences || {}), categories: { ...current.categories, ...((payload.preferences && payload.preferences.categories) || {}) } }));
      setNotificationMessage('Notification preferences saved.');
      toast('Notification preferences saved', 'ok');
    } catch (err) {
      const message = err && err.message ? err.message : 'Could not save notification preferences.';
      setNotificationMessage(message);
      toast(message, 'danger');
    } finally {
      setNotificationBusy(false);
    }
  };
  const saveProfile = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.updateProfile || profileBusy) return;
    setProfileBusy(true);
    setProfileMessage('');
    try {
      await window.RP_ACCOUNT.updateProfile({
        display_name: displayName,
        quote_phone: quotePhone,
        quote_location: quoteLocation,
        setup_complete: true
      });
      setProfileMessage('Profile saved.');
      toast('Profile saved', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not save profile.';
      setProfileMessage(msg);
      toast(msg, 'danger');
    } finally {
      setProfileBusy(false);
    }
  };
  const savePassword = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.updatePassword || passwordBusy) return;
    setPasswordMessage('');
    if (newPassword !== confirmPassword) {
      setPasswordMessage('Passwords do not match.');
      return;
    }
    setPasswordBusy(true);
    try {
      await window.RP_ACCOUNT.updatePassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('Password updated.');
      toast('Password updated', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not update password.';
      setPasswordMessage(msg);
      toast(msg, 'danger');
    } finally {
      setPasswordBusy(false);
    }
  };
  const sendReset = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.sendPasswordResetEmail || passwordBusy) return;
    setPasswordBusy(true);
    setPasswordMessage('');
    try {
      await window.RP_ACCOUNT.sendPasswordResetEmail(who.email || authProfile.email);
      setPasswordMessage('Reset email sent.');
      toast('Password reset email sent', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not send reset email.';
      setPasswordMessage(msg);
      toast(msg, 'danger');
    } finally {
      setPasswordBusy(false);
    }
  };
  return (
    <div className="rp-fade">
      <MHeader title="My Account" nav={nav} back />
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
          <Avatar name={who.name} size={58} color="var(--navy)" />
          <div><div className="disp" style={{ fontSize: 22, textTransform: 'uppercase' }}>{who.name}</div><div style={{ fontSize: 13, color: 'var(--muted)' }}>{who.role} · {who.desc}</div></div>
        </div>

        <div className="eyebrow" style={{ marginBottom: 10 }}>Profile</div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', marginBottom: 20 }}>
          {[
            ['Email', who.email || authProfile.email || (who.name || 'user').toLowerCase().split(' ')[0] + '@ranchpower.com'],
            ['Role', who.role],
            ['Territory', authProfile.territory || who.desc],
            ['Quote phone', authProfile.quote_phone || 'Not shown'],
            ['Quote location', authProfile.quote_location || authProfile.territory || 'Not shown'],
            ['Account status', rpAccountStatus(authProfile, authDetails)],
            ['Last sign-in', live ? rpFormatAccountTime(authDetails.lastSignInAt) : 'Demo session'],
            ['Device', live ? 'This browser' : 'iPhone 15 · Field Kit']
          ].map((r, i, arr) => (
            <div key={r[0]} style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 15px', borderBottom: i < arr.length - 1 ? '1px solid var(--line-2)' : 'none', fontSize: 13.5 }}>
              <span style={{ color: 'var(--muted)' }}>{r[0]}</span><span style={{ fontWeight: 500 }}>{r[1]}</span>
            </div>
          ))}
        </div>

        {live && (
          <>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Public Quote Profile</div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', marginBottom: 20, padding: 14 }}>
              <label className="field-label">Display name</label>
              <input className="field" value={displayName} onChange={e => setDisplayName(e.target.value)} autoComplete="name" />
              <label className="field-label" style={{ marginTop: 10 }}>Phone shown to retailers</label>
              <input className="field" value={quotePhone} onChange={e => setQuotePhone(e.target.value)} autoComplete="tel" inputMode="tel" maxLength={40} placeholder="e.g. 778-868-1514" />
              <label className="field-label" style={{ marginTop: 10 }}>Local location or address</label>
              <input className="field" value={quoteLocation} onChange={e => setQuoteLocation(e.target.value)} autoComplete="street-address" maxLength={160} placeholder="e.g. Squamish, BC" />
              <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45, marginTop: 8 }}>Shown on your retailer sell sheets and direct quote emails. Leave either blank to omit it.</div>
              <button className="btn btn-primary" disabled={profileBusy || !displayName.trim()} onClick={saveProfile} style={{ width: '100%', marginTop: 10 }}><Icon name="check" size={15} />Save Profile</button>
              {profileMessage && <div style={{ fontSize: 11.5, color: profileMessage.includes('saved') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{profileMessage}</div>}
            </div>

            <div className="eyebrow" style={{ marginBottom: 10 }}>Password</div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', marginBottom: 20, padding: 14 }}>
              <label className="field-label">New password</label>
              <input className="field" type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 12 characters" />
              <label className="field-label" style={{ marginTop: 10 }}>Confirm password</label>
              <input className="field" type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              <button className="btn btn-primary" disabled={passwordBusy || !newPassword || !confirmPassword} onClick={savePassword} style={{ width: '100%', marginTop: 10 }}><Icon name="check" size={15} />Set Password</button>
              <button className="btn" disabled={passwordBusy} onClick={sendReset} style={{ width: '100%', marginTop: 8 }}><Icon name="mail" size={15} />Email Reset Link</button>
              {passwordMessage && <div style={{ fontSize: 11.5, color: passwordMessage.includes('updated') || passwordMessage.includes('sent') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{passwordMessage}</div>}
            </div>
          </>
        )}

        <div className="eyebrow" style={{ marginBottom: 10 }}>Notifications</div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', marginBottom: 20, overflow: 'hidden' }}>
          {[['pushEnabled', 'Push notifications']].map(r => (
            <div key={r[0]} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 15px', borderBottom: '1px solid var(--line-2)' }}>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>{r[1]}</span>
              <button disabled={notificationBusy} onClick={() => setNotificationPrefs(current => ({ ...current, pushEnabled: !current.pushEnabled }))} style={{ width: 44, height: 26, borderRadius: 100, border: 'none', background: notificationPrefs.pushEnabled ? 'var(--ok)' : 'var(--line)', position: 'relative', transition: 'background .18s', flexShrink: 0 }}>
                <span style={{ position: 'absolute', top: 3, left: notificationPrefs.pushEnabled ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'left .18s' }} />
              </button>
            </div>
          ))}
          {[['order', 'Order updates'], ['task', 'Task assignments'], ['stock_request', 'Stock request decisions']].map(r => {
            const enabled = notificationPrefs.categories[r[0]] !== false;
            return <div key={r[0]} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 15px', borderBottom: '1px solid var(--line-2)' }}>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>{r[1]}</span>
              <button disabled={notificationBusy} onClick={() => toggleNotificationCategory(r[0])} style={{ width: 44, height: 26, borderRadius: 100, border: 'none', background: enabled ? 'var(--ok)' : 'var(--line)', position: 'relative', transition: 'background .18s', flexShrink: 0 }}>
                <span style={{ position: 'absolute', top: 3, left: enabled ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'left .18s' }} />
              </button>
            </div>;
          })}
          <div style={{ padding: '12px 15px', borderBottom: '1px solid var(--line-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>Quiet hours</span>
              <button disabled={notificationBusy} onClick={() => setNotificationPrefs(current => ({ ...current, quietHoursEnabled: !current.quietHoursEnabled }))} style={{ width: 44, height: 26, borderRadius: 100, border: 'none', background: notificationPrefs.quietHoursEnabled ? 'var(--ok)' : 'var(--line)', position: 'relative', flexShrink: 0 }}>
                <span style={{ position: 'absolute', top: 3, left: notificationPrefs.quietHoursEnabled ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
              </button>
            </div>
            {notificationPrefs.quietHoursEnabled && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>From<input className="field" type="time" value={notificationPrefs.quietHoursStart} onChange={e => setNotificationPrefs(current => ({ ...current, quietHoursStart: e.target.value }))} /></label>
              <label style={{ fontSize: 11, color: 'var(--muted)' }}>Until<input className="field" type="time" value={notificationPrefs.quietHoursEnd} onChange={e => setNotificationPrefs(current => ({ ...current, quietHoursEnd: e.target.value }))} /></label>
            </div>}
            <div style={{ color: 'var(--faint)', fontSize: 11, marginTop: 7 }}>{notificationPrefs.timezone}</div>
          </div>
          {live ? <div style={{ padding: 12 }}>
            <button className="btn btn-primary" disabled={notificationBusy} onClick={saveNotificationPrefs} style={{ width: '100%' }}>{notificationBusy ? 'Saving…' : 'Save notification preferences'}</button>
            {notificationMessage && <div style={{ fontSize: 11.5, color: notificationMessage.includes('saved') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{notificationMessage}</div>}
          </div> : <div style={{ padding: 12, color: 'var(--muted)', fontSize: 11.5 }}>Demo preview — sign in to save notification preferences.</div>}
        </div>

        <button onClick={() => mobileSignOut(nav, role)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, background: 'transparent', border: '1px solid var(--line)', borderRadius: 12, padding: '13px', color: 'var(--danger)', fontSize: 14, fontWeight: 600 }}><Icon name="arrowL" size={16} />Sign Out</button>
      </div>
    </div>
  );
};

/* ---------- DOCUMENTS ---------- */
const DOC_SETS = {
  rep: [
    { cat: 'Sell Sheets', docs: [['Ranch Power Line Card 2026', 'PDF · 2.1 MB'], ['Ginger Citrus One-Pager', 'PDF · 800 KB'], ['Mixer Pack Sell Sheet', 'PDF · 1.2 MB']] },
    { cat: 'Pricing', docs: [['Default Retail Price List', 'PDF · Eff. Jun 15'], ['Distributor Price List', 'PDF · Eff. Jun 1']] },
    { cat: 'Forms', docs: [['New Account Setup', 'Form'], ['Credit Application', 'PDF'], ['Trade Spend Agreement', 'PDF']] },
  ],
  warehouse: [
    { cat: 'SOPs', docs: [['Receiving SOP', 'PDF · 1.0 MB'], ['Pick & Pack SOP', 'PDF · 900 KB'], ['Damage Handling SOP', 'PDF']] },
    { cat: 'Quality', docs: [['COA — RP-GC-001', 'PDF'], ['COA — RP-LIME-001', 'PDF'], ['COA — RP-BR-001', 'PDF'], ['FEFO Lot Guide', 'PDF']] },
    { cat: 'Safety', docs: [['Forklift Checklist', 'Form'], ['WHMIS Sheets', 'PDF']] },
  ],
  dispatch: [
    { cat: 'Shipping Docs', docs: [['BOL — SH-330', 'PDF'], ['CHEP Transfer Slip', 'PDF'], ['Delivery Manifest — Today', 'PDF']] },
    { cat: 'Vehicle', docs: [['Pre-Trip Inspection', 'Form'], ['Insurance & Registration', 'PDF']] },
  ],
};
MOBILE_SCREENS['documents'] = function Documents({ nav, role }) {
  const [q, setQ] = useState('');
  const sets = DOC_SETS[role] || DOC_SETS.rep;
  return (
    <div className="rp-fade">
      <MHeader title="Documents" nav={nav} back />
      <div style={{ padding: '12px 16px 0' }}>
        <div style={{ position: 'relative' }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--faint)' }} />
          <input className="field" style={{ paddingLeft: 36 }} placeholder="Search documents…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
      </div>
      {sets.map(set => {
        const docs = set.docs.filter(d => d[0].toLowerCase().includes(q.toLowerCase()));
        if (!docs.length) return null;
        return (
          <div key={set.cat} style={{ padding: '0 16px', marginTop: 20 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>{set.cat}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {docs.map(d => (
                <button key={d[0]} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '13px 14px', textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
                  <span style={{ width: 34, height: 40, borderRadius: 6, background: 'var(--danger-bg)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="doc" size={18} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13.5, fontWeight: 600 }}>{d[0]}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{d[1]}</div></div>
                  <Icon name="download" size={17} style={{ color: 'var(--muted)' }} />
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <div style={{ height: 20 }} />
    </div>
  );
};

/* ---------- TRANSFER STOCK (warehouse) ---------- */
MOBILE_SCREENS['transfer-stock'] = function TransferStock({ nav }) {
  const live = rpHasLiveAuth();
  const [from, setFrom] = useState('WH-SQ');
  const [to, setTo] = useState('WH-KEL');
  const [qty, setQty] = useState({});
  const [pallets, setPallets] = useState(2);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ref, setRef] = useState(() => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return `TO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
    return `TO-${Date.now().toString(36).toUpperCase()}`;
  });
  const [done, setDone] = useState(false);
  const whs = RP.LOCATIONS.filter(l => l.active !== false && ['Main Warehouse', 'Micro-Warehouse', 'Distributor', 'Production Facility', '3PL Hub', '3PL Warehouse', 'FBA'].includes(l.type));
  const sourceStatus = RP.loc(from)?.type === 'Micro-Warehouse'
    ? new Set(['Available', 'Fronted to Rep', 'Purchased by Rep'])
    : new Set(['Available']);
  const holdingMap = new Map();
  (RP.INV || [])
    .filter(row => row && row.loc === from && Number(row.qty || 0) > 0 && String(row.unit || 'case').toLowerCase() !== 'can' && sourceStatus.has(row.status || 'Available'))
    .forEach(row => {
      const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(row.sku) : row.sku;
      const lot = String(row.lot || '').trim();
      if (!skuId || !lot || lot === 'UNASSIGNED' || lot === 'CYCLE_COUNT') return;
      const key = `${skuId}|${lot}`;
      const current = holdingMap.get(key) || { key, lot, quantity: 0, skuId };
      current.quantity += Number(row.qty || 0);
      holdingMap.set(key, current);
    });
  const holdings = Array.from(holdingMap.values()).sort((a, b) => `${a.skuId}|${a.lot}`.localeCompare(`${b.skuId}|${b.lot}`));
  const lines = Object.entries(qty)
    .map(([key, value]) => {
      const [skuId, lot] = key.split('|');
      return { lot, quantity: Number(value || 0), skuId };
    })
    .filter(line => line.quantity > 0);
  const total = lines.reduce((s, line) => s + line.quantity, 0);
  const sameLoc = from === to;
  const lineOverdraw = live && lines.find(line => {
    const available = Number((holdingMap.get(`${line.skuId}|${line.lot}`) || {}).quantity || 0);
    return line.quantity > available;
  });
  const canSubmit = !sameLoc && total > 0 && !lineOverdraw && !busy;
  const submit = async () => {
    setError('');
    if (!canSubmit) return;
    setBusy(true);
    try {
      if (live) {
        const response = await rpApi('/api/inventory/transfer', {
          method: 'POST',
          body: JSON.stringify({
            fromLocationId: from,
            lines,
            movementMethod: 'internal_transfer',
            chepPalletCount: pallets,
            chepUsage: pallets > 0 ? 'used' : 'not_used',
            notes: notes.trim() || undefined,
            occurredAt: new Date().toISOString(),
            ref,
            toLocationId: to
          })
        });
        setRef(response.ref || '');
        await rpRefreshAfterMutation(`Transfer ${response.ref || 'saved'}`);
      } else {
        const first = lines[0];
        setRef(RP.addTransfer({ from, lot: first.lot, qty: total, ref, sku: first.skuId, to }));
      }
      setDone(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create transfer.');
    } finally {
      setBusy(false);
    }
  };
  const fromName = RP.loc(from)?.name || from;
  const toName = RP.loc(to)?.name || to;
  if (done) return <Success nav={nav} title="Transfer Recorded" sub={`${total} cases - ${fromName} to ${toName}`} note={`${ref || 'Transfer'} posted as a completed custody handoff.`} />;
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Transfer Stock" sub="Warehouse to warehouse" nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1 }}><Lbl>From</Lbl><select className="field" value={from} onChange={e => { setFrom(e.target.value); setQty({}); }}>{whs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
          <div style={{ paddingBottom: 9, color: 'var(--muted)' }}><Icon name="arrowR" size={20} /></div>
          <div style={{ flex: 1 }}><Lbl>To</Lbl><select className="field" value={to} onChange={e => setTo(e.target.value)}>{whs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
        </div>
        {sameLoc && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 600, marginTop: -8, marginBottom: 16 }}><Icon name="alert" size={16} />Source and destination must differ.</div>}
        <Lbl>Items</Lbl>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {holdings.map(holding => {
            const s = rpSkuItem(holding.skuId) || holding.skuId;
            const available = holding.quantity;
            const over = live && (qty[holding.key] || 0) > available;
            return (
            <div key={holding.key} style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--surface)', border: '1px solid ' + (over ? 'var(--danger)' : 'var(--line)'), borderRadius: 11, padding: 11 }}>
              <FlavorChip sku={s} size={30} />
              <div style={{ flex: 1 }}><div style={{ fontSize: 13.5, fontWeight: 500 }}>{rpSkuDisplayName(s)}</div><div className="mono" style={{ fontSize: 10.5, color: over ? 'var(--danger)' : 'var(--faint)', fontWeight: over ? 700 : 400 }}>{holding.lot} - {available} cs</div></div>
              <Stepper value={qty[holding.key] || 0} onChange={v => setQty(q => ({ ...q, [holding.key]: v }))} />
            </div>
          ); })}
          {!holdings.length && <div style={{ background: 'var(--surface)', border: '1px dashed var(--line)', borderRadius: 11, padding: 16, textAlign: 'center', color: 'var(--muted)', fontSize: 12.5 }}>No exact-lot case inventory is available at this source.</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: '13px 14px', marginTop: 14 }}>
          <span><span style={{ display: 'block', fontSize: 13.5, fontWeight: 500 }}>CHEP pallets moved</span><span style={{ display: 'block', fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>Set 0 only when no CHEP pallet was used.</span></span><Stepper value={pallets} onChange={setPallets} />
        </div>
        <Lbl style={{ marginTop: 16 }}>Notes</Lbl>
        <textarea className="field" rows="2" placeholder="Replenish Kelowna for weekend..." value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'none' }} />
        {lineOverdraw && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, marginTop: 12 }}><Icon name="alert" size={16} />Requested transfer exceeds available source inventory.</div>}
        {error && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, marginTop: 12 }}><Icon name="alert" size={16} />{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" style={{ flex: 1, opacity: canSubmit ? 1 : 0.5 }} disabled={!canSubmit} onClick={submit}><Icon name="layers" size={17} />{busy ? 'Saving...' : `Record Completed Transfer - ${total} cs`}</button></MActionBar>
    </div>
  );
};

/* ---------- CYCLE COUNT ---------- */
const COUNT_TARGETS = {
  'WH-SQ': [['RP-12-GC', 120], ['RP-12-KL', 44], ['RP-12-BR', 36], ['RP-12-PM', 64]],
  'WH-KEL': [['RP-12-GC', 86], ['RP-12-KL', 80], ['RP-12-PL', 28]],
  'MW-SQ': [['RP-12-GC', 18], ['RP-12-KL', 12], ['RP-12-PM', 5]],
  'van1': [['RP-12-GC', 18], ['RP-12-KL', 12], ['RP-12-PM', 8]],
};
MOBILE_SCREENS['cycle-count'] = function CycleCount({ nav, loc = 'WH-SQ' }) {
  const locName = RP.loc(loc) ? RP.loc(loc).name : 'Ranch Van 1';
  const live = rpHasLiveAuth();
  const location = RP.loc(loc);
  const countStatuses = new Set(location && location.type === 'Micro-Warehouse'
    ? ['Available', 'Fronted to Rep', 'Purchased by Rep', 'Sample', 'Sample / Demo']
    : ['Available']);
  const liveTargetMap = new Map();
  (RP.INV || [])
    .filter(row => row && row.loc === loc && String(row.unit || 'case').toLowerCase() !== 'can' && countStatuses.has(row.status || 'Available'))
    .forEach(row => {
      const skuId = typeof rpCanonicalInventorySkuId === 'function' ? rpCanonicalInventorySkuId(row.sku) : row.sku;
      const lot = String(row.lot || 'UNASSIGNED');
      const key = `${skuId}|${lot}`;
      const current = liveTargetMap.get(key) || [skuId, 0, lot];
      current[1] += Number(row.qty || 0);
      liveTargetMap.set(key, current);
    });
  const currentTargets = live
    ? Array.from(liveTargetMap.values()).filter(([, qty]) => qty >= 0).sort((a, b) => `${a[0]}|${a[2]}`.localeCompare(`${b[0]}|${b[2]}`))
    : (COUNT_TARGETS[loc] || COUNT_TARGETS['WH-SQ']);
  const [targets] = useState(() => currentTargets.map(target => [...target]));
  const [counts, setCounts] = useState(() => targets.map(() => null));
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ref, setRef] = useState(() => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return `CC-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()}`;
    return `CC-${Date.now().toString(36).toUpperCase()}`;
  });
  const variances = counts.map((count, index) => count == null ? null : count - targets[index][1]);
  const countedLines = counts.filter(count => count != null).length;
  const canSubmit = targets.length > 0 && countedLines === targets.length && !busy;
  const setCount = (index, value) => setCounts(current => current.map((count, currentIndex) => (
    currentIndex === index ? (value == null ? null : Math.max(0, Math.floor(Number(value) || 0))) : count
  )));
  const submit = async () => {
    setError('');
    if (!canSubmit) return;
    setBusy(true);
    try {
      if (live) {
        const response = await rpApi('/api/inventory/cycle-count', {
          method: 'POST',
          body: JSON.stringify({
            lines: targets.map(([skuId, , lot], index) => ({ countedQuantity: counts[index] ?? 0, lot: lot || undefined, skuId })),
            locationId: loc,
            notes: `Mobile cycle count for ${locName}`,
            ref
          })
        });
        setRef(response.ref || '');
        await rpRefreshAfterMutation(`Cycle count ${response.ref || 'submitted'}`);
      }
      setDone(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not submit cycle count.');
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    const adj = variances.filter(v => v != null && v !== 0).length;
    return <Success nav={nav} title="Count Submitted" sub={locName} note={adj ? `${ref || 'Cycle count'} posted ${adj} variance${adj > 1 ? 's' : ''} to the ledger.` : 'No variances - system matches the count.'} />;
  }
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Cycle Count" sub={locName} nav={nav} back right={<button style={{ border: 'none', background: 'var(--navy)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="scan" size={18} /></button>} />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)', borderRadius: 10, padding: '10px 13px', marginBottom: 14, fontSize: 12.5, color: 'var(--muted)' }}><Icon name="layers" size={15} />Enter the physical case count. System quantities stay hidden until you commit.</div>
        {!targets.length && <div style={{ background: 'var(--surface)', border: '1px dashed var(--line)', borderRadius: 12, padding: 18, color: 'var(--muted)', textAlign: 'center', fontSize: 13.5, fontWeight: 600 }}>No live inventory balances are available for this location yet.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {targets.map(([id, , lot], i) => { const s = RP.sku(id); const counted = counts[i] != null; return (
            <div key={`${id}-${lot || 'all'}`} style={{ background: 'var(--surface)', border: '1px solid ' + (counted ? 'var(--ok)' : 'var(--line)'), borderRadius: 12, padding: 13, boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <FlavorChip sku={s} size={30} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 13.5, fontWeight: 600 }}>{rpSkuDisplayName(s || id)}</div>
                  {lot && <div className="mono" style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2 }}>Lot {lot}</div>}
                  <div style={{ fontSize: 11.5, color: counted ? 'var(--ok)' : 'var(--faint)', fontWeight: counted ? 700 : 400, marginTop: 2 }}>{counted ? 'Physical count entered' : 'Not counted yet'}</div>
                </div>
              </div>
              <CountRow
                label="Physical cases"
                value={counts[i]}
                onMinus={() => setCount(i, Math.max(0, Number(counts[i] || 0) - 1))}
                onPlus={() => setCount(i, Number(counts[i] || 0) + 1)}
                onChange={value => setCount(i, value)}
                quick={[5, 10]}
                allowBlank
              />
            </div>
          ); })}
        </div>
        {error && <div style={{ display: 'flex', gap: 9, alignItems: 'center', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, marginTop: 12 }}><Icon name="alert" size={16} />{error}</div>}
      </div>
      <MActionBar>
        <div style={{ flex: 1, alignSelf: 'center', paddingLeft: 4 }}><div style={{ fontSize: 11, color: 'var(--faint)' }}>Count progress</div><div className="disp" style={{ fontSize: 20, color: countedLines === targets.length ? 'var(--ok)' : 'var(--ink)' }}>{countedLines} / {targets.length}</div></div>
        <button className="btn btn-lg btn-primary" disabled={!canSubmit} style={{ flex: '0 0 auto', opacity: canSubmit ? 1 : 0.5 }} onClick={submit}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Submit Count'}</button>
      </MActionBar>
    </div>
  );
};


/* ===== mobile_ops_ext.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : Tasks · Expenses · My Goals
   Field & staff side of the ops add-ons.
   ============================================================ */

async function saveMobileTaskChanges(id, changes, expectedUpdatedAt) {
  if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
    const currentTask = RP.taskById(id);
    const response = await rpApi('/api/tasks/update', {
      method: 'POST',
      body: JSON.stringify({ id, expectedUpdatedAt: expectedUpdatedAt || currentTask && currentTask.updatedAt || undefined, ...changes })
    });
    const task = RP.upsertTask(response.task);
    if (response.auditWarning) toast(response.auditWarning, 'warn');
    if (response.notificationWarning) toast(response.notificationWarning, 'warn');
    await rpRefreshAfterMutation(`Task ${task.id} updated`);
    return RP.taskById(task.id) || task;
  }
  let task = RP.taskById(id);
  if (!task) throw new Error('Task was not found.');
  if (changes.status) task = RP.setTaskStatus(id, changes.status) || task;
  if (Object.prototype.hasOwnProperty.call(changes, 'dueDate')) task.due = changes.dueDate || '';
  return RP.upsertTask(task);
}

async function saveMobileTaskStatus(id, status, expectedUpdatedAt) {
  return saveMobileTaskChanges(id, { status }, expectedUpdatedAt);
}

async function saveMobileTaskDueDate(id, dueDate, expectedUpdatedAt) {
  return saveMobileTaskChanges(id, { dueDate }, expectedUpdatedAt);
}

/* ---------- MY TASKS ---------- */
MOBILE_SCREENS['my-tasks'] = function MyTasks({ nav, role }) {
  const me = rpFieldUserName(role);
  const [filter, setFilter] = useState('Open');
  const [, force] = useState(0);
  let list = RP.TASKS.filter(t => t.owner === me);
  if (filter === 'Open') list = list.filter(t => t.status !== 'Done');
  if (filter === 'Done') list = list.filter(t => t.status === 'Done');
  // sort by due then priority
  const prioRank = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
  list = [...list].sort((a, b) => (a.due || '9').localeCompare(b.due || '9') || prioRank[a.priority] - prioRank[b.priority]);
  const openCount = RP.TASKS.filter(t => t.owner === me && t.status !== 'Done').length;

  return (
    <div className="rp-fade">
      <MHeader title="My Tasks" sub={`${openCount} open`} nav={nav} back />
      <div style={{ padding: '12px 16px 0', display: 'flex', gap: 7 }}>
        {['Open', 'All', 'Done'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 100, border: '1px solid var(--line)', background: filter === f ? 'var(--navy)' : 'var(--surface)', color: filter === f ? '#fff' : 'var(--muted)' }}>{f}</button>
        ))}
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.map(t => {
          const dm = dueMeta(t.due);
          const done = t.status === 'Done';
          const cust = t.cust ? RP.cust(t.cust) : null;
          return (
            <div key={t.id} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 13, boxShadow: 'var(--shadow-sm)', borderLeft: '3px solid ' + (done ? 'var(--ok)' : PRIO_COLOR[t.priority]) }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                <button onClick={async () => { try { await saveMobileTaskStatus(t.id, done ? 'To Do' : 'Done'); force(x => x + 1); toast(done ? 'Task reopened' : 'Task completed', done ? 'info' : 'ok'); } catch (err) { toast(err && err.message ? err.message : 'Could not update task.', 'danger'); } }}
                  style={{ width: 24, height: 24, borderRadius: 7, border: '2px solid ' + (done ? 'var(--ok)' : 'var(--faint)'), background: done ? 'var(--ok)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{done && <Icon name="check" size={14} style={{ color: '#fff' }} />}</button>
                <button onClick={() => nav.push('task-detail', { id: t.id })} style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', padding: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.32, textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.55 : 1 }}>{t.title}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
                    {!done && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--' + dm.tone + ')' }}>{dm.label}</span>}
                    <span style={{ fontSize: 11, color: 'var(--faint)' }}>{t.cat}</span>
                    {cust && <span style={{ fontSize: 11, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="customers" size={11} />{cust.name}</span>}
                  </div>
                </button>
                {t.status === 'Blocked' && <Pill status="Blocked" />}
              </div>
            </div>
          );
        })}
        {list.length === 0 && <MEmpty icon="tasks" title="All clear" sub={filter === 'Done' ? 'No completed tasks yet.' : filter === 'All' ? 'No tasks assigned to you.' : 'No open tasks assigned to you.'} />}
      </div>
    </div>
  );
};

/* ---------- TASK DETAIL ---------- */
MOBILE_SCREENS['task-detail'] = function TaskDetail({ nav, id }) {
  const t = RP.taskById(id);
  const [status, setStatus] = useState(() => t ? t.status : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!t) {
    return (
      <div className="rp-fade" data-task-deep-link-missing="true">
        <MHeader title="Task unavailable" nav={nav} back />
        <div style={{ padding: 16 }}><MEmpty icon="tasks" title="Task not available" sub={`Task ${id || ''} was not found in your current sales scope.`} /></div>
      </div>
    );
  }
  const dm = dueMeta(t.due);
  const cust = t.cust ? RP.cust(t.cust) : null;
  const portalInvite = taskPortalInviteState(t, cust);
  const openLinkedCustomer = () => {
    if (cust) nav.push('rep-customer', { id: cust.id });
  };
  const setTaskState = async (s, closeAfter) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const next = await saveMobileTaskStatus(id, s);
      setStatus(next.status);
      toast(`Task → ${next.status}`, next.status === 'Done' ? 'ok' : 'info');
      if (closeAfter) nav.pop();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update task.');
    } finally {
      setBusy(false);
    }
  };
  const sendPortalInvite = async () => {
    if (busy || !portalInvite.portalTask) return;
    setBusy(true);
    setError('');
    try {
      const next = await sendPortalInviteFromTask(t, cust);
      setStatus(next.status || 'Done');
      toast(`Portal invite sent to ${portalInvite.buyerEmail}`, 'ok');
      nav.pop();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not send portal invite.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title={t.id} sub={t.cat} nav={nav} back right={<Pill status={status} />} />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.3, marginBottom: 14 }}>{t.title}</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: '11px 13px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Priority</div><PriorityTag p={t.priority} /></div>
          <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: '11px 13px' }}><div className="eyebrow" style={{ marginBottom: 5 }}>Due</div><span style={{ fontSize: 14, fontWeight: 700, color: 'var(--' + dm.tone + ')' }}>{t.due ? t.due.slice(5) : '—'}</span></div>
        </div>
        {t.note && <div style={{ background: 'var(--surface-2)', borderRadius: 11, padding: '13px 15px', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5, marginBottom: 16 }}>{t.note}</div>}
        {cust && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 11 }}>
              <Icon name={portalInvite.portalTask ? 'mail' : 'user'} size={16} style={{ color: 'var(--amber-deep)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{portalInvite.portalTask ? 'Portal invite action' : 'Linked account action'}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{portalInvite.portalTask ? `Send order portal access to ${portalInvite.buyerEmail || 'the buyer'}.` : `Open ${cust.name} to complete this task.`}</div>
              </div>
              <Pill status={cust.status || 'Account'} />
            </div>
            <button className="btn btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={openLinkedCustomer}><Icon name="customers" size={14} />Open Account</button>
            {portalInvite.portalTask && <button className="btn btn-sm btn-primary" disabled={busy || !portalInvite.canSend} style={{ width: '100%', justifyContent: 'center', marginTop: 8, opacity: busy || !portalInvite.canSend ? 0.6 : 1 }} onClick={sendPortalInvite}><Icon name="mail" size={14} />{busy ? 'Sending...' : 'Send Setup Link'}</button>}
            {portalInvite.portalTask && portalInvite.reason && <div style={{ fontSize: 11.5, color: 'var(--amber-deep)', marginTop: 9, fontWeight: 700 }}>{portalInvite.reason}</div>}
          </div>
        )}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 16 }}>
          <Field2 k="Assigned by" v={t.assigner} />
          <Field2 k="Created" v={t.created} />
          {cust && <button onClick={openLinkedCustomer} style={{ textAlign: 'left', background: 'none', border: 'none', padding: 0 }}><Field2 k="Linked account" v={<span style={{ color: 'var(--amber-deep)' }}>{cust.name} ›</span>} /></button>}
        </div>
        <Lbl>Update Status</Lbl>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {RP.TASK_STATUSES.map(s => (
            <button key={s} disabled={busy} onClick={() => setTaskState(s)} style={{ padding: '12px 10px', borderRadius: 11, border: '1.5px solid ' + (status === s ? 'var(--amber)' : 'var(--line)'), background: status === s ? 'var(--amber-soft)' : 'var(--surface)', fontSize: 13, fontWeight: 600, color: status === s ? 'var(--amber-deep)' : 'var(--ink)' }}>{s}</button>
          ))}
        </div>
        {error && <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: 12.5, fontWeight: 600 }}>{error}</div>}
      </div>
      <MActionBar>
        {status === 'Done'
          ? <button className="btn btn-lg" disabled={busy} style={{ flex: 1 }} onClick={() => setTaskState('In Progress')}><Icon name="refresh" size={17} />{busy ? 'Saving...' : 'Reopen Task'}</button>
          : portalInvite.portalTask && portalInvite.canSend
            ? <button className="btn btn-lg btn-primary" disabled={busy} style={{ flex: 1 }} onClick={sendPortalInvite}><Icon name="mail" size={17} />{busy ? 'Sending...' : 'Send Setup Link'}</button>
            : <button className="btn btn-lg btn-primary" disabled={busy} style={{ flex: 1 }} onClick={() => setTaskState('Done', true)}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Mark Done'}</button>}
      </MActionBar>
    </div>
  );
};

/* ---------- SUPPORT / FEEDBACK ---------- */
MOBILE_SCREENS['support-ticket'] = function MobileSupportTicket({ nav, role }) {
  const persona = rpFieldPersona(role);
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Support" sub="Bugs, feature requests, customer issues" nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 14, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 16 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--amber-soft)', color: 'var(--amber-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="mail" size={19} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)' }}>{persona.name || 'Ranch Power'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{persona.role || rpRoleLabel(role)}</div>
            </div>
          </div>
          <SupportTicketForm source="Sales app" compact onCancel={() => nav.pop()} onCreated={() => {}} />
        </div>
      </div>
    </div>
  );
};

/* ---------- SUBMIT EXPENSE ---------- */
function rpExpenseClientRequestId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const RP_EXPENSE_PENDING_STORAGE_KEY = 'rp.pending-expense-submission.v1';
function rpExpenseActorKey() {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : null;
  return String(profile && (profile.id || profile.email) || '').trim().toLowerCase();
}
function rpExpenseRequestIdValid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
}
async function rpExpenseSha256(value) {
  if (!(window.crypto && window.crypto.subtle)) throw new Error('Secure browser storage is unavailable. Refresh or update your browser before submitting this expense.');
  const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : new Uint8Array(await value.arrayBuffer());
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
function rpExpenseRequirePendingStorage() {
  const actorKey = rpExpenseActorKey();
  if (!actorKey) throw new Error('Your authenticated profile is not ready. Refresh the page before submitting this expense.');
  try {
    const probeKey = `${RP_EXPENSE_PENDING_STORAGE_KEY}.probe`;
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
  } catch {
    throw new Error('This browser cannot safely retain an expense retry. Enable session storage and try again.');
  }
  return actorKey;
}
function rpLoadPendingExpenseSubmission() {
  const actorKey = rpExpenseActorKey();
  if (!actorKey) return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(RP_EXPENSE_PENDING_STORAGE_KEY) || 'null');
    const valid = parsed && parsed.version === 1 && parsed.actorKey === actorKey
      && rpExpenseRequestIdValid(parsed.requestId) && /^[a-f0-9]{64}$/.test(String(parsed.fingerprint || ''))
      && parsed.payload && typeof parsed.payload === 'object' && !Array.isArray(parsed.payload)
      && (!parsed.uploadedReceipt || /^expenses\/[0-9a-f-]{36}\/[a-zA-Z0-9._,@+=-]+$/.test(String(parsed.uploadedReceipt.path || '')))
      && (!parsed.receiptFileFingerprint || /^[a-f0-9]{64}$/.test(String(parsed.receiptFileFingerprint)));
    if (valid) return parsed;
    if (parsed) window.sessionStorage.removeItem(RP_EXPENSE_PENDING_STORAGE_KEY);
  } catch {
    try { window.sessionStorage.removeItem(RP_EXPENSE_PENDING_STORAGE_KEY); } catch {}
  }
  return null;
}
function rpSavePendingExpenseSubmission(value) {
  const actorKey = rpExpenseRequirePendingStorage();
  window.sessionStorage.setItem(RP_EXPENSE_PENDING_STORAGE_KEY, JSON.stringify({
    actorKey,
    fingerprint: value.fingerprint,
    payload: value.payload,
    receiptFileFingerprint: value.receiptFileFingerprint || '',
    requestId: value.requestId,
    uploadedReceipt: value.uploadedReceipt && value.uploadedReceipt.path ? { path: value.uploadedReceipt.path } : null,
    version: 1
  }));
}
function rpClearPendingExpenseSubmission(requestId) {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(RP_EXPENSE_PENDING_STORAGE_KEY) || 'null');
    if (!parsed || !requestId || parsed.requestId === requestId) window.sessionStorage.removeItem(RP_EXPENSE_PENDING_STORAGE_KEY);
  } catch {
    try { window.sessionStorage.removeItem(RP_EXPENSE_PENDING_STORAGE_KEY); } catch {}
  }
}

MOBILE_SCREENS['submit-expense'] = function SubmitExpense({ nav, role }) {
  const me = rpFieldPersona(role);
  const cats = Object.keys(RP.EXP_CATS);
  const recoveredRef = useRef(undefined);
  if (recoveredRef.current === undefined) recoveredRef.current = rpLoadPendingExpenseSubmission();
  const recovered = recoveredRef.current;
  const recoveredPayload = recovered && recovered.payload || {};
  const today = typeof rpTodayYmd === 'function' ? rpTodayYmd() : new Date().toISOString().slice(0, 10);
  const [cat, setCat] = useState(cats.includes(recoveredPayload.category) ? recoveredPayload.category : 'Mileage');
  const [amount, setAmount] = useState(recoveredPayload.amount === undefined ? '' : String(recoveredPayload.amount));
  const [merchant, setMerchant] = useState(String(recoveredPayload.merchant || ''));
  const [cust, setCust] = useState(String(recoveredPayload.customerId || ''));
  const [date, setDate] = useState(/^\d{4}-\d{2}-\d{2}$/.test(String(recoveredPayload.date || '')) ? recoveredPayload.date : today);
  const [reimb, setReimb] = useState(recoveredPayload.reimbursable !== false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptAttached, setReceiptAttached] = useState(Boolean(recovered && recovered.uploadedReceipt && recovered.uploadedReceipt.path));
  const [note, setNote] = useState(String(recoveredPayload.note || ''));
  const [recoveredPending] = useState(Boolean(recovered));
  const [tried, setTried] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const receiptInput = useRef(null);
  const inFlightRef = useRef(false);
  const submissionRef = useRef(recovered ? {
    fingerprint: recovered.fingerprint,
    receiptFileFingerprint: recovered.receiptFileFingerprint || '',
    requestId: recovered.requestId,
    uploadedReceipt: recovered.uploadedReceipt || null
  } : { fingerprint: '', receiptFileFingerprint: '', requestId: '', uploadedReceipt: null });
  const amountText = String(amount || '').trim();
  const amt = Number(amountText);
  const normalizedAmount = Math.round((amt + Number.EPSILON) * 100) / 100;
  const valid = /^\d+(?:\.\d{0,2})?$/.test(amountText) && amt > 0 && amt <= 25000 && normalizedAmount === amt;
  const receipt = Boolean(receiptFile) || receiptAttached;
  if (done) return <Success nav={nav} title="Expense Submitted" sub={`${RP.money2(result ? result.amount : normalizedAmount)} · ${cat}`} note={`${result ? result.id + ' · ' : ''}Sent to Accounting. They'll set the tax code and approve it for reimbursement.`} />;
  const submit = async () => {
    setTried(true);
    setError('');
    if (!valid || busy || inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    const local = { who: me.name || 'Ranch Power', role: me.role || 'Sales Rep', date, cat, amount: normalizedAmount, merchant: merchant.trim() || cat, cust: cust || null, reimbursable: reimb, receipt, note: note.trim() || undefined, flag: receipt ? undefined : 'Receipt missing' };
    try {
      let serverExpense = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        rpExpenseRequirePendingStorage();
        let uploadedReceipt = receiptAttached && submissionRef.current.uploadedReceipt
          ? submissionRef.current.uploadedReceipt
          : null;
        let receiptFileFingerprint = submissionRef.current.receiptFileFingerprint || '';
        if (receiptFile) {
          const prior = submissionRef.current;
          const selectedFileFingerprint = await rpExpenseSha256(receiptFile);
          if (prior.receiptFileFingerprint === selectedFileFingerprint && prior.uploadedReceipt) {
            uploadedReceipt = prior.uploadedReceipt;
          } else {
            uploadedReceipt = await rpUploadPrivateFile('expense_receipt', receiptFile, 'receipt');
            submissionRef.current = { fingerprint: '', receiptFileFingerprint: selectedFileFingerprint, requestId: '', uploadedReceipt };
          }
          receiptFileFingerprint = selectedFileFingerprint;
        }
        const requestPayload = {
          amount: normalizedAmount,
          category: cat,
          customerId: cust || undefined,
          date,
          hasReceipt: Boolean(uploadedReceipt),
          merchant: merchant.trim() || cat,
          note: note.trim(),
          receiptPath: uploadedReceipt ? uploadedReceipt.path : undefined,
          reimbursable: reimb
        };
        const fingerprint = await rpExpenseSha256(JSON.stringify(requestPayload));
        if (!submissionRef.current.requestId || submissionRef.current.fingerprint !== fingerprint) {
          submissionRef.current = {
            ...submissionRef.current,
            fingerprint,
            receiptFileFingerprint,
            requestId: rpExpenseClientRequestId(),
            uploadedReceipt
          };
        }
        rpSavePendingExpenseSubmission({
          fingerprint,
          payload: requestPayload,
          receiptFileFingerprint,
          requestId: submissionRef.current.requestId,
          uploadedReceipt
        });
        const response = await rpApi('/api/expenses/submit', {
          method: 'POST',
          body: JSON.stringify({
            ...requestPayload,
            clientRequestId: submissionRef.current.requestId
          })
        });
        serverExpense = response.expense;
      }
      const expense = serverExpense
        ? RP.upsertExpense({ ...serverExpense, persisted: true })
        : RP.addExpense(local);
      if (serverExpense) await rpRefreshAfterMutation(`Expense ${expense.id} submitted`);
      setResult(expense);
      toast(`Expense ${expense.id} submitted`, 'ok');
      if (serverExpense) rpClearPendingExpenseSubmission(submissionRef.current.requestId);
      setDone(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not submit expense.');
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };
  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Submit Expense" sub="Field & travel costs" nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        {recoveredPending && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', border: '1px solid var(--amber)', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12, fontWeight: 700 }}>Recovered your last unconfirmed submission. Submit again to verify the original result without creating a duplicate.</div>}
        <Lbl>Category</Lbl>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 18 }}>
          {cats.map(cn => {
            const on = cat === cn;
            return <button key={cn} onClick={() => setCat(cn)} style={{ padding: '11px 6px', borderRadius: 11, border: '1.5px solid ' + (on ? 'var(--amber)' : 'var(--line)'), background: on ? 'var(--amber-soft)' : 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: on ? 'var(--amber-deep)' : 'var(--ink)' }}><Icon name={RP.EXP_CATS[cn].icon} size={18} /><span style={{ fontSize: 10.5, fontWeight: 600, textAlign: 'center', lineHeight: 1.15 }}>{cn}</span></button>;
          })}
        </div>
        <Lbl>Amount (tax incl.)</Lbl>
        <div style={{ position: 'relative', marginBottom: 18 }}>
          <span style={{ position: 'absolute', left: 14, top: 13, color: 'var(--muted)', fontWeight: 700, fontSize: 16 }}>$</span>
          <input className="field" type="number" inputMode="decimal" min="0.01" max="25000" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} style={{ paddingLeft: 30, fontSize: 18, fontWeight: 600, height: 50, borderColor: tried && !valid ? 'var(--danger)' : undefined }} />
          {tried && !valid && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginTop: 5 }}>Enter $0.01–$25,000 using no more than 2 decimal places</div>}
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
          <div style={{ flex: 1 }}><Lbl>Merchant</Lbl><input className="field" placeholder="e.g. Petro-Canada" value={merchant} onChange={e => setMerchant(e.target.value)} /></div>
          <div style={{ flex: 1 }}><Lbl>Date</Lbl><input className="field" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        </div>
        <Lbl>Link to account (optional)</Lbl>
        <select className="field" value={cust} onChange={e => setCust(e.target.value)} style={{ marginBottom: 18 }}><option value="">None</option>{RP.CUSTOMERS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <Lbl>Receipt {receipt ? '' : <span style={{ color: 'var(--danger)' }}>*recommended</span>}</Lbl>
        <input ref={receiptInput} type="file" accept="image/*,application/pdf" capture="environment" style={{ display: 'none' }} onChange={e => { const file = e.target.files && e.target.files[0]; if (file) { setReceiptFile(file); setReceiptAttached(true); } }} />
        <button onClick={() => (window.RP_AUTH && window.RP_AUTH.demo) ? setReceiptAttached(r => !r) : receiptInput.current && receiptInput.current.click()} className="btn" style={{ width: '100%', minHeight: 80, flexDirection: 'column', gap: 6, borderStyle: receipt ? 'solid' : 'dashed', borderColor: receipt ? 'var(--ok)' : undefined, background: receipt ? 'var(--ok-bg)' : undefined, color: receipt ? 'var(--ok)' : undefined, marginBottom: receipt ? 8 : 18, padding: '12px 14px' }}><Icon name={receipt ? 'check' : 'camera'} size={24} /><span style={{ fontSize: 12, textAlign: 'center', lineHeight: 1.35 }}>{receiptFile ? rpUploadLabel(receiptFile) : receipt ? 'Receipt attached' : 'Snap / attach receipt'}</span></button>
        {receipt && <button className="btn btn-ghost btn-sm" onClick={() => { const priorRequestId = submissionRef.current.requestId; setReceiptFile(null); setReceiptAttached(false); submissionRef.current = { fingerprint: '', receiptFileFingerprint: '', requestId: '', uploadedReceipt: null }; rpClearPendingExpenseSubmission(priorRequestId); if (receiptInput.current) receiptInput.current.value = ''; }} style={{ width: '100%', marginBottom: 18, color: 'var(--danger)' }}>Remove receipt</button>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 11, padding: '13px 14px', marginBottom: 14 }}>
          <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Reimburse to me</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>Off = paid on company card</div></div>
          <button onClick={() => setReimb(r => !r)} style={{ width: 44, height: 26, borderRadius: 100, border: 'none', background: reimb ? 'var(--ok)' : 'var(--line)', position: 'relative', flexShrink: 0 }}><span style={{ position: 'absolute', top: 3, left: reimb ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} /></button>
        </div>
        <Lbl>Note</Lbl>
        <textarea className="field" rows="2" placeholder="What was this for?" value={note} onChange={e => setNote(e.target.value)} style={{ resize: 'none' }} />
        {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy} style={{ flex: 1, opacity: valid && !busy ? 1 : 0.5 }} onClick={submit}><Icon name="check" size={17} />{busy ? 'Submitting...' : `Submit${valid ? ' · ' + RP.money2(normalizedAmount) : ''}`}</button></MActionBar>
    </div>
  );
};

/* ---------- MY EXPENSES ---------- */
MOBILE_SCREENS['my-expenses'] = function MyExpenses({ nav, role, id }) {
  const me = rpFieldUserName(role);
  const scopedExpenses = RP.EXPENSES.filter(e => e.who === me);
  const selectedExpense = id ? scopedExpenses.find(expense => String(expense.id) === String(id)) : null;
  const list = selectedExpense ? [selectedExpense, ...scopedExpenses.filter(expense => expense !== selectedExpense)] : scopedExpenses;
  const pending = list.filter(e => e.status === 'Submitted' || e.status === 'Needs Info').reduce((s, e) => s + e.amount, 0);
  const owed = list.filter(e => e.status === 'Approved' && e.reimbursable !== false).reduce((s, e) => s + e.amount, 0);
  return (
    <div className="rp-fade" data-expense-deep-link={id || ''}>
      <MHeader title="My Expenses" sub={selectedExpense ? `Opened ${selectedExpense.id}` : undefined} nav={nav} back right={<button onClick={() => nav.push('submit-expense')} style={{ border: 'none', background: 'var(--amber)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={19} /></button>} />
      <div style={{ padding: 16 }}>
        {id && !selectedExpense && <div data-expense-deep-link-missing="true" style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', border: '1px solid var(--amber)', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12, fontWeight: 700 }}>Expense {id} is not available in your current sales scope.</div>}
        <div style={{ background: 'var(--navy)', color: '#fff', borderRadius: 14, padding: 16, marginBottom: 16, display: 'flex' }}>
          <MStat label="Pending review" value={RP.money(pending)} accent="#fff" />
          <span style={{ width: 1, background: 'oklch(1 0 0 / 0.14)' }} />
          <MStat label="Owed to you" value={RP.money(owed)} accent="var(--amber)" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map(e => {
            const c = RP.EXP_CATS[e.cat] || {};
            const selected = Boolean(selectedExpense && selectedExpense === e);
            return (
              <div key={e.id} data-expense-record-id={e.id} data-expense-selected={selected ? 'true' : 'false'} style={{ background: selected ? 'var(--amber-soft)' : 'var(--surface)', border: '1px solid ' + (selected ? 'var(--amber)' : 'var(--line)'), borderRadius: 12, padding: 13, boxShadow: selected ? '0 0 0 2px var(--amber-soft)' : 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--surface-2)', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={c.icon || 'box'} size={18} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{e.cat}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.merchant} · {e.date.slice(5)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}><div className="disp" style={{ fontSize: 18 }}>{RP.money2(e.amount)}</div><div style={{ marginTop: 3 }}><Pill status={e.status} /></div></div>
                </div>
                {e.flag && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9, fontSize: 11.5, color: 'var(--danger)', fontWeight: 600 }}><Icon name="alert" size={13} />{e.flag} — add it to get approved</div>}
                {selected && <div data-expense-selected-detail="true" style={{ borderTop: '1px solid var(--amber)', marginTop: 10, paddingTop: 9, color: 'var(--ink-soft)', fontSize: 11.5, lineHeight: 1.45 }}><strong>Selected expense</strong><br />{[e.merchant, e.date, e.note].filter(Boolean).join(' - ') || 'No additional details were saved.'}</div>}
              </div>
            );
          })}
          {list.length === 0 && <MEmpty icon="receipt" title="No expenses yet" sub="Submit your first travel or field cost." action={<button className="btn btn-primary btn-sm" onClick={() => nav.push('submit-expense')}><Icon name="plus" size={15} />Submit Expense</button>} />}
        </div>
      </div>
    </div>
  );
};

/* ---------- MY GOALS (rep) ---------- */
MOBILE_SCREENS['my-goals'] = function MyGoals({ nav, role }) {
  const me = rpFieldUserName(role);
  const goals = (RP.SALES_GOALS || []).filter(g => g && g.rep === me && g.active !== false);
  return (
    <div className="rp-fade">
      <MHeader title="My Goals" sub="Targets set by management" nav={nav} back />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {goals.map(g => {
          const elapsed = RP.PERIOD_ELAPSED[g.period] || 0.17;
          const overall = RP.goalOverall(g, elapsed);
          return (
            <div key={g.id}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div className="disp" style={{ fontSize: 20, textTransform: 'uppercase' }}>{g.period}</div>
                <Pill status={overall} />
              </div>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 16, boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <MGoalRow icon="trade" label="Revenue" actual={g.revActual} target={g.revTarget} fmt={RP.money} elapsed={elapsed} />
                <MGoalRow icon="package" label="Cases" actual={g.casesActual} target={g.casesTarget} fmt={v => v.toLocaleString()} elapsed={elapsed} />
                <MGoalRow icon="customers" label="New Doors" actual={g.doorsActual} target={g.doorsTarget} fmt={v => '' + v} elapsed={elapsed} />
              </div>
              {g.note && <div style={{ background: 'var(--amber-soft)', borderRadius: 12, padding: 13, marginTop: 12 }}><div className="eyebrow" style={{ color: 'var(--amber-deep)', marginBottom: 5 }}>Coaching · {g.setBy}</div><div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.45 }}>{g.note}</div></div>}
            </div>
          );
        })}
        {goals.length === 0 && <MEmpty icon="target" title="No goals set" sub="Your manager hasn't set targets for this period yet." />}
      </div>
    </div>
  );
};
function MGoalRow({ icon, label, actual, target, fmt, elapsed }) {
  const st = RP.goalStatus(actual, target, elapsed);
  const color = st === 'Behind' ? 'var(--danger)' : st === 'At Risk' ? 'var(--warn)' : st === 'Ahead' ? 'var(--info)' : 'var(--ok)';
  const pct = target > 0 ? Math.round(actual / target * 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600 }}><Icon name={icon} size={15} style={{ color: 'var(--muted)' }} />{label}</span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}><span className="disp" style={{ fontSize: 18 }}>{fmt(actual)}</span><span className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>/ {fmt(target)}</span></span>
      </div>
      <div style={{ position: 'relative' }}>
        <Bar value={pct} max={100} color={color} h={9} />
        <div style={{ position: 'absolute', left: Math.min(100, elapsed * 100) + '%', top: -3, bottom: -3, width: 2, background: 'var(--ink)', opacity: 0.5, borderRadius: 2 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}><span style={{ fontSize: 11, fontWeight: 700, color }}>{pct}% · {st}</span><span style={{ fontSize: 10.5, color: 'var(--faint)' }}>pace {Math.round(elapsed * 100)}%</span></div>
    </div>
  );
}

Object.assign(window, { MGoalRow });


/* ===== mobile_leads.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : Leads Pipeline + Add Lead
   ============================================================ */

const LA = () => RP.LEADS_API;

function leadLiveMode() { return !(window.RP_AUTH && window.RP_AUTH.demo); }
async function leadRequest(path, body) {
  const response = await rpApi('/api/leads/' + path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (response.lead) LA().upsertLead(response.lead);
  if (response.deletedLeadId) LA().deleteLead(response.deletedLeadId);
  return response;
}
function leadDueIn(days) {
  if (typeof rpTodayYmd === 'function') return rpTodayYmd(null, days || 0);
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function leadNormalizeFollowDue(value, fallbackDays) {
  const text = String(value || '').trim().replace(' ', 'T');
  const dateTime = text.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (dateTime) return `${dateTime[1]}T${dateTime[2]}:${dateTime[3]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  return leadDueIn(fallbackDays == null ? 3 : fallbackDays);
}
function leadFollowTime(value) {
  const match = String(value || '').match(/[T\s](\d{2}:\d{2})/);
  return match ? match[1] : '09:00';
}
function leadFollowDateTimeIn(days, currentValue) {
  return `${leadDueIn(days == null ? 3 : days)}T${leadFollowTime(currentValue)}`;
}
function leadFollowInputValue(value, fallbackDays) {
  const normalized = leadNormalizeFollowDue(value, fallbackDays);
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return `${normalized}T${leadFollowTime(value)}`;
  return normalized;
}
function leadNextActionForStage(stage, days, dueOverride) {
  const pb = LA().PLAYBOOK[stage];
  return { type: pb ? pb.next : 'call', due: leadNormalizeFollowDue(dueOverride, days == null ? 3 : days), note: pb ? pb.cta : 'Follow up' };
}
function tempColor(t) { return t === 'Hot' ? 'var(--danger)' : t === 'Warm' ? 'var(--amber)' : 'var(--info)'; }
function healthPill(h) {
  const map = { ok: ['var(--ok)', 'var(--ok-bg)'], danger: ['var(--danger)', 'var(--danger-bg)'], warn: ['var(--amber-deep)', 'var(--warn-bg)'], info: ['var(--info)', 'var(--info-bg)'], neutral: ['var(--muted)', 'var(--surface-2)'] };
  return map[h.tone] || map.neutral;
}
function leadDefaultDeliveryFromNotes(notes, fallback = 'Ranch Delivery') {
  const match = String(notes || '').match(/Default delivery preference:\s*([^\n]+)/i);
  const raw = match ? match[1].trim() : fallback;
  return typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(raw, fallback) : (raw || fallback);
}
function leadNotesWithDeliveryPreference(notes, method) {
  const normalized = typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(method) : (method || 'Ranch Delivery');
  const base = String(notes || '').replace(/^Default delivery preference:\s*.*$/gim, '').trim();
  return [base, `Default delivery preference: ${normalized}`].filter(Boolean).join('\n');
}

/* ---------- PIPELINE (rep tab root) ---------- */
MOBILE_SCREENS['rep-pipeline'] = function RepPipeline({ nav, role }) {
  const [seg, setSeg] = useState('active'); // active | won | lost
  const [stageFilter, setStageFilter] = useState('');
  const all = rpHasLiveAuth()
    ? (Array.isArray(RP.LEADS) ? RP.LEADS : [])
    : LA().forRep(rpFieldUserName(role));
  const active = all.filter(l => LA().ACTIVE_STAGES.includes(l.stage));
  const overdue = active.filter(l => LA().isOverdue(l));
  const segmentList = seg === 'active' ? active : seg === 'won' ? all.filter(l => l.stage === 'won') : all.filter(l => l.stage === 'lost');
  const list = seg === 'active' && stageFilter ? segmentList.filter(l => l.stage === stageFilter) : segmentList;
  // sort active by score desc, surfacing overdue
  const sorted = [...list].sort((a, b) => {
    if (seg === 'active') { const ov = (LA().isOverdue(b) ? 1 : 0) - (LA().isOverdue(a) ? 1 : 0); if (ov) return ov; return LA().score(b) - LA().score(a); }
    return 0;
  });
  // funnel counts
  const funnel = LA().ACTIVE_STAGES.map(s => ({ s, n: active.filter(l => l.stage === s).length }));
  const pipelineValue = active.reduce((s, l) => s + l.potential, 0);
  const selectSegment = next => {
    setSeg(next);
    setStageFilter('');
  };
  const selectStage = stage => {
    setSeg('active');
    setStageFilter(current => current === stage ? '' : stage);
  };
  const selectedStageLabel = stageFilter ? LA().stage(stageFilter).label : '';
  const emptyLabel = seg === 'active' && selectedStageLabel ? selectedStageLabel.toLowerCase() : seg;

  return (
    <div className="rp-fade">
      {/* hero */}
      <div style={{ background: 'var(--navy)', color: '#fff', padding: '4px 18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div className="disp" style={{ fontSize: 24, textTransform: 'uppercase' }}>Pipeline</div>
          <button onClick={() => nav.push('add-lead')} style={{ border: 'none', background: 'var(--amber)', color: '#fff', height: 36, borderRadius: 10, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 13 }}><Icon name="plus" size={17} />Lead</button>
        </div>
        {/* funnel */}
        <div role="group" aria-label="Filter active leads by stage" data-pipeline-stage-filters="true" style={{ display: 'flex', gap: 5, marginBottom: 12 }}>
          {funnel.map((f, i) => {
            const st = LA().stage(f.s);
            const selected = seg === 'active' && stageFilter === f.s;
            return (
              <button type="button" key={f.s} data-pipeline-stage-filter={f.s} aria-pressed={selected} aria-label={`${selected ? 'Clear' : 'Filter by'} ${st.label} stage`} onClick={() => selectStage(f.s)} style={{ flex: 1, minWidth: 0, color: '#fff', background: selected ? 'oklch(1 0 0 / 0.2)' : 'oklch(1 0 0 / 0.08)', border: '1px solid ' + (selected ? 'var(--amber)' : 'transparent'), borderRadius: 8, padding: '9px 6px', textAlign: 'center', position: 'relative' }}>
                <div className="disp" style={{ fontSize: 22, lineHeight: 1 }}>{f.n}</div>
                <div style={{ fontSize: 9.5, opacity: 0.7, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{st.short}</div>
                <div style={{ height: 3, borderRadius: 3, background: st.color, marginTop: 6 }} />
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span style={{ opacity: 0.7 }}>{active.length} active · ~{pipelineValue} cases/mo potential</span>
          {overdue.length > 0 && <span style={{ color: 'var(--amber)', fontWeight: 700 }}>{overdue.length} need follow-up</span>}
        </div>
      </div>

      {/* coaching banner: most urgent */}
      {overdue.length > 0 && seg === 'active' && (
        <div style={{ padding: '14px 16px 0' }}>
          <button onClick={() => nav.push('lead-detail', { id: overdue[0].id })} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--danger-bg)', border: '1px solid oklch(0.57 0.16 27 / 0.3)', borderRadius: 12, padding: '12px 14px' }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--danger)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="alert" size={18} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--danger)' }}>Follow up with {overdue[0].name}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 1 }}>{overdue[0].nextAction.note}</div>
            </div>
            <Icon name="chevR" size={18} style={{ color: 'var(--danger)' }} />
          </button>
        </div>
      )}

      {/* segments */}
      <div role="group" aria-label="Pipeline result status" data-pipeline-segment-filters="true" style={{ padding: '14px 16px 8px', display: 'flex', gap: 7 }}>
        {[['active', `Active ${active.length}`], ['won', `Won ${all.filter(l => l.stage === 'won').length}`], ['lost', `Lost ${all.filter(l => l.stage === 'lost').length}`]].map(([k, l]) => (
          <button type="button" key={k} data-pipeline-segment={k} aria-pressed={seg === k} onClick={() => selectSegment(k)} style={{ border: '1px solid ' + (seg === k ? 'var(--navy)' : 'var(--line)'), background: seg === k ? 'var(--navy)' : 'var(--surface)', color: seg === k ? '#fff' : 'var(--muted)', borderRadius: 100, padding: '7px 14px', fontSize: 12.5, fontWeight: 600 }}>{l}</button>
        ))}
      </div>

      {/* list */}
      <div data-pipeline-results="true" data-result-count={sorted.length} style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sorted.map(l => <LeadCard key={l.id} lead={l} onClick={() => nav.push('lead-detail', { id: l.id })} />)}
        {sorted.length === 0 && <div data-pipeline-empty="true" style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--faint)', fontSize: 13.5 }}>No {emptyLabel} leads.</div>}
      </div>
    </div>
  );
};

function LeadCard({ lead, onClick }) {
  const h = LA().health(lead);
  const [fg, bg] = healthPill(h);
  const st = LA().stage(lead.stage);
  const ds = LA().daysSince(lead.lastTouch);
  const overdue = LA().isOverdue(lead);
  return (
    <button data-lead-stage={lead.stage} onClick={onClick} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid ' + (overdue ? 'oklch(0.57 0.16 27 / 0.35)' : 'var(--line)'), borderRadius: 12, padding: 14, boxShadow: 'var(--shadow-sm)', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: tempColor(lead.temp), marginTop: 6, flexShrink: 0 }} title={lead.temp} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.name}</span>
            <span className="pill" style={{ background: bg, color: fg, flexShrink: 0 }}>{h.label}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 2 }}>{lead.type} · {lead.city} · ~{lead.potential} cs/mo</div>
          {/* stage progress */}
          <div style={{ display: 'flex', gap: 3, marginTop: 10 }}>
            {LA().ACTIVE_STAGES.map(s => {
              const idx = LA().ACTIVE_STAGES.indexOf(lead.stage);
              const here = LA().ACTIVE_STAGES.indexOf(s) <= idx;
              const wonLost = lead.stage === 'won' || lead.stage === 'lost';
              return <div key={s} style={{ flex: 1, height: 4, borderRadius: 3, background: wonLost ? (lead.stage === 'won' ? 'var(--ok)' : 'var(--line)') : here ? st.color : 'var(--surface-2)' }} />;
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 9 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: st.color === 'var(--faint)' ? 'var(--muted)' : st.color }}>{st.label}</span>
            {lead.nextAction && <span style={{ fontSize: 11.5, color: overdue ? 'var(--danger)' : 'var(--muted)', fontWeight: overdue ? 700 : 500, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '65%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><Icon name={LA().actType(lead.nextAction.type).icon} size={12} style={{ flexShrink: 0 }} />{(overdue ? 'Overdue' : 'Next: ' + LA().actType(lead.nextAction.type).label) + (lead.nextAction.due ? ' · ' + (LA().formatDue ? LA().formatDue(lead.nextAction.due) : lead.nextAction.due) : '')}</span>}
            {!lead.nextAction && ds != null && <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>{ds}d ago</span>}
          </div>
        </div>
      </div>
    </button>
  );
}

/* ---------- ADD LEAD ---------- */
MOBILE_SCREENS['add-lead'] = function AddLead({ nav, role }) {
  const repTerr = rpFieldTerritory(role);
  const authRole = rpNormalizeRole(rpAuthProfile().role || role);
  const territoryLocked = ['rep', 'partner'].includes(authRole);
  const territoryOptions = territoryLocked ? [repTerr] : RP.TERRITORIES;
  const accountDefaultTier = rpRetailAccountDefaultPriceListName();
  const canSetCustomAccountPricing = rpCanSetCustomAccountPricing();
  const [f, setF] = useState({ address: '', city: '', name: '', type: 'Retailer', tier: accountDefaultTier, defaultDeliveryMethod: 'Ranch Delivery', postal: '', territory: repTerr, temp: 'Warm', source: 'Rep idea', potential: 10, contact: '', email: '', phone: '', notes: '' });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const [tried, setTried] = useState(false);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const emailOk = !f.email.trim() || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim());

  const save = async () => {
    setTried(true);
    setError('');
    if (!f.name.trim() || !emailOk || busy) return;
    setBusy(true);
    const notes = leadNotesWithDeliveryPreference(f.notes, f.defaultDeliveryMethod);
    try {
      const lead = leadLiveMode()
        ? (await leadRequest('create', {
          address: f.address.trim(),
          city: f.city.trim() || (f.territory + ', BC'),
          contact: f.contact,
          email: f.email.trim().toLowerCase(),
          name: f.name.trim(),
          notes,
          phone: f.phone,
          postal: f.postal.trim(),
          potential: f.potential,
          source: f.source,
          temp: f.temp,
          tier: f.tier,
          territory: f.territory,
          type: f.type,
        })).lead
        : LA().addLead({ ...f, address: f.address.trim(), city: f.city.trim() || (f.territory + ', BC'), email: f.email.trim().toLowerCase(), name: f.name.trim(), notes, postal: f.postal.trim(), buyer: f.contact, rep: rpFieldUserName(role) });
      setDone(lead);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create lead.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const nba = LA().nextBestAction(done);
    return (
      <div className="rp-fade" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 28, textAlign: 'center', gap: 16 }}>
        <div style={{ width: 74, height: 74, borderRadius: '50%', background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'rp-pop .4s ease' }}><Icon name="check" size={38} /></div>
        <div><div className="disp" style={{ fontSize: 24, textTransform: 'uppercase' }}>Lead Added</div><div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 5 }}>{done.name} · {done.id}</div></div>
        <div style={{ background: 'var(--amber-soft)', borderRadius: 12, padding: '14px 16px', width: '100%', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}><Icon name="star" size={15} style={{ color: 'var(--amber-deep)' }} /><span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--amber-deep)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your next move</span></div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{nba.title}</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 3, lineHeight: 1.45 }}>{nba.detail}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.reset('rep-pipeline')}>Pipeline</button>
          <button className="btn btn-lg btn-primary" style={{ flex: 1 }} onClick={() => nav.reset('lead-detail', { id: done.id })}>Open Lead</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="New Lead" sub="Capture an idea or prospect" nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <Lbl>Business Name <span style={{ color: 'var(--danger)' }}>*</span></Lbl>
        <input className="field" placeholder="e.g. Sea to Sky Bottle Co." value={f.name} onChange={e => set('name', e.target.value)} style={{ marginBottom: tried && !f.name.trim() ? 6 : 16, borderColor: tried && !f.name.trim() ? 'var(--danger)' : undefined }} />
        {tried && !f.name.trim() && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginBottom: 14 }}>Name is required</div>}

        <Lbl>Type</Lbl>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 16 }}>
          {(window.RP_LEAD_TYPES || ['Retailer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor', 'Other']).map(t => (
            <button key={t} onClick={() => set('type', t)} style={{ padding: '8px 13px', borderRadius: 100, border: '1.5px solid ' + (f.type === t ? 'var(--amber)' : 'var(--line)'), background: f.type === t ? 'var(--amber-soft)' : 'var(--surface)', fontSize: 12.5, fontWeight: 600, color: f.type === t ? 'var(--amber-deep)' : 'var(--ink-soft)' }}>{t}</button>
          ))}
        </div>

        <Lbl>Retailer Type / Price List</Lbl>
        <select className="field" value={f.tier} disabled={!canSetCustomAccountPricing} onChange={e => set('tier', e.target.value)} style={{ marginBottom: 16 }}>
          {rpAccountPriceListOptionNames({ currentName: f.tier, excludeConsumer: true }).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}
        </select>

        <Lbl>Default Delivery</Lbl>
        <select className="field" value={f.defaultDeliveryMethod} onChange={e => set('defaultDeliveryMethod', e.target.value)} style={{ marginBottom: 16 }}>
          {(window.RP_DELIVERY_METHOD_OPTIONS || ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight']).map(option => <option key={option} value={option}>{option}</option>)}
        </select>
        {f.defaultDeliveryMethod !== 'Customer Pickup' && !f.address.trim() && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35, marginTop: -8, marginBottom: 16 }}>Delivery orders will need a ship-to address. Choose Customer Pickup if this lead should be allowed to order without one.</div>}

        <Lbl>How warm is this lead?</Lbl>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['Hot', 'Warm', 'Cold'].map(t => (
            <button key={t} onClick={() => set('temp', t)} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1.5px solid ' + (f.temp === t ? tempColor(t) : 'var(--line)'), background: f.temp === t ? `color-mix(in oklab, ${tempColor(t)} 12%, var(--surface))` : 'var(--surface)', fontSize: 13, fontWeight: 700, color: f.temp === t ? tempColor(t) : 'var(--muted)' }}>{t}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}><Lbl>Territory</Lbl><select className="field" value={f.territory} disabled={territoryLocked} onChange={e => set('territory', e.target.value)} style={{ opacity: territoryLocked ? 0.75 : 1 }}>{territoryOptions.map(t => <option key={t}>{t}</option>)}</select></div>
          <div style={{ flex: 1 }}><Lbl>Source</Lbl><select className="field" value={f.source} onChange={e => set('source', e.target.value)}>{['Rep idea', 'Referral', 'Walk-in', 'Event', 'Inbound'].map(t => <option key={t}>{t}</option>)}</select></div>
        </div>
        {territoryLocked && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -10, marginBottom: 16 }}>Your account is limited to this territory.</div>}

        <Lbl>Street Address <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
        <input className="field" placeholder="e.g. 38005 Cleveland Ave" value={f.address} onChange={e => set('address', e.target.value)} style={{ marginBottom: 12 }} />
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}><Lbl>City</Lbl><input className="field" placeholder="Squamish" value={f.city} onChange={e => set('city', e.target.value)} /></div>
          <div style={{ flex: 1 }}><Lbl>Postal</Lbl><input className="field" placeholder="V8B 0A1" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} /></div>
        </div>

        <Lbl>Est. potential (cases / month)</Lbl>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <input type="range" min="2" max="60" step="2" value={f.potential} onChange={e => set('potential', +e.target.value)} style={{ flex: 1, accentColor: 'var(--amber)' }} />
          <span className="disp" style={{ fontSize: 22, minWidth: 56, textAlign: 'right' }}>{f.potential}<span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}> cs</span></span>
        </div>

        <Lbl>Contact <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
        <input className="field" placeholder="Name / role" value={f.contact} onChange={e => set('contact', e.target.value)} style={{ marginBottom: 12 }} />
        <input className="field" type="tel" placeholder="Phone" value={f.phone} onChange={e => set('phone', e.target.value)} style={{ marginBottom: 12 }} />
        <input className="field" type="email" placeholder="Email" value={f.email} onChange={e => set('email', e.target.value)} style={{ marginBottom: tried && !emailOk ? 6 : 16, borderColor: tried && !emailOk ? 'var(--danger)' : undefined }} />
        {tried && !emailOk && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginBottom: 14 }}>Enter a valid email</div>}

        <Lbl>Why this lead? <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(your notes)</span></Lbl>
        <textarea className="field" rows="3" placeholder="What you noticed, the angle, who to ask for…" value={f.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'none' }} />
        {error && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 600 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy || !f.name.trim() || !emailOk} style={{ flex: 1, opacity: f.name.trim() && emailOk && !busy ? 1 : 0.5 }} onClick={save}><Icon name="plus" size={17} />{busy ? 'Adding...' : 'Add to Pipeline'}</button></MActionBar>
    </div>
  );
};

Object.assign(window, { LeadCard, tempColor, healthPill, leadDefaultDeliveryFromNotes, leadNotesWithDeliveryPreference, leadLiveMode, leadRequest, leadDueIn, leadNormalizeFollowDue, leadFollowDateTimeIn, leadFollowInputValue, leadNextActionForStage });


/* ===== mobile_leads_detail.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : Lead Detail (coaching), Log Touch, Convert
   ============================================================ */

function mobileLeadClean(value) {
  return String(value || '').trim();
}

function mobileLeadPhoneHref(phone) {
  const raw = mobileLeadClean(phone);
  if (!raw) return '';
  const dial = raw.replace(/[^\d+]/g, '');
  return dial ? `tel:${dial}` : `tel:${encodeURIComponent(raw)}`;
}

function MobileLeadHeaderActions({ lead, onEmail }) {
  const phoneHref = mobileLeadPhoneHref(lead && lead.phone);
  if (!phoneHref && !onEmail) return null;
  const actionStyle = { border: 'none', background: 'oklch(1 0 0 / 0.14)', color: '#fff', width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' };
  return (
    <div style={{ display: 'flex', gap: 7 }}>
      {phoneHref && <a href={phoneHref} aria-label="Call lead" style={actionStyle}><Icon name="phone" size={17} /></a>}
      {onEmail && <button type="button" onClick={onEmail} aria-label="Build email for lead" style={actionStyle}><Icon name="mail" size={17} /></button>}
    </div>
  );
}

function MobileLeadContactCard({ lead, onEdit, onEmail }) {
  const contactName = mobileLeadClean(lead && lead.contact);
  const phone = mobileLeadClean(lead && lead.phone);
  const email = mobileLeadClean(lead && lead.email);
  const phoneHref = mobileLeadPhoneHref(phone);
  const addressText = [lead && lead.address, lead && (lead.city || lead.territory), lead && lead.postal].filter(Boolean).join(', ');
  const actionBase = { flex: 1, minHeight: 44, borderRadius: 11, padding: '10px 8px', fontSize: 12.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, textDecoration: 'none' };
  const enabledAction = { ...actionBase, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)' };
  const disabledAction = { ...actionBase, border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--faint)', opacity: 0.75 };
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, marginBottom: 13 }}>
        <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface-2)', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="user" size={18} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow" style={{ marginBottom: 3 }}>Contact</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contactName || 'No contact name'}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{lead.type || 'Lead'} - {lead.city || lead.territory || 'No city'}</div>
        </div>
        <button type="button" onClick={onEdit} style={{ border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-soft)', width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-label="Edit contact"><Icon name="pen" size={16} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 13 }}>
        <Field2 k="Phone" v={phoneHref ? <a href={phoneHref} style={{ color: 'var(--amber-deep)', textDecoration: 'none', fontWeight: 800 }}>{phone}</a> : 'Missing'} />
        <Field2 k="Email" v={email ? <span style={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{email}</span> : 'Missing'} />
        <Field2 k="Address" v={addressText || 'Missing'} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {phoneHref ? <a href={phoneHref} style={enabledAction}><Icon name="phone" size={16} />Call</a> : <span style={disabledAction}><Icon name="phone" size={16} />No phone</span>}
        <button type="button" onClick={onEmail} style={enabledAction}><Icon name="mail" size={16} />{email ? 'Build email' : 'Add & email'}</button>
      </div>
    </div>
  );
}

function MobileLeadNotesCard({ lead }) {
  const notes = mobileLeadClean(lead && lead.notes);
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
      <div className="eyebrow" style={{ marginBottom: 9 }}>Notes</div>
      <div style={{ fontSize: 13, color: notes ? 'var(--ink-soft)' : 'var(--faint)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{notes || 'No lead notes recorded yet.'}</div>
    </div>
  );
}

/* ---------- LEAD DETAIL ---------- */
MOBILE_SCREENS['lead-detail'] = function LeadDetail({ nav, id }) {
  const [, force] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const lead = RP.LEADS.find(l => l.id === id);
  if (!lead) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Lead not found.</div>;
  const st = LA().stage(lead.stage);
  const nba = LA().nextBestAction(lead);
  const h = LA().health(lead);
  const [fg, bg] = healthPill(h);
  const stageIdx = LA().ACTIVE_STAGES.indexOf(lead.stage);
  const isActive = LA().ACTIVE_STAGES.includes(lead.stage);
  const canDeleteDuplicate = typeof rpCanDeleteDuplicateLead === 'function' && rpCanDeleteDuplicateLead(lead);

  const updateStage = async (stage, extra, message) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (leadLiveMode()) {
        await leadRequest('stage', { leadId: id, stage, ...(extra || {}) });
      } else {
        LA().advanceStage(id, stage, {
          lostReason: extra && extra.lostReason != null ? extra.lostReason : null,
          nextAction: extra && Object.prototype.hasOwnProperty.call(extra, 'nextAction') ? extra.nextAction : lead.nextAction,
          revisit: extra && extra.revisitDate ? extra.revisitDate : lead.revisit,
        });
      }
      force(x => x + 1);
      toast(message);
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update lead.');
    } finally {
      setBusy(false);
    }
  };

  const doPrimary = async () => {
    if (nba.action === 'convert') { nav.push('convert-lead', { id }); return; }
    if (nba.action === 'advance') { nav.push('log-touch', { id, advance: true }); return; }
    if (nba.action === 'reopen') {
      await updateStage('contacted', { nextAction: { type: 'call', due: leadFollowDateTimeIn(1), note: 'Re-engage' } }, 'Lead reopened');
    }
  };
  const deleteLead = async () => {
    if (busy) return;
    const confirmed = window.confirm ? window.confirm(`Permanently delete ${lead.name} as a duplicate or test lead?\n\nIf this is a real lead that is no longer interested, cancel and use Mark as lost so its history is preserved. Converted leads and leads with customer, contact, task, or email history cannot be deleted.`) : true;
    if (!confirmed) return;
    setBusy(true);
    setError('');
    try {
      if (leadLiveMode()) {
        await leadRequest('delete', { leadId: id, reason: 'Confirmed duplicate or test lead cleanup.' });
      } else {
        LA().deleteLead(id);
      }
      toast(`${lead.name} duplicate/test lead deleted`, 'warn');
      nav.reset('rep-pipeline');
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not delete lead.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {emailOpen && <LeadEmailComposer lead={lead} allowSend={true} onClose={() => setEmailOpen(false)} onSent={() => {
        rpRefreshAfterMutation('Lead email activity refreshed').then(() => force(x => x + 1));
      }} />}
      <MHeader title={lead.name} sub={`${lead.type} · ${lead.city}`} nav={nav} back dark
        right={<MobileLeadHeaderActions lead={lead} onEmail={() => setEmailOpen(true)} />} />

      {/* stage stepper on navy */}
      <div style={{ background: 'var(--navy)', padding: '0 16px 16px' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {LA().ACTIVE_STAGES.map((s, i) => {
            const ss = LA().stage(s);
            const reached = isActive ? i <= stageIdx : lead.stage === 'won';
            return (
              <div key={s} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: 5, borderRadius: 3, background: reached ? (lead.stage === 'won' ? 'var(--ok)' : ss.color) : 'oklch(1 0 0 / 0.15)' }} />
                <div style={{ fontSize: 9.5, marginTop: 5, color: i === stageIdx && isActive ? '#fff' : 'oklch(0.7 0.02 235)', fontWeight: i === stageIdx ? 700 : 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{ss.short}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <span className="pill" style={{ background: bg, color: fg }}>{h.label}</span>
          <span className="pill" style={{ background: 'oklch(1 0 0 / 0.1)', color: '#fff' }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: tempColor(lead.temp) }} />{lead.temp}</span>
          <span className="pill" style={{ background: 'oklch(1 0 0 / 0.1)', color: '#fff' }}>~{lead.potential} cs/mo</span>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, color: '#fff' }}><span className="disp" style={{ fontSize: 20 }}>{LA().score(lead)}</span><span style={{ fontSize: 10, opacity: 0.7 }}>score</span></span>
        </div>
      </div>

      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        {/* COACH CARD — next best action */}
        <div style={{ background: nba.urgent ? 'var(--danger-bg)' : 'var(--amber-soft)', border: '1px solid ' + (nba.urgent ? 'oklch(0.57 0.16 27 / 0.3)' : 'oklch(0.64 0.115 64 / 0.3)'), borderRadius: 14, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
            <Icon name={nba.urgent ? 'alert' : 'star'} size={16} style={{ color: nba.urgent ? 'var(--danger)' : 'var(--amber-deep)' }} />
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: nba.urgent ? 'var(--danger)' : 'var(--amber-deep)' }}>{nba.urgent ? 'Act now' : 'Your next move'}</span>
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.25 }}>{nba.title}</div>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 5, lineHeight: 1.45 }}>{nba.detail}</div>
          <button className="btn btn-primary" disabled={busy} style={{ width: '100%', marginTop: 13, opacity: busy ? 0.6 : 1 }} onClick={doPrimary}>
            <Icon name={nba.action === 'convert' ? 'check' : nba.channel ? LA().actType(nba.channel).icon : 'arrowR'} size={16} />{nba.cta}
          </button>
          {error && <div style={{ color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{error}</div>}
        </div>

        {/* Playbook tips */}
        {nba.tips && nba.tips.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}><Icon name="doc" size={15} style={{ color: 'var(--navy)' }} /><span className="eyebrow">{st.label} playbook</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {nba.tips.map((tip, i) => (
                <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--surface-2)', color: 'var(--amber-deep)', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                  <span style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.4 }}>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick log actions */}
        {isActive && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {LA().ACTIVITY_TYPES.slice(0, 4).map(a => (
              <button key={a.id} onClick={() => a.id === 'email' ? setEmailOpen(true) : nav.push('log-touch', { id, preset: a.id })} style={{ flex: 1, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 11, padding: '11px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, boxShadow: 'var(--shadow-sm)' }}>
                <Icon name={a.icon} size={18} style={{ color: 'var(--navy)' }} />
                <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ink-soft)' }}>{a.label}</span>
              </button>
            ))}
          </div>
        )}

        <MobileLeadContactCard lead={lead} onEdit={() => nav.push('edit-lead-contact', { id })} onEmail={() => setEmailOpen(true)} />
        <MobileLeadNotesCard lead={lead} />

        {/* Lead meta */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field2 k="Source" v={lead.source} />
            <Field2 k="Territory" v={lead.territory} />
            <Field2 k="Price list" v={lead.tier || 'Default Retail'} />
            <Field2 k="Created" v={lead.created || '-'} />
          </div>
        </div>

        {/* Next action */}
        {lead.nextAction && isActive && (
          <button onClick={() => nav.push('log-touch', { id })} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: LA().isOverdue(lead) ? 'var(--danger-bg)' : 'var(--surface-2)', color: LA().isOverdue(lead) ? 'var(--danger)' : 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="calendar" size={17} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: 'var(--faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scheduled follow-up</div>
              <div style={{ fontSize: 13.5, fontWeight: 600, marginTop: 2 }}>{lead.nextAction.note}</div>
              <div style={{ fontSize: 12, color: LA().isOverdue(lead) ? 'var(--danger)' : 'var(--muted)', fontWeight: LA().isOverdue(lead) ? 700 : 500, marginTop: 1 }}>{LA().isOverdue(lead) ? 'Overdue · ' : 'Due '}{LA().formatDue ? LA().formatDue(lead.nextAction.due) : lead.nextAction.due}</div>
            </div>
            <Icon name="pen" size={16} style={{ color: 'var(--faint)' }} />
          </button>
        )}

        {/* Activity timeline */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span className="eyebrow">Activity</span>
          {isActive && <button onClick={() => nav.push('log-touch', { id })} style={{ border: 'none', background: 'none', color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 700 }}>+ Log touch</button>}
        </div>
        <div>
          {lead.activities.map((a, i) => {
            const at = LA().actType(a.type);
            return (
              <div key={i} style={{ display: 'flex', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span style={{ width: 30, height: 30, borderRadius: '50%', background: i === 0 ? 'var(--navy)' : 'var(--surface-2)', color: i === 0 ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={at.icon} size={15} /></span>
                  {i < lead.activities.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--line-2)', minHeight: 12 }} />}
                </div>
                <div style={{ paddingBottom: 18, flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ fontSize: 13, fontWeight: 600 }}>{at.label}</span><span style={{ fontSize: 11, color: 'var(--faint)' }}>{a.date}</span></div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginTop: 3, lineHeight: 1.45 }}>{a.note}</div>
                </div>
              </div>
            );
          })}
        </div>

        {isActive && (
          <button disabled={busy} onClick={() => updateStage('lost', { lostReason: 'Not interested', nextAction: null }, 'Marked as lost')} style={{ width: '100%', border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)', borderRadius: 10, padding: 12, fontSize: 13, fontWeight: 600, marginTop: 6, opacity: busy ? 0.6 : 1 }}>Mark as lost - keep history</button>
        )}
        {canDeleteDuplicate && <button disabled={busy} onClick={deleteLead} style={{ width: '100%', border: '1px solid oklch(0.57 0.16 27 / 0.3)', background: 'transparent', color: 'var(--danger)', borderRadius: 10, padding: 12, fontSize: 13, fontWeight: 700, marginTop: 8, opacity: busy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}><Icon name="trash" size={15} />Delete duplicate / test lead</button>}
      </div>
    </div>
  );
};

MOBILE_SCREENS['edit-lead-contact'] = function EditLeadContact({ nav, id }) {
  const lead = RP.LEADS.find(l => l.id === id);
  const [f, setF] = useState(() => ({
    address: lead && lead.address || '',
    city: lead && lead.city || '',
    contact: lead && lead.contact || '',
    email: lead && lead.email || '',
    name: lead && lead.name || '',
    notes: lead && lead.notes || '',
    phone: lead && lead.phone || '',
    postal: lead && lead.postal || ''
  }));
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setF(state => ({ ...state, [key]: value }));
  const name = f.name.trim();
  const email = f.email.trim().toLowerCase();
  const emailOk = !email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  if (!lead) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Lead not found.</div>;

  const save = async () => {
    setTried(true);
    setError('');
    if (busy || !name || !emailOk) return;
    setBusy(true);
    const patch = {
      address: f.address.trim(),
      city: f.city.trim(),
      contact: f.contact.trim(),
      email,
      name,
      notes: f.notes.trim(),
      phone: f.phone.trim(),
      postal: f.postal.trim()
    };
    try {
      if (leadLiveMode()) {
        await leadRequest('update', { leadId: id, ...patch });
      } else {
        LA().upsertLead({ id, ...patch });
      }
      toast('Lead details updated');
      nav.pop();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update contact details.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Edit Lead" sub={lead.name} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <Lbl>Business Name <span style={{ color: 'var(--danger)' }}>*</span></Lbl>
        <input className="field" placeholder="e.g. Sea to Sky Bottle Co." value={f.name} onChange={e => set('name', e.target.value)} style={{ marginBottom: tried && !name ? 6 : 14, borderColor: tried && !name ? 'var(--danger)' : undefined }} />
        {tried && !name && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginBottom: 14 }}>Business name is required.</div>}

        <Lbl>Street Address</Lbl>
        <input className="field" placeholder="e.g. 38005 Cleveland Ave" value={f.address} onChange={e => set('address', e.target.value)} style={{ marginBottom: 14 }} />

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}><Lbl>City</Lbl><input className="field" placeholder="Squamish" value={f.city} onChange={e => set('city', e.target.value)} /></div>
          <div style={{ flex: 1 }}><Lbl>Postal</Lbl><input className="field" placeholder="V8B 0A1" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} /></div>
        </div>

        <Lbl>Contact name / role</Lbl>
        <input className="field" placeholder="e.g. Owner - Ray" value={f.contact} onChange={e => set('contact', e.target.value)} style={{ marginBottom: 14 }} />

        <Lbl>Phone</Lbl>
        <input className="field" type="tel" placeholder="(604) 555-0100" value={f.phone} onChange={e => set('phone', e.target.value)} style={{ marginBottom: 14 }} />

        <Lbl>Email</Lbl>
        <input className="field" type="email" placeholder="buyer@example.com" value={f.email} onChange={e => set('email', e.target.value)} style={{ marginBottom: tried && !emailOk ? 6 : 14, borderColor: tried && !emailOk ? 'var(--danger)' : undefined }} />
        {tried && !emailOk && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginBottom: 14 }}>Enter a valid email or leave it blank.</div>}

        <Lbl>Notes</Lbl>
        <textarea className="field" rows="6" placeholder="Buying context, contacts, hours, objections, next details..." value={f.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />

        <div style={{ marginTop: 14, background: 'var(--info-bg)', color: 'var(--info)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.45, display: 'flex', gap: 9, alignItems: 'flex-start' }}>
          <Icon name="user" size={16} style={{ flexShrink: 0, marginTop: 1 }} />These details stay on the pipeline lead and become the default ship-to details when a new customer is created.
        </div>
        {error && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 600 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy || !name || !emailOk} style={{ flex: 1, opacity: busy || !name || !emailOk ? 0.6 : 1 }} onClick={save}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Save Lead'}</button></MActionBar>
    </div>
  );
};

/* ---------- LOG TOUCH ---------- */
MOBILE_SCREENS['log-touch'] = function LogTouch({ nav, id, preset, advance }) {
  const lead = RP.LEADS.find(l => l.id === id);
  const [type, setType] = useState(preset || (lead.nextAction ? lead.nextAction.type : 'call'));
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState('positive');
  const curIdx = LA().ACTIVE_STAGES.indexOf(lead.stage);
  const nextStage = LA().ACTIVE_STAGES[curIdx + 1];
  const [moveNext, setMoveNext] = useState(!!advance && !!nextStage);
  const [followDays, setFollowDays] = useState(3);
  const [followAt, setFollowAt] = useState(() => leadFollowInputValue(lead && lead.nextAction && lead.nextAction.due, 3));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    const targetStage = moveNext && nextStage ? nextStage : lead.stage;
    const nextAction = leadNextActionForStage(targetStage, followDays, followAt);
    const activity = {
      type, outcome, note: note || LA().actType(type).label + ' logged',
      nextAction,
    };
    try {
      if (leadLiveMode()) {
        await leadRequest('activity', {
          leadId: id,
          type,
          outcome,
          note: activity.note,
          nextAction,
          stage: moveNext && nextStage ? nextStage : undefined,
        });
      } else {
        LA().logActivity(id, activity);
        if (moveNext && nextStage) LA().advanceStage(id, nextStage);
      }
      if (moveNext && nextStage) toast(`Logged · advanced to ${LA().stage(nextStage).label}`);
      else toast('Touch logged · follow-up set');
      nav.pop();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not log touch.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Log a Touch" sub={lead.name} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <Lbl>What did you do?</Lbl>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
          {LA().ACTIVITY_TYPES.map(a => (
            <button key={a.id} onClick={() => setType(a.id)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 13px', borderRadius: 100, border: '1.5px solid ' + (type === a.id ? 'var(--amber)' : 'var(--line)'), background: type === a.id ? 'var(--amber-soft)' : 'var(--surface)', fontSize: 12.5, fontWeight: 600, color: type === a.id ? 'var(--amber-deep)' : 'var(--ink-soft)' }}><Icon name={a.icon} size={15} />{a.label}</button>
          ))}
        </div>

        <Lbl>How did it go?</Lbl>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {[['positive', 'Positive', 'var(--ok)'], ['neutral', 'Neutral', 'var(--muted)'], ['negative', 'Negative', 'var(--danger)']].map(([k, l, c]) => (
            <button key={k} onClick={() => setOutcome(k)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1.5px solid ' + (outcome === k ? c : 'var(--line)'), background: outcome === k ? `color-mix(in oklab, ${c} 12%, var(--surface))` : 'var(--surface)', fontSize: 12.5, fontWeight: 700, color: outcome === k ? c : 'var(--muted)' }}>{l}</button>
          ))}
        </div>

        <Lbl>Notes</Lbl>
        <textarea className="field" rows="3" placeholder="What was said, who you met, next step…" value={note} onChange={e => setNote(e.target.value)} style={{ resize: 'none', marginBottom: 18 }} />

        {nextStage && (
          <div onClick={() => setMoveNext(m => !m)} style={{ display: 'flex', alignItems: 'center', gap: 12, background: moveNext ? 'var(--amber-soft)' : 'var(--surface)', border: '1.5px solid ' + (moveNext ? 'var(--amber)' : 'var(--line)'), borderRadius: 12, padding: '13px 14px', marginBottom: 18, cursor: 'pointer' }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, border: '2px solid ' + (moveNext ? 'var(--amber)' : 'var(--faint)'), background: moveNext ? 'var(--amber)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{moveNext && <Icon name="check" size={13} style={{ color: '#fff' }} />}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Advance to {LA().stage(nextStage).label}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{LA().stage(nextStage).hint}</div>
            </div>
          </div>
        )}

        <Lbl>Set next follow-up</Lbl>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['Tomorrow', 1], ['In 3 days', 3], ['In 1 week', 7], ['In 2 weeks', 14]].map(([l, d]) => (
            <button key={d} onClick={() => { setFollowDays(d); setFollowAt(leadFollowDateTimeIn(d, followAt)); }} style={{ flex: 1, padding: '10px 4px', borderRadius: 9, border: '1px solid ' + (followDays === d ? 'var(--navy)' : 'var(--line)'), background: followDays === d ? 'var(--navy)' : 'var(--surface)', color: followDays === d ? '#fff' : 'var(--ink-soft)', fontSize: 11.5, fontWeight: 600 }}>{l}</button>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <Lbl>Specific date and time</Lbl>
          <input className="field" type="datetime-local" value={followAt} onChange={e => setFollowAt(e.target.value)} />
          <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 6 }}>Used for the next task reminder and overdue status.</div>
        </div>
        {error && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 600 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy} style={{ flex: 1, opacity: busy ? 0.6 : 1 }} onClick={save}><Icon name="check" size={17} />{busy ? 'Saving...' : `Save${moveNext && nextStage ? ' & Advance' : ''}`}</button></MActionBar>
    </div>
  );
};

/* ---------- CONVERT LEAD → CUSTOMER ---------- */
function leadContactDrafts(lead) {
  const raw = String(lead && lead.contact || '').trim();
  let name = raw;
  let role = 'Buyer';
  const parts = raw.split(/\s+[—-]\s+/).filter(Boolean);
  if (parts.length >= 2) {
    role = parts[0].trim() || role;
    name = parts.slice(1).join(' - ').trim();
  }
  return [rpContactDraftFrom({
    email: lead && lead.email || '',
    name,
    phone: lead && lead.phone || '',
    primary: true,
    role
  }, {}, true)];
}

function mobileLeadExistingCustomerOptions(lead, search) {
  if (!lead) return [];
  const scopedCustomers = typeof rpFieldCustomers === 'function' ? rpFieldCustomers() : rpVisibleCustomers(RP.CUSTOMERS);
  const duplicateMatches = typeof rpCustomerDuplicateCandidates === 'function'
    ? rpCustomerDuplicateCandidates({ name: lead.name, email: lead.email, phone: lead.phone, address: lead.address, city: lead.city, postal: lead.postal, territory: lead.territory })
    : [];
  const matchIds = new Set(duplicateMatches.map(match => match.customer && match.customer.id).filter(Boolean));
  const query = String(search || '').trim().toLowerCase();
  return (scopedCustomers || [])
    .filter(customer => customer && customer.id)
    .filter(customer => {
      if (!query) return true;
      return [customer.name, customer.banner, customer.address, customer.city, customer.postal, customer.territory, customer.type, customer.tier, customer.email, customer.phone]
        .join(' ')
        .toLowerCase()
        .includes(query);
    })
    .sort((a, b) => (matchIds.has(b.id) ? 1 : 0) - (matchIds.has(a.id) ? 1 : 0) || String(a.name || '').localeCompare(String(b.name || '')))
    .slice(0, 80);
}

MOBILE_SCREENS['convert-lead'] = function ConvertLead({ nav, id }) {
  const accountDefaultTier = rpRetailAccountDefaultPriceListName();
  const canSetCustomAccountPricing = rpCanSetCustomAccountPricing();
  const lead = RP.LEADS.find(l => l.id === id);
  const duplicateMatches = typeof rpCustomerDuplicateCandidates === 'function'
    ? rpCustomerDuplicateCandidates({ name: lead && lead.name, email: lead && lead.email, phone: lead && lead.phone, address: lead && lead.address, city: lead && lead.city, postal: lead && lead.postal, territory: lead && lead.territory })
    : [];
  const initialCustomerId = (duplicateMatches[0] && duplicateMatches[0].customer && duplicateMatches[0].customer.id) || '';
  const [mode, setMode] = useState(initialCustomerId ? 'existing' : 'new');
  const [customerSearch, setCustomerSearch] = useState(lead && lead.name || '');
  const existingCustomerOptions = mobileLeadExistingCustomerOptions(lead, customerSearch);
  const [existingCustomerId, setExistingCustomerId] = useState(initialCustomerId || (existingCustomerOptions[0] && existingCustomerOptions[0].id) || '');
  const [tier, setTier] = useState(canSetCustomAccountPricing ? rpRetailAccountNewPriceListName(lead && lead.tier) : accountDefaultTier);
  const [terms, setTerms] = useState('Prepaid');
  const [defaultDeliveryMethod, setDefaultDeliveryMethod] = useState(() => typeof leadDefaultDeliveryFromNotes === 'function' ? leadDefaultDeliveryFromNotes(lead && lead.notes) : 'Ranch Delivery');
  const [contacts, setContacts] = useState(() => leadContactDrafts(lead));
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tried, setTried] = useState(false);
  const selectedCustomer = RP.CUSTOMERS.find(customer => customer.id === existingCustomerId);

  useEffect(() => {
    if (mode !== 'existing') return;
    if (existingCustomerId && existingCustomerOptions.some(customer => customer.id === existingCustomerId)) return;
    setExistingCustomerId(initialCustomerId || (existingCustomerOptions[0] && existingCustomerOptions[0].id) || '');
  }, [mode, customerSearch]);

  if (!lead) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Lead not found.</div>;

  const convert = async () => {
    if (busy) return;
    setTried(true);
    if (mode === 'existing' && !existingCustomerId) {
      setError('Choose an existing customer to link.');
      return;
    }
    if (mode === 'new' && rpCustomerContactsHaveInvalidEmail(contacts)) {
      setError('Enter valid email addresses for every contact or leave them blank.');
      return;
    }
    setBusy(true);
    setError('');
    const cleanedContacts = mode === 'new' ? rpCleanCustomerContactsDrafts(contacts) : [];
    const primaryContact = cleanedContacts.find(contact => contact.primary) || cleanedContacts[0] || {};
    try {
      let cust = null;
      if (leadLiveMode()) {
        const response = await leadRequest('convert', mode === 'existing'
          ? {
              existingCustomerId,
              leadId: id
            }
          : {
              buyerEmail: primaryContact.email || '',
              buyerName: primaryContact.name || '',
              contacts: cleanedContacts,
              defaultDeliveryMethod,
              leadId: id,
              phone: primaryContact.phone || '',
              requestApproval: true,
              tier,
              terms
            });
        if (response.approvalTask) RP.upsertTask(response.approvalTask);
        cust = RP.upsertCustomer({ ...response.customer, approvalTask: response.approvalTask || null, skipCreds: true, persisted: true });
        await rpRefreshAfterMutation(mode === 'existing' ? `${lead.name} linked` : `${cust.name} converted`);
      } else if (mode === 'existing') {
        cust = selectedCustomer;
        if (!cust) throw new Error('Choose an existing customer to link.');
        LA().advanceStage(id, 'won', { convertedTo: cust.id, nextAction: null });
      } else {
        cust = LA().convertLead(id, { contacts: cleanedContacts, defaultDeliveryMethod, tier, terms });
      }
      if (cust) { setDone({ ...cust, conversionMode: mode }); toast(mode === 'existing' ? `${lead.name} linked to ${cust.name}` : `${lead.name} converted - approval queued`); }
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not convert lead.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    const approvalTask = done.approvalTask;
    const linkedExisting = done.conversionMode === 'existing';
    return (
      <div className="rp-fade" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 26, textAlign: 'center', gap: 16 }}>
        <div style={{ width: 78, height: 78, borderRadius: '50%', background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'rp-pop .4s ease' }}><Icon name="check" size={40} /></div>
        <div><div className="disp" style={{ fontSize: 25, textTransform: 'uppercase' }}>{linkedExisting ? 'Customer Linked' : 'Prospect Created'}</div><div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 5 }}>{done.name} - {done.id}</div></div>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.45, maxWidth: 360 }}>{linkedExisting ? `${lead.name} is now connected to the existing customer account.` : `${done.name} is saved as a prospect. Approval is required before portal access or ordering.`}</div>
        {approvalTask && (
          <div style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}><Icon name="flag" size={16} style={{ color: 'var(--amber)' }} /><span style={{ fontSize: 12.5, fontWeight: 700 }}>Approval task queued</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}><span style={{ fontSize: 11.5, color: 'var(--faint)', fontWeight: 600 }}>TASK</span><span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{approvalTask.id}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 11.5, color: 'var(--faint)', fontWeight: 600 }}>OWNER</span><span style={{ fontSize: 12, fontWeight: 600 }}>{approvalTask.owner}</span></div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.reset('rep-pipeline')}>Pipeline</button>
          <button className="btn btn-lg btn-primary" style={{ flex: 1 }} onClick={() => nav.reset('rep-customer', { id: done.id })}><Icon name="user" size={16} />Customer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="Convert to Customer" sub={lead.name} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div style={{ background: 'var(--ok-bg)', borderRadius: 12, padding: 14, marginBottom: 18, display: 'flex', gap: 11, alignItems: 'center' }}>
          <Icon name="star" size={20} style={{ color: 'var(--ok)', flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.45 }}>Nice work closing <b>{lead.name}</b>. Link it to an existing customer or create a new prospect account.</div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 18, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field2 k="Business" v={lead.name} /><Field2 k="Type" v={lead.type} />
            <Field2 k="Address" v={[lead.address, lead.city || lead.territory, lead.postal].filter(Boolean).join(', ') || 'Missing'} />
            <Field2 k="Contact" v={lead.contact || '—'} /><Field2 k="Territory" v={lead.territory} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 18 }}>
          <button type="button" onClick={() => setMode('existing')} style={{ border: '1.5px solid ' + (mode === 'existing' ? 'var(--amber)' : 'var(--line)'), background: mode === 'existing' ? 'var(--amber-soft)' : 'var(--surface)', color: mode === 'existing' ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 11, padding: '12px 10px', fontSize: 13, fontWeight: 800 }}>Link existing</button>
          <button type="button" onClick={() => setMode('new')} style={{ border: '1.5px solid ' + (mode === 'new' ? 'var(--amber)' : 'var(--line)'), background: mode === 'new' ? 'var(--amber-soft)' : 'var(--surface)', color: mode === 'new' ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 11, padding: '12px 10px', fontSize: 13, fontWeight: 800 }}>Create new</button>
        </div>

        {mode === 'existing' ? <>
          {duplicateMatches.length > 0 && <div style={{ background: 'var(--info-bg)', color: 'var(--info)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 500, lineHeight: 1.4, marginBottom: 14 }}>
            Possible existing account: {duplicateMatches.slice(0, 2).map(match => match.customer.name).join(', ')}.
          </div>}
          <Lbl>Find Customer</Lbl>
          <input className="field" value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Search name, city, territory, email..." style={{ marginBottom: 12 }} />
          <Lbl>Customer to Link</Lbl>
          <select className="field" value={existingCustomerId} onChange={e => setExistingCustomerId(e.target.value)} style={{ marginBottom: 14 }}>
            <option value="">Choose an existing customer...</option>
            {existingCustomerOptions.map(customer => <option key={customer.id} value={customer.id}>{customer.name} - {customer.territory}</option>)}
          </select>
          {selectedCustomer && <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 14, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>{selectedCustomer.name}</div>
            <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 3 }}>{selectedCustomer.type} - {selectedCustomer.tier || 'No tier'} - {selectedCustomer.status || 'No status'}</div>
            <div style={{ color: 'var(--faint)', fontSize: 12, marginTop: 3 }}>{selectedCustomer.city || selectedCustomer.territory}{selectedCustomer.email ? ` - ${selectedCustomer.email}` : ''}</div>
          </div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 500, lineHeight: 1.4 }}>
            <Icon name="link" size={16} style={{ flexShrink: 0 }} />This marks the lead won. If the selected account is missing a ship-to address, this lead's address fills it in.
          </div>
        </> : <>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Contacts</div>
          <MobileCustomerContactsEditor contacts={contacts} onChange={setContacts} tried={tried} />

          <Lbl>Price List / Tier</Lbl>
          <select className="field" value={tier} disabled={!canSetCustomAccountPricing} onChange={e => setTier(e.target.value)} style={{ marginBottom: 16 }}>
            {rpAccountPriceListOptionNames({ currentName: tier, excludeConsumer: true }).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}
          </select>
          <Lbl>Default Delivery</Lbl>
          <select className="field" value={defaultDeliveryMethod} onChange={e => setDefaultDeliveryMethod(e.target.value)} style={{ marginBottom: 16 }}>
            {(window.RP_DELIVERY_METHOD_OPTIONS || ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight']).map(option => <option key={option} value={option}>{option}</option>)}
          </select>
          {defaultDeliveryMethod !== 'Customer Pickup' && !String(lead.address || '').trim() && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35, marginTop: -8, marginBottom: 16 }}>Delivery orders will need a ship-to address. Choose Customer Pickup if this account should be allowed to order without one.</div>}
          <Lbl>Payment Terms</Lbl>
          <select className="field" value={terms} onChange={e => setTerms(e.target.value)} style={{ marginBottom: 16 }}>
            {['Prepaid', 'COD', 'Net 30'].map(t => <option key={t}>{t}</option>)}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '11px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 500, lineHeight: 1.4 }}>
            <Icon name="user" size={16} style={{ flexShrink: 0 }} />A customer record and approval task will be created. The lead address becomes the default ship-to address when present.
          </div>
        </>}
        {error && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 600 }}>{error}</div>}
      </div>
      <MActionBar><button className="btn btn-lg btn-primary" disabled={busy || (mode === 'existing' && !existingCustomerId)} style={{ flex: 1, opacity: busy || (mode === 'existing' && !existingCustomerId) ? 0.6 : 1 }} onClick={convert}><Icon name="check" size={17} />{busy ? 'Converting...' : mode === 'existing' ? 'Link Customer' : 'Create Prospect'}</button></MActionBar>
    </div>
  );
};


/* ===== mobile_customer.jsx ===== */
/* ============================================================
   RANCH POWER — Mobile : Add Customer (New Account) flow
   ============================================================ */

const CUST_TYPES = window.RP_CUSTOMER_TYPES || ['Retailer', 'Consumer', 'Convenience', 'Grocery', 'Gym', 'Cafe', 'Tourism / Recreation', 'Food Truck', 'Events', 'Catering', 'Distributor', 'Direct / Other'];

function mobileIsDirectConsumerType(type, customer) {
  if (typeof rpIsConsumerCustomer === 'function') return rpIsConsumerCustomer({ ...(customer || {}), type });
  return /consumer|direct|^shopify$/i.test(String(type || '').trim());
}

function mobilePlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function mobilePstNumber(customer) {
  if (typeof rpCustomerPstNumber === 'function') return rpCustomerPstNumber(customer);
  const overrides = mobilePlainObject(customer && customer.invoiceOverrides);
  return String(overrides.pstNumber || overrides.pst_number || '').trim();
}

function mobilePstExempt(customer) {
  const exemptions = mobilePlainObject(customer && customer.taxExemptions);
  if (typeof rpExemptionEnabled === 'function' && rpExemptionEnabled(exemptions, 'PST')) return true;
  return exemptions.PST === true || exemptions.pst === true || !!mobilePstNumber(customer);
}

function mobilePrimaryContactDraft(contacts) {
  const rows = Array.isArray(contacts) ? contacts : [];
  return rows.find(contact => contact && contact.primary) || rows[0] || {};
}

function MobileCustomerSupplyControl({ channel, distributorName, onChange, disabled = false }) {
  return <div style={{ marginBottom: 18 }}>
    <Lbl>Supplied by</Lbl>
    <select className="field" aria-label="Supplied by" value={channel} disabled={disabled} onChange={e => onChange(e.target.value, distributorName)}>
      <option value="direct">Ranch Power directly</option>
      <option value="distributor">A distributor</option>
    </select>
    {channel === 'distributor' && <>
      <div style={{ marginTop: 10 }}><Lbl>Distributor name (optional)</Lbl><input className="field" value={distributorName} disabled={disabled} onChange={e => onChange(channel, e.target.value)} placeholder="e.g. Slopeside Supply" /></div>
      <div style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.45, marginTop: 9 }}>Save the business and location for your customer directory and Find Us. Contact details are optional.</div>
    </>}
  </div>;
}

function MobileCustomerPoPolicy({ value, onChange, disabled = false }) {
  return <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 13, border: '1px solid var(--line)', borderRadius: 10, marginBottom: 18 }}>
    <input type="checkbox" checked={value} disabled={disabled} onChange={e => onChange(e.target.checked)} />
    <span><strong>Requires PO</strong><span style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--muted)' }}>Invoices require the customer's purchase order number.</span></span>
  </label>;
}

function MobileCustomerPstPolicy({ exempt, number, onExemptChange, onNumberChange }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 13, marginBottom: 18, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
        <span style={{ width: 32, height: 32, borderRadius: 10, background: exempt ? 'var(--amber-soft)' : 'var(--surface-2)', color: exempt ? 'var(--amber-deep)' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="doc" size={16} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>PST</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>{exempt ? 'PST will not be added to orders.' : 'PST will be added where applicable.'}</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8, marginBottom: exempt ? 12 : 0 }}>
        <button type="button" onClick={() => onExemptChange(false)} style={{ border: '1.5px solid ' + (!exempt ? 'var(--amber)' : 'var(--line)'), background: !exempt ? 'var(--amber-soft)' : 'var(--surface-2)', color: !exempt ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 10, padding: '10px 8px', fontSize: 12.5, fontWeight: 800 }}>Charge PST</button>
        <button type="button" onClick={() => onExemptChange(true)} style={{ border: '1.5px solid ' + (exempt ? 'var(--amber)' : 'var(--line)'), background: exempt ? 'var(--amber-soft)' : 'var(--surface-2)', color: exempt ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 10, padding: '10px 8px', fontSize: 12.5, fontWeight: 800 }}>Do not charge</button>
      </div>
      {exempt && <>
        <Lbl>PST Number <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(if known)</span></Lbl>
        <input className="field" placeholder="e.g. PST-1234-5678" value={number || ''} onChange={e => onNumberChange(e.target.value)} style={{ marginBottom: number ? 0 : 10 }} />
        {!number && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35 }}>PST number still needs to be captured for this account.</div>}
      </>}
    </div>
  );
}

function MobileCustomerDeliveryControl({ value, onChange, hasAddress }) {
  const method = typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(value) : (value || 'Ranch Delivery');
  const needsAddress = typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(method) : method !== 'Customer Pickup';
  const options = window.RP_DELIVERY_METHOD_OPTIONS || ['Ranch Delivery', 'Customer Pickup', 'Carrier / Freight'];
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 13, marginBottom: 18, boxShadow: 'var(--shadow-sm)' }}>
      <Lbl>Default Delivery Method</Lbl>
      <select className="field" value={method} onChange={e => onChange(e.target.value)} style={{ marginBottom: needsAddress && !hasAddress ? 10 : 0 }}>
        {options.map(option => <option key={option} value={option}>{option}</option>)}
      </select>
      {needsAddress && !hasAddress && <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 9, padding: '9px 11px', fontSize: 11.5, fontWeight: 700, lineHeight: 1.35 }}>Delivery orders need a ship-to address. Choose Customer Pickup if this account should be allowed to order without one.</div>}
      {!needsAddress && <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 7, lineHeight: 1.35 }}>Pickup accounts can be created and ordered without a ship-to address.</div>}
    </div>
  );
}

function MobileCustomerContactsEditor({ contacts, onChange, tried, consumer }) {
  const rows = contacts && contacts.length ? contacts : [rpBlankCustomerContact(true)];
  const primary = rows.find(contact => contact && contact.primary) || rows[0] || {};
  const setContact = (index, key, value) => {
    onChange(rows.map((contact, i) => {
      if (key === 'primary') return { ...contact, primary: i === index };
      return i === index ? { ...contact, [key]: value } : contact;
    }));
  };
  const removeContact = index => {
    const next = rows.filter((_, i) => i !== index);
    if (!next.length) onChange([rpBlankCustomerContact(true)]);
    else if (!next.some(contact => contact.primary)) onChange(next.map((contact, i) => ({ ...contact, primary: i === 0 })));
    else onChange(next);
  };
  const addContact = () => onChange([...rows, rpBlankCustomerContact(false)]);
  const copyPrimaryToContact = index => {
    onChange(rows.map((contact, i) => i === index ? {
      ...contact,
      email: primary.email || '',
      name: primary.name || '',
      phone: primary.phone || ''
    } : contact));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
      {rows.map((contact, index) => {
        const badEmail = tried && rpContactEmailInvalid(contact);
        return (
          <div key={index} style={{ background: 'var(--surface)', border: '1px solid ' + (badEmail ? 'var(--danger)' : contact.primary ? 'var(--navy)' : 'var(--line)'), borderRadius: 12, padding: 13, boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
              <span style={{ width: 32, height: 32, borderRadius: 10, background: contact.primary ? 'var(--navy)' : 'var(--surface-2)', color: contact.primary ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="user" size={16} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{consumer && contact.primary ? 'Customer Contact' : contact.primary ? 'Primary Buyer' : `Contact ${index + 1}`}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>{consumer && contact.primary ? 'Required for consumer orders' : contact.primary ? 'Buyer, orders, account setup' : 'Backup, billing, owner, receiver'}</div>
              </div>
              {!contact.primary && <button type="button" className="btn btn-sm" onClick={() => copyPrimaryToContact(index)} style={{ flexShrink: 0 }}>Same</button>}
              {!contact.primary && <button type="button" className="btn btn-sm" onClick={() => setContact(index, 'primary', true)} style={{ flexShrink: 0 }}>Primary</button>}
              {rows.length > 1 && <button type="button" className="btn btn-sm btn-ghost" aria-label="Remove contact" onClick={() => removeContact(index)} style={{ padding: 8, flexShrink: 0 }}><Icon name="x" size={15} /></button>}
            </div>
            <Lbl>Name</Lbl>
            <input className="field" placeholder="Contact name" value={contact.name || ''} onChange={e => setContact(index, 'name', e.target.value)} style={{ marginBottom: 10 }} />
            <Lbl>Role</Lbl>
            <input className="field" placeholder={contact.primary ? 'Buyer' : 'Billing, Owner...'} value={contact.role || ''} onChange={e => setContact(index, 'role', e.target.value)} style={{ marginBottom: 10 }} />
            <Lbl>{consumer && contact.primary ? 'Email *' : contact.primary ? 'Buyer Email' : 'Email'}</Lbl>
            <input className="field" type="email" placeholder="name@example.com" value={contact.email || ''} onChange={e => setContact(index, 'email', e.target.value)} style={{ marginBottom: badEmail ? 5 : 10, borderColor: badEmail ? 'var(--danger)' : undefined }} />
            {badEmail && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 700, marginBottom: 10 }}>Enter a valid email</div>}
            <Lbl>{consumer && contact.primary ? 'Phone *' : contact.primary ? 'Primary Phone' : 'Phone'}</Lbl>
            <input className="field" type="tel" placeholder="(604) 555-0100" value={contact.phone || ''} onChange={e => setContact(index, 'phone', e.target.value)} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12 }}><input type="checkbox" checked={contact.receivesInvoices === true} onChange={e => setContact(index, 'receivesInvoices', e.target.checked)} />Receives invoice and receipt copies</label>
            <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 5 }}>In addition to the primary invoice address or billing override.</div>
          </div>
        );
      })}
      <button type="button" className="btn" onClick={addContact} style={{ width: '100%', justifyContent: 'center' }}><Icon name="plus" size={16} />Add Contact</button>
    </div>
  );
}

MOBILE_SCREENS['add-customer'] = function AddCustomer({ nav, role }) {
  const repName = rpFieldUserName(role);
  const repTerr = rpFieldTerritory(role);
  const authRole = rpNormalizeRole(rpAuthProfile().role || role);
  const territoryLocked = ['rep', 'partner'].includes(authRole);
  const territoryOptions = territoryLocked ? [repTerr] : RP.TERRITORIES;
  const accountDefaultTier = rpRetailAccountDefaultPriceListName();
  const canSetCustomAccountPricing = rpCanSetCustomAccountPricing();
  const canManageCustomerAccountPricing = rpCanManageCustomerAccountPricing();
  const canManageInvoicePolicy = ['admin', 'leadership', 'ops', 'sales_manager'].includes(authRole);
  const [f, setF] = useState({
    name: '', door: '', type: 'Retailer', territory: repTerr, address: '', city: '', postal: '',
    tier: accountDefaultTier, terms: rpPriceListTerms(accountDefaultTier, 'Prepaid'), customCasePrice: '', defaultDeliveryMethod: 'Ranch Delivery', note: '', lat: '', lng: '', coordinateVerification: null,
    supplyChannel: 'direct', distributorName: '', requiresPo: false, pub: true,
  });
  const [contacts, setContacts] = useState([rpBlankCustomerContact(true)]);
  const [approveNow, setApproveNow] = useState(false);
  const [pstExempt, setPstExempt] = useState(false);
  const [pstNumber, setPstNumber] = useState('');
  const set = (k, v) => setF(s => ({ ...s, [k]: v, ...(['address', 'city', 'postal'].includes(k) && s[k] !== v ? { lat: '', lng: '', coordinateVerification: null } : {}) }));
  const [tried, setTried] = useState(false);
  const [result, setResult] = useState(null); // 'submit' | 'draft'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const consumerAccount = mobileIsDirectConsumerType(f.type);
  const distributorAccount = !consumerAccount && f.supplyChannel === 'distributor';
  const verifiedLocation = Boolean(f.coordinateVerification && !rpCoordinatePairIncomplete(f.lat, f.lng) && String(f.lat).trim() && String(f.lng).trim() && !rpLatitudeInvalid(f.lat) && !rpLongitudeInvalid(f.lng));
  const defaultDeliveryMethod = typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(f.defaultDeliveryMethod) : f.defaultDeliveryMethod;
  const deliveryNeedsAddress = typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(defaultDeliveryMethod) : defaultDeliveryMethod !== 'Customer Pickup';
  const canApproveRetailOnCreate = !consumerAccount && !distributorAccount && rpCanApproveCustomers();
  const primaryDraft = mobilePrimaryContactDraft(contacts);
  const primaryEmail = String(primaryDraft.email || '').trim().toLowerCase();
  const primaryPhone = String(primaryDraft.phone || '').trim();
  const primaryEmailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(primaryEmail);
  const customCasePriceText = String(f.customCasePrice || '').trim();
  const customCasePriceValue = customCasePriceText ? Number(customCasePriceText) : null;
  const customCasePriceInvalid = customCasePriceValue !== null && (!Number.isFinite(customCasePriceValue) || customCasePriceValue < 0.01 || customCasePriceValue > 99999999.99);
  const selectedMasterPrice = rpPriceListByName(f.tier);
  const duplicateCandidates = rpCustomerDuplicateCandidates({
    address: f.address,
    city: f.city || (f.territory + ', BC'),
    email: primaryEmail,
    name: f.name,
    phone: primaryPhone,
    postal: f.postal,
    territory: f.territory,
    type: f.type
  });
  const errs = {
    name: !f.name.trim(),
    address: (distributorAccount || deliveryNeedsAddress) && !f.address.trim(),
    city: distributorAccount && !f.city.trim(),
    coordinates: rpCoordinatePairIncomplete(f.lat, f.lng) || rpLatitudeInvalid(f.lat) || rpLongitudeInvalid(f.lng),
    email: consumerAccount && !primaryEmailValid,
    phone: consumerAccount && !primaryPhone,
    customPrice: !distributorAccount && canManageCustomerAccountPricing && customCasePriceInvalid,
  };
  const hasErr = Object.values(errs).some(Boolean);
  const setCustomerType = type => setF(s => ({
    ...s,
    type,
    terms: mobileIsDirectConsumerType(type) ? 'Prepaid' : s.terms,
    tier: mobileIsDirectConsumerType(type)
      ? 'Consumer'
      : (s.tier === 'Consumer' || !canSetCustomAccountPricing ? accountDefaultTier : s.tier)
  }));
  const setPstPolicy = value => {
    setPstExempt(value);
    if (!value) setPstNumber('');
  };

  const save = async (mode) => {
    setTried(true);
    setError('');
    if (busy || errs.customPrice || errs.coordinates || ((mode === 'submit' || distributorAccount) && hasErr)) {
      if (errs.customPrice) setError('Enter a custom 12-pack price greater than zero, or leave it blank.');
      if (errs.coordinates) setError('Enter a complete valid coordinate pair, or clear the pin.');
      return;
    }
    if (mode === 'submit' && !f.name.trim()) return;
    if (rpCustomerContactsHaveInvalidEmail(contacts)) {
      setError('Enter valid email addresses for every contact or leave them blank.');
      return;
    }
    setBusy(true);
    const cleanedContacts = rpCleanCustomerContactsDrafts(contacts);
    const primaryContact = cleanedContacts.find(contact => contact.primary) || cleanedContacts[0] || {};
    const accountLocation = { name: f.door.trim() || f.name.trim() || 'Location', type: distributorAccount ? 'Storefront' : defaultDeliveryMethod === 'Customer Pickup' ? 'Pickup' : 'Ship-to', address: f.address.trim(), city: f.city || (f.territory + ', BC'), postal: f.postal.trim(), phone: primaryContact.phone || '', contacts: cleanedContacts, lat: rpCoordinateInput(f.lat, null), lng: rpCoordinateInput(f.lng, null), coordinateVerification: f.coordinateVerification };
    const status = consumerAccount && mode !== 'draft' ? 'Approved' : mode === 'draft' ? 'Draft' : canApproveRetailOnCreate && approveNow ? 'Approved' : 'Prospect';
    const acceptedPaymentMethods = { account_terms: true, bank_transfer: true, e_transfer: true, cheque: true, cash: true, credit_card: false };
    const cleanPstNumber = String(pstNumber || '').trim();
    const taxExemptions = pstExempt ? { PST: true } : {};
    const invoiceOverrides = cleanPstNumber ? { pstNumber: cleanPstNumber } : {};
    const local = {
      name: f.name.trim() || 'Draft Account',
      door: f.door.trim(),
      type: f.type, territory: f.territory, city: f.city || (f.territory + ', BC'),
      tier: f.tier, rep: repName, contact: primaryContact.name || '', buyer: primaryContact.name || '—',
      phone: primaryContact.phone || '', email: primaryContact.email || '', contacts: cleanedContacts, status,
      risk: status === 'Approved' ? 'Low' : mode === 'draft' ? 'Draft' : 'Prospect',
      credit: status === 'Approved' ? 'Good' : 'New',
      lat: rpCoordinateInput(f.lat, null),
      lng: rpCoordinateInput(f.lng, null),
      coordinateVerification: f.coordinateVerification,
      supplyChannel: distributorAccount ? 'distributor' : 'direct',
      distributorName: distributorAccount ? f.distributorName.trim() : '',
      requiresPo: canManageInvoicePolicy && f.requiresPo,
      public: distributorAccount && f.pub && verifiedLocation && mode !== 'draft',
      address: f.address.trim(), postal: f.postal.trim(), locations: [accountLocation],
      defaultDeliveryMethod,
      onboardingQuestionnaire: { defaultDeliveryMethod },
      acceptedPaymentMethods,
      customCasePrice: canManageCustomerAccountPricing ? customCasePriceValue : null,
      terms: consumerAccount ? 'Prepaid' : f.terms,
      invoiceOverrides,
      taxExemptions,
      taxRegion: 'BC',
      skipCreds: true,
      note: distributorAccount ? f.note.trim() : f.note || (mode === 'draft' ? 'Saved as draft — finish setup before ordering.' : 'New prospect — pending sales manager approval for credit terms.'),
    };
    try {
      let serverCustomer = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/create', {
          method: 'POST',
          body: JSON.stringify({
            address: f.address.trim(),
            ...(!distributorAccount ? { acceptedPaymentMethods } : {}),
            buyerEmail: primaryContact.email || '',
            buyerName: primaryContact.name || '',
            city: f.city || (f.territory + ', BC'),
            contacts: cleanedContacts,
            ...(!distributorAccount ? { customCasePrice: canManageCustomerAccountPricing ? customCasePriceValue : null } : {}),
            customerType: f.type,
            supplyChannel: local.supplyChannel,
            distributorName: local.distributorName,
            ...(canManageInvoicePolicy ? { requiresPo: f.requiresPo } : {}),
            publicLocator: local.public,
            coordinateVerification: f.coordinateVerification,
            defaultDeliveryMethod,
            displayName: local.name,
            internalNote: f.note.trim(),
            lat: local.lat,
            locations: [accountLocation],
            lng: local.lng,
            notes: f.note.trim(),
            phone: primaryContact.phone || '',
            postal: f.postal.trim(),
            status,
            ...(!distributorAccount ? { invoiceOverrides, taxExemptions, taxRegion: 'BC', terms: consumerAccount ? 'Prepaid' : f.terms } : {}),
            territory: f.territory,
            ...(!distributorAccount ? { tier: consumerAccount && f.tier === 'Default Retail' ? 'Consumer' : f.tier } : {})
          })
        });
        serverCustomer = response.customer;
      }
      const c = serverCustomer
        ? RP.upsertCustomer({ ...local, ...serverCustomer, skipCreds: true, persisted: true })
        : RP.addCustomer(local);
      if (serverCustomer) await rpRefreshAfterMutation(`${c.name} added`);
      setResult({ mode, id: c.id, name: c.name, status: c.status || status, creds: null, distributor: distributorAccount, public: c.public });
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not create customer.');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const draft = result.mode === 'draft';
    return (
      <div className="rp-fade" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30, textAlign: 'center', gap: 16 }}>
        <div style={{ width: 74, height: 74, borderRadius: '50%', background: draft ? 'var(--surface-2)' : 'var(--ok-bg)', color: draft ? 'var(--muted)' : 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'rp-pop .4s ease' }}><Icon name={draft ? 'doc' : 'check'} size={38} /></div>
        <div>
          <div className="disp" style={{ fontSize: 25, textTransform: 'uppercase' }}>{draft ? 'Draft Saved' : 'Customer Added'}</div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 5 }}>{result.name} · {result.id}</div>
        </div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 11, padding: '13px 16px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>
          {result.distributor ? result.public ? 'Saved to your customer directory and listed on Find Us.' : 'Saved to your customer directory. Confirm the store location to publish it on Find Us.' : draft ? 'Saved to your accounts as a draft. Finish the contact before you can order.' : result.status === 'Approved' ? 'Account is approved and ready for orders.' : 'Created as a Prospect. Request approval before orders or portal access are enabled.'}
        </div>
        {!draft && result.creds && (
          <div style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, textAlign: 'left', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}><Icon name="mail" size={16} style={{ color: 'var(--ok)' }} /><span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>Portal login emailed to buyer</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 11.5, color: 'var(--faint)', fontWeight: 600 }}>USERNAME</span><span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{result.creds.username}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 11.5, color: 'var(--faint)', fontWeight: 600 }}>TEMP PASSWORD</span><span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{result.creds.password}</span></div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button className="btn btn-lg" style={{ flex: 1 }} onClick={() => nav.reset('rep-customers')}>View List</button>
          {!draft && <button className="btn btn-lg btn-primary" style={{ flex: 1 }} onClick={() => nav.reset('rep-customer', { id: result.id })}>Open Profile</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MHeader title="New Customer" sub={consumerAccount ? 'Fast consumer account' : 'Create a door in your territory'} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>{consumerAccount ? 'Consumer' : 'Business'}</div>
        <Lbl>{consumerAccount ? 'Customer Name' : 'Business Name'} <span style={{ color: 'var(--danger)' }}>*</span></Lbl>
        <input className="field" placeholder={consumerAccount ? 'e.g. Alex Morgan' : 'e.g. Cliffside Market'} value={f.name} onChange={e => set('name', e.target.value)} style={{ marginBottom: tried && errs.name ? 6 : 16, borderColor: tried && errs.name ? 'var(--danger)' : undefined }} />
        {tried && errs.name && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginBottom: 14 }}>{consumerAccount ? 'Customer name is required' : 'Business name is required'}</div>}
        <CustomerDuplicateWarning candidates={duplicateCandidates} />
        {!consumerAccount && <MobileCustomerSupplyControl channel={f.supplyChannel} distributorName={f.distributorName} onChange={(supplyChannel, distributorName) => setF(current => ({ ...current, supplyChannel, distributorName, pub: supplyChannel === 'distributor' && current.supplyChannel !== supplyChannel ? true : current.pub, type: supplyChannel === 'distributor' && !rpPublicLocatorEligibleType(current.type) ? 'Retailer' : current.type }))} />}

        {!consumerAccount && !distributorAccount && <>
          <Lbl>Store / Door Name <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, marginBottom: 16 }}>
            <input className="field" placeholder="If different from business" value={f.door} onChange={e => set('door', e.target.value)} />
            <button type="button" className="btn" onClick={() => set('door', f.name)} style={{ whiteSpace: 'nowrap' }}>Same</button>
          </div>
        </>}

        <Lbl>Customer Type <span style={{ color: 'var(--danger)' }}>*</span></Lbl>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 16 }}>
          {CUST_TYPES.filter(t => !distributorAccount || rpPublicLocatorEligibleType(t)).map(t => (
            <button key={t} onClick={() => setCustomerType(t)} style={{ padding: '8px 13px', borderRadius: 100, border: '1.5px solid ' + (f.type === t ? 'var(--amber)' : 'var(--line)'), background: f.type === t ? 'var(--amber-soft)' : 'var(--surface)', fontSize: 12.5, fontWeight: 600, color: f.type === t ? 'var(--amber-deep)' : 'var(--ink-soft)' }}>{t}</button>
          ))}
        </div>

        {!distributorAccount && <MobileCustomerDeliveryControl value={f.defaultDeliveryMethod} onChange={value => set('defaultDeliveryMethod', value)} hasAddress={!!f.address.trim()} />}

        <Lbl>Territory</Lbl>
        <select className="field" value={f.territory} disabled={territoryLocked} onChange={e => set('territory', e.target.value)} style={{ marginBottom: territoryLocked ? 6 : 20, opacity: territoryLocked ? 0.75 : 1 }}>
          {territoryOptions.map(t => <option key={t}>{t}</option>)}
        </select>
        {territoryLocked && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 20 }}>Your account is limited to this territory.</div>}

        <div className="eyebrow" style={{ marginBottom: 10 }}>Location</div>
        <Lbl>Address {(distributorAccount || deliveryNeedsAddress) && <span style={{ color: 'var(--danger)' }}>*</span>}{!distributorAccount && !deliveryNeedsAddress && <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span>}</Lbl>
        <input className="field" placeholder="Street address" value={f.address} onChange={e => set('address', e.target.value)} style={{ marginBottom: tried && errs.address ? 6 : 10, borderColor: tried && errs.address ? 'var(--danger)' : undefined }} />
        {tried && errs.address && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 600, marginBottom: 10 }}>{distributorAccount ? 'Enter the store street address.' : `Address is required for ${defaultDeliveryMethod} orders`}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.8fr)', gap: 10, marginBottom: 10 }}>
          <div>
            <Lbl>City {distributorAccount && <span style={{ color: 'var(--danger)' }}>*</span>}</Lbl>
            <input className="field" placeholder={f.territory + ', BC'} value={f.city} onChange={e => set('city', e.target.value)} />
            {tried && errs.city && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Enter the store city.</div>}
          </div>
          <div>
            <Lbl>Postal</Lbl>
            <input className="field" placeholder="V8B 0A1" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} />
          </div>
        </div>
        <CoordinatePicker label="Store location" lat={f.lat} lng={f.lng} address={f.address} city={f.city} postal={f.postal} verification={f.coordinateVerification} onChange={(lat, lng, coordinateVerification) => setF(current => ({ ...current, lat, lng, coordinateVerification, pub: coordinateVerification && current.supplyChannel === 'distributor' ? true : current.pub }))} />
        {distributorAccount && <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'flex', gap: 10 }}><input type="checkbox" checked={f.pub} onChange={e => set('pub', e.target.checked)} />List on Find Us when the location is confirmed</label>
          {f.pub && !verifiedLocation && <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 12 }}>You can save this record now. Confirm the pin before publishing.</div>}
        </div>}

        <details open={!distributorAccount} style={{ marginBottom: 16 }}><summary style={{ fontWeight: 700, marginBottom: 10 }}>Contacts {distributorAccount ? '(optional)' : ''}</summary>
        <MobileCustomerContactsEditor contacts={contacts} onChange={setContacts} tried={tried} consumer={consumerAccount} />
        </details>
        {consumerAccount && tried && (errs.email || errs.phone) && <div style={{ marginTop: -8, marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12, fontWeight: 700 }}>Consumer accounts require a valid email and phone number.</div>}

        {!distributorAccount && <>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Terms</div>
        <Lbl>Price List</Lbl>
        <select className="field" aria-label="Master Price List" value={f.tier} disabled={consumerAccount || (!canManageCustomerAccountPricing && !canSetCustomAccountPricing)} onChange={e => { const tier = e.target.value; setF(current => ({ ...current, tier, terms: rpPriceListTerms(tier, current.terms) })); }} style={{ marginBottom: 16 }}>
          {(consumerAccount ? ['Consumer'] : canManageCustomerAccountPricing ? rpExistingCustomerPriceListOptionNames({ currentName: f.tier, excludeConsumer: true }) : rpAccountPriceListOptionNames({ currentName: f.tier, excludeConsumer: true })).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}
        </select>
        {canManageCustomerAccountPricing && <>
          <Lbl>Custom 12-Pack Price <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
          <input className="field" aria-label="Custom 12-Pack Price" aria-invalid={tried && customCasePriceInvalid} type="number" min="0.01" max="99999999.99" step="0.01" inputMode="decimal" placeholder={selectedMasterPrice && selectedMasterPrice.skuPrice ? RP.money2(selectedMasterPrice.skuPrice) : 'Use master price'} value={f.customCasePrice} onChange={e => set('customCasePrice', e.target.value)} style={{ marginBottom: customCasePriceInvalid ? 6 : 16, borderColor: tried && customCasePriceInvalid ? 'var(--danger)' : undefined }} />
          {tried && customCasePriceInvalid && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 700, marginBottom: 14 }}>Enter a price greater than zero or leave it blank.</div>}
        </>}
        <Lbl>Payment Terms</Lbl>
        <select className="field" aria-label="Payment Terms" value={consumerAccount ? 'Prepaid' : f.terms} disabled={consumerAccount} onChange={e => set('terms', e.target.value)} style={{ marginBottom: 16 }}>
          {rpAccountPaymentTermOptionNames(f.terms).map(term => <option key={term}>{term}</option>)}
        </select>
        <MobileCustomerPstPolicy exempt={pstExempt} number={pstNumber} onExemptChange={setPstPolicy} onNumberChange={setPstNumber} />
        <MobileCustomerPoPolicy value={f.requiresPo} onChange={value => set('requiresPo', value)} disabled={!canManageInvoicePolicy} />
        </>}
        {canApproveRetailOnCreate && (
          <button type="button" onClick={() => setApproveNow(value => !value)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, border: '1.5px solid ' + (approveNow ? 'var(--amber)' : 'var(--line)'), background: approveNow ? 'var(--amber-soft)' : 'var(--surface)', color: approveNow ? 'var(--amber-deep)' : 'var(--ink)', borderRadius: 11, padding: '12px 13px', textAlign: 'left', marginBottom: 16 }}>
            <span style={{ width: 22, height: 22, borderRadius: 7, border: '2px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{approveNow && <Icon name="check" size={13} />}</span>
            <span style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 13.5, fontWeight: 800 }}>Approve for ordering now</span><span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Use for retail customers you have fully vetted.</span></span>
          </button>
        )}
        <Lbl>Notes <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
        <textarea className="field" rows="2" placeholder="Delivery prefs, opportunity size…" value={f.note} onChange={e => set('note', e.target.value)} style={{ resize: 'none', marginBottom: 16 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '11px 13px', borderRadius: 10, fontSize: 12, fontWeight: 500, lineHeight: 1.4 }}>
          <Icon name="user" size={16} style={{ flexShrink: 0 }} />{distributorAccount ? 'This store is supplied through a distributor and saved for your directory and Find Us.' : consumerAccount ? 'Consumer accounts are prepaid and ready for orders immediately.' : canApproveRetailOnCreate ? 'You can approve vetted retail accounts now, or save them as prospects for review.' : 'New accounts start as a Prospect. Credit terms need sales-manager approval.'}
        </div>
        {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12, fontWeight: 700 }}>{error}</div>}
      </div>
      <div style={{ flexShrink: 0, padding: '12px 16px', borderTop: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', gap: 10 }}>
        <button className="btn btn-lg" disabled={busy} style={{ flex: 1 }} onClick={() => save('draft')}>{busy ? 'Saving...' : 'Save Draft'}</button>
        <button className="btn btn-lg btn-primary" disabled={busy} style={{ flex: 1.4, opacity: hasErr || busy ? 0.5 : 1 }} onClick={() => save('submit')}><Icon name="check" size={17} />{busy ? 'Adding...' : 'Add Customer'}</button>
      </div>
    </div>
  );
};

MOBILE_SCREENS['edit-customer'] = function EditCustomer({ nav, id }) {
  const exactCustomer = useRpExactCustomer({ customerId: id });
  if (exactCustomer.loading) return (
    <div className="rp-fade" data-exact-customer-loading="true">
      <MHeader title="Edit Customer" sub={`Loading ${id || ''}`} nav={nav} back />
      <MEmpty icon="refresh" title="Loading customer" sub="Fetching this account from the complete customer directory." />
    </div>
  );
  if (exactCustomer.error) return (
    <div className="rp-fade" data-exact-customer-error="true">
      <MHeader title="Edit Customer" sub="Customer unavailable" nav={nav} back />
      <MEmpty icon="alert" title="Customer could not load" sub={exactCustomer.error} action={<button type="button" className="btn btn-primary btn-sm" onClick={exactCustomer.retry}>Try Again</button>} />
    </div>
  );
  if (!exactCustomer.customer) return (
    <div className="rp-fade" data-exact-customer-missing="true">
      <MHeader title="Customer Missing" nav={nav} back />
      <MEmpty icon="customers" title="Customer not found" sub="This account does not exist or is outside your sales scope." />
    </div>
  );
  return <EditCustomerForm key={exactCustomer.customer.id} nav={nav} id={id} customer={exactCustomer.customer} />;
};

function EditCustomerForm({ nav, id, customer }) {
  const c = customer;
  const savedLocations = (Array.isArray(c.locations) ? c.locations : []).filter(location => location && location.active !== false);
  const primaryLocation = savedLocations.find(location => location.id === `${c.id}-MAIN`)
    || savedLocations.find(location => String(location.type || '').toLowerCase() !== 'billing') || savedLocations[0] || c;
  const primaryShipping = mobilePlainObject(primaryLocation.shippingAddress);
  const initialLocation = {
    address: primaryLocation.address ?? primaryShipping.address ?? primaryShipping.address1 ?? c.address ?? '',
    city: primaryLocation.city ?? primaryShipping.city ?? c.city ?? '',
    postal: primaryLocation.postal ?? primaryShipping.postal ?? c.postal ?? '',
    lat: rpCoordinateDraft(primaryLocation.lat),
    lng: rpCoordinateDraft(primaryLocation.lng),
    coordinateVerification: primaryLocation.coordinateVerification || null
  };
  const canEdit = c && (typeof rpCanEditCustomerProfile === 'function' ? rpCanEditCustomerProfile(c) : rpCustomerInProfileScope(rpAuthProfile(), c));
  const canManageCustomerAccountPricing = rpCanManageCustomerAccountPricing();
  const canManageInvoicePolicy = ['admin', 'leadership', 'ops', 'sales_manager'].includes(rpNormalizeRole(rpAuthProfile().role));
  const accountDefaultTier = rpRetailAccountDefaultPriceListName();
  const [f, setF] = useState(() => ({
    address: initialLocation.address,
    city: initialLocation.city,
    customCasePrice: c && c.customCasePrice != null ? String(c.customCasePrice) : '',
    defaultDeliveryMethod: c && typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(c) : 'Ranch Delivery',
    name: c && c.name || '',
    note: c && c.note || '',
    postal: initialLocation.postal,
    terms: c && c.terms || rpPriceListTerms(c && c.tier || accountDefaultTier, 'Prepaid'),
    tier: c && c.tier || accountDefaultTier,
    type: c && c.type || 'Retailer',
    supplyChannel: c.supplyChannel === 'distributor' ? 'distributor' : 'direct',
    distributorName: c.distributorName || '',
    requiresPo: c.requiresPo === true,
    lat: initialLocation.lat,
    lng: initialLocation.lng,
    coordinateVerification: initialLocation.coordinateVerification,
    pub: !!c.public
  }));
  const [contacts, setContacts] = useState(() => {
    const fallback = { email: c && c.email || '', name: c && c.buyer === 'Buyer' ? '' : (c && c.buyer || ''), phone: c ? rpPrimaryCustomerPhone(c) : '', role: 'Buyer' };
    const existing = rpCustomerContacts(c);
    const drafts = existing.length
      ? existing.map((contact, index) => rpContactDraftFrom(contact, index === 0 ? fallback : {}, Boolean(contact.primary || index === 0)))
      : [rpContactDraftFrom({}, fallback, true)];
    if (drafts.length && !drafts.some(contact => contact.primary)) drafts[0].primary = true;
    return drafts.length ? drafts : [rpBlankCustomerContact(true)];
  });
  const [pstExempt, setPstExempt] = useState(() => mobilePstExempt(c));
  const [pstNumber, setPstNumber] = useState(() => mobilePstNumber(c));
  const set = (key, value) => setF(current => ({ ...current, [key]: value, ...(['address', 'city', 'postal'].includes(key) && current[key] !== value ? { lat: '', lng: '', coordinateVerification: null } : {}) }));
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const consumerAccount = mobileIsDirectConsumerType(f.type || c.type || c.tier, c);
  const distributorAccount = !consumerAccount && f.supplyChannel === 'distributor';
  const verifiedLocation = Boolean(f.coordinateVerification && !rpCoordinatePairIncomplete(f.lat, f.lng) && String(f.lat).trim() && String(f.lng).trim() && !rpLatitudeInvalid(f.lat) && !rpLongitudeInvalid(f.lng));
  const defaultDeliveryMethod = typeof rpNormalizeDeliveryMethod === 'function' ? rpNormalizeDeliveryMethod(f.defaultDeliveryMethod) : f.defaultDeliveryMethod;
  const deliveryNeedsAddress = typeof rpCustomerDeliveryNeedsAddress === 'function' ? rpCustomerDeliveryNeedsAddress(defaultDeliveryMethod) : defaultDeliveryMethod !== 'Customer Pickup';
  const primaryDraft = mobilePrimaryContactDraft(contacts);
  const primaryEmail = String(primaryDraft.email || '').trim().toLowerCase();
  const primaryPhone = String(primaryDraft.phone || '').trim();
  const primaryEmailValid = !primaryEmail || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(primaryEmail);
  const customCasePriceText = String(f.customCasePrice || '').trim();
  const customCasePriceValue = customCasePriceText ? Number(customCasePriceText) : null;
  const customCasePriceInvalid = customCasePriceValue !== null && (!Number.isFinite(customCasePriceValue) || customCasePriceValue < 0.01 || customCasePriceValue > 99999999.99);
  const selectedMasterPrice = rpPriceListByName(f.tier);
  const errs = {
    name: !f.name.trim(),
    address: (distributorAccount || deliveryNeedsAddress) && !f.address.trim(),
    city: distributorAccount && !f.city.trim(),
    coordinates: rpCoordinatePairIncomplete(f.lat, f.lng) || rpLatitudeInvalid(f.lat) || rpLongitudeInvalid(f.lng),
    email: consumerAccount && !primaryEmail,
    emailFormat: !!primaryEmail && !primaryEmailValid,
    phone: consumerAccount && !primaryPhone,
    customPrice: !distributorAccount && canManageCustomerAccountPricing && customCasePriceInvalid
  };
  const hasErr = Object.values(errs).some(Boolean) || !canEdit;
  const setPstPolicy = value => {
    setPstExempt(value);
    if (!value) setPstNumber('');
  };
  const setCustomerType = type => setF(current => {
    const nextConsumer = mobileIsDirectConsumerType(type);
    const nextTier = nextConsumer
      ? 'Consumer'
      : current.tier === 'Consumer' ? accountDefaultTier : current.tier;
    return {
      ...current,
      type,
      customCasePrice: current.customCasePrice,
      terms: nextConsumer
        ? 'Prepaid'
        : current.tier === 'Consumer' ? rpPriceListTerms(nextTier, current.terms) : current.terms,
      tier: nextTier
    };
  });

  const save = async () => {
    setTried(true);
    setError('');
    if (busy || hasErr) {
      if (!canEdit) setError('Your profile can only edit customers assigned to your scope.');
      else if (errs.coordinates) setError('Enter a complete valid coordinate pair, or clear the pin.');
      return;
    }
    if (rpCustomerContactsHaveInvalidEmail(contacts)) {
      setError('Enter valid email addresses for every contact or leave them blank.');
      return;
    }
    setBusy(true);
    const cleanedContacts = rpCleanCustomerContactsDrafts(contacts);
    const primaryContact = cleanedContacts.find(contact => contact.primary) || cleanedContacts[0] || {};
    const cleanPstNumber = String(pstNumber || '').trim();
    const taxExemptions = { ...mobilePlainObject(c.taxExemptions), PST: pstExempt };
    if (!pstExempt) delete taxExemptions.PST;
    const invoiceOverrides = { ...mobilePlainObject(c.invoiceOverrides), pstNumber: cleanPstNumber };
    if (!cleanPstNumber) delete invoiceOverrides.pstNumber;
    const locationChanged = ['address', 'city', 'postal', 'lat', 'lng', 'coordinateVerification']
      .some(key => JSON.stringify(f[key]) !== JSON.stringify(initialLocation[key]));
    const locationPatch = {
      address: f.address.trim(), city: f.city.trim(), postal: f.postal.trim(),
      lat: rpCoordinateInput(f.lat, null), lng: rpCoordinateInput(f.lng, null),
      coordinateVerification: f.coordinateVerification
    };
    const nextPrimaryLocation = {
      ...(primaryLocation === c ? {} : primaryLocation),
      ...locationPatch,
      id: primaryLocation === c ? `${c.id}-MAIN` : primaryLocation.id,
      name: primaryLocation === c ? f.name.trim() : primaryLocation.name,
      shippingAddress: { ...primaryShipping, address: locationPatch.address, address1: locationPatch.address, city: locationPatch.city, postal: locationPatch.postal, zip: locationPatch.postal },
      ...(primaryLocation === c ? { billingAddress: c.billingAddress, contacts: c.contacts, phoneNumbers: c.phoneNumbers, emailAddresses: c.emailAddresses, type: distributorAccount ? 'Storefront' : 'Ship-to' } : {})
    };
    const nextLocations = savedLocations.length
      ? savedLocations.map(location => location === primaryLocation ? nextPrimaryLocation : location)
      : [nextPrimaryLocation];
    const payload = {
      address: f.address.trim(),
      buyerEmail: primaryContact.email || '',
      buyerName: primaryContact.name || '',
      city: f.city.trim() || c.territory || '',
      contacts: cleanedContacts,
      customerType: f.type,
      supplyChannel: distributorAccount ? 'distributor' : 'direct',
      distributorName: distributorAccount ? f.distributorName.trim() : '',
      ...(canManageInvoicePolicy ? { requiresPo: f.requiresPo } : {}),
      ...(locationChanged ? { lat: locationPatch.lat, lng: locationPatch.lng, coordinateVerification: locationPatch.coordinateVerification, locations: nextLocations } : {}),
      ...(distributorAccount ? { publicLocator: f.pub && verifiedLocation } : {}),
      defaultDeliveryMethod,
      displayName: f.name.trim(),
      ...(c.legalName ? { legalName: c.legalName } : {}),
      id: c.id,
      internalNote: f.note.trim(),
      ...(distributorAccount ? {} : { invoiceOverrides }),
      phone: primaryContact.phone || '',
      postal: f.postal.trim(),
      ...(distributorAccount ? {} : { taxExemptions })
    };
    if (canManageCustomerAccountPricing) {
      if (!distributorAccount) Object.assign(payload, {
          customCasePrice: customCasePriceValue,
          terms: consumerAccount ? 'Prepaid' : f.terms,
          tier: consumerAccount ? 'Consumer' : f.tier
        });
    }
    try {
      let updated = null;
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        const response = await rpApi('/api/customers/update', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        updated = RP.upsertCustomer({ ...c, ...response.customer, skipCreds: true, persisted: true });
        await rpRefreshAfterMutation(`${updated.name} updated`);
      } else {
        updated = RP.upsertCustomer({
          ...c,
          address: payload.address,
          banner: payload.displayName,
          buyer: payload.buyerName || 'Buyer',
          city: payload.city,
          contacts: cleanedContacts,
          email: payload.buyerEmail,
          invoiceOverrides: distributorAccount ? c.invoiceOverrides : invoiceOverrides,
          name: payload.displayName,
          note: payload.internalNote,
          defaultDeliveryMethod,
          onboardingQuestionnaire: {
            ...mobilePlainObject(c.onboardingQuestionnaire),
            defaultDeliveryMethod
          },
          phone: payload.phone,
          postal: payload.postal,
          taxExemptions: distributorAccount ? c.taxExemptions : taxExemptions,
          type: payload.customerType,
          supplyChannel: payload.supplyChannel,
          distributorName: payload.distributorName,
          requiresPo: canManageInvoicePolicy ? f.requiresPo : c.requiresPo,
          ...(locationChanged ? { ...locationPatch, locations: nextLocations } : {}),
          ...(Object.prototype.hasOwnProperty.call(payload, 'publicLocator') ? { public: payload.publicLocator } : {}),
          ...(canManageCustomerAccountPricing && !distributorAccount ? {
            customCasePrice: payload.customCasePrice,
            terms: payload.terms,
            tier: payload.tier
          } : {})
        });
      }
      toast(`${updated.name} updated`, 'ok');
      nav.pop();
    } catch (err) {
      setError(err && err.message ? err.message : 'Could not update customer.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-fade" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <MHeader title="Edit Customer" sub={c.name} nav={nav} back />
      <div className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
        {!canEdit && <div style={{ marginBottom: 14, background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '11px 13px', fontSize: 12, fontWeight: 700 }}>This account is outside your assigned scope.</div>}
        <div className="eyebrow" style={{ marginBottom: 10 }}>Account</div>
        <Lbl>{consumerAccount ? 'Customer Name' : 'Business Name'} <span style={{ color: 'var(--danger)' }}>*</span></Lbl>
        <input className="field" value={f.name} onChange={e => set('name', e.target.value)} style={{ marginBottom: tried && errs.name ? 6 : 16, borderColor: tried && errs.name ? 'var(--danger)' : undefined }} />
        {tried && errs.name && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 700, marginBottom: 14 }}>Name is required</div>}
        {!consumerAccount && <MobileCustomerSupplyControl channel={f.supplyChannel} distributorName={f.distributorName} onChange={(supplyChannel, distributorName) => setF(current => ({ ...current, supplyChannel, distributorName, pub: supplyChannel === 'distributor' && current.supplyChannel !== supplyChannel ? true : current.pub, type: supplyChannel === 'distributor' && !rpPublicLocatorEligibleType(current.type) ? 'Retailer' : current.type }))} disabled={!canEdit} />}
        <Lbl>Customer Type</Lbl>
        <select className="field" value={f.type} onChange={e => setCustomerType(e.target.value)} style={{ marginBottom: 16 }}>
          {CUST_TYPES.filter(t => !distributorAccount || rpPublicLocatorEligibleType(t)).map(t => <option key={t}>{t}</option>)}
        </select>
        {!distributorAccount && <MobileCustomerDeliveryControl value={f.defaultDeliveryMethod} onChange={value => set('defaultDeliveryMethod', value)} hasAddress={!!f.address.trim()} />}

        {canManageCustomerAccountPricing && !distributorAccount && <>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Pricing</div>
          <Lbl>Master Price List</Lbl>
          <select className="field" aria-label="Master Price List" value={consumerAccount ? 'Consumer' : f.tier} disabled={consumerAccount} onChange={e => { const tier = e.target.value; setF(current => ({ ...current, tier, terms: rpPriceListTerms(tier, current.terms) })); }} style={{ marginBottom: 16 }}>
            {(consumerAccount ? ['Consumer'] : rpExistingCustomerPriceListOptionNames({ currentName: f.tier, excludeConsumer: true })).map(t => <option key={t} value={t}>{rpPriceListOptionLabel(t)}</option>)}
          </select>
          <Lbl>Custom 12-Pack Price <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span></Lbl>
          <input className="field" aria-label="Custom 12-Pack Price" aria-invalid={tried && customCasePriceInvalid} type="number" min="0.01" max="99999999.99" step="0.01" inputMode="decimal" placeholder={selectedMasterPrice && selectedMasterPrice.skuPrice ? RP.money2(selectedMasterPrice.skuPrice) : 'Use master price'} value={f.customCasePrice} onChange={e => set('customCasePrice', e.target.value)} style={{ marginBottom: customCasePriceInvalid ? 6 : 16, borderColor: tried && customCasePriceInvalid ? 'var(--danger)' : undefined }} />
          {tried && customCasePriceInvalid && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 700, marginBottom: 14 }}>Enter a price greater than zero or leave it blank.</div>}
          <Lbl>Payment Terms</Lbl>
          <select className="field" aria-label="Payment Terms" value={consumerAccount ? 'Prepaid' : f.terms} disabled={consumerAccount} onChange={e => set('terms', e.target.value)} style={{ marginBottom: 16 }}>
            {rpAccountPaymentTermOptionNames(f.terms).map(term => <option key={term}>{term}</option>)}
          </select>
        </>}

        <div className="eyebrow" style={{ marginBottom: 10 }}>Location</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.8fr)', gap: 10, marginBottom: 12 }}>
          <div>
            <Lbl>City {distributorAccount && <span style={{ color: 'var(--danger)' }}>*</span>}</Lbl>
            <input className="field" value={f.city} onChange={e => set('city', e.target.value)} placeholder={c.territory || 'City'} />
            {tried && errs.city && <div style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 5 }}>Enter the store city.</div>}
          </div>
          <div>
            <Lbl>Postal</Lbl>
            <input className="field" value={f.postal} onChange={e => set('postal', e.target.value.toUpperCase())} placeholder={c.postal || 'V8B 0A1'} />
          </div>
        </div>
        <Lbl>Address {(distributorAccount || deliveryNeedsAddress) && <span style={{ color: 'var(--danger)' }}>*</span>}</Lbl>
        <input className="field" value={f.address} onChange={e => set('address', e.target.value)} placeholder="Street address" style={{ marginBottom: tried && errs.address ? 6 : 18, borderColor: tried && errs.address ? 'var(--danger)' : undefined }} />
        {tried && errs.address && <div style={{ fontSize: 11.5, color: 'var(--danger)', fontWeight: 700, marginBottom: 14 }}>{distributorAccount ? 'Enter the store street address.' : `Address is required for ${defaultDeliveryMethod} orders`}</div>}
        <CoordinatePicker label="Store location" lat={f.lat} lng={f.lng} address={f.address} city={f.city} postal={f.postal} province={primaryLocation.province || primaryShipping.province || ''} country={primaryLocation.country || primaryShipping.country || ''} verification={f.coordinateVerification} onChange={(lat, lng, coordinateVerification) => setF(current => ({ ...current, lat, lng, coordinateVerification, pub: coordinateVerification && current.supplyChannel === 'distributor' ? true : current.pub }))} />
        {distributorAccount && <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'flex', gap: 10 }}><input type="checkbox" checked={f.pub} onChange={e => set('pub', e.target.checked)} />List on Find Us when the location is confirmed</label>
          {f.pub && !verifiedLocation && <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 12 }}>This record stays in your directory. Confirm the pin before publishing.</div>}
        </div>}

        <details open={!distributorAccount} style={{ marginBottom: 16 }}><summary style={{ fontWeight: 700, marginBottom: 10 }}>Contacts {distributorAccount ? '(optional)' : ''}</summary>
        <MobileCustomerContactsEditor contacts={contacts} onChange={setContacts} tried={tried} consumer={consumerAccount} />
        </details>
        {tried && (errs.email || errs.emailFormat || errs.phone) && <div style={{ marginTop: -8, marginBottom: 14, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12, fontWeight: 700 }}>{errs.emailFormat ? 'Enter a valid primary email address.' : 'Consumer accounts require a primary email and phone number.'}</div>}
        {!distributorAccount && <><div className="eyebrow" style={{ marginBottom: 10 }}>Invoice & Tax</div>
        <MobileCustomerPoPolicy value={f.requiresPo} onChange={value => set('requiresPo', value)} disabled={!canManageInvoicePolicy} />
        <MobileCustomerPstPolicy exempt={pstExempt} number={pstNumber} onExemptChange={setPstPolicy} onNumberChange={setPstNumber} />
        </>}
        <div className="eyebrow" style={{ marginBottom: 10 }}>Notes</div>
        <textarea className="field" rows="3" placeholder="Delivery prefs, meeting place, account reminders" value={f.note} onChange={e => set('note', e.target.value)} style={{ resize: 'none', marginBottom: 16 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--info-bg)', color: 'var(--info)', padding: '11px 13px', borderRadius: 10, fontSize: 12, fontWeight: 500, lineHeight: 1.4 }}>
          <Icon name="admin" size={16} style={{ flexShrink: 0 }} />{distributorAccount ? 'This store is supplied through a distributor and saved for your directory and Find Us.' : canManageCustomerAccountPricing ? 'Account pricing changes are saved with the customer and apply to future orders.' : 'Sales edits are limited to basic account, location, contact, notes, and PST details.'}
        </div>
        {error && <div style={{ marginTop: 12, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 10, padding: '11px 13px', fontSize: 12, fontWeight: 700 }}>{error}</div>}
      </div>
      <div style={{ flexShrink: 0, padding: '12px 16px', borderTop: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', gap: 10 }}>
        <button className="btn btn-lg" disabled={busy} style={{ flex: 1 }} onClick={() => nav.pop()}>Cancel</button>
        <button className="btn btn-lg btn-primary" disabled={busy || hasErr} style={{ flex: 1.3, opacity: busy || hasErr ? 0.55 : 1 }} onClick={save}><Icon name="check" size={17} />{busy ? 'Saving...' : 'Save Changes'}</button>
      </div>
    </div>
  );
}


/* ===== sales_desktop.jsx ===== */
/* ============================================================
   RANCH POWER - Sales desktop workspace
   Reuses the field-app workflows behind a desktop-only shell.
   The authenticated field profile and server-scoped bootstrap remain
   the authority for every row and action shown here.
   ============================================================ */

const SALES_LAYOUT_PREFERENCE_KEY = 'rp:sales-layout';
const SALES_DESKTOP_BREAKPOINT = 1040;

const SALES_DESKTOP_NAV = [
  { id: 'sales-overview', label: 'Overview', icon: 'home', group: 'Sales' },
  { id: 'rep-pipeline', label: 'Pipeline', icon: 'trend', group: 'Sales' },
  { id: 'rep-customers', label: 'Customers', icon: 'customers', group: 'Sales' },
  { id: 'rep-orders', label: 'Orders', icon: 'orders', group: 'Sales' },
  { id: 'order-exceptions', label: 'Exceptions', icon: 'alert', group: 'Sales' },
  { id: 'rep-inventory', label: 'My Inventory', icon: 'inventory', group: 'Field Work' },
  { id: 'stock-requests', label: 'Stock Requests', icon: 'layers', group: 'Field Work' },
  { id: 'rep-events', label: 'Events', icon: 'calendar', group: 'Field Work' },
  { id: 'sales-visits', label: 'Visit Log', icon: 'pin', group: 'Field Work' },
  { id: 'my-tasks', label: 'My Tasks', icon: 'tasks', group: 'My Work' },
  { id: 'my-goals', label: 'My Goals', icon: 'target', group: 'My Work' },
  { id: 'my-expenses', label: 'My Expenses', icon: 'receipt', group: 'My Work' },
  { id: 'support-ticket', label: 'Support', icon: 'mail', group: 'Resources' },
  { id: 'notifications', label: 'Notifications', icon: 'bell', group: 'Account' },
  { id: 'settings', label: 'My Account', icon: 'user', group: 'Account' },
];

const SALES_DESKTOP_ROOT_IDS = new Set(SALES_DESKTOP_NAV.map(item => item.id));
const SALES_DESKTOP_ALLOWED_ROLES = new Set(['rep']);

function salesLayoutQueryPreference() {
  if (typeof window === 'undefined') return '';
  const value = new URLSearchParams(window.location.search).get('layout');
  return value === 'desktop' || value === 'mobile' ? value : '';
}

function salesStoredLayoutPreference() {
  if (typeof window === 'undefined') return 'auto';
  try {
    const value = window.localStorage.getItem(SALES_LAYOUT_PREFERENCE_KEY);
    return value === 'desktop' || value === 'mobile' ? value : 'auto';
  } catch {
    return 'auto';
  }
}

function salesCurrentRole(fallbackRole) {
  const profile = typeof rpAuthProfile === 'function' ? rpAuthProfile() : {};
  return rpNormalizeRole(profile.role || fallbackRole || 'rep');
}

function salesDesktopEligible(role) {
  return SALES_DESKTOP_ALLOWED_ROLES.has(salesCurrentRole(role));
}

function SalesExperience({ role }) {
  const queryPreference = salesLayoutQueryPreference();
  const [preference, setPreference] = useState(() => queryPreference || salesStoredLayoutPreference());
  const [viewportWidth, setViewportWidth] = useState(() => typeof window === 'undefined' ? 390 : window.innerWidth);
  const activeRole = salesCurrentRole(role);

  useEffect(() => {
    const resize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', resize);
    resize();
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const setLayout = next => {
      const value = next === 'desktop' || next === 'mobile' ? next : 'auto';
      try {
        if (value === 'auto') window.localStorage.removeItem(SALES_LAYOUT_PREFERENCE_KEY);
        else window.localStorage.setItem(SALES_LAYOUT_PREFERENCE_KEY, value);
      } catch {
        // The current tab can still switch even when storage is unavailable.
      }
      setPreference(value);
    };
    window.RP_SET_SALES_LAYOUT = setLayout;
    window.RP_SALES_LAYOUT = preference;
    return () => {
      if (window.RP_SET_SALES_LAYOUT === setLayout) window.RP_SET_SALES_LAYOUT = null;
    };
  }, [preference]);

  const useDesktop = salesDesktopEligible(activeRole)
    && (preference === 'desktop' || (preference === 'auto' && viewportWidth >= SALES_DESKTOP_BREAKPOINT));

  return useDesktop
    ? <SalesDesktopApp role={activeRole} onUseMobile={() => window.RP_SET_SALES_LAYOUT && window.RP_SET_SALES_LAYOUT('mobile')} />
    : <MobileApp role={role} />;
}

function salesDesktopParent(screen, params, role) {
  const parent = typeof mobileParentRoute === 'function'
    ? mobileParentRoute(screen, params, role, 'rep-home')
    : null;
  if (!parent) return null;
  if (parent.s === 'rep-home' || parent.s === 'more') return { s: 'sales-overview', p: {} };
  return parent;
}

function salesDesktopSection(screen, params, role) {
  let current = screen;
  let currentParams = params || {};
  const seen = new Set();
  while (current && !SALES_DESKTOP_ROOT_IDS.has(current) && !seen.has(current)) {
    seen.add(current);
    const parent = salesDesktopParent(current, currentParams, role);
    if (!parent) break;
    current = parent.s;
    currentParams = parent.p || {};
  }
  return SALES_DESKTOP_ROOT_IDS.has(current) ? current : 'sales-overview';
}

function salesDesktopRouteFromLocation() {
  const route = mobileRouteFromLocation('sales-overview', {
    allowScreen: screen => SALES_DESKTOP_ROOT_IDS.has(screen) || Boolean(MOBILE_SCREENS[screen])
  });
  return ['rep-home', 'more'].includes(route.s) ? { s: 'sales-overview', p: {} } : route;
}

function SalesDesktopApp({ role, onUseMobile }) {
  const [stack, setStack] = useState(() => [salesDesktopRouteFromLocation()]);
  const [collapsed, setCollapsed] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const isNarrow = useIsNarrow(900);
  const cur = stack[stack.length - 1] || { s: 'sales-overview', p: {} };
  const activeSection = salesDesktopSection(cur.s, cur.p, role);
  const mini = collapsed && !isNarrow;

  const nav = useMemo(() => ({
    push: (s, p = {}) => {
      const route = { s, p };
      setStack(previous => [...previous, route]);
      mobileWriteRoute(route);
    },
    pop: () => setStack(previous => {
      if (previous.length > 1) {
        const next = previous[previous.length - 2];
        mobileWriteRoute(next, 'replace');
        return previous.slice(0, -1);
      }
      const current = previous[0] || { s: 'sales-overview', p: {} };
      const parent = salesDesktopParent(current.s, current.p, role);
      const route = parent || { s: 'sales-overview', p: {} };
      mobileWriteRoute(route, 'replace');
      return [route];
    }),
    tab: (s) => {
      const route = { s, p: {} };
      setStack([route]);
      mobileWriteRoute(route);
      setDrawer(false);
    },
    reset: (s, p = {}) => {
      const route = { s, p };
      setStack([route]);
      mobileWriteRoute(route, 'replace');
      setDrawer(false);
    },
  }), [role]);

  useEffect(() => {
    const restoreRoute = () => {
      setStack([salesDesktopRouteFromLocation()]);
      setDrawer(false);
    };
    window.addEventListener('popstate', restoreRoute);
    return () => window.removeEventListener('popstate', restoreRoute);
  }, []);

  useEffect(() => {
    const openAccount = () => nav.tab('settings');
    window.RP_OPEN_ACCOUNT = openAccount;
    return () => { if (window.RP_OPEN_ACCOUNT === openAccount) window.RP_OPEN_ACCOUNT = null; };
  }, [nav]);

  const Component = cur.s === 'sales-overview'
    ? SalesDesktopOverview
    : cur.s === 'sales-visits'
      ? SalesDesktopVisits
      : cur.s === 'order-exceptions'
        ? SalesDesktopOrderExceptions
      : MOBILE_SCREENS[cur.s];
  const content = Component
    ? <Component key={`${cur.s}:${JSON.stringify(cur.p)}`} nav={nav} role={role} {...cur.p} />
    : <SalesDesktopMissing nav={nav} screen={cur.s} />;
  const currentNav = SALES_DESKTOP_NAV.find(item => item.id === activeSection) || SALES_DESKTOP_NAV[0];
  const identity = useRpIdentity(role);
  const groups = Array.from(new Set(SALES_DESKTOP_NAV.map(item => item.group)));

  const sidebar = (
    <aside style={{ width: mini ? 70 : 244, height: '100%', flexShrink: 0, background: 'var(--navy)', color: 'oklch(0.9 0.01 235)', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--navy-900)', transition: 'width .2s ease' }}>
      <div style={{ padding: mini ? '20px 0 18px' : '20px 14px 18px', borderBottom: '1px solid oklch(1 0 0 / 0.08)', display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'space-between', gap: 8 }}>
        {mini ? <Buffalo size={28} /> : <Logo color="oklch(0.97 0.01 80)" />}
        {!isNarrow && !mini && <button onClick={() => setCollapsed(true)} title="Collapse" style={{ border: 'none', background: 'oklch(1 0 0 / 0.08)', color: 'oklch(0.85 0.02 235)', width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="chevL" size={16} /></button>}
        {isNarrow && <button onClick={() => setDrawer(false)} aria-label="Close menu" style={{ border: 'none', background: 'oklch(1 0 0 / 0.08)', color: '#fff', width: 30, height: 30, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={17} /></button>}
      </div>
      {!mini && <div style={{ padding: '16px 20px 6px', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--amber)', fontFamily: 'var(--ff-display)', fontWeight: 700 }}>Sales Workspace</div>}
      <nav className="rp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: mini ? '10px 10px 12px' : '2px 12px 12px' }}>
        {groups.map(group => (
          <div key={group} style={{ marginTop: mini ? 5 : 10 }}>
            {!mini && <div style={{ padding: '5px 8px', color: 'oklch(0.7 0.02 235)', fontSize: 9.5, fontWeight: 750, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{group}</div>}
            {SALES_DESKTOP_NAV.filter(item => item.group === group).map(item => {
              const active = item.id === activeSection;
              return (
                <button key={item.id} onClick={() => nav.tab(item.id)} title={mini ? item.label : undefined} style={{ display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'flex-start', gap: 11, padding: mini ? '11px 0' : '8px 11px', marginBottom: 1, borderRadius: 8, border: 'none', background: active ? 'oklch(1 0 0 / 0.12)' : 'transparent', color: active ? '#fff' : 'oklch(0.85 0.015 235)', fontSize: 13, fontWeight: active ? 700 : 500, textAlign: 'left', width: '100%', position: 'relative' }}>
                  {active && !mini && <span style={{ position: 'absolute', left: -12, top: 5, bottom: 5, width: 3, background: 'var(--amber)', borderRadius: 3 }} />}
                  <Icon name={item.icon} size={18} />
                  {!mini && <span>{item.label}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      {!mini && (
        <div style={{ borderTop: '1px solid oklch(1 0 0 / 0.08)', padding: 12 }}>
          <button onClick={onUseMobile} style={{ width: '100%', border: '1px solid oklch(1 0 0 / 0.12)', background: 'oklch(1 0 0 / 0.05)', color: 'oklch(0.88 0.015 235)', borderRadius: 8, padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, fontWeight: 650 }}><Icon name="phone" size={15} />Use mobile layout</button>
          <button onClick={() => nav.tab('settings')} style={{ width: '100%', border: 0, background: 'transparent', color: 'inherit', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '12px 4px 2px' }}>
            <Avatar name={identity.name} color="var(--amber)" size={32} />
            <span style={{ minWidth: 0 }}><span style={{ display: 'block', color: '#fff', fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{identity.name}</span><span style={{ display: 'block', color: 'oklch(0.7 0.02 235)', fontSize: 10.5, marginTop: 2 }}>{identity.role}</span></span>
          </button>
        </div>
      )}
      {mini && <button onClick={() => setCollapsed(false)} title="Expand" style={{ margin: '12px auto', border: 'none', background: 'oklch(1 0 0 / 0.08)', color: 'oklch(0.85 0.02 235)', width: 30, height: 30, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="chevR" size={16} /></button>}
    </aside>
  );

  return (
    <div className="sales-desktop-shell" style={{ display: 'flex', height: '100%', background: 'var(--paper)', position: 'relative', overflow: 'hidden' }}>
      {!isNarrow && sidebar}
      {isNarrow && <><div onClick={() => setDrawer(false)} style={{ position: 'absolute', inset: 0, zIndex: 40, background: 'oklch(0.24 0.035 235 / 0.5)', opacity: drawer ? 1 : 0, pointerEvents: drawer ? 'auto' : 'none', transition: 'opacity .2s' }} /><div style={{ position: 'absolute', inset: '0 auto 0 0', zIndex: 41, transform: drawer ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .24s ease', boxShadow: drawer ? 'var(--shadow-lg)' : 'none' }}>{sidebar}</div></>}
      <div className="sales-desktop-content" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flexShrink: 0, height: 52, borderBottom: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px' }}>
          <button onClick={() => isNarrow ? setDrawer(true) : setCollapsed(value => !value)} title="Menu" style={{ border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={isNarrow ? 'more' : (mini ? 'chevR' : 'chevL')} size={18} sw={2} style={isNarrow ? { transform: 'rotate(90deg)' } : undefined} /></button>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9 }}><Icon name={currentNav.icon} size={17} style={{ color: 'var(--amber-deep)' }} /><span className="disp" style={{ fontSize: 16, textTransform: 'uppercase', letterSpacing: '0.04em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentNav.label}</span></div>
          {stack.length > 1 && <button className="btn btn-sm" onClick={nav.pop}><Icon name="arrowL" size={14} />Back</button>}
          <button onClick={() => nav.tab('notifications')} aria-label="Notifications" style={{ border: '1px solid var(--line)', background: activeSection === 'notifications' ? 'var(--surface-2)' : 'var(--surface)', color: 'var(--ink)', width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="bell" size={17} /></button>
        </div>
        <main className="rp-scroll sales-desktop-workflow" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}><PageHelp page={cur.s} detail={cur.p} role={role} portal="field" />{content}</main>
      </div>
    </div>
  );
}

function SalesDesktopOverview({ nav, role }) {
  const isCompact = useIsNarrow(1120);
  const personalLocations = inventoryPersonalFieldLocations(role);
  const salesRepackAllowed = inventoryRepackSalesRoleAllowed(role);
  const [inventoryLocationId, setInventoryLocationId] = useState(() => inventoryPersonalPreferredFieldLocationId(personalLocations));
  const micro = salesRepackAllowed ? inventoryPersonalResolveFieldLocation(personalLocations, inventoryLocationId) : rpFieldMicroWarehouse(role);
  const [newRepack, setNewRepack] = useState(false);
  const name = rpFieldUserName(role);
  const firstName = rpFieldFirstName(role);
  const territory = rpAreaLabel(rpFieldTerritories(role), 'Unassigned');
  const customers = rpFieldCustomers(role);
  const orders = rpFieldOrders(role);
  const inventory = rpFieldInventory(role, micro);
  const salesAnalytics = useRpSalesAnalytics('mtd');
  const demoSales = rpSalesSummary(orders, { range: 'mtd' });
  const liveSummary = salesAnalytics.data && salesAnalytics.data.summary;
  const liveOperational = salesAnalytics.data && salesAnalytics.data.operational;
  const salesRevenue = salesAnalytics.isLive ? (liveSummary ? dashboardExactNumber(liveSummary.revenue) : null) : demoSales.revenue;
  const salesCases = salesAnalytics.isLive ? (liveSummary ? dashboardExactNumber(liveSummary.cases) : null) : demoSales.cases;
  const openOrderCount = salesAnalytics.isLive
    ? (liveOperational ? dashboardExactNumber(liveOperational.openOrders) : null)
    : orders.filter(order => typeof rpOrderIsOpenForField === 'function' ? rpOrderIsOpenForField(order) : !['Delivered', 'Invoiced', 'Paid', 'Closed', 'Cancelled', 'Canceled'].includes(String(order.status || ''))).length;
  const activeCustomerCount = salesAnalytics.isLive
    ? (liveSummary ? dashboardExactNumber(liveSummary.activeDoors) : null)
    : customers.filter(customer => rpCustomerOrderReady(customer)).length;
  const restock = typeof rpRestockRecommendations === 'function' ? rpRestockRecommendations(customers, { limit: 6 }) : [];
  const priorityCustomers = customers.map(repCustomerHealth).filter(row => row.tone !== 'Low').sort((a, b) => b.score - a.score).slice(0, 5);
  const recentOrders = orders.slice().sort((a, b) => String(b.created || b.updated || b.date || '').localeCompare(String(a.created || a.updated || a.date || ''))).slice(0, 6);
  const goals = (RP.SALES_GOALS || []).filter(goal => goal && goal.active !== false && (goal.rep === name || !goal.rep));
  const defaultPeriod = typeof salesGoalDefaultPeriod === 'function' ? salesGoalDefaultPeriod() : '';
  const goal = goals.find(item => item.period === defaultPeriod) || goals[0] || null;
  const inventoryUsed = rpFieldInventoryUsedCases(inventory);
  const inventoryCapacity = Math.max(rpFiniteNumber(micro && micro.cap, 100), inventoryUsed, 1);
  const visitCustomerId = rpFieldFirstCustomerId(role);
  const quickActions = [
    { label: 'New Order', icon: 'plus', action: () => nav.push('create-order') },
    { label: 'Quick Retail Quote', icon: 'doc', action: () => nav.push('quick-retail-quote') },
    { label: 'Saved Quotes', icon: 'doc', action: () => nav.push('retail-quotes') },
    { label: 'Add Customer', icon: 'customers', action: () => nav.push('add-customer') },
    { label: 'Add Lead', icon: 'trend', action: () => nav.push('add-lead') },
    { label: 'Log Visit', icon: 'pin', action: () => visitCustomerId ? nav.push('visit-log', { id: visitCustomerId }) : nav.tab('rep-customers') },
    { label: 'Request Stock', icon: 'layers', action: () => micro ? nav.push('request-stock', { loc: micro.id }) : toast(inventoryPersonalNoLocationMessage(), 'info') },
    { label: 'Submit Expense', icon: 'receipt', action: () => nav.push('submit-expense') },
    salesRepackAllowed ? {
      label: 'Build Mix Packs',
      icon: 'layers',
      action: () => personalLocations.length
        ? setNewRepack(true)
        : toast(inventoryPersonalNoLocationMessage(), 'info')
    } : null,
  ].filter(Boolean);

  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px', maxWidth: 1500, margin: '0 auto' }}>
      {newRepack && personalLocations.length > 0 && <FinishedGoodsRepackModal
        allowedLocationIds={personalLocations.map(location => location.id)}
        onClose={() => setNewRepack(false)}
        onAdded={result => {
          const savedLocationId = inventoryPersonalRememberFieldLocation(personalLocations, result && result.locationId);
          if (savedLocationId) setInventoryLocationId(savedLocationId);
          setNewRepack(false);
        }}
      />}
      <PageHead eyebrow={`${territory} - Sales Rep`} title={`Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, ${firstName}`}
        sub="Your customers, sales, field inventory, tasks, and expenses are scoped to your sales profile."
        actions={<button className="btn btn-sm btn-primary" onClick={() => nav.push('create-order')}><Icon name="plus" size={15} />New Order</button>} />

      <SalesAnalyticsStatus state={salesAnalytics} label="my MTD and operational sales totals" />

      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 18 }}>
        <KPICard label="My Revenue - MTD" value={salesRevenue == null ? '—' : RP.money(Math.round(salesRevenue))} icon="trade" accent="var(--ok)" />
        <KPICard label="My Cases - MTD" value={salesCases == null ? '—' : Math.round(salesCases).toLocaleString()} icon="package" accent="var(--amber)" />
        <KPICard label="My Open Orders" value={openOrderCount == null ? '—' : openOrderCount.toLocaleString()} icon="orders" accent="var(--info)" onClick={() => nav.tab('rep-orders')} />
        <KPICard label="My Active Customers" value={activeCustomerCount == null ? '—' : activeCustomerCount.toLocaleString()} icon="customers" accent="var(--navy)" onClick={() => nav.tab('rep-customers')} />
      </div>

      <div className="card" data-sales-my-day="true" style={{ padding: '18px 20px', marginBottom: 18 }}>
        <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => nav.tab('my-tasks')}>All tasks<Icon name="chevR" size={14} /></button>}>My Day</SectionTitle>
        <RepMyDayQueue nav={nav} role={role} limit={8} desktop />
      </div>

      <div className="card" style={{ padding: '16px 18px', marginBottom: 18 }}>
        <SectionTitle>Quick Actions</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 9 }}>
          {quickActions.map(item => <button key={item.label} className="btn" onClick={item.action} style={{ justifyContent: 'flex-start', minHeight: 42 }}><Icon name={item.icon} size={16} />{item.label}</button>)}
        </div>
        {salesRepackAllowed && !personalLocations.length && <div role="status" style={{ marginTop: 10, border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 9, padding: '10px 12px', color: 'var(--muted)', fontSize: 12 }}>{inventoryPersonalNoLocationMessage()}</div>}
      </div>

      <div className="card" style={{ padding: '18px 20px', marginBottom: 18 }}>
        <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => nav.tab('rep-customers')}>Customers<Icon name="chevR" size={14} /></button>}>Customer Priorities</SectionTitle>
        <div style={{ display: 'grid', gap: 8 }}>
          {priorityCustomers.map(row => {
            const [fg, bg] = statusColors(row.tone);
            return <button key={row.customer.id} onClick={() => nav.push('rep-customer', { id: row.customer.id })} style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 11, textAlign: 'left' }}><Avatar name={row.customer.name} size={32} color={row.tone === 'High' ? 'var(--danger)' : 'var(--amber-deep)'} /><span style={{ flex: 1, minWidth: 0 }}><span style={{ display: 'block', fontSize: 13.5, fontWeight: 750, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.customer.name}</span><span style={{ display: 'block', color: 'var(--faint)', fontSize: 11.5, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.reasons.slice(0, 2).join(' - ')}</span></span><span className="pill" style={{ color: fg, background: bg }}>{row.label}</span><Icon name="chevR" size={15} style={{ color: 'var(--faint)' }} /></button>;
          })}
          {!priorityCustomers.length && <div style={{ border: '1px dashed var(--line)', borderRadius: 10, padding: 22, textAlign: 'center', color: 'var(--faint)', fontSize: 13 }}>No customer follow-ups are currently due.</div>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1.2fr) minmax(320px, .8fr)', gap: 18, marginBottom: 18 }}>
        <div className="card" style={{ padding: '18px 20px', overflow: 'hidden' }}>
          <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => nav.tab('rep-orders')}>View all<Icon name="chevR" size={14} /></button>}>{salesAnalytics.isLive ? 'Current Order Queue' : 'Recent Orders'}</SectionTitle>
          <div style={{ overflowX: 'auto' }}><table className="rp-table"><thead><tr><th>Order</th><th>Customer</th><th>Status</th><th className="num">Cases</th><th className="num">Total</th></tr></thead><tbody>{recentOrders.map(order => { const customer = RP.cust(order.cust); const totals = rpOrderTotals(order); return <tr key={order.id} onClick={() => nav.push('rep-order-detail', { id: order.id })} style={{ cursor: 'pointer' }}><td className="mono" style={{ fontWeight: 700 }}>{order.id}</td><td>{customer ? customer.name : order.cust}</td><td><Pill status={order.status} /></td><td className="num">{Math.round(totals.cases || 0)}</td><td className="num">{RP.money2(totals.total || totals.sub || 0)}</td></tr>; })}{!recentOrders.length && <tr><td colSpan="5" style={{ padding: 24, textAlign: 'center', color: 'var(--faint)' }}>No orders are assigned to your profile yet.</td></tr>}</tbody></table></div>
        </div>

        <div style={{ display: 'grid', gap: 18 }}>
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => nav.tab('rep-inventory')}>Details<Icon name="chevR" size={14} /></button>}>My Field Inventory</SectionTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8, color: 'var(--muted)', fontSize: 12.5 }}><span>{micro ? micro.name : 'Assigned storage'}</span><span className="mono">{inventoryUsed} / {inventoryCapacity} cs</span></div>
            {!micro && <div role="status" style={{ marginBottom: 10, color: 'var(--muted)', fontSize: 12 }}>{personalLocations.length > 1 ? 'Choose an associated storage location in Details.' : inventoryPersonalNoLocationMessage()}</div>}
            <Bar value={inventoryUsed} max={inventoryCapacity} color="var(--amber)" />
            <div style={{ display: 'grid', gap: 7, marginTop: 12 }}>{inventory.slice(0, 4).map(item => <div key={item.sku} style={{ display: 'flex', alignItems: 'center', gap: 9 }}><FlavorChip sku={RP.sku(item.sku) || item.sku} size={24} /><span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rpSkuDisplayName(RP.sku(item.sku) || item.sku)}</span><span className="mono" style={{ fontSize: 12, color: item.available <= 5 ? 'var(--danger)' : 'var(--ink)', fontWeight: 800 }}>{item.available} cs</span></div>)}</div>
          </div>
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => nav.tab('my-goals')}>Details<Icon name="chevR" size={14} /></button>}>My Goal</SectionTitle>
            {goal ? <><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 8 }}><span className="disp" style={{ fontSize: 24 }}>{RP.money(Math.round(goal.revActual || 0))}</span><span className="mono" style={{ color: 'var(--faint)', fontSize: 12 }}>/ {RP.money(Math.round(goal.revTarget || 0))}</span></div><Bar value={Number(goal.revActual || 0)} max={Math.max(1, Number(goal.revTarget || 0))} color="var(--ok)" /><div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 8 }}>{goal.period} - {goal.casesActual || 0}/{goal.casesTarget || 0} cases</div></> : <div style={{ border: '1px dashed var(--line)', borderRadius: 10, padding: 18, textAlign: 'center', color: 'var(--faint)', fontSize: 12.5 }}>No active goal assigned.</div>}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: '18px 20px' }}>
        <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => nav.tab('sales-visits')}>Visit log<Icon name="chevR" size={14} /></button>}>Restock Watch</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 9 }}>{restock.slice(0, 6).map(row => <button key={row.customer.id} onClick={() => nav.push('rep-customer', { id: row.customer.id })} style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '11px 12px', textAlign: 'left' }}><span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ fontSize: 13, fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.customer.name}</span><Pill status={row.status}>{row.label}</Pill></span><span style={{ display: 'block', color: 'var(--faint)', fontSize: 11.5, marginTop: 6 }}>{row.reasons.slice(0, 2).join(' - ')}</span></button>)}</div>
      </div>
    </div>
  );
}

function SalesDesktopVisits({ nav, role }) {
  const [search, setSearch] = useState('');
  const customers = rpFieldCustomers(role);
  const customerById = new Map(customers.map(customer => [customer.id, customer]));
  const visitCustomerId = visit => String(visit.cust || visit.customerId || visit.customer_id || '').trim();
  const visits = (RP.VISITS || [])
    .filter(visit => customerById.has(visitCustomerId(visit)))
    .slice()
    .sort((a, b) => String(b.date || b.created || '').localeCompare(String(a.date || a.created || '')));
  const term = search.trim().toLowerCase();
  const filtered = visits.filter(visit => {
    const customer = customerById.get(visitCustomerId(visit)) || {};
    const rep = visit.who || visit.rep || visit.repName || visit.rep_name || rpFieldUserName(role);
    const followUp = visit.followUp ? 'Needed' : 'Complete';
    return !term || [visit.note, visit.notes, customer.name, customer.city, customer.id, rep, visit.date, visit.created, followUp]
      .some(value => String(value || '').toLowerCase().includes(term));
  });
  const defaultCustomerId = rpFieldFirstCustomerId(role);
  return (
    <div className="rp-fade" style={{ padding: '26px 30px 40px', maxWidth: 1400, margin: '0 auto' }}>
      <PageHead eyebrow="My Field Activity" title="Visit Log" sub="Visits and shelf checks for customers in your assigned sales scope." actions={<button className="btn btn-sm btn-primary" disabled={!defaultCustomerId} onClick={() => defaultCustomerId && nav.push('visit-log', { id: defaultCustomerId })}><Icon name="plus" size={15} />Log Visit</button>} />
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}><div style={{ position: 'relative', flex: 1, maxWidth: 460 }}><Icon name="search" size={15} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--faint)' }} /><input className="field" data-sales-visit-search value={search} onChange={event => setSearch(event.target.value)} placeholder="Search visits or customers..." style={{ paddingLeft: 36 }} /></div><button className="btn" onClick={() => nav.tab('rep-customers')}><Icon name="customers" size={15} />Choose Customer</button></div>
      <div className="card" data-sales-visit-count={filtered.length} style={{ overflow: 'hidden' }}><table className="rp-table"><thead><tr><th>Date</th><th>Customer</th><th>Rep</th><th>Follow-up</th><th>Notes</th><th></th></tr></thead><tbody>{filtered.map(visit => { const customerId = visitCustomerId(visit); const customer = customerById.get(customerId); const rep = visit.who || visit.rep || visit.repName || visit.rep_name || rpFieldUserName(role); const followUp = visit.followUp ? 'Needed' : 'Complete'; return <tr key={visit.id || `${customerId}-${visit.date}`} data-sales-visit-id={visit.id || ''} data-sales-visit-customer-id={customerId} data-sales-visit-follow-up={followUp.toLowerCase()}><td className="mono">{visit.date || visit.created || '-'}</td><td style={{ fontWeight: 700 }}>{customer ? customer.name : customerId || '-'}</td><td>{rep}</td><td><Pill status={visit.followUp ? 'Pending' : 'Submitted'}>{followUp}</Pill></td><td style={{ maxWidth: 420, color: 'var(--muted)' }}>{visit.note || visit.notes || '-'}</td><td>{customer && <button className="btn btn-sm" onClick={() => nav.push('rep-customer', { id: customer.id })}>Open<Icon name="chevR" size={13} /></button>}</td></tr>; })}{!filtered.length && <tr><td colSpan="6" style={{ padding: 28, textAlign: 'center', color: 'var(--faint)' }}>No visits match this view.</td></tr>}</tbody></table></div>
    </div>
  );
}

function SalesDesktopOrderExceptions({ nav, role }) {
  return <SalesOrderExceptionsWorkspace nav={nav} role={role} desktop />;
}

function SalesDesktopMissing({ nav, screen }) {
  return <div style={{ padding: 30 }}><PageHead eyebrow="Sales Workspace" title="Workflow unavailable" sub={`The ${screen} workflow could not be opened.`} /><button className="btn btn-primary" onClick={() => nav.tab('sales-overview')}>Back to Overview</button></div>;
}

Object.assign(window, { SalesExperience, SalesDesktopApp, SalesDesktopOverview, SALES_DESKTOP_NAV });


/* ===== client_portal.jsx ===== */
/* ============================================================
   RANCH POWER — Client Portal (customer-facing ordering web app)
   Customers log in to reorder, track shipments, manage account.
   ============================================================ */

const PORTAL_AVAIL = { 'RP-12-GC': 'In stock', 'RP-12-KL': 'In stock', 'RP-12-BR': 'In stock', 'RP-12-PM': 'In stock', 'RP-12-PL': 'Low stock', 'RP-12-BO': 'Backorder' };
function PORTAL_SKUS() {
  return rpCustomerCatalogSkus().filter(sku => !rpSkuIsMixer(sku));
}
function PORTAL_MIXERS() {
  return rpCustomerCatalogSkus().filter(rpSkuIsMixer);
}
function portalOrderSkus() {
  return rpCustomerCatalogSkus();
}
function portalFulfillmentSource(c) {
  const rows = Array.isArray(RP.PORTAL_FULFILLMENT) ? RP.PORTAL_FULFILLMENT : [];
  const customerId = String(c && c.id || '').trim();
  return rows.find(row => String(row.customerId || '') === customerId) || rows[0] || null;
}
function portalOrderSourceId(c) {
  const portalSource = portalFulfillmentSource(c);
  if (portalSource && portalSource.sourceId) return portalSource.sourceId;
  return rpDefaultOrderSourceForCustomer(c);
}
function portalAvailabilityRow(skuId, sourceId) {
  const rows = Array.isArray(RP.PORTAL_AVAILABILITY) ? RP.PORTAL_AVAILABILITY : [];
  return rows.find(row => String(row.sku || '') === String(skuId || '') && (!sourceId || String(row.sourceId || '') === String(sourceId || '')));
}
function portalHasScopedAvailability() {
  return rpHasLiveAuth() && rpNormalizeRole(rpAuthProfile().role) === 'customer';
}
function portalSkuAvailableCases(skuId, sourceOrCustomer) {
  if (!rpHasLiveAuth()) return null;
  const sourceId = typeof sourceOrCustomer === 'string' ? sourceOrCustomer : portalOrderSourceId(sourceOrCustomer);
  const portalRow = portalAvailabilityRow(skuId, sourceId);
  if (portalRow) return Math.max(0, Math.floor(Number(portalRow.available) || 0));
  if (portalHasScopedAvailability()) return 0;
  return rpOrderAvailableCases(skuId, sourceId);
}
function portalSkuAvailability(skuId, sourceOrCustomer) {
  const sourceId = typeof sourceOrCustomer === 'string' ? sourceOrCustomer : portalOrderSourceId(sourceOrCustomer);
  if (rpHasLiveAuth()) {
    const qty = portalSkuAvailableCases(skuId, sourceId);
    if (qty == null) return 'In stock';
    if (qty <= 0) return 'Out of stock';
    if (qty < 15) return 'Low stock';
    return 'In stock';
  }
  return PORTAL_AVAIL[skuId] || 'In stock';
}
function portalSkuStockFilter(skuOrId, sourceOrCustomer) {
  const skuId = typeof skuOrId === 'string' ? skuOrId : skuOrId && skuOrId.id;
  const avail = portalSkuAvailability(skuId, sourceOrCustomer);
  return avail === 'Out of stock' || avail === 'Backorder' ? 'out' : 'in';
}
function portalSkuStockRank(skuOrId, sourceOrCustomer) {
  const skuId = typeof skuOrId === 'string' ? skuOrId : skuOrId && skuOrId.id;
  const avail = portalSkuAvailability(skuId, sourceOrCustomer);
  if (avail === 'In stock') return 0;
  if (avail === 'Low stock') return 1;
  if (avail === 'Backorder') return 2;
  if (avail === 'Out of stock') return 3;
  return 1;
}
function portalSortSkusByStock(skus, sourceOrCustomer) {
  return (Array.isArray(skus) ? skus : [])
    .map((sku, index) => ({ sku, index }))
    .sort((a, b) => {
      const diff = portalSkuStockRank(a.sku, sourceOrCustomer) - portalSkuStockRank(b.sku, sourceOrCustomer);
      return diff || a.index - b.index;
    })
    .map(row => row.sku);
}

function priceFor(cust, sku) {
  return rpCustomerSkuPrice(cust, sku);
}

function portalRequestedScreen(customer) {
  if (!customer || (typeof rpCustomerOrderReady === 'function' && !rpCustomerOrderReady(customer))) return 'home';
  try {
    const params = new URLSearchParams(window.location.search || '');
    const requested = String(params.get('portalScreen') || params.get('next') || '').trim().toLowerCase();
    return requested === 'shop' ? 'shop' : 'home';
  } catch {
    return 'home';
  }
}

function ClientPortal({ custId }) {
  const live = rpHasLiveAuth();
  const persona = useRpIdentity('customer');
  const exactCustomer = useRpExactCustomer({ customerId: custId, enabled: Boolean(custId) });
  if (exactCustomer.loading) return <ClientPortalMissingAccount persona={persona} live={live} loading />;
  if (exactCustomer.error) return <ClientPortalMissingAccount persona={persona} live={live} error={exactCustomer.error} retry={exactCustomer.retry} />;
  if (!exactCustomer.customer) return <ClientPortalMissingAccount persona={persona} live={live} />;
  if (typeof rpCustomerOrderReady === 'function' && !rpCustomerOrderReady(exactCustomer.customer)) {
    return <ClientPortalPendingApproval customer={exactCustomer.customer} persona={persona} live={live} retry={exactCustomer.retry} />;
  }
  return <ClientPortalFound key={exactCustomer.customer.id} custId={custId} customer={exactCustomer.customer} live={live} persona={persona} />;
}

function ClientPortalFound({ custId, customer, live, persona }) {
  const c = customer;
  const [screen, setScreen] = useState(() => portalRequestedScreen(customer));
  const [orderId, setOrderId] = useState(null);
  const [cart, setCart] = useState({}); // skuId -> qty
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const isNarrow = useIsNarrow(760);
  const [menu, setMenu] = useState(false);
  const orderHistoryPage = useRpServerPagedRows({ mode: 'orders', limit: 50, customerId: custId });
  const orderHistory = live
    ? orderHistoryPage.items.map(item => item.order).filter(order => order && (!c || order.cust === c.id))
    : (c ? clientOrders(c) : []);

  const cartCount = Object.values(cart).reduce((s, v) => s + v, 0);
  const addToCart = (sku, q) => setCart(p => {
    if (!rpSkuCustomerVisible(sku)) return p;
    const n = { ...p };
    const max = c ? portalSkuAvailableCases(sku, c) : null;
    const nextQty = Math.max(0, max == null ? q : Math.min(q, max));
    if (nextQty <= 0) delete n[sku];
    else n[sku] = nextQty;
    return n;
  });
  const go = (s, oid) => { setScreen(s); setOrderId(oid || null); setMenu(false); window.scrollTo && window.scrollTo(0, 0); const m = document.querySelector('.rp-portal-main'); if (m) m.scrollTop = 0; };
  useEffect(() => {
    const openAccount = () => go('account');
    window.RP_OPEN_ACCOUNT = openAccount;
    return () => { if (window.RP_OPEN_ACCOUNT === openAccount) window.RP_OPEN_ACCOUNT = null; };
  }, []);

  const nav = [['home', 'Home', 'home'], ['shop', 'Shop', 'package'], ['orders', 'My Orders', 'orders'], ['invoices', 'Invoices', 'receipt'], ['support', 'Support', 'mail'], ['account', 'Account', 'user']];

  let body;
  if (screen === 'shop') body = <PortalShop c={c} cart={cart} addToCart={addToCart} go={go} cartCount={cartCount} />;
  else if (screen === 'cart') body = <PortalCart c={c} cart={cart} addToCart={addToCart} setCart={setCart} go={go} onSubmit={o => { setSubmittedOrder(o); go('confirmed'); }} />;
  else if (screen === 'confirmed') body = <PortalConfirmed c={c} order={submittedOrder} go={go} />;
  else if (screen === 'orders') body = <PortalOrders c={c} go={go} live={live} orders={orderHistory} page={orderHistoryPage} />;
  else if (screen === 'order') body = <PortalOrderDetail c={c} id={orderId} go={go} />;
  else if (screen === 'invoices') body = <PortalInvoices c={c} go={go} />;
  else if (screen === 'invoice') body = <PortalInvoiceDetail c={c} id={orderId} go={go} />;
  else if (screen === 'support') body = <PortalSupport c={c} go={go} />;
  else if (screen === 'account') body = <PortalAccount c={c} go={go} />;
  else body = <PortalHome c={c} go={go} addToCart={addToCart} live={live} orders={orderHistory} page={orderHistoryPage} />;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      {/* Top nav */}
      <header style={{ flexShrink: 0, background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', gap: 16, padding: isNarrow ? '0 14px' : '0 26px', height: 60, borderBottom: '1px solid var(--navy-900)' }}>
        {isNarrow && <button onClick={() => setMenu(true)} style={{ border: 'none', background: 'oklch(1 0 0 / 0.1)', color: '#fff', width: 38, height: 38, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="more" size={19} style={{ transform: 'rotate(90deg)' }} /></button>}
        <button onClick={() => go('home')} style={{ border: 'none', background: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}><Logo color="#fff" compact={isNarrow} /></button>
        {!isNarrow && (
          <nav style={{ display: 'flex', gap: 4, marginLeft: 18 }}>
            {nav.map(([id, label]) => {
              const on = screen === id || (id === 'orders' && screen === 'order') || (id === 'invoices' && screen === 'invoice');
              return <button key={id} onClick={() => go(id)} style={{ border: 'none', background: on ? 'oklch(1 0 0 / 0.12)' : 'transparent', color: on ? '#fff' : 'oklch(0.82 0.02 235)', fontSize: 14, fontWeight: 600, padding: '9px 15px', borderRadius: 8 }}>{label}</button>;
            })}
          </nav>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => go('cart')} title="Cart" style={{ border: 'none', background: cartCount ? 'var(--amber)' : 'oklch(1 0 0 / 0.1)', color: '#fff', height: 38, borderRadius: 9, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, position: 'relative' }}>
          <Icon name="package" size={18} />{cartCount > 0 && <span className="tnum">{cartCount}</span>}
        </button>
        <button onClick={() => go('account')} style={{ border: 'none', background: 'none', padding: 0 }}><Avatar name={live ? persona.name : c.name} size={36} color="var(--amber)" /></button>
      </header>

      {/* Mobile drawer */}
      {isNarrow && (
        <>
          <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, background: 'oklch(0.24 0.035 235 / 0.5)', zIndex: 60, opacity: menu ? 1 : 0, pointerEvents: menu ? 'auto' : 'none', transition: 'opacity .2s' }} />
          <div style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: 250, background: 'var(--navy)', zIndex: 61, transform: menu ? 'none' : 'translateX(-100%)', transition: 'transform .24s', padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}><Logo color="#fff" /><button onClick={() => setMenu(false)} style={{ border: 'none', background: 'oklch(1 0 0 / 0.1)', color: '#fff', width: 32, height: 32, borderRadius: 8 }}><Icon name="x" size={17} /></button></div>
            {nav.map(([id, label, icon]) => {
              const on = screen === id || (id === 'orders' && screen === 'order') || (id === 'invoices' && screen === 'invoice');
              return <button key={id} onClick={() => go(id)} style={{ display: 'flex', alignItems: 'center', gap: 12, border: 'none', background: on ? 'oklch(1 0 0 / 0.12)' : 'transparent', color: on ? '#fff' : 'oklch(0.82 0.02 235)', fontSize: 14.5, fontWeight: 600, padding: '12px 14px', borderRadius: 9, textAlign: 'left' }}><Icon name={icon} size={18} />{label}</button>;
            })}
          </div>
        </>
      )}

      {/* Body */}
      <main className="rp-scroll rp-portal-main" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>{body}</main>
    </div>
  );
}

function ClientPortalPendingApproval({ customer, persona, live, retry }) {
  const submitted = Boolean(customer && customer.onboardingSubmittedAt);
  const signOut = () => {
    if (window.RP_SIGN_OUT) window.RP_SIGN_OUT();
    else toast('Signed out (demo)', 'info');
  };
  const refresh = async () => {
    if (typeof rpRefreshLiveData === 'function') await rpRefreshLiveData();
    if (typeof retry === 'function') await retry();
  };
  return (
    <div className="rp-portal-pending" style={{ minHeight: '100%', background: 'var(--paper)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ flexShrink: 0, background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', padding: '0 24px', minHeight: 60 }}><Logo color="#fff" /></header>
      <PortalWrap>
        <div className="card" style={{ padding: 26, maxWidth: 650, margin: '34px auto 0' }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Secure retailer account</div>
          <h1 className="disp" style={{ margin: 0, fontSize: 30, textTransform: 'uppercase' }}>{submitted ? 'Application received' : 'Finish your retailer setup'}</h1>
          <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.55, margin: '12px 0 18px' }}>
            {submitted
              ? `Thanks${persona && persona.name ? `, ${persona.name.split(/\s+/)[0]}` : ''}. Your retailer information for ${customer.name || 'this account'} is being reviewed. We will unlock online ordering as soon as the account is approved.`
              : 'Complete the retailer intake form shown on this page. It gives us the delivery, billing, and contact details needed to activate online ordering.'}
          </p>
          <div style={{ background: submitted ? 'var(--ok-bg)' : 'var(--warn-bg)', borderRadius: 10, padding: '13px 14px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 18 }}>
            {submitted
              ? 'Nothing else is required right now. Your rep can still help with an order while approval is pending.'
              : 'The shop stays locked until the intake is complete and the account is approved. No order will be submitted automatically.'}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {submitted && live && <button className="btn btn-primary" onClick={refresh}><Icon name="refresh" size={15} />Check status</button>}
            <a className="btn" href="mailto:sales@ranchpower.com"><Icon name="mail" size={15} />Contact Ranch Power</a>
            <button className="btn" onClick={signOut}><Icon name="arrowL" size={15} />Sign out</button>
          </div>
        </div>
      </PortalWrap>
    </div>
  );
}

/* ---- shared portal bits ---- */
function PortalWrap({ children, wide }) {
  return <div className="rp-fade" style={{ maxWidth: wide ? 1080 : 920, margin: '0 auto', padding: '28px 22px 60px' }}>{children}</div>;
}
function PortalH({ eyebrow, title, sub, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
      <div>
        {eyebrow && <div className="eyebrow" style={{ marginBottom: 6 }}>{eyebrow}</div>}
        <h1 className="disp" style={{ margin: 0, fontSize: 30, textTransform: 'uppercase', letterSpacing: '0.01em' }}>{title}</h1>
        {sub && <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 5 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

function PortalSupport({ c, go }) {
  const narrow = useIsNarrow(760);
  return (
    <PortalWrap>
      <PortalH eyebrow="Customer Support" title="Support" sub="Send order, billing, account, or portal issues to Ranch Power." right={<button className="btn btn-sm" onClick={() => go('account')}><Icon name="user" size={15} />Account</button>} />
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1fr) 280px', gap: 16 }}>
        <div className="card" style={{ padding: 18 }}>
          <SupportTicketForm customerId={c.id} customerLocked source="Customer portal" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card" style={{ padding: 16 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Account</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>{c.name}</div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>{c.city || c.territory || ''}</div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Direct Contact</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
              <Row k="Email" v="orders@ranchpower.com" />
              <Row k="Phone" v="+1 (587) 887-1164" />
            </div>
          </div>
        </div>
      </div>
    </PortalWrap>
  );
}

function ClientPortalMissingAccount({ persona, live, loading = false, error = '', retry = null }) {
  const profile = rpAuthProfile();
  const signOut = () => {
    if (window.RP_SIGN_OUT) window.RP_SIGN_OUT();
    else toast('Signed out (demo)', 'info');
  };
  const openPrimaryPortal = () => {
    const role = rpNormalizeRole(profile.role);
    const target = role === 'customer'
      ? 'https://order.ranchpower.com'
      : ['rep', 'partner', 'warehouse', 'dispatch'].includes(role)
        ? 'https://sales.ranchpower.com'
        : 'https://operations.ranchpower.com';
    window.location.assign(target);
  };
  return (
    <div data-exact-customer-loading={loading ? 'true' : undefined} data-exact-customer-error={error ? 'true' : undefined} data-exact-customer-missing={!loading && !error ? 'true' : undefined} style={{ minHeight: '100%', background: 'var(--paper)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ flexShrink: 0, background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', gap: 16, padding: '0 26px', minHeight: 60, borderBottom: '1px solid var(--navy-900)' }}>
        <Logo color="#fff" />
      </header>
      <PortalWrap>
        <div className="card" style={{ padding: 24, maxWidth: 620, margin: '34px auto 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <Avatar name={persona.name || profile.email || 'RP'} size={50} color="var(--navy)" />
            <div>
              <div className="eyebrow" style={{ marginBottom: 5 }}>Secure Account</div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>{loading ? 'Loading customer account' : error ? 'Customer account could not load' : 'Customer account required'}</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 3 }}>{persona.email || profile.email || 'Signed-in user'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, fontSize: 13.5, marginBottom: 18 }}>
            <Row k="Signed in as" v={persona.name || profile.display_name || 'Ranch Power user'} />
            <Row k="Role" v={rpRoleLabel(profile.role)} />
            <Row k="Customer link" v={<Pill status={loading ? 'Pending' : error ? 'Error' : 'Missing'}>{loading ? 'Loading' : error ? 'Unavailable' : 'Missing'}</Pill>} />
          </div>
          <div style={{ background: 'var(--warn-bg)', color: 'var(--amber-deep)', borderRadius: 10, padding: '12px 14px', fontSize: 13, lineHeight: 1.45, fontWeight: 600, marginBottom: 16 }}>
            {loading ? 'Fetching your exact customer account from the complete directory.' : error || 'This live ordering portal is locked until the signed-in profile is linked to an active customer record.'}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {retry && <button className="btn btn-primary" onClick={retry}><Icon name="refresh" size={15} />Try Again</button>}
            {!loading && !retry && live && <button className="btn btn-primary" onClick={openPrimaryPortal}><Icon name="home" size={15} />Open My Portal</button>}
            <button className="btn" onClick={signOut}><Icon name="arrowL" size={15} />Sign out</button>
          </div>
        </div>
      </PortalWrap>
    </div>
  );
}

function clientOrders(c) { return RP.ORDERS.filter(o => o.cust === c.id); }

/* ---- HOME ---- */
function PortalHome({ c, go, addToCart, live = false, orders: providedOrders, page }) {
  const orders = Array.isArray(providedOrders) ? providedOrders : clientOrders(c);
  const aggregateLastOrderId = String(c.lastOrderId || '').trim();
  const exactLastOrder = useRpExactOrder({ orderId: aggregateLastOrderId, enabled: live && Boolean(aggregateLastOrderId) });
  const demoLastOrder = orders.find(o => o.status === 'Delivered') || orders[0] || null;
  const lastOrder = live
    ? (exactLastOrder.order && String(exactLastOrder.order.cust || '') === String(c.id || '') ? exactLastOrder.order : null)
    : demoLastOrder;
  const openOrders = orders.filter(o => rpOrderDeliveryScan(o).open).slice(0, 6);
  const reorderLoading = live && Boolean(aggregateLastOrderId) && exactLastOrder.loading;
  const reorderUnavailable = live && Boolean(aggregateLastOrderId) && !exactLastOrder.loading && !lastOrder
    && (Boolean(exactLastOrder.error) || exactLastOrder.notFound || exactLastOrder.loaded);
  const reorder = () => {
    if (reorderLoading) return;
    if (live && aggregateLastOrderId && !lastOrder) {
      exactLastOrder.retry();
      return;
    }
    if (!lastOrder) {
      go('shop');
      return;
    }
    if (live) rpUpsertOrder(lastOrder);
    const cart = {};
    (lastOrder.lines || []).forEach(l => { if (rpSkuCustomerVisible(l.sku)) cart[l.sku] = l.qty; });
    window.__portalSeed = cart;
    go('shop');
  };
  const reorderLabel = reorderLoading
    ? 'Loading Reorder...'
    : reorderUnavailable
      ? 'Retry Quick Reorder'
      : lastOrder ? 'Quick Reorder' : 'Start an Order';
  return (
    <div>
      {/* hero */}
      <div style={{ background: 'var(--navy)', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(oklch(1 0 0 / 0.05) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '34px 22px 38px', position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.7, fontWeight: 500 }}>Welcome back, {c.buyer}</div>
            <h1 className="disp" style={{ margin: '4px 0 0', fontSize: 38, textTransform: 'uppercase', letterSpacing: '0.01em' }}>{c.name}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <span className="pill" style={{ background: 'oklch(1 0 0 / 0.12)', color: '#fff' }}>Terms: {c.terms}</span>
              <span className="pill" style={{ background: c.credit === 'Watch' ? 'var(--warn-bg)' : 'oklch(1 0 0 / 0.12)', color: c.credit === 'Watch' ? 'var(--amber-deep)' : '#fff' }}>Credit: {c.credit}</span>
            </div>
            <div style={{ marginTop: 10, maxWidth: 620, fontSize: 11.5, lineHeight: 1.45, opacity: 0.78 }}>{RP_CREDIT_CARD_TERMS_NOTE}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-lg btn-primary" disabled={reorderLoading} onClick={reorder} style={{ opacity: reorderLoading ? 0.65 : 1 }}><Icon name={lastOrder || aggregateLastOrderId ? 'refresh' : 'plus'} size={17} />{reorderLabel}</button>
            <button className="btn btn-lg" style={{ background: 'oklch(1 0 0 / 0.1)', color: '#fff', borderColor: 'transparent' }} onClick={() => go('shop')}>Browse Catalog</button>
          </div>
        </div>
      </div>

      <PortalWrap wide>
        {reorderUnavailable && <div data-quick-reorder-error="true" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 10, color: 'var(--danger)', padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 18 }}>Quick reorder could not load the exact previous order. No substitute order was selected. <button type="button" className="btn btn-sm" onClick={exactLastOrder.retry} style={{ marginLeft: 8 }}>Try Again</button></div>}
        {live && page && !page.initialLoading && !page.loaded && page.error && <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 10, color: 'var(--danger)', padding: '11px 13px', fontSize: 12.5, fontWeight: 700, marginBottom: 18 }}>Order history could not load: {page.error} <button type="button" className="btn btn-sm" onClick={page.retry} style={{ marginLeft: 8 }}>Try Again</button></div>}
        {/* Open orders */}
        {openOrders.length > 0 && (
          <div style={{ marginBottom: 30 }}>
            <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => go('orders')}>All orders<Icon name="chevR" size={14} /></button>}>Active Orders</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
              {openOrders.map(o => { const t = orderTotals(o); return (
                <button key={o.id} onClick={() => { if (live) rpUpsertOrder(o); go('order', o.id); }} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, boxShadow: 'var(--shadow-sm)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span className="mono nowrap" style={{ fontWeight: 600, fontSize: 13 }}>{o.id}</span><Pill status={o.status} /></div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>{o.lines.slice(0, 5).map((l, i) => <FlavorChip key={i} sku={rpSkuItem(l.sku) || l.sku} size={24} />)}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 14 }}><span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t.cases} cases · ETA {rpOrderDateLabel(o)}</span><span className="disp" style={{ fontSize: 18 }}>{RP.money(t.total)}</span></div>
                </button>
              ); })}
            </div>
          </div>
        )}

        {/* Reorder favourites */}
        <SectionTitle right={<button className="btn btn-ghost btn-sm" onClick={() => go('shop')}>Shop all<Icon name="chevR" size={14} /></button>}>Your Regulars</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
          {portalSortSkusByStock(portalOrderSkus(), c).slice(0, 4).map(s => <ProductCard key={s.id} sku={s} c={c} onAdd={q => { window.__portalSeed = { ...(window.__portalSeed || {}), [s.id]: q }; go('shop'); }} compact />)}
        </div>
      </PortalWrap>
    </div>
  );
}

/* ---- PRODUCT CARD ---- */
function ProductCard({ sku, c, onAdd, compact }) {
  const [qty, setQty] = useState(0);
  const avail = portalSkuAvailability(sku.id, c);
  const availableCases = portalSkuAvailableCases(sku.id, c);
  const visual = rpSkuVisualMeta(sku);
  const price = priceFor(c, sku);
  const disabled = avail === 'Out of stock' || avail === 'Backorder';
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: `linear-gradient(160deg, ${visual.color} 0%, oklch(from ${visual.color} calc(l - 0.12) c h) 100%)`, padding: '18px 0 14px', display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <img src={RP.can(visual.flavorId || rpSkuVisualFlavor(sku))} alt={sku.name} style={{ height: compact ? 92 : 110, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 6px 12px rgba(20,30,40,.35))' }} />
        <span className="pill" style={{ position: 'absolute', top: 10, right: 10, background: avail === 'In stock' ? 'oklch(1 0 0 / 0.9)' : avail === 'Low stock' ? 'var(--warn-bg)' : 'var(--danger-bg)', color: avail === 'In stock' ? 'var(--ok)' : avail === 'Low stock' ? 'var(--amber-deep)' : 'var(--danger)' }}>{avail}</span>
      </div>
      <div style={{ padding: '13px 14px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.2 }}>{rpSkuDisplayName(sku)}</div>
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 3 }}>{sku.pack || 'Case'} · {rpSkuExternalIdentity(sku)}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 8 }}>
          <span className="disp" style={{ fontSize: 20 }}>{RP.money2(price)}</span><span style={{ fontSize: 11.5, color: 'var(--muted)' }}>/ case</span>
        </div>
        <div style={{ marginTop: 12 }}>
          {qty === 0 ? (
            <button className="btn btn-primary" style={{ width: '100%', opacity: disabled ? 0.5 : 1 }} disabled={disabled} onClick={() => { const v = Math.min(1, availableCases == null ? 1 : availableCases); setQty(v); onAdd && onAdd(v); }}>{disabled ? 'Out of stock' : <><Icon name="plus" size={15} />Add</>}</button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--amber)', borderRadius: 8, overflow: 'hidden' }}>
              <button className="btn btn-ghost" style={{ flex: 1, borderRadius: 0, color: 'var(--ink)' }} onClick={() => { const v = Math.max(0, qty - 1); setQty(v); onAdd && onAdd(v); }}><Icon name="minus" size={15} /></button>
              <span className="mono tnum" style={{ minWidth: 36, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>{qty}</span>
              <button className="btn btn-ghost" disabled={availableCases != null && qty >= availableCases} style={{ flex: 1, borderRadius: 0, color: 'var(--amber-deep)', opacity: availableCases != null && qty >= availableCases ? 0.45 : 1 }} onClick={() => { const v = availableCases == null ? qty + 1 : Math.min(availableCases, qty + 1); setQty(v); onAdd && onAdd(v); }}><Icon name="plus" size={15} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { ClientPortal, ClientPortalMissingAccount, PortalWrap, PortalH, PortalSupport, ProductCard, clientOrders, priceFor, PORTAL_SKUS, PORTAL_MIXERS, PORTAL_AVAIL, portalFulfillmentSource, portalOrderSkus, portalOrderSourceId, portalSkuAvailableCases, portalSkuAvailability, portalSkuStockFilter, portalSkuStockRank, portalSortSkusByStock });


/* ===== client_portal_views.jsx ===== */
/* ============================================================
   RANCH POWER — Client Portal : Shop · Cart · Orders · Account
   ============================================================ */

/* ---- SHOP ---- */
function PortalShop({ c, cart, addToCart, go, cartCount }) {
  const [cat, setCat] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const sourceId = portalOrderSourceId(c);
  // seed from quick-reorder
  useEffect(() => { if (window.__portalSeed) { Object.entries(window.__portalSeed).forEach(([k, v]) => { if (rpSkuCustomerVisible(k)) addToCart(k, v); }); window.__portalSeed = null; } }, []);
  const packs = PORTAL_SKUS();
  const mixers = PORTAL_MIXERS();
  const rawList = cat === 'mixers' ? mixers : cat === 'packs' ? packs : [...packs, ...mixers];
  const sortedList = portalSortSkusByStock(rawList, sourceId);
  const stockCounts = sortedList.reduce((counts, sku) => {
    const key = portalSkuStockFilter(sku, sourceId);
    counts[key] += 1;
    return counts;
  }, { all: sortedList.length, in: 0, out: 0 });
  const list = sortedList.filter(sku => stockFilter === 'all' || portalSkuStockFilter(sku, sourceId) === stockFilter);
  const chipStyle = active => ({ border: '1px solid ' + (active ? 'var(--navy)' : 'var(--line)'), background: active ? 'var(--navy)' : 'var(--surface)', color: active ? '#fff' : 'var(--ink-soft)', borderRadius: 100, padding: '8px 16px', fontSize: 13, fontWeight: 600, minHeight: 36 });
  const cartTotal = rpOrderTotals({ cust: c.id, lines: Object.entries(cart).filter(([sku]) => rpSkuCustomerVisible(sku)).map(([sku, qty]) => ({ sku, qty, price: priceFor(c, rpSkuItem(sku)) })) }).total;
  return (
    <PortalWrap wide>
      <PortalH eyebrow="Catalog" title="Shop" />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['all', 'All Products'], ['packs', '12-Packs'], ['mixers', 'Mixer Packs']].map(([k, l]) => (
            <button key={k} aria-pressed={cat === k} onClick={() => setCat(k)} style={chipStyle(cat === k)}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['all', 'All Stock', stockCounts.all], ['in', 'In stock', stockCounts.in], ['out', 'Out of stock', stockCounts.out]].map(([k, l, count]) => (
            <button key={k} aria-pressed={stockFilter === k} onClick={() => setStockFilter(k)} style={chipStyle(stockFilter === k)}>{l} <span className="mono tnum" style={{ fontSize: 12, opacity: 0.72 }}>{count}</span></button>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16 }}>
        {list.length === 0 ? (
          <div className="card" style={{ gridColumn: '1 / -1', padding: 26, textAlign: 'center', color: 'var(--muted)', borderStyle: 'dashed' }}>No products match this stock filter.</div>
        ) : list.map(s => (
          <ProductCardLive key={s.id} sku={s} c={c} sourceId={sourceId} qty={cart[s.id] || 0} onChange={q => addToCart(s.id, q)} />
        ))}
      </div>
      {cartCount > 0 && (
        <div style={{ position: 'sticky', bottom: 18, marginTop: 26, display: 'flex', justifyContent: 'center', zIndex: 5 }}>
          <button onClick={() => go('cart')} className="btn btn-lg btn-primary" style={{ boxShadow: 'var(--shadow-lg)', paddingLeft: 22, paddingRight: 22 }}>
            <Icon name="package" size={18} />Review Request · {cartCount} {cartCount === 1 ? 'case' : 'cases'} · {RP.money(cartTotal)}
          </button>
        </div>
      )}
    </PortalWrap>
  );
}

/* Product card bound to external cart state */
function ProductCardLive({ sku, c, sourceId, qty, onChange }) {
  const avail = portalSkuAvailability(sku.id, sourceId);
  const availableCases = portalSkuAvailableCases(sku.id, sourceId);
  const visual = rpSkuVisualMeta(sku);
  const price = priceFor(c, sku);
  const disabled = avail === 'Out of stock' || avail === 'Backorder';
  const plusBlocked = availableCases != null && qty >= availableCases;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid ' + (qty > 0 ? 'var(--amber)' : 'var(--line)'), borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: `linear-gradient(160deg, ${visual.color} 0%, oklch(from ${visual.color} calc(l - 0.12) c h) 100%)`, padding: '18px 0 14px', display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <img src={RP.can(visual.flavorId || rpSkuVisualFlavor(sku))} alt={sku.name} style={{ height: 104, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 6px 12px rgba(20,30,40,.35))' }} />
        <span className="pill" style={{ position: 'absolute', top: 10, right: 10, background: avail === 'In stock' ? 'oklch(1 0 0 / 0.92)' : avail === 'Low stock' ? 'var(--warn-bg)' : 'var(--danger-bg)', color: avail === 'In stock' ? 'var(--ok)' : avail === 'Low stock' ? 'var(--amber-deep)' : 'var(--danger)' }}>{avail}</span>
        {sku.pack === 'Mixer 12' && <span className="pill" style={{ position: 'absolute', top: 10, left: 10, background: 'oklch(0.24 0.035 235 / 0.55)', color: '#fff' }}>Mixer</span>}
      </div>
      <div style={{ padding: '13px 14px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.2 }}>{rpSkuDisplayName(sku)}</div>
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 3 }}>{sku.pack || 'Case'} · {rpSkuExternalIdentity(sku)}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 8, marginBottom: 12 }}>
          <span className="disp" style={{ fontSize: 20 }}>{RP.money2(price)}</span><span style={{ fontSize: 11.5, color: 'var(--muted)' }}>/ case</span>
        </div>
        <div style={{ marginTop: 'auto' }}>
          {qty === 0 ? (
            <button className="btn btn-primary" style={{ width: '100%', opacity: disabled ? 0.55 : 1 }} disabled={disabled} onClick={() => onChange(Math.min(1, availableCases == null ? 1 : availableCases))}>{disabled ? 'Out of stock' : <><Icon name="plus" size={15} />Add</>}</button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--amber)', borderRadius: 8, overflow: 'hidden' }}>
              <button className="btn btn-ghost" style={{ flex: 1, borderRadius: 0, color: 'var(--ink)' }} onClick={() => onChange(qty - 1)}><Icon name="minus" size={15} /></button>
              <span className="mono tnum" style={{ minWidth: 40, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>{qty}</span>
              <button className="btn btn-ghost" disabled={plusBlocked} style={{ flex: 1, borderRadius: 0, color: 'var(--amber-deep)', opacity: plusBlocked ? 0.45 : 1 }} onClick={() => onChange(availableCases == null ? qty + 1 : Math.min(availableCases, qty + 1))}><Icon name="plus" size={15} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- CART / REQUEST ---- */
const PORTAL_DELIVERY_OPTIONS = [
  { method: 'Ranch Delivery', lead: 2, note: '2 business days, weekdays only' },
  { method: 'Customer Pickup', lead: 1, note: '1 business day, weekdays only' },
  { method: 'Carrier / Freight', lead: 5, note: '5 business days, weekdays only' }
];

function portalDeliveryRule(method) {
  return PORTAL_DELIVERY_OPTIONS.find(option => option.method === method) || PORTAL_DELIVERY_OPTIONS[0];
}

function portalDateInput(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function portalToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function portalDateFromInput(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(parsed.getTime())) return null;
  if (
    parsed.getFullYear() !== Number(match[1]) ||
    parsed.getMonth() !== Number(match[2]) - 1 ||
    parsed.getDate() !== Number(match[3])
  ) return null;
  return parsed;
}

function portalIsWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function portalAddBusinessDays(base, days) {
  const date = new Date(base);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    if (!portalIsWeekend(date)) added += 1;
  }
  return date;
}

function portalDeliveryMinDate(method) {
  return portalDateInput(portalAddBusinessDays(portalToday(), portalDeliveryRule(method).lead));
}

function portalCustomerLocations(c) {
  const rows = Array.isArray(c && c.locations) ? c.locations.filter(location => location && location.active !== false) : [];
  if (rows.length) return rows;
  if (!c) return [];
  return [{ id: `${c.id || 'customer'}-MAIN`, name: c.name || 'Location', address: c.address || '', city: c.city || '', postal: c.postal || '', active: true }];
}

function portalDeliveryAddress(c, location) {
  const loc = location || portalCustomerLocations(c)[0] || {};
  return [loc.address || c.address, loc.city || c.city, loc.postal || c.postal].filter(Boolean).join(', ');
}

function portalDeliveryError(c, method, date, location, timing = 'date') {
  const parsed = portalDateFromInput(date);
  const min = portalDeliveryMinDate(method);
  const loc = location || portalCustomerLocations(c)[0] || {};
  if (timing !== 'asap') {
    if (!parsed) return 'Choose a valid requested delivery date.';
    if (portalIsWeekend(parsed)) return 'Requested delivery date must be a weekday.';
    if (date < min) return `${method} is available starting ${min}.`;
  }
  if (method !== 'Customer Pickup' && !String(loc.address || c.address || '').trim()) return `${method} needs a shipping address on your account.`;
  return '';
}

function PortalCart({ c, cart, addToCart, setCart, go, onSubmit }) {
  const initialPortalDeliveryMethod = typeof rpCustomerDefaultDeliveryMethod === 'function' ? rpCustomerDefaultDeliveryMethod(c) : 'Ranch Delivery';
  const [method, setMethod] = useState(initialPortalDeliveryMethod);
  const [deliveryTiming, setDeliveryTiming] = useState('date');
  const [date, setDate] = useState(() => portalDeliveryMinDate(initialPortalDeliveryMethod));
  const [locationId, setLocationId] = useState(() => (portalCustomerLocations(c)[0] || {}).id || '');
  const [po, setPo] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitOrderId] = useState(() => window.crypto && typeof window.crypto.randomUUID === 'function'
    ? `SO-${window.crypto.randomUUID().replace(/-/g, '').slice(0, 20).toUpperCase()}`
    : `SO-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`);
  const [addressOverride, setAddressOverride] = useState(null);
  const [addressCaptureOpen, setAddressCaptureOpen] = useState(false);
  const [addressDraft, setAddressDraft] = useState({ address: '', city: '', postal: '' });
  const [addressBusy, setAddressBusy] = useState(false);
  const [addressError, setAddressError] = useState('');
  const lines = Object.entries(cart).filter(([k, v]) => v > 0 && rpSkuCustomerVisible(k));
  const sourceId = portalOrderSourceId(c);
  const portalSource = portalFulfillmentSource(c);
  const sourceLocation = RP.loc(sourceId) || { name: (portalSource && portalSource.sourceName) || sourceId || 'Ranch Power warehouse' };
  const portalCustomer = addressOverride && addressOverride.shippingAddress ? {
    ...c,
    address: addressOverride.shippingAddress.address || c.address,
    city: addressOverride.shippingAddress.city || c.city,
    postal: addressOverride.shippingAddress.postal || c.postal,
    shippingAddress: addressOverride.shippingAddress
  } : c;
  const baseCustomerLocations = portalCustomerLocations(portalCustomer);
  const customerLocations = addressOverride && addressOverride.location && addressOverride.location.id
    ? [addressOverride.location, ...baseCustomerLocations.filter(location => location && location.id !== addressOverride.location.id)]
    : baseCustomerLocations;
  const stockIssues = lines.map(([sku, qty]) => {
    const available = portalSkuAvailableCases(sku, sourceId);
    return available != null && qty > available ? { sku, qty, available } : null;
  }).filter(Boolean);
  const stockError = stockIssues.length
    ? `${rpSkuDisplayName(rpSkuItem(stockIssues[0].sku) || stockIssues[0].sku)} has only ${stockIssues[0].available} cases available from ${sourceLocation.name}.`
    : '';
  const estimate = rpOrderTotals({ cust: c.id, lines: lines.map(([sku, qty]) => ({ sku, qty, price: priceFor(c, rpSkuItem(sku)) })) });
  const sub = estimate.sub;
  const containerFee = estimate.containerFee;
  const tax = estimate.tax;
  const cases = estimate.cases;
  const minDeliveryDate = portalDeliveryMinDate(method);
  const selectedLocation = customerLocations.find(location => location.id === locationId) || customerLocations[0] || null;
  const selectedLocationId = selectedLocation?.id || '';
  const deliveryAddress = portalDeliveryAddress(portalCustomer, selectedLocation);
  const deliveryAddressMissing = Boolean(method !== 'Customer Pickup' && !String((selectedLocation && selectedLocation.address) || portalCustomer.address || '').trim());
  const deliveryError = portalDeliveryError(portalCustomer, method, date, selectedLocation, deliveryTiming);
  const requestedDelivery = deliveryTiming === 'asap' ? 'ASAP' : date;

  useEffect(() => {
    if (deliveryTiming === 'asap') return;
    const nextMin = portalDeliveryMinDate(method);
    if (!date || date < nextMin || (portalDateFromInput(date) && portalIsWeekend(portalDateFromInput(date)))) {
      setDate(nextMin);
    }
  }, [method, deliveryTiming]);

  useEffect(() => {
    if (!customerLocations.some(location => location.id === locationId)) {
      setLocationId((customerLocations[0] || {}).id || '');
    }
  }, [c && c.id, addressOverride && addressOverride.location && addressOverride.location.id]);

  if (lines.length === 0) {
    return <PortalWrap><PortalH eyebrow="Request" title="Your Cart" />
      <div style={{ textAlign: 'center', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <span style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--surface-2)', color: 'var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="package" size={30} /></span>
        <div><div style={{ fontSize: 17, fontWeight: 700 }}>Your cart is empty</div><div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 4 }}>Add products to request your next order.</div></div>
        <button className="btn btn-lg btn-primary" onClick={() => go('shop')}>Browse Catalog</button>
      </div>
    </PortalWrap>;
  }

  const submit = async () => {
    if (submitting) return;
    if (deliveryError) {
      setSubmitError(deliveryError);
      return;
    }
    if (stockError) {
      setSubmitError(stockError);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    const id = submitOrderId;
    const orderLines = lines.map(([sku, q]) => ({ sku, qty: q }));
    let external = null;
    let response = null;

    try {
      response = await rpApi('/api/orders/submit', {
        method: 'POST',
        body: JSON.stringify({
          id: submitOrderId,
          customerId: c.id,
          customerEmail: c.email || undefined,
          customerLocationId: selectedLocationId || undefined,
          deliveryDate: requestedDelivery,
          deliveryMethod: method,
          po,
          note,
          sourceLocationId: sourceId,
          lineItems: lines.map(([sku, q]) => {
            const item = rpSkuItem(sku);
            return {
              sku,
              title: item.name,
              quantity: q,
              originalUnitPrice: priceFor(c, item).toFixed(2)
            };
          })
        })
      });
      external = response.draftOrder || response;
    } catch (error) {
      if (!(window.RP_AUTH && window.RP_AUTH.demo)) {
        setSubmitError(error.message || 'Could not submit this order request.');
        setSubmitting(false);
        return;
      }
      external = { mode: 'local-demo' };
    }

    const status = (response && response.order && response.order.status) || (external && external.id ? 'Submitted' : 'Requested');
    const o = { id, cust: c.id, customerLocationId: selectedLocationId || null, territory: c.territory, rep: c.rep, source: sourceId, delivery: method, status, date: requestedDelivery, deliveryPreference: deliveryTiming === 'asap' ? 'ASAP' : 'Scheduled', created: typeof rpNowLocalInput === 'function' ? rpNowLocalInput(c) : new Date().toISOString().slice(0, 16).replace('T', ' '), reason: deliveryTiming === 'asap' ? 'Delivery requested: ASAP.\nCustomer portal request' : 'Customer portal request', po, note, external, lines: orderLines };
    const savedOrder = rpUpsertOrder(rpOrderFromApi(response, o));
    await rpRefreshAfterMutation(`Order request ${savedOrder.id} submitted`);
    const syncedOrder = RP.ORDERS.find(x => x.id === savedOrder.id) || savedOrder;
    toast(`Order request ${syncedOrder.id} submitted`, 'ok');
    setCart({});
    setSubmitting(false);
    onSubmit(syncedOrder);
  };

  const openPortalAddressCapture = () => {
    setAddressDraft({
      address: String((selectedLocation && selectedLocation.address) || portalCustomer.address || '').trim(),
      city: String((selectedLocation && selectedLocation.city) || portalCustomer.city || '').trim(),
      postal: String((selectedLocation && selectedLocation.postal) || portalCustomer.postal || '').trim()
    });
    setAddressError('');
    setSubmitError('');
    setAddressCaptureOpen(true);
  };

  const savePortalAddress = async () => {
    if (addressBusy) return;
    const address = String(addressDraft.address || '').trim().replace(/\s+/g, ' ');
    const city = String(addressDraft.city || '').trim().replace(/\s+/g, ' ');
    const postal = String(addressDraft.postal || '').trim().toUpperCase();
    if (!address) {
      setAddressError('Street address is required for delivery.');
      return;
    }
    setAddressBusy(true);
    setAddressError('');
    try {
      const response = await rpApi('/api/customers/portal-address', {
        method: 'POST',
        body: JSON.stringify({
          address,
          city,
          customerId: c.id,
          customerLocationId: selectedLocationId || undefined,
          postal
        })
      });
      const shippingAddress = response.shippingAddress || { address, city, postal };
      const savedLocation = response.location || {
        ...(selectedLocation || {}),
        id: selectedLocationId || `${c.id}-MAIN`,
        name: (selectedLocation && selectedLocation.name) || c.name || 'Location',
        address: shippingAddress.address || address,
        city: shippingAddress.city || city,
        postal: shippingAddress.postal || postal,
        type: 'Ship-to',
        active: true
      };
      setAddressOverride({ shippingAddress, location: savedLocation });
      if (savedLocation.id) setLocationId(savedLocation.id);
      const mergedLocations = [savedLocation, ...portalCustomerLocations(c).filter(location => location && location.id !== savedLocation.id)];
      RP.upsertCustomer({
        ...c,
        address: shippingAddress.address || address,
        city: shippingAddress.city || city,
        postal: shippingAddress.postal || postal,
        shippingAddress,
        locations: mergedLocations
      });
      await rpRefreshAfterMutation('Delivery address saved');
      setAddressCaptureOpen(false);
      setSubmitError('');
      toast('Delivery address saved', 'ok');
    } catch (error) {
      setAddressError(error && error.message ? error.message : 'Could not save the delivery address.');
    } finally {
      setAddressBusy(false);
    }
  };

  return (
    <PortalWrap>
      <PortalH eyebrow="Request" title="Review Order" sub="Submit a request — your rep confirms pricing & delivery." />
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          {lines.map(([k, v], i) => { const s = rpSkuItem(k); const f = rpSkuVisualFlavor(s); return (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: 14, borderBottom: i < lines.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
              <CanThumb flavorId={f} h={48} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{rpSkuDisplayName(s || k)}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{RP.money2(priceFor(c, s))} / case</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                <button className="btn btn-ghost" style={{ padding: '6px 9px', borderRadius: 0 }} onClick={() => addToCart(k, v - 1)}><Icon name="minus" size={14} /></button>
                <span className="mono tnum" style={{ width: 34, textAlign: 'center', fontWeight: 600 }}>{v}</span>
                <button className="btn btn-ghost" disabled={portalSkuAvailableCases(k, sourceId) != null && v >= portalSkuAvailableCases(k, sourceId)} style={{ padding: '6px 9px', borderRadius: 0, color: 'var(--amber-deep)', opacity: portalSkuAvailableCases(k, sourceId) != null && v >= portalSkuAvailableCases(k, sourceId) ? 0.45 : 1 }} onClick={() => addToCart(k, v + 1)}><Icon name="plus" size={14} /></button>
              </div>
              <div style={{ width: 78, textAlign: 'right', fontWeight: 600, fontSize: 14 }}>{RP.money2(priceFor(c, s) * v)}</div>
              <button onClick={() => addToCart(k, 0)} style={{ border: 'none', background: 'none', color: 'var(--faint)', padding: 4 }}><Icon name="x" size={16} /></button>
            </div>
          ); })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 18 }}>
            <SectionTitle>Order Details</SectionTitle>
            <label className="field-label">Delivery method</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 14 }}>
              {PORTAL_DELIVERY_OPTIONS.map(option => (
                <button key={option.method} type="button" onClick={() => { setMethod(option.method); setSubmitError(''); }} style={{ border: '1px solid ' + (method === option.method ? 'var(--amber)' : 'var(--line)'), background: method === option.method ? 'var(--amber-soft)' : 'var(--surface)', color: method === option.method ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 8, padding: '9px 10px', fontSize: 12.5, fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                  {option.method}
                </button>
              ))}
            </div>
            <label className="field-label">Requested delivery</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginBottom: 8 }}>
              {[{ value: 'asap', label: 'ASAP' }, { value: 'date', label: 'Pick date' }].map(option => (
                <button key={option.value} type="button" onClick={() => setDeliveryTiming(option.value)} style={{ border: '1px solid ' + (deliveryTiming === option.value ? 'var(--amber)' : 'var(--line)'), background: deliveryTiming === option.value ? 'var(--amber-soft)' : 'var(--surface)', color: deliveryTiming === option.value ? 'var(--amber-deep)' : 'var(--ink-soft)', borderRadius: 8, padding: '9px 10px', fontSize: 12.5, fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                  {option.label}
                </button>
              ))}
            </div>
            {deliveryTiming === 'date' && <input className="field" type="date" min={minDeliveryDate} value={date} onChange={e => setDate(e.target.value)} style={{ marginBottom: 8 }} />}
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line-2)', borderRadius: 9, padding: '10px 11px', marginBottom: 14, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4 }}>
              <div><b style={{ color: 'var(--ink)' }}>{deliveryTiming === 'asap' ? 'ASAP requested - timing confirmed by your rep' : portalDeliveryRule(method).note}</b></div>
              <div>{method === 'Customer Pickup' ? `Pickup from ${sourceLocation.name} once confirmed.` : `Ship to ${deliveryAddress || 'address needed'}.`}</div>
            </div>
            {deliveryAddressMissing && <div style={{ background: 'var(--warn-bg)', border: '1px solid oklch(0.72 0.16 68 / .28)', borderRadius: 10, padding: '11px 12px', marginBottom: 14, color: 'var(--amber-deep)', fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Icon name="alert" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{method} needs a shipping address. Add one here and it will be saved to your account.</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button type="button" className="btn btn-sm" onClick={openPortalAddressCapture}>Add address</button>
                <button type="button" className="btn btn-sm" onClick={() => { setMethod('Customer Pickup'); setSubmitError(''); setAddressCaptureOpen(false); }}>Use pickup</button>
              </div>
              {addressCaptureOpen && <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface)', color: 'var(--ink)' }}>
                <label className="field-label">Street address</label>
                <input className="field" value={addressDraft.address} onChange={e => setAddressDraft(current => ({ ...current, address: e.target.value }))} placeholder="Street address" style={{ marginBottom: 10 }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,.75fr)', gap: 9, marginBottom: 10 }}>
                  <div>
                    <label className="field-label">City</label>
                    <input className="field" value={addressDraft.city} onChange={e => setAddressDraft(current => ({ ...current, city: e.target.value }))} placeholder={c.city || 'City'} />
                  </div>
                  <div>
                    <label className="field-label">Postal</label>
                    <input className="field" value={addressDraft.postal} onChange={e => setAddressDraft(current => ({ ...current, postal: e.target.value.toUpperCase() }))} placeholder="V8B 0A1" />
                  </div>
                </div>
                {addressError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 8, padding: '9px 10px', fontSize: 12, marginBottom: 10 }}>{addressError}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn" disabled={addressBusy} style={{ flex: 1 }} onClick={() => { setAddressCaptureOpen(false); setAddressError(''); }}>Cancel</button>
                  <button type="button" className="btn btn-primary" disabled={addressBusy} style={{ flex: 1.2, opacity: addressBusy ? 0.65 : 1 }} onClick={savePortalAddress}><Icon name="check" size={15} />{addressBusy ? 'Saving...' : 'Save address'}</button>
                </div>
              </div>}
            </div>}
            {customerLocations.length > 1 && <>
              <label className="field-label">Ship-to location</label>
              <select className="field" value={selectedLocationId} onChange={e => setLocationId(e.target.value)} style={{ marginBottom: 14 }}>
                {customerLocations.map(location => <option key={location.id} value={location.id}>{location.name || 'Location'}{location.city ? ` - ${location.city}` : ''}</option>)}
              </select>
            </>}
            <label className="field-label">PO number <span style={{ color: 'var(--faint)', fontWeight: 400 }}>{c && c.requiresPo ? '(required before invoicing)' : '(optional)'}</span></label>
            <input className="field" placeholder="—" value={po} onChange={e => setPo(e.target.value)} style={{ marginBottom: 14 }} />
            <label className="field-label">Notes</label>
            <textarea className="field" rows="2" placeholder="Delivery instructions…" value={note} onChange={e => setNote(e.target.value)} style={{ resize: 'none' }} />
          </div>
          <div className="card" style={{ padding: 18 }}>
            <PricingBreakdown order={{ cust: c.id, lines: lines.map(([sku, qty]) => ({ sku, qty, price: priceFor(c, rpSkuItem(sku)) })) }} totals={estimate} estimated />
            {(submitError || deliveryError || stockError) && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, marginTop: 14 }}>{submitError || deliveryError || stockError}</div>}
            <button className="btn btn-lg btn-primary" disabled={submitting || !!deliveryError || !!stockError} style={{ width: '100%', marginTop: 16, opacity: submitting || deliveryError || stockError ? 0.65 : 1 }} onClick={submit}><Icon name="check" size={17} />{submitting ? 'Submitting...' : 'Submit Order Request'}</button>
            <div style={{ fontSize: 11.5, color: 'var(--faint)', textAlign: 'center', marginTop: 10, lineHeight: 1.4 }}>Final pricing confirmed by your rep. {c.terms} terms apply. {RP_CREDIT_CARD_TERMS_NOTE}</div>
          </div>
        </div>
      </div>
    </PortalWrap>
  );
}

/* ---- CONFIRMED ---- */
function PortalConfirmed({ c, order, go }) {
  const t = order ? orderTotals(order) : { total: 0, cases: 0 };
  return (
    <PortalWrap>
      <div style={{ textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
        <div style={{ width: 84, height: 84, borderRadius: '50%', background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'rp-pop .4s ease' }}><Icon name="check" size={44} /></div>
        <div>
          <h1 className="disp" style={{ margin: 0, fontSize: 30, textTransform: 'uppercase' }}>Request Received</h1>
          <div style={{ color: 'var(--muted)', fontSize: 14.5, marginTop: 6, maxWidth: 420 }}>Thanks, {c.buyer}. {order ? order.id : ''} is with your rep {c.rep} — you'll get a confirmation once pricing & delivery are locked.</div>
        </div>
        {order && (
          <div className="card" style={{ padding: 18, width: '100%', maxWidth: 420, textAlign: 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}><span className="mono" style={{ fontWeight: 600 }}>{order.id}</span><Pill status={order.status || 'Requested'} /></div>
            <div style={{ display: 'flex', gap: 5, marginBottom: 14 }}>{order.lines.map((l, i) => <FlavorChip key={i} sku={rpSkuItem(l.sku) || l.sku} size={26} />)}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}><span style={{ color: 'var(--muted)' }}>{t.cases} cases · {order.delivery || 'Delivery'} · ETA {rpOrderDateLabel(order)}</span><span className="disp" style={{ fontSize: 18 }}>{RP.money(t.total)}</span></div>
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line)', color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.45 }}>{c.terms} terms apply. {RP_CREDIT_CARD_TERMS_NOTE}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-lg" onClick={() => go('orders')}>Track Order</button>
          <button className="btn btn-lg btn-primary" onClick={() => go('shop')}>Keep Shopping</button>
        </div>
      </div>
    </PortalWrap>
  );
}

/* ---- MY ORDERS ---- */
function portalInvoiceMethodLabel(code) {
  const labels = { account_terms: 'Account terms', bank_transfer: 'Bank transfer', e_transfer: 'e-Transfer', cheque: 'Cheque', credit_card: 'Credit card', shopify_checkout: 'Shopify checkout', shopify_pos: 'Shopify POS' };
  return labels[code] || code || 'Invoice';
}

function portalOpenInvoice(order, event) {
  event && event.stopPropagation && event.stopPropagation();
  rpOpenInvoicePdf(order.id).catch(err => toast(err && err.message ? err.message : 'Could not open invoice PDF.', 'danger'));
}

function PortalOrders({ c, go, live = false, orders: providedOrders, page }) {
  const orders = Array.isArray(providedOrders) ? providedOrders : clientOrders(c);
  return (
    <PortalWrap>
      <PortalH eyebrow="History" title="My Orders" right={<button className="btn btn-primary" onClick={() => go('shop')}><Icon name="plus" size={15} />New Order</button>} />
      {live && page && page.initialLoading ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)' }}><Icon name="refresh" size={20} /><div style={{ marginTop: 8, color: 'var(--ink)', fontWeight: 700 }}>Loading your complete order history...</div></div>
      ) : live && page && !page.loaded && page.error ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--danger)' }}><div style={{ fontWeight: 700 }}>{page.error}</div><button type="button" className="btn btn-sm" onClick={page.retry} style={{ marginTop: 10 }}>Try Again</button></div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)' }}>No orders yet. <button className="btn btn-ghost btn-sm" style={{ color: 'var(--amber-deep)' }} onClick={() => go('shop')}>Place your first order</button></div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {orders.map(o => { const t = orderTotals(o); return (
              <button key={o.id} onClick={() => { if (live) rpUpsertOrder(o); go('order', o.id); }} style={{ textAlign: 'left', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><span className="mono nowrap" style={{ fontWeight: 600, fontSize: 13.5 }}>{o.id}</span><Pill status={o.status} />{o.invoiceNumber && <Pill status="Paid">{o.invoiceNumber}</Pill>}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>Ordered {String(o.created || '').slice(0, 10)} · ETA {rpOrderDateLabel(o)}{o.invoiceDueDate ? ` · Invoice due ${o.invoiceDueDate}` : ''}</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>{(o.lines || []).slice(0, 6).map((l, i) => <FlavorChip key={i} sku={rpSkuItem(l.sku) || l.sku} size={22} />)}</div>
                <div style={{ textAlign: 'right' }}><div className="disp" style={{ fontSize: 19 }}>{RP.money(t.total)}</div><div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{t.cases} cases</div></div>
                <Icon name="chevR" size={18} style={{ color: 'var(--faint)' }} />
              </button>
            ); })}
          </div>
          {live && page && page.loaded && <div style={{ marginTop: 12, border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}><RpServerLoadMore {...page} label="orders" onLoadMore={page.loadMore} onRetry={page.loadMore} /></div>}
        </>
      )}
    </PortalWrap>
  );
}

/* ---- ORDER DETAIL (client view) ---- */
const PORTAL_STAGES = ['Requested', 'Approved', 'Allocated', 'Picked', 'Dispatched', 'Delivered'];
function PortalOrderDetail({ c, id, go }) {
  const exactOrder = useRpExactOrder({ orderId: id });
  const o = exactOrder.order;
  if (exactOrder.loading) return <PortalWrap><div data-exact-order-loading="true" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}><Icon name="refresh" size={20} /><div style={{ color: 'var(--ink)', fontWeight: 700, marginTop: 8 }}>Loading order {id}...</div></div></PortalWrap>;
  if (exactOrder.error) return <PortalWrap><div data-exact-order-error="true" role="alert" style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}><div style={{ fontWeight: 700 }}>Order could not load</div><div style={{ marginTop: 6 }}>{exactOrder.error}</div><button type="button" className="btn btn-sm" onClick={exactOrder.retry} style={{ marginTop: 12 }}>Try Again</button></div></PortalWrap>;
  if (!o || o.cust !== c.id) return <PortalWrap><div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Order not found.</div></PortalWrap>;
  const t = orderTotals(o);
  const timeline = rpOrderTimeline(o);
  const fulfillmentStage = rpOrderFulfillmentStage(o.status, PORTAL_STAGES);
  const portalStage = fulfillmentStage === 'Submitted' ? 'Requested' : fulfillmentStage;
  const cur = Math.max(0, PORTAL_STAGES.indexOf(portalStage));
  const reorder = () => { const cart = {}; o.lines.forEach(l => { if (rpSkuCustomerVisible(l.sku)) cart[l.sku] = l.qty; }); window.__portalSeed = cart; go('shop'); };
  return (
    <PortalWrap>
      <button className="btn btn-ghost btn-sm" onClick={() => go('orders')} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />My Orders</button>
      <PortalH eyebrow={`Ordered ${String(o.created || '').slice(0, 10)}`} title={o.id}
        right={<><Pill status={o.status} /><button className="btn btn-sm" onClick={reorder} style={{ marginLeft: 10 }}><Icon name="refresh" size={15} />Reorder</button></>} />

      {/* tracker */}
      <div className="card rp-stepper" style={{ padding: '22px 24px', marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 13, left: 14, right: 14, height: 2, background: 'var(--line)' }} />
          <div style={{ position: 'absolute', top: 13, left: 14, height: 2, background: 'var(--ok)', width: `calc(${(cur / (PORTAL_STAGES.length - 1)) * 100}% - 28px)`, transition: 'width .4s' }} />
          {PORTAL_STAGES.map((s, i) => { const done = i <= cur; return (
            <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, position: 'relative', zIndex: 1, flex: 1 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: done ? 'var(--ok)' : 'var(--surface)', border: '2px solid ' + (done ? 'var(--ok)' : 'var(--line)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{done ? <Icon name="check" size={15} /> : <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--faint)' }} />}</div>
              <span style={{ fontSize: 11.5, fontWeight: i === cur ? 700 : 500, color: i === cur ? 'var(--ink)' : 'var(--muted)' }}>{s}</span>
            </div>
          ); })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 18 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          {o.lines.map((l, i) => { const s = rpSkuItem(l.sku); const f = rpSkuVisualFlavor(s); return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: 14, borderBottom: i < o.lines.length - 1 ? '1px solid var(--line-2)' : 'none' }}>
              <FlavorChip flavorId={f} size={30} />
              <div style={{ flex: 1 }}><div style={{ fontWeight: 500, fontSize: 14 }}>{rpSkuDisplayName(s || l.sku)}</div><div className="mono" style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 2 }}>{s && s.pack || 'Case'} · {rpSkuExternalIdentity(s || l.sku)}</div></div>
              <span className="mono" style={{ fontWeight: 600 }}>{l.qty} cs</span>
              <span style={{ width: 80, textAlign: 'right', fontWeight: 600 }}>{RP.money2(priceFor(c, s) * l.qty)}</span>
            </div>
          ); })}
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 18px', background: 'var(--surface-2)', borderTop: '1px solid var(--line)' }}>
            <div style={{ width: 220, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13.5 }}>
              <PricingBreakdown order={o} totals={t} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card" style={{ padding: 18, height: 'fit-content' }}>
            <SectionTitle>Delivery</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5 }}>
              <Row k="Status" v={<Pill status={o.status} />} />
              <Row k="Method" v={o.delivery} />
              <Row k="Requested" v={<span className="mono">{rpOrderDateLabel(o)}</span>} />
              <Row k="Ship to" v={c.city} />
              <Row k="Your rep" v={c.rep} />
              {o.po && <Row k="PO #" v={o.po} />}
            </div>
          </div>
          {o.invoiceNumber && (
            <div className="card" style={{ padding: 18, height: 'fit-content' }}>
              <SectionTitle right={<Pill status={o.invoiceStatus || 'Issued'}>{o.invoiceStatus || 'Issued'}</Pill>}>Invoice</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5, marginBottom: 13 }}>
                <Row k="Number" v={<span className="mono">{o.invoiceNumber}</span>} />
                <Row k="Payment" v={portalInvoiceMethodLabel(o.invoicePaymentMethod)} />
                {o.invoiceDueDate && <Row k="Due" v={<span className="mono">{o.invoiceDueDate}</span>} />}
                {o.creditCardFeeTotal > 0 && <Row k="Card fee" v={RP.money2(o.creditCardFeeTotal)} />}
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 11.5, lineHeight: 1.45, marginBottom: 13 }}>{RP_CREDIT_CARD_TERMS_NOTE}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-primary" onClick={() => go('invoice', o.invoiceNumber || o.id)}><Icon name="receipt" size={14} />{o.invoiceStatus === 'Paid' || o.financialStatus === 'Paid' ? 'View Receipt' : 'View Invoice'}</button>
                <button className="btn btn-sm" onClick={event => portalOpenInvoice(o, event)}><Icon name="doc" size={14} />PDF</button>
              </div>
            </div>
          )}
          <div className="card" style={{ padding: 18, height: 'fit-content' }}>
            <SectionTitle>Timeline</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {timeline.map((a, i, arr) => (
                <div key={a.key} style={{ display: 'flex', gap: 11 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: a.tone === 'danger' ? 'var(--danger)' : a.tone === 'warn' ? 'var(--amber)' : i === arr.length - 1 ? 'var(--amber)' : 'var(--ok)', marginTop: 5 }} />
                    {i < arr.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--line-2)' }} />}
                  </div>
                  <div style={{ paddingBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</div>
                    {a.detail && <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>{a.detail}</div>}
                    <div className="mono" style={{ fontSize: 11, color: 'var(--faint)', marginTop: 3 }}>{a.when}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalWrap>
  );
}

Object.assign(window, { PortalShop, ProductCardLive, PortalCart, PortalConfirmed, PortalOrders, PortalOrderDetail });


/* ===== client_portal_invoices.jsx ===== */
/* ============================================================
   RANCH POWER - Client portal invoices
   ============================================================ */

function portalInvoices(c) {
  return RP.invoicesForCust(c.id).filter(inv => RP.invoiceStatus(inv) !== 'Draft');
}

function PortalInvoices({ c, go }) {
  const live = typeof rpHasLiveAuth === 'function' && rpHasLiveAuth();
  const page = useRpServerPagedRows({ mode: 'invoices', limit: 50, customerId: c.id });
  const invs = live
    ? page.items.map(item => typeof rpInvoiceFromOrder === 'function' ? rpInvoiceFromOrder(item.order, item.customer) : null).filter(inv => inv && inv.cust === c.id)
    : portalInvoices(c);
  const openInvoice = inv => {
    if (live && inv && inv.sourceOrder) rpUpsertOrder(inv.sourceOrder);
    go('invoice', inv.id);
  };
  const outstanding = invs.reduce((sum, inv) => {
    const st = RP.invoiceStatus(inv);
    return st === 'Paid' ? sum : sum + RP.invoiceTotals(inv).balance;
  }, 0);
  const overdue = invs.filter(inv => RP.invoiceStatus(inv) === 'Overdue');
  return (
    <PortalWrap>
      <PortalH eyebrow="Billing" title="Invoices" sub={`Open balances and invoice history for ${c.name}`} />
      {live && page.initialLoading ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)' }}><Icon name="refresh" size={20} /><div style={{ marginTop: 8, color: 'var(--ink)', fontWeight: 700 }}>Loading your complete invoice history...</div></div>
      ) : live && !page.loaded && page.error ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--danger)' }}><div style={{ fontWeight: 700 }}>{page.error}</div><button type="button" className="btn btn-sm" onClick={page.retry} style={{ marginTop: 10 }}>Try Again</button></div>
      ) : invs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)' }}>No invoices yet.</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 22 }}>
            <div className="card" style={{ padding: '16px 18px' }}>
              <div className="eyebrow">{live && page.page.hasMore ? 'Loaded Balance' : 'Outstanding Balance'}</div>
              <div className="disp" style={{ fontSize: 30, marginTop: 6, color: outstanding > 0.005 ? 'var(--ink)' : 'var(--ok)' }}>{RP.money2(outstanding)}</div>
            </div>
            <div className="card" style={{ padding: '16px 18px' }}>
              <div className="eyebrow">{live && page.page.hasMore ? 'Loaded Overdue' : 'Overdue'}</div>
              <div className="disp" style={{ fontSize: 30, marginTop: 6, color: overdue.length ? 'var(--danger)' : 'var(--ink)' }}>{RP.money2(overdue.reduce((sum, inv) => sum + RP.invoiceTotals(inv).balance, 0))}</div>
            </div>
            <div className="card" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.45 }}><Icon name="card" size={16} style={{ flexShrink: 0, marginTop: 1 }} /><span>Credit-card invoices use authorized cards saved securely with Stripe. {RP_CREDIT_CARD_TERMS_NOTE}</span></div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {invs.map(inv => {
              const t = RP.invoiceTotals(inv);
              const st = RP.invoiceStatus(inv);
              const over = st === 'Overdue';
              const paid = st === 'Paid';
              return (
                <div key={inv.id} style={{ background: 'var(--surface)', border: '1px solid ' + (over ? 'oklch(0.57 0.16 27 / 0.4)' : 'var(--line)'), borderRadius: 12, padding: 16, boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <button onClick={() => openInvoice(inv)} style={{ flex: 1, minWidth: 180, textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="mono" style={{ fontWeight: 650, fontSize: 13.5 }}>{inv.id}</span><Pill status={st}>{invoiceStatusLabel(st)}</Pill></div>
                    <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>Issued {inv.issued} - due {inv.due}{over ? ` - ${RP.invDaysOverdue(inv)} days late` : ''}</div>
                  </button>
                  <div style={{ textAlign: 'right' }}>
                    <div className="disp" style={{ fontSize: 20, color: over ? 'var(--danger)' : 'var(--ink)' }}>{paid ? RP.money2(t.total) : RP.money2(t.balance)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--faint)' }}>{paid ? 'paid' : 'balance due'}</div>
                  </div>
                  {paid ? (
                    <button className="btn" onClick={() => openInvoice(inv)}><Icon name="receipt" size={15} />Receipt</button>
                  ) : (
                    <button className="btn btn-primary" onClick={() => openInvoice(inv)}><Icon name="receipt" size={15} />View</button>
                  )}
                </div>
              );
            })}
          </div>
          {live && page.loaded && <div style={{ marginTop: 12, border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden' }}><RpServerLoadMore {...page} label="invoices" onLoadMore={page.loadMore} onRetry={page.loadMore} /></div>}
        </>
      )}
    </PortalWrap>
  );
}

function PortalInvoiceDetail({ c, id, go }) {
  const exactOrder = useRpExactOrder({ invoiceId: id });
  const isNarrow = useIsNarrow(860);
  const inv = RP.invoice(id);
  if (exactOrder.loading) return <PortalWrap><div data-exact-invoice-loading="true" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}><Icon name="refresh" size={20} /><div style={{ color: 'var(--ink)', fontWeight: 700, marginTop: 8 }}>Loading invoice {id}...</div></div></PortalWrap>;
  if (exactOrder.error) return <PortalWrap><div data-exact-invoice-error="true" role="alert" style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}><div style={{ fontWeight: 700 }}>Invoice could not load</div><div style={{ marginTop: 6 }}>{exactOrder.error}</div><button type="button" className="btn btn-sm" onClick={exactOrder.retry} style={{ marginTop: 12 }}>Try Again</button></div></PortalWrap>;
  if (!inv || inv.cust !== c.id) return <PortalWrap><div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Invoice not found.</div></PortalWrap>;
  const t = RP.invoiceTotals(inv);
  const st = RP.invoiceStatus(inv);
  const paid = st === 'Paid';
  const download = async () => {
    try {
      await rpOpenInvoicePdf(inv.order);
    } catch (err) {
      toast(err && err.message ? err.message : 'Could not open invoice PDF.', 'danger');
    }
  };
  return (
    <PortalWrap wide>
      <button className="btn btn-ghost btn-sm" onClick={() => go('invoices')} style={{ marginBottom: 14, paddingLeft: 4 }}><Icon name="arrowL" size={16} />Invoices</button>
      <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'minmax(0, 1.55fr) minmax(280px, 0.85fr)', gap: 18, alignItems: 'start' }}>
        <InvoiceDocument inv={inv} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: isNarrow ? 'static' : 'sticky', top: 18 }}>
          <div className="card" style={{ padding: 20 }}>
            {paid ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, color: 'var(--ok)' }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--ok-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={24} /></div>
                  <div><div className="disp" style={{ fontSize: 22 }}>Paid</div><div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Thank you.</div></div>
                </div>
                <button className="btn" style={{ width: '100%', marginTop: 14, justifyContent: 'center' }} onClick={download}><Icon name="download" size={15} />Download Receipt</button>
              </>
            ) : (
              <>
                <div className="eyebrow">{st === 'Overdue' ? 'Overdue Balance' : 'Balance Due'}</div>
                <div className="disp" style={{ fontSize: 36, marginTop: 6, color: st === 'Overdue' ? 'var(--danger)' : 'var(--ink)' }}>{RP.money2(t.balance)}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>Due {inv.due} - {inv.terms}</div>
                <div style={{ marginTop: 16, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 13px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45 }}>Payment method is {rpInvoicePaymentMethodLabel(inv.paymentMethod)}. Ranch Power will process authorized saved-card payments through Stripe; follow the invoice instructions for other methods. {RP_CREDIT_CARD_TERMS_NOTE}</div>
              </>
            )}
          </div>
          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>Invoice</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5 }}>
              <Row k="Number" v={<span className="mono">{inv.id}</span>} />
              <Row k="Order" v={<button onClick={() => go('order', inv.order)} style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--amber-deep)', fontWeight: 750, cursor: 'pointer' }}>{inv.order}</button>} />
              <Row k="Payment" v={rpInvoicePaymentMethodLabel(inv.paymentMethod)} />
              <Row k="Status" v={<Pill status={st}>{invoiceStatusLabel(st)}</Pill>} />
            </div>
            <button className="btn" style={{ width: '100%', marginTop: 14, justifyContent: 'center' }} onClick={download}><Icon name="doc" size={15} />Open PDF</button>
          </div>
          {(inv.payments || []).length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <SectionTitle>Payments</SectionTitle>
              <PaymentHistory inv={inv} />
            </div>
          )}
        </div>
      </div>
    </PortalWrap>
  );
}

Object.assign(window, { PortalInvoices, PortalInvoiceDetail, portalInvoices });


/* ===== client_portal_account.jsx ===== */
/* ============================================================
   RANCH POWER - Client Portal : Account + Security
   ============================================================ */

function PortalAccount({ c, go }) {
  const [, force] = useState(0);
  const live = rpHasLiveAuth();
  const persona = useRpIdentity('customer');
  const authProfile = rpAuthProfile();
  const authDetails = rpAuthDetails();
  const creds = live ? null : (c.creds || (c.creds = RP.genCreds(c, false)));
  const [showPw, setShowPw] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [displayName, setDisplayName] = useState(persona.name || '');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicMessage, setMagicMessage] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestType, setRequestType] = useState('Buyer/contact');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [cardOpen, setCardOpen] = useState(false);
  const fees = (Array.isArray(RP.DEDUCTIONS) ? RP.DEDUCTIONS : []).filter(d => d && d.cust === c.id);
  const savedCard = typeof rpStripeDefaultCard === 'function' ? rpStripeDefaultCard(c) : null;
  useEffect(() => {
    setDisplayName(persona.name || '');
  }, [persona.name]);

  const copy = (text, label) => {
    try { navigator.clipboard && navigator.clipboard.writeText(text); } catch (e) { }
    toast((label || 'Copied') + ' to clipboard', 'info');
  };
  const doReset = () => {
    if (!creds) return;
    RP.resetPassword(c.id);
    setShowPw(true);
    setResetOpen(false);
    force(x => x + 1);
    toast('New password generated and emailed to you', 'ok');
  };
  const saveProfile = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.updateProfile || profileBusy) return;
    setProfileBusy(true);
    setProfileMessage('');
    try {
      await window.RP_ACCOUNT.updateProfile({ display_name: displayName, setup_complete: true });
      setProfileMessage('Profile saved.');
      toast('Profile saved', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not save profile.';
      setProfileMessage(msg);
      toast(msg, 'danger');
    } finally {
      setProfileBusy(false);
    }
  };
  const savePassword = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.updatePassword || passwordBusy) return;
    setPasswordMessage('');
    if (newPassword !== confirmPassword) {
      setPasswordMessage('Passwords do not match.');
      return;
    }
    setPasswordBusy(true);
    try {
      await window.RP_ACCOUNT.updatePassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('Password updated.');
      toast('Password updated', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not update password.';
      setPasswordMessage(msg);
      toast(msg, 'danger');
    } finally {
      setPasswordBusy(false);
    }
  };
  const sendReset = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.sendPasswordResetEmail || passwordBusy) return;
    setPasswordBusy(true);
    setPasswordMessage('');
    try {
      await window.RP_ACCOUNT.sendPasswordResetEmail(persona.email || authProfile.email);
      setPasswordMessage('Reset email sent.');
      toast('Password reset email sent', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not send reset email.';
      setPasswordMessage(msg);
      toast(msg, 'danger');
    } finally {
      setPasswordBusy(false);
    }
  };
  const sendMagic = async () => {
    if (!window.RP_ACCOUNT || !window.RP_ACCOUNT.sendMagicLinkEmail || magicBusy) return;
    setMagicBusy(true);
    setMagicMessage('');
    try {
      await window.RP_ACCOUNT.sendMagicLinkEmail(persona.email || authProfile.email);
      setMagicMessage('Magic link sent.');
      toast('Magic link sent', 'ok');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Could not send magic link.';
      setMagicMessage(msg);
      toast(msg, 'danger');
    } finally {
      setMagicBusy(false);
    }
  };
  const signOut = () => {
    if (window.RP_SIGN_OUT) window.RP_SIGN_OUT();
    else toast('Signed out (demo)', 'info');
  };
  const openChangeRequest = () => {
    if (!live) {
      toast('Your rep has been notified to update account details', 'info');
      return;
    }
    setRequestOpen(true);
  };
  const submitChangeRequest = async () => {
    if (requestBusy) return;
    setRequestError('');
    if (requestMessage.trim().length < 8) {
      setRequestError('Tell us what should be changed.');
      return;
    }
    setRequestBusy(true);
    try {
      const response = await rpApi('/api/account/request-change', {
        method: 'POST',
        body: JSON.stringify({
          customerId: c.id,
          message: requestMessage.trim(),
          requestType
        })
      });
      setRequestMessage('');
      setRequestOpen(false);
      toast(`Request ${response.id || ''} sent to Operations`, 'ok');
      await rpRefreshAfterMutation('Account change request sent');
    } catch (err) {
      setRequestError(err && err.message ? err.message : 'Could not send request.');
    } finally {
      setRequestBusy(false);
    }
  };

  return (
    <PortalWrap>
      {!live && resetOpen && (
        <Modal title="Reset Password" sub="We'll generate a new temporary password" onClose={() => setResetOpen(false)} width={440}
          footer={<><button className="btn" onClick={() => setResetOpen(false)}>Cancel</button><button className="btn btn-primary" onClick={doReset}><Icon name="refresh" size={15} />Reset & Email</button></>}>
          <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>A new temporary password will be generated and emailed to <b>{creds.username}</b>. You'll be asked to change it at next sign-in.</div>
        </Modal>
      )}
      {requestOpen && (
        <Modal title="Request Account Change" sub="Send an account correction to Operations" onClose={() => setRequestOpen(false)} width={500}
          footer={<><button className="btn" onClick={() => setRequestOpen(false)}>Cancel</button><button className="btn btn-primary" disabled={requestBusy} onClick={submitChangeRequest}><Icon name="check" size={15} />{requestBusy ? 'Sending...' : 'Send Request'}</button></>}>
          <MField label="What should change?">
            <select className="field" value={requestType} onChange={e => setRequestType(e.target.value)}>
              {['Buyer/contact', 'Billing terms', 'Delivery details', 'Store listing', 'Other'].map(t => <option key={t}>{t}</option>)}
            </select>
          </MField>
          <MField label="Details" required>
            <textarea className="field" rows="4" value={requestMessage} onChange={e => setRequestMessage(e.target.value)} placeholder="Tell us exactly what needs updating..." style={{ resize: 'none' }} />
          </MField>
          {requestError && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 9, padding: '10px 12px', fontSize: 12.5, fontWeight: 700 }}>{requestError}</div>}
        </Modal>
      )}
      {cardOpen && <StripeCardSetupModal customer={c} onClose={() => setCardOpen(false)} onSaved={() => force(x => x + 1)} consentText="I authorize Ranch Power to securely save this card with Stripe and charge approved Ranch Power invoices to this saved card." />}
      <PortalH eyebrow="Account" title="My Account" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
            <Avatar name={c.name} size={50} color="var(--navy)" />
            <div><div style={{ fontWeight: 700, fontSize: 17 }}>{c.name}</div><div style={{ fontSize: 13, color: 'var(--muted)' }}>{c.type} - {c.city}</div></div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, fontSize: 13.5 }}>
            <Row k="Buyer" v={c.buyer} />
            <Row k="Payment terms" v={c.terms} />
            <Row k="Credit status" v={<Pill status={c.credit} />} />
            <Row k="Account rep" v={c.rep} />
            <Row k="Territory" v={c.territory} />
          </div>
          <div style={{ marginTop: 12, color: 'var(--muted)', fontSize: 12, lineHeight: 1.45 }}>{RP_CREDIT_CARD_TERMS_NOTE}</div>
          <button className="btn" style={{ width: '100%', marginTop: 16 }} onClick={openChangeRequest}><Icon name="pen" size={15} />Request a change</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>Payment Card</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11, fontSize: 13.5, marginBottom: 14 }}>
              <Row k="Status" v={<Pill status={savedCard ? 'Paid' : 'Pending'}>{savedCard ? 'Saved' : 'No card'}</Pill>} />
              <Row k="Card" v={typeof rpStripeCardLabel === 'function' ? rpStripeCardLabel(savedCard) : (savedCard ? `${savedCard.brand || 'Card'} ending ${savedCard.last4 || ''}` : 'Not saved')} />
              <Row k="Processor" v="Stripe" />
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45, marginBottom: 12 }}>Card details are handled by Stripe. Ranch Power stores only the secure payment reference and card summary.</div>
            <button className="btn btn-primary" disabled={!live} onClick={() => setCardOpen(true)} style={{ width: '100%', opacity: live ? 1 : 0.6 }}><Icon name="card" size={15} />{savedCard ? 'Replace Card' : 'Add Card'}</button>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>Sign-in & Security</SectionTitle>
            {live ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 11, fontSize: 13.5, marginBottom: 16 }}>
                  <Row k="Signed in as" v={persona.name} />
                  <Row k="Email" v={persona.email || authProfile.email} />
                  <Row k="Role" v={persona.role} />
                  <Row k="Customer context" v={c.name} />
                  <Row k="Account status" v={rpAccountStatus(authProfile, authDetails)} />
                  <Row k="Last sign-in" v={rpFormatAccountTime(authDetails.lastSignInAt)} />
                </div>

                <label className="field-label">Display name</label>
                <input className="field" value={displayName} onChange={e => setDisplayName(e.target.value)} autoComplete="name" />
                <button className="btn btn-primary" disabled={profileBusy || !displayName.trim()} onClick={saveProfile} style={{ width: '100%', marginTop: 10 }}><Icon name="check" size={15} />Save Profile</button>
                {profileMessage && <div style={{ fontSize: 11.5, color: profileMessage.includes('saved') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{profileMessage}</div>}

                <div style={{ height: 16 }} />
                <label className="field-label">New password</label>
                <input className="field" type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 12 characters" />
                <label className="field-label" style={{ marginTop: 10 }}>Confirm password</label>
                <input className="field" type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                <button className="btn btn-primary" disabled={passwordBusy || !newPassword || !confirmPassword} onClick={savePassword} style={{ width: '100%', marginTop: 10 }}><Icon name="check" size={15} />Set Password</button>
                <button className="btn" disabled={passwordBusy} onClick={sendReset} style={{ width: '100%', marginTop: 8 }}><Icon name="mail" size={15} />Email Reset Link</button>
                {passwordMessage && <div style={{ fontSize: 11.5, color: passwordMessage.includes('updated') || passwordMessage.includes('sent') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{passwordMessage}</div>}
              </>
            ) : (
              <>
                {creds.mustReset && <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--warn-bg)', color: 'var(--amber-deep)', padding: '10px 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}><Icon name="alert" size={15} />Temporary password - please change it.</div>}
                <label className="field-label">Username</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <input className="field mono" readOnly value={creds.username} style={{ flex: 1 }} />
                  <button className="btn" onClick={() => copy(creds.username, 'Username')}><Icon name="doc" size={15} /></button>
                </div>
                <label className="field-label">Password</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input className="field mono" readOnly type={showPw ? 'text' : 'password'} value={showPw ? creds.password : '**********'} style={{ flex: 1 }} />
                  <button className="btn" onClick={() => setShowPw(s => !s)}>{showPw ? 'Hide' : 'Show'}</button>
                </div>
                <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={() => setResetOpen(true)}><Icon name="refresh" size={15} />Reset Password</button>
                <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 10 }}>Last sign-in: {creds.lastLogin || 'First login pending'}</div>
              </>
            )}
          </div>

          <div className="card" style={{ padding: 20 }}>
            <SectionTitle>{live ? 'Access Policy' : 'Quick Access'}</SectionTitle>
            {live ? (
              <>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45, marginBottom: 12 }}>One-tap reorder links are disabled for live accounts. Use Ranch Power secure sign-in or an expiring magic link.</div>
                <button className="btn btn-primary" disabled={magicBusy} onClick={sendMagic} style={{ width: '100%' }}><Icon name="mail" size={15} />{magicBusy ? 'Sending...' : 'Email Magic Link'}</button>
                {magicMessage && <div style={{ fontSize: 11.5, color: magicMessage.includes('sent') ? 'var(--ok)' : 'var(--danger)', marginTop: 8 }}>{magicMessage}</div>}
              </>
            ) : (
              <>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.45 }}>Bookmark your one-tap reorder link - no password needed on trusted devices.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="field mono" readOnly value={creds.link} style={{ flex: 1, fontSize: 12 }} />
                  <button className="btn btn-navy" onClick={() => copy('https://' + creds.link, 'Quick-access link')}><Icon name="doc" size={15} />Copy</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {fees.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <SectionTitle>Account Credits & Fees</SectionTitle>
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="rp-table">
              <thead><tr><th>Reference</th><th>Type</th><th>Period</th><th className="num">Amount</th><th>Status</th></tr></thead>
              <tbody>
                {fees.map(d => (
                  <tr key={d.id}><td className="mono" style={{ fontWeight: 600 }}>{d.id}</td><td style={{ color: 'var(--muted)' }}>{d.type}</td><td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{d.period}</td><td className="num" style={{ fontWeight: 600 }}>{RP.money(d.amount)}</td><td><Pill status={d.status} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
        <button className="btn" style={{ color: 'var(--danger)' }} onClick={signOut}><Icon name="arrowL" size={15} />Sign out</button>
      </div>
    </PortalWrap>
  );
}

Object.assign(window, { PortalAccount });


/* ===== main.jsx ===== */
/* ============================================================
   RANCH POWER OPERATIONS HUB — root + platform/role switcher
   ============================================================ */

const OPS_ROLES = [
  { id: 'ops', name: 'Operations', who: 'A. Reyes' },
  { id: 'leadership', name: 'Leadership', who: 'C. Hale' },
];
const FIELD_ROLES = [
  { id: 'rep', name: 'Sales Rep', who: 'D. Whitfield' },
  { id: 'warehouse', name: 'Warehouse', who: 'J. Park' },
  { id: 'dispatch', name: 'Dispatcher', who: 'L. Tran' },
];

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": ["#b97a2e", "#9c5a2a"],
  "navy": ["#2c4456", "#20384a", "#16293a"],
  "density": "regular",
  "uiScale": 100,
  "corners": "rounded"
}/*EDITMODE-END*/;

const ACCENT_OPTS = [
  ['#b97a2e', '#9c5a2a'],   // tobacco amber (brand)
  ['#c0492a', '#a23b21'],   // blood-orange rust
  ['#3f6fc4', '#3257a0'],   // steel blue
  ['#5b8a3c', '#4a7230'],   // ranch green
];
const NAVY_OPTS = [
  ['#2c4456', '#20384a', '#16293a'],  // brand slate-navy
  ['#34404e', '#262f3a', '#1a212a'],  // graphite
  ['#2e3a52', '#222c40', '#161d2c'],  // deep indigo
];
const RADIUS = { sharp: ['4px', '3px', '7px'], rounded: ['9px', '6px', '14px'], soft: ['14px', '10px', '20px'] };

function testChromeEnabled() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('demoChrome') === '1' || params.get('testChrome') === '1';
}

function Root() {
  const initialMode = resolveInitialMode();
  const modeLocked = isModeLocked();
  const demoMode = !!(window.RP_AUTH && window.RP_AUTH.demo);
  const showTestChrome = demoMode && testChromeEnabled();
  const [, setAuthTick] = useState(0);
  const [mode, setMode] = useState(initialMode); // operations | field | client
  const [opsRole, setOpsRole] = useState(getInitialRole('operations'));
  const [fieldRole, setFieldRole] = useState(getInitialRole('field'));
  useEffect(() => {
    const refresh = () => setAuthTick(x => x + 1);
    window.addEventListener('rp-auth-profile-change', refresh);
    window.addEventListener('rp-data-refresh', refresh);
    return () => {
      window.removeEventListener('rp-auth-profile-change', refresh);
      window.removeEventListener('rp-data-refresh', refresh);
    };
  }, []);
  useEffect(() => {
    const notify = input => {
      if (typeof rpNotifyErrorWithTicket !== 'function') return;
      const message = String((input && (input.message || input.reason || input.error)) || '').trim();
      if (/resizeobserver loop/i.test(message)) return;
      rpNotifyErrorWithTicket(input);
    };
    const onError = event => {
      if (event && event.target && event.target !== window) return;
      notify({
        error: event.error || event.message || 'Uncaught browser error.',
        title: 'Uncaught browser error',
        context: {
          file: event.filename || '',
          line: event.lineno || '',
          column: event.colno || ''
        },
        source: 'Automatic error report - browser runtime'
      });
    };
    const onRejection = event => {
      notify({
        error: event.reason || 'Unhandled promise rejection.',
        title: 'Unhandled browser promise rejection',
        source: 'Automatic error report - browser runtime'
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
  const authProfile = rpAuthProfile();
  const CLIENT_ACCTS = (typeof rpVisibleCustomers === 'function' ? rpVisibleCustomers(RP.CUSTOMERS) : RP.CUSTOMERS).filter(rpCustomerOrderReady);
  const allowedClientId = authProfile.customer_id || authProfile.customerId || null;
  const liveLockedClient = modeLocked && initialMode === 'client' && !demoMode;
  const visibleClientAccts = modeLocked && mode === 'client' && allowedClientId ? CLIENT_ACCTS.filter(c => c.id === allowedClientId) : CLIENT_ACCTS;
  const [clientId, setClientId] = useState(liveLockedClient ? allowedClientId : (allowedClientId || (CLIENT_ACCTS[0] ? CLIENT_ACCTS[0].id : 'C-001')));
  const exactLinkedCustomer = useRpExactCustomer({ customerId: allowedClientId, enabled: liveLockedClient && Boolean(allowedClientId) });
  useEffect(() => {
    if (liveLockedClient && allowedClientId && clientId !== allowedClientId) setClientId(allowedClientId);
  }, [liveLockedClient, allowedClientId, clientId]);
  const selectedClient = liveLockedClient ? exactLinkedCustomer.customer : (clientId ? RP.cust(clientId) : null);
  const [hdrNarrow, setHdrNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 720);
  useEffect(() => {
    const on = () => setHdrNarrow(window.innerWidth < 720);
    window.addEventListener('resize', on); on();
    return () => window.removeEventListener('resize', on);
  }, []);
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const roles = mode === 'operations' ? OPS_ROLES : FIELD_ROLES;
  const role = mode === 'operations' ? opsRole : fieldRole;
  const setRole = mode === 'operations' ? setOpsRole : setFieldRole;
  const eyebrowLabel = mode === 'operations' ? 'OPERATIONS HUB' : mode === 'field' ? 'FIELD APP' : 'CLIENT PORTAL';

  // Apply tweaks as CSS variables on the root node
  const dens = t.density === 'compact' ? 0.82 : t.density === 'comfy' ? 1.15 : 1;
  const [r1, r2, r3] = RADIUS[t.corners] || RADIUS.rounded;
  const cssVars = {
    '--amber': t.accent[0], '--amber-deep': t.accent[1],
    '--navy': t.navy[0], '--navy-800': t.navy[1], '--navy-900': t.navy[2],
    '--radius': r1, '--radius-sm': r2, '--radius-lg': r3,
    '--dens': dens,
    fontSize: (t.uiScale / 100) + 'em',
  };

  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: mode === 'field' && !showTestChrome ? 'var(--paper)' : 'var(--navy-900)', ...cssVars }}>
      {/* OS chrome */}
      {showTestChrome && <header style={{ height: 52, flexShrink: 0, background: 'var(--navy-900)', borderBottom: '1px solid oklch(1 0 0 / 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: hdrNarrow ? 8 : 14, padding: hdrNarrow ? '0 10px' : '0 16px', color: '#fff', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexShrink: 0 }}>
          <Buffalo size={28} />
          {!hdrNarrow && <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div className="disp" style={{ fontSize: 15, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1, whiteSpace: 'nowrap' }}>Ranch Power</div>
            <div style={{ fontSize: 9, letterSpacing: '0.2em', color: 'var(--amber)', fontFamily: 'var(--ff-display)', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>{eyebrowLabel}</div>
          </div>}
        </div>

        {/* Mode switch */}
        {!modeLocked && <div style={{ display: 'inline-flex', background: 'oklch(1 0 0 / 0.08)', borderRadius: 9, padding: 3, gap: 2, flexShrink: 0 }}>
          {[['operations', 'Operations', 'admin'], ['field', 'Field', 'phone'], ['client', 'Client', 'customers']].map(([m, l, ic]) => {
            const on = mode === m;
            return <button key={m} onClick={() => setMode(m)} title={l} style={{ display: 'flex', alignItems: 'center', gap: 7, border: 'none', borderRadius: 7, padding: hdrNarrow ? '8px 9px' : '7px 14px', fontSize: 13, fontWeight: 600, background: on ? 'var(--surface)' : 'transparent', color: on ? 'var(--ink)' : 'oklch(0.8 0.02 235)', transition: 'all .15s' }}><Icon name={ic} size={15} />{!hdrNarrow && l}</button>;
          })}
        </div>}

        {/* Role / account switch */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {!hdrNarrow && <span style={{ fontSize: 11, color: 'oklch(0.65 0.02 235)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, flexShrink: 0 }}>{mode === 'client' ? 'Logged in as' : 'Viewing as'}</span>}
          {mode === 'client' ? (
            modeLocked ? (
              <span style={{ background: selectedClient ? 'var(--amber)' : 'var(--danger)', color: '#fff', border: '1px solid ' + (selectedClient ? 'var(--amber-deep)' : 'var(--danger)'), borderRadius: 100, padding: '7px 13px', fontSize: 12.5, fontWeight: 600, maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedClient ? selectedClient.name : 'No customer linked'}</span>
            ) : (
              <select value={clientId} onChange={e => setClientId(e.target.value)} style={{ background: 'var(--amber)', color: '#fff', border: '1px solid var(--amber-deep)', borderRadius: 100, padding: '7px 13px', fontSize: 12.5, fontWeight: 600, maxWidth: 200, cursor: 'pointer' }}>
                {visibleClientAccts.map(c => <option key={c.id} value={c.id} style={{ background: 'var(--surface)', color: 'var(--ink)' }}>{c.name}</option>)}
              </select>
            )
          ) : (
          modeLocked ? (
            <span style={{ border: '1px solid var(--amber)', background: 'var(--amber)', color: '#fff', borderRadius: 100, padding: '6px 13px', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{authProfile.display_name || authProfile.email || (mode === 'operations' ? 'Operations' : 'Field')}</span>
          ) : <div className="rp-scroll" style={{ display: 'flex', gap: 4, overflowX: 'auto', maxWidth: '100%' }}>
            {roles.map(r => {
              const on = role === r.id;
              return <button key={r.id} onClick={() => setRole(r.id)} title={r.who} style={{ border: '1px solid ' + (on ? 'var(--amber)' : 'oklch(1 0 0 / 0.14)'), background: on ? 'var(--amber)' : 'transparent', color: on ? '#fff' : 'oklch(0.82 0.02 235)', borderRadius: 100, padding: '6px 13px', fontSize: 12.5, fontWeight: 600, transition: 'all .15s', whiteSpace: 'nowrap', flexShrink: 0 }}>{r.name}</button>;
            })}
          </div>
          )}
        </div>
      </header>}

      {/* App body */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {mode === 'operations' ? <DeskShell key={'ops-' + role} role={role} />
          : mode === 'field' ? <SalesExperience key={'field-' + role} role={role} />
            : <ClientPortal key={'client-' + clientId} custId={clientId} />}
      </div>

      <ToastHost />

      {showTestChrome && <TweaksPanel title="Tweaks">
        <TweakSection label="Brand" />
        <TweakColor label="Action accent" value={t.accent} options={ACCENT_OPTS} onChange={v => setTweak('accent', v)} />
        <TweakColor label="Chrome navy" value={t.navy} options={NAVY_OPTS} onChange={v => setTweak('navy', v)} />
        <TweakSection label="Layout" />
        <TweakRadio label="Density" value={t.density} options={['compact', 'regular', 'comfy']} onChange={v => setTweak('density', v)} />
        <TweakRadio label="Corners" value={t.corners} options={['sharp', 'rounded', 'soft']} onChange={v => setTweak('corners', v)} />
        <TweakSlider label="UI scale" value={t.uiScale} min={85} max={115} step={5} unit="%" onChange={v => setTweak('uiScale', v)} />
      </TweaksPanel>}
    </div>
  );
}

createRoot(document.getElementById('root')).render(<PlatformGate><Root /></PlatformGate>);

